export type NlsStringSurface =
  | 'common'
  | 'auth'
  | 'onboarding'
  | 'shell'
  | 'dashboard'
  | 'manage'
  | 'checklist'
  | 'chat'
  | 'upload'
  | 'forms'
  | 'meetings'
  | 'ai'
  | 'linkhub'
  | 'reports'
  | 'analysis'
  | 'settings'
  | 'owner-console'
  | 'workbench'
  | 'experience'
  | 'resources'
  | 'member'
  | 'helper'
  | 'capacitor'
  | 'clients'
  | 'starter-kits'
  | 'timesheets'

export type NlsStringMetadata = {
  text: string
  description: string
  surface: NlsStringSurface
  maxChars?: number
}

import { KIT_STRINGS_NLS } from './kitStrings_nls'

export const STRINGS_NLS = {
  ...KIT_STRINGS_NLS,
  NLSChartSeriesOther: { text: 'Other', description: 'Other series label in chart breakdown.', surface: 'common', maxChars: 20 },
  NLSChartSeriesUnassigned: { text: 'Unassigned', description: 'Unassigned series label in chart breakdown.', surface: 'common', maxChars: 20 },
  NLSChartStateLoading: { text: 'Loading chart...', description: 'Loading state for chart renderer.', surface: 'common', maxChars: 40 },
  NLSChartStateEmpty: { text: 'No data to display', description: 'Empty state message for chart.', surface: 'common', maxChars: 40 },
  NLSChartStateError: { text: 'Could not load chart data', description: 'Error state message for chart.', surface: 'common', maxChars: 50 },
  NLSChartStateDeleted: { text: 'This chart was deleted', description: 'Deleted state message for chart embed.', surface: 'common', maxChars: 50 },
  NLSChartStateNoAccess: { text: 'You do not have access to view this chart', description: 'No-access state message for chart embed.', surface: 'common', maxChars: 60 },
  NLSChartActionRefresh: { text: 'Refresh', description: 'Refresh chart action button.', surface: 'common', maxChars: 20 },
  NLSChartActionExportCsv: { text: 'Download CSV', description: 'Download CSV action button for chart.', surface: 'common', maxChars: 25 },
  NLSChartActionExportPng: { text: 'Download PNG', description: 'Download PNG action button for chart.', surface: 'common', maxChars: 25 },
  NLSChartTableShowMore: { text: 'Showing {count} of {total} rows', description: 'Status text for table rows cap.', surface: 'common', maxChars: 40 },
  NLSChartA11yDataTable: { text: 'Data table for {title}', description: 'Screen reader hidden data table label.', surface: 'common', maxChars: 50 },
  NLSChartTarget: { text: 'Target', description: 'Target label on charts.', surface: 'common', maxChars: 20 },
  NLSChartActual: { text: 'Actual', description: 'Actual label on charts.', surface: 'common', maxChars: 20 },
  NLSChartIdeal: { text: 'Ideal', description: 'Ideal line label on burndown/burnup charts.', surface: 'common', maxChars: 20 },
  NLSChartPreviousPeriod: { text: 'vs previous period', description: 'Comparison label on big number charts.', surface: 'common', maxChars: 30 },
  NLSChartExportAsOf: { text: 'As of {date}', description: 'Dated snapshot caption for exported chart shape.', surface: 'common', maxChars: 40 },
  NLSDesignChartDeletedOrUnreadable: { text: 'This chart was deleted or is unreadable', description: 'Placeholder message when a chart shape cannot be loaded.', surface: 'workbench', maxChars: 60 },
  NLSDesignChartSelectTitle: { text: 'Insert chart', description: 'Title for chart picker modal in Design Hub.', surface: 'workbench', maxChars: 30 },
  NLSDesignChartNew: { text: 'New chart', description: 'Button to create a new chart in Design Hub chart picker.', surface: 'workbench', maxChars: 25 },
  NLSChartPhoneTableSeries: { text: 'Series', description: 'Table column header for chart series name.', surface: 'common', maxChars: 20 },
  NLSChartPhoneTableX: { text: 'X', description: 'Table column header for horizontal dimension/category value.', surface: 'common', maxChars: 15 },
  NLSChartPhoneTableY: { text: 'Y', description: 'Table column header for vertical measure value.', surface: 'common', maxChars: 15 },
  NLSChartPhoneFallbackTitle: { text: 'Chart', description: 'Fallback title for a chart when none is set.', surface: 'common', maxChars: 25 },
  NLSChartPhoneFallbackData: { text: 'Data', description: 'Fallback data title when spec title is missing.', surface: 'common', maxChars: 25 },
  NLSChartLibraryPickerSearchPlaceholder: { text: 'Search charts…', description: 'Placeholder for searching charts in library picker.', surface: 'common', maxChars: 30 },
  NLSChartLibraryPickerEmpty: { text: 'No saved charts found in this workspace.', description: 'Message when no saved charts exist.', surface: 'common', maxChars: 60 },
  NLSChartLibraryPickerError: { text: 'Could not load charts from library.', description: 'Error message when listing charts fails.', surface: 'common', maxChars: 50 },
  NLSChartLibraryPickerPlace: { text: 'Place chart', description: 'Confirm button to place the selected chart.', surface: 'common', maxChars: 25 },
  NLSChartLibraryPickerRetry: { text: 'Retry', description: 'Button to retry loading charts.', surface: 'common', maxChars: 20 },
  NLSReportCenterTitle: { text: 'Report Center', description: 'Report Center product name.', surface: 'reports', maxChars: 30 },
  NLSReportCenterAriaNavigation: { text: 'Report Center navigation', description: 'Accessible label for Report Center navigation.', surface: 'reports', maxChars: 40 },
  NLSReportCenterAriaFilters: { text: 'Report filters', description: 'Accessible label for Report Center filters.', surface: 'reports', maxChars: 30 },
  NLSReportCenterActionGenerate: { text: 'Generate', description: 'Starts Report Center generation.', surface: 'reports', maxChars: 20 },
  NLSReportCenterFilterAll: { text: 'All', description: 'Report Center all filter.', surface: 'reports', maxChars: 16 },
  NLSReportCenterFilterReady: { text: 'Ready', description: 'Report Center ready filter.', surface: 'reports', maxChars: 16 },
  NLSReportCenterFilterPinned: { text: 'Pinned', description: 'Report Center pinned filter.', surface: 'reports', maxChars: 16 },
  NLSReportCenterSubtitle: { text: 'Briefings, documents and schedules', description: 'Report Center shell subtitle.', surface: 'reports', maxChars: 50 },
  NLSReportCenterNavHome: { text: 'Home', description: 'Report Center nav item Home.', surface: 'reports', maxChars: 20 },
  NLSReportCenterNavHomeSubtitle: { text: 'Overview and recent briefs', description: 'Report Center nav Home subtitle.', surface: 'reports', maxChars: 40 },
  NLSReportCenterNavTemplates: { text: 'Templates', description: 'Report Center nav item Templates.', surface: 'reports', maxChars: 20 },
  NLSReportCenterNavTemplatesSubtitle: { text: 'Prebuilt frameworks', description: 'Report Center nav Templates subtitle.', surface: 'reports', maxChars: 40 },
  NLSReportCenterNavGenerate: { text: 'Generate', description: 'Report Center nav item Generate.', surface: 'reports', maxChars: 20 },
  NLSReportCenterNavGenerateSubtitle: { text: 'AI-assisted briefing', description: 'Report Center nav Generate subtitle.', surface: 'reports', maxChars: 40 },
  NLSReportCenterNavBuild: { text: 'Build', description: 'Report Center nav item Build.', surface: 'reports', maxChars: 20 },
  NLSReportCenterNavBuildSubtitle: { text: 'Manual document editor', description: 'Report Center nav Build subtitle.', surface: 'reports', maxChars: 40 },
  NLSReportCenterNavSchedules: { text: 'Schedules', description: 'Report Center nav item Schedules.', surface: 'reports', maxChars: 20 },
  NLSReportCenterNavSchedulesSubtitle: { text: 'Automated cadences', description: 'Report Center nav Schedules subtitle.', surface: 'reports', maxChars: 40 },
  NLSReportCenterNavShares: { text: 'Shares', description: 'Report Center nav item Shares.', surface: 'reports', maxChars: 20 },
  NLSReportCenterNavSharesSubtitle: { text: 'External access links', description: 'Report Center nav Shares subtitle.', surface: 'reports', maxChars: 40 },
  NLSReportCenterRailFooter: { text: '{ready} ready · {scheduled} scheduled', description: 'Status footer in left rail.', surface: 'reports', maxChars: 40 },
  NLSReportCenterActionNewReport: { text: 'New report', description: 'Action button to start generating a report.', surface: 'reports', maxChars: 20 },
  NLSReportCenterActionBack: { text: 'Back to reports', description: 'Action button to return from reading a report.', surface: 'reports', maxChars: 24 },
  NLSReportCenterUntitledReport: { text: 'Untitled report', description: 'Fallback title for untitled reports.', surface: 'reports', maxChars: 30 },
  NLSReportCenterMetricTotal: { text: 'Total', description: 'Metric card eyebrow for total reports.', surface: 'reports', maxChars: 20 },
  NLSReportCenterMetricTotalSubtitle: { text: 'all reports', description: 'Metric card subtitle for total reports.', surface: 'reports', maxChars: 30 },
  NLSReportCenterMetricReady: { text: 'Ready', description: 'Metric card eyebrow for ready reports.', surface: 'reports', maxChars: 20 },
  NLSReportCenterMetricReadySubtitle: { text: 'available to read', description: 'Metric card subtitle for ready reports.', surface: 'reports', maxChars: 30 },
  NLSReportCenterMetricScheduled: { text: 'Scheduled', description: 'Metric card eyebrow for scheduled reports.', surface: 'reports', maxChars: 20 },
  NLSReportCenterMetricScheduledSubtitle: { text: 'active cadences', description: 'Metric card subtitle for scheduled reports.', surface: 'reports', maxChars: 30 },
  NLSReportCenterMetricShared: { text: 'Shared', description: 'Metric card eyebrow for shared reports.', surface: 'reports', maxChars: 20 },
  NLSReportCenterMetricSharedSubtitle: { text: 'active web links', description: 'Metric card subtitle for shared reports.', surface: 'reports', maxChars: 30 },
  NLSReportCenterLaunchGenerateTitle: { text: 'Generate with AI', description: 'Action card title to generate with AI.', surface: 'reports', maxChars: 30 },
  NLSReportCenterLaunchGenerateSubtitle: { text: 'Create a tailored briefing from workspace data.', description: 'Action card subtitle to generate with AI.', surface: 'reports', maxChars: 60 },
  NLSReportCenterLaunchBuildTitle: { text: 'Build from scratch', description: 'Action card title to build manually.', surface: 'reports', maxChars: 30 },
  NLSReportCenterLaunchBuildSubtitle: { text: 'Compose a custom multi-block report by hand.', description: 'Action card subtitle to build manually.', surface: 'reports', maxChars: 60 },
  NLSReportCenterLaunchTemplatesTitle: { text: 'Start from a template', description: 'Action card title to start from template.', surface: 'reports', maxChars: 30 },
  NLSReportCenterLaunchTemplatesSubtitle: { text: 'Pick an executive, operational, or status starter.', description: 'Action card subtitle to start from template.', surface: 'reports', maxChars: 60 },
  NLSReportCenterLaunchSchedulesTitle: { text: 'Set up a schedule', description: 'Action card title to set up a schedule.', surface: 'reports', maxChars: 30 },
  NLSReportCenterLaunchSchedulesSubtitle: { text: 'Automate weekly or monthly distributions.', description: 'Action card subtitle to set up a schedule.', surface: 'reports', maxChars: 60 },
  NLSReportCenterRecentHeading: { text: 'Recent reports', description: 'Eyebrow for recent reports list.', surface: 'reports', maxChars: 30 },
  NLSReportCenterRecentSubtitle: { text: 'Pick up where you left off', description: 'Heading for recent reports list.', surface: 'reports', maxChars: 40 },
  NLSReportCenterEmptyRecent: { text: 'No matching reports found', description: 'Empty state message when recent filter matches nothing.', surface: 'reports', maxChars: 40 },
  NLSReportCenterEmptyTitle: { text: 'Your report desk is ready', description: 'Empty hub state title.', surface: 'reports', maxChars: 40 },
  NLSReportCenterEmptyDesc: { text: 'Generate a visual briefing or begin a document from scratch.', description: 'Empty hub state description.', surface: 'reports', maxChars: 80 },
  NLSReportCenterEmptyActionGenerate: { text: 'Generate report', description: 'Empty hub state primary action.', surface: 'reports', maxChars: 24 },
  NLSReportCenterEmptyActionBuild: { text: 'Build from scratch', description: 'Empty hub state secondary action.', surface: 'reports', maxChars: 24 },
  NLSReportCenterSidecarAria: { text: 'Report context', description: 'Accessible label for Report Center sidecar.', surface: 'reports', maxChars: 30 },
  NLSReportCenterUpcomingSchedules: { text: 'Upcoming schedules', description: 'Sidecar upcoming schedules heading.', surface: 'reports', maxChars: 30 },
  NLSReportCenterActiveShares: { text: 'Active share links', description: 'Sidecar active share links heading.', surface: 'reports', maxChars: 30 },
  NLSReportCenterEmptySchedules: { text: 'No upcoming schedules', description: 'Sidecar empty upcoming schedules.', surface: 'reports', maxChars: 30 },
  NLSReportCenterEmptyShares: { text: 'No active share links', description: 'Sidecar empty active share links.', surface: 'reports', maxChars: 30 },
  NLSReportCenterViewAllSchedules: { text: 'View all schedules', description: 'Sidecar link to view all schedules.', surface: 'reports', maxChars: 30 },
  NLSReportCenterViewAllShares: { text: 'View all shares', description: 'Sidecar link to view all shares.', surface: 'reports', maxChars: 30 },
  NLSReportCenterNextRun: { text: 'Next run: {time}', description: 'Next run time for a schedule.', surface: 'reports', maxChars: 40 },
  NLSReportCenterViewsCount: { text: '{count} views', description: 'View count for a share link.', surface: 'reports', maxChars: 24 },
  NLSReportCenterErrorLoading: { text: 'Failed to load report data', description: 'Error message when report data fails to load.', surface: 'reports', maxChars: 60 },
  NLSReportCenterActionRetry: { text: 'Retry', description: 'Button label to retry loading report data.', surface: 'reports', maxChars: 20 },
  NLSReportCenterLoading: { text: 'Loading reports...', description: 'Loading indicator text for Report Center.', surface: 'reports', maxChars: 30 },
  NLSReportCenterScheduledReportFallback: { text: 'Scheduled report', description: 'Fallback name for scheduled report with unknown template.', surface: 'reports', maxChars: 40 },
  NLSReportCenterEmptyNoReports: { text: 'No reports yet. Use a tile above to start.', description: 'Empty state message on Report Center home.', surface: 'reports', maxChars: 60 },
  NLSReportStatusDraft: { text: 'Draft', description: 'Report status Draft.', surface: 'reports', maxChars: 20 },
  NLSReportStatusGenerating: { text: 'Generating', description: 'Report status Generating.', surface: 'reports', maxChars: 20 },
  NLSReportStatusReady: { text: 'Ready', description: 'Report status Ready.', surface: 'reports', maxChars: 20 },
  NLSReportStatusFailed: { text: 'Failed', description: 'Report status Failed.', surface: 'reports', maxChars: 20 },
  NLSReportStatusArchived: { text: 'Archived', description: 'Report status Archived.', surface: 'reports', maxChars: 20 },
  NLSRoleOwner: {
    text: 'Owner',
    description: 'Workspace role label: Owner.',
    surface: 'common',
    maxChars: 20,
  },
  NLSRoleTeamAdmin: {
    text: 'Team admin',
    description: 'Workspace role label: Team admin.',
    surface: 'common',
    maxChars: 20,
  },
  NLSRoleEditor: {
    text: 'Editor',
    description: 'Workspace role label: Editor.',
    surface: 'common',
    maxChars: 20,
  },
  NLSRoleViewer: {
    text: 'Viewer',
    description: 'Workspace role label: Viewer.',
    surface: 'common',
    maxChars: 20,
  },
  NLSRoleReadonly: {
    text: 'Read-only',
    description: 'Workspace role label: Read-only.',
    surface: 'common',
    maxChars: 20,
  },
  NLSRoleHelper: {
    text: 'Helper',
    description: 'Workspace role label: Helper.',
    surface: 'common',
    maxChars: 20,
  },
  NLSRoleLearner: {
    text: 'Learner',
    description: 'Workspace role label: Learner.',
    surface: 'common',
    maxChars: 20,
  },
  NLSEduConsentNoticeTitle: {
    text: 'Parent & Guardian Consent',
    description: 'DRAFT v0, legal review by D before launch',
    surface: 'common',
    maxChars: 50,
  },
  NLSEduConsentNoticeBody: {
    text: "The institution holds the child's records. As a guardian, you can see attendance, homework, and marks. The data is used only for the child's schooling or training. Consent can be withdrawn any time in Settings, which freezes the child's data and asks the institution to delete it.",
    description: 'DRAFT v0, legal review by D before launch',
    surface: 'common',
    maxChars: 300,
  },
  NLSItemNotAvailable: {
    text: "This item isn't available.",
    description: "Neutral message shown when a deep-linked item is missing, inaccessible, or not loaded.",
    surface: 'common',
    maxChars: 40,
  },
  NLSOwnersTitle: {
    text: 'Workspace owners',
    description: 'Title for workspace owners section in permissions panel.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSOwnersSubtitle: {
    text: 'Owners can manage team admins, roles, settings, and ownership.',
    description: 'Subtitle explaining owner privileges.',
    surface: 'settings',
    maxChars: 80,
  },
  NLSOwnersBuyerBadge: {
    text: 'Buyer',
    description: 'Badge for the original buyer owner.',
    surface: 'settings',
    maxChars: 15,
  },
  NLSOwnersAddAction: {
    text: 'Add owner',
    description: 'Button to add a new workspace owner.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSOwnersRemoveAction: {
    text: 'Remove',
    description: 'Button to remove a workspace owner.',
    surface: 'settings',
    maxChars: 15,
  },
  NLSOwnersHandOverAction: {
    text: 'Hand over',
    description: 'Button to hand over workspace ownership.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSOwnersDeleteAction: {
    text: 'Delete workspace',
    description: 'Button to delete workspace.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSOwnersAddModalTitle: {
    text: 'Add workspace owner',
    description: 'Modal title for adding a workspace owner.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSOwnersAddModalPrompt: {
    text: 'Select an active team member to make an owner.',
    description: 'Modal prompt for selecting a member to promote to owner.',
    surface: 'settings',
    maxChars: 70,
  },
  NLSOwnersRemoveModalTitle: {
    text: 'Remove workspace owner',
    description: 'Modal title for removing an owner.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSOwnersRemoveModalPrompt: {
    text: 'Are you sure you want to remove this owner? They will become a team admin.',
    description: 'Confirmation prompt for removing an owner.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSOwnersHandOverModalTitle: {
    text: 'Hand over workspace ownership',
    description: 'Modal title for handing over ownership.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSOwnersHandOverModalPrompt: {
    text: 'Select a team member to hand over ownership to. You will become a team admin.',
    description: 'Confirmation prompt for handing over ownership.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSOwnersCandidateSelectPlaceholder: {
    text: 'Choose member',
    description: 'Placeholder for candidate selection dropdown.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSOwnersCandidateSelectLabel: {
    text: 'Eligible member',
    description: 'Accessible label for member selection dropdown.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSOwnersNoCandidates: {
    text: 'No eligible members available.',
    description: 'Notice when no non-owner active non-helper members exist.',
    surface: 'settings',
    maxChars: 50,
  },
  NLSOwnersSelfBuyerRemovePrompt: {
    text: 'Are you sure you want to remove yourself as owner? Ownership will move to another owner and you will become a team admin.',
    description: 'Confirmation prompt when buyer removes themselves.',
    surface: 'settings',
    maxChars: 140,
  },
  NLSDeleteWorkspaceTitle: {
    text: 'Delete Workspace',
    description: 'Modal title for deleting workspace.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSDeleteWorkspaceError: {
    text: 'Could not delete workspace.',
    description: 'Error fallback when deleting workspace fails.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSDeleteWorkspaceCancel: {
    text: 'Cancel',
    description: 'Cancel button label in delete workspace modal.',
    surface: 'settings',
    maxChars: 15,
  },
  NLSDeleteWorkspaceDeleting: {
    text: 'Deleting',
    description: 'Button state while deleting workspace.',
    surface: 'settings',
    maxChars: 15,
  },
  NLSDeleteWorkspaceConfirmAction: {
    text: 'Delete Workspace',
    description: 'Confirm button label for deleting workspace.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSDeleteWorkspacePermanentLabel: {
    text: 'Permanent action',
    description: 'Warning eyebrow for permanent workspace deletion.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSDeleteWorkspaceWarning: {
    text: 'This will permanently delete this workspace and all its timelines.',
    description: 'Warning message in delete workspace modal.',
    surface: 'settings',
    maxChars: 80,
  },
  NLSDeleteWorkspaceInstructionPrefix: {
    text: 'TYPE ',
    description: 'Prefix instruction for confirmation text.',
    surface: 'settings',
    maxChars: 15,
  },
  NLSDeleteWorkspaceInstructionSuffix: {
    text: ' TO DELETE',
    description: 'Suffix instruction for confirmation text.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSDeletionScheduledGrace: {
    text: 'Scheduled for deletion on {date}. You can restore it until then.',
    description: 'Notice that resource is scheduled for deletion with 30-day grace.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSAccountDeletionGraceNotice: {
    text: 'Your account is scheduled for deletion on {date}.',
    description: 'Notice shown when an account is scheduled for deletion.',
    surface: 'settings',
    maxChars: 80,
  },
  NLSWorkspaceDeletionBannerTitle: {
    text: 'This workspace is scheduled for deletion on {date}.',
    description: 'Banner title for workspace scheduled for deletion.',
    surface: 'settings',
    maxChars: 80,
  },
  NLSRestoreWorkspaceAction: {
    text: 'Restore',
    description: 'Action to restore a workspace scheduled for deletion.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSRestoreAccountAction: {
    text: 'Restore Account',
    description: 'Action to restore an account scheduled for deletion.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSRestoringAction: {
    text: 'Restoring...',
    description: 'Action label while restore is pending.',
    surface: 'common',
    maxChars: 20,
  },
  NLSAccountDeletionGraceTitle: {
    text: 'Account Pending Deletion',
    description: 'Title for screen blocking access during 30-day deletion grace.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSCommonActionBackToWorkspace: {
    text: 'Back to workspace',
    description: 'Dismisses a blocking shared overlay and returns focus to the current workspace.',
    surface: 'common',
    maxChars: 30,
  },
  NLSCommonActionDownload: {
    text: 'Download',
    description: 'Downloads a file or generated artifact.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonActionOpen: {
    text: 'Open',
    description: 'Opens a file, link, or record.',
    surface: 'common',
    maxChars: 14,
  },
  NLSCommonActionReloadThisTab: {
    text: 'Reload this tab',
    description: 'Retries rendering the current tab after a scoped tab error.',
    surface: 'common',
    maxChars: 32,
  },
  NLSCommonActionBack: {
    text: 'Back',
    description: 'Generic compact back action.',
    surface: 'common',
    maxChars: 18,
  },
  NLSPathOnlyViewOnly: {
    text: '{title}, view only',
    description: 'Accessible label for path-only parent elements.',
    surface: 'common',
    maxChars: 80,
  },
  NLSPathOnlyUntitled: {
    text: 'Untitled',
    description: 'Fallback title for path-only element.',
    surface: 'common',
    maxChars: 36,
  },
  NLSScopeEmptyRestricted: {
    text: "You haven't been given any goals yet — ask an admin",
    description: 'Empty state message for restricted member with no assigned goals.',
    surface: 'common',
    maxChars: 80,
  },
  NLSCommonActionCancel: {
    text: 'Cancel',
    description: 'Generic compact cancel action.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonActionConfirm: {
    text: 'Confirm',
    description: 'Generic compact confirm action.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonActionApprove: {
    text: 'Approve',
    description: 'Generic compact approve action.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonActionReject: {
    text: 'Reject',
    description: 'Generic compact reject action.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonActionPrint: {
    text: 'Print',
    description: 'Generic compact print action.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonActionClose: {
    text: 'Close',
    description: 'Generic compact close action.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonActionContinue: {
    text: 'Continue',
    description: 'Generic continue action.',
    surface: 'common',
    maxChars: 22,
  },
  NLSCommonActionSignOut: {
    text: 'Sign out',
    description: 'Signs the current user out of the app.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonActionRetryLoadingWorkspaces: {
    text: 'Retry loading workspaces',
    description: 'Retries onboarding workspace hydration.',
    surface: 'common',
    maxChars: 42,
  },
  NLSCommonActionRetrying: {
    text: 'Retrying…',
    description: 'Busy label while retrying a request.',
    surface: 'common',
    maxChars: 24,
  },
  NLSCommonActionMarkUpInDesign: {
    text: 'Mark up in Design',
    description: 'Sends a previewable attachment into the Design annotation surface.',
    surface: 'common',
    maxChars: 34,
  },
  NLSCommonAriaAccessDenied: {
    text: 'Access denied',
    description: 'Accessible label for the access-denied page shell.',
    surface: 'common',
    maxChars: 28,
  },
  NLSCommonAriaClosePreview: {
    text: 'Close preview',
    description: 'Accessible label for closing the shared attachment preview dialog.',
    surface: 'common',
    maxChars: 28,
  },
  NLSCommonAriaConnectionStatus: {
    text: 'Connection status',
    description: 'Accessible label for the network failure status row.',
    surface: 'common',
    maxChars: 32,
  },
  NLSCommonAriaPreviewAttachment: {
    text: 'Preview {label}',
    description: 'Accessible label for the shared attachment preview dialog. {label} is the attachment label.',
    surface: 'common',
    maxChars: 80,
  },
  NLSCommonRailPageShellResizeRail: {
    text: 'Resize side panel',
    description: 'Accessible label for the left rail resize handle.',
    surface: 'common',
    maxChars: 40,
  },
  NLSCommonRailPageShellResizeSidecar: {
    text: 'Resize details panel',
    description: 'Accessible label for the right sidecar resize handle.',
    surface: 'common',
    maxChars: 40,
  },
  NLSCommonRailPageShellSidecarSheet: {
    text: 'Details panel',
    description: 'Accessible label for the mobile sidecar bottom-sheet toggle handle.',
    surface: 'common',
    maxChars: 40,
  },
  NLSCommonEmptyNoActions: {
    text: 'No actions',
    description: 'Empty state for shared action menus.',
    surface: 'common',
    maxChars: 22,
  },
  NLSCommonEmptyNoOptions: {
    text: 'No options',
    description: 'Empty state for shared selection controls.',
    surface: 'common',
    maxChars: 22,
  },
  NLSCommonMultiSelectSelectAll: {
    text: 'Select all',
    description: 'Bulk select all visible options in MultiSelect.',
    surface: 'common',
    maxChars: 30,
  },
  NLSCommonMultiSelectClear: {
    text: 'Clear',
    description: 'Bulk clear selection in MultiSelect.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonMultiSelectSelectedCount: {
    text: '{count} of {total} selected',
    description: 'Summary count of selected options in MultiSelect bulk row.',
    surface: 'common',
    maxChars: 50,
  },
  NLSCommonMultiSelectCreateOption: {
    text: 'Create “{text}”',
    description: 'Menu action to create a new option in MultiSelect.',
    surface: 'common',
    maxChars: 50,
  },
  NLSCommonMultiSelectRemoveTag: {
    text: 'Remove {label}',
    description: 'Aria label for removing a selected tag in MultiSelect.',
    surface: 'common',
    maxChars: 50,
  },
  NLSCommonEntityFormRemoveKpi: {
    text: 'Remove KPI',
    description: 'Aria label for removing a custom KPI row in the entity form.',
    surface: 'common',
    maxChars: 30,
  },
  NLSCommonEntityFormRemoveTier: {
    text: 'Remove reward tier',
    description: 'Aria label for removing a reward tier row in the entity form.',
    surface: 'common',
    maxChars: 30,
  },
  NLSCommonLabelFill: {
    text: 'Fill',
    description: 'Short label on a labeled select for the fill type.',
    surface: 'common',
    maxChars: 16,
  },
  NLSCommonLabelDesign: {
    text: 'Design',
    description: 'Short label on the labeled select for choosing which design to attach.',
    surface: 'common',
    maxChars: 16,
  },
  NLSCommonLockBadgeLabel: {
    text: 'Upgrade Lock',
    description: 'Default label for the upgrade lock badge.',
    surface: 'common',
    maxChars: 24,
  },
  NLSCommonLockBadgeRequires: {
    text: 'Requires {tier}',
    description: 'Tooltip title for the upgrade lock badge indicating the required pricing tier.',
    surface: 'common',
    maxChars: 30,
  },
  NLSCommonLockBadgePaidTier: {
    text: 'paid',
    description: 'Fallback tier name for the upgrade lock badge when no specific tier is resolved.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonProBadge: {
    text: 'PRO',
    description: 'Default label for the PRO feature badge.',
    surface: 'common',
    maxChars: 10,
  },
  NLSCommonChipGroupMore: {
    text: '+{count}',
    description: 'Overflow chip label showing remaining count.',
    surface: 'common',
    maxChars: 10,
  },
  NLSCommonTabMore: {
    text: 'More',
    description: 'Label for the More tab in mobile bottom navigation.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabMoreAriaLabel: {
    text: 'More tabs',
    description: 'Aria label for the More tabs sheet dialog.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonTabMoreHint: {
    text: 'Long-press a tab in the bar to swap it',
    description: 'Hint text at the bottom of the More tabs sheet.',
    surface: 'common',
    maxChars: 50,
  },
  NLSCommonTabEditBar: {
    text: 'Edit bar',
    description: 'Action button to open the bottom tab bar editor.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabBarEditorTitle: {
    text: 'Edit bar',
    description: 'Title of the bottom tab bar customization editor.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabBarEditorSubtitle: {
    text: 'Pick up to 4 tabs. More stays last.',
    description: 'Subtitle explaining bottom tab bar constraints.',
    surface: 'common',
    maxChars: 50,
  },
  NLSCommonTabBarInBarSection: {
    text: 'In the bar · {count} of 4',
    description: 'Section header indicating how many tabs are pinned in the bottom bar.',
    surface: 'common',
    maxChars: 30,
  },
  NLSCommonTabBarInMoreSection: {
    text: 'In More',
    description: 'Section header for tabs available in the More sheet.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabBarResetDefault: {
    text: 'Reset to workspace default',
    description: 'Action button to reset tab bar to workspace default layout.',
    surface: 'common',
    maxChars: 30,
  },
  NLSCommonTabBarSave: {
    text: 'Save bar',
    description: 'Button to save customized bottom tab bar layout.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabBarAlwaysFirst: {
    text: 'Always first',
    description: 'Badge indicating the first tab is fixed and cannot be moved or removed.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabBarMoveUp: {
    text: 'Move up',
    description: 'Aria label to move tab up in the bottom bar.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabBarMoveDown: {
    text: 'Move down',
    description: 'Aria label to move tab down in the bottom bar.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabBarMoveToMore: {
    text: 'Move to More',
    description: 'Aria label to unpin tab and move it to More.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabBarAddToBar: {
    text: 'Add to bar',
    description: 'Aria label to pin tab from More into the bar.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonTabUnreadDotAria: {
    text: 'unread',
    description: 'Accessible label for unread indicator dot on mobile tabs.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonChipGroupMoreAria: {
    text: '{count} more: {labels}',
    description: 'Accessibility announcement for overflow chip listing hidden labels.',
    surface: 'common',
    maxChars: 200,
  },
  NLSCommonOverflowToolbarMore: {
    text: 'More',
    description: 'Aria label for the overflow toolbar kebab menu trigger.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonTextareaCounterLabel: {
    text: '{count} / {max}',
    description: 'Character counter shown in the Textarea footer. {count} = current length, {max} = maxLength.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonTextareaCounterAriaLabel: {
    text: '{count} of {max} characters used',
    description: 'Screen-reader text for the Textarea character counter.',
    surface: 'common',
    maxChars: 40,
  },
  NLSCommonTimePickerHour: {
    text: 'Hour',
    description: 'Column heading for the hour column in TimePicker.',
    surface: 'common',
    maxChars: 10,
  },
  NLSCommonTimePickerMin: {
    text: 'Min',
    description: 'Column heading for the minute column in TimePicker.',
    surface: 'common',
    maxChars: 10,
  },
  NLSCommonTimePickerAm: {
    text: 'AM',
    description: 'AM label in the 12-hour TimePicker AM/PM column.',
    surface: 'common',
    maxChars: 4,
  },
  NLSCommonTimePickerPm: {
    text: 'PM',
    description: 'PM label in the 12-hour TimePicker AM/PM column.',
    surface: 'common',
    maxChars: 4,
  },
  NLSCommonTimePickerNow: {
    text: 'Now',
    description: 'Footer button in TimePicker that sets the time to the current time rounded to minuteStep.',
    surface: 'common',
    maxChars: 10,
  },
  NLSCommonTimePickerDone: {
    text: 'Done',
    description: 'Footer button in TimePicker that commits the selected time.',
    surface: 'common',
    maxChars: 10,
  },
  NLSCommonTimePickerClear: {
    text: 'Clear time',
    description: 'Footer or trigger clear action in TimePicker.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonTimePickerPlaceholder: {
    text: 'Pick a time',
    description: 'Default placeholder shown in the TimePicker trigger when no time is selected.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonTimePickerPeriod: {
    text: 'AM/PM',
    description: 'Accessible label for the AM/PM column in the 12-hour TimePicker.',
    surface: 'common',
    maxChars: 10,
  },
  NLSCommonColorPickerHexLabel: {
    text: 'Hex colour',
    description: 'Accessible label for the hex input field inside the ColorPicker popover.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonColorPickerInvalidColor: {
    text: 'Invalid colour',
    description: 'Error shown under the hex field in ColorPicker when the entered value is not a valid hex colour.',
    surface: 'common',
    maxChars: 24,
  },
  NLSCommonFilePickerDropzoneTitle: {
    text: 'Drop files here or {browse}',
    description: 'FilePicker dropzone title. {browse} is replaced with the bold word for the browse action (see NLSCommonFilePickerDropzoneBrowse). Translators may reorder {browse} within the sentence.',
    surface: 'common',
    maxChars: 40,
  },
  NLSCommonFilePickerDropzoneBrowse: {
    text: 'browse',
    description: 'Bold clickable word in the FilePicker dropzone title that opens the system file chooser.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonFilePickerHintUpTo: {
    text: 'up to {size}',
    description: 'Suffix appended to the FilePicker default hint, e.g. "PDF, PNG · up to 25 MB". {size} is replaced with the formatted max file size.',
    surface: 'common',
    maxChars: 20,
  },
  NLSCommonFilePickerRejectSize: {
    text: '{name} exceeds the maximum file size.',
    description: 'Error shown under the FilePicker when a file is rejected because it is too large. {name} is replaced with the file name.',
    surface: 'common',
    maxChars: 60,
  },
  NLSCommonFilePickerRejectType: {
    text: '{name} is not an accepted file type.',
    description: 'Error shown under the FilePicker when a file is rejected because its type is not in the accept list. {name} is replaced with the file name.',
    surface: 'common',
    maxChars: 60,
  },
  NLSCommonFilePickerRemoveFile: {
    text: 'Remove {name}',
    description: 'Accessible aria-label on the remove button in FileRow. {name} is replaced with the file name.',
    surface: 'common',
    maxChars: 40,
  },
  NLSCommonEntityFormNew: {
    text: 'New',
    description: 'Mode indicator shown on new entity form dialogs.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonEntityFormEditingTitle: {
    text: 'Editing {title}',
    description: 'Mode indicator shown on existing entity form dialogs. {title} is the current entity title.',
    surface: 'common',
    maxChars: 40,
  },
  NLSCommonEntityFormSave: {
    text: 'Save',
    description: 'Primary action button to save the entity form.',
    surface: 'common',
    maxChars: 16,
  },
  NLSCommonEntityFormSaving: {
    text: 'Saving…',
    description: 'Loading state text for the save button on the entity form.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonEntityFormSaveAndAddAnother: {
    text: 'Save & add another',
    description: 'Secondary action button to save the current entity and open a blank form.',
    surface: 'common',
    maxChars: 28,
  },
  NLSCommonBulkImportStepUpload: {
    text: 'Upload',
    description: 'Step 1 indicator in the bulk import dialog header.',
    surface: 'common',
    maxChars: 16,
  },
  NLSCommonBulkImportStepPreview: {
    text: 'Preview',
    description: 'Step 2 indicator in the bulk import dialog header.',
    surface: 'common',
    maxChars: 16,
  },
  NLSCommonBulkImportStepCommit: {
    text: 'Commit',
    description: 'Step 3 indicator in the bulk import dialog header.',
    surface: 'common',
    maxChars: 16,
  },
  NLSCommonBulkImportActionImportCount: {
    text: 'Import {count} {entityName}',
    description: 'Primary commit action button text in bulk import. {count} is the row count and {entityName} is the entity label.',
    surface: 'common',
    maxChars: 36,
  },
  NLSCommonBulkImportActionDone: {
    text: 'Done',
    description: 'Primary completion button in bulk import dialog.',
    surface: 'common',
    maxChars: 16,
  },
  NLSCommonErrorGeneric: {
    text: 'Something went wrong.',
    description: 'Generic visible error message when an unknown failure is caught.',
    surface: 'common',
    maxChars: 42,
  },
  NLSCommonInputSearchPlaceholder: {
    text: 'Search',
    description: 'Compact placeholder for shared search fields.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonInputSearchEllipsisPlaceholder: {
    text: 'Search…',
    description: 'Compact placeholder for shared search fields that use ellipsis styling.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonInputSearchOptionsPlaceholder: {
    text: 'Search options',
    description: 'Placeholder for searching option lists in compact filters.',
    surface: 'common',
    maxChars: 28,
  },
  NLSCommonInputSelectPlaceholder: {
    text: 'Select',
    description: 'Compact placeholder for shared select controls.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonInputSelectEllipsisPlaceholder: {
    text: 'Select…',
    description: 'Compact placeholder for shared select controls that already use ellipsis styling.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonStatusApi: {
    text: 'API',
    description: 'Generic request method fallback shown in connection failure state.',
    surface: 'common',
    maxChars: 12,
  },
  NLSCommonStatusLoading: {
    text: 'Loading',
    description: 'Generic accessible loading label.',
    surface: 'common',
    maxChars: 18,
  },
  NLSLoaderStallTitle: {
    text: "Can't reach Timelining",
    description: 'Title shown on the fullscreen loader stall card when connection stalls or errors.',
    surface: 'common',
    maxChars: 30,
  },
  NLSLoaderStallDescription: {
    text: 'Check your internet connection and try again.',
    description: 'Help text explaining connection failure on the fullscreen loader stall card.',
    surface: 'common',
    maxChars: 60,
  },
  NLSLoaderStallRetry: {
    text: 'Retry',
    description: 'Button label on the fullscreen loader stall card to reload the page.',
    surface: 'common',
    maxChars: 16,
  },
  NLSCommonSuccess: {
    text: 'Done',
    description: 'Generic success toast confirmation message.',
    surface: 'common',
    maxChars: 24,
  },
  NLSCommonStatusSaving: {
    text: 'Saving...',
    description: 'Generic saving status.',
    surface: 'common',
    maxChars: 24,
  },
  NLSCommonLanguageAriaLabel: {
    text: 'App language',
    description: 'Accessible label for the authenticated app language selector.',
    surface: 'common',
    maxChars: 34,
  },
  NLSCommonLanguageLabel: {
    text: 'Language',
    description: 'Visible label for app language controls.',
    surface: 'common',
    maxChars: 22,
  },
  NLSCommonAccessDeniedDefaultMessage: {
    text: 'Your account role cannot access this area.',
    description: 'Default access-denied message when a route is blocked by role.',
    surface: 'common',
    maxChars: 90,
  },
  NLSCommonAccessDeniedTitle: {
    text: 'Access denied',
    description: 'Heading for shared access-denied routes.',
    surface: 'common',
    maxChars: 32,
  },
  NLSCommonConnectivityDescription: {
    text: 'The last request failed because this device appears to be offline. Reconnect, then retry the action you were taking. Your current workspace remains open underneath.',
    description: 'Network failure overlay body copy.',
    surface: 'common',
    maxChars: 222,
  },
  NLSCommonMaintenanceBannerDismiss: {
    text: 'Dismiss maintenance notice',
    description: 'Accessible label for the button that hides the maintenance banner for this session.',
    surface: 'common',
    maxChars: 40,
  },
  NLSCommonConnectivityEyebrow: {
    text: 'Connection interrupted',
    description: 'Small eyebrow label for the network failure overlay.',
    surface: 'common',
    maxChars: 42,
  },
  NLSCommonConnectivityRequestPaused: {
    text: 'Request paused',
    description: 'Status label shown beside request metadata after an offline failure.',
    surface: 'common',
    maxChars: 28,
  },
  NLSCommonConnectivityTitle: {
    text: 'Timelining could not reach the network.',
    description: 'Network failure overlay title.',
    surface: 'common',
    maxChars: 72,
  },
  NLSCommonPreviewAttachmentUnavailable: {
    text: 'Attachment not available.',
    description: 'Fallback shown when an attachment cannot be resolved.',
    surface: 'common',
    maxChars: 60,
  },
  NLSCommonPreviewFormatUnavailable: {
    text: 'Preview is not available for this format here. You can still open or download the original file.',
    description: 'Fallback body for formats that cannot render inside the preview dialog.',
    surface: 'common',
    maxChars: 150,
  },
  NLSCommonPreviewKindAsset: {
    text: 'Asset',
    description: 'Attachment preview kind label for design or creative assets.',
    surface: 'common',
    maxChars: 22,
  },
  NLSCommonPreviewKindAudio: {
    text: 'Audio',
    description: 'Attachment preview kind label for audio files.',
    surface: 'common',
    maxChars: 22,
  },
  NLSCommonPreviewKindDocument: {
    text: 'Document',
    description: 'Attachment preview kind label for document files.',
    surface: 'common',
    maxChars: 24,
  },
  NLSCommonPreviewKindFile: {
    text: 'File',
    description: 'Attachment preview kind label for generic files.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonPreviewKindImage: {
    text: 'Image',
    description: 'Attachment preview kind label for images.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonPreviewKindLink: {
    text: 'Link',
    description: 'Attachment preview kind label for links.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonPreviewKindSpreadsheet: {
    text: 'Spreadsheet',
    description: 'Attachment preview kind label for spreadsheets.',
    surface: 'common',
    maxChars: 28,
  },
  NLSCommonPreviewKindTimelineItem: {
    text: 'Timeline item',
    description: 'Attachment preview kind label for a timeline item reference.',
    surface: 'common',
    maxChars: 30,
  },
  NLSCommonPreviewKindVideo: {
    text: 'Video',
    description: 'Attachment preview kind label for video files.',
    surface: 'common',
    maxChars: 18,
  },
  NLSCommonPreviewPreparingText: {
    text: 'Preparing text preview...',
    description: 'Temporary body while a text attachment preview loads.',
    surface: 'common',
    maxChars: 56,
  },
  NLSCommonPreviewTextBlocked: {
    text: 'Text preview is blocked for this file. Open or download the original instead.',
    description: 'Fallback body when text preview fetch is blocked by browser or storage policy.',
    surface: 'common',
    maxChars: 120,
  },
  NLSCommonPreviewTextTruncated: {
    text: 'Preview truncated',
    description: 'Marker appended when text preview content is shortened.',
    surface: 'common',
    maxChars: 34,
  },
  NLSCommonTabErrorBody: {
    text: 'The rest of the app is fine. Use the tabs at the bottom to move around, or try reloading this tab.',
    description: 'Scoped tab crash explanation used by the shared tab error boundary.',
    surface: 'common',
    maxChars: 150,
  },
  NLSCommonTabErrorTitle: {
    text: 'This tab hit a snag',
    description: 'Heading for the shared tab error boundary fallback.',
    surface: 'common',
    maxChars: 46,
  },
  NLSSettingsTitleSettings: { text: 'Settings', description: 'Settings hub title.', surface: 'settings', maxChars: 24 },
  NLSSettingsSubtitlePersonalPreferences: { text: 'Personal preferences', description: 'Settings hub subtitle.', surface: 'settings', maxChars: 40 },
  NLSSettingsAriaCommandHub: { text: 'Command & Experience Hub', description: 'Accessible label for settings shell.', surface: 'settings', maxChars: 50 },
  NLSSettingsAriaSettingsCategories: { text: 'Settings categories', description: 'Accessible label for settings navigation.', surface: 'settings', maxChars: 42 },
  NLSSettingsGroupSystem: { text: 'System Settings', description: 'Legacy settings navigation group label.', surface: 'settings', maxChars: 36 },
  NLSSettingsGroupEngagement: { text: 'Engagement & Education', description: 'Legacy settings navigation group label.', surface: 'settings', maxChars: 46 },
  NLSSettingsGroupAppearance: { text: 'Appearance & Engine', description: 'Legacy settings navigation group label.', surface: 'settings', maxChars: 42 },
  NLSSettingsGroupAccount: { text: 'Account', description: 'Settings navigation group label.', surface: 'settings', maxChars: 24 },
  NLSSettingsGroupPreferences: { text: 'Preferences', description: 'Settings navigation group label.', surface: 'settings', maxChars: 24 },
  NLSSettingsGroupHelp: { text: 'Help', description: 'Settings navigation group label.', surface: 'settings', maxChars: 24 },
  NLSSettingsGroupExperience: { text: 'Experience', description: 'Legacy settings navigation group label.', surface: 'settings', maxChars: 28 },
  NLSSettingsGroupGuidance: { text: 'Guidance', description: 'Legacy settings navigation group label.', surface: 'settings', maxChars: 26 },
  NLSSettingsGroupSupport: { text: 'Support', description: 'Legacy settings navigation group label.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionProfile: { text: 'Profile', description: 'Settings section title.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionProfileDetail: { text: 'Identity, avatar, locale', description: 'Settings section detail.', surface: 'settings', maxChars: 48 },
  NLSSettingsSectionProfileSubtitle: { text: 'Identity, avatar, display mode, and language.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 74 },
  NLSSettingsSectionGeneral: { text: 'Account & data', description: 'Settings section title.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionGeneralDetail: { text: 'Account, data export & data lifecycle', description: 'Settings section detail.', surface: 'settings', maxChars: 62 },
  NLSSettingsSectionGeneralSubtitle: {
    text: 'Account details, data export, and end-of-life data controls.',
    description: 'Settings panel subtitle.',
    surface: 'settings',
    maxChars: 110,
  },
  NLSSettingsSectionSecurity: { text: 'Security & devices', description: 'Settings section title.', surface: 'settings', maxChars: 30 },
  NLSSettingsSectionSecurityDetail: { text: 'Watchdog session lease & phone apps', description: 'Settings section detail.', surface: 'settings', maxChars: 50 },
  NLSSettingsSectionSecuritySubtitle: { text: 'Watchdog lease timing, session cache handling, phone apps, and updates.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 90 },
  NLSSettingsSectionDevices: { text: 'Devices', description: 'Settings section title.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionDevicesDetail: { text: 'Install the phone app & manage updates', description: 'Settings section detail.', surface: 'settings', maxChars: 62 },
  NLSSettingsSectionDevicesSubtitle: { text: 'Install the Timelining phone app and check for updates.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 82 },
  NLSAdminDashboardTitleBoardSettings: { text: 'Board settings', description: 'Title for Board settings identity.', surface: 'settings', maxChars: 30 },
  NLSAdminDashboardAdminAndControls: { text: 'Admin & controls', description: 'Subtitle for Board settings identity.', surface: 'settings', maxChars: 30 },
  NLSAdminDashboardAriaBoardSettingsCategories: { text: 'Board settings categories', description: 'Aria label for Board settings navigation.', surface: 'settings', maxChars: 40 },
  NLSAdminDashboardAriaBoardSettings: { text: 'Board settings', description: 'Aria label for Board settings shell.', surface: 'settings', maxChars: 30 },
  NLSSettingsSectionAccount: { text: 'Account', description: 'Title for Account section in Account & data settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionExportData: { text: 'Export your data', description: 'Title for Export your data section in Account & data settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsSectionDangerZone: { text: 'Danger zone', description: 'Title for Danger zone section in settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionIdentity: { text: 'Identity', description: 'Title for Identity section in Profile settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionLanguage: { text: 'Language', description: 'Title for Language section in Profile settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsHintLanguage: { text: "Changes the app's language for your account.", description: 'Hint for display language setting.', surface: 'settings', maxChars: 60 },
  NLSSettingsLabelDisplayLanguage: { text: 'Display language', description: 'Label for display language select in Profile settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionSession: { text: 'Session', description: 'Title for Session section in Security settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsLabelWatchdogLease: { text: 'Watchdog lease timeout', description: 'Label for watchdog lease slider row.', surface: 'settings', maxChars: 36 },
  NLSSettingsHintWatchdogLease: { text: 'Interval before idle background sessions release locks.', description: 'Hint for watchdog lease timeout.', surface: 'settings', maxChars: 80 },
  NLSSettingsSectionPhoneApps: { text: 'Phone apps', description: 'Title for Phone apps section in Devices settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionAppUpdates: { text: 'App updates', description: 'Title for App updates section in Devices settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionPlanAndBalance: { text: 'Plan & balance', description: 'Title for Plan & balance section in Billing settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsSectionAiSpendLast30Days: { text: 'AI spend, last 30 days', description: 'Title for AI spend table section in Billing settings.', surface: 'settings', maxChars: 40 },
  NLSSettingsSectionTransactions: { text: 'Transactions', description: 'Title for Transactions section in Billing settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsLabelWorkspaceCurrency: { text: 'Workspace currency', description: 'Label for Workspace currency row in Billing settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintWorkspaceCurrencyLocked: { text: 'Locked when this workspace was created', description: 'Hint for locked workspace currency.', surface: 'settings', maxChars: 50 },
  NLSSettingsChipLocked: { text: 'Locked', description: 'Badge text for locked settings item.', surface: 'settings', maxChars: 16 },
  NLSSettingsActionDownload: { text: 'Download', description: 'Action button text for downloading mobile app.', surface: 'settings', maxChars: 20 },
  NLSSettingsActionCopyLink: { text: 'Copy link', description: 'Action button text for copying app link.', surface: 'settings', maxChars: 20 },
  NLSSettingsActionShare: { text: 'Share', description: 'Action button text for sharing app link.', surface: 'settings', maxChars: 20 },
  NLSSettingsToastLinkCopied: { text: 'App link copied to clipboard.', description: 'Toast when app link is copied.', surface: 'settings', maxChars: 40 },
  NLSSettingsToastShareFailed: { text: 'Could not share link.', description: 'Toast when share fails.', surface: 'settings', maxChars: 30 },
  NLSSettingsLabelLatestVersion: { text: 'Latest version', description: 'Label for app version row.', surface: 'settings', maxChars: 24 },
  NLSSettingsLabelNoUpdatesAvailable: { text: 'App is up to date', description: 'Status indicating no app updates available.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionCheckUpdates: { text: 'Check for updates', description: 'Action button to check for app updates.', surface: 'settings', maxChars: 30 },
  NLSSettingsLabelPersonalWallet: { text: 'Personal wallet', description: 'Label for personal wallet row in Billing settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsScopePersonal: { text: 'Personal', description: 'Segment label for personal scope.', surface: 'settings', maxChars: 16 },
  NLSSettingsScopeWorkspace: { text: 'Workspace', description: 'Segment label for workspace scope.', surface: 'settings', maxChars: 16 },
  NLSSettingsAriaTransactionScope: { text: 'Transaction scope', description: 'Aria label for transaction scope tabs in Billing settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsColumnTier: { text: 'Tier', description: 'Column header for AI spend tier.', surface: 'settings', maxChars: 20 },
  NLSSettingsColumnCredits: { text: 'Credits', description: 'Column header for AI spend credits.', surface: 'settings', maxChars: 20 },
  NLSSettingsColumnCost: { text: 'Cost', description: 'Column header for AI spend cost.', surface: 'settings', maxChars: 20 },
  NLSSettingsSectionSupport: { text: 'Support', description: 'Settings section title.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionSupportDetail: { text: 'Tickets, diagnostics, resolution', description: 'Settings section detail.', surface: 'settings', maxChars: 62 },
  NLSSettingsSectionSupportSubtitle: {
    text: 'Write support tickets, optionally attach diagnostics, and track owner resolution.',
    description: 'Settings panel subtitle.',
    surface: 'settings',
    maxChars: 110,
  },
  NLSSettingsSectionDebug: { text: 'Support', description: 'Legacy alias for the renamed Support settings section.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionDebugDetail: { text: 'Tickets, diagnostics, resolution', description: 'Legacy alias for the renamed Support settings section detail.', surface: 'settings', maxChars: 62 },
  NLSSettingsSectionDebugSubtitle: {
    text: 'Write support tickets, optionally attach diagnostics, and track owner resolution.',
    description: 'Legacy alias for the renamed Support settings panel subtitle.',
    surface: 'settings',
    maxChars: 110,
  },
  NLSSettingsSectionNotifications: { text: 'Notifications', description: 'Settings section title.', surface: 'settings', maxChars: 32 },
  NLSSettingsSectionNotificationsDetail: { text: 'Push, Guardian voice, achievements', description: 'Settings section detail.', surface: 'settings', maxChars: 58 },
  NLSSettingsSectionNotificationsSubtitle: { text: 'Choose what notifies you and how.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 60 },
  NLSSettingsSectionAiMagic: { text: 'AI Magic', description: 'Settings section title.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionAiMagicDetail: { text: 'Line, quick answers, draft alerts', description: 'Settings section detail.', surface: 'settings', maxChars: 58 },
  NLSSettingsSectionAiMagicSubtitle: { text: 'Line behavior, quick answers, and draft notifications.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 82 },
  NLSSettingsSectionBilling: { text: 'Billing', description: 'Settings section title.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionBillingDetail: { text: 'Credits, tier, balances', description: 'Settings section detail.', surface: 'settings', maxChars: 46 },
  NLSSettingsSectionBillingSubtitle: { text: 'Credits ledger, profile balance, and your active tier.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 80 },
  NLSSettingsSectionAppGuides: { text: 'App Guides', description: 'Settings section title.', surface: 'settings', maxChars: 28 },
  NLSSettingsSectionAppGuidesDetail: { text: 'Cinematic walkthroughs of core flows', description: 'Settings section detail.', surface: 'settings', maxChars: 62 },
  NLSSettingsSectionAppGuidesSubtitle: { text: 'Cinematic walkthroughs you can replay anytime.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 74 },
  NLSSettingsSectionInteractiveTours: { text: 'Interactive Tours', description: 'Settings section title.', surface: 'settings', maxChars: 36 },
  NLSSettingsSectionInteractiveToursDetail: { text: 'Guided spotlight walkthroughs', description: 'Settings section detail.', surface: 'settings', maxChars: 54 },
  NLSSettingsSectionInteractiveToursSubtitle: { text: 'Guided in-app journeys that move with you.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 74 },
  NLSSettingsSectionActionsCatalog: { text: 'Actions Catalog', description: 'Settings section title.', surface: 'settings', maxChars: 32 },
  NLSSettingsSectionActionsCatalogDetail: { text: 'Directory of system actions and how-tos', description: 'Settings section detail.', surface: 'settings', maxChars: 64 },
  NLSSettingsSectionActionsCatalogSubtitle: {
    text: 'Browse system actions, run eligible commands, or launch interactive how-tos.',
    description: 'Settings panel subtitle.',
    surface: 'settings',
    maxChars: 88,
  },
  NLSSettingsSectionFeedbackCenter: { text: 'Feedback Center', description: 'Settings section title.', surface: 'settings', maxChars: 36 },
  NLSSettingsSectionFeedbackCenterDetail: { text: 'Unanswered & skipped feedback', description: 'Settings section detail.', surface: 'settings', maxChars: 54 },
  NLSSettingsSectionFeedbackCenterSubtitle: { text: 'Inline forms for prompts you haven’t answered yet.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 78 },
  NLSSettingsSectionAppearance: { text: 'Appearance', description: 'Settings section title.', surface: 'settings', maxChars: 28 },
  NLSSettingsSectionAppearanceDetail: { text: 'Themes, soundscapes, animation profile', description: 'Settings section detail.', surface: 'settings', maxChars: 64 },
  NLSSettingsSectionAppearanceTitle: { text: 'Appearance & Engine', description: 'Settings appearance panel title.', surface: 'settings', maxChars: 42 },
  NLSSettingsSectionAppearanceSubtitle: { text: 'Themes, soundscapes, and the celebration engine.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 76 },
  NLSThemeMosaicLightLabel: { text: 'Mosaic Light', description: 'Mosaic Light theme name.', surface: 'settings', maxChars: 24 },
  NLSThemeMosaicLightDescription: { text: 'Calm daylight palette with soft grey surfaces and clean contrast.', description: 'Mosaic Light theme description.', surface: 'settings', maxChars: 100 },
  NLSThemeMosaicDarkLabel: { text: 'Mosaic Dark', description: 'Mosaic Dark theme name.', surface: 'settings', maxChars: 24 },
  NLSThemeMosaicDarkDescription: { text: 'Deep navy night palette with crisp contrast and solid cards.', description: 'Mosaic Dark theme description.', surface: 'settings', maxChars: 100 },
  NLSMosaicMovedToast: {
    text: 'Timelining has a new look. You can switch themes in Settings → Appearance.',
    description: 'Notification toast informing users that Timelining has switched to the new Mosaic look and themes can be customized in Settings.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSSettingsSectionDesignEditor: { text: 'Design Editor', description: 'Settings section title.', surface: 'settings', maxChars: 32 },
  NLSSettingsSectionDesignEditorDetail: { text: 'Canvas defaults, autosave, export', description: 'Settings section detail.', surface: 'settings', maxChars: 58 },
  NLSSettingsSectionDesignEditorSubtitle: { text: 'Canvas defaults, autosave cadence, and export presets.', description: 'Settings panel subtitle.', surface: 'settings', maxChars: 84 },
  NLSSettingsPublicMaskingTitle: { text: 'Mask Viewer PII', description: 'Title for public workspace share viewer PII masking toggle.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicMaskingSubtitle: {
    text: 'Hide member names, emails, avatars, external connected account emails, and external resource titles from public viewers.',
    description: 'Subtitle explaining what viewer PII masking does.',
    surface: 'settings',
    maxChars: 130,
  },
  NLSSettingsPublicMaskingEnabledBadge: { text: 'PII Masking Active', description: 'Badge showing that viewer PII masking is enabled.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicMaskingPreviewHeading: { text: 'Viewer PII Masking Preview', description: 'Heading for the preview box showing what a public viewer sees.', surface: 'settings', maxChars: 40 },
  NLSSettingsPublicMaskingPreviewExplainer: {
    text: 'Public viewers see rotating deterministic aliases. Freeform task text and timestamps remain authentic.',
    description: 'Note explaining preview behavior.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSSettingsPublicMaskingRealDataLabel: { text: 'Real Workspace Data', description: 'Label for original unmasked identity.', surface: 'settings', maxChars: 28 },
  NLSSettingsPublicMaskingMaskedDataLabel: { text: 'What Public Viewers See', description: 'Label for masked simulated identity.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicMaskingMembersSee: { text: 'Members see', description: 'Left column header showing unmasked member data.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicMaskingPublicSee: { text: 'Public viewers see', description: 'Right column header showing masked member data.', surface: 'settings', maxChars: 28 },
  NLSSettingsPublicMaskingConnectedAccount: { text: 'Connected Account', description: 'Label for connected account in masking preview.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicMaskingExternalResource: { text: 'External Resource', description: 'Label for external resource name in masking preview.', surface: 'settings', maxChars: 24 },
  NLSSettingsStatusSignInNotifications: { text: 'Sign in to manage notifications.', description: 'Settings signed-out notification empty state.', surface: 'settings', maxChars: 64 },

  NLSSettingsLabelAccount: { text: 'Account', description: 'Settings account label.', surface: 'settings', maxChars: 22 },
  NLSSettingsLabelUserId: { text: 'User ID', description: 'Settings user identifier label.', surface: 'settings', maxChars: 20 },
  NLSSettingsStatusNotSignedIn: { text: 'Not signed in', description: 'Signed-out fallback label.', surface: 'settings', maxChars: 32 },
  NLSSettingsHintUserId: { text: 'Used to key personal data and ledger entries.', description: 'Settings user ID hint.', surface: 'settings', maxChars: 70 },
  NLSSettingsLabelDeleteData: { text: 'Delete data', description: 'Settings data deletion heading.', surface: 'settings', maxChars: 28 },
  NLSSettingsBodyDeleteData: {
    text: 'These actions open a confirmation modal before any timeline, team, or account data is deleted.',
    description: 'Settings data deletion body.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSSettingsActionDeletePersonalData: { text: 'Delete All Personal Data', description: 'Delete personal data action.', surface: 'settings', maxChars: 46 },
  NLSSettingsActionDeleteTeamData: { text: 'Delete All Team Data', description: 'Delete team data action.', surface: 'settings', maxChars: 40 },
  NLSSettingsActionDeleteAllData: { text: 'Delete All Data', description: 'Delete all data action.', surface: 'settings', maxChars: 34 },
  NLSDataExportPersonalTitle: { text: 'Download my data', description: 'Personal data export section title.', surface: 'settings', maxChars: 40 },
  NLSDataExportPersonalDesc: { text: 'Export a copy of your personal data, profile, and history as a zip archive containing JSON and CSV files.', description: 'Personal data export description.', surface: 'settings', maxChars: 150 },
  NLSDataExportWorkspaceTitle: { text: 'Download workspace data', description: 'Workspace data export section title.', surface: 'settings', maxChars: 40 },
  NLSDataExportWorkspaceDesc: { text: 'Export a full copy of this workspace\'s elements, checklists, expenses, and settings as a zip archive.', description: 'Workspace data export description.', surface: 'settings', maxChars: 150 },
  NLSDataExportButton: { text: 'Request export', description: 'Data export request button.', surface: 'settings', maxChars: 30 },
  NLSDataExportStatusQueued: { text: 'Export queued', description: 'Status when export is queued.', surface: 'settings', maxChars: 30 },
  NLSDataExportStatusRunning: { text: 'Building export archive...', description: 'Status when export is building.', surface: 'settings', maxChars: 40 },
  NLSDataExportStatusReady: { text: 'Export ready', description: 'Status when export is ready.', surface: 'settings', maxChars: 30 },
  NLSDataExportStatusFailed: { text: 'Export generation failed', description: 'Status when export failed.', surface: 'settings', maxChars: 40 },
  NLSDataExportStatusExpired: { text: 'Export archive expired', description: 'Status when export expired.', surface: 'settings', maxChars: 40 },
  NLSDataExportDownloadButton: { text: 'Download archive', description: 'Button to download the export archive.', surface: 'settings', maxChars: 30 },
  NLSDataExportExpiresOn: { text: 'Expires {date}', description: 'Export expiry notice.', surface: 'settings', maxChars: 40 },
  NLSDataExportRateLimited: { text: 'You can request another export after {date}.', description: 'Notice when export is rate limited.', surface: 'settings', maxChars: 80 },
  NLSDataExportRequesting: { text: 'Requesting export...', description: 'Loading state while requesting export.', surface: 'settings', maxChars: 40 },
  NLSDataExportSubscriptionError: { text: 'Unable to load export job status.', description: 'Error message when export job subscription fails.', surface: 'settings', maxChars: 60 },
  NLSOwnerConsoleExportTitle: { text: 'Data Export', description: 'Owner Console data export card title.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleExportDesc: { text: 'Request an official DPDP/GDPR export bundle for this person.', description: 'Owner Console data export card description.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleExportButton: { text: 'Export this person\'s data', description: 'Owner Console export button.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleExportStatus: { text: 'Export status: {status}', description: 'Owner Console export status label.', surface: 'owner-console', maxChars: 40 },
  NLSSettingsLabelWatchdogLeaseTimeout: { text: 'Watchdog Lease Timeout', description: 'Security setting label.', surface: 'settings', maxChars: 44 },
  NLSSettingsAriaWatchdogLeaseTimeout: { text: 'Watchdog lease timeout', description: 'Security setting accessible label.', surface: 'settings', maxChars: 44 },
  NLSSettingsHintWatchdogLeaseTimeout: { text: 'Minutes before the lockout countdown begins.', description: 'Security setting hint.', surface: 'settings', maxChars: 70 },
  NLSSettingsLabelCacheHandling: { text: 'Cache Handling', description: 'Security cache heading.', surface: 'settings', maxChars: 34 },
  NLSSettingsBodyCacheHandling: {
    text: 'Workspace cache is cleared automatically during dormant-ledger recovery and account logout paths.',
    description: 'Security cache body.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSSettingsLabelDiagnosticDebugMode: { text: 'Diagnostic Debug Mode', description: 'Debug mode setting label.', surface: 'settings', maxChars: 46 },
  NLSSettingsBodyDiagnosticDebugMode: {
    text: 'Records routes, clicks, non-printable keypresses, network calls, console errors, form activity, visible headings, and browser errors locally while enabled. Typed text values are never logged.',
    description: 'Debug mode setting body.',
    surface: 'settings',
    maxChars: 220,
  },
  NLSSettingsEventsCount: { text: '{count} events', description: 'Debug session event count. {count} is formatted.', surface: 'settings', maxChars: 34 },
  NLSSettingsStartedAt: { text: 'Started {time}', description: 'Debug session start time. {time} is formatted.', surface: 'settings', maxChars: 60 },
  NLSSettingsStoppedAt: { text: 'Stopped {time}', description: 'Debug session stop time. {time} is formatted.', surface: 'settings', maxChars: 60 },
  NLSSettingsStatusNoSessionYet: { text: 'No session yet', description: 'Debug session missing status.', surface: 'settings', maxChars: 32 },
  NLSSettingsActionCopiedToClipboard: { text: 'Copied to clipboard', description: 'Debug copy button success label.', surface: 'settings', maxChars: 38 },
  NLSSettingsActionCopyIssueBundle: { text: 'Copy Issue Bundle', description: 'Debug copy action.', surface: 'settings', maxChars: 38 },
  NLSSettingsActionDownloadJson: { text: 'Download JSON', description: 'Debug download action.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionClearLog: { text: 'Clear Log', description: 'Debug clear action.', surface: 'settings', maxChars: 24 },
  NLSSettingsLabelSuggestedAnswers: { text: 'Suggested answers', description: 'AI Magic quick-answer group label.', surface: 'settings', maxChars: 32 },
  NLSSettingsHintSuggestedAnswers: {
    text: 'Line offers quick answers you can Enter to submit. Autopick and inline apply stay off in this build.',
    description: 'AI Magic quick-answer hint.',
    surface: 'settings',
    maxChars: 130,
  },
  NLSSettingsLabelLineLearningSignals: { text: 'Help improve Line planning', description: 'Opt-in label for anonymous, local Line learning signal capture.', surface: 'settings', maxChars: 48 },
  NLSSettingsHintLineLearningSignals: {
    text: 'Keep anonymous, coarsened planning outcomes on this device for Line learning. You can turn this off at any time.',
    description: 'Privacy explanation for Line learning signal capture.',
    surface: 'settings',
    maxChars: 130,
  },
  NLSSettingsBadgeSelectSubmitLocked: { text: 'Select-to-submit locked', description: 'AI Magic hint badge indicating manual submit only.', surface: 'settings', maxChars: 38 },
  NLSSettingsAiRuleDeleteTitle: { text: 'Delete rule?', description: 'Confirmation modal title when deleting an AI user rule.', surface: 'settings', maxChars: 32 },
  NLSSettingsLabelDraftReadyNotifications: { text: 'Draft-ready notifications', description: 'AI Magic notification toggle label.', surface: 'settings', maxChars: 46 },
  NLSSettingsHintDraftReadyNotifications: {
    text: 'Alert when a background AI generation drafts something ready to review.',
    description: 'AI Magic draft-ready hint.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSSettingsLabelManageHandoff: { text: 'Manage handoff', description: 'AI Magic manage-drafts handoff label.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintManageHandoff: {
    text: 'Choose whether Manage drafts open automatically once ready or wait for you.',
    description: 'AI Magic manage-drafts hint.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSSettingsActionManual: { text: 'Manual', description: 'AI Magic manage-drafts option.', surface: 'settings', maxChars: 18 },
  NLSSettingsActionAutoOpen: { text: 'Auto-open', description: 'AI Magic manage-drafts option.', surface: 'settings', maxChars: 22 },
  NLSSettingsLabelTestingMode: { text: 'Testing mode', description: 'AI Magic testing status label.', surface: 'settings', maxChars: 26 },
  NLSSettingsStatusHistoryLimitsBypassed: { text: 'History limits bypassed', description: 'AI Magic testing status value.', surface: 'settings', maxChars: 40 },
  NLSSettingsHintHistoryLimitsBypassed: { text: 'Beta workspaces keep every AI Magic conversation without pruning.', description: 'AI Magic testing status hint.', surface: 'settings', maxChars: 100 },
  NLSSettingsLabelLineSuggestions: { text: 'Line suggestions', description: 'Line suggestions section title in AI Magic settings.', surface: 'settings', maxChars: 32 },
  NLSSettingsHintLineSuggestions: {
    text: 'Ambient suggestions Line has muted after repeated dismissals. Unmute a category to see it again.',
    description: 'Line suggestions section subtitle explaining muted ambient suggestions.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSSettingsLineSuggestionsEmpty: {
    text: 'No suggestion categories are muted right now.',
    description: 'Empty state message when no ambient suggestions are muted.',
    surface: 'settings',
    maxChars: 60,
  },
  NLSSettingsLineSuggestionsMutedUntil: {
    text: 'Muted until {date}',
    description: 'Timestamp indicating until when an ambient suggestion category is muted.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSSettingsLineSuggestionsReasonDismissals: {
    text: 'auto-muted after 3 dismissals',
    description: 'Reason text for ambient suggestions auto-muted after repeated dismissals.',
    surface: 'settings',
    maxChars: 45,
  },
  NLSSettingsLineSuggestionsReasonSettings: {
    text: 'muted from Settings',
    description: 'Reason text for ambient suggestions muted manually from Settings.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSSettingsActionUnmute: {
    text: 'Unmute',
    description: 'Action button label to unmute a muted ambient suggestion category.',
    surface: 'settings',
    maxChars: 16,
  },
  NLSSettingsLabelPersonalWalletBalance: { text: 'Personal Wallet Balance', description: 'Billing metric label.', surface: 'settings', maxChars: 46 },
  NLSSettingsHintSpendablePersonalCredits: { text: 'Spendable personal credits', description: 'Billing metric hint.', surface: 'settings', maxChars: 46 },
  NLSSettingsLabelActiveTier: { text: 'Active Tier', description: 'Billing metric label.', surface: 'settings', maxChars: 26 },
  NLSSettingsHintCurrentAccountAccessLevel: { text: 'Current account access level', description: 'Billing metric hint.', surface: 'settings', maxChars: 50 },
  NLSSettingsLabelAiSpendByTier: { text: 'AI spend by tier (last {days} days)', description: 'AI spend panel label. {days} is a number.', surface: 'settings', maxChars: 60 },
  NLSSettingsEmptyNoLineProMax: {
    text: "You haven't used Line Pro or Line Max yet - pick a tier in the AI Magic composer to try them.",
    description: 'AI spend panel empty state.',
    surface: 'settings',
    maxChars: 130,
  },
  NLSSettingsLabelTotal: { text: 'Total', description: 'Billing total label.', surface: 'settings', maxChars: 18 },
  NLSSettingsCreditPersonalWallet: { text: 'Personal Wallet', description: 'Credit balance default personal label.', surface: 'settings', maxChars: 36 },
  NLSSettingsCreditWorkspaceBalance: { text: 'Workspace Balance', description: 'Credit balance default workspace label.', surface: 'settings', maxChars: 40 },
  NLSSettingsCreditLedger: { text: 'Ledger', description: 'Credit transactions eyebrow.', surface: 'settings', maxChars: 20 },
  NLSSettingsCreditTransactions: { text: 'Credit transactions', description: 'Credit transactions heading.', surface: 'settings', maxChars: 40 },
  NLSSettingsAriaTransactionFilters: { text: 'Transaction filters', description: 'Credit transaction filter tablist label.', surface: 'settings', maxChars: 40 },
  NLSSettingsAriaCreditTransactions: { text: 'Credit transactions', description: 'Credit transaction list label.', surface: 'settings', maxChars: 40 },
  NLSSettingsCreditFilterAll: { text: 'All', description: 'Credit transaction filter.', surface: 'settings', maxChars: 14 },
  NLSSettingsCreditFilterEarned: { text: 'Earned', description: 'Credit transaction filter.', surface: 'settings', maxChars: 20 },
  NLSSettingsCreditFilterSpent: { text: 'Spent', description: 'Credit transaction filter.', surface: 'settings', maxChars: 18 },
  NLSSettingsCreditFilterPurchases: { text: 'Purchases', description: 'Credit transaction filter.', surface: 'settings', maxChars: 26 },
  NLSSettingsCreditFilterTransfers: { text: 'Transfers', description: 'Credit transaction filter.', surface: 'settings', maxChars: 26 },
  NLSSettingsCreditFilterReservations: { text: 'Reservations', description: 'Credit transaction filter.', surface: 'settings', maxChars: 30 },
  NLSSettingsCreditTxnSignupBonus: { text: 'Signup bonus', description: 'Credit transaction type label.', surface: 'settings', maxChars: 32 },
  NLSSettingsCreditTxnReward: { text: 'Reward', description: 'Credit transaction type label.', surface: 'settings', maxChars: 20 },
  NLSSettingsCreditTxnPlanGrant: { text: 'Plan grant', description: 'Credit transaction type label.', surface: 'settings', maxChars: 28 },
  NLSSettingsCreditTxnPurchase: { text: 'Purchase', description: 'Credit transaction type label.', surface: 'settings', maxChars: 24 },
  NLSSettingsCreditTxnTransferIn: { text: 'Transfer in', description: 'Credit transaction type label.', surface: 'settings', maxChars: 28 },
  NLSSettingsCreditTxnTransferOut: { text: 'Transfer out', description: 'Credit transaction type label.', surface: 'settings', maxChars: 30 },
  NLSSettingsCreditTxnReserved: { text: 'Reserved', description: 'Credit transaction type label.', surface: 'settings', maxChars: 24 },
  NLSSettingsCreditTxnCommitted: { text: 'Committed', description: 'Credit transaction type label.', surface: 'settings', maxChars: 26 },
  NLSSettingsCreditTxnReleased: { text: 'Released', description: 'Credit transaction type label.', surface: 'settings', maxChars: 24 },
  NLSSettingsCreditTxnRefund: { text: 'Refund', description: 'Credit transaction type label.', surface: 'settings', maxChars: 20 },
  NLSSettingsCreditTxnAdjustment: { text: 'Adjustment', description: 'Credit transaction type label.', surface: 'settings', maxChars: 28 },
  NLSSettingsStatusCreditPending: { text: 'Pending', description: 'Credit transaction pending date fallback.', surface: 'settings', maxChars: 20 },
  NLSSettingsStatusTransactionsIndex: {
    text: 'Setting up transactions... this can take a minute after deployment. Check back shortly.',
    description: 'Credit transaction index setup error.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSSettingsStatusTransactionsPermission: { text: 'You don’t have permission to view this ledger.', description: 'Credit transaction permission error.', surface: 'settings', maxChars: 80 },
  NLSSettingsStatusTransactionsCouldNotLoad: {
    text: 'Could not load transactions. Please try again in a moment.',
    description: 'Credit transaction generic load error.',
    surface: 'settings',
    maxChars: 90,
  },
  NLSSettingsStatusNoTransactions: { text: 'No transactions yet.', description: 'Credit transaction empty state.', surface: 'settings', maxChars: 40 },
  NLSSettingsStatusNoFilteredTransactions: {
    text: 'No {filter} transactions in this window.',
    description: 'Credit transaction filtered empty state. {filter} is a local filter label.',
    surface: 'settings',
    maxChars: 70,
  },
  NLSSettingsCreditBalanceAfter: { text: 'balance {balance}', description: 'Credit transaction balance-after label. {balance} is formatted.', surface: 'settings', maxChars: 40 },
  NLSSettingsActionLoadMore: { text: 'Load more', description: 'Load more transactions action.', surface: 'settings', maxChars: 24 },
  NLSSettingsActionLoading: { text: 'Loading...', description: 'Generic loading action label.', surface: 'settings', maxChars: 24 },
  NLSSettingsActionSaving: { text: 'Saving', description: 'Generic saving action label.', surface: 'settings', maxChars: 18 },
  NLSSettingsPurchaseTitle: { text: 'Purchase', description: 'Purchase page title.', surface: 'settings', maxChars: 24 },
  NLSSettingsPurchaseHero: { text: 'Choose access, top up credits, and tune your workspace limits.', description: 'Purchase page hero heading.', surface: 'settings', maxChars: 90 },
  NLSSettingsAriaWalletBalance: { text: 'Wallet balance', description: 'Purchase wallet balance aria label.', surface: 'settings', maxChars: 32 },
  NLSSettingsAriaPurchaseCategories: { text: 'Purchase categories', description: 'Purchase tablist aria label.', surface: 'settings', maxChars: 40 },
  NLSSettingsAriaBillingCycle: { text: 'Billing cycle', description: 'Billing toggle aria label.', surface: 'settings', maxChars: 32 },
  NLSSettingsBillingMonthly: { text: 'Monthly', description: 'Monthly billing option.', surface: 'settings', maxChars: 20 },
  NLSSettingsBillingYearly: { text: 'Yearly', description: 'Yearly billing option.', surface: 'settings', maxChars: 20 },
  NLSSettingsBillingYearlyRibbon: { text: 'yearly', description: 'Yearly billing discount ribbon.', surface: 'settings', maxChars: 18 },
  NLSSettingsLabelCurrentPlan: { text: 'Current plan', description: 'Current plan marker.', surface: 'settings', maxChars: 26 },
  NLSSettingsActionCurrentPlan: { text: 'Current Plan', description: 'Current plan disabled CTA.', surface: 'settings', maxChars: 26 },
  NLSSettingsActionDisabledForTesting: { text: 'Disabled for testing', description: 'Purchase disabled CTA.', surface: 'settings', maxChars: 42 },
  NLSSettingsLabelNoMonthlyCredits: { text: 'No monthly credits', description: 'Plan credit badge with no credits.', surface: 'settings', maxChars: 36 },
  NLSSettingsCreditsPerPeriod: { text: 'Credits / {period}', description: 'Plan credit badge period label. {period} is local period text.', surface: 'settings', maxChars: 38 },
  NLSSettingsPeriodYear: { text: 'year', description: 'Year period label.', surface: 'settings', maxChars: 14 },
  NLSSettingsPeriodMonth: { text: 'month', description: 'Month period label.', surface: 'settings', maxChars: 14 },
  NLSSettingsPeriodYearShort: { text: 'yr', description: 'Short year period label.', surface: 'settings', maxChars: 8 },
  NLSSettingsPeriodMonthShort: { text: 'mo', description: 'Short month period label.', surface: 'settings', maxChars: 8 },
  NLSSettingsStorageByPlan: { text: 'Storage by plan', description: 'Plan storage fallback label.', surface: 'settings', maxChars: 34 },
  NLSSettingsStorageMegabytes: { text: '{amount} MB storage', description: 'Plan storage megabyte label. {amount} is formatted.', surface: 'settings', maxChars: 36 },
  NLSSettingsStorageGigabytes: { text: '{amount} GB storage', description: 'Plan storage gigabyte label. {amount} is formatted.', surface: 'settings', maxChars: 36 },
  NLSSettingsCreditOneTimeCredits: { text: 'One-time credits', description: 'Credit pack badge label.', surface: 'settings', maxChars: 34 },
  NLSSettingsActionSelectCreditPack: { text: 'Select credit pack', description: 'Credit pack purchase action.', surface: 'settings', maxChars: 38 },
  NLSSettingsPersonalPlansEyebrow: { text: 'Personal plans', description: 'Personal purchase tab eyebrow.', surface: 'settings', maxChars: 34 },
  NLSSettingsPersonalPlansTitle: { text: 'Choose personal access.', description: 'Personal purchase tab title.', surface: 'settings', maxChars: 46 },
  NLSSettingsPersonalPlansBody: {
    text: 'Personal packs cover private non-credit usage. Credit-heavy actions use included credits or credit packs.',
    description: 'Personal purchase tab body.',
    surface: 'settings',
    maxChars: 130,
  },
  NLSSettingsCreditPacksEyebrow: { text: 'Credit packs', description: 'Credit packs tab eyebrow.', surface: 'settings', maxChars: 30 },
  NLSSettingsCreditPacksTitle: { text: 'Top up credit-metered actions.', description: 'Credit packs tab title.', surface: 'settings', maxChars: 54 },
  NLSSettingsCreditPacksBody: {
    text: 'Credit packs keep AI-heavy generation, report work, and export costs separate from subscription pricing.',
    description: 'Credit packs tab body.',
    surface: 'settings',
    maxChars: 130,
  },
  NLSSettingsAriaPersonalPricingPlans: { text: 'Personal pricing plans', description: 'Personal pricing section aria label.', surface: 'settings', maxChars: 44 },
  NLSSettingsAriaCreditPacks: { text: 'Credit packs', description: 'Credit packs section aria label.', surface: 'settings', maxChars: 30 },
  NLSSettingsStorageEyebrow: { text: 'Storage', description: 'Storage add-ons eyebrow.', surface: 'settings', maxChars: 24 },
  NLSSettingsStorageTitle: { text: 'Add workspace storage.', description: 'Storage add-ons title.', surface: 'settings', maxChars: 42 },
  NLSSettingsStorageBody: {
    text: 'Extra storage applies to UploadHub, attachments, previews, form files, and design assets.',
    description: 'Storage add-ons body.',
    surface: 'settings',
    maxChars: 110,
  },
  NLSSettingsStorageExtraGb: { text: 'GB extra storage', description: 'Storage add-on badge label.', surface: 'settings', maxChars: 36 },
  NLSSettingsActionSelectStorage: { text: 'Select storage', description: 'Storage add-on purchase action.', surface: 'settings', maxChars: 32 },
  NLSSettingsAriaStorageAddOns: { text: 'Storage add-ons', description: 'Storage add-ons section aria label.', surface: 'settings', maxChars: 34 },
  NLSSettingsNotificationsSignedOut: { text: 'Sign in to manage notification delivery.', description: 'Notification settings signed-out message.', surface: 'settings', maxChars: 72 },
  NLSSettingsNotificationsEyebrow: { text: 'Delivery matrix', description: 'Notification settings eyebrow.', surface: 'settings', maxChars: 34 },
  NLSSettingsNotificationsTitle: { text: 'Notifications', description: 'Notification settings title.', surface: 'settings', maxChars: 32 },
  NLSSettingsNotificationsBody: {
    text: 'Control how each signal reaches you. Email preferences are stored now; delivery is coming soon.',
    description: 'Notification settings body.',
    surface: 'settings',
    maxChars: 130,
  },
  NLSSettingsNotificationEmailComingSoon: {
    text: 'Email delivery is coming soon; your choice is kept.',
    description: 'Notification settings tooltip for email coming soon.',
    surface: 'settings',
    maxChars: 60,
  },
  NLSSettingsNotificationsDigestTitle: { text: 'Digest schedule', description: 'Notification digest heading.', surface: 'settings', maxChars: 34 },
  NLSSettingsNotificationsDigestBody: { text: 'Bundle eligible low-priority signals at 09:00 and 18:00 local time.', description: 'Notification digest body.', surface: 'settings', maxChars: 100 },
  NLSSettingsNotificationsEnableDigests: { text: 'Enable notification digests', description: 'Notification digest toggle label.', surface: 'settings', maxChars: 52 },
  NLSSettingsNotificationsQuietModeTitle: { text: 'Quiet Mode', description: 'Notification quiet mode title.', surface: 'settings', maxChars: 28 },
  NLSSettingsNotificationsEnableQuietMode: { text: 'Enable Quiet Mode', description: 'Notification quiet mode toggle label.', surface: 'settings', maxChars: 40 },
  NLSSettingsNotificationsStarts: { text: 'Starts', description: 'Quiet mode start label.', surface: 'settings', maxChars: 18 },
  NLSSettingsNotificationsEnds: { text: 'Ends', description: 'Quiet mode end label.', surface: 'settings', maxChars: 18 },
  NLSSettingsNotificationsQuietModeHint: { text: 'Pushes wait until quiet hours end. The in-app list updates at once.', description: 'Quiet hours behavior hint.', surface: 'settings', maxChars: 100 },
  NLSSettingsNotificationsBundleRepeats: { text: 'Bundle repeat-item notices into one daily summary', description: 'Toggle label to bundle repeat notifications into a daily summary.', surface: 'settings', maxChars: 65 },
  NLSSettingsNotificationsBundleRepeatsHint: { text: 'Receive one daily summary of repeat-item notices instead of individual pushes.', description: 'Hint explaining repeat-item notification bundling.', surface: 'settings', maxChars: 100 },
  NLSSettingsDigestQuietHoursMoved: { text: 'Quiet hours: set in Notifications.', description: 'Note on digest panel directing users to notification quiet hours settings.', surface: 'settings', maxChars: 60 },
  NLSSettingsAriaQuietStartHour: { text: 'Quiet mode start hour', description: 'Quiet mode start select aria label.', surface: 'settings', maxChars: 46 },
  NLSSettingsAriaQuietEndHour: { text: 'Quiet mode end hour', description: 'Quiet mode end select aria label.', surface: 'settings', maxChars: 44 },
  NLSSettingsStatusNotificationPolicySaved: { text: 'Notification policy saved.', description: 'Notification save success message.', surface: 'settings', maxChars: 50 },
  NLSSettingsStatusNotificationPolicyCouldNotSave: { text: 'Could not save notification policy.', description: 'Notification save error message.', surface: 'settings', maxChars: 70 },
  NLSSettingsActionSavePolicy: { text: 'Save policy', description: 'Notification save action.', surface: 'settings', maxChars: 28 },
  // Settings Kit (R15 P1)
  NLSSettingsKitUnsavedChanges: { text: 'Unsaved changes', description: 'Label on sticky save bar when draft changes are pending.', surface: 'settings', maxChars: 30 },
  NLSSettingsKitDiscard: { text: 'Discard', description: 'Discard button on sticky save bar.', surface: 'settings', maxChars: 15 },
  NLSSettingsKitSave: { text: 'Save', description: 'Save button on sticky save bar.', surface: 'settings', maxChars: 15 },
  NLSSettingsKitSaving: { text: 'Saving…', description: 'Loading state for save button on sticky save bar.', surface: 'settings', maxChars: 15 },
  NLSSettingsKitSaved: { text: 'Saved', description: 'Success tick text shown after an instant setting change saves.', surface: 'settings', maxChars: 15 },
  NLSSettingsKitSaveFailed: { text: 'Save failed', description: 'Fallback error text when saving a setting fails.', surface: 'settings', maxChars: 25 },
  NLSSettingsKitComingSoon: { text: 'Coming soon', description: 'Badge or chip label on coming soon settings tabs and sections.', surface: 'settings', maxChars: 20 },
  NLSSettingsKitPreview: { text: 'Preview', description: 'Preview action label on locked choice grid tiles.', surface: 'settings', maxChars: 15 },
  NLSSettingsKitLocked: { text: 'Locked', description: 'Locked badge or chip label on choice grid tiles.', surface: 'settings', maxChars: 15 },
  NLSSettingsKitCopied: { text: 'Copied', description: 'Feedback label after copying a value from a read-only row.', surface: 'settings', maxChars: 15 },
  NLSSettingsKitCopyValue: { text: 'Copy value', description: 'Accessibility label on copy button in read-only rows.', surface: 'settings', maxChars: 25 },
  NLSSettingsSectionTheme: { text: 'Theme', description: 'Section title for Theme in Appearance settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionFocus: { text: 'Focus', description: 'Section title for Focus in Appearance settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionMotionAndFeedback: { text: 'Motion & feedback', description: 'Section title for Motion & feedback in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsSectionLayout: { text: 'Layout', description: 'Section title for Layout in Appearance settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsLabelCanvasTheme: { text: 'Canvas theme', description: 'Label for canvas theme choice in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintCanvasTheme: { text: 'Applies to the Console. Premium themes unlock through the Character vault.', description: 'Hint for canvas theme in Appearance settings.', surface: 'settings', maxChars: 90 },
  NLSSettingsLabelMemberAppTheme: { text: 'Member app theme', description: 'Label for member app theme choice in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintMemberAppTheme: { text: 'Controls the Member app from this central Appearance panel. Follow Console leaves it aligned with Canvas Theme.', description: 'Hint for member app theme in Appearance settings.', surface: 'settings', maxChars: 120 },
  NLSSettingsLabelCanvasBackground: { text: 'Canvas background', description: 'Label for canvas background choice in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintCanvasBackground: { text: 'Applies to the checklist drawing board and all dashboard views.', description: 'Hint for canvas background in Appearance settings.', surface: 'settings', maxChars: 90 },
  NLSSettingsLabelFocusSoundscape: { text: 'Focus soundscape', description: 'Label for focus soundscape choice in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintFocusSoundscape: { text: 'Chooses the visual atmosphere and audio for Focus Mode. Unlock by equipping the matching Avatar Guardian.', description: 'Hint for focus soundscape in Appearance settings.', surface: 'settings', maxChars: 120 },
  NLSSettingsLabelAnimationProfile: { text: 'Animation profile', description: 'Label for animation profile in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsLabelReducedMotion: { text: 'Reduced motion', description: 'Label for reduced motion toggle in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintReducedMotion: { text: 'Forces the lightest animation profile regardless of system preference.', description: 'Hint for reduced motion in Appearance settings.', surface: 'settings', maxChars: 90 },
  NLSSettingsLabelCelebration: { text: 'Enable celebration (confetti)', description: 'Label for celebration toggle in Appearance settings.', surface: 'settings', maxChars: 36 },
  NLSSettingsHintCelebration: { text: 'Bursts of confetti on level-ups, streaks, and completions.', description: 'Hint for celebration in Appearance settings.', surface: 'settings', maxChars: 90 },
  NLSSettingsLabelHighFidelity: { text: 'High fidelity effects', description: 'Label for high fidelity effects toggle in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintHighFidelity: { text: 'Allows richer ambient effects on mobile.', description: 'Hint for high fidelity in Appearance settings.', surface: 'settings', maxChars: 80 },
  NLSSettingsLabelSoundCues: { text: 'Sound cues', description: 'Label for sound cues toggle in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintSoundCues: { text: 'Local UI tones for scan and mutation outcomes.', description: 'Hint for sound cues in Appearance settings.', surface: 'settings', maxChars: 80 },
  NLSSettingsLabelHaptics: { text: 'Haptics', description: 'Label for haptics toggle in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintHaptics: { text: 'Native vibration feedback in mobile wrappers when available.', description: 'Hint for haptics in Appearance settings.', surface: 'settings', maxChars: 80 },
  NLSSettingsLabelRailCollapsedLabels: { text: 'Icon labels in collapsed rail', description: 'Label for rail collapsed labels toggle in Appearance settings.', surface: 'settings', maxChars: 36 },
  NLSSettingsHintRailCollapsedLabels: { text: 'Show the small text under each icon when the side nav is slim.', description: 'Hint for rail collapsed labels in Appearance settings.', surface: 'settings', maxChars: 90 },
  NLSSettingsLabelRailHoverPopup: { text: 'Side nav hover popup', description: 'Label for rail hover popup toggle in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintRailHoverPopup: { text: 'Expand the slim side nav while your pointer is over it.', description: 'Hint for rail hover popup in Appearance settings.', surface: 'settings', maxChars: 80 },
  NLSSettingsLabelCollapsingHeader: { text: 'Collapsing page headers', description: 'Label for collapsing page headers toggle in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintCollapsingHeader: { text: 'Let eligible page headers compact while scrolling dense work surfaces.', description: 'Hint for collapsing page headers in Appearance settings.', surface: 'settings', maxChars: 90 },
  NLSSettingsThemeFollowConsole: { text: 'Follow Console', description: 'Follow Console option for member app theme in Appearance settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsThemeFollowConsoleDesc: { text: 'Use the current Canvas Theme in the Member app.', description: 'Description for Follow Console option in Appearance settings.', surface: 'settings', maxChars: 60 },
  NLSSettingsSectionCalendar: { text: 'Calendar', description: 'Section title for Calendar in Appearance settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsLabelCalendarStyle: { text: 'Calendar style', description: 'Label for calendar style choice in Appearance settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintCalendarStyle: { text: 'Applies to every calendar view; colours follow your theme.', description: 'Hint for calendar style in Appearance settings.', surface: 'settings', maxChars: 90 },
  NLSSettingsCalendarStyleCardsHint: { text: 'Standard solid card surfaces.', description: 'Hint for cards calendar style.', surface: 'settings', maxChars: 60 },
  NLSSettingsCalendarStyleGlassHint: { text: 'Translucent glass surfaces with backdrop blur.', description: 'Hint for glass calendar style.', surface: 'settings', maxChars: 60 },
  NLSSettingsLabelCalendarBoardsShown: { text: 'Boards shown by default', description: 'Label for default boards shown in calendar in Appearance settings.', surface: 'settings', maxChars: 36 },
  NLSSettingsHintCalendarBoardsShown: { text: 'Choose whether the calendar defaults to the current board or custom boards.', description: 'Hint for boards shown by default in Appearance settings.', surface: 'settings', maxChars: 100 },
  NLSSettingsCalendarBoardScopeCurrent: { text: 'Current board', description: 'Option for showing only the current board by default in calendar.', surface: 'settings', maxChars: 24 },
  NLSSettingsCalendarBoardScopeCurrentHint: { text: 'Focus on events from the active board.', description: 'Hint for current board scope option.', surface: 'settings', maxChars: 60 },
  NLSSettingsCalendarBoardScopeCustom: { text: 'Custom selection', description: 'Option for custom board scope by default in calendar.', surface: 'settings', maxChars: 24 },
  NLSSettingsCalendarBoardScopeCustomHint: { text: 'Include events from multiple configured boards.', description: 'Hint for custom board scope option.', surface: 'settings', maxChars: 60 },
  NLSSettingsSectionChannels: { text: 'Channels', description: 'Section title for Channels in Notification settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionDigest: { text: 'Digest', description: 'Section title for Digest in Notification settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionQuietHours: { text: 'Quiet hours', description: 'Section title for Quiet hours in Notification settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsLabelDigestCadence: { text: 'Digest cadence', description: 'Label for digest cadence in Notification settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsHintDigestCadence: { text: 'Pick how often the workspace bundles low-priority activity for you.', description: 'Hint for digest cadence in Notification settings.', surface: 'settings', maxChars: 80 },
  NLSSettingsDigestCadenceDaily: { text: 'Daily', description: 'Daily digest cadence option label.', surface: 'settings', maxChars: 12 },
  NLSSettingsDigestCadenceDailyHint: { text: 'One digest each morning', description: 'Daily digest cadence hint.', surface: 'settings', maxChars: 30 },
  NLSSettingsDigestCadenceWeekly: { text: 'Weekly', description: 'Weekly digest cadence option label.', surface: 'settings', maxChars: 12 },
  NLSSettingsDigestCadenceWeeklyHint: { text: 'One digest at the start of the week', description: 'Weekly digest cadence hint.', surface: 'settings', maxChars: 40 },
  NLSSettingsAriaDigestAndQuietHoursPreferences: { text: 'Digest and quiet-hours preferences', description: 'Aria label for digest preferences panel.', surface: 'settings', maxChars: 45 },
  NLSSettingsAriaDigestCadence: { text: 'Digest cadence', description: 'Aria label for digest cadence choice control.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionAnswersAndDrafts: { text: 'Answers & drafts', description: 'Section title for Answers & drafts in AI Magic settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionLearning: { text: 'Learning', description: 'Section title for Learning in AI Magic settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionYourAiRules: { text: 'Your AI rules', description: 'Section title for Your AI rules in AI Magic settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionLineSuggestions: { text: 'Line suggestions', description: 'Section title for Line suggestions in AI Magic settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsChipOwnerOnly: { text: 'Owner only', description: 'Badge or chip label indicating an owner-only setting or metric.', surface: 'settings', maxChars: 20 },
  NLSSettingsLabelNewRule: { text: 'New rule', description: 'Label for new rule input in AI rules panel.', surface: 'settings', maxChars: 20 },
  NLSSettingsPlaceholderNewRule: { text: 'e.g. Always end plans with a review step.', description: 'Placeholder for new rule input.', surface: 'settings', maxChars: 50 },
  NLSSettingsActionAddRule: { text: 'Add rule', description: 'Button text to add a new AI rule.', surface: 'settings', maxChars: 20 },
  NLSSettingsSectionCanvas: { text: 'Canvas', description: 'Section title for Canvas in Design Editor settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionAutosave: { text: 'Autosave', description: 'Section title for Autosave in Design Editor settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionExport: { text: 'Export', description: 'Section title for Export in Design Editor settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsSectionLiveData: { text: 'Live data', description: 'Section title for Live data in Design Editor settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsActionResetDefaults: { text: 'Reset defaults', description: 'Button text to reset Design Editor preferences to defaults.', surface: 'settings', maxChars: 20 },
  NLSSettingsResetDefaultsTitle: { text: 'Reset Design Editor defaults?', description: 'Dialog title for resetting Design Editor preferences.', surface: 'settings', maxChars: 40 },
  NLSSettingsResetDefaultsBody: { text: 'This will restore all canvas, autosave, export, and live data settings to their initial values.', description: 'Dialog body for resetting Design Editor preferences.', surface: 'settings', maxChars: 110 },
  NLSSettingsLabelSnapToGrid: { text: 'Snap to grid', description: 'Label for snap to grid toggle.', surface: 'settings', maxChars: 24 },
  NLSSettingsHintSnapToGrid: { text: 'Keep moved shapes aligned to the grid.', description: 'Hint for snap to grid toggle.', surface: 'settings', maxChars: 50 },
  NLSSettingsLabelGridSize: { text: 'Grid size', description: 'Label for grid size stepper.', surface: 'settings', maxChars: 20 },
  NLSSettingsHintGridSize: { text: 'Pixels between grid lines.', description: 'Hint for grid size stepper.', surface: 'settings', maxChars: 40 },
  NLSSettingsLabelDefaultCanvasPreset: { text: 'Default canvas preset', description: 'Label for default canvas preset select.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintDefaultCanvasPreset: { text: 'Surface used for new designs.', description: 'Hint for default canvas preset select.', surface: 'settings', maxChars: 40 },
  NLSSettingsLabelShowRuler: { text: 'Show ruler by default', description: 'Label for show ruler toggle.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintShowRuler: { text: 'Start new editor sessions with canvas rulers visible.', description: 'Hint for show ruler toggle.', surface: 'settings', maxChars: 60 },
  NLSSettingsLabelAutosaveInterval: { text: 'Autosave interval', description: 'Label for autosave interval stepper.', surface: 'settings', maxChars: 24 },
  NLSSettingsHintAutosaveInterval: { text: 'Seconds between silent editor saves.', description: 'Hint for autosave interval stepper.', surface: 'settings', maxChars: 50 },
  NLSSettingsLabelAutoSnapshotCap: { text: 'Auto-snapshot cap', description: 'Label for auto-snapshot cap stepper.', surface: 'settings', maxChars: 24 },
  NLSSettingsHintAutoSnapshotCap: { text: 'Maximum automatic snapshots retained per design.', description: 'Hint for auto-snapshot cap stepper.', surface: 'settings', maxChars: 60 },
  NLSSettingsLabelDefaultExportFormat: { text: 'Default export format', description: 'Label for default export format select.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintDefaultExportFormat: { text: 'Preferred format for new export sessions.', description: 'Hint for default export format select.', surface: 'settings', maxChars: 50 },
  NLSSettingsLabelPngScale: { text: 'PNG scale', description: 'Label for PNG scale stepper.', surface: 'settings', maxChars: 20 },
  NLSSettingsHintPngScale: { text: 'Resolution multiplier for PNG exports.', description: 'Hint for PNG scale stepper.', surface: 'settings', maxChars: 50 },
  NLSSettingsLabelFilenamePattern: { text: 'Filename pattern', description: 'Label for filename pattern input.', surface: 'settings', maxChars: 24 },
  NLSSettingsHintFilenamePattern: { text: 'Supports design name and date tokens.', description: 'Hint for filename pattern input.', surface: 'settings', maxChars: 50 },
  NLSSettingsLabelRefreshCadence: { text: 'Refresh cadence', description: 'Label for bound refresh cadence select.', surface: 'settings', maxChars: 24 },
  NLSSettingsHintRefreshCadence: { text: 'Default cadence for Bound asset re-rendering.', description: 'Hint for bound refresh cadence select.', surface: 'settings', maxChars: 60 },
  NLSSettingsAiRulesSignInRequired: { text: 'Sign in to manage AI rules.', description: 'Empty state message when not signed in on AI rules panel.', surface: 'settings', maxChars: 64 },
  NLSSettingsAiRulesEmpty: { text: 'No saved rules yet.', description: 'Empty state message when no user rules are saved.', surface: 'settings', maxChars: 40 },
  NLSSettingsAiRuleTooShort: { text: 'Rule is too short.', description: 'Validation error when rule text is under 4 characters.', surface: 'settings', maxChars: 40 },
  NLSSettingsAiRuleDuplicate: { text: 'That rule already exists.', description: 'Message when adding a duplicate user rule.', surface: 'settings', maxChars: 40 },
  NLSSettingsAiRuleSaved: { text: 'Rule saved.', description: 'Toast notification after saving an AI user rule.', surface: 'settings', maxChars: 30 },
  NLSSettingsAiRuleSaveFailed: { text: 'Could not save rule.', description: 'Error toast when saving an AI user rule fails.', surface: 'settings', maxChars: 40 },
  NLSSettingsAiRulesLoadFailed: { text: 'Could not load your saved rules.', description: 'Error toast when loading AI user rules fails.', surface: 'settings', maxChars: 50 },
  NLSSettingsAiRuleDeleteFailed: { text: 'Could not delete rule.', description: 'Error toast when deleting an AI user rule fails.', surface: 'settings', maxChars: 40 },
  NLSSettingsAiRuleScopeFailed: { text: 'Could not update rule scope.', description: 'Error toast when updating an AI user rule scope fails.', surface: 'settings', maxChars: 50 },
  NLSSettingsAiRuleScopeAll: { text: 'All prompts', description: 'Label for rule scope applied to all prompts.', surface: 'settings', maxChars: 24 },
  NLSSettingsAiRuleScopeCreate: { text: 'Create only', description: 'Label for rule scope applied to creation only.', surface: 'settings', maxChars: 24 },
  NLSSettingsAiRuleScopeEdit: { text: 'Edit only', description: 'Label for rule scope applied to editing only.', surface: 'settings', maxChars: 24 },
  NLSSettingsAiRuleScope: { text: 'Scope', description: 'Label for rule scope select control.', surface: 'settings', maxChars: 20 },
  NLSSettingsAiRuleAutoDetected: { text: 'Auto-detected', description: 'Meta text for auto-detected AI rules.', surface: 'settings', maxChars: 24 },
  NLSSettingsAiRuleManuallySaved: { text: 'Manually saved', description: 'Meta text for manually saved AI rules.', surface: 'settings', maxChars: 24 },
  NLSSettingsAiRuleDelete: { text: 'Delete', description: 'Action label for deleting an AI user rule.', surface: 'settings', maxChars: 20 },
  NLSSettingsAiRuleScopeGroupAria: { text: 'Rule scope', description: 'Aria label for the rule scope radio group.', surface: 'settings', maxChars: 24 },
  NLSSettingsAiRulesSavedTitle: { text: 'Saved rules', description: 'Title for saved rules subheader in AI Magic settings.', surface: 'settings', maxChars: 30 },
  NLSSettingsHintYourAiRules: { text: 'Line applies these to every prompt. Say “always…”, “never…”, “from now on…”, or “remember…” in chat and Line will offer to save the rule automatically.', description: 'Hint for Your AI rules section in settings.', surface: 'settings', maxChars: 160 },
  NLSSettingsAriaGridSize: { text: 'Grid size in pixels', description: 'Aria label for grid size stepper.', surface: 'settings', maxChars: 30 },
  NLSSettingsAriaAutosaveInterval: { text: 'Autosave interval in seconds', description: 'Aria label for autosave interval stepper.', surface: 'settings', maxChars: 40 },
  NLSSettingsAriaAutoSnapshotCap: { text: 'Auto-snapshot cap per asset', description: 'Aria label for auto snapshot cap stepper.', surface: 'settings', maxChars: 40 },
  NLSSettingsAriaPngScale: { text: 'PNG export scale', description: 'Aria label for PNG scale stepper.', surface: 'settings', maxChars: 30 },
  NLSSettingsExportFormatPng: { text: 'PNG (image)', description: 'Export format option for PNG.', surface: 'settings', maxChars: 20 },
  NLSSettingsExportFormatSvg: { text: 'SVG (vector)', description: 'Export format option for SVG.', surface: 'settings', maxChars: 20 },
  NLSSettingsExportFormatPdf: { text: 'PDF (coming soon)', description: 'Export format option for PDF.', surface: 'settings', maxChars: 24 },
  NLSSettingsBoundRefreshOnSave: { text: 'On save (default)', description: 'Bound refresh option on save.', surface: 'settings', maxChars: 24 },
  NLSSettingsBoundRefresh5Min: { text: 'Every 5 minutes', description: 'Bound refresh option every 5 minutes.', surface: 'settings', maxChars: 24 },
  NLSSettingsBoundRefreshHour: { text: 'Every hour', description: 'Bound refresh option every hour.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsNotificationsSaved: { text: 'Notification policy saved for {audience}.', description: 'Notification policy saved for an audience.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsNotificationsSaveFailed: { text: "Couldn't save the notification policy. Try again.", description: 'Notification policy save failure error message.', surface: 'settings', maxChars: 70 },
  NLSSettingsResetDefaultsFailed: { text: 'Could not reset preferences.', description: 'Error toast when resetting Design Editor preferences fails.', surface: 'settings', maxChars: 40 },
  NLSSettingsGuideSeen: { text: 'Seen', description: 'Chip indicating a guide or tour has been seen.', surface: 'settings', maxChars: 16 },
  NLSSettingsGuidePlay: { text: 'Play', description: 'Button to play a guide.', surface: 'settings', maxChars: 16 },
  NLSSettingsGuideReplay: { text: 'Replay', description: 'Button to replay a seen guide.', surface: 'settings', maxChars: 16 },
  NLSSettingsGuideResetProgress: { text: 'Reset progress', description: 'Button to mark a guide as unseen.', surface: 'settings', maxChars: 24 },
  NLSSettingsGuideResetProgressTooltipSeen: { text: 'Mark this guide as unseen so it surfaces again.', description: 'Tooltip for reset progress button when seen.', surface: 'settings', maxChars: 60 },
  NLSSettingsGuideResetProgressTooltipUnseen: { text: 'Already unseen.', description: 'Tooltip for reset progress button when not seen.', surface: 'settings', maxChars: 24 },
  NLSSettingsGuideEmptyTitle: { text: 'No guides published yet', description: 'Title when no guides are registered.', surface: 'settings', maxChars: 40 },
  NLSSettingsGuideEmptyDesc: { text: 'Check back after the next experience drop.', description: 'Description when no guides are registered.', surface: 'settings', maxChars: 60 },
  NLSSettingsGuideErrorTitle: { text: 'Guides are temporarily unavailable', description: 'Error title when loading guides fails.', surface: 'settings', maxChars: 45 },
  NLSSettingsTourStart: { text: 'Start tour', description: 'Button to launch an interactive tour.', surface: 'settings', maxChars: 20 },
  NLSSettingsTourActiveTooltip: { text: 'Finish or skip the active tour first.', description: 'Tooltip when a tour is already active.', surface: 'settings', maxChars: 50 },
  NLSSettingsTourLaunchTooltip: { text: 'Launch this tour now.', description: 'Tooltip to start a tour.', surface: 'settings', maxChars: 30 },
  NLSSettingsTourAlsoInActions: { text: 'Also in Actions', description: 'Caption linking tour to an action in catalog.', surface: 'settings', maxChars: 24 },
  NLSSettingsTourEmptyTitle: { text: 'No interactive tours registered', description: 'Title when no tours are registered.', surface: 'settings', maxChars: 40 },
  NLSSettingsTourEmptyDesc: { text: 'Tours will appear as features and guided flows are enabled.', description: 'Description when no tours are registered.', surface: 'settings', maxChars: 70 },
  NLSSettingsActionCopyId: { text: 'Copy action ID: {id}', description: 'Tooltip for copying action ID.', surface: 'settings', maxChars: 60 },
  NLSSettingsActionCopied: { text: 'Action ID copied', description: 'Toast when action ID is copied.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionRun: { text: 'Run', description: 'Button to execute an action.', surface: 'settings', maxChars: 16 },
  NLSSettingsActionRunning: { text: 'Running...', description: 'Button text while action is running.', surface: 'settings', maxChars: 20 },
  NLSSettingsActionRunTooltip: { text: 'Run {label}', description: 'Tooltip for running an action.', surface: 'settings', maxChars: 40 },
  NLSSettingsActionShowMeHow: { text: 'Show me how', description: 'Button to launch interactive how-to tour for an action.', surface: 'settings', maxChars: 20 },
  NLSSettingsActionShowMeHowTooltip: { text: 'Launch interactive how-to tour.', description: 'Tooltip for Show me how button.', surface: 'settings', maxChars: 40 },
  NLSSettingsActionContextRequired: { text: 'Runs from Orbit / AI Magic', description: 'Button text for targeted actions needing parameters.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionContextTooltip: { text: 'Requires contextual target parameters from Orbit or AI Magic', description: 'Tooltip for actions needing context.', surface: 'settings', maxChars: 70 },
  NLSSettingsActionIntro: { text: 'All registered actions available across Timelining. Execute non-targeted actions directly, or launch interactive tours to learn how to perform them.', description: 'Intro text for actions catalog.', surface: 'settings', maxChars: 170 },
  NLSSettingsActionEmptyTitle: { text: 'No catalog actions registered', description: 'Title when no catalog actions exist.', surface: 'settings', maxChars: 40 },
  NLSSettingsActionEmptyDesc: { text: 'Actions will appear here as features are registered.', description: 'Description when no catalog actions exist.', surface: 'settings', maxChars: 60 },
  NLSSettingsActionDomainCountChip: { text: '{count} actions in {domain}', description: 'Accessible label for action count in domain.', surface: 'settings', maxChars: 60 },
  NLSSettingsActionDomainAutomation: { text: 'Automation', description: 'Action domain label for automation.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainBoard: { text: 'Board', description: 'Action domain label for board.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainChart: { text: 'Chart', description: 'Action domain label for chart.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainChat: { text: 'Chat', description: 'Action domain label for chat.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainChecklist: { text: 'Checklist', description: 'Action domain label for checklist.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainClient: { text: 'Client', description: 'Action domain label for client.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainDashboard: { text: 'Dashboard', description: 'Action domain label for dashboard.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainDesign: { text: 'Design', description: 'Action domain label for design.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainExplore: { text: 'Explore', description: 'Action domain label for explore.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainForm: { text: 'Form', description: 'Action domain label for form.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainGoal: { text: 'Goal', description: 'Action domain label for goal.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainMarker: { text: 'Marker', description: 'Action domain label for marker.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainMeeting: { text: 'Meeting', description: 'Action domain label for meeting.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainMember: { text: 'Member', description: 'Action domain label for member.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainNavigation: { text: 'Navigation', description: 'Action domain label for navigation.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainOps: { text: 'Operations', description: 'Action domain label for ops.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainReport: { text: 'Report', description: 'Action domain label for report.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainStock: { text: 'Stock', description: 'Action domain label for stock.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainTask: { text: 'Task', description: 'Action domain label for task.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainTimeline: { text: 'Timeline', description: 'Action domain label for timeline.', surface: 'settings', maxChars: 30 },
  NLSSettingsActionDomainTimesheets: { text: 'Timesheets', description: 'Action domain label for timesheets.', surface: 'settings', maxChars: 30 },
  NLSSettingsFeedbackSummary: { text: '{pending} prompts waiting · {deferred} deferred · +{credits} credits', description: 'Summary line for feedback queue.', surface: 'settings', maxChars: 70 },
  NLSSettingsFeedbackSummarySingle: { text: '{pending} prompt waiting · {deferred} deferred · +{credits} credits', description: 'Summary line for feedback queue with single prompt.', surface: 'settings', maxChars: 70 },
  NLSSettingsFeedbackThanks: { text: 'Thanks — +{credits} credits.', description: 'Status message after submitting feedback.', surface: 'settings', maxChars: 40 },
  NLSSettingsFeedbackEmptyTitle: { text: 'No prompts waiting', description: 'Title when feedback queue is empty.', surface: 'settings', maxChars: 30 },
  NLSSettingsFeedbackEmptyDesc: { text: 'You are all caught up on feedback prompts.', description: 'Description when feedback queue is empty.', surface: 'settings', maxChars: 60 },
  NLSSettingsFeedbackCaughtUpTitle: { text: "You're all caught up.", description: 'Empty state title for feedback queue.', surface: 'settings', maxChars: 30 },
  NLSSettingsFeedbackCaughtUpDesc: { text: 'New prompts appear here as you use the app — reward is earned for each answer.', description: 'Empty state description for feedback queue.', surface: 'settings', maxChars: 80 },
  NLSSettingsFeedbackDeferredNotice: { text: "Deferred — you'll see it again after the others.", description: 'Feedback deferred toast/status.', surface: 'settings', maxChars: 50 },
  NLSSettingsFeedbackRemovedNotice: { text: 'Removed from your feedback queue.', description: 'Feedback removed toast/status.', surface: 'settings', maxChars: 40 },
  NLSSettingsFeedbackSkipFailed: { text: 'Skip failed; try again.', description: 'Error message when skip fails.', surface: 'settings', maxChars: 30 },
  NLSSettingsFeedbackSubmitFailed: { text: 'Submission failed; try again.', description: 'Error message when submission fails.', surface: 'settings', maxChars: 35 },
  NLSSettingsFeedbackRemoveFailed: { text: 'Remove failed; try again.', description: 'Error message when remove fails.', surface: 'settings', maxChars: 30 },
  NLSSettingsFeedbackQueueUnavailable: { text: 'Feedback queue is temporarily unavailable: {error}', description: 'Error when feedback queue fails to load.', surface: 'settings', maxChars: 65 },
  NLSSettingsFeedbackSignInPrompt: { text: 'Sign in to view your feedback queue.', description: 'Notice to sign in to see feedback queue.', surface: 'settings', maxChars: 45 },
  NLSSettingsFeedbackLoading: { text: 'Loading feedback queue...', description: 'Loading text for feedback queue.', surface: 'settings', maxChars: 30 },
  NLSSettingsFeedbackProgressAria: { text: 'Feedback progress: {cleared} of {baseline} cleared ({percent}%)', description: 'Aria label for feedback progress bar.', surface: 'settings', maxChars: 70 },
  NLSSettingsFeedbackNotesLabel: { text: 'Notes', description: 'Label for feedback notes.', surface: 'settings', maxChars: 16 },
  NLSSettingsFeedbackNotesOptional: { text: '(optional)', description: 'Optional indicator for feedback notes.', surface: 'settings', maxChars: 16 },
  NLSSettingsFeedbackNotesPlaceholder: { text: 'What could be sharper?', description: 'Placeholder for feedback notes.', surface: 'settings', maxChars: 30 },
  NLSSettingsFeedbackSkipForever: { text: 'Skip forever', description: 'Button label to discard feedback form forever.', surface: 'settings', maxChars: 20 },
  NLSSettingsFeedbackSkip: { text: 'Skip', description: 'Button label to defer feedback form.', surface: 'settings', maxChars: 16 },
  NLSSettingsFeedbackSubmit: { text: 'Submit', description: 'Button label to submit feedback form.', surface: 'settings', maxChars: 16 },
  NLSSettingsFeedbackRatingAria: { text: 'Feedback rating', description: 'Aria label for rating group.', surface: 'settings', maxChars: 24 },
  NLSSettingsFeedbackDeferred: { text: 'Deferred', description: 'Chip indicating deferred status.', surface: 'settings', maxChars: 16 },
  NLSSettingsFeedbackDraft: { text: 'Draft', description: 'Chip indicating draft status.', surface: 'settings', maxChars: 16 },
  NLSSettingsFeedbackRewardChip: { text: '+{credits}', description: 'Chip for feedback credit reward.', surface: 'settings', maxChars: 12 },
  NLSSettingsFeedbackRewardTooltip: { text: '+{credits} credits when submitted', description: 'Tooltip for credit reward badge.', surface: 'settings', maxChars: 40 },
  NLSSettingsFeedbackCategoryTask: { text: 'Work flow', description: 'Category label for task feedback.', surface: 'settings', maxChars: 20 },
  NLSSettingsFeedbackCategoryAi: { text: 'AI Magic', description: 'Category label for AI feedback.', surface: 'settings', maxChars: 20 },
  NLSSettingsFeedbackCategoryFallback: { text: 'Experience', description: 'Category label for fallback feedback.', surface: 'settings', maxChars: 20 },
  NLSSettingsFeedbackRecentTaskAction: { text: 'Recent task or checklist action', description: 'Subtitle for task feedback form.', surface: 'settings', maxChars: 40 },
  NLSSettingsFeedbackRecentAiAction: { text: 'Recent AI action', description: 'Subtitle for AI feedback form.', surface: 'settings', maxChars: 30 },
  NLSSettingsFeedbackRecentAppAction: { text: 'Recent app action', description: 'Subtitle for general app feedback form.', surface: 'settings', maxChars: 30 },
  NLSSettingsSupportDiagnosticsChipNone: { text: 'No session', description: 'Chip when no diagnostic session is active.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportDiagnosticsChipActive: { text: '{events} events', description: 'Chip when diagnostic session is active.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportReopen: { text: 'Reopen', description: 'Button to reopen a support ticket.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportSectionNewTicket: { text: 'New ticket', description: 'Section title for creating a new support ticket.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportSectionNewTicketDesc: { text: 'Write a ticket and attach diagnostics only when they help explain the problem.', description: 'Description for new ticket section.', surface: 'settings', maxChars: 90 },
  NLSSettingsSupportSectionYourTickets: { text: 'Your tickets', description: 'Section title for user ticket list.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportSectionYourTicketsDesc: { text: 'Owner acceptance and resolution updates appear here automatically.', description: 'Description for user tickets section.', surface: 'settings', maxChars: 80 },
  NLSSettingsSupportSectionDiagnostics: { text: 'Diagnostics', description: 'Section title for support diagnostics.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportSectionDiagnosticsDesc: { text: 'Capture browser events, screenshots, and network traces to help diagnose issues.', description: 'Description for diagnostics section.', surface: 'settings', maxChars: 90 },
  NLSSettingsSupportEmptyTitle: { text: 'No support tickets in this view', description: 'Empty state title for support tickets list.', surface: 'settings', maxChars: 40 },
  NLSSettingsSupportEmptyDesc: { text: 'Submitted requests stay in this user-level queue.', description: 'Empty state description for support tickets list.', surface: 'settings', maxChars: 60 },
  NLSSettingsSupportErrorTitle: { text: 'Could not load tickets', description: 'Error title when loading tickets fails.', surface: 'settings', maxChars: 40 },
  NLSSettingsSupportOwnerRestrictionActive: { text: 'Owner restriction active', description: 'Notice that owner restriction is active.', surface: 'settings', maxChars: 40 },
  NLSSettingsSupportOwnerRestrictionDefault: { text: 'The owner has limited support ticket actions for this account.', description: 'Default restriction reason message.', surface: 'settings', maxChars: 80 },
  NLSSettingsSupportRevokedTickets: { text: 'Owner has revoked new support tickets for this account.', description: 'Notice that new tickets are revoked.', surface: 'settings', maxChars: 70 },
  NLSSettingsSupportSignInRequired: { text: 'Sign in before submitting support tickets.', description: 'Notice that sign in is required for tickets.', surface: 'settings', maxChars: 60 },
  NLSSettingsSupportStatusSubmitted: { text: 'Submitted', description: 'Support ticket status Submitted.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportStatusReopened: { text: 'Reopened', description: 'Support ticket status Reopened.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportStatusAccepted: { text: 'Accepted', description: 'Support ticket status Accepted.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportStatusInvestigating: { text: 'Investigating', description: 'Support ticket status Investigating.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportStatusResolved: { text: 'Resolved', description: 'Support ticket status Resolved.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportStatusDismissed: { text: 'Dismissed', description: 'Support ticket status Dismissed.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportStatusSkipped: { text: 'Skipped', description: 'Support ticket status Skipped.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportNoTimestamp: { text: 'No timestamp', description: 'Fallback text when date timestamp is missing.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportFieldTitle: { text: 'Title', description: 'Field label for support ticket title.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportPlaceholderTitle: { text: 'Short summary', description: 'Placeholder for support ticket title.', surface: 'settings', maxChars: 30 },
  NLSSettingsSupportFieldDescription: { text: 'Description', description: 'Field label for support ticket description.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportPlaceholderDescription: { text: 'Describe what happened, what you expected, and where you were working.', description: 'Placeholder for support ticket description.', surface: 'settings', maxChars: 90 },
  NLSSettingsSupportLengthHint: { text: '{current}/{max} · minimum {min}', description: 'Character counter hint for support ticket fields.', surface: 'settings', maxChars: 40 },
  NLSSettingsSupportFieldSeverity: { text: 'Severity', description: 'Field label for support ticket severity.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportAriaSeverity: { text: 'Severity', description: 'Aria label for severity radiogroup.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportSeverityLow: { text: 'Low', description: 'Severity option Low.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportSeverityMedium: { text: 'Medium', description: 'Severity option Medium.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportSeverityHigh: { text: 'High', description: 'Severity option High.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportSeverityCritical: { text: 'Critical', description: 'Severity option Critical.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportAttachDiagnostics: { text: 'Attach diagnostics', description: 'Title of collapsible section to attach diagnostics.', surface: 'settings', maxChars: 30 },
  NLSSettingsSupportDiagnosticsAttached: { text: '{events} events · {screenshots} shots · {size}', description: 'Diagnostics chip label when attached.', surface: 'settings', maxChars: 60 },
  NLSSettingsSupportNoDiagnostics: { text: 'No diagnostics attached', description: 'Diagnostics chip label when no diagnostics attached.', surface: 'settings', maxChars: 40 },
  NLSSettingsSupportDiagnosticIntro: { text: "Capture a short session to attach browser events, screenshots, and network traces to this ticket. We'll take you to the dashboard so you can reproduce the issue in a fresh view.", description: 'Explainer text for diagnostic session capture.', surface: 'settings', maxChars: 190 },
  NLSSettingsSupportBtnStartDiagnosticCapture: { text: 'Start a diagnostic capture', description: 'Button to start diagnostic capture session.', surface: 'settings', maxChars: 34 },
  NLSSettingsSupportBtnSubmitTicket: { text: 'Submit ticket', description: 'Button to submit support ticket.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportFilterAria: { text: 'Support ticket filters', description: 'Aria label for support ticket filter tabs.', surface: 'settings', maxChars: 30 },
  NLSSettingsSupportFilterOpen: { text: 'Open', description: 'Filter tab label for Open tickets.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportFilterResolved: { text: 'Resolved', description: 'Filter tab label for Resolved tickets.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportFilterAll: { text: 'All', description: 'Filter tab label for All tickets.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportNoMessage: { text: 'No written message.', description: 'Fallback text when ticket has no message.', surface: 'settings', maxChars: 30 },
  NLSSettingsSupportOwnerNote: { text: 'Owner note:', description: 'Prefix for owner note in ticket details.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportStatEvents: { text: 'Events', description: 'Stat label for events.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportStatErrors: { text: 'Errors', description: 'Stat label for errors.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportStatNetwork: { text: 'Network', description: 'Stat label for network failures.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportStatScreens: { text: 'Screens', description: 'Stat label for screenshots.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportStatFiles: { text: 'Files', description: 'Stat label for attached files.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportStatBundle: { text: 'Bundle', description: 'Stat label for total bundle size.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportCsatAria: { text: 'Support rating', description: 'Aria label for CSAT rating radiogroup.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportCsatVeryPoor: { text: 'Very poor', description: 'Rating 1 label.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportCsatPoor: { text: 'Poor', description: 'Rating 2 label.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportCsatOkay: { text: 'Okay', description: 'Rating 3 label.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportCsatGood: { text: 'Good', description: 'Rating 4 label.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportCsatGreat: { text: 'Great', description: 'Rating 5 label.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportDiagnosticModeTitle: { text: 'Diagnostic mode', description: 'Title of diagnostic mode toggle.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportDiagnosticModeDesc: { text: 'Advanced local capture for routes, errors, network calls, screenshots, and files.', description: 'Description of diagnostic mode.', surface: 'settings', maxChars: 100 },
  NLSSettingsSupportDiagnosticEmpty: { text: "No session active. Start a capture to record interactions locally before submitting. You'll be taken to the dashboard so the first recorded event is the beginning of your repro.", description: 'Empty state message for diagnostics session.', surface: 'settings', maxChars: 190 },
  NLSSettingsSupportBtnStartCapture: { text: 'Start capture', description: 'Button text to start capture.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportBtnStopCapture: { text: 'Stop capture', description: 'Button text to stop capture.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportBtnCopyJson: { text: 'Copy JSON', description: 'Button text to copy diagnostic JSON.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportBtnDownloadJson: { text: 'Download JSON', description: 'Button text to download diagnostic JSON.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportBtnClear: { text: 'Clear', description: 'Button text to clear diagnostic log.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportFieldSessionNote: { text: 'Session note', description: 'Label for diagnostic session note field.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportPlaceholderSessionNote: { text: 'What happened? What did you expect?', description: 'Placeholder for diagnostic session note.', surface: 'settings', maxChars: 50 },
  NLSSettingsSupportBtnAddNote: { text: 'Add note', description: 'Button to append note to session.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportFieldScreenshotLabel: { text: 'Screenshot label', description: 'Label for screenshot label input.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportFieldAttachmentLabel: { text: 'Attachment label', description: 'Label for attachment label input.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportPlaceholderOptionalLabel: { text: 'Optional label', description: 'Placeholder for optional evidence label.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportBtnCaptureScreenshot: { text: 'Capture screenshot', description: 'Button to take screenshot in diagnostic mode.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportBtnAddFiles: { text: 'Add files', description: 'Button to attach files to diagnostic session.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportShotFallback: { text: '{source} shot', description: 'Fallback label for screenshot without user label.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportBtnRemove: { text: 'Remove', description: 'Button text to remove screenshot or attachment.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportAriaRemoveScreenshot: { text: 'Remove screenshot', description: 'Aria label for removing screenshot.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportAriaRemoveAttachment: { text: 'Remove attachment', description: 'Aria label for removing attachment.', surface: 'settings', maxChars: 24 },
  NLSSettingsSupportDialogStartCaptureTitle: { text: 'Start capturing your session?', description: 'Title of start capture confirmation dialog.', surface: 'settings', maxChars: 40 },
  NLSSettingsSupportDialogStartCaptureBody1: { text: "We'll record clicks, screenshots, and errors so you can hand a full context to the owner. When you click Start capture, you'll be taken to the dashboard — that keeps the timeline focused on your bug reproduction, not the settings you're on now.", description: 'Body paragraph 1 of start capture dialog.', surface: 'settings', maxChars: 280 },
  NLSSettingsSupportDialogStartCaptureBody2: { text: "Tap the Debug badge at the top of the screen when you're done. You'll come back here to write a title and submit the ticket.", description: 'Body paragraph 2 of start capture dialog.', surface: 'settings', maxChars: 150 },
  NLSSettingsSupportDialogReopenTitle: { text: 'Reopen support ticket', description: 'Title of reopen ticket dialog.', surface: 'settings', maxChars: 30 },
  NLSSettingsSupportFieldReopenNote: { text: 'Reopen note', description: 'Field label for reopen note.', surface: 'settings', maxChars: 20 },
  NLSSettingsSupportPlaceholderReopenNote: { text: 'What is still happening?', description: 'Placeholder for reopen note.', surface: 'settings', maxChars: 30 },
  NLSSettingsSupportDialogCancel: { text: 'Cancel', description: 'Cancel action in support dialogs.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportDialogReopen: { text: 'Reopen', description: 'Reopen action in reopen dialog.', surface: 'settings', maxChars: 16 },
  NLSSettingsSupportToastCaptureStarted: { text: 'Capture started — reproduce the issue, then click the Debug badge to end.', description: 'Toast when session capture starts.', surface: 'settings', maxChars: 90 },
  NLSSettingsSupportToastNoSession: { text: 'No diagnostic session is available to submit.', description: 'Toast when trying to submit without session.', surface: 'settings', maxChars: 60 },
  NLSSettingsSupportToastTicketSubmitted: { text: 'Support ticket submitted.', description: 'Toast on successful support ticket submission.', surface: 'settings', maxChars: 30 },
  NLSSettingsSupportToastTicketSubmitFailed: { text: 'Could not submit support ticket.', description: 'Toast on failed support ticket submission.', surface: 'settings', maxChars: 40 },
  NLSSettingsSupportToastTicketReopened: { text: 'Support ticket reopened.', description: 'Toast on successful support ticket reopen.', surface: 'settings', maxChars: 30 },
  NLSSettingsSupportToastTicketReopenFailed: { text: 'Could not reopen support ticket.', description: 'Toast on failed support ticket reopen.', surface: 'settings', maxChars: 40 },
  NLSSettingsSupportToastFeedbackFailed: { text: 'Failed to submit feedback', description: 'Toast on CSAT submission error.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsSectionDashboardViews: { text: 'Dashboard views', description: 'Section title for dashboard views.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionDashboardViewsDesc: { text: 'Choose which board views are available and set the workspace default.', description: 'Description for dashboard views.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsLastViewHint: { text: 'At least one view stays on', description: 'Hint when only one dashboard view is enabled.', surface: 'settings', maxChars: 35 },
  NLSBoardSettingsDefaultViewLabel: { text: 'Default view', description: 'Label for default dashboard view select row.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsDefaultViewHint: { text: 'Initial view when opening this workspace.', description: 'Hint for default dashboard view select.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsSectionCards: { text: 'Cards', description: 'Section title for card settings in Board views.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSectionCardsDesc: { text: 'Let member and helper Todo cards display uploaded card backgrounds while keeping tier colours.', description: 'Description for cards section.', surface: 'settings', maxChars: 110 },
  NLSBoardSettingsSectionBranding: { text: 'Branding', description: 'Section title for branding in Board views.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSectionBrandingDesc: { text: 'Workspace logo and accent colour used across boards and client views.', description: 'Description for branding section in Board views.', surface: 'settings', maxChars: 90 },
  NLSBoardSettingsLabelCardBackgrounds: { text: 'Enable card backgrounds', description: 'Label for card backgrounds toggle.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsHintCardBackgrounds: { text: 'When off, all checklist cards inherit only the parent tier colour.', description: 'Hint for card backgrounds toggle.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsSectionGovernance: { text: 'Governance', description: 'Section title for governance in Governance & data.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsGovernanceRowLabel: { text: 'Audit ledger, reports and governance controls', description: 'Label for governance center row.', surface: 'settings', maxChars: 55 },
  NLSBoardSettingsGovernanceRowHint: { text: 'View activity logs, generate compliance reports, and manage retention.', description: 'Hint for governance center row.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsOpenGovernanceCenter: { text: 'Open Governance Center', description: 'Button text to open governance center.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsSectionDataTools: { text: 'Data tools', description: 'Section title for data tools in Governance & data.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionDataToolsDesc: { text: 'Export workspace data and run element dates backfill maintenance.', description: 'Description for data tools section.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsSectionDangerZone: { text: 'Danger zone', description: 'Section title for workspace danger zone.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsDeleteTeamDataTitle: { text: 'Delete team data', description: 'Title for deleting team workspace data.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsDeletePersonalDataTitle: { text: 'Delete personal data', description: 'Title for deleting personal workspace data.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsDeleteDataDesc: { text: 'These actions open a confirmation modal before workspace data is deleted.', description: 'Description for delete workspace data action.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsDeleteTeamDataAction: { text: 'Delete Team Data', description: 'Button label for deleting team data.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsDeletePersonalDataAction: { text: 'Delete Personal Data', description: 'Button label for deleting personal data.', surface: 'settings', maxChars: 25 },
  NLSSettingsJumpListOnThisPage: { text: 'On this page', description: 'Label for in-page jump list.', surface: 'settings', maxChars: 20 },
  NLSSettingsPublicSectionPublishing: { text: 'Publishing', description: 'Section title for publishing settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicSectionPublishingDesc: { text: 'Configure workspace public link and Explore listing.', description: 'Description for publishing section.', surface: 'settings', maxChars: 80 },
  NLSSettingsPublicSectionProfile: { text: 'Public profile', description: 'Section title for public profile.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicSectionProfileDesc: { text: 'Profile metadata, cover image, and tags shown on Explore.', description: 'Description for public profile section.', surface: 'settings', maxChars: 80 },
  NLSSettingsPublicSectionCuration: { text: 'Curation', description: 'Section title for curation settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicSectionCurationDesc: { text: 'Spotlight settings on Explore shelves and badges.', description: 'Description for curation section.', surface: 'settings', maxChars: 80 },
  NLSSettingsPublicSectionViewersSee: { text: 'What viewers see', description: 'Section title for viewer masking settings.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicSectionViewersSeeDesc: { text: 'Set permissions, surfaces, and field masking for public viewers.', description: 'Description for what viewers see section.', surface: 'settings', maxChars: 90 },
  NLSSettingsPublicSectionPreview: { text: 'Preview', description: 'Section title for preview.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicSectionPreviewDesc: { text: 'Side-by-side comparison of member and public views.', description: 'Description for preview section.', surface: 'settings', maxChars: 80 },
  NLSSettingsPublicPublishWorkspace: { text: 'Publish workspace', description: 'Toggle label for publishing workspace.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicPublishWorkspaceHint: { text: 'When on, anyone with the link (and, optionally, anyone in Explore) can open this workspace as a Viewer.', description: 'Hint for publishing toggle.', surface: 'settings', maxChars: 110 },
  NLSSettingsPublicListInExplore: { text: 'List in Explore', description: 'Toggle label for listing in explore.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicListInExploreHint: { text: 'Off (default): link-only — only visitors with the URL can find it. On: appears in Explore search + featured lists.', description: 'Hint for explore listing toggle.', surface: 'settings', maxChars: 120 },
  NLSSettingsPublicWorkspacePublished: { text: 'Workspace is published.', description: 'Status message when workspace is published.', surface: 'settings', maxChars: 40 },
  NLSSettingsPublicWorkspaceUnpublished: { text: 'Workspace is unpublished.', description: 'Status message when workspace is unpublished.', surface: 'settings', maxChars: 40 },
  NLSSettingsPublicPermissionRequired: { text: 'Requires the Publish workspace permission.', description: 'Notice when user cannot publish.', surface: 'settings', maxChars: 60 },
  NLSSettingsPublicPersonalWorkspaceNotice: { text: 'Personal workspaces cannot be published to Explore.', description: 'Notice that personal workspaces cannot be published.', surface: 'settings', maxChars: 70 },
  NLSSettingsPublicTaglineLabel: { text: 'Tagline', description: 'Label for tagline input.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicTaglinePlaceholder: { text: 'A one-sentence pitch.', description: 'Placeholder for tagline.', surface: 'settings', maxChars: 40 },
  NLSSettingsPublicCategoryLabel: { text: 'Category', description: 'Label for category input.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicCategoryPlaceholder: { text: 'e.g. Community, Portfolio, Documentation…', description: 'Placeholder for category.', surface: 'settings', maxChars: 50 },
  NLSSettingsPublicAboutLabel: { text: 'About (Markdown)', description: 'Label for about textarea.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicAboutPlaceholder: { text: 'What is this workspace? Who is it for? What can visitors expect to see?', description: 'Placeholder for about text.', surface: 'settings', maxChars: 90 },
  NLSSettingsPublicCoverLabel: { text: 'Explore cover', description: 'Label for explore cover.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicCoverHint: { text: 'Hero art and metadata shown on Explore cards. Cover image wins over the gradient.', description: 'Hint for explore cover.', surface: 'settings', maxChars: 95 },
  NLSSettingsPublicUploadImage: { text: 'Upload image', description: 'Button label to upload cover image.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicReplaceImage: { text: 'Replace image', description: 'Button label to replace cover image.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicRemoveImage: { text: 'Remove image', description: 'Button label to remove cover image.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicUploadingImage: { text: 'Uploading…', description: 'Button label while uploading cover image.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicCoverFormatHint: { text: 'JPG · PNG · WebP · 2 MB max', description: 'Cover image format and size constraints.', surface: 'settings', maxChars: 40 },
  NLSSettingsPublicHeroGradientLabel: { text: 'Hero gradient', description: 'Label for hero gradient chip grid.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicTagsLabel: { text: 'Tags · up to {max}', description: 'Label for tags field.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicTagPlaceholder: { text: 'Type a tag, press Enter', description: 'Placeholder for tag input.', surface: 'settings', maxChars: 35 },
  NLSSettingsPublicTagLimitReached: { text: 'Tag limit reached', description: 'Placeholder when max tags reached.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicRemoveTag: { text: 'Remove tag {tag}', description: 'Accessible label for remove tag button.', surface: 'settings', maxChars: 35 },
  NLSSettingsPublicFeaturedLabel: { text: 'Featured on Explore', description: 'Label for featured toggle.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicFeaturedHint: { text: "Shows in Explore's Featured shelf above the main grid.", description: 'Hint for featured toggle.', surface: 'settings', maxChars: 70 },
  NLSSettingsPublicVerifiedLabel: { text: 'Verified badge', description: 'Label for verified toggle.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicVerifiedHint: { text: 'Adds a trust check next to the name. Turn on only for first-party or partner references.', description: 'Hint for verified toggle.', surface: 'settings', maxChars: 100 },
  NLSSettingsPublicPresetsLabel: { text: 'Surface presets', description: 'Label for surface presets.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicPresetsHint: { text: 'Quickly configure which screens a viewer can open.', description: 'Hint for surface presets.', surface: 'settings', maxChars: 65 },
  NLSSettingsPublicSurfacesGroup: { text: 'Surfaces', description: 'Group title for surface toggles.', surface: 'settings', maxChars: 24 },
  NLSSettingsPublicSurfacesHint: { text: 'Toggle each surface a viewer is allowed to open. Anything off is redirected to the Dashboard.', description: 'Hint for surface toggles.', surface: 'settings', maxChars: 100 },
  NLSSettingsPublicSensitiveGroup: { text: 'Sensitive-field visibility', description: 'Group title for sensitive field toggles.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicSensitiveHint: { text: 'Viewers never edit. These toggles decide whether specific sensitive fields are visible or masked in read-only mode.', description: 'Hint for sensitive toggles.', surface: 'settings', maxChars: 130 },
  NLSSettingsPublicTreatmentGroup: { text: 'Viewer treatment', description: 'Group title for viewer treatment.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicTreatmentHint: { text: 'Optional refinements — how viewers experience the workspace.', description: 'Hint for viewer treatment.', surface: 'settings', maxChars: 80 },
  NLSSettingsPublicShowRosterLabel: { text: 'Show member roster', description: 'Toggle label for showing member roster.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicShowRealNamesLabel: { text: 'Show real names', description: 'Toggle label for showing real names.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicShowRealNamesHint: { text: 'Show real names (otherwise: role labels only)', description: 'Hint for showing real names.', surface: 'settings', maxChars: 60 },
  NLSSettingsPublicAllowPdfLabel: { text: 'Allow "Export as PDF"', description: 'Toggle label for allowing PDF export.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicAllowPdfHint: { text: 'Allow "Export as PDF" from viewer screens', description: 'Hint for allowing PDF export.', surface: 'settings', maxChars: 50 },
  NLSSettingsPublicWatermarkLabel: { text: 'Watermark screenshots', description: 'Toggle label for watermarking screenshots.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicWatermarkHint: { text: 'Watermark visible screenshots with workspace name', description: 'Hint for watermarking screenshots.', surface: 'settings', maxChars: 60 },
  NLSSettingsPublicChatWindowLabel: { text: 'Chat window (days visible)', description: 'Label for chat window stepper.', surface: 'settings', maxChars: 35 },
  NLSSettingsPublicSaveVisibility: { text: 'Save public visibility', description: 'Button label to save public visibility.', surface: 'settings', maxChars: 30 },
  NLSSettingsPublicSavedToast: { text: 'Public visibility settings saved.', description: 'Toast when public visibility saves successfully.', surface: 'settings', maxChars: 50 },
  NLSSettingsPublicErrorToast: { text: 'Could not update public visibility.', description: 'Toast when public visibility save fails.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsSectionModules: { text: 'Modules', description: 'Section title for modules.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionModulesDesc: { text: 'Choose which modules and addons are active for this workspace.', description: 'Description for modules section.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsSectionHelperApp: { text: 'Helper app', description: 'Section title for helper app tools.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionHelperAppDesc: { text: 'Manage helper application tools available to team members.', description: 'Description for helper app section.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsSectionBudgetCategories: { text: 'Budget categories', description: 'Section title for budget categories.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsBudgetCategoriesRowLabel: { text: 'Categories', description: 'Row label for budget categories setting.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsBudgetCategoriesHint: { text: 'Categories are fixed for now.', description: 'Hint for budget categories.', surface: 'settings', maxChars: 40 },
  NLSBudgetCategory_Labor: { text: 'Labor', description: 'Budget category label for Labor.', surface: 'settings', maxChars: 20 },
  NLSBudgetCategory_Materials: { text: 'Materials', description: 'Budget category label for Materials.', surface: 'settings', maxChars: 20 },
  NLSBudgetCategory_Travel: { text: 'Travel', description: 'Budget category label for Travel.', surface: 'settings', maxChars: 20 },
  NLSBudgetCategory_Vendors: { text: 'Vendors', description: 'Budget category label for Vendors.', surface: 'settings', maxChars: 20 },
  NLSBudgetCategory_Software: { text: 'Software', description: 'Budget category label for Software.', surface: 'settings', maxChars: 20 },
  NLSBudgetCategory_Other: { text: 'Other', description: 'Budget category label for Other.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsModulesReadOnlyNotice: { text: 'Requires Edit workspace settings permission.', description: 'Notice for users who cannot edit workspace modules.', surface: 'settings', maxChars: 60 },
  NLSWorkspaceModulesNeedsStock: { text: 'Needs Stock', description: 'Short badge/hint when a helper tool requires Stock module.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModulesNeedsForms: { text: 'Needs Forms', description: 'Short badge/hint when a helper tool requires Forms module.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSectionRecurringRetention: { text: 'Recurring retention', description: 'Section title for recurring checklist retention.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionRecurringRetentionDesc: { text: 'Control how Daily and Weekly checklist history is retained and archived.', description: 'Description for recurring retention section.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsSectionBlockedCards: { text: 'Blocked cards', description: 'Section title for blocked todo policy.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionBlockedCardsDesc: { text: 'Choose what Todo actions stay available while dependencies are still open.', description: 'Description for blocked cards section.', surface: 'settings', maxChars: 90 },
  NLSBoardSettingsChecklistsReadOnlyNotice: { text: 'Requires Edit workspace settings permission.', description: 'Notice for users who cannot edit checklist settings.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsChecklistsUnavailableNotice: { text: 'Recurring settings are disabled for public preview workspaces.', description: 'Notice that recurring settings are unavailable in preview.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsRetentionModeLabel: { text: 'Retention mode', description: 'Label for retention mode select.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsRetentionModeHint: { text: 'Free workspaces prune older occurrence state. Subscribers can keep full history.', description: 'Hint for retention mode.', surface: 'settings', maxChars: 90 },
  NLSBoardSettingsPruneDaysLabel: { text: 'Prune occurrence history after', description: 'Label for prune days stepper.', surface: 'settings', maxChars: 35 },
  NLSBoardSettingsPruneDaysHint: { text: 'Older completion records are eligible for cleanup after this many days.', description: 'Hint for prune days stepper.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsMaterializeLabel: { text: 'Materialize upcoming', description: 'Label for materialize upcoming pages stepper.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsMaterializeHint: { text: 'Fixed service window: today plus the next two Daily or Weekly pages.', description: 'Hint for materialize upcoming stepper.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsArchiveThresholdLabel: { text: 'Archive completed after', description: 'Label for archive completed windows stepper.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsArchiveThresholdHint: { text: 'Windows this far behind today render grayed and read-only. Range 1-5.', description: 'Hint for archive completed stepper.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsRecurringSettingsSaved: { text: 'Recurring checklist settings updated.', description: 'Toast when recurring checklist settings are updated.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsRecurringSettingsSaveFailed: { text: 'Could not update recurring checklist settings.', description: 'Toast when recurring checklist settings update fails.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsSubscriberOnlyBenefit: { text: 'Keeping history forever is a subscriber-only benefit.', description: 'Message when a free workspace tries to keep history forever.', surface: 'settings', maxChars: 65 },
  NLSBoardSettingsSectionLimits: { text: 'Limits', description: 'Section title for board limits.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsLimitsModeEditable: { text: 'Editable up to plan max', description: 'Subtitle mode for editable limits.', surface: 'settings', maxChars: 35 },
  NLSBoardSettingsLimitsModePersonalLocked: { text: 'Locked for personal workspaces', description: 'Subtitle mode for locked personal limits.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsLimitsModeReadOnly: { text: 'Read only', description: 'Subtitle mode for read-only limits.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSameLimitsLabel: { text: 'Same limits for all boards', description: 'Toggle label for same limits across boards.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsSameLimitsHint: { text: 'Apply the same limits across all timeline and board views.', description: 'Hint for same limits toggle.', surface: 'settings', maxChars: 65 },
  NLSBoardSettingsBoardPickerLabel: { text: 'Board', description: 'Label for board picker select.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsLimitPlanMaxHint: { text: '{suffix} / plan max {max}', description: 'Hint showing limit suffix and plan maximum.', surface: 'settings', maxChars: 35 },
  NLSBoardSettingsLimitUnlimited: { text: 'unlimited', description: 'Label for unlimited limit maximum in plan max hint.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsLimitsSaved: { text: 'Workspace limits updated.', description: 'Toast when workspace limits are saved.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsLimitsSaveFailed: { text: 'Could not update workspace limits.', description: 'Toast when workspace limits save fails.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsRetentionPruneAfterN: { text: 'Prune after N days', description: 'Retention mode option to prune after N days.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsRetentionKeepForever: { text: 'Keep forever', description: 'Retention mode option to keep forever.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsRetentionKeepForeverSubscribersOnly: { text: 'Keep forever (subscribers only)', description: 'Retention mode option for subscribers only.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsPruneDaysAria: { text: 'Prune recurring occurrence history after days', description: 'Accessible label for prune days stepper.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsMaterializeAria: { text: 'Materialized upcoming recurring pages', description: 'Accessible label for materialize upcoming stepper.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsArchiveThresholdAria: { text: 'Archive completed recurring windows after', description: 'Accessible label for archive completed windows stepper.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsBlockedActionAllowCompletionLabel: { text: 'Allow marking done', description: 'Blocked action label: allow completion.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsBlockedActionAllowCompletionDetail: { text: 'Members can complete cards before dependencies close.', description: 'Blocked action detail: allow completion.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsBlockedActionAllowProofLabel: { text: 'Allow proof upload', description: 'Blocked action label: allow proof upload.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsBlockedActionAllowProofDetail: { text: 'Members can attach evidence while the card is blocked.', description: 'Blocked action detail: allow proof upload.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsBlockedActionAllowStatusChangeLabel: { text: 'Allow status changes', description: 'Blocked action label: allow status changes.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsBlockedActionAllowStatusChangeDetail: { text: 'Members can move blocked cards across workflow states.', description: 'Blocked action detail: allow status changes.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsBlockedActionAllowCommentLabel: { text: 'Allow comments', description: 'Blocked action label: allow comments.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsBlockedActionAllowCommentDetail: { text: 'Members can keep discussion open on blocked cards.', description: 'Blocked action detail: allow comments.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsRecurringPermissionDenied: { text: 'You do not have permission to initialize recurring settings.', description: 'Error when lacking permission to bootstrap recurring settings.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsRecurringInitFailed: { text: 'Could not initialize recurring settings.', description: 'Error when recurring settings bootstrap fails.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsRecurringLoadFailed: { text: 'Could not load recurring settings.', description: 'Error when loading recurring settings fails.', surface: 'settings', maxChars: 50 },

  // --- Settings Redesign P11: Forms, Automation, Games / Raid ---
  NLSBoardSettingsFormsComingSoonTitle: { text: 'Forms settings are coming soon', description: 'Title for Forms coming soon card in Board settings.', surface: 'settings', maxChars: 45 },
  NLSBoardSettingsFormsComingSoonBody: { text: "Soon you'll set how long responses are kept, sharing defaults, and where responses are sent.", description: 'Body for Forms coming soon card.', surface: 'settings', maxChars: 120 },
  NLSBoardSettingsFormsComingSoonBullet1: { text: 'Response retention and archive policy', description: 'Bullet 1 for Forms coming soon.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsFormsComingSoonBullet2: { text: 'Workspace sharing and anonymous access defaults', description: 'Bullet 2 for Forms coming soon.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsFormsComingSoonBullet3: { text: 'Webhook routing and downstream delivery', description: 'Bullet 3 for Forms coming soon.', surface: 'settings', maxChars: 50 },

  NLSBoardSettingsSectionAutomationAccess: { text: 'Access by role', description: 'Section title for automation access matrix.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionAutomationAccessDesc: { text: 'Automate access is role-gated. Manage roles from the Members section.', description: 'Description for automation access matrix.', surface: 'settings', maxChars: 90 },
  NLSBoardSettingsAutomationTableRole: { text: 'Role', description: 'Column header for Role in automation access table.', surface: 'settings', maxChars: 15 },
  NLSBoardSettingsAutomationTableView: { text: 'View', description: 'Column header for View in automation access table.', surface: 'settings', maxChars: 15 },
  NLSBoardSettingsAutomationTableManage: { text: 'Manage', description: 'Column header for Manage in automation access table.', surface: 'settings', maxChars: 15 },
  NLSBoardSettingsAutomationTableAria: { text: 'Automation access by role', description: 'Accessible label for automation access table.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsAutomationChangeRoles: { text: 'Change role capabilities', description: 'Button to jump to Members settings from Automation.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsAutomationOpenChecklists: { text: 'Checklist settings', description: 'Button to jump to Checklists settings from Automation.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsAutomationComingSoonTitle: { text: 'Runtime & managed automations', description: 'Title for runtime automations note.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsAutomationComingSoonBody: { text: 'Worker snapshots, bulk pause/activate, and managed recurring rollers live in the Automate hub.', description: 'Body for runtime automations note.', surface: 'settings', maxChars: 120 },
  NLSBoardSettingsAutomationComingSoonBullet1: { text: 'Live workflow counts and worker status in Automate', description: 'Bullet 1 for runtime automations.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsAutomationComingSoonBullet2: { text: 'Recurring checklist rollers governed from Checklists', description: 'Bullet 2 for runtime automations.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsAutomationComingSoonBullet3: { text: 'Workspace-wide status flips in Automate settings', description: 'Bullet 3 for runtime automations.', surface: 'settings', maxChars: 60 },

  NLSBoardSettingsSectionRaidBlueprint: { text: 'Blueprint', description: 'Section title for Raid blueprint.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSectionRaidBlueprintDesc: { text: 'Formula and damage multipliers shown in Raid before a boss is forged.', description: 'Description for Raid blueprint section.', surface: 'settings', maxChars: 85 },
  NLSBoardSettingsSectionRaidRewards: { text: 'Rewards', description: 'Section title for Raid rewards.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSectionRaidRewardsDesc: { text: 'Reward distribution and stakes defaults for new raids.', description: 'Description for Raid rewards section.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsSectionRaidDefaults: { text: 'Defaults', description: 'Section title for Raid defaults.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSectionRaidDefaultsDesc: { text: 'Default schedule, notifications, and arena settings.', description: 'Description for Raid defaults section.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsSectionRaidPreview: { text: 'Preview', description: 'Section title for Raid preview.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSectionRaidPreviewDesc: { text: 'Sample boss stats and arena view availability.', description: 'Description for Raid preview section.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsRaidRestoreDefaults: { text: 'Restore defaults', description: 'Button text to restore raid blueprint defaults.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsRaidTaskHpMultiplier: { text: 'Task HP multiplier', description: 'Label for task HP multiplier slider.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsRaidTaskHpMultiplierHint: { text: 'Task count times target weight times this value.', description: 'Hint for task HP multiplier.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsRaidChecklistHpMultiplier: { text: 'Checklist HP multiplier', description: 'Label for checklist HP multiplier slider.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsRaidChecklistHpMultiplierHint: { text: 'Nested checklist count times this value.', description: 'Hint for checklist HP multiplier.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsRaidChecklistDamage: { text: 'Checklist damage', description: 'Label for checklist damage stepper.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsRaidChecklistDamageHint: { text: 'Preview damage for completed checklist items.', description: 'Hint for checklist damage.', surface: 'settings', maxChars: 55 },
  NLSBoardSettingsRaidTaskDamage: { text: 'Task damage', description: 'Label for task damage stepper.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsRaidTaskDamageHint: { text: 'Preview damage for completed tasks.', description: 'Hint for task damage.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsRaidFocusDamage: { text: 'Focus damage', description: 'Label for focus damage stepper.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsRaidFocusDamageHint: { text: 'Preview damage for completed focus blocks.', description: 'Hint for focus damage.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsRaidDefaultEnrageDays: { text: 'Enrage in', description: 'Label for default enrage days stepper.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsRaidDefaultEnrageDaysHint: { text: 'Default number of days until the boss enrages.', description: 'Hint for default enrage days.', surface: 'settings', maxChars: 55 },
  NLSBoardSettingsRaidDefaultFunding: { text: 'Funding', description: 'Label for default funding credits stepper.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsRaidDefaultFundingHint: { text: 'Default credit pool committed by the organizer.', description: 'Hint for default funding.', surface: 'settings', maxChars: 55 },
  NLSBoardSettingsRaidRewardSplit: { text: 'Reward split', description: 'Label for reward split select.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsRaidRewardSplitHint: { text: 'Preview mode shown in the Raid forge.', description: 'Hint for reward split select.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsRaidAutoPenalty: { text: 'Auto-arm high-stakes penalty', description: 'Label for auto-penalty toggle.', surface: 'settings', maxChars: 35 },
  NLSBoardSettingsRaidAutoPenaltyHint: { text: 'Targets with weight 6 or higher start as penalty raids.', description: 'Hint for auto-penalty toggle.', surface: 'settings', maxChars: 65 },
  NLSBoardSettingsRaidDefaultView: { text: 'Default arena', description: 'Label for default arena view select.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsRaidDefaultViewHint: { text: 'Initial arena selected for newly forged raids.', description: 'Hint for default arena select.', surface: 'settings', maxChars: 55 },
  NLSBoardSettingsRaidNotifyOnStart: { text: 'Notify on Raid start', description: 'Label for notify on start toggle.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsRaidNotifyOnStartHint: { text: 'Announce newly forged raids to the workspace.', description: 'Hint for notify on start toggle.', surface: 'settings', maxChars: 55 },
  NLSBoardSettingsRaidSampleHp: { text: 'Sample HP from 8 nested tasks, 14 weight mass, and 5 checklists.', description: 'Sample HP preview note.', surface: 'settings', maxChars: 75 },
  NLSBoardSettingsRaidRewardPreview: { text: 'Reward split preview. Server payout remains registry-authoritative.', description: 'Reward split preview note.', surface: 'settings', maxChars: 75 },
  NLSBoardSettingsRaidArenaDefault: { text: 'Default arena view.', description: 'Hint when an arena view is default.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsRaidArenaAvailable: { text: 'Available from the Raid arena switcher.', description: 'Hint when an arena view is available.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsRaidReadOnly: { text: 'Requires Edit workspace settings permission.', description: 'Notice for users who cannot edit raid settings.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsRaidPreviewUnavailable: { text: 'Raid blueprint tuning is disabled for public preview workspaces.', description: 'Notice for preview workspaces.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsRaidSaved: { text: 'Raid blueprint saved.', description: 'Status text when raid blueprint is saved.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsRaidSaveFailed: { text: 'Could not save raid blueprint.', description: 'Error text when raid blueprint save fails.', surface: 'settings', maxChars: 40 },

  // --- Settings Redesign P12: Members & roles, Notifications ---
  NLSBoardSettingsSectionCustomRoles: { text: 'Custom roles', description: 'Section title for custom roles.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionCustomRolesDesc: { text: 'Create role templates and assign workspace capabilities.', description: 'Description for custom roles section.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsSectionPermissions: { text: 'Permissions', description: 'Section title for workspace permissions.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionPermissionsDesc: { text: 'Review and manage member-level permissions.', description: 'Description for permissions section.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsSectionHelperVisibility: { text: 'Helper visibility', description: 'Section title for helper visibility.', surface: 'settings', maxChars: 24 },
  NLSBoardSettingsSectionHelperVisibilityDesc: { text: 'Control which helpers can see masked workspace data.', description: 'Description for helper visibility section.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsCustomRoleEditAria: { text: 'Edit {name}', description: 'Accessible label for custom role edit button.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsCustomRoleDeleteAria: { text: 'Delete {name}', description: 'Accessible label for custom role delete button.', surface: 'settings', maxChars: 60 },

  // --- Settings Redesign P12 Fix1: Custom roles, Permission manager, Notifications ---
  NLSBoardSettingsCustomRolesDropdownLabel: { text: 'Custom roles', description: 'Eyebrow label for custom roles header.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsCustomRolesHeading: { text: 'Design your own workspace roles', description: 'Heading for custom roles header.', surface: 'settings', maxChars: 45 },
  NLSBoardSettingsCustomRolesLead: { text: 'Each custom role inherits from a base role and can tighten permissions per action. A custom role never grants more power than its base.', description: 'Lead description for custom roles.', surface: 'settings', maxChars: 160 },
  NLSBoardSettingsCustomRolesQuotaUpgrade: { text: 'Upgrade to unlock custom roles.', description: 'Quota hint when plan has no custom roles.', surface: 'settings', maxChars: 45 },
  NLSBoardSettingsCustomRolesQuotaUnlimited: { text: 'Unlimited on your plan.', description: 'Quota hint when plan has unlimited custom roles.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsCustomRolesQuotaUsed: { text: 'Custom roles used on your plan.', description: 'Quota hint showing used count on plan.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsCustomRolesNewRoleBtn: { text: 'New Custom Role', description: 'Button text to create a new custom role.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsCustomRolesRequiresAccess: { text: 'Requires Configure permissions access.', description: 'Empty state title when user lacks permissions for custom roles.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsCustomRolesRequiresAccessDesc: { text: 'Custom workspace roles can only be viewed and configured by members with manage permissions access.', description: 'Empty state description when user lacks permissions for custom roles.', surface: 'settings', maxChars: 120 },
  NLSBoardSettingsCustomRolesUpsellTitle: { text: 'Custom roles are a paid workspace feature.', description: 'Paid feature note title in custom roles.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsCustomRolesUpsellDesc: { text: 'Essential unlocks 3, Growth 5, Professional 10, Enterprise unlimited.', description: 'Paid feature tier quotas description.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsCustomRoleInheritsFromOne: { text: 'Inherits from {base} · 1 override', description: 'Inheritance and single override count.', surface: 'settings', maxChars: 45 },
  NLSBoardSettingsCustomRoleInheritsFromMany: { text: 'Inherits from {base} · {count} overrides', description: 'Inheritance and multiple overrides count.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsCustomRoleEditorHeading: { text: 'Custom role editor', description: 'Heading for sidecar custom role editor.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsCustomRoleEmptyHint: { text: 'No custom roles yet. Click + New Custom Role to design one.', description: 'Empty state hint in custom roles editor.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsCustomRolePickHint: { text: 'Pick a custom role to edit its base + per-action overrides.', description: 'Hint instructing to pick a role to edit.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsCustomRoleDeleteTitle: { text: 'Delete role', description: 'Tooltip title for delete role button.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsCustomRoleEditHeading: { text: 'Edit custom role', description: 'Heading when editing an existing role.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsCustomRoleNewHeading: { text: 'New custom role', description: 'Heading when creating a new custom role.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsCustomRoleOverridesCountOne: { text: '1 override', description: 'Override count pill text for single override.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsCustomRoleOverridesCountMany: { text: '{count} overrides', description: 'Override count pill text for multiple overrides.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsCustomRoleFieldName: { text: 'Name', description: 'Label for role name field.', surface: 'settings', maxChars: 15 },
  NLSBoardSettingsCustomRoleNamePlaceholder: { text: 'Contractor, Auditor, QA Reviewer…', description: 'Placeholder for role name input.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsCustomRoleFieldBaseRole: { text: 'Base role', description: 'Label for base role select.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsCustomRoleFieldColor: { text: 'Color', description: 'Legend for color picker fieldset.', surface: 'settings', maxChars: 15 },
  NLSBoardSettingsCustomRoleColorAria: { text: 'Color {color}', description: 'Accessible label for role color option.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsCustomRoleInheritHint: { text: 'Leave an action on Inherit to use the workspace default for the base role. Overrides can only raise the required role for this custom role — they never lower it.', description: 'Hint explaining inheritance behavior in custom role editor.', surface: 'settings', maxChars: 170 },
  NLSBoardSettingsCustomRoleOverrideActionAria: { text: 'Override {label}', description: 'Accessible label for action override select.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsCustomRoleOptionInherit: { text: 'Inherit', description: 'Option to inherit action permission from base role.', surface: 'settings', maxChars: 15 },
  NLSBoardSettingsCustomRoleCancelBtn: { text: 'Cancel', description: 'Cancel button label in custom role editor.', surface: 'settings', maxChars: 15 },
  NLSBoardSettingsCustomRoleSaveChangesBtn: { text: 'Save changes', description: 'Save button label when updating a custom role.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsCustomRoleCreateRoleBtn: { text: 'Create role', description: 'Create button label when creating a custom role.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsCustomRoleSignInRequired: { text: 'Sign in to update custom roles.', description: 'Error message when signed out.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsCustomRoleNameRequired: { text: 'Custom role name is required.', description: 'Validation error when role name is empty.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsCustomRoleNameTooLong: { text: 'Name must be {max} characters or fewer.', description: 'Validation error when role name exceeds length limit.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsCustomRoleUpdated: { text: 'Custom role updated.', description: 'Success message after updating custom role.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsCustomRoleCreated: { text: 'Custom role created.', description: 'Success message after creating custom role.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsCustomRoleSaveFailed: { text: 'Could not save custom role.', description: 'Error message when saving custom role fails.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsCustomRoleRemoved: { text: 'Custom role removed. Members assigned to it fall back to their base role.', description: 'Success message after deleting custom role.', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsCustomRoleRemoveFailed: { text: 'Could not remove role.', description: 'Error message when deleting custom role fails.', surface: 'settings', maxChars: 30 },

  NLSBoardSettingsPermManagerSignInRequired: { text: 'Sign in to update workspace permissions.', description: 'Error message when updating permissions while signed out.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsPermManagerWorkspaceNotFound: { text: 'Workspace not found.', description: 'Error message when workspace document is missing.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsPermManagerUpdated: { text: 'Workspace permissions updated.', description: 'Success banner message when permissions are updated.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsPermManagerSaveFailed: { text: 'Could not save permissions.', description: 'Error banner message when saving permissions fails.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsPermManagerDropdownLabel: { text: 'Permission manager', description: 'Eyebrow dropdown label for permission manager header.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsPermManagerHeading: { text: 'Workspace action policy', description: 'Main heading for workspace permission manager.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsPermManagerLead: { text: 'Choose the minimum role required for every action. The workspace owner always retains full access.', description: 'Lead description for workspace permission policy.', surface: 'settings', maxChars: 110 },
  NLSBoardSettingsPermManagerRoleAria: { text: 'Minimum role for {label}', description: 'Accessible label for permission role select.', surface: 'settings', maxChars: 40 },

  NLSBoardSettingsNotificationsStandaloneHint: { text: 'Workspace defaults remain available to administrators; personal delivery choices are enforced afterward.', description: 'Hint in standalone notification settings panel.', surface: 'settings', maxChars: 120 },
  NLSBoardSettingsNotificationsSaving: { text: 'Saving...', description: 'Busy state for saving notification preferences.', surface: 'settings', maxChars: 15 },
  NLSBoardSettingsNotificationsSavePolicy: { text: 'Save workspace policy', description: 'Button text in standalone notification settings panel.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsNotificationsAudienceAria: { text: '{audience} workspace notifications', description: 'Accessible label for audience notification checkbox.', surface: 'settings', maxChars: 50 },

  NLSBoardSettingsSectionNotifications: { text: 'Who gets workspace notifications', description: 'Section title for workspace notification recipients.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsSectionNotificationsDesc: { text: "Master switch per audience. Workspace defaults remain available to administrators; each member's personal delivery choices are enforced afterward.", description: 'Description for workspace notifications section.', surface: 'settings', maxChars: 160 },
  NLSBoardSettingsAudienceAdmin: { text: 'Admins', description: 'Label for Admins notification audience row.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAudienceAdminHint: { text: 'Administrators with full workspace management access.', description: 'Hint for Admins notification row.', surface: 'settings', maxChars: 65 },
  NLSBoardSettingsAudienceEditor: { text: 'Editors', description: 'Label for Editors notification audience row.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAudienceEditorHint: { text: 'Members with edit and contribute permissions.', description: 'Hint for Editors notification row.', surface: 'settings', maxChars: 65 },
  NLSBoardSettingsAudienceViewer: { text: 'Viewers', description: 'Label for Viewers notification audience row.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAudienceViewerHint: { text: 'Members with read-only board and document access.', description: 'Hint for Viewers notification row.', surface: 'settings', maxChars: 65 },
  NLSBoardSettingsAudienceReadonly: { text: 'Read-only members', description: 'Label for Read-only members notification audience row.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsAudienceReadonlyHint: { text: 'Members restricted to viewing without interaction.', description: 'Hint for Read-only notification row.', surface: 'settings', maxChars: 65 },
  NLSBoardSettingsAudienceAllMembers: { text: 'Everyone', description: 'Label for Everyone notification audience row.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAudienceAllMembersHint: { text: 'All workspace members regardless of assigned role.', description: 'Hint for Everyone notification row.', surface: 'settings', maxChars: 65 },
  NLSBoardSettingsNotificationsRequiresAccess: { text: 'Requires Edit workspace settings access.', description: 'Empty state title when user cannot edit notification settings.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsNotificationsRequiresAccessDesc: { text: 'Workspace notification settings can only be edited by administrators.', description: 'Empty state description when user cannot edit notification settings.', surface: 'settings', maxChars: 80 },

  // Settings Redesign PC1: Board › Modules & Hubs
  NLSBoardSettingsBudgetApprovalThresholdLabel: { text: 'Approval needed above', description: 'Label for expense approval threshold setting row.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsBudgetApprovalThresholdHint: { text: 'Expenses above this amount queue for admin approval regardless of role. Leave empty for helper-only.', description: 'Hint for expense approval threshold setting row.', surface: 'settings', maxChars: 110 },
  NLSBoardSettingsBudgetApprovalThresholdPlaceholder: { text: 'e.g. 200.00', description: 'Placeholder for expense approval threshold number input.', surface: 'settings', maxChars: 15 },
  NLSBoardSettingsBudgetApprovalThresholdInvalid: { text: 'Enter a valid non-negative amount.', description: 'Validation error when budget approval threshold input is invalid.', surface: 'settings', maxChars: 50 },
  NLSBoardSettingsBudgetApprovalThresholdSavedToast: { text: 'Approval threshold updated.', description: 'Toast when expense approval threshold is saved.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsSectionQuotaCoaching: { text: 'Quota coaching', description: 'Section title for quota coaching nudges and policies.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsSectionQuotaCoachingDesc: { text: 'Automated coaching nudges and absence policies when members approach or meet quotas.', description: 'Description for quota coaching section.', surface: 'settings', maxChars: 100 },
  NLSBoardSettingsQuotaCoachingSixtyLabel: { text: '60% nudge', description: 'Label for 60 percent quota nudge template.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsQuotaCoachingEightyLabel: { text: '80% nudge', description: 'Label for 80 percent quota nudge template.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsQuotaCoachingNearMissLabel: { text: 'Near miss', description: 'Label for near miss quota nudge template.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsQuotaCoachingMetLabel: { text: 'Quota met', description: 'Label for quota met template.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsQuotaExtendOnResumeLabel: { text: 'Extend on resume', description: 'Label for auto-extend quota on resume toggle.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsQuotaExtendOnResumeHint: { text: 'Auto-extend quota end date by pause duration upon resume.', description: 'Hint for auto-extend quota on resume toggle.', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsQuotaCoachingSavedToast: { text: 'Coaching templates and absence policy saved.', description: 'Toast when quota coaching templates are saved.', surface: 'settings', maxChars: 55 },
  NLSBudgetHubSettingsChipLabel: { text: 'Budget settings', description: 'Label for link chip to Board budget settings.', surface: 'settings', maxChars: 25 },
  NLSQuotaHubSettingsChipLabel: { text: 'Quota settings', description: 'Label for link chip to Board quota settings.', surface: 'settings', maxChars: 25 },

  // Settings Redesign PC2: Board › Governance & data (Audit ledger)
  NLSBoardSettingsSectionAuditLedger: { text: 'Audit ledger', description: 'Section title for audit ledger governance.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSectionAuditLedgerDesc: { text: 'Retention policy, monitored categories, and ledger purge controls.', description: 'Description for audit ledger section.', surface: 'settings', maxChars: 90 },
  NLSBoardSettingsAuditRetentionLabel: { text: 'Retention policy', description: 'Label for retention policy setting row.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsAuditRetentionStandard: { text: 'Standard (48 hours)', description: 'Option for standard 48-hour audit retention.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsAuditRetentionFull: { text: 'Full audit retention', description: 'Option for full tier audit retention.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsAuditRetentionFullDisabled: { text: 'Full audit retention requires a tier upgrade.', description: 'Hint when full audit retention is unavailable.', surface: 'settings', maxChars: 60 },
  NLSBoardSettingsAuditCategoriesLabel: { text: 'Monitored audit categories', description: 'Label for audit category checkboxes row.', surface: 'settings', maxChars: 35 },
  NLSBoardSettingsAuditCategoriesHint: { text: 'Choose which activity categories are recorded in the immutable audit trail.', description: 'Hint for audit category checkboxes row.', surface: 'settings', maxChars: 90 },
  NLSBoardSettingsAuditResetMinimal: { text: 'Reset to minimal', description: 'Button to reset audit categories to minimal compliance.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsAuditPurgeNow: { text: 'Purge standard logs now', description: 'Button to trigger standard audit log purge.', surface: 'settings', maxChars: 30 },
  NLSBoardSettingsAuditLifecycleAlertLabel: { text: 'Lifecycle alert', description: 'Label for lifecycle alert toggle row.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsAuditLifecycleAlertHint: { text: 'Send administrative alerts when audit entries approach the retention threshold.', description: 'Hint for lifecycle alert toggle row.', surface: 'settings', maxChars: 90 },
  NLSBoardSettingsAuditConfirmStandardTitle: { text: 'Switch to Standard Retention?', description: 'Confirm modal title when switching to standard retention.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsAuditConfirmStandardBody: { text: 'Switching to Standard retention will purge audit logs older than 48 hours. This action cannot be undone.', description: 'Confirm modal body when switching to standard retention.', surface: 'settings', maxChars: 120 },
  NLSBoardSettingsAuditConfirmStandardAction: { text: 'Switch and purge', description: 'Confirm action button label when switching to standard retention.', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsAuditConfirmPurgeTitle: { text: 'Purge standard audit logs now?', description: 'Confirm modal title when purging standard audit logs.', surface: 'settings', maxChars: 40 },
  NLSBoardSettingsAuditConfirmPurgeBody: { text: 'This will permanently delete standard temporary audit events beyond the retention window. This action cannot be undone.', description: 'Confirm modal body when purging standard audit logs.', surface: 'settings', maxChars: 140 },
  NLSBoardSettingsAuditConfirmPurgeAction: { text: 'Purge standard logs', description: 'Confirm action button label when purging standard audit logs.', surface: 'settings', maxChars: 25 },
  NLSAuditHubGovernanceChipLabel: { text: 'Governance settings', description: 'Label for link chip to Board governance settings.', surface: 'settings', maxChars: 30 },

  // Audit Category Labels & Descriptions (mapped through NLS)
  NLSBoardSettingsAuditCatSecurityLabel: { text: 'Security', description: 'Audit category label: Security', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAuditCatSecurityDesc: { text: 'Security anomalies, gates, and governance protection events.', description: 'Audit category desc: Security', surface: 'settings', maxChars: 70 },
  NLSBoardSettingsAuditCatBillingLabel: { text: 'Billing', description: 'Audit category label: Billing', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAuditCatBillingDesc: { text: 'Credit, wallet, subscription, stock, budget, and paid action events.', description: 'Audit category desc: Billing', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsAuditCatRoleChangesLabel: { text: 'Role changes', description: 'Audit category label: Role changes', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAuditCatRoleChangesDesc: { text: 'Member roles, permissions, invite claims, and access policy changes.', description: 'Audit category desc: Role changes', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsAuditCatContentChangesLabel: { text: 'Content changes', description: 'Audit category label: Content changes', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAuditCatContentChangesDesc: { text: 'Timeline, checklist, forms, meetings, uploads, proof, and workspace content changes.', description: 'Audit category desc: Content changes', surface: 'settings', maxChars: 95 },
  NLSBoardSettingsAuditCatIntegrationsLabel: { text: 'Integrations', description: 'Audit category label: Integrations', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAuditCatIntegrationsDesc: { text: 'Provider connections, revocations, syncs, and readiness gate events.', description: 'Audit category desc: Integrations', surface: 'settings', maxChars: 80 },
  NLSBoardSettingsAuditCatAiAutomationLabel: { text: 'AI and automation', description: 'Audit category label: AI and automation', surface: 'settings', maxChars: 25 },
  NLSBoardSettingsAuditCatAiAutomationDesc: { text: 'AI proposals, Pulse summaries, automation runs, and automation failures.', description: 'Audit category desc: AI and automation', surface: 'settings', maxChars: 85 },
  NLSBoardSettingsAuditCatExportsLabel: { text: 'Exports', description: 'Audit category label: Exports', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsAuditCatExportsDesc: { text: 'Audit exports, report generation, and raw CSV export requests.', description: 'Audit category desc: Exports', surface: 'settings', maxChars: 75 },

  // Settings Redesign PC4: Duplicates & Chips
  NLSBoardSettingsSectionOwners: { text: 'Owners', description: 'Section title for workspace owners in Board settings.', surface: 'settings', maxChars: 20 },
  NLSBoardSettingsSectionOwnersDesc: { text: 'Owners can manage team admins, roles, settings, and ownership.', description: 'Description for owners section.', surface: 'settings', maxChars: 75 },
  NLSAutomateNavToneSettings: { text: 'Runtime health & controls', description: 'Nav tone for Automate settings section.', surface: 'workbench', maxChars: 30 },
  NLSAutomateStageHeadingSettings: { text: 'Runtime health & controls', description: 'Stage heading for Automate settings section.', surface: 'workbench', maxChars: 30 },
  NLSAutomateStageDescSettings: { text: 'Runtime health, managed companion automations, and bulk workspace controls.', description: 'Stage description for Automate settings section.', surface: 'workbench', maxChars: 90 },
  NLSAutomateSettingsChipLabel: { text: 'Automation settings', description: 'Label for link chip to Board automation settings.', surface: 'settings', maxChars: 25 },
  NLSFormsSettingsChipLabel: { text: 'Forms settings', description: 'Label for link chip to Board forms settings.', surface: 'settings', maxChars: 25 },


  // Visibility mask field labels
  NLSVisibilityMaskField_title: { text: 'Title', description: 'Helper visibility field: Title', surface: 'settings', maxChars: 20 },
  NLSVisibilityMaskField_description: { text: 'Description', description: 'Helper visibility field: Description', surface: 'settings', maxChars: 25 },
  NLSVisibilityMaskField_completionCriteria: { text: 'Completion criteria', description: 'Helper visibility field: Completion criteria', surface: 'settings', maxChars: 30 },
  NLSVisibilityMaskField_priorityChips: { text: 'Priority chips (urgency / impact / effort)', description: 'Helper visibility field: Priority chips', surface: 'settings', maxChars: 50 },
  NLSVisibilityMaskField_urgencyStars: { text: 'Urgency stars', description: 'Helper visibility field: Urgency stars', surface: 'settings', maxChars: 25 },
  NLSVisibilityMaskField_importanceStars: { text: 'Importance stars', description: 'Helper visibility field: Importance stars', surface: 'settings', maxChars: 25 },
  NLSVisibilityMaskField_complexityStars: { text: 'Complexity stars', description: 'Helper visibility field: Complexity stars', surface: 'settings', maxChars: 25 },
  NLSVisibilityMaskField_weightage: { text: 'Weightage', description: 'Helper visibility field: Weightage', surface: 'settings', maxChars: 20 },
  NLSVisibilityMaskField_scheduleWindow: { text: 'Schedule dates', description: 'Helper visibility field: Schedule dates', surface: 'settings', maxChars: 25 },
  NLSVisibilityMaskField_timeWindow: { text: 'Time-of-day window', description: 'Helper visibility field: Time-of-day window', surface: 'settings', maxChars: 30 },
  NLSVisibilityMaskField_estimatedHours: { text: 'Estimated hours', description: 'Helper visibility field: Estimated hours', surface: 'settings', maxChars: 25 },
  NLSVisibilityMaskField_actualCost: { text: 'Actual cost', description: 'Helper visibility field: Actual cost', surface: 'settings', maxChars: 20 },
  NLSVisibilityMaskField_currencyCode: { text: 'Currency code', description: 'Helper visibility field: Currency code', surface: 'settings', maxChars: 20 },
  NLSVisibilityMaskField_requiresProofFile: { text: 'Requires proof file', description: 'Helper visibility field: Requires proof file', surface: 'settings', maxChars: 30 },
  NLSVisibilityMaskField_requiresLocationPin: { text: 'Requires location pin', description: 'Helper visibility field: Requires location pin', surface: 'settings', maxChars: 30 },
  NLSVisibilityMaskField_targetCoordinates: { text: 'Target coordinates', description: 'Helper visibility field: Target coordinates', surface: 'settings', maxChars: 30 },
  NLSVisibilityMaskField_dependencies: { text: 'Dependencies & blockers', description: 'Helper visibility field: Dependencies & blockers', surface: 'settings', maxChars: 30 },
  NLSVisibilityMaskField_commentsCount: { text: 'Comment count', description: 'Helper visibility field: Comment count', surface: 'settings', maxChars: 25 },
  NLSVisibilityMaskField_workflowStatus: { text: 'Workflow status chip', description: 'Helper visibility field: Workflow status chip', surface: 'settings', maxChars: 30 },
  NLSVisibilityMaskHeaderTitle: { text: 'Helper visibility — default workspace mask', description: 'Header title for helper visibility mask.', surface: 'settings', maxChars: 50 },
  NLSVisibilityMaskHeaderDescription: { text: "Hidden fields never reach the Helper's phone, even in memory. Per-item overrides on the canvas take precedence.", description: 'Header description for helper visibility mask.', surface: 'settings', maxChars: 140 },
  NLSVisibilityMaskErrorFailed: { text: 'Could not update visibility.', description: 'Error message when visibility update fails.', surface: 'settings', maxChars: 40 },
  NLSVisibilityMaskSubscriptionFailed: { text: 'Could not load visibility settings.', description: 'Error message displayed when loading helper visibility mask fails.', surface: 'settings', maxChars: 50 },
  NLSVisibilityMaskToggleHide: { text: 'HIDE', description: 'Toggle off-label to hide a field in helper visibility mask.', surface: 'settings', maxChars: 10 },
  NLSVisibilityMaskToggleShow: { text: 'SHOW', description: 'Toggle on-label to show a field in helper visibility mask.', surface: 'settings', maxChars: 10 },
  NLSProfileStatusLoading: { text: 'Loading profile...', description: 'Profile loading state.', surface: 'settings', maxChars: 42 },
  NLSProfileStatusUnavailable: { text: 'Profile not available.', description: 'Profile unavailable state.', surface: 'settings', maxChars: 50 },
  NLSProfileStatusNotSet: { text: 'Not set', description: 'Profile missing value fallback.', surface: 'settings', maxChars: 20 },
  NLSProfileStatusPending: { text: 'Pending', description: 'Profile pending label.', surface: 'settings', maxChars: 20 },
  NLSProfileStatusNoSnapshotYet: { text: 'No snapshot yet', description: 'Profile reliability empty summary.', surface: 'settings', maxChars: 34 },
  NLSProfileStatusSaved: { text: 'Profile saved.', description: 'Profile save success.', surface: 'settings', maxChars: 34 },
  NLSProfileStatusCouldNotSave: { text: 'Profile could not be saved.', description: 'Profile save error.', surface: 'settings', maxChars: 60 },
  NLSProfileStatusImageCouldNotUpdate: { text: 'Profile image could not be updated.', description: 'Profile image update error.', surface: 'settings', maxChars: 70 },
  NLSProfileActionPicture: { text: 'Picture', description: 'Profile picture action.', surface: 'settings', maxChars: 20 },
  NLSProfileActionChangePicture: { text: 'Change picture', description: 'Profile change picture action.', surface: 'settings', maxChars: 34 },
  NLSProfileActionEditProfile: { text: 'Edit profile', description: 'Profile edit action.', surface: 'settings', maxChars: 30 },
  NLSProfileActionChange: { text: 'Change', description: 'Profile change action.', surface: 'settings', maxChars: 18 },
  NLSProfileActionSave: { text: 'Save', description: 'Profile save action.', surface: 'settings', maxChars: 16 },
  NLSProfileActionSaving: { text: 'Saving...', description: 'Profile saving action.', surface: 'settings', maxChars: 22 },
  NLSProfileAriaChangePicture: { text: 'Change profile picture', description: 'Change profile picture aria label.', surface: 'settings', maxChars: 46 },
  NLSProfileAriaDetails: { text: 'Profile details', description: 'Profile details aria label.', surface: 'settings', maxChars: 34 },
  NLSProfileAriaSummary: { text: 'Profile summary', description: 'Profile summary aria label.', surface: 'settings', maxChars: 34 },
  NLSProfileHelperProfile: { text: 'Helper profile', description: 'Helper profile eyebrow.', surface: 'settings', maxChars: 34 },
  NLSProfileHelperProfileBody: { text: 'Control the helper identity visible to teams and task owners.', description: 'Helper profile body.', surface: 'settings', maxChars: 90 },
  NLSProfileIdentityConsole: { text: 'Identity Console', description: 'Member profile hero eyebrow.', surface: 'settings', maxChars: 36 },
  NLSProfileLabelDisplayName: { text: 'Display name', description: 'Profile field label.', surface: 'settings', maxChars: 30 },
  NLSProfileLabelReliabilityBadge: { text: 'Reliability badge', description: 'Profile metric label.', surface: 'settings', maxChars: 38 },
  NLSProfileLabelRaidsCompleted: { text: 'Raids completed', description: 'Profile metric label.', surface: 'settings', maxChars: 36 },
  NLSProfileLabelLoginStreak: { text: 'Login streak', description: 'Profile metric label.', surface: 'settings', maxChars: 30 },
  NLSProfileLabelLevelXp: { text: 'Level · XP', description: 'Profile metric label.', surface: 'settings', maxChars: 24 },
  NLSProfileLabelSpinTickets: { text: 'Spin tickets', description: 'Profile metric label.', surface: 'settings', maxChars: 30 },
  NLSProfileLabelReliabilitySignals: { text: 'Reliability signals', description: 'Profile metric label.', surface: 'settings', maxChars: 42 },
  NLSProfileLabelEquippedCosmetics: { text: 'Equipped cosmetics', description: 'Profile cosmetics metric label.', surface: 'settings', maxChars: 44 },
  NLSProfileLabelUsername: { text: 'Username', description: 'Profile field label.', surface: 'settings', maxChars: 24 },
  NLSProfileLabelMobileNumber: { text: 'Mobile number', description: 'Profile field label.', surface: 'settings', maxChars: 34 },
  NLSProfileLabelEmail: { text: 'Email', description: 'Profile field label.', surface: 'settings', maxChars: 20 },
  NLSProfileLabelInternalId: { text: 'Internal ID', description: 'Profile helper internal ID label.', surface: 'settings', maxChars: 28 },
  NLSProfileLabelDisplayPreference: { text: 'Display preference', description: 'Profile display preference label.', surface: 'settings', maxChars: 42 },
  NLSProfileLabelFirstName: { text: 'First name', description: 'Profile field label.', surface: 'settings', maxChars: 28 },
  NLSProfileLabelLastName: { text: 'Last name', description: 'Profile field label.', surface: 'settings', maxChars: 28 },
  NLSProfileLabelAddress: { text: 'Address', description: 'Profile field label.', surface: 'settings', maxChars: 22 },
  NLSProfileLabelEducationNotes: { text: 'Education / Notes', description: 'Profile field label.', surface: 'settings', maxChars: 38 },
  NLSProfileLabelDisplayAs: { text: 'Display as', description: 'Profile display-as field label.', surface: 'settings', maxChars: 28 },
  NLSProfileLabelComputedName: { text: 'Computed name', description: 'Profile computed name label.', surface: 'settings', maxChars: 34 },
  NLSProfileSectionPresenceContact: { text: 'Presence & Contact', description: 'Profile section title.', surface: 'settings', maxChars: 42 },
  NLSProfileSectionPersonalMetadata: { text: 'Personal Metadata', description: 'Profile section title.', surface: 'settings', maxChars: 40 },
  NLSProfileHintUsernamePermanent: { text: 'Permanent account anchor. It cannot be changed.', description: 'Profile username helper text.', surface: 'settings', maxChars: 74 },
  NLSProfileHintContactLinked: { text: 'Linked to sign-in. Use Change to verify and update it safely.', description: 'Profile contact helper text.', surface: 'settings', maxChars: 92 },
  NLSProfileDisplayFirstLast: { text: 'First + last', description: 'Profile display preference option.', surface: 'settings', maxChars: 28 },
  NLSProfileDisplayUsername: { text: 'Username', description: 'Profile display preference option.', surface: 'settings', maxChars: 24 },
  NLSProfileDisplayPhone: { text: 'Phone', description: 'Profile display preference option.', surface: 'settings', maxChars: 18 },
  NLSProfileErrorAddressRequired: { text: 'Address is required.', description: 'Profile validation error.', surface: 'settings', maxChars: 42 },
  NLSProfileDigestLabel: { text: 'Digest', description: 'Profile digest cadence summary prefix.', surface: 'settings', maxChars: 16 },
  NLSProfileDigestDaily: { text: 'Daily', description: 'Daily digest cadence label.', surface: 'settings', maxChars: 12 },
  NLSProfileDigestWeekly: { text: 'Weekly', description: 'Weekly digest cadence label.', surface: 'settings', maxChars: 12 },
  NLSProfileQuietOff: { text: 'Quiet off', description: 'Quiet hours disabled label.', surface: 'settings', maxChars: 16 },
  NLSProfileQuietHours: { text: 'Quiet {start}:00–{end}:00', description: 'Quiet hours active range label.', surface: 'settings', maxChars: 28 },
  NLSProfileChangeInSettings: { text: 'Change in Settings', description: 'Digest strip link to settings.', surface: 'settings', maxChars: 24 },
  NLSProfileTicketSingular: { text: 'ticket', description: 'Spin ticket count singular unit.', surface: 'settings', maxChars: 12 },
  NLSProfileTicketPlural: { text: 'tickets', description: 'Spin ticket count plural unit.', surface: 'settings', maxChars: 12 },
  NLSProfileDefaultTheme: { text: 'Default theme', description: 'Fallback label for equipped theme.', surface: 'settings', maxChars: 24 },
  NLSProfileDefaultCanvas: { text: 'Default canvas', description: 'Fallback label for equipped canvas background.', surface: 'settings', maxChars: 24 },
  NLSProfileRoleLabel: { text: 'Role: {role}', description: 'Profile role preview line. {role} is the role name.', surface: 'settings', maxChars: 36 },
  NLSProfileLevelXpFormat: { text: 'Lv {level} · {current}/{needed} XP', description: 'Level and XP progress format. {level}, {current}, {needed} are numbers.', surface: 'settings', maxChars: 40 },
  NLSFocusModePickerDueLabel: { text: 'Due {due}', description: 'Task due date format in focus picker. {due} is formatted date.', surface: 'settings', maxChars: 30 },
  NLSInvitesHubTitle: { text: 'Invites', description: 'Invites hub title.', surface: 'settings', maxChars: 28 },
  NLSInvitesHubEyebrow: { text: 'Invites Hub', description: 'Invites hub eyebrow.', surface: 'settings', maxChars: 30 },
  NLSInvitesHubBody: { text: 'Members, helpers, and outgoing invitations in one operational shell.', description: 'Invites hub body.', surface: 'settings', maxChars: 90 },
  NLSInvitesAriaMembersCategory: { text: 'Members category', description: 'Invites category navigation aria label.', surface: 'settings', maxChars: 40 },
  NLSInvitesSelectWorkspace: { text: 'Select a workspace to manage invites.', description: 'Invites hub no-workspace state.', surface: 'settings', maxChars: 70 },
  NLSInvitesPersonalCannotInvite: { text: 'Personal workspaces cannot invite', description: 'Invites hub personal workspace title.', surface: 'settings', maxChars: 60 },
  NLSInvitesPersonalCannotInviteBody: {
    text: 'Switch to a Team workspace to manage members, helpers, and invitations.',
    description: 'Invites hub personal workspace body.',
    surface: 'settings',
    maxChars: 90,
  },
  NLSInvitesActionNewMemberInvite: { text: 'New Member Invite', description: 'Invites action.', surface: 'settings', maxChars: 40 },
  NLSInvitesActionNewHelperInvite: { text: 'New Helper Invite', description: 'Invites action.', surface: 'settings', maxChars: 40 },
  NLSInvitesActionManageInvites: { text: 'Manage invites', description: 'Invites mobile action.', surface: 'settings', maxChars: 34 },
  NLSWhatsappInviteAction: {
    text: 'Invite on WhatsApp',
    description: 'Action button to open WhatsApp invite QR and link for a workspace member or helper.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSWhatsappInviteDialogTitle: {
    text: 'Invite on WhatsApp',
    description: 'Title for the dialog displaying the WhatsApp invite QR code and link.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSWhatsappInviteDialogCopy: {
    text: 'Copy link',
    description: 'Button to copy the WhatsApp invite link to clipboard.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSWhatsappInviteDialogShare: {
    text: 'Share',
    description: 'Button to trigger native system share for the WhatsApp invite link.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSWhatsappSendFromMineAction: {
    text: 'Send from my WhatsApp',
    description: 'Action button to open a direct wa.me link to message the person. Needs no Meta account and has no cost.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSWhatsappSendFromMineText: {
    text: 'Hi {name}, this is {sender} from {workspace}.',
    description: 'Pre-filled WhatsApp message sent from the user own WhatsApp client. Needs no Meta account and has no cost.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSWhatsappSlipQrCaption: {
    text: 'Chat with us on WhatsApp',
    description: 'Caption under the WhatsApp QR code printed on join code and learner slips.',
    surface: 'common',
    maxChars: 40,
  },
  NLSWhatsappChipLinked: {
    text: 'WhatsApp linked',
    description: 'Status pill showing person has active WhatsApp link.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSWhatsappChipNotYet: {
    text: 'WhatsApp: not yet',
    description: 'Status pill showing person does not have active WhatsApp link yet.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSWhatsappChipStopped: {
    text: 'WhatsApp: stopped',
    description: 'Status pill showing person has opted out or stopped WhatsApp messages.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduAnnouncementSendWhatsappAction: {
    text: 'Send on WhatsApp',
    description: 'Button on education announcement card to send via WhatsApp to guardians.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAnnouncementSendWhatsappDialogTitle: {
    text: 'Send on WhatsApp',
    description: 'Title for confirmation dialog when sending announcement on WhatsApp.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAnnouncementSendWhatsappDialogConfirm: {
    text: 'Send',
    description: 'Confirm button to send announcement on WhatsApp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAnnouncementSendWhatsappSummaryRecipients: {
    text: 'Sends to {n} guardians',
    description: 'Recipient count estimate line in Send on WhatsApp dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAnnouncementSendWhatsappSummaryCredits: {
    text: 'Sends to {n} guardians · about {credits} credits',
    description: 'Cost and recipient estimate line in Send on WhatsApp dialog.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduAnnouncementSendWhatsappSummaryFree: {
    text: "Free under your plan's cap",
    description: 'Cost line when WhatsApp send is free under monthly plan allowance.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAnnouncementSendWhatsappReasonOff: {
    text: 'WhatsApp is disabled for this workspace.',
    description: 'Reason message when WhatsApp is off in settings.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduAnnouncementSendWhatsappReasonNotifyOff: {
    text: 'Admin announcement sends are turned off in settings.',
    description: 'Reason message when adminSend notify setting is off.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduAnnouncementSendWhatsappReasonNoRecipients: {
    text: 'No guardians with verified phone consent found.',
    description: 'Reason message when no eligible guardians exist for target classes.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduAnnouncementSendWhatsappReasonPaused: {
    text: 'WhatsApp sending is paused due to insufficient credits.',
    description: 'Reason message when sending is paused due to credits.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSEduAnnouncementSentWhatsappCount: {
    text: 'Sent on WhatsApp to {count}',
    description: 'Label on announcement card showing how many recipients received it via WhatsApp.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSInvitesRosterCapacity: { text: 'Roster capacity', description: 'Invites capacity metric label.', surface: 'settings', maxChars: 36 },
  NLSInvitesPendingCapacity: { text: 'Pending capacity', description: 'Invites capacity metric label.', surface: 'settings', maxChars: 38 },
  NLSInvitesStatusLoading: { text: 'Loading', description: 'Invites loading status.', surface: 'settings', maxChars: 20 },
  NLSInvitesStatusInvited: { text: 'invited', description: 'Invites pending pill.', surface: 'settings', maxChars: 20 },
  NLSInvitesStatusActive: { text: 'active', description: 'Invites active pill.', surface: 'settings', maxChars: 18 },
  NLSInvitesStatusHelper: { text: 'helper', description: 'Invites helper pill.', surface: 'settings', maxChars: 18 },
  NLSInvitesStatusExpired: { text: 'Expired', description: 'Expired invite status chip.', surface: 'settings', maxChars: 12 },
  NLSFieldPassStatusExpired: { text: 'Expired', description: 'Expired field pass status chip.', surface: 'common', maxChars: 12 },
  NLSInvitesInviteSent: { text: 'Email invite is pending after registration.', description: 'Email invite sent status.', surface: 'settings', maxChars: 60 },
  NLSInvitesInviteResent: { text: 'Email invite resent.', description: 'Email invite resent status.', surface: 'settings', maxChars: 40 },
  NLSInvitesRequiresPermissionsAccess: { text: 'Requires Configure permissions access.', description: 'Invites permissions denied state.', surface: 'settings', maxChars: 70 },
  NLSInvitesCategoryInvites: { text: 'Invites', description: 'Invites category label.', surface: 'settings', maxChars: 24 },
  NLSInvitesCategoryMembers: { text: 'Active Members', description: 'Invites members category label.', surface: 'settings', maxChars: 34 },
  NLSInvitesCategoryHelpers: { text: 'Active Helpers', description: 'Invites helpers category label.', surface: 'settings', maxChars: 34 },
  NLSInvitesCategoryFieldPasses: { text: 'Field Passes', description: 'Invites field passes category label.', surface: 'settings', maxChars: 32 },
  NLSInvitesCategoryPermissions: { text: 'Permissions', description: 'Invites permissions category label.', surface: 'settings', maxChars: 30 },
  NLSInvitesCategoryJoinCodes: { text: 'Join Codes', description: 'Invites join codes category label.', surface: 'settings', maxChars: 30 },
  NLSInvitesCategoryJoinCodesCaption: { text: 'Generate and manage staff join codes.', description: 'Invites join codes category caption description.', surface: 'settings', maxChars: 60 },
  NLSInvitesCategoryJoinCodesTag: { text: 'Codes', description: 'Invites join codes mobile rail tag.', surface: 'settings', maxChars: 16 },
  NLSJoinWithCodeAction: { text: 'Join with a code', description: 'Action button to open the join-with-a-code dialog.', surface: 'settings', maxChars: 30 },
  NLSJoinWithCodeDialogTitle: { text: 'Join with a code', description: 'Title of the join with a code dialog.', surface: 'settings', maxChars: 30 },
  NLSJoinWithCodeDialogDescription: { text: 'Enter the code and PIN provided by your workspace admin to join.', description: 'Description in join with a code dialog.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeCodeLabel: { text: 'Join code', description: 'Label for join code input field.', surface: 'settings', maxChars: 24 },
  NLSJoinWithCodeCodePlaceholder: { text: 'e.g. ABCD-1234', description: 'Placeholder for join code input field.', surface: 'settings', maxChars: 24 },
  NLSJoinWithCodePinLabel: { text: '6-digit PIN', description: 'Label for 6-digit PIN input field.', surface: 'settings', maxChars: 24 },
  NLSJoinWithCodePinPlaceholder: { text: '••••••', description: 'Placeholder for 6-digit PIN input field.', surface: 'settings', maxChars: 12 },
  NLSJoinWithCodeSubmit: { text: 'Claim code', description: 'Submit button to claim a join code.', surface: 'settings', maxChars: 24 },
  NLSJoinWithCodeSubmitting: { text: 'Claiming...', description: 'Button text while claiming a join code.', surface: 'settings', maxChars: 24 },
  NLSJoinWithCodeSuccessTitle: { text: 'Joined workspace', description: 'Title shown when a join code claim succeeds.', surface: 'settings', maxChars: 30 },
  NLSJoinWithCodeSuccessBody: { text: 'You are now a {role} of {workspaceName}.', description: 'Body shown when a join code claim succeeds.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorInvalid: { text: 'Invalid code or PIN. Please check and try again.', description: 'Error message for invalid code or PIN.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorTooManyAttempts: { text: 'Too many failed attempts. Please try again later.', description: 'Error message when claim attempts exceeded.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorExpired: { text: 'This join code has expired.', description: 'Error message when join code has expired.', surface: 'settings', maxChars: 60 },
  NLSJoinWithCodeErrorRevoked: { text: 'This join code has been revoked.', description: 'Error message when join code has been revoked.', surface: 'settings', maxChars: 60 },
  NLSJoinWithCodeErrorLocked: { text: 'This join code is locked due to too many failed attempts.', description: 'Error message when join code is locked.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorAlreadyUsed: { text: 'This join code has already been used.', description: 'Error message when join code is already claimed.', surface: 'settings', maxChars: 60 },
  NLSJoinWithCodeErrorAlreadyMember: { text: 'You are already a member of this workspace.', description: 'Error message when user is already a workspace member.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorSeatsFull: { text: 'This workspace has reached its member limit.', description: 'Error message when workspace roster is full.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorNotAvailable: { text: 'This join code or workspace is not available.', description: 'Error message when workspace is not available.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorGoogleSignIn: { text: 'Sign in with Google is required to claim this code.', description: 'Error message when Google sign-in is required.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorLearnerFrozen: { text: 'This learner record is currently frozen.', description: 'Error message when learner record is frozen.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorGuardianFlow: { text: 'This code is for a parent or guardian; the guardian steps open here soon', description: 'Message for guardian code claim in progress.', surface: 'settings', maxChars: 100 },
  NLSJoinWithCodePhoneStepTitle: { text: 'Guardian phone verification', description: 'Title for guardian phone verification step in join with code.', surface: 'settings', maxChars: 40 },
  NLSJoinWithCodePhoneStepDescription: { text: 'A verified phone number is required to claim a join code for a minor.', description: 'Description for guardian phone verification step.', surface: 'settings', maxChars: 100 },
  NLSJoinWithCodeSendCode: { text: 'Send code', description: 'Action to send verification code to guardian phone.', surface: 'settings', maxChars: 24 },
  NLSJoinWithCodeSendingCode: { text: 'Sending...', description: 'Sending verification code button state.', surface: 'settings', maxChars: 24 },
  NLSJoinWithCodeOtpLabel: { text: '6-digit verification code', description: 'Label for phone verification OTP input.', surface: 'settings', maxChars: 30 },
  NLSJoinWithCodeOtpPlaceholder: { text: '••••••', description: 'Placeholder for phone verification OTP.', surface: 'settings', maxChars: 12 },
  NLSJoinWithCodeConfirmPhone: { text: 'Verify phone', description: 'Button to confirm phone verification code.', surface: 'settings', maxChars: 24 },
  NLSJoinWithCodeConfirmingPhone: { text: 'Verifying...', description: 'Button state while confirming phone verification code.', surface: 'settings', maxChars: 24 },
  NLSJoinWithCodeErrorInvalidOtp: { text: 'Invalid or expired verification code.', description: 'Error message when phone verification code is wrong or expired.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorPhoneInUse: { text: 'This phone number is already associated with another account.', description: 'Error message when phone number is in use on another account.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorTooManyRequests: { text: 'Too many requests. Please try again later.', description: 'Error message when rate limited.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeErrorInternationalPhone: { text: 'Use an international phone number, for example +919876543210.', description: 'Error message when international phone format is missing.', surface: 'settings', maxChars: 80 },
  NLSJoinWithCodeConsentAgree: { text: 'I agree to the parent and guardian terms', description: 'Checkbox label for agreeing to guardian consent.', surface: 'settings', maxChars: 60 },
  NLSJoinWithCodeConsentContinue: { text: 'Agree & continue', description: 'Button to continue after agreeing to guardian consent.', surface: 'settings', maxChars: 30 },
  NLSJoinWithCodeConsentContinuing: { text: 'Submitting...', description: 'Button state while submitting guardian consent.', surface: 'settings', maxChars: 24 },
  NLSInvitesGenerateTitle: { text: 'Generate Invite', description: 'Invite generator title.', surface: 'settings', maxChars: 36 },
  NLSInvitesGenerateBody: {
    text: 'Send a member email invite or a helper phone invite. Generated links land in the Pending tab until claimed or revoked.',
    description: 'Invite generator body.',
    surface: 'settings',
    maxChars: 150,
  },
  NLSInvitesTargetMember: { text: 'Member', description: 'Invite target member option.', surface: 'settings', maxChars: 20 },
  NLSInvitesTargetHelper: { text: 'Helper', description: 'Invite target helper option.', surface: 'settings', maxChars: 20 },
  NLSInvitesActionCopyInviteLink: { text: 'Copy Invite Link', description: 'Copy invite link action.', surface: 'settings', maxChars: 38 },
  NLSInvitesActionInvalidateRegenerate: { text: 'Invalidate & Regenerate', description: 'Regenerate invite action.', surface: 'settings', maxChars: 48 },
  NLSInvitesActionSendEmailInvite: { text: 'Send Email Invite', description: 'Send email invite action.', surface: 'settings', maxChars: 40 },
  NLSInvitesActionSendHelperInvite: { text: 'Send Helper Invite', description: 'Send helper invite action.', surface: 'settings', maxChars: 40 },
  NLSInvitesRightsRequired: { text: 'Invite rights required', description: 'Invite rights empty title.', surface: 'settings', maxChars: 46 },
  NLSInvitesRightsRequiredBody: { text: 'Ask a workspace admin for invite rights to create invitations.', description: 'Invite rights empty body.', surface: 'settings', maxChars: 90 },
  NLSInvitesLabelWorkspaceRole: { text: 'Workspace role', description: 'Member sidecar role heading.', surface: 'settings', maxChars: 36 },
  NLSInvitesLabelBoardPermissions: { text: 'Board permissions', description: 'Member sidecar board permissions heading.', surface: 'settings', maxChars: 40 },
  NLSInvitesLabelCredits: { text: 'Credits', description: 'Member sidecar credits heading.', surface: 'settings', maxChars: 22 },
  NLSInvitesHelperAppLanguageTitle: { text: 'Helper app language', description: 'Active helper sidecar heading for the per-helper Helper app UI language.', surface: 'settings', maxChars: 42 },
  NLSInvitesHelperAppLanguageBody: {
    text: 'This helper sees the Helper app UI in this language for this workspace.',
    description: 'Active helper sidecar copy for the workspace-scoped Helper app UI language.',
    surface: 'settings',
    maxChars: 110,
  },
  NLSInvitesHelperAppLanguageAria: { text: 'Helper app language', description: 'Accessible label for the active helper sidecar Helper app UI language selector.', surface: 'settings', maxChars: 42 },
  NLSInvitesHelperFieldPassDelegationTitle: {
    text: 'Field Pass delegation',
    description: 'Active helper sidecar heading for allowing a helper to delegate assigned work by Field Pass.',
    surface: 'settings',
    maxChars: 44,
  },
  NLSInvitesHelperFieldPassDelegationBody: {
    text: 'Allow this helper to create short-lived Field Pass links for their own assigned checklist items.',
    description: 'Active helper sidecar copy for Field Pass delegation rights.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSInvitesHelperFieldPassDelegationAria: { text: 'Allow Field Pass delegation', description: 'Accessible label for helper Field Pass delegation toggle.', surface: 'settings', maxChars: 48 },
  NLSInvitesHelperFieldPassDelegationOn: { text: 'Delegation enabled', description: 'Enabled state label for helper Field Pass delegation.', surface: 'settings', maxChars: 40 },
  NLSInvitesHelperFieldPassDelegationOff: { text: 'Delegation disabled', description: 'Disabled state label for helper Field Pass delegation.', surface: 'settings', maxChars: 40 },
  NLSInvitesHelperFieldPassDelegationEnabled: { text: 'Field Pass delegation enabled.', description: 'Status message after enabling helper Field Pass delegation.', surface: 'settings', maxChars: 56 },
  NLSInvitesHelperFieldPassDelegationDisabled: {
    text: 'Field Pass delegation disabled.',
    description: 'Status message after disabling helper Field Pass delegation.',
    surface: 'settings',
    maxChars: 56,
  },
  NLSInvitesHelperFieldPassDelegationFailed: {
    text: 'Could not update Field Pass delegation.',
    description: 'Error message when helper Field Pass delegation update fails.',
    surface: 'settings',
    maxChars: 70,
  },
  NLSInvitesConfirmRemoveMemberTitle: { text: 'Remove member?', description: 'Confirmation modal title for removing an active member.', surface: 'settings', maxChars: 34 },
  NLSInvitesConfirmRemoveMemberBody: {
    text: '{name} will lose access immediately. Their assigned tasks will be unassigned. Their existing messages, comments, and history stay in this workspace.',
    description: 'Confirmation modal body for removing a member.',
    surface: 'settings',
    maxChars: 220,
  },
  NLSInvitesConfirmRemoveHelperTitle: { text: 'Remove helper?', description: 'Confirmation modal title for removing an active helper.', surface: 'settings', maxChars: 34 },
  NLSInvitesConfirmRemoveHelperBody: {
    text: '{name} will lose access immediately. Their assigned tasks will be unassigned. Their existing scans, notes, and history stay in this workspace.',
    description: 'Confirmation modal body for removing a helper.',
    surface: 'settings',
    maxChars: 220,
  },
  NLSFieldPassConfirmRevokeTitle: { text: 'Revoke this Field Pass?', description: 'Confirmation modal title for revoking a Field Pass.', surface: 'common', maxChars: 40 },
  NLSFieldPassConfirmRevokeBody: {
    text: 'The current holder will lose access immediately. Task and chat history stays on the workspace.',
    description: 'Confirmation modal body for Field Pass revoke.',
    surface: 'common',
    maxChars: 180,
  },
  NLSFieldPassConfirmRevokeAction: { text: 'Revoke', description: 'Confirm button label for revoking a Field Pass.', surface: 'common', maxChars: 12 },
  NLSInvitesLeaveWorkspaceTitle: { text: 'Leave this workspace?', description: 'Confirmation modal title for self-leaving a workspace.', surface: 'settings', maxChars: 38 },
  NLSInvitesLeaveWorkspaceBody: {
    text: 'You will lose access immediately. Rejoining requires a new invite from the workspace admin.',
    description: 'Confirmation modal body for self-leaving.',
    surface: 'settings',
    maxChars: 180,
  },
  NLSInvitesLeaveWorkspaceAction: { text: 'Leave workspace', description: 'Workspace switcher menu item to leave a workspace as a member.', surface: 'settings', maxChars: 24 },
  NLSInvitesRemoveMemberAction: { text: 'Remove member', description: 'InvitesHubV2 action button to remove an active member.', surface: 'settings', maxChars: 22 },
  NLSInvitesRemoveHelperAction: { text: 'Remove helper', description: 'InvitesHubV2 action button to remove an active helper.', surface: 'settings', maxChars: 22 },
  NLSInvitesRemoveOwnerTooltip: { text: 'Workspace owner cannot be removed here.', description: 'Tooltip on disabled remove button for the workspace owner.', surface: 'settings', maxChars: 60 },
  NLSInvitesAdminOwnerOnlyTooltip: { text: 'Only an owner can change or remove a team admin.', description: 'Tooltip on disabled role change or remove button for team admin when viewer is not an owner.', surface: 'settings', maxChars: 60 },
  NLSInvitesLastGeneratedTitle: { text: 'Last generated invite', description: 'Title of the Last Generated Invite card in InvitesHubV2.', surface: 'settings', maxChars: 34 },
  NLSInvitesLastGeneratedHint: { text: 'Ready to share. Also tracked in the Pending tab.', description: 'Hint under the Last Generated Invite card title.', surface: 'settings', maxChars: 70 },
  NLSInvitesLastGeneratedInviteLabel: { text: 'Workspace invite', description: 'Row label for the workspace invite URL in the Last Generated Invite card.', surface: 'settings', maxChars: 24 },
  NLSInvitesLastGeneratedApkLabel: { text: 'Android app download', description: 'Row label for the Android APK URL in the Last Generated Invite card.', surface: 'settings', maxChars: 32 },
  NLSInvitesLastGeneratedCopyLink: { text: 'Copy link', description: 'Per-row Copy button label in the Last Generated Invite card.', surface: 'settings', maxChars: 16 },
  NLSInvitesLastGeneratedCopyAll: { text: 'Copy everything', description: 'Bulk copy button label in the Last Generated Invite card.', surface: 'settings', maxChars: 22 },
  NLSConfirmActionConfirm: { text: 'Confirm', description: 'Generic confirm button label in ConfirmActionModal.', surface: 'common', maxChars: 16 },
  NLSConfirmActionCancel: { text: 'Cancel', description: 'Generic cancel button label in ConfirmActionModal.', surface: 'common', maxChars: 16 },
  NLSWorkbenchAutomateTitle: { text: 'Automate', description: 'Automate shell title.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchAutomateSubtitle: { text: 'Build, run, and manage workflows', description: 'Automate shell subtitle.', surface: 'workbench', maxChars: 50 },
  NLSWorkbenchAutomateAria: { text: 'Automate Hub', description: 'Automate shell aria label.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchAutomateSectionsAria: { text: 'Automate hub sections', description: 'Automate rail tablist aria label.', surface: 'workbench', maxChars: 42 },
  NLSWorkbenchAutomateNavOverview: { text: 'Overview', description: 'Automate navigation label.', surface: 'workbench', maxChars: 24 },
  NLSWorkbenchAutomateNavOverviewTone: { text: 'At a glance', description: 'Automate navigation detail.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchAutomateNavWorkflows: { text: 'Workflows', description: 'Automate navigation label.', surface: 'workbench', maxChars: 28 },
  NLSWorkbenchAutomateNavWorkflowsTone: { text: 'All of your workflows', description: 'Automate navigation detail.', surface: 'workbench', maxChars: 44 },
  NLSWorkbenchAutomateNavTriggers: { text: 'Triggers', description: 'Automate navigation label.', surface: 'workbench', maxChars: 26 },
  NLSWorkbenchAutomateNavTriggersTone: { text: 'Events that start a workflow', description: 'Automate navigation detail.', surface: 'workbench', maxChars: 54 },
  NLSWorkbenchAutomateNavActions: { text: 'Actions', description: 'Automate navigation label.', surface: 'workbench', maxChars: 24 },
  NLSWorkbenchAutomateNavActionsTone: { text: 'Steps a workflow can run', description: 'Automate navigation detail.', surface: 'workbench', maxChars: 50 },
  NLSWorkbenchAutomateNavTemplates: { text: 'Templates', description: 'Automate navigation label.', surface: 'workbench', maxChars: 28 },
  NLSWorkbenchAutomateNavTemplatesTone: { text: 'Ready-made starters', description: 'Automate navigation detail.', surface: 'workbench', maxChars: 42 },
  NLSWorkbenchAutomateNavSettings: { text: 'Settings', description: 'Automate navigation label.', surface: 'workbench', maxChars: 24 },
  NLSWorkbenchAutomateNavSettingsTone: { text: 'Runtime health & controls', description: 'Automate navigation detail.', surface: 'workbench', maxChars: 38 },
  NLSWorkbenchAutomateChipAll: { text: 'All', description: 'Automate status filter chip.', surface: 'workbench', maxChars: 14 },
  NLSWorkbenchAutomateChipLive: { text: 'Live', description: 'Automate status filter chip.', surface: 'workbench', maxChars: 16 },
  NLSWorkbenchAutomateChipPaused: { text: 'Paused', description: 'Automate status filter chip.', surface: 'workbench', maxChars: 18 },
  NLSWorkbenchAutomateChipNeedsAttention: { text: 'Needs attention', description: 'Automate status filter chip.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchAutomateDeleteWorkflowTitle: { text: 'Delete workflow?', description: 'Confirmation title for deleting a workflow.', surface: 'workbench', maxChars: 40 },
  NLSWorkbenchAutomateBulkStatusTitle: { text: 'Change workflow status?', description: 'Confirmation title for changing workflow status in bulk.', surface: 'workbench', maxChars: 40 },
  NLSWorkbenchDesignTitle: { text: 'Design Hub', description: 'Design shell title.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchDesignSubtitle: { text: 'Premium visual factory', description: 'Design shell subtitle.', surface: 'workbench', maxChars: 42 },
  NLSWorkbenchDesignAria: { text: 'Design Hub', description: 'Design shell aria label.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchDesignNavTemplates: { text: 'Templates', description: 'Design navigation label.', surface: 'workbench', maxChars: 28 },
  NLSWorkbenchDesignNavTemplatesTone: { text: 'Reusable starters', description: 'Design navigation detail.', surface: 'workbench', maxChars: 38 },
  NLSWorkbenchDesignNavBound: { text: 'Bound Studio', description: 'Design navigation label.', surface: 'workbench', maxChars: 32 },
  NLSWorkbenchDesignNavBoundTone: { text: 'Live dashboards', description: 'Design navigation detail.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchDesignNavChecklists: { text: 'Checklists', description: 'Design navigation label.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchDesignNavChecklistsTone: { text: 'Attach Bound to work', description: 'Design navigation detail.', surface: 'workbench', maxChars: 42 },
  NLSWorkbenchDesignNavFree: { text: 'Free Studio', description: 'Design navigation label.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchDesignNavFreeTone: { text: 'Visual diagrams', description: 'Design navigation detail.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchDesignNavLibrary: { text: 'Library', description: 'Design navigation label.', surface: 'workbench', maxChars: 24 },
  NLSWorkbenchDesignNavLibraryTone: { text: 'All assets', description: 'Design navigation detail.', surface: 'workbench', maxChars: 24 },
  NLSWorkbenchDesignNavEmbeds: { text: 'Embeds', description: 'Design navigation label.', surface: 'workbench', maxChars: 24 },
  NLSWorkbenchDesignNavEmbedsTone: { text: 'Reports and items', description: 'Design navigation detail.', surface: 'workbench', maxChars: 36 },
  NLSWorkbenchDesignNavSettings: { text: 'Settings', description: 'Design navigation label.', surface: 'workbench', maxChars: 24 },
  NLSWorkbenchDesignNavSettingsTone: { text: 'Studio policy', description: 'Design navigation detail.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchDesignNoPermission: { text: "You don't have permission to view Design Hub in this workspace.", description: 'Design access denied message.', surface: 'workbench', maxChars: 90 },
  NLSWorkbenchExploreTitle: { text: 'Explore Hub', description: 'Explore shell title.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchExploreSubtitle: { text: 'Shared library', description: 'Explore shell subtitle.', surface: 'workbench', maxChars: 32 },
  NLSWorkbenchExploreAria: { text: 'Explore Hub', description: 'Explore shell aria label.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchExploreWorkspaceTitle: { text: 'Discover and join spaces', description: 'Explore workspace page title.', surface: 'workbench', maxChars: 52 },
  NLSWorkbenchExploreWorkspaceBody: {
    text: 'Create teams, review invites, track join requests, and preview public timelines without switching context.',
    description: 'Explore workspace page body.',
    surface: 'workbench',
    maxChars: 130,
  },
  NLSWorkbenchExploreSearchPublic: { text: 'Search public workspaces', description: 'Explore public search placeholder.', surface: 'workbench', maxChars: 44 },
  NLSWorkbenchExploreSearchMine: { text: 'Search your workspaces', description: 'Explore personal workspace search placeholder.', surface: 'workbench', maxChars: 44 },
  NLSWorkbenchExploreCreateWorkspace: { text: 'Create Workspace', description: 'Explore create workspace action.', surface: 'workbench', maxChars: 38 },
  NLSWorkbenchExploreBackDashboard: { text: 'Back to Dashboard', description: 'Explore back action.', surface: 'workbench', maxChars: 38 },
  NLSWorkbenchExploreClosePublicView: { text: 'Close Public View', description: 'Explore public preview close action.', surface: 'workbench', maxChars: 38 },
  NLSWorkbenchExploreImportEditorsOnly: {
    text: 'Only editors and admins can import templates',
    description: 'Hint shown when user has viewer role and cannot import templates.',
    surface: 'workbench',
    maxChars: 50,
  },
  NLSWorkbenchExploreImportFailed: {
    text: "Couldn't import this template.",
    description: 'Error toast when template import fails.',
    surface: 'workbench',
    maxChars: 40,
  },
  NLSWorkbenchExploreMaterializeFailed: {
    text: "Template saved, but it couldn't be set up in this workspace.",
    description: 'Error toast when template is saved to workspace but live materialization fails.',
    surface: 'workbench',
    maxChars: 65,
  },
  NLSWorkbenchOverviewTitle: { text: 'Overview', description: 'Workspace Overview title.', surface: 'workbench', maxChars: 28 },
  NLSWorkbenchOverviewModeTeam: { text: 'Team', description: 'Overview mode label.', surface: 'workbench', maxChars: 18 },
  NLSWorkbenchOverviewModePublic: { text: 'Public', description: 'Overview mode label.', surface: 'workbench', maxChars: 18 },
  NLSWorkbenchOverviewSectionHero: { text: 'Hero', description: 'Overview section label.', surface: 'workbench', maxChars: 20 },
  NLSWorkbenchOverviewSectionHeroDetail: { text: 'Mission, workspace identity, and lead signal.', description: 'Overview section detail.', surface: 'workbench', maxChars: 72 },
  NLSWorkbenchOverviewSectionPulse: { text: 'Pulse', description: 'Overview section label.', surface: 'workbench', maxChars: 22 },
  NLSWorkbenchOverviewSectionPulseDetail: { text: 'Live progress and completion stats.', description: 'Overview section detail.', surface: 'workbench', maxChars: 62 },
  NLSWorkbenchOverviewSectionGoals: { text: 'Goals', description: 'Overview section label.', surface: 'workbench', maxChars: 22 },
  NLSWorkbenchOverviewSectionGoalsDetail: { text: 'Selected or top active goals.', description: 'Overview section detail.', surface: 'workbench', maxChars: 54 },
  NLSWorkbenchOverviewSectionTasks: { text: 'Tasks', description: 'Overview section label.', surface: 'workbench', maxChars: 22 },
  NLSWorkbenchOverviewSectionTasksDetail: { text: 'Focus tasks from live timeline data.', description: 'Overview section detail.', surface: 'workbench', maxChars: 64 },
  NLSWorkbenchOverviewUnsavedTitle: { text: 'Unsaved overview changes', description: 'Overview dirty dialog title.', surface: 'workbench', maxChars: 52 },
  NLSWorkbenchOverviewUnsavedBody: { text: 'Save your Overview edits before leaving this mode or switching screens?', description: 'Overview dirty dialog body.', surface: 'workbench', maxChars: 96 },
  NLSWorkbenchOverviewKeepEditing: { text: 'Keep editing', description: 'Overview dirty dialog action.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchOverviewDiscardChanges: { text: 'Discard changes', description: 'Overview dirty dialog action.', surface: 'workbench', maxChars: 36 },
  NLSWorkbenchOverviewSaveContinue: { text: 'Save and continue', description: 'Overview dirty dialog action.', surface: 'workbench', maxChars: 38 },
  NLSWorkbenchFocusTitle: { text: 'Focus', description: 'Focus shell title.', surface: 'workbench', maxChars: 22 },
  NLSWorkbenchFocusSubtitle: { text: 'Deep work', description: 'Focus shell subtitle.', surface: 'workbench', maxChars: 24 },
  NLSWorkbenchFocusAria: { text: 'Focus mode', description: 'Focus shell aria label.', surface: 'workbench', maxChars: 28 },
  NLSWorkbenchFocusGuardian: { text: 'Focus Guardian', description: 'Focus guardian label.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchFocusTarget: { text: 'Focus target', description: 'Focus target label.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchFocusTargetAria: { text: 'Select focus target', description: 'Focus target select aria label.', surface: 'workbench', maxChars: 42 },
  NLSWorkbenchFocusIntent: { text: 'Session intent', description: 'Focus intent label.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchFocusDurationAria: { text: 'Focus duration', description: 'Focus duration group aria label.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchFocusStopwatch: { text: 'Stopwatch', description: 'Focus stopwatch option.', surface: 'workbench', maxChars: 26 },
  NLSWorkbenchFocusSoundscape: { text: 'Soundscape', description: 'Focus soundscape label.', surface: 'workbench', maxChars: 28 },
  NLSWorkbenchFocusSoundscapeAria: { text: 'Focus soundscape', description: 'Focus soundscape select aria label.', surface: 'workbench', maxChars: 38 },
  NLSWorkbenchFocusVolumeAria: { text: 'Focus volume', description: 'Focus volume slider aria label.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchFocusEnter: { text: 'Enter Focus', description: 'Focus start action.', surface: 'workbench', maxChars: 28 },
  NLSWorkbenchFocusFreeformTitle: { text: 'Freeform focus', description: 'Focus freeform target title.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchFocusFreeformBody: { text: 'Untethered deep-work block. Nothing tracked to a work object.', description: 'Focus freeform target body.', surface: 'workbench', maxChars: 90 },
  NLSWorkbenchFocusFreeformParent: { text: 'Just you and the timer', description: 'Focus freeform target parent label.', surface: 'workbench', maxChars: 42 },
  NLSWorkbenchFocusSoundSilence: { text: 'Deep Silence', description: 'Focus soundscape label.', surface: 'workbench', maxChars: 30 },
  NLSWorkbenchFocusSoundLofi: { text: 'Lo-Fi Rain', description: 'Focus soundscape label.', surface: 'workbench', maxChars: 26 },
  NLSWorkbenchFocusSoundAlpha: { text: 'Alpha Wave', description: 'Focus soundscape label.', surface: 'workbench', maxChars: 28 },
  NLSWorkbenchFocusNoAudio: { text: 'No audio', description: 'Focus soundscape intensity.', surface: 'workbench', maxChars: 20 },
  NLSWorkbenchFocusLayeredAmbient: { text: 'Layered ambient', description: 'Focus soundscape intensity.', surface: 'workbench', maxChars: 34 },
  NLSWorkbenchFocusFuturisticCalm: { text: 'Futuristic calm', description: 'Focus soundscape intensity.', surface: 'workbench', maxChars: 34 },
  NLSExperienceYourPageAria: { text: 'Your Page', description: 'Accessible label for the gamification profile shell.', surface: 'experience', maxChars: 28 },
  NLSExperienceYourPageEyebrow: { text: 'Your Page', description: 'Eyebrow for the gamification profile shell.', surface: 'experience', maxChars: 24 },
  NLSExperienceYourPageSectionsAria: { text: 'Your Page sections', description: 'Accessible label for Your Page tabs.', surface: 'experience', maxChars: 38 },
  NLSExperienceProfileBioAria: { text: 'Bio', description: 'Accessible label for profile bio editor.', surface: 'experience', maxChars: 24 },
  NLSExperienceYourTabDetails: { text: 'Your Details', description: 'Your Page identity details tab.', surface: 'experience', maxChars: 30 },
  NLSExperienceYourTabProfile: { text: 'Your Profile', description: 'Your Page profile canvas tab.', surface: 'experience', maxChars: 30 },
  NLSExperienceYourTabAchievements: { text: 'Achievements', description: 'Your Page achievements tab.', surface: 'experience', maxChars: 30 },
  NLSExperienceYourTabCharacter: { text: 'Character', description: 'Your Page character tab.', surface: 'experience', maxChars: 28 },
  NLSExperienceRarityCommon: { text: 'Common', description: 'Cosmetic rarity label.', surface: 'experience', maxChars: 18 },
  NLSExperienceRarityRare: { text: 'Rare', description: 'Cosmetic rarity label.', surface: 'experience', maxChars: 18 },
  NLSExperienceRarityEpic: { text: 'Epic', description: 'Cosmetic rarity label.', surface: 'experience', maxChars: 18 },
  NLSExperienceRarityLegendary: { text: 'Legendary', description: 'Cosmetic rarity label.', surface: 'experience', maxChars: 24 },
  NLSExperienceSpinTierLucky: { text: 'Lucky', description: 'Lucky spin tier label.', surface: 'experience', maxChars: 18 },
  NLSExperienceSpinTierMega: { text: 'Mega', description: 'Mega spin tier label.', surface: 'experience', maxChars: 18 },
  NLSExperienceSpinTierGiga: { text: 'Giga', description: 'Giga spin tier label.', surface: 'experience', maxChars: 18 },
  NLSExperienceFilterAll: { text: 'All', description: 'Progression track filter.', surface: 'experience', maxChars: 14 },
  NLSExperienceFilterInProgress: { text: 'In Progress', description: 'Progression track filter.', surface: 'experience', maxChars: 28 },
  NLSExperienceFilterClaimable: { text: 'Claimable', description: 'Progression track filter.', surface: 'experience', maxChars: 24 },
  NLSExperienceFilterCompleted: { text: 'Completed', description: 'Progression track filter.', surface: 'experience', maxChars: 24 },
  NLSExperienceFilterAscended: { text: 'Ascended', description: 'Progression track filter.', surface: 'experience', maxChars: 24 },
  NLSExperienceLoadingProgression: { text: 'Loading your progression...', description: 'Progression loading label.', surface: 'experience', maxChars: 46 },
  NLSExperienceLoadingVault: { text: 'Loading your vault...', description: 'Character vault loading label.', surface: 'experience', maxChars: 42 },
  NLSExperienceProgressionTitle: { text: 'Operational Mastery', description: 'Achievements progression hero title.', surface: 'experience', maxChars: 42 },
  NLSExperienceLevelAbbrev: { text: 'LV', description: 'Compact level abbreviation.', surface: 'experience', maxChars: 8 },
  NLSExperienceLevelAria: { text: 'Level {level}', description: 'Accessible level label. {level} is a number.', surface: 'experience', maxChars: 30 },
  NLSExperienceXpToLevel: { text: '{count} XP to Lv {level}', description: 'XP remaining label. {count} and {level} are numbers.', surface: 'experience', maxChars: 40 },
  NLSExperienceConsecutiveStreakTitle: { text: 'Consecutive-day login streak', description: 'Tooltip for streak counter.', surface: 'experience', maxChars: 52 },
  NLSExperienceDayStreak: { text: '{count}-day streak', description: 'Login streak label. {count} is a number.', surface: 'experience', maxChars: 32 },
  NLSExperienceReadyTitle: { text: 'Ready: {title}', description: 'Next unlock ready label. {title} is a runtime reward title.', surface: 'experience', maxChars: 70 },
  NLSExperienceNextClaimableTitle: { text: 'Next claimable: {title}', description: 'Next claimable tooltip. {title} is a runtime reward title.', surface: 'experience', maxChars: 80 },
  NLSExperienceSpinVaultTitle: { text: 'Spin Vault', description: 'Spin ticket section title.', surface: 'experience', maxChars: 30 },
  NLSExperienceMasteryTracksTitle: { text: 'Mastery Tracks', description: 'Progression tracks section title.', surface: 'experience', maxChars: 36 },
  NLSExperienceMasteryTrackFilterAria: { text: 'Mastery track filter', description: 'Accessible label for progression track filters.', surface: 'experience', maxChars: 42 },
  NLSExperienceRetentionTitle: { text: 'Retention', description: 'Retention section title.', surface: 'experience', maxChars: 28 },
  NLSExperienceTotalLogins: { text: 'Total Logins', description: 'Retention metric title.', surface: 'experience', maxChars: 32 },
  NLSExperienceLoginStreak: { text: 'Login Streak', description: 'Retention metric title.', surface: 'experience', maxChars: 32 },
  NLSExperienceDaysCount: { text: '{count} days', description: 'Day count label. {count} is a number.', surface: 'experience', maxChars: 26 },
  NLSExperienceBossCurseActive: { text: 'Boss Curse active.', description: 'Boss curse warning title.', surface: 'experience', maxChars: 34 },
  NLSExperienceBossCurseBody: {
    text: 'Spins and ticket grants are frozen across this workspace until the curse expires (24h) or the team breaks it.',
    description: 'Boss curse warning body.',
    surface: 'experience',
    maxChars: 140,
  },
  NLSExperienceSpinFrozenTitle: { text: 'Boss Curse active - spins frozen for 24h', description: 'Spin disabled tooltip.', surface: 'experience', maxChars: 70 },
  NLSExperienceSpinFrozen: { text: 'Frozen', description: 'Disabled spin action.', surface: 'experience', maxChars: 18 },
  NLSExperienceSpinAction: { text: 'Spin', description: 'Spin action.', surface: 'experience', maxChars: 16 },
  NLSExperienceSpinNoTickets: { text: 'No tickets', description: 'No spin tickets action label.', surface: 'experience', maxChars: 24 },
  NLSExperienceSpinLabel: { text: '{tier} Spin', description: 'Spin tier display label. {tier} is a localized tier label.', surface: 'experience', maxChars: 34 },
  NLSExperienceEveryTierUnlocked: { text: 'Every tier unlocked', description: 'Ascended track tooltip.', surface: 'experience', maxChars: 40 },
  NLSExperienceAscended: { text: 'Ascended', description: 'Fully completed track label.', surface: 'experience', maxChars: 24 },
  NLSExperienceTierProgress: { text: 'Tier {level}/4', description: 'Track tier badge. {level} is a number.', surface: 'experience', maxChars: 24 },
  NLSExperienceTierLockedTitle: { text: 'Locked - reach the previous tier to reveal', description: 'Locked tier tooltip.', surface: 'experience', maxChars: 64 },
  NLSExperienceTierThresholdTitle: { text: 'Tier {tier}: {threshold} {unit}', description: 'Tier threshold tooltip.', surface: 'experience', maxChars: 64 },
  NLSExperienceFullyAscended: { text: '{count} - fully ascended', description: 'Fully ascended progress text. {count} is a number.', surface: 'experience', maxChars: 44 },
  NLSExperienceNext: { text: 'Next', description: 'Next reward label.', surface: 'experience', maxChars: 14 },
  NLSExperienceMoveTicketsTitle: {
    text: 'Move {summary} into your spin wallet',
    description: 'Collect tickets tooltip. {summary} is a localized ticket summary.',
    surface: 'experience',
    maxChars: 80,
  },
  NLSExperienceEarnTicketsTitle: { text: 'Earn spin tickets by reaching new tiers', description: 'Disabled collect tooltip.', surface: 'experience', maxChars: 70 },
  NLSExperienceCollecting: { text: 'Collecting...', description: 'Collect tickets busy label.', surface: 'experience', maxChars: 30 },
  NLSExperienceCollect: { text: 'Collect', description: 'Collect tickets action.', surface: 'experience', maxChars: 20 },
  NLSExperienceIdentityLoadout: { text: 'Identity Loadout', description: 'Character hero eyebrow.', surface: 'experience', maxChars: 34 },
  NLSExperienceEquippedLoadoutAria: { text: 'Equipped loadout', description: 'Accessible label for equipped character loadout.', surface: 'experience', maxChars: 36 },
  NLSExperienceTheme: { text: 'Theme', description: 'Character loadout theme label.', surface: 'experience', maxChars: 18 },
  NLSExperienceCanvas: { text: 'Canvas', description: 'Character loadout canvas label.', surface: 'experience', maxChars: 18 },
  NLSExperienceSoundscape: { text: 'Soundscape', description: 'Character loadout soundscape label.', surface: 'experience', maxChars: 28 },
  NLSExperienceEquipped: { text: 'Equipped', description: 'Equipped cosmetic status.', surface: 'experience', maxChars: 24 },
  NLSExperienceEquip: { text: 'Equip', description: 'Equip cosmetic action.', surface: 'experience', maxChars: 18 },
  NLSExperienceUnlock: { text: 'Unlock', description: 'Unlock cosmetic action.', surface: 'experience', maxChars: 18 },
  NLSExperiencePreviewing: { text: 'Previewing...', description: 'Cosmetic preview busy label.', surface: 'experience', maxChars: 28 },
  NLSExperiencePreview: { text: 'Preview', description: 'Cosmetic preview action.', surface: 'experience', maxChars: 20 },
  NLSExperienceGuardianBundleTitle: { text: 'This guardian brings a bundled soundscape', description: 'Guardian bundle tooltip.', surface: 'experience', maxChars: 64 },
  NLSExperienceGuardianBundles: { text: 'Bundles {label}', description: 'Guardian bundled soundscape label. {label} is a catalog soundscape label.', surface: 'experience', maxChars: 70 },
  NLSExperiencePrefix: { text: 'Prefix', description: 'Title prefix label.', surface: 'experience', maxChars: 18 },
  NLSExperienceSuffix: { text: 'Suffix', description: 'Title suffix label.', surface: 'experience', maxChars: 18 },
  NLSExperienceTitlePrefixAria: { text: 'Title prefix', description: 'Accessible label for title prefix options.', surface: 'experience', maxChars: 30 },
  NLSExperienceTitleSuffixAria: { text: 'Title suffix', description: 'Accessible label for title suffix options.', surface: 'experience', maxChars: 30 },
  NLSExperienceForgeTitle: { text: 'Forge Title', description: 'Equip forged title action.', surface: 'experience', maxChars: 30 },
  NLSExperienceGuardianSanctum: { text: 'Guardian Sanctum', description: 'Guardian section title.', surface: 'experience', maxChars: 34 },
  NLSExperienceGuardianSanctumBody: { text: 'Choose the presence bound to your workspace.', description: 'Guardian section subtitle.', surface: 'experience', maxChars: 70 },
  NLSExperienceEnvironmentChamber: { text: 'Environment Chamber', description: 'Environment section title.', surface: 'experience', maxChars: 38 },
  NLSExperienceEnvironmentChamberBody: { text: 'The canvas your workspace paints itself onto.', description: 'Environment section subtitle.', surface: 'experience', maxChars: 70 },
  NLSExperienceThemes: { text: 'Themes', description: 'Themes subsection label.', surface: 'experience', maxChars: 22 },
  NLSExperienceCanvasBackgrounds: { text: 'Canvas Backgrounds', description: 'Canvas backgrounds subsection label.', surface: 'experience', maxChars: 42 },
  NLSExperienceSoundscapeVault: { text: 'Soundscape Vault', description: 'Soundscape section title.', surface: 'experience', maxChars: 38 },
  NLSExperienceSoundscapeVaultBody: { text: 'Bundled with Guardians. Attunes your focus sessions.', description: 'Soundscape section subtitle.', surface: 'experience', maxChars: 80 },
  NLSExperienceTitleForge: { text: 'Title Forge', description: 'Title forge section title.', surface: 'experience', maxChars: 30 },
  NLSExperienceTitleForgeBody: { text: 'Compose the words that ride beside your name.', description: 'Title forge subtitle.', surface: 'experience', maxChars: 70 },
  NLSExperienceEquipGuardian: { text: 'Equip its Guardian', description: 'Locked soundscape label.', surface: 'experience', maxChars: 34 },
  NLSExperienceDefault: { text: 'Default', description: 'Default cosmetic fallback.', surface: 'experience', maxChars: 22 },
  NLSExperienceSilence: { text: 'Silence', description: 'Silent soundscape fallback.', surface: 'experience', maxChars: 22 },
  NLSExperienceLevelShort: { text: 'Lv {level}', description: 'Short level label. {level} is a number.', surface: 'experience', maxChars: 18 },
  NLSExperienceAchievementUnlocked: { text: 'Achievement unlocked', description: 'Celebration overlay eyebrow.', surface: 'experience', maxChars: 42 },
  NLSExperienceAchievementsCollected: { text: '{count} achievements collected', description: 'Celebration overlay multi-achievement title. {count} is a number.', surface: 'experience', maxChars: 52 },
  NLSExperienceNotificationHubAria: { text: 'Notification Hub', description: 'Notification Hub accessible label.', surface: 'experience', maxChars: 34 },
  NLSExperienceNotificationSignalStream: { text: 'Signal stream', description: 'Notification Hub eyebrow.', surface: 'experience', maxChars: 30 },
  NLSExperienceNotificationsTitle: { text: 'Notifications', description: 'Notification Hub title.', surface: 'experience', maxChars: 32 },
  NLSExperienceNotificationUnreadAria: { text: '{count} unread', description: 'Unread notification count aria label. {count} is a number.', surface: 'experience', maxChars: 30 },
  NLSExperienceNotificationNoSignals: { text: 'No signals here', description: 'Notification Hub empty title.', surface: 'experience', maxChars: 34 },
  NLSExperienceNotificationEmptyBody: { text: 'New workspace activity will appear automatically.', description: 'Notification Hub empty body.', surface: 'experience', maxChars: 74 },
  NLSExperienceNotificationGroupToday: { text: 'Today', description: 'Notification group label.', surface: 'experience', maxChars: 18 },
  NLSExperienceNotificationGroupYesterday: { text: 'Yesterday', description: 'Notification group label.', surface: 'experience', maxChars: 24 },
  NLSExperienceNotificationGroupOlder: { text: 'Older', description: 'Notification group label.', surface: 'experience', maxChars: 18 },
  NLSExperienceNotificationRelativeToday: { text: 'today', description: 'Notification group relative label.', surface: 'experience', maxChars: 18 },
  NLSExperienceNotificationRelativeYesterday: { text: 'yesterday', description: 'Notification group relative label.', surface: 'experience', maxChars: 24 },
  NLSExperienceNotificationRelativeDaysAgo: { text: '{count} days ago', description: 'Older notification relative label. {count} is a number.', surface: 'experience', maxChars: 30 },
  NLSExperienceNotificationClearGroupAria: {
    text: 'Clear {group} notifications',
    description: 'Accessible label for clearing a notification group. {group} is a localized group label.',
    surface: 'experience',
    maxChars: 70,
  },
  NLSExperienceNotificationClearGroupTitle: {
    text: 'Clear {group}',
    description: 'Tooltip for clearing a notification group. {group} is a localized group label.',
    surface: 'experience',
    maxChars: 46,
  },
  NLSExperienceNotificationScrollLatest: { text: 'Scroll to view latest notifications', description: 'Notification scroll hint.', surface: 'experience', maxChars: 58 },
  NLSExperienceNotificationMarkAllReadAria: { text: 'Mark all notifications as read', description: 'Accessible label for marking all notifications read.', surface: 'experience', maxChars: 54 },
  NLSExperienceNotificationMarkAllReadTitle: { text: 'Mark all read', description: 'Tooltip for marking all notifications read.', surface: 'experience', maxChars: 30 },
  NLSExperienceNotificationClearAll: { text: 'Clear all', description: 'Clear all web notification action.', surface: 'experience', maxChars: 18 },
  NLSExperienceNotificationClearAllAria: { text: 'Clear all notifications', description: 'Accessible label for clearing all web notifications.', surface: 'experience', maxChars: 38 },
  NLSExperienceNotificationClearAllTitle: { text: 'Clear all notifications', description: 'Tooltip for clearing all web notifications.', surface: 'experience', maxChars: 38 },
  NLSExperienceNotificationDismissAria: { text: 'Dismiss notification', description: 'Accessible label for dismissing one web notification.', surface: 'experience', maxChars: 34 },
  NLSExperienceNotificationDismissed: { text: 'Notification dismissed.', description: 'Web notification undo snackbar message.', surface: 'experience', maxChars: 34 },
  NLSExperienceNotificationAllCleared: { text: 'All {count} notifications cleared.', description: 'Web notification bulk undo snackbar message.', surface: 'experience', maxChars: 48 },
  NLSExperienceNotificationClearAllConfirmTitle: { text: 'Clear all notifications?', description: 'Web notification bulk clear confirmation title.', surface: 'experience', maxChars: 42 },
  NLSExperienceNotificationClearAllConfirmBody: {
    text: 'This clears the current notification list and cannot be undone.',
    description: 'Web notification bulk clear confirmation body.',
    surface: 'experience',
    maxChars: 90,
  },
  NLSExperienceRaidArenaBattlefield: { text: 'Battlefield', description: 'Raid arena label.', surface: 'experience', maxChars: 28 },
  NLSExperienceRaidArenaBattlefieldDetail: { text: 'HP + leaderboard + resolve controls.', description: 'Raid arena description.', surface: 'experience', maxChars: 62 },
  NLSExperienceRaidArenaWarRoom: { text: 'War Room', description: 'Raid arena label.', surface: 'experience', maxChars: 26 },
  NLSExperienceRaidArenaWarRoomDetail: { text: 'Big enrage clock + top-5 kill feed.', description: 'Raid arena description.', surface: 'experience', maxChars: 62 },
  NLSExperienceRaidArenaConstellation: { text: 'Constellation', description: 'Raid arena label.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidArenaConstellationDetail: { text: 'Boss core with task satellites.', description: 'Raid arena description.', surface: 'experience', maxChars: 58 },
  NLSExperienceRaidArenaTimeline: { text: 'Timeline', description: 'Raid arena label.', surface: 'experience', maxChars: 24 },
  NLSExperienceRaidArenaTimelineDetail: { text: 'Subtree stripboard by status.', description: 'Raid arena description.', surface: 'experience', maxChars: 56 },
  NLSExperienceRaidTitle: { text: 'Raid', description: 'Raid shell title.', surface: 'experience', maxChars: 20 },
  NLSExperienceRaidSubtitle: { text: 'Boss engagement', description: 'Raid shell subtitle.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidAria: { text: 'Co-op Raid', description: 'Raid shell accessible label.', surface: 'experience', maxChars: 28 },
  NLSExperienceRaidCoop: { text: 'Co-op Raid', description: 'Raid hero eyebrow.', surface: 'experience', maxChars: 28 },
  NLSExperienceRaidTarget: { text: 'Target', description: 'Raid target label.', surface: 'experience', maxChars: 18 },
  NLSExperienceRaidNoTargets: {
    text: 'No goals, tasks, or subtasks in this workspace yet. Add work in the Timeline first.',
    description: 'Raid no-target empty text.',
    surface: 'experience',
    maxChars: 110,
  },
  NLSExperienceRaidTargetPlaceholder: { text: 'Pick a Goal, Task, or Subtask...', description: 'Raid target select placeholder.', surface: 'experience', maxChars: 50 },
  NLSExperienceRaidTargetAria: { text: 'Raid target', description: 'Raid target select aria label.', surface: 'experience', maxChars: 28 },
  NLSExperienceRaidBossTitle: { text: 'Boss title', description: 'Raid boss title field label.', surface: 'experience', maxChars: 30 },
  NLSExperienceRaidEnrageDays: { text: 'Enrage in (days) · Orbit', description: 'Raid enrage input label.', surface: 'experience', maxChars: 46 },
  NLSExperienceRaidEnrageDaysAria: { text: 'Enrage in days', description: 'Raid enrage input aria label.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidFundingCredits: { text: 'Funding (credits) · Stack', description: 'Raid funding input label.', surface: 'experience', maxChars: 48 },
  NLSExperienceRaidFundingCreditsAria: { text: 'Funding credits', description: 'Raid funding input aria label.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidPenaltyLabel: {
    text: 'Penalty raid - {multiplier}x pool on victory, {credits}c curse on failure',
    description: 'Raid penalty checkbox label. {multiplier} and {credits} are numbers.',
    surface: 'experience',
    maxChars: 96,
  },
  NLSExperienceRaidHpPreview: { text: 'Boss HP preview', description: 'Raid HP preview label.', surface: 'experience', maxChars: 36 },
  NLSExperienceRaidSplit: { text: 'Split: {label}', description: 'Raid reward split label. {label} is a runtime split label.', surface: 'experience', maxChars: 70 },
  NLSExperienceRaidForgeBoss: { text: 'Forge Boss', description: 'Start raid action.', surface: 'experience', maxChars: 30 },
  NLSExperienceRaidForging: { text: 'Forging', description: 'Start raid busy label.', surface: 'experience', maxChars: 24 },
  NLSExperienceRaidManageRequired: {
    text: 'Only Admins and Editors can forge raids. Ask a workspace lead to start the next boss.',
    description: 'Raid permission gate body.',
    surface: 'experience',
    maxChars: 110,
  },
  NLSExperienceRaidBlueprint: { text: 'Raid Blueprint', description: 'Raid blueprint action.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidHideBlueprint: { text: 'Hide Blueprint', description: 'Hide raid blueprint action.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidArena: { text: 'Arena', description: 'Raid arena rail label.', surface: 'experience', maxChars: 18 },
  NLSExperienceRaidBoss: { text: 'Boss', description: 'Raid boss rail label.', surface: 'experience', maxChars: 18 },
  NLSExperienceRaidPenaltyVictory: { text: 'Penalty · {multiplier}x on victory', description: 'Active penalty raid label. {multiplier} is a number.', surface: 'experience', maxChars: 54 },
  NLSExperienceRaidNormal: { text: 'Normal raid', description: 'Active normal raid label.', surface: 'experience', maxChars: 28 },
  NLSExperienceRaidPoolCredits: { text: 'Pool {credits}c', description: 'Raid pool credit label. {credits} is a number.', surface: 'experience', maxChars: 28 },
  NLSExperienceRaidContributorSummary: {
    text: '{count} contributors · {damage} total DMG',
    description: 'Raid contributor summary. {count} and {damage} are numbers.',
    surface: 'experience',
    maxChars: 60,
  },
  NLSExperienceRaidEndEarly: { text: 'End Raid Early', description: 'End raid early action.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidBreakCurse: { text: 'Break Curse ({credits}c)', description: 'Break curse action. {credits} is a number.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidBreakCurseTitle: { text: 'Curse-break payment lands in a follow-up wallet integration.', description: 'Break curse disabled tooltip.', surface: 'experience', maxChars: 84 },
  NLSExperienceRaidTargetLine: { text: 'Target · {title}', description: 'Raid active target label. {title} is a runtime target title.', surface: 'experience', maxChars: 80 },
  NLSExperienceRaidForgeBossTitle: { text: 'Forge a boss', description: 'Raid idle hero title.', surface: 'experience', maxChars: 32 },
  NLSExperienceRaidForgeBossBody: {
    text: 'Turn any Goal, Task, or Subtask into a workspace boss. Every checklist, task completion, and focus sprint chips away at its HP.',
    description: 'Raid idle hero body.',
    surface: 'experience',
    maxChars: 150,
  },
  NLSExperienceRaidPreviewHp: { text: 'Preview HP', description: 'Raid preview HP badge label.', surface: 'experience', maxChars: 28 },
  NLSExperienceRaidUntitled: { text: 'Untitled', description: 'Raid target untitled fallback.', surface: 'experience', maxChars: 24 },
  NLSExperienceRaidTargetTasks: { text: '{count} tasks in subtree', description: 'Raid target task count. {count} is a number.', surface: 'experience', maxChars: 42 },
  NLSExperienceRaidTargetChecklists: { text: '{count} checklists ({done} done)', description: 'Raid target checklist count. {count} and {done} are numbers.', surface: 'experience', maxChars: 54 },
  NLSExperienceRaidTargetWeight: { text: 'Weight avg {weight}', description: 'Raid target average weight. {weight} is formatted.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidEmpty: { text: 'Empty', description: 'Raid empty target kind.', surface: 'experience', maxChars: 18 },
  NLSExperienceRaidNothingSelected: { text: 'Nothing selected', description: 'Raid no selection title.', surface: 'experience', maxChars: 34 },
  NLSExperienceRaidChooseTarget: {
    text: 'Choose a target from the rail. Any Goal, Task, or Subtask on the Timeline is fair game.',
    description: 'Raid no selection body.',
    surface: 'experience',
    maxChars: 120,
  },
  NLSExperienceRaidDamageHow: { text: 'How damage lands', description: 'Raid damage education title.', surface: 'experience', maxChars: 38 },
  NLSExperienceRaidDamageChecklist: { text: 'per checklist completion', description: 'Raid checklist damage label.', surface: 'experience', maxChars: 42 },
  NLSExperienceRaidDamageTask: { text: 'per task/subtask completion', description: 'Raid task damage label.', surface: 'experience', maxChars: 42 },
  NLSExperienceRaidDamageFocus: { text: 'per completed focus sprint', description: 'Raid focus damage label.', surface: 'experience', maxChars: 42 },
  NLSExperienceRaidPayout: { text: 'Payout', description: 'Raid payout education title.', surface: 'experience', maxChars: 18 },
  NLSExperienceRaidServerPayout: { text: '{label}. Server pays out on victory.', description: 'Raid payout body. {label} is a runtime split label.', surface: 'experience', maxChars: 90 },
  NLSExperienceRaidPastEmpty: {
    text: 'No raids have finished yet. When your workspace clears its first boss, it lands here.',
    description: 'Past raids empty state.',
    surface: 'experience',
    maxChars: 110,
  },
  NLSExperienceRaidCouldNotStart: { text: 'Could not start raid.', description: 'Raid start fallback error.', surface: 'experience', maxChars: 42 },
  NLSExperienceRaidCouldNotResolve: { text: 'Could not resolve raid.', description: 'Raid resolve fallback error.', surface: 'experience', maxChars: 42 },
  NLSExperienceRaidLiveAudit: { text: 'Live audit', description: 'Raid audit ledger eyebrow.', surface: 'experience', maxChars: 26 },
  NLSExperienceRaidFeed: { text: 'Raid feed', description: 'Raid audit ledger title.', surface: 'experience', maxChars: 24 },
  NLSExperienceRaidDamageEmpty: { text: 'Damage events appear here as they land.', description: 'Raid audit empty body.', surface: 'experience', maxChars: 64 },
  NLSExperienceRaidDamageDetailTop: { text: 'landed {damage} DMG - top of the board', description: 'Top raid damage detail. {damage} is a number.', surface: 'experience', maxChars: 70 },
  NLSExperienceRaidDamageDetail: { text: 'landed {damage} DMG', description: 'Raid damage detail. {damage} is a number.', surface: 'experience', maxChars: 42 },
  NLSExperienceDebugModeActiveAria: { text: 'Debug mode is active', description: 'Debug mode indicator aria label.', surface: 'experience', maxChars: 42 },
  NLSExperienceDebugModeActiveTitle: { text: 'Debug mode is active - open capture panel', description: 'Debug mode indicator tooltip.', surface: 'experience', maxChars: 70 },
  NLSExperienceDebug: { text: 'Debug', description: 'Debug indicator label.', surface: 'experience', maxChars: 18 },
  NLSExperienceDebugCapturePanelAria: { text: 'Debug capture panel', description: 'Debug popover aria label.', surface: 'experience', maxChars: 42 },
  NLSExperienceDebugCapture: { text: 'Debug capture', description: 'Debug popover title.', surface: 'experience', maxChars: 34 },
  NLSExperienceDebugSession: { text: 'Session {id}', description: 'Debug session id label. {id} is a short id.', surface: 'experience', maxChars: 34 },
  NLSExperienceDebugClosePanelAria: { text: 'Close debug capture panel', description: 'Close debug popover aria label.', surface: 'experience', maxChars: 50 },
  NLSExperienceDebugCounterEvents: { text: 'events', description: 'Debug counter label.', surface: 'experience', maxChars: 16 },
  NLSExperienceDebugCounterShots: { text: 'shots', description: 'Debug counter label.', surface: 'experience', maxChars: 16 },
  NLSExperienceDebugCounterFiles: { text: 'files', description: 'Debug counter label.', surface: 'experience', maxChars: 16 },
  NLSExperienceDebugCounterNet: { text: 'net', description: 'Debug counter label.', surface: 'experience', maxChars: 12 },
  NLSExperienceDebugCounterBundle: { text: 'bundle', description: 'Debug counter label.', surface: 'experience', maxChars: 18 },
  NLSExperienceDebugCounterElapsed: { text: 'elapsed', description: 'Debug counter label.', surface: 'experience', maxChars: 18 },
  NLSExperienceDebugCaptureScreenshot: { text: 'Capture a screenshot now', description: 'Screenshot capture section label.', surface: 'experience', maxChars: 46 },
  NLSExperienceDebugScreenshotPlaceholder: { text: 'Optional label (e.g. broken state)', description: 'Screenshot label placeholder.', surface: 'experience', maxChars: 60 },
  NLSExperienceDebugCapturing: { text: 'Capturing...', description: 'Screenshot capture busy label.', surface: 'experience', maxChars: 28 },
  NLSExperienceDebugCaptureAction: { text: 'Capture', description: 'Screenshot capture action.', surface: 'experience', maxChars: 20 },
  NLSExperienceDebugAttachFiles: { text: 'Attach files (images, PDFs, logs)', description: 'Debug attachment section label.', surface: 'experience', maxChars: 56 },
  NLSExperienceDebugOptionalLabel: { text: 'Optional label', description: 'Debug optional label placeholder.', surface: 'experience', maxChars: 30 },
  NLSExperienceDebugAddFiles: { text: 'Add files', description: 'Debug add files action.', surface: 'experience', maxChars: 24 },
  NLSExperienceDebugAddNote: { text: 'Add a note to the timeline', description: 'Debug note section label.', surface: 'experience', maxChars: 50 },
  NLSExperienceDebugNotePlaceholder: { text: 'e.g. clicked Save, spinner never stopped', description: 'Debug note placeholder.', surface: 'experience', maxChars: 70 },
  NLSExperienceDebugAppendNote: { text: 'Append note', description: 'Debug append note action.', surface: 'experience', maxChars: 28 },
  NLSExperienceDebugInBundle: { text: 'In this bundle', description: 'Debug bundle manifest label.', surface: 'experience', maxChars: 32 },
  NLSExperienceDebugRemoveScreenshotAria: { text: 'Remove screenshot', description: 'Remove debug screenshot aria label.', surface: 'experience', maxChars: 36 },
  NLSExperienceDebugRemoveAttachmentAria: { text: 'Remove attachment', description: 'Remove debug attachment aria label.', surface: 'experience', maxChars: 36 },
  NLSExperienceDebugManifestHint: {
    text: 'You can remove your own captures. Recorded events cannot be edited before sending.',
    description: 'Debug manifest hint.',
    surface: 'experience',
    maxChars: 110,
  },
  NLSExperienceDebugSendsTo: { text: 'Sends to', description: 'Debug recipient footer prefix.', surface: 'experience', maxChars: 18 },
  NLSExperienceDebugNoRecipient: { text: 'no recipient configured', description: 'Debug missing recipient label.', surface: 'experience', maxChars: 38 },
  NLSExperienceDebugEndSend: { text: 'End & Send', description: 'Debug end and send action.', surface: 'experience', maxChars: 28 },
  NLSExperienceDebugEndTitle: { text: 'End Debug Mode', description: 'End debug mode dialog title.', surface: 'experience', maxChars: 34 },
  NLSExperienceDebugEndSummary: {
    text: 'Bundle summary - this exact set will be emailed. Events cannot be edited; only your added captures.',
    description: 'End debug summary body.',
    surface: 'experience',
    maxChars: 130,
  },
  NLSExperienceDebugEventsCount: { text: '{count} events', description: 'Debug event count. {count} is a number.', surface: 'experience', maxChars: 26 },
  NLSExperienceDebugScreenshotsCount: { text: '{count} screenshots', description: 'Debug screenshot count. {count} is a number.', surface: 'experience', maxChars: 34 },
  NLSExperienceDebugAttachedFilesCount: { text: '{count} attached files', description: 'Debug attached file count. {count} is a number.', surface: 'experience', maxChars: 38 },
  NLSExperienceDebugNetworkCount: { text: '{count} network calls captured', description: 'Debug network count. {count} is a number.', surface: 'experience', maxChars: 46 },
  NLSExperienceDebugBundleSize: { text: 'Bundle size ~ {size}', description: 'Debug bundle size. {size} is formatted.', surface: 'experience', maxChars: 36 },
  NLSExperienceDebugFinalMessage: { text: 'Final message (optional)', description: 'Debug final message label.', surface: 'experience', maxChars: 42 },
  NLSExperienceDebugFinalMessagePlaceholder: { text: 'Anything else the reader should know?', description: 'Debug final message placeholder.', surface: 'experience', maxChars: 70 },
  NLSExperienceDebugRecipient: { text: 'Recipient: {email}', description: 'Debug recipient label. {email} is an email address.', surface: 'experience', maxChars: 90 },
  NLSExperienceDebugNoRecipientDownload: {
    text: 'No recipient email is configured; the zip will still download locally.',
    description: 'Debug no recipient dialog note.',
    surface: 'experience',
    maxChars: 100,
  },
  NLSExperienceDebugSending: { text: 'Sending...', description: 'Debug send busy label.', surface: 'experience', maxChars: 26 },
  NLSExperienceDebugEndSendBundle: { text: 'End and Send Bundle', description: 'Debug end and send dialog action.', surface: 'experience', maxChars: 40 },
  NLSExperienceDebugCaptureFailed: { text: 'Could not capture the screen right now.', description: 'Screenshot capture failure toast.', surface: 'experience', maxChars: 62 },
  NLSExperienceDebugQuotaReached: { text: 'Attachment quota (15 MB) reached.', description: 'Debug attachment quota toast.', surface: 'experience', maxChars: 52 },
  NLSExperienceDebugScreenshotSaveFailed: { text: 'Screenshot could not be saved.', description: 'Screenshot save failure toast.', surface: 'experience', maxChars: 52 },
  NLSExperienceDebugScreenshotAdded: { text: 'Screenshot added to the debug bundle.', description: 'Screenshot added toast.', surface: 'experience', maxChars: 58 },
  NLSExperienceDebugAttachedFilesToast: {
    text: 'Attached {count} file{plural} to the debug bundle.',
    description: 'Debug files attached toast. {count} is a number; {plural} is s or empty.',
    surface: 'experience',
    maxChars: 70,
  },
  NLSExperienceDebugNoteAdded: { text: 'Note added to the timeline.', description: 'Debug note added toast.', surface: 'experience', maxChars: 46 },
  NLSExperienceDebugEndedNoSession: { text: 'Debug mode ended. No debug session was available to send.', description: 'Debug ended missing session toast.', surface: 'experience', maxChars: 82 },
  NLSExperienceDebugBundleEmailed: { text: 'Debug bundle emailed to {email}.', description: 'Debug email success toast. {email} is an email address.', surface: 'experience', maxChars: 90 },
  NLSExperienceDebugBundleDownloaded: {
    text: 'Debug bundle downloaded. Email endpoint is not configured - attach the zip to the email that opened.',
    description: 'Debug downloaded fallback toast.',
    surface: 'experience',
    maxChars: 130,
  },
  NLSExperienceDebugCouldNotSend: { text: 'Could not send debug logs.', description: 'Debug send fallback error.', surface: 'experience', maxChars: 50 },
  NLSAuthBrandName: {
    text: 'Timelining',
    description: 'Product name shown on authenticated app sign-in surfaces.',
    surface: 'auth',
    maxChars: 20,
  },
  NLSAuthEyebrowCloudWorkspace: {
    text: 'Cloud workspace',
    description: 'Eyebrow label on the web login surface.',
    surface: 'auth',
    maxChars: 34,
  },
  NLSAuthEyebrowHelperAccess: {
    text: 'Helper access',
    description: 'Eyebrow label on the helper phone login surface.',
    surface: 'auth',
    maxChars: 34,
  },
  NLSAuthTitleCloudWorkspace: {
    text: "Map your goals. Or your team's.",
    description: 'Headline for normal web login.',
    surface: 'auth',
    maxChars: 80,
  },
  NLSAuthTitleHelperPhone: {
    text: 'Sign in with your phone.',
    description: 'Headline for helper phone login.',
    surface: 'auth',
    maxChars: 70,
  },
  NLSAuthBodyCloudWorkspace: {
    text: 'Sign in to create private or shared workspaces, generate plans with AI, and keep everything synced across devices.',
    description: 'Body copy for normal web login.',
    surface: 'auth',
    maxChars: 180,
  },
  NLSAuthBodyHelperPhone: {
    text: 'Enter the phone number your admin registered for you. You will receive a verification code by SMS.',
    description: 'Body copy for helper phone login.',
    surface: 'auth',
    maxChars: 170,
  },
  NLSAuthActionCheckSession: {
    text: 'Check session',
    description: 'Button label for unlocking or checking an existing session.',
    surface: 'auth',
    maxChars: 32,
  },
  NLSAuthActionContinueGoogle: {
    text: 'Continue with Google',
    description: 'Button label for Google sign-in.',
    surface: 'auth',
    maxChars: 38,
  },
  NLSAuthActionOpeningGoogle: {
    text: 'Opening Google…',
    description: 'Busy label while Google sign-in opens.',
    surface: 'auth',
    maxChars: 34,
  },
  NLSAuthActionSendOtp: {
    text: 'Send OTP',
    description: 'Button label for requesting an SMS code.',
    surface: 'auth',
    maxChars: 22,
  },
  NLSAuthActionSendingOtp: {
    text: 'Sending OTP…',
    description: 'Busy label while requesting an SMS code.',
    surface: 'auth',
    maxChars: 28,
  },
  NLSAuthActionVerifySignIn: {
    text: 'Verify & sign in',
    description: 'Button label for verifying an OTP and signing in.',
    surface: 'auth',
    maxChars: 34,
  },
  NLSAuthActionVerifying: {
    text: 'Verifying…',
    description: 'Busy label while verifying an OTP.',
    surface: 'auth',
    maxChars: 28,
  },
  NLSAuthActionUseDifferentNumber: {
    text: 'Use a different number',
    description: 'Button label for returning from OTP entry to phone entry.',
    surface: 'auth',
    maxChars: 44,
  },
  NLSAuthFieldPhoneNumber: {
    text: 'Phone number',
    description: 'Phone number input label on helper login.',
    surface: 'auth',
    maxChars: 32,
  },
  NLSAuthFieldVerificationCode: {
    text: 'Verification code',
    description: 'OTP input label on helper login.',
    surface: 'auth',
    maxChars: 34,
  },
  NLSAuthErrorChoosePhone: {
    text: 'Choose a country code and enter the phone number digits.',
    description: 'Validation error for incomplete helper phone sign-in input.',
    surface: 'auth',
    maxChars: 90,
  },
  NLSAuthErrorSendOtp: {
    text: 'Could not send OTP. Please try again.',
    description: 'Fallback error when helper OTP sending fails.',
    surface: 'auth',
    maxChars: 70,
  },
  NLSAuthErrorEnterOtp: {
    text: 'Enter the numeric OTP you received.',
    description: 'Validation error for malformed OTP input.',
    surface: 'auth',
    maxChars: 70,
  },
  NLSAuthErrorInvalidOtp: {
    text: 'Invalid OTP. Please try again.',
    description: 'Fallback error when OTP verification fails.',
    surface: 'auth',
    maxChars: 60,
  },
  NLSAuthErrorGoogleSignIn: {
    text: 'Could not sign in. Please try again.',
    description: 'Fallback error when Google sign-in fails.',
    surface: 'auth',
    maxChars: 70,
  },
  NLSOnboardingAriaWorkspaceSetup: {
    text: 'Workspace setup',
    description: 'Accessible label for the onboarding gate page.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSOnboardingDialogTeamSetup: {
    text: 'Set up your new workspace',
    description: 'Accessible label for the team setup dialog.',
    surface: 'onboarding',
    maxChars: 58,
  },
  NLSOnboardingDialogPersonalSetup: {
    text: 'Set up your Personal Space',
    description: 'Accessible label for the personal setup dialog.',
    surface: 'onboarding',
    maxChars: 58,
  },
  NLSOnboardingEyebrowNewSpace: {
    text: 'A new space is waiting',
    description: 'Eyebrow on the normal onboarding landing stage.',
    surface: 'onboarding',
    maxChars: 46,
  },
  NLSOnboardingEyebrowWorkspaceSetup: {
    text: 'Workspace setup',
    description: 'Eyebrow on the switch-workspace setup chooser.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSOnboardingTitleBegin: {
    text: 'Where would you like to begin?',
    description: 'Title on the onboarding workspace choice stage.',
    surface: 'onboarding',
    maxChars: 70,
  },
  NLSOnboardingTitleSwitchSetup: {
    text: 'Choose an available setup.',
    description: 'Title on the switch-workspace setup chooser.',
    surface: 'onboarding',
    maxChars: 66,
  },
  NLSOnboardingLedeOwnership: {
    text: 'One account can own one Personal Space and one Team workspace. Invites and public-team requests stay separate.',
    description: 'Onboarding explanation for workspace ownership limits.',
    surface: 'onboarding',
    maxChars: 180,
  },
  NLSOnboardingPersonalEyebrow: {
    text: 'Solo · private',
    description: 'Eyebrow for the Personal Space onboarding option.',
    surface: 'onboarding',
    maxChars: 34,
  },
  NLSOnboardingPersonalTitle: {
    text: 'Personal Space',
    description: 'Title for the Personal Space onboarding option.',
    surface: 'onboarding',
    maxChars: 34,
  },
  NLSOnboardingPersonalDescription: {
    text: 'A blank canvas for your goals, plans, and AI-drafted timelines. Nobody sees it but you.',
    description: 'Description for the Personal Space onboarding option.',
    surface: 'onboarding',
    maxChars: 150,
  },
  NLSOnboardingActionLaunchPersonal: {
    text: 'Launch Personal Space',
    description: 'CTA for opening or creating a Personal Space.',
    surface: 'onboarding',
    maxChars: 44,
  },
  NLSOnboardingTeamEyebrowCreate: {
    text: 'Collaborate · public or private',
    description: 'Eyebrow for the Team workspace creation option.',
    surface: 'onboarding',
    maxChars: 52,
  },
  NLSOnboardingTeamEyebrowAccess: {
    text: 'Invites · public teams',
    description: 'Eyebrow for the Team access option when creation is unavailable.',
    surface: 'onboarding',
    maxChars: 46,
  },
  NLSOnboardingTeamTitleCreate: {
    text: 'Team Workspace',
    description: 'Title for the Team workspace creation option.',
    surface: 'onboarding',
    maxChars: 38,
  },
  NLSOnboardingTeamTitleAccess: {
    text: 'Team access',
    description: 'Title for the Team access option.',
    surface: 'onboarding',
    maxChars: 34,
  },
  NLSOnboardingTeamDescriptionCreate: {
    text: 'Join by invite, request access to a public team, or found your one Team workspace.',
    description: 'Description for the Team workspace creation option.',
    surface: 'onboarding',
    maxChars: 150,
  },
  NLSOnboardingTeamDescriptionAccess: {
    text: 'You already own a Team workspace. You can still accept invites or request access to teams owned by others.',
    description: 'Description when Team creation is unavailable.',
    surface: 'onboarding',
    maxChars: 170,
  },
  NLSOnboardingActionExploreTeam: {
    text: 'Explore team options',
    description: 'CTA for opening Team workspace options.',
    surface: 'onboarding',
    maxChars: 44,
  },
  NLSOnboardingActionViewInvitesTeams: {
    text: 'View invites and public teams',
    description: 'CTA for opening Team access options.',
    surface: 'onboarding',
    maxChars: 58,
  },
  NLSOnboardingInviteWaiting: {
    text: '{count} invite waiting',
    description: 'Singular pending invite badge on onboarding.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSOnboardingInvitesWaiting: {
    text: '{count} invites waiting',
    description: 'Plural pending invite badge on onboarding.',
    surface: 'onboarding',
    maxChars: 42,
  },
  NLSOnboardingInviteBannerRegionAriaLabel: {
    text: 'Pending invitations',
    description: 'Accessibility region label for pending invite banner.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSOnboardingInviteBannerHint: {
    text: 'You have been invited to join a team workspace.',
    description: 'Hint text inside the pending invite banner on onboarding.',
    surface: 'onboarding',
    maxChars: 70,
  },
  NLSOnboardingTeamDescriptionInvitesWaiting: {
    text: 'You have pending team invitations waiting for your response. Review and accept to join.',
    description: 'Description on team hero card when invitations are waiting.',
    surface: 'onboarding',
    maxChars: 120,
  },
  NLSOnboardingInvitesLoadError: {
    text: 'Could not load invitations.',
    description: 'Error message when loading pending invitations fails.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSPendingInvitesRetry: {
    text: 'Retry',
    description: 'Button label to retry loading pending invitations.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSPendingInvitesLoadError: {
    text: 'Could not load pending invites.',
    description: 'Fallback error message when loading pending invitations fails.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSPendingInvitesAcceptError: {
    text: 'Could not accept this invite.',
    description: 'Fallback error message when accepting an invitation fails.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSPendingInvitesDeclineError: {
    text: 'Could not decline this invite.',
    description: 'Fallback error message when declining an invitation fails.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSOnboardingFootnoteNoSetups: {
    text: 'You already own your Personal Space and Team workspace. Use invites or public-team requests to access teams owned by others.',
    description: 'Footnote when no owned workspace setup choices are available.',
    surface: 'onboarding',
    maxChars: 190,
  },
  NLSOnboardingFootnoteOwnershipCap: {
    text: 'Ownership cap: one Personal Space and one Team workspace per account.',
    description: 'Footnote explaining workspace ownership caps.',
    surface: 'onboarding',
    maxChars: 110,
  },
  NLSOnboardingTeamStageEyebrow: {
    text: 'Team workspaces',
    description: 'Eyebrow for the Team workspace options stage.',
    surface: 'onboarding',
    maxChars: 38,
  },
  NLSOnboardingTeamStageTitle: {
    text: 'Join a team, discover a public one, or found your own.',
    description: 'Title for the Team workspace options stage.',
    surface: 'onboarding',
    maxChars: 100,
  },
  NLSOnboardingTabInvites: {
    text: 'Invites',
    description: 'Onboarding Team tab for invitations.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSOnboardingTabDiscover: {
    text: 'Discover',
    description: 'Onboarding Team tab for public workspace discovery.',
    surface: 'onboarding',
    maxChars: 26,
  },
  NLSOnboardingTabCreateNew: {
    text: 'Create new',
    description: 'Onboarding Team tab for creating a workspace.',
    surface: 'onboarding',
    maxChars: 28,
  },
  NLSOnboardingEmptyNoInvitesTitle: {
    text: 'No invites right now',
    description: 'Empty state title when onboarding has no pending invites.',
    surface: 'onboarding',
    maxChars: 42,
  },
  NLSOnboardingEmptyNoInvitesForEmail: {
    text: "We'll show any invite sent to {email} here.",
    description: 'Empty state body when signed-in onboarding has no pending invites. {email} is the normalized address.',
    surface: 'onboarding',
    maxChars: 110,
  },
  NLSOnboardingEmptySignInInvites: {
    text: 'Sign in to see invites addressed to you.',
    description: 'Empty state body when onboarding invite lookup has no email.',
    surface: 'onboarding',
    maxChars: 80,
  },
  NLSOnboardingSearchPublicTeamsPlaceholder: {
    text: 'Search public teams by name',
    description: 'Search input placeholder for onboarding public Team discovery.',
    surface: 'onboarding',
    maxChars: 58,
  },
  NLSOnboardingActionAccept: {
    text: 'Accept',
    description: 'Accepts a workspace invite.',
    surface: 'onboarding',
    maxChars: 18,
  },
  NLSOnboardingActionDecline: {
    text: 'Decline',
    description: 'Declines a workspace invite.',
    surface: 'onboarding',
    maxChars: 18,
  },
  NLSOnboardingActionEnter: {
    text: 'Enter',
    description: 'Enters an accepted workspace.',
    surface: 'onboarding',
    maxChars: 18,
  },
  NLSOnboardingActionRequested: {
    text: 'Requested',
    description: 'Disabled state after requesting access to a workspace.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSOnboardingActionRequestAccess: {
    text: 'Request access',
    description: 'Requests access to a public workspace.',
    surface: 'onboarding',
    maxChars: 34,
  },
  NLSSetupStepIntentEyebrow: {
    text: 'Warm-up',
    description: 'Eyebrow for warm-up intent step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSSetupStepIntentQuestion: {
    text: 'What do you have in mind?',
    description: 'Question for warm-up intent step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupStepIntentSub: {
    text: 'Tell us in your own words — a sentence is plenty. We’ll pre-fill a few things from it.',
    description: 'Subtext for warm-up intent step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 120,
  },
  NLSSetupStepRoleEyebrow: {
    text: 'You',
    description: 'Eyebrow for role step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSSetupStepRoleQuestion: {
    text: 'What best describes your role?',
    description: 'Question for role step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupStepRoleSub: {
    text: 'We use this to shape the defaults, not to label you.',
    description: 'Subtext for role step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 90,
  },
  NLSSetupStepUseCaseEyebrow: {
    text: 'Intent',
    description: 'Eyebrow for use case step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSSetupStepUseCaseQuestion: {
    text: 'Which of these is closest to how you’ll use it?',
    description: 'Question for use case step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 70,
  },
  NLSSetupStepUseCaseSub: {
    text: 'Pick the closest one — you can mix and match later.',
    description: 'Subtext for use case step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 90,
  },
  NLSSetupStepCurrencyEyebrow: {
    text: 'Currency',
    description: 'Eyebrow for currency step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSSetupStepCurrencyQuestion: {
    text: 'What currency does your work happen in?',
    description: 'Question for currency step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupStepCurrencySub: {
    text: "You can't change this later — pick with intention.",
    description: 'Subtext for currency step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 90,
  },
  NLSSetupStepWhoEyebrow: {
    text: 'Shape',
    description: 'Eyebrow for who-will-use-this step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSSetupStepWhoQuestion: {
    text: 'Who will use this?',
    description: 'Question for who-will-use-this step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupStepWhoSub: {
    text: 'We’ll tailor your workspace defaults and invite tools.',
    description: 'Subtext for who-will-use-this step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 90,
  },
  NLSSetupStepFocusAreasEyebrow: {
    text: 'Life',
    description: 'Eyebrow for focus areas step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSSetupStepFocusAreasQuestion: {
    text: 'What do you want this workspace to help you organise?',
    description: 'Question for focus areas step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 70,
  },
  NLSSetupStepFocusAreasSub: {
    text: 'Pick as many as feel right — or skip and add later.',
    description: 'Subtext for focus areas step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 90,
  },
  NLSSetupStepAppearanceEyebrow: {
    text: 'Feel',
    description: 'Eyebrow for appearance step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSSetupStepAppearanceQuestion: {
    text: 'How would you like it to look?',
    description: 'Question for appearance step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupStepAppearanceSub: {
    text: 'Nothing is permanent — tweak anytime in Settings.',
    description: 'Subtext for appearance step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 90,
  },
  NLSSetupStepExtrasEyebrow: {
    text: 'Behaviour',
    description: 'Eyebrow for extras step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSSetupStepExtrasQuestion: {
    text: 'A couple of small choices to finish.',
    description: 'Question for extras step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupStepExtrasSub: {
    text: 'Turn on what sounds useful. All of these are togglable later.',
    description: 'Subtext for extras step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 100,
  },
  NLSSetupStepWorkspaceNameEyebrow: {
    text: 'Christening',
    description: 'Eyebrow for workspace name step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSSetupStepWorkspaceNameQuestion: {
    text: 'What should we call this workspace?',
    description: 'Question for workspace name step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupStepWorkspaceNameSub: {
    text: 'Give it a name and a permanent handle.',
    description: 'Subtext for workspace name step in onboarding setup.',
    surface: 'onboarding',
    maxChars: 90,
  },
  NLSSetupWhoSoloLabel: {
    text: 'Just me',
    description: 'Option label for solo audience choice in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupWhoSoloHint: {
    text: 'Solo canvas',
    description: 'Option hint for solo audience choice in onboarding.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupWhoHelpersLabel: {
    text: 'Me + helpers',
    description: 'Option label for solo with helpers audience choice in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupWhoHelpersHint: {
    text: 'Field or operational help',
    description: 'Option hint for solo with helpers audience choice in onboarding.',
    surface: 'onboarding',
    maxChars: 45,
  },
  NLSSetupWhoTeamLabel: {
    text: 'A team',
    description: 'Option label for team audience choice in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupWhoTeamHint: {
    text: 'Collaborate together',
    description: 'Option hint for team audience choice in onboarding.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupTeamSizeSmallLabel: {
    text: '2–5',
    description: 'Chip label for small team size choice in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupTeamSizeSmallHint: {
    text: 'Small crew',
    description: 'Chip hint for small team size choice in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupTeamSizeMediumLabel: {
    text: '6–20',
    description: 'Chip label for medium team size choice in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupTeamSizeMediumHint: {
    text: 'Growing team',
    description: 'Chip hint for medium team size choice in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupTeamSizeLargeLabel: {
    text: '20+',
    description: 'Chip label for large team size choice in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupTeamSizeLargeHint: {
    text: 'Org scale',
    description: 'Chip hint for large team size choice in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupUseCasePersonalGoals: {
    text: 'Personal goals',
    description: 'Use case title for personal goals in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCasePersonalGoalsHint: {
    text: 'Habits · milestones · life',
    description: 'Use case hint for personal goals in onboarding.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupUseCaseTeamProjects: {
    text: 'Team projects',
    description: 'Use case title for team projects in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseTeamProjectsHint: {
    text: 'Ship together, on time',
    description: 'Use case hint for team projects in onboarding.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupUseCaseStudyPlanning: {
    text: 'Study planning',
    description: 'Use case title for study planning in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseStudyPlanningHint: {
    text: 'Semesters, exams, deep-work',
    description: 'Use case hint for study planning in onboarding.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupUseCaseContentPlanning: {
    text: 'Content planning',
    description: 'Use case title for content planning in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseContentPlanningHint: {
    text: 'Scripts, drops, calendars',
    description: 'Use case hint for content planning in onboarding.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupUseCaseStartupBuilding: {
    text: 'Building a startup',
    description: 'Use case title for building a startup in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupUseCaseStartupBuildingHint: {
    text: 'Founding, launching, fundraising',
    description: 'Use case hint for building a startup in onboarding.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupUseCaseOther: {
    text: 'Something else',
    description: 'Use case title for other in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseOtherHint: {
    text: 'Tell us the shape',
    description: 'Use case hint for other in onboarding.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupUseCaseFieldTeam: {
    text: 'Field team',
    description: 'Use case title for field team in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseFieldTeamHint: {
    text: 'Staff on the move, tasks and checks',
    description: 'Use case hint for field team in onboarding.',
    surface: 'onboarding',
    maxChars: 55,
  },
  NLSSetupUseCaseShopStock: {
    text: 'Shop & stock',
    description: 'Use case title for shop & stock in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseShopStockHint: {
    text: 'Inventory, counts and spend',
    description: 'Use case hint for shop & stock in onboarding.',
    surface: 'onboarding',
    maxChars: 55,
  },
  NLSSetupUseCaseEvents: {
    text: 'Events',
    description: 'Use case title for events in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseEventsHint: {
    text: 'Weddings, launches, shows',
    description: 'Use case hint for events in onboarding.',
    surface: 'onboarding',
    maxChars: 55,
  },
  NLSSetupUseCaseSchool: {
    text: 'School',
    description: 'Use case title for school in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseSchoolHint: {
    text: 'Classes, departments and terms',
    description: 'Use case hint for school in onboarding.',
    surface: 'onboarding',
    maxChars: 55,
  },
  NLSSetupUseCaseTuitionCoaching: {
    text: 'Tuition & coaching',
    description: 'Use case title for tuition & coaching in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseTuitionCoachingHint: {
    text: 'Batches, students and progress',
    description: 'Use case hint for tuition & coaching in onboarding.',
    surface: 'onboarding',
    maxChars: 55,
  },
  NLSSetupUseCaseAcademyTraining: {
    text: 'Academy training',
    description: 'Use case title for academy training in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseAcademyTrainingHint: {
    text: 'Cohorts, drills and performance',
    description: 'Use case hint for academy training in onboarding.',
    surface: 'onboarding',
    maxChars: 55,
  },
  NLSSetupUseCaseCorporateTraining: {
    text: 'Corporate training',
    description: 'Use case title for corporate training in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupUseCaseCorporateTrainingHint: {
    text: 'Workshops, tracks and skill paths',
    description: 'Use case hint for corporate training in onboarding.',
    surface: 'onboarding',
    maxChars: 55,
  },
  NLSSetupUseCaseAgency: {
    text: 'Agency & client work',
    description: 'Use case title for agency in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupUseCaseAgencyHint: {
    text: 'Clients, deliverables and approvals',
    description: 'Use case hint for agency in onboarding.',
    surface: 'onboarding',
    maxChars: 55,
  },
  NLSSetupThemeDarkLabel: {
    text: 'Dark',
    description: 'Label for dark theme preference in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupThemeDarkHint: {
    text: 'Deep, focused',
    description: 'Hint for dark theme preference in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupThemeLightLabel: {
    text: 'Light',
    description: 'Label for light theme preference in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupThemeLightHint: {
    text: 'Bright, airy',
    description: 'Hint for light theme preference in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupThemeSystemLabel: {
    text: 'System',
    description: 'Label for system theme preference in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupThemeSystemHint: {
    text: 'Match device',
    description: 'Hint for system theme preference in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupExtraAiTitle: {
    text: 'AI suggestions',
    description: 'Title for AI suggestions toggle in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupExtraAiHint: {
    text: 'Let Timelining draft plans and next-steps in the background.',
    description: 'Hint for AI suggestions toggle in onboarding.',
    surface: 'onboarding',
    maxChars: 110,
  },
  NLSSetupExtraNotifTitle: {
    text: 'Notifications',
    description: 'Title for notifications toggle in onboarding.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupExtraNotifHint: {
    text: 'Gentle nudges when a deadline is close or a teammate mentions you.',
    description: 'Hint for notifications toggle in onboarding.',
    surface: 'onboarding',
    maxChars: 110,
  },
  NLSSetupNameDisplayNameLabel: {
    text: 'Display name',
    description: 'Label for workspace display name field in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupNameDisplayNamePlaceholder: {
    text: 'e.g. Mission Control',
    description: 'Placeholder for workspace display name input in onboarding.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupNameWorkspaceIdLabel: {
    text: 'Workspace ID',
    description: 'Label for workspace ID slug field in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupNameWorkspaceIdPermanent: {
    text: '(permanent)',
    description: 'Permanent note next to workspace ID field label in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupNameWorkspaceIdPlaceholder: {
    text: 'workspace-id',
    description: 'Placeholder for workspace ID slug input in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupNamePublicToggleTitle: {
    text: 'Show in public discovery',
    description: 'Title for public workspace discovery toggle in onboarding.',
    surface: 'onboarding',
    maxChars: 45,
  },
  NLSSetupNamePublicToggleHint: {
    text: 'Anyone can find and request access to this workspace by name.',
    description: 'Hint for public workspace discovery toggle in onboarding.',
    surface: 'onboarding',
    maxChars: 110,
  },
  NLSSetupSlugHintDefault: {
    text: 'Use lowercase letters, numbers, and hyphens.',
    description: 'Default slug hint for workspace creation in onboarding.',
    surface: 'onboarding',
    maxChars: 70,
  },
  NLSSetupSlugHintFormatValid: {
    text: 'Workspace ID format looks good.',
    description: 'Format validation message for workspace slug in onboarding.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupSlugHintChecking: {
    text: 'Checking availability…',
    description: 'Checking availability indicator for workspace slug in onboarding.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupSlugHintAvailable: {
    text: 'Workspace ID is available.',
    description: 'Availability success message for workspace slug in onboarding.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupSlugHintUnavailable: {
    text: 'Workspace ID is not available.',
    description: 'Availability failure message for workspace slug in onboarding.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupSlugHintError: {
    text: 'Could not check this workspace ID right now.',
    description: 'Network or check error for workspace slug in onboarding.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupActionSkip: {
    text: 'Skip',
    description: 'Skip button label in onboarding setup.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupActionSkipThis: {
    text: 'Skip this',
    description: 'Skip this choice button label in onboarding setup.',
    surface: 'onboarding',
    maxChars: 25,
  },
  NLSSetupActionContinue: {
    text: 'Continue',
    description: 'Continue button label in onboarding setup.',
    surface: 'onboarding',
    maxChars: 25,
  },
  NLSSetupActionSend: {
    text: 'Send',
    description: 'Send button label for intent in onboarding setup.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupActionThinking: {
    text: 'Thinking',
    description: 'Thinking state button label in onboarding setup.',
    surface: 'onboarding',
    maxChars: 25,
  },
  NLSSetupActionOpenWorkspace: {
    text: 'Open workspace',
    description: 'Button to finish onboarding and open team workspace.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupActionOpenPersonalSpace: {
    text: 'Open Personal Space',
    description: 'Button to finish onboarding and open personal workspace.',
    surface: 'onboarding',
    maxChars: 35,
  },
  NLSSetupActionBack: {
    text: 'Back',
    description: 'Back navigation button label in onboarding setup.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptTitleUntitled: {
    text: 'Untitled workspace',
    description: 'Fallback receipt title for unnamed team workspace in onboarding.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupReceiptTitlePersonal: {
    text: 'Personal Space',
    description: 'Receipt title for personal space in onboarding.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupReceiptUrlPending: {
    text: 'Workspace URL will be generated',
    description: 'Receipt URL placeholder before slug is chosen in onboarding.',
    surface: 'onboarding',
    maxChars: 55,
  },
  NLSSetupReceiptRoleLabel: {
    text: 'Role',
    description: 'Receipt row label for role in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptUseCaseLabel: {
    text: 'Use case',
    description: 'Receipt row label for use case in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptCurrencyLabel: {
    text: 'Currency',
    description: 'Receipt row label for currency in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptWhoLabel: {
    text: 'Who',
    description: 'Receipt row label for who/audience in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptFocusLabel: {
    text: 'Focus',
    description: 'Receipt row label for focus areas in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptFeelLabel: {
    text: 'Feel',
    description: 'Receipt row label for feel/theme in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptExtrasLabel: {
    text: 'Extras',
    description: 'Receipt row label for extras in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptFootPersonal: {
    text: 'Personal Space stays private and uses fixed limits.',
    description: 'Receipt footer for personal space in onboarding.',
    surface: 'onboarding',
    maxChars: 120,
  },
  NLSSetupReceiptFootTeam: {
    text: 'Every choice is editable later in Workspace Settings.',
    description: 'Receipt footer for team workspace in onboarding.',
    surface: 'onboarding',
    maxChars: 120,
  },
  NLSSetupReceiptAiPill: {
    text: 'AI {state}',
    description: 'Receipt pill label for AI suggestions state in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptNotifPill: {
    text: 'Notifs {state}',
    description: 'Receipt pill label for notifications state in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupReceiptStateOn: {
    text: 'on',
    description: 'On state word for receipt extras in onboarding.',
    surface: 'onboarding',
    maxChars: 10,
  },
  NLSSetupReceiptStateOff: {
    text: 'off',
    description: 'Off state word for receipt extras in onboarding.',
    surface: 'onboarding',
    maxChars: 10,
  },
  NLSSetupFocusSelectedCount: {
    text: '{count} selected.',
    description: 'Counter for selected focus areas in onboarding.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupFocusSkipHint: {
    text: 'Pick as many as you like — or skip.',
    description: 'Hint to skip or pick multiple focus areas in onboarding.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupFreeformPlaceholder: {
    text: "What's on your mind? e.g. Launch a brand, write a thesis, remodel the kitchen...",
    description: 'Placeholder for warm-up intent freeform textarea in onboarding.',
    surface: 'onboarding',
    maxChars: 100,
  },
  NLSSetupRoleFounder: {
    text: 'Founder / CEO',
    description: 'Role choice Founder / CEO in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupRolePM: {
    text: 'Product Manager',
    description: 'Role choice Product Manager in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupRoleEngineer: {
    text: 'Engineer',
    description: 'Role choice Engineer in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupRoleDesigner: {
    text: 'Designer',
    description: 'Role choice Designer in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupRoleMarketer: {
    text: 'Marketer',
    description: 'Role choice Marketer in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupRoleOperator: {
    text: 'Operator',
    description: 'Role choice Operator in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupRoleStudent: {
    text: 'Student',
    description: 'Role choice Student in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupRoleResearcher: {
    text: 'Researcher',
    description: 'Role choice Researcher in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupRoleCreator: {
    text: 'Creator',
    description: 'Role choice Creator in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupRoleOther: {
    text: 'Other',
    description: 'Role choice Other in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupFocusProductivity: {
    text: 'Productivity',
    description: 'Focus area Productivity in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupFocusLearning: {
    text: 'Learning',
    description: 'Focus area Learning in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupFocusHealth: {
    text: 'Health',
    description: 'Focus area Health in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupFocusCareer: {
    text: 'Career',
    description: 'Focus area Career in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupFocusSideProjects: {
    text: 'Side projects',
    description: 'Focus area Side projects in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupFocusCreative: {
    text: 'Creative',
    description: 'Focus area Creative in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupFocusResearch: {
    text: 'Research',
    description: 'Focus area Research in onboarding setup.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupActionCancel: {
    text: 'Cancel',
    description: 'Cancel setup button in onboarding.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupProgressStep: {
    text: 'Step {current} · {total}',
    description: 'Step progress label in onboarding header.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupProgressAria: {
    text: 'Step {current} of {total}',
    description: 'Aria label for setup progress.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupMainAria: {
    text: 'Workspace setup',
    description: 'Aria label for main onboarding section.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupReceiptAria: {
    text: 'Your choices so far',
    description: 'Aria label for onboarding setup receipt aside.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSSetupThinkingSub: {
    text: 'Reading between the lines…',
    description: 'Thinking subtext in warm-up intent step.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSSetupAppearanceThemeLegend: {
    text: 'Theme',
    description: 'Legend for theme group in appearance step.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSSetupCompletionEyebrowPersonal: {
    text: 'Preparing your Personal Space',
    description: 'Completion eyebrow for personal space.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupCompletionEyebrowTeam: {
    text: 'Assembling your workspace',
    description: 'Completion eyebrow for team workspace.',
    surface: 'onboarding',
    maxChars: 50,
  },
  NLSSetupCompletionTitleNamed: {
    text: 'Making room for {name}…',
    description: 'Completion title with workspace name.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupCompletionTitleDefault: {
    text: 'Wiring the stars together…',
    description: 'Completion title default when unnamed.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupCompletionSub: {
    text: 'We’re applying your setup choices and opening the workspace the second it’s ready.',
    description: 'Completion subtext.',
    surface: 'onboarding',
    maxChars: 120,
  },
  NLSSetupSuggestionMarathon: {
    text: 'Marathon training + a side project',
    description: 'Intent suggestion chip.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupSuggestionPodcast: {
    text: 'Ship a podcast + weekly deep work',
    description: 'Intent suggestion chip.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupSuggestionPhd: {
    text: 'PhD reading + writing sprints',
    description: 'Intent suggestion chip.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupSuggestionStartup: {
    text: 'Launch a startup with my co-founder',
    description: 'Intent suggestion chip.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupSuggestionSemester: {
    text: 'Organise my life for the semester',
    description: 'Intent suggestion chip.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSSetupBrandName: {
    text: 'Timelining',
    description: 'Brand name in onboarding setup header.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSShellRailSectionBoard: {
    text: 'Board',
    description: 'Right rail section title for board/workspace destinations.',
    surface: 'shell',
    maxChars: 18,
  },
  NLSShellRailSectionControls: {
    text: 'Controls',
    description: 'Right rail section title for account/control destinations.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailDays: {
    text: 'days',
    description: 'Short unit label for workspace total days in the right rail summary.',
    surface: 'shell',
    maxChars: 12,
  },
  NLSShellRailPersonalWallet: {
    text: 'Personal Wallet',
    description: 'Right rail label for personal wallet credits.',
    surface: 'shell',
    maxChars: 34,
  },
  NLSShellRailOpenYourDetails: {
    text: 'Open your details',
    description: 'Accessible label for opening the user profile details from the right rail.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSShellRailPinSideNav: {
    text: 'Pin side nav',
    description: 'Tooltip for pinning the right rail open.',
    surface: 'shell',
    maxChars: 34,
  },
  NLSShellRailUnpinSideNav: {
    text: 'Unpin side nav',
    description: 'Tooltip for unpinning the right rail.',
    surface: 'shell',
    maxChars: 36,
  },
  NLSShellRailExpandSideNav: {
    text: 'Expand side nav',
    description: 'Tooltip and aria label for expanding the slim right rail.',
    surface: 'shell',
    maxChars: 38,
  },
  NLSShellRailTileAiMagic: {
    text: 'AI Magic',
    description: 'Right rail destination label for AI Magic.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailTileDashboard: {
    text: 'Dashboard',
    description: 'Right rail destination label for Dashboard.',
    surface: 'shell',
    maxChars: 26,
  },
  NLSShellRailTileOverview: {
    text: 'Overview',
    description: 'Right rail destination label for Overview.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailTileCalendar: {
    text: 'Calendar',
    description: 'Right rail destination label for Calendar.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailTileMeetings: {
    text: 'Meetings',
    description: 'Right rail destination label for Meetings.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailTileChat: {
    text: 'Chat',
    description: 'Right rail destination label for Chat.',
    surface: 'shell',
    maxChars: 18,
  },
  NLSShellRailTileTodos: {
    text: 'Todos',
    description: 'Right rail destination label for Todos.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSShellRailTileChecklists: {
    text: 'Checklists',
    description: 'Right rail destination label for Checklists.',
    surface: 'shell',
    maxChars: 28,
  },
  NLSShellRailTileForms: {
    text: 'Forms',
    description: 'Right rail destination label for Forms.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSShellRailTileDesignHub: {
    text: 'Design Hub',
    description: 'Right rail destination label for Design Hub.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSShellRailTileUpload: {
    text: 'Upload',
    description: 'Right rail destination label for Upload Hub.',
    surface: 'shell',
    maxChars: 22,
  },
  NLSShellRailTileMembers: {
    text: 'Members',
    description: 'Right rail destination label for Members/Invites.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailTileTeam: {
    text: 'Team',
    description: 'Right rail destination label for Team hub.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSSeatsStripActiveMembers: {
    text: '{count} active members',
    description: 'Count of active members in seats strip.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSSeatsStripUnusedCodes: {
    text: '{count} unused join codes',
    description: 'Count of pending join codes holding seats.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSSeatsStripSeatLimit: {
    text: '{limit} total seats',
    description: 'Total seat capacity in seats strip.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSMemberPeopleEyebrow: {
    text: 'Team',
    description: 'Eyebrow for member team page.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberPeopleTitle: {
    text: 'Your team',
    description: 'Title for member team page.',
    surface: 'member',
    maxChars: 30,
  },
  NLSMemberSystemPulseTitle: {
    text: 'System pulse',
    description: 'Eyebrow and section header for the member system pulse widget.',
    surface: 'member',
    maxChars: 30,
  },
  NLSMemberSystemPulseWorkspaces: {
    text: 'Workspaces',
    description: 'Tile label for workspace count in member system pulse widget.',
    surface: 'member',
    maxChars: 24,
  },
  NLSMemberSystemPulseUnread: {
    text: 'Unread',
    description: 'Tile label for unread notifications count in member system pulse widget.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberSystemPulseQueued: {
    text: 'Queued',
    description: 'Tile label for queued offline sync operations in member system pulse widget.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberSystemPulseFailed: {
    text: 'Failed',
    description: 'Tile label for failed sync operations in member system pulse widget.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberSystemPulseDetail: {
    text: 'Sync pending · {pending} / Sync failed · {failed}',
    description: 'Detail text for sync status in member system pulse widget. {pending} and {failed} are counts.',
    surface: 'member',
    maxChars: 80,
  },
  NLSTeamSectionPeople: {
    text: 'People',
    description: 'Team tab section label for People.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamSectionJoinCodes: {
    text: 'Join codes',
    description: 'Team tab section label for Join codes.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamSectionInvites: {
    text: 'Invites',
    description: 'Team tab section label for Invites.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamSectionHelpers: {
    text: 'Helpers',
    description: 'Team tab section label for Helpers.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamSectionFieldPasses: {
    text: 'Field Passes',
    description: 'Team tab section label for Field Passes.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamSectionPermissions: {
    text: 'Roles & permissions',
    description: 'Team tab section label for Roles & permissions.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSTeamFieldPassSubhead: {
    text: 'Temporary access for assigned work.',
    description: 'Subhead for Field Pass in team tab.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSTeamSavePermissions: {
    text: 'Save permissions',
    description: 'Button label to save permissions in team tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSTeamCreateFieldPassFab: {
    text: 'Create field pass',
    description: 'FAB action to create field pass.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSTeamInviteFab: {
    text: 'Invite',
    description: 'FAB action to invite member.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamFilterFab: {
    text: 'Filter',
    description: 'FAB action to filter roster.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamFilterRosterTitle: {
    text: 'Filter roster',
    description: 'Title of roster filter bottom sheet.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSTeamFilterReset: {
    text: 'Reset',
    description: 'Action to reset roster filters.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSTeamFilterRole: {
    text: 'Role',
    description: 'Section header for role filter.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSTeamFilterStatus: {
    text: 'Status',
    description: 'Section header for status filter.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSTeamFilterSortBy: {
    text: 'Sort by',
    description: 'Section header for sort by filter.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSTeamSortRecentlyActive: {
    text: 'Recently active',
    description: 'Sort option by recent activity.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSTeamSortName: {
    text: 'Name A–Z',
    description: 'Sort option by name alphabetical.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamSortRole: {
    text: 'Role',
    description: 'Sort option by role.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSTeamSortRecentlyAdded: {
    text: 'Recently added',
    description: 'Sort option by recently added.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSTeamShowFilteredMembers: {
    text: 'Show {count} members',
    description: 'Button label showing count of filtered members.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSTeamFilterNoMatches: {
    text: 'No matches',
    description: 'Label when roster filters match 0 members.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamFilterClear: {
    text: 'Clear',
    description: 'Action to clear filters.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSTeamClearFilters: {
    text: 'Clear filters',
    description: 'Action button to clear all active filters.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSTeamEmptyFilterTitle: {
    text: 'No members match these filters.',
    description: 'Empty state title when filter matches none.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSTeamEmptyNoMembersTitle: {
    text: 'No active members yet',
    description: 'Empty state title when roster has no members.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSTeamEmptyFilterHint: {
    text: 'Try clearing one or more filters.',
    description: 'Empty state hint when filter matches none.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSTeamEmptyNoMembersHint: {
    text: 'Invited members and helpers appear here after they accept.',
    description: 'Empty state hint when workspace has no members.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSPresenceOnline: {
    text: 'Online',
    description: 'Online presence status label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSPresenceAway: {
    text: 'Away',
    description: 'Away presence status label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSPresenceOffline: {
    text: 'Offline',
    description: 'Offline presence status label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSTeamRelativeJustNow: {
    text: 'just now',
    description: 'Relative time label for just now.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSTeamRelativeMinutesAgo: {
    text: '{minutes}m ago',
    description: 'Relative time label for minutes ago.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamRelativeHoursAgo: {
    text: '{hours}h ago',
    description: 'Relative time label for hours ago.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamRelativeDaysAgo: {
    text: '{days}d ago',
    description: 'Relative time label for days ago.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTeamRelativeNoActivity: {
    text: 'No activity yet',
    description: 'Relative time label when no activity exists.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSTeamDefaultMemberTitle: {
    text: 'Member',
    description: 'Fallback member sheet title when member name is missing.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSShellRailTileLedger: {
    text: 'Ledger',
    description: 'Right rail destination label for Ledger.',
    surface: 'shell',
    maxChars: 22,
  },
  NLSShellRailTileAnalysis: {
    text: 'Analysis',
    description: 'Right rail destination label for workspace Analysis.',
    surface: 'shell',
    maxChars: 22,
  },
  NLSShellRailTileReports: {
    text: 'Reports',
    description: 'Right rail destination label for Reports.',
    surface: 'shell',
    maxChars: 22,
  },
  NLSShellRailTileLink: {
    text: 'Link',
    description: 'Right rail destination label for Link Hub.',
    surface: 'shell',
    maxChars: 18,
  },
  NLSShellRailTileStock: {
    text: 'Stock',
    description: 'Right rail destination label for Stock.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSShellRailTileBudget: {
    text: 'Budget',
    description: 'Right rail destination label for Budget.',
    surface: 'shell',
    maxChars: 22,
  },
  NLSShellRailTileQuota: {
    text: 'Quota',
    description: 'Right rail destination label for Quota.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSShellRailTileTeaching: {
    text: 'Teaching',
    description: 'Right rail destination label for Teaching.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSShellRailTileTraining: {
    text: 'Training',
    description: 'Right rail destination label for Training.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSShellRailTileAutomate: {
    text: 'Automate',
    description: 'Right rail destination label for Automate.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailTileManage: {
    text: 'Manage',
    description: 'Right rail destination label for Manage.',
    surface: 'shell',
    maxChars: 22,
  },
  NLSShellRailTileFamilyView: {
    text: 'Family view',
    description: 'Right rail destination label for switching to Family view.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailTileSettings: {
    text: 'Settings',
    description: 'Right rail destination label for Settings.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailTileRaid: {
    text: 'Raid',
    description: 'Right rail destination label for Raid.',
    surface: 'shell',
    maxChars: 18,
  },
  NLSShellRailTileFocus: {
    text: 'Focus',
    description: 'Right rail destination label for Focus.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSShellRailTileYourPage: {
    text: 'Your Page',
    description: 'Right rail destination label for Your Page.',
    surface: 'shell',
    maxChars: 28,
  },
  NLSShellRailTileExplore: {
    text: 'Explore',
    description: 'Right rail destination label for Explore.',
    surface: 'shell',
    maxChars: 22,
  },
  NLSShellRailTilePurchase: {
    text: 'Purchase',
    description: 'Right rail destination label for Purchase.',
    surface: 'shell',
    maxChars: 24,
  },
  NLSShellRailTileNotifications: {
    text: 'Notifications',
    description: 'Right rail destination label for Notifications.',
    surface: 'shell',
    maxChars: 34,
  },
  NLSShellRailTileLogOut: {
    text: 'Log Out',
    description: 'Right rail destination label for logging out.',
    surface: 'shell',
    maxChars: 22,
  },
  NLSShellRailShortAi: {
    text: 'AI',
    description: 'Short right rail label for AI Magic.',
    surface: 'shell',
    maxChars: 8,
  },
  NLSShellRailShortDashboard: {
    text: 'Dash',
    description: 'Short right rail label for Dashboard.',
    surface: 'shell',
    maxChars: 12,
  },
  NLSShellRailShortOverview: {
    text: 'About',
    description: 'Short right rail label for Overview.',
    surface: 'shell',
    maxChars: 14,
  },
  NLSShellRailShortCalendar: {
    text: 'Cal',
    description: 'Short right rail label for Calendar.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSShellRailShortMeetings: {
    text: 'Meet',
    description: 'Short right rail label for Meetings.',
    surface: 'shell',
    maxChars: 12,
  },
  NLSShellRailShortTodos: {
    text: 'Todo',
    description: 'Short right rail label for Todos.',
    surface: 'shell',
    maxChars: 12,
  },
  NLSShellRailShortChecklists: {
    text: 'List',
    description: 'Short right rail label for Checklists.',
    surface: 'shell',
    maxChars: 12,
  },
  NLSShellRailShortAutomate: {
    text: 'Auto',
    description: 'Short right rail label for Automate.',
    surface: 'shell',
    maxChars: 12,
  },
  NLSShellRailTitleWorkspaceOverview: {
    text: 'Workspace Overview',
    description: 'Tooltip title for the Overview right rail destination.',
    surface: 'shell',
    maxChars: 44,
  },
  NLSShellRailTitleVideoCommandHub: {
    text: 'Video command hub',
    description: 'Tooltip title for the Meetings right rail destination.',
    surface: 'shell',
    maxChars: 44,
  },
  NLSShellRailTitleAutomatedIntake: {
    text: 'Automated intake engine',
    description: 'Tooltip title for the Forms right rail destination.',
    surface: 'shell',
    maxChars: 52,
  },
  NLSShellRailTitleDesignFactory: {
    text: 'Visual asset factory',
    description: 'Tooltip title for the Design right rail destination.',
    surface: 'shell',
    maxChars: 48,
  },
  NLSShellRailTitleWorkspaceUploads: {
    text: 'Workspace uploads',
    description: 'Tooltip title for the Upload right rail destination.',
    surface: 'shell',
    maxChars: 44,
  },
  NLSShellRailTitleWorkspaceIntegrations: {
    text: 'Workspace integrations',
    description: 'Tooltip title for the Link right rail destination.',
    surface: 'shell',
    maxChars: 52,
  },
  NLSShellRailTitleOperationalStock: {
    text: 'Operational stock & resources',
    description: 'Tooltip title for the Stock right rail destination.',
    surface: 'shell',
    maxChars: 62,
  },
  NLSShellRailTitleWorkspaceBudget: {
    text: 'Workspace budget hub',
    description: 'Tooltip title for the Budget right rail destination.',
    surface: 'shell',
    maxChars: 48,
  },
  NLSShellRailTitlePerformanceQuotas: {
    text: 'Performance quotas',
    description: 'Tooltip title for the Quota right rail destination.',
    surface: 'shell',
    maxChars: 46,
  },
  NLSShellRailTitleWorkflowAutomation: {
    text: 'Workflow automation engine',
    description: 'Tooltip title for the Automate right rail destination.',
    surface: 'shell',
    maxChars: 58,
  },
  NLSShellRailTitleWorkspaceSettings: {
    text: 'Workspace Settings',
    description: 'Tooltip title for the Workspace Settings right rail destination.',
    surface: 'shell',
    maxChars: 46,
  },
  NLSShellRailTitleHorizonFocus: {
    text: 'Horizon Focus chamber',
    description: 'Tooltip title for the Focus right rail destination.',
    surface: 'shell',
    maxChars: 52,
  },
  NLSShellRailTitleUserSettings: {
    text: 'User Settings',
    description: 'Tooltip title for the User Settings right rail destination.',
    surface: 'shell',
    maxChars: 38,
  },
  NLSDashboardViewTimeline: {
    text: 'Timeline',
    description: 'Dashboard visual view label for the timeline view.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardViewConstellation: {
    text: 'Constellation',
    description: 'Dashboard visual view label for the constellation view.',
    surface: 'dashboard',
    maxChars: 34,
  },
  NLSDashboardViewRadar: {
    text: 'Radar',
    description: 'Dashboard visual view label for the radar view.',
    surface: 'dashboard',
    maxChars: 22,
  },
  NLSDashboardViewSandfall: {
    text: 'Sandfall',
    description: 'Dashboard visual view label for the burndown sandfall view.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardViewJourney: {
    text: 'Journey',
    description: 'Dashboard visual view label for the journey view.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardViewGrid: {
    text: 'Grid',
    description: 'Dashboard visual view label for the grid view.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardViewHeatmap: {
    text: 'Heatmap',
    description: 'Dashboard visual view label for the contribution heatmap view.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardViewStripboard: {
    text: 'Stripboard',
    description: 'Dashboard visual view label for the stripboard matrix view.',
    surface: 'dashboard',
    maxChars: 30,
  },
  NLSDashboardViewWarRoom: {
    text: 'War Room',
    description: 'Dashboard visual view label for the war room blueprint view.',
    surface: 'dashboard',
    maxChars: 28,
  },
  NLSDashboardViewReliability: {
    text: 'Reliability',
    description: 'Dashboard visual view label for the reliability leaderboard view.',
    surface: 'dashboard',
    maxChars: 30,
  },
  NLSDashboardViewPerimeter: {
    text: 'Perimeter',
    description: 'Dashboard visual view label for the perimeter countdown view.',
    surface: 'dashboard',
    maxChars: 28,
  },
  NLSDashboardViewLootDrop: {
    text: 'Loot Drop',
    description: 'Dashboard visual view label for the loot drop lanes view.',
    surface: 'dashboard',
    maxChars: 28,
  },
  NLSDashboardViewFlowband: {
    text: 'Flowband',
    description: 'Dashboard visual view label for the flowband share-over-time view.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardViewBurnshore: {
    text: 'Burnshore',
    description: 'Dashboard visual view label for the burnshore weighted-burndown view.',
    surface: 'dashboard',
    maxChars: 26,
  },
  NLSDashboardViewCadence: {
    text: 'Cadence',
    description: 'Dashboard visual view label for the cadence completion-time histogram view.',
    surface: 'dashboard',
    maxChars: 22,
  },
  NLSDashboardViewTooltipTimeline: {
    text: 'Chronological plan with pan, zoom and cursor pings.',
    description: 'Dashboard view switcher tooltip for the timeline view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipConstellation: {
    text: 'Interactive nodes and connections that map how work depends on other work.',
    description: 'Dashboard view switcher tooltip for the constellation view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipRadar: {
    text: 'Sweeping radar that surfaces the most urgent items in the current window.',
    description: 'Dashboard view switcher tooltip for the radar view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipSandfall: {
    text: 'Weighted burndown of remaining work as it drains from top to bottom.',
    description: 'Dashboard view switcher tooltip for the sandfall view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipJourney: {
    text: 'Guided path through the plan with milestone stops.',
    description: 'Dashboard view switcher tooltip for the journey view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipGrid: {
    text: 'Contribution grid at day granularity, coloured by activity.',
    description: 'Dashboard view switcher tooltip for the grid view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipHeatmap: {
    text: 'Contribution heatmap grouped by week, coloured by activity.',
    description: 'Dashboard view switcher tooltip for the heatmap view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipStripboard: {
    text: 'Kanban strip of checklist items grouped by workflow status.',
    description: 'Dashboard view switcher tooltip for the stripboard view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipWarRoom: {
    text: 'Blueprint layout showing owners and blockers of active work.',
    description: 'Dashboard view switcher tooltip for the war room view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipReliability: {
    text: 'Reliability rankings of collaborators over the current window.',
    description: 'Dashboard view switcher tooltip for the reliability view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipPerimeter: {
    text: 'Countdown of items nearing their due-date perimeter.',
    description: 'Dashboard view switcher tooltip for the perimeter view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipLootDrop: {
    text: 'Sequence of lanes releasing wins over the current window.',
    description: 'Dashboard view switcher tooltip for the loot drop view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipFlowband: {
    text: 'Share of remaining weight per parent, over time.',
    description: 'Dashboard view switcher tooltip for the flowband view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipBurnshore: {
    text: 'Weighted burndown per parent, with checklist occurrences.',
    description: 'Dashboard view switcher tooltip for the burnshore view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardViewTooltipCadence: {
    text: 'Completion-time distribution across fixed buckets.',
    description: 'Dashboard view switcher tooltip for the cadence view.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardActionGhostEcho: {
    text: 'Ghost Echo',
    description: 'Dashboard action button that sends the current board view to mobile.',
    surface: 'dashboard',
    maxChars: 28,
  },
  NLSDashboardActionWatch: {
    text: 'Watch',
    description: 'Dashboard action button that starts watching a mobile view.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardActionWatching: {
    text: 'Watching',
    description: 'Dashboard active state label while watching a mobile view.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardActionStopWatching: {
    text: 'Stop watching',
    description: 'Accessible label for stopping the dashboard watch mode.',
    surface: 'dashboard',
    maxChars: 36,
  },
  NLSDashboardActionWatchingStopHint: {
    text: 'Watching - click to stop',
    description: 'Tooltip for stopping the dashboard watch mode.',
    surface: 'dashboard',
    maxChars: 48,
  },
  NLSDashboardActionSendBoardToMobile: {
    text: 'Send board view to mobile',
    description: 'Accessible label for sending the current dashboard board view to mobile.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardActionGhostEchoBoardToMobile: {
    text: 'Ghost Echo board to mobile',
    description: 'Tooltip for sending the dashboard board view to mobile.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardPromptGhostEchoUid: {
    text: 'Send Ghost Echo to user UID:',
    description: 'Dashboard prompt asking which user UID should receive a Ghost Echo.',
    surface: 'dashboard',
    maxChars: 70,
  },
  NLSDashboardSenderDesktopFallback: {
    text: 'Desktop',
    description: 'Fallback sender name for dashboard Ghost Echo sync from desktop.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardActionGhostEchoToMobile: {
    text: 'Ghost Echo to mobile',
    description: 'Tooltip for sending a selected dashboard item to mobile.',
    surface: 'dashboard',
    maxChars: 46,
  },
  NLSDashboardToastGhostEchoSent: {
    text: 'Ghost Echo sent.',
    description: 'Dashboard toast shown after successfully sending a Ghost Echo.',
    surface: 'dashboard',
    maxChars: 38,
  },
  NLSDashboardToastGhostEchoFailed: {
    text: 'Ghost Echo could not be sent.',
    description: 'Dashboard toast shown when sending a Ghost Echo fails without a specific error message.',
    surface: 'dashboard',
    maxChars: 68,
  },
  NLSDashboardActionSendSelectedToMobile: {
    text: 'Send selected item to mobile',
    description: 'Accessible label for sending the selected dashboard item to mobile.',
    surface: 'dashboard',
    maxChars: 58,
  },
  NLSDashboardActionScrollLeftLarge: {
    text: 'Scroll left by a large amount',
    description: 'Dashboard toolbar action for a large pan or horizontal scroll left.',
    surface: 'dashboard',
    maxChars: 64,
  },
  NLSDashboardActionScrollLeft: {
    text: 'Scroll left',
    description: 'Dashboard toolbar action for a small pan or horizontal scroll left.',
    surface: 'dashboard',
    maxChars: 34,
  },
  NLSDashboardActionScrollRight: {
    text: 'Scroll right',
    description: 'Dashboard toolbar action for a small pan or horizontal scroll right.',
    surface: 'dashboard',
    maxChars: 34,
  },
  NLSDashboardActionScrollRightLarge: {
    text: 'Scroll right by a large amount',
    description: 'Dashboard toolbar action for a large pan or horizontal scroll right.',
    surface: 'dashboard',
    maxChars: 64,
  },
  NLSDashboardActionZoomOut: {
    text: 'Zoom out',
    description: 'Dashboard toolbar action for zooming out.',
    surface: 'dashboard',
    maxChars: 26,
  },
  NLSDashboardActionZoomIn: {
    text: 'Zoom in',
    description: 'Dashboard toolbar action for zooming in.',
    surface: 'dashboard',
    maxChars: 26,
  },
  NLSDashboardActionFitFullTimeline: {
    text: 'Fit full timeline',
    description: 'Dashboard toolbar action for fitting the full timeline in view.',
    surface: 'dashboard',
    maxChars: 42,
  },
  NLSDashboardActionFocusSelectedItem: {
    text: 'Focus selected item',
    description: 'Dashboard toolbar action for focusing the selected item.',
    surface: 'dashboard',
    maxChars: 48,
  },
  NLSDashboardActionViewLegend: {
    text: 'View legend',
    description: 'Dashboard toolbar group and tooltip label for the visual view legend.',
    surface: 'dashboard',
    maxChars: 34,
  },
  NLSDashboardActionShowViewLegend: {
    text: 'Show view legend',
    description: 'Dashboard toolbar action for opening the visual view legend.',
    surface: 'dashboard',
    maxChars: 42,
  },
  NLSDashboardActionHideViewLegend: {
    text: 'Hide view legend',
    description: 'Dashboard toolbar action for closing the visual view legend.',
    surface: 'dashboard',
    maxChars: 42,
  },
  NLSDashboardActionExpandDashboardView: {
    text: 'Expand dashboard view',
    description: 'Dashboard toolbar action for expanding the stage.',
    surface: 'dashboard',
    maxChars: 50,
  },
  NLSDashboardActionExitExpandedView: {
    text: 'Exit expanded view',
    description: 'Dashboard toolbar action for leaving expanded stage mode.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardActionCopyItem: {
    text: 'Copy item',
    description: 'Dashboard detail action for copying the selected item.',
    surface: 'dashboard',
    maxChars: 30,
  },
  NLSDashboardActionCutItem: {
    text: 'Cut item',
    description: 'Dashboard detail action for cutting the selected item.',
    surface: 'dashboard',
    maxChars: 28,
  },
  NLSDashboardActionPasteItem: {
    text: 'Paste',
    description: 'Dashboard detail action title for pasting an item.',
    surface: 'dashboard',
    maxChars: 22,
  },
  NLSDashboardActionPasteIntoSelectedItem: {
    text: 'Paste into selected item',
    description: 'Accessible label for pasting into the selected dashboard item.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardActionMoveHere: {
    text: 'Move here',
    description: 'Dashboard paste confirmation action for moving a cut item.',
    surface: 'dashboard',
    maxChars: 30,
  },
  NLSDashboardActionPasteCopy: {
    text: 'Paste copy',
    description: 'Dashboard paste confirmation action for copying an item.',
    surface: 'dashboard',
    maxChars: 32,
  },
  NLSDashboardActionMoveGoalToVisionboard: {
    text: 'Move goal to Visionboard',
    description: 'Dashboard detail action for moving a goal subtree to Visionboard.',
    surface: 'dashboard',
    maxChars: 58,
  },
  NLSDashboardActionMoveToVisionboard: {
    text: 'Move to Visionboard',
    description: 'Dashboard detail tooltip and confirmation label for moving a goal to Visionboard.',
    surface: 'dashboard',
    maxChars: 48,
  },
  NLSDashboardActionMoveGoalToArchive: {
    text: 'Move goal to Archive',
    description: 'Dashboard detail action for moving a goal subtree to Archive.',
    surface: 'dashboard',
    maxChars: 52,
  },
  NLSDashboardActionMoveToArchive: {
    text: 'Move to Archive',
    description: 'Dashboard detail tooltip and confirmation label for moving a goal to Archive.',
    surface: 'dashboard',
    maxChars: 42,
  },
  NLSDashboardActionMoveGoalToBin: {
    text: 'Move goal to Bin',
    description: 'Dashboard detail action for moving a goal subtree to Bin.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardActionMoveToBin: {
    text: 'Move to Bin',
    description: 'Dashboard detail tooltip and confirmation label for moving a goal to Bin.',
    surface: 'dashboard',
    maxChars: 34,
  },
  NLSDashboardActionWorking: {
    text: 'Working...',
    description: 'Dashboard confirmation dialog busy action label.',
    surface: 'dashboard',
    maxChars: 28,
  },
  NLSDashboardActionConfirm: {
    text: 'Confirm',
    description: 'Dashboard generic confirmation dialog action label.',
    surface: 'dashboard',
    maxChars: 26,
  },
  NLSDashboardAriaViewSelect: {
    text: 'Dashboard view',
    description: 'Accessible label for the dashboard view selector.',
    surface: 'dashboard',
    maxChars: 38,
  },
  NLSDashboardAriaTimelineBreadcrumb: {
    text: 'Timeline breadcrumb',
    description: 'Accessible label for the dashboard breadcrumb trail.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardAriaBoard: {
    text: 'board',
    description: 'Accessible label for the dashboard breadcrumb board segment.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardAriaGoal: {
    text: 'goal',
    description: 'Accessible label for the dashboard breadcrumb goal segment.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardAriaTask: {
    text: 'task',
    description: 'Accessible label for the dashboard breadcrumb task segment.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardAriaSubtask: {
    text: 'subtask',
    description: 'Accessible label for the dashboard breadcrumb subtask segment.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardAriaMemberViewControls: {
    text: 'Member dashboard view controls',
    description: 'Accessible label for member mobile dashboard controls.',
    surface: 'dashboard',
    maxChars: 66,
  },
  NLSDashboardAriaDashboardZoom: {
    text: 'Dashboard zoom',
    description: 'Accessible label for dashboard zoom controls.',
    surface: 'dashboard',
    maxChars: 38,
  },
  NLSDashboardMemberCommandStripAria: {
    text: 'Dashboard command strip',
    description: 'Accessible label for the compact top strip inside the member dashboard sub-tab that houses the breadcrumb and the legend/fit/landscape controls.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardMemberViewRailAria: {
    text: 'Dashboard view picker',
    description: 'Accessible label for the horizontal view-picker rail on the member dashboard sub-tab.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardMemberViewPillAria: {
    text: 'Show {view} view',
    description: 'Accessible label for a single view pill in the member dashboard view picker rail. {view} is a translated view label.',
    surface: 'dashboard',
    maxChars: 60,
  },
  NLSDashboardMemberGestureDockAria: {
    text: 'Dashboard zoom dock',
    description: 'Accessible label for the floating vertical zoom dock overlaying the member dashboard canvas.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardMemberEnterLandscape: {
    text: 'Landscape mode',
    description: 'Tooltip for the member dashboard landscape entry control.',
    surface: 'dashboard',
    maxChars: 32,
  },
  NLSDashboardMemberPeekOpenAria: {
    text: 'Show dashboard summary',
    description: 'Accessible label for opening the collapsed peek panel on the member dashboard sub-tab.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardMemberPeekCloseAria: {
    text: 'Hide dashboard summary',
    description: 'Accessible label for collapsing the expanded peek panel on the member dashboard sub-tab.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardMemberPeekHandle: {
    text: 'Summary',
    description: 'Short label shown on the collapsed peek panel handle for the member dashboard sub-tab.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardMemberPeekEmpty: {
    text: 'No item selected',
    description: 'Fallback text shown in the collapsed peek panel when no dashboard element is selected.',
    surface: 'dashboard',
    maxChars: 32,
  },
  NLSDashboardMemberPeekSectionSelected: {
    text: 'Selected',
    description: 'Section eyebrow above the selected-element block in the member dashboard peek panel.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardMemberPeekSectionInWindow: {
    text: 'In view',
    description: 'Section eyebrow above the in-window counts block in the member dashboard peek panel.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardMemberPeekSectionReadiness: {
    text: 'Resource attention',
    description: 'Section eyebrow above the resource readiness block in the member dashboard peek panel.',
    surface: 'dashboard',
    maxChars: 28,
  },
  NLSDashboardMemberPeekCounts: {
    text: '{goals} goals · {tasks} tasks · {subtasks} subtasks',
    description: 'One-line summary of macro-element counts inside the current dashboard view window. Placeholders are integer counts.',
    surface: 'dashboard',
    maxChars: 69,
  },
  NLSDashboardMemberPeekOpenItem: {
    text: 'Open item',
    description: 'Action button inside the expanded peek panel that opens the selected dashboard element in the checklist detail popup.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardAriaDashboardViewControls: {
    text: 'Dashboard view controls',
    description: 'Accessible label for the desktop dashboard control workbench.',
    surface: 'dashboard',
    maxChars: 58,
  },
  NLSDashboardAriaDashboardVisualView: {
    text: 'Dashboard visual view',
    description: 'Accessible label for the dashboard visual view selector group.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardAriaCurrentViewControls: {
    text: '{view} view controls',
    description: 'Accessible label for controls belonging to the current dashboard view. {view} is a view label.',
    surface: 'dashboard',
    maxChars: 80,
  },
  NLSDashboardAriaPanControls: {
    text: 'Pan controls',
    description: 'Accessible label for dashboard pan controls.',
    surface: 'dashboard',
    maxChars: 34,
  },
  NLSDashboardAriaZoomControls: {
    text: 'Zoom controls',
    description: 'Accessible label for dashboard zoom controls group.',
    surface: 'dashboard',
    maxChars: 36,
  },
  NLSDashboardAriaFocusControls: {
    text: 'Focus controls',
    description: 'Accessible label for dashboard focus controls group.',
    surface: 'dashboard',
    maxChars: 38,
  },
  NLSDashboardAriaPanForwardControls: {
    text: 'Pan forward controls',
    description: 'Accessible label for dashboard forward pan controls group.',
    surface: 'dashboard',
    maxChars: 48,
  },
  NLSDashboardAriaStageControls: {
    text: 'Stage controls',
    description: 'Accessible label for dashboard stage controls group.',
    surface: 'dashboard',
    maxChars: 38,
  },
  NLSDashboardAriaSelectedItemDetails: {
    text: 'Selected item details',
    description: 'Accessible label for the dashboard selected item details card.',
    surface: 'dashboard',
    maxChars: 48,
  },
  NLSDashboardAriaItemDateRange: {
    text: 'Item date range',
    description: 'Accessible label for the dashboard selected item date range.',
    surface: 'dashboard',
    maxChars: 40,
  },
  NLSDashboardAriaDetailActions: {
    text: 'Detail actions',
    description: 'Accessible label for dashboard detail action controls.',
    surface: 'dashboard',
    maxChars: 38,
  },
  NLSDashboardAriaShare: {
    text: 'Share',
    description: 'Accessible label for the dashboard detail share action group.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardAriaClipboard: {
    text: 'Clipboard',
    description: 'Accessible label for the dashboard detail clipboard action group.',
    surface: 'dashboard',
    maxChars: 30,
  },
  NLSDashboardAriaBoards: {
    text: 'Boards',
    description: 'Accessible label for the dashboard detail board move action group.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardAriaCurrentZoomPercent: {
    text: 'Current zoom {percent} percent',
    description: 'Accessible label for the dashboard zoom readout. {percent} is a number.',
    surface: 'dashboard',
    maxChars: 70,
  },
  NLSDashboardStatusLoadingCanvas: {
    text: 'Loading canvas...',
    description: 'Dashboard canvas loading label.',
    surface: 'dashboard',
    maxChars: 42,
  },
  NLSDashboardStatusDaySpan: {
    text: '{days}d span',
    description: 'Compact dashboard zoom readout showing visible day span. {days} is a number.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardEmptyNoDescription: {
    text: 'No description yet',
    description: 'Fallback description for a dashboard item without description text.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardEmptyNoDate: {
    text: 'No date',
    description: 'Fallback date label for a dashboard item without a valid date.',
    surface: 'dashboard',
    maxChars: 26,
  },
  NLSDashboardDetailTypeSubtask: {
    text: 'Sub-task',
    description: 'Dashboard detail item type label for subtasks.',
    surface: 'dashboard',
    maxChars: 28,
  },
  NLSDashboardDetailTypeGoal: {
    text: 'Goal',
    description: 'Dashboard detail item type label for goals.',
    surface: 'dashboard',
    maxChars: 22,
  },
  NLSDashboardDetailTypeTask: {
    text: 'Task',
    description: 'Dashboard detail item type label for tasks.',
    surface: 'dashboard',
    maxChars: 22,
  },
  NLSDashboardDetailStart: {
    text: 'Start',
    description: 'Dashboard detail date range start label.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardDetailEnd: {
    text: 'End',
    description: 'Dashboard detail date range end label.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardDialogPasteTitle: {
    text: 'Paste {title}?',
    description: 'Dashboard paste confirmation dialog title. {title} is the item title.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardDialogPastePrefix: {
    text: 'This will {action}',
    description: 'First phrase in dashboard paste confirmation copy. {action} is move or copy.',
    surface: 'dashboard',
    maxChars: 60,
  },
  NLSDashboardDialogPasteActionMove: {
    text: 'move',
    description: 'Verb used in dashboard paste confirmation copy for cut items.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardDialogPasteActionCopy: {
    text: 'copy',
    description: 'Verb used in dashboard paste confirmation copy for copied items.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardDialogPasteWithSubtasks: {
    text: ' with its subtasks',
    description: 'Optional dashboard paste confirmation phrase for task subtrees.',
    surface: 'dashboard',
    maxChars: 46,
  },
  NLSDashboardDialogPasteInto: {
    text: 'into',
    description: 'Middle phrase in dashboard paste confirmation copy.',
    surface: 'dashboard',
    maxChars: 20,
  },
  NLSDashboardDialogPasteSuffix: {
    text: 'while preserving the valid Goal -> Task -> Subtask hierarchy.',
    description: 'Final phrase in dashboard paste confirmation copy.',
    surface: 'dashboard',
    maxChars: 110,
  },
  NLSDashboardDialogMoveVisionboardTitle: {
    text: 'Move {title} to Visionboard?',
    description: 'Dashboard confirmation title for moving a goal to Visionboard. {title} is the goal title.',
    surface: 'dashboard',
    maxChars: 140,
  },
  NLSDashboardDialogMoveVisionboardMessage: {
    text: 'This will move the goal and all internal tasks, subtasks, and checklist items to Visionboard.',
    description: 'Dashboard confirmation body for moving a goal subtree to Visionboard.',
    surface: 'dashboard',
    maxChars: 160,
  },
  NLSDashboardDialogMoveArchiveTitle: {
    text: 'Move {title} to Archive?',
    description: 'Dashboard confirmation title for moving a goal to Archive. {title} is the goal title.',
    surface: 'dashboard',
    maxChars: 130,
  },
  NLSDashboardDialogMoveArchiveMessage: {
    text: 'This will move the goal and all internal tasks, subtasks, and checklist items to Archive.',
    description: 'Dashboard confirmation body for moving a goal subtree to Archive.',
    surface: 'dashboard',
    maxChars: 150,
  },
  NLSDashboardDialogMoveBinTitle: {
    text: 'Move {title} to Bin?',
    description: 'Dashboard confirmation title for moving a goal to Bin. {title} is the goal title.',
    surface: 'dashboard',
    maxChars: 120,
  },
  NLSDashboardDialogMoveBinMessage: {
    text: 'This will move the goal and all internal tasks, subtasks, and checklist items to Bin. If it is deleted from Bin later, the item tree will be removed permanently.',
    description: 'Dashboard confirmation body for moving a goal subtree to Bin.',
    surface: 'dashboard',
    maxChars: 220,
  },
  NLSChecklistDialogMoveToBinMessage: {
    text: 'This item moves to the Bin. You can restore it from there; deleting it from the Bin removes it permanently.',
    description: 'Checklist confirmation body for moving an item to Bin.',
    surface: 'checklist',
    maxChars: 180,
  },
  NLSDashboardLegendSummary: {
    text: '{goals} goals · {tasks} tasks · {subtasks} subtasks',
    description: 'Dashboard visual legend item counts. Values are numbers.',
    surface: 'dashboard',
    maxChars: 90,
  },
  NLSDashboardLegendWindow: {
    text: 'Window',
    description: 'Dashboard legend range label for Radar.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardLegendViewWindow: {
    text: 'View window',
    description: 'Dashboard legend range label for non-Radar views.',
    surface: 'dashboard',
    maxChars: 34,
  },
  NLSDashboardLegendToday: {
    text: 'today {date}',
    description: 'Dashboard legend marker for today. {date} is a localized date string.',
    surface: 'dashboard',
    maxChars: 50,
  },
  NLSDashboardLegendHighRiskSignal: {
    text: '{count} high-risk signal',
    description: 'Singular dashboard legend high-risk count. {count} is a number.',
    surface: 'dashboard',
    maxChars: 50,
  },
  NLSDashboardLegendHighRiskSignals: {
    text: '{count} high-risk signals',
    description: 'Plural dashboard legend high-risk count. {count} is a number.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardLegendRadarInnerOuter: {
    text: 'Inner = urgent · outer = distant',
    description: 'Dashboard Radar legend explanation for ring distance.',
    surface: 'dashboard',
    maxChars: 72,
  },
  NLSDashboardLegendAnglePosition: {
    text: 'Angle = position in view window',
    description: 'Dashboard Radar legend explanation for angular position.',
    surface: 'dashboard',
    maxChars: 72,
  },
  NLSDashboardLegendRadarTodaySweep: {
    text: 'Amber line = today · cyan sweep = live scan',
    description: 'Dashboard Radar legend explanation for today and sweep markers.',
    surface: 'dashboard',
    maxChars: 90,
  },
  NLSDashboardLegendJourneyPath: {
    text: 'Path = goals in time order',
    description: 'Dashboard Journey legend explanation for the path.',
    surface: 'dashboard',
    maxChars: 64,
  },
  NLSDashboardLegendAmberLineToday: {
    text: 'Amber line = today',
    description: 'Dashboard legend explanation for the amber today line.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardLegendDotsTasksSubtasks: {
    text: 'Dots = tasks / subtasks by time',
    description: 'Dashboard Journey legend explanation for task and subtask dots.',
    surface: 'dashboard',
    maxChars: 72,
  },
  NLSDashboardLegendGridCell: {
    text: 'Cell = day · color = element',
    description: 'Dashboard Grid legend explanation for cells.',
    surface: 'dashboard',
    maxChars: 70,
  },
  NLSDashboardLegendGridFrame: {
    text: 'Frame = drill scope',
    description: 'Dashboard Grid legend explanation for drill scope frames.',
    surface: 'dashboard',
    maxChars: 56,
  },
  NLSDashboardLegendGridSkySpine: {
    text: "Sky spine = today's week",
    description: 'Dashboard Grid legend explanation for the current week marker.',
    surface: 'dashboard',
    maxChars: 64,
  },
  NLSDashboardLegendGridStripes: {
    text: 'Stripes = overlapping items',
    description: 'Dashboard Grid legend explanation for overlapping items.',
    surface: 'dashboard',
    maxChars: 64,
  },
  NLSDashboardLegendConstellationRing: {
    text: 'Ring = view window · arc = goal in view',
    description: 'Dashboard Constellation legend explanation for rings and arcs.',
    surface: 'dashboard',
    maxChars: 90,
  },
  NLSDashboardLegendConstellationAmberTick: {
    text: 'Amber tick = today',
    description: 'Dashboard Constellation legend explanation for the today tick.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardLegendColorSize: {
    text: 'Color = element · size = level',
    description: 'Dashboard shared legend explanation for node color and size.',
    surface: 'dashboard',
    maxChars: 72,
  },
  NLSDashboardLegendDiamondChecklist: {
    text: 'Diamond = checklist (green = done)',
    description: 'Dashboard shared legend explanation for checklist diamonds.',
    surface: 'dashboard',
    maxChars: 78,
  },
  NLSDashboardLegendSkyGlowSelected: {
    text: 'Sky glow = selected',
    description: 'Dashboard shared legend explanation for selected item glow.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardEmptyAiMagicReady: {
    text: 'AI Magic is ready',
    description: 'Dashboard empty-state eyebrow when no goals exist.',
    surface: 'dashboard',
    maxChars: 44,
  },
  NLSDashboardEmptyWelcomePhraseStart: {
    text: 'Get started with Timelining',
    description: 'Dashboard empty-state rotating headline phrase.',
    surface: 'dashboard',
    maxChars: 60,
  },
  NLSDashboardEmptyWelcomePhraseCreate: {
    text: 'Create timelines easily',
    description: 'Dashboard empty-state rotating headline phrase.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardEmptyWelcomePhraseManage: {
    text: 'Manage calendars differently',
    description: 'Dashboard empty-state rotating headline phrase.',
    surface: 'dashboard',
    maxChars: 62,
  },
  NLSDashboardEmptyWelcomePhraseMotion: {
    text: 'Turn plans into motion',
    description: 'Dashboard empty-state rotating headline phrase.',
    surface: 'dashboard',
    maxChars: 54,
  },
  NLSDashboardEmptyAiMagicCopy: {
    text: 'Open AI Magic from the side navigation to describe a plan and generate goals, tasks, subtasks, and dates.',
    description: 'Dashboard empty-state body copy guiding users to AI Magic.',
    surface: 'dashboard',
    maxChars: 180,
  },
  NLSDashboardTimelineMarkerWeek: {
    text: 'Week {week}',
    description: 'Dashboard canvas timeline marker for a week number. {week} is a number.',
    surface: 'dashboard',
    maxChars: 24,
  },
  NLSDashboardCanvasSelectedSubtask: {
    text: 'Selected subtask',
    description: 'Dashboard canvas row meta when a subtask is selected.',
    surface: 'dashboard',
    maxChars: 42,
  },
  NLSDashboardCanvasSubtasksAcrossTask: {
    text: '{count} subtasks across this task',
    description: 'Dashboard canvas row meta for subtask count within the active task.',
    surface: 'dashboard',
    maxChars: 70,
  },
  NLSDashboardCanvasTasksAcrossGoal: {
    text: '{count} tasks across this goal',
    description: 'Dashboard canvas row meta for task count within a goal.',
    surface: 'dashboard',
    maxChars: 64,
  },
  NLSManageActionApplyAndClose: {
    text: 'Apply & close',
    description: 'Confirm button for per-item discard choices that will close Manage.',
    surface: 'manage',
    maxChars: 28,
  },
  NLSManageActionApplyStayOpen: {
    text: 'Apply & stay open',
    description: 'Confirm button for per-item discard choices that keep Manage open.',
    surface: 'manage',
    maxChars: 34,
  },
  NLSManageActionClose: {
    text: 'Close',
    description: 'Close the Detailed Review panel.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageActionCopy: {
    text: 'Copy',
    description: 'Detailed Review clipboard copy action.',
    surface: 'manage',
    maxChars: 16,
  },
  NLSManageActionCut: {
    text: 'Cut',
    description: 'Detailed Review clipboard cut action.',
    surface: 'manage',
    maxChars: 12,
  },
  NLSManageActionDelete: {
    text: 'Delete',
    description: 'Detailed Review delete action accessible label.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageActionDeleteForever: {
    text: 'Delete forever',
    description: 'Permanent delete confirmation action.',
    surface: 'manage',
    maxChars: 30,
  },
  NLSManageActionDiscard: {
    text: 'Discard',
    description: 'Discard pending changes on the selected Manage item.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageActionDiscardAll: {
    text: 'Discard all',
    description: 'Discard every pending and session-saved Manage change.',
    surface: 'manage',
    maxChars: 26,
  },
  NLSManageActionDiscardAllUnsaved: {
    text: 'Discard all unsaved',
    description: 'Discard all unsaved Manage changes.',
    surface: 'manage',
    maxChars: 34,
  },
  NLSManageActionDiscardAndClose: {
    text: 'Discard and close',
    description: 'Confirm button for closing Manage while discarding unsaved changes.',
    surface: 'manage',
    maxChars: 36,
  },
  NLSManageActionDrop: {
    text: 'Drop',
    description: 'Per-item discard choice that removes an unsaved item without saving it.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageActionKeepEditing: {
    text: 'Keep editing',
    description: 'Per-item discard choice that aborts close and keeps the item open.',
    surface: 'manage',
    maxChars: 28,
  },
  NLSManageActionMove: {
    text: 'Move',
    description: 'Move an item out of Bin.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageActionMoving: {
    text: 'Moving',
    description: 'Busy label while moving an item out of Bin.',
    surface: 'manage',
    maxChars: 22,
  },
  NLSManageActionMoveToBin: {
    text: 'Move to Bin',
    description: 'Move selected item to Bin action tooltip.',
    surface: 'manage',
    maxChars: 30,
  },
  NLSManageActionPaste: {
    text: 'Paste',
    description: 'Detailed Review clipboard paste action.',
    surface: 'manage',
    maxChars: 16,
  },
  NLSManageActionPermanentlyDelete: {
    text: 'Permanently delete',
    description: 'Detailed Review permanent delete action label.',
    surface: 'manage',
    maxChars: 34,
  },
  NLSManageActionSave: {
    text: 'Save',
    description: 'Save pending changes on the selected Manage item.',
    surface: 'manage',
    maxChars: 16,
  },
  NLSManageActionSaveAll: {
    text: 'Save all',
    description: 'Save every pending Manage change.',
    surface: 'manage',
    maxChars: 20,
  },
  NLSManageActionSaveAllChildItems: {
    text: 'Save all child items',
    description: 'Save pending descendant changes under the selected Manage item.',
    surface: 'manage',
    maxChars: 36,
  },
  NLSManageActionSaveToBin: {
    text: 'Save to Bin',
    description: 'Per-item discard choice that persists an unsaved item into Bin.',
    surface: 'manage',
    maxChars: 28,
  },
  NLSManageActionYesSure: {
    text: 'Yes, I am sure',
    description: 'Default confirm action in Detailed Review confirmation dialogs.',
    surface: 'manage',
    maxChars: 34,
  },
  NLSManageAriaBoard: {
    text: 'Manage board',
    description: 'Accessible label for the Manage board selector.',
    surface: 'manage',
    maxChars: 34,
  },
  NLSManageAriaClipboard: {
    text: 'Clipboard',
    description: 'Accessible label for the Detailed Review clipboard toolbar.',
    surface: 'manage',
    maxChars: 28,
  },
  NLSManageAriaPageControls: {
    text: 'Manage page controls',
    description: 'Accessible label for Manage page header controls.',
    surface: 'manage',
    maxChars: 42,
  },
  NLSManageAriaSave: {
    text: 'Save',
    description: 'Accessible label for the Manage save button group.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageAriaSaveOptions: {
    text: 'Save options',
    description: 'Accessible label for collapsed Manage save options.',
    surface: 'manage',
    maxChars: 30,
  },
  NLSManageAriaDiscard: {
    text: 'Discard',
    description: 'Accessible label for the Manage discard button group.',
    surface: 'manage',
    maxChars: 22,
  },
  NLSManageAriaDiscardOptions: {
    text: 'Discard options',
    description: 'Accessible label for collapsed Manage discard options.',
    surface: 'manage',
    maxChars: 34,
  },
  NLSManageAriaPerItemActionFor: {
    text: 'Action for {title}',
    description: 'Accessible label for per-item discard choice radio groups.',
    surface: 'manage',
    maxChars: 90,
  },
  NLSManageAriaSwitchView: {
    text: 'Switch Manage view',
    description: 'Accessible label for the Manage Board/Review view switch group.',
    surface: 'manage',
    maxChars: 40,
  },
  NLSManageAriaWorkspace: {
    text: 'Manage workspace',
    description: 'Accessible label for the Manage workspace page stage.',
    surface: 'manage',
    maxChars: 40,
  },
  NLSManageBinMoveBody: {
    text: 'Restore {title} ({type}) to another board.',
    description: 'Body copy for moving an item out of Bin. {title} is item title and {type} is item type.',
    surface: 'manage',
    maxChars: 110,
  },
  NLSManageBinMoveEmpty: {
    text: 'No {typePlural} on this board yet. Pick another board or create one first.',
    description: 'Empty state when a Bin move target board has no required parent nodes.',
    surface: 'manage',
    maxChars: 130,
  },
  NLSManageBinMoveHintGoalsRoot: {
    text: 'Goals land at the root of the chosen board.',
    description: 'Hint for moving goals out of Bin.',
    surface: 'manage',
    maxChars: 80,
  },
  NLSManageBinMoveParentLabel: {
    text: 'Parent {type} on this board',
    description: 'Label for the required parent selector when moving tasks or subtasks from Bin.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageBinMovePickParentToast: {
    text: 'Pick a parent on the chosen board.',
    description: 'Toast shown when move from Bin needs a parent selection.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageBinMoveSelectAria: {
    text: 'Select a {type} to restore under',
    description: 'Accessible label for selecting a parent during Bin restore.',
    surface: 'manage',
    maxChars: 80,
  },
  NLSManageBinMoveSelectPlaceholder: {
    text: 'Select a {type}...',
    description: 'Placeholder for parent selector during Bin restore.',
    surface: 'manage',
    maxChars: 44,
  },
  NLSManageBinMoveTitle: {
    text: 'Move out of Bin',
    description: 'Dialog title for moving an item out of Bin.',
    surface: 'manage',
    maxChars: 40,
  },
  NLSManageBoardArchive: {
    text: 'Archive',
    description: 'Manage board label for Archive.',
    surface: 'manage',
    maxChars: 22,
  },
  NLSManageBoardBin: {
    text: 'Bin',
    description: 'Manage board label for Bin.',
    surface: 'manage',
    maxChars: 16,
  },
  NLSManageBoardDashboard: {
    text: 'Dashboard',
    description: 'Manage board label for Dashboard.',
    surface: 'manage',
    maxChars: 24,
  },
  NLSManageBoardVisionboard: {
    text: 'Visionboard',
    description: 'Manage board label for Visionboard.',
    surface: 'manage',
    maxChars: 28,
  },
  NLSManageDialogDiscardAllSessionBody: {
    text: 'All pending edits are reverted AND items already committed during this Manage session will be rolled back in Firestore. This cannot be undone.',
    description: 'Body for discarding all pending and session-saved Manage changes.',
    surface: 'manage',
    maxChars: 220,
  },
  NLSManageDialogDiscardAllSessionTitle: {
    text: 'Discard everything (including saved this session)?',
    description: 'Title for discarding all pending and session-saved Manage changes.',
    surface: 'manage',
    maxChars: 90,
  },
  NLSManageReviewLiveUpdatesAvailable: {
    text: 'This plan changed while you were editing. Save or discard your changes to see the latest version.',
    description: 'Review notice shown when live goals, tasks, or checklist items changed while the user has unsaved edits.',
    surface: 'manage',
    maxChars: 140,
  },
  NLSManageReviewLiveUpdatesRefresh: {
    text: 'Discard and refresh',
    description: 'Button on the Review live-updates notice: discards unsaved edits so Review reloads the latest plan.',
    surface: 'manage',
    maxChars: 32,
  },
  NLSManageDialogDiscardAllUnsavedBody: {
    text: 'All unsaved edits in Manage will be reverted. Items already saved during this session will keep their committed state.',
    description: 'Body for discarding all unsaved Manage changes.',
    surface: 'manage',
    maxChars: 180,
  },
  NLSManageDialogDiscardAllUnsavedTitle: {
    text: 'Discard every pending change?',
    description: 'Title for discarding all unsaved Manage changes.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageDialogDiscardSelfBody: {
    text: 'Pending changes on this {noun} will be reverted to the previous state.{cascadeNote}',
    description: 'Body for discarding pending changes on a selected Manage item.',
    surface: 'manage',
    maxChars: 180,
  },
  NLSManageDialogDiscardSelfCascadeGoal: {
    text: ' This drops every task and subtask under it too.',
    description: 'Cascade note when discarding a goal in Manage.',
    surface: 'manage',
    maxChars: 90,
  },
  NLSManageDialogDiscardSelfCascadeTask: {
    text: ' This drops every subtask under it too.',
    description: 'Cascade note when discarding a task in Manage.',
    surface: 'manage',
    maxChars: 80,
  },
  NLSManageDialogDiscardSelfTitle: {
    text: 'Discard this {noun}?',
    description: 'Title for discarding pending changes on a selected Manage item.',
    surface: 'manage',
    maxChars: 60,
  },
  NLSManageDialogDiscardUnsavedBody: {
    text: "You have {count} unsaved {noun} that aren't marked for save. Closing now will revert them to their previous state. Marked items stay saved.",
    description: 'Dirty-close body in Manage. {count} is a number and {noun} is change/changes.',
    surface: 'manage',
    maxChars: 220,
  },
  NLSManageDialogDiscardUnsavedTitle: {
    text: 'Discard unsaved changes?',
    description: 'Dirty-close title when Manage has unsaved changes.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageDialogPerItemBody: {
    text: "You added {count} new {noun} but never clicked Save. Pick what should happen on close - closing won't proceed until each row has a choice.",
    description: 'Per-item discard body. {count} is a number and {noun} is item/items.',
    surface: 'manage',
    maxChars: 220,
  },
  NLSManageDialogPerItemTitle: {
    text: 'What should we do with these unsaved items?',
    description: 'Title for per-item discard choices on Manage close.',
    surface: 'manage',
    maxChars: 90,
  },
  NLSManageDialogPermanentDeleteBody: {
    text: 'This removes the item and everything inside it from Firestore. The action cannot be undone - it does not go back to Bin.',
    description: 'Body for permanent delete confirmation.',
    surface: 'manage',
    maxChars: 190,
  },
  NLSManageDialogPermanentDeleteTitle: {
    text: 'Permanently delete?',
    description: 'Title for permanent delete confirmation.',
    surface: 'manage',
    maxChars: 50,
  },
  NLSManageFieldBoard: {
    text: 'Board',
    description: 'Visible label for the Manage board selector.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageFieldTargetBoard: {
    text: 'Target board',
    description: 'Visible label for the Bin restore target board picker.',
    surface: 'manage',
    maxChars: 34,
  },
  NLSManageItemFallback: {
    text: 'item',
    description: 'Fallback noun for a Manage item.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageItemTitleFallback: {
    text: '(untitled)',
    description: 'Fallback title for a Manage item without a title.',
    surface: 'manage',
    maxChars: 24,
  },
  NLSManageKindGoal: {
    text: 'goal',
    description: 'Manage item kind label for a goal.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageKindGoalPlural: {
    text: 'goals',
    description: 'Plural Manage item kind label for goals.',
    surface: 'manage',
    maxChars: 22,
  },
  NLSManageKindItem: {
    text: 'item',
    description: 'Generic Manage item kind label.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageKindItems: {
    text: 'items',
    description: 'Generic plural Manage item kind label.',
    surface: 'manage',
    maxChars: 22,
  },
  NLSManageKindSubtask: {
    text: 'subtask',
    description: 'Manage item kind label for a subtask.',
    surface: 'manage',
    maxChars: 24,
  },
  NLSManageKindTask: {
    text: 'task',
    description: 'Manage item kind label for a task.',
    surface: 'manage',
    maxChars: 18,
  },
  NLSManageKindTaskPlural: {
    text: 'tasks',
    description: 'Plural Manage item kind label for tasks.',
    surface: 'manage',
    maxChars: 22,
  },
  NLSManageStatusCloseAbortedKeepEditing: {
    text: 'Close aborted - items marked "Keep editing" stay open.',
    description: 'Toast after per-item discard keeps Manage open.',
    surface: 'manage',
    maxChars: 100,
  },
  NLSManageStatusCouldNotSavePending: {
    text: 'Could not save pending items.',
    description: 'Fallback reason for failed pending-item save during Manage close.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageStatusDiscarded: {
    text: 'Discarded.',
    description: 'Short discard toast prefix.',
    surface: 'manage',
    maxChars: 28,
  },
  NLSManageStatusMoveFailed: {
    text: 'Move failed.',
    description: 'Fallback toast when moving an item from Bin fails.',
    surface: 'manage',
    maxChars: 40,
  },
  NLSManageStatusMovedOutOfBin: {
    text: 'Moved {title} out of Bin.',
    description: 'Toast after moving an item out of Bin.',
    surface: 'manage',
    maxChars: 90,
  },
  NLSManageStatusPendingDiscarded: {
    text: 'Pending changes discarded.',
    description: 'Toast after pending Manage changes are discarded.',
    surface: 'manage',
    maxChars: 60,
  },
  NLSManageStatusPendingDiscardedNothingSaved: {
    text: 'Pending changes discarded (nothing was saved this session).',
    description: 'Toast after all-session discard finds no saved changes to roll back.',
    surface: 'manage',
    maxChars: 100,
  },
  NLSManageStatusReverted: {
    text: 'Reverted.',
    description: 'Toast after selected Manage changes are reverted.',
    surface: 'manage',
    maxChars: 28,
  },
  NLSManageStatusSaveFailed: {
    text: 'Save failed: {reason}',
    description: 'Toast prefix for failed Manage save. {reason} is the error summary.',
    surface: 'manage',
    maxChars: 120,
  },
  NLSManageStatusSaved: {
    text: 'Saved.',
    description: 'Toast after one Manage item is saved.',
    surface: 'manage',
    maxChars: 24,
  },
  NLSManageStatusSavedCount: {
    text: 'Saved {count} items.',
    description: 'Toast after multiple Manage items are saved.',
    surface: 'manage',
    maxChars: 44,
  },
  NLSManageTitleBinItemNotFound: {
    text: 'Item not found',
    description: 'Dialog title when a Bin item is unavailable.',
    surface: 'manage',
    maxChars: 36,
  },
  NLSManageTitleClipboardEmpty: {
    text: 'Clipboard is empty',
    description: 'Tooltip when Detailed Review paste has no clipboard entry.',
    surface: 'manage',
    maxChars: 42,
  },
  NLSManageTitleCopyShortcut: {
    text: 'Copy (Cmd/Ctrl+C)',
    description: 'Detailed Review copy tooltip with keyboard shortcut.',
    surface: 'manage',
    maxChars: 42,
  },
  NLSManageTitleCutShortcut: {
    text: 'Cut (Cmd/Ctrl+X)',
    description: 'Detailed Review cut tooltip with keyboard shortcut.',
    surface: 'manage',
    maxChars: 40,
  },
  NLSManageTitleDeleteSelectTarget: {
    text: 'Select a goal / task / subtask to delete',
    description: 'Tooltip when Detailed Review delete has no valid target.',
    surface: 'manage',
    maxChars: 76,
  },
  NLSManageTitleManage: {
    text: 'Manage',
    description: 'Detailed View heading outside Manage chrome.',
    surface: 'manage',
    maxChars: 24,
  },
  NLSManageTitlePasteShortcut: {
    text: 'Paste (Cmd/Ctrl+V)',
    description: 'Detailed Review paste tooltip with keyboard shortcut.',
    surface: 'manage',
    maxChars: 44,
  },
  NLSManageTitleReview: {
    text: 'Review',
    description: 'Detailed Review heading inside Manage chrome.',
    surface: 'manage',
    maxChars: 24,
  },
  NLSManageTitleSwitchBoard: {
    text: 'Switch to Board view',
    description: 'Tooltip and accessible label for switching Manage to Board view.',
    surface: 'manage',
    maxChars: 44,
  },
  NLSManageTitleSwitchReview: {
    text: 'Switch to Review view',
    description: 'Tooltip and accessible label for switching Manage to Review view.',
    surface: 'manage',
    maxChars: 44,
  },
  NLSManageTitleBlockedEditField: {
    text: 'Edit one field before saving this draft.',
    description: 'Save button disabled reason when a confidence draft is locked.',
    surface: 'manage',
    maxChars: 80,
  },
  NLSManageTitleBlockedFixItems: {
    text: 'Fix the items flagged "needs attention" before saving.',
    description: 'Save button disabled reason when Manage has invalid items.',
    surface: 'manage',
    maxChars: 90,
  },
  NLSManageTitleDiscardAllSession: {
    text: 'Discard all including saved this session',
    description: 'Tooltip for all-session discard action.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageTitleFixBeforeClosing: {
    text: 'Fix the items flagged "needs attention" before closing.',
    description: 'Toast shown when closing Manage is blocked by invalid items.',
    surface: 'manage',
    maxChars: 90,
  },
  NLSManageTitleFixBlockedBeforeSave: {
    text: 'Fix blocked items before saving',
    description: 'Fallback title when a bulk Manage save is blocked.',
    surface: 'manage',
    maxChars: 60,
  },
  NLSManageTitleItemUnavailable: {
    text: 'This item is no longer available.',
    description: 'Dialog body when a Bin item is unavailable.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageTitleLockedEditing: {
    text: 'Locked - {name} is editing',
    description: 'Tooltip when a selected Manage item is locked by another user.',
    surface: 'manage',
    maxChars: 80,
  },
  NLSManageTitleNoChildItems: {
    text: 'No child items',
    description: 'Tooltip when selected Manage item has no descendants.',
    surface: 'manage',
    maxChars: 36,
  },
  NLSManageTitleNoPending: {
    text: 'Nothing pending',
    description: 'Tooltip when there are no pending Manage changes.',
    surface: 'manage',
    maxChars: 36,
  },
  NLSManageTitleNoPendingChildChanges: {
    text: 'No pending child changes',
    description: 'Tooltip when descendants have no pending Manage changes.',
    surface: 'manage',
    maxChars: 52,
  },
  NLSManageTitleNoPendingItem: {
    text: 'No pending changes on this item',
    description: 'Tooltip when selected Manage item has no pending self change.',
    surface: 'manage',
    maxChars: 64,
  },
  NLSManageTitleNoPendingSelection: {
    text: 'No pending changes in this selection',
    description: 'Tooltip when selected Manage subtree has no pending changes.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageTitleNothingToDiscard: {
    text: 'Nothing to discard',
    description: 'Tooltip when there is nothing to discard.',
    surface: 'manage',
    maxChars: 42,
  },
  NLSManageTitlePerItemApplyClose: {
    text: 'Apply choices and close.',
    description: 'Tooltip for enabled per-item discard close action.',
    surface: 'manage',
    maxChars: 60,
  },
  NLSManageTitlePerItemKeepOpen: {
    text: 'Close will abort - items marked "Keep editing" will stay open for you.',
    description: 'Tooltip when per-item discard choices include keep editing.',
    surface: 'manage',
    maxChars: 120,
  },
  NLSManageTitlePerItemPickEveryRow: {
    text: 'Pick an action for every row first.',
    description: 'Tooltip when per-item discard choices are incomplete.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageTitlePermanentDeleteCannotUndo: {
    text: 'Permanently delete (cannot be undone)',
    description: 'Tooltip for permanent delete from Bin.',
    surface: 'manage',
    maxChars: 70,
  },
  NLSManageTitleSaveParentFirst: {
    text: 'Save the parent first',
    description: 'Fallback title when save-order guard blocks a Manage item.',
    surface: 'manage',
    maxChars: 50,
  },
  NLSManageTitleSelectItemFirst: {
    text: 'Select an item first',
    description: 'Tooltip when a Manage action requires a selected item.',
    surface: 'manage',
    maxChars: 44,
  },
  NLSManageTitleSelectValidClipboardTarget: {
    text: 'Select a valid target for the {kind} on the clipboard',
    description: 'Tooltip when paste target is invalid. {kind} is copy or cut.',
    surface: 'manage',
    maxChars: 100,
  },
  NLSManageUserTeammateFallback: {
    text: 'a teammate',
    description: 'Fallback lock owner name when another editor locks an item.',
    surface: 'manage',
    maxChars: 30,
  },
  NLSChecklistActionAttachFiles: {
    text: 'Attach files',
    description: 'Checklist comment attachment picker action.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistActionCaptureCurrentLocation: {
    text: 'Capture Current Location',
    description: 'Checklist detail action that captures target location.',
    surface: 'checklist',
    maxChars: 44,
  },
  NLSChecklistActionClose: {
    text: 'Close',
    description: 'Checklist dialog close action.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistActionCreateItem: {
    text: 'Create item',
    description: 'Checklist new item save action.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistActionDelete: {
    text: 'Delete',
    description: 'Checklist delete action.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistActionDeleteNode: {
    text: 'Delete node',
    description: 'Checklist canvas node delete action.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistActionDiscard: {
    text: 'Discard',
    description: 'Checklist draft discard action.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistActionEdit: {
    text: 'Edit',
    description: 'Checklist edit action.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistActionOpenInCanvas: {
    text: 'Open in canvas',
    description: 'Checklist detail action that opens the item on the canvas.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistActionRecordVideoMemo: {
    text: 'Record Live Video Inspection Memo',
    description: 'Checklist proof action that records a video memo.',
    surface: 'checklist',
    maxChars: 64,
  },
  NLSChecklistActionReply: {
    text: 'Reply',
    description: 'Checklist comment reply action.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistActionReverse: {
    text: 'Reverse',
    description: 'Checklist financials reverse expense action.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistActionSave: {
    text: 'Save',
    description: 'Checklist detail save action.',
    surface: 'checklist',
    maxChars: 16,
  },
  NLSChecklistActionSaveLock: {
    text: 'Save & lock',
    description: 'Checklist detail save tooltip.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistActionSaveChanges: {
    text: 'Save changes',
    description: 'Checklist detail save button accessible label.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistActionSaving: {
    text: 'Saving...',
    description: 'Checklist detail saving state label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistActionVouchCompletion: {
    text: 'Vouch for completion',
    description: 'Checklist peer verification action.',
    surface: 'checklist',
    maxChars: 38,
  },
  NLSChecklistAriaClosePanel: {
    text: 'Close panel',
    description: 'Checklist panel close accessible label.',
    surface: 'checklist',
    maxChars: 30,
  },
  NLSChecklistAriaDateJump: {
    text: 'Jump to date',
    description: 'Checklist daily date jump input accessible label.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistAriaDeleteGate: {
    text: 'Delete this gate',
    description: 'Checklist logic gate delete accessible label.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistAriaIncomingWireAnchor: {
    text: 'Incoming wire anchor',
    description: 'Checklist node incoming connector accessible label.',
    surface: 'checklist',
    maxChars: 44,
  },
  NLSChecklistAriaOutgoingWireAnchor: {
    text: 'Outgoing wire anchor',
    description: 'Checklist node outgoing connector accessible label.',
    surface: 'checklist',
    maxChars: 44,
  },
  NLSChecklistAriaPostUpdate: {
    text: 'Post update',
    description: 'Checklist comment composer post accessible label.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistAriaProofApprover: {
    text: 'Proof approver',
    description: 'Checklist proof approver select accessible label.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistAriaRecurringChecklist: {
    text: 'Recurring {cadence} checklist',
    description: 'Checklist type chip accessible label. {cadence} is daily or weekly.',
    surface: 'checklist',
    maxChars: 56,
  },
  NLSChecklistAriaSpecificDate: {
    text: 'Jump to a specific date',
    description: 'Checklist occurrence sidebar date input label.',
    surface: 'checklist',
    maxChars: 48,
  },
  NLSChecklistAriaSpecificWeek: {
    text: 'Jump to a specific week',
    description: 'Checklist occurrence sidebar week input label.',
    surface: 'checklist',
    maxChars: 48,
  },
  NLSChecklistAriaWeekJump: {
    text: 'Jump to week',
    description: 'Checklist weekly date jump input accessible label.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistAttachmentDeleteConfirm: {
    text: 'Delete',
    description: 'Checklist attachment delete confirmation button label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistAttachmentDeleteTitle: {
    text: 'Delete attachment',
    description: 'Checklist attachment delete confirmation title.',
    surface: 'checklist',
    maxChars: 30,
  },
  NLSChecklistBoardNodeAssignBeforeWork: {
    text: 'Assign before work',
    description: 'Checklist canvas badge shown before work can be started.',
    surface: 'checklist',
    maxChars: 36,
  },
  NLSChecklistBoardNodeAssignedTo: {
    text: 'Assigned to {name}',
    description: 'Checklist canvas assignee tooltip. {name} is the assignee.',
    surface: 'checklist',
    maxChars: 70,
  },
  NLSChecklistBoardNodeGateDrag: {
    text: '{gate} gate (drag)',
    description: 'Checklist logic gate drag accessible label. {gate} is AND or OR.',
    surface: 'checklist',
    maxChars: 40,
  },
  NLSChecklistBoardNodeGateOutput: {
    text: '{gate} gate output',
    description: 'Checklist logic gate output connector accessible label. {gate} is AND or OR.',
    surface: 'checklist',
    maxChars: 40,
  },
  NLSChecklistBoardNodeGateOutputHint: {
    text: 'Drag from here to connect downstream',
    description: 'Checklist logic gate output tooltip.',
    surface: 'checklist',
    maxChars: 70,
  },
  NLSChecklistBoardNodeLockedBy: {
    text: 'Locked by {name}',
    description: 'Checklist canvas lock badge. {name} is lock holder.',
    surface: 'checklist',
    maxChars: 70,
  },
  NLSChecklistBoardNodeLockedByBlocker: {
    text: 'Locked by Blocker: {title}',
    description: 'Checklist canvas dependency blocker overlay. {title} is blocker title.',
    surface: 'checklist',
    maxChars: 90,
  },
  NLSChecklistBoardNodePendingCosign: {
    text: 'Pending co-sign',
    description: 'Checklist canvas pending review badge.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistCardDraftUnsaved: {
    text: 'Draft · unsaved',
    description: 'Checklist card eyebrow for unsaved drafts.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistCardStatusDone: {
    text: 'Done',
    description: 'Checklist card completed status.',
    surface: 'checklist',
    maxChars: 16,
  },
  NLSChecklistCardStatusOpen: {
    text: 'Open',
    description: 'Checklist card open status.',
    surface: 'checklist',
    maxChars: 16,
  },
  NLSChecklistCardUntitled: {
    text: 'Untitled checklist',
    description: 'Checklist card fallback title.',
    surface: 'checklist',
    maxChars: 36,
  },
  NLSChecklistCardUpdated: {
    text: 'Updated {time}',
    description: 'Checklist card updated timestamp. {time} is a compact relative time.',
    surface: 'checklist',
    maxChars: 46,
  },
  NLSChecklistCardWeight: {
    text: 'Weight {weight}',
    description: 'Checklist card weight label. {weight} is a number.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistCommentActionDelete: {
    text: 'Delete',
    description: 'Checklist comment delete action.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistCommentActionEdit: {
    text: 'Edit',
    description: 'Checklist comment edit action.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistCommentActionReply: {
    text: 'Reply',
    description: 'Checklist comment reply action.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistCommentDeleted: {
    text: 'Comment deleted',
    description: 'Deleted checklist comment placeholder.',
    surface: 'checklist',
    maxChars: 36,
  },
  NLSChecklistCommentMetaEdited: {
    text: 'Edited',
    description: 'Checklist comment edited metadata chip.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistCommentMetaReply: {
    text: 'Reply',
    description: 'Checklist comment reply metadata chip.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistCommentReactionCelebrate: {
    text: 'Celebrate',
    description: 'Checklist comment celebration reaction accessible label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistCommentReactionCheck: {
    text: 'Check',
    description: 'Checklist comment check reaction accessible label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistCommentReactionCount: {
    text: '{label} reaction {count}',
    description: 'Checklist comment reaction label with count.',
    surface: 'checklist',
    maxChars: 60,
  },
  NLSChecklistCommentReactionEyes: {
    text: 'Eyes',
    description: 'Checklist comment eyes reaction accessible label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistCommentReactionHeart: {
    text: 'Heart',
    description: 'Checklist comment heart reaction accessible label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistCommentReactionNoCount: {
    text: '{label} reaction',
    description: 'Checklist comment reaction label without count.',
    surface: 'checklist',
    maxChars: 46,
  },
  NLSChecklistCommentReactionThumbsUp: {
    text: 'Thumbs up',
    description: 'Checklist comment thumbs-up reaction accessible label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistDetailActive: {
    text: 'Active',
    description: 'Checklist recurring occurrence active state.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistDetailActivityHistory: {
    text: 'Activity history',
    description: 'Checklist detail activity history heading.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistDetailActivityDiscussion: {
    text: 'Node Activity & Discussion',
    description: 'Checklist detail activity and comments section title.',
    surface: 'checklist',
    maxChars: 58,
  },
  NLSChecklistDetailAddTitleToCreate: {
    text: 'Add a title to create',
    description: 'Checklist new item disabled save tooltip.',
    surface: 'checklist',
    maxChars: 44,
  },
  NLSChecklistDetailAssignee: {
    text: 'Assignee',
    description: 'Checklist assignee field label.',
    surface: 'checklist',
    maxChars: 22,
  },
  NLSChecklistDetailAssigneeAria: {
    text: 'Checklist assignee',
    description: 'Checklist assignee select accessible label.',
    surface: 'checklist',
    maxChars: 42,
  },
  NLSChecklistDetailChecklistDetails: {
    text: 'Checklist details',
    description: 'Checklist detail dialog fallback aria label.',
    surface: 'checklist',
    maxChars: 38,
  },
  NLSChecklistDetailCommentEditing: {
    text: 'Editing comment',
    description: 'Checklist comment composer editing context.',
    surface: 'checklist',
    maxChars: 38,
  },
  NLSChecklistDetailCommentReplying: {
    text: 'Replying to comment',
    description: 'Checklist comment composer reply context.',
    surface: 'checklist',
    maxChars: 42,
  },
  NLSChecklistDetailCompletionPerInstance: {
    text: 'Completion is per instance · detail saves can target one or future instances',
    description: 'Checklist recurring occurrence sidebar footer.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistDetailCreatedBy: {
    text: 'Created by',
    description: 'Checklist creator field label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistDetailDependencies: {
    text: 'Dependencies',
    description: 'Checklist dependency section title.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistDetailExternalLinks: {
    text: 'External links',
    description: 'Checklist external links section header.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistExternalLinksEmpty: {
    text: 'Not linked to Notion or Jira',
    description: 'Empty state message when checklist item has no external links.',
    surface: 'checklist',
    maxChars: 60,
  },
  NLSChecklistDetailDetails: {
    text: 'Details',
    description: 'Checklist details section title.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistDetailDraftBar: {
    text: 'New checklist · Draft',
    description: 'Checklist detail draft header eyebrow.',
    surface: 'checklist',
    maxChars: 42,
  },
  NLSChecklistDetailDraftFooter: {
    text: 'This checklist will not be saved until you press Save.',
    description: 'Checklist draft footer hint.',
    surface: 'checklist',
    maxChars: 90,
  },
  NLSChecklistDetailEmpty: {
    text: 'Empty',
    description: 'Checklist detail empty state chip.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistDetailLoadingItem: {
    text: 'Loading checklist item...',
    description: 'Checklist detail loading message.',
    surface: 'checklist',
    maxChars: 50,
  },
  NLSChecklistDetailNewChecklist: {
    text: 'New checklist',
    description: 'Checklist detail new checklist dialog label.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistDetailNoChanges: {
    text: 'No changes to save',
    description: 'Checklist detail disabled save tooltip.',
    surface: 'checklist',
    maxChars: 42,
  },
  NLSChecklistDetailNoDiscussion: {
    text: 'No discussion yet.',
    description: 'Checklist detail comments empty state.',
    surface: 'checklist',
    maxChars: 42,
  },
  NLSChecklistDetailNoRecordedActivity: {
    text: 'No recorded activity yet.',
    description: 'Checklist detail activity empty state.',
    surface: 'checklist',
    maxChars: 48,
  },
  NLSChecklistDetailOptions: {
    text: 'Options',
    description: 'Checklist draft options section title.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistDetailPostingUpdate: {
    text: 'Posting update',
    description: 'Checklist comment sending status.',
    surface: 'checklist',
    maxChars: 36,
  },
  NLSChecklistDetailPostPlaceholder: {
    text: 'Add a node update or @mention a teammate',
    description: 'Checklist comment composer placeholder.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistDetailProofOfCompletion: {
    text: 'Proof of completion',
    description: 'Checklist proof requirements group label.',
    surface: 'checklist',
    maxChars: 44,
  },
  NLSChecklistDetailProofGated: {
    text: 'Proof gated',
    description: 'Checklist proof section chip when proof is required.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistDetailRecurrenceInstances: {
    text: 'Recurrence · Instances',
    description: 'Checklist recurring occurrence sidebar heading.',
    surface: 'checklist',
    maxChars: 46,
  },
  NLSChecklistDetailSaveUnlocksOptions: {
    text: 'Save this checklist to unlock financials, dependencies, and attachments.',
    description: 'Checklist draft options hint.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistDetailSet: {
    text: 'Set',
    description: 'Checklist detail set state chip.',
    surface: 'checklist',
    maxChars: 16,
  },
  NLSChecklistDetailUnassigned: {
    text: 'Unassigned',
    description: 'Checklist assignee empty option.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistDetailUploadingAttachments: {
    text: 'Uploading attachments',
    description: 'Checklist comment attachment upload status.',
    surface: 'checklist',
    maxChars: 46,
  },
  NLSChecklistDetailUnavailable: {
    text: 'This checklist item is no longer available.',
    description: 'Checklist detail unavailable message.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistDetailUntitled: {
    text: 'Untitled',
    description: 'Checklist detail fallback title.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistDependencyBlockedByEmpty: {
    text: 'Nothing is blocking this node yet.',
    description: 'Checklist dependency empty state for blockers.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistDependencyBlockedByTitle: {
    text: 'is blocked by',
    description: 'Checklist dependency list title for upstream blockers.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistDependencyBlockerForEmpty: {
    text: 'This node is not blocking anything yet.',
    description: 'Checklist dependency empty state for downstream blocked items.',
    surface: 'checklist',
    maxChars: 90,
  },
  NLSChecklistDependencyBlockerForTitle: {
    text: 'is a blocker for',
    description: 'Checklist dependency list title for downstream items.',
    surface: 'checklist',
    maxChars: 38,
  },
  NLSChecklistFieldBy: {
    text: 'by',
    description: 'Checklist duration end date label.',
    surface: 'checklist',
    maxChars: 12,
  },
  NLSChecklistFieldCompletionCriteria: {
    text: 'Completion criteria',
    description: 'Checklist completion criteria field label.',
    surface: 'checklist',
    maxChars: 42,
  },
  NLSChecklistFieldDated: {
    text: 'Dated',
    description: 'Checklist dated schedule option.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistFieldDescription: {
    text: 'Description',
    description: 'Checklist description field label.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistFieldDuration: {
    text: 'Duration',
    description: 'Checklist duration schedule option.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistFieldEndDate: {
    text: 'End date',
    description: 'Checklist end date input accessible label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistFieldExpenseOutlay: {
    text: 'Expense Outlay',
    description: 'Checklist expense outlay dial label.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistFieldEstimatedHours: {
    text: 'ETC (hrs)',
    description: 'Checklist estimated time label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistFieldFrom: {
    text: 'From',
    description: 'Checklist duration start label.',
    surface: 'checklist',
    maxChars: 16,
  },
  NLSChecklistFieldOn: {
    text: 'On',
    description: 'Checklist dated schedule label.',
    surface: 'checklist',
    maxChars: 12,
  },
  NLSChecklistFieldPlaceholderCompletion: {
    text: 'State the conditions that define completion',
    description: 'Checklist completion criteria placeholder.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistFieldPlaceholderDescription: {
    text: 'Describe the work to be done',
    description: 'Checklist description placeholder.',
    surface: 'checklist',
    maxChars: 60,
  },
  NLSChecklistFieldPlaceholderTitle: {
    text: 'Checklist item title',
    description: 'Checklist title input placeholder.',
    surface: 'checklist',
    maxChars: 48,
  },
  NLSChecklistFieldRiskResourceSummary: {
    text: 'Risk & Resource Summary',
    description: 'Checklist risk and resource section title.',
    surface: 'checklist',
    maxChars: 54,
  },
  NLSChecklistFieldScheduleType: {
    text: 'Schedule type',
    description: 'Checklist schedule type radiogroup label.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistFieldStartDate: {
    text: 'Start date',
    description: 'Checklist start date input accessible label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistFieldTimeFrom: {
    text: 'from',
    description: 'Checklist time window start label.',
    surface: 'checklist',
    maxChars: 16,
  },
  NLSChecklistFieldTimeTo: {
    text: 'to',
    description: 'Checklist time window end label.',
    surface: 'checklist',
    maxChars: 16,
  },
  NLSChecklistFieldTimeWindow: {
    text: 'Time window',
    description: 'Checklist time window toggle label.',
    surface: 'checklist',
    maxChars: 30,
  },
  NLSChecklistFieldTitle: {
    text: 'Title',
    description: 'Checklist title field label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistFieldWeight: {
    text: 'Weight',
    description: 'Checklist weight label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistFieldRecordEstimateToggle: {
    text: 'Record estimate as spend when completed',
    description: 'Toggle to record estimate as spend upon checklist completion',
    surface: 'checklist',
    maxChars: 50,
  },
  NLSChecklistFieldRecordEstimateHintNoEstimate: {
    text: 'Set an estimated cost above to enable automatic spend recording on completion.',
    description: 'Hint when record estimate toggle is disabled because no estimated cost is set',
    surface: 'checklist',
    maxChars: 90,
  },
  NLSChecklistFieldRecordEstimateCategoryLabel: {
    text: 'Spend category',
    description: 'Label for spend category selector on checklist record estimate card',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistFieldRecordEstimateRecorded: {
    text: 'Recorded {amount} on {date}',
    description: 'Notice that estimate was recorded in ledger upon checklist completion',
    surface: 'checklist',
    maxChars: 60,
  },
  NLSChecklistFieldRecordEstimateViewLedger: {
    text: 'View in ledger',
    description: 'Link to view recorded expense in budget ledger',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistFieldRecordEstimateHintDisabled: {
    text: 'Automatically post to budget ledger when marked completed.',
    description: 'Hint when record estimate toggle is disabled but estimate is available',
    surface: 'checklist',
    maxChars: 70,
  },
  NLSChecklistFieldBudgetBindingUpdateFailed: {
    text: 'Could not update budget binding.',
    description: 'Error notice when updating checklist budget binding fails',
    surface: 'checklist',
    maxChars: 40,
  },
  NLSMemberChecklistAutoSpendBadge: {
    text: 'Auto-Spend',
    description: 'Badge indicating checklist item has automated budget spend binding',
    surface: 'member',
    maxChars: 16,
  },
  NLSMemberChecklistAutoSpendNotice: {
    text: 'Will record estimate as spend ({category}) on completion',
    description: 'Read-only notice on member checklist detail showing auto spend binding',
    surface: 'member',
    maxChars: 70,
  },
  NLSMemberChecklistSpendRecordedNotice: {
    text: 'Recorded spend: {amount}',
    description: 'Read-only notice on member checklist detail showing recorded spend on completion',
    surface: 'member',
    maxChars: 40,
  },
  NLSChecklistKindDaily: {
    text: 'Daily',
    description: 'Checklist daily recurrence label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistWorkspaceDayLabel: {
    text: 'Workspace day: {day}',
    description: 'Workspace day label shown when device day differs from workspace day. {day} is formatted weekday and date.',
    surface: 'checklist',
    maxChars: 40,
  },
  NLSChecklistWindowEnded: {
    text: 'Window ended',
    description: 'Chip label shown when repeating checklist window has ended.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistMissedPeriods: {
    text: 'Missed: {periods}',
    description: 'Chip label listing missed periods for a repeating dependency. {periods} is comma separated dates.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistChipLocked: {
    text: 'Locked',
    description: 'Chip label shown when a checklist item is locked by a dependency.',
    surface: 'checklist',
    maxChars: 15,
  },
  NLSChecklistLockReasonInBin: {
    text: 'Waiting on an item in the Bin',
    description: 'Accessible readout and notice when a checklist item is blocked by a dependency that is in the Bin.',
    surface: 'checklist',
    maxChars: 45,
  },
  NLSChecklistTypeSwitchS5Notice: {
    text: 'Switching makes a new item. Its links are removed and its history stays on the old one.',
    description: 'Notice shown in checklist type switch wizard before deriving a new item.',
    surface: 'checklist',
    maxChars: 100,
  },
  NLSChecklistKindIndividual: {
    text: 'Individual',
    description: 'Checklist individual mode label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistKindWeekly: {
    text: 'Weekly',
    description: 'Checklist weekly recurrence label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistKindDefault: {
    text: 'Checklist',
    description: 'Default checklist book name.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistOccurrenceArchived: {
    text: 'Archived',
    description: 'Checklist occurrence archived state.',
    surface: 'checklist',
    maxChars: 22,
  },
  NLSChecklistOccurrenceArchivedBeyondParent: {
    text: 'Archived · beyond parent range',
    description: 'Checklist recurring occurrence archived meta.',
    surface: 'checklist',
    maxChars: 60,
  },
  NLSChecklistOccurrenceArchivedTooltip: {
    text: 'Archived occurrence - parent range has passed.',
    description: 'Checklist recurring occurrence archived tooltip.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistRepeatPausedParentDates: {
    text: 'Repeat paused: parent needs both dates',
    description: 'Notice shown when a repeating item is paused because its parent element is missing start or end date.',
    surface: 'checklist',
    maxChars: 60,
  },
  NLSChecklistOccurrenceCompleted: {
    text: 'Completed {time}',
    description: 'Checklist occurrence completed timestamp. {time} is localized time.',
    surface: 'checklist',
    maxChars: 60,
  },
  NLSChecklistOccurrenceCompletedBy: {
    text: 'Completed by {name}',
    description: 'Checklist occurrence completed-by meta. {name} is a display name.',
    surface: 'checklist',
    maxChars: 70,
  },
  NLSChecklistOccurrenceCompletedByArchived: {
    text: 'Completed by {name} · archived',
    description: 'Checklist occurrence completed-by archived meta. {name} is a display name.',
    surface: 'checklist',
    maxChars: 90,
  },
  NLSChecklistOccurrenceDailyHistory: {
    text: 'Daily history',
    description: 'Checklist daily occurrence popover heading.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistOccurrenceNotCompleted: {
    text: 'Not completed',
    description: 'Checklist occurrence incomplete past state.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistOccurrencePast: {
    text: 'Past occurrence',
    description: 'Checklist past occurrence meta.',
    surface: 'checklist',
    maxChars: 38,
  },
  NLSChecklistOccurrenceThisWeek: {
    text: 'This week',
    description: 'Checklist current week label.',
    surface: 'checklist',
    maxChars: 22,
  },
  NLSChecklistOccurrenceToday: {
    text: 'Today',
    description: 'Checklist today label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistOccurrenceUpcoming: {
    text: 'Upcoming',
    description: 'Checklist upcoming occurrence label.',
    surface: 'checklist',
    maxChars: 22,
  },
  NLSChecklistOccurrenceUpcomingTooltip: {
    text: "Upcoming - parent range hasn't started yet.",
    description: 'Checklist recurring occurrence upcoming tooltip.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistOccurrenceWeeklyHistory: {
    text: 'Weekly history',
    description: 'Checklist weekly occurrence popover heading.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistProofAutoComplete: {
    text: 'Auto-complete on upload',
    description: 'Checklist proof approver empty option.',
    surface: 'checklist',
    maxChars: 46,
  },
  NLSChecklistProofApprover: {
    text: 'Approver',
    description: 'Checklist proof approver field label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistProofApproverHelpAssigned: {
    text: 'Item moves to Assessing on proof upload and only the approver can complete it.',
    description: 'Checklist proof approver assigned help text.',
    surface: 'checklist',
    maxChars: 130,
  },
  NLSChecklistProofApproverHelpAuto: {
    text: 'No approver - proof upload auto-completes the item.',
    description: 'Checklist proof approver automatic completion help text.',
    surface: 'checklist',
    maxChars: 100,
  },
  NLSChecklistProofDropFile: {
    text: 'Drop proof file or click to attach',
    description: 'Checklist proof upload dropzone label.',
    surface: 'checklist',
    maxChars: 70,
  },
  NLSChecklistProofEnforceLocation: {
    text: 'Enforce Local Location Check-In',
    description: 'Checklist proof requirement toggle for location check-ins.',
    surface: 'checklist',
    maxChars: 62,
  },
  NLSChecklistProofFile: {
    text: 'Proof file',
    description: 'Checklist proof file section label.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistProofFileRequired: {
    text: 'Proof file Required',
    description: 'Checklist proof requirement toggle for file uploads.',
    surface: 'checklist',
    maxChars: 42,
  },
  NLSChecklistProofFileAttached: {
    text: 'Proof attached',
    description: 'Checklist proof attachment fallback name.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistProofLocationGate: {
    text: 'Location gate',
    description: 'Checklist location gate section label.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistProofOptional: {
    text: 'optional',
    description: 'Checklist proof approver optional hint.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistProofPeerVerification: {
    text: 'Peer Verification',
    description: 'Checklist peer verification section label.',
    surface: 'checklist',
    maxChars: 40,
  },
  NLSChecklistProofPeerVerificationHelp: {
    text: "Vouch for this helper's physical completion. The reliability boost is applied immediately.",
    description: 'Checklist peer verification help text.',
    surface: 'checklist',
    maxChars: 140,
  },
  NLSChecklistProofAssignedHelper: {
    text: 'Assigned helper',
    description: 'Checklist peer verification assignee fallback.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistProofPlayMemo: {
    text: 'Play memo',
    description: 'Checklist voice memo playback accessible label.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistProofRecordMemo: {
    text: 'Record memo',
    description: 'Checklist voice memo record accessible label.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistProofRecorded: {
    text: 'Recorded',
    description: 'Checklist video memo recorded status.',
    surface: 'checklist',
    maxChars: 22,
  },
  NLSChecklistProofRecording: {
    text: 'Recording...',
    description: 'Checklist proof recording busy label.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistProofRemoveProof: {
    text: 'Remove proof',
    description: 'Checklist proof removal accessible label.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistProofStopRecording: {
    text: 'Stop recording',
    description: 'Checklist voice memo stop recording accessible label.',
    surface: 'checklist',
    maxChars: 38,
  },
  NLSChecklistProofThirdPartyRequired: {
    text: 'Third-party verification is required.',
    description: 'Checklist peer verification unavailable hint.',
    surface: 'checklist',
    maxChars: 70,
  },
  NLSChecklistProofVideoMemo: {
    text: 'Video memo',
    description: 'Checklist video memo section label.',
    surface: 'checklist',
    maxChars: 26,
  },
  NLSChecklistProofVideoInspectionMemo: {
    text: 'Video inspection memo',
    description: 'Checklist video recorder modal title.',
    surface: 'checklist',
    maxChars: 44,
  },
  NLSChecklistProofVideoReady: {
    text: 'Ready',
    description: 'Checklist video recorder ready status.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistProofVideoStart15s: {
    text: 'Start 15s Recording',
    description: 'Checklist video recorder start action.',
    surface: 'checklist',
    maxChars: 46,
  },
  NLSChecklistProofVoiceNoMemo: {
    text: 'No memo',
    description: 'Checklist voice memo empty label.',
    surface: 'checklist',
    maxChars: 20,
  },
  NLSChecklistProofVoiceRecording: {
    text: 'Recording',
    description: 'Checklist voice memo recording label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistShellJumpThisWeek: {
    text: 'This week',
    description: 'Checklist weekly jump button label.',
    surface: 'checklist',
    maxChars: 22,
  },
  NLSChecklistShellAlreadyThisWeek: {
    text: 'Already on this week',
    description: 'Checklist weekly paginator disabled return-to-current tooltip.',
    surface: 'checklist',
    maxChars: 44,
  },
  NLSChecklistShellAlreadyToday: {
    text: 'Already on today',
    description: 'Checklist daily paginator disabled return-to-current tooltip.',
    surface: 'checklist',
    maxChars: 38,
  },
  NLSChecklistShellAriaCanvasView: {
    text: 'Canvas view',
    description: 'Checklist shell view tablist accessible label.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistShellAriaOccurrenceNavigation: {
    text: 'Occurrence navigation',
    description: 'Checklist shell recurring paginator accessible label.',
    surface: 'checklist',
    maxChars: 46,
  },
  NLSChecklistShellAriaPage: {
    text: 'Checklists',
    description: 'Checklist page accessible label.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistShellEmptyNoBoard: {
    text: 'Open a Board / Goal / Task from the sidebar to see its checklists here.',
    description: 'Checklist shell empty state when no parent board item is selected.',
    surface: 'checklist',
    maxChars: 130,
  },
  NLSChecklistShellEmptyNoWorkspace: {
    text: 'Open a workspace to see checklists.',
    description: 'Checklist shell empty state when no workspace is active.',
    surface: 'checklist',
    maxChars: 70,
  },
  NLSChecklistShellLoadingCanvas: {
    text: 'Loading canvas...',
    description: 'Checklist shell canvas loading fallback.',
    surface: 'checklist',
    maxChars: 42,
  },
  NLSChecklistShellRecurringSettings: {
    text: 'Recurring settings',
    description: 'Checklist recurring settings action.',
    surface: 'checklist',
    maxChars: 42,
  },
  NLSChecklistShellSubtitle: {
    text: 'Boards & flows',
    description: 'Checklist shell page subtitle.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistShellTitle: {
    text: 'Checklists',
    description: 'Checklist shell page title.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistShellJumpThisWeekAria: {
    text: 'Jump to this week',
    description: 'Checklist weekly jump button accessible label.',
    surface: 'checklist',
    maxChars: 38,
  },
  NLSChecklistShellJumpToday: {
    text: 'Today',
    description: 'Checklist daily jump button label.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistShellJumpTodayAria: {
    text: 'Jump to today',
    description: 'Checklist daily jump button accessible label.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistShellNextDay: {
    text: 'Next day',
    description: 'Checklist shell next day button accessible label.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistShellNextWeek: {
    text: 'Next week',
    description: 'Checklist shell next week button accessible label.',
    surface: 'checklist',
    maxChars: 28,
  },
  NLSChecklistShellPreviousDay: {
    text: 'Previous day',
    description: 'Checklist shell previous day button accessible label.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistShellPreviousWeek: {
    text: 'Previous week',
    description: 'Checklist shell previous week button accessible label.',
    surface: 'checklist',
    maxChars: 34,
  },
  NLSChecklistStatusClear: {
    text: 'Clear',
    description: 'Checklist dependency clear status chip.',
    surface: 'checklist',
    maxChars: 18,
  },
  NLSChecklistStatusOpen: {
    text: 'Open',
    description: 'Checklist open status chip.',
    surface: 'checklist',
    maxChars: 16,
  },
  NLSChecklistStatusBlocked: {
    text: 'Blocked',
    description: 'Checklist blocked status chip.',
    surface: 'checklist',
    maxChars: 22,
  },
  NLSChecklistStatusNotNeeded: {
    text: 'Not needed',
    description: 'Checklist not needed status chip label.',
    surface: 'checklist',
    maxChars: 24,
  },
  NLSChecklistNotNeededTitle: {
    text: 'Mark not needed',
    description: 'Dialog title when marking a repeating checklist item as not needed.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistNotNeededJustThisDay: {
    text: 'Just this day',
    description: 'Radio option to mark a daily checklist item not needed for this day only.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistNotNeededFromTodayOn: {
    text: 'From today on',
    description: 'Radio option to mark a daily checklist item not needed from this day onward.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistNotNeededJustThisWeek: {
    text: 'Just this week',
    description: 'Radio option to mark a weekly checklist item not needed for this week only.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistNotNeededFromThisWeekOn: {
    text: 'From this week on',
    description: 'Radio option to mark a weekly checklist item not needed from this week onward.',
    surface: 'checklist',
    maxChars: 36,
  },
  NLSChecklistNotNeededConfirm: {
    text: 'Mark not needed',
    description: 'Confirm button label for marking a repeating checklist item not needed.',
    surface: 'checklist',
    maxChars: 32,
  },
  NLSChecklistNotifyUnlockedTitle: {
    text: 'Ready to start: {title}',
    description: 'Notification title when a checklist item is unlocked.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyUnlockedBody: {
    text: '{done} is done{dayLabel}.',
    description: 'Notification body when a checklist item is unlocked.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistNotifyLinkClearedBody: {
    text: 'Nothing blocks this item any more.',
    description: 'Notification body when an item is unlocked because its dependency links were removed.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistNotifyRelockedTitle: {
    text: 'On hold again: {title}',
    description: 'Notification title when a checklist item is relocked.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyRelockedBody: {
    text: 'A dependency now blocks this item again{dayLabel}.',
    description: 'Notification body when a checklist item is relocked.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistNotifyNotRequiredTitle: {
    text: 'Not needed: {title}{dayLabel}',
    description: 'Notification title when a checklist item is marked not required.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyNotRequiredBody: {
    text: 'This item was marked not needed{dayLabel}.',
    description: 'Notification body when a checklist item is marked not required.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistNotifyProofSubmittedTitle: {
    text: 'Proof needs review: {title}{dayLabel}',
    description: 'Notification title when proof is submitted for review.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyProofSubmittedBody: {
    text: '{submitter} submitted proof for your approval.',
    description: 'Notification body when proof is submitted for review.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistNotifyApprovedTitle: {
    text: 'Approved: {title}{dayLabel}',
    description: 'Notification title when proof is approved.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyApprovedBody: {
    text: '{approver} approved your proof. Nice work.',
    description: 'Notification body when proof is approved.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistNotifyRejectedTitle: {
    text: 'Proof rejected: {title}{dayLabel}',
    description: 'Notification title when proof is rejected.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyRejectedBody: {
    text: '{note}',
    description: 'Notification body when proof is rejected.',
    surface: 'checklist',
    maxChars: 200,
  },
  NLSChecklistNotifyStatusChangedTitle: {
    text: 'Status changed: {title}',
    description: 'Notification title when checklist item status changes.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyStatusChangedBody: {
    text: '{from} -> {to}.',
    description: 'Notification body when checklist item status changes.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyAssignedTitle: {
    text: 'Assigned: {title}',
    description: 'Notification title when checklist item is assigned.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyAssignedBody: {
    text: 'This checklist item is now assigned to you.',
    description: 'Notification body when checklist item is assigned.',
    surface: 'checklist',
    maxChars: 100,
  },
  NLSChecklistNotifyBlockedTitle: {
    text: 'Blocked: {title}',
    description: 'Notification title when checklist item is blocked.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyBlockedBody: {
    text: 'A dependency now blocks this checklist item.',
    description: 'Notification body when checklist item is blocked.',
    surface: 'checklist',
    maxChars: 100,
  },
  NLSChecklistNotifyDueTitle: {
    text: 'Due today: {title}{dayLabel}',
    description: 'Notification title when a checklist item is due today.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyDueBody: {
    text: 'This item is due today{dayLabel}.',
    description: 'Notification body when a checklist item is due today.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistNotifyMissedTitle: {
    text: 'Missed: {title}{dayLabel}',
    description: 'Notification title when a checklist item period was missed.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyMissedBody: {
    text: 'This item was missed{dayLabel}.',
    description: 'Notification body when a checklist item period was missed.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistNotifyWindowEndingTitle: {
    text: 'Last day tomorrow: {title}',
    description: 'Notification title when tomorrow is the last day of a recurring window.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifyWindowEndingBody: {
    text: 'Tomorrow is the last scheduled day for this item.',
    description: 'Notification body when tomorrow is the last day of a recurring window.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChecklistNotifySummaryTitle: {
    text: "Today's repeats: {due} due, {done} done, {missed} missed",
    description: 'Notification title for the daily bundled repeats summary.',
    surface: 'checklist',
    maxChars: 80,
  },
  NLSChecklistNotifySummaryBody: {
    text: 'Daily repeat checklist summary for today.',
    description: 'Notification body for the daily bundled repeats summary.',
    surface: 'checklist',
    maxChars: 120,
  },
  NLSChatStatusSending: {
    text: 'Sending',
    description: 'Status label for WhatsApp sending message',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatStatusSent: {
    text: 'Sent',
    description: 'Status label for WhatsApp sent message',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatStatusDelivered: {
    text: 'Delivered',
    description: 'Status label for WhatsApp delivered message',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatStatusRead: {
    text: 'Read',
    description: 'Status label for WhatsApp read message',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatStatusHeldOutsideWindow: {
    text: 'Held outside 24-hour window',
    description: 'Status label for WhatsApp held message',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatStatusPaused: {
    text: 'Sending paused',
    description: 'Status label for WhatsApp paused message',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatStatusFailedToSend: {
    text: 'Failed to send',
    description: 'Status label for WhatsApp failed message',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatSectionWhatsApp: {
    text: 'WhatsApp',
    description: 'WhatsApp section title in chat lists',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatSectionPinned: {
    text: 'Pinned',
    description: 'Pinned section title in member picker',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatSectionChannels: {
    text: 'Channels',
    description: 'Channels section title in member picker',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatSectionDMs: {
    text: 'DMs',
    description: 'DMs section title in member picker',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatNoChannelMatchesHint: {
    text: 'Try another channel name or open the switcher.',
    description: 'Hint when no channel matches query in member picker',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatRouteTimelining: {
    text: 'Timelining number',
    description: 'Route chip label for Timelining number',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatRouteOwn: {
    text: 'Own number',
    description: 'Route chip label for Own number',
    surface: 'chat',
    maxChars: 25,
  },
  NLSChatReplyWindowLeft: {
    text: 'Reply window: {hours} h left',
    description: 'Reply window timer text with hours remaining',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatReplyWindowClosed: {
    text: 'Reply window closed',
    description: 'Reply window closed text',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatAnalysisOff: {
    text: 'Analysis off',
    description: 'Muted chip for AI analysis disabled',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatTagViaPhone: {
    text: 'via phone app',
    description: 'Tag indicating message was sent via WhatsApp Business phone app',
    surface: 'chat',
    maxChars: 25,
  },
  NLSChatTagImported: {
    text: 'Imported',
    description: 'Tag indicating imported history message',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatNoticePaused: {
    text: 'WhatsApp sending is paused — add credits or connect your own number',
    description: 'Notice banner text when WhatsApp channel is paused',
    surface: 'chat',
    maxChars: 85,
  },
  NLSChatHintCost: {
    text: "Sent from the Timelining number. Over your plan's monthly cap, every 4 messages use 1 credit.",
    description: 'One-line cost hint above composer on Timelining number',
    surface: 'chat',
    maxChars: 110,
  },
  NLSChatHintClosedWindow: {
    text: 'The recipient will receive an alert and the message is delivered when they reply.',
    description: 'Hint above composer when 24h reply window is closed',
    surface: 'chat',
    maxChars: 110,
  },
  NLSLinkHubWhatsAppFootnote: {
    text: 'Uses credits: 1 credit for every 4 messages sent from the Timelining number. Messages people send you are free. Connect your own WhatsApp Business number to send for free.',
    description: 'Footnote for Timelining number in Link Hub WhatsApp settings',
    surface: 'linkhub',
    maxChars: 180,
  },
  NLSLinkHubWhatsAppUsageLiveNotice: {
    text: 'Usage appears once WhatsApp is live',
    description: 'Notice shown when usage callable is not yet deployed',
    surface: 'linkhub',
    maxChars: 50,
  },
  NLSLinkHubWhatsAppOwnRequirements: {
    text: 'Requires Meta Business verification and WhatsApp Business phone app on this number.',
    description: 'One line requirements for connecting own number',
    surface: 'linkhub',
    maxChars: 100,
  },
  NLSLinkHubWhatsAppOwnOnlyOwner: {
    text: 'Only workspace owners can connect a custom WhatsApp number',
    description: 'Helper text when connect own number is disabled for admin',
    surface: 'linkhub',
    maxChars: 80,
  },
  NLSLinkHubWhatsAppOwnLimits: {
    text: 'Until Meta verification completes, Meta limits new outbound conversations to ~250/day. No green badge.',
    description: 'Meta limits for custom WhatsApp number',
    surface: 'linkhub',
    maxChars: 120,
  },
  NLSLinkHubWhatsAppOwnPending: {
    text: 'Meta review pending — connecting own numbers will unlock when approved.',
    description: 'Pending review notice for custom WhatsApp number',
    surface: 'linkhub',
    maxChars: 100,
  },
  NLSLinkHubWhatsAppRouteTimelining: {
    text: 'Use the Timelining number',
    description: 'Radio option for using Timelining WhatsApp number',
    surface: 'linkhub',
    maxChars: 40,
  },
  NLSLinkHubWhatsAppRouteOwn: {
    text: 'Connect our WhatsApp number',
    description: 'Radio option for connecting own WhatsApp number',
    surface: 'linkhub',
    maxChars: 40,
  },
  NLSLinkHubWhatsAppAudiencesMin: {
    text: 'Select at least one audience when WhatsApp is enabled.',
    description: 'Validation error when no audience is selected',
    surface: 'linkhub',
    maxChars: 70,
  },
  NLSLinkHubWhatsAppAudienceHelper: {
    text: 'Helpers',
    description: 'Audience checkbox label for helpers',
    surface: 'linkhub',
    maxChars: 20,
  },
  NLSLinkHubWhatsAppAudienceFieldPass: {
    text: 'Field Pass holders',
    description: 'Audience checkbox label for field pass holders',
    surface: 'linkhub',
    maxChars: 25,
  },
  NLSLinkHubWhatsAppAudienceGuardian: {
    text: 'Guardians / parents',
    description: 'Audience checkbox label for guardians and parents',
    surface: 'linkhub',
    maxChars: 25,
  },
  NLSLinkHubWhatsAppAudienceClient: {
    text: 'Clients',
    description: 'Audience checkbox label for clients',
    surface: 'linkhub',
    maxChars: 20,
  },
  NLSLinkHubWhatsAppAudienceVendor: {
    text: 'Vendors',
    description: 'Audience checkbox label for vendors',
    surface: 'linkhub',
    maxChars: 20,
  },
  NLSLinkHubWhatsAppNotifyEducation: {
    text: 'Education attendance alerts',
    description: 'Notification checkbox for education alerts',
    surface: 'linkhub',
    maxChars: 40,
  },
  NLSLinkHubWhatsAppNotifyHelperJobs: {
    text: 'Helper job notices',
    description: 'Notification checkbox for helper job notices',
    surface: 'linkhub',
    maxChars: 30,
  },
  NLSLinkHubWhatsAppNotifyApprovals: {
    text: 'Client approvals',
    description: 'Notification checkbox for client approvals',
    surface: 'linkhub',
    maxChars: 30,
  },
  NLSLinkHubWhatsAppNotifyAdminSend: {
    text: 'Admin manual sends',
    description: 'Notification checkbox for admin manual sends',
    surface: 'linkhub',
    maxChars: 30,
  },
  NLSLinkHubWhatsAppNotifyBookings: {
    text: 'Booking & membership notices',
    description: 'Notification checkbox for booking and membership notices',
    surface: 'linkhub',
    maxChars: 40,
  },
  NLSLinkHubWhatsAppPushGrace: {
    text: 'Push grace (minutes)',
    description: 'Label for push grace minutes setting',
    surface: 'linkhub',
    maxChars: 30,
  },
  NLSLinkHubWhatsAppUsageThisMonth: {
    text: 'This month: {used} / {cap} messages',
    description: 'Usage summary showing sent messages vs monthly cap',
    surface: 'linkhub',
    maxChars: 50,
  },
  NLSLinkHubWhatsAppCreditsUsed: {
    text: 'Credits used: {count}',
    description: 'Credits used display',
    surface: 'linkhub',
    maxChars: 30,
  },
  NLSLinkHubWhatsAppStatusConnected: {
    text: 'Connected',
    description: 'Health status when WhatsApp is enabled',
    surface: 'linkhub',
    maxChars: 20,
  },
  NLSLinkHubWhatsAppStatusOff: {
    text: 'Off',
    description: 'Health status when WhatsApp is disabled',
    surface: 'linkhub',
    maxChars: 20,
  },
  NLSLinkHubWhatsAppSaveFailed: {
    text: 'Could not save WhatsApp settings. Try again.',
    description: 'Error toast when saving WhatsApp settings fails',
    surface: 'linkhub',
    maxChars: 60,
  },
  NLSLinkHubWhatsAppSave: {
    text: 'Save changes',
    description: 'Save WhatsApp settings button',
    surface: 'linkhub',
    maxChars: 25,
  },
  NLSLinkHubWhatsAppSaved: {
    text: 'WhatsApp settings saved',
    description: 'Success toast for WhatsApp settings',
    surface: 'linkhub',
    maxChars: 40,
  },
  NLSLinkHubWhatsAppEnable: {
    text: 'Enable WhatsApp',
    description: 'Toggle label to enable WhatsApp in workspace settings',
    surface: 'linkhub',
    maxChars: 30,
  },
  NLSLinkHubWhatsAppNumberChoice: {
    text: 'Number Choice',
    description: 'Section heading for WhatsApp number choice',
    surface: 'linkhub',
    maxChars: 30,
  },
  NLSLinkHubWhatsAppAudiences: {
    text: 'Audiences',
    description: 'Section heading for WhatsApp audiences',
    surface: 'linkhub',
    maxChars: 25,
  },
  NLSLinkHubWhatsAppUsageLoadFailed: {
    text: 'Could not load WhatsApp usage.',
    description: 'Error line when loading WhatsApp usage fails',
    surface: 'linkhub',
    maxChars: 40,
  },
  NLSChatActionRetry: {
    text: 'Retry',
    description: 'Button label to retry sending failed or paused chat message',
    surface: 'chat',
    maxChars: 15,
  },
  NLSChatActionAcknowledge: {
    text: 'Acknowledge',
    description: 'Chat operational proposal acknowledge action.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatActionArchive: {
    text: 'Archive',
    description: 'Chat archive channel action.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatActionAttachFiles: {
    text: 'Attach files',
    description: 'Chat composer attach files action.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatActionCancel: {
    text: 'Cancel',
    description: 'Chat dialog cancel action.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatActionCloseAiControls: {
    text: 'Close AI controls',
    description: 'Chat AI controls close accessible label.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatActionCloseChannels: {
    text: 'Close channels',
    description: 'Chat channel rail close accessible label.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionCloseEntityContext: {
    text: 'Close entity context',
    description: 'Chat entity sidecar close accessible label.',
    surface: 'chat',
    maxChars: 44,
  },
  NLSChatActionCloseMembers: {
    text: 'Close members',
    description: 'Chat members rail close accessible label.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionComment: {
    text: 'Comment',
    description: 'Helper chat quick action for comment.',
    surface: 'chat',
    maxChars: 22,
  },
  NLSChatActionCreateChannel: {
    text: 'Create channel',
    description: 'Chat channel create action.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionDelete: {
    text: 'Delete',
    description: 'Chat delete action.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatActionDeleteForever: {
    text: 'Delete forever',
    description: 'Chat permanent delete action.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionDictateMessage: {
    text: 'Dictate message',
    description: 'Chat composer dictation action.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionDismiss: {
    text: 'Dismiss',
    description: 'Chat dismiss action.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatActionDone: {
    text: 'Done',
    description: 'Helper chat quick action for done.',
    surface: 'chat',
    maxChars: 16,
  },
  NLSChatActionEdit: {
    text: 'Edit',
    description: 'Chat edit action.',
    surface: 'chat',
    maxChars: 16,
  },
  NLSChatActionEvidence: {
    text: '+ Evidence',
    description: 'Helper chat quick action for evidence.',
    surface: 'chat',
    maxChars: 24,
  },
  NLSChatActionExecute: {
    text: 'Execute',
    description: 'Chat command execute action.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatActionOpenFullDetail: {
    text: 'Open full detail',
    description: 'Chat entity sidecar open detail action.',
    surface: 'chat',
    maxChars: 36,
  },
  NLSChatActionRecordAudioMemo: {
    text: 'Record audio memo',
    description: 'Chat composer audio recording action.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatActionRemove: {
    text: 'remove',
    description: 'Chat attachment remove action.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatActionRestore: {
    text: 'Restore channel',
    description: 'Chat restore channel action title.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionSave: {
    text: 'Save',
    description: 'Chat save action.',
    surface: 'chat',
    maxChars: 16,
  },
  NLSChatActionSaveChanges: {
    text: 'Save changes',
    description: 'Chat channel save changes action.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionSaveEdit: {
    text: 'Save edit',
    description: 'Chat message edit save action.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatActionScan: {
    text: 'Scan',
    description: 'Helper chat quick action for scan.',
    surface: 'chat',
    maxChars: 16,
  },
  NLSChatActionSelectChatsForAi: {
    text: 'select chats for AI',
    description: 'Chat AI selection toggle label.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatActionSendMessage: {
    text: 'Send message',
    description: 'Chat composer send action.',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatActionSendSelectedChatsToAi: {
    text: 'Send to AI',
    description: 'Chat send selected messages to AI action.',
    surface: 'chat',
    maxChars: 26,
  },
  NLSChatActionShowChannels: {
    text: 'Show channels',
    description: 'Chat channel rail open accessible label.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionShowMembers: {
    text: 'Show members and details',
    description: 'Chat members/context rail open accessible label.',
    surface: 'chat',
    maxChars: 52,
  },
  NLSChatActionStopAndSendAudio: {
    text: 'Stop and send audio',
    description: 'Chat composer stop audio recording action.',
    surface: 'chat',
    maxChars: 44,
  },
  NLSChatActionStopDictation: {
    text: 'Stop dictation',
    description: 'Chat composer stop dictation action.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionTranslate: {
    text: 'Translate',
    description: 'Chat message translate action.',
    surface: 'chat',
    maxChars: 24,
  },
  NLSChatActionUndoDelete: {
    text: 'Undo delete',
    description: 'Chat message restore action.',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatAriaCommandAction: {
    text: '{type} operational action',
    description: 'Chat command bubble accessible label. {type} is a command type.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatAriaDismissQuotaWarning: {
    text: 'Dismiss quota warning',
    description: 'Chat quota warning dismiss accessible label.',
    surface: 'chat',
    maxChars: 44,
  },
  NLSChatAriaLineOperationalPlans: {
    text: 'Line operational plans',
    description: 'Chat proposal sidecar accessible label.',
    surface: 'chat',
    maxChars: 48,
  },
  NLSChatAriaSelectChatForAi: {
    text: 'Select chat for AI',
    description: 'Chat message selection accessible label.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatComposerArchivedPlaceholder: {
    text: 'Archived channel',
    description: 'Chat composer placeholder for archived channels.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatComposerLockedPlaceholder: {
    text: 'Waiting for one more person (member or helper)',
    description: 'Chat composer placeholder before chat unlocks.',
    surface: 'chat',
    maxChars: 90,
  },
  NLSChatComposerMessageAllPlaceholder: {
    text: 'Message all chats',
    description: 'Chat composer active placeholder.',
    surface: 'chat',
    maxChars: 44,
  },
  NLSChatContextTitle: {
    text: 'Context',
    description: 'Chat context sidebar title.',
    surface: 'chat',
    maxChars: 22,
  },
  NLSChatDialogApproveOperationalPlan: {
    text: 'Approve operational plan?',
    description: 'Chat operational proposal approval dialog title.',
    surface: 'chat',
    maxChars: 56,
  },
  NLSChatDialogArchiveChannel: {
    text: 'Archive channel?',
    description: 'Chat channel archive confirmation title.',
    surface: 'chat',
    maxChars: 38,
  },
  NLSChatDialogArchiveChannelMessage: {
    text: 'Move #{channel} to the chat archive? Admins can restore it from Archive.',
    description: 'Chat channel archive confirmation message. {channel} is a channel slug/name.',
    surface: 'chat',
    maxChars: 120,
  },
  NLSChatDialogDeleteForever: {
    text: 'Delete archived channel forever?',
    description: 'Chat archived channel permanent delete title.',
    surface: 'chat',
    maxChars: 64,
  },
  NLSChatDialogDeleteForeverMessage: {
    text: 'Permanently remove #{channel} from the archive. This cannot be undone from Chat.',
    description: 'Chat archived channel permanent delete message. {channel} is a channel slug/name.',
    surface: 'chat',
    maxChars: 130,
  },
  NLSChatDialogEditMessage: {
    text: 'Edit message',
    description: 'Chat edit message dialog title.',
    surface: 'chat',
    maxChars: 32,
  },
  NLSChatEmptyNoArchivedChannels: {
    text: 'No archived channels',
    description: 'Chat archive empty state.',
    surface: 'chat',
    maxChars: 42,
  },
  NLSChatEmptyNoChannelMatches: {
    text: 'No channel matches',
    description: 'Chat channel filter empty state.',
    surface: 'chat',
    maxChars: 42,
  },
  NLSChatEmptyNoChannels: {
    text: 'No channels yet',
    description: 'Chat channel list empty state.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatEmptyNoChannelsHelperHint: {
    text: 'Your admin will add you to channels.',
    description: 'Helper and member channel picker empty-state reassurance.',
    surface: 'chat',
    maxChars: 56,
  },
  NLSChatEmptyNoHashtags: {
    text: 'No hashtags yet',
    description: 'Chat tag list empty state.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatEmptyNoMessages: {
    text: 'No messages in this channel yet.',
    description: 'Chat empty channel message.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatEmptyNoMessagesHelper: {
    text: 'No messages are visible in this channel yet. Use the channel menu to switch conversations if your notification opened a different thread.',
    description: 'Helper chat empty channel message.',
    surface: 'chat',
    maxChars: 180,
  },
  NLSChatEmptyNoTagMatches: {
    text: 'No tag matches',
    description: 'Chat tag filter empty state.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatEntityChecklistStatusActions: {
    text: 'Checklist status actions',
    description: 'Chat entity sidecar checklist action group accessible label.',
    surface: 'chat',
    maxChars: 52,
  },
  NLSChatEntityContext: {
    text: 'Entity context',
    description: 'Chat sidecar entity context title.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatEntityMemberContext: {
    text: 'Member context',
    description: 'Chat sidecar member context title.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatEntityReadOnly: {
    text: 'Read-only for your current workspace role.',
    description: 'Chat entity sidecar read-only notice.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatEntityTimelineStatusActions: {
    text: 'Timeline status actions',
    description: 'Chat entity sidecar timeline action group accessible label.',
    surface: 'chat',
    maxChars: 52,
  },
  NLSChatEntityTitle: {
    text: 'Title',
    description: 'Chat entity sidecar title field label.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatEntityWorkspaceItem: {
    text: 'Workspace item',
    description: 'Chat entity sidecar fallback title.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatErrorArchivedChannelRestore: {
    text: 'Restore this channel before sending new messages.',
    description: 'Chat send error for archived channel.',
    surface: 'chat',
    maxChars: 90,
  },
  NLSChatErrorAttachmentLimit: {
    text: 'Attach up to 5 files per chat message.',
    description: 'Chat attachment count error.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatErrorAudioUnavailable: {
    text: 'Audio recording is not available in this browser.',
    description: 'Chat audio recording unsupported error.',
    surface: 'chat',
    maxChars: 90,
  },
  NLSChatErrorChannelRemoveFailed: {
    text: 'Channel could not be removed.',
    description: 'Chat channel archive failure fallback.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatErrorChannelRestoreFailed: {
    text: 'Channel could not be restored.',
    description: 'Chat channel restore failure fallback.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatErrorCreateChannelFailed: {
    text: 'Failed to create channel.',
    description: 'Chat create channel fallback error.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatErrorDailyQuotaReached: {
    text: 'Daily chat quota reached.',
    description: 'Chat quota exhausted message.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatErrorDeleteMessageFailed: {
    text: 'Message could not be deleted.',
    description: 'Chat message delete fallback error.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatErrorDictationUnavailable: {
    text: 'Dictation is not available in this browser.',
    description: 'Chat dictation unsupported error.',
    surface: 'chat',
    maxChars: 90,
  },
  NLSChatErrorEditMessageFailed: {
    text: 'Message could not be edited.',
    description: 'Chat message edit fallback error.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatErrorOneTagPerMessage: {
    text: 'Use only one #tag per message.',
    description: 'Chat hashtag validation error.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatErrorPermanentDeleteFailed: {
    text: 'Archived channel could not be permanently removed.',
    description: 'Chat permanent channel delete failure fallback.',
    surface: 'chat',
    maxChars: 90,
  },
  NLSChatErrorRestoreMessageFailed: {
    text: 'Message could not be restored.',
    description: 'Chat message restore fallback error.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatErrorRetryFailed: {
    text: 'Retry failed.',
    description: 'Chat failed-message retry fallback error.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatErrorSendFailed: {
    text: 'Failed to send message.',
    description: 'Chat send fallback error.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatErrorStartAudioFailed: {
    text: 'Could not start audio recording.',
    description: 'Chat audio recording start failure fallback.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatErrorTranslationFailed: {
    text: 'Translation failed.',
    description: 'Chat message translation fallback error.',
    surface: 'chat',
    maxChars: 50,
  },
  NLSChatErrorUpdateHuddleFailed: {
    text: 'Failed to update huddle.',
    description: 'Chat huddle update fallback error.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatFilterChannelsAria: {
    text: 'Filter channels',
    description: 'Chat channel filter accessible label.',
    surface: 'chat',
    maxChars: 36,
  },
  NLSChatFilterChannelsPlaceholder: {
    text: 'Filter channels',
    description: 'Chat channel filter placeholder.',
    surface: 'chat',
    maxChars: 36,
  },
  NLSChatFilterTagsAria: {
    text: 'Filter tags',
    description: 'Chat tag filter accessible label.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatFilterTagsPlaceholder: {
    text: 'Filter tags',
    description: 'Chat tag filter placeholder.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatHuddleJoin: {
    text: 'Join Huddle',
    description: 'Chat huddle join action.',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatHuddleLeave: {
    text: 'Leave Huddle',
    description: 'Chat huddle leave action.',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatHuddleParticipantsAria: {
    text: 'Huddle participants',
    description: 'Chat huddle participants accessible label.',
    surface: 'chat',
    maxChars: 44,
  },
  NLSChatLabelAccess: {
    text: 'Access',
    description: 'Chat channel access field label.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatLabelActive: {
    text: 'Active',
    description: 'Chat active channel list tab.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatLabelAi: {
    text: 'AI',
    description: 'Chat AI trigger short label.',
    surface: 'chat',
    maxChars: 10,
  },
  NLSChatLabelAiControls: {
    text: 'AI controls',
    description: 'Chat AI controls panel accessible label.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatLabelAllChats: {
    text: 'All chats',
    description: 'Chat all chats tag filter label.',
    surface: 'chat',
    maxChars: 24,
  },
  NLSChatLabelArchive: {
    text: 'Archive',
    description: 'Chat archive channel list tab.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatLabelArchivedChannels: {
    text: 'Archived channels',
    description: 'Chat archived channel rail heading.',
    surface: 'chat',
    maxChars: 42,
  },
  NLSChatLabelChannelListMode: {
    text: 'Channel list mode',
    description: 'Chat channel list tablist accessible label.',
    surface: 'chat',
    maxChars: 42,
  },
  NLSChatLabelChannels: {
    text: 'Channels',
    description: 'Chat active channel rail heading.',
    surface: 'chat',
    maxChars: 26,
  },
  NLSChatLabelChatsDiscussions: {
    text: 'Chats and discussions',
    description: 'Chat sidebar title.',
    surface: 'chat',
    maxChars: 46,
  },
  NLSChatLabelChecklist: {
    text: 'Checklist',
    description: 'Chat entity sidecar checklist fallback type label.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatLabelEdited: {
    text: 'Edited',
    description: 'Chat message edited label.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatLabelLineOperations: {
    text: 'Line operations',
    description: 'Chat proposal sidecar eyebrow label.',
    surface: 'chat',
    maxChars: 36,
  },
  NLSChatLabelMembers: {
    text: 'Members',
    description: 'Chat members action label.',
    surface: 'chat',
    maxChars: 22,
  },
  NLSChatLabelMembersCount: {
    text: 'Members - {count}',
    description: 'Chat members sidebar heading. {count} is the member count.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatLabelMemberCount: {
    text: '{count} member{plural}',
    description: 'Chat header member count label. {count} is a number, {plural} is empty or s.',
    surface: 'chat',
    maxChars: 32,
  },
  NLSChatLabelMessage: {
    text: 'Message',
    description: 'Chat message edit field label.',
    surface: 'chat',
    maxChars: 22,
  },
  NLSChatLabelOperationalAction: {
    text: 'Operational action',
    description: 'Chat command bubble title.',
    surface: 'chat',
    maxChars: 42,
  },
  NLSChatLabelOperationalPlan: {
    text: 'Operational plan',
    description: 'Chat operational proposal preview title.',
    surface: 'chat',
    maxChars: 42,
  },
  NLSChatLabelOperationalPlans: {
    text: 'Operational plans',
    description: 'Chat proposal sidecar heading.',
    surface: 'chat',
    maxChars: 42,
  },
  NLSChatLabelOwner: {
    text: 'Owner',
    description: 'Chat private-channel owner role label.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatLabelPending: {
    text: 'pending',
    description: 'Chat proposal pending status chip.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatLabelPrivacy: {
    text: 'Privacy',
    description: 'Chat channel privacy field label.',
    surface: 'chat',
    maxChars: 22,
  },
  NLSChatLabelPrivate: {
    text: 'Private',
    description: 'Chat private channel type label.',
    surface: 'chat',
    maxChars: 22,
  },
  NLSChatLabelPublic: {
    text: 'Public',
    description: 'Chat public channel type label.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatLabelPublicChannel: {
    text: 'Public channel',
    description: 'Chat public channel metadata label.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatLabelPrivateChannel: {
    text: 'Private channel',
    description: 'Chat private channel metadata label.',
    surface: 'chat',
    maxChars: 36,
  },
  NLSChatLabelReview: {
    text: 'review',
    description: 'Chat proposal review status chip.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatLabelRoleFieldPass: {
    text: 'Field Pass',
    description: 'Chat role display label for field pass users.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatLabelRoleMember: {
    text: 'Member',
    description: 'Chat role display label for members.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatLabelTags: {
    text: 'Tags',
    description: 'Chat tag rail heading.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatLabelTaskTag: {
    text: 'Task tag',
    description: 'Helper chat task tag picker label.',
    surface: 'chat',
    maxChars: 24,
  },
  NLSChatLabelUnread: {
    text: 'Unread',
    description: 'Chat unread indicator accessible label.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatLabelYou: {
    text: 'You',
    description: 'Chat current-user author label.',
    surface: 'chat',
    maxChars: 12,
  },
  NLSChatMessageDeleted: {
    text: 'This message was deleted.',
    description: 'Chat deleted message placeholder.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatNoticeArchived: {
    text: 'This channel is archived. Restore it from the archive before sending new messages.',
    description: 'Chat archived channel notice.',
    surface: 'chat',
    maxChars: 140,
  },
  NLSChatNoticeLocked: {
    text: 'Chat unlocks once at least two people (members or helpers) have joined.',
    description: 'Chat locked notice.',
    surface: 'chat',
    maxChars: 120,
  },
  NLSChatNoticeQuotaUsage: {
    text: "Workspace chat has used {percent}% of today's allocation ({used} / {limit} units).",
    description: 'Chat quota warning. {percent}, {used}, and {limit} are numbers.',
    surface: 'chat',
    maxChars: 140,
  },
  NLSChatPageLabel: {
    text: 'Chat Hub',
    description: 'Chat page accessible label.',
    surface: 'chat',
    maxChars: 44,
  },
  NLSChatProposalApprove: {
    text: 'Approve',
    description: 'Chat proposal approve action.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatProposalApprovedToast: {
    text: 'Operational plan approved through the gated mutation path.',
    description: 'Chat proposal approval success toast.',
    surface: 'chat',
    maxChars: 100,
  },
  NLSChatProposalApproving: {
    text: 'Approving...',
    description: 'Chat proposal approving state.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatProposalApprovalCredits: {
    text: '{type} · {credits} credits on approval',
    description: 'Chat proposal credit metadata. {type} is proposal type; {credits} is number.',
    surface: 'chat',
    maxChars: 90,
  },
  NLSChatProposalCouldNotApprove: {
    text: 'The proposal could not be approved.',
    description: 'Chat proposal approval failure fallback.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatProposalCouldNotUpdate: {
    text: 'The proposal could not be updated.',
    description: 'Chat proposal update failure fallback.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatProposalEmpty: {
    text: 'No pending Line operational plans.',
    description: 'Chat proposal sidecar empty state.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatProposalRejectedToast: {
    text: 'The pending proposal was rejected.',
    description: 'Chat proposal rejected toast.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatProposalAcknowledgedToast: {
    text: 'Your pending proposal was acknowledged.',
    description: 'Chat proposal acknowledged toast.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatProposalResolutionTree: {
    text: 'Resolution tree',
    description: 'Chat resolution tree proposal type label.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatProposalStatusDelta: {
    text: 'Status delta',
    description: 'Chat status-delta proposal type label.',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatProposalStatusDeltaSummary: {
    text: 'Move {kind} {id} to {status}.',
    description: 'Chat status-delta proposal summary. {kind}, {id}, and {status} are proposal data.',
    surface: 'chat',
    maxChars: 100,
  },
  NLSChatProposalTreeCreate: {
    text: 'Tree create',
    description: 'Chat tree-create proposal type label.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatProposalTreeSummary: {
    text: '{title}{taskText}.',
    description: 'Chat tree proposal summary. {title} is proposal data; {taskText} is empty or a localized task count suffix.',
    surface: 'chat',
    maxChars: 140,
  },
  NLSChatProposalTreeTaskSuffix: {
    text: ' with {count} task{plural}',
    description: 'Chat tree proposal task count suffix. {count} is a number; {plural} is s or empty.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatQuickForTask: {
    text: 'For {label}',
    description: 'Helper chat quick action selected task label. {label} is user content.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatQuickPickFocusedTask: {
    text: 'Pick a focused task first',
    description: 'Helper chat quick action empty task hint.',
    surface: 'chat',
    maxChars: 50,
  },
  NLSChatQuickPickOneTask: {
    text: 'Pick one task',
    description: 'Helper chat task picker empty option.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatSidecarProjectItem: {
    text: 'Project item',
    description: 'Chat legacy entity reference project item fallback.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatSidecarTimelineItem: {
    text: 'Timeline item',
    description: 'Chat legacy entity reference timeline item fallback.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatSidecarWorkspaceMember: {
    text: 'Workspace member',
    description: 'Chat legacy entity reference workspace member fallback.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatStatusComplete: {
    text: 'COMPLETE',
    description: 'Chat timeline status button label.',
    surface: 'chat',
    maxChars: 24,
  },
  NLSChatStatusCompleted: {
    text: 'COMPLETED',
    description: 'Chat checklist status button label.',
    surface: 'chat',
    maxChars: 24,
  },
  NLSChatStatusExecuted: {
    text: 'Executed',
    description: 'Chat command executed button label.',
    surface: 'chat',
    maxChars: 24,
  },
  NLSChatStatusExecuting: {
    text: 'Executing...',
    description: 'Chat command executing button label.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatStatusFailed: {
    text: 'Failed',
    description: 'Chat command failed button label.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatStatusInProgress: {
    text: 'IN PROGRESS',
    description: 'Chat timeline status button label.',
    surface: 'chat',
    maxChars: 28,
  },
  NLSChatStatusOngoing: {
    text: 'ONGOING',
    description: 'Chat checklist status button label.',
    surface: 'chat',
    maxChars: 22,
  },
  NLSChatStatusOnline: {
    text: 'Online',
    description: 'Chat connectivity online label.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatStatusOffline: {
    text: 'Offline',
    description: 'Chat connectivity offline label.',
    surface: 'chat',
    maxChars: 20,
  },
  NLSChatStatusPostponed: {
    text: 'POSTPONED',
    description: 'Chat checklist status button label.',
    surface: 'chat',
    maxChars: 26,
  },
  NLSChatStatusRejected: {
    text: 'Rejected',
    description: 'Chat command rejected button label.',
    surface: 'chat',
    maxChars: 24,
  },
  NLSChatStatusTodo: {
    text: 'TODO',
    description: 'Chat timeline status button label.',
    surface: 'chat',
    maxChars: 16,
  },
  NLSChatTitleArchiveChannel: {
    text: 'Archive channel',
    description: 'Chat archive channel action title.',
    surface: 'chat',
    maxChars: 38,
  },
  NLSChatTitleCreateChannel: {
    text: 'Create a channel',
    description: 'Chat create channel modal title.',
    surface: 'chat',
    maxChars: 42,
  },
  NLSChatTitleEditChannel: {
    text: 'Edit channel',
    description: 'Chat edit channel modal title.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatTitleSelectChannel: {
    text: 'Select a channel',
    description: 'Chat header no-channel title.',
    surface: 'chat',
    maxChars: 38,
  },
  NLSChatSubtitleCreateChannel: {
    text: 'Channels group conversation around a project, audience, or topic.',
    description: 'Chat create channel modal subtitle.',
    surface: 'chat',
    maxChars: 110,
  },
  NLSChatSubtitleEditChannel: {
    text: 'Tune the name, owners, and private member access.',
    description: 'Chat edit channel modal subtitle.',
    surface: 'chat',
    maxChars: 100,
  },
  NLSChatSubtitlePickChannel: {
    text: 'Pick a channel from the sidebar to start chatting.',
    description: 'Chat header no-channel subtitle.',
    surface: 'chat',
    maxChars: 90,
  },
  NLSChatTextAccessPublic: {
    text: 'This is a public workspace channel. Everyone with chat access can read and send here.',
    description: 'Chat public channel access description.',
    surface: 'chat',
    maxChars: 140,
  },
  NLSChatTextAttachmentUnavailable: {
    text: 'Attachment not available.',
    description: 'Chat unavailable attachment fallback.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatTextSharedInChat: {
    text: 'Shared in chat.',
    description: 'Chat attachment preview summary.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatTextPrivateHint: {
    text: 'Invite-only - pick members below.',
    description: 'Chat private channel type hint.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatTextPublicHint: {
    text: 'Anyone in the workspace can read and post.',
    description: 'Chat public channel type hint.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatTitlePrivacyPrivateDenied: {
    text: 'Only ADMIN or EDITOR can create private channels.',
    description: 'Chat private channel unavailable tooltip.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatTitlePrivacyPublicDenied: {
    text: 'Your role cannot create channels.',
    description: 'Chat public channel unavailable tooltip.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatActionAdd: {
    text: 'Add',
    description: 'Chat member manager add action short label.',
    surface: 'chat',
    maxChars: 16,
  },
  NLSChatActionAddMember: {
    text: 'Add member',
    description: 'Chat member manager add member accessible label.',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatActionClose: {
    text: 'Close',
    description: 'Chat modal close action.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatActionCreating: {
    text: 'Creating',
    description: 'Chat create channel submitting state.',
    surface: 'chat',
    maxChars: 24,
  },
  NLSChatActionLeaveChannel: {
    text: 'Leave channel',
    description: 'Chat member manager leave channel action.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionManageMembers: {
    text: 'Manage members',
    description: 'Chat private channel manage members action.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionRemoveMember: {
    text: 'Remove member',
    description: 'Chat member manager remove member accessible label.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatActionSaving: {
    text: 'Saving',
    description: 'Chat save submitting state.',
    surface: 'chat',
    maxChars: 22,
  },
  NLSChatActionTranslating: {
    text: 'Translating...',
    description: 'Chat message translating state.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatAriaArchiveChannel: {
    text: 'Archive {channel}',
    description: 'Chat channel archive accessible label. {channel} is a channel name.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatAriaDeleteChannelForever: {
    text: 'Delete {channel} forever',
    description: 'Chat channel permanent delete accessible label. {channel} is a channel name.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatAriaEditChannel: {
    text: 'Edit {channel}',
    description: 'Chat channel edit accessible label. {channel} is a channel name.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatAriaMentionMember: {
    text: 'Mention a member',
    description: 'Chat mention popover member list accessible label.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatAriaMentionProjectItem: {
    text: 'Mention a project item',
    description: 'Chat mention popover project item list accessible label.',
    surface: 'chat',
    maxChars: 50,
  },
  NLSChatAriaMoreParticipants: {
    text: '{count} more',
    description: 'Chat huddle participant overflow accessible label. {count} is a number.',
    surface: 'chat',
    maxChars: 26,
  },
  NLSChatAriaRestoreChannel: {
    text: 'Restore {channel}',
    description: 'Chat channel restore accessible label. {channel} is a channel name.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatEmptyNoChannelsCreateOne: {
    text: 'No channels yet. Create one to get started.',
    description: 'Chat legacy sidebar empty state with create prompt.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatEmptyNoMatchingResults: {
    text: 'No matching results',
    description: 'Chat mention popover empty state.',
    surface: 'chat',
    maxChars: 42,
  },
  NLSChatErrorActionExecutionFailed: {
    text: 'Operational action execution failed.',
    description: 'Chat command execution fallback error.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatErrorAddMemberFailed: {
    text: 'Failed to add member.',
    description: 'Chat member manager add member failure fallback.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatErrorLeaveChannelFailed: {
    text: 'Failed to leave channel.',
    description: 'Chat member manager leave channel failure fallback.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatErrorRemoveMemberFailed: {
    text: 'Failed to remove member.',
    description: 'Chat member manager remove member failure fallback.',
    surface: 'chat',
    maxChars: 70,
  },
  NLSChatFieldChannelName: {
    text: 'Name',
    description: 'Chat channel name field label.',
    surface: 'chat',
    maxChars: 18,
  },
  NLSChatLabelAddMembers: {
    text: 'Add members',
    description: 'Chat member manager add members section label.',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChatLabelAdmin: {
    text: 'admin',
    description: 'Chat private channel admin role marker.',
    surface: 'chat',
    maxChars: 16,
  },
  NLSChatLabelInitialMembers: {
    text: 'Initial members ({count})',
    description: 'Chat create channel selected initial members count. {count} is a number.',
    surface: 'chat',
    maxChars: 46,
  },
  NLSChatLabelInThisChannel: {
    text: 'In this channel ({count})',
    description: 'Chat member manager present members count. {count} is a number.',
    surface: 'chat',
    maxChars: 48,
  },
  NLSChatLabelMembersOwners: {
    text: 'Members and owners ({count})',
    description: 'Chat edit channel member-owner count. {count} is a number.',
    surface: 'chat',
    maxChars: 56,
  },
  NLSChatLabelProjectItems: {
    text: 'Project items',
    description: 'Chat mention popover project items heading.',
    surface: 'chat',
    maxChars: 34,
  },
  NLSChatPlaceholderDesignReviews: {
    text: 'e.g. design-reviews',
    description: 'Chat channel name placeholder example.',
    surface: 'chat',
    maxChars: 38,
  },
  NLSChatToastChannelCreated: {
    text: '#{channel} created',
    description: 'Chat channel created toast. {channel} is a channel slug.',
    surface: 'chat',
    maxChars: 50,
  },
  NLSChatToastChannelUpdated: {
    text: '#{channel} updated',
    description: 'Chat channel updated toast. {channel} is a channel slug.',
    surface: 'chat',
    maxChars: 50,
  },
  NLSChatToastMemberAdded: {
    text: 'Member added.',
    description: 'Chat member manager add success toast.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatToastMemberRemoved: {
    text: 'Member removed.',
    description: 'Chat member manager remove success toast.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatToastLeftChannel: {
    text: 'You left the channel.',
    description: 'Chat member manager leave channel success toast.',
    surface: 'chat',
    maxChars: 50,
  },
  NLSChatTypingOne: {
    text: '{name} is typing',
    description: 'Chat typing indicator for one person. {name} is a member display name.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatTypingTwo: {
    text: '{first} and {second} are typing',
    description: 'Chat typing indicator for two people. {first} and {second} are member display names.',
    surface: 'chat',
    maxChars: 90,
  },
  NLSChatTypingMany: {
    text: '{count} people are typing',
    description: 'Chat typing indicator for three or more people. {count} is a number.',
    surface: 'chat',
    maxChars: 46,
  },
  NLSChatValidationNameLetters: {
    text: 'Channel name must contain letters or numbers.',
    description: 'Chat channel name slug validation error.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatValidationNameRequired: {
    text: 'Channel name is required.',
    description: 'Chat channel name required error.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatValidationNameTooLong: {
    text: 'Channel name is too long (max 64 chars).',
    description: 'Chat channel name length validation error.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatValidationOnlyAdminEditorEdit: {
    text: 'Only admins and editors can edit channels.',
    description: 'Chat edit channel role validation error.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatValidationPrivateCreateDenied: {
    text: 'Your workspace role cannot create private channels.',
    description: 'Chat private channel creation denied error.',
    surface: 'chat',
    maxChars: 90,
  },
  NLSChatValidationPrivateNeedsOwner: {
    text: 'Private channels need at least one owner.',
    description: 'Chat private channel owner validation error.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSChatValidationPublicCreateDenied: {
    text: 'Your workspace role cannot create channels.',
    description: 'Chat public channel creation denied error.',
    surface: 'chat',
    maxChars: 80,
  },
  NLSUploadViewAllUploadsLabel: {
    text: 'All Uploads',
    description: 'Upload Hub left-rail label for the complete artifact ledger.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadViewAllUploadsDescription: {
    text: 'Workspace artifact ledger',
    description: 'Upload Hub left-rail description for all workspace uploads.',
    surface: 'upload',
    maxChars: 56,
  },
  NLSUploadViewAiMagicUploadsLabel: {
    text: 'AI Magic Uploads',
    description: 'Upload Hub left-rail label for files attached to AI Magic chats.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadViewAiMagicUploadsDescription: {
    text: 'Private files attached to AI Magic chats',
    description: 'Upload Hub left-rail description for AI Magic upload filtering.',
    surface: 'upload',
    maxChars: 68,
  },
  NLSUploadViewChatUploadsLabel: {
    text: 'Chat uploads',
    description: 'Upload Hub left-rail label for files shared in workspace chat.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadViewChatUploadsDescription: {
    text: 'Files shared in group chat',
    description: 'Upload Hub left-rail description for chat attachment filtering.',
    surface: 'upload',
    maxChars: 60,
  },
  NLSUploadViewChecklistUploadsLabel: {
    text: 'Checklists',
    description: 'Upload Hub left-rail label for checklist proofs and voice memos.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadViewChecklistUploadsDescription: {
    text: 'Proofs and voice memos from checklists',
    description: 'Upload Hub left-rail description for checklist evidence uploads.',
    surface: 'upload',
    maxChars: 72,
  },
  NLSUploadViewMeetingUploadsLabel: {
    text: 'Meeting Uploads',
    description: 'Upload Hub left-rail label for documents attached to meetings.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadViewMeetingUploadsDescription: {
    text: 'Documents attached to meetings',
    description: 'Upload Hub left-rail description for meeting document uploads.',
    surface: 'upload',
    maxChars: 64,
  },
  NLSUploadViewBatchQueuesLabel: {
    text: 'Batch Queues',
    description: 'Upload Hub left-rail label for active and pending batch uploads.',
    surface: 'upload',
    maxChars: 32,
  },
  NLSUploadViewBatchQueuesDescription: {
    text: 'Active and pending batches',
    description: 'Upload Hub left-rail description for batch upload queues.',
    surface: 'upload',
    maxChars: 58,
  },
  NLSUploadViewSyncSchedulerLabel: {
    text: 'Sync Scheduler',
    description: 'Upload Hub left-rail label for scheduled upload and provider sync runs.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadViewSyncSchedulerDescription: {
    text: 'Scheduled batch executions',
    description: 'Upload Hub left-rail description for scheduled upload executions.',
    surface: 'upload',
    maxChars: 58,
  },
  NLSUploadViewStorageAnalyticsLabel: {
    text: 'Storage Analytics',
    description: 'Upload Hub left-rail label for storage usage analytics.',
    surface: 'upload',
    maxChars: 36,
  },
  NLSUploadViewStorageAnalyticsDescription: {
    text: 'Tier usage and breakdown',
    description: 'Upload Hub left-rail description for storage usage analytics.',
    surface: 'upload',
    maxChars: 58,
  },
  NLSUploadNavLockedTitle: {
    text: 'Clear the artifact filter to browse this section.',
    description: 'Tooltip shown on disabled Upload Hub navigation while a deep-link artifact filter is active.',
    surface: 'upload',
    maxChars: 78,
  },
  NLSUploadLabelUnlimited: {
    text: 'Unlimited',
    description: 'Storage quota label when the workspace allowance has no hard limit.',
    surface: 'upload',
    maxChars: 22,
  },
  NLSUploadLabelPending: {
    text: 'Pending',
    description: 'Upload Hub fallback date/status label while a timestamp is unavailable.',
    surface: 'upload',
    maxChars: 22,
  },
  NLSUploadLabelPendingDate: {
    text: 'Pending date',
    description: 'Artifact Manager fallback timestamp label when a file has no created date.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadCountFileOne: {
    text: '{count} file',
    description: 'Singular file count. {count} is a number.',
    surface: 'upload',
    maxChars: 24,
  },
  NLSUploadCountFileMany: {
    text: '{count} files',
    description: 'Plural file count. {count} is a number.',
    surface: 'upload',
    maxChars: 24,
  },
  NLSUploadCountUploadOne: {
    text: '{count} upload',
    description: 'Singular upload count. {count} is a number.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadCountUploadMany: {
    text: '{count} uploads',
    description: 'Plural upload count. {count} is a number.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadCountArtifactOne: {
    text: '{count} artifact',
    description: 'Singular artifact count. {count} is a number.',
    surface: 'upload',
    maxChars: 30,
  },
  NLSUploadCountArtifactMany: {
    text: '{count} artifacts',
    description: 'Plural artifact count. {count} is a number.',
    surface: 'upload',
    maxChars: 30,
  },
  NLSUploadCountProviderOne: {
    text: '{count} provider',
    description: 'Singular connected provider count. {count} is a number.',
    surface: 'upload',
    maxChars: 30,
  },
  NLSUploadCountProviderMany: {
    text: '{count} providers',
    description: 'Plural connected provider count. {count} is a number.',
    surface: 'upload',
    maxChars: 30,
  },
  NLSUploadCountChecklistOne: {
    text: '{count} checklist',
    description: 'Singular checklist count. {count} is a number.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadCountChecklistMany: {
    text: '{count} checklists',
    description: 'Plural checklist count. {count} is a number.',
    surface: 'upload',
    maxChars: 32,
  },
  NLSUploadArtifactCategoryChatAttachment: {
    text: 'Chat attachment',
    description: 'Artifact category label for files shared from chat.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadArtifactCategoryAiMagicUpload: {
    text: 'AI Magic upload',
    description: 'Artifact category label for files uploaded through AI Magic.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadArtifactCategoryFormResponse: {
    text: 'Form response',
    description: 'Artifact category label for files attached to form responses.',
    surface: 'upload',
    maxChars: 32,
  },
  NLSUploadArtifactCategoryChecklistProof: {
    text: 'Checklist proof',
    description: 'Artifact category label for checklist proof files.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadArtifactCategoryVoiceMemo: {
    text: 'Voice memo',
    description: 'Artifact category label for checklist voice memo uploads.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadArtifactCategoryDesignAsset: {
    text: 'Design asset',
    description: 'Artifact category label for design assets.',
    surface: 'upload',
    maxChars: 30,
  },
  NLSUploadArtifactCategoryDriveReference: {
    text: 'Drive reference',
    description: 'Artifact category label for files referenced from Google Drive.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadArtifactCategoryMeetingDocument: {
    text: 'Meeting document',
    description: 'Artifact category label for meeting documents.',
    surface: 'upload',
    maxChars: 36,
  },
  NLSUploadActionBatchUpload: {
    text: 'Batch Upload',
    description: 'Primary Upload Hub action for opening the batch upload drawer.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadActionSyncing: {
    text: 'Syncing…',
    description: 'Busy action label while Upload Hub provider sync is running.',
    surface: 'upload',
    maxChars: 24,
  },
  NLSUploadActionSyncAll: {
    text: 'Sync All',
    description: 'Upload Hub action for syncing linked providers.',
    surface: 'upload',
    maxChars: 22,
  },
  NLSUploadActionManagePlan: {
    text: 'Manage plan',
    description: 'Upload Hub action for opening plan management.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadAriaWorkspaceUploadHub: {
    text: 'Workspace upload hub',
    description: 'Accessible label for the Upload Hub page shell.',
    surface: 'upload',
    maxChars: 42,
  },
  NLSUploadAriaSections: {
    text: 'Upload Hub sections',
    description: 'Accessible label for Upload Hub section navigation.',
    surface: 'upload',
    maxChars: 40,
  },
  NLSUploadAriaCloseSelectionDetails: {
    text: 'Close selection details',
    description: 'Accessible label for closing the mobile Upload Hub selection sidecar.',
    surface: 'upload',
    maxChars: 46,
  },
  NLSUploadAriaSelectionDetails: {
    text: 'Selection details',
    description: 'Accessible label for the Upload Hub selected-artifact sidecar.',
    surface: 'upload',
    maxChars: 38,
  },
  NLSUploadIdentityTitle: {
    text: 'Upload Hub',
    description: 'Upload Hub page identity title.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadIdentitySubtitle: {
    text: 'Artifact vault',
    description: 'Upload Hub page identity subtitle.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadChecklistTypeAll: {
    text: 'All',
    description: 'Checklist Uploads type filter label for all checklist types.',
    surface: 'upload',
    maxChars: 18,
  },
  NLSUploadChecklistTypeIndividual: {
    text: 'Individual',
    description: 'Checklist Uploads type filter label for individual checklist items.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadChecklistTypeDaily: {
    text: 'Daily',
    description: 'Checklist Uploads type filter label for daily recurring checklist items.',
    surface: 'upload',
    maxChars: 20,
  },
  NLSUploadChecklistTypeWeekly: {
    text: 'Weekly',
    description: 'Checklist Uploads type filter label for weekly recurring checklist items.',
    surface: 'upload',
    maxChars: 22,
  },
  NLSUploadChecklistRecurringIndividual: {
    text: 'Individual',
    description: 'Checklist upload card eyebrow for individual checklist items.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadChecklistRecurringDaily: {
    text: 'Daily recurring',
    description: 'Checklist upload card eyebrow for daily recurring checklist items.',
    surface: 'upload',
    maxChars: 34,
  },
  NLSUploadChecklistRecurringWeekly: {
    text: 'Weekly recurring',
    description: 'Checklist upload card eyebrow for weekly recurring checklist items.',
    surface: 'upload',
    maxChars: 36,
  },
  NLSUploadChecklistKindProof: {
    text: 'Proofs',
    description: 'Checklist Uploads filter label for proof files.',
    surface: 'upload',
    maxChars: 22,
  },
  NLSUploadChecklistKindVoiceMemo: {
    text: 'Voice memos',
    description: 'Checklist Uploads filter label for voice memo files.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSUploadChecklistKindProofShort: {
    text: 'proof',
    description: 'Compact thumbnail badge for checklist proof uploads.',
    surface: 'upload',
    maxChars: 14,
  },
  NLSUploadChecklistKindMemoShort: {
    text: 'memo',
    description: 'Compact thumbnail badge for checklist voice memo uploads.',
    surface: 'upload',
    maxChars: 14,
  },
  NLSLinkRequiredTitle: {
    text: 'Link required',
    description: 'Header for the integration link required tooltip.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSLinkRequiredAdminTitle: {
    text: 'Admin required',
    description: 'Header for the integration link required tooltip when workspace admin action is needed.',
    surface: 'upload',
    maxChars: 28,
  },
  NLSLinkRequiredMessage: {
    text: 'Connect {provider} in Link Hub before running this action.',
    description: 'Notice informing user to connect the provider in Link Hub.',
    surface: 'upload',
    maxChars: 140,
  },
  NLSLinkRequiredAdminMessage: {
    text: '{provider} is unavailable. A workspace Admin must connect it in Link Hub.',
    description: 'Notice informing user that a workspace admin must connect the provider in Link Hub.',
    surface: 'upload',
    maxChars: 140,
  },
  NLSLinkRequiredOpenAction: {
    text: 'Open Link Hub',
    description: 'Button action label to navigate to Link Hub.',
    surface: 'upload',
    maxChars: 32,
  },
  NLSFormsIdentityTitle: {
    text: 'Forms',
    description: 'Forms page identity title.',
    surface: 'forms',
    maxChars: 18,
  },
  NLSFormsIdentitySubtitle: {
    text: 'Intake engine',
    description: 'Forms page identity subtitle.',
    surface: 'forms',
    maxChars: 34,
  },
  NLSFormsAriaWorkspace: {
    text: 'Forms workspace',
    description: 'Accessible label for the Forms page shell.',
    surface: 'forms',
    maxChars: 42,
  },
  NLSFormsAriaNavigation: {
    text: 'Forms navigation',
    description: 'Accessible label for Forms section navigation.',
    surface: 'forms',
    maxChars: 42,
  },
  NLSFormsSectionHubLabel: { text: 'Form Hub', description: 'Forms navigation label for the main definitions hub.', surface: 'forms', maxChars: 28 },
  NLSFormsSectionHubTone: { text: 'Definition ledger', description: 'Forms navigation subtitle for saved form definitions.', surface: 'forms', maxChars: 42 },
  NLSFormsSectionTemplatesLabel: { text: 'Templates', description: 'Forms navigation label for templates.', surface: 'forms', maxChars: 24 },
  NLSFormsSectionTemplatesTone: { text: 'Reusable intake kits', description: 'Forms navigation subtitle for templates.', surface: 'forms', maxChars: 42 },
  NLSFormsSectionDraftsLabel: { text: 'Drafts', description: 'Forms navigation label for draft definitions.', surface: 'forms', maxChars: 22 },
  NLSFormsSectionDraftsTone: { text: 'Work in progress', description: 'Forms navigation subtitle for draft definitions.', surface: 'forms', maxChars: 40 },
  NLSFormsSectionArchiveLabel: { text: 'Archive', description: 'Forms navigation label for archived definitions.', surface: 'forms', maxChars: 24 },
  NLSFormsSectionArchiveTone: { text: 'Retained definitions', description: 'Forms navigation subtitle for archived definitions.', surface: 'forms', maxChars: 42 },
  NLSFormsSectionAssignLabel: { text: 'Assign Forms', description: 'Forms navigation label for assignment workflow.', surface: 'forms', maxChars: 30 },
  NLSFormsSectionAssignTone: { text: 'Request engine', description: 'Forms navigation subtitle for assignment workflow.', surface: 'forms', maxChars: 36 },
  NLSFormsSectionAssignedLabel: { text: 'Assigned to You', description: 'Forms navigation label for responder queue.', surface: 'forms', maxChars: 34 },
  NLSFormsSectionAssignedTone: { text: 'Responder queue', description: 'Forms navigation subtitle for assigned forms.', surface: 'forms', maxChars: 38 },
  NLSFormsSectionResponsesLabel: { text: 'Form Responses', description: 'Forms navigation label for response ledger.', surface: 'forms', maxChars: 34 },
  NLSFormsSectionResponsesTone: { text: 'Submission ledger', description: 'Forms navigation subtitle for responses.', surface: 'forms', maxChars: 40 },
  NLSFormsSectionSettingsLabel: { text: 'Settings', description: 'Forms navigation label for module settings.', surface: 'forms', maxChars: 24 },
  NLSFormsSectionSettingsTone: { text: 'Module policy', description: 'Forms navigation subtitle for module settings.', surface: 'forms', maxChars: 34 },
  NLSFormsActionCreateNewForm: { text: 'Create New Form', description: 'Primary Forms action for creating a form definition.', surface: 'forms', maxChars: 38 },
  NLSFormsActionExplore: { text: 'Explore', description: 'Forms action for opening Explore templates.', surface: 'forms', maxChars: 20 },
  NLSFormsActionBackToForms: { text: 'Back to forms', description: 'Returns from a form sub-screen to the Forms manager.', surface: 'forms', maxChars: 34 },
  NLSFormsActionRequestMembers: { text: 'Request members', description: 'Forms action for requesting member responses.', surface: 'forms', maxChars: 34 },
  NLSFormsActionOpenLiveForm: { text: 'Open live form', description: 'Opens a form in responder mode.', surface: 'forms', maxChars: 34 },
  NLSFormsActionSubmitAnother: { text: 'Submit another response', description: 'Resets a completed form response for another submission.', surface: 'forms', maxChars: 46 },
  NLSFormsActionSubmitResponse: { text: 'Submit response', description: 'Submits a form response.', surface: 'forms', maxChars: 34 },
  NLSFormsActionSubmitting: { text: 'Submitting...', description: 'Busy label while a form response is submitting.', surface: 'forms', maxChars: 28 },
  NLSFormsActionSaveForm: { text: 'Save form', description: 'Saves the current form builder draft.', surface: 'forms', maxChars: 24 },
  NLSFormsActionSaving: { text: 'Saving...', description: 'Busy label while a form draft is saving.', surface: 'forms', maxChars: 24 },
  NLSFormsActionUseTemplate: { text: 'Use template', description: 'Uses a Forms template.', surface: 'forms', maxChars: 28 },
  NLSFormsActionOpenTemplateFactory: { text: 'Open Template Factory', description: 'Opens the broader template factory from Forms.', surface: 'forms', maxChars: 44 },
  NLSFormsActionAssignForm: { text: 'Assign form', description: 'Assigns a selected form to direct recipients.', surface: 'forms', maxChars: 30 },
  NLSFormsActionAssigning: { text: 'Assigning...', description: 'Busy label while assigning a form.', surface: 'forms', maxChars: 28 },
  NLSFormsActionTagAndAssign: { text: 'Tag and assign form', description: 'Tags a workspace item with a form and assigns responders.', surface: 'forms', maxChars: 42 },
  NLSFormsActionSendRequest: { text: 'Send request', description: 'Sends one form request from the request drawer.', surface: 'forms', maxChars: 30 },
  NLSFormsActionSendRequests: { text: 'Send requests', description: 'Sends multiple form requests from the request drawer.', surface: 'forms', maxChars: 32 },
  NLSFormsActionSending: { text: 'Sending...', description: 'Busy label while sending form requests.', surface: 'forms', maxChars: 26 },
  NLSFormsActionExportLatestResponse: { text: 'Export latest response', description: 'Exports the latest form response to a provider.', surface: 'forms', maxChars: 46 },
  NLSFormsActionImportDefinition: { text: 'Import definition', description: 'Imports a form definition from a provider.', surface: 'forms', maxChars: 38 },
  NLSFormsActionConnectToEnable: { text: 'Connect to enable', description: 'Forms provider action or disabled reason when a connection is missing.', surface: 'forms', maxChars: 40 },
  NLSFormsActionRemoveMember: { text: 'Remove', description: 'Removes a selected member from person field selection.', surface: 'forms', maxChars: 20 },
  NLSFormsActionGenerateInsight: { text: 'Generate insight', description: 'Disabled AI insight action in Forms sidecar.', surface: 'forms', maxChars: 36 },
  NLSFormsDialogDirtyTitle: { text: 'Unsaved form changes', description: 'Dirty-navigation dialog title for the Forms builder.', surface: 'forms', maxChars: 48 },
  NLSFormsDialogDirtyMessage: { text: 'Save this draft before leaving the builder?', description: 'Dirty-navigation dialog message for the Forms builder.', surface: 'forms', maxChars: 90 },
  NLSFormsDialogDeleteTitle: { text: 'Delete archived definition?', description: 'Dialog title for deleting an archived form definition.', surface: 'forms', maxChars: 54 },
  NLSFormsDialogDeleteMessage: {
    text: 'The form definition will be permanently removed. Historical response records remain preserved for governance.',
    description: 'Dialog message for deleting an archived form definition.',
    surface: 'forms',
  },
  NLSFormsStatusDraft: { text: 'Draft', description: 'Form lifecycle status label for drafts.', surface: 'forms', maxChars: 22 },
  NLSFormsStatusActive: { text: 'Active', description: 'Form lifecycle status label for active definitions.', surface: 'forms', maxChars: 22 },
  NLSFormsStatusArchived: { text: 'Archived', description: 'Form lifecycle status label for archived definitions.', surface: 'forms', maxChars: 26 },
  NLSFormsStatusPending: { text: 'Pending', description: 'Form request or response status label for pending records.', surface: 'forms', maxChars: 24 },
  NLSFormsStatusCompleted: { text: 'Completed', description: 'Form request status label for completed requests.', surface: 'forms', maxChars: 28 },
  NLSFormsStatusCancelled: { text: 'Cancelled', description: 'Form request status label for cancelled requests.', surface: 'forms', maxChars: 28 },
  NLSFormsStatusExpired: { text: 'Expired', description: 'Form request status label for expired requests.', surface: 'forms', maxChars: 24 },
  NLSFormsStatusReviewed: { text: 'Reviewed', description: 'Form response status label for reviewed responses.', surface: 'forms', maxChars: 26 },
  NLSFormsStatusExported: { text: 'Exported', description: 'Form response status label for exported responses.', surface: 'forms', maxChars: 26 },
  NLSFormsStatusRejected: { text: 'Rejected', description: 'Form response status label for rejected responses.', surface: 'forms', maxChars: 26 },
  NLSFormsLabelAll: { text: 'All', description: 'Compact all-option label for Forms filters.', surface: 'forms', maxChars: 16 },
  NLSFormsLabelAllStatuses: { text: 'All statuses', description: 'Forms lifecycle filter option for all statuses.', surface: 'forms', maxChars: 34 },
  NLSFormsLabelStatus: { text: 'Status', description: 'Forms status label on inline status selects.', surface: 'forms', maxChars: 20 },
  NLSFormsLabelAllItems: { text: 'All items', description: 'Forms response item filter option for all source items.', surface: 'forms', maxChars: 28 },
  NLSFormsLabelFields: { text: 'fields', description: 'Forms field count suffix.', surface: 'forms', maxChars: 18 },
  NLSFormsLabelResponsesOne: { text: '{count} response', description: 'Singular Forms response count. {count} is a number.', surface: 'forms', maxChars: 34 },
  NLSFormsLabelResponsesMany: { text: '{count} responses', description: 'Plural Forms response count. {count} is a number.', surface: 'forms', maxChars: 36 },
  NLSFormsLabelRequired: { text: 'Required', description: 'Required field badge in Forms.', surface: 'forms', maxChars: 26 },
  NLSFormsLabelSelectOption: { text: 'Select an option', description: 'Default dropdown placeholder in live forms.', surface: 'forms', maxChars: 36 },
  NLSFormsLabelNoResponse: { text: 'No response', description: 'Empty response value label in response review.', surface: 'forms', maxChars: 28 },
  NLSFormsLabelYes: { text: 'Yes', description: 'Boolean yes response label.', surface: 'forms', maxChars: 14 },
  NLSFormsLabelNo: { text: 'No', description: 'Boolean no response label.', surface: 'forms', maxChars: 14 },
  NLSFormsLabelNoFilesUploaded: { text: 'No files uploaded', description: 'Empty file-upload response label.', surface: 'forms', maxChars: 38 },
  NLSFormsFileChooseUpToOne: { text: 'Choose up to {count} file', description: 'File upload chooser label for a singular max file count. {count} is a number.', surface: 'forms', maxChars: 48 },
  NLSFormsFileChooseUpToMany: { text: 'Choose up to {count} files', description: 'File upload chooser label for a plural max file count. {count} is a number.', surface: 'forms', maxChars: 50 },
  NLSFormsLabelAttachmentUnavailable: { text: 'Attachment not available.', description: 'Unavailable form response attachment fallback.', surface: 'forms', maxChars: 60 },
  NLSFormsLabelPendingTimestamp: { text: 'Pending timestamp', description: 'Fallback when a response submitted timestamp has not resolved.', surface: 'forms', maxChars: 42 },
  NLSFormsModePreview: { text: 'Preview', description: 'Forms experience tab label for preview mode.', surface: 'forms', maxChars: 24 },
  NLSFormsModeFill: { text: 'Fill', description: 'Forms experience tab label for response mode.', surface: 'forms', maxChars: 18 },
  NLSFormsModeResponses: { text: 'Responses', description: 'Forms experience tab label for response review mode.', surface: 'forms', maxChars: 28 },
  NLSFormsFieldTypeText: { text: 'Text', description: 'Forms field type label for text fields.', surface: 'forms', maxChars: 18 },
  NLSFormsFieldTypeParagraph: { text: 'Paragraph', description: 'Forms field type label for multiline text fields.', surface: 'forms', maxChars: 28 },
  NLSFormsFieldTypeNumber: { text: 'Number', description: 'Forms field type label for number fields.', surface: 'forms', maxChars: 22 },
  NLSFormsFieldTypeDate: { text: 'Date', description: 'Forms field type label for date fields.', surface: 'forms', maxChars: 18 },
  NLSFormsFieldTypeDropdown: { text: 'Dropdown', description: 'Forms field type label for dropdown fields.', surface: 'forms', maxChars: 26 },
  NLSFormsFieldTypeCheckbox: { text: 'Checkbox', description: 'Forms field type label for checkbox fields.', surface: 'forms', maxChars: 26 },
  NLSFormsFieldTypeFileUpload: { text: 'File upload', description: 'Forms field type label for file upload fields.', surface: 'forms', maxChars: 30 },
  NLSFormsFieldTypePerson: { text: 'Person', description: 'Forms field type label for person fields.', surface: 'forms', maxChars: 20 },
  NLSFormsFormerMember: { text: 'Former member', description: 'Label when a member who submitted or was chosen in a form is no longer in the workspace.', surface: 'forms', maxChars: 30 },
  NLSFormsSelectMember: { text: 'Select member', description: 'Forms button label to pick a workspace member.', surface: 'forms', maxChars: 24 },
  NLSFormsAddMember: { text: 'Add member', description: 'Forms button label to pick an additional workspace member.', surface: 'forms', maxChars: 24 },
  NLSFormsPersonEmptyDirectory: { text: 'No workspace members available to select.', description: 'Notice shown when a person field cannot list any members because the directory is empty.', surface: 'forms', maxChars: 60 },
  NLSFormsPersonDirectoryError: { text: 'Unable to load workspace members.', description: 'Error message shown when the member directory subscription fails.', surface: 'forms', maxChars: 50 },
  NLSFormsBuilderMode: { text: 'Builder mode', description: 'Forms builder sidecar placeholder label.', surface: 'forms', maxChars: 32 },
  NLSFormsLoadingForm: { text: 'Loading form...', description: 'Loader label while a form definition loads.', surface: 'forms', maxChars: 34 },
  NLSFormsLoadingBuilder: { text: 'Loading builder...', description: 'Loader label while the form builder loads.', surface: 'forms', maxChars: 38 },
  NLSFormsLoadingForms: { text: 'Loading forms...', description: 'Loader label while form definitions load.', surface: 'forms', maxChars: 34 },
  NLSFormsLoadingResponses: { text: 'Loading responses...', description: 'Loader label while form responses load.', surface: 'forms', maxChars: 42 },
  NLSFormsEmptyFormUnavailableTitle: { text: 'Form unavailable', description: 'Error title when a form cannot be loaded.', surface: 'forms', maxChars: 42 },
  NLSFormsEmptyFormUnavailableBody: { text: 'The definition could not be loaded.', description: 'Error body when a form cannot be loaded.', surface: 'forms', maxChars: 70 },
  NLSFormsEmptyNoResponsesYetTitle: { text: 'No responses yet', description: 'Empty response review title.', surface: 'forms', maxChars: 38 },
  NLSFormsEmptyNoResponsesYetBody: { text: 'Submitted records will appear here for review.', description: 'Empty response review body.', surface: 'forms', maxChars: 90 },
  NLSFormsToastSaved: { text: 'Form saved to the workspace.', description: 'Toast after saving a form definition.', surface: 'forms' },
  NLSFormsToastArchived: { text: 'Form archived. Responses remain available.', description: 'Toast after archiving a form definition.', surface: 'forms' },
  NLSFormsToastDeleted: { text: 'Archived definition deleted. Historical responses were retained.', description: 'Toast after deleting an archived form definition.', surface: 'forms' },
  NLSFormsSubmissionsTrendAria: { text: 'Responses over the last 14 days', description: 'Accessible label for the 14-day form submissions trend chart.', surface: 'forms', maxChars: 60 },
  NLSFormsPersonPickScopeLabel: { text: 'Who can be picked', description: 'Label for who can be picked in a Person form field.', surface: 'forms', maxChars: 30 },
  NLSFormsPersonPickScopeMembers: { text: 'Members only', description: 'Option to restrict Person field picking to members only.', surface: 'forms', maxChars: 30 },
  NLSFormsPersonPickScopeMembersAndHelpers: { text: 'Members and Helpers', description: 'Option to allow Person field picking from both members and helpers.', surface: 'forms', maxChars: 30 },
  NLSFormsActionMarkReviewed: { text: 'Mark reviewed', description: 'Action button to mark a form response as reviewed.', surface: 'forms', maxChars: 24 },
  NLSFormsActionMarkExported: { text: 'Mark exported', description: 'Action button to mark a form response as exported.', surface: 'forms', maxChars: 24 },
  NLSMeetingsIdentityTitle: { text: 'Meetings', description: 'Meetings page identity title.', surface: 'meetings', maxChars: 22 },
  NLSMeetingsIdentitySubtitle: { text: 'Live coordination', description: 'Meetings page identity subtitle.', surface: 'meetings', maxChars: 36 },
  NLSMeetingsAriaWorkspace: { text: 'Meetings workspace', description: 'Accessible label for the Meetings page shell.', surface: 'meetings', maxChars: 46 },
  NLSMeetingsAriaScheduler: { text: 'Meetings scheduler', description: 'Accessible label for mobile Meetings scheduler.', surface: 'meetings', maxChars: 46 },
  NLSMeetingsAriaMode: { text: 'Meeting mode', description: 'Accessible label for Meeting section tabs.', surface: 'meetings', maxChars: 34 },
  NLSMeetingsAriaActions: { text: 'Meeting actions', description: 'Accessible label for meeting action button group.', surface: 'meetings', maxChars: 38 },
  NLSMeetingsSectionScheduleLabel: { text: 'Schedule', description: 'Meetings section label for the schedule view.', surface: 'meetings', maxChars: 24 },
  NLSMeetingsSectionScheduleTone: { text: 'Calendar planner', description: 'Meetings section subtitle for the schedule view.', surface: 'meetings', maxChars: 38 },
  NLSMeetingsSectionHuddleLabel: { text: 'Huddle', description: 'Meetings section label for the huddle view.', surface: 'meetings', maxChars: 22 },
  NLSMeetingsSectionHuddleTone: { text: 'Voice presence', description: 'Meetings section subtitle for the huddle view.', surface: 'meetings', maxChars: 34 },
  NLSMeetingsSectionMeetingLabel: { text: 'Meetings', description: 'Meetings section label for room controls.', surface: 'meetings', maxChars: 24 },
  NLSMeetingsSectionMeetingRoomLabel: { text: 'Meeting Room', description: 'Meetings rail label for room controls.', surface: 'meetings', maxChars: 30 },
  NLSMeetingsSectionMeetingTone: { text: 'Live join controls', description: 'Meetings section subtitle for room controls.', surface: 'meetings', maxChars: 40 },
  NLSMeetingsViewDay: { text: 'Day', description: 'Meetings range view label for one day.', surface: 'meetings', maxChars: 16 },
  NLSMeetingsViewWeek: { text: 'Week', description: 'Meetings range view label for one week.', surface: 'meetings', maxChars: 18 },
  NLSMeetingsViewMonth: { text: 'Month', description: 'Meetings range view label for one month.', surface: 'meetings', maxChars: 20 },
  NLSMeetingsActionScheduleMeeting: { text: 'Schedule Meeting', description: 'Primary action for scheduling a meeting.', surface: 'meetings', maxChars: 38 },
  NLSMeetingsActionStartHuddle: { text: 'Start Huddle', description: 'Primary action for starting a huddle meeting.', surface: 'meetings', maxChars: 34 },
  NLSMeetingsActionJoinRoom: { text: 'Join Room', description: 'Primary action for joining a meeting room.', surface: 'meetings', maxChars: 28 },
  NLSMeetingsActionNewMeeting: { text: 'New meeting', description: 'Action for creating a new meeting.', surface: 'meetings', maxChars: 30 },
  NLSMeetingsActionJoin: { text: 'Join', description: 'Compact action for joining a meeting.', surface: 'meetings', maxChars: 16 },
  NLSMeetingsActionJoinMeeting: { text: 'Join meeting', description: 'Action for joining a provider meeting.', surface: 'meetings', maxChars: 30 },
  NLSMeetingsActionJoinHuddle: { text: 'Join huddle', description: 'Action for joining a huddle.', surface: 'meetings', maxChars: 30 },
  NLSMeetingsActionJoinHuddleMeeting: { text: 'Join huddle meeting', description: 'Action for joining a huddle meeting.', surface: 'meetings', maxChars: 42 },
  NLSMeetingsActionShowHuddles: { text: 'Show huddles', description: 'Calendar control that shows historical huddle sessions.', surface: 'meetings', maxChars: 28 },
  NLSMeetingsActionHideHuddles: { text: 'Hide huddles', description: 'Calendar control that hides historical huddle sessions.', surface: 'meetings', maxChars: 28 },
  NLSMeetingsActionOpening: { text: 'Opening', description: 'Busy label while a meeting join is opening.', surface: 'meetings', maxChars: 22 },
  NLSMeetingsActionChat: { text: 'Chat', description: 'Action for opening meeting chat.', surface: 'meetings', maxChars: 18 },
  NLSMeetingsActionDocs: { text: 'Docs', description: 'Action for opening meeting documents.', surface: 'meetings', maxChars: 18 },
  NLSMeetingsActionTranscribe: { text: 'Transcribe', description: 'Action for requesting meeting transcription.', surface: 'meetings', maxChars: 28 },
  NLSMeetingsActionCancel: { text: 'Cancel', description: 'Action for cancelling a meeting.', surface: 'meetings', maxChars: 18 },
  NLSMeetingsActionSaveMeeting: { text: 'Save meeting', description: 'Action for saving a new meeting.', surface: 'meetings', maxChars: 32 },
  NLSMeetingsActionSaveSendUpdates: { text: 'Save and send updates', description: 'Dirty dock title for saving meeting changes.', surface: 'meetings', maxChars: 48 },
  NLSMeetingsActionUpdateNew: { text: 'Update New', description: 'Meeting save action for notifying only newly added participants.', surface: 'meetings', maxChars: 30 },
  NLSMeetingsActionUpdateAll: { text: 'Update All', description: 'Meeting save action for notifying all participants.', surface: 'meetings', maxChars: 28 },
  NLSMeetingsActionDiscard: { text: 'Discard', description: 'Discards unsaved meeting changes.', surface: 'meetings', maxChars: 22 },
  NLSMeetingsActionPeople: { text: 'People', description: 'Mobile Meetings action for opening the roster.', surface: 'meetings', maxChars: 20 },
  NLSMeetingsActionSchedule: { text: 'Schedule', description: 'Mobile Meetings action for opening the scheduler.', surface: 'meetings', maxChars: 24 },
  NLSMeetingsActionDetails: { text: 'Details', description: 'Mobile Meetings action for opening details.', surface: 'meetings', maxChars: 22 },
  NLSMeetingsActionToday: { text: 'Today', description: 'Calendar action to jump to today.', surface: 'meetings', maxChars: 18 },
  NLSMeetingsLabelMembers: { text: 'Members', description: 'Meetings roster heading.', surface: 'meetings', maxChars: 24 },
  NLSMeetingsLabelPeople: { text: 'People', description: 'Meetings rail people section label.', surface: 'meetings', maxChars: 20 },
  NLSMeetingsLabelChannels: { text: 'Channels', description: 'Meetings channel list heading.', surface: 'meetings', maxChars: 24 },
  NLSMeetingsLabelMeetingHub: { text: 'Meeting Hub', description: 'Desktop Meetings middle header title.', surface: 'meetings', maxChars: 30 },
  NLSMeetingsLabelMeetingDetails: { text: 'Meeting details', description: 'Meeting details sidecar heading.', surface: 'meetings', maxChars: 34 },
  NLSMeetingsLabelWorkspaceStats: { text: 'Workspace meeting stats', description: 'Accessible label for Meetings stats.', surface: 'meetings', maxChars: 50 },
  NLSMeetingsLabelTotal: { text: 'Total', description: 'Meeting stats total label.', surface: 'meetings', maxChars: 18 },
  NLSMeetingsLabelUpcoming: { text: 'Upcoming', description: 'Meeting stats upcoming label.', surface: 'meetings', maxChars: 26 },
  NLSMeetingsLabelCalendar: { text: 'Calendar', description: 'Meeting stats calendar label.', surface: 'meetings', maxChars: 24 },
  NLSMeetingsLabelHuddle: { text: 'Huddle', description: 'Meeting stats huddle label.', surface: 'meetings', maxChars: 22 },
  NLSMeetingsLabelMeetingOne: { text: 'meeting', description: 'Singular meeting count suffix.', surface: 'meetings', maxChars: 20 },
  NLSMeetingsLabelMeetingMany: { text: 'meetings', description: 'Plural meeting count suffix.', surface: 'meetings', maxChars: 22 },
  NLSMeetingsLabelFromToday: { text: 'from today', description: 'Meeting stats upcoming suffix.', surface: 'meetings', maxChars: 28 },
  NLSMeetingsLabelBusySlots: { text: 'busy slots', description: 'Meeting stats busy slots suffix.', surface: 'meetings', maxChars: 28 },
  NLSMeetingsLabelListening: { text: 'listening', description: 'Huddle listening state/count label.', surface: 'meetings', maxChars: 26 },
  NLSMeetingsLabelSpeaking: { text: 'speaking', description: 'Huddle speaking state/count label.', surface: 'meetings', maxChars: 24 },
  NLSMeetingsLabelPresent: { text: 'present', description: 'Huddle present state label.', surface: 'meetings', maxChars: 22 },
  NLSMeetingsLabelReady: { text: 'Ready', description: 'Meeting readiness gauge label.', surface: 'meetings', maxChars: 18 },
  NLSMeetingsLabelParticipants: { text: 'Participants', description: 'Meeting participants section heading.', surface: 'meetings', maxChars: 34 },
  NLSMeetingsRsvpPending: { text: 'Pending', description: 'Meeting participant RSVP status pending.', surface: 'meetings', maxChars: 16 },
  NLSMeetingsRsvpAccepted: { text: 'Accepted', description: 'Meeting participant RSVP status accepted.', surface: 'meetings', maxChars: 16 },
  NLSMeetingsRsvpTentative: { text: 'Tentative', description: 'Meeting participant RSVP status tentative.', surface: 'meetings', maxChars: 16 },
  NLSMeetingsRsvpDeclined: { text: 'Declined', description: 'Meeting participant RSVP status declined.', surface: 'meetings', maxChars: 16 },
  NLSMeetingsLabelLinkedChannels: { text: 'Linked channels', description: 'Meeting linked-channels section heading.', surface: 'meetings', maxChars: 38 },
  NLSMeetingsLabelSharedAgenda: { text: 'Shared agenda', description: 'Meeting shared agenda section heading.', surface: 'meetings', maxChars: 34 },
  NLSMeetingsEmptyTimelineTitle: { text: 'Nothing scheduled here yet', description: 'Empty timeline board title.', surface: 'meetings', maxChars: 54 },
  NLSMeetingsEmptyTimelineCanSchedule: { text: 'Drag on the calendar or tap Schedule to add a meeting.', description: 'Empty timeline body for users who can schedule meetings.', surface: 'meetings' },
  NLSMeetingsEmptyTimelineReadonly: { text: 'No meetings in this range.', description: 'Empty timeline body for users without schedule permission.', surface: 'meetings', maxChars: 60 },
  NLSMeetingsEmptyNoMeetingSelectedTitle: { text: 'No meeting selected', description: 'Empty meeting sidecar title.', surface: 'meetings', maxChars: 42 },
  NLSMeetingsEmptyNoMeetingSelectedBody: { text: 'Pick a meeting from the calendar to see its details, agenda and join controls.', description: 'Empty meeting sidecar body.', surface: 'meetings' },
  NLSMeetingsEmptyNoMembersMatch: { text: 'No members match the current filters.', description: 'Empty meeting roster search result.', surface: 'meetings', maxChars: 74 },
  NLSMeetingsEmptyNoChannels: { text: 'No channels yet.', description: 'Empty meeting channel list label.', surface: 'meetings', maxChars: 34 },
  NLSMeetingsEmptyNoHuddle: { text: 'No one is in this huddle yet.', description: 'Empty huddle roster label.', surface: 'meetings', maxChars: 62 },
  NLSMeetingsEmptyStreamEyebrow: { text: 'Meeting stream', description: 'Empty legacy meeting stream eyebrow.', surface: 'meetings', maxChars: 34 },
  NLSMeetingsEmptyStreamTitle: { text: 'No meetings scheduled', description: 'Empty legacy meeting stream title.', surface: 'meetings', maxChars: 46 },
  NLSMeetingsEmptyStreamBody: { text: 'Use Magic Concierge to find an open slot and create the first command session.', description: 'Empty legacy meeting stream body.', surface: 'meetings' },
  NLSMeetingsReadinessChecking: { text: 'Checking Calendar sync', description: 'Meeting readiness banner title while checking calendar sync.', surface: 'meetings', maxChars: 52 },
  NLSMeetingsReadinessOptional: { text: 'Calendar sync optional', description: 'Meeting readiness banner title when calendar sync is disconnected.', surface: 'meetings', maxChars: 52 },
  NLSMeetingsReadinessBody: {
    text: 'Meetings work locally without Calendar. Connect Google Calendar in Link Hub when you want external calendar sync.',
    description: 'Meeting readiness banner body.',
    surface: 'meetings',
  },
  NLSMeetingsReadinessOpenLinkHub: { text: 'Open Link Hub', description: 'Meeting readiness banner action.', surface: 'meetings', maxChars: 32 },
  NLSMeetingsDialogDiscardTitle: { text: 'Discard meeting changes?', description: 'Dirty dialog title for unsaved meeting edits.', surface: 'meetings', maxChars: 52 },
  NLSMeetingsDialogDiscardMessage: {
    text: 'This meeting has unsaved edits. Save or discard before switching screens.',
    description: 'Dirty dialog message for unsaved meeting edits.',
    surface: 'meetings',
  },
  NLSAiPulseRestricted: {
    text: "Workspace Pulse covers the whole workspace, so it isn't available with limited access.",
    description: 'Notice shown when Workspace Pulse is unavailable because member access is restricted.',
    surface: 'ai',
    maxChars: 120,
  },
  NLSAiAriaOpenChatHistory: { text: 'Open chat history', description: 'Accessible label for opening the AI Magic chat history drawer.', surface: 'ai', maxChars: 42 },
  NLSAiAriaCloseChatHistory: { text: 'Close chat history', description: 'Accessible label for closing the AI Magic chat history drawer.', surface: 'ai', maxChars: 42 },
  NLSAiAriaChatHistory: { text: 'AI chat history', description: 'Accessible label for the AI Magic chat history rail.', surface: 'ai', maxChars: 36 },
  NLSAiAriaActionsFor: { text: 'Actions for {label}', description: 'Accessible label for AI chat history row actions. {label} is a chat title.', surface: 'ai', maxChars: 90 },
  NLSAiActionNewChat: { text: 'New chat', description: 'AI Magic action for starting a new chat.', surface: 'ai', maxChars: 24 },
  NLSAiActionRename: { text: 'Rename', description: 'AI Magic action for renaming a chat history item.', surface: 'ai', maxChars: 22 },
  NLSAiActionDelete: { text: 'Delete', description: 'AI Magic action for deleting a chat history item.', surface: 'ai', maxChars: 22 },
  NLSAiActionAdd: { text: 'Add', description: 'AI Magic compact action for adding context.', surface: 'ai', maxChars: 16 },
  NLSAiActionRetry: { text: 'Retry', description: 'AI Magic action for retrying a failed proposal operation.', surface: 'ai', maxChars: 18 },
  NLSAiActionReject: { text: 'Reject', description: 'AI Magic action for rejecting a proposed action.', surface: 'ai', maxChars: 20 },
  NLSAiActionStop: { text: 'Stop', description: 'AI Magic action for stopping Line thinking.', surface: 'ai', maxChars: 16 },
  NLSAiActionEdit: { text: 'Edit', description: 'AI Magic compact edit target selector action.', surface: 'ai', maxChars: 16 },
  NLSAiActionSave: { text: 'Save', description: 'AI Magic generated tree save action.', surface: 'ai', maxChars: 16 },
  NLSAiActionSaving: { text: 'Saving...', description: 'AI Magic generated tree saving state.', surface: 'ai', maxChars: 24 },
  NLSAiActionSaved: { text: 'Saved', description: 'AI Magic generated tree saved state.', surface: 'ai', maxChars: 20 },
  NLSAiActionDiscard: { text: 'Discard', description: 'AI Magic generated tree discard action.', surface: 'ai', maxChars: 22 },
  NLSAiActionYesIAmSure: { text: 'Yes, I am sure', description: 'AI Magic generated tree destructive confirmation action.', surface: 'ai', maxChars: 34 },
  NLSAiActionReviewDependencies: { text: 'Review dependencies', description: 'AI Magic proposal action for reviewing dependency changes.', surface: 'ai', maxChars: 42 },
  NLSAiActionReviewPatches: { text: 'Review patches', description: 'AI Magic proposal action for reviewing multiple patches.', surface: 'ai', maxChars: 34 },
  NLSAiActionReviewPatch: { text: 'Review patch', description: 'AI Magic proposal action for reviewing one patch.', surface: 'ai', maxChars: 32 },
  NLSAiActionMoveToManage: { text: 'Move to Manage', description: 'AI Magic proposal action for moving a generated draft into Manage.', surface: 'ai', maxChars: 34 },
  NLSAiActionCollapseGoal: { text: 'Collapse goal', description: 'Accessible label for collapsing a goal in the AI target selector.', surface: 'ai', maxChars: 34 },
  NLSAiActionExpandGoal: { text: 'Expand goal', description: 'Accessible label for expanding a goal in the AI target selector.', surface: 'ai', maxChars: 32 },
  NLSAiActionCollapseTask: { text: 'Collapse task', description: 'Accessible label for collapsing a task in the AI target selector.', surface: 'ai', maxChars: 34 },
  NLSAiActionExpandTask: { text: 'Expand task', description: 'Accessible label for expanding a task in the AI target selector.', surface: 'ai', maxChars: 32 },
  NLSAiActionCollapseSubtask: { text: 'Collapse subtask', description: 'Accessible label for collapsing a subtask in the AI target selector.', surface: 'ai', maxChars: 38 },
  NLSAiActionExpandSubtask: { text: 'Expand subtask', description: 'Accessible label for expanding a subtask in the AI target selector.', surface: 'ai', maxChars: 36 },
  NLSAiActionSnoozeSuggestion: { text: 'Snooze suggestion', description: 'Accessible label for snoozing a proactive Line suggestion.', surface: 'ai', maxChars: 40 },
  NLSAiActionAcceptSuggestion: { text: 'Accept suggestion', description: 'Accessible label for accepting a proactive Line suggestion.', surface: 'ai', maxChars: 40 },
  NLSAiActionDismissSuggestion: { text: 'Dismiss suggestion', description: 'Accessible label for dismissing an ambient Line suggestion.', surface: 'ai', maxChars: 42 },
  NLSAiMagicStatusDeltaTitle: {
    text: 'Move next task',
    description: 'AI Magic quick action title for advancing task status.',
    surface: 'ai',
    maxChars: 24,
  },
  NLSAiMagicStatusDeltaDescription: {
    text: 'Move {task} to the next status',
    description: 'AI Magic quick action description to advance task status.',
    surface: 'ai',
    maxChars: 60,
  },
  NLSAiMagicStatusDeltaEmptyDescription: {
    text: 'No task or subtask is ready for a status move.',
    description: 'AI Magic quick action empty state when no tasks are ready.',
    surface: 'ai',
    maxChars: 60,
  },
  NLSAiComposerAddContext: { text: 'Add context', description: 'Accessible label for AI Magic composer context button.', surface: 'ai', maxChars: 28 },
  NLSAiComposerOptions: { text: 'AI Magic options', description: 'Accessible label for the AI Magic composer options menu.', surface: 'ai', maxChars: 36 },
  NLSAiComposerContextCount: {
    text: 'Context {count}/{limit}',
    description: 'AI Magic composer attachment count. {count} is current attachments and {limit} is the tier limit.',
    surface: 'ai',
    maxChars: 34,
  },
  NLSAiComposerAddToContext: { text: 'Add to context', description: 'AI Magic composer menu group label.', surface: 'ai', maxChars: 32 },
  NLSAiComposerBack: { text: 'Back', description: 'AI Magic composer submenu back action.', surface: 'ai', maxChars: 18 },
  NLSAiComposerAddPhotosFiles: { text: 'Add photos and files', description: 'AI Magic composer action for adding local files.', surface: 'ai', maxChars: 42 },
  NLSAiComposerAddPhotosFilesHint: { text: 'Images, PDF, documents, spreadsheets', description: 'AI Magic composer hint for supported local file types.', surface: 'ai', maxChars: 58 },
  NLSAiComposerAttachPublicLink: { text: 'Attach public link', description: 'AI Magic composer action for attaching a public link.', surface: 'ai', maxChars: 40 },
  NLSAiComposerAttachPublicLinkHint: { text: 'Line validates readable HTTPS pages', description: 'AI Magic composer hint for public link validation.', surface: 'ai', maxChars: 58 },
  NLSAiComposerUseAppAsset: { text: 'Use app asset', description: 'AI Magic composer action for attaching an existing upload.', surface: 'ai', maxChars: 34 },
  NLSAiComposerUseAppAssetHint: { text: 'Reference an accessible existing upload', description: 'AI Magic composer hint for app asset references.', surface: 'ai', maxChars: 58 },
  NLSAiComposerSelectTimelineItems: { text: 'Select timeline items', description: 'AI Magic composer action for selecting timeline context.', surface: 'ai', maxChars: 44 },
  NLSAiComposerSelectedCount: { text: 'Any board - {count} selected', description: 'AI Magic composer timeline selection count. {count} is selected target count.', surface: 'ai', maxChars: 44 },
  NLSAiComposerOperatingMode: { text: 'Operating mode', description: 'AI Magic composer group label for planning/execution mode.', surface: 'ai', maxChars: 34 },
  NLSAiComposerPlanMode: { text: 'Plan mode - AI Project Architect', description: 'AI Magic composer plan-mode toggle title.', surface: 'ai', maxChars: 54 },
  NLSAiComposerPlanModeHint: {
    text: 'Read-only strategy from a paragraph - leave unchecked to push generated work onto the app.',
    description: 'AI Magic composer plan-mode toggle hint.',
    surface: 'ai',
    maxChars: 110,
  },
  NLSAiComposerPastePublicLink: { text: 'Paste one public HTTPS link', description: 'AI Magic composer public-link textarea placeholder.', surface: 'ai', maxChars: 48 },
  NLSAiComposerChecking: { text: 'Checking...', description: 'AI Magic composer link validation busy state.', surface: 'ai', maxChars: 24 },
  NLSAiComposerLineCanReadLink: { text: 'Line can read this link.', description: 'AI Magic composer link validation success state.', surface: 'ai', maxChars: 44 },
  NLSAiComposerLinkValidationPending: { text: 'Add becomes available after validation.', description: 'AI Magic composer link validation waiting state.', surface: 'ai', maxChars: 62 },
  NLSAiComposerLineCouldNotReachLink: { text: 'Line could not reach this link.', description: 'AI Magic composer link validation failure fallback.', surface: 'ai', maxChars: 56 },
  NLSAiStatusConfirmed: { text: 'Confirmed', description: 'Line transcript status chip for confirmed output.', surface: 'ai', maxChars: 24 },
  NLSAiStatusPendingApproval: { text: 'Approval needed', description: 'Line transcript status chip for approval-needed proposals.', surface: 'ai', maxChars: 34 },
  NLSAiStatusPendingClarification: { text: 'Needs detail', description: 'Line transcript status chip for pending clarification.', surface: 'ai', maxChars: 30 },
  NLSAiStatusRejected: { text: 'Rejected', description: 'Line transcript status chip for rejected proposals.', surface: 'ai', maxChars: 24 },
  NLSAiTierDraftedBy: { text: 'Drafted by {tierLongLabel}', description: 'Line tier badge tooltip. {tierLongLabel} is the friendly AI tier label.', surface: 'ai', maxChars: 80 },
  NLSAiLabelSystem: { text: 'System', description: 'Line transcript label for system/warning turns.', surface: 'ai', maxChars: 20 },
  NLSAiLabelYou: { text: 'You', description: 'Line transcript label for the current user turn.', surface: 'ai', maxChars: 14 },
  NLSAiLabelAttachmentUnavailable: { text: 'Attachment not available.', description: 'Line attachment preview unavailable fallback.', surface: 'ai', maxChars: 60 },
  NLSAiAriaAttachedPreviews: { text: 'Attached previews', description: 'Accessible label for Line attachment preview strip.', surface: 'ai', maxChars: 38 },
  NLSAiExecutionResultsAria: { text: 'Execution results', description: 'Accessible label for Line execution result details.', surface: 'ai', maxChars: 40 },
  NLSAiExecutionDetails: { text: 'Execution details', description: 'Heading for Line execution result details.', surface: 'ai', maxChars: 38 },
  NLSAiExecutionCompletedFraction: { text: '{completed}/{total} completed', description: 'Line execution result completion count. {completed} and {total} are numbers.', surface: 'ai', maxChars: 34 },
  NLSAiExecutionDone: { text: 'Done', description: 'Line execution result success label.', surface: 'ai', maxChars: 16 },
  NLSAiExecutionFailed: { text: 'Failed', description: 'Line execution result failure label.', surface: 'ai', maxChars: 18 },
  NLSAiQuickActionsAria: { text: 'Line proposal actions', description: 'Accessible label for Line quick proposal actions.', surface: 'ai', maxChars: 44 },
  NLSAiClarificationFormAria: { text: 'Line clarification form', description: 'Accessible label for the Line structured clarification form.', surface: 'ai', maxChars: 46 },
  NLSAiClarificationEyebrow: { text: 'Line needs one detail', description: 'Eyebrow on the Line structured clarification form.', surface: 'ai', maxChars: 44 },
  NLSAiClarificationSelectOption: { text: 'Select...', description: 'Placeholder option for Line clarification select controls.', surface: 'ai', maxChars: 24 },
  NLSAiThinkingDefault: { text: 'Line is thinking', description: 'Default Line thinking indicator label.', surface: 'ai', maxChars: 34 },
  NLSAiSuggestionGapAnalysis: { text: 'Gap analysis', description: 'Line proactive suggestion kind label for gap analysis.', surface: 'ai', maxChars: 30 },
  NLSAiSuggestionPastContext: { text: 'Past context', description: 'Line proactive suggestion kind label for context linker.', surface: 'ai', maxChars: 30 },
  NLSAiSuggestionAria: { text: '{kind} suggestion', description: 'Accessible label for a Line proactive suggestion. {kind} is the suggestion kind label.', surface: 'ai', maxChars: 50 },
  NLSAiAmbientSuggestionAria: { text: 'Line suggestion: {title}', description: 'Accessible label for the ambient Line suggestion chip. {title} is suggestion content.', surface: 'ai', maxChars: 90 },
  NLSAiGoalTreeNoGoals: { text: 'No goals yet - generate one first, then edit it here.', description: 'AI target selector empty state when no goals exist.', surface: 'ai', maxChars: 78 },
  NLSAiGoalTreeNoChecklistItems: { text: 'No checklist items', description: 'AI target selector empty state for an expanded subtask without checklist items.', surface: 'ai', maxChars: 38 },
  NLSAiEditExistingAria: { text: 'Edit an existing goal, task, or subtask', description: 'Accessible label for the AI edit target selector trigger.', surface: 'ai', maxChars: 58 },
  NLSAiEditExistingTitle: { text: 'Edit existing items', description: 'Tooltip for the AI edit target selector trigger.', surface: 'ai', maxChars: 40 },
  NLSAiEditExistingMenuLabel: { text: 'Edit existing', description: 'Accessible label for the AI edit target selector menu.', surface: 'ai', maxChars: 34 },
  NLSAiEditExistingSummary: { text: 'Edit existing - {count}/3 - one goal', description: 'AI edit target selector summary. {count} is selected target count.', surface: 'ai', maxChars: 52 },
  NLSAiPendingTreeReviewSaveTitle: { text: 'Review and save - goal first, subtasks last', description: 'AI generated create-tree save ceremony title.', surface: 'ai', maxChars: 66 },
  NLSAiPendingTreeSaveEditsTitle: { text: 'Save your edits - goal first, subtasks last', description: 'AI generated edit-tree save ceremony title.', surface: 'ai', maxChars: 66 },
  NLSAiDiscardSubtaskConfirm: { text: 'Delete this subtask? This cannot be undone.', description: 'AI generated tree discard confirmation for a subtask.', surface: 'ai', maxChars: 68 },
  NLSAiDiscardTaskConfirm: { text: 'Delete this task and all its subtasks? This cannot be undone.', description: 'AI generated tree discard confirmation for a task.', surface: 'ai', maxChars: 82 },
  NLSAiDiscardGoalConfirm: { text: 'Delete this goal and everything under it? This cannot be undone.', description: 'AI generated tree discard confirmation for a goal.', surface: 'ai', maxChars: 88 },
  NLSAiToastTimelineSaved: { text: 'Timeline saved.', description: 'Toast after saving an AI generated timeline draft.', surface: 'ai', maxChars: 34 },
  NLSAiToastChangesSaved: { text: 'Changes saved.', description: 'Toast after saving an AI edited timeline draft.', surface: 'ai', maxChars: 34 },
  NLSAiToastCouldNotSaveTimeline: { text: 'Could not save the timeline.', description: 'Toast when an AI generated timeline draft cannot be saved.', surface: 'ai', maxChars: 58 },
  NLSAiToastGenerationCleared: { text: 'Generation cleared.', description: 'Toast after clearing an AI generated draft.', surface: 'ai', maxChars: 38 },
  NLSAiIdentityTitle: { text: 'AI Magic', description: 'AI Magic page identity title.', surface: 'ai', maxChars: 24 },
  NLSAiIdentitySubtitle: { text: 'LINE', description: 'AI Magic page identity subtitle for the Line persona.', surface: 'ai', maxChars: 16 },
  NLSAiAriaLineTranscript: { text: 'Line chat transcript', description: 'Accessible label for the AI Magic transcript.', surface: 'ai', maxChars: 46 },
  NLSAiProposalSummaryAria: { text: 'Proposal summary', description: 'Accessible label for AI Magic proposal summaries.', surface: 'ai', maxChars: 40 },
  NLSAiProposalSummaryHeard: { text: 'Here is what I heard', description: 'Label before an AI Magic proposal summary.', surface: 'ai', maxChars: 42 },
  NLSAiProposalReadyHeading: { text: 'Ready to build', description: 'Fallback heading for an AI Magic proposal summary.', surface: 'ai', maxChars: 34 },
  NLSAiProposalTimelineBullet: { text: 'Timeline: {start} -> {end}', description: 'AI Magic proposal summary timeline bullet. {start} and {end} are dates.', surface: 'ai', maxChars: 120 },
  NLSAiProposalScopeBullet: { text: 'Scope: {scope}', description: 'AI Magic proposal summary scope bullet. {scope} is a localized list of item counts.', surface: 'ai', maxChars: 120 },
  NLSAiProposalTaskCountOne: { text: '{count} task', description: 'Singular task count in an AI Magic proposal summary.', surface: 'ai', maxChars: 24 },
  NLSAiProposalTaskCountMany: { text: '{count} tasks', description: 'Plural task count in an AI Magic proposal summary.', surface: 'ai', maxChars: 24 },
  NLSAiProposalSubtaskCountOne: { text: '{count} subtask', description: 'Singular subtask count in an AI Magic proposal summary.', surface: 'ai', maxChars: 28 },
  NLSAiProposalSubtaskCountMany: { text: '{count} subtasks', description: 'Plural subtask count in an AI Magic proposal summary.', surface: 'ai', maxChars: 30 },
  NLSAiProposalChecklistCountOne: { text: '{count} checklist item', description: 'Singular checklist item count in an AI Magic proposal summary.', surface: 'ai', maxChars: 38 },
  NLSAiProposalChecklistCountMany: { text: '{count} checklist items', description: 'Plural checklist item count in an AI Magic proposal summary.', surface: 'ai', maxChars: 40 },
  NLSAiProposalBuildPrompt: {
    text: 'Say "build it" (or hit Build below) to move this into Manage. Say what to change and I will revise.',
    description: 'AI Magic proposal summary footer prompt.',
    surface: 'ai',
    maxChars: 150,
  },
  NLSAiProposalChangingHint: {
    text: 'Tell me what to change and I will revise this proposal.',
    description: 'Inline hint after the user asks to change an AI Magic proposal.',
    surface: 'ai',
    maxChars: 90,
  },
  NLSAiProposalActionDiscard: { text: 'Discard', description: 'AI Magic proposal summary action for discarding a pending draft.', surface: 'ai', maxChars: 22 },
  NLSAiProposalActionDiscardConfirm: { text: 'Are you sure?', description: 'Inline confirmation before discarding an AI Magic pending draft.', surface: 'ai', maxChars: 28 },
  NLSAiProposalDiscardedToast: { text: 'Draft discarded', description: 'Toast after discarding an AI Magic pending draft.', surface: 'ai', maxChars: 32 },
  NLSAiProposalDiscardedUndo: { text: 'Undo', description: 'Undo action for a discarded AI Magic pending draft.', surface: 'ai', maxChars: 18 },
  NLSOwnerConsoleLineTab: { text: 'Line', description: 'Owner Console sidebar tab for workspace-specific Line policy controls.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLinePolicySavedToast: { text: 'Line policy saved', description: 'Owner Console toast after saving a workspace Line policy control.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleGroupHome: { text: 'Home', description: 'Owner Console rail group title for Home.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleGroupPeopleWorkspaces: { text: 'People & Workspaces', description: 'Owner Console rail group title for People and Workspaces.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleGroupSupport: { text: 'Support', description: 'Owner Console rail group title for Support.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleGroupHealth: { text: 'Health', description: 'Owner Console rail group title for Health.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleGroupConfig: { text: 'Config', description: 'Owner Console rail group title for Config.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleGroupActivity: { text: 'Activity', description: 'Owner Console rail group title for Activity.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavHome: { text: 'Home', description: 'Owner Console nav item for Home dashboard.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavWorkspaces: { text: 'Workspaces', description: 'Owner Console nav item for Workspaces list.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavPeople: { text: 'People', description: 'Owner Console nav item for People list.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsolePeoplePage: { text: 'Page {page}', description: 'Current server page in the Owner Console People list.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePeopleNextPage: { text: 'Next page', description: 'Advance the Owner Console People server page.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceTitle: { text: 'Workspaces', description: 'Owner Console workspace list title.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceSubtitle: { text: 'Indexed workspace health and support signals', description: 'Owner Console workspace list subtitle.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspaceViewsLabel: { text: 'Workspace views', description: 'Accessible label for workspace preset views.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceViewAll: { text: 'All', description: 'All workspace preset.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceViewNeedsAttention: { text: 'Needs attention', description: 'Workspace risk preset.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceViewEnterprise: { text: 'Enterprise', description: 'Enterprise workspace preset.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceViewCreditEmpty: { text: 'Credit empty', description: 'Empty-credit workspace preset.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceViewOpenTickets: { text: 'Has open tickets', description: 'Open-ticket workspace preset.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceViewRevenueModeled: { text: 'Revenue (modeled)', description: 'Modeled-revenue workspace preset.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceSearchLabel: { text: 'Search workspaces', description: 'Accessible label for workspace search.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceSearchPlaceholder: { text: 'Search name, owner email, or short ID…', description: 'Workspace search placeholder.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspacePlanLabel: { text: 'Plan', description: 'Workspace plan filter label.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceTypeLabel: { text: 'Type', description: 'Workspace type filter label.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceStatusLabel: { text: 'Status', description: 'Workspace status filter label.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceSortLabel: { text: 'Sort workspaces', description: 'Workspace sort label.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceAllPlans: { text: 'All plans', description: 'No plan filter.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspacePlanFree: { text: 'Free', description: 'Free plan filter.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspacePlanEssential: { text: 'Essential', description: 'Essential plan filter.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspacePlanGrowth: { text: 'Growth', description: 'Growth plan filter.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspacePlanProfessional: { text: 'Professional', description: 'Professional plan filter.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspacePlanEnterprise: { text: 'Enterprise', description: 'Enterprise plan filter.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceAllTypes: { text: 'All types', description: 'No workspace type filter.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceTeam: { text: 'Team', description: 'Team workspace type.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspacePersonal: { text: 'Personal', description: 'Personal workspace type.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceAllStatuses: { text: 'All statuses', description: 'No workspace status filter.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceStatusActive: { text: 'Active', description: 'Active workspace status.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceStatusSuspended: { text: 'Suspended', description: 'Suspended workspace status.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceStatusTrialExpired: { text: 'Trial expired', description: 'Expired-trial workspace status.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceSortLastActive: { text: 'Sort: Last active', description: 'Workspace last-active sort.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceSortRevenue: { text: 'Sort: Revenue (modeled)', description: 'Workspace modeled revenue sort.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleWorkspaceSortRisk: { text: 'Sort: Risk', description: 'Workspace risk sort.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceColumnWorkspace: { text: 'Workspace', description: 'Workspace table column.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceColumnPlan: { text: 'Plan', description: 'Workspace plan column.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceColumnOwner: { text: 'Owner', description: 'Workspace owner column.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceColumnPeople: { text: 'People', description: 'Workspace people column.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceColumnStorage: { text: 'Storage', description: 'Workspace storage column.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceColumnCredits: { text: 'Credits', description: 'Workspace credits column.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceColumnMrr: { text: 'MRR (modeled)', description: 'Workspace modeled revenue column.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceColumnRisk: { text: 'Risk (estimate)', description: 'Workspace heuristic risk column.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceColumnLastActive: { text: 'Last active', description: 'Workspace last-active column.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspacePeopleTooltip: { text: '{members} active members and {helpers} helpers', description: 'Workspace people breakdown tooltip.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspaceCreditsTooltip: { text: '{spent} spent lifetime', description: 'Workspace credits tooltip.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleWorkspaceModeled: { text: 'modeled', description: 'Modeled-data label.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceHeuristic: { text: 'heuristic', description: 'Heuristic-data label.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceModeledTooltip: { text: 'Estimated from the workspace plan; not booked revenue.', description: 'Modeled revenue tooltip.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleWorkspaceRiskTooltip: { text: 'Heuristic estimate from workspace health signals.', description: 'Risk tooltip.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleWorkspaceMissing: { text: 'Unnamed workspace', description: 'Missing workspace name fallback.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceLoading: { text: 'Loading workspaces…', description: 'Workspace list loading state.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceLoadError: { text: 'Failed to load the workspace list.', description: 'Workspace list error.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleWorkspaceEmptyTitle: { text: 'No workspaces match these filters', description: 'Workspace list empty title.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleWorkspaceEmptyBody: { text: 'Try clearing a filter or using a different search.', description: 'Workspace list empty body.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsoleWorkspaceEmptyExplanation: { text: 'This list is populated from the indexed workspace projection.', description: 'Workspace list empty explanation.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleWorkspaceSearchTruncated: { text: 'Search reached its {cap}-workspace candidate window; results may be incomplete.', description: 'Workspace truncated-search notice.', surface: 'owner-console', maxChars: 90 },
  NLSOwnerConsoleWorkspaceOrderingChanged: { text: 'A preset-required ordering is primary; your selected sort is secondary.', description: 'Workspace range-preset ordering notice.', surface: 'owner-console', maxChars: 90 },
  NLSOwnerConsoleWorkspacePaginationLabel: { text: 'Workspace server pagination', description: 'Accessible workspace pagination label.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleWorkspacePage: { text: 'Page {page}', description: 'Current workspace server page.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceNextPage: { text: 'Next page', description: 'Advance workspace server page.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailUnavailableTitle: { text: 'Workspace detail unavailable', description: 'Temporary exact-ID detail state title.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleWorkspaceDetailUnavailableBody: { text: 'Workspace {id} cannot be loaded from the legacy detail data.', description: 'Temporary exact-ID detail state body.', surface: 'owner-console', maxChars: 90 },
  NLSOwnerConsoleWorkspaceDetailUnavailableExplanation: { text: 'The full indexed workspace detail lands in Phase 1c-3.', description: 'Temporary exact-ID detail state explanation.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleWorkspaceDetailLoadingTitle: { text: 'Loading workspace detail', description: 'Exact-ID legacy detail loading title.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleWorkspaceDetailLoadingBody: { text: 'Checking whether workspace {id} is available.', description: 'Exact-ID legacy detail loading body.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleWorkspaceDetailBack: { text: 'Back to workspaces', description: 'Workspace detail back action.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceDetailLoading: { text: 'Loading workspace detail…', description: 'Workspace detail loading state.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleWorkspaceDetailOwner: { text: 'Owner', description: 'Workspace detail owner label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailId: { text: 'ID', description: 'Workspace detail ID label.', surface: 'owner-console', maxChars: 12 },
  NLSOwnerConsoleWorkspaceDetailOverview: { text: 'Overview', description: 'Workspace detail overview tab.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailPeople: { text: 'People', description: 'Workspace detail people tab.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailMoney: { text: 'Money', description: 'Workspace detail money tab.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailActivity: { text: 'Activity', description: 'Workspace detail activity tab.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailTickets: { text: 'Tickets', description: 'Workspace detail tickets tab.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailOps: { text: 'Operations', description: 'Workspace detail operations tab.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailConfig: { text: 'Configuration', description: 'Workspace detail configuration tab.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailDanger: { text: 'Danger zone', description: 'Workspace detail danger tab.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailActions: { text: 'Workspace actions', description: 'Workspace detail action heading.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceDetailReason: { text: 'Reason', description: 'Workspace action reason field.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailPreview: { text: 'Preview impact', description: 'Workspace action preview button.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailConfirm: { text: 'Confirm action', description: 'Workspace action confirm button.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailTarget: { text: 'Immutable target', description: 'Workspace action target label.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailTypeName: { text: 'Type workspace name to confirm', description: 'Destructive workspace action confirmation label.', surface: 'owner-console', maxChars: 48 },
  NLSOwnerConsoleWorkspaceDetailPreviewRequired: { text: 'Run a successful preview before confirming this action.', description: 'Workspace action preview requirement.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsoleWorkspaceDetailNoRows: { text: 'No records are available.', description: 'Workspace detail empty section state.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleWorkspaceDetailUnavailable: { text: 'This section is temporarily unavailable.', description: 'Workspace detail unavailable section state.', surface: 'owner-console', maxChars: 52 },
  NLSOwnerConsoleWorkspaceDetailCredits: { text: 'Credits', description: 'Workspace credits action label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailPlan: { text: 'Plan', description: 'Workspace plan field label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailFailedRun: { text: 'Failed run', description: 'Workspace failed-run action label.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailResult: { text: 'Action result', description: 'Workspace action result heading.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailGrantCredits: { text: 'Grant credits', description: 'Workspace credit action.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailChangePlan: { text: 'Change plan', description: 'Workspace plan action.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailSuspend: { text: 'Suspend / resume', description: 'Workspace suspension action.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleWorkspaceDetailRefreshUsage: { text: 'Force usage refresh', description: 'Workspace usage action.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleWorkspaceDetailRetry: { text: 'Retry failed job', description: 'Workspace retry action.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailInspect: { text: 'Inspect workspace', description: 'Workspace inspect action.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailResetContent: { text: 'Reset workspace content', description: 'Dangerous workspace reset action.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceDetailResetChat: { text: 'Reset workspace chat', description: 'Dangerous workspace chat reset action.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleWorkspaceDetailResetDesign: { text: 'Reset Design Hub STALE', description: 'Dangerous Design Hub reset action.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceDetailWorking: { text: 'Working…', description: 'Pending workspace action copy.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceDetailRevenue: { text: 'Revenue (modeled)', description: 'Modeled workspace revenue label.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleWorkspaceDetailCost: { text: 'Cost (estimate)', description: 'Estimated workspace cost label.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailMargin: { text: 'Margin (modeled)', description: 'Modeled workspace margin label.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleWorkspaceDetailRisk: { text: 'Risk (heuristic)', description: 'Heuristic workspace risk label.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleWorkspaceDetailStorage: { text: 'Storage', description: 'Workspace storage label.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceDetailOwnerAudit: { text: 'Owner audit', description: 'Workspace owner audit heading.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailNextPage: { text: 'Next page', description: 'Workspace detail next page action.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceDetailSuspendWorkspace: { text: 'Suspend workspace', description: 'Workspace suspension toggle.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailDemoUnlock: { text: 'Demo unlock', description: 'Title of the demo unlock card and action in workspace detail.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDetailDemoUnlockOn: { text: 'On', description: 'Demo unlock state: switched on.', surface: 'owner-console', maxChars: 12 },
  NLSOwnerConsoleWorkspaceDetailDemoUnlockOff: { text: 'Off', description: 'Demo unlock state: switched off.', surface: 'owner-console', maxChars: 12 },
  NLSOwnerConsoleWorkspaceDetailDemoUnlockHelp: { text: 'Lifts plan limits and credit charges on this workspace for demos. Roles and permissions stay as they are. Not available on production.', description: 'Help text on the demo unlock card.', surface: 'owner-console', maxChars: 200 },
  NLSOwnerConsoleWorkspaceDetailDemoUnlockSetBy: { text: 'Set by {user} on {date}', description: 'Who last changed the demo unlock switch, and when.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleWorkspaceDetailDemoUnlockNever: { text: 'Never switched', description: 'Demo unlock has never been changed on this workspace.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceDetailDemoUnlockTurnOn: { text: 'Turn demo unlock on', description: 'Switch label and drawer text to enable demo unlock.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceDetailDemoUnlockTurnOff: { text: 'Turn demo unlock off', description: 'Switch label and drawer text to disable demo unlock.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceDemoStoryTitle: { text: 'Demo story', description: 'Title of the demo story card in workspace detail.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDemoStoryHelp: { text: 'Save this workspace as a story, then restore it before a demo. Dates move forward so today still looks like today. Roles and members are never changed.', description: 'Help text on the demo story card.', surface: 'owner-console', maxChars: 220 },
  NLSOwnerConsoleWorkspaceDemoStoryNeedsUnlock: { text: 'Turn on Demo unlock first', description: 'Note shown when save and restore are off because demo unlock is off.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleWorkspaceDemoStoryFounderOnly: { text: 'Only the founder can use demo stories', description: 'Note shown to non-founder owner roles.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspaceDemoStoryNameLabel: { text: 'Story name', description: 'Label of the story name input.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDemoStorySave: { text: 'Save story now', description: 'Button to save the workspace as a demo story.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceDemoStoryNameRequired: { text: 'Give the story a name', description: 'Error when saving a demo story without a name.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleWorkspaceDemoStoryLoading: { text: 'Loading saved stories…', description: 'Shown while the saved stories load.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceDemoStoryLoadError: { text: 'Could not load saved stories', description: 'Shown when the saved stories list fails to load.', surface: 'owner-console', maxChars: 48 },
  NLSOwnerConsoleWorkspaceDemoStoryEmpty: { text: 'No saved stories yet', description: 'Shown when the workspace has no saved demo stories.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceDemoStoryRow: { text: '{name} · {date} · {docs} docs · {size}', description: 'One saved story: name, saved date, document count, size.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleWorkspaceDemoStoryRestore: { text: 'Restore', description: 'Button to restore a saved demo story.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceDemoStoryRestoreNamed: { text: 'Restore {name}', description: 'Accessible name of the restore button for one story.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleWorkspaceDemoStoryDelete: { text: 'Delete', description: 'Button to delete a saved demo story.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceDemoStoryDeleteNamed: { text: 'Delete {name}', description: 'Accessible name of the delete button for one story.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleWorkspaceDemoStoryActionSave: { text: 'Save demo story', description: 'Drawer title for saving a demo story.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceDemoStoryActionRestore: { text: 'Restore demo story', description: 'Drawer title for restoring a demo story.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceDemoStoryActionDelete: { text: 'Delete demo story', description: 'Drawer title for deleting a demo story.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleWorkspaceDemoStoryRestoreSummary: { text: 'Restores "{name}" with dates moved forward {days} days. {docs} documents are written and {remove} current documents are removed.', description: 'Plain summary of the restore dry run.', surface: 'owner-console', maxChars: 200 },
  NLSOwnerConsoleWorkspaceDemoStoryDeleteWarning: { text: 'Deletes the saved story for good. The workspace itself is not changed.', description: 'Warning in the delete story drawer.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleWorkspaceDemoBadge: { text: 'Demo', description: 'Badge on workspace list rows that have demo unlock on.', surface: 'owner-console', maxChars: 12 },
  NLSOwnerConsoleWorkspaceAside: { text: 'Aside', description: 'Button to preview workspace in aside drawer.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceAsidePreview: { text: 'Preview in aside', description: 'Title/aria-label for aside drawer preview button.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceAsidePreviewNamed: { text: 'Preview {name} in aside', description: 'Title/aria-label for aside drawer preview button with workspace name.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspaceOpenDetail: { text: 'Open detail', description: 'Title/aria-label for full detail link.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceOpenDetailNamed: { text: 'Open {name} detail', description: 'Title/aria-label for full detail link with workspace name.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspaceAsideClose: { text: 'Close aside', description: 'Button to close the aside drawer.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceAsideOpenFull: { text: 'Open full page', description: 'Link to open workspace detail in full page from aside.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceOwnerBadge: { text: 'Owner', description: 'Badge for workspace owner in roster.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleWorkspaceOverviewSignals: { text: 'Open tickets: {tickets} · Failed runs: {jobs} · Open risks: {risks}', description: 'Signals summary on workspace overview.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsoleWorkspaceAlerts: { text: 'Alerts', description: 'Header for active alerts on workspace overview.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceNoAlerts: { text: 'No active alerts', description: 'Message when workspace has no active alerts.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceAlertsUnavailable: { text: 'Alert assessment unavailable (read failed)', description: 'Message when projection read failed so alerts cannot be assessed.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspaceAlertsUnknown: { text: 'Alert assessment incomplete (no projection data)', description: 'Message when projection document is absent so alerts cannot be evaluated.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspaceAlertsPartial: { text: 'Assessment incomplete — some required data is unavailable.', description: 'Note shown alongside known alerts when dependencies or check inputs are missing/unavailable; the alert list may be incomplete.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleWorkspaceModuleSignals: { text: 'Module usage signals', description: 'Header for module signals on workspace overview.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceModuleSignalsUnavailable: { text: 'Module usage signals unavailable (read failed)', description: 'Message when reading module usage signals fails.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspaceSignalObserved: { text: 'observed', description: 'Badge label for observed module usage.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceSignalZero: { text: '0 (no activity)', description: 'Badge label for zero module usage.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceSignalUnknown: { text: 'unknown (no metric)', description: 'Badge label for unknown module usage.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceSignalUnavailable: { text: 'unavailable', description: 'Badge label for unavailable module usage.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceSectionUnavailable: { text: 'Section page unavailable', description: 'Error fallback when a section fails to load.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePeopleLastOwnerWarning: { text: 'Last active owner of {count} workspace(s): {names}. Disabling leaves them without a working owner.', description: 'Warning in the Owner Console disable-account confirmation when the person is the last active owner of co-owned workspaces.', surface: 'owner-console', maxChars: 200 },
  NLSOwnerConsoleWorkspaceDetailOwners: { text: 'Owners', description: 'Workspace detail label for the list of all workspace owners.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceConfigOwner: { text: 'Owner: {email}', description: 'Owner field on config tab.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleWorkspaceConfigCreated: { text: 'Created: {created} · Updated: {updated}', description: 'Timestamps on config tab.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsoleWorkspaceConfigProjection: { text: 'Projection: {state}', description: 'Projection state on config tab.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceConfigStatus: { text: 'Status: {status}', description: 'Workspace status on config tab.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceConfigType: { text: 'Type: {type}', description: 'Workspace type on config tab.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleNavInbox: { text: 'Support', description: 'Owner Console nav item for Support.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportTitle: { text: 'Support', description: 'Header title for Owner Console Support panel.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleSupportSubtitle: { text: 'Customer support tickets, triage, and SLA tracking', description: 'Subtitle for Owner Console Support panel.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsoleSupportViewsTitle: { text: 'Saved views', description: 'Header for saved views list in Support rail.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportViewOpen: { text: 'Open', description: 'Saved view for open tickets.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportViewAwaitingMe: { text: 'Awaiting me', description: 'Saved view for tickets awaiting owner triage.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportViewSnoozed: { text: 'Snoozed', description: 'Saved view for snoozed tickets.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportViewBreachedSla: { text: 'Breached SLA', description: 'Saved view for SLA breached tickets.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportViewBundled: { text: 'Bundled', description: 'Saved view for tickets with diagnostic bundles.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportViewRecentlyResolved: { text: 'Recently resolved', description: 'Saved view for recently resolved tickets.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportViewAll: { text: 'All', description: 'Saved view for all tickets.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportFilterStatus: { text: 'Status', description: 'Filter chip label for status.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleSupportFilterPriority: { text: 'Priority', description: 'Filter chip label for priority.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleSupportFilterBundle: { text: 'Has bundle', description: 'Filter chip label for debug bundles.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleSupportFilterAttachments: { text: 'Has attachments', description: 'Filter chip label for ticket attachments.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSupportEmptyTitle: { text: 'No tickets found', description: 'Empty state title when ticket list is empty.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleSupportEmptyDesc: {
    text: 'No support tickets match the active view or filters.',
    description: 'Empty state description when ticket list is empty.',
    surface: 'owner-console',
    maxChars: 80,
  },
  NLSOwnerConsoleSupportEmptyDetailTitle: { text: 'No ticket selected', description: 'Right pane title when no ticket is selected.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleSupportEmptyDetailDesc: {
    text: 'Select a ticket from the list to view its message thread and details.',
    description: 'Right pane description when no ticket is selected.',
    surface: 'owner-console',
    maxChars: 90,
  },
  NLSOwnerConsoleSupportDetailSeamTitle: { text: 'Ticket conversation landing in 1a-2', description: 'Detail pane placeholder title.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleSupportDetailSeamDesc: {
    text: 'Full message thread, reply composer, internal notes, and delivery channels will land in the next prompt.',
    description: 'Detail pane placeholder description.',
    surface: 'owner-console',
    maxChars: 120,
  },
  NLSOwnerConsoleNavFeedback: { text: 'Feedback', description: 'Owner Console nav item for Feedback responses.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavBroadcast: { text: 'Broadcast', description: 'Owner Console nav item for Broadcast.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavOps: { text: 'Ops & incidents', description: 'Owner Console nav item for Ops and incidents.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleNavLogs: { text: 'Logs', description: 'Owner Console nav item for Logs and telemetry.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsTitle: { text: 'Logs & Telemetry', description: 'Header title for Owner Console Logs panel.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleLogsSubtitle: { text: 'Structured event stream and distributed session trace viewer', description: 'Subtitle for Owner Console Logs panel.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsoleLogsFilterDateRange: { text: 'Time window', description: 'Label for date range filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterSeverity: { text: 'Severity', description: 'Label for severity filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterEvent: { text: 'Event', description: 'Label for event filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterSource: { text: 'Source', description: 'Label for log source filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterPlatform: { text: 'Platform', description: 'Label for platform filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterEnv: { text: 'Environment', description: 'Label for environment filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterUid: { text: 'User ID', description: 'Label for user ID filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterWorkspaceId: { text: 'Workspace ID', description: 'Label for workspace ID filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterHandler: { text: 'Handler / Route', description: 'Label for handler filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterSessionId: { text: 'Debug session ID', description: 'Label for debug session ID filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterTag: { text: 'Tag', description: 'Label for tag filter.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsFilterFreeText: { text: 'Search current page', description: 'Label for in-memory free text filter.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleLogsFilterFreeTextHint: { text: 'Searches error message and code on current page only', description: 'Hint for in-memory free text filter.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleLogsClearFilters: { text: 'Clear all filters', description: 'Button label to reset all filters.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsTraceMode: { text: 'Trace Mode Active', description: 'Badge for trace mode.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsTraceStart: { text: 'Started', description: 'Label for session start time in trace mode.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsTraceDuration: { text: 'Duration', description: 'Label for trace duration.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsTraceEvents: { text: 'Events', description: 'Label for event count in trace strip.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsTraceErrors: { text: 'Errors', description: 'Label for error count in trace strip.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsExitTrace: { text: 'Exit trace mode', description: 'Button to exit trace mode.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsColWhen: { text: 'When', description: 'Table column header for timestamp.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsColSev: { text: 'Sev', description: 'Table column header for severity.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsColEvent: { text: 'Event', description: 'Table column header for event name.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsColSource: { text: 'Source', description: 'Table column header for source.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsColHandlerRoute: { text: 'Handler / Route', description: 'Table column header for handler or route.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsColUser: { text: 'User ID', description: 'Table column header for user ID.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsColWorkspace: { text: 'Workspace ID', description: 'Table column header for workspace ID.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsColLatency: { text: 'Latency', description: 'Table column header for latency.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsColOutcome: { text: 'Outcome', description: 'Table column header for outcome.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsColError: { text: 'Error', description: 'Table column header for error.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsExportCsv: { text: 'Export CSV', description: 'Button label to export CSV.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsDiagnostics: { text: 'Diagnostics', description: 'Button label to open telemetry diagnostics.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsDetailTitle: { text: 'Log Entry Details', description: 'Drawer title for log entry details.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleLogsCopyJson: { text: 'Copy JSON', description: 'Button to copy entry JSON.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsDeepLink: { text: 'Copy Link', description: 'Button to copy deep link to log.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleLogsViewTrace: { text: 'View full trace', description: 'Button to view full trace for session.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleLogsEmptyFilterTitle: { text: 'No log entries match', description: 'Empty state title when filters match zero rows.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleLogsEmptyFilterBody: {
    text: 'No telemetry events match your selected filters. Try widening the time window or clearing active filters.',
    description: 'Empty state body when filters match zero rows.',
    surface: 'owner-console',
    maxChars: 120,
  },
  NLSOwnerConsoleLogsEmptyCollectionTitle: { text: 'No telemetry events captured yet', description: 'Empty state title when collection is empty.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleLogsEmptyCollectionBody: {
    text: 'Logging pipeline is live but no telemetry records exist yet. Trigger a WARN or ERROR event from any client or start a debug session to populate this feed.',
    description: 'Empty state body when collection is empty.',
    surface: 'owner-console',
    maxChars: 160,
  },
  NLSOwnerConsoleLogsAccessDeniedTitle: { text: 'Founder Access Required', description: 'Title when non-founder attempts to access logs.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleLogsAccessDeniedBody: {
    text: 'Telemetry logs and session traces contain operational diagnostics and are restricted to the founder role.',
    description: 'Body text when non-founder attempts to access logs.',
    surface: 'owner-console',
    maxChars: 120,
  },
  NLSOwnerConsoleTableColumns: { text: 'Columns', description: 'Button label to toggle table column visibility.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleNavAnalytics: { text: 'Product analytics', description: 'Owner Console nav item for Product analytics.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleNavRevenue: { text: 'Revenue', description: 'Owner Console nav item for Revenue.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavCosts: { text: 'Costs', description: 'Owner Console nav item for Costs.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavSettings: { text: 'Settings', description: 'Owner Console nav item for Settings.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavFlags: { text: 'Feature flags', description: 'Owner Console nav item for Feature flags.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavEconomy: { text: 'Economy', description: 'Owner Console nav item for Economy.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavPlanLimits: { text: 'Plans & Limits', description: 'Owner Console nav item for Plans & Limits.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavTemplates: { text: 'Templates', description: 'Owner Console nav item for Templates.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavFeedbackAuthoring: { text: 'Feedback authoring', description: 'Owner Console nav item for Survey feedback authoring.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleNavExperienceEngine: { text: 'Experience Engine', description: 'Owner Console nav item for Experience Engine tuning.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleNavModeration: { text: 'Moderation', description: 'Owner Console nav item for Moderation.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavActions: { text: 'Actions & runbooks', description: 'Owner Console nav item for Actions and runbooks.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleNavIntegrations: { text: 'Integrations', description: 'Owner Console nav item for Integrations.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavActivity: { text: 'Activity', description: 'Owner Console nav item for Activity log.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNavClientOutreach: { text: 'Client Outreach', description: 'Owner Console nav item for Client Outreach.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleClientOutreachTitle: { text: 'Client Outreach', description: 'Owner Console panel title for Client Outreach.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleClientOutreachSubtitle: { text: 'Global controls, default caps, workspace overrides, and delivery health.', description: 'Owner Console panel subtitle for Client Outreach.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleGlobalControls: { text: 'Global Controls', description: 'Section title for global client outreach controls.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleGlobalKillSwitch: { text: 'Global Kill-Switch', description: 'Label for global outreach kill-switch.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleGlobalKillSwitchDesc: { text: 'When enabled, client outreach is globally paused across all workspaces.', description: 'Description for global outreach kill-switch.', surface: 'owner-console', maxChars: 90 },
  NLSOwnerConsoleGlobalKillSwitchActive: { text: 'Client outreach is globally DISABLED.', description: 'Status text when outreach is globally disabled.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleGlobalKillSwitchInactive: { text: 'Client outreach is active.', description: 'Status text when outreach is globally active.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDisableOutreach: { text: 'Disable Outreach', description: 'Button label to disable client outreach.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleEnableOutreach: { text: 'Enable Outreach', description: 'Button label to enable client outreach.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleConfirmDisableTitle: { text: 'Disable Client Outreach Globally', description: 'Modal title to confirm disabling outreach globally.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleConfirmDisablePrompt: { text: 'Type DISABLE to confirm disabling client outreach globally.', description: 'Modal prompt to type DISABLE.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsoleDefaultDailyCap: { text: 'Default Daily Cap', description: 'Label for default daily email cap.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDefaultDailyCapDesc: { text: 'Maximum client emails allowed per workspace per day when no override is set (0–10000).', description: 'Description for default daily cap.', surface: 'owner-console', maxChars: 95 },
  NLSOwnerConsoleSaveCap: { text: 'Save Cap', description: 'Button label to save default daily cap.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCapSaved: { text: 'Default daily cap saved', description: 'Toast text after saving default cap.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleWorkspaces: { text: 'Workspaces', description: 'Workspaces section title.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleSearchWorkspaces: { text: 'Search workspaces...', description: 'Placeholder for workspace search input.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleWorkspaceColName: { text: 'Workspace', description: 'Table column header for workspace name.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceColStatus: { text: 'Status', description: 'Table column header for outreach status.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceColCap: { text: 'Daily Cap', description: 'Table column header for daily cap.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceColToday: { text: 'Today Sent', description: 'Table column header for sent count today.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceColActions: { text: 'Actions', description: 'Table column header for actions.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceOverrideClear: { text: 'Restore Default', description: 'Button label to clear workspace override.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceOverrideSet: { text: 'Set Cap', description: 'Button label to set workspace override cap.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceOverrideDisable: { text: 'Override Disable', description: 'Button label to disable workspace outreach.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleWorkspaceOverrideEnable: { text: 'Enable', description: 'Button label to enable workspace outreach override.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleStatsTitle: { text: '30-Day Delivery Health', description: 'Section title for outreach delivery stats.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleStatsSent: { text: 'Sent', description: 'Metric label for sent emails.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleStatsBounced: { text: 'Bounced', description: 'Metric label for bounced emails.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleStatsComplained: { text: 'Complained', description: 'Metric label for complained emails.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleStatsOptedOut: { text: 'Opted Out', description: 'Metric label for opted out clients.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleTrendLabel: { text: '30-day outreach volume trend', description: 'Accessible label for trend sparkline.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleNoWorkspaces: { text: 'No workspaces found.', description: 'Empty state message when no workspaces match.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleNoStats: { text: 'No outreach activity recorded in this period.', description: 'Empty state message when stats are empty.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleStatsTruncated: { text: 'Results cut: maximum limit reached.', description: 'Note shown when stats results are cut.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleStatsColTotals: { text: 'Totals', description: 'Table column header for totals.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleStatsColTrend: { text: 'Trend', description: 'Table column header for trend.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleStatsRowTrendLabel: { text: '{name} trend', description: 'Accessible label for row trend sparkline.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleWorkspaceActive: { text: 'Active', description: 'Status badge when workspace outreach is active.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceModuleDisabled: { text: 'Disabled (module)', description: 'Status badge when client outreach module is disabled.', surface: 'owner-console', maxChars: 25 },
  NLSOwnerConsoleWorkspaceOverrideBadge: { text: 'Override', description: 'Badge showing that daily cap is an override.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePaginationPage: { text: 'Page {page}', description: 'Page number in pagination controls.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleSearchBtn: { text: 'Search', description: 'Search button label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleTrendLast7Days: { text: 'Trend (last 7 days)', description: 'Subheading for sparkline trend.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleSummaryHeading: { text: 'All workspaces, last 30 days (UTC)', description: 'Headline summary label.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleStatsPartialError: { text: 'Some workspaces could not be read.', description: 'Warning note when stats response is partial.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleLoadError: { text: 'Failed to load client outreach data.', description: 'Error message when outreach data fails to load.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleRetry: { text: 'Retry', description: 'Button label to retry loading.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleStatsRowError: { text: 'Error', description: 'Indicator when workspace stats failed to load.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleWorkspaceTodayError: { text: "Couldn't read", description: 'Badge shown when today outreach count could not be loaded.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePaginationNext: { text: 'Next', description: 'Button label for next page.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePaginationPrev: { text: 'Previous', description: 'Button label for previous page.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleConfirm: { text: 'Confirm', description: 'Button label to confirm an action.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFounderOnlyNote: { text: 'Read-only mode. Founder role is required to modify controls.', description: 'Note shown when user role cannot write.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsolePlanLimitsTitle: { text: 'Plans & Limits', description: 'Title for the Plans & Limits panel.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsSubtitle: { text: 'Configure per-tier resource limits, team workspace defaults, and caps.', description: 'Subtitle for the Plans & Limits panel.', surface: 'owner-console', maxChars: 90 },
  NLSOwnerConsolePlanLimitsNonEditableNote: { text: 'Personal workspaces and input lengths aren\'t editable.', description: 'Note explaining uneditable limits.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsolePlanLimitsGroupTierLimits: { text: 'Tier Limits', description: 'Group header for tier limits.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsGroupWorkspaceDefaults: { text: 'Team Workspace Defaults', description: 'Group header for workspace defaults.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsGroupWorkspaceCaps: { text: 'Team Workspace Caps', description: 'Group header for workspace caps.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsColResource: { text: 'Resource / Limit', description: 'Column header for resources.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsTierFree: { text: 'Free', description: 'Tier name for Free tier.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePlanLimitsTierEssential: { text: 'Essential', description: 'Tier name for Essential tier.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePlanLimitsTierGrowth: { text: 'Growth', description: 'Tier name for Growth tier.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePlanLimitsTierProfessional: { text: 'Professional', description: 'Tier name for Professional tier.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePlanLimitsTierEnterprise: { text: 'Enterprise', description: 'Tier name for Enterprise tier.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePlanLimitsUnlimited: { text: 'Unlimited', description: 'Label for unlimited value (-1).', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePlanLimitsOverridden: { text: 'Overridden', description: 'Badge indicating a limit is overridden.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePlanLimitsCodeDefault: { text: 'Default: {value}', description: 'Text showing code default value.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsCeiling: { text: 'Ceiling: {value}', description: 'Text showing ceiling value.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsEditBtn: { text: 'Edit plan limits', description: 'Button to enter tier editor.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsSelectTier: { text: 'Select a tier to edit', description: 'Prompt to choose a tier to edit.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsEditingTier: { text: 'Editing {tier}', description: 'Header showing which tier is being edited.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsChooseTier: { text: 'Tier to edit', description: 'Label for tier select dropdown.', surface: 'owner-console', maxChars: 25 },
  NLSOwnerConsolePlanLimitsToggleUnlimited: { text: 'Unlimited', description: 'Toggle label for unlimited limit.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePlanLimitsUseCodeDefault: { text: 'Use code default', description: 'Button to revert to code default.', surface: 'owner-console', maxChars: 25 },
  NLSOwnerConsolePlanLimitsReasonLabel: { text: 'Reason for change (min 6 chars)', description: 'Label for reason input.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsReasonPlaceholder: { text: 'Enter reason for changing plan limits...', description: 'Placeholder for reason input.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsolePlanLimitsReasonError: { text: 'Reason must be at least 6 characters.', description: 'Validation error for short reason.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsolePlanLimitsPreviewBtn: { text: 'Preview changes', description: 'Button to dry-run and preview changes.', surface: 'owner-console', maxChars: 25 },
  NLSOwnerConsolePlanLimitsPreviewingBtn: { text: 'Previewing...', description: 'Button loading state for preview.', surface: 'owner-console', maxChars: 25 },
  NLSOwnerConsolePlanLimitsConfirmBtn: { text: 'Confirm and apply', description: 'Button to confirm and apply plan limits.', surface: 'owner-console', maxChars: 25 },
  NLSOwnerConsolePlanLimitsApplyingBtn: { text: 'Applying...', description: 'Button loading state for applying.', surface: 'owner-console', maxChars: 25 },
  NLSOwnerConsolePlanLimitsCancelBtn: { text: 'Cancel', description: 'Button to cancel editing.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsolePlanLimitsDiffsTitle: { text: 'Proposed Changes', description: 'Section title for preview diffs.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsNoChanges: { text: 'No changes detected.', description: 'Message when diff is empty.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsWorkspaceCount: { text: 'Workspaces on this tier: {count}', description: 'Count of workspaces on tier.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsolePlanLimitsCouldNotCount: { text: "couldn't count", description: 'Fallback text when tier workspace count failed.', surface: 'owner-console', maxChars: 25 },
  NLSOwnerConsolePlanLimitsServerNote: { text: 'Note: {note}', description: 'Server note in preview.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsolePlanLimitsStaleVersion: { text: 'Plan limits changed in the background. Please preview again.', description: 'Error when store version changed.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsolePlanLimitsRePreviewRequired: { text: 'Draft modified. Please generate a new preview.', description: 'Notice when draft changed after preview.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsolePlanLimitsSuccess: { text: 'Plan limits applied successfully. Action ID: {actionId}', description: 'Success banner text.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsolePlanLimitsViewActivity: { text: 'View in Activity', description: 'Link to activity detail.', surface: 'owner-console', maxChars: 25 },
  NLSOwnerConsolePlanLimitsLoading: { text: 'Loading plan limits...', description: 'Loading state.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsError: { text: 'An error occurred while saving plan limits.', description: 'Generic error message.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsolePlanLimitsApplyFailedNoActionId: { text: 'Apply completed without an action ID.', description: 'Error message when apply succeeds but actionId is missing.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsolePlanLimitsErrorMustBeInteger: { text: 'Must be an integer.', description: 'Validation error when limit input is not an integer.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsErrorMustBeNumber: { text: 'Must be a valid number.', description: 'Validation error when limit input is not a number.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsErrorMustBeNonNegative: { text: 'Must be -1 or greater than or equal to 0.', description: 'Validation error when limit input is negative and not -1.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsolePlanLimitsKey_workspaces: { text: 'Workspaces', description: 'Limit name for workspaces.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsKey_storageGB: { text: 'Storage (GB)', description: 'Limit name for storageGB.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePlanLimitsKey_chatHistoryDays: { text: 'Chat history (days)', description: 'Limit name for chatHistoryDays.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_chatMessageLimit: { text: 'Chat message limit', description: 'Limit name for chatMessageLimit.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_activeIntegrations: { text: 'Active integrations', description: 'Limit name for activeIntegrations.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_meetingsPerMonth: { text: 'Meetings per month', description: 'Limit name for meetingsPerMonth.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_webhookCallsPerMonth: { text: 'Webhook calls per month', description: 'Limit name for webhookCallsPerMonth.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_auditLogRetentionDays: { text: 'Audit log retention (days)', description: 'Limit name for auditLogRetentionDays.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_syncSpeedSeconds: { text: 'Sync speed (seconds)', description: 'Limit name for syncSpeedSeconds.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_aiChatHistories: { text: 'AI chat histories', description: 'Limit name for aiChatHistories.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_aiContextAttachments: { text: 'AI context attachments', description: 'Limit name for aiContextAttachments.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_maxSavedCharts: { text: 'Max saved charts', description: 'Limit name for maxSavedCharts.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_whatsappMonthlyCap: { text: 'WhatsApp monthly cap', description: 'Limit name for whatsappMonthlyCap.', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsolePlanLimitsKey_maxGoalsPerBoard: { text: 'Max goals per board', description: 'Limit name for maxGoalsPerBoard.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsKey_maxTasksPerBoard: { text: 'Max tasks per board', description: 'Limit name for maxTasksPerBoard.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsKey_maxTasksPerGoal: { text: 'Max tasks per goal', description: 'Limit name for maxTasksPerGoal.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsKey_maxSubtasksPerBoard: { text: 'Max subtasks per board', description: 'Limit name for maxSubtasksPerBoard.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsKey_maxSubtasksPerTask: { text: 'Max subtasks per task', description: 'Limit name for maxSubtasksPerTask.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsKey_maxChecklistsPerBoard: { text: 'Max checklists per board', description: 'Limit name for maxChecklistsPerBoard.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsKey_maxChecklistsPerContainer: { text: 'Max checklists per container', description: 'Limit name for maxChecklistsPerContainer.', surface: 'owner-console', maxChars: 45 },
  NLSOwnerConsolePlanLimitsKey_maxWorkspaceMembers: { text: 'Max workspace members', description: 'Limit name for maxWorkspaceMembers.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsKey_maxPendingInvites: { text: 'Max pending invites', description: 'Limit name for maxPendingInvites.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsKey_maxEnabledDashboardViews: { text: 'Max enabled dashboard views', description: 'Limit name for maxEnabledDashboardViews.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsolePlanLimitsKey_maxAiPromptCharacters: { text: 'Max AI prompt characters', description: 'Limit name for maxAiPromptCharacters.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerActivityPartialTitle: { text: 'No matches in this batch', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityPartialBody: { text: 'More history remains. Continue loading to search the remaining records.', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityOutcomeSUCCESS: { text: 'Succeeded', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityOutcomeFAILED: { text: 'Failed', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityOutcomePREVIEW: { text: 'Preview', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityOutcomeUNKNOWN: { text: 'Unknown', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityScopeall: { text: 'Any scope', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityScopeglobal: { text: 'Global', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityScopeworkspace: { text: 'Workspace', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityScopeuser: { text: 'Person', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityScopebreakglass: { text: 'Break-glass', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityScopeunknown: { text: 'Unknown scope', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityEnvsit: { text: 'SIT', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityEnvdev: { text: 'Development', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityEnvprod: { text: 'Production', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityEnvunknown: { text: 'Unknown environment', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind0: { text: 'Grant credits', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind1: { text: 'Grant workspace credits', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind2: { text: 'Grant personal credits', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind3: { text: 'Change plan', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind4: { text: 'Change workspace currency', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind5: { text: 'Suspend workspace', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind6: { text: 'Force usage refresh', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind7: { text: 'Retry failed job', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind8: { text: 'Set maintenance mode', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind9: { text: 'Reset workspace content', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind10: { text: 'Reset gamification for user', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind11: { text: 'Reset chat for workspace', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind12: { text: 'Reset ai history for user', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind13: { text: 'Reset design hub stale', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind14: { text: 'Force onboarding for user', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind15: { text: 'Inspect workspace', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind16: { text: 'Content break glass', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind17: { text: 'Force sign out everywhere', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind18: { text: 'Force password reset', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind19: { text: 'Toggle account disabled', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind20: { text: 'Currency migration auto usd', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind21: { text: 'Support ticket update', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind22: { text: 'Support restriction update', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind23: { text: 'Support ticket internal note', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind24: { text: 'Support ticket reply', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind25: { text: 'Support canned reply upsert', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind26: { text: 'Support canned reply delete', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind27: { text: 'Support ticket merge', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind28: { text: 'Support macro upsert', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind29: { text: 'Support macro run', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind30: { text: 'Revert owner action', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKind31: { text: 'Export activity', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKindCohortUpsert: { text: 'Upsert cohort', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKindCohortArchive: { text: 'Archive cohort', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKindCohortPreviewMembers: { text: 'Preview cohort members', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKindFlagUpdate: { text: 'Update flag', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityKindSetPlanLimits: { text: 'Set plan limits', description: 'Activity filter or state label.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerActivityExactActor: { text: 'Exact operator ID', description: 'Activity exact actor filter input.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerActivityApplyActor: { text: 'Apply operator', description: 'Apply Activity exact actor filter.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerActivityTitle: { text: 'Activity', description: 'Owner Console Activity page title.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerActivitySubtitle: { text: 'Founder-only history of administrative actions and previews.', description: 'Owner Console Activity page subtitle.', surface: 'owner-console', maxChars: 90 },
  NLSOwnerActivityViews: { text: 'Built-in views', description: 'Accessible label for Activity built-in views.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerActivityViewAll: { text: 'All', description: 'Activity view showing all actions.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityViewMine: { text: 'My activity · 7 days', description: 'Activity view for the current founder over seven days.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerActivityViewDestructive: { text: 'Destructive · 24 hours', description: 'Activity view for destructive actions over 24 hours.', surface: 'owner-console', maxChars: 34 },
  NLSOwnerActivityViewBreakglass: { text: 'Break-glass reveals', description: 'Activity view for break-glass reveal actions.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerActivityViewFailed: { text: 'Failed actions', description: 'Activity view for failed actions.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerActivityViewReverts: { text: 'Reverts', description: 'Activity view showing reverted actions.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityFilters: { text: 'Activity filters', description: 'Accessible label for Activity filters.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerActivityFrom: { text: 'From date', description: 'Activity date range start label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityTo: { text: 'To date', description: 'Activity date range end label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityActor: { text: 'Actor', description: 'Activity actor filter and detail label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityActorAll: { text: 'Any operator', description: 'Activity actor filter for every operator.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerActivityActorSelf: { text: 'My actions', description: 'Activity actor filter for the signed-in founder.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityKinds: { text: 'Action kinds', description: 'Activity multi-select label for action kinds.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerActivityScope: { text: 'Scope', description: 'Activity scope filter label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityOutcomes: { text: 'Outcomes', description: 'Activity outcome multi-select label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityEnvironments: { text: 'Environments', description: 'Activity environment multi-select label.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerActivityReset: { text: 'Reset filters', description: 'Activity action to clear filters.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerActivityRetry: { text: 'Retry', description: 'Activity action to retry a failed read.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerActivityLoading: { text: 'Loading activity…', description: 'Activity list loading state.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerActivityLoadingDetail: { text: 'Loading action detail…', description: 'Activity detail loading state.', surface: 'owner-console', maxChars: 34 },
  NLSOwnerActivityLoadMore: { text: 'Load more activity', description: 'Activity cursor pagination action.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerActivityLoadError: { text: 'Activity could not be loaded.', description: 'Activity list read error.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerActivityDetailError: { text: 'Action detail could not be loaded.', description: 'Activity exact-detail read error.', surface: 'owner-console', maxChars: 54 },
  NLSOwnerActivityInvalidFilters: { text: 'Invalid Activity filters', description: 'Activity invalid URL filter title.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerActivityEmptyTitle: { text: 'No matching activity', description: 'Activity empty-state title.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerActivityEmptyBody: { text: 'No administrative actions match these filters.', description: 'Activity empty-state body.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerActivityAccessDeniedTitle: { text: 'Founder access required', description: 'Activity access-denied title.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerActivityAccessDeniedBody: { text: 'This Activity surface is limited to the founder role.', description: 'Activity access-denied body.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerActivityColumnAction: { text: 'Action', description: 'Activity table action column.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityColumnActor: { text: 'Actor', description: 'Activity table actor column.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityColumnOutcome: { text: 'Outcome', description: 'Activity table outcome column.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityColumnEnvironment: { text: 'Environment', description: 'Activity table environment column.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityColumnTime: { text: 'Time', description: 'Activity table time column.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityDetail: { text: 'Action detail', description: 'Accessible label for Activity detail.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerActivityCloseDetail: { text: 'Close detail', description: 'Activity action to close exact detail.', surface: 'owner-console', maxChars: 22 },
  NLSOwnerActivitySubject: { text: 'Subject', description: 'Activity detail subject label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityTime: { text: 'Time', description: 'Activity detail time label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityEnvironment: { text: 'Environment', description: 'Activity detail environment label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityReason: { text: 'Reason', description: 'Activity detail reason label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityError: { text: 'Error', description: 'Activity detail error label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityActionId: { text: 'Action ID', description: 'Activity canonical action ID label.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityStoredId: { text: 'Stored legacy ID', description: 'Activity stored legacy alias label.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerActivityBefore: { text: 'Before', description: 'Activity allowlisted before-state heading.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityAfter: { text: 'After', description: 'Activity allowlisted after-state heading.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityNoSnapshot: { text: 'No allowlisted state was recorded.', description: 'Activity empty administrative snapshot label.', surface: 'owner-console', maxChars: 52 },
  NLSOwnerActivitySignaturePresent: { text: 'A signature is stored.', description: 'Activity signature-presence label without validation claim.', surface: 'owner-console', maxChars: 34 },
  NLSOwnerActivitySignatureMissing: { text: 'No signature is recorded.', description: 'Activity missing-signature label.', surface: 'owner-console', maxChars: 38 },
  NLSOwnerActivityIntegrityUnverified: { text: 'Historical integrity has not been verified by this reader.', description: 'Activity historical integrity limitation.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerActivityRevert: { text: 'Revert', description: 'Activity action button to revert an owner action.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityReverting: { text: 'Reverting…', description: 'Activity action button label while checking revert dry run.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerActivityRevertConfirmTitle: { text: 'Confirm Revert', description: 'Title for confirm revert modal.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerActivityRevertConfirmAction: { text: 'Revert action', description: 'Confirm button label for reverting an action.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerActivityRevertTarget: { text: 'Target', description: 'Target label in revert confirmation.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityRevertBefore: { text: 'Before', description: 'Before label in revert confirmation.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityRevertAfter: { text: 'After', description: 'After label in revert confirmation.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityRevertedBy: { text: 'Reverted by', description: 'Label for link to the revert action.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityReverts: { text: 'Reverts', description: 'Label for link to the original action being reverted.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityRevertError: { text: 'Revert failed.', description: 'Inline error label when revert fails.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerActivityExportCsv: { text: 'Export CSV', description: 'Activity action to export filtered rows as CSV.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityExporting: { text: 'Exporting…', description: 'Activity action label while exporting CSV.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerActivityExportSuccess: { text: 'Exported {count} rows', description: 'Message when CSV export completes.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerActivityExportTruncated: { text: 'First 5,000 matching rows exported', description: 'Message when CSV export is truncated at the cap.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerActivityExportError: { text: 'Export failed.', description: 'Inline error label when export fails.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerActivityHelpOne: { text: 'Owner action writers add rows to the administrative activity collection.', description: 'Activity empty-state explanation one.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerActivityHelpTwo: { text: 'Filters are applied by the founder-only callable over timestamp-indexed rows.', description: 'Activity empty-state explanation two.', surface: 'owner-console', maxChars: 110 },
  NLSOwnerConsoleHomeTicketsTitle: { text: 'Tickets awaiting owner', description: 'Home tile title for tickets awaiting owner.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleHomeSignupsTitle: { text: 'Signups today', description: 'Home tile title for signups today.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHomeBurnTitle: { text: 'Credits burned today', description: 'Home tile title for credits burned today.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleHomeMoneyTitle: { text: 'Money', description: 'Home tile title for Money tile.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleHomeRiskTitle: { text: 'Operational risk', description: 'Home tile title for Operational risk.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHomeRecentActionsTitle: { text: 'Recent owner actions', description: 'Home section title for recent owner actions.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleHomeRecentTicketsTitle: { text: 'Recent tickets', description: 'Home section title for recent support tickets.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHomeTotals: { text: '{workspaces} workspaces · {users} users', description: 'Home totals line showing total workspaces and users.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleHomeAsOf: { text: 'As of {time}', description: 'Home freshness label with viewer timezone time.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHomeModeledChip: { text: 'Modeled', description: 'Chip indicating modeled data.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleHomeLiveChip: { text: 'Live', description: 'Chip indicating live data.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleHomeMrrLabel: { text: 'Modeled MRR', description: 'Label for modeled MRR in Money tile.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHomeBurnMonthLabel: { text: 'Credits burned this month', description: 'Label for credits burned this month in Money tile.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleHomeDeltaVsAvg: { text: '{delta} vs 7d avg', description: 'Delta compared to 7-day average.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHomeBreachedCount: { text: '{count} breached SLA', description: 'Breached SLA indicator in tickets tile.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHomeOpenRisks: { text: '{count} open risks', description: 'Open risks count in risk tile.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHomeFailedAutomations: { text: '{count} failed automations', description: 'Failed automations count in risk tile.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleHomeError: { text: 'Unable to load owner home snapshot.', description: 'Generic error message when snapshot fails to load.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleHomeNoActions: { text: 'No recent owner actions', description: 'Empty state when no recent actions exist.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleHomeNoTickets: { text: 'No recent tickets', description: 'Empty state when no recent tickets exist.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHomeSectionUnavailable: { text: 'Section data unavailable.', description: 'Fallback message when a section is unavailable.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleHomeViewAction: { text: 'View', description: 'Action button to view section target.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleNavCohorts: { text: 'Cohorts', description: 'Owner Console nav item for user cohorts.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsTitle: { text: 'Cohorts', description: 'Header title for Cohorts surface.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsSubtitle: { text: 'Define and manage dynamic user groups for targeting and feature rollouts.', description: 'Header subtitle for Cohorts surface.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleCohortsNewCohort: { text: 'New cohort', description: 'Button to create a new cohort.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsShowArchived: { text: 'Show archived', description: 'Toggle label to show archived cohorts.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsColName: { text: 'Name', description: 'Table column header for cohort name.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsColRules: { text: 'Rules summary', description: 'Table column header for cohort rules summary in words.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsColMembers: { text: 'Members', description: 'Table column header for cohort member count.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsColManual: { text: 'Manual', description: 'Table column header for cohort manual member count.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsColStatus: { text: 'Status', description: 'Table column header for cohort status.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsBuiltInChip: { text: 'Built-in', description: 'Chip indicating a built-in cohort.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleCohortsArchivedChip: { text: 'Archived', description: 'Chip indicating an archived cohort.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleCohortsEmptyTitle: { text: 'No cohorts found', description: 'Empty state title when no cohorts match.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsEmptySubtitle: { text: 'No user cohorts match the current filter.', description: 'Empty state subtitle when no cohorts match.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleCohortsBack: { text: '← Back to cohorts', description: 'Back button to return to cohort registry.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsEditTitle: { text: 'Cohort: {name}', description: 'Title when editing a cohort.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleCohortsCreateTitle: { text: 'New cohort', description: 'Title when creating a cohort.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsReadOnlyNotice: { text: 'Read-only mode. Only founder can modify cohorts.', description: 'Banner for read-only roles.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsoleCohortsNameLabel: { text: 'Cohort name', description: 'Label for cohort name field.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsNamePlaceholder: { text: 'e.g. Beta Power Users', description: 'Placeholder for cohort name.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsDescriptionLabel: { text: 'Description', description: 'Label for cohort description field.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsDescriptionPlaceholder: { text: 'Brief purpose of this cohort', description: 'Placeholder for cohort description.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleCohortsMatchLabel: { text: 'Match criteria', description: 'Label for match type selector.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsMatchAll: { text: 'Match all rules (AND)', description: 'Option to match all rules.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsMatchAny: { text: 'Match any rule (OR)', description: 'Option to match any rule.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsRulesHeading: { text: 'Rule definitions', description: 'Heading for rules list.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsNoRules: { text: 'No rules defined. This cohort matches based on manual members only (or everyone if empty).', description: 'Help text when no rules are present.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleCohortsAddRule: { text: 'Add rule', description: 'Button to add a rule row.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsRemoveRule: { text: 'Remove', description: 'Button to remove a rule row.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleCohortsFieldLabel: { text: 'Field', description: 'Label for rule field dropdown.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsOpLabel: { text: 'Operator', description: 'Label for rule operator dropdown.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsValueLabel: { text: 'Value', description: 'Label for rule value input.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsFieldPlan: { text: 'User plan', description: 'Rule field option: plan.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsFieldSignupAge: { text: 'Signup age (days)', description: 'Rule field option: signupAgeDays.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsFieldLastActive: { text: 'Days since active', description: 'Rule field option: lastActiveDays.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsFieldTickets: { text: 'Open ticket count', description: 'Rule field option: openTicketCount.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsFieldWorkspaces: { text: 'Workspaces owned', description: 'Rule field option: workspacesOwnedCount.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsFieldCredits: { text: 'Personal credits', description: 'Rule field option: personalCredits.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsFieldRestrictions: { text: 'Has restrictions', description: 'Rule field option: hasRestrictions.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsOpEq: { text: 'equals (=)', description: 'Rule op: eq.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsOpIn: { text: 'is one of (in)', description: 'Rule op: in.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsOpLte: { text: 'is at most (≤)', description: 'Rule op: lte.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsOpGte: { text: 'is at least (≥)', description: 'Rule op: gte.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsValueTrue: { text: 'True', description: 'Boolean option true.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleCohortsValueFalse: { text: 'False', description: 'Boolean option false.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleCohortsPlanInPlaceholder: { text: 'e.g. pro, team', description: 'Placeholder for plan in operator.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsManualLabel: { text: 'Manual member emails', description: 'Label for manual emails textarea.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsManualPlaceholder: { text: 'user@example.com (one per line)', description: 'Placeholder for manual emails textarea.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleCohortsManualHelp: { text: 'Users explicitly added to this cohort, one email per line (max 500).', description: 'Help text for manual emails.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleCohortsLiveCount: { text: 'Estimated members: {count}', description: 'Live estimate of cohort size.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsLiveCountEstimating: { text: 'Estimating members…', description: 'Status while debounced estimate is running.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsLiveCountCapped: { text: '5000+', description: 'Capped cohort member count.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsSave: { text: 'Save cohort', description: 'Action button to save cohort.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsSaving: { text: 'Saving…', description: 'Saving state on Save button.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsArchive: { text: 'Archive cohort', description: 'Action button to archive cohort.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsArchiving: { text: 'Archiving…', description: 'Archiving state on button.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsPreview: { text: 'Preview members', description: 'Action button to preview cohort members.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsPreviewing: { text: 'Loading preview…', description: 'Loading state for preview members.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsSaveConfirmTitle: { text: 'Save cohort changes', description: 'Title for save confirmation dialog.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSaveConfirmDesc: { text: 'Specify a reason for saving changes to cohort "{name}".', description: 'Description for save confirmation dialog.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleCohortsArchiveConfirmTitle: { text: 'Archive cohort', description: 'Title for archive confirmation dialog.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsArchiveConfirmDesc: { text: 'Are you sure you want to archive cohort "{name}"? Built-in cohorts cannot be archived.', description: 'Description for archive confirmation dialog.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleCohortsPreviewTitle: { text: 'Cohort members preview (first 50)', description: 'Title for preview modal.', surface: 'owner-console', maxChars: 45 },
  NLSOwnerConsoleCohortsPreviewClose: { text: 'Close preview', description: 'Close button for preview modal.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCohortsPreviewColEmail: { text: 'Email', description: 'Column header for preview email.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsPreviewColPlan: { text: 'Plan', description: 'Column header for preview plan.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsPreviewEmpty: { text: 'No members match this cohort sample.', description: 'Empty state in preview modal.', surface: 'owner-console', maxChars: 45 },
  NLSOwnerConsoleCohortsErrorLoadList: { text: 'Failed to load cohorts.', description: 'Error notification when cohorts list fails.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsErrorLoadDetail: { text: 'Failed to load cohort details.', description: 'Error notification when cohort detail fails.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsErrorEstimate: { text: 'Failed to estimate cohort size.', description: 'Error notification when estimate fails.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsErrorSave: { text: 'Failed to save cohort.', description: 'Error notification when save fails.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsErrorArchive: { text: 'Failed to archive cohort.', description: 'Error notification when archive fails.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsErrorPreview: { text: 'Failed to preview cohort members.', description: 'Error notification when preview fails.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSuccessSave: { text: 'Cohort saved successfully.', description: 'Success notification after saving.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSuccessArchive: { text: 'Cohort archived successfully.', description: 'Success notification after archiving.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSummaryEveryone: { text: 'All users across the platform', description: 'Summary for everyone cohort.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSummaryManualOnly: { text: 'Manual list only ({count} users)', description: 'Summary for manual only cohort.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSummaryAllUsers: { text: 'All users', description: 'Summary for empty rules cohort.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRulePlanEq: { text: 'Plan = {value}', description: 'Summary for plan equals rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRulePlanIn: { text: 'Plan in [{value}]', description: 'Summary for plan in rule.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSummaryRuleSignupAgeLte: { text: 'Signed up ≤ {value} days ago', description: 'Summary for signup age lte rule.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSummaryRuleSignupAgeGte: { text: 'Signed up ≥ {value} days ago', description: 'Summary for signup age gte rule.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSummaryRuleLastActiveLte: { text: 'Active within last {value} days', description: 'Summary for last active lte rule.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSummaryRuleLastActiveGte: { text: 'Inactive for ≥ {value} days', description: 'Summary for last active gte rule.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsSummaryRuleTicketsEq: { text: 'Open tickets = {value}', description: 'Summary for open tickets eq rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleTicketsLte: { text: 'Open tickets ≤ {value}', description: 'Summary for open tickets lte rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleTicketsGte: { text: 'Open tickets ≥ {value}', description: 'Summary for open tickets gte rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleWorkspacesEq: { text: 'Workspaces = {value}', description: 'Summary for workspaces owned eq rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleWorkspacesLte: { text: 'Workspaces ≤ {value}', description: 'Summary for workspaces owned lte rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleWorkspacesGte: { text: 'Workspaces ≥ {value}', description: 'Summary for workspaces owned gte rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleCreditsEq: { text: 'Credits = {value}', description: 'Summary for credits eq rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleCreditsLte: { text: 'Credits ≤ {value}', description: 'Summary for credits lte rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleCreditsGte: { text: 'Credits ≥ {value}', description: 'Summary for credits gte rule.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleRestrictionsTrue: { text: 'Has restrictions', description: 'Summary for hasRestrictions true.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryRuleRestrictionsFalse: { text: 'No restrictions', description: 'Summary for hasRestrictions false.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryMatchAllPrefix: { text: 'All of: ', description: 'Prefix for match all rules.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsSummaryMatchAnyPrefix: { text: 'Any of: ', description: 'Prefix for match any rules.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsSummaryManualSuffix: { text: ' (+ {count} manual)', description: 'Suffix indicating manual user count.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsSummaryNoOneYet: { text: 'No one yet', description: 'Summary for match any cohort with no rules and no manual members.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCohortsFinishRulesToEstimate: { text: 'Finish the rules to see a count', description: 'Notice shown when rules are incomplete.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCohortsPlanFree: { text: 'Free', description: 'Cohort plan option Free.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsPlanEssential: { text: 'Essential', description: 'Cohort plan option Essential.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsPlanGrowth: { text: 'Growth', description: 'Cohort plan option Growth.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsPlanProfessional: { text: 'Professional', description: 'Cohort plan option Professional.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleCohortsPlanEnterprise: { text: 'Enterprise', description: 'Cohort plan option Enterprise.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsTitle: { text: 'Feature Flags', description: 'Header title for Feature Flags surface.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleFlagsSubtitle: { text: 'Manage progressive rollouts, cohort targeting, and runtime gates.', description: 'Header subtitle for Feature Flags surface.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleFlagsColKey: { text: 'Key', description: 'Table column header for flag key.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsColDescription: { text: 'Description', description: 'Table column header for flag description.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsColDefault: { text: 'Default', description: 'Table column header for flag default value.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsColStatus: { text: 'Status', description: 'Table column header for flag status summary.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsColUpdated: { text: 'Last changed', description: 'Table column header for flag last updated time.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsNotEnforcedChip: { text: 'Not enforced', description: 'Chip indicating a flag is not enforced in code.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsOn: { text: 'On', description: 'Boolean flag value On.', surface: 'owner-console', maxChars: 10 },
  NLSOwnerConsoleFlagsOff: { text: 'Off', description: 'Boolean flag value Off.', surface: 'owner-console', maxChars: 10 },
  NLSOwnerConsoleFlagsStatusDefaultOn: { text: 'On for everyone', description: 'Flag status summary when no rules and default is on.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsStatusDefaultOff: { text: 'Off for everyone', description: 'Flag status summary when no rules and default is off.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsStatusRuleFull: { text: '{value} for {cohort}', description: 'Rule status at 100% targeting.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsStatusRulePercent: { text: '{value} for {percent}% of {cohort}', description: 'Rule status at fractional targeting.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleFlagsStatusThenDefault: { text: '{rules}, then {default}', description: 'Rules summary followed by default fallback.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleFlagsNever: { text: 'Never', description: 'Label when flag has never been updated.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsBack: { text: '← Back to flags', description: 'Back button to return to flag registry.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsDetailTitle: { text: 'Flag: {key}', description: 'Header title for flag detail view.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleFlagsKeyLabel: { text: 'Flag key', description: 'Label for flag key field.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleFlagsDescLabel: { text: 'Description', description: 'Label for flag description field.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsDescPlaceholder: { text: 'Flag description', description: 'Placeholder for flag description field.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsDefaultLabel: { text: 'Default value', description: 'Label for flag default value toggle.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsTargetingHeading: { text: 'Targeting rules', description: 'Section heading for flag targeting rules.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsTargetingHelp: { text: 'Rules are evaluated in order. The first rule matching a user determines their flag value.', description: 'Help text for flag targeting rules.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleFlagsNoRules: { text: 'No targeting rules defined. All users receive the default value.', description: 'Notice when no targeting rules are set.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleFlagsAddRule: { text: 'Add rule', description: 'Button to add a targeting rule row.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsRemoveRule: { text: 'Remove', description: 'Button to remove a targeting rule row.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleFlagsMoveUp: { text: 'Move up', description: 'Button to move a targeting rule up in evaluation order.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsMoveDown: { text: 'Move down', description: 'Button to move a targeting rule down in evaluation order.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsRuleCohortLabel: { text: 'Cohort', description: 'Label for rule cohort selector.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsRuleCohortSelect: { text: 'Select a cohort…', description: 'Placeholder for rule cohort dropdown.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsRuleValueLabel: { text: 'Value', description: 'Label for rule value selector.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsRulePercentageLabel: { text: 'Percentage (0–100)', description: 'Label for rule percentage input.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsRuleCohortMissingWarning: { text: 'Cohort is missing or archived. Evaluation skips this rule today; it must be removed or changed before saving.', description: 'Warning when a rule references an archived or nonexistent cohort.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleFlagsNotEnforcedBanner: { text: 'This flag is not enforced in application code yet. Changes will have no client effect until code integration lands.', description: 'Warning banner for unenforced flags.', surface: 'owner-console', maxChars: 130 },
  NLSOwnerConsoleFlagsReadOnlyNotice: { text: 'Read-only mode. Only founder can modify feature flags.', description: 'Banner for read-only roles viewing feature flags.', surface: 'owner-console', maxChars: 70 },
  NLSOwnerConsoleFlagsSave: { text: 'Save flag', description: 'Button to save flag configuration.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsSaving: { text: 'Saving…', description: 'Status while saving flag.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsConfirmTitle: { text: 'Save Feature Flag', description: 'Title for firm confirm dialog when saving flag.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsConfirmDescription: { text: 'Updating targeting rules affects client flag delivery in real time.', description: 'Description in confirm dialog when saving flag.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleFlagsConfirmButton: { text: 'Save changes', description: 'Confirm button label when saving flag.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleFlagsSaveSuccess: { text: 'Feature flag saved successfully.', description: 'Success alert after saving a feature flag.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsErrorLoadList: { text: 'Failed to load feature flags.', description: 'Error message when flag list fails to load.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleFlagsErrorLoadDetail: { text: 'Failed to load feature flag details.', description: 'Error message when flag detail fails to load.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsErrorSave: { text: 'Failed to update feature flag.', description: 'Error message when flag update fails.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleFlagsErrorLoadCohorts: { text: 'Failed to load cohorts for targeting.', description: 'Error message when cohorts fail to load in flag detail.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsEmptyTitle: { text: 'No feature flags found', description: 'Empty state title for feature flags.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsEmptySubtitle: { text: 'No flags in the registry.', description: 'Empty state subtitle for feature flags.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleFlagsKilledChip: { text: 'Killed', description: 'Chip indicating a flag is killed and forced off.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsKillButton: { text: 'Kill switch', description: 'Button to trigger emergency kill switch on flag.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsKillConfirmTitle: { text: 'Kill Feature Flag', description: 'Title for hard confirm dialog when killing flag.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsKillConfirmDesc: { text: 'Killing this flag forces it Off for everyone immediately. Existing rules and targeting will be kept.', description: 'Description when killing a flag.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleFlagsKillSuccess: { text: 'Feature flag killed successfully.', description: 'Success notification after killing flag.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsRestoreButton: { text: 'Restore', description: 'Button to restore a killed flag.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsRestoreConfirmTitle: { text: 'Restore Feature Flag', description: 'Title for hard confirm dialog when restoring flag.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsRestoreConfirmDesc: { text: 'Restoring this flag re-enables rule-based targeting and defaults.', description: 'Description when restoring a flag.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleFlagsRestoreSuccess: { text: 'Feature flag restored successfully.', description: 'Success notification after restoring flag.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsKilledBanner: { text: 'Killed — everyone gets Off. Rules are kept.', description: 'Banner displayed when flag is currently killed.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleFlagsKilledMeta: { text: 'Killed by {by} at {at}. Reason: {reason}', description: 'Audit details on killed banner.', surface: 'owner-console', maxChars: 150 },
  NLSOwnerConsoleFlagsNeedsLabel: { text: 'Needs (Dependencies)', description: 'Field label for flag prerequisites.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsNeedsHelp: { text: 'Select flags that must be On before this flag is evaluated. Max 5 dependencies.', description: 'Help text for flag dependencies field.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleFlagsNeedsChip: { text: 'Needs: {deps}', description: 'Chip indicating prerequisites for a flag.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleFlagsErrorCycle: { text: 'Dependency cycle: {path}', description: 'Error message when flag update creates a dependency cycle.', surface: 'owner-console', maxChars: 150 },
  NLSOwnerConsoleFlagsErrorAction: { text: "Couldn't do that: {message}", description: 'Error message when flag action fails with specific precondition or argument error.', surface: 'owner-console', maxChars: 150 },
  NLSOwnerConsoleFlagsHistoryHeading: { text: 'Version history', description: 'Section heading for flag revision history.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsHistoryHelp: { text: 'Previous versions of targeting rules and kill-switch actions.', description: 'Help text for flag revision history section.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleFlagsHistoryVersion: { text: 'v{version}', description: 'Version number label in flag history row.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsHistoryActionUpdate: { text: 'Update', description: 'Action label for flag update in history.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsHistoryActionKill: { text: 'Kill switch', description: 'Action label for flag kill in history.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsHistoryActionRestore: { text: 'Restore', description: 'Action label for flag restore in history.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsHistoryActionRollback: { text: 'Rollback', description: 'Action label for flag rollback in history.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsHistoryRollbackBtn: { text: 'Roll back to this', description: 'Button to trigger rollback to a historical version.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleFlagsHistoryRollbackTitle: { text: 'Roll Back Feature Flag', description: 'Title for firm confirm when rolling back flag.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsHistoryRollbackDesc: { text: 'Rolling back will restore defaults, rules, and dependencies from version {version}.', description: 'Description when rolling back flag.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleFlagsHistoryRollbackButton: { text: 'Roll back', description: 'Confirm action button for rollback.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsHistoryRollbackSuccess: { text: 'Feature flag rolled back successfully.', description: 'Success notification after rolling back flag.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsHistoryLoadMore: { text: 'Load more', description: 'Button to load older flag history entries.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsHistoryNoEntries: { text: 'No history records found.', description: 'Notice when flag history has no records.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleFlagsHistoryCurrent: { text: 'Current', description: 'Badge for current flag version.', surface: 'owner-console', maxChars: 15 },
  NLSOwnerConsoleFlagsHistoryKilled: { text: 'Killed', description: 'History change summary for kill action.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsHistoryRestored: { text: 'Restored', description: 'History change summary for restore action.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsHistoryRolledBack: { text: 'Rolled back from v{version}', description: 'History change summary for rollback action.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleFlagsHistoryDefaultChanged: { text: 'Default changed: {from} → {to}', description: 'History change summary for default value change.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsHistoryRulesChanged: { text: 'Rules count: {from} → {to}', description: 'History change summary for rules count change.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleFlagsHistoryDepsAdded: { text: 'Added dependencies: {keys}', description: 'History change summary for added dependencies.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleFlagsHistoryDepsRemoved: { text: 'Removed dependencies: {keys}', description: 'History change summary for removed dependencies.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleFlagsHistoryNoChanges: { text: 'No structural changes', description: 'History change summary when no rule/default changes occurred.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleFlagsImpactLoading: { text: 'Estimating impact…', description: 'Loading message during flag impact estimation.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsImpactSummary: { text: 'Would turn on for {turnsOn}, off for {turnsOff} (of {scanned} checked)', description: 'Summary message of impact estimation.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleFlagsImpactDependent: { text: 'Also changes {key}: on {turnsOn}, off {turnsOff}', description: 'Impact preview line for dependent flag.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleFlagsImpactError: { text: "Couldn't estimate impact", description: 'Warning when impact estimation fails.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleFlagsUnknown: { text: 'unknown', description: 'Fallback text for unknown user in flags console.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsColAdoption: { text: 'Adoption', description: 'Column header for feature flag adoption rate in flags list.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsAdoptionTooltip: { text: 'Share of users getting On, from last night\u2019s snapshot (up to 5,000 users)', description: 'Tooltip for adoption column header.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleFlagsAdoptionCapped: { text: '5,000+ users checked', description: 'Badge shown on adoption column header when the latest snapshot was capped.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleFlagsAdoptionSectionHeading: { text: 'Adoption', description: 'Section heading for adoption chart in flag detail.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleFlagsAdoptionSectionHelp: { text: '30-day history of the share of users receiving On.', description: 'Subtext below adoption chart heading.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleFlagsAdoptionEmpty: { text: 'No adoption data yet \u2014 the first snapshot runs tonight at 04:30 UTC.', description: 'Empty state for adoption chart when no data is available.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleFlagsAdoptionError: { text: 'Could not load adoption data.', description: 'Inline error message when adoption data fails to load.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleSettingsFlagsHeading: { text: 'Feature flags', description: 'Section heading for feature flags pointer in Settings.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleSettingsFlagsDescription: { text: 'Global flag evaluation now lives under Feature Flags with cohort targeting.', description: 'Help text explaining feature flags moved to their own panel.', surface: 'owner-console', maxChars: 85 },
  NLSOwnerConsoleSettingsFlagsPointer: { text: 'Feature flags moved \u2014 open Feature Flags', description: 'Label for the button/link that navigates from Settings to the Flags panel.', surface: 'owner-console', maxChars: 60 },

  NLSOwnerActivityHelpThree: { text: 'Historical rows without a valid timestamp cannot appear in ordered list traversal.', description: 'Activity empty-state explanation three.', surface: 'owner-console', maxChars: 110 },
  NLSOwnerConsoleCollapseRail: { text: 'Collapse sidebar', description: 'Owner Console action tooltip to collapse rail.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleExpandRail: { text: 'Expand sidebar', description: 'Owner Console action tooltip to expand rail.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleRefresh: { text: 'Refresh', description: 'Owner Console action button to refresh current view data.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleSignOut: { text: 'Sign out', description: 'Owner Console action button to sign out.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleForceRefresh: { text: 'Force refresh token', description: 'Owner Console action to force refresh auth token.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleCopyUid: { text: 'Copy session UID', description: 'Owner Console action to copy session UID.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleUidCopied: { text: 'Session UID copied', description: 'Owner Console notification when UID is copied.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleBrandSub: { text: 'Owner OS', description: 'Owner Console sidebar subtitle.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleNotBuiltYet: { text: 'Scaffolding & Planned Features', description: 'Header for unbuilt route scaffold placeholder.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleNotBuiltChapter: { text: 'Planned for {chapter}', description: 'Subtitle showing the chapter building this capability.', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleNavPlayground: { text: 'Kit Playground', description: 'Owner Console nav item for Design Kit Playground.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCommandPalette: { text: 'Command Palette', description: 'Owner Console title for command palette.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCommandPalettePlaceholder: { text: 'Type a route, action, or command…', description: 'Placeholder for command palette input.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleRecents: { text: 'Recents', description: 'Label for recents in command palette and header dropdown.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleShortcuts: { text: 'Keyboard Shortcuts', description: 'Title for keyboard shortcuts sheet.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleNoResults: { text: 'No matching commands found', description: 'Empty search result in command palette.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleDataUnavailable: { text: 'Owner data unavailable', description: 'Header badge when all owner data calls fail.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleSourcesFailed: { text: '{failed} of {total} sources failed', description: 'Header badge when some owner data calls fail.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleLoadingData: { text: 'Loading…', description: 'Header badge while data is loading.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleSure: { text: 'Sure?', description: 'Soft confirm question text.', surface: 'owner-console', maxChars: 12 },
  NLSOwnerConsoleYes: { text: 'yes', description: 'Soft confirm yes button.', surface: 'owner-console', maxChars: 10 },
  NLSOwnerConsoleNo: { text: 'no', description: 'Soft confirm no button.', surface: 'owner-console', maxChars: 10 },
  NLSOwnerConsoleDoIt: { text: 'Do it', description: 'Firm confirm action button.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleCancel: { text: 'Cancel', description: 'Confirm cancel button.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleReasonRequired: { text: 'Reason required (min 12 chars)', description: 'Placeholder or hint for mutation reason input.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDensityComfortable: { text: 'Comfortable', description: 'Table density toggle comfortable.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleDensityCompact: { text: 'Compact', description: 'Table density toggle compact.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleAriaTableDensity: { text: 'Table density', description: 'Accessible label for table density toggle group.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleCountItemOne: { text: '1 item', description: 'Table item count singular.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleCountItemMany: { text: '{count} items', description: 'Table item count plural.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleActions: { text: 'Actions', description: 'Table actions column header or label.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsolePageOf: { text: 'Page {current} of {total}', description: 'Table pagination page counter.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsolePrevious: { text: 'Previous', description: 'Table pagination previous button.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleNext: { text: 'Next', description: 'Table pagination next button.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleZeroItems: { text: '0 items', description: 'Empty scaffold item count zero.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleAriaZeroItems: { text: 'Zero items', description: 'Accessible label for empty scaffold zero items.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleHowDataGetsHere: { text: 'How does data get here?', description: 'Empty scaffold collapsible guide title.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleReadArchitectureDocs: { text: 'Read the architecture docs ↗', description: 'Empty scaffold documentation link.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleCardOptions: { text: 'Card options', description: 'Accessible label for card context menu button.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleSearch: { text: 'Search', description: 'Label on command trigger search button.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleAriaCommandPalette: { text: 'Open Command Palette (⌘K)', description: 'Accessible label and tooltip for command palette trigger.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleSomeSourcesFailed: { text: 'Some sources failed', description: 'Fallback tooltip when partial sources fail.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleSourcesPartiallyFailed: { text: 'Sources partially failed', description: 'Fallback label when partial sources fail.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleAllSourcesFailed: { text: 'All owner sources failed', description: 'Tooltip when all owner callables fail.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleSessionRemaining: { text: 'Session: {minutes}m remaining', description: 'Header chip warning when session lease has < 5 min remaining.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleSessionStale: { text: 'Session expired', description: 'Modal title when owner session lease has expired.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleSessionStalePrompt: {
    text: 'Your owner session has reached the maximum lease duration (30 min). Refresh your session to continue working.',
    description: 'Prompt text when session lease is stale.',
    surface: 'owner-console',
    maxChars: 140,
  },
  NLSOwnerConsoleSessionRefresh: { text: 'Refresh session', description: 'Action button to re-authenticate session lease.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleBreakGlassActive: {
    text: 'Break-glass active on {subject} · reason recorded · exits in {time}',
    description: 'Amber banner under header while break-glass is active.',
    surface: 'owner-console',
    maxChars: 120,
  },
  NLSOwnerConsoleBreakGlassEndNow: { text: 'End now', description: 'Button to end break-glass grant immediately.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleBreakGlassExtend: { text: 'Extend +5min', description: 'Button to extend break-glass grant by 5 minutes.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleBreakGlassRevealedBadge: { text: 'Revealed by break-glass', description: 'Badge on content items revealed via break-glass.', surface: 'owner-console', maxChars: 28 },
  NLSOwnerConsoleBreakGlassHiddenPlaceholder: { text: '🔒 Content hidden by default', description: 'Stub tile header when content is locked.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleBreakGlassRevealButton: { text: 'Reveal content — reason required', description: 'Button to trigger break-glass reveal modal.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleBreakGlassModalTitle: { text: 'Break-glass content reveal', description: 'Title of the break-glass reason modal.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerConsoleBreakGlassTargetLabel: { text: 'Target', description: 'Label for target subject in break-glass modal.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleBreakGlassReasonLabel: { text: 'Reason', description: 'Label for reason ENUM dropdown in break-glass modal.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleBreakGlassLinkedIdLabel: { text: 'Linked ID', description: 'Label for linked ID input in break-glass modal.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleBreakGlassElaborationLabel: { text: 'Elaboration (optional)', description: 'Label for optional elaboration textarea in break-glass modal.', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleBreakGlassDurationLabel: { text: 'Reveal duration', description: 'Label for reveal duration dropdown in break-glass modal.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleBreakGlassNotifyOwner: { text: 'Notify workspace owner', description: 'Checkbox label to notify workspace owner about break-glass reveal.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleBreakGlassRevealAction: { text: 'Reveal', description: 'Submit button on break-glass modal.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleBreakGlassExtending: { text: 'Extending…', description: 'Button text while break-glass extension is in progress.', surface: 'owner-console', maxChars: 20 },
  NLSLineGoldenPromptEditor: { text: 'Golden prompt library', description: 'Owner Console Line verifier prompt editor title.', surface: 'owner-console', maxChars: 32 },
  NLSLineSeedKnobs: { text: 'Seed knobs', description: 'Owner Console action to queue founder-authored Line knobs.', surface: 'owner-console', maxChars: 22 },
  NLSLineSignalWeights: { text: 'Signal weights', description: 'Owner Console Line learning signal weight controls.', surface: 'owner-console', maxChars: 24 },
  NLSLineClusterKillSwitch: { text: 'Cluster kill switch', description: 'Owner Console Line cluster-level safety control.', surface: 'owner-console', maxChars: 28 },
  NLSLineDigestFrequency: { text: 'Approval digest frequency', description: 'Owner Console Line proposal digest scheduling control.', surface: 'owner-console', maxChars: 34 },
  NLSAiProposalRevisionHint: { text: 'Tell me what to change and I will revise this proposal.', description: 'Hint after requesting an AI Magic proposal revision.', surface: 'ai', maxChars: 90 },
  NLSAiProposalEarlierSummary: {
    text: 'Earlier proposal — {tasks} tasks, {subtasks} subtasks, {checklists} checklist items',
    description: 'Collapsed summary for an earlier AI Magic proposal revision.',
    surface: 'ai',
    maxChars: 110,
  },
  NLSAiPreviewUnavailable: {
    text: 'This preview is no longer available. Send the request again.',
    description: 'Error shown when an AI Magic pending proposal payload cannot be found.',
    surface: 'ai',
    maxChars: 90,
  },
  NLSAiActionBuildIt: { text: 'Build it', description: 'AI Magic action to build a summarized proposal.', surface: 'ai', maxChars: 22 },
  NLSAiActionChangeSomething: { text: 'Change something', description: 'AI Magic action to refine a summarized proposal.', surface: 'ai', maxChars: 36 },
  NLSAiRuleSavedPrefix: { text: 'Saved:', description: 'Prefix for an AI Magic saved user-rule chip.', surface: 'ai', maxChars: 18 },
  NLSAiRuleSavePrompt: { text: 'Save as rule?', description: 'Prompt on an AI Magic detected-rule chip.', surface: 'ai', maxChars: 30 },
  NLSAiActionSaveRule: { text: 'Save rule', description: 'AI Magic action for saving a detected Line user rule.', surface: 'ai', maxChars: 24 },
  NLSAiActionDismiss: { text: 'Dismiss', description: 'AI Magic compact dismiss action.', surface: 'ai', maxChars: 22 },
  NLSAiManageDraftReadyAria: { text: 'Manage draft ready', description: 'Accessible label for the AI Magic ready draft card.', surface: 'ai', maxChars: 40 },
  NLSAiManageDraftReadyText: { text: 'Your Manage draft is ready.', description: 'AI Magic ready draft card body.', surface: 'ai', maxChars: 48 },
  NLSAiActionRefine: { text: 'Refine', description: 'AI Magic action for refining a pending draft.', surface: 'ai', maxChars: 20 },
  NLSAiModeCreate: { text: 'Create', description: 'AI Magic mode tab for creating or planning work.', surface: 'ai', maxChars: 20 },
  NLSAiModeExecute: { text: 'Execute', description: 'AI Magic mode tab for executing or walking through work.', surface: 'ai', maxChars: 22 },
  NLSAiComposerDockLabel: {
    text: 'Ask AI · {mode}',
    description: 'Keyboard dock header label for AI Magic composer. {mode} is resolved to the current mode (Create / Execute).',
    surface: 'ai',
    maxChars: 36,
  },
  NLSAiInterviewDockLabel: { text: 'Answer questions', description: 'Keyboard dock header label shown while the AI interview carousel is active.', surface: 'ai', maxChars: 36 },
  NLSAiModeAria: { text: 'AI Magic mode', description: 'Accessible label for AI Magic mode tabs.', surface: 'ai', maxChars: 34 },
  NLSAiCheckingReplyAria: { text: 'Checking your reply', description: 'Accessible label for the AI Magic off-topic reply checker.', surface: 'ai', maxChars: 42 },
  NLSAiOffTopicMessage: {
    text: "That reply looks unrelated to Line's open question. Drop the question and send your message as a new prompt?",
    description: 'AI Magic off-topic clarification warning.',
    surface: 'ai',
    maxChars: 130,
  },
  NLSAiActionDropSendFresh: { text: 'Drop and send fresh', description: 'AI Magic action for discarding an open clarification and sending a new prompt.', surface: 'ai', maxChars: 42 },
  NLSAiActionTreatAsAnswer: { text: 'Treat as answer', description: 'AI Magic action for treating text as an answer to an open clarification.', surface: 'ai', maxChars: 36 },
  NLSAiAriaEditingTargets: { text: 'Editing targets', description: 'Accessible label for selected AI Magic edit targets.', surface: 'ai', maxChars: 36 },
  NLSAiAriaRemoveItem: { text: 'Remove {label}', description: 'Accessible label for removing an AI Magic selected item. {label} is item content.', surface: 'ai', maxChars: 90 },
  NLSAiListening: { text: 'Listening...', description: 'AI Magic dictation live transcript idle state.', surface: 'ai', maxChars: 28 },
  NLSAiAriaAttachedFiles: { text: 'Attached files', description: 'Accessible label for AI Magic draft attachments.', surface: 'ai', maxChars: 34 },
  NLSAiAriaSuggestedReplies: { text: 'Suggested replies', description: 'Accessible label for AI Magic quick reply controls.', surface: 'ai', maxChars: 38 },
  NLSAiActionSubmit: { text: 'Submit', description: 'AI Magic action for submitting selected quick replies.', surface: 'ai', maxChars: 20 },
  NLSAiComposerChars: { text: '{count} / {max} chars', description: 'AI Magic composer character count. {count} and {max} are formatted numbers.', surface: 'ai', maxChars: 34 },
  NLSAiComposerDataSourceOne: { text: '{count} data source', description: 'Singular AI Magic attached data source count.', surface: 'ai', maxChars: 34 },
  NLSAiComposerDataSourceMany: { text: '{count} data sources', description: 'Plural AI Magic attached data source count.', surface: 'ai', maxChars: 36 },
  NLSAiComposerImageOne: { text: '{count} image', description: 'Singular AI Magic attached image count.', surface: 'ai', maxChars: 26 },
  NLSAiComposerImageMany: { text: '{count} images', description: 'Plural AI Magic attached image count.', surface: 'ai', maxChars: 28 },
  NLSAiComposerEstimatedCreditsPrefix: { text: 'Est.', description: 'AI Magic composer estimated-credit prefix.', surface: 'ai', maxChars: 12 },
  NLSAiComposerCreditsSuffix: { text: 'credits', description: 'AI Magic composer credit count suffix.', surface: 'ai', maxChars: 18 },
  NLSAiComposerPlanModeShort: { text: 'Plan mode', description: 'AI Magic composer compact planning-mode label.', surface: 'ai', maxChars: 26 },
  NLSAiComposerBuildDirect: { text: 'Build direct', description: 'AI Magic composer compact build-direct mode label.', surface: 'ai', maxChars: 28 },
  NLSAiComposerPersonalWallet: { text: 'Personal wallet', description: 'AI Magic composer personal wallet balance label.', surface: 'ai', maxChars: 34 },
  NLSAiComposerWorkspaceBalance: { text: 'Workspace balance', description: 'AI Magic composer workspace balance label.', surface: 'ai', maxChars: 38 },
  NLSAiComposerPersonalWalletTitle: {
    text: 'Personal wallet {credits}',
    description: 'AI Magic composer personal wallet tooltip. {credits} is a formatted credit count.',
    surface: 'ai',
    maxChars: 50,
  },
  NLSAiComposerWorkspaceBalanceTitle: {
    text: 'Workspace {workspace} - Personal {personal}',
    description: 'AI Magic composer workspace balance tooltip. {workspace} and {personal} are formatted credit counts.',
    surface: 'ai',
    maxChars: 80,
  },
  NLSAiComposerTrialExhausted: { text: 'Trial exhausted', description: 'AI Magic composer badge when trial credits are exhausted.', surface: 'ai', maxChars: 34 },
  NLSAiActionStopDictation: { text: 'Stop dictation', description: 'AI Magic action for stopping speech dictation.', surface: 'ai', maxChars: 34 },
  NLSAiActionStartDictation: { text: 'Start dictation', description: 'AI Magic action for starting speech dictation.', surface: 'ai', maxChars: 36 },
  NLSAiTitleListeningTapToStop: { text: 'Listening - tap to stop', description: 'AI Magic dictation button tooltip while listening.', surface: 'ai', maxChars: 44 },
  NLSAiTitleTapToDictate: { text: 'Tap to dictate', description: 'AI Magic dictation button tooltip while idle.', surface: 'ai', maxChars: 34 },
  NLSAiActionStopInFlightGeneration: { text: 'Stop in-flight generation', description: 'Accessible label for stopping an in-flight AI Magic request.', surface: 'ai', maxChars: 52 },
  NLSAiActionSendFollowUpToLine: { text: 'Send follow-up to Line', description: 'Accessible label for sending a follow-up in AI Magic.', surface: 'ai', maxChars: 46 },
  NLSAiActionSendMessageToLine: { text: 'Send message to Line', description: 'Accessible label for sending a message in AI Magic.', surface: 'ai', maxChars: 44 },
  NLSAiActionSendFollowUp: { text: 'Send follow-up', description: 'Tooltip for sending a follow-up in AI Magic.', surface: 'ai', maxChars: 34 },
  NLSAiActionSend: { text: 'Send', description: 'Tooltip for sending a message in AI Magic.', surface: 'ai', maxChars: 16 },
  NLSAiHistoryLimitTitle: { text: 'Delete a chat to start a new one', description: 'AI Magic free history limit dialog title.', surface: 'ai', maxChars: 58 },
  NLSAiHistoryLimitBody: {
    text: 'Your Free plan stores up to 50 AI Magic chats. Deleting a chat is permanent and also removes files uploaded only for that chat.',
    description: 'AI Magic free history limit dialog body.',
    surface: 'ai',
  },
  NLSAiDialogRenameTitle: { text: 'Rename chat', description: 'AI Magic rename chat dialog title.', surface: 'ai', maxChars: 34 },
  NLSAiDialogRenameBody: { text: 'Update how this conversation appears in your AI Magic history.', description: 'AI Magic rename chat dialog body.', surface: 'ai', maxChars: 90 },
  NLSAiDialogChatTitleLabel: { text: 'Chat title', description: 'AI Magic rename chat field label.', surface: 'ai', maxChars: 28 },
  NLSAiDialogChatTitlePlaceholder: { text: 'Name this chat', description: 'AI Magic rename chat field placeholder.', surface: 'ai', maxChars: 34 },
  NLSAiDialogDeleteTitle: { text: 'Delete chat?', description: 'AI Magic delete chat dialog title.', surface: 'ai', maxChars: 34 },
  NLSAiDialogDeleteBody: {
    text: 'This permanently removes the saved conversation and any uploads that belong only to it.',
    description: 'AI Magic delete chat dialog body.',
    surface: 'ai',
    maxChars: 110,
  },
  NLSAiDialogDeleteMeta: { text: 'AI Magic history and chat-owned uploads will be deleted.', description: 'AI Magic delete chat summary metadata.', surface: 'ai', maxChars: 78 },
  NLSAiComposerReplyFreely: { text: 'Or reply freely to Line...', description: 'AI Magic composer placeholder during structured interview mode.', surface: 'ai', maxChars: 46 },
  NLSAiInterviewAttachmentIntro: {
    text: 'I picked up {count} items from your files — keep which?',
    description: 'Line attachment-inventory interview introduction. {count} is the extracted item count.',
    surface: 'ai',
    maxChars: 78,
  },
  NLSAiInterviewAttachmentSelectAll: { text: 'Select all', description: 'Selects every attachment inventory item.', surface: 'ai', maxChars: 24 },
  NLSAiInterviewAttachmentSelectNone: { text: 'Select none', description: 'Clears every attachment inventory selection.', surface: 'ai', maxChars: 24 },
  NLSAiInterviewAttachmentKeepTop20: { text: 'Keep top 20 only', description: 'Keeps only the first twenty attachment inventory items.', surface: 'ai', maxChars: 32 },
  NLSAiInterviewAttachmentSelectFile: { text: 'Select all in {source}', description: 'Selects all extracted items from one attachment source.', surface: 'ai', maxChars: 60 },
  NLSAiInterviewAttachmentSelectNoneFile: { text: 'Select none in {source}', description: 'Clears all extracted items from one attachment source.', surface: 'ai', maxChars: 62 },
  NLSAiInterviewAttachmentShowAll: {
    text: 'Show all {count}',
    description: 'Expands an attachment inventory beyond its initial top forty items. {count} is total extracted items.',
    surface: 'ai',
    maxChars: 32,
  },
  NLSAiInterviewScaleQuestion: {
    text: 'Looks like this could be about {tasks} tasks and {checklists} checklist items. Keep it that size, or narrower?',
    description: 'Line compact interview prompt when the projected draft exceeds the default scale threshold.',
    surface: 'ai',
    maxChars: 130,
  },
  NLSAiInterviewScaleKeepAsIs: { text: 'Keep as is', description: 'Keeps Line projected plan scope unchanged.', surface: 'ai', maxChars: 28 },
  NLSAiInterviewScaleNarrower: { text: 'Narrower (specify)', description: 'Asks for a smaller Line plan scope.', surface: 'ai', maxChars: 38 },
  NLSAiInterviewScaleWider: { text: 'Wider', description: 'Requests a broader Line plan scope.', surface: 'ai', maxChars: 20 },
  NLSAiInterviewAttachmentConflict: {
    text: 'Your files suggest ~{files} items; your prompt hints closer to {prompt}. Which should Line follow?',
    description: 'Line compact interview prompt when attachment scope exceeds the prompt scope.',
    surface: 'ai',
    maxChars: 120,
  },
  NLSAiInterviewAttachmentConflictFiles: { text: 'Files', description: 'Chooses attachment-derived scope during a Line scope conflict.', surface: 'ai', maxChars: 20 },
  NLSAiInterviewAttachmentConflictPrompt: { text: 'Prompt', description: 'Chooses typed-prompt scope during a Line scope conflict.', surface: 'ai', maxChars: 20 },
  NLSAiInterviewAttachmentConflictBlend: { text: 'Blend', description: 'Chooses a blend of typed-prompt and attachment scope.', surface: 'ai', maxChars: 20 },
  NLSAiInterviewAttachmentConflictNarrower: { text: 'Narrower (specify)', description: 'Requests a narrower plan during a Line attachment scope conflict.', surface: 'ai', maxChars: 38 },
  NLSAiInterviewProgress: { text: '{current} of {total}', description: 'Interview question position readout.', surface: 'member', maxChars: 24 },
  NLSAiComposerReplyToOpenQuestion: { text: "Reply to Line's open question...", description: 'AI Magic composer placeholder during a clarification.', surface: 'ai', maxChars: 52 },
  NLSAiComposerFollowUpAssistant: {
    text: 'Follow up on "{snippet}..." - ask more, or dig deeper...',
    description: 'AI Magic composer placeholder for following up on Line output. {snippet} is chat content.',
    surface: 'ai',
    maxChars: 110,
  },
  NLSAiComposerFollowUpUser: {
    text: 'Pick up from "{snippet}..." - Line has not replied yet...',
    description: 'AI Magic composer placeholder for following up before Line replies. {snippet} is chat content.',
    surface: 'ai',
    maxChars: 110,
  },
  NLSAiComposerContinueExecute: {
    text: 'Continue this chat - ask a follow-up or dig deeper...',
    description: 'AI Magic Execute composer placeholder for an existing chat without a snippet.',
    surface: 'ai',
    maxChars: 86,
  },
  NLSAiComposerDescribeExecute: {
    text: 'Describe what you want walked through ("how do I transfer credits to a member?")',
    description: 'AI Magic Execute composer placeholder for a new chat.',
    surface: 'ai',
    maxChars: 100,
  },
  NLSAiComposerRefineAssistant: {
    text: 'Refine or extend "{snippet}..." - add constraints or ask for edits...',
    description: 'AI Magic Create composer placeholder for refining Line output. {snippet} is chat content.',
    surface: 'ai',
    maxChars: 120,
  },
  NLSAiComposerContinueCreate: {
    text: 'Continue this chat - refine the plan, add constraints, or ask for edits...',
    description: 'AI Magic Create composer placeholder for an existing chat without a snippet.',
    surface: 'ai',
    maxChars: 100,
  },
  NLSAiComposerGuidanceForData: {
    text: 'Add any guidance for Line, or send to map the attached data...',
    description: 'AI Magic composer placeholder when data is attached.',
    surface: 'ai',
    maxChars: 90,
  },
  NLSAiComposerImageGuidance: {
    text: 'Tell Line how the attached image(s) should shape the timeline.',
    description: 'AI Magic composer placeholder when images are attached.',
    surface: 'ai',
    maxChars: 90,
  },
  NLSAiComposerDefaultPromptHint: {
    text: 'I want to launch a SaaS by December. First, I need to do user research in October...',
    description: 'AI Magic default composer placeholder example.',
    surface: 'ai',
    maxChars: 110,
  },
  NLSAiToastCouldNotArchiveChat: { text: 'Could not archive the previous chat.', description: 'Toast when AI Magic cannot archive the previous chat.', surface: 'ai', maxChars: 64 },
  NLSAiToastCouldNotRenameChat: { text: 'Could not rename the chat.', description: 'Toast when AI Magic cannot rename a chat.', surface: 'ai', maxChars: 54 },
  NLSAiToastCouldNotDeleteChat: { text: 'Could not delete the chat.', description: 'Toast when AI Magic cannot delete a chat.', surface: 'ai', maxChars: 54 },
  NLSAiSpeechUnavailable: { text: 'Speech recognition is not available in this browser.', description: 'AI Magic speech recognition unavailable error.', surface: 'ai', maxChars: 78 },
  NLSAiSpeechCouldNotStart: { text: 'Could not start speech capture.', description: 'AI Magic speech recognition start failure.', surface: 'ai', maxChars: 58 },
  NLSAiToastAttachmentLimit: {
    text: 'Your plan allows {limit} combined context attachments.',
    description: 'Toast when AI Magic attachment limit is reached. {limit} is the attachment limit.',
    surface: 'ai',
    maxChars: 78,
  },
  NLSAiToastAutomationDraftCreated: { text: 'Automation draft created in Automate.', description: 'Toast after AI Magic creates an Automate draft.', surface: 'ai', maxChars: 58 },
  NLSAiToastSignInToSaveRules: { text: 'Sign in to save AI rules.', description: 'Toast when AI Magic rule saving requires sign-in.', surface: 'ai', maxChars: 52 },
  NLSAiToastRuleSaved: { text: 'Rule saved - Line will apply it from now on.', description: 'Toast after AI Magic saves a detected user rule.', surface: 'ai', maxChars: 68 },
  NLSAiToastAlreadySaved: { text: 'Already saved.', description: 'Toast when an AI Magic detected user rule is already saved.', surface: 'ai', maxChars: 34 },
  NLSAiToastCouldNotSaveRule: { text: 'Could not save that rule. Try again in a moment.', description: 'Toast when AI Magic cannot save a detected user rule.', surface: 'ai', maxChars: 76 },
  NLSAiCitationOpenSource: { text: 'Open source', description: 'Line citation action for opening the cited source.', surface: 'ai', maxChars: 30 },
  NLSAiCitationInferred: { text: 'inferred', description: 'Line citation badge for inferred source connections.', surface: 'ai', maxChars: 22 },
  NLSAiCitationInferredTitle: { text: 'Line inferred this connection', description: 'Tooltip for an inferred Line citation badge.', surface: 'ai', maxChars: 54 },
  NLSAiCitationChipMobileAria: {
    text: 'Citation {index}: {title}. Tap to view source.',
    description: 'Accessible label for a mobile Line citation chip. {index} is a number and {title} is source content.',
    surface: 'ai',
    maxChars: 120,
  },
  NLSAiCitationChipDesktopAria: {
    text: 'Citation {index}: {title}. Click to open source, hover to preview.',
    description: 'Accessible label for a desktop Line citation chip. {index} is a number and {title} is source content.',
    surface: 'ai',
    maxChars: 140,
  },
  NLSReportsTitleReports: { text: 'Reports', description: 'Title for the Reports hub.', surface: 'reports', maxChars: 28 },
  NLSReportsSubtitleAuditExports: { text: 'Audit exports', description: 'Reports hub subtitle.', surface: 'reports', maxChars: 34 },
  NLSReportsActionEvidenceLibrary: { text: 'Evidence Library', description: 'Action to open the evidence library from Reports.', surface: 'reports', maxChars: 34 },
  NLSReportsActionNewReport: { text: 'New report', description: 'Action to create a new report.', surface: 'reports', maxChars: 24 },
  NLSReportsActionRefresh: { text: 'Refresh', description: 'Action to refresh report or audit data.', surface: 'reports', maxChars: 20 },
  NLSReportsActionBack: { text: 'Back', description: 'Back action in report surfaces.', surface: 'reports', maxChars: 18 },
  NLSReportsActionBackToAuditLedger: { text: 'Back to Audit Ledger', description: 'Back action from an audit report preview to the Audit Ledger.', surface: 'reports', maxChars: 42 },
  NLSReportsActionDownload: { text: 'Download', description: 'Action to download a report artifact.', surface: 'reports', maxChars: 22 },
  NLSReportsActionDownloadLatest: { text: 'Download Latest', description: 'Action to download the latest audit report.', surface: 'reports', maxChars: 32 },
  NLSReportsActionGenerateWorkspaceReport: { text: 'Generate Workspace Report', description: 'Action to open workspace report generation.', surface: 'reports', maxChars: 48 },
  NLSReportsActionGenerateReport: { text: 'Generate report', description: 'Action to generate a report.', surface: 'reports', maxChars: 34 },
  NLSReportsActionGenerateAuditPreview: { text: 'Generate Audit Preview', description: 'Action to generate an audit preview.', surface: 'reports', maxChars: 42 },
  NLSReportsActionFinalize: { text: 'Finalize', description: 'Action to finalize an audit report.', surface: 'reports', maxChars: 20 },
  NLSReportsActionFinalizeAndStore: { text: 'Finalize & Store', description: 'Action to finalize and store an audit report.', surface: 'reports', maxChars: 34 },
  NLSReportsActionCopy: { text: 'Copy', description: 'Action to copy a report link or identifier.', surface: 'reports', maxChars: 18 },
  NLSReportsActionCopyLink: { text: 'Copy Link', description: 'Action to copy a report link.', surface: 'reports', maxChars: 22 },
  NLSReportsActionCopyTargetId: { text: 'Copy Target ID', description: 'Action to copy an audit target identifier.', surface: 'reports', maxChars: 32 },
  NLSReportsActionPreview: { text: 'Preview', description: 'Action to preview a report.', surface: 'reports', maxChars: 20 },
  NLSReportsActionViewPreview: { text: 'View Preview', description: 'Action to view a report preview.', surface: 'reports', maxChars: 28 },
  NLSReportsActionRegenerate: { text: 'Regenerate', description: 'Action to regenerate a report.', surface: 'reports', maxChars: 26 },
  NLSReportsActionRename: { text: 'Rename', description: 'Action to rename a report.', surface: 'reports', maxChars: 18 },
  NLSReportsActionRevoke: { text: 'Revoke', description: 'Action to revoke a report.', surface: 'reports', maxChars: 18 },
  NLSReportsActionArchive: { text: 'Archive', description: 'Action to archive an audit report.', surface: 'reports', maxChars: 18 },
  NLSReportsActionUnarchive: { text: 'Unarchive', description: 'Action to unarchive an audit report.', surface: 'reports', maxChars: 22 },
  NLSReportsActionPrevious: { text: 'Previous', description: 'Pagination previous action.', surface: 'reports', maxChars: 22 },
  NLSReportsActionNext: { text: 'Next', description: 'Pagination next action.', surface: 'reports', maxChars: 18 },
  NLSReportsActionSaveToDrive: { text: 'Save to Drive', description: 'Action to save a report artifact to Google Drive.', surface: 'reports', maxChars: 30 },
  NLSReportsActionSync: { text: 'Sync', description: 'Action to sync a report destination.', surface: 'reports', maxChars: 16 },
  NLSReportsActionRephrase: { text: 'Rephrase', description: 'Action to rephrase a report using AI.', surface: 'reports', maxChars: 22 },
  NLSReportsActionClose: { text: 'Close', description: 'Action to close a report or audit dialog.', surface: 'reports', maxChars: 18 },
  NLSReportsActionKeepEditing: { text: 'Keep editing', description: 'Action to keep editing a report draft.', surface: 'reports', maxChars: 30 },
  NLSReportsActionDiscard: { text: 'Discard', description: 'Action to discard a report draft.', surface: 'reports', maxChars: 20 },
  NLSReportsChangeSource: { text: 'Change source', description: 'Button label to change data source for a report block.', surface: 'reports', maxChars: 25 },
  NLSReportsChooseFromLibrary: { text: 'Choose from library', description: 'Button label in change source dialog to open chart library picker.', surface: 'reports', maxChars: 30 },
  NLSReportsActionExport: { text: 'Export', description: 'Action button label to export a report.', surface: 'reports', maxChars: 20 },
  NLSReportsStatusPreparingExport: { text: 'Preparing {format}…', description: 'Progress label while preparing report export.', surface: 'reports', maxChars: 30 },
  NLSReportsErrorExportFailed: { text: 'Could not export report.', description: 'Fallback error text when report export fails.', surface: 'reports', maxChars: 50 },
  NLSReportsSummaryUnavailable: {
    text: 'Summary unavailable for this generated report.',
    description: 'Fallback text when generated report summary cannot be recovered.',
    surface: 'reports',
    maxChars: 70,
  },
  NLSReportsActionConfirmGenerate: { text: 'Confirm & Generate', description: 'Action to confirm spending credits and generate a report.', surface: 'reports', maxChars: 38 },
  NLSReportsActionGenerating: { text: 'Generating', description: 'Busy label while generating report content.', surface: 'reports', maxChars: 24 },
  NLSReportsActionFinalizing: { text: 'Finalizing', description: 'Busy label while finalizing an audit report.', surface: 'reports', maxChars: 24 },
  NLSReportsActionPurging: { text: 'Purging...', description: 'Busy label while purging standard audit logs.', surface: 'reports', maxChars: 24 },
  NLSReportsActionQuickPurgeStandardLogs: { text: 'Quick Purge Standard Logs', description: 'Action to purge standard-retention audit logs.', surface: 'reports', maxChars: 48 },
  NLSReportsActionResetMinimalistCompliance: {
    text: 'Reset Minimalist Compliance',
    description: 'Action to reset audit category settings to minimalist compliance.',
    surface: 'reports',
    maxChars: 52,
  },
  NLSReportsActionOpenReport: { text: 'Open report', description: 'Action to open a legacy report artifact.', surface: 'reports', maxChars: 26 },
  NLSReportsActionAddWorkspace: { text: 'Add workspace', description: 'Report wizard action to add workspace scope.', surface: 'reports', maxChars: 30 },
  NLSReportsActionAddBoard: { text: 'Add board', description: 'Report wizard action to add board scope.', surface: 'reports', maxChars: 24 },
  NLSReportsActionAddGoal: { text: 'Add goal', description: 'Report wizard action to add goal scope.', surface: 'reports', maxChars: 22 },
  NLSReportsActionAddTask: { text: 'Add task', description: 'Report wizard action to add task scope.', surface: 'reports', maxChars: 22 },
  NLSReportsActionRemove: { text: 'Remove', description: 'Report wizard action to remove a selected scope.', surface: 'reports', maxChars: 18 },
  NLSReportsAriaRemoveScope: { text: 'Remove {title}', description: 'Accessible label for removing a selected report scope. {title} is scope content.', surface: 'reports', maxChars: 90 },
  NLSReportsFilterAll: { text: 'All', description: 'Audit filter count fallback when no values are selected.', surface: 'reports', maxChars: 16 },
  NLSReportsLabelLedger: { text: 'Ledger', description: 'Short title for Audit Ledger rail identity.', surface: 'reports', maxChars: 22 },
  NLSReportsLabelComplianceActivity: { text: 'Compliance & activity', description: 'Audit Ledger rail subtitle.', surface: 'reports', maxChars: 42 },
  NLSReportsLabelGovernanceCenter: { text: 'Governance center', description: 'Eyebrow label for Audit Ledger governance surfaces.', surface: 'reports', maxChars: 40 },
  NLSReportsLabelAuditLedger: { text: 'Audit Ledger', description: 'Title for the Audit Ledger.', surface: 'reports', maxChars: 30 },
  NLSReportsLabelLedgerControls: { text: 'Ledger controls', description: 'Toolbar label for Audit Ledger actions.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelFilters: { text: 'Filters', description: 'Label for Audit Ledger filters.', surface: 'reports', maxChars: 22 },
  NLSReportsLabelTime: { text: 'Time', description: 'Audit filter group label for time.', surface: 'reports', maxChars: 16 },
  NLSReportsLabelLastPeriod: { text: 'Last period', description: 'Audit filter fieldset label for presets.', surface: 'reports', maxChars: 28 },
  NLSReportsLabelDateRange: { text: 'Date range', description: 'Audit filter fieldset label for date range.', surface: 'reports', maxChars: 28 },
  NLSReportsLabelScope: { text: 'Scope', description: 'Audit or report scope label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelSearch: { text: 'Search', description: 'Audit search label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelMembers: { text: 'Members', description: 'Audit member filter label.', surface: 'reports', maxChars: 22 },
  NLSReportsLabelItems: { text: 'Items', description: 'Audit item filter label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelActivity: { text: 'Activity', description: 'Audit activity filter label.', surface: 'reports', maxChars: 24 },
  NLSReportsLabelActionType: { text: 'Action type', description: 'Audit action type filter label.', surface: 'reports', maxChars: 28 },
  NLSReportsLabelEntityType: { text: 'Entity type', description: 'Audit entity type filter label.', surface: 'reports', maxChars: 28 },
  NLSReportsLabelExportedReports: { text: 'Exported Reports', description: 'Audit exports section label.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelComplianceVault: { text: 'Compliance vault', description: 'Audit report vault heading.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelFinalizedReports: { text: 'Finalized reports', description: 'Audit vault finalized reports heading.', surface: 'reports', maxChars: 36 },
  NLSReportsLabelRawExportJobs: { text: 'Raw export jobs', description: 'Audit vault raw export jobs heading.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelWorkspaceActivity: { text: 'Workspace activity', description: 'Audit activity feed heading.', surface: 'reports', maxChars: 38 },
  NLSReportsLabelFullLogs: { text: 'Full logs', description: 'Audit full logs label.', surface: 'reports', maxChars: 24 },
  NLSReportsLabelLiveLedger: { text: 'Live ledger', description: 'Audit live ledger label.', surface: 'reports', maxChars: 26 },
  NLSReportsLabelInspect: { text: 'Inspect', description: 'Audit sidecar inspect tab.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelInsights: { text: 'Insights', description: 'Audit sidecar insights tab.', surface: 'reports', maxChars: 20 },
  NLSReportsLabelGovernance: { text: 'Governance', description: 'Audit sidecar governance tab.', surface: 'reports', maxChars: 24 },
  NLSReportsLabelDiagnostics: { text: 'Diagnostics', description: 'Audit sidecar diagnostics tab.', surface: 'reports', maxChars: 26 },
  NLSReportsLabelContextualInspect: { text: 'Contextual inspect', description: 'Audit inspector eyebrow.', surface: 'reports', maxChars: 36 },
  NLSReportsLabelProcessedIntelligence: { text: 'Processed intelligence', description: 'Audit insights eyebrow.', surface: 'reports', maxChars: 42 },
  NLSReportsLabelGovernancePanel: { text: 'Governance panel', description: 'Audit governance panel eyebrow.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelAuditCategories: { text: 'Audit categories', description: 'Audit governance categories heading.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelOperationalDiagnostics: { text: 'Operational diagnostics', description: 'Diagnostics sidecar eyebrow.', surface: 'reports', maxChars: 42 },
  NLSReportsLabelHealthSummaries: { text: 'Health summaries', description: 'Diagnostics sidecar heading.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelAdapter: { text: 'Adapter: {mode}', description: 'Diagnostics adapter mode label. {mode} is a runtime adapter ID.', surface: 'reports', maxChars: 42 },
  NLSReportsLabelSamples: { text: 'Samples', description: 'Diagnostics metric label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelDegraded: { text: 'Degraded', description: 'Diagnostics metric label.', surface: 'reports', maxChars: 20 },
  NLSReportsLabelFailures: { text: 'Failures', description: 'Diagnostics metric label.', surface: 'reports', maxChars: 20 },
  NLSReportsLabelRetries: { text: 'Retries', description: 'Diagnostics metric label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelRetrievalTraces: { text: 'Retrieval traces', description: 'Diagnostics retrieval traces heading.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelAiTraces: { text: 'AI traces', description: 'Diagnostics AI traces heading.', surface: 'reports', maxChars: 24 },
  NLSReportsLabelRecentEvents: { text: 'Recent events', description: 'Diagnostics recent events heading.', surface: 'reports', maxChars: 30 },
  NLSReportsLabelReportPages: { text: 'Report pages', description: 'Accessible label for report pagination.', surface: 'reports', maxChars: 30 },
  NLSReportsLabelReportTitle: { text: 'Report title', description: 'Field label for report title inputs.', surface: 'reports', maxChars: 28 },
  NLSReportsLabelOutputFormat: { text: 'Output format', description: 'Field label for report output format.', surface: 'reports', maxChars: 30 },
  NLSReportsLabelDestination: { text: 'Destination', description: 'Field label for report destination.', surface: 'reports', maxChars: 28 },
  NLSReportsLabelGeneratedInsight: { text: 'Generated insight', description: 'Report drawer generated insight heading.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelRecentExports: { text: 'Recent exports', description: 'Report drawer recent exports heading.', surface: 'reports', maxChars: 32 },
  NLSReportsLabelInsightsReport: { text: 'Insights report', description: 'Report drawer eyebrow.', surface: 'reports', maxChars: 32 },
  NLSReportsLabelMandatoryEstimate: { text: 'Mandatory estimate', description: 'Audit report generator estimate label.', surface: 'reports', maxChars: 36 },
  NLSReportsLabelReadOnlyPreview: { text: 'Read-only preview', description: 'Audit preview eyebrow.', surface: 'reports', maxChars: 36 },
  NLSReportsLabelAuditPreview: { text: 'Audit preview', description: 'Audit report preview eyebrow.', surface: 'reports', maxChars: 30 },
  NLSReportsLabelReportBlueprint: { text: 'Report Blueprint', description: 'Report wizard blueprint sidecar eyebrow.', surface: 'reports', maxChars: 36 },
  NLSReportsAriaReportBlueprint: { text: 'Report blueprint', description: 'Accessible label for the report wizard blueprint sidecar.', surface: 'reports', maxChars: 36 },
  NLSReportsLabelReportProfile: { text: 'Report profile', description: 'Report wizard section title for audience/depth/story.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelScopeSelection: { text: 'Scope selection', description: 'Report wizard section title for report scope.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelReportCategory: { text: 'Report category', description: 'Report wizard section title for category selection.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelTemplateSelection: { text: 'Template selection', description: 'Report wizard section title for template selection.', surface: 'reports', maxChars: 38 },
  NLSReportsLabelAdvancedInstructions: { text: 'Advanced AI instructions', description: 'Report wizard section title for advanced AI instructions.', surface: 'reports', maxChars: 46 },
  NLSReportsLabelFiltersGovernance: { text: 'Filters & governance', description: 'Report wizard section title for filters and governance.', surface: 'reports', maxChars: 38 },
  NLSReportsLabelExportFinalization: { text: 'Export & finalization', description: 'Report wizard section title for export and finalization.', surface: 'reports', maxChars: 42 },
  NLSReportsLabelAudience: { text: 'Audience', description: 'Report wizard audience label.', surface: 'reports', maxChars: 22 },
  NLSReportsLabelDepth: { text: 'Depth', description: 'Report wizard depth label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelStoryShape: { text: 'Story shape', description: 'Report wizard story shape label.', surface: 'reports', maxChars: 28 },
  NLSReportsLabelCreatorAssigneeRole: { text: 'Creator or assignee role', description: 'Report wizard role filter label.', surface: 'reports', maxChars: 42 },
  NLSReportsLabelRiskSeverity: { text: 'Risk severity', description: 'Audit report wizard risk severity label.', surface: 'reports', maxChars: 30 },
  NLSReportsLabelRiskThreshold: { text: 'Risk threshold', description: 'Report wizard risk threshold label.', surface: 'reports', maxChars: 32 },
  NLSReportsLabelAction: { text: 'Action', description: 'Audit inspector action field label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelCategory: { text: 'Category', description: 'Audit inspector category field label.', surface: 'reports', maxChars: 22 },
  NLSReportsLabelActor: { text: 'Actor', description: 'Audit inspector actor field label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelTarget: { text: 'Target', description: 'Audit inspector target field label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelTargetId: { text: 'Target ID', description: 'Audit inspector target ID field label.', surface: 'reports', maxChars: 24 },
  NLSReportsLabelMetadata: { text: 'Metadata', description: 'Audit inspector metadata heading.', surface: 'reports', maxChars: 24 },
  NLSReportsLabelDiffSnapshot: { text: 'Diff snapshot', description: 'Audit inspector diff snapshot heading.', surface: 'reports', maxChars: 32 },
  NLSReportsLabelTotalActions: { text: 'Total actions', description: 'Audit metrics total actions label.', surface: 'reports', maxChars: 30 },
  NLSReportsLabelBlockers: { text: 'Blockers', description: 'Audit metrics blockers label.', surface: 'reports', maxChars: 22 },
  NLSReportsLabelBlockersDelays: { text: 'Blockers/delays', description: 'Audit metrics blockers and delays label.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelVelocityPerDay: { text: 'Velocity/day', description: 'Audit metrics velocity per day label.', surface: 'reports', maxChars: 28 },
  NLSReportsLabelInventoryAccuracy: { text: 'Inventory accuracy', description: 'Audit metrics inventory accuracy label.', surface: 'reports', maxChars: 38 },
  NLSReportsLabelActionTypes: { text: 'Action types', description: 'Audit metrics action-type breakdown heading.', surface: 'reports', maxChars: 30 },
  NLSReportsLabelStory: { text: 'Story', description: 'Report blueprint story label.', surface: 'reports', maxChars: 18 },
  NLSReportsLabelEstimate: { text: 'Estimate', description: 'Report blueprint credit estimate label.', surface: 'reports', maxChars: 22 },
  NLSReportsLabelTemplate: { text: 'Template', description: 'Report wizard readiness template label.', surface: 'reports', maxChars: 24 },
  NLSReportsLabelCredits: { text: 'Credits', description: 'Report wizard readiness credits label.', surface: 'reports', maxChars: 22 },
  NLSReportsLabelCreditEstimate: { text: 'Credit estimate', description: 'Report generation credit estimate label.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelPrivacyCost: { text: 'Privacy & cost', description: 'Report wizard privacy/cost section label.', surface: 'reports', maxChars: 32 },
  NLSReportsLabelPlannedVisuals: { text: 'Planned visuals', description: 'Report wizard planned visuals section label.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelAppendix: { text: 'Appendix', description: 'Generated report appendix heading.', surface: 'reports', maxChars: 22 },
  NLSReportsLabelActions: { text: 'Actions', description: 'Report preview actions heading.', surface: 'reports', maxChars: 20 },
  NLSReportsLabelVersionHistory: { text: 'Version history', description: 'Report preview version history heading.', surface: 'reports', maxChars: 34 },
  NLSReportsLabelAiNarrative: { text: 'AI narrative', description: 'Report preview rephrased AI narrative heading.', surface: 'reports', maxChars: 28 },
  NLSReportsLabelUseExistingData: { text: 'Use Existing Data', description: 'Report preview cache reuse toggle label.', surface: 'reports', maxChars: 34 },
  NLSReportsFieldSearchLedgerPlaceholder: { text: 'Search ledger items', description: 'Placeholder for Audit Ledger search.', surface: 'reports', maxChars: 42 },
  NLSReportsFieldSearchItemsPlaceholder: { text: 'Search goals, tasks, checklists...', description: 'Placeholder for Audit Ledger item search.', surface: 'reports', maxChars: 52 },
  NLSReportsFieldInstructionsPlaceholder: {
    text: 'Refine tone, emphasis, comparisons, or decision criteria...',
    description: 'Report wizard instruction placeholder.',
    surface: 'reports',
    maxChars: 90,
  },
  NLSReportsFieldAuditInstructionsPlaceholder: {
    text: 'Specify compliance standards, exceptions, evidence emphasis, or reviewer questions...',
    description: 'Audit report generator instruction placeholder.',
    surface: 'reports',
    maxChars: 110,
  },
  NLSReportsChooseCategoryFirst: { text: 'Choose a report category first.', description: 'Report wizard empty state before a report category is selected.', surface: 'reports', maxChars: 56 },
  NLSReportsDrawerBody: {
    text: 'Generate a structured JSON intelligence report from the current ledger window. This operation costs 10 credits.',
    description: 'Report drawer body explaining workspace report generation.',
    surface: 'reports',
    maxChars: 130,
  },
  NLSReportsDrawerMobileConfirm: {
    text: 'Mobile confirmation: this call spends 10 credits and can take ~30 seconds. Tap Confirm to proceed.',
    description: 'Mobile report drawer credit confirmation copy.',
    surface: 'reports',
    maxChars: 130,
  },
  NLSReportsActionConfirmCredits: { text: 'Confirm · 10 credits + 30s', description: 'Mobile report drawer confirmation button label.', surface: 'reports', maxChars: 42 },
  NLSReportsActionGenerateReportCredits10: { text: 'Generate report · 10 credits', description: 'Report drawer generate button label.', surface: 'reports', maxChars: 44 },
  NLSReportsActionShowArchived: { text: 'Show archived', description: 'Audit vault archived toggle label.', surface: 'reports', maxChars: 30 },
  NLSReportsInspectEmpty: { text: 'Select an audit event to inspect payloads, diffs, and target references.', description: 'Audit inspector empty state.', surface: 'reports', maxChars: 100 },
  NLSReportsInsightsEmpty: { text: 'Generate a workspace report to populate JSON audit metrics here.', description: 'Audit insights empty state.', surface: 'reports', maxChars: 90 },
  NLSReportsAdminGovernanceRequired: {
    text: 'Admin access is required to change audit governance settings.',
    description: 'Audit governance permission empty state.',
    surface: 'reports',
    maxChars: 90,
  },
  NLSReportsToggleFullAuditEnabled: { text: 'Full audit enabled', description: 'Audit governance full-audit toggle label.', surface: 'reports', maxChars: 34 },
  NLSReportsRetentionIndefinite: { text: 'Indefinite Enterprise retention.', description: 'Audit governance indefinite retention helper.', surface: 'reports', maxChars: 50 },
  NLSReportsRetentionTierDays: { text: '{days}-day tier retention.', description: 'Audit governance tier retention helper. {days} is a number.', surface: 'reports', maxChars: 44 },
  NLSReportsRetentionStandard48: { text: 'Standard retention: 48 hours.', description: 'Audit governance standard retention helper.', surface: 'reports', maxChars: 50 },
  NLSReportsToggleDataLifecycleAlert: { text: 'Data lifecycle alert', description: 'Audit governance lifecycle alert toggle label.', surface: 'reports', maxChars: 40 },
  NLSReportsDialogFilterAuditActivity: { text: 'Filter audit activity', description: 'Audit Ledger filter modal title.', surface: 'reports', maxChars: 42 },
  NLSReportsToggleExcludeCompleted: { text: 'Exclude completed', description: 'Report wizard filter toggle label.', surface: 'reports', maxChars: 38 },
  NLSReportsToggleIncludeArchive: { text: 'Include Archive', description: 'Report wizard filter toggle label.', surface: 'reports', maxChars: 34 },
  NLSReportsToggleIncludeBin: { text: 'Include Bin', description: 'Report wizard filter toggle label.', surface: 'reports', maxChars: 30 },
  NLSReportsToggleIncludeComments: { text: 'Include comments', description: 'Report wizard filter toggle label.', surface: 'reports', maxChars: 38 },
  NLSReportsToggleIncludeAttachmentMetadata: { text: 'Include attachment metadata', description: 'Report wizard filter toggle label.', surface: 'reports', maxChars: 50 },
  NLSReportsToggleUnauthorizedAccess: { text: 'Unauthorized Access', description: 'Audit report wizard risk toggle label.', surface: 'reports', maxChars: 42 },
  NLSReportsToggleDeletionEvents: { text: 'Deletion Events', description: 'Audit report wizard risk toggle label.', surface: 'reports', maxChars: 36 },
  NLSReportsToggleRoleChanges: { text: 'Role Changes', description: 'Audit report wizard risk toggle label.', surface: 'reports', maxChars: 32 },
  NLSReportsToggleIncludeFullWorkspaceHistory: { text: 'Include full workspace history', description: 'Audit report wizard history toggle label.', surface: 'reports', maxChars: 54 },
  NLSReportsSeverityHigh: { text: 'High', description: 'Audit report wizard high severity filter label.', surface: 'reports', maxChars: 18 },
  NLSReportsSeverityMedium: { text: 'Medium', description: 'Audit report wizard medium severity filter label.', surface: 'reports', maxChars: 18 },
  NLSReportsFieldAutoGeneratedTitle: { text: 'Auto-generated title', description: 'Placeholder for report title input.', surface: 'reports', maxChars: 44 },
  NLSReportsFieldAutoGeneratedAuditTitle: { text: 'Auto-generated audit title', description: 'Placeholder for audit report title input.', surface: 'reports', maxChars: 50 },
  NLSReportsAriaAuditDateRange: { text: 'Audit date range', description: 'Accessible label for audit date range picker.', surface: 'reports', maxChars: 34 },
  NLSReportsAriaLedgerScope: { text: 'Ledger scope', description: 'Accessible label for Audit Ledger tabs.', surface: 'reports', maxChars: 28 },
  NLSReportsAriaAuditInspector: { text: 'Audit inspector', description: 'Accessible label for Audit Ledger inspector sheet.', surface: 'reports', maxChars: 32 },
  NLSReportsAriaLedgerTab: { text: 'Ledger tab', description: 'Accessible label for Audit Ledger rail tabs.', surface: 'reports', maxChars: 28 },
  NLSReportsAriaReportPreview: { text: 'Report preview', description: 'Accessible label for report preview pages.', surface: 'reports', maxChars: 30 },
  NLSReportsAriaAuditReportPreview: { text: 'Audit report preview', description: 'Accessible label for audit report preview dialog.', surface: 'reports', maxChars: 40 },
  NLSReportsAriaReportGeneration: { text: 'Report generation', description: 'Accessible label for the report generation wizard.', surface: 'reports', maxChars: 38 },
  NLSReportsAriaReportScopeBreadcrumb: { text: 'Report scope breadcrumb', description: 'Accessible label for report scope breadcrumb.', surface: 'reports', maxChars: 44 },
  NLSReportsAriaReportsBreadcrumb: { text: 'Reports breadcrumb', description: 'Accessible label for Reports breadcrumb.', surface: 'reports', maxChars: 36 },
  NLSReportsAriaLedgerBreadcrumb: { text: 'Ledger breadcrumb', description: 'Accessible label for Ledger breadcrumb.', surface: 'reports', maxChars: 36 },
  NLSReportsAriaCloseReportWizard: { text: 'Close report wizard', description: 'Accessible label for closing the report wizard.', surface: 'reports', maxChars: 40 },
  NLSReportsAriaChooseReportOutputFormat: { text: 'Choose report output format', description: 'Accessible label for output format selector.', surface: 'reports', maxChars: 46 },
  NLSReportsAriaChooseReportDestination: { text: 'Choose report destination', description: 'Accessible label for report destination selector.', surface: 'reports', maxChars: 44 },
  NLSReportsStatusPending: { text: 'Pending', description: 'Report pending status fallback.', surface: 'reports', maxChars: 18 },
  NLSReportsStatusPendingTimestamp: { text: 'Pending timestamp', description: 'Report pending timestamp fallback.', surface: 'reports', maxChars: 34 },
  NLSReportsStatusNever: { text: 'never', description: 'Audit last-updated fallback.', surface: 'reports', maxChars: 16 },
  NLSReportsStatusLoadingAuthenticatedReport: { text: 'Loading authenticated report...', description: 'Audit report loading status.', surface: 'reports', maxChars: 50 },
  NLSReportsStatusLoadingReports: { text: 'Loading reports...', description: 'Loading label for Reports hub.', surface: 'reports', maxChars: 34 },
  NLSReportsStatusLoadingPreview: { text: 'Loading preview...', description: 'Loading label for report preview.', surface: 'reports', maxChars: 34 },
  NLSReportsStatusLoadingActivity: { text: 'Loading activity...', description: 'Loading label for Audit Ledger activity.', surface: 'reports', maxChars: 36 },
  NLSReportsStatusGeneratingReport: { text: 'Generating report...', description: 'Loading label while generating a report.', surface: 'reports', maxChars: 36 },
  NLSReportsStatusGeneratingAuditPreview: { text: 'Generating audit preview...', description: 'Loading label while generating audit preview.', surface: 'reports', maxChars: 44 },
  NLSReportsStatusReportsRestricted: { text: 'Reports generation restricted to Admins', description: 'Message shown when non-admins cannot generate reports.', surface: 'reports', maxChars: 70 },
  NLSReportsStatusFullLogsRequireTier: { text: 'Full Logs require a Full Audit tier.', description: 'Empty state when Full Logs are gated by plan.', surface: 'reports', maxChars: 70 },
  NLSReportsStatusNoActivityMatches: { text: 'No activity matches the current filters.', description: 'Audit Ledger empty state for filtered activity.', surface: 'reports', maxChars: 70 },
  NLSReportsStatusNoFinalizedReports: { text: 'No finalized audit reports.', description: 'Audit vault empty finalized reports state.', surface: 'reports', maxChars: 60 },
  NLSReportsStatusNoRawExportJobs: { text: 'No raw export jobs.', description: 'Audit vault empty raw export jobs state.', surface: 'reports', maxChars: 50 },
  NLSReportsStatusNoReportsYet: { text: 'No reports yet', description: 'Reports hub empty state heading.', surface: 'reports', maxChars: 34 },
  NLSReportsStatusNoReportsBody: { text: 'Generate the first workspace report from live timeline data.', description: 'Reports hub empty state body.', surface: 'reports', maxChars: 86 },
  NLSReportsStatusNoEarlierExportJobs: { text: 'No earlier export jobs.', description: 'Report drawer empty recent exports state.', surface: 'reports', maxChars: 50 },
  NLSReportsStatusNoDiagnostics: { text: 'No bounded diagnostic samples have been recorded in this session.', description: 'Diagnostics empty summary fallback.', surface: 'reports', maxChars: 92 },
  NLSReportsStatusDiagnosticsInitializing: { text: 'Diagnostics are initializing.', description: 'Diagnostics initializing empty state.', surface: 'reports', maxChars: 50 },
  NLSReportsStatusNoSubsystemEvents: { text: 'No events recorded for this subsystem.', description: 'Diagnostics empty subsystem-events state.', surface: 'reports', maxChars: 70 },
  NLSReportsStatusNoScans: { text: 'No scans', description: 'Audit/report inventory scan empty metric label.', surface: 'reports', maxChars: 20 },
  NLSReportsStatusUnknownDate: { text: 'Unknown date', description: 'Report date fallback.', surface: 'reports', maxChars: 26 },
  NLSReportsStatusReportSetupIndex: {
    text: 'Reports are finishing backend setup. The Firestore index is still building; refresh in a few minutes.',
    description: 'Reports hub load error for missing Firestore index.',
    surface: 'reports',
    maxChars: 140,
  },
  NLSReportsStatusReportAccessUnavailable: {
    text: 'Reports access is unavailable for this workspace role or the latest Firestore rules are not active.',
    description: 'Reports hub load error for permission denial.',
    surface: 'reports',
    maxChars: 140,
  },
  NLSReportsStatusReportsCouldNotLoad: { text: 'Reports could not be loaded.', description: 'Reports hub generic load error.', surface: 'reports', maxChars: 60 },
  NLSReportsStatusReportCouldNotLoad: { text: 'Report could not be loaded.', description: 'Report preview generic load error.', surface: 'reports', maxChars: 60 },
  NLSReportsStatusAuditReportNotFound: { text: 'Audit report not found or access is unavailable.', description: 'Audit report missing or inaccessible error.', surface: 'reports', maxChars: 80 },
  NLSReportsStatusAuditReportCouldNotLoad: { text: 'Could not load audit report.', description: 'Audit report generic load error.', surface: 'reports', maxChars: 60 },
  NLSReportsStatusCouldNotQueueAuditExport: { text: 'Could not queue audit export.', description: 'Report drawer queue failure.', surface: 'reports', maxChars: 60 },
  NLSReportsStatusReportGenerationFailed: { text: 'Report generation failed.', description: 'Report wizard generation failure.', surface: 'reports', maxChars: 60 },
  NLSReportsStatusCouldNotFinalizeAuditReport: { text: 'Could not finalize the audit report.', description: 'Audit finalization failure.', surface: 'reports', maxChars: 70 },
  NLSReportsStatusCouldNotLoadDiagnostics: { text: 'Could not load diagnostics.', description: 'Diagnostics load failure.', surface: 'reports', maxChars: 60 },
  NLSReportsStatusCouldNotSubscribeDiagnostics: { text: 'Could not subscribe to diagnostics.', description: 'Diagnostics subscription failure.', surface: 'reports', maxChars: 70 },
  NLSReportsStatusQuickPurgeFailed: { text: 'Quick Purge failed.', description: 'Audit quick purge generic failure.', surface: 'reports', maxChars: 48 },
  NLSReportsStatusDebugPurgeSimulated: { text: 'Debug mode: purged 0 temporary events.', description: 'Debug quick-purge simulated result.', surface: 'reports', maxChars: 70 },
  NLSReportsStatusDebugStandardPurgeSimulated: { text: 'Debug mode: standard log purge simulated.', description: 'Debug standard-purge simulated result.', surface: 'reports', maxChars: 70 },
  NLSReportsAuditErrorSignInAgain: {
    text: 'Sign in again to view the Audit Ledger. Firebase has not provided a secure auth token for this session.',
    description: 'Audit Ledger unauthenticated session recovery message.',
    surface: 'reports',
    maxChars: 140,
  },
  NLSReportsAuditAuthRestoredMissing: {
    text: 'Sign in again to view the Audit Ledger. The local session is present, but Firebase has not restored a secure auth token.',
    description: 'Audit Ledger auth restoration error.',
    surface: 'reports',
    maxChars: 150,
  },
  NLSReportsAuditRetentionMinimalist: { text: 'Minimalist Compliance', description: 'Audit retention mode label for standard retention.', surface: 'reports', maxChars: 36 },
  NLSReportsAuditRetentionEnterprise: { text: 'Enterprise Full Audit', description: 'Audit retention mode label for indefinite retention.', surface: 'reports', maxChars: 38 },
  NLSReportsAuditRetentionDays: { text: '{days}d Full Audit', description: 'Audit retention mode label with day count. {days} is a number.', surface: 'reports', maxChars: 34 },
  NLSReportsAuditTabLiveLedger: { text: 'Live Ledger', description: 'Audit Ledger live tab label.', surface: 'reports', maxChars: 26 },
  NLSReportsAuditTabLiveLedgerDetail: { text: 'Current governed activity', description: 'Audit Ledger live tab detail.', surface: 'reports', maxChars: 46 },
  NLSReportsAuditTabFullLogs: { text: 'Full Logs', description: 'Audit Ledger full logs tab label.', surface: 'reports', maxChars: 22 },
  NLSReportsAuditTabFullLogsDetail: { text: 'Full-history ledger view', description: 'Audit Ledger full logs tab detail.', surface: 'reports', maxChars: 44 },
  NLSReportsAuditRequiresFullAudit: { text: 'Requires Full Audit', description: 'Audit Ledger full audit plan requirement detail.', surface: 'reports', maxChars: 36 },
  NLSReportsAuditTabExportedReports: { text: 'Exported Reports', description: 'Audit Ledger exported reports tab label.', surface: 'reports', maxChars: 34 },
  NLSReportsAuditTabExportedReportsDetail: { text: 'Vault and export jobs', description: 'Audit Ledger exported reports tab detail.', surface: 'reports', maxChars: 42 },
  NLSReportsGeneratedPrefix: { text: 'Generated:', description: 'Prefix before generated report timestamp.', surface: 'reports', maxChars: 22 },
  NLSReportsGeneratedAt: { text: 'Generated {time}', description: 'Generated timestamp label. {time} is a formatted timestamp.', surface: 'reports', maxChars: 50 },
  NLSReportsUpdatedAt: { text: 'Updated {time}', description: 'Audit Ledger last-updated label. {time} is a formatted timestamp.', surface: 'reports', maxChars: 42 },
  NLSReportsDeletedBeyondWindow: {
    text: 'Deleted {count} events beyond the 48-hour window.',
    description: 'Audit purge result for standard retention. {count} is a formatted number.',
    surface: 'reports',
    maxChars: 80,
  },
  NLSReportsDeletedTemporaryEvents: { text: 'Deleted {count} temporary events.', description: 'Audit quick purge result. {count} is a formatted number.', surface: 'reports', maxChars: 60 },
  NLSReportsPageOfEvents: { text: 'Page {page} · {visible} of {total} events', description: 'Audit feed pagination status. Values are formatted numbers.', surface: 'reports', maxChars: 70 },
  NLSReportsPageOfPages: { text: 'Page {page} of {pageCount}', description: 'Reports hub pagination status. Values are formatted numbers.', surface: 'reports', maxChars: 42 },
  NLSReportsCurrentReportsCount: { text: '{count} current reports', description: 'Reports hub current report count. {count} is a formatted number.', surface: 'reports', maxChars: 42 },
  NLSReportsSelectedCount: { text: '{count} selected', description: 'Report wizard selected-scope count. {count} is a formatted number.', surface: 'reports', maxChars: 34 },
  NLSReportsRequired: { text: 'Required', description: 'Report wizard required-field status.', surface: 'reports', maxChars: 24 },
  NLSReportsEstimatedSourceRecords: { text: '{count} estimated source records', description: 'Report wizard header source record estimate. {count} is formatted.', surface: 'reports', maxChars: 56 },
  NLSReportsCreditsChargedSourceRecords: {
    text: '{credits} credits charged · {records} source records',
    description: 'Audit preview footer credit and source record count.',
    surface: 'reports',
    maxChars: 70,
  },
  NLSReportsInstructionBriefCount: {
    text: '{count} characters · brief text is included in the single report-generation AI call.',
    description: 'Report wizard instruction brief character count helper.',
    surface: 'reports',
    maxChars: 100,
  },
  NLSReportsUntitledReport: { text: 'Untitled report', description: 'Report wizard untitled report fallback.', surface: 'reports', maxChars: 34 },
  NLSReportsChooseWorkspaceBoardGoalTask: { text: 'Choose workspace, board, goal, or task', description: 'Report wizard scope row fallback.', surface: 'reports', maxChars: 70 },
  NLSReportsChooseCategory: { text: 'Choose a category', description: 'Report wizard category row fallback.', surface: 'reports', maxChars: 38 },
  NLSReportsChooseTemplate: { text: 'Choose a template', description: 'Report wizard template row fallback.', surface: 'reports', maxChars: 38 },
  NLSReportsCustomInstructionsAdded: { text: 'Custom AI instructions added', description: 'Report wizard summary when custom instructions exist.', surface: 'reports', maxChars: 52 },
  NLSReportsDefaultGenerationLogic: { text: 'Default generation logic', description: 'Report wizard summary when no custom instructions exist.', surface: 'reports', maxChars: 48 },
  NLSReportsOpenItems: { text: 'Open items', description: 'Report wizard filter summary for open items.', surface: 'reports', maxChars: 24 },
  NLSReportsAllStatuses: { text: 'All statuses', description: 'Report wizard filter summary for all statuses.', surface: 'reports', maxChars: 26 },
  NLSReportsRiskSummary: { text: 'Risk {threshold}+', description: 'Report wizard risk threshold summary. {threshold} is a number.', surface: 'reports', maxChars: 22 },
  NLSReportsDownloadDestination: { text: 'Download', description: 'Report wizard local output destination summary.', surface: 'reports', maxChars: 22 },
  NLSReportsSanitizedScanOneCall: {
    text: 'Sanitized scan, one AI generation call, deterministic visuals after scan.',
    description: 'Report wizard privacy/cost assurance.',
    surface: 'reports',
    maxChars: 110,
  },
  NLSReportsVerifiedCredits: {
    text: 'Verified against {credits} available credits',
    description: 'Credit verification label. {credits} is a formatted credit amount.',
    surface: 'reports',
    maxChars: 70,
  },
  NLSReportsAvailableCredits: { text: '{credits} available credits', description: 'Available credits label. {credits} is a formatted credit amount.', surface: 'reports', maxChars: 54 },
  NLSReportsGenerateWithCredits: { text: 'Generate · {credits} credits', description: 'Generate button label with credit estimate. {credits} is a number.', surface: 'reports', maxChars: 42 },
  NLSReportsGeneratePreviewWithCredits: {
    text: 'Generate preview · {credits}',
    description: 'Audit preview button label with credit estimate. {credits} is a number.',
    surface: 'reports',
    maxChars: 46,
  },
  NLSReportsActionGenerateAuditSummary: { text: 'Generate Audit Summary', description: 'Audit summary generation page title.', surface: 'reports', maxChars: 48 },
  NLSReportsLabelAuditSummary: { text: 'Audit summary', description: 'Audit summary breadcrumb label.', surface: 'reports', maxChars: 34 },
  NLSReportsSpendCreditsTitle: { text: 'Spend workspace credits?', description: 'Credit spend confirmation dialog title.', surface: 'reports', maxChars: 44 },
  NLSReportsSpendCreditsBody: {
    text: 'Generating this preview reserves up to {credits} workspace credits. Credits used by successful AI inference are non-refundable even if you do not finalize.',
    description: 'Audit preview credit confirmation body. {credits} is a number.',
    surface: 'reports',
    maxChars: 180,
  },
  NLSReportsDiscardDraftTitle: { text: 'Discard report draft?', description: 'Dirty-leave confirmation title for report wizard.', surface: 'reports', maxChars: 42 },
  NLSReportsDiscardDraftBody: {
    text: 'Your selections are still dirty and will be discarded for this session.',
    description: 'Dirty-leave confirmation body for report wizard.',
    surface: 'reports',
    maxChars: 90,
  },
  NLSReportsRevokeDialogTitle: { text: 'Revoke report?', description: 'Report revoke confirmation title.', surface: 'reports', maxChars: 34 },
  NLSReportsRevokeDialogBody: {
    text: 'The report will disappear from the Hub. Stored artifacts remain available for audit integrity.',
    description: 'Report revoke confirmation body.',
    surface: 'reports',
    maxChars: 120,
  },
  NLSReportsRenameDialogTitle: { text: 'Rename report', description: 'Report rename dialog title.', surface: 'reports', maxChars: 34 },
  NLSReportsCacheReuseBody: { text: 'Reuse the prior sanitized scan to reduce credits.', description: 'Report preview cache reuse helper text.', surface: 'reports', maxChars: 70 },
  NLSReportsStaleDataChanged: { text: 'Data may be stale. Source records changed after generation.', description: 'Report preview stale-source warning.', surface: 'reports', maxChars: 86 },
  NLSReportsStaleCacheOld: { text: 'Data may be stale. The cache is older than 24 hours.', description: 'Report preview old-cache warning.', surface: 'reports', maxChars: 80 },
  NLSReportsRephrasedCredits: {
    text: 'Report rephrased for {credits} credits.',
    description: 'Report preview AI rephrase completion message. {credits} is a number.',
    surface: 'reports',
    maxChars: 60,
  },
  NLSReportsActionCompleted: { text: '{action} completed.', description: 'Report preview generic action completion message. {action} is a local action label.', surface: 'reports', maxChars: 60 },
  NLSReportsActionFailed: { text: '{action} failed.', description: 'Report preview generic action failure message. {action} is a local action label.', surface: 'reports', maxChars: 50 },
  NLSReportsCategoryStatus: { text: 'Status', description: 'Report category label.', surface: 'reports', maxChars: 20 },
  NLSReportsCategoryRoadmap: { text: 'Roadmap', description: 'Report category label.', surface: 'reports', maxChars: 22 },
  NLSReportsCategoryDependency: { text: 'Dependency', description: 'Report category label.', surface: 'reports', maxChars: 26 },
  NLSReportsCategoryBudget: { text: 'Budget', description: 'Report category label.', surface: 'reports', maxChars: 20 },
  NLSReportsCategoryResource: { text: 'Resource', description: 'Report category label.', surface: 'reports', maxChars: 22 },
  NLSReportsCategoryVelocity: { text: 'Velocity', description: 'Report category label.', surface: 'reports', maxChars: 22 },
  NLSReportsCategoryPerformance: { text: 'Performance', description: 'Report category label.', surface: 'reports', maxChars: 28 },
  NLSReportsCategoryCompliance: { text: 'Compliance', description: 'Report category label.', surface: 'reports', maxChars: 26 },
  NLSReportsCategoryUserActivity: { text: 'User Activity', description: 'Report category label.', surface: 'reports', maxChars: 30 },
  NLSReportsCategoryBillableHours: { text: 'Billable Hours', description: 'Report category label for billable hours.', surface: 'reports', maxChars: 30 },
  NLSReportsCategoryTeamHours: { text: 'Team Hours', description: 'Report category label for team hours.', surface: 'reports', maxChars: 24 },
  NLSReportsCategoryPlannedVsActual: { text: 'Planned vs Actual', description: 'Report category label for planned vs actual hours.', surface: 'reports', maxChars: 24 },
  NLSReportsCategoryConstruction: { text: 'Construction', description: 'Report category label for construction reports.', surface: 'reports', maxChars: 24 },
  NLSReportsScheduleBuiltinBillableHours: { text: 'Billable hours', description: 'Built-in report schedule choice for billable hours.', surface: 'reports', maxChars: 30 },
  NLSReportsScheduleBuiltinTeamHours: { text: 'Team hours & utilisation', description: 'Built-in report schedule choice for team hours.', surface: 'reports', maxChars: 35 },
  NLSReportsScheduleBuiltinPlannedVsActual: { text: 'Planned vs actual hours', description: 'Built-in report schedule choice for planned vs actual hours.', surface: 'reports', maxChars: 35 },
  NLSReportsCategoryCustom: { text: 'Custom', description: 'Report category label.', surface: 'reports', maxChars: 20 },
  NLSReportsDepthEssential: { text: 'Essential', description: 'Report depth label.', surface: 'reports', maxChars: 22 },
  NLSReportsDepthPremium: { text: 'Premium', description: 'Report depth label.', surface: 'reports', maxChars: 20 },
  NLSReportsDepthBoardroom: { text: 'Boardroom', description: 'Report depth label.', surface: 'reports', maxChars: 24 },
  NLSReportsDestinationLocal: { text: 'Local download', description: 'Report destination label.', surface: 'reports', maxChars: 30 },
  NLSReportsDestinationDrive: { text: 'Google Drive', description: 'Report destination label.', surface: 'reports', maxChars: 30 },
  NLSReportsDestinationDocs: { text: 'Google Docs', description: 'Report destination label.', surface: 'reports', maxChars: 30 },
  NLSReportsDestinationSheets: { text: 'Google Sheets', description: 'Report destination label.', surface: 'reports', maxChars: 32 },
  NLSReportsDestinationSlides: { text: 'Google Slides', description: 'Report destination label.', surface: 'reports', maxChars: 32 },
  NLSReportsSizeShort: { text: 'Short', description: 'Report size option for short report.', surface: 'reports', maxChars: 20 },
  NLSReportsSizeFull: { text: 'Full', description: 'Report size option for full report.', surface: 'reports', maxChars: 20 },
  NLSReportsSizeSelectorAria: { text: 'Report size', description: 'Aria label for report size selector.', surface: 'reports', maxChars: 30 },
  NLSReportsActionGeneratingProgress: { text: 'Generating…', description: 'Button label while report generation is in progress.', surface: 'reports', maxChars: 30 },
  NLSReportsErrorNotEnoughCredits: { text: 'Not enough credits', description: 'Error message when wallet has insufficient credits.', surface: 'reports', maxChars: 40 },
  NLSReportsTitleInputAria: { text: 'Report title', description: 'Aria label for the editable report title input.', surface: 'reports', maxChars: 30 },
  NLSReportsErrorGenerationFailed: { text: 'Report generation failed.', description: 'Error message when report generation fails.', surface: 'reports', maxChars: 60 },
  NLSReportsChartUnavailable: { text: 'Chart unavailable', description: 'Fallback note displayed when a chart has no visual snapshot.', surface: 'reports', maxChars: 30 },
  NLSReportsReaderDraftNotice: { text: "This report is a draft and hasn't been published yet.", description: 'Notice displayed in reader for unpublished draft reports.', surface: 'reports', maxChars: 70 },
  NLSLinkHubIdentityTitle: { text: 'Link Hub', description: 'Title for the external integrations hub.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubIdentitySubtitle: { text: 'Connect team tools and sync live metadata.', description: 'Subtitle for the external integrations hub.', surface: 'linkhub', maxChars: 70 },
  NLSLinkHubAriaIntegrationCategories: { text: 'Integration categories', description: 'Accessible label for the Link Hub category rail.', surface: 'linkhub', maxChars: 40 },
  NLSLinkHubAriaConnectionDetails: { text: 'Connection details', description: 'Accessible label for the Link Hub connection details sidecar.', surface: 'linkhub', maxChars: 38 },
  NLSLinkHubAriaCloseConnectionDetails: { text: 'Close connection details', description: 'Accessible label for closing the Link Hub connection details sidecar.', surface: 'linkhub', maxChars: 46 },
  NLSLinkHubCategoryAll: { text: 'All', description: 'Link Hub category rail label for all integrations.', surface: 'linkhub', maxChars: 18 },
  NLSLinkHubCategoryProjects: { text: 'Projects', description: 'Link Hub category rail label for project integrations.', surface: 'linkhub', maxChars: 22 },
  NLSLinkHubCategoryDocuments: { text: 'Documents', description: 'Link Hub category rail label for document integrations.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubCategoryCalendar: { text: 'Calendar', description: 'Link Hub category rail label for calendar integrations.', surface: 'linkhub', maxChars: 22 },
  NLSLinkHubCategoryCommunication: { text: 'Communication', description: 'Link Hub category rail label for communication integrations.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubCategoryTitleAll: { text: 'All integrations', description: 'Link Hub main heading when every integration category is visible.', surface: 'linkhub', maxChars: 42 },
  NLSLinkHubCategoryTitleProjects: { text: 'Project integrations', description: 'Link Hub main heading for project integrations.', surface: 'linkhub', maxChars: 46 },
  NLSLinkHubCategoryTitleDocuments: { text: 'Document integrations', description: 'Link Hub main heading for document integrations.', surface: 'linkhub', maxChars: 48 },
  NLSLinkHubCategoryTitleCalendar: { text: 'Calendar integrations', description: 'Link Hub main heading for calendar integrations.', surface: 'linkhub', maxChars: 48 },
  NLSLinkHubCategoryTitleCommunication: { text: 'Communication integrations', description: 'Link Hub main heading for communication integrations.', surface: 'linkhub', maxChars: 54 },
  NLSLinkHubLabelRegistryGroup: { text: 'Registry group', description: 'Small label above a grouped integration category.', surface: 'linkhub', maxChars: 32 },
  NLSLinkHubLabelLiveMetadata: { text: 'Live metadata', description: 'Footer label in the Link Hub rail.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubLabelProvider: { text: 'Provider', description: 'Generic fallback provider label in Link Hub messages.', surface: 'linkhub', maxChars: 22 },
  NLSLinkHubLabelConnectionDetails: { text: 'Connection details', description: 'Small heading label in the Link Hub sidecar.', surface: 'linkhub', maxChars: 36 },
  NLSLinkHubLabelStatus: { text: 'Status', description: 'Metadata label for connection status.', surface: 'linkhub', maxChars: 18 },
  NLSLinkHubLabelLastSync: { text: 'Last sync', description: 'Metadata label for the last successful integration sync.', surface: 'linkhub', maxChars: 22 },
  NLSLinkHubLabelProviderId: { text: 'Provider ID', description: 'Metadata label for the integration provider identifier.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubLabelRegistryPhase: { text: 'Registry phase', description: 'Metadata label for the integration registry phase.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubLabelRuntimeLifecycle: { text: 'Runtime lifecycle', description: 'Label for the runtime lifecycle status panel.', surface: 'linkhub', maxChars: 34 },
  NLSLinkHubLabelAiUsable: { text: 'AI usable', description: 'Runtime fact label when integration data is available to AI features.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubLabelNotAiUsable: { text: 'Not AI usable', description: 'Runtime fact label when integration data is not available to AI features.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubLabelAuthorizedScopes: { text: 'Authorized scopes', description: 'Label for OAuth scopes recorded on a Link Hub connection.', surface: 'linkhub', maxChars: 34 },
  NLSLinkHubIntegrationCountOne: { text: '{count} integration', description: 'Singular integration count in a Link Hub group. {count} is a formatted number.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubIntegrationCountMany: { text: '{count} integrations', description: 'Plural integration count in a Link Hub group. {count} is a formatted number.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubUsageConnectedPending: {
    text: 'connected · {pending} pending',
    description: 'Usage summary for Link Hub active and reserved integrations. {pending} is a number.',
    surface: 'linkhub',
    maxChars: 42,
  },
  NLSLinkHubStatusConnected: { text: 'Connected', description: 'Card status for an active Link Hub connection.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubStatusAwaitingAuth: { text: 'Awaiting auth', description: 'Card status for an OAuth connection waiting for authorization.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubStatusTierLimitReached: { text: 'Tier Limit Reached', description: 'Card status when the workspace plan blocks another integration.', surface: 'linkhub', maxChars: 34 },
  NLSLinkHubStatusConfigurationNeeded: { text: 'Configuration Needed', description: 'Card status when provider infrastructure or secrets are missing.', surface: 'linkhub', maxChars: 36 },
  NLSLinkHubStatusRequiresAuth: { text: 'Requires auth', description: 'Card status when an integration needs authorization.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubStatusChecking: { text: 'Checking...', description: 'Busy status while checking Link Hub readiness.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubRuntimeActive: { text: 'Active', description: 'Runtime lifecycle status for a healthy integration.', surface: 'linkhub', maxChars: 20 },
  NLSLinkHubRuntimeConnecting: { text: 'Connecting', description: 'Runtime lifecycle status while an integration is connecting.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubRuntimeReauthRequired: { text: 'Re-auth required', description: 'Runtime lifecycle status when credentials must be renewed.', surface: 'linkhub', maxChars: 32 },
  NLSLinkHubRuntimeDisconnected: { text: 'Disconnected', description: 'Runtime lifecycle status for a disconnected integration.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubRuntimeDisabled: { text: 'Disabled', description: 'Runtime lifecycle status for an unavailable integration.', surface: 'linkhub', maxChars: 22 },
  NLSLinkHubRuntimeDegraded: { text: 'Degraded', description: 'Runtime lifecycle status when an integration is partly unhealthy.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubRuntimeRateLimited: { text: 'Rate limited', description: 'Runtime lifecycle status when provider calls are rate limited.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubRuntimeFailed: { text: 'Failed', description: 'Runtime lifecycle status when an integration failed.', surface: 'linkhub', maxChars: 20 },
  NLSLinkHubRuntimeUnknown: { text: 'Unknown', description: 'Fallback runtime lifecycle status.', surface: 'linkhub', maxChars: 20 },
  NLSLinkHubRuntimeNoDiagnostics: {
    text: 'This provider has not reported runtime diagnostics yet.',
    description: 'Runtime panel body when no diagnostic summary has arrived.',
    surface: 'linkhub',
    maxChars: 90,
  },
  NLSLinkHubRuntimeInfrastructureMissing: {
    text: 'Provider infrastructure is not configured.',
    description: 'Runtime panel body when provider infrastructure is unavailable.',
    surface: 'linkhub',
    maxChars: 70,
  },
  NLSLinkHubPhaseOne: { text: 'Phase 1', description: 'Registry phase label for launched Link Hub integrations.', surface: 'linkhub', maxChars: 18 },
  NLSLinkHubPhaseTwo: { text: 'Phase 2', description: 'Short registry phase label for roadmap Link Hub integrations.', surface: 'linkhub', maxChars: 18 },
  NLSLinkHubPhaseRoadmap: { text: 'Phase 2 roadmap', description: 'Registry phase label for roadmap Link Hub integrations.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubPhaseTestingEnabled: { text: 'Testing override enabled', description: 'Registry phase label when local testing override makes integrations available.', surface: 'linkhub', maxChars: 42 },
  NLSLinkHubActionManagePlan: { text: 'Manage plan', description: 'Action to open plan management from Link Hub.', surface: 'linkhub', maxChars: 26 },
  NLSLinkHubActionConnect: { text: 'Connect', description: 'Short action label to connect an integration.', surface: 'linkhub', maxChars: 18 },
  NLSLinkHubActionConnectProvider: { text: 'Connect provider', description: 'Sidecar action label to connect the selected provider.', surface: 'linkhub', maxChars: 34 },
  NLSLinkHubActionConnectNamedProvider: { text: 'Connect {name}', description: 'Tooltip for connecting a named provider. {name} is the provider display name.', surface: 'linkhub', maxChars: 70 },
  NLSLinkHubActionReauthenticate: { text: 'Re-authenticate', description: 'Short action label to renew provider authorization.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubActionReauthenticateProvider: { text: 'Re-authenticate provider', description: 'Action label to renew provider authorization.', surface: 'linkhub', maxChars: 42 },
  NLSLinkHubActionOpeningProvider: { text: 'Opening provider...', description: 'Busy label while starting provider authorization.', surface: 'linkhub', maxChars: 36 },
  NLSLinkHubActionOpenDetails: { text: 'Open details', description: 'Footer action hint for an active integration card.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubActionSyncNow: { text: 'Sync now', description: 'Action label to manually sync an integration.', surface: 'linkhub', maxChars: 22 },
  NLSLinkHubActionSyncInProgress: { text: 'Sync in progress...', description: 'Busy label when an integration sync job is active.', surface: 'linkhub', maxChars: 36 },
  NLSLinkHubActionRefreshing: { text: 'Refreshing...', description: 'Busy label while refreshing integration readiness.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubActionForceRefreshReadiness: { text: 'Force refresh readiness', description: 'Action label to manually refresh integration readiness.', surface: 'linkhub', maxChars: 44 },
  NLSLinkHubActionConnectWebhook: { text: 'Connect webhook', description: 'Heading/action label for connecting a generic webhook.', surface: 'linkhub', maxChars: 34 },
  NLSLinkHubActionEncrypting: { text: 'Encrypting...', description: 'Busy label while saving an encrypted webhook secret.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubActionSaveEncryptedWebhook: { text: 'Save encrypted webhook', description: 'Action label to save a generic webhook URL and encrypted secret.', surface: 'linkhub', maxChars: 44 },
  NLSLinkHubActionRevoking: { text: 'Revoking...', description: 'Busy label while revoking integration access.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubActionRevokeAccess: { text: 'Revoke access', description: 'Action label to revoke an integration connection.', surface: 'linkhub', maxChars: 28 },
  NLSLinkHubCardOpenDetailsAria: {
    text: 'Open {name} connection details',
    description: 'Accessible label for opening a provider card. {name} is the provider display name.',
    surface: 'linkhub',
    maxChars: 80,
  },
  NLSLinkHubCardCompleteProviderAuthorization: {
    text: 'Complete provider authorization',
    description: 'Footer hint on a provider card that is waiting for OAuth authorization.',
    surface: 'linkhub',
    maxChars: 50,
  },
  NLSLinkHubCardUpgradePlanToConnect: { text: 'Upgrade plan to connect', description: 'Footer hint when a plan limit blocks a provider card.', surface: 'linkhub', maxChars: 42 },
  NLSLinkHubCardRoadmapIntegration: { text: 'Roadmap integration', description: 'Footer hint for roadmap provider cards.', surface: 'linkhub', maxChars: 34 },
  NLSLinkHubCardSecretsUnavailable: { text: 'Secrets unavailable', description: 'Footer hint when provider secrets are not configured.', surface: 'linkhub', maxChars: 34 },
  NLSLinkHubAdminRequiredManage: {
    text: 'Admin role required to manage integrations',
    description: 'Tooltip when non-admin users cannot manage Link Hub integrations.',
    surface: 'linkhub',
    maxChars: 70,
  },
  NLSLinkHubAdminRequiredTitle: { text: 'Admin required', description: 'Heading for non-admin Link Hub management gate.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubAdminRequiredBody: { text: 'Ask a workspace admin to connect or revoke integrations.', description: 'Body for non-admin Link Hub management gate.', surface: 'linkhub', maxChars: 90 },
  NLSLinkHubAdminRequiredRevoke: { text: 'Admin role required to revoke access', description: 'Tooltip when non-admin users cannot revoke provider access.', surface: 'linkhub', maxChars: 60 },
  NLSLinkHubConfigPhaseTwoBody: { text: 'This provider is listed for a future Link Hub phase.', description: 'Configuration gate body for roadmap integrations.', surface: 'linkhub', maxChars: 90 },
  NLSLinkHubConfigMissingBody: {
    text: 'Provider secrets or runtime settings are missing.',
    description: 'Configuration gate body when launch provider settings are unavailable.',
    surface: 'linkhub',
    maxChars: 80,
  },
  NLSLinkHubInputSigningSecret: { text: 'Signing secret', description: 'Input placeholder for a generic webhook signing secret.', surface: 'linkhub', maxChars: 34 },
  NLSLinkHubInputWebhookUrl: { text: 'Webhook URL', description: 'Accessible label for the generic webhook URL input.', surface: 'linkhub', maxChars: 32 },
  NLSLinkHubRevokeWarningBody: {
    text: 'Revoking access stops future syncs until the provider is connected again.',
    description: 'Warning body before revoking integration access.',
    surface: 'linkhub',
    maxChars: 110,
  },
  NLSLinkHubDialogRevokeTitle: { text: 'Revoke {provider}?', description: 'Dialog title before revoking provider access. {provider} is the provider display name.', surface: 'linkhub', maxChars: 60 },
  NLSLinkHubDialogRevokeMessage: { text: 'This removes stored access for the selected integration.', description: 'Dialog body before revoking integration access.', surface: 'linkhub', maxChars: 90 },
  NLSLinkHubEmptyNoIntegrationsTitle: { text: 'No integrations here', description: 'Empty state heading when a Link Hub category has no integrations.', surface: 'linkhub', maxChars: 40 },
  NLSLinkHubEmptyNoIntegrationsBody: { text: 'Try another category.', description: 'Empty state body when a Link Hub category has no integrations.', surface: 'linkhub', maxChars: 42 },
  NLSLinkHubEmptySelectIntegrationTitle: { text: 'Select an integration', description: 'Sidecar empty state heading before an integration card is selected.', surface: 'linkhub', maxChars: 42 },
  NLSLinkHubEmptySelectIntegrationBody: {
    text: 'Choose a provider to inspect readiness, scopes, and sync state.',
    description: 'Sidecar empty state body before an integration card is selected.',
    surface: 'linkhub',
    maxChars: 90,
  },
  NLSLinkHubEmptyNoScopesRecorded: { text: 'No scopes recorded.', description: 'Empty state when a connection has no recorded OAuth scopes.', surface: 'linkhub', maxChars: 40 },
  NLSLinkHubNoSuccessfulSyncYet: { text: 'No successful sync yet', description: 'Fallback shown when an integration has never synced successfully.', surface: 'linkhub', maxChars: 38 },
  NLSLinkHubSyncJobCompleted: {
    text: 'Job {id} · {status} · {count} changes',
    description: 'Completed sync job status. {id} is a short job ID, {status} is the backend job status, and {count} is a number.',
    surface: 'linkhub',
    maxChars: 70,
  },
  NLSLinkHubSyncJobStatus: { text: 'Job {id} · {status}', description: 'Active sync job status. {id} is a short job ID and {status} is the backend job status.', surface: 'linkhub', maxChars: 46 },
  NLSLinkHubCallbackSuccess: {
    text: '{provider} connected successfully.',
    description: 'Toast after OAuth callback succeeds. {provider} is the provider display name.',
    surface: 'linkhub',
    maxChars: 80,
  },
  NLSLinkHubCallbackError: { text: 'OAuth could not be completed. No connection changes were saved.', description: 'Toast after OAuth callback fails.', surface: 'linkhub', maxChars: 100 },
  NLSLinkHubToastDebugOAuthBlocked: {
    text: 'OAuth redirect disabled while debug bypass is active.',
    description: 'Toast when local debug bypass blocks OAuth redirect.',
    surface: 'linkhub',
    maxChars: 80,
  },
  NLSLinkHubToastCouldNotInitiateOAuth: { text: 'Could not initiate OAuth.', description: 'Toast when OAuth initiation fails without a specific error.', surface: 'linkhub', maxChars: 50 },
  NLSLinkHubToastAccessRevoked: {
    text: '{provider} access revoked.',
    description: 'Toast after provider access is revoked. {provider} is the provider display name.',
    surface: 'linkhub',
    maxChars: 70,
  },
  NLSLinkHubToastCouldNotRevokeProvider: { text: 'Could not revoke provider.', description: 'Toast when revoking provider access fails without a specific error.', surface: 'linkhub', maxChars: 52 },
  NLSLinkHubToastDebugWebhookBlocked: {
    text: 'Webhook save disabled while debug bypass is active.',
    description: 'Toast when local debug bypass blocks webhook saving.',
    surface: 'linkhub',
    maxChars: 80,
  },
  NLSLinkHubToastGenericWebhookConnected: { text: 'Generic webhook connected.', description: 'Toast after a generic webhook is connected.', surface: 'linkhub', maxChars: 52 },
  NLSLinkHubToastCouldNotSaveWebhook: { text: 'Could not save webhook.', description: 'Toast when generic webhook saving fails without a specific error.', surface: 'linkhub', maxChars: 48 },
  NLSLinkHubToastSyncQueued: { text: '{provider} sync queued.', description: 'Toast after a manual sync job is queued. {provider} is the provider display name.', surface: 'linkhub', maxChars: 70 },
  NLSLinkHubToastCouldNotQueueSync: { text: 'Could not queue sync.', description: 'Toast when manual sync fails without a specific error.', surface: 'linkhub', maxChars: 46 },
  NLSLinkHubTeamRequiredEyebrow: { text: 'Team workspace required', description: 'Eyebrow for the Link Hub team workspace requirement gate.', surface: 'linkhub', maxChars: 42 },
  NLSLinkHubUnavailableTitle: { text: 'Link Hub is unavailable', description: 'Title for the Link Hub unavailable gate.', surface: 'linkhub', maxChars: 50 },
  NLSLinkHubTeamRequiredBody: {
    text: 'External integrations are restricted to team workspaces.',
    description: 'Body for the Link Hub team workspace requirement gate.',
    surface: 'linkhub',
    maxChars: 90,
  },
  NLSLinkHubRestrictedEyebrow: { text: 'Restricted', description: 'Eyebrow for the Link Hub permission gate.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubAccessRequiredTitle: { text: 'Link Hub access required', description: 'Title for the Link Hub permission gate.', surface: 'linkhub', maxChars: 50 },
  NLSLinkHubAccessRequiredBody: { text: 'Your workspace permission policy does not allow this page.', description: 'Body for the Link Hub permission gate.', surface: 'linkhub', maxChars: 90 },
  NLSLinkHubEmptyExploreDemo: { text: 'Explore integrations demo', description: 'Link Hub empty state action to open the Integrations Demo public workspace.', surface: 'linkhub', maxChars: 36 },
  NLSLinkHubTabConnections: { text: 'Connections', description: 'Link Hub tab label for the connection catalog and grid.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubTabReports: { text: 'Reports', description: 'Link Hub tab label for integration reports.', surface: 'linkhub', maxChars: 24 },
  NLSLinkHubReportsTitle: { text: 'Integration reports', description: 'Heading for the Link Hub reports tab.', surface: 'linkhub', maxChars: 36 },
  NLSLinkHubReportsSubtitle: { text: 'Sync history, execution cadence, and telemetry per provider.', description: 'Subtitle for the Link Hub reports tab.', surface: 'linkhub', maxChars: 80 },
  NLSLinkHubReportsSyncHistory: { text: 'Sync history', description: 'Heading for the provider sync history section.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubReportsInboundEvents: { text: 'Inbound events', description: 'Heading for the provider inbound events section.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubReportsInboundComingSoon: {
    text: 'Coming soon — inbound receiver in a future release.',
    description: 'Callout for the deferred inbound event receiver.',
    surface: 'linkhub',
    maxChars: 70,
  },
  NLSLinkHubReportsErrorRate: { text: 'Error rate (24 h)', description: 'Heading for the 24-hour error rate telemetry section.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubReportsErrorRateComingSoon: { text: 'Coming soon.', description: 'Callout for deferred error rate analytics.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubReportsNextScheduledRun: { text: 'Next scheduled run', description: 'Heading for the next scheduled sync execution card.', surface: 'linkhub', maxChars: 32 },
  NLSLinkHubReportsCadenceHourly: { text: 'Hourly (:00 UTC)', description: 'Cadence description for automated hourly sync.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubReportsStatusScheduled: { text: 'Scheduled', description: 'Badge indicating automated sync is active and scheduled.', surface: 'linkhub', maxChars: 20 },
  NLSLinkHubReportsStatusPaused: { text: 'Paused (not connected)', description: 'Badge indicating automated sync is paused due to inactive connection.', surface: 'linkhub', maxChars: 36 },
  NLSLinkHubReportsNoHistory: { text: 'No sync history recorded yet for this provider.', description: 'Empty state message when no sync jobs exist for a provider.', surface: 'linkhub', maxChars: 65 },
  NLSLinkHubGenericWebhookComingSoon: {
    text: 'Coming soon — inbound receiver in a future release.',
    description: 'Explicit badge/tooltip for Generic Webhook connection tile.',
    surface: 'linkhub',
    maxChars: 70,
  },
  NLSOwnerIntegrationsAuditTitle: { text: 'Integrations Audit', description: 'Heading for the Owner Console Integrations Audit panel.', surface: 'owner-console', maxChars: 36 },
  NLSOwnerIntegrationsAuditSubtitle: {
    text: 'Cross-workspace connection health, sync diagnostics, and stream telemetry.',
    description: 'Subtitle for Owner Console Integrations Audit.',
    surface: 'owner-console',
    maxChars: 85,
  },
  NLSOwnerIntegrationsInboundStreamComingSoon: {
    text: 'Coming soon — cross-workspace inbound event stream in a future release.',
    description: 'Callout for deferred cross-workspace inbound event stream.',
    surface: 'owner-console',
    maxChars: 85,
  },
  NLSResourcesActionCancel: { text: 'Cancel', description: 'Generic cancel action in resource modules.', surface: 'resources', maxChars: 18 },
  NLSResourcesActionClose: { text: 'Close', description: 'Generic close action in resource modules.', surface: 'resources', maxChars: 18 },
  NLSResourcesActionManagePlan: { text: 'Manage plan', description: 'Opens workspace plan or module settings from resource modules.', surface: 'resources', maxChars: 28 },
  NLSResourcesActionSaving: { text: 'Saving...', description: 'Busy action label while saving resource module changes.', surface: 'resources', maxChars: 24 },
  NLSResourcesBudgetAllMembers: { text: 'All members', description: 'Budget ledger member filter option for every member.', surface: 'resources', maxChars: 28 },
  NLSResourcesBudgetBreaches: { text: 'breaches', description: 'Budget gauge breach count label.', surface: 'resources', maxChars: 18 },
  NLSResourcesBudgetBurnChartAria: { text: 'Daily burn rate chart', description: 'Accessible label for the budget burn-rate chart.', surface: 'resources', maxChars: 42 },
  NLSResourcesBudgetBurnRateTitle: { text: 'Burn rate (net daily spend)', description: 'Budget dashboard burn-rate card title.', surface: 'resources', maxChars: 42 },
  NLSResourcesBudgetBurnWindowAria: { text: 'Burn rate window', description: 'Accessible label for burn-rate window radio controls.', surface: 'resources', maxChars: 36 },
  NLSResourcesBudgetCancelSelect: { text: 'Cancel select', description: 'Budget ledger action to exit row selection mode.', surface: 'resources', maxChars: 28 },
  NLSResourcesBudgetCategoryPrefix: { text: 'Category ·', description: 'Budget ledger category label prefix before the runtime category value.', surface: 'resources', maxChars: 24 },
  NLSResourcesBudgetCumulative: { text: 'Cumulative', description: 'Budget burn-rate chart cumulative legend label.', surface: 'resources', maxChars: 22 },
  NLSResourcesBudgetDailyNet: { text: 'Daily net', description: 'Budget burn-rate chart daily spend legend label.', surface: 'resources', maxChars: 22 },
  NLSResourcesBudgetEmptySummaryBody: {
    text: "Log your first expense from a node's Financials tab. The summary builds itself from there.",
    description: 'Budget dashboard empty summary guidance.',
    surface: 'resources',
    maxChars: 120,
  },
  NLSResourcesBudgetEmptySummaryTitle: { text: 'Budget summary is empty', description: 'Budget dashboard empty summary title.', surface: 'resources', maxChars: 48 },
  NLSResourcesBudgetEntityTypeAria: { text: 'Entity type', description: 'Accessible label for budget ledger entity type filters.', surface: 'resources', maxChars: 28 },
  NLSResourcesBudgetExportCsv: { text: 'Export CSV', description: 'Budget ledger CSV export action.', surface: 'resources', maxChars: 24 },
  NLSResourcesBudgetExportXlsx: { text: 'Export XLSX', description: 'Budget ledger XLSX export action.', surface: 'resources', maxChars: 24 },
  NLSResourcesBudgetFieldEntity: { text: 'Entity', description: 'Budget ledger entity search field label.', surface: 'resources', maxChars: 18 },
  NLSResourcesBudgetFieldFrom: { text: 'From', description: 'Budget ledger from-date field label.', surface: 'resources', maxChars: 18 },
  NLSResourcesBudgetFieldMember: { text: 'Member', description: 'Budget ledger member filter label.', surface: 'resources', maxChars: 18 },
  NLSResourcesBudgetFieldTo: { text: 'To', description: 'Budget ledger to-date field label.', surface: 'resources', maxChars: 18 },
  NLSResourcesBudgetFilterFromAria: { text: 'Filter from date', description: 'Accessible label for budget ledger from-date filter.', surface: 'resources', maxChars: 36 },
  NLSResourcesBudgetFilterToAria: { text: 'Filter to date', description: 'Accessible label for budget ledger to-date filter.', surface: 'resources', maxChars: 36 },
  NLSResourcesBudgetGaugeAria: { text: 'Budget consumed gauge', description: 'Accessible label for budget consumed gauge.', surface: 'resources', maxChars: 40 },
  NLSResourcesBudgetHealthTitle: { text: 'Workspace Budget Health', description: 'Budget dashboard health card title.', surface: 'resources', maxChars: 42 },
  NLSResourcesBudgetLoadingLedger: { text: 'Loading ledger...', description: 'Budget ledger loading label.', surface: 'resources', maxChars: 34 },
  NLSResourcesBudgetLoadingSummary: { text: 'Loading workspace summary...', description: 'Budget dashboard loading label.', surface: 'resources', maxChars: 44 },
  NLSResourcesBudgetMemberFilterAria: { text: 'Budget ledger member filter', description: 'Accessible label for budget ledger member filter.', surface: 'resources', maxChars: 48 },
  NLSResourcesBudgetNoActivityInWindow: { text: 'No budget activity in this window.', description: 'Budget ledger empty filtered state.', surface: 'resources', maxChars: 58 },
  NLSResourcesBudgetNoBudgetSet: { text: 'No budget set', description: 'Budget gauge empty budget denominator label.', surface: 'resources', maxChars: 28 },
  NLSResourcesBudgetNoNodesOverBudget: { text: 'No nodes over budget', description: 'Budget dashboard health card healthy label.', surface: 'resources', maxChars: 36 },
  NLSResourcesBudgetNoRowsToExport: { text: 'No expense rows in the current filter to export.', description: 'Budget ledger export empty notice.', surface: 'resources', maxChars: 80 },
  NLSResourcesBudgetNoSpendInWindow: { text: 'No spend in this window.', description: 'Budget burn-rate empty state.', surface: 'resources', maxChars: 44 },
  NLSResourcesBudgetNoSpendRecorded: { text: 'No spend recorded yet on Goals or Tasks.', description: 'Budget dashboard top spenders empty state.', surface: 'resources', maxChars: 70 },
  NLSResourcesBudgetNodesOverBudget: {
    text: '{count} node{plural} over budget',
    description: 'Budget dashboard over-budget node count. {count} is a number; {plural} is s or empty.',
    surface: 'resources',
    maxChars: 48,
  },
  NLSResourcesBudgetOfAmount: { text: 'of {amount}', description: 'Budget gauge denominator prefix. {amount} is a formatted currency amount.', surface: 'resources', maxChars: 44 },
  NLSResourcesBudgetOffBody: { text: 'An admin can enable Budget from Workspace Settings to Resources.', description: 'Budget disabled module body.', surface: 'resources', maxChars: 90 },
  NLSResourcesBudgetOffTitle: { text: 'Budget is off.', description: 'Budget disabled module title.', surface: 'resources', maxChars: 30 },
  NLSResourcesBudgetOverrides: { text: 'overrides', description: 'Budget gauge override count label.', surface: 'resources', maxChars: 20 },
  NLSResourcesBudgetReverseFailed: { text: 'Bulk reverse failed.', description: 'Budget ledger bulk reverse failure notice.', surface: 'resources', maxChars: 42 },
  NLSResourcesBudgetReversePartial: { text: 'Reversed {reversed}; {failed} failed.', description: 'Budget ledger partial bulk reverse notice. Values are counts.', surface: 'resources', maxChars: 58 },
  NLSResourcesBudgetReverseReasonPlaceholder: { text: 'Shared reason for reversal (required)', description: 'Budget ledger bulk reversal reason placeholder.', surface: 'resources', maxChars: 60 },
  NLSResourcesBudgetReverseReasonRequired: { text: 'Add a reason before reversing.', description: 'Budget ledger validation notice for missing reversal reason.', surface: 'resources', maxChars: 54 },
  NLSResourcesBudgetReverseSelected: { text: 'Reverse selected ({count})', description: 'Budget ledger reverse selected action. {count} is selected row count.', surface: 'resources', maxChars: 42 },
  NLSResourcesBudgetReverseSuccess: { text: 'Reversed {count} entries.', description: 'Budget ledger bulk reverse success notice. {count} is a number.', surface: 'resources', maxChars: 42 },
  NLSResourcesBudgetReversing: { text: 'Reversing', description: 'Budget ledger reverse busy label.', surface: 'resources', maxChars: 22 },
  NLSResourcesBudgetSearchTitlePlaceholder: { text: 'Search title...', description: 'Budget ledger entity title search placeholder.', surface: 'resources', maxChars: 34 },
  NLSResourcesBudgetSelectRows: { text: 'Select rows', description: 'Budget ledger row selection action.', surface: 'resources', maxChars: 28 },
  NLSResourcesBudgetSubtitle: { text: 'Financial planner', description: 'Budget module header subtitle.', surface: 'resources', maxChars: 34 },
  NLSResourcesBudgetTabDashboard: { text: 'Dashboard', description: 'Budget hub dashboard tab label.', surface: 'resources', maxChars: 24 },
  NLSResourcesBudgetTabLedger: { text: 'Ledger', description: 'Budget hub ledger tab label.', surface: 'resources', maxChars: 20 },
  NLSResourcesBudgetTabIntake: { text: 'Intake', description: 'Budget hub helper expense intake tab label.', surface: 'resources', maxChars: 20 },
  NLSResourcesBudgetTabsAria: { text: 'Budget hub sections', description: 'Accessible label for Budget hub tab navigation.', surface: 'resources', maxChars: 40 },
  NLSResourcesBudgetApprovalThresholdAria: { text: 'Expense approval threshold', description: 'Accessible label for expense approval threshold input.', surface: 'resources', maxChars: 40 },
  NLSResourcesBudgetMonthlyLimitAria: { text: 'Monthly limit', description: 'Accessible label for category monthly limit input.', surface: 'resources', maxChars: 24 },
  NLSResourcesBudgetQuarterlyLimitAria: { text: 'Quarterly limit', description: 'Accessible label for category quarterly limit input.', surface: 'resources', maxChars: 24 },
  NLSResourcesBudgetYearlyLimitAria: { text: 'Yearly limit', description: 'Accessible label for category yearly limit input.', surface: 'resources', maxChars: 24 },
  NLSResourcesBudgetDeleteVendorTitle: { text: 'Delete vendor?', description: 'Confirmation dialog title for deleting a budget vendor.', surface: 'resources', maxChars: 32 },
  NLSResourcesBudgetDeleteRecurringTitle: { text: 'Delete recurring rule?', description: 'Confirmation dialog title for deleting a budget recurring rule.', surface: 'resources', maxChars: 40 },
  NLSResourcesBudgetPendingEyebrow: { text: 'Helper intake', description: 'Budget pending helper expense panel eyebrow.', surface: 'resources', maxChars: 30 },
  NLSResourcesBudgetPendingTitle: { text: 'Pending helper expenses', description: 'Budget pending helper expense panel title.', surface: 'resources', maxChars: 42 },
  NLSResourcesBudgetPendingLoading: { text: 'Loading pending expenses', description: 'Budget pending helper expenses loading label.', surface: 'resources', maxChars: 42 },
  NLSResourcesBudgetPendingEmpty: { text: 'No helper receipts are waiting for review.', description: 'Budget pending helper expenses empty state.', surface: 'resources', maxChars: 70 },
  NLSResourcesBudgetPendingLoadFailed: { text: 'Could not load pending helper expenses.', description: 'Budget pending helper expense load failure toast.', surface: 'resources', maxChars: 70 },
  NLSResourcesBudgetPendingApproved: { text: 'Expense approved.', description: 'Budget pending helper expense approval toast.', surface: 'resources', maxChars: 34 },
  NLSResourcesBudgetPendingRejected: { text: 'Expense rejected.', description: 'Budget pending helper expense rejection toast.', surface: 'resources', maxChars: 34 },
  NLSResourcesBudgetPendingReviewFailed: { text: 'Could not review expense.', description: 'Budget pending helper expense review failure toast.', surface: 'resources', maxChars: 54 },
  NLSResourcesBudgetPendingRowMeta: { text: '{helper} - {category}', description: 'Budget pending helper expense row metadata.', surface: 'resources', maxChars: 90 },
  NLSResourcesBudgetPendingOpenReceipt: { text: 'Open receipt', description: 'Budget pending helper expense receipt link.', surface: 'resources', maxChars: 28 },
  NLSResourcesBudgetTitle: { text: 'Budget', description: 'Budget module title.', surface: 'resources', maxChars: 20 },
  NLSResourcesBudgetTopSpendersSubtitle: { text: 'Ranked by absolute spend (top 20)', description: 'Budget dashboard top spenders subtitle.', surface: 'resources', maxChars: 54 },
  NLSResourcesBudgetTopSpendersTitle: { text: 'Top spenders', description: 'Budget dashboard top spenders card title.', surface: 'resources', maxChars: 30 },
  NLSResourcesBudgetWindowTotal: { text: 'Window total: {amount}', description: 'Budget burn-rate total label. {amount} is a formatted currency amount.', surface: 'resources', maxChars: 52 },
  NLSResourcesBudgetWorkspaceRequired: { text: 'Select a workspace to view its budget.', description: 'Budget module no-workspace body.', surface: 'resources', maxChars: 60 },
  NLSResourcesEntityAll: { text: 'All', description: 'Resource entity filter for all entity types.', surface: 'resources', maxChars: 14 },
  NLSResourcesEntityChecklist: { text: 'Checklist', description: 'Resource entity label for checklist items.', surface: 'resources', maxChars: 22 },
  NLSResourcesEntityGoal: { text: 'Goal', description: 'Resource entity label for goals.', surface: 'resources', maxChars: 18 },
  NLSResourcesEntityTask: { text: 'Task', description: 'Resource entity label for tasks.', surface: 'resources', maxChars: 18 },
  NLSResourcesJustNow: { text: 'just now', description: 'Resource module fallback relative timestamp.', surface: 'resources', maxChars: 18 },
  NLSResourcesLoadingEllipsis: { text: 'Loading...', description: 'Generic compact loading label in resource modules.', surface: 'resources', maxChars: 24 },
  NLSResourcesModuleDisabledEyebrow: { text: 'Resource module disabled', description: 'Eyebrow for disabled Stock module gate.', surface: 'resources', maxChars: 42 },
  NLSResourcesQuotaAssignQuota: { text: 'Assign quota', description: 'Quota management create quota action.', surface: 'resources', maxChars: 28 },
  NLSResourcesQuotaAssigneeAria: { text: 'Quota assignee', description: 'Accessible label for quota assignee select.', surface: 'resources', maxChars: 34 },
  NLSResourcesQuotaAttainmentAria: { text: '{name} attainment', description: 'Accessible label for a quota progress gauge. {name} is assignee display text.', surface: 'resources', maxChars: 70 },
  NLSResourcesQuotaCreateFailed: { text: 'Failed to create quota', description: 'Quota creation fallback error.', surface: 'resources', maxChars: 44 },
  NLSResourcesQuotaEndDateAria: { text: 'End date', description: 'Accessible label for quota end date picker.', surface: 'resources', maxChars: 24 },
  NLSResourcesQuotaFieldAssignee: { text: 'Assignee', description: 'Quota assignee field label.', surface: 'resources', maxChars: 22 },
  NLSResourcesQuotaFieldEnd: { text: 'End', description: 'Quota end date field label.', surface: 'resources', maxChars: 16 },
  NLSResourcesQuotaFieldStart: { text: 'Start', description: 'Quota start date field label.', surface: 'resources', maxChars: 18 },
  NLSResourcesQuotaGoToManagement: { text: 'Go to Management', description: 'Quota empty state action to open Management tab.', surface: 'resources', maxChars: 34 },
  NLSResourcesQuotaInProgressStatus: { text: 'In progress', description: 'Quota performance card in-progress status.', surface: 'resources', maxChars: 28 },
  NLSResourcesQuotaInvalidTargetValue: { text: 'Invalid value for {label}', description: 'Quota target validation error. {label} is a target label.', surface: 'resources', maxChars: 60 },
  NLSResourcesQuotaMetStatus: { text: '✓ Met', description: 'Quota met status label.', surface: 'resources', maxChars: 18 },
  NLSResourcesQuotaNoActiveAdminBody: { text: 'Head to Management to assign your first quota.', description: 'Quota leaderboard empty admin body.', surface: 'resources', maxChars: 70 },
  NLSResourcesQuotaNoActiveMemberBody: { text: "Your workspace admins haven't set quotas for this period.", description: 'Quota leaderboard empty member body.', surface: 'resources', maxChars: 90 },
  NLSResourcesQuotaNoActiveQuotas: { text: 'No active quotas yet.', description: 'Quota management empty active list label.', surface: 'resources', maxChars: 44 },
  NLSResourcesQuotaNoActiveQuotasStrong: { text: 'No active quotas yet.', description: 'Quota leaderboard empty state title.', surface: 'resources', maxChars: 44 },
  NLSResourcesQuotaNoArchivedBody: { text: 'Completed and expired quotas land here automatically.', description: 'Quota history empty state body.', surface: 'resources', maxChars: 80 },
  NLSResourcesQuotaNoArchivedTitle: { text: 'No archived quotas.', description: 'Quota history empty state title.', surface: 'resources', maxChars: 40 },
  NLSResourcesQuotaNoMembers: { text: 'No members', description: 'Quota assignee empty option.', surface: 'resources', maxChars: 24 },
  NLSResourcesQuotaOffBody: { text: 'An admin can enable Quota from Workspace Settings to Resources.', description: 'Quota disabled module body.', surface: 'resources', maxChars: 90 },
  NLSResourcesQuotaOffTitle: { text: 'Quota is off.', description: 'Quota disabled module title.', surface: 'resources', maxChars: 28 },
  NLSResourcesQuotaPercentAttainmentAria: {
    text: '{percent}% attainment',
    description: 'Fallback accessible label for quota progress gauge. {percent} is a number.',
    surface: 'resources',
    maxChars: 40,
  },
  NLSResourcesQuotaPickAssignee: { text: 'Pick an assignee', description: 'Quota validation error when no assignee is selected.', surface: 'resources', maxChars: 36 },
  NLSResourcesQuotaSetAtLeastOneTarget: { text: 'Set at least one target value', description: 'Quota validation error when all targets are empty.', surface: 'resources', maxChars: 54 },
  NLSResourcesQuotaSetupFirstBody: {
    text: 'Open the Management tab to assign performance targets for the team.',
    description: 'Quota first setup empty state body.',
    surface: 'resources',
    maxChars: 90,
  },
  NLSResourcesQuotaSetupFirstTitle: { text: 'Set up your first quota.', description: 'Quota first setup empty state title.', surface: 'resources', maxChars: 44 },
  NLSResourcesQuotaSoftDelete: { text: 'Soft-delete', description: 'Quota management soft-delete action.', surface: 'resources', maxChars: 24 },
  NLSResourcesQuotaStartDateAria: { text: 'Start date', description: 'Accessible label for quota start date picker.', surface: 'resources', maxChars: 26 },
  NLSResourcesQuotaSubtitle: { text: 'Usage & limits', description: 'Quota module header subtitle.', surface: 'resources', maxChars: 30 },
  NLSResourcesQuotaTabHistory: { text: 'History', description: 'Quota hub history tab label.', surface: 'resources', maxChars: 20 },
  NLSResourcesQuotaTabLeaderboard: { text: 'Leaderboard', description: 'Quota hub leaderboard tab label.', surface: 'resources', maxChars: 28 },
  NLSResourcesQuotaTabManagement: { text: 'Management', description: 'Quota hub management tab label.', surface: 'resources', maxChars: 28 },
  NLSResourcesQuotaTabRecurring: { text: 'Recurring & templates', description: 'Quota hub recurring templates tab label.', surface: 'resources', maxChars: 32 },
  NLSResourcesQuotaTabAnalytics: { text: 'Analytics', description: 'Quota hub analytics tab label.', surface: 'resources', maxChars: 24 },
  NLSResourcesQuotaTabAudit: { text: 'Audit', description: 'Quota hub audit tab label.', surface: 'resources', maxChars: 20 },
  NLSResourcesQuotaTabsAria: { text: 'Quota hub sections', description: 'Accessible label for Quota hub tab navigation.', surface: 'resources', maxChars: 40 },
  NLSResourcesQuotaTargetChecklists: { text: 'Checklists', description: 'Short quota target label for checklists done.', surface: 'resources', maxChars: 24 },
  NLSResourcesQuotaTargetChecklistsDone: { text: 'Checklists done', description: 'Full quota target label for checklists done.', surface: 'resources', maxChars: 32 },
  NLSResourcesQuotaTargetTasks: { text: 'Tasks', description: 'Short quota target label for completed tasks.', surface: 'resources', maxChars: 18 },
  NLSResourcesQuotaTargetTasksCompleted: { text: 'Tasks completed', description: 'Full quota target label for completed tasks.', surface: 'resources', maxChars: 34 },
  NLSResourcesQuotaTargetUnitsSold: { text: 'Units sold', description: 'Quota target label for sold units.', surface: 'resources', maxChars: 26 },
  NLSResourcesQuotaTargetWeighted: { text: 'Weighted', description: 'Short quota target label for weighted progress.', surface: 'resources', maxChars: 22 },
  NLSResourcesQuotaTargetWeightedProgress: { text: 'Weighted progress', description: 'Full quota target label for weighted progress.', surface: 'resources', maxChars: 36 },
  NLSResourcesQuotaTargetHours: { text: 'Hours', description: 'Short quota target label for hours logged.', surface: 'resources', maxChars: 16 },
  NLSResourcesQuotaTargetHoursLogged: { text: 'Hours logged', description: 'Full quota target label for hours logged.', surface: 'resources', maxChars: 24 },
  NLSResourcesQuotaTitle: { text: 'Quota', description: 'Quota module title.', surface: 'resources', maxChars: 18 },
  NLSResourcesQuotaWorkspaceRequired: { text: 'Select a workspace to view its quotas.', description: 'Quota module no-workspace body.', surface: 'resources', maxChars: 60 },
  NLSResourcesReadinessEmpty: { text: 'No resource risks in the current workspace.', description: 'Resource readiness panel empty state.', surface: 'resources', maxChars: 70 },
  NLSResourcesReadinessTitle: { text: 'Resource Readiness', description: 'Resource readiness panel title.', surface: 'resources', maxChars: 36 },
  NLSResourcesSignalBudgetRisk: { text: 'Budget risk', description: 'Resource warning badge label for budget signals.', surface: 'resources', maxChars: 28 },
  NLSResourcesSignalQuotaImpact: { text: 'Quota impact', description: 'Resource warning badge label for quota signals.', surface: 'resources', maxChars: 30 },
  NLSResourcesSignalStockShort: { text: 'Stock short', description: 'Resource warning badge label for stock shortage signals.', surface: 'resources', maxChars: 28 },
  NLSResourcesSignalsAria: { text: 'Resource signals', description: 'Accessible label for resource warning badges.', surface: 'resources', maxChars: 36 },
  NLSResourcesStockAddFirstItem: { text: 'Add your first item', description: 'Stock empty state primary action.', surface: 'resources', maxChars: 36 },
  NLSResourcesStockAddItemPlaceholder: { text: 'Add a stock item...', description: 'Checklist stock requirement picker placeholder.', surface: 'resources', maxChars: 38 },
  NLSResourcesStockAddRequiredItemAria: { text: 'Add required stock item', description: 'Accessible label for checklist stock requirement picker.', surface: 'resources', maxChars: 46 },
  NLSResourcesStockAddResourceTitle: { text: 'Add a resource', description: 'Stock item drawer create title.', surface: 'resources', maxChars: 34 },
  NLSResourcesStockAvailableOption: { text: '{name} ({count} avail)', description: 'Stock picker option. {name} is item name; {count} is available quantity.', surface: 'resources', maxChars: 90 },
  NLSResourcesStockConsumeOnCompletion: { text: 'Consume on completion', description: 'Stock consumption mode option.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockConsumptionModeAria: { text: 'Consumption mode', description: 'Accessible label for stock consumption mode tabs.', surface: 'resources', maxChars: 36 },
  NLSResourcesStockCreateDialogAria: { text: 'Create stock item', description: 'Accessible label for create stock item dialog.', surface: 'resources', maxChars: 38 },
  NLSResourcesStockCreateItem: { text: 'Create item', description: 'Stock drawer create submit action.', surface: 'resources', maxChars: 28 },
  NLSResourcesStockCreatedToast: { text: 'Stock item created.', description: 'Stock item create success toast.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockDeleteConfirm: { text: 'Delete "{name}"? This cannot be undone.', description: 'Stock item delete confirmation. {name} is item name.', surface: 'resources', maxChars: 90 },
  NLSResourcesStockDeleteFailed: { text: 'Could not delete stock item.', description: 'Stock item delete fallback error.', surface: 'resources', maxChars: 52 },
  NLSResourcesStockDeleteItemAria: { text: 'Delete {name}', description: 'Accessible label for stock item delete action. {name} is item name.', surface: 'resources', maxChars: 70 },
  NLSResourcesStockDeletedReferenceTitle: { text: 'Deleted stock item', description: 'Checklist stock requirement deleted reference title.', surface: 'resources', maxChars: 38 },
  NLSResourcesStockDeletedToast: { text: 'Stock item deleted.', description: 'Stock item delete success toast.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockEditDialogAria: { text: 'Edit stock item', description: 'Accessible label for edit stock item dialog.', surface: 'resources', maxChars: 36 },
  NLSResourcesStockEditItemAria: { text: 'Edit {name}', description: 'Accessible label for stock item edit action. {name} is item name.', surface: 'resources', maxChars: 70 },
  NLSResourcesStockEditItemEyebrow: { text: 'Edit stock item', description: 'Stock item drawer edit eyebrow.', surface: 'resources', maxChars: 34 },
  NLSResourcesStockEditItemFallback: { text: 'Edit item', description: 'Stock item drawer fallback edit title.', surface: 'resources', maxChars: 26 },
  NLSResourcesStockEmptyFilter: { text: 'No items match this filter.', description: 'Stock filter empty state.', surface: 'resources', maxChars: 48 },
  NLSResourcesStockEmptyItems: { text: 'No stock items yet.', description: 'Stock list empty state.', surface: 'resources', maxChars: 38 },
  NLSResourcesStockFieldLocation: { text: 'Location', description: 'Stock item location field label.', surface: 'resources', maxChars: 22 },
  NLSResourcesStockFieldName: { text: 'Name', description: 'Stock item name field label.', surface: 'resources', maxChars: 18 },
  NLSResourcesStockFieldQuantity: { text: 'Quantity', description: 'Stock item quantity field label.', surface: 'resources', maxChars: 22 },
  NLSResourcesStockFieldReserved: { text: 'Reserved', description: 'Stock item reserved quantity label.', surface: 'resources', maxChars: 22 },
  NLSResourcesStockFieldSku: { text: 'SKU', description: 'Stock item SKU field label.', surface: 'resources', maxChars: 12 },
  NLSResourcesStockFieldUnit: { text: 'Unit', description: 'Stock item unit field label.', surface: 'resources', maxChars: 18 },
  NLSResourcesStockLinkedTasks: { text: 'Linked tasks', description: 'Stock item linked task count label.', surface: 'resources', maxChars: 28 },
  NLSResourcesStockLoadFailed: { text: 'Could not load stock items.', description: 'Stock subscription fallback error toast.', surface: 'resources', maxChars: 54 },
  NLSResourcesStockLoadingItems: { text: 'Loading stock items...', description: 'Stock list loading label.', surface: 'resources', maxChars: 38 },
  NLSResourcesStockCountEyebrow: { text: 'Helper audit', description: 'Stock count suggestion panel eyebrow.', surface: 'resources', maxChars: 28 },
  NLSResourcesStockCountTitle: { text: 'Pending stock counts', description: 'Stock count suggestion panel title.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockCountLoading: { text: 'Loading pending counts', description: 'Stock count suggestion loading label.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockCountEmpty: { text: 'No stock counts are waiting for review.', description: 'Stock count suggestion empty state.', surface: 'resources', maxChars: 70 },
  NLSResourcesStockCountLoadFailed: { text: 'Could not load stock count suggestions.', description: 'Stock count suggestion load failure toast.', surface: 'resources', maxChars: 70 },
  NLSResourcesStockCountApproved: { text: 'Stock count approved.', description: 'Stock count suggestion approval toast.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockCountRejected: { text: 'Stock count rejected.', description: 'Stock count suggestion rejection toast.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockCountReviewFailed: { text: 'Could not review stock count.', description: 'Stock count suggestion review failure toast.', surface: 'resources', maxChars: 58 },
  NLSResourcesStockCountRound: { text: 'Round {id}', description: 'Stock count suggestion round heading. {id} is a short round id.', surface: 'resources', maxChars: 36 },
  NLSResourcesStockCountRoundItems: { text: '{count} item(s)', description: 'Stock count suggestion round item count.', surface: 'resources', maxChars: 30 },
  NLSResourcesStockCountRowMeta: { text: '{helper}: {before} -> {after} ({delta})', description: 'Stock count suggestion row metadata.', surface: 'resources', maxChars: 90 },
  NLSResourcesStockCountRowItem: { text: '{name}', description: 'Stock count suggestion row item name (falls back to SKU when unknown).', surface: 'resources', maxChars: 60 },
  NLSResourcesStockCountRowSubmitted: { text: 'Submitted {time}', description: 'Stock count suggestion row submitted-time meta.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockCountApproveAllInRound: { text: 'Approve all in round', description: 'Bulk-approve button label on a stock count round header.', surface: 'resources', maxChars: 30 },
  NLSResourcesStockCountApproveAllConfirmTitle: { text: 'Approve this round?', description: 'Confirm modal title before bulk-approving a stock count round.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockCountApproveAllConfirmBody: {
    text: 'This approves {count} pending stock counts at once. Each one is applied to inventory immediately.',
    description: 'Confirm modal body before bulk-approving a stock count round.',
    surface: 'resources',
    maxChars: 140,
  },
  NLSResourcesStockCountApproveAllResult: { text: 'Approved {approved}, failed {failed}.', description: 'Bulk-approve summary toast.', surface: 'resources', maxChars: 50 },
  NLSResourcesStockCountStale: { text: 'Stock changed since approval; recount instead.', description: 'Stale error message when stock changed after approval.', surface: 'resources', maxChars: 70 },
  NLSResourcesStockCountUndoRoundResult: { text: 'Undid {undone}, skipped {skipped}.', description: 'Toast reporting count of undone and skipped round items.', surface: 'resources', maxChars: 50 },
  NLSResourcesStockCountUndoAction: { text: 'Undo', description: 'Undo stock count approval button.', surface: 'resources', maxChars: 20 },
  NLSResourcesStockCountUndoRoundAction: { text: 'Undo round', description: 'Undo round approval button.', surface: 'resources', maxChars: 24 },
  NLSResourcesStockCountUndone: { text: 'Stock count approval undone.', description: 'Toast after successfully undoing stock count approval.', surface: 'resources', maxChars: 50 },
  NLSResourcesStockCountUndoExpired: { text: 'The undo window has expired.', description: 'Toast when 5-minute undo window has passed.', surface: 'resources', maxChars: 50 },
  NLSResourcesStockCountRecentlyApproved: { text: 'Recently approved by you', description: 'Section title for approvals within 5-minute undo window.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockCountApproveAllPendingResult: { text: '{pending} item(s) waiting for Admin approval.', description: 'Toast reporting items waiting for Admin approval after round approve.', surface: 'resources', maxChars: 60 },
  NLSResourcesStockCountFilterSubmitter: { text: 'Submitted by', description: 'Submitter filter chip row label.', surface: 'resources', maxChars: 24 },
  NLSResourcesStockCountFilterItem: { text: 'Item', description: 'Item filter chip row label.', surface: 'resources', maxChars: 24 },
  NLSResourcesStockLocationPlaceholder: { text: 'Studio A · Shelf 3', description: 'Stock item location placeholder example.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockMaintenanceToggle: { text: 'Under maintenance (overrides availability)', description: 'Stock item maintenance toggle label.', surface: 'resources', maxChars: 64 },
  NLSResourcesStockNamePlaceholder: { text: 'e.g. Camera Battery', description: 'Stock item name placeholder example.', surface: 'resources', maxChars: 42 },
  NLSResourcesStockNewItem: { text: 'New item', description: 'Stock hub create item action.', surface: 'resources', maxChars: 24 },
  NLSStockNewStockItem: { text: '+ New stock item', description: 'Stock hub primary create button.', surface: 'resources', maxChars: 30 },
  NLSStockSectionBasics: { text: 'Basics', description: 'Stock form basics section header.', surface: 'resources', maxChars: 24 },
  NLSStockSectionQuantityUnit: { text: 'Quantity & unit', description: 'Stock form quantity & unit section header.', surface: 'resources', maxChars: 32 },
  NLSStockSectionCostSupplier: { text: 'Cost & supplier', description: 'Stock form cost & supplier section header.', surface: 'resources', maxChars: 32 },
  NLSStockSectionThresholdsReordering: { text: 'Thresholds & reordering', description: 'Stock form thresholds & reordering section header.', surface: 'resources', maxChars: 36 },
  NLSStockSectionThresholdsHint: {
    text: 'When quantity drops below reorderPoint, a restock request is created.',
    description: 'Stock form reorder thresholds hint.',
    surface: 'resources',
    maxChars: 90,
  },
  NLSStockSectionIdentification: { text: 'Identification', description: 'Stock form identification section header.', surface: 'resources', maxChars: 30 },
  NLSStockSectionMediaNotes: { text: 'Media & notes', description: 'Stock form media & notes section header.', surface: 'resources', maxChars: 30 },
  NLSStockFieldCategory: { text: 'Category', description: 'Stock item category label.', surface: 'resources', maxChars: 24 },
  NLSStockFieldStatus: { text: 'Status', description: 'Stock item status label.', surface: 'resources', maxChars: 20 },
  NLSStockFieldCostPerUnit: { text: 'Cost per unit', description: 'Stock item cost per unit label.', surface: 'resources', maxChars: 26 },
  NLSStockFieldSupplier: { text: 'Supplier', description: 'Stock item supplier label.', surface: 'resources', maxChars: 24 },
  NLSStockFieldMinThreshold: { text: 'Min threshold', description: 'Stock item minimum threshold label.', surface: 'resources', maxChars: 26 },
  NLSStockFieldMaxThreshold: { text: 'Max threshold', description: 'Stock item maximum threshold label.', surface: 'resources', maxChars: 26 },
  NLSStockFieldReorderPoint: { text: 'Reorder point', description: 'Stock item reorder point label.', surface: 'resources', maxChars: 26 },
  NLSStockFieldReorderQuantity: { text: 'Reorder quantity', description: 'Stock item reorder quantity label.', surface: 'resources', maxChars: 26 },
  NLSStockFieldBarcode: { text: 'Barcode', description: 'Stock item barcode label.', surface: 'resources', maxChars: 24 },
  NLSStockFieldSerialNumbers: { text: 'Serial numbers', description: 'Stock item serial numbers label.', surface: 'resources', maxChars: 26 },
  NLSStockFieldExpiryDate: { text: 'Expiry date', description: 'Stock item expiry date label.', surface: 'resources', maxChars: 24 },
  NLSStockFieldImage: { text: 'Image', description: 'Stock item image label.', surface: 'resources', maxChars: 20 },
  NLSStockFieldNotes: { text: 'Notes', description: 'Stock item notes label.', surface: 'resources', maxChars: 20 },
  NLSStockTabItems: { text: 'Items', description: 'Stock items tab label.', surface: 'resources', maxChars: 20 },
  NLSStockTabRestock: { text: 'Restock queue', description: 'Stock restock queue tab label.', surface: 'resources', maxChars: 28 },
  NLSStockTabSuppliers: { text: 'Suppliers', description: 'Stock suppliers tab label.', surface: 'resources', maxChars: 24 },
  NLSStockTabAnalytics: { text: 'Analytics', description: 'Stock analytics tab label.', surface: 'resources', maxChars: 24 },
  NLSStockTabAudit: { text: 'Audit', description: 'Stock audit ledger tab label.', surface: 'resources', maxChars: 20 },
  NLSStockRestockEmpty: { text: 'No items below reorder point.', description: 'Stock restock empty state text.', surface: 'resources', maxChars: 50 },
  NLSStockActionMarkOrdered: { text: 'Mark ordered', description: 'Stock restock mark ordered button.', surface: 'resources', maxChars: 24 },
  NLSStockActionMarkReceived: { text: 'Mark received', description: 'Stock restock mark received button.', surface: 'resources', maxChars: 24 },
  NLSStockActionImportCsv: { text: 'Import CSV…', description: 'Stock hub import CSV action label.', surface: 'resources', maxChars: 24 },
  NLSStockActionNewItem: { text: 'New item', description: 'Stock hub new item dropdown action.', surface: 'resources', maxChars: 20 },
  NLSStockActionAddSupplier: { text: '+ Add supplier', description: 'Stock hub add supplier button.', surface: 'resources', maxChars: 24 },
  NLSStockActionAdjustQty: { text: 'Adjust qty', description: 'Stock item adjust quantity action.', surface: 'resources', maxChars: 20 },
  NLSStockTabRestockQueueWithCount: { text: 'Restock queue ({count})', description: 'Stock restock queue tab label with count.', surface: 'resources', maxChars: 32 },
  NLSStockActionImportCsvPlain: { text: 'Import CSV', description: 'Stock hub import CSV button label without ellipsis.', surface: 'resources', maxChars: 20 },
  NLSStockActionCancel: { text: 'Cancel', description: 'Stock restock cancel action button.', surface: 'resources', maxChars: 16 },
  NLSStockActionDelete: { text: 'Delete', description: 'Stock delete confirmation action button.', surface: 'resources', maxChars: 16 },
  NLSStockPromptReceivedQuantity: { text: 'Received quantity?', description: 'Stock restock received quantity prompt message.', surface: 'resources', maxChars: 30 },
  NLSStockSuppliersHeading: { text: 'Suppliers', description: 'Stock suppliers section heading.', surface: 'resources', maxChars: 20 },
  NLSStockActionAddSupplierBtn: { text: 'Add supplier', description: 'Stock add supplier button without plus symbol.', surface: 'resources', maxChars: 20 },
  NLSStockSuppliersEmpty: { text: 'No suppliers yet', description: 'Stock suppliers empty state message.', surface: 'resources', maxChars: 30 },
  NLSStockActionAddFirstSupplier: { text: 'Add first supplier', description: 'Stock suppliers empty state button label.', surface: 'resources', maxChars: 30 },
  NLSStockConfirmDeleteSupplier: { text: 'Delete supplier "{name}"?', description: 'Stock supplier delete confirmation prompt.', surface: 'resources', maxChars: 60 },
  NLSStockToastSupplierDeleted: { text: 'Supplier deleted', description: 'Stock supplier deleted toast notification.', surface: 'resources', maxChars: 30 },
  NLSStockToastSupplierCreated: { text: 'Supplier created.', description: 'Stock supplier created toast notification.', surface: 'resources', maxChars: 30 },
  NLSStockToastSupplierUpdated: { text: 'Supplier updated.', description: 'Stock supplier updated toast notification.', surface: 'resources', maxChars: 30 },
  NLSStockAuditHeading: { text: 'Audit trail', description: 'Stock audit section heading.', surface: 'resources', maxChars: 24 },
  NLSStockAuditDescription: {
    text: 'All stock mutations (create, update, delete, adjust, reorder, bulk import) are written to the workspace audit ledger with the stock.* action namespace. Use the workspace Audit hub for the complete history and export tooling.',
    description: 'Stock audit section description.',
    surface: 'resources',
    maxChars: 250,
  },
  NLSBudgetTabRecurring: { text: 'Recurring', description: 'Budget recurring rules tab label.', surface: 'resources', maxChars: 20 },
  NLSBudgetTabVendors: { text: 'Vendors', description: 'Budget vendors tab label.', surface: 'resources', maxChars: 20 },
  NLSBudgetTabCategories: { text: 'Categories & limits', description: 'Budget category limits and policy tab label.', surface: 'resources', maxChars: 28 },
  NLSBudgetTabApprovals: { text: 'Approvals', description: 'Budget approvals queue tab label.', surface: 'resources', maxChars: 20 },
  NLSBudgetTabAudit: { text: 'Audit', description: 'Budget audit tab label.', surface: 'resources', maxChars: 20 },
  NLSBudgetExpenseOverCategoryLimit: {
    text: 'Expense logged — {category} is over its {period} limit.',
    description: 'Warning toast when an expense pushes a category over its limit.',
    surface: 'resources',
    maxChars: 80,
  },
  NLSResourcesBudgetCardEyebrow: { text: 'Budget', description: 'Uppercase eyebrow label on node budget limit card.', surface: 'resources', maxChars: 20 },
  NLSResourcesBudgetCardInputAria: { text: 'Budget limit', description: 'Aria label for budget limit input.', surface: 'resources', maxChars: 40 },
  NLSResourcesBudgetCardSaving: { text: 'Saving…', description: 'Saving indicator status.', surface: 'resources', maxChars: 20 },
  NLSResourcesBudgetCardSaved: { text: 'Saved', description: 'Saved indicator status.', surface: 'resources', maxChars: 20 },
  NLSResourcesBudgetCardNoLimit: { text: 'No limit set', description: 'Caption when no budget limit is set.', surface: 'resources', maxChars: 30 },
  NLSResourcesBudgetCardMeterCaption: { text: '{spent} spent of {limit} · {percent}% used', description: 'Caption showing spend vs limit and percentage used.', surface: 'resources', maxChars: 80 },
  NLSResourcesBudgetCardInvalidNumber: { text: 'Budget must be a non-negative number.', description: 'Validation message for negative or non-numeric budget limit.', surface: 'resources', maxChars: 80 },
  NLSResourcesBudgetCardSaveFailed: { text: 'Could not update budget limit.', description: 'Error message when updating budget limit fails.', surface: 'resources', maxChars: 60 },
  NLSResourcesBudgetCardReadOnlyTag: { text: 'View only', description: 'Badge indicating budget limit cannot be edited.', surface: 'resources', maxChars: 20 },

  NLSResourcesStockNewItemEyebrow: { text: 'New stock item', description: 'Stock item drawer create eyebrow.', surface: 'resources', maxChars: 34 },
  NLSResourcesStockNoLocation: { text: 'no location', description: 'Stock requirement missing location fallback.', surface: 'resources', maxChars: 24 },
  NLSResourcesStockNoRequirements: { text: 'No stock items required.', description: 'Checklist stock requirement empty state.', surface: 'resources', maxChars: 44 },
  NLSResourcesStockOffBody: { text: 'An admin can enable Stock from Workspace Settings to Resources.', description: 'Stock disabled module body.', surface: 'resources', maxChars: 90 },
  NLSResourcesStockOffTitle: { text: 'Stock is off', description: 'Stock disabled module title.', surface: 'resources', maxChars: 28 },
  NLSResourcesStockQty: { text: 'Qty', description: 'Compact quantity label in stock requirement controls.', surface: 'resources', maxChars: 12 },
  NLSResourcesStockRemoveDeletedReferenceAria: {
    text: 'Remove deleted reference',
    description: 'Accessible label for removing a deleted stock requirement reference.',
    surface: 'resources',
    maxChars: 50,
  },
  NLSResourcesStockRemoveItemAria: { text: 'Remove {name}', description: 'Accessible label for removing a stock requirement. {name} is item name.', surface: 'resources', maxChars: 70 },
  NLSResourcesStockRequirementDetail: {
    text: '{available} available · needs {quantity} {unit} · {location}',
    description: 'Checklist stock requirement detail. Values are quantities, unit text, and location text.',
    surface: 'resources',
    maxChars: 110,
  },
  NLSResourcesStockRequired: { text: 'Required', description: 'Stock requirement required toggle label.', surface: 'resources', maxChars: 22 },
  NLSResourcesStockReserveWhileActive: { text: 'Reserve while active', description: 'Stock consumption mode option.', surface: 'resources', maxChars: 38 },
  NLSResourcesStockSaveChanges: { text: 'Save changes', description: 'Stock drawer edit submit action.', surface: 'resources', maxChars: 30 },
  NLSResourcesStockSaveFailed: { text: 'Could not save stock item.', description: 'Stock item save fallback error.', surface: 'resources', maxChars: 50 },
  NLSResourcesStockSearchPlaceholder: { text: 'Search by name, SKU, or location', description: 'Stock search field placeholder.', surface: 'resources', maxChars: 58 },
  NLSResourcesStockSkuPlaceholder: { text: 'SKU-001', description: 'Stock item SKU placeholder example.', surface: 'resources', maxChars: 24 },
  NLSResourcesStockStatusAll: { text: 'All', description: 'Stock status filter for all items.', surface: 'resources', maxChars: 14 },
  NLSResourcesStockStatusAvailable: { text: 'Available', description: 'Stock status label for available items.', surface: 'resources', maxChars: 22 },
  NLSResourcesStockStatusFilterAria: { text: 'Stock status filter', description: 'Accessible label for stock status filters.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockStatusInUse: { text: 'In-Use', description: 'Stock status label for in-use items.', surface: 'resources', maxChars: 20 },
  NLSResourcesStockStatusMaintenance: { text: 'Maintenance', description: 'Stock status label for maintenance items.', surface: 'resources', maxChars: 28 },
  NLSResourcesStockSubtitle: { text: 'Inventory ledger', description: 'Stock module header subtitle.', surface: 'resources', maxChars: 34 },
  NLSResourcesStockTitle: { text: 'Stock', description: 'Stock module title.', surface: 'resources', maxChars: 18 },
  NLSResourcesStockTotalItems: { text: 'Total items', description: 'Stock summary total item count label.', surface: 'resources', maxChars: 28 },
  NLSResourcesStockUnavailableTitle: { text: 'Stock is unavailable', description: 'Stock no-workspace gate title.', surface: 'resources', maxChars: 42 },
  NLSResourcesStockUnitPlaceholder: { text: 'unit / box / kg', description: 'Stock item unit placeholder examples.', surface: 'resources', maxChars: 36 },
  NLSResourcesStockUpdatedAt: { text: 'Updated {time}', description: 'Stock item updated timestamp. {time} is formatted.', surface: 'resources', maxChars: 60 },
  NLSResourcesStockUpdatedToast: { text: 'Stock item updated.', description: 'Stock item update success toast.', surface: 'resources', maxChars: 40 },
  NLSResourcesStockWorkspaceRequiredBody: { text: 'Select a workspace to manage stock items.', description: 'Stock module no-workspace body.', surface: 'resources', maxChars: 64 },
  NLSResourcesWorkspaceRequiredEyebrow: { text: 'Workspace required', description: 'Resource module no-workspace gate eyebrow.', surface: 'resources', maxChars: 36 },
  NLSMemberTabTodos: { text: 'Todos', description: 'Member app primary tab for assigned work.', surface: 'member', maxChars: 18 },
  NLSMemberTabBoard: { text: 'Board', description: 'Member app primary tab for board work.', surface: 'member', maxChars: 18 },
  NLSMemberTabAiMagic: { text: 'AI Magic', description: 'Member app primary tab for AI Magic.', surface: 'member', maxChars: 20 },
  NLSMemberTabNexus: { text: 'Nexus', description: 'Member app primary tab grouping chat, calendar, and meetings.', surface: 'member', maxChars: 18 },
  NLSMemberTabControls: { text: 'Controls', description: 'Member app primary tab for controls.', surface: 'member', maxChars: 20 },
  NLSMemberTabYourPage: { text: 'Your Page', description: 'Member app profile route label.', surface: 'member', maxChars: 24 },
  NLSMemberTabStock: { text: 'Stock', description: 'Member app primary tab for inventory stock.', surface: 'member', maxChars: 18 },
  NLSMemberTabForms: { text: 'Forms', description: 'Member app primary tab for forms.', surface: 'member', maxChars: 18 },
  NLSMemberTabSync: { text: 'Sync', description: 'Member app primary tab for sync center.', surface: 'member', maxChars: 18 },
  NLSMemberTabAutomations: { text: 'Automations', description: 'Member app primary tab for automations.', surface: 'member', maxChars: 24 },
  NLSMemberTabTeam: { text: 'Team', description: 'Member app primary tab for team and workspace members.', surface: 'member', maxChars: 18 },
  NLSMemberTabClass: { text: 'Class', description: 'Member app primary tab for education class schedule and homework.', surface: 'member', maxChars: 18 },
  NLSMemberTabProjects: { text: 'Projects', description: 'Member bottom tab label when only Construction sector is active.', surface: 'member', maxChars: 18 },
  NLSMemberTabHospital: { text: 'Hospital', description: 'Member bottom tab label when only Hospital sector is active.', surface: 'member', maxChars: 18 },
  NLSMemberTabOperations: { text: 'Operations', description: 'Member bottom tab label when multiple sectors or general operations are active.', surface: 'member', maxChars: 18 },
  NLSMemberOpsApprovalsTitle: { text: 'Approvals', description: 'Section title for Member operations approvals inbox.', surface: 'member', maxChars: 20 },
  NLSMemberOpsEmptyApprovals: { text: 'No pending approvals.', description: 'Empty state when no approvals await decision.', surface: 'member', maxChars: 40 },
  NLSMemberOpsRosterTitle: { text: 'Roster', description: 'Section title for Member operations roster shifts.', surface: 'member', maxChars: 20 },
  NLSMemberOpsEmptyRoster: { text: 'No scheduled shifts for this period.', description: 'Empty state when no shifts are scheduled.', surface: 'member', maxChars: 50 },
  NLSMemberOpsRegistersTitle: { text: 'Registers', description: 'Section title for Member operations registers and records.', surface: 'member', maxChars: 20 },
  NLSMemberOpsEmptyRegisters: { text: 'No records found.', description: 'Empty state when no records exist.', surface: 'member', maxChars: 30 },
  NLSMemberOpsRequestsTitle: { text: 'Requests', description: 'Section title for Member operations requests board.', surface: 'member', maxChars: 20 },
  NLSMemberOpsEmptyRequests: { text: 'No requests found.', description: 'Empty state when no requests exist.', surface: 'member', maxChars: 30 },
  NLSMemberOpsTodayTitle: { text: 'Today', description: 'Section title for Member operations today overview counts.', surface: 'member', maxChars: 20 },
  NLSMemberOpsDuesDone: { text: 'Dues done', description: 'Count label for completed dues today.', surface: 'member', maxChars: 20 },
  NLSMemberOpsDuesOpen: { text: 'Dues open', description: 'Count label for open dues today.', surface: 'member', maxChars: 20 },
  NLSMemberOpsDuesMissed: { text: 'Dues missed', description: 'Count label for missed dues today.', surface: 'member', maxChars: 20 },
  NLSMemberOpsRequestsOpen: { text: 'Open requests', description: 'Count label for open requests.', surface: 'member', maxChars: 24 },
  NLSMemberOpsRequestsInProgress: { text: 'In progress', description: 'Count label for in-progress requests.', surface: 'member', maxChars: 20 },
  NLSMemberOpsAttendanceMarked: { text: 'Attendance marked', description: 'Count label for marked attendance entries today.', surface: 'member', maxChars: 28 },
  NLSMemberOpsAttendanceNotMarked: { text: 'Attendance unmarked', description: 'Count label for unmarked attendance entries today.', surface: 'member', maxChars: 28 },
  NLSMemberOpsCoSignAgree: { text: 'Co-sign', description: 'Button label to confirm co-signing a record.', surface: 'member', maxChars: 18 },
  NLSMemberOpsCoSignRefuse: { text: 'Refuse', description: 'Button label to refuse co-signing a record.', surface: 'member', maxChars: 18 },
  NLSMemberOpsSelfApprovalReason: { text: 'Self-approval reason', description: 'Prompt label for self-approval reason.', surface: 'member', maxChars: 30 },
  NLSMemberOpsReturnReason: { text: 'Return reason', description: 'Prompt label for returning a record.', surface: 'member', maxChars: 30 },
  NLSMemberOpsNewRecord: { text: 'New record', description: 'Action button to create a new register record.', surface: 'member', maxChars: 20 },
  NLSMemberOpsAcknowledgeShift: { text: 'Acknowledge roster', description: 'Button to acknowledge published roster version.', surface: 'member', maxChars: 25 },
  NLSMemberOpsExceptionsTitle: { text: 'Exceptions', description: 'Section title for Member operations hospital exceptions inbox.', surface: 'member', maxChars: 20 },
  NLSMemberOpsExceptionsEmpty: { text: 'No exceptions in this unit.', description: 'Empty state when no exceptions exist in selected unit.', surface: 'member', maxChars: 40 },
  NLSMemberOpsSelectUnit: { text: 'Select unit', description: 'Label for unit picker dropdown in Member exceptions inbox.', surface: 'member', maxChars: 20 },
  NLSMemberOpsNoUnitsFound: { text: 'No units found.', description: 'Empty state when no units are available to member.', surface: 'member', maxChars: 30 },
  NLSMemberOpsEquipmentTitle: { text: 'Equipment Down', description: 'Section title for Member operations equipment down now.', surface: 'member', maxChars: 24 },
  NLSMemberOpsEquipmentEmpty: { text: 'No equipment currently down.', description: 'Empty state when no equipment is down.', surface: 'member', maxChars: 40 },
  NLSMemberOpsReleaseAction: { text: 'Release', description: 'Button label to release an isolated or maintenance asset.', surface: 'member', maxChars: 18 },
  NLSMemberOpsReleaseReasonPlaceholder: { text: 'Release notes or reason (optional)', description: 'Placeholder for asset release notes.', surface: 'member', maxChars: 50 },
  NLSMemberOpsReleaseConfirmTitle: { text: 'Release equipment', description: 'Title for release confirmation dialog.', surface: 'member', maxChars: 30 },
  NLSMemberOpsDownSinceUnknown: { text: 'since —', description: 'Label when asset downSince timestamp is unknown.', surface: 'member', maxChars: 15 },
  NLSMemberOpsDownSince: { text: 'since {time}', description: 'Label showing when asset went down.', surface: 'member', maxChars: 30 },
  NLSMemberOpsBedsTitle: { text: 'Bed board', description: 'Section title for Member operations bed board.', surface: 'member', maxChars: 20 },
  NLSMemberOpsBedsEmpty: { text: 'No beds in this ward.', description: 'Empty state when ward has no beds.', surface: 'member', maxChars: 30 },
  NLSMemberOpsSelectWard: { text: 'Select ward', description: 'Label for ward picker in Member bed board.', surface: 'member', maxChars: 20 },
  NLSMemberOpsNoWardsFound: { text: 'No wards found.', description: 'Empty state when no wards are available.', surface: 'member', maxChars: 30 },
  NLSMemberOpsDischargeConfirm: { text: 'Discharge patient? A cleaning request will be raised.', description: 'Confirmation message before discharging a bed.', surface: 'member', maxChars: 60 },
  NLSMemberOpsBlockConfirmTitle: { text: 'Block bed', description: 'Title for blocking a bed dialog.', surface: 'member', maxChars: 25 },
  NLSMemberOpsOverrideConfirmTitle: { text: 'Override ready', description: 'Title for overriding bed to ready dialog.', surface: 'member', maxChars: 25 },
  NLSMemberOpsBlockReasonPlaceholder: { text: 'Reason for blocking (min 3 chars)', description: 'Placeholder for reason input when blocking a bed.', surface: 'member', maxChars: 50 },
  NLSMemberOpsOverrideReasonPlaceholder: { text: 'Reason for override (min 3 chars)', description: 'Placeholder for reason input when overriding a bed state.', surface: 'member', maxChars: 50 },
  NLSMemberOpsActionMarkOccupied: { text: 'Mark Occupied', description: 'Button label to mark a ready bed as occupied.', surface: 'member', maxChars: 20 },
  NLSMemberOpsActionDischarge: { text: 'Discharge', description: 'Button label to discharge an occupied bed.', surface: 'member', maxChars: 18 },
  NLSMemberOpsActionBlock: { text: 'Block', description: 'Button label to block a bed from assignment.', surface: 'member', maxChars: 18 },
  NLSMemberOpsActionUnblock: { text: 'Unblock', description: 'Button label to unblock a blocked bed.', surface: 'member', maxChars: 18 },
  NLSMemberOpsActionOverride: { text: 'Override Ready', description: 'Button label to override cleaning/awaiting bed to ready.', surface: 'member', maxChars: 22 },
  NLSMemberOpsCountAll: { text: 'All', description: 'Count label for all beds in ward.', surface: 'member', maxChars: 12 },
  NLSMemberOpsCountReady: { text: 'Ready', description: 'Count label for ready beds in ward.', surface: 'member', maxChars: 12 },
  NLSMemberOpsCountOccupied: { text: 'Occupied', description: 'Count label for occupied beds in ward.', surface: 'member', maxChars: 12 },
  NLSMemberOpsCountAwaitingClean: { text: 'Awaiting clean', description: 'Count label for beds awaiting cleaning.', surface: 'member', maxChars: 18 },
  NLSMemberOpsCountCleaning: { text: 'Cleaning', description: 'Count label for beds currently being cleaned.', surface: 'member', maxChars: 15 },
  NLSMemberOpsCountBlocked: { text: 'Blocked', description: 'Count label for blocked beds in ward.', surface: 'member', maxChars: 15 },
  NLSMemberOpsBmwTitle: { text: 'Biomedical Waste Register', description: 'Section title for Member operations biomedical waste register.', surface: 'member', maxChars: 32 },
  NLSMemberOpsBmwDesc: { text: 'Daily waste category weights, corrections, and storage status.', description: 'Description for Member operations biomedical waste register.', surface: 'member', maxChars: 70 },
  NLSMemberOpsBmwCorrect: { text: 'Submit Correction', description: 'Button to correct locked BMW record in member app.', surface: 'member', maxChars: 25 },
  NLSMemberOpsBmwReason: { text: 'Reason for correction (3-500 chars)', description: 'Label for BMW correction reason input.', surface: 'member', maxChars: 40 },
  NLSMemberOpsOnCallTitle: { text: 'My On-Call Duty', description: 'Section title for Member operations on-call duty schedule.', surface: 'member', maxChars: 24 },
  NLSMemberOpsOnCallDesc: { text: 'Upcoming on-call duty slots and assignment acknowledgement.', description: 'Description for Member operations on-call duty schedule.', surface: 'member', maxChars: 70 },
  NLSMemberOpsOfflineNotice: { text: 'You are currently offline. Actions are disabled.', description: 'Offline banner notice.', surface: 'member', maxChars: 60 },
  NLSMemberOpsDprTitle: { text: 'Daily Progress Review', description: 'Section title for Member DPR review.', surface: 'member', maxChars: 30 },
  NLSMemberOpsDprSubtitle: { text: 'Review and approve submitted daily progress records.', description: 'Subtitle for Member DPR review.', surface: 'member', maxChars: 70 },
  NLSMemberOpsDprProjectPickerLabel: { text: 'Project', description: 'Label for project selector in DPR review.', surface: 'member', maxChars: 20 },
  NLSMemberOpsDprEmptyQueue: { text: 'No DPRs waiting for review.', description: 'Empty state when no DPRs are submitted or reviewed.', surface: 'member', maxChars: 40 },
  NLSMemberOpsDprShiftLabel: { text: 'Shift', description: 'Shift label.', surface: 'member', maxChars: 15 },
  NLSMemberOpsDprHeadcountLabel: { text: 'Headcount', description: 'Headcount label.', surface: 'member', maxChars: 15 },
  NLSMemberOpsDprLinesLabel: { text: 'Activities', description: 'Activities count label.', surface: 'member', maxChars: 15 },
  NLSMemberOpsDprLinesTitle: { text: 'Activities & Trades', description: 'Section title for DPR lines.', surface: 'member', maxChars: 30 },
  NLSMemberOpsDprNoLines: { text: 'No activities recorded.', description: 'Empty state for DPR lines.', surface: 'member', maxChars: 30 },
  NLSMemberOpsDprCorrectionsTitle: { text: 'Corrections History', description: 'Title for corrections history.', surface: 'member', maxChars: 30 },
  NLSMemberOpsDprReviewButton: { text: 'Review DPR', description: 'Button label to mark DPR reviewed.', surface: 'member', maxChars: 20 },
  NLSMemberOpsDprReturnButton: { text: 'Return DPR', description: 'Button label to return DPR with reason.', surface: 'member', maxChars: 20 },
  NLSMemberOpsDprApproveButton: { text: 'Approve DPR', description: 'Button label to approve DPR.', surface: 'member', maxChars: 20 },
  NLSMemberOpsDprConfirmReview: { text: 'Confirm Review', description: 'Dialog confirm button to review DPR.', surface: 'member', maxChars: 20 },
  NLSMemberOpsDprConfirmReturn: { text: 'Return with Reason', description: 'Dialog confirm button to return DPR.', surface: 'member', maxChars: 25 },
  NLSMemberOpsDprConfirmApprove: { text: 'Confirm Approval', description: 'Dialog confirm button to approve DPR.', surface: 'member', maxChars: 20 },
  NLSMemberOpsDprReviewNotesLabel: { text: 'Review Notes (optional)', description: 'Label for review notes input.', surface: 'member', maxChars: 30 },
  NLSMemberOpsDprReviewNotesPlaceholder: { text: 'Optional comments...', description: 'Placeholder for review notes.', surface: 'member', maxChars: 40 },
  NLSMemberOpsDprReturnReasonLabel: { text: 'Return Reason (min 3 chars)', description: 'Label for return reason input.', surface: 'member', maxChars: 35 },
  NLSMemberOpsDprReturnReasonPlaceholder: { text: 'Reason for returning DPR...', description: 'Placeholder for return reason.', surface: 'member', maxChars: 40 },
  NLSMemberOpsDprApproveNotesLabel: { text: 'Approval Notes (optional)', description: 'Label for approval notes input.', surface: 'member', maxChars: 30 },
  NLSMemberOpsDprApproveNotesPlaceholder: { text: 'Optional comments...', description: 'Placeholder for approval notes.', surface: 'member', maxChars: 40 },
  NLSMemberOpsInspectionsTitle: { text: 'My Inspections', description: 'Title for member inspections section.', surface: 'member', maxChars: 30 },
  NLSMemberOpsInspectionsSubtitle: { text: 'Pour and pre-concealment cards awaiting your inspection.', description: 'Subtitle for member inspections section.', surface: 'member', maxChars: 70 },
  NLSMemberOpsInspectionsEmptyQueue: { text: 'No inspection cards waiting for outcome.', description: 'Empty state when no requested cards.', surface: 'member', maxChars: 50 },
  NLSMemberOpsInspectionsKindLabel: { text: 'Kind', description: 'Inspection card kind label.', surface: 'member', maxChars: 15 },
  NLSMemberOpsInspectionsPlannedLabel: { text: 'Planned', description: 'Inspection card planned date label.', surface: 'member', maxChars: 15 },
  NLSMemberOpsInspectionsChecklistTitle: { text: 'Checklist', description: 'Title for inspection checklist.', surface: 'member', maxChars: 20 },
  NLSMemberOpsInspectionsNoChecklist: { text: 'No checklist items specified.', description: 'Empty state for inspection checklist.', surface: 'member', maxChars: 40 },
  NLSMemberOpsInspectionsPassButton: { text: 'PASS', description: 'Button label to record PASS outcome.', surface: 'member', maxChars: 15 },
  NLSMemberOpsInspectionsFailButton: { text: 'FAIL', description: 'Button label to record FAIL outcome.', surface: 'member', maxChars: 15 },
  NLSMemberOpsInspectionsPassDialogTitle: { text: 'Record PASS Outcome', description: 'Dialog title for passing inspection.', surface: 'member', maxChars: 30 },
  NLSMemberOpsInspectionsFailDialogTitle: { text: 'Record FAIL Outcome', description: 'Dialog title for failing inspection.', surface: 'member', maxChars: 30 },
  NLSMemberOpsInspectionsConfirmPass: { text: 'Confirm PASS', description: 'Confirm button for PASS.', surface: 'member', maxChars: 20 },
  NLSMemberOpsInspectionsConfirmFail: { text: 'Confirm FAIL', description: 'Confirm button for FAIL.', surface: 'member', maxChars: 20 },
  NLSMemberOpsInspectionsNcrNotice: { text: 'An NCR will be automatically raised for this rejection.', description: 'Notice that an NCR is generated on FAIL.', surface: 'member', maxChars: 70 },
  NLSMemberOpsInspectionsNotesLabel: { text: 'Inspection Notes (optional)', description: 'Label for pass notes.', surface: 'member', maxChars: 30 },
  NLSMemberOpsInspectionsNotesPlaceholder: { text: 'Remarks or comments...', description: 'Placeholder for pass notes.', surface: 'member', maxChars: 40 },
  NLSMemberOpsInspectionsReasonLabel: { text: 'Failure Reason (min 3 chars)', description: 'Label for fail reason.', surface: 'member', maxChars: 35 },
  NLSMemberOpsInspectionsReasonPlaceholder: { text: 'Reason for failure...', description: 'Placeholder for fail reason.', surface: 'member', maxChars: 40 },
  NLSMemberOpsConApprovalsTitle: { text: 'Construction Issues Waiting on Me', description: 'Group title for construction hindrance/rfi/selection waiting on member.', surface: 'member', maxChars: 40 },
  NLSMemberOpsConApprovalsEmpty: { text: 'No construction issues waiting on your response.', description: 'Empty state when no issues waiting on member.', surface: 'member', maxChars: 60 },
  NLSMemberOpsConApprovalsRespondBtn: { text: 'Respond', description: 'Button to respond to an issue.', surface: 'member', maxChars: 20 },
  NLSMemberOpsConApprovalsDialogTitle: { text: 'Respond to Issue', description: 'Dialog title to respond to construction issue.', surface: 'member', maxChars: 30 },
  NLSMemberOpsConApprovalsResponseLabel: { text: 'Response Text (1-4000 chars)', description: 'Label for issue response text input.', surface: 'member', maxChars: 40 },
  NLSMemberOpsConApprovalsResponsePlaceholder: { text: 'Enter response...', description: 'Placeholder for issue response text.', surface: 'member', maxChars: 40 },
  NLSMemberOpsConApprovalsConfirm: { text: 'Submit Response', description: 'Confirm button to submit issue response.', surface: 'member', maxChars: 25 },
  NLSMemberOpsConReportsTitle: { text: 'Construction Reports', description: 'Section title for construction reports preview.', surface: 'member', maxChars: 30 },
  NLSMemberOpsConReportsDeskLink: { text: 'Full report on desk', description: 'Link to desk con-reports tab.', surface: 'member', maxChars: 30 },
  NLSMemberOpsConReportsRowsShown: { text: 'Showing {shown} of {total} rows', description: 'Rows shown counter.', surface: 'member', maxChars: 40 },
  NLSMemberOpsConReportsEmpty: { text: 'No report data found for this range.', description: 'Empty state for construction report preview.', surface: 'member', maxChars: 50 },
  NLSMemberOpsConReportsKindLabel: { text: 'Report Type', description: 'Report kind selector label.', surface: 'member', maxChars: 20 },
  NLSMemberOpsConReportsDaysCappedNotice: { text: 'Date range is capped at {maxDays} days.', description: 'Warning when date range exceeds max days.', surface: 'member', maxChars: 60 },
  NLSMemberOpsConApprovalsProjectsLoadFailed: { text: 'Failed to load issues for {count} project(s).', description: 'Warning when some projects failed to load issues.', surface: 'member', maxChars: 60 },
  NLSMemberOpsConReportKindApprovedDpr: { text: 'Approved DPRs', description: 'Report kind label for approved DPRs.', surface: 'member', maxChars: 25 },
  NLSMemberOpsConReportKindHindranceAgeing: { text: 'Hindrance Ageing', description: 'Report kind label for hindrance ageing.', surface: 'member', maxChars: 25 },
  NLSMemberOpsConReportKindOverdueQuality: { text: 'Overdue Quality', description: 'Report kind label for overdue quality.', surface: 'member', maxChars: 25 },
  NLSMemberOpsConReportKindAttendanceProvisional: { text: 'Provisional Attendance', description: 'Report kind label for provisional attendance.', surface: 'member', maxChars: 30 },
  NLSMemberOpsConReportKindHandoverIndex: { text: 'Handover Index', description: 'Report kind label for handover index.', surface: 'member', maxChars: 25 },
  NLSEduMobileTabClass: { text: 'Class', description: 'Class tab label for School.', surface: 'member', maxChars: 10 },
  NLSEduMobileTabCoaching: { text: 'Coaching', description: 'Class tab label for Tuition & Coaching.', surface: 'member', maxChars: 10 },
  NLSEduMobileTabAcademy: { text: 'Academy', description: 'Class tab label for Sports & Performing Arts Academy.', surface: 'member', maxChars: 10 },
  NLSEduMobileTabTraining: { text: 'Training', description: 'Class tab label for Corporate & Skills Training.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabToday: { text: 'Today', description: 'Class subtab Today label.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabRegister: { text: 'Register', description: 'Class subtab Register label for schools.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabRollCall: { text: 'Roll call', description: 'Class subtab Roll call label for academies.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabHomework: { text: 'Homework', description: 'Class subtab Homework label for schools.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabPractice: { text: 'Practice', description: 'Class subtab Practice label for academies and corporate.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabMarks: { text: 'Marks', description: 'Class subtab Marks label for schools.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabProgress: { text: 'Progress', description: 'Class subtab Progress label for academies.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabStudents: { text: 'Students', description: 'Class subtab Students label for schools.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabTrainees: { text: 'Trainees', description: 'Class subtab Trainees label for academies and corporate.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabFees: { text: 'Fees', description: 'Admin class subtab Fees label for schools.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabPacks: { text: 'Packs', description: 'Admin class subtab Packs label for academies.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabAdmissions: { text: 'Admissions', description: 'Admin class subtab Admissions label for schools.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabTrials: { text: 'Trials', description: 'Admin class subtab Trials label for academies.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabTimetable: { text: 'Timetable', description: 'Admin class subtab Timetable label.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubtabFeed: { text: 'Feed', description: 'Admin class subtab Feed label.', surface: 'member', maxChars: 10 },
  NLSEduMobileSubmitAttendance: { text: 'Submit attendance', description: 'Action button label to submit attendance roll.', surface: 'member', maxChars: 24 },
  NLSEduMobileUpdateAttendance: { text: 'Update attendance', description: 'Action button label to update submitted attendance roll.', surface: 'member', maxChars: 24 },
  NLSEduMobileSubmitRegister: { text: 'Submit register', description: 'Action button label to submit class register.', surface: 'member', maxChars: 24 },
  NLSEduMobileUpdateRegister: { text: 'Update register', description: 'Action button label to update submitted class register.', surface: 'member', maxChars: 24 },
  NLSEduMobileNeedsYou: { text: 'Needs you', description: 'Section title for items requiring teacher attention.', surface: 'member', maxChars: 20 },
  NLSEduMobileNeedsHw: { text: 'Homework to check', description: 'Pending homework submissions count row.', surface: 'member', maxChars: 28 },
  NLSEduMobileNeedsPractice: { text: 'Practice to check', description: 'Pending practice submissions count row for academy.', surface: 'member', maxChars: 28 },
  NLSEduMobileNeedsLeave: { text: 'Leave requests', description: 'Pending leave requests count row.', surface: 'member', maxChars: 24 },
  NLSEduMobileNeedsMessages: { text: 'Messages in office hours', description: 'Unread office hours messages count row.', surface: 'member', maxChars: 32 },
  NLSEduMobilePillTakeRoll: { text: 'Take roll', description: 'Pill action to take attendance roll for session.', surface: 'member', maxChars: 16 },
  NLSEduMobilePillCover: { text: 'Cover', description: 'Pill indicating substitute session coverage.', surface: 'member', maxChars: 12 },
  NLSEduMobilePillTaken: { text: 'Taken', description: 'Pill indicating attendance roll has been taken.', surface: 'member', maxChars: 12 },
  NLSEduMobileCoverColleague: { text: 'Covering for colleague', description: 'Subtitle for substitute cover session.', surface: 'member', maxChars: 30 },
  NLSEduMobileTypeAbForAbsent: { text: 'type AB for absent', description: 'Instructional hint for entering absent mark.', surface: 'member', maxChars: 24 },
  NLSEduMobileMarksLocked: { text: 'Marks locked', description: 'Label when marks entry is locked.', surface: 'member', maxChars: 18 },
  NLSEduMobileLockMarks: { text: 'Lock marks', description: 'Button label to lock marks entry.', surface: 'member', maxChars: 18 },
  NLSEduMobileDraftRemarks: { text: 'Draft remarks', description: 'Button label for drafting report remarks.', surface: 'member', maxChars: 18 },
  NLSEduMobileDraftRemarksHint: { text: 'Coming soon', description: 'Hint showing draft remarks AI is coming soon.', surface: 'member', maxChars: 18 },
  NLSEduMobileLowAttendance: { text: 'Needs attention · low attendance', description: 'Status hint for learner with low attendance.', surface: 'member', maxChars: 40 },
  NLSEduMobileAttendanceOnTrack: { text: 'On track', description: 'Status hint for learner with good attendance.', surface: 'member', maxChars: 18 },
  NLSEduMobileAttendance: { text: 'attendance', description: 'Label for attendance percentage.', surface: 'member', maxChars: 16 },
  NLSEduMobileSentBackWithNote: { text: 'Sent back with a note', description: 'Status line when submission was returned with teacher feedback.', surface: 'member', maxChars: 30 },
  NLSEduMobileSendBack: { text: 'Send back', description: 'Aria label for returning a homework submission.', surface: 'member', maxChars: 16 },
  NLSEduMobileApprove: { text: 'Approve', description: 'Aria label for approving a homework submission.', surface: 'member', maxChars: 16 },
  NLSEduMobileTraineeTime: { text: 'Trainee time', description: 'Aria label for entering trainee performance time.', surface: 'member', maxChars: 20 },
  NLSEduMobileTodaySessionsHeader: { text: 'Today · {count} sessions', description: 'Section header showing number of today sessions.', surface: 'member', maxChars: 32 },
  NLSEduMobileMarksOutOf: { text: 'Out of {max}', description: 'Assessment max score display.', surface: 'member', maxChars: 20 },
  NLSEduMobileMarksAverage: { text: 'class average {average}', description: 'Class average display on marks entry.', surface: 'member', maxChars: 28 },
  NLSEduMobileSignedOut: { text: 'Please sign in to view class details', description: 'Empty state when no user is signed in.', surface: 'member', maxChars: 44 },
  NLSEduMobileNoSessionsToday: { text: 'No sessions scheduled today', description: 'Empty state when teacher has no sessions today.', surface: 'member', maxChars: 36 },
  NLSEduMobileNoHomework: { text: 'No homework assigned', description: 'Empty state when class has no homework items.', surface: 'member', maxChars: 30 },
  NLSEduMobileNoSubmissions: { text: 'No submissions yet', description: 'Empty state when homework has no submissions.', surface: 'member', maxChars: 30 },
  NLSEduMobileNoMarksAssessment: { text: 'No assessment found for this class', description: 'Empty state when gradebook has no assessments.', surface: 'member', maxChars: 44 },
  NLSEduMobileNoProgressMetrics: { text: 'No progress metrics configured', description: 'Empty state when academy has no progress metrics.', surface: 'member', maxChars: 40 },
  NLSEduMobileNoLearners: { text: 'No learners in your classes', description: 'Empty state when member has no learners.', surface: 'member', maxChars: 36 },
  NLSEduMobileEnrolled: { text: 'Enrolled', description: 'Default status label for enrolled learner.', surface: 'member', maxChars: 16 },
  NLSEduMobileRollNumber: { text: 'Roll No: {rollNo}', description: 'Roll number label in learner summary dialog.', surface: 'member', maxChars: 24 },
  NLSEduMobileClose: { text: 'Close', description: 'Close button label.', surface: 'member', maxChars: 12 },
  NLSEduMobileBack: { text: 'Back', description: 'Back button aria label.', surface: 'member', maxChars: 12 },
  NLSEduMobileNewHomework: { text: 'New homework', description: 'Button label to assign new homework.', surface: 'member', maxChars: 18 },
  NLSEduMobileNewPractice: { text: 'New practice', description: 'Button label to assign new practice in academy.', surface: 'member', maxChars: 18 },
  NLSEduMobileHomeworkTitleLabel: { text: 'Title', description: 'Form label for homework title.', surface: 'member', maxChars: 16 },
  NLSEduMobileHomeworkTitlePlaceholder: { text: 'Enter assignment title', description: 'Placeholder for homework title input.', surface: 'member', maxChars: 32 },
  NLSEduMobileDueDateLabel: { text: 'Due date', description: 'Form label for homework due date.', surface: 'member', maxChars: 16 },
  NLSEduMobileMaxScoreLabel: { text: 'Max score', description: 'Form label for homework max score.', surface: 'member', maxChars: 16 },
  NLSEduMobileAssignHomework: { text: 'Assign homework', description: 'Submit button to save homework.', surface: 'member', maxChars: 20 },
  NLSEduMobileCancel: { text: 'Cancel', description: 'Cancel button label.', surface: 'member', maxChars: 12 },
  NLSEduMobileErrorTitleRequired: { text: 'Please enter a title', description: 'Validation error when title is empty.', surface: 'member', maxChars: 32 },
  NLSEduMobileErrorDueDateRequired: { text: 'Please select a due date', description: 'Validation error when due date is empty.', surface: 'member', maxChars: 32 },
  NLSEduMobileHomeworkCreated: { text: 'Homework assigned', description: 'Success notification when homework is created.', surface: 'member', maxChars: 24 },
  NLSEduMobileHomeworkError: { text: 'Failed to assign homework', description: 'Error message when homework creation fails.', surface: 'member', maxChars: 32 },
  NLSEduMobilePromotePasses: { text: 'Promote passes', description: 'Button label to promote eligible learners in academy.', surface: 'member', maxChars: 20 },
  NLSEduMobilePass: { text: 'Pass', description: 'Badge label for passing grade/time.', surface: 'member', maxChars: 10 },
  NLSEduMobileRetry: { text: 'Retry', description: 'Badge label for retry requirement.', surface: 'member', maxChars: 10 },
  NLSEduMobileApproved: { text: 'Approved', description: 'Status badge when submission is approved.', surface: 'member', maxChars: 14 },
  NLSEduMobileHandedIn: { text: 'Handed in', description: 'Status label when submission is turned in.', surface: 'member', maxChars: 16 },
  NLSEduMobileLearnerMarksAria: { text: '{name} marks', description: 'Aria label for learner marks input.', surface: 'member', maxChars: 30 },
  NLSEduMobileLearnerAttendanceAria: { text: '{name} attendance', description: 'Aria label for learner attendance card.', surface: 'member', maxChars: 32 },
  NLSEduMobileLockNote: { text: 'Locked by teacher', description: 'Audit note when teacher locks marks.', surface: 'member', maxChars: 28 },
  NLSEduMobileSavesOffline: { text: 'Saves offline', description: 'Indicator when attendance is queued offline.', surface: 'member', maxChars: 18 },
  NLSEduMobileSynced: { text: 'synced', description: 'Indicator when attendance is synced online.', surface: 'member', maxChars: 16 },
  NLSEduMobileDueToday: { text: 'Due {date}', description: 'Due date display for homework.', surface: 'member', maxChars: 24 },
  NLSEduMobileSubmissionsCount: { text: '{count} submitted', description: 'Count of submitted homework items.', surface: 'member', maxChars: 20 },
  NLSEduMobileLoadError: { text: 'Failed to load class sessions.', description: 'Error message when loading sessions fails.', surface: 'member', maxChars: 40 },
  NLSEduMobileSessionRegister: { text: 'Session register', description: 'Default title for session register.', surface: 'member', maxChars: 24 },
  NLSEduMobileRegisterHint: { text: 'Everyone starts Present. Tap a letter to switch P → A → L. {count} enrolled.', description: 'Instructional hint for attendance register.', surface: 'member', maxChars: 80 },
  NLSEduMobileApprovedLeaveNotice: { text: 'On leave (approved by parent request)', description: 'Notice shown for locked approved leave row.', surface: 'member', maxChars: 44 },
  NLSEduMobileLoadRollError: { text: 'Failed to load session roll.', description: 'Error message when loading session roll fails.', surface: 'member', maxChars: 36 },
  NLSEduMobileSaveAttendanceError: { text: 'Failed to save attendance.', description: 'Error message when saving attendance fails.', surface: 'member', maxChars: 36 },
  NLSEduMobileFeesMarkPaid: { text: 'Mark paid', description: 'Button to mark a fee due as paid.', surface: 'member', maxChars: 15 },
  NLSEduMobileFeesReceiptSent: { text: 'Receipt #{receiptNo} issued to family.', description: 'Notice when fee payment is recorded and receipt issued.', surface: 'member', maxChars: 50 },
  NLSEduMobileFeesEmptyTitle: { text: 'No fee dues', description: 'Empty state title for mobile fees panel.', surface: 'member', maxChars: 20 },
  NLSEduMobileFeesEmptyBody: { text: 'All fee dues for this class are paid or up to date.', description: 'Empty state body for mobile fees panel.', surface: 'member', maxChars: 60 },
  NLSEduMobileFeesOverdueBadge: { text: 'Overdue', description: 'Overdue status badge text.', surface: 'member', maxChars: 12 },
  NLSEduMobileAdmissionsContacted: { text: 'Contacted', description: 'Button to mark enquiry as contacted.', surface: 'member', maxChars: 15 },
  NLSEduMobileAdmissionsAdmit: { text: 'Admit', description: 'Button to admit enquiry.', surface: 'member', maxChars: 12 },
  NLSEduMobileAdmissionsEmptyTitle: { text: 'No enquiries', description: 'Empty state title for admissions panel.', surface: 'member', maxChars: 20 },
  NLSEduMobileAdmissionsEmptyBody: { text: 'No active admissions enquiries found.', description: 'Empty state body for admissions panel.', surface: 'member', maxChars: 50 },
  NLSEduMobileTimetableToday: { text: 'Today', description: 'Toggle label to show today\'s timetable sessions.', surface: 'member', maxChars: 10 },
  NLSEduMobileTimetableWeek: { text: 'Week', description: 'Toggle label to show the week\'s timetable sessions.', surface: 'member', maxChars: 10 },
  NLSEduMobileTimetableEmptyTitle: { text: 'No sessions', description: 'Empty state title for the timetable panel.', surface: 'member', maxChars: 20 },
  NLSEduMobileTimetableEmptyBody: { text: 'No sessions scheduled for this period.', description: 'Empty state body for the timetable panel.', surface: 'member', maxChars: 50 },
  NLSEduMobileTimetableGapLabel: { text: 'Gap', description: 'Status pill for a session with no available teacher.', surface: 'member', maxChars: 10 },
  NLSEduMobileTimetableCovered: { text: 'Covered', description: 'Status pill for a session with a teacher assigned.', surface: 'member', maxChars: 12 },
  NLSEduMobileTimetableFindCover: { text: 'Find cover', description: 'Button to open free teacher list for cover assignment.', surface: 'member', maxChars: 16 },
  NLSEduMobileTimetableIssuePass: { text: 'Issue substitute pass', description: 'Button to issue a school field pass for substitute coverage.', surface: 'member', maxChars: 28 },
  NLSEduMobileTimetableNoFreeTeachers: { text: 'No teachers are free at this time.', description: 'Message when no teachers are available to cover a session gap.', surface: 'member', maxChars: 40 },
  NLSEduMobileTimetableGapBadge: { text: 'No teacher', description: 'Badge indicating timetable gap.', surface: 'member', maxChars: 15 },
  NLSEduMobileTimetableCoverAssigned: { text: 'Cover assigned: {name}', description: 'Notice when cover teacher is assigned.', surface: 'member', maxChars: 40 },
  NLSEduMobileFeedPostBtn: { text: 'Post', description: 'Button to post to class feed.', surface: 'member', maxChars: 10 },
  NLSEduMobileFeedCompose: { text: 'Compose', description: 'Button to open mobile feed composer.', surface: 'member', maxChars: 15 },
  NLSEduMobileFeedPosting: { text: 'Posting...', description: 'Posting in progress label.', surface: 'member', maxChars: 15 },
  NLSEduMobileFeedClassSelect: { text: 'Class', description: 'Class selection label in feed composer.', surface: 'member', maxChars: 15 },
  NLSEduMobileFeedAllClasses: { text: 'All classes', description: 'Option to post to all classes.', surface: 'member', maxChars: 20 },
  NLSEduMobileFeedRemoveFile: { text: 'Remove', description: 'Remove attached file.', surface: 'member', maxChars: 12 },
  NLSEduMobileFeedReach: { text: '{count} reach', description: 'Reach count for class feed post.', surface: 'member', maxChars: 20 },
  NLSEduMobileFeedLearnerTagsLabel: { text: 'Tag learners', description: 'Label for tagging learners in feed composer.', surface: 'member', maxChars: 20 },
  NLSEduMobileFeedEmptyTitle: { text: 'No feed posts', description: 'Empty state title for class feed panel.', surface: 'member', maxChars: 20 },
  NLSEduMobileFeedEmptyBody: { text: 'Share updates, activities and photos with the class.', description: 'Empty state body for class feed panel.', surface: 'member', maxChars: 65 },
  NLSHelperTabSchool: { text: 'School', description: 'Helper app tab for education school assignments.', surface: 'helper', maxChars: 18 },
  NLSTabLockedOffForRole: { text: 'Off for your role', description: 'Tab locked message when disabled for user role.', surface: 'member', maxChars: 30 },
  NLSTabLockedTurnedOffByAdmin: { text: 'Turned off by your admin', description: 'Tab locked message when disabled by workspace admin.', surface: 'member', maxChars: 40 },
  NLSMemberSubtabStripboard: { text: 'Stripboard', description: 'Member board secondary tab for stripboard.', surface: 'member', maxChars: 22 },
  NLSMemberSubtabReview: { text: 'Review', description: 'Member board secondary tab for review.', surface: 'member', maxChars: 20 },
  NLSMemberSubtabFolder: { text: 'Folder', description: 'Member board secondary tab for folder view.', surface: 'member', maxChars: 18 },
  NLSMemberSubtabHistory: { text: 'History', description: 'Member AI secondary tab for history.', surface: 'member', maxChars: 20 },
  NLSMemberSubtabCreate: { text: 'Create', description: 'Member AI secondary tab for create.', surface: 'member', maxChars: 18 },
  NLSMemberSubtabExecute: { text: 'Execute', description: 'Member AI secondary tab for execute.', surface: 'member', maxChars: 18 },
  NLSMemberSubtabPulse: { text: 'Pulse', description: 'Member AI secondary tab for pulse.', surface: 'member', maxChars: 18 },
  NLSMemberSubtabMagic: { text: 'Magic', description: 'Member AI secondary tab for AI Magic.', surface: 'member', maxChars: 18 },
  NLSMemberSubtabForms: { text: 'Forms', description: 'Member controls secondary tab for forms.', surface: 'member', maxChars: 18 },
  NLSMemberSubtabSync: { text: 'Sync', description: 'Member controls secondary tab for sync.', surface: 'member', maxChars: 18 },
  NLSMemberSubtabAutomations: { text: 'Automations', description: 'Member controls secondary tab for automations.', surface: 'member', maxChars: 24 },
  NLSMemberSubtabAudit: { text: 'Audit', description: 'Member controls secondary tab for audit.', surface: 'member', maxChars: 18 },
  NLSMemberSubtabMembers: { text: 'Members', description: 'Member controls secondary tab for member management.', surface: 'member', maxChars: 20 },
  NLSWorkspaceTabBarDefaultsTitle: { text: 'Phone tab bar', description: 'Title for workspace tab bar defaults settings card.', surface: 'settings', maxChars: 30 },
  NLSWorkspaceTabBarDefaultsSubtitle: { text: 'Default bottom tabs and role lock policy for phones.', description: 'Subtitle for workspace tab bar defaults settings card.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceTabBarDefaultsSave: { text: 'Save tab defaults', description: 'Save button label for workspace tab defaults.', surface: 'settings', maxChars: 30 },
  NLSWorkspaceTabBarDefaultsSaved: { text: 'Tab defaults updated.', description: 'Status message when workspace tab defaults are saved.', surface: 'settings', maxChars: 40 },
  NLSWorkspaceTabBarDefaultsReset: { text: 'Restore defaults', description: 'Reset button label for workspace tab defaults.', surface: 'settings', maxChars: 30 },
  NLSWorkspaceTabBarDefaultsRoleAdmin: { text: 'Admin', description: 'Admin role selector label for tab bar defaults.', surface: 'settings', maxChars: 15 },
  NLSWorkspaceTabBarDefaultsRoleEditor: { text: 'Editor', description: 'Editor role selector label for tab bar defaults.', surface: 'settings', maxChars: 15 },
  NLSWorkspaceTabBarDefaultsRoleViewer: { text: 'Viewer', description: 'Viewer role selector label for tab bar defaults.', surface: 'settings', maxChars: 15 },
  NLSWorkspaceTabBarDefaultsRoleHelper: { text: 'Helper app', description: 'Helper app selector label for tab bar defaults.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceTabBarDefaultsLockOffFor: { text: 'Lock off for this role', description: 'Header for role tab lock configuration.', surface: 'settings', maxChars: 35 },
  NLSWorkspaceTabBarDefaultsLockNone: { text: 'Unlocked', description: 'Option to leave tab unlocked for role.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceTabBarDefaultsLockHidden: { text: 'Hide completely', description: 'Option to hide tab completely for role.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceTabBarDefaultsLockVisibleOff: { text: 'Show locked', description: 'Option to show tab locked in more.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceTabBarDefaultsPreviewTitle: { text: 'Phone preview', description: 'Title for live phone tab bar preview.', surface: 'settings', maxChars: 30 },
  NLSWorkspaceTabBarDefaultsReadOnly: { text: 'Read-only tab bar defaults. Requires Edit workspace settings permission.', description: 'Notice for users who cannot edit workspace settings.', surface: 'settings', maxChars: 80 },
  NLSWorkspaceTabBarDefaultsRoleHeader: { text: 'Role layout & policy', description: 'Header for role target selection in tab defaults.', surface: 'settings', maxChars: 30 },
  NLSWorkspaceTabBarDefaultsRoleSubhead: { text: 'Configure default bottom tab bar and lock policies for phones.', description: 'Subhead for role target selection in tab defaults.', surface: 'settings', maxChars: 75 },
  NLSWorkspaceTabBarDefaultsRoleTargetAria: { text: 'Role target', description: 'Aria label for role target tablist in tab defaults.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceTabBarDefaultsPinnedHeader: { text: 'Default pinned tabs ({count}/4)', description: 'Header with count for pinned tab list.', surface: 'settings', maxChars: 40 },
  NLSWorkspaceTabBarDefaultsPinnedSubhead: { text: 'Top items appear directly in the bottom bar on phone screens.', description: 'Subhead for pinned tabs section.', surface: 'settings', maxChars: 75 },
  NLSWorkspaceTabBarDefaultsFixedTitle: { text: 'Fixed first tab', description: 'Tooltip or title for fixed first tab badge.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceTabBarDefaultsFixedBadge: { text: 'First', description: 'Badge text indicating fixed first tab.', surface: 'settings', maxChars: 15 },
  NLSWorkspaceTabBarDefaultsMoveUpAria: { text: 'Move {label} up', description: 'Aria label to move tab up in pinned list.', surface: 'settings', maxChars: 35 },
  NLSWorkspaceTabBarDefaultsMoveDownAria: { text: 'Move {label} down', description: 'Aria label to move tab down in pinned list.', surface: 'settings', maxChars: 35 },
  NLSWorkspaceTabBarDefaultsRemoveAria: { text: 'Remove {label}', description: 'Aria label to remove tab from pinned list.', surface: 'settings', maxChars: 35 },
  NLSWorkspaceTabBarDefaultsAvailableHeader: { text: 'Available tabs', description: 'Section header for available tabs to pin.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceTabBarDefaultsAvailableHint: { text: 'Click to add tab to the default pinned list.', description: 'Hint for available tabs section.', surface: 'settings', maxChars: 60 },
  NLSWorkspaceTabBarDefaultsLockHint: { text: 'Policy overrides for this role: hide completely or display as locked in More.', description: 'Hint describing tab locking behavior for role.', surface: 'settings', maxChars: 90 },
  NLSWorkspaceTabBarDefaultsLockModeAria: { text: 'Lock mode for {label}', description: 'Aria label for lock mode selector dropdown.', surface: 'settings', maxChars: 35 },
  NLSWorkspaceTabBarDefaultsPreviewHint: { text: 'Live resolution for {role} on phone.', description: 'Hint for live phone preview section.', surface: 'settings', maxChars: 50 },
  NLSWorkspaceTabBarDefaultsMockTabBarAria: { text: 'Mock bottom tab bar', description: 'Aria label for mock phone bottom tab bar.', surface: 'settings', maxChars: 30 },
  NLSWorkspaceTabBarDefaultsInMoreSheet: { text: 'In More sheet:', description: 'Label preceding tabs resolved into More sheet.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceTabBarDefaultsResetHint: { text: 'Reset {role} to built-in factory defaults.', description: 'Hint describing reset defaults action.', surface: 'settings', maxChars: 60 },
  NLSWorkspaceTabBarDefaultsSaveError: { text: 'Could not save tab defaults', description: 'Fallback error message when saving tab defaults fails.', surface: 'settings', maxChars: 45 },
  NLSMemberControlsSectionMembers: { text: 'Members', description: 'Member controls secondary tab for team members and invitations.', surface: 'member', maxChars: 20 },
  NLSMemberSubtabChat: { text: 'Chat', description: 'Member Nexus secondary tab for chat.', surface: 'member', maxChars: 18 },
  NLSMemberSubtabCalendar: { text: 'Calendar', description: 'Member Nexus secondary tab for calendar.', surface: 'member', maxChars: 20 },
  NLSMemberSubtabHuddle: { text: 'Huddle', description: 'Member Nexus secondary tab for huddles.', surface: 'member', maxChars: 20 },
  NLSMemberSubtabMeetings: { text: 'Meetings', description: 'Member Nexus secondary tab for meetings.', surface: 'member', maxChars: 22 },
  NLSMemberHuddleEyebrow: { text: 'Huddle', description: 'Member Huddle screen eyebrow.', surface: 'member', maxChars: 20 },
  NLSMemberHuddleLive: { text: 'Live', description: 'Member Huddle section heading for active huddles.', surface: 'member', maxChars: 18 },
  NLSMemberHuddleScheduled: { text: 'Scheduled', description: 'Member Huddle section heading and scheduled status.', surface: 'member', maxChars: 24 },
  NLSMemberHuddleRecent: { text: 'Recent', description: 'Member Huddle section heading for completed sessions.', surface: 'member', maxChars: 18 },
  NLSMemberHuddleStart: { text: 'Start huddle', description: 'Member action that starts a huddle in the active chat channel.', surface: 'member', maxChars: 28 },
  NLSMemberHuddleJoin: { text: 'Join huddle', description: 'Member action that joins the active huddle.', surface: 'member', maxChars: 28 },
  NLSMemberHuddleNoLiveTitle: { text: 'No live huddle', description: 'Member Huddle empty-state title.', surface: 'member', maxChars: 30 },
  NLSMemberHuddleNoLiveHint: { text: 'Start a lightweight session with the current channel.', description: 'Member Huddle empty-state hint.', surface: 'member', maxChars: 70 },
  NLSMemberHuddleNoScheduled: { text: 'No huddles scheduled', description: 'Member Huddle scheduled empty-state title.', surface: 'member', maxChars: 34 },
  NLSMemberHuddleNoRecent: { text: 'No recent huddles', description: 'Member Huddle recent empty-state title.', surface: 'member', maxChars: 30 },
  NLSMemberHuddleOpenChatFirst: { text: 'Open a chat channel before starting a huddle.', description: 'Member Huddle toast when no channel is available.', surface: 'member', maxChars: 60 },
  NLSMemberHuddleStarted: { text: 'Huddle started.', description: 'Member Huddle start success toast.', surface: 'member', maxChars: 24 },
  NLSMemberHuddleLeave: { text: 'Leave huddle', description: 'Member action that leaves the active huddle.', surface: 'member', maxChars: 22 },
  NLSMemberHuddleLeft: { text: 'You left the huddle.', description: 'Member Huddle leave success toast.', surface: 'member', maxChars: 32 },
  NLSMemberHuddleSpeaking: { text: 'Speaking', description: 'Member Huddle participant speaking status.', surface: 'member', maxChars: 20 },
  NLSMemberHuddleListening: { text: 'Listening', description: 'Member Huddle participant listening status.', surface: 'member', maxChars: 20 },
  NLSMemberHuddleUntitledMeeting: { text: 'Untitled huddle', description: 'Fallback title for a scheduled member huddle.', surface: 'member', maxChars: 28 },
  NLSMemberHuddleUntitledChannel: { text: 'Huddle', description: 'Fallback title for an ad-hoc member huddle session.', surface: 'member', maxChars: 20 },
  NLSMemberHuddleCompleted: { text: 'Completed', description: 'Status for a completed member huddle session.', surface: 'member', maxChars: 20 },
  NLSMemberHuddleInHuddle: { text: 'In huddle', description: 'Member Huddle live roster count label.', surface: 'member', maxChars: 20 },
  NLSMemberHuddleJoinFailed: { text: 'Huddle could not be joined.', description: 'Fallback error when joining a member huddle fails.', surface: 'member', maxChars: 40 },
  NLSMemberMeetingsEyebrow: { text: 'Meetings', description: 'Member Meetings screen eyebrow.', surface: 'member', maxChars: 22 },
  NLSMemberMeetingsTitle: { text: 'Meeting', description: 'Fallback title for a Member meeting details sheet.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsBusy: { text: 'Busy', description: 'Member Meetings hero chip label for upcoming meetings.', surface: 'member', maxChars: 18 },
  NLSMemberMeetingsInHuddle: { text: 'In huddle', description: 'Member Meetings hero chip label linking to Huddle.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsPast: { text: 'Past', description: 'Member Meetings section heading for historical meetings.', surface: 'member', maxChars: 18 },
  NLSMemberMeetingsEmptyTitle: { text: 'No meetings scheduled', description: 'Member Meetings empty-state title.', surface: 'member', maxChars: 34 },
  NLSMemberMeetingsEmptyHint: { text: 'Create a meeting or pull one in from Calendar.', description: 'Member Meetings empty-state hint.', surface: 'member', maxChars: 68 },
  NLSMemberMeetingsEnd: { text: "You've reached the end · {count} meeting(s)", description: 'Member Meetings historical list footer.', surface: 'member', maxChars: 52 },
  NLSMemberMeetingsUntitled: { text: 'Untitled meeting', description: 'Fallback title for a Member meeting card.', surface: 'member', maxChars: 28 },
  NLSMemberMeetingsNoNotes: { text: 'No notes yet', description: 'Member Meetings detail fallback for empty notes.', surface: 'member', maxChars: 24 },
  NLSMemberMeetingsNotesOpened: { text: 'Meeting notes opened.', description: 'Member Meetings notes action toast.', surface: 'member', maxChars: 32 },
  NLSMemberMeetingsOpenNotes: { text: 'Open notes', description: 'Member Meetings detail action.', surface: 'member', maxChars: 22 },
  NLSMemberMeetingsHideNotes: { text: 'Hide notes', description: 'Member Meetings detail action that hides the notes panel.', surface: 'member', maxChars: 22 },
  NLSMemberMeetingsMessageChannel: { text: 'Message channel', description: 'Member Meetings detail action.', surface: 'member', maxChars: 28 },
  NLSMemberMeetingsOpenBoard: { text: 'Open board', description: 'Member Meetings detail action.', surface: 'member', maxChars: 22 },
  NLSMemberMeetingsNoBoardLink: { text: 'No board item is linked.', description: 'Member Meetings toast when no agenda board item exists.', surface: 'member', maxChars: 42 },
  NLSMemberMeetingsSchedule: { text: 'Schedule', description: 'Member Meetings scheduling action.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsCreated: { text: 'Meeting scheduled.', description: 'Confirmation after creating a member meeting.', surface: 'member', maxChars: 36 },
  NLSMemberMeetingsCreateFailed: { text: 'Meeting could not be scheduled.', description: 'Fallback error after member meeting creation fails.', surface: 'member', maxChars: 44 },
  NLSMemberMeetingsCreating: { text: 'Scheduling...', description: 'Pending label while a member meeting is being created.', surface: 'member', maxChars: 24 },
  NLSMemberMeetingsType: { text: 'Type', description: 'Member meeting type field.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsTypeMeeting: { text: 'Meeting', description: 'Standard meeting type in the member scheduler.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsTypeHuddle: { text: 'Huddle', description: 'Scheduled huddle type in the member scheduler.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsDate: { text: 'Date', description: 'Member meeting date field.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsTime: { text: 'Time', description: 'Member meeting time field.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsDuration: { text: 'Duration', description: 'Member meeting duration field.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsDurationMinutes: { text: '{minutes} min', description: 'Member meeting duration option.', surface: 'member', maxChars: 20 },
  NLSMemberMeetingsParticipants: { text: 'Participants', description: 'Member meeting participants field.', surface: 'member', maxChars: 24 },
  NLSMemberMeetingsAddParticipant: { text: 'Add participant', description: 'Member meeting participant picker placeholder.', surface: 'member', maxChars: 30 },
  NLSMemberMeetingsRemoveParticipant: { text: 'Remove {name}', description: 'Removes a selected member meeting participant.', surface: 'member', maxChars: 48 },
  NLSMemberMeetingsScheduleTitle: { text: 'Schedule meeting', description: 'Member Meetings scheduling sheet title.', surface: 'member', maxChars: 30 },
  NLSMemberMeetingsFieldTitle: { text: 'Title', description: 'Member Meetings scheduling title field label.', surface: 'member', maxChars: 18 },
  NLSMemberMeetingsFieldNotes: { text: 'Notes', description: 'Member Meetings scheduling notes field label.', surface: 'member', maxChars: 18 },
  NLSMemberMeetingsTitlePlaceholder: { text: 'Meeting title', description: 'Member Meetings scheduling title placeholder.', surface: 'member', maxChars: 28 },
  NLSMemberMeetingsNotesPlaceholder: { text: 'Agenda or notes', description: 'Member Meetings scheduling notes placeholder.', surface: 'member', maxChars: 28 },
  NLSMemberMeetingsDraftStaged: { text: 'Meeting draft staged for scheduling.', description: 'Member Meetings scheduling stub success toast.', surface: 'member', maxChars: 48 },
  NLSMemberCalendarToday: { text: 'Today', description: 'Member calendar hero chip label.', surface: 'member', maxChars: 18 },
  NLSMemberCalendarHuddles: { text: 'Huddles', description: 'Member calendar hero chip label.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarHuddle: { text: 'Huddle', description: 'Member calendar event source label.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarMeeting: { text: 'Meeting', description: 'Member calendar event source label.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarOngoing: { text: 'Ongoing', description: 'Member calendar status for a live huddle.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarConfirmed: { text: 'Confirmed', description: 'Member calendar status for a future event.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarDone: { text: 'Done', description: 'Member calendar status for a completed event.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarAllDay: { text: 'All day', description: 'Member calendar time fallback.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarModeAria: { text: 'Calendar mode', description: 'Accessible label for Member calendar range controls.', surface: 'member', maxChars: 28 },
  NLSMemberCalendarDay: { text: 'Day', description: 'Member calendar day range control.', surface: 'member', maxChars: 16 },
  NLSMemberCalendarWeek: { text: 'Week', description: 'Member calendar week range control.', surface: 'member', maxChars: 18 },
  NLSMemberCalendarMonth: { text: 'Month', description: 'Member calendar month range control.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarEmptyTitle: { text: 'Your day is clear', description: 'Member calendar empty-state title.', surface: 'member', maxChars: 30 },
  NLSMemberCalendarEmptyReadOnlyHint: { text: 'Nothing is scheduled for this date.', description: 'Member calendar read-only empty-state hint.', surface: 'member', maxChars: 52 },
  NLSMemberCalendarEmptyHint: { text: 'Add a schedule item when you need one.', description: 'Member calendar editable empty-state hint.', surface: 'member', maxChars: 56 },
  NLSMemberCalendarAdd: { text: 'Add', description: 'Member calendar add action.', surface: 'member', maxChars: 16 },
  NLSMemberCalendarAddEvent: { text: 'Add event', description: 'Member calendar empty-state action.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarEvent: { text: 'Event', description: 'Fallback title for a Member calendar event sheet.', surface: 'member', maxChars: 18 },
  NLSMemberCalendarAddToTodos: { text: 'Add to todos', description: 'Member calendar event-sheet action.', surface: 'member', maxChars: 24 },
  NLSMemberCalendarMessageChannel: { text: 'Message channel', description: 'Member calendar event-sheet action.', surface: 'member', maxChars: 28 },
  NLSMemberCalendarTodoQueued: { text: 'Todo handoff queued.', description: 'Member calendar todo action toast.', surface: 'member', maxChars: 32 },
  NLSMemberCalendarSchedule: { text: 'Schedule', description: 'Member calendar scheduling sheet title.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarCreated: { text: 'Meeting scheduled.', description: 'Confirmation after creating a calendar meeting.', surface: 'member', maxChars: 36 },
  NLSMemberCalendarCreateFailed: { text: 'Meeting could not be scheduled.', description: 'Fallback error after calendar meeting creation fails.', surface: 'member', maxChars: 44 },
  NLSMemberCalendarCreating: { text: 'Scheduling...', description: 'Pending label while a calendar meeting is being created.', surface: 'member', maxChars: 24 },
  NLSMemberCalendarType: { text: 'Type', description: 'Calendar meeting type field.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarTypeMeeting: { text: 'Meeting', description: 'Standard meeting type in calendar scheduler.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarTypeHuddle: { text: 'Huddle', description: 'Scheduled huddle type in calendar scheduler.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarTime: { text: 'Time', description: 'Calendar meeting time field.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarDuration: { text: 'Duration', description: 'Calendar meeting duration field.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarDurationMinutes: { text: '{minutes} min', description: 'Calendar meeting duration option.', surface: 'member', maxChars: 20 },
  NLSMemberCalendarParticipants: { text: 'Participants', description: 'Calendar meeting participants field.', surface: 'member', maxChars: 24 },
  NLSMemberCalendarAddParticipant: { text: 'Add participant', description: 'Calendar meeting participant picker placeholder.', surface: 'member', maxChars: 30 },
  NLSMemberCalendarRemoveParticipant: { text: 'Remove {name}', description: 'Removes a selected calendar meeting participant.', surface: 'member', maxChars: 48 },
  NLSMemberCalendarTitle: { text: 'Title', description: 'Member calendar scheduling title field.', surface: 'member', maxChars: 18 },
  NLSMemberCalendarTitlePlaceholder: { text: 'Event title', description: 'Member calendar scheduling title placeholder.', surface: 'member', maxChars: 22 },
  NLSMemberCalendarDate: { text: 'Date', description: 'Member calendar scheduling date field.', surface: 'member', maxChars: 18 },
  NLSMemberCalendarNotes: { text: 'Notes', description: 'Member calendar scheduling notes field.', surface: 'member', maxChars: 18 },
  NLSMemberCalendarNotesPlaceholder: { text: 'Agenda, location, or attendees', description: 'Member calendar scheduling notes placeholder.', surface: 'member', maxChars: 42 },
  NLSMemberCalendarAttachTask: { text: 'Attach to task', description: 'Member calendar draft task attachment field.', surface: 'member', maxChars: 26 },
  NLSMemberCalendarNone: { text: 'None', description: 'Member calendar draft empty attachment option.', surface: 'member', maxChars: 18 },
  NLSMemberCalendarReadOnly: { text: 'Calendar is read-only for your role.', description: 'Member calendar read-only scheduling toast.', surface: 'member', maxChars: 48 },
  NLSMemberCalendarDraftStaged: { text: 'Calendar draft staged for the schedule flow.', description: 'Member calendar scheduling stub success toast.', surface: 'member', maxChars: 54 },
  NLSMemberAriaNavigation: { text: 'Member navigation', description: 'Accessible label for member navigation.', surface: 'member', maxChars: 40 },
  NLSMemberAriaTabs: { text: 'Member tabs', description: 'Accessible label for member bottom tabs.', surface: 'member', maxChars: 32 },
  NLSMemberAriaBoardSections: { text: 'Board sections', description: 'Accessible label for member board secondary tabs.', surface: 'member', maxChars: 36 },
  NLSMemberAriaNexusSections: { text: 'Nexus sections', description: 'Accessible label for member Nexus secondary tabs.', surface: 'member', maxChars: 38 },
  NLSMemberAriaAiSections: { text: 'AI Magic sections', description: 'Accessible label for member AI secondary tabs.', surface: 'member', maxChars: 42 },
  NLSMemberAriaControlSections: { text: 'Control sections', description: 'Accessible label for member controls tabs.', surface: 'member', maxChars: 40 },
  NLSMemberAriaOpenMenu: { text: 'Open menu', description: 'Accessible label for opening the member menu.', surface: 'member', maxChars: 26 },
  NLSMemberAriaOpenNotifications: { text: 'Open notifications', description: 'Accessible label for opening member notifications.', surface: 'member', maxChars: 38 },
  NLSMemberAriaUnread: { text: 'unread', description: 'Accessible label for unread member indicator dot.', surface: 'member', maxChars: 18 },
  NLSMemberNexusToday: { text: 'Today', description: 'Member Nexus chip label for today events.', surface: 'member', maxChars: 18 },
  NLSMemberNexusUnread: { text: 'Unread', description: 'Member Nexus chip label for unread chat threads.', surface: 'member', maxChars: 18 },
  NLSMemberNexusInHuddle: { text: 'In huddle', description: 'Member Nexus chip label for active huddles.', surface: 'member', maxChars: 20 },
  NLSMemberWorkspaceOwner: { text: 'Workspace owner', description: 'Fallback owner name in the member meeting participant picker.', surface: 'member', maxChars: 30 },
  NLSMemberWorkspaceEyebrow: { text: 'Member workspace', description: 'Eyebrow in the member desktop shell.', surface: 'member', maxChars: 36 },
  NLSMemberContextEyebrow: { text: 'Context', description: 'Eyebrow in the member sidecar.', surface: 'member', maxChars: 20 },
  NLSMemberContextFallback: { text: 'Select a board card to inspect basic element details.', description: 'Member sidecar fallback body.', surface: 'member', maxChars: 80 },
  NLSMemberLabelWorkspace: { text: 'Workspace', description: 'Member sidecar workspace label.', surface: 'member', maxChars: 24 },
  NLSMemberLabelMode: { text: 'Mode', description: 'Member sidecar mode label.', surface: 'member', maxChars: 18 },
  NLSMemberLabelType: { text: 'Type', description: 'Member sidecar type label.', surface: 'member', maxChars: 18 },
  NLSMemberLabelStatus: { text: 'Status', description: 'Member sidecar status label.', surface: 'member', maxChars: 18 },
  NLSMemberModeMember: { text: 'Member', description: 'Member app role label.', surface: 'member', maxChars: 18 },
  NLSMemberPanelOverview: { text: 'Overview', description: 'Member mobile overview panel title.', surface: 'member', maxChars: 24 },
  NLSMemberPanelIncomingInvites: { text: 'Incoming invites', description: 'Member mobile incoming invites panel title.', surface: 'member', maxChars: 34 },
  NLSMemberPanelProfile: { text: 'Profile', description: 'Member mobile profile panel title.', surface: 'member', maxChars: 24 },
  NLSMemberPanelProfileEyebrow: { text: 'Member profile', description: 'Member mobile profile panel eyebrow.', surface: 'member', maxChars: 34 },
  NLSMemberPanelSettings: { text: 'Member settings', description: 'Member mobile settings panel title.', surface: 'member', maxChars: 34 },
  NLSMemberNotificationTitle: { text: 'Notifications', description: 'Member notification drawer title.', surface: 'member', maxChars: 24 },
  NLSMemberNotificationClose: { text: 'Close notifications', description: 'Accessible notification drawer close label.', surface: 'member', maxChars: 36 },
  NLSMemberNotificationClear: { text: 'Clear', description: 'Clear a notification group.', surface: 'member', maxChars: 18 },
  NLSMemberNotificationClearAll: { text: 'Clear all', description: 'Clear all notifications action.', surface: 'member', maxChars: 18 },
  NLSMemberNotificationEmpty: { text: "You're all caught up.", description: 'Notification drawer empty state.', surface: 'member', maxChars: 42 },
  NLSMemberNotificationSettings: { text: 'Notification settings', description: 'Notification drawer settings action.', surface: 'member', maxChars: 34 },
  NLSMemberNotificationCleared: { text: 'Notification cleared.', description: 'Notification undo snackbar message.', surface: 'member', maxChars: 34 },
  NLSMemberNotificationsCleared: { text: 'Notifications cleared.', description: 'Bulk notification undo snackbar message.', surface: 'member', maxChars: 34 },
  NLSMemberNotificationConfirmTitle: { text: 'Clear all notifications?', description: 'Bulk notification clear confirmation title.', surface: 'member', maxChars: 42 },
  NLSMemberNotificationConfirmBody: {
    text: 'This clears the current notification list and cannot be undone.',
    description: 'Bulk notification clear confirmation body.',
    surface: 'member',
    maxChars: 90,
  },
  NLSMemberNodeDetailsTitle: { text: 'Details', description: 'Fallback title for the member node details sheet.', surface: 'member', maxChars: 24 },
  NLSMemberNodeDetailsDiscard: { text: 'Discard', description: 'Discard member node details changes.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsSave: { text: 'Save', description: 'Save member node details changes.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsSaving: { text: 'Saving...', description: 'Member node details save in progress.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsChecklist: { text: 'Checklist', description: 'Member node details checklist badge.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsFolder: { text: 'Folder', description: 'Member node details fallback badge.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsName: { text: 'Name', description: 'Member node details name label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsDescription: { text: 'Description', description: 'Member node details description label.', surface: 'member', maxChars: 24 },
  NLSMemberNodeDetailsStart: { text: 'Start', description: 'Member node details start label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsEnd: { text: 'End', description: 'Member node details end label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsStartDate: { text: 'Start date', description: 'Member node details start date accessibility label.', surface: 'member', maxChars: 24 },
  NLSMemberNodeDetailsEndDate: { text: 'End date', description: 'Member node details end date accessibility label.', surface: 'member', maxChars: 24 },
  NLSMemberNodeDetailsStatus: { text: 'Status', description: 'Member node details status label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsWorkflowStatus: { text: 'Workflow status', description: 'Member node details workflow status accessibility label.', surface: 'member', maxChars: 30 },
  NLSMemberNodeDetailsWeight: { text: 'Weight', description: 'Member node details weight label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsComplexity: { text: 'Complexity', description: 'Member node details complexity label.', surface: 'member', maxChars: 20 },
  NLSMemberNodeDetailsHours: { text: 'Hours', description: 'Member node details hours label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsCost: { text: 'Cost', description: 'Member node details cost label.', surface: 'member', maxChars: 18 },
  NLSMemberNotificationOpening: { text: 'Opening notification', description: 'Member notification action loading title.', surface: 'member', maxChars: 30 },
  NLSMemberNotificationOpeningOverlay: { text: 'Opening details...', description: 'Member notification overlay loading status.', surface: 'member', maxChars: 32 },
  NLSMemberNotificationMarkingRead: { text: 'Marking as read...', description: 'Member notification read-state loading status.', surface: 'member', maxChars: 32 },
  NLSMemberNotificationPreventingRepeat: { text: 'Preventing repeated taps...', description: 'Member notification loading status.', surface: 'member', maxChars: 42 },
  NLSMemberNotificationItemUnavailable: { text: 'That item is no longer available.', description: 'Member notification stale item message.', surface: 'member', maxChars: 52 },
  NLSMemberNodeDetailsSummary: { text: 'Item summary', description: 'Accessible member node details summary heading.', surface: 'member', maxChars: 24 },
  NLSMemberNodeDetailsDue: { text: 'Due', description: 'Member node details due-date label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsProgress: { text: 'Progress', description: 'Member node details progress label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsChecklistReadiness: { text: 'Checklist readiness', description: 'Member node details checklist readiness label.', surface: 'member', maxChars: 34 },
  NLSMemberNodeDetailsPriority: { text: 'Priority', description: 'Member node details priority label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsAssignee: { text: 'Assignee', description: 'Member node details checklist assignee label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsUnassigned: { text: 'Unassigned', description: 'Member node details unresolved assignee fallback.', surface: 'member', maxChars: 22 },
  NLSMemberNodeDetailsStatusTodo: { text: 'To do', description: 'Member node details TODO status label.', surface: 'member', maxChars: 18 },
  NLSMemberNodeDetailsStatusInProgress: { text: 'In progress', description: 'Member node details IN_PROGRESS status label.', surface: 'member', maxChars: 22 },
  NLSMemberNodeDetailsStatusComplete: { text: 'Complete', description: 'Member node details COMPLETE status label.', surface: 'member', maxChars: 18 },
  NLSMemberNotificationDismiss: { text: 'Dismiss', description: 'Member notification swipe-dismiss label.', surface: 'member', maxChars: 18 },
  NLSMemberNotificationBack: { text: 'Back to notifications', description: 'Member notification digest back action.', surface: 'member', maxChars: 34 },
  NLSMemberNotificationGroupActivity: { text: 'Activity', description: 'Member notification activity group heading.', surface: 'member', maxChars: 20 },
  NLSMemberNotificationGroupActivityEyebrow: { text: 'People · deadlines · chat', description: 'Member notification activity group description.', surface: 'member', maxChars: 42 },
  NLSMemberNotificationGroupAi: { text: 'AI', description: 'Member notification AI group heading.', surface: 'member', maxChars: 12 },
  NLSMemberNotificationGroupAiEyebrow: { text: 'AI work', description: 'Member notification AI group description.', surface: 'member', maxChars: 20 },
  NLSMemberNotificationGroupRewards: { text: 'Rewards', description: 'Member notification rewards group heading.', surface: 'member', maxChars: 20 },
  NLSMemberNotificationGroupRewardsEyebrow: { text: 'Streaks · achievements', description: 'Member notification rewards group description.', surface: 'member', maxChars: 38 },
  NLSMemberNotificationGroupSystem: { text: 'System', description: 'Member notification system group heading.', surface: 'member', maxChars: 20 },
  NLSMemberNotificationGroupSystemEyebrow: { text: 'Billing · security · updates', description: 'Member notification system group description.', surface: 'member', maxChars: 44 },
  NLSMemberPanelCommandPreferences: { text: 'Command preferences', description: 'Member mobile settings panel eyebrow.', surface: 'member', maxChars: 40 },
  NLSMemberWidgetCommandEyebrow: { text: 'Widget command', description: 'Member native-widget command panel eyebrow.', surface: 'capacitor', maxChars: 36 },
  NLSMemberWidgetBroadcastTitle: { text: 'Broadcast Alert', description: 'Member native-widget broadcast panel title.', surface: 'capacitor', maxChars: 38 },
  NLSMemberWidgetPriorityPing: { text: 'Priority ping', description: 'Member broadcast widget panel eyebrow.', surface: 'capacitor', maxChars: 30 },
  NLSMemberWidgetBroadcastConfirmTitle: { text: 'Confirm helper broadcast', description: 'Member broadcast widget confirmation title.', surface: 'capacitor', maxChars: 54 },
  NLSMemberWidgetBroadcastBody: {
    text: 'Send a high-priority field alert after backend delivery is connected. This confirmation screen prevents accidental widget taps.',
    description: 'Member broadcast widget body.',
    surface: 'capacitor',
    maxChars: 150,
  },
  NLSMemberWidgetBroadcastConfirmAction: { text: 'Confirm broadcast', description: 'Member broadcast widget confirmation action.', surface: 'capacitor', maxChars: 38 },
  NLSMemberWidgetQuickVouchTitle: { text: 'Quick Vouch', description: 'Member native-widget vouch panel title.', surface: 'capacitor', maxChars: 30 },
  NLSMemberWidgetPendingVerification: { text: 'Pending verification', description: 'Member vouch widget panel eyebrow.', surface: 'capacitor', maxChars: 42 },
  NLSMemberWidgetVouchCount: { text: '{count} items ready', description: 'Member vouch widget plural ready-count title. {count} is a number.', surface: 'capacitor', maxChars: 44 },
  NLSMemberWidgetVouchCountOne: { text: '{count} item ready', description: 'Member vouch widget singular ready-count title. {count} is 1.', surface: 'capacitor', maxChars: 44 },
  NLSMemberWidgetVouchBody: {
    text: 'Choose a completed checklist item, then process the vouch from its detail surface.',
    description: 'Member vouch widget body.',
    surface: 'capacitor',
    maxChars: 110,
  },
  NLSMemberWidgetNoVouches: { text: 'No completed helper work is waiting for vouching.', description: 'Member vouch widget empty state.', surface: 'capacitor', maxChars: 80 },
  NLSMemberWidgetWhisperTitle: { text: 'Whisper Bridge', description: 'Member native-widget voice panel title.', surface: 'capacitor', maxChars: 34 },
  NLSMemberWidgetVoiceToTask: { text: 'Voice-to-task', description: 'Member voice widget eyebrow.', surface: 'capacitor', maxChars: 32 },
  NLSMemberWidgetOpenAiComposerTitle: { text: 'Open AI Magic composer', description: 'Member voice widget title.', surface: 'capacitor', maxChars: 48 },
  NLSMemberWidgetVoiceBody: {
    text: 'Use the AI Magic agent as the voice-to-task landing surface. Native speech capture can be layered here after permission review.',
    description: 'Member voice widget body.',
    surface: 'capacitor',
    maxChars: 150,
  },
  NLSMemberWidgetOpenComposerAction: { text: 'Open composer', description: 'Member voice widget action.', surface: 'capacitor', maxChars: 34 },
  NLSMemberStatusCreated: { text: 'Created', description: 'Member checklist workflow status label.', surface: 'member', maxChars: 20 },
  NLSMemberStatusAssigned: { text: 'Assigned', description: 'Member checklist workflow status label.', surface: 'member', maxChars: 20 },
  NLSMemberStatusAccepted: { text: 'Accepted', description: 'Member checklist workflow status label.', surface: 'member', maxChars: 20 },
  NLSMemberStatusOngoing: { text: 'Ongoing', description: 'Member checklist workflow status label.', surface: 'member', maxChars: 20 },
  NLSMemberStatusAssessing: { text: 'Assessing', description: 'Member checklist workflow status label.', surface: 'member', maxChars: 22 },
  NLSMemberStatusCompleted: { text: 'Completed', description: 'Member checklist workflow status label.', surface: 'member', maxChars: 22 },
  NLSMemberStatusNotRequired: { text: 'Not Required', description: 'Member checklist workflow status label.', surface: 'member', maxChars: 28 },
  NLSMemberStatusPostponed: { text: 'Postponed', description: 'Member checklist workflow status label.', surface: 'member', maxChars: 22 },
  NLSMemberFilterAll: { text: 'All', description: 'Member stripboard all-status filter.', surface: 'member', maxChars: 14 },
  NLSMemberFilterAllHint: { text: 'Every checklist on the board', description: 'Member stripboard all-status filter hint.', surface: 'member', maxChars: 54 },
  NLSMemberStatusCreatedHint: { text: 'Not assigned yet', description: 'Member stripboard created status hint.', surface: 'member', maxChars: 42 },
  NLSMemberStatusAssignedHint: { text: 'Waiting for acceptance', description: 'Member stripboard assigned status hint.', surface: 'member', maxChars: 46 },
  NLSMemberStatusAcceptedHint: { text: 'Ready to start', description: 'Member stripboard accepted status hint.', surface: 'member', maxChars: 38 },
  NLSMemberStatusOngoingHint: { text: 'Currently moving', description: 'Member stripboard ongoing status hint.', surface: 'member', maxChars: 40 },
  NLSMemberStatusAssessingHint: { text: 'Ready for review', description: 'Member stripboard assessing status hint.', surface: 'member', maxChars: 42 },
  NLSMemberStatusCompletedHint: { text: 'Finished work', description: 'Member stripboard completed status hint.', surface: 'member', maxChars: 36 },
  NLSMemberStatusNotRequiredHint: { text: 'Skipped by rule', description: 'Member stripboard not-required status hint.', surface: 'member', maxChars: 38 },
  NLSMemberStatusPostponedHint: { text: 'Deferred work', description: 'Member stripboard postponed status hint.', surface: 'member', maxChars: 36 },
  NLSMemberChecklistUntitled: { text: 'Untitled checklist', description: 'Member checklist title fallback.', surface: 'member', maxChars: 36 },
  NLSMemberChecklistBoardParent: { text: 'Board checklist', description: 'Member checklist parent fallback for board-level work.', surface: 'member', maxChars: 34 },
  NLSMemberChecklistWorkspaceParent: { text: 'Workspace checklist', description: 'Member checklist parent fallback for workspace-level work.', surface: 'member', maxChars: 46 },
  NLSMemberChecklistNoDueDate: { text: 'No due date', description: 'Member checklist date fallback.', surface: 'member', maxChars: 28 },
  NLSMemberBoardDailyChecklist: { text: 'daily checklist', description: 'Member board card recurring daily eyebrow.', surface: 'member', maxChars: 34 },
  NLSMemberBoardWeeklyChecklist: { text: 'weekly checklist', description: 'Member board card recurring weekly eyebrow.', surface: 'member', maxChars: 34 },
  NLSMemberBoardChecklist: { text: 'checklist', description: 'Member board card checklist eyebrow.', surface: 'member', maxChars: 25 },
  NLSMemberBoardRoot: { text: 'Root', description: 'Member board root-chip label.', surface: 'member', maxChars: 14 },
  NLSMemberBoardNoDescription: { text: 'No description added.', description: 'Member board card description fallback.', surface: 'member', maxChars: 44 },
  NLSMemberBoardRequirementsAria: { text: 'Checklist requirements', description: 'Accessible label for member checklist requirement chips.', surface: 'member', maxChars: 42 },
  NLSMemberBoardProofNeeded: { text: 'Proof needed', description: 'Member checklist requirement chip.', surface: 'member', maxChars: 26 },
  NLSMemberBoardLocationNeeded: { text: 'Location needed', description: 'Member checklist requirement chip.', surface: 'member', maxChars: 30 },
  NLSMemberBoardReviewPending: { text: 'Review pending', description: 'Member checklist requirement chip.', surface: 'member', maxChars: 30 },
  NLSMemberBoardRecurring: { text: 'Recurring', description: 'Member board recurring chip label.', surface: 'member', maxChars: 24 },
  NLSMemberBoardStatusMenu: { text: 'Status', description: 'Member status menu heading.', surface: 'member', maxChars: 18 },
  NLSMemberBoardTitle: { text: 'Checklist workflow', description: 'Member stripboard title.', surface: 'member', maxChars: 40 },
  NLSMemberBoardEyebrow: { text: 'Stripboard', description: 'Member stripboard eyebrow.', surface: 'member', maxChars: 24 },
  NLSMemberBoardFolderEyebrow: { text: 'Folder', description: 'Member board folder eyebrow.', surface: 'member', maxChars: 24 },
  NLSMemberBoardFolderTitle: { text: 'Folders', description: 'Member board folder title.', surface: 'member', maxChars: 40 },
  NLSMemberBoardFolderCount: { text: '{count} items', description: 'Member board folder item count. {count} is a number.', surface: 'member', maxChars: 30 },
  NLSMemberBoardFolderCountOne: { text: '{count} item', description: 'Singular member board folder item count. {count} is 1.', surface: 'member', maxChars: 30 },
  NLSMemberBoardReviewQueueAria: { text: 'Member review queue', description: 'Accessible label for member review queue.', surface: 'member', maxChars: 40 },
  NLSMemberBoardReviewEyebrow: { text: 'Review command', description: 'Member review command eyebrow.', surface: 'member', maxChars: 24 },
  NLSMemberBoardReviewTitle: { text: 'Work awaiting sign-off', description: 'Member review title.', surface: 'member', maxChars: 40 },
  NLSMemberBoardReviewSummaryAria: { text: 'Review queue summary', description: 'Accessible label for review queue summary.', surface: 'member', maxChars: 40 },
  NLSMemberBoardReviewStatPending: { text: '{count} pending', description: 'Review queue pending count. {count} is a number.', surface: 'member', maxChars: 28 },
  NLSMemberBoardReviewStatPendingOne: { text: '{count} pending', description: 'Singular review queue pending count. {count} is 1.', surface: 'member', maxChars: 28 },
  NLSMemberBoardReviewStatReady: { text: '{count} ready', description: 'Review queue ready count. {count} is a number.', surface: 'member', maxChars: 28 },
  NLSMemberBoardReviewStatReadyOne: { text: '{count} ready', description: 'Singular review queue ready count. {count} is 1.', surface: 'member', maxChars: 28 },
  NLSMemberBoardReviewStatBlocked: { text: '{count} blocked', description: 'Review queue blocked count. {count} is a number.', surface: 'member', maxChars: 28 },
  NLSMemberBoardReviewStatBlockedOne: { text: '{count} blocked', description: 'Singular review queue blocked count. {count} is 1.', surface: 'member', maxChars: 28 },
  NLSMemberBoardReviewItemsAria: { text: 'Review items', description: 'Accessible label for review queue items.', surface: 'member', maxChars: 30 },
  NLSMemberBoardReviewSelectedAria: { text: 'Selected review item', description: 'Accessible label for selected review item inspector.', surface: 'member', maxChars: 36 },
  NLSMemberBoardSyncing: { text: 'Syncing...', description: 'Member board sync-in-progress badge.', surface: 'member', maxChars: 24 },
  NLSMemberBoardSynced: { text: 'Synced', description: 'Member board synced badge.', surface: 'member', maxChars: 18 },
  NLSMemberBoardMutable: { text: 'Checklist statuses mutable', description: 'Member board status-mutation availability badge.', surface: 'member', maxChars: 44 },
  NLSMemberBoardViewOnly: { text: 'View only', description: 'Member board read-only badge.', surface: 'member', maxChars: 20 },
  NLSMemberBoardAriaStripboard: { text: 'Member stripboard', description: 'Accessible label for the member stripboard.', surface: 'member', maxChars: 38 },
  NLSMemberBoardAriaStatuses: { text: 'Checklist statuses', description: 'Accessible label for checklist status filters.', surface: 'member', maxChars: 38 },
  NLSMemberBoardAriaChangeStatus: { text: 'Change status for {title}', description: 'Accessible label for a member board status action. {title} is checklist title.', surface: 'member', maxChars: 90 },
  NLSMemberBoardAriaStatusCount: {
    text: '{label}, {count} items',
    description: 'Accessible label for a status filter count. {label} is a status; {count} is a number.',
    surface: 'member',
    maxChars: 70,
  },
  NLSMemberBoardAriaStatusCountOne: {
    text: '{label}, {count} item',
    description: 'Accessible label for a singular status filter count. {label} is a status; {count} is 1.',
    surface: 'member',
    maxChars: 70,
  },
  NLSMemberBoardAriaItemsInStatus: {
    text: '{label} checklist items',
    description: 'Accessible label for member checklist list by status. {label} is the active filter.',
    surface: 'member',
    maxChars: 70,
  },
  NLSMemberBoardEmptyStatus: { text: 'No checklist items in {label}.', description: 'Member board empty state for a status filter. {label} is the active filter.', surface: 'member', maxChars: 72 },
  NLSMemberBoardToastStatusFailed: { text: 'Could not update checklist status.', description: 'Member board status-update fallback error toast.', surface: 'member', maxChars: 60 },
  NLSMemberBoardActionAccept: { text: 'Accept', description: 'Member board quick action to accept an assigned checklist.', surface: 'member', maxChars: 20 },
  NLSMemberBoardActionOpenDetail: { text: 'Open detail', description: 'Member board quick action to open checklist detail.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionCopyLink: { text: 'Copy link', description: 'Member board quick action to copy checklist link.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionMenuAria: {
    text: 'Actions for {title}',
    description: 'Accessible label for checklist card 3-dot action menu trigger. {title} is the checklist title.',
    surface: 'member',
    maxChars: 90,
  },
  NLSMemberBoardActionMenuTitle: { text: 'Actions', description: 'Tooltip / title for 3-dot action menu trigger.', surface: 'member', maxChars: 20 },
  NLSMemberBoardActionGroupJumpTo: { text: 'Jump to', description: 'Group label for deep link quick actions in member board card menu.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionDescription: { text: 'Description', description: 'Quick action to open checklist description.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionAssign: { text: 'Assign', description: 'Quick action to open checklist assignment section.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionReassign: { text: 'Reassign', description: 'Quick action to open checklist assignment section when already assigned.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionProof: { text: 'Attach proof', description: 'Quick action to open checklist proof section.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionDates: { text: 'Due date / Schedule', description: 'Quick action to open checklist schedule section.', surface: 'member', maxChars: 30 },
  NLSMemberBoardActionResources: { text: 'Resources', description: 'Quick action to open checklist resources section.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionCompletionCriteria: { text: 'Completion criteria', description: 'Quick action to open checklist completion criteria section.', surface: 'member', maxChars: 30 },
  NLSMemberBoardActionSignals: { text: 'Signals', description: 'Quick action to open checklist signals section.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionFieldPass: { text: 'Field pass', description: 'Quick action to open checklist field pass section.', surface: 'member', maxChars: 24 },
  NLSMemberBoardActionAttachedForms: { text: 'Attached forms', description: 'Quick action to open checklist attached forms section.', surface: 'member', maxChars: 24 },
  NLSMemberAiEyebrow: { text: 'AI Magic', description: 'Eyebrow for member AI Magic surface.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryTitle: { text: 'History', description: 'Title of the member AI Magic history tab.', surface: 'member', maxChars: 20 },
  NLSMemberAiHistoryFilterAria: { text: 'History mode filter', description: 'Accessible label for history mode segmented control.', surface: 'member', maxChars: 40 },
  NLSMemberAiHistoryModeCreate: { text: 'Create', description: 'Label for Create mode in AI history.', surface: 'member', maxChars: 20 },
  NLSMemberAiHistoryModeExecute: { text: 'Execute', description: 'Label for Execute mode in AI history.', surface: 'member', maxChars: 20 },
  NLSMemberAiHistorySearchLabel: { text: 'Search chats', description: 'Floating input label and placeholder for searching AI history chats.', surface: 'member', maxChars: 30 },
  NLSMemberAiHistoryActionOpen: { text: 'Open chat', description: 'Accessible label for open chat action button.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryActionArchive: { text: 'Archive chat', description: 'Accessible label for archive chat action button.', surface: 'member', maxChars: 28 },
  NLSMemberAiHistoryActionDelete: { text: 'Delete chat', description: 'Accessible label for delete chat action button.', surface: 'member', maxChars: 28 },
  NLSMemberAiHistoryEmptyTitle: { text: 'No matching AI chats', description: 'Empty state title when no AI chats match filter or query.', surface: 'member', maxChars: 44 },
  NLSMemberAiHistoryEmptyHint: { text: 'Start a new chat or clear the filter.', description: 'Empty state hint when no AI chats match.', surface: 'member', maxChars: 80 },
  NLSMemberAiHistoryFabNew: { text: 'New chat', description: 'Floating action button label to start a new AI chat.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryPreviewTitle: { text: 'AI chat preview', description: 'Fallback title for AI chat bottom sheet preview.', surface: 'member', maxChars: 36 },
  NLSMemberAiHistoryPreviewFirstAsk: { text: 'First ask', description: 'Heading for first user turn in AI chat preview.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryPreviewLastAnswer: { text: 'Last answer', description: 'Heading for last assistant turn in AI chat preview.', surface: 'member', maxChars: 26 },
  NLSMemberAiHistoryPreviewNoUserTurn: { text: 'No user turn saved.', description: 'Fallback text when no user turn exists in preview.', surface: 'member', maxChars: 44 },
  NLSMemberAiHistoryPreviewNoAnswer: { text: 'No answer saved.', description: 'Fallback text when no assistant turn exists in preview.', surface: 'member', maxChars: 40 },
  NLSMemberAiHistoryPreviewOpenAction: { text: 'Open', description: 'Action button in preview bottom sheet to open the full chat.', surface: 'member', maxChars: 16 },
  NLSMemberAiHistoryGroupToday: { text: 'Today', description: 'AI chat history date group label for today.', surface: 'member', maxChars: 16 },
  NLSMemberAiHistoryGroupYesterday: { text: 'Yesterday', description: 'AI chat history date group label for yesterday.', surface: 'member', maxChars: 22 },
  NLSMemberAiHistoryGroupThisWeek: { text: 'This week', description: 'AI chat history date group label for this week.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryGroupOlder: { text: 'Older', description: 'AI chat history date group label for older chats.', surface: 'member', maxChars: 18 },
  NLSMemberAiHistoryTimeRecently: { text: 'recently', description: 'Relative time fallback when timestamp is missing.', surface: 'member', maxChars: 20 },
  NLSMemberAiHistoryTimeJustNow: { text: 'just now', description: 'Relative time label for less than a minute ago.', surface: 'member', maxChars: 20 },
  NLSMemberAiHistoryTimeMinutesAgo: { text: '{count}m ago', description: 'Relative time label in minutes. {count} is a number.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryTimeHoursAgo: { text: '{count}h ago', description: 'Relative time label in hours. {count} is a number.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryTimeDaysAgo: { text: '{count}d ago', description: 'Relative time label in days. {count} is a number.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryTurnsCount: { text: '{count} turns', description: 'Turn count label in AI chat card subtitle. {count} is a number.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryTurnsCountOne: { text: '{count} turn', description: 'Singular turn count label in AI chat card subtitle.', surface: 'member', maxChars: 24 },
  NLSMemberAiHistoryWorkspaceCreditsTitle: { text: 'Workspace credits', description: 'Title tooltip for workspace credits chip.', surface: 'member', maxChars: 36 },
  NLSMemberAiHistoryAriaSessionSub: { text: '{mode} - {turns} - {time}', description: 'Subtitle formatting for AI chat session card.', surface: 'member', maxChars: 90 },
  NLSMemberAiHistoryArchiveConfirmTitle: { text: 'Archive chat?', description: 'Title for archive chat confirmation sheet.', surface: 'member', maxChars: 30 },
  NLSMemberAiHistoryArchiveConfirmBody: {
    text: 'This chat will be archived and hidden from your active history.',
    description: 'Body text for archive chat confirmation sheet.',
    surface: 'member',
    maxChars: 100,
  },
  NLSMemberAiHistoryArchiveConfirmAction: { text: 'Archive', description: 'Action button to confirm archiving an AI chat.', surface: 'member', maxChars: 20 },
  NLSMemberAiHistoryDeleteConfirmTitle: { text: 'Delete chat?', description: 'Title for delete chat confirmation sheet.', surface: 'member', maxChars: 30 },
  NLSMemberAiHistoryDeleteConfirmBody: {
    text: 'This chat and all of its messages will be permanently deleted.',
    description: 'Body text for delete chat confirmation sheet.',
    surface: 'member',
    maxChars: 100,
  },
  NLSMemberAiHistoryDeleteConfirmAction: { text: 'Delete', description: 'Action button to confirm deleting an AI chat.', surface: 'member', maxChars: 20 },
  NLSMemberAiHistoryFabAriaLabel: { text: 'New chat', description: 'Accessible label for the new chat floating action button.', surface: 'member', maxChars: 24 },
  NLSMemberControlsEyebrow: { text: 'Controls', description: 'Member controls hub eyebrow.', surface: 'member', maxChars: 24 },
  NLSMemberControlsTitle: { text: 'Workspace controls', description: 'Member controls hub title.', surface: 'member', maxChars: 42 },
  NLSMemberControlsAuditRestrictedTitle: { text: 'Audit is restricted', description: 'Member controls restricted audit heading.', surface: 'member', maxChars: 44 },
  NLSMemberControlsAuditRestrictedBody: { text: 'Admin or Editor access is required.', description: 'Member controls restricted audit body.', surface: 'member', maxChars: 60 },
  NLSMemberControlsInvitesRestrictedTitle: { text: 'Invites are restricted', description: 'Member controls restricted invites heading.', surface: 'member', maxChars: 44 },
  NLSMemberControlsInvitesRestrictedBody: { text: 'Workspace invite management requires permission.', description: 'Member controls restricted invites body.', surface: 'member', maxChars: 71 },
  NLSMemberNativeUnavailableEyebrow: { text: 'Member app unavailable', description: 'Native member app unavailable eyebrow.', surface: 'capacitor', maxChars: 44 },
  NLSMemberNativeUnavailableTitle: { text: 'Workspace under maintenance', description: 'Native member app unavailable title.', surface: 'capacitor', maxChars: 54 },
  NLSMemberNativeUnavailableBody: { text: 'No member workspace is available for this account right now.', description: 'Native member app unavailable body.', surface: 'capacitor', maxChars: 90 },
  NLSHelperTabTodos: { text: 'Todos', description: 'Helper app primary tab for assigned work.', surface: 'helper', maxChars: 18 },
  NLSHelperTabCalendar: { text: 'Calendar', description: 'Helper app primary tab for calendar.', surface: 'helper', maxChars: 20 },
  NLSHelperTabMeetings: { text: 'Meetings', description: 'Helper app primary tab for meetings.', surface: 'helper', maxChars: 22 },
  NLSHelperTabChat: { text: 'Chat', description: 'Helper app primary tab for chat.', surface: 'helper', maxChars: 18 },
  NLSHelperTabControls: { text: 'Controls', description: 'Helper app primary tab for controls.', surface: 'helper', maxChars: 20 },
  NLSHelperTabField: { text: 'Field', description: 'Helper app primary tab for field work and inventory.', surface: 'helper', maxChars: 18 },
  NLSHelperTabSite: { text: 'Site', description: 'Helper bottom tab label when only Construction sector is active.', surface: 'helper', maxChars: 18 },
  NLSHelperTabWard: { text: 'Ward', description: 'Helper bottom tab label when only Hospital sector is active.', surface: 'helper', maxChars: 18 },
  NLSHelperTabWork: { text: 'Work', description: 'Helper bottom tab label when multiple sectors or general operations are active.', surface: 'helper', maxChars: 18 },
  NLSHelperSubtabToday: { text: 'Today', description: 'Helper field today schedule subtab.', surface: 'helper', maxChars: 18 },
  NLSHelperSubtabScan: { text: 'Scan', description: 'Helper controls scan subtab.', surface: 'helper', maxChars: 18 },
  NLSHelperSubtabCount: { text: 'Count', description: 'Helper controls stock count subtab.', surface: 'helper', maxChars: 18 },
  NLSHelperSubtabMe: { text: 'Me', description: 'Helper controls personal performance subtab.', surface: 'helper', maxChars: 18 },
  NLSHelperSubtabForms: { text: 'Forms', description: 'Helper controls forms subtab.', surface: 'helper', maxChars: 18 },
  NLSHelperSubtabSync: { text: 'Sync', description: 'Helper controls sync subtab.', surface: 'helper', maxChars: 18 },
  NLSHelperFieldSubtabsAria: { text: 'Field subtabs', description: 'Accessible label for helper field subtab strip.', surface: 'helper', maxChars: 32 },
  NLSHelperFieldDefaultWorkspace: { text: 'Workspace', description: 'Fallback workspace label for helper field app bar.', surface: 'helper', maxChars: 24 },
  NLSHelperFieldQueuedCount: { text: '{count} queued', description: 'Queued mutations badge in helper field app bar.', surface: 'helper', maxChars: 20 },
  NLSHelperFieldScheduleTitle: { text: 'Schedule', description: 'Title for today schedule section in helper field.', surface: 'helper', maxChars: 24 },
  NLSHelperFieldNoSchedule: { text: 'No assigned tasks for today', description: 'Empty state for today schedule in helper field.', surface: 'helper', maxChars: 50 },
  NLSHelperFieldSyncQueueTitle: { text: 'Sync queue', description: 'Title for outbox sync queue in helper field today.', surface: 'helper', maxChars: 24 },
  NLSHelperFieldSyncQueueEmpty: { text: 'All changes synced', description: 'Empty state for sync queue in helper field today.', surface: 'helper', maxChars: 40 },
  NLSHelperFieldBlockersTitle: { text: 'Blockers raised today', description: 'Title for blockers raised today in helper field today.', surface: 'helper', maxChars: 36 },
  NLSHelperFieldNoBlockers: { text: 'No blockers raised', description: 'Empty state for blockers in helper field today.', surface: 'helper', maxChars: 36 },
  NLSHelperFieldSyncRetry: { text: 'Retry sync', description: 'Action button to retry failed or waiting outbox items.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsTabTitle: { text: 'Operations', description: 'Helper bottom tab title for operations work.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsCheckInSectionTitle: { text: 'Place check-in', description: 'Section title for place QR scanning.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsScanPlaceBtn: { text: 'Scan place', description: 'Button to open barcode scanner for place QR code.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsScanPlaceDesc: { text: 'Scan place QR code to view details, dues, or requests.', description: 'Description explaining place scan action.', surface: 'helper', maxChars: 70 },
  NLSHelperOpsDueSectionTitle: { text: 'My due checks', description: 'Section title for scheduled due checks.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsDuesEmpty: { text: 'No checks due right now.', description: 'Empty state when no dues are scheduled or overdue.', surface: 'helper', maxChars: 40 },
  NLSHelperOpsRecordNowBtn: { text: 'Record', description: 'Button to record data for a due check.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsScanCheckpointBtn: { text: 'Checkpoint scan', description: 'Button to record a checkpoint QR scan for a due.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsSkipBtn: { text: 'Skip', description: 'Button to skip a due check.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsSkipTitle: { text: 'Skip due check', description: 'Title for skip due dialog.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsSkipReasonPlaceholder: { text: 'Reason for skipping check', description: 'Placeholder for due skip reason input.', surface: 'helper', maxChars: 40 },
  NLSHelperOpsSkipConfirm: { text: 'Confirm skip', description: 'Confirmation button to skip a due check.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsRequestsSectionTitle: { text: 'Requests', description: 'Section title for raising and viewing ops requests.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsRaiseRequestTitle: { text: 'Raise request', description: 'Title for raising an operations issue or request.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsKindPickerPlaceholder: { text: 'Select request kind', description: 'Placeholder for picking request kind.', surface: 'helper', maxChars: 30 },
  NLSHelperOpsPlacePickerPlaceholder: { text: 'Select place', description: 'Placeholder for picking request place.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsRequestTextPlaceholder: { text: 'Describe what needs attention...', description: 'Placeholder for request description.', surface: 'helper', maxChars: 50 },
  NLSHelperOpsSubmitRequestBtn: { text: 'Submit request', description: 'Button to submit a new ops request.', surface: 'helper', maxChars: 22 },
  NLSHelperOpsMyRequestsTitle: { text: 'My requests', description: 'Title for list of user authored/assigned requests.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsMyRequestsEmpty: { text: 'No requests raised or assigned.', description: 'Empty state when user has no requests.', surface: 'helper', maxChars: 45 },
  NLSHelperOpsFilterUnitLabel: { text: 'Filter by unit', description: 'Label for unit filter in helper due checks.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsAllUnits: { text: 'All units', description: 'Option to show all units in helper due checks filter.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsStartRequestBtn: { text: 'Start', description: 'Button to start an assigned request.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsDoneRequestBtn: { text: 'Done', description: 'Button to mark an assigned request done.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsDonePhotoTitle: { text: 'Attach completion photo', description: 'Title for photo step when marking request done.', surface: 'helper', maxChars: 30 },
  NLSHelperOpsDonePhotoRequired: { text: 'A completion photo is required for this request.', description: 'Error message when marking request done without a required photo.', surface: 'helper', maxChars: 50 },
  NLSHelperOpsDonePhotoChoose: { text: 'Choose photo', description: 'Label for choosing a photo when completing a request.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsMarkDoneTitle: { text: 'Mark done', description: 'Title for mark done dialog in helper.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsMarkDoneConfirm: { text: 'Mark this request as done?', description: 'Confirmation prompt to mark request done.', surface: 'helper', maxChars: 35 },
  NLSHelperOpsRollSectionTitle: { text: 'Attendance roll', description: 'Section title for today worker attendance roll.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsRollEmpty: { text: 'No workers scheduled for roll today.', description: 'Empty state when no roster shifts are found today.', surface: 'helper', maxChars: 50 },
  NLSHelperOpsMarkPresent: { text: 'Present', description: 'Button to mark worker present in roll.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsMarkAbsent: { text: 'Absent', description: 'Button to mark worker absent in roll.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsMarkLate: { text: 'Late', description: 'Button to mark worker late in roll.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsQueueSectionTitle: { text: 'Offline sync queue', description: 'Section title for pending and refused ops mutations.', surface: 'helper', maxChars: 28 },
  NLSHelperOpsQueuePendingCount: { text: '{count} ops changes waiting to sync', description: 'Status message for pending queue items.', surface: 'helper', maxChars: 45 },
  NLSHelperOpsQueueRefusedTitle: { text: 'Refused sync items', description: 'Title for refused ops mutations list.', surface: 'helper', maxChars: 28 },
  NLSHelperOpsQueueRefusedEmpty: { text: 'No refused items.', description: 'Empty state when there are no refused mutations.', surface: 'helper', maxChars: 30 },
  NLSHelperOpsQueueDismissBtn: { text: 'Dismiss', description: 'Button to dismiss a refused outbox mutation.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsQueueRefusedFallback: { text: 'Permission denied or item not allowed on phone', description: 'Fallback message for refused mutation.', surface: 'helper', maxChars: 60 },
  NLSHelperOpsNoAllowedItems: { text: 'No operations tools are assigned to your account in this workspace.', description: 'Message when helper has no allowed ops items.', surface: 'helper', maxChars: 80 },
  NLSHelperOpsStatusOpen: { text: 'Open', description: 'Status label for open request.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsStatusInProgress: { text: 'In progress', description: 'Status label for in progress request.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsStatusResolved: { text: 'Resolved', description: 'Status label for resolved request.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsStatusClosed: { text: 'Closed', description: 'Status label for closed request.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsPlaceScanNotAllowed: { text: 'Operations actions are not available for your role on this phone.', description: 'Notice on place scan page when helper holds no allowed items.', surface: 'helper', maxChars: 80 },
  NLSHelperOpsRequestSubmitted: { text: 'Request submitted.', description: 'Success message after submitting an operations request.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsNoFieldsInTemplate: { text: 'No fields configured for this checklist.', description: 'Empty state message when a template version has no input fields.', surface: 'helper', maxChars: 50 },
  NLSHelperOpsRequiredFieldMissing: { text: '{field} is required.', description: 'Error message when a required form field is left empty.', surface: 'helper', maxChars: 40 },
  NLSHelperOpsPhotoOfflineDisabled: { text: 'Photo upload requires an internet connection.', description: 'Reason message explaining why photo upload is disabled when offline.', surface: 'helper', maxChars: 50 },
  NLSHelperOpsBmwTitle: { text: 'Biomedical Waste Entry', description: 'Section title for Helper operations biomedical waste entry.', surface: 'helper', maxChars: 28 },
  NLSHelperOpsBmwDesc: { text: 'Daily waste category weights and storage status.', description: 'Description for Helper operations biomedical waste entry.', surface: 'helper', maxChars: 60 },
  NLSHelperOpsBmwKg: { text: 'Weight (kg)', description: 'Label for BMW weight input in kg.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsBmwBags: { text: 'Bags (opt)', description: 'Label for optional BMW bag count input.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsBmwSaveDraft: { text: 'Save Draft', description: 'Button to save BMW daily draft.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsBmwSubmitted: { text: 'Day Locked (Submitted)', description: 'Badge indicating today BMW record has been locked.', surface: 'helper', maxChars: 28 },
  NLSHelperOpsBmwNeedsConnection: { text: 'Submit requires an active internet connection.', description: 'Hint explaining submit is disabled when offline.', surface: 'helper', maxChars: 50 },
  NLSHelperOpsBmwFacility: { text: 'Facility', description: 'Label for facility picker in BMW entry section.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsBmwNoFacilities: { text: 'No facilities available for BMW entry today.', description: 'Notice when no facilities are available for BMW entry.', surface: 'helper', maxChars: 50 },
  NLSOpsHosBmwDailyDraftSaved: { text: 'BMW daily draft saved.', description: 'Success notice when BMW daily draft is saved.', surface: 'shell', maxChars: 30 },
  NLSOpsHosBmwDailyLocked: { text: 'BMW record submitted and locked.', description: 'Success notice when BMW daily record is submitted.', surface: 'shell', maxChars: 40 },
  NLSOpsHosBmwCorrectionSaved: { text: 'BMW correction submitted.', description: 'Success notice when BMW correction is submitted.', surface: 'shell', maxChars: 30 },
  NLSOpsHosBmwConfirmAndSubmit: { text: 'Confirm & Submit', description: 'Button label to confirm unusual weight and submit BMW record.', surface: 'shell', maxChars: 24 },
  NLSHelperOpsOnCallTitle: { text: 'My On-Call Duty', description: 'Section title for Helper operations on-call duty schedule.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsOnCallDesc: { text: 'Upcoming on-call duty slots and assignment acknowledgement.', description: 'Description for Helper operations on-call duty schedule.', surface: 'helper', maxChars: 70 },
  NLSHelperOpsOnCallAckBtn: { text: 'Acknowledge', description: 'Button to acknowledge an on-call duty assignment.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsOnCallAcked: { text: 'Acknowledged', description: 'Status label for acknowledged on-call assignment.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsOnCallPending: { text: 'Pending acknowledgement', description: 'Status label for unacknowledged on-call assignment.', surface: 'helper', maxChars: 30 },
  NLSHelperOpsOnCallEmpty: { text: 'No upcoming on-call duty slots.', description: 'Empty state when caller has no on-call duty slots in next 7 days.', surface: 'helper', maxChars: 40 },
  NLSHelperOpsOnCallNeedsConnection: { text: 'Acknowledgement requires an active internet connection.', description: 'Notice explaining on-call acknowledge is disabled offline.', surface: 'helper', maxChars: 60 },
  NLSHelperOpsOnCallStaleRoster: { text: 'Roster changed, refreshing schedule.', description: 'Notice shown when an on-call slot version is stale and reloaded.', surface: 'helper', maxChars: 50 },
  NLSHelperOpsDprTitle: { text: 'Daily Progress Report', description: 'Title for Helper DPR section.', surface: 'helper', maxChars: 28 },
  NLSHelperOpsDprDesc: { text: 'Record daily site progress, quantities and photos.', description: 'Description for Helper DPR section.', surface: 'helper', maxChars: 60 },
  NLSHelperOpsDprProjectLabel: { text: 'Project', description: 'Label for project selector in Helper DPR.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsDprShiftLabel: { text: 'Shift', description: 'Label for shift selector in Helper DPR.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsDprDateLabel: { text: 'Date', description: 'Label for date display in Helper DPR.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsDprReturnedTitle: { text: 'Returned for correction', description: 'Banner title for returned DPR reason.', surface: 'helper', maxChars: 30 },
  NLSHelperOpsDprLineHeader: { text: 'Line {index}', description: 'Header for DPR production line.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsDprRemoveLineBtn: { text: 'Remove', description: 'Button to remove a DPR line.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsDprPlaceLabel: { text: 'Location', description: 'Label for place picker in DPR line.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsDprTradeLabel: { text: 'Trade', description: 'Label for trade input in DPR line.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsDprPackageLabel: { text: 'Package', description: 'Label for package select in DPR line.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsDprActivityLabel: { text: 'Activity', description: 'Label for activity input in DPR line.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsDprPlannedQtyLabel: { text: 'Planned Qty', description: 'Label for planned quantity input in DPR line.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsDprDoneQtyLabel: { text: 'Done Qty', description: 'Label for done quantity input in DPR line.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsDprPhotosLabel: { text: 'Photos ({count}/20)', description: 'Label for photo attachments in DPR line.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsDprAddLineBtn: { text: 'Add line', description: 'Button to add a production line.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsDprSaveDraftBtn: { text: 'Save Draft', description: 'Button to save DPR draft.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsDprSubmitBtn: { text: 'Submit DPR', description: 'Button to submit DPR report.', surface: 'helper', maxChars: 18 },
  NLSHelperOpsDprDraftSaved: { text: 'DPR draft saved.', description: 'Status message when DPR draft is saved.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsDprSubmitted: { text: 'DPR submitted.', description: 'Status message when DPR is submitted.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsDprShiftDay: { text: 'Day', description: 'Day shift label in Helper DPR selector.', surface: 'helper', maxChars: 12 },
  NLSHelperOpsDprShiftNight: { text: 'Night', description: 'Night shift label in Helper DPR selector.', surface: 'helper', maxChars: 12 },
  NLSHelperOpsDprShiftSingle: { text: 'Single', description: 'Single shift label in Helper DPR selector.', surface: 'helper', maxChars: 12 },
  NLSHelperOpsDprNone: { text: '(None)', description: 'None option label in Helper DPR selector.', surface: 'helper', maxChars: 12 },
  NLSHelperOpsDprPlaceNotFound: { text: 'Place not found.', description: 'Error shown when scanned place token cannot be found.', surface: 'helper', maxChars: 30 },
  NLSHelperOpsDprPlaceOutsideSite: { text: 'Place is outside project site.', description: 'Error shown when scanned place is outside project site.', surface: 'helper', maxChars: 40 },
  NLSHelperOpsDprOfflineNoCacheHint: { text: 'Offline — no cached report available.', description: 'Hint shown when offline with no cached DPR.', surface: 'helper', maxChars: 45 },
  NLSHelperOpsDprPhotoAlt: { text: 'Line photo {index}', description: 'Alt text for DPR line photo preview.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsScanPlaceTitle: { text: 'Scan Place', description: 'Modal title for barcode place scanner.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsHindranceSectionTitle: { text: 'Raise hindrance', description: 'Section title for hindrance reporting.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsHindranceSectionDesc: { text: 'Report a site obstacle, delay or blocker.', description: 'Description for hindrance reporting.', surface: 'helper', maxChars: 60 },
  NLSHelperOpsHindranceProjectLabel: { text: 'Project', description: 'Label for project selector in hindrance form.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsHindrancePlaceLabel: { text: 'Location', description: 'Label for location in hindrance form.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsHindranceDescLabel: { text: 'Description', description: 'Label for hindrance description input.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsHindrancePartyLabel: { text: 'Responsible party', description: 'Label for responsible party select in hindrance form.', surface: 'helper', maxChars: 24 },
  NLSHelperOpsHindranceImpactLabel: { text: 'Impact', description: 'Label for impact select in hindrance form.', surface: 'helper', maxChars: 16 },
  NLSHelperOpsHindranceSubmitBtn: { text: 'Raise hindrance', description: 'Button to submit hindrance report.', surface: 'helper', maxChars: 20 },
  NLSHelperOpsHindranceSuccess: { text: 'Hindrance report submitted.', description: 'Notice when hindrance is submitted.', surface: 'helper', maxChars: 36 },
  NLSHelperAriaNavigation: { text: 'Helper navigation', description: 'Accessible label for helper navigation.', surface: 'helper', maxChars: 40 },
  NLSHelperAriaTabs: { text: 'Helper tabs', description: 'Accessible label for helper bottom tabs.', surface: 'helper', maxChars: 32 },
  NLSHelperAriaOpenMenu: { text: 'Open menu', description: 'Accessible label for opening the helper menu.', surface: 'helper', maxChars: 26 },
  NLSHelperMenuFooterTitle: { text: 'Timelining Helper', description: 'Helper menu drawer footer app title.', surface: 'helper', maxChars: 24 },
  NLSHelperAriaOpenNotifications: { text: 'Open notifications', description: 'Accessible label for opening helper notifications.', surface: 'helper', maxChars: 38 },
  NLSHelperAriaUnread: { text: 'unread', description: 'Accessible label for unread helper indicator dot.', surface: 'helper', maxChars: 18 },
  NLSHelperPanelOverview: { text: 'Overview', description: 'Helper mobile overview panel title.', surface: 'helper', maxChars: 24 },
  NLSHelperPanelIncomingInvites: { text: 'Incoming invites', description: 'Helper mobile incoming invites panel title.', surface: 'helper', maxChars: 34 },
  NLSHelperPanelProfile: { text: 'Profile', description: 'Helper mobile profile panel title.', surface: 'helper', maxChars: 24 },
  NLSHelperPanelProfileEyebrow: { text: 'Helper profile', description: 'Helper mobile profile panel eyebrow.', surface: 'helper', maxChars: 34 },
  NLSHelperPanelSettings: { text: 'Helper settings', description: 'Helper mobile settings panel title.', surface: 'helper', maxChars: 34 },
  NLSHelperPanelCommandPreferences: { text: 'Command preferences', description: 'Helper mobile settings panel eyebrow.', surface: 'helper', maxChars: 40 },
  NLSHelperWaitingTitle: { text: 'Waiting for assignment', description: 'Helper no-workspace waiting title.', surface: 'helper', maxChars: 44 },
  NLSHelperAppAccessBlockedNotice: {
    text: 'Your team uses WhatsApp for your work. Message {workspaceName} on WhatsApp to see your jobs.',
    description: 'Notice shown when helper app access is disabled for a workspace.',
    surface: 'helper',
    maxChars: 120,
  },
  NLSHelperAppAccessBlockedAction: {
    text: 'Message on WhatsApp',
    description: 'Button to message the workspace on WhatsApp when app access is disabled.',
    surface: 'helper',
    maxChars: 30,
  },
  NLSHelperWorkspaceBlockedTag: {
    text: 'WhatsApp only',
    description: 'Tag displayed in workspace switcher when app access is disabled.',
    surface: 'helper',
    maxChars: 20,
  },
  NLSHelperNotifyViaBoth: {
    text: 'App first',
    description: 'Notification routing mode: App first',
    surface: 'helper',
    maxChars: 20,
  },
  NLSHelperNotifyViaAppOnly: {
    text: 'App only',
    description: 'Notification routing mode: App only',
    surface: 'helper',
    maxChars: 20,
  },
  NLSHelperNotifyViaWhatsappOnly: {
    text: 'WhatsApp only',
    description: 'Notification routing mode: WhatsApp only',
    surface: 'helper',
    maxChars: 20,
  },
  NLSHelperNotifyViaLabel: {
    text: 'Notifications',
    description: 'Label for helper notification routing',
    surface: 'helper',
    maxChars: 24,
  },
  NLSHelperNotifyViaSaved: {
    text: 'Notification preferences updated.',
    description: 'Toast when helper updates notification preferences',
    surface: 'helper',
    maxChars: 50,
  },
  NLSHelperVerificationVerified: {
    text: 'Verified',
    description: 'Helper phone link verified chip',
    surface: 'helper',
    maxChars: 16,
  },
  NLSHelperVerificationWaiting: {
    text: 'Waiting',
    description: 'Helper phone link waiting verification chip',
    surface: 'helper',
    maxChars: 16,
  },
  NLSHelperAppAccessToggleLabel: {
    text: 'App access',
    description: 'Label for helper app access toggle',
    surface: 'helper',
    maxChars: 20,
  },
  NLSHelperAppAccessConfirmTitle: {
    text: 'Disable app access',
    description: 'Title for helper app access confirmation dialog',
    surface: 'helper',
    maxChars: 30,
  },
  NLSHelperAppAccessConfirmBody: {
    text: "They can't use the Helper app; their jobs come on WhatsApp.",
    description: 'Confirmation message when disabling helper app access',
    surface: 'helper',
    maxChars: 80,
  },
  NLSHelperAppAccessErrorGeneric: {
    text: 'Failed to update helper app access.',
    description: 'Error message when updating helper app access fails',
    surface: 'helper',
    maxChars: 60,
  },
  NLSHelperNotifyViaErrorGeneric: {
    text: 'Failed to update notification routing.',
    description: 'Error message when updating helper notification routing fails',
    surface: 'helper',
    maxChars: 60,
  },
  NLSHelperWaitingBody: {
    text: "You don't belong to any workspace yet. Once a workspace admin invites you, your tasks will show up here.",
    description: 'Helper no-workspace waiting body.',
    surface: 'helper',
    maxChars: 141,
  },
  NLSHelperPickWorkspaceBeforeScan: { text: 'Pick a workspace before scanning inventory.', description: 'Helper scan gate body.', surface: 'helper', maxChars: 70 },
  NLSHelperChatFocusRequired: { text: 'Open or focus a task before using this chat action.', description: 'Helper chat quick-action target warning.', surface: 'helper', maxChars: 80 },
  NLSHelperToastEvidenceQueued: { text: 'Evidence queued for sync.', description: 'Helper evidence quick-action toast.', surface: 'helper', maxChars: 44 },
  NLSHelperToastDoneQueued: { text: 'Done queued for sync.', description: 'Helper done quick-action toast.', surface: 'helper', maxChars: 40 },
  NLSHelperToastCommentQueued: { text: 'Comment queued for sync.', description: 'Helper comment quick-action toast.', surface: 'helper', maxChars: 44 },
  NLSHelperToastInventoryScanFailed: { text: 'Inventory scan failed.', description: 'Helper inventory fallback error toast.', surface: 'helper', maxChars: 48 },
  NLSHelperTaskLoading: { text: 'Loading your tasks...', description: 'Helper task carousel loading label.', surface: 'helper', maxChars: 42 },
  NLSHelperTaskEmptyTitle: { text: 'All caught up', description: 'Helper task carousel empty title.', surface: 'helper', maxChars: 34 },
  NLSHelperTaskEmptyBody: {
    text: 'No active tasks in this workspace. Switch workspaces from the top bar to see more.',
    description: 'Helper task carousel empty body.',
    surface: 'helper',
    maxChars: 111,
  },
  NLSHelperTaskDoneWhen: { text: 'Done when', description: 'Helper task completion criteria label.', surface: 'helper', maxChars: 24 },
  NLSHelperTaskProof: { text: 'Proof', description: 'Helper task proof signal label.', surface: 'helper', maxChars: 18 },
  NLSHelperTaskLocation: { text: 'Location', description: 'Helper task location signal label.', surface: 'helper', maxChars: 22 },
  NLSHelperTaskDependencyLocked: { text: 'Dependency locked', description: 'Helper task dependency-locked label.', surface: 'helper', maxChars: 36 },
  NLSHelperTaskBlocked: { text: 'blocked', description: 'Helper task compact blocked chip.', surface: 'helper', maxChars: 18 },
  NLSHelperTaskSwipe: { text: 'Swipe', description: 'Helper task swipe instruction lead-in.', surface: 'helper', maxChars: 18 },
  NLSHelperTaskForDetails: { text: 'for details', description: 'Helper task swipe instruction suffix.', surface: 'helper', maxChars: 24 },
  NLSHelperTaskGoToTask: { text: 'Go to task {count}', description: 'Accessible label for helper carousel dot. {count} is a number.', surface: 'helper', maxChars: 40 },
  NLSHelperTaskProofLocked: { text: 'Proof locked', description: 'Helper task proof action disabled by dependency.', surface: 'helper', maxChars: 26 },
  NLSHelperTaskUploadProof: { text: 'Upload proof', description: 'Helper task proof upload action.', surface: 'helper', maxChars: 28 },
  NLSHelperTaskNoProofNeeded: { text: 'No proof needed', description: 'Helper task proof-not-needed action label.', surface: 'helper', maxChars: 34 },
  NLSHelperTaskCheckIn: { text: 'Check in', description: 'Helper location check-in action.', surface: 'helper', maxChars: 22 },
  NLSHelperTaskNoLocation: { text: 'No location', description: 'Helper task no-location action label.', surface: 'helper', maxChars: 24 },
  NLSHelperTaskProofUploadBlocked: { text: 'Blocked by dependency. Proof upload is disabled.', description: 'Helper proof upload blocked toast.', surface: 'helper', maxChars: 80 },
  NLSHelperTaskProofAttached: { text: 'Proof attached ({name})', description: 'Helper proof attached toast. {name} is the file name.', surface: 'helper', maxChars: 80 },
  NLSHelperTaskProofAttachFailed: { text: 'Could not attach proof.', description: 'Helper proof attach fallback error.', surface: 'helper', maxChars: 46 },
  NLSHelperTaskLocationUnavailable: { text: 'Location is not available on this device.', description: 'Helper location unavailable toast.', surface: 'helper', maxChars: 70 },
  NLSHelperTaskLocating: { text: 'Locating…', description: 'Helper location lookup progress toast.', surface: 'helper', maxChars: 24 },
  NLSHelperTaskLocationPinned: { text: 'Pinned {lat}, {lng}', description: 'Helper location success toast. {lat} and {lng} are formatted coordinates.', surface: 'helper', maxChars: 60 },
  NLSHelperTaskLocationRecordFailed: { text: 'Could not record location.', description: 'Helper location record fallback error.', surface: 'helper', maxChars: 50 },
  NLSHelperTaskLocationPermissionDenied: { text: 'Location permission denied.', description: 'Helper location denied fallback error.', surface: 'helper', maxChars: 54 },
  NLSHelperReadinessTitle: { text: 'Readiness', description: 'Helper readiness strip eyebrow.', surface: 'helper', maxChars: 24 },
  NLSHelperReadinessReady: { text: 'Ready to work', description: 'Helper readiness clear state.', surface: 'helper', maxChars: 32 },
  NLSHelperReadinessNeedsAttention: { text: 'Needs attention', description: 'Helper readiness warning state.', surface: 'helper', maxChars: 36 },
  NLSHelperReadinessStockShort: { text: 'Stock is short', description: 'Helper readiness stock shortage state.', surface: 'helper', maxChars: 34 },
  NLSHelperReadinessBlockedCount: { text: '{count} open request(s)', description: 'Helper readiness open blocker count. {count} is a number.', surface: 'helper', maxChars: 42 },
  NLSHelperReadinessNeedMore: { text: 'Need more', description: 'Helper replenishment request action.', surface: 'helper', maxChars: 24 },
  NLSHelperReadinessReport: { text: 'Report', description: 'Helper blocker report action.', surface: 'helper', maxChars: 18 },
  NLSHelperBlockerReportTitle: { text: 'Report blocker', description: 'Helper blocker report sheet title.', surface: 'helper', maxChars: 36 },
  NLSHelperBlockerReportBody: { text: 'Tell admins what is stopping this task.', description: 'Helper blocker report sheet body.', surface: 'helper', maxChars: 70 },
  NLSHelperBlockerReportPlaceholder: { text: "What's blocking this?", description: 'Helper blocker report note placeholder.', surface: 'helper', maxChars: 52 },
  NLSHelperBlockerNeedMoreTitle: { text: 'Need more stock', description: 'Helper replenishment sheet title.', surface: 'helper', maxChars: 36 },
  NLSHelperBlockerNeedMoreBody: { text: 'Send the shortage list to admins and editors.', description: 'Helper replenishment sheet body.', surface: 'helper', maxChars: 74 },
  NLSHelperBlockerNeedMorePlaceholder: { text: 'Optional note for the stock request', description: 'Helper replenishment note placeholder.', surface: 'helper', maxChars: 60 },
  NLSHelperBlockerShortages: { text: 'Shortages', description: 'Helper replenishment shortage list heading.', surface: 'helper', maxChars: 24 },
  NLSHelperBlockerSending: { text: 'Sending...', description: 'Helper blocker submit busy label.', surface: 'helper', maxChars: 24 },
  NLSHelperBlockerSendReport: { text: 'Send report', description: 'Helper blocker submit label.', surface: 'helper', maxChars: 28 },
  NLSHelperBlockerSendNeedMore: { text: 'Send request', description: 'Helper replenishment submit label.', surface: 'helper', maxChars: 30 },
  NLSHelperBlockerQueued: { text: 'Request queued for sync.', description: 'Helper blocker queued toast.', surface: 'helper', maxChars: 44 },
  NLSHelperBlockerQueueFailed: { text: 'Could not queue request.', description: 'Helper blocker queue failed toast.', surface: 'helper', maxChars: 48 },
  NLSHelperControlsSubtabsAria: { text: 'Controls subtabs', description: 'Accessible label for helper controls subtabs.', surface: 'helper', maxChars: 38 },
  NLSHelperFormsAssignedForm: { text: 'Assigned form', description: 'Helper active assigned form eyebrow.', surface: 'helper', maxChars: 32 },
  NLSHelperFormsDirectAssignment: { text: 'Direct assignment', description: 'Helper assigned form source fallback.', surface: 'helper', maxChars: 34 },
  NLSHelperFormsBack: { text: 'Back', description: 'Helper assigned form back action.', surface: 'helper', maxChars: 18 },
  NLSHelperFormsTitle: { text: 'Forms', description: 'Helper forms list eyebrow.', surface: 'helper', maxChars: 18 },
  NLSHelperFormsNoForms: { text: 'No forms', description: 'Helper forms empty count title.', surface: 'helper', maxChars: 24 },
  NLSHelperFormsCountOne: { text: '{count} form', description: 'Helper singular forms count. {count} is 1.', surface: 'helper', maxChars: 24 },
  NLSHelperFormsCountMany: { text: '{count} forms', description: 'Helper plural forms count. {count} is a number.', surface: 'helper', maxChars: 28 },
  NLSHelperFormsStatusAria: { text: 'Helper assigned form status', description: 'Accessible label for helper form status filter.', surface: 'helper', maxChars: 54 },
  NLSHelperSignOutUnsentTitle: { text: 'Items not sent', description: 'Title for unsent items confirmation on helper sign out.', surface: 'helper', maxChars: 30 },
  NLSHelperSignOutUnsentBody: { text: '{count} items not sent', description: 'Helper sign out confirmation when outbox has unsent mutations. {count} is a number.', surface: 'helper', maxChars: 40 },
  NLSHelperSignOutCancel: { text: 'Stay', description: 'Cancel button label for helper unsent items sign out confirm.', surface: 'helper', maxChars: 18 },
  NLSHelperSwitchGuardCountDraftsTitle: { text: 'Unsaved stock counts', description: 'Title for count draft guard sheet on workspace switch.', surface: 'helper', maxChars: 32 },
  NLSHelperSwitchGuardCountDraftsBody: { text: 'You have unsubmitted stock count drafts in this workspace.', description: 'Body for count draft guard sheet on workspace switch.', surface: 'helper', maxChars: 80 },
  NLSHelperSwitchGuardSubmit: { text: 'Submit', description: 'Submit drafts action button in switch guard sheet.', surface: 'helper', maxChars: 18 },
  NLSHelperSwitchGuardDiscard: { text: 'Discard', description: 'Discard drafts action button in switch guard sheet.', surface: 'helper', maxChars: 18 },
  NLSHelperSwitchGuardStay: { text: 'Stay', description: 'Cancel switch action button in switch guard sheet.', surface: 'helper', maxChars: 18 },
  NLSAdminModuleHelperScanSubtitle: { text: 'Let helpers scan barcodes into workspace targets.', description: 'Admin workspace module subtitle for Helper Scan.', surface: 'settings', maxChars: 70 },
  NLSAdminModuleHelperCountSubtitle: {
    text: 'Let helpers submit inventory count suggestions. Requires Stock.',
    description: 'Admin workspace module subtitle for Helper Count.',
    surface: 'settings',
    maxChars: 80,
  },
  NLSAdminModuleHelperFormsSubtitle: {
    text: 'Let helpers see and submit workspace form requests.',
    description: 'Admin workspace module subtitle for Helper Forms.',
    surface: 'settings',
    maxChars: 70,
  },
  NLSAdminWorkspaceCountryTitle: { text: 'Workspace Country', description: 'Title for the Workspace Country section in Board Settings.', surface: 'settings', maxChars: 36 },
  NLSAdminWorkspaceCountrySubtitle: { text: 'Used for regional starter kit features and localized defaults.', description: 'Subtitle for Workspace Country in Board Settings.', surface: 'settings', maxChars: 80 },
  NLSAdminWorkspaceCountryLabel: { text: 'Country', description: 'Label for Workspace Country select in Board Settings.', surface: 'settings', maxChars: 20 },
  NLSAdminWorkspaceCountryAriaLabel: { text: 'Select workspace country', description: 'Aria label for Workspace Country select in Board Settings.', surface: 'settings', maxChars: 36 },
  NLSAdminWorkspaceCountryUpdated: { text: 'Workspace country updated.', description: 'Status message when workspace country is updated.', surface: 'settings', maxChars: 40 },
  NLSAdminWorkspaceCountryUpdateFailed: { text: 'Could not update workspace country.', description: 'Error message when updating workspace country fails.', surface: 'settings', maxChars: 60 },
  NLSAdminWorkspaceCountryGlobal: { text: 'Global (All regions)', description: 'Option for Global region in workspace country select.', surface: 'settings', maxChars: 36 },
  NLSAdminElementDatesTitle: {
    text: 'Fill in old dates',
    description: 'Title for element timestamp backfill card in workspace settings.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminElementDatesDescription: {
    text: 'Finds items missing a finish or creation date and fills them in from their history. Nothing changes until you confirm.',
    description: 'Description for element timestamp backfill card in workspace settings.',
    surface: 'settings',
    maxChars: 140,
  },
  NLSAdminElementDatesCheckButton: {
    text: 'Check old dates',
    description: 'Button to trigger a dry run check for missing element dates.',
    surface: 'settings',
    maxChars: 28,
  },
  NLSAdminElementDatesConfirmButton: {
    text: 'Fill in dates',
    description: 'Button to confirm and execute the timestamp backfill.',
    surface: 'settings',
    maxChars: 28,
  },
  NLSAdminElementDatesChecking: {
    text: 'Checking…',
    description: 'Busy state label while checking missing element dates.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSAdminElementDatesBackfilling: {
    text: 'Filling in…',
    description: 'Busy state label while executing timestamp backfill.',
    surface: 'settings',
    maxChars: 22,
  },
  NLSAdminElementDatesNothingToFill: {
    text: 'Every item already has its dates. Nothing to fill in.',
    description: 'Message displayed when no element dates need backfilling.',
    surface: 'settings',
    maxChars: 65,
  },
  NLSAdminElementDatesSuccess: {
    text: 'Done. The dates are filled in.',
    description: 'Success message after backfilling timestamps.',
    surface: 'settings',
    maxChars: 38,
  },
  NLSAdminElementDatesScanned: {
    text: 'Items checked: {count}',
    description: 'Count of scanned workspace elements. {count} is a number.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminElementDatesCompletedToFill: {
    text: 'Finish dates to fill in: {count}',
    description: 'Count of completed timestamps that will be filled. {count} is a number.',
    surface: 'settings',
    maxChars: 42,
  },
  NLSAdminElementDatesCompletedFilled: {
    text: 'Finish dates filled in: {count}',
    description: 'Count of finish dates that were filled in. {count} is a number.',
    surface: 'settings',
    maxChars: 42,
  },
  NLSAdminElementDatesCompletedNoSource: {
    text: 'Finished items with no history: {count}',
    description: 'Count of completed elements with no historical delta operations. {count} is a number.',
    surface: 'settings',
    maxChars: 50,
  },
  NLSAdminElementDatesCreatedToFill: {
    text: 'Creation dates to fill in: {count}',
    description: 'Count of creation timestamps that will be filled. {count} is a number.',
    surface: 'settings',
    maxChars: 44,
  },
  NLSAdminElementDatesCreatedFilled: {
    text: 'Creation dates filled in: {count}',
    description: 'Count of creation dates that were filled in. {count} is a number.',
    surface: 'settings',
    maxChars: 44,
  },
  NLSAdminElementDatesCreatedNoSource: {
    text: 'Items with no creation history: {count}',
    description: 'Count of elements with no public ID history. {count} is a number.',
    surface: 'settings',
    maxChars: 50,
  },
  NLSWorkspaceModulesGroupAddons: { text: 'Add-ons', description: 'Modules settings add-ons group heading.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModulesGroupFunPack: { text: 'Fun pack', description: 'Modules settings fun pack group heading.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModulesGroupHelper: { text: 'Helper app', description: 'Modules settings helper app group heading.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceModuleAnalysisTitle: { text: 'Analysis', description: 'Analysis module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleAutomateTitle: { text: 'Automate', description: 'Automate module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleBudgetTitle: { text: 'Budget', description: 'Budget module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleDesignTitle: { text: 'Design', description: 'Design module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleFormsTitle: { text: 'Forms', description: 'Forms module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleLinkHubTitle: { text: 'Link Hub', description: 'Link Hub module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleMeetingsTitle: { text: 'Meetings', description: 'Meetings module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleQuotaTitle: { text: 'Quota', description: 'Quota module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleReportsTitle: { text: 'Reports', description: 'Reports module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleStockTitle: { text: 'Stock', description: 'Stock module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleUploadTitle: { text: 'Upload hub', description: 'Upload hub module card title.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceModuleFunPackTitle: { text: 'Fun pack', description: 'Fun pack module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleTeachingTitle: { text: 'Teaching', description: 'Teaching module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleTrainingTitle: { text: 'Training', description: 'Training module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleClientOutreachTitle: { text: 'Client outreach', description: 'Client outreach module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleConstructionTitle: { text: 'Construction', description: 'Construction module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleHospitalTitle: { text: 'Hospital', description: 'Hospital module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleSitesTitle: { text: 'Sites', description: 'Sites module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleTimesheetsTitle: { text: 'Timesheets', description: 'Timesheets module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleDeliveryTitle: { text: 'Delivery', description: 'Delivery module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleShopsTitle: { text: 'Shops', description: 'Shops module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleStaysTitle: { text: 'Stays', description: 'Stays module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleJobsTitle: { text: 'Jobs', description: 'Jobs module card title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleClinicsTitle: { text: 'Clinics & labs', description: 'Clinics & labs module card title.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceModulePlantTitle: { text: 'Plant & machinery', description: 'Plant module card title.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceModuleFitnessTitle: { text: 'Fitness & sport', description: 'Fitness & sport module card title.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceModuleHelperCountTitle: { text: 'Helper: Count', description: 'Helper Count module card title.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceModuleHelperFormsTitle: { text: 'Helper: Forms', description: 'Helper Forms module card title.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceModuleHelperScanTitle: { text: 'Helper: Scan', description: 'Helper Scan module card title.', surface: 'settings', maxChars: 25 },
  NLSWorkspaceModuleAnalysisSubtitle: { text: 'Charts and trends across your work.', description: 'Analysis module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleAutomateSubtitle: { text: 'Rules that act on your work for you.', description: 'Automate module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleBudgetSubtitle: { text: 'Expense categories, budget limits, and burn risk.', description: 'Budget module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleDesignSubtitle: { text: 'Design boards and visual assets.', description: 'Design module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleFormsSubtitle: { text: 'Forms, responses and the updates they trigger.', description: 'Forms module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleLinkHubSubtitle: { text: 'Connected apps and shared links.', description: 'Link Hub module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleMeetingsSubtitle: { text: 'Meetings, huddles and meeting notes.', description: 'Meetings module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleQuotaSubtitle: { text: 'Weighted progress targets and performance impact.', description: 'Quota module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleReportsSubtitle: { text: 'Reports and report links.', description: 'Reports module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleStockSubtitle: { text: 'Quantity-aware operational requirements and shortages.', description: 'Stock module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleUploadSubtitle: { text: 'The shared files page. Checklist proofs and chat files stay on.', description: 'Upload hub module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleFunPackSubtitle: { text: 'XP, streaks, spins, achievements, rewards, raids, celebrations and the leaderboard.', description: 'Fun pack module card subtitle.', surface: 'settings', maxChars: 90 },
  NLSWorkspaceModuleTeachingSubtitle: { text: 'Classes, timetable, attendance and learning.', description: 'Teaching module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleTrainingSubtitle: { text: 'Programmes, timetable, attendance and skills.', description: 'Training module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleConstructionSubtitle: { text: 'Sites, DPRs, quality, materials, and daily site records.', description: 'Construction module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleHospitalSubtitle: { text: 'Facilities, wards, equipment, shift checks, and registers.', description: 'Hospital module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleSitesSubtitle: { text: 'Client sites, posts, rounds, incident logs, and field registers.', description: 'Sites module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleTimesheetsSubtitle: { text: 'Work logging, clock-in, timers, approvals, and rates.', description: 'Timesheets module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleDeliverySubtitle: { text: 'Client projects, engagements, rates, and margin.', description: 'Delivery module card subtitle.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleShopsSubtitle: { text: 'Multi-outlet retail, kirana, apparel, food, and restaurant operations', description: 'Shops module card subtitle.', surface: 'settings', maxChars: 80 },
  NLSWorkspaceModuleStaysSubtitle: { text: 'Hotels, homestays, serviced apartments, and hostel operations', description: 'Stays module card subtitle.', surface: 'settings', maxChars: 80 },
  NLSWorkspaceModuleJobsSubtitle: { text: 'Field service, installers, technicians, repairs, and recurring AMC visits', description: 'Jobs module card subtitle.', surface: 'settings', maxChars: 80 },
  NLSWorkspaceModuleClinicsSubtitle: { text: 'Outpatient clinics, diagnostic labs, collection centres and equipment management', description: 'Clinics module card subtitle.', surface: 'settings', maxChars: 90 },
  NLSWorkspaceModulePlantSubtitle: { text: 'Manufacturing plants, production lines, machines, breakdowns, and downtime tracking', description: 'Plant module card subtitle.', surface: 'settings', maxChars: 90 },
  NLSWorkspaceModuleFitnessSubtitle: { text: 'Gyms, studios, pools, turf venues, and sports academies', description: 'Fitness module card subtitle.', surface: 'settings', maxChars: 90 },
  NLSEducationTeachingTitle: { text: 'Teaching', description: 'Teaching hub header title.', surface: 'shell', maxChars: 20 },
  NLSEducationTeachingSubtitle: { text: 'Classes, timetable, attendance and learning.', description: 'Teaching hub header subtitle.', surface: 'shell', maxChars: 80 },
  NLSEducationTrainingTitle: { text: 'Training', description: 'Training hub header title.', surface: 'shell', maxChars: 20 },
  NLSEducationTrainingSubtitle: { text: 'Programmes, timetable, attendance and skills.', description: 'Training hub header subtitle.', surface: 'shell', maxChars: 80 },
  NLSEducationTabOverview: { text: 'Overview', description: 'Education hub Overview tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabClasses: { text: 'Classes', description: 'Education hub Classes tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabProgrammes: { text: 'Programmes', description: 'Education hub Programmes tab.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderTeachingEmptyTitle: { text: 'No academic years yet', description: 'Empty state title for teaching classes builder.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderTeachingEmptyBody: { text: 'Create an academic year with classes, or adopt an existing curriculum goal to get started.', description: 'Empty state body for teaching classes builder.', surface: 'shell', maxChars: 120 },
  NLSEducationBuilderTrainingEmptyTitle: { text: 'No programmes yet', description: 'Empty state title for training programmes builder.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderTrainingEmptyBody: { text: 'Create a training programme with groups or clients, or adopt an existing goal to get started.', description: 'Empty state body for training programmes builder.', surface: 'shell', maxChars: 120 },
  NLSEducationBuilderNewYear: { text: 'New year', description: 'Button label to create academic year.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderNewProgramme: { text: 'New programme', description: 'Button label to create programme.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderAdoptGoal: { text: 'Use an existing goal', description: 'Button label to adopt existing goal into education.', surface: 'shell', maxChars: 30 },
  NLSEducationBuilderAdoptGoalTitle: { text: 'Adopt existing goal', description: 'Dialog title for adopting existing goal.', surface: 'shell', maxChars: 35 },
  NLSEducationBuilderAdoptGoalDescription: { text: 'Choose an unlinked goal. Select which tasks are classes (or groups/clients) and subtasks are subjects.', description: 'Dialog description for adopting existing goal.', surface: 'shell', maxChars: 120 },
  NLSEducationBuilderNoAdoptableGoals: { text: 'No unlinked goals available in this workspace.', description: 'Notice when no goals can be adopted.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderSelectGoal: { text: 'Select a goal to adopt', description: 'Label for goal selection.', surface: 'shell', maxChars: 35 },
  NLSEducationBuilderAddClass: { text: 'Add class', description: 'Button to add class.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderAddClasses: { text: 'Add classes', description: 'Dialog title to add classes.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderAddGroup: { text: 'Add group', description: 'Button to add training group.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderAddClient: { text: 'Add client', description: 'Button to add training client.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderAddGroupsClients: { text: 'Add groups or clients', description: 'Dialog title to add groups/clients.', surface: 'shell', maxChars: 30 },
  NLSEducationBuilderAddSubject: { text: 'Add subject', description: 'Button to add subject.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderAddSubjects: { text: 'Add subjects', description: 'Dialog title to add subjects.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderAddPhase: { text: 'Add phase', description: 'Button to add phase.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderAddPhases: { text: 'Add phases', description: 'Dialog title to add phases.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderCopySubjects: { text: 'Copy subjects', description: 'Action menu item to copy subjects.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderCopyPhases: { text: 'Copy phases', description: 'Action menu item to copy phases.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderCopySubjectsTitle: { text: 'Copy subjects to other classes', description: 'Dialog title for copying subjects.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderCopyPhasesTitle: { text: 'Copy phases to other groups/clients', description: 'Dialog title for copying phases.', surface: 'shell', maxChars: 45 },
  NLSEducationBuilderCopySubjectsDesc: { text: 'Select target classes under this year. Existing subjects with matching names will be skipped.', description: 'Description for copy subjects dialog.', surface: 'shell', maxChars: 110 },
  NLSEducationBuilderCopyPhasesDesc: { text: 'Select target groups/clients under this programme. Existing phases with matching names will be skipped.', description: 'Description for copy phases dialog.', surface: 'shell', maxChars: 120 },
  NLSEducationBuilderSkippedCountNotice: { text: '{count} item(s) will be skipped because they already exist on target.', description: 'Notice for skipped items in copy subjects.', surface: 'shell', maxChars: 80 },
  NLSEducationBuilderRename: { text: 'Rename', description: 'Action menu item to rename node.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderRenameTitle: { text: 'Rename', description: 'Dialog title to rename node.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderTitleLabel: { text: 'Title', description: 'Label for title field.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderStartDateLabel: { text: 'Start date', description: 'Label for start date field.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderEndDateLabel: { text: 'End date', description: 'Label for end date field.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderClassesTextareaLabel: { text: 'Classes (one name per line)', description: 'Label for classes textarea.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderGroupsClientsTextareaLabel: { text: 'Groups / Clients (one name per line)', description: 'Label for groups textarea.', surface: 'shell', maxChars: 45 },
  NLSEducationBuilderSubjectsTextareaLabel: { text: 'Subjects (one name per line)', description: 'Label for subjects textarea.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderPhasesTextareaLabel: { text: 'Phases (one name per line)', description: 'Label for phases textarea.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderKindSelectLabel: { text: 'Class level type', description: 'Label for kind selection in training.', surface: 'shell', maxChars: 30 },
  NLSEducationBuilderKindGroup: { text: 'Group', description: 'Option for training group.', surface: 'shell', maxChars: 15 },
  NLSEducationBuilderKindClient: { text: 'Client', description: 'Option for training client.', surface: 'shell', maxChars: 15 },
  NLSEducationBuilderDuplicateNameError: { text: 'Duplicate names in list: {names}', description: 'Inline validation error for duplicate names in list.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderClashNameError: { text: 'Already exists under parent: {names}', description: 'Inline validation error for names clashing with existing siblings.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderEmptyNameError: { text: 'Please enter at least one name.', description: 'Validation error when textarea is empty.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderTitleRequired: { text: 'Title is required.', description: 'Validation error when title is empty.', surface: 'shell', maxChars: 30 },
  NLSEducationBuilderPastYearsTitle: { text: 'Past years', description: 'Collapse section title for past academic years.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderPastProgrammesTitle: { text: 'Past programmes', description: 'Collapse section title for past training programmes.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderRolledOverTo: { text: 'Rolled over to {target}', description: 'Subtitle showing roll-over target for archived root.', surface: 'shell', maxChars: 50 },
  NLSEducationBuilderRetryTag: { text: 'Retry tagging', description: 'Button to retry tagging after failure.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderTagFailedNotice: { text: 'Items were created, but education tagging failed. Retry tagging to finish setup.', description: 'Warning notice when tagging failed.', surface: 'shell', maxChars: 100 },
  NLSEducationBuilderErrorArchivedYear: { text: 'This academic year is archived and cannot be changed.', description: 'Error message for ARCHIVED_YEAR.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorModuleOff: { text: 'The education module is disabled in this workspace.', description: 'Error message for MODULE_OFF.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorInvalidArgument: { text: 'Invalid structure data was provided.', description: 'Error message for invalid-argument.', surface: 'shell', maxChars: 50 },
  NLSEducationBuilderErrorPermissionDenied: { text: 'You do not have permission to perform this action.', description: 'Error message for permission-denied.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorGeneric: { text: 'An error occurred while saving the structure.', description: 'Generic error message for structure operations.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderActions: { text: 'Actions', description: 'Aria label for node actions menu.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderSubmit: { text: 'Create', description: 'Submit button to create elements.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderSave: { text: 'Save', description: 'Save button.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderCancel: { text: 'Cancel', description: 'Cancel button.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderNoClasses: { text: 'No classes in this year yet.', description: 'Empty state under academic year.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderNoGroups: { text: 'No groups or clients in this programme yet.', description: 'Empty state under programme.', surface: 'shell', maxChars: 50 },
  NLSEducationBuilderNoSubjects: { text: 'No subjects added yet.', description: 'Empty state under class.', surface: 'shell', maxChars: 30 },
  NLSEducationBuilderNoPhases: { text: 'No phases added yet.', description: 'Empty state under group/client.', surface: 'shell', maxChars: 30 },
  NLSEducationBuilderPartialClassCreateError: { text: 'The academic year was created, but classes could not be added. You can add classes using the Add class button.', description: 'Error when classes creation fails after academic year was created.', surface: 'shell', maxChars: 130 },
  NLSEducationBuilderPartialGroupCreateError: { text: 'The programme was created, but groups could not be added. You can add them using the Add groups button.', description: 'Error when groups creation fails after programme was created.', surface: 'shell', maxChars: 130 },
  NLSEducationBuilderTeachersMenu: { text: 'Teachers…', description: 'Action menu item to open Teachers dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderLearnersMenu: { text: 'Learners…', description: 'Action menu item to open Learners dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderTeachersDialogTitle: { text: 'Teachers for {title}', description: 'Title of Teachers dialog.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderNoTeachers: { text: 'No teachers assigned yet.', description: 'Notice when node has no assigned teachers.', surface: 'shell', maxChars: 30 },
  NLSEducationBuilderAddTeacher: { text: 'Add teacher', description: 'Section label to add teacher.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderSelectStaff: { text: 'Select staff member', description: 'Placeholder for staff selector.', surface: 'shell', maxChars: 30 },
  NLSEducationBuilderRemoveTeacher: { text: 'Remove', description: 'Button to remove assigned teacher.', surface: 'shell', maxChars: 15 },
  NLSEducationBuilderAccessClassTeacher: { text: 'Edit on the class', description: 'Access granted by class teacher assignment.', surface: 'shell', maxChars: 30 },
  NLSEducationBuilderAccessSubjectTeacher: { text: 'Edit on the subject, View on the class', description: 'Access granted by subject teacher assignment.', surface: 'shell', maxChars: 50 },
  NLSEducationBuilderAssignSuccessCounts: { text: '{assigned} assigned, {removed} removed, {skipped} skipped', description: 'Result counts after assigning teachers.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorRoleTooLow: { text: 'Selected teacher must be an Editor or Admin in this workspace.', description: 'Error message for ROLE_TOO_LOW.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderInviteTeacherBtn: { text: 'Invite a teacher', description: 'Button to open Invite teacher dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderInviteTeacherTitle: { text: 'Invite a teacher', description: 'Dialog title for inviting a teacher.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderInviteTypeEmail: { text: 'Email', description: 'Option to invite teacher by email.', surface: 'shell', maxChars: 15 },
  NLSEducationBuilderInviteTypePhone: { text: 'Phone', description: 'Option to invite teacher by phone.', surface: 'shell', maxChars: 15 },
  NLSEducationBuilderInviteEmailLabel: { text: 'Email address', description: 'Label for teacher invite email.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderInvitePhoneLabel: { text: 'Phone number', description: 'Label for teacher invite phone.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderInviteSendBtn: { text: 'Send invite', description: 'Button to send teacher invite.', surface: 'shell', maxChars: 20 },
  NLSEducationBuilderInviteSuccessNote: { text: 'Once the teacher joins, add them here to record "teaches".', description: 'Note shown after teacher invite is sent.', surface: 'shell', maxChars: 80 },
  NLSEducationBuilderInvitePendingLimitError: { text: 'This workspace has reached its pending invite limit.', description: 'Error when pending invites limit is reached.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderInviteInvalidEmail: { text: 'Please enter a valid email address.', description: 'Error message for invalid email invite.', surface: 'shell', maxChars: 50 },
  NLSEducationBuilderInviteInvalidPhone: { text: 'Please enter a valid phone number in international format.', description: 'Error message for invalid phone invite.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderLearnersDialogTitle: { text: 'Learners for {title}', description: 'Title of Learners dialog.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderNoLearnersAvailable: { text: 'No learners available in this workspace.', description: 'Notice when no learners exist.', surface: 'shell', maxChars: 45 },
  NLSEducationBuilderSearchLearners: { text: 'Search learners', description: 'Placeholder for learner search input.', surface: 'shell', maxChars: 25 },
  NLSEducationBuilderMovesFrom: { text: 'moves from {classTitle}', description: 'Indicator when learner is currently enrolled in another class.', surface: 'shell', maxChars: 40 },
  NLSEducationBuilderFrozenNote: { text: 'Frozen', description: 'Badge/note for frozen learner.', surface: 'shell', maxChars: 15 },
  NLSEducationBuilderEnrolSuccessCounts: { text: '{enrolled} enrolled, {ended} ended, {skipped} skipped', description: 'Result counts after updating enrolments.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorLearnerFrozen: { text: 'Cannot change enrolments for a frozen learner.', description: 'Error message for LEARNER_FROZEN.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorNotInClass: { text: 'Learner must be actively enrolled in the class before enrolling in a subject.', description: 'Error message for NOT_IN_CLASS.', surface: 'shell', maxChars: 80 },
  NLSEducationBuilderErrorPromotionMissing: { text: 'Promotion target is missing for one or more classes.', description: 'Error message for PROMOTION_MISSING.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorRolloverInProgress: { text: 'A rollover is already in progress for this academic year.', description: 'Error message for ROLLOVER_IN_PROGRESS.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorAlreadyArchived: { text: 'This academic year has already been archived.', description: 'Error message for ALREADY_ARCHIVED.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorTooManyNodes: { text: 'Too many nodes to copy in a single rollover.', description: 'Error message for TOO_MANY_NODES.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorNotTimetableEditor: { text: 'You do not have permission to edit this timetable.', description: 'Error message for NOT_TIMETABLE_EDITOR.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorRoomNotFound: { text: 'The selected room was not found or is inactive.', description: 'Error message for ROOM_NOT_FOUND.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorSeriesNotFound: { text: 'Timetable series not found.', description: 'Error message for SERIES_NOT_FOUND.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorSeriesHasSessions: { text: 'Cannot delete series that already has session records.', description: 'Error message for SERIES_HAS_SESSIONS.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorNotAnOccurrence: { text: 'The selected date is not an occurrence of this series or falls on a holiday.', description: 'Error message for NOT_AN_OCCURRENCE.', surface: 'shell', maxChars: 90 },
  NLSEducationBuilderErrorInvalidPattern: { text: 'Invalid session recurrence pattern.', description: 'Error message for INVALID_PATTERN.', surface: 'shell', maxChars: 50 },
  NLSEducationBuilderErrorReasonRequired: { text: 'A reason between 3 and 500 characters is required for past attendance modifications.', description: 'Error message for REASON_REQUIRED.', surface: 'shell', maxChars: 90 },
  NLSEducationBuilderErrorTooLateToEdit: { text: 'Past attendance can only be modified by administrators or class teachers.', description: 'Error message for TOO_LATE_TO_EDIT.', surface: 'shell', maxChars: 80 },
  NLSEducationBuilderErrorNotYet: { text: 'Attendance cannot be marked for future dates.', description: 'Error message for NOT_YET.', surface: 'shell', maxChars: 50 },
  NLSEducationBuilderErrorSessionCancelled: { text: 'Cannot mark attendance for a cancelled session.', description: 'Error message for SESSION_CANCELLED.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorNotOnRoll: { text: 'Learner is not on the roll for this session.', description: 'Error message for NOT_ON_ROLL.', surface: 'shell', maxChars: 50 },
  NLSEducationBuilderErrorHasSubmissions: { text: 'Cannot clear homework that already has submissions.', description: 'Error message for HAS_SUBMISSIONS.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorAlreadyApproved: { text: 'Homework has already been approved.', description: 'Error message for ALREADY_APPROVED.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorFeedbackRequired: { text: 'Feedback of at least 3 characters is required when returning homework.', description: 'Error message for FEEDBACK_REQUIRED.', surface: 'shell', maxChars: 80 },
  NLSEducationBuilderErrorScoreNotAllowed: { text: 'Scores cannot be given when maximum score is not set.', description: 'Error message for SCORE_NOT_ALLOWED.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorScoreOutOfBounds: { text: 'Score must be between 0 and the maximum score.', description: 'Error message for SCORE_OUT_OF_BOUNDS.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorNotLearnerOrGuardian: { text: 'Only the learner or their guardian can access family data.', description: 'Error message for NOT_LEARNER_OR_GUARDIAN.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorNotActiveMember: { text: 'You do not have an active membership in this workspace.', description: 'Error message for NOT_ACTIVE_MEMBER.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorAttachmentNotYours: { text: 'Only files uploaded for this homework can be attached.', description: 'Error message for ATTACHMENT_NOT_YOURS.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorInvalidLeaveRange: { text: 'Invalid leave dates. Range must be up to 31 days within the allowed window.', description: 'Error message for INVALID_LEAVE_RANGE.', surface: 'shell', maxChars: 80 },
  NLSEducationBuilderErrorLeaveOverlap: { text: 'Leave dates overlap with an existing pending or approved request.', description: 'Error message for LEAVE_OVERLAP.', surface: 'shell', maxChars: 75 },
  NLSEducationBuilderErrorLeaveNotPending: { text: 'Only pending leave requests can be updated or cancelled.', description: 'Error message for LEAVE_NOT_PENDING.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorNotLeaveApprover: { text: 'Only administrators or class teachers can decide leave requests.', description: 'Error message for NOT_LEAVE_APPROVER.', surface: 'shell', maxChars: 75 },
  NLSEducationBuilderErrorLeaveNotApproved: { text: 'Only approved leave requests can be revoked.', description: 'Error message for LEAVE_NOT_APPROVED.', surface: 'shell', maxChars: 60 },
  NLSEducationBuilderErrorLeaveTooLarge: { text: 'Leave range touches too many records to process at once.', description: 'Error message for LEAVE_TOO_LARGE.', surface: 'shell', maxChars: 70 },
  NLSEducationBuilderErrorLearnerOnLeave: { text: 'Learner is on approved leave for this date.', description: 'Error message for LEARNER_ON_LEAVE.', surface: 'shell', maxChars: 55 },
  NLSFamilyLoading: { text: 'Loading family overview…', description: 'Loading state for family view.', surface: 'shell', maxChars: 40 },
  NLSFamilyErrorTitle: { text: 'Could not load family data', description: 'Error state heading for family view.', surface: 'shell', maxChars: 40 },
  NLSFamilyErrorBody: { text: 'Please check your connection and try again.', description: 'Error state message for family view.', surface: 'shell', maxChars: 60 },
  NLSFamilyRetry: { text: 'Retry', description: 'Retry loading family data.', surface: 'shell', maxChars: 15 },
  NLSFamilyEmptyTitle: { text: 'No learners linked yet', description: 'Empty state heading for family view.', surface: 'shell', maxChars: 40 },
  NLSFamilyEmptyBody: { text: 'You are not currently linked to any learner records. If you have an invite or join code, enter it below to connect.', description: 'Empty state message for family view.', surface: 'shell', maxChars: 130 },
  NLSFamilyJoinWithCode: { text: 'Join with a code', description: 'Action button to join with a code on empty family view.', surface: 'shell', maxChars: 25 },
  NLSFamilyLearnersHeading: { text: 'Family learners', description: 'Heading for list of linked learners.', surface: 'shell', maxChars: 30 },
  NLSFamilyFrozenNotice: { text: 'Consent withdrawn — learner data is currently unavailable.', description: 'Notice displayed when a learner record is frozen.', surface: 'shell', maxChars: 80 },
  NLSFamilyNoClasses: { text: 'No enrolled classes', description: 'Label displayed when learner has no classes enrolled.', surface: 'shell', maxChars: 30 },
  NLSMemberOpeningFamily: { text: 'Opening Family…', description: 'Transition state when routing a user to /family.', surface: 'shell', maxChars: 30 },
  NLSFamilyOpenStaffWorkspace: { text: 'Open staff workspace', description: 'Button to return to staff workspace from family view.', surface: 'shell', maxChars: 25 },
  NLSFamilyAriaLoading: { text: 'Loading family', description: 'Aria label for loading state in family view.', surface: 'shell', maxChars: 30 },
  NLSFamilyAriaError: { text: 'Family error', description: 'Aria label for error state in family view.', surface: 'shell', maxChars: 30 },
  NLSFamilyAriaEmpty: { text: 'Family empty', description: 'Aria label for empty state in family view.', surface: 'shell', maxChars: 30 },
  NLSFamilyAriaLearners: { text: 'Family learners', description: 'Aria label for list of learners in family view.', surface: 'shell', maxChars: 30 },
  NLSFamilyStaffView: { text: 'Staff view', description: 'Button to return to staff view from family shell.', surface: 'shell', maxChars: 20 },
  NLSFamilyTabToday: { text: 'Today', description: 'Family Today tab label.', surface: 'shell', maxChars: 20 },
  NLSFamilyAlertsButtonAria: { text: 'Alerts, {count} unread', description: 'Aria label for alerts bell button in family shell.', surface: 'shell', maxChars: 30 },
  NLSFamilyAlertsDialogTitle: { text: 'Alerts', description: 'Title of alerts dialog in family shell.', surface: 'shell', maxChars: 20 },
  NLSFamilyAlertsMarkAllRead: { text: 'Mark all read', description: 'Action button to mark all notifications read in family alerts dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertsEmpty: { text: 'No alerts', description: 'Empty state message when there are no notifications in family inbox.', surface: 'shell', maxChars: 30 },
  NLSFamilyAlertsClose: { text: 'Close', description: 'Close button for alerts dialog.', surface: 'shell', maxChars: 15 },
  NLSFamilyAlertsUnreadItem: { text: 'Unread', description: 'Visually hidden label indicating an unread alert item in family alerts list.', surface: 'shell', maxChars: 15 },
  NLSFamilyAlertsBannerText: { text: 'Get notifications for your child\'s attendance, classes, and homework.', description: 'Prompt banner in family shell asking user to enable notifications.', surface: 'shell', maxChars: 90 },
  NLSFamilyAlertsTurnOn: { text: 'Turn on alerts', description: 'Button label to enable mobile push alerts.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertsNotNow: { text: 'Not now', description: 'Dismiss button for family alerts banner.', surface: 'shell', maxChars: 20 },
  NLSFamilyAlertsSettings: { text: 'Alert settings', description: 'Action to open alert notification settings dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertsCategoryAttendance: { text: 'Attendance', description: 'Category label for attendance alerts in settings dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertsCategoryLeave: { text: 'Leave', description: 'Category label for leave request alerts in settings dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertsCategoryHomework: { text: 'Homework', description: 'Category label for homework alerts in settings dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertsCategoryClasses: { text: 'Classes', description: 'Category label for class schedule alerts in settings dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertsCategoryToday: { text: 'Today at school', description: 'Category label for today at school daily summary alerts in settings dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertsOn: { text: 'On', description: 'Toggle ON label in alert settings.', surface: 'shell', maxChars: 10 },
  NLSFamilyAlertsOff: { text: 'Off', description: 'Toggle OFF label in alert settings.', surface: 'shell', maxChars: 10 },
  NLSFamilyAlertsPushOn: { text: 'Alerts are on', description: 'Status message indicating push notifications are active.', surface: 'shell', maxChars: 30 },
  NLSFamilyAlertsPushBlocked: { text: 'Alerts are blocked in browser settings', description: 'Status message indicating push notifications are blocked.', surface: 'shell', maxChars: 50 },
  NLSFamilyAlertsPushUnsupported: { text: 'Alerts not supported in this browser', description: 'Status message indicating browser lacks push notification support.', surface: 'shell', maxChars: 50 },
  NLSFamilyAlertsSaveError: { text: 'Could not save alert settings', description: 'Error message when updating alert preferences fails.', surface: 'shell', maxChars: 40 },
  NLSFamilyChildPaused: { text: 'Paused', description: 'Chip indicating child consent is paused.', surface: 'shell', maxChars: 15 },

  NLSFamilySwitchChildTitle: { text: 'Switch child', description: 'Title for switch child sheet.', surface: 'shell', maxChars: 25 },
  NLSFamilyChildSwitcherAria: { text: 'Switch child', description: 'Aria label for child switcher.', surface: 'shell', maxChars: 25 },
  NLSFamilyTabsAria: { text: 'Family navigation', description: 'Aria label for family navigation tabs.', surface: 'shell', maxChars: 30 },
  NLSFamilyNoticeHeading: { text: 'Notice', description: 'Heading for family notice card.', surface: 'shell', maxChars: 20 },
  NLSFamilyTodayAttendanceHeading: { text: 'Attendance', description: 'Heading for today attendance card in family view.', surface: 'shell', maxChars: 25 },
  NLSFamilyTodayClassesHeading: { text: 'Classes today', description: 'Heading for today classes card in family view.', surface: 'shell', maxChars: 25 },
  NLSFamilyTodayHomeworkHeading: { text: 'Due today / soon', description: 'Heading for today and soon homework card in family view.', surface: 'shell', maxChars: 25 },
  NLSFamilyTodayNotMarkedYet: { text: 'Not marked yet', description: 'Label when session attendance has not been marked yet.', surface: 'shell', maxChars: 25 },
  NLSFamilyTodaySubstitute: { text: 'Substitute: {name}', description: 'Substitute teacher indicator in family classes card.', surface: 'shell', maxChars: 35 },
  NLSFamilyTodayJoinOnline: { text: 'Join online class', description: 'Link to join online class session in family classes card.', surface: 'shell', maxChars: 30 },
  NLSFamilyTodayEmpty: { text: 'Nothing scheduled today', description: 'Message shown when nothing is scheduled for today in family view.', surface: 'shell', maxChars: 35 },
  NLSFamilyTabWork: { text: 'Work', description: 'Family Work tab label.', surface: 'shell', maxChars: 20 },
  NLSFamilyWorkCardHeading: { text: 'Homework', description: 'Card title for homework in Work tab.', surface: 'shell', maxChars: 20 },
  NLSFamilyWorkEmpty: { text: 'No homework assigned', description: 'Empty state message when no homework is assigned.', surface: 'shell', maxChars: 30 },
  NLSFamilyWorkHandIn: { text: 'Hand in', description: 'Button label to hand in homework.', surface: 'shell', maxChars: 15 },
  NLSFamilyWorkHandInAgain: { text: 'Hand in again', description: 'Button label to re-submit homework.', surface: 'shell', maxChars: 20 },
  NLSFamilyWorkFeedbackHeading: { text: 'Teacher feedback', description: 'Heading for teacher feedback on returned homework.', surface: 'shell', maxChars: 25 },
  NLSFamilyWorkScore: { text: 'Score: {score} / {maxScore}', description: 'Score display for approved homework.', surface: 'shell', maxChars: 35 },
  NLSFamilyWorkScoreNoMax: { text: 'Score: {score}', description: 'Score display without max.', surface: 'shell', maxChars: 20 },
  NLSFamilyWorkAttachments: { text: 'Attachments', description: 'Heading for attachments in homework row.', surface: 'shell', maxChars: 20 },
  NLSFamilyWorkAriaAttachments: { text: 'Homework attachments', description: 'Aria label for attachments list.', surface: 'shell', maxChars: 30 },
  NLSFamilyWorkStartQuiz: { text: 'Start quiz', description: 'Button label to start a quiz in family work tab.', surface: 'shell', maxChars: 15 },
  NLSFamilyWorkSeeAnswers: { text: 'See answers', description: 'Button label to see quiz answers in family work tab.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizDialogTitle: { text: 'Quiz', description: 'Default title of family quiz dialog.', surface: 'shell', maxChars: 20 },
  NLSFamilyQuizPassMark: { text: 'Pass mark', description: 'Label for quiz pass mark.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizAttempts: { text: 'Attempts', description: 'Label for quiz attempts.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizAttemptsLeft: { text: '{count} left', description: 'Label showing remaining attempts.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizNoAttemptsLeft: { text: 'No attempts left', description: 'Notice that no attempts remain.', surface: 'shell', maxChars: 20 },
  NLSFamilyQuizBestResult: { text: 'Best result', description: 'Label for best quiz score result.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizDueDate: { text: 'Due date', description: 'Label for quiz due date.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizStart: { text: 'Start quiz', description: 'Button to start taking quiz.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizPassed: { text: 'Passed', description: 'Quiz result status passed.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizNotPassed: { text: 'Not passed', description: 'Quiz result status not passed.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizScore: { text: 'Score: {score} / {maxScore}', description: 'Quiz result score.', surface: 'shell', maxChars: 30 },
  NLSFamilyQuizTryAgain: { text: 'Try again', description: 'Button to try the quiz again.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizClose: { text: 'Close', description: 'Button to close quiz dialog.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizLoading: { text: 'Loading quiz…', description: 'Loading state for quiz dialog.', surface: 'shell', maxChars: 20 },
  NLSFamilyQuizYourAnswer: { text: 'Your answer', description: 'Label for learner answer in quiz review.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizCorrectAnswer: { text: 'Correct answer', description: 'Label for correct answer in quiz review.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizExplanation: { text: 'Explanation', description: 'Label for explanation in quiz review.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizCorrect: { text: 'Correct', description: 'Status badge for correct answer in review.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizIncorrect: { text: 'Incorrect', description: 'Status badge for incorrect answer in review.', surface: 'shell', maxChars: 15 },
  NLSFamilyQuizReviewAnswersTitle: { text: 'Answers & explanations', description: 'Heading for quiz review section.', surface: 'shell', maxChars: 30 },
  NLSFamilyHandInDialogTitle: { text: 'Hand in homework', description: 'Title of hand-in dialog.', surface: 'shell', maxChars: 30 },
  NLSFamilyHandInDialogNoteLabel: { text: 'Note (optional)', description: 'Label for optional note in hand-in dialog.', surface: 'shell', maxChars: 20 },
  NLSFamilyHandInDialogNotePlaceholder: { text: 'Add a note for your teacher…', description: 'Placeholder for note in hand-in dialog.', surface: 'shell', maxChars: 40 },
  NLSFamilyHandInDialogFilesLabel: { text: 'Files or photos', description: 'Label for files section in hand-in dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyHandInDialogSubmit: { text: 'Hand in', description: 'Submit button in hand-in dialog.', surface: 'shell', maxChars: 15 },
  NLSFamilyHandInDialogSubmitting: { text: 'Submitting…', description: 'Loading state for submission.', surface: 'shell', maxChars: 20 },
  NLSFamilyHandInDialogUploading: { text: 'Uploading ({completed}/{total})…', description: 'Progress label during upload.', surface: 'shell', maxChars: 40 },
  NLSFamilyHandInDialogCancel: { text: 'Cancel', description: 'Cancel button in hand-in dialog.', surface: 'shell', maxChars: 15 },
  NLSFamilyHandInDialogTooManyFiles: { text: 'You can upload up to 5 files.', description: 'Validation error when file limit is exceeded.', surface: 'shell', maxChars: 40 },
  NLSFamilyHandInDialogFileRemove: { text: 'Remove file', description: 'Aria label to remove a chosen file in hand-in dialog.', surface: 'shell', maxChars: 20 },
  NLSFamilyHandInDialogSavedOffline: { text: "Saved, will send when you're online", description: 'Status message shown when homework is queued offline in the outbox.', surface: 'shell', maxChars: 45 },
  NLSFamilyHandInDialogClose: { text: 'Close', description: 'Close button in hand-in dialog when queued offline.', surface: 'shell', maxChars: 15 },
  NLSFamilyTabMore: { text: 'More', description: 'Family More tab label.', surface: 'shell', maxChars: 20 },
  NLSFamilyMoreCardHeading: { text: 'More options', description: 'Heading for options card in Family More tab.', surface: 'shell', maxChars: 25 },
  NLSFamilyMoreAskForLeave: { text: 'Ask for leave', description: 'Option to open leave requests in Family More tab.', surface: 'shell', maxChars: 25 },
  NLSFamilyLeaveHeading: { text: 'Leave requests', description: 'Heading for leave requests list.', surface: 'shell', maxChars: 25 },
  NLSFamilyLeaveAskButton: { text: 'Ask for leave', description: 'Button to open leave request dialog.', surface: 'shell', maxChars: 20 },
  NLSFamilyLeaveEmpty: { text: 'No leave requests recorded', description: 'Empty state message when no leave requests exist.', surface: 'shell', maxChars: 40 },
  NLSFamilyLeaveStatusPending: { text: 'Pending', description: 'Leave status pending approval.', surface: 'shell', maxChars: 15 },
  NLSFamilyLeaveStatusApproved: { text: 'Approved', description: 'Leave status approved.', surface: 'shell', maxChars: 15 },
  NLSFamilyLeaveStatusDeclined: { text: 'Declined', description: 'Leave status declined.', surface: 'shell', maxChars: 15 },
  NLSFamilyLeaveStatusCancelled: { text: 'Cancelled', description: 'Leave status cancelled.', surface: 'shell', maxChars: 15 },
  NLSFamilyLeaveStatusRevoked: { text: 'Revoked', description: 'Leave status revoked.', surface: 'shell', maxChars: 15 },
  NLSFamilyLeaveDecisionNote: { text: 'Decision note: {note}', description: 'Decision note displayed on leave request row.', surface: 'shell', maxChars: 60 },
  NLSFamilyLeaveCancelAction: { text: 'Cancel request', description: 'Button label to cancel a pending leave request.', surface: 'shell', maxChars: 20 },
  NLSFamilyLeaveCancelConfirmTitle: { text: 'Cancel leave request?', description: 'Confirmation title when cancelling a leave request.', surface: 'shell', maxChars: 30 },
  NLSFamilyLeaveCancelConfirmBody: { text: 'Are you sure you want to cancel this pending leave request?', description: 'Confirmation body when cancelling a leave request.', surface: 'shell', maxChars: 70 },
  NLSFamilyLeaveCancelConfirmButton: { text: 'Cancel request', description: 'Confirmation action button to cancel leave request.', surface: 'shell', maxChars: 25 },
  NLSFamilyLeaveBack: { text: 'Back to more', description: 'Back button to return to More menu.', surface: 'shell', maxChars: 20 },
  NLSFamilyLeaveDialogTitle: { text: 'Ask for leave', description: 'Title of leave request dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyLeaveDialogDatesLabel: { text: 'Dates', description: 'Label for date range picker in leave dialog.', surface: 'shell', maxChars: 15 },
  NLSFamilyLeaveDialogDatesPlaceholder: { text: 'Select leave dates', description: 'Placeholder for date range picker in leave dialog.', surface: 'shell', maxChars: 25 },
  NLSFamilyLeaveDialogReasonLabel: { text: 'Reason', description: 'Label for leave reason textarea.', surface: 'shell', maxChars: 15 },
  NLSFamilyLeaveDialogReasonPlaceholder: { text: 'Why is leave needed? (3 to 500 characters)', description: 'Placeholder for reason textarea in leave dialog.', surface: 'shell', maxChars: 50 },
  NLSFamilyLeaveDialogRangeMaxHint: { text: 'Leave cannot exceed 31 days.', description: 'Validation hint when date range exceeds 31 days.', surface: 'shell', maxChars: 40 },
  NLSFamilyLeaveDialogSubmit: { text: 'Submit request', description: 'Submit button in leave dialog.', surface: 'shell', maxChars: 20 },
  NLSFamilyLeaveDialogSubmitting: { text: 'Submitting…', description: 'Loading state for leave dialog submit button.', surface: 'shell', maxChars: 20 },
  NLSFamilyLeaveDialogCancel: { text: 'Cancel', description: 'Cancel button in leave dialog.', surface: 'shell', maxChars: 15 },


  NLSEduAttendanceStatusPresent: { text: 'Present', description: 'Attendance status Present.', surface: 'shell', maxChars: 15 },
  NLSEduAttendanceStatusPresentShort: { text: 'P', description: 'Short form of attendance status Present.', surface: 'shell', maxChars: 5 },
  NLSEduAttendanceStatusAbsent: { text: 'Absent', description: 'Attendance status Absent.', surface: 'shell', maxChars: 15 },
  NLSEduAttendanceStatusAbsentShort: { text: 'A', description: 'Short form of attendance status Absent.', surface: 'shell', maxChars: 5 },
  NLSEduAttendanceStatusLate: { text: 'Late', description: 'Attendance status Late.', surface: 'shell', maxChars: 15 },
  NLSEduAttendanceStatusLateShort: { text: 'L', description: 'Short form of attendance status Late.', surface: 'shell', maxChars: 5 },
  NLSEduAttendanceStatusOnLeave: { text: 'On leave', description: 'Attendance status On leave.', surface: 'shell', maxChars: 15 },
  NLSEduAttendanceStatusOnLeaveShort: { text: 'OL', description: 'Short form of attendance status On leave.', surface: 'shell', maxChars: 5 },
  NLSEduAttendanceStatusExcused: { text: 'Excused', description: 'Attendance status Excused.', surface: 'shell', maxChars: 15 },
  NLSEduAttendanceStatusExcusedShort: { text: 'E', description: 'Short form of attendance status Excused.', surface: 'shell', maxChars: 5 },
  NLSEducationRolloverStartNewYear: { text: 'Start new year…', description: 'Menu item label to start new academic year.', surface: 'shell', maxChars: 25 },
  NLSEducationRolloverStartNextIntake: { text: 'Start next intake…', description: 'Menu item label to start next training intake.', surface: 'shell', maxChars: 25 },
  NLSEducationRolloverDialogTitleTeaching: { text: 'Start new academic year', description: 'Dialog title for starting new academic year.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverDialogTitleTraining: { text: 'Start next intake', description: 'Dialog title for starting next training intake.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverStepNewRoot: { text: '1. New year', description: 'Wizard step 1 label for teaching.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverStepNewRootTraining: { text: '1. New intake', description: 'Wizard step 1 label for training.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverStepCopy: { text: '2. Copy structure', description: 'Wizard step 2 label.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverStepPromotion: { text: '3. Promotion', description: 'Wizard step 3 label.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverStepReview: { text: '4. Review', description: 'Wizard step 4 label.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverStepResults: { text: '5. Complete', description: 'Wizard step 5 label.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverBack: { text: 'Back', description: 'Wizard back button.', surface: 'shell', maxChars: 15 },
  NLSEducationRolloverNext: { text: 'Next', description: 'Wizard next button.', surface: 'shell', maxChars: 15 },
  NLSEducationRolloverCancel: { text: 'Cancel', description: 'Wizard cancel button.', surface: 'shell', maxChars: 15 },
  NLSEducationRolloverRun: { text: 'Start rollover', description: 'Wizard run button.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverDone: { text: 'Done', description: 'Wizard done button.', surface: 'shell', maxChars: 15 },
  NLSEducationRolloverRetry: { text: 'Retry', description: 'Wizard retry button.', surface: 'shell', maxChars: 15 },
  NLSEducationRolloverTitleLabel: { text: 'Title', description: 'Label for new year title.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverTitleRequired: { text: 'Title is required.', description: 'Validation error when title is empty.', surface: 'shell', maxChars: 25 },
  NLSEducationRolloverStartDateLabel: { text: 'Start date', description: 'Label for start date.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverEndDateLabel: { text: 'End date', description: 'Label for end date.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverCopyClassesIntro: { text: 'Select classes and subjects to copy into the new year. Renamed titles will be applied.', description: 'Copy step instructions for teaching.', surface: 'shell', maxChars: 90 },
  NLSEducationRolloverCopyProgrammesIntro: { text: 'Select groups, clients and phases to copy into the new intake.', description: 'Copy step instructions for training.', surface: 'shell', maxChars: 90 },
  NLSEducationRolloverRenameLabel: { text: 'Rename', description: 'Label for rename field.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverDuplicateNameError: { text: 'Duplicate name: {name}', description: 'Inline error when node names clash.', surface: 'shell', maxChars: 50 },
  NLSEducationRolloverEmptyNameError: { text: 'Name cannot be empty.', description: 'Inline error when node name is empty.', surface: 'shell', maxChars: 30 },
  NLSEducationRolloverPromotionIntro: { text: 'Choose the promotion target for each class with active learners.', description: 'Promotion step instructions.', surface: 'shell', maxChars: 80 },
  NLSEducationRolloverClassColumn: { text: 'Class', description: 'Class column header in promotions.', surface: 'shell', maxChars: 20 },
  NLSEducationRolloverTargetColumn: { text: 'Promotion target', description: 'Target column header in promotions.', surface: 'shell', maxChars: 25 },
  NLSEducationRolloverLeaves: { text: 'Leaves', description: 'Option for learner or class leaving.', surface: 'shell', maxChars: 15 },
  NLSEducationRolloverUntickedTargetError: { text: 'Target class was unticked in the copy step.', description: 'Error when promotion target is not copied.', surface: 'shell', maxChars: 60 },
  NLSEducationRolloverExceptionsTitle: { text: 'Learner exceptions', description: 'Section title for promotion exceptions.', surface: 'shell', maxChars: 30 },
  NLSEducationRolloverExceptionsSearchPlaceholder: { text: 'Search learners by name…', description: 'Placeholder for learner search input.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverSameAsClass: { text: 'Same as class ({target})', description: 'Option for learner following class promotion.', surface: 'shell', maxChars: 45 },
  NLSEducationRolloverLeavesFrozen: { text: 'Leaves (frozen)', description: 'Display for frozen learner leaving.', surface: 'shell', maxChars: 25 },
  NLSEducationRolloverReviewTitle: { text: 'Review rollover details', description: 'Title for review step.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverReviewClassesCount: { text: 'Classes to copy: {count}', description: 'Summary count of classes to copy.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverReviewSubjectsCount: { text: 'Subjects to copy: {count}', description: 'Summary count of subjects to copy.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverReviewMovingCount: { text: 'Learners moving up: {count}', description: 'Summary count of learners moving.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverReviewStayingCount: { text: 'Learners staying in class: {count}', description: 'Summary count of learners staying.', surface: 'shell', maxChars: 40 },
  NLSEducationRolloverReviewLeavingCount: { text: 'Learners leaving: {count}', description: 'Summary count of learners leaving.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverReviewTeachersTitle: { text: 'Teacher assignments', description: 'Section header for teacher review.', surface: 'shell', maxChars: 30 },
  NLSEducationRolloverTeacherUntickedNote: { text: 'Restricted teacher access to past records will still be copied during rollover.', description: 'Notice when a teacher is unticked.', surface: 'shell', maxChars: 90 },
  NLSEducationRolloverReadOnlyWarning: { text: 'Warning: Once completed, the current academic year will become read-only and move to Past years.', description: 'Warning that old year becomes read-only.', surface: 'shell', maxChars: 110 },
  NLSEducationRolloverProgressCreating: { text: 'Creating new structure...', description: 'Progress label for element creation.', surface: 'shell', maxChars: 40 },
  NLSEducationRolloverProgressTagging: { text: 'Tagging education hierarchy...', description: 'Progress label for tagging structure.', surface: 'shell', maxChars: 40 },
  NLSEducationRolloverProgressAssigning: { text: 'Assigning teacher roles...', description: 'Progress label for teacher assignment.', surface: 'shell', maxChars: 40 },
  NLSEducationRolloverProgressRollingOver: { text: 'Executing rollover and promotions...', description: 'Progress label for executing rollover.', surface: 'shell', maxChars: 50 },
  NLSEducationRolloverResultsTitle: { text: 'Rollover complete', description: 'Header for results step.', surface: 'shell', maxChars: 30 },
  NLSEducationRolloverResultsCarried: { text: 'Learners carried forward: {count}', description: 'Count of carried learners.', surface: 'shell', maxChars: 40 },
  NLSEducationRolloverResultsEnded: { text: 'Enrolments ended: {count}', description: 'Count of ended enrolments.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverResultsLeft: { text: 'Learners left: {count}', description: 'Count of left learners.', surface: 'shell', maxChars: 30 },
  NLSEducationRolloverResultsGrantsCopied: { text: 'Access grants copied: {count}', description: 'Count of copied grants.', surface: 'shell', maxChars: 35 },
  NLSEducationRolloverResultsArchived: { text: 'Nodes archived: {count}', description: 'Count of archived nodes.', surface: 'shell', maxChars: 30 },
  NLSEducationRolloverNoEnrolmentsNotice: { text: 'No classes have active enrolments. All classes can be copied without promotions.', description: 'Notice when no enrolments exist.', surface: 'shell', maxChars: 90 },

  NLSEducationTabTimetable: { text: 'Timetable', description: 'Education hub Timetable tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabAttendance: { text: 'Attendance', description: 'Education hub Attendance tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabWork: { text: 'Work', description: 'Education hub Work tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabAssessments: { text: 'Assessments', description: 'Education hub Assessments tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabLearners: { text: 'Learners', description: 'Education hub Learners tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabFees: { text: 'Fees', description: 'Education hub Fees tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabAdmissions: { text: 'Admissions', description: 'Education hub Admissions tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabFeed: { text: 'Class feed', description: 'Education hub Feed tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabMessages: { text: 'Messages', description: 'Education hub Messages tab.', surface: 'shell', maxChars: 20 },
  NLSEducationSubTabAnnouncements: { text: 'Announcements', description: 'Announcements sub-tab label.', surface: 'shell', maxChars: 25 },
  NLSEducationSubTabFeed: { text: 'Feed', description: 'Feed sub-tab label.', surface: 'shell', maxChars: 20 },
  NLSEducationFeedComingSoonTitle: { text: 'Class feed coming next', description: 'Empty state title for feed in b1a.', surface: 'shell', maxChars: 50 },
  NLSEducationFeedComingSoonBody: { text: 'Photos, highlights and tag-aware updates arrive in the next update.', description: 'Empty state description for feed in b1a.', surface: 'shell', maxChars: 120 },
  NLSEducationAllClasses: { text: 'All classes', description: 'Option to select all classes.', surface: 'shell', maxChars: 30 },
  NLSEducationAllBatches: { text: 'All batches', description: 'Option to select all batches.', surface: 'shell', maxChars: 30 },
  NLSEducationSelectClassPrompt: { text: 'Select class', description: 'Prompt to select a class.', surface: 'shell', maxChars: 30 },
  NLSEducationSelectBatchPrompt: { text: 'Select batch', description: 'Prompt to select a batch.', surface: 'shell', maxChars: 30 },
  NLSEducationNewAnnouncement: { text: 'New announcement', description: 'Button and modal title for creating announcement.', surface: 'shell', maxChars: 30 },
  NLSEducationEditAnnouncement: { text: 'Edit announcement', description: 'Modal title for editing announcement.', surface: 'shell', maxChars: 30 },
  NLSEducationAnnouncementTitle: { text: 'Title', description: 'Announcement title input label.', surface: 'shell', maxChars: 20 },
  NLSEducationAnnouncementTitlePlaceholder: { text: 'Announcement title...', description: 'Placeholder for announcement title.', surface: 'shell', maxChars: 50 },
  NLSEducationAnnouncementBody: { text: 'Message', description: 'Announcement message body label.', surface: 'shell', maxChars: 20 },
  NLSEducationAnnouncementBodyPlaceholder: { text: 'Write your announcement here...', description: 'Placeholder for announcement body.', surface: 'shell', maxChars: 60 },
  NLSEducationAnnouncementTarget: { text: 'Target', description: 'Target class or batch for announcement.', surface: 'shell', maxChars: 20 },
  NLSEducationAnnouncementAttachments: { text: 'Attachments', description: 'Attachments label for announcement.', surface: 'shell', maxChars: 30 },
  NLSEducationAnnouncementAddFiles: { text: 'Add files', description: 'Button to add file attachments.', surface: 'shell', maxChars: 25 },
  NLSEducationAnnouncementMaxAttachments: { text: 'Up to 5 files allowed.', description: 'Attachment limit message.', surface: 'shell', maxChars: 40 },
  NLSEducationAnnouncementPostAction: { text: 'Post announcement', description: 'Action button to post announcement.', surface: 'shell', maxChars: 30 },
  NLSEducationAnnouncementSaveAction: { text: 'Save changes', description: 'Action button to save announcement.', surface: 'shell', maxChars: 25 },
  NLSEducationAnnouncementDeleteAction: { text: 'Delete announcement', description: 'Button to delete announcement.', surface: 'shell', maxChars: 30 },
  NLSEducationAnnouncementDeleteConfirmTitle: { text: 'Delete announcement?', description: 'Confirm title for deleting announcement.', surface: 'shell', maxChars: 40 },
  NLSEducationAnnouncementDeleteConfirmBody: { text: 'This announcement and its attachments will be permanently removed.', description: 'Confirm body for deleting announcement.', surface: 'shell', maxChars: 120 },
  NLSEducationAnnouncementDeleteConfirmButton: { text: 'Delete', description: 'Confirm button to delete announcement.', surface: 'shell', maxChars: 20 },
  NLSEducationAnnouncementCancelButton: { text: 'Cancel', description: 'Cancel button label.', surface: 'shell', maxChars: 20 },
  NLSEducationAnnouncementEmptyTitle: { text: 'No announcements yet', description: 'Empty state title for announcements.', surface: 'shell', maxChars: 40 },
  NLSEducationAnnouncementEmptyBody: { text: 'Share updates, reminders and announcements with your classes.', description: 'Empty state body for announcements.', surface: 'shell', maxChars: 120 },
  NLSEducationAnnouncementEdited: { text: 'edited', description: 'Mark on edited announcements.', surface: 'shell', maxChars: 15 },
  NLSEducationAnnouncementReadCount: { text: 'Read by {count}', description: 'Read count display.', surface: 'shell', maxChars: 30 },
  NLSEducationAnnouncementLoadMore: { text: 'Load more', description: 'Button to load more announcements.', surface: 'shell', maxChars: 25 },
  NLSEducationMessagingDisabledTitle: { text: '1:1 messaging is turned off', description: 'Title when messaging is disabled.', surface: 'shell', maxChars: 50 },
  NLSEducationMessagingDisabledBody: { text: 'Direct messaging between staff and families is currently disabled for this workspace.', description: 'Body when messaging is disabled.', surface: 'shell', maxChars: 140 },
  NLSEducationMessagingOpenSettings: { text: 'Education settings', description: 'Link to settings tab.', surface: 'shell', maxChars: 30 },
  NLSEducationMessagesInboxTitle: { text: '1:1 Messages', description: 'Messages tab header title.', surface: 'shell', maxChars: 30 },
  NLSEducationMessagesNewButton: { text: 'New message', description: 'Button to start a new 1:1 thread.', surface: 'shell', maxChars: 25 },
  NLSEducationMessagesEmptyInbox: { text: 'No messages yet', description: 'Empty inbox description.', surface: 'shell', maxChars: 40 },
  NLSEducationMessagesEmptyInboxBody: { text: 'Start a conversation with a learner family or wait for incoming messages.', description: 'Empty inbox body description.', surface: 'shell', maxChars: 120 },
  NLSEducationMessagesLoadMoreThreads: { text: 'Load more conversations', description: 'Button to load more threads.', surface: 'shell', maxChars: 40 },
  NLSEducationMessagesLoadEarlier: { text: 'Load earlier messages', description: 'Button to load earlier thread messages.', surface: 'shell', maxChars: 40 },
  NLSEducationMessagesTypePlaceholder: { text: 'Write a message...', description: 'Placeholder for thread message composer.', surface: 'shell', maxChars: 40 },
  NLSEducationMessagesSendAction: { text: 'Send', description: 'Send message button label.', surface: 'shell', maxChars: 20 },
  NLSEducationMessagesEditedMark: { text: 'edited', description: 'Mark indicating message was edited.', surface: 'shell', maxChars: 15 },
  NLSEducationMessagesEditAction: { text: 'Edit', description: 'Button to edit message.', surface: 'shell', maxChars: 20 },
  NLSEducationMessagesDeleteAction: { text: 'Delete', description: 'Button to delete message.', surface: 'shell', maxChars: 20 },
  NLSEducationMessagesDeleteConfirmTitle: { text: 'Delete message?', description: 'Confirm title to delete message.', surface: 'shell', maxChars: 30 },
  NLSEducationMessagesDeleteConfirmBody: { text: 'Are you sure you want to delete this message?', description: 'Confirm body to delete message.', surface: 'shell', maxChars: 80 },
  NLSEducationMessagesPickLearnerTitle: { text: 'Message a family', description: 'Modal title to pick a learner.', surface: 'shell', maxChars: 35 },
  NLSEducationMessagesPickLearnerPrompt: { text: 'Choose a learner to open or continue a 1:1 conversation:', description: 'Prompt to choose learner.', surface: 'shell', maxChars: 100 },
  NLSEducationMessagesNoLearnersFound: { text: 'No messageable learners found.', description: 'Notice when no messageable learners.', surface: 'shell', maxChars: 60 },
  NLSEducationMessagesStartThreadAction: { text: 'Start conversation', description: 'Action button to open thread.', surface: 'shell', maxChars: 30 },
  NLSEducationMessagesThreadSubtitle: { text: 'Direct conversation with family', description: 'Subtitle for inbox thread item.', surface: 'shell', maxChars: 40 },
  NLSEducationOfficeHoursTitle: { text: 'My office hours', description: 'Office hours section title.', surface: 'shell', maxChars: 30 },
  NLSEducationOfficeHoursNotice: { text: 'Messages from families outside these hours arrive at once; their alerts are held until your next window.', description: 'Office hours explanation.', surface: 'shell', maxChars: 140 },
  NLSEducationOfficeHoursAddWindow: { text: 'Add window', description: 'Button to add office hours window.', surface: 'shell', maxChars: 25 },
  NLSEducationOfficeHoursSaveAction: { text: 'Save office hours', description: 'Button to save office hours.', surface: 'shell', maxChars: 30 },
  NLSEducationOfficeHoursSavedSuccess: { text: 'Office hours saved.', description: 'Success notice when office hours saved.', surface: 'shell', maxChars: 40 },
  NLSEducationOfficeHoursErrorOverlap: { text: 'Windows cannot overlap on the same day.', description: 'Validation error for overlapping hours.', surface: 'shell', maxChars: 70 },
  NLSEducationOfficeHoursErrorInvalidTimes: { text: 'Start time must be before end time.', description: 'Validation error when start is after end.', surface: 'shell', maxChars: 60 },
  NLSEducationMessagesSafeguardingToggle: { text: 'All threads (safeguarding)', description: 'Toggle label for admin safeguarding view.', surface: 'shell', maxChars: 45 },
  NLSEducationMessagesSafeguardingNotice: { text: 'You are viewing all threads as an administrator. Opening threads is recorded in the audit log.', description: 'Audit notice for safeguarding view.', surface: 'shell', maxChars: 140 },
  NLSEducationSettingsMessagingTitle: { text: '1:1 messages with families', description: 'Title for 1:1 messaging toggle in settings.', surface: 'shell', maxChars: 45 },
  NLSEducationSettingsMessagingDescription: { text: 'Allow families and teachers to message each other directly with office hours alert scheduling.', description: 'Description for 1:1 messaging toggle in settings.', surface: 'shell', maxChars: 130 },
  NLSEduErrorEditWindowExpired: { text: 'The edit window has expired (24 hours maximum).', description: 'Error when editing past 24 hours.', surface: 'shell', maxChars: 70 },
  NLSEduErrorDeleteWindowExpired: { text: 'The delete window has expired (24 hours maximum).', description: 'Error when deleting past 24 hours.', surface: 'shell', maxChars: 70 },
  NLSEduErrorMinorReadOnly: { text: 'Learner accounts for minors cannot send direct messages.', description: 'Error when minor tries to message.', surface: 'shell', maxChars: 80 },
  NLSEduErrorNotMessageableTeacher: { text: 'You are not assigned as a teacher for this learner.', description: 'Error when not an assigned teacher.', surface: 'shell', maxChars: 80 },
  NLSEduErrorNotLearnerTeacher: { text: 'You do not teach this learner.', description: 'Error when staff does not teach learner.', surface: 'shell', maxChars: 60 },
  NLSEduErrorCannotOpenForOtherStaff: { text: 'Threads can only be opened for yourself.', description: 'Error when opening thread for other staff.', surface: 'shell', maxChars: 70 },
  NLSEduErrorAdminOnly: { text: 'This action is restricted to workspace administrators.', description: 'Error when non-admin tries admin action.', surface: 'shell', maxChars: 80 },
  NLSEduErrorNotClassStaff: { text: 'You are not assigned as staff for this class.', description: 'Error when not class staff.', surface: 'shell', maxChars: 70 },
  NLSEduErrorNotProgrammeTrainer: { text: 'You are not a trainer for this programme.', description: 'Error when not programme trainer.', surface: 'shell', maxChars: 70 },
  NLSEduErrorAttachmentNotYours: { text: 'One or more attachments do not belong to this upload.', description: 'Error when attachment is not caller upload.', surface: 'shell', maxChars: 80 },
  NLSEduErrorMessagingOff: { text: '1:1 messaging is disabled for this workspace.', description: 'Error when messaging is disabled.', surface: 'shell', maxChars: 70 },
  NLSEduErrorConsentBlocksTag: { text: 'Media consent blocks tagging for: {names}', description: 'Error when tagging learners without photo consent', surface: 'shell', maxChars: 120 },
  NLSEduErrorConsentBlocksTagGeneric: { text: 'Media consent blocks tagging for one or more learners.', description: 'Error when tagging without photo consent', surface: 'shell', maxChars: 70 },
  NLSEduErrorNodeRequired: { text: 'Selecting a class is required.', description: 'Error when class node is required', surface: 'shell', maxChars: 50 },
  NLSEduErrorPinLimit: { text: 'Cannot pin more than 20 items for a learner.', description: 'Error when pin limit of 20 is reached', surface: 'shell', maxChars: 60 },
  NLSEduErrorPostNotVisible: { text: 'You do not have access to this feed post.', description: 'Error when post is not visible', surface: 'shell', maxChars: 60 },
  NLSEducationMessagesFamilyOf: { text: 'Family of {name}', description: 'Display name for family sender in messages', surface: 'shell', maxChars: 60 },
  NLSEduStaffSender: { text: 'Staff', description: 'Fallback sender name for staff member', surface: 'shell', maxChars: 20 },
  NLSEducationLearnerDefault: { text: 'Learner', description: 'Fallback learner name', surface: 'shell', maxChars: 20 },
  NLSEducationFeedTabTitle: { text: 'Feed', description: 'Feed tab or segment title', surface: 'shell', maxChars: 20 },
  NLSEducationFeedEmptyTitle: { text: 'No feed posts yet', description: 'Empty state title for class feed', surface: 'shell', maxChars: 40 },
  NLSEducationFeedEmptyBody: { text: 'Post photos, videos, and class updates to share with families.', description: 'Empty state description for class feed', surface: 'shell', maxChars: 100 },
  NLSEducationFeedLoadMore: { text: 'Load more posts', description: 'Load more feed posts action button', surface: 'shell', maxChars: 30 },
  NLSEducationFeedNewPost: { text: 'New post', description: 'Button to open feed post composer', surface: 'shell', maxChars: 20 },
  NLSEducationFeedEditPost: { text: 'Edit post', description: 'Dialog title for editing feed post', surface: 'shell', maxChars: 20 },
  NLSEducationFeedDeletePost: { text: 'Delete post', description: 'Action button to delete feed post', surface: 'shell', maxChars: 25 },
  NLSEducationFeedDeleteConfirmTitle: { text: 'Delete feed post', description: 'Confirmation title for deleting feed post', surface: 'shell', maxChars: 35 },
  NLSEducationFeedDeleteConfirmBody: { text: 'Are you sure you want to delete this feed post? It will no longer be visible.', description: 'Confirmation body for deleting feed post', surface: 'shell', maxChars: 110 },
  NLSEducationFeedDeleteConfirmButton: { text: 'Delete post', description: 'Confirm button to delete feed post', surface: 'shell', maxChars: 25 },
  NLSEducationFeedCancelButton: { text: 'Cancel', description: 'Cancel button', surface: 'shell', maxChars: 20 },
  NLSEducationFeedSaveAction: { text: 'Save changes', description: 'Button to save edited feed post', surface: 'shell', maxChars: 25 },
  NLSEducationFeedPublishAction: { text: 'Post', description: 'Button to publish feed post', surface: 'shell', maxChars: 20 },
  NLSEducationFeedReachLabel: { text: 'Reach', description: 'Reach option label in feed composer', surface: 'shell', maxChars: 20 },
  NLSEducationFeedReachClass: { text: 'Whole class', description: 'Whole class reach option', surface: 'shell', maxChars: 25 },
  NLSEducationFeedReachTagged: { text: 'Tagged families only', description: 'Tagged families reach option', surface: 'shell', maxChars: 35 },
  NLSEducationFeedBodyLabel: { text: 'Message', description: 'Feed post body input label', surface: 'shell', maxChars: 20 },
  NLSEducationFeedBodyPlaceholder: { text: 'Share updates, activities, or photos with families...', description: 'Placeholder for feed post body', surface: 'shell', maxChars: 80 },
  NLSEducationFeedMediaLabel: { text: 'Photos and videos', description: 'Label for feed post file picker', surface: 'shell', maxChars: 35 },
  NLSEducationFeedTagLearnersLabel: { text: 'Tag learners', description: 'Label for tagging learners in feed post', surface: 'shell', maxChars: 25 },
  NLSEducationFeedTagLearnersNote: { text: 'Only tag learners shown in the photo. Learners without photo consent can\'t be tagged in photo posts.', description: 'Consent advisory for photo tagging', surface: 'shell', maxChars: 140 },
  NLSEducationFeedTagSearchPlaceholder: { text: 'Search learners...', description: 'Search placeholder for tag picker', surface: 'shell', maxChars: 35 },
  NLSEducationFeedTaggedCount: { text: 'Tagged: {count}', description: 'Chip indicating number of tagged learners', surface: 'shell', maxChars: 30 },
  NLSEducationFeedSeenCount: { text: '{count} seen', description: 'Count of families who have seen post', surface: 'shell', maxChars: 25 },
  NLSEducationFeedEditedMark: { text: 'edited', description: 'Mark indicating post has been edited', surface: 'shell', maxChars: 20 },
  NLSEducationFeedConsentChangedBadge: { text: 'Consent changed since posting; only their own family still sees it ({names})', description: 'Warning banner for altered media consent', surface: 'shell', maxChars: 140 },
  NLSEducationFeedMediaConsentBtn: { text: 'Media consent', description: 'Button to open media consent roster', surface: 'shell', maxChars: 25 },
  NLSEducationFeedConsentPhotosOk: { text: 'Photos OK', description: 'Media consent level: Photos OK', surface: 'shell', maxChars: 25 },
  NLSEducationFeedConsentClassOnly: { text: 'Class only', description: 'Media consent level: Class only', surface: 'shell', maxChars: 25 },
  NLSEducationFeedConsentNone: { text: 'No photos', description: 'Media consent level: No photos', surface: 'shell', maxChars: 25 },
  NLSEducationFeedConsentDisabledNone: { text: 'Family has not consented to photos', description: 'Reason why learner cannot be tagged in media post', surface: 'shell', maxChars: 60 },
  NLSEducationFeedConsentDisabledClassOnlyAllClasses: { text: 'Class only consent cannot be tagged under All classes', description: 'Reason why class only learner cannot be tagged under all classes', surface: 'shell', maxChars: 80 },
  NLSEducationFeedReachTaggedMinOne: { text: 'Tagged families only reach requires tagging at least one learner.', description: 'Validation message when reach is TAGGED with 0 tags', surface: 'shell', maxChars: 90 },
  NLSEducationFeedMaxMediaError: { text: 'Maximum 5 photos or videos allowed.', description: 'Validation error when file limit is exceeded in feed composer.', surface: 'shell', maxChars: 50 },
  NLSEducationFeedMaxCharsError: { text: 'Maximum 5000 characters.', description: 'Validation error when body length exceeds 5000 characters in feed composer.', surface: 'shell', maxChars: 40 },
  NLSEducationFeedNoLearnersFound: { text: 'No learners found', description: 'Empty state message when no learners are found in consent picker or roster.', surface: 'shell', maxChars: 30 },
  NLSFamilyTabClass: { text: 'Class', description: 'Family Class tab label.', surface: 'shell', maxChars: 20 },
  NLSFamilySubTabAnnouncements: { text: 'Announcements', description: 'Announcements subtab label in Family Class tab.', surface: 'shell', maxChars: 25 },
  NLSFamilySubTabFeed: { text: 'Feed', description: 'Feed subtab label in Family Class tab.', surface: 'shell', maxChars: 20 },
  NLSFamilySubTabMessages: { text: 'Messages', description: 'Messages subtab label in Family Class tab.', surface: 'shell', maxChars: 20 },
  NLSFamilyHeaderMenuAria: { text: 'More options', description: 'Aria label for header menu button in family shell.', surface: 'shell', maxChars: 30 },
  NLSFamilyNewFromSchoolTitle: { text: 'New from school', description: 'Heading for new updates from school row in Family Today tab.', surface: 'shell', maxChars: 30 },
  NLSFamilyNewAnnouncementsCount: { text: '{count} announcements', description: 'Unread announcements count in Today strip.', surface: 'shell', maxChars: 30 },
  NLSFamilyNewFeedCount: { text: '{count} new posts', description: 'New feed posts count in Today strip.', surface: 'shell', maxChars: 30 },
  NLSFamilyNewMessagesCount: { text: '{count} messages', description: 'Unread messages count in Today strip.', surface: 'shell', maxChars: 30 },
  NLSFamilyMinorReadOnlyNote: { text: 'Minor learner accounts are read-only.', description: 'Notice shown to minor accounts explaining read-only access.', surface: 'shell', maxChars: 50 },
  NLSFamilyAnnouncementsEmptyTitle: { text: 'No announcements yet', description: 'Empty state title for announcements.', surface: 'shell', maxChars: 35 },
  NLSFamilyAnnouncementsEmptyBody: { text: 'Announcements from your teachers and school will appear here.', description: 'Empty state description for announcements.', surface: 'shell', maxChars: 90 },
  NLSFamilyAnnouncementsLoadMore: { text: 'Load more announcements', description: 'Button to load more announcements.', surface: 'shell', maxChars: 35 },
  NLSFamilyAnnouncementEdited: { text: 'edited', description: 'Mark indicating announcement was edited.', surface: 'shell', maxChars: 15 },
  NLSFamilyAnnouncementAttachments: { text: 'Attachments', description: 'Label for announcement attachments list.', surface: 'shell', maxChars: 20 },
  NLSFamilyFeedEmptyTitle: { text: 'No feed posts yet', description: 'Empty state title for family feed.', surface: 'shell', maxChars: 35 },
  NLSFamilyFeedEmptyBody: { text: 'Class photos and activity highlights will appear here.', description: 'Empty state description for family feed.', surface: 'shell', maxChars: 90 },
  NLSFamilyFeedLoadMore: { text: 'Load more posts', description: 'Button to load more feed posts.', surface: 'shell', maxChars: 30 },
  NLSFamilyFeedEdited: { text: 'edited', description: 'Mark indicating feed post was edited.', surface: 'shell', maxChars: 15 },
  NLSFamilyFeedVideoLink: { text: 'Watch video', description: 'Link text for feed post video attachment.', surface: 'shell', maxChars: 25 },
  NLSFamilyFeedViewImage: { text: 'View photo', description: 'Action button or alt text to view full-size photo.', surface: 'shell', maxChars: 25 },
  NLSFamilyFeedDialogClose: { text: 'Close', description: 'Close button for photo dialog in family feed.', surface: 'shell', maxChars: 15 },
  NLSFamilyMessagesEmptyTitle: { text: 'No messages yet', description: 'Empty state title for messages.', surface: 'shell', maxChars: 30 },
  NLSFamilyMessagesEmptyBody: { text: 'Start a 1:1 conversation with one of your teachers.', description: 'Empty state body for messages.', surface: 'shell', maxChars: 80 },
  NLSFamilyMessagesDisabledTitle: { text: '1:1 messaging is disabled', description: 'Notice that 1:1 messaging is off for this workspace.', surface: 'shell', maxChars: 40 },
  NLSFamilyMessagesDisabledBody: { text: 'Direct messaging with teachers is turned off for this school.', description: 'Description explaining 1:1 messaging is turned off.', surface: 'shell', maxChars: 90 },
  NLSFamilyMessagesNewMessage: { text: 'New message', description: 'Button to start a new 1:1 message thread.', surface: 'shell', maxChars: 20 },
  NLSFamilyMessagesSelectTeacher: { text: 'Select a teacher', description: 'Heading or prompt to pick a teacher for new message.', surface: 'shell', maxChars: 30 },
  NLSFamilyMessagesOutsideOfficeHours: { text: 'Outside office hours — they will be alerted at {time}', description: 'Notice indicating teacher is outside office hours.', surface: 'shell', maxChars: 80 },
  NLSFamilyMessagesDeliveredNotice: { text: 'Delivered. Your teacher will be alerted at {time}', description: 'Confirmation banner that message was saved and alert held.', surface: 'shell', maxChars: 80 },
  NLSFamilyMessagesSend: { text: 'Send', description: 'Send button for thread message.', surface: 'shell', maxChars: 15 },
  NLSFamilyMessagesSending: { text: 'Sending…', description: 'Sending state for message button.', surface: 'shell', maxChars: 20 },
  NLSFamilyMessagesPlaceholder: { text: 'Type a message…', description: 'Placeholder for thread message text area.', surface: 'shell', maxChars: 30 },
  NLSFamilyMessagesAttachFiles: { text: 'Attach photos or PDFs (max 5)', description: 'Button or label for attaching files to thread message.', surface: 'shell', maxChars: 40 },
  NLSFamilyMessagesEdit: { text: 'Edit', description: 'Action to edit sent message.', surface: 'shell', maxChars: 15 },
  NLSFamilyMessagesSaveEdit: { text: 'Save', description: 'Button to save edited message.', surface: 'shell', maxChars: 15 },
  NLSFamilyMessagesCancelEdit: { text: 'Cancel', description: 'Cancel message edit button.', surface: 'shell', maxChars: 15 },
  NLSFamilyMessagesDelete: { text: 'Delete', description: 'Action to delete sent message.', surface: 'shell', maxChars: 15 },
  NLSFamilyMessagesDeleteConfirmTitle: { text: 'Delete message?', description: 'Confirmation title for deleting message.', surface: 'shell', maxChars: 30 },
  NLSFamilyMessagesDeleteConfirmBody: { text: 'Are you sure you want to delete this message? This cannot be undone.', description: 'Confirmation body for deleting message.', surface: 'shell', maxChars: 90 },
  NLSFamilyMessagesLoadMore: { text: 'Load earlier messages', description: 'Button to load earlier messages in thread.', surface: 'shell', maxChars: 35 },
  NLSFamilyMessagesBackToThreads: { text: 'Back to conversations', description: 'Button to navigate back to list of threads.', surface: 'shell', maxChars: 30 },
  NLSFamilyMessagesMinorNotice: { text: 'Conversations are managed by your parent or guardian.', description: 'Notice for minor learner viewing message threads.', surface: 'shell', maxChars: 70 },
  NLSEduConsentRosterTitle: { text: 'Media consent roster', description: 'Dialog title for class media consent roster', surface: 'shell', maxChars: 35 },
  NLSEduConsentRosterNotice: { text: 'Consent levels are set by families. Staff cannot change them.', description: 'Advisory notice on consent roster', surface: 'shell', maxChars: 90 },
  NLSEduConsentRosterPhotosOkCount: { text: 'Photos OK: {count}', description: 'Count of learners with Photos OK consent', surface: 'shell', maxChars: 30 },
  NLSEduConsentRosterClassOnlyCount: { text: 'Class only: {count}', description: 'Count of learners with Class only consent', surface: 'shell', maxChars: 30 },
  NLSEduConsentRosterNoneCount: { text: 'No photos: {count}', description: 'Count of learners with No photos consent', surface: 'shell', maxChars: 30 },
  NLSEduConsentRosterTotal: { text: 'Total: {count}', description: 'Total count of learners on roster', surface: 'shell', maxChars: 25 },
  NLSEduConsentRosterClose: { text: 'Close', description: 'Close button for consent roster dialog', surface: 'shell', maxChars: 20 },
  NLSEduLearnerPageBack: { text: 'Back to learners', description: 'Button to return to learners table', surface: 'shell', maxChars: 30 },
  NLSEduLearnerPageTitle: { text: 'Learner profile', description: 'Title of learner detail page', surface: 'shell', maxChars: 30 },
  NLSEduLearnerPageMinorBadge: { text: 'Minor', description: 'Badge indicating learner is a minor', surface: 'shell', maxChars: 20 },
  NLSEduLearnerPageAdultBadge: { text: 'Adult', description: 'Badge indicating learner is an adult', surface: 'shell', maxChars: 20 },
  NLSEduLearnerPageClassesLabel: { text: 'Classes & Programmes', description: 'Label for enrolled classes and programmes', surface: 'shell', maxChars: 35 },
  NLSEduLearnerPageConsentSection: { text: 'Media consent', description: 'Media consent section title on learner page', surface: 'shell', maxChars: 25 },
  NLSEduLearnerPageConsentSetByFamily: { text: 'Set by family', description: 'Subtitle indicating consent is set by family', surface: 'shell', maxChars: 25 },
  NLSEduLearnerPagePortfolioSection: { text: 'Portfolio', description: 'Portfolio section title on learner page', surface: 'shell', maxChars: 25 },
  NLSEduLearnerPagePortfolioDownloadPdf: { text: 'Download portfolio PDF', description: 'Button to download portfolio as PDF', surface: 'shell', maxChars: 40 },
  NLSEduLearnerPageMessageFamily: { text: 'Message family', description: 'Button to start or view thread with family', surface: 'shell', maxChars: 30 },
  NLSEduLearnerPageOpenBtn: { text: 'Open', description: 'Button in learners table to open learner profile', surface: 'shell', maxChars: 20 },
  NLSEduPortfolioEmpty: { text: 'No portfolio items yet.', description: 'Empty state message for learner portfolio', surface: 'shell', maxChars: 40 },
  NLSEduPortfolioLoadMore: { text: 'Load more items', description: 'Button to load more portfolio items', surface: 'shell', maxChars: 30 },
  NLSEduPortfolioPinnedSection: { text: 'Pinned items', description: 'Header for pinned portfolio items', surface: 'shell', maxChars: 25 },
  NLSEduPortfolioTimelineSection: { text: 'Timeline', description: 'Header for chronological portfolio items', surface: 'shell', maxChars: 20 },
  NLSEduPortfolioKindHomework: { text: 'Homework', description: 'Label for homework portfolio item', surface: 'shell', maxChars: 20 },
  NLSEduPortfolioKindFeed: { text: 'Feed post', description: 'Label for feed portfolio item', surface: 'shell', maxChars: 20 },
  NLSEduPortfolioKindCertificate: { text: 'Certificate', description: 'Label for certificate portfolio item', surface: 'shell', maxChars: 25 },
  NLSEduPortfolioKindPractice: { text: 'Practice log', description: 'Label for practice log portfolio item', surface: 'shell', maxChars: 25 },
  NLSEduPortfolioPinAction: { text: 'Pin', description: 'Action button to pin portfolio item', surface: 'shell', maxChars: 20 },
  NLSEduPortfolioUnpinAction: { text: 'Unpin', description: 'Action button to unpin portfolio item', surface: 'shell', maxChars: 20 },
  NLSEduPortfolioHideAction: { text: 'Hide', description: 'Action button to hide portfolio item from family', surface: 'shell', maxChars: 20 },
  NLSEduPortfolioUnhideAction: { text: 'Unhide', description: 'Action button to unhide portfolio item for family', surface: 'shell', maxChars: 20 },
  NLSEduPortfolioHiddenBadge: { text: 'Hidden from family', description: 'Badge on portfolio item hidden from family', surface: 'shell', maxChars: 30 },
  NLSEduPortfolioPinnedBadge: { text: 'Pinned', description: 'Badge on pinned portfolio item', surface: 'shell', maxChars: 20 },
  NLSEduPortfolioViewCertPdf: { text: 'View PDF', description: 'Action to view certificate PDF', surface: 'shell', maxChars: 20 },
  NLSEduTraineeLearnerPageLink: { text: 'Learner page', description: 'Link from trainee progress view to learner page', surface: 'shell', maxChars: 25 },
  NLSEduSettingsBackfillTitle: { text: 'Build portfolios from past work', description: 'Settings section title for portfolio backfill', surface: 'shell', maxChars: 50 },
  NLSEduSettingsBackfillDescription: { text: 'Scan approved homework, tagged feed posts, certificates, and practice logs to build or update learners\' portfolios.', description: 'Settings description for portfolio backfill', surface: 'shell', maxChars: 160 },
  NLSEduSettingsBackfillAction: { text: 'Start backfill', description: 'Button to trigger portfolio backfill', surface: 'shell', maxChars: 30 },
  NLSEduSettingsBackfillCancelAction: { text: 'Cancel backfill', description: 'Button to stop running backfill', surface: 'shell', maxChars: 30 },
  NLSEduSettingsBackfillRunning: { text: 'Backfilling portfolios... {count} items processed', description: 'Running progress indicator for backfill', surface: 'shell', maxChars: 80 },
  NLSEduSettingsBackfillCompleted: { text: 'Backfill complete. {count} items processed.', description: 'Completion status for backfill', surface: 'shell', maxChars: 70 },
  NLSEduSettingsBackfillCancelled: { text: 'Backfill cancelled. {count} items processed so far.', description: 'Cancelled status for backfill', surface: 'shell', maxChars: 80 },
  NLSEducationTabSettings: { text: 'Settings', description: 'Education hub Settings tab.', surface: 'shell', maxChars: 20 },
  NLSEducationTabsAria: { text: 'Education sections', description: 'Aria label for education tabs.', surface: 'shell', maxChars: 30 },
  NLSEducationTeachingEmptyTitle: { text: 'Teaching is being set up', description: 'Empty state title for teaching hub.', surface: 'shell', maxChars: 60 },
  NLSEducationTeachingEmptyBody: { text: 'Classes, timetable and attendance arrive in the next updates.', description: 'Empty state body for teaching hub.', surface: 'shell', maxChars: 120 },
  NLSEducationTrainingEmptyTitle: { text: 'Training is being set up', description: 'Empty state title for training hub.', surface: 'shell', maxChars: 60 },
  NLSEducationTrainingEmptyBody: { text: 'Programmes, timetable and attendance arrive in the next updates.', description: 'Empty state body for training hub.', surface: 'shell', maxChars: 120 },
  NLSEducationLearnersTitle: { text: 'Learners', description: 'Learners tab header title.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersEmptyTitle: { text: 'No learners yet', description: 'Learners tab empty state title.', surface: 'shell', maxChars: 40 },
  NLSEducationLearnersEmptyBody: { text: 'Add learners or import a roster from CSV to issue join codes.', description: 'Learners tab empty state body.', surface: 'shell', maxChars: 120 },
  NLSEducationLearnersNoAccessTitle: { text: 'Learners roster unavailable', description: 'Title when user role cannot access learners.', surface: 'shell', maxChars: 40 },
  NLSEducationLearnersNoAccessBody: { text: 'Only workspace editors and managers can view learners.', description: 'Body when user role cannot access learners.', surface: 'shell', maxChars: 100 },
  NLSEducationLearnersAddOne: { text: 'Add learner', description: 'Button label to add a single learner.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersImportCsv: { text: 'Import CSV', description: 'Button label to import learners from CSV.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersColName: { text: 'Name', description: 'Learners table column header for name.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersColAge: { text: 'Category', description: 'Learners table column header for Minor/Adult.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersColAdmission: { text: 'Admission no.', description: 'Learners table column header for admission number.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersColRoll: { text: 'Roll no.', description: 'Learners table column header for roll number.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersColClass: { text: 'Class', description: 'Learners table column header for class.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersColStatus: { text: 'Status', description: 'Learners table column header for status.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersColJoinCode: { text: 'Join code', description: 'Learners table column header for join code status.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersColExpiry: { text: 'Expiry', description: 'Learners codes table column header for code expiry date.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersColLinked: { text: 'Linked', description: 'Learners table column header for linked account/guardian status.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersColActions: { text: 'Actions', description: 'Learners table column header for actions.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersMinor: { text: 'Minor', description: 'Label for minor learner.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersAdult: { text: 'Adult', description: 'Label for adult trainee/learner.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersFrozenPill: { text: 'Frozen', description: 'Status pill for frozen learner.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersSelfLinked: { text: 'Self-linked', description: 'Status text when learner self-linked account.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersGuardiansCount: { text: '{count} guardian(s)', description: 'Guardian count for learner.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersNotLinked: { text: 'Not linked', description: 'Status text when learner has no linked accounts.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersViewContact: { text: 'Guardian contact', description: 'Action button to view guardian contact info.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersContactTitle: { text: 'Guardian contact details', description: 'Dialog title for guardian contact.', surface: 'shell', maxChars: 40 },
  NLSEducationLearnersContactEmail: { text: 'Guardian email', description: 'Label for guardian email.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersContactPhone: { text: 'Guardian phone', description: 'Label for guardian phone.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersContactNone: { text: 'No guardian contact on file', description: 'Message when no guardian contact is found.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnersAddDialogTitle: { text: 'Add learner', description: 'Dialog title for adding learner.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersNameRequired: { text: 'Name is required', description: 'Validation error when learner name is missing.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersMinorLabel: { text: 'Minor (under 18)', description: 'Switch label for minor learner.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersMinorHint: { text: 'Requires institution attestation of parental consent', description: 'Hint for minor learner switch.', surface: 'shell', maxChars: 80 },
  NLSEducationLearnersDobLabel: { text: 'Date of birth', description: 'Label for date of birth field.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersAdmissionNoLabel: { text: 'Admission number', description: 'Label for admission number field.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersRollNoLabel: { text: 'Roll number', description: 'Label for roll number field.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersClassLabel: { text: 'Class / Programme', description: 'Label for class selection.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersClassPlaceholder: { text: 'Select a class or programme', description: 'Placeholder for class select.', surface: 'shell', maxChars: 40 },
  NLSEducationLearnersGuardianEmailLabel: { text: 'Guardian email', description: 'Label for guardian email input.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersGuardianPhoneLabel: { text: 'Guardian phone', description: 'Label for guardian phone input.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersAttestationLabel: { text: 'Institution confirms parental consent on file', description: 'Attestation switch label for minor learner.', surface: 'shell', maxChars: 70 },
  NLSEducationLearnersAttestationRequired: { text: 'Consent attestation is required for minors', description: 'Error message when attestation is omitted for minor.', surface: 'shell', maxChars: 60 },
  NLSEducationLearnersAttestationDialogTitle: { text: 'Parental consent attestation', description: 'Title of parental consent confirmation dialog.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnersAttestationDialogBody: { text: 'The institution confirms that parental consent has been collected and verified for all minor learners in this import.', description: 'Body text of parental consent confirmation dialog.', surface: 'shell', maxChars: 150 },
  NLSEducationLearnersAttestationDialogConfirm: { text: 'I confirm and proceed', description: 'Confirm button label for consent attestation dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersCodesPanelTitle: { text: 'Learner Join Codes', description: 'Header title for codes once panel.', surface: 'shell', maxChars: 35 },
  NLSEducationLearnersCodesPanelNotice: { text: "These PINs won't be shown again. Copy or distribute them now.", description: 'Notice warning that PINs will not be shown again.', surface: 'shell', maxChars: 90 },
  NLSEducationLearnersCodesCopy: { text: 'Copy code & PIN', description: 'Action button to copy join code and PIN.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersCodesCopied: { text: 'Copied!', description: 'Tooltip or feedback indicating code copied.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersCodesDone: { text: 'Done', description: 'Button label to dismiss codes panel.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersExpiresAt: { text: 'Expires {date}', description: 'Text indicating code expiry date.', surface: 'shell', maxChars: 35 },
  NLSEducationLearnersBandFull: { text: 'Learner band limit reached ({used}/{band}). Upgrade or contact support to raise the limit.', description: 'Error message when learner band limit reached.', surface: 'shell', maxChars: 100 },
  NLSEducationLearnersModuleOff: { text: 'Teaching or training module must be enabled.', description: 'Error message when education module is turned off.', surface: 'shell', maxChars: 60 },
  NLSEducationLearnersImportDialogTitle: { text: 'Import learners from CSV', description: 'Dialog title for bulk CSV import of learners.', surface: 'shell', maxChars: 40 },
  NLSEducationLearnersImportProgress: { text: 'Importing {committed} of {total} learners...', description: 'Progress label during bulk import.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnersInvalidEmail: { text: 'Invalid email address', description: 'Validation error for invalid email.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersInvalidPhone: { text: 'Phone must have + and 8-15 digits', description: 'Validation error for invalid phone number.', surface: 'shell', maxChars: 45 },
  NLSEducationLearnersClassNotFound: { text: 'Class not found', description: 'Error when class is not found in candidate classes.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersClassAmbiguous: { text: 'Multiple classes match this title', description: 'Error when multiple classes match title.', surface: 'shell', maxChars: 45 },
  NLSEducationLearnersSectionBasic: { text: 'Basic details', description: 'Section title in add learner dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersSectionAcademic: { text: 'Academic details', description: 'Section title in add learner dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersSectionGuardian: { text: 'Guardian contact', description: 'Section title in add learner dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersSectionAttestation: { text: 'Consent attestation', description: 'Section title in add learner dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersCloseContact: { text: 'Close', description: 'Close button label in contact modal.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersPartialCommitError: { text: '{created} of {total} learners created. Failed to create remaining learners.', description: 'Error message showing partial progress on bulk import failure.', surface: 'shell', maxChars: 80 },
  NLSEducationLearnersLoadError: { text: 'Failed to load learners', description: 'Error message when loading learners fails.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersGenericError: { text: 'An unexpected error occurred', description: 'Generic error message when an operation fails.', surface: 'shell', maxChars: 40 },
  NLSEducationLearnersStatusActive: { text: 'Active', description: 'Status label for active learner.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersStatusLeft: { text: 'Left', description: 'Status label for learner who has left.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersStatusAlumni: { text: 'Alumni', description: 'Status label for alumni learner.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersCodeStatusPending: { text: 'Pending', description: 'Join code status label for pending code.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersCodeStatusClaimed: { text: 'Claimed', description: 'Join code status label for claimed code.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersCodeStatusExpired: { text: 'Expired', description: 'Join code status label for expired code.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersCodeStatusRevoked: { text: 'Revoked', description: 'Join code status label for revoked code.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersCodeStatusLocked: { text: 'Locked', description: 'Join code status label for locked code.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersInvalidMinor: { text: 'Must be yes, no, or blank', description: 'Validation error when minor column value is unrecognized.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersCodesBtn: { text: 'Codes', description: 'Button label to open codes dialog for a learner.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersCodesDialogTitle: { text: 'Join codes for {name}', description: 'Dialog title for learner join codes.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnersCodesDialogEmpty: { text: 'No join codes found for this learner.', description: 'Empty message in learner join codes dialog.', surface: 'shell', maxChars: 60 },
  NLSEducationLearnersReissueCode: { text: 'Re-issue', description: 'Action button to re-issue a join code.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersRevokeCode: { text: 'Revoke', description: 'Action button to revoke a join code.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersRevokeConfirmTitle: { text: 'Revoke join code?', description: 'Confirmation title for revoking a join code.', surface: 'shell', maxChars: 35 },
  NLSEducationLearnersRevokeConfirmBody: { text: 'This join code will be permanently invalidated. The learner will not be able to use it.', description: 'Confirmation body for revoking a join code.', surface: 'shell', maxChars: 120 },
  NLSEducationLearnersRevokeConfirmAction: { text: 'Revoke', description: 'Confirm action button label to revoke join code.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersExtraGuardianCode: { text: 'Extra guardian code', description: 'Button to generate an additional guardian join code.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersFrozenNote: { text: 'Learner is frozen. Join code actions are disabled.', description: 'Note indicating code actions are disabled for frozen learner.', surface: 'shell', maxChars: 70 },
  NLSEducationLearnersLearnerFrozenError: { text: 'Learner is frozen. Changes to codes are disabled.', description: 'Error message when an action is attempted on a frozen learner.', surface: 'shell', maxChars: 70 },
  NLSEducationLearnersPrintSlips: { text: 'Print slips', description: 'Button label to open print slips sheet.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersSlipStep1: { text: '1. Open the app or scan the QR code', description: 'Instruction step 1 on learner slip.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnersSlipStep2: { text: '2. Sign in with your own Google account', description: 'Instruction step 2 on learner slip.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnersSlipStep3: { text: '3. Enter the join code and PIN', description: 'Instruction step 3 on learner slip.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnersSlipKeepPrivate: { text: 'Keep this private', description: 'Security notice on learner slip.', surface: 'shell', maxChars: 30 },
  NLSEducationLearnersSlipCode: { text: 'Code', description: 'Label for code on learner slip.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersSlipPin: { text: 'PIN', description: 'Label for PIN on learner slip.', surface: 'shell', maxChars: 15 },
  NLSEducationTimetableNoAccessTitle: { text: 'Timetable unavailable', description: 'Title when user role cannot access timetable.', surface: 'shell', maxChars: 40 },
  NLSEducationTimetableNoAccessBody: { text: 'Only workspace editors and managers can view the timetable.', description: 'Body when user role cannot access timetable.', surface: 'shell', maxChars: 100 },
  NLSEducationTimetableEmptyTitle: { text: 'No academic years or programmes yet', description: 'Empty state title when no roots exist.', surface: 'shell', maxChars: 50 },
  NLSEducationTimetableEmptyBody: { text: 'Create an academic year or programme in Classes/Programmes to begin timetabling.', description: 'Empty state body when no roots exist.', surface: 'shell', maxChars: 120 },
  NLSEducationTimetableNoSlotsTitle: { text: 'No timetable slots', description: 'Empty state title when selection has no slots.', surface: 'shell', maxChars: 30 },
  NLSEducationTimetableNoSlotsBody: { text: 'No scheduled slots found for this selection.', description: 'Empty state body when selection has no slots.', surface: 'shell', maxChars: 60 },
  NLSEducationTimetableFilterClass: { text: 'Class', description: 'Filter button label for class view.', surface: 'shell', maxChars: 20 },
  NLSEducationTimetableFilterTeacher: { text: 'Teacher', description: 'Filter button label for teacher view.', surface: 'shell', maxChars: 20 },
  NLSEducationTimetableFilterRoom: { text: 'Room', description: 'Filter button label for room view.', surface: 'shell', maxChars: 20 },
  NLSEducationTimetableSelectClass: { text: 'Select class', description: 'Aria label for class select.', surface: 'shell', maxChars: 25 },
  NLSEducationTimetableSelectTeacher: { text: 'Select teacher', description: 'Aria label for teacher select.', surface: 'shell', maxChars: 25 },
  NLSEducationTimetableSelectRoom: { text: 'Select room', description: 'Aria label for room select.', surface: 'shell', maxChars: 25 },
  NLSEducationTimetableSelectYear: { text: 'Academic year / programme', description: 'Aria label for root year/programme select.', surface: 'shell', maxChars: 35 },
  NLSEducationTimetableWeekPicker: { text: 'Week', description: 'Label for week picker.', surface: 'shell', maxChars: 20 },
  NLSEducationTimetableAddSlot: { text: 'Add slot', description: 'Button label to add timetable slot.', surface: 'shell', maxChars: 20 },
  NLSEducationTimetableCancelled: { text: 'Cancelled', description: 'Badge for cancelled session occurrence.', surface: 'shell', maxChars: 20 },
  NLSEducationTimetableHoliday: { text: 'Holiday', description: 'Badge for holiday on timetable grid.', surface: 'shell', maxChars: 20 },
  NLSEducationTimetableColMon: { text: 'Mon', description: 'Monday day column header.', surface: 'shell', maxChars: 10 },
  NLSEducationTimetableColTue: { text: 'Tue', description: 'Tuesday day column header.', surface: 'shell', maxChars: 10 },
  NLSEducationTimetableColWed: { text: 'Wed', description: 'Wednesday day column header.', surface: 'shell', maxChars: 10 },
  NLSEducationTimetableColThu: { text: 'Thu', description: 'Thursday day column header.', surface: 'shell', maxChars: 10 },
  NLSEducationTimetableColFri: { text: 'Fri', description: 'Friday day column header.', surface: 'shell', maxChars: 10 },
  NLSEducationTimetableColSat: { text: 'Sat', description: 'Saturday day column header.', surface: 'shell', maxChars: 10 },
  NLSEducationTimetableColSun: { text: 'Sun', description: 'Sunday day column header.', surface: 'shell', maxChars: 10 },
  NLSEducationSlotEditorTitleNew: { text: 'New timetable slot', description: 'Dialog title for creating new timetable slot.', surface: 'shell', maxChars: 35 },
  NLSEducationSlotEditorTitleEdit: { text: 'Edit timetable slot', description: 'Dialog title for editing timetable slot.', surface: 'shell', maxChars: 35 },
  NLSEducationSlotEditorTitleView: { text: 'View slot (read-only)', description: 'Dialog title for read-only timetable slot.', surface: 'shell', maxChars: 35 },
  NLSEducationSlotEditorNodeLabel: { text: 'Class or subject', description: 'Field label for class or subject select.', surface: 'shell', maxChars: 30 },
  NLSEducationSlotEditorTeachersLabel: { text: 'Teachers', description: 'Field label for teachers multi-select.', surface: 'shell', maxChars: 25 },
  NLSEducationSlotEditorWeekdaysLabel: { text: 'Days of week', description: 'Field label for weekdays selection.', surface: 'shell', maxChars: 25 },
  NLSEducationSlotEditorStartTimeLabel: { text: 'Start time', description: 'Field label for start time picker.', surface: 'shell', maxChars: 25 },
  NLSEducationSlotEditorDurationLabel: { text: 'Duration (minutes)', description: 'Field label for duration input.', surface: 'shell', maxChars: 30 },
  NLSEducationSlotEditorFromDateLabel: { text: 'From date', description: 'Field label for slot start date.', surface: 'shell', maxChars: 25 },
  NLSEducationSlotEditorToDateLabel: { text: 'To date', description: 'Field label for slot end date.', surface: 'shell', maxChars: 25 },
  NLSEducationSlotEditorRoomLabel: { text: 'Room', description: 'Field label for room selection.', surface: 'shell', maxChars: 20 },
  NLSEducationSlotEditorNoRoom: { text: 'No room assigned', description: 'Option label when no room is assigned.', surface: 'shell', maxChars: 25 },
  NLSEducationSlotEditorOnlineUrlLabel: { text: 'Online link (https://)', description: 'Field label for online meeting link.', surface: 'shell', maxChars: 30 },
  NLSEducationSlotEditorOnlineUrlPlaceholder: { text: 'https://meet.google.com/...', description: 'Placeholder for online meeting link.', surface: 'shell', maxChars: 40 },
  NLSEducationSlotEditorSave: { text: 'Save slot', description: 'Save button in slot editor.', surface: 'shell', maxChars: 20 },
  NLSEducationSlotEditorSaving: { text: 'Saving…', description: 'Saving state in slot editor.', surface: 'shell', maxChars: 20 },
  NLSEducationSlotEditorEnd: { text: 'End slot', description: 'Button to end slot on selected date.', surface: 'shell', maxChars: 20 },
  NLSEducationSlotEditorEndDate: { text: 'End date', description: 'Label for end date picker.', surface: 'shell', maxChars: 20 },
  NLSEducationSlotEditorDelete: { text: 'Delete slot', description: 'Button to delete timetable slot.', surface: 'shell', maxChars: 20 },
  NLSEducationSlotEditorDeleteConfirm: { text: 'Confirm delete', description: 'Confirmation button to delete timetable slot.', surface: 'shell', maxChars: 20 },
  NLSEducationSlotEditorCancel: { text: 'Cancel', description: 'Cancel button in slot editor.', surface: 'shell', maxChars: 15 },
  NLSEducationSlotEditorClose: { text: 'Close', description: 'Close button in slot editor.', surface: 'shell', maxChars: 15 },
  NLSEducationSlotEditorClashWarning: { text: 'Clash detected: clashes with {slotTitle} ({reason})', description: 'Clash warning notification banner.', surface: 'shell', maxChars: 80 },
  NLSEducationSlotEditorClashReasonTeacher: { text: 'teacher conflict', description: 'Reason for clash: shared teacher.', surface: 'shell', maxChars: 25 },
  NLSEducationSlotEditorClashReasonRoom: { text: 'room conflict', description: 'Reason for clash: shared room.', surface: 'shell', maxChars: 25 },
  NLSEducationSlotEditorErrNoDays: { text: 'Select at least one day.', description: 'Validation error when no weekdays selected.', surface: 'shell', maxChars: 35 },
  NLSEducationSlotEditorErrInvalidTime: { text: 'Invalid start time.', description: 'Validation error for invalid start time.', surface: 'shell', maxChars: 30 },
  NLSEducationSlotEditorErrInvalidDuration: { text: 'Duration must be between 5 and 480 minutes.', description: 'Validation error for invalid duration.', surface: 'shell', maxChars: 50 },
  NLSEducationSlotEditorErrInvalidDateRange: { text: 'Start date must be before or equal to end date.', description: 'Validation error for dates.', surface: 'shell', maxChars: 50 },
  NLSEducationSlotEditorErrInvalidUrl: { text: 'Online link must start with https://', description: 'Validation error for non-https url.', surface: 'shell', maxChars: 45 },
  NLSEducationSlotEditorErrTooManyTeachers: { text: 'At most 5 teachers can be assigned to a slot.', description: 'Validation error for teacher count.', surface: 'shell', maxChars: 50 },
  NLSEducationSettingsAbsenceAlertsLabel: { text: 'Alert families about absences', description: 'Label for absence alerts toggle.', surface: 'shell', maxChars: 40 },
  NLSEducationSettingsAbsenceAlertsHelp: { text: 'Guardians get an alert when their child is marked absent or late.', description: 'Help text for absence alerts toggle.', surface: 'shell', maxChars: 80 },
  NLSEducationSettingsAbsenceAlertsError: { text: 'Failed to update alert settings.', description: 'Error message when saving absence alerts fails.', surface: 'shell', maxChars: 40 },
  NLSEducationSettingsRoomsTitle: { text: 'Rooms', description: 'Settings tab rooms section header.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsRoomsDescription: { text: 'Manage classrooms, labs and studios for timetabling.', description: 'Settings tab rooms section subtitle.', surface: 'shell', maxChars: 70 },
  NLSEducationSettingsRoomsReadOnly: { text: 'Rooms (read-only)', description: 'Header for non-admin rooms view.', surface: 'shell', maxChars: 30 },
  NLSEducationSettingsRoomsAdd: { text: 'Add room', description: 'Button to add new room.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsRoomsSave: { text: 'Save rooms', description: 'Button to save rooms.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsRoomsSaving: { text: 'Saving…', description: 'Saving state for rooms.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsRoomsNamePlaceholder: { text: 'Room name (e.g. Room 101, Science Lab)', description: 'Placeholder for room name input.', surface: 'shell', maxChars: 50 },
  NLSEducationSettingsRoomsActive: { text: 'Active', description: 'Status label for active room.', surface: 'shell', maxChars: 15 },
  NLSEducationSettingsRoomsInactive: { text: 'Inactive', description: 'Status label for inactive room.', surface: 'shell', maxChars: 15 },
  NLSEducationSettingsRoomsDeactivate: { text: 'Deactivate', description: 'Action button to deactivate room.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsRoomsReactivate: { text: 'Reactivate', description: 'Action button to reactivate room.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsRoomsEmpty: { text: 'No rooms added yet.', description: 'Empty state message for rooms.', surface: 'shell', maxChars: 30 },
  NLSEducationHolidaysMenu: { text: 'Holidays…', description: 'Action menu item to open year holidays dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationHolidaysTitle: { text: 'Year holidays', description: 'Dialog title for year holidays.', surface: 'shell', maxChars: 25 },
  NLSEducationHolidaysDescription: { text: 'Holidays exclude all recurring timetable slots and are displayed on the grid.', description: 'Description for year holidays dialog.', surface: 'shell', maxChars: 85 },
  NLSEducationHolidaysAdd: { text: 'Add holiday', description: 'Button to add holiday entry.', surface: 'shell', maxChars: 20 },
  NLSEducationHolidaysSave: { text: 'Save holidays', description: 'Button to save holidays.', surface: 'shell', maxChars: 20 },
  NLSEducationHolidaysSaving: { text: 'Saving…', description: 'Saving state for holidays.', surface: 'shell', maxChars: 20 },
  NLSEducationHolidaysClose: { text: 'Close', description: 'Close button for holidays dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationHolidaysFromLabel: { text: 'From date', description: 'Label for holiday start date.', surface: 'shell', maxChars: 20 },
  NLSEducationHolidaysToLabel: { text: 'To date', description: 'Label for holiday end date.', surface: 'shell', maxChars: 20 },
  NLSEducationHolidaysLabelLabel: { text: 'Holiday label', description: 'Label for holiday name input.', surface: 'shell', maxChars: 20 },
  NLSEducationHolidaysLabelPlaceholder: { text: 'e.g. Diwali Break, Winter Recess', description: 'Placeholder for holiday label.', surface: 'shell', maxChars: 40 },
  NLSEducationHolidaysRemove: { text: 'Remove', description: 'Button to remove holiday entry.', surface: 'shell', maxChars: 15 },
  NLSEducationHolidaysEmpty: { text: 'No holidays defined for this year.', description: 'Notice when no holidays are configured.', surface: 'shell', maxChars: 40 },
  NLSEducationHolidaysErrInvalidDate: { text: 'Invalid date format (YYYY-MM-DD).', description: 'Validation error for invalid holiday date.', surface: 'shell', maxChars: 45 },
  NLSEducationHolidaysErrDateOrder: { text: 'Start date must be before or equal to end date.', description: 'Validation error when holiday from > to.', surface: 'shell', maxChars: 50 },
  NLSEducationSessionDialogTitle: { text: 'Class session', description: 'Dialog title for session details.', surface: 'shell', maxChars: 30 },
  NLSEducationSessionTime: { text: 'Time', description: 'Label for session time.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionClassSubject: { text: 'Class / Subject', description: 'Label for class and subject.', surface: 'shell', maxChars: 25 },
  NLSEducationSessionTeachers: { text: 'Teachers', description: 'Label for assigned teachers.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionRoom: { text: 'Room', description: 'Label for room.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionStatus: { text: 'Status', description: 'Label for session status.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionStatusPlanned: { text: 'Planned', description: 'Planned status label.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionStatusCancelled: { text: 'Cancelled', description: 'Cancelled status label.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionSubstitute: { text: 'Substitute', description: 'Label for substitute teacher section.', surface: 'shell', maxChars: 25 },
  NLSEducationSessionNoSubstitute: { text: 'No substitute assigned', description: 'Text when no substitute is set.', surface: 'shell', maxChars: 30 },
  NLSEducationSessionSubstituteSelectPlaceholder: { text: 'Select substitute teacher…', description: 'Placeholder for substitute select.', surface: 'shell', maxChars: 35 },
  NLSEducationSessionSaveSubstitute: { text: 'Save substitute', description: 'Button to set substitute.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionClearSubstitute: { text: 'Clear substitute', description: 'Button to clear substitute.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionTopics: { text: 'Topics / chapters', description: 'Label for topics section.', surface: 'shell', maxChars: 30 },
  NLSEducationSessionTopicsPlaceholder: { text: 'Select topic items (≤ 20)…', description: 'Placeholder for topics select.', surface: 'shell', maxChars: 35 },
  NLSEducationSessionNote: { text: 'Session note', description: 'Label for session note input.', surface: 'shell', maxChars: 25 },
  NLSEducationSessionNotePlaceholder: { text: 'Add notes for this session (≤ 500 chars)…', description: 'Placeholder for note input.', surface: 'shell', maxChars: 50 },
  NLSEducationSessionSaveTopics: { text: 'Save topics & note', description: 'Button to save topics and note.', surface: 'shell', maxChars: 25 },
  NLSEducationSessionCancelSession: { text: 'Cancel session', description: 'Button to cancel session.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionRestoreSession: { text: 'Restore session', description: 'Button to restore session.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionEditSlot: { text: 'Edit slot…', description: 'Button to edit slot definition.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionJoin: { text: 'Join', description: 'Button/link to join online session.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionClose: { text: 'Close', description: 'Close button in session dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationSessionUpdating: { text: 'Updating…', description: 'Updating spinner in session dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationSessionTakeAttendance: { text: 'Take attendance', description: 'Button to open the attendance register from the session dialog.', surface: 'shell', maxChars: 25 },

  // ED4.b: EduRegisterDialog
  NLSEducationRegisterTitle: { text: 'Attendance register', description: 'Dialog title for the attendance register.', surface: 'shell', maxChars: 30 },
  NLSEducationRegisterSession: { text: 'Session', description: 'Label for session info in register dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationRegisterDate: { text: 'Date', description: 'Label for date in register dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationRegisterLoading: { text: 'Loading roll…', description: 'Loading state in register dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationRegisterEmpty: { text: 'No learners on the roll for this session.', description: 'Empty state when roll has no learners.', surface: 'shell', maxChars: 60 },
  NLSEducationRegisterColLearner: { text: 'Learner', description: 'Column header for learner name in register.', surface: 'shell', maxChars: 20 },
  NLSEducationRegisterColRoll: { text: 'Roll no.', description: 'Column header for roll number in register.', surface: 'shell', maxChars: 15 },
  NLSEducationRegisterColStatus: { text: 'Status', description: 'Column header for attendance status in register.', surface: 'shell', maxChars: 15 },
  NLSEducationRegisterReasonLabel: { text: 'Reason for past edit', description: 'Label for reason textarea in register (past date).', surface: 'shell', maxChars: 30 },
  NLSEducationRegisterReasonPlaceholder: { text: 'Required for edits to past sessions (3–500 chars)…', description: 'Placeholder for reason textarea.', surface: 'shell', maxChars: 60 },
  NLSEducationRegisterSave: { text: 'Save', description: 'Save button in register dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationRegisterSaving: { text: 'Saving…', description: 'Saving state in register dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationRegisterClose: { text: 'Close', description: 'Close button in register dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationRegisterSummaryPresent: { text: '{count} present', description: 'Summary count of present learners.', surface: 'shell', maxChars: 25 },
  NLSEducationRegisterSummaryAbsent: { text: '{count} absent', description: 'Summary count of absent learners.', surface: 'shell', maxChars: 25 },
  NLSEducationRegisterSummaryLate: { text: '{count} late', description: 'Summary count of late learners.', surface: 'shell', maxChars: 25 },
  NLSEducationRegisterFrozenNote: { text: 'Learner is frozen — status fixed.', description: 'Note for frozen learner in register.', surface: 'shell', maxChars: 40 },
  NLSEducationRegisterLearnerOptions: { text: '{name} options', description: 'Action menu aria-label for learner options in register.', surface: 'shell', maxChars: 40 },

  // ED4.b: attendance status labels (EQ54)
  NLSEducationAttendanceStatusPresent: { text: 'Present', description: 'Attendance status label for Present.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceStatusAbsent: { text: 'Absent', description: 'Attendance status label for Absent.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceStatusLate: { text: 'Late', description: 'Attendance status label for Late.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceStatusOnLeave: { text: 'On leave', description: 'Attendance status label for On leave.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceStatusExcused: { text: 'Excused', description: 'Attendance status label for Excused.', surface: 'shell', maxChars: 15 },

  // ED4.b: AttendanceTab
  NLSEducationAttendanceNoAccess: { text: 'Only workspace editors and managers can view attendance.', description: 'No-access body for attendance tab.', surface: 'shell', maxChars: 80 },
  NLSEducationAttendanceNoAccessTitle: { text: 'Attendance unavailable', description: 'No-access title for attendance tab.', surface: 'shell', maxChars: 40 },
  NLSEducationAttendanceTodayTitle: { text: "Today's sessions", description: "Section title for today's sessions in attendance tab.", surface: 'shell', maxChars: 30 },
  NLSEducationAttendanceTodayEmpty: { text: 'No sessions scheduled for today.', description: 'Empty state for today sessions.', surface: 'shell', maxChars: 50 },
  NLSEducationAttendanceTakenLabel: { text: 'Taken', description: 'Badge when roll has been taken for a session.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceNotTakenLabel: { text: 'Not taken', description: 'Badge when roll has not been taken for a session.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceSummaryTitle: { text: 'Class summary', description: 'Section title for class attendance summary.', surface: 'shell', maxChars: 25 },
  NLSEducationAttendanceSummaryRange: { text: 'Date range', description: 'Label for date range in attendance summary.', surface: 'shell', maxChars: 20 },
  NLSEducationAttendanceSummaryFrom: { text: 'From', description: 'From date label for attendance range.', surface: 'shell', maxChars: 10 },
  NLSEducationAttendanceSummaryTo: { text: 'To', description: 'To date label for attendance range.', surface: 'shell', maxChars: 10 },
  NLSEducationAttendanceSummaryClassLabel: { text: 'Class', description: 'Class label in attendance summary.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceSummaryClassPlaceholder: { text: 'Select a class…', description: 'Placeholder for class select in attendance summary.', surface: 'shell', maxChars: 30 },
  NLSEducationAttendanceSummaryLoad: { text: 'Load', description: 'Button to load attendance summary.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceSummaryLoading: { text: 'Loading…', description: 'Loading state in attendance summary.', surface: 'shell', maxChars: 20 },
  NLSEducationAttendanceSummaryEmpty: { text: 'No attendance records in this range.', description: 'Empty state for attendance summary.', surface: 'shell', maxChars: 50 },
  NLSEducationAttendanceSummaryColLearner: { text: 'Learner', description: 'Column header for learner in attendance summary.', surface: 'shell', maxChars: 20 },
  NLSEducationAttendanceSummaryColPercent: { text: '% Attendance', description: 'Column header for attendance % in summary.', surface: 'shell', maxChars: 20 },
  NLSEducationAttendanceSummaryColPresent: { text: 'Present', description: 'Column header for present count in summary.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceSummaryColAbsent: { text: 'Absent', description: 'Column header for absent count in summary.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceSummaryColLate: { text: 'Late', description: 'Column header for late count in summary.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceSummaryNoData: { text: '—', description: 'Dash when attendance % cannot be calculated.', surface: 'shell', maxChars: 5 },
  NLSEducationAttendanceUnknownLearner: { text: 'Unknown learner', description: 'Fallback name for unknown learner in attendance summary.', surface: 'shell', maxChars: 30 },

  // ED4.b: learner attendance action in LearnersTab
  NLSEducationLearnersAttendanceBtn: { text: 'Attendance…', description: 'Action button to view learner attendance report.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersAttendanceDialogTitle: { text: 'Attendance for {name}', description: 'Dialog title for learner attendance report.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnersAttendancePercent: { text: 'Attendance', description: 'Label for attendance percentage in learner dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersAttendancePercentValue: { text: '{percent}%', description: 'Attendance percent value display.', surface: 'shell', maxChars: 10 },
  NLSEducationLearnersAttendancePercentNA: { text: 'N/A', description: 'Displayed when attendance % cannot be computed (no denominator).', surface: 'shell', maxChars: 10 },
  NLSEducationLearnersAttendanceDateFrom: { text: 'From', description: 'From date picker label in learner attendance dialog.', surface: 'shell', maxChars: 10 },
  NLSEducationLearnersAttendanceDateTo: { text: 'To', description: 'To date picker label in learner attendance dialog.', surface: 'shell', maxChars: 10 },
  NLSEducationLearnersAttendanceLoad: { text: 'Load', description: 'Load button in learner attendance dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnersAttendanceLoading: { text: 'Loading…', description: 'Loading state in learner attendance dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnersAttendanceEmpty: { text: 'No attendance records in this period.', description: 'Empty state in learner attendance dialog.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnersAttendanceError: { text: 'Failed to load attendance data.', description: 'Error state in learner attendance dialog when the load call fails.', surface: 'shell', maxChars: 60 },
  NLSEducationLearnersAttendancePresentCount: { text: '{count} present', description: 'Present count in learner attendance dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersAttendanceAbsentCount: { text: '{count} absent', description: 'Absent count in learner attendance dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnersAttendanceLateCount: { text: '{count} late', description: 'Late count in learner attendance dialog.', surface: 'shell', maxChars: 25 },
  // ED4.b fix1: attendance register menu items
  NLSEducationRegisterMenuSetOnLeave: { text: 'Set On leave', description: 'Menu item to set attendance status to On leave.', surface: 'shell', maxChars: 20 },
  NLSEducationRegisterMenuSetExcused: { text: 'Set Excused', description: 'Menu item to set attendance status to Excused.', surface: 'shell', maxChars: 20 },
  NLSEducationRegisterMenuResetPresent: { text: 'Reset to Present', description: 'Menu item to reset a fixed status back to Present.', surface: 'shell', maxChars: 25 },
  // ED4.b fix1: attendance tab Mine/All filter
  NLSEducationAttendanceMineLabel: { text: 'Mine', description: 'Filter chip label for showing only my sessions in attendance tab.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceAllLabel: { text: 'All', description: 'Filter chip label for showing all sessions in attendance tab.', surface: 'shell', maxChars: 15 },
  NLSEducationAttendanceSessionFilterAriaLabel: { text: 'Session filter', description: 'Aria-label for session filter chip group in attendance tab.', surface: 'shell', maxChars: 25 },
  // ED5.b2: Leave review in AttendanceTab and Register lock
  NLSEducationRegisterOnLeaveNote: { text: 'On approved leave', description: 'Note shown next to learner on approved leave in attendance register.', surface: 'shell', maxChars: 30 },
  NLSEducationLeaveSectionTitle: { text: 'Leave requests', description: 'Title of the leave requests section in Attendance tab.', surface: 'shell', maxChars: 25 },
  NLSEducationLeaveSectionClassLabel: { text: 'Class', description: 'Label for class picker in leave requests section.', surface: 'shell', maxChars: 15 },
  NLSEducationLeaveSectionStatusLabel: { text: 'Status', description: 'Label for status filter in leave requests section.', surface: 'shell', maxChars: 15 },
  NLSEducationLeaveSectionFilterPending: { text: 'Pending', description: 'Filter option to show pending leave requests.', surface: 'shell', maxChars: 15 },
  NLSEducationLeaveSectionFilterAll: { text: 'All', description: 'Filter option to show all leave requests.', surface: 'shell', maxChars: 15 },
  NLSEducationLeaveSectionEmpty: { text: 'No leave requests found for this class.', description: 'Empty state message when no leave requests exist for class.', surface: 'shell', maxChars: 50 },
  NLSEducationLeaveActionApprove: { text: 'Approve', description: 'Button label to approve a pending leave request.', surface: 'shell', maxChars: 15 },
  NLSEducationLeaveActionDecline: { text: 'Decline', description: 'Button label to decline a pending leave request.', surface: 'shell', maxChars: 15 },
  NLSEducationLeaveActionRevoke: { text: 'Revoke', description: 'Button label to revoke an approved leave request.', surface: 'shell', maxChars: 15 },
  NLSEducationLeaveLoading: { text: 'Loading leave requests…', description: 'Loading indicator for leave requests.', surface: 'shell', maxChars: 30 },
  NLSEducationLeaveDeclineDialogTitle: { text: 'Decline leave request', description: 'Title of decline leave dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationLeaveDeclineNoteLabel: { text: 'Reason (optional)', description: 'Label for decline reason note textarea.', surface: 'shell', maxChars: 25 },
  NLSEducationLeaveDeclineNotePlaceholder: { text: 'Optional reason for declining (max 500 characters)', description: 'Placeholder for decline reason note.', surface: 'shell', maxChars: 55 },
  NLSEducationLeaveDeclineConfirm: { text: 'Decline', description: 'Confirm button to decline leave request.', surface: 'shell', maxChars: 15 },
  NLSEducationLeaveDeclineCancel: { text: 'Cancel', description: 'Cancel button in decline leave dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationLeaveRevokeDialogTitle: { text: 'Revoke approved leave?', description: 'Confirmation title for revoking approved leave.', surface: 'shell', maxChars: 35 },
  NLSEducationLeaveRevokeDialogBody: { text: 'Are you sure you want to revoke this approved leave request?', description: 'Confirmation body for revoking approved leave.', surface: 'shell', maxChars: 80 },
  NLSEducationLeaveRevokeConfirm: { text: 'Revoke leave', description: 'Confirm button label to revoke approved leave.', surface: 'shell', maxChars: 20 },
  // ED6.b1: Work tab and homework creation
  NLSEducationWorkNoAccessTitle: { text: 'Work unavailable', description: 'No-access title for work tab.', surface: 'shell', maxChars: 30 },
  NLSEducationWorkNoAccess: { text: 'Only workspace editors and managers can view work and homework.', description: 'No-access body for work tab.', surface: 'shell', maxChars: 80 },
  NLSEducationWorkNewHomework: { text: 'New homework', description: 'Button to open new homework dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkClassSelectLabel: { text: 'Class', description: 'Label for class select in work tab.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkEmpty: { text: 'No homework assigned yet for this class.', description: 'Empty state message for work tab.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkColTitle: { text: 'Homework', description: 'Column header for homework title.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkColSubject: { text: 'Subject', description: 'Column header for subject name.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkColDueDate: { text: 'Due date', description: 'Column header for due date.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkColSubmitted: { text: 'Submitted', description: 'Column header for submitted count.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkColPending: { text: 'Pending', description: 'Column header for pending count.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkColLate: { text: 'Late', description: 'Column header for late count.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkPaperHandIn: { text: 'Handed in on paper', description: 'Button label to mark paper hand-in for homework.', surface: 'shell', maxChars: 30 },
  NLSEducationWorkFrozenBadge: { text: 'Frozen', description: 'Badge for frozen learner in homework roll.', surface: 'shell', maxChars: 15 },
  NLSEducationHomeworkStatusAssigned: { text: 'Assigned', description: 'Homework status chip label for assigned.', surface: 'shell', maxChars: 20 },
  NLSEducationHomeworkStatusSubmitted: { text: 'Submitted', description: 'Homework status chip label for submitted.', surface: 'shell', maxChars: 20 },
  NLSEducationHomeworkStatusLate: { text: 'Late', description: 'Homework status chip label for late.', surface: 'shell', maxChars: 20 },
  NLSEducationHomeworkStatusReturned: { text: 'Returned', description: 'Homework status chip label for returned.', surface: 'shell', maxChars: 20 },
  NLSEducationHomeworkStatusApproved: { text: 'Approved', description: 'Homework status chip label for approved.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkNewHomeworkClassLabel: { text: 'Class', description: 'Label for class in new homework dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkNewHomeworkSubjectLabel: { text: 'Subject', description: 'Label for subject in new homework dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkNewHomeworkTitleLabel: { text: 'Title', description: 'Label for title in new homework dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkNewHomeworkInstructionsLabel: { text: 'Instructions', description: 'Label for instructions in new homework dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkNewHomeworkDueDateLabel: { text: 'Due date', description: 'Label for due date in new homework dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkNewHomeworkMaxScoreLabel: { text: 'Out of', description: 'Label for max score in new homework dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkNewHomeworkMaxScoreHint: { text: 'Optional maximum score (1–1000)', description: 'Hint for max score in new homework dialog.', surface: 'shell', maxChars: 45 },
  NLSEducationWorkNewHomeworkSave: { text: 'Assign homework', description: 'Save button label in new homework dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkNewHomeworkCancel: { text: 'Cancel', description: 'Cancel button label in new homework dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkPastDueDateError: { text: 'Due date cannot be before today.', description: 'Validation error for past due date.', surface: 'shell', maxChars: 45 },
  NLSEducationWorkScoreBoundsError: { text: 'Score must be a whole number between 1 and 1000.', description: 'Validation error for out of bounds score.', surface: 'shell', maxChars: 65 },
  NLSEducationWorkRollLoading: { text: 'Loading roll…', description: 'Loading indicator for homework roll.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkPaperSubmitting: { text: 'Marking…', description: 'Submitting indicator for paper mark.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkRollEmpty: { text: 'No learners in this class.', description: 'Empty state for homework roll.', surface: 'shell', maxChars: 40 },
  NLSEducationWorkNoSubjects: { text: 'No subjects available where you can set homework.', description: 'Notice when user has no set permissions on any subject.', surface: 'shell', maxChars: 65 },
  NLSEducationWorkReviewBtn: { text: 'Review', description: 'Button label on homework roll row to open review panel.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkRollScore: { text: 'Score: {score}', description: 'Read-only score displayed on approved homework roll row.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkRollFeedback: { text: 'Feedback: {feedback}', description: 'Read-only feedback displayed on reviewed homework roll row.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizSectionTitle: { text: 'Online quiz', description: 'Header for online quiz section in homework drawer.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkQuizSetupBtn: { text: 'Set up quiz', description: 'Button to configure a quiz for a homework item.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizEditBtn: { text: 'Edit quiz', description: 'Button to edit quiz settings for a homework item.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizFormLabel: { text: 'Form: {title}', description: 'Form title summary in homework roll quiz section.', surface: 'shell', maxChars: 40 },
  NLSEducationWorkQuizPassMark: { text: 'Pass mark: {percent}%', description: 'Pass mark percentage in quiz summary.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkQuizMaxAttempts: { text: 'Attempts allowed: {attempts}', description: 'Allowed attempt count in quiz summary.', surface: 'shell', maxChars: 30 },
  NLSEducationWorkQuizAttemptCount: { text: 'Attempts taken: {count}', description: 'Taken attempts count in quiz summary.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkQuizStateLocked: { text: 'Locked', description: 'Chip indicating quiz answer keys are locked.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkQuizStateClosed: { text: 'Closed', description: 'Chip indicating quiz is closed.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkQuizStateOpen: { text: 'Open', description: 'Chip indicating quiz is open.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkQuizOnlineToggle: { text: 'Online quiz', description: 'Toggle label for online quiz in new homework dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizOnlineHint: { text: 'Configure questions and answers after saving homework.', description: 'Hint for online quiz toggle.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizDialogTitle: { text: 'Quiz setup', description: 'Dialog title for quiz setup.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkQuizDialogFormLabel: { text: 'Form', description: 'Form selector label in quiz setup.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkQuizDialogFormPlaceholder: { text: 'Select a form', description: 'Placeholder for form select in quiz setup.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkQuizDialogQuestionsHeader: { text: 'Questions & scoring', description: 'Section header for quiz questions in setup dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationWorkQuizDialogNoQuestions: { text: 'No scorable questions found in this form.', description: 'Empty state when form has no scorable questions.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizDialogScored: { text: 'Scored', description: 'Toggle label to include question in quiz scoring.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkQuizDialogNotScored: { text: 'Not scored', description: 'Indicator for un-scorable question types.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizDialogPointsLabel: { text: 'Points', description: 'Label for question points in quiz setup.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkQuizDialogCorrectAnswerLabel: { text: 'Correct answer', description: 'Label for correct answer input in quiz setup.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkQuizDialogToleranceLabel: { text: 'Tolerance (±)', description: 'Label for number tolerance in quiz setup.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizDialogExplanationLabel: { text: 'Explanation (shown after quiz closes)', description: 'Label for question explanation in quiz setup.', surface: 'shell', maxChars: 45 },
  NLSEducationWorkQuizDialogPassPercentLabel: { text: 'Pass mark (%)', description: 'Label for pass mark percentage in quiz setup.', surface: 'shell', maxChars: 25 },
  NLSEducationWorkQuizDialogMaxAttemptsLabel: { text: 'Attempts allowed (1–10)', description: 'Label for maximum attempts in quiz setup.', surface: 'shell', maxChars: 30 },
  NLSEducationWorkQuizDialogAllowEditsLabel: { text: 'Allow answer changes after attempts start', description: 'Label for switch to allow key edits after attempts.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizDialogAllowEditsHint: { text: 'Off by default: questions and answers lock on the first attempt.', description: 'Hint for allow key edits switch.', surface: 'shell', maxChars: 70 },
  NLSEducationWorkQuizDialogLockedNotice: { text: 'Answers and scoring are locked because attempts have been made.', description: 'Notice banner when quiz keys are locked.', surface: 'shell', maxChars: 70 },
  NLSEducationWorkQuizDialogClosedNotice: { text: 'This quiz is closed. All settings are read-only.', description: 'Notice banner when quiz is closed.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizDialogSave: { text: 'Save quiz', description: 'Button label to save quiz configuration.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizDialogCancel: { text: 'Cancel', description: 'Button label to cancel quiz setup dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkQuizDialogSaving: { text: 'Saving…', description: 'Indicator when quiz setup is saving.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizDialogLoading: { text: 'Loading quiz setup…', description: 'Indicator when quiz setup is loading.', surface: 'shell', maxChars: 30 },
  NLSEducationWorkQuizErrorKeysLocked: { text: 'Quiz keys are locked because attempts have started.', description: 'Error message for QUIZ_KEYS_LOCKED.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizErrorHasAttempts: { text: 'Cannot remove or clear quiz because attempts exist.', description: 'Error message for QUIZ_HAS_ATTEMPTS.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizErrorClosed: { text: 'This quiz is closed and can no longer be edited.', description: 'Error message for QUIZ_CLOSED.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizErrorFormInUse: { text: 'This form is already in use by another quiz.', description: 'Error message for FORM_IN_USE.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizErrorFormNotFound: { text: 'Form not found in workspace.', description: 'Error message for FORM_NOT_FOUND.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorFormHasBindings: { text: 'Quiz forms cannot have stock or budget bindings.', description: 'Error message for FORM_HAS_BINDINGS.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizErrorFormHasFileFields: { text: 'Quiz forms cannot have file upload questions.', description: 'Error message for FORM_HAS_FILE_FIELDS.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizErrorInvalidPassPercent: { text: 'Pass percentage must be between 0 and 100.', description: 'Error message for INVALID_PASS_PERCENT.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorInvalidMaxAttempts: { text: 'Attempts allowed must be between 1 and 10.', description: 'Error message for INVALID_MAX_ATTEMPTS.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorKeysRequired: { text: 'At least one scored question with an answer key is required.', description: 'Error message for KEYS_REQUIRED.', surface: 'shell', maxChars: 65 },
  NLSEducationWorkQuizErrorFieldNotFound: { text: 'A question field was not found in the form.', description: 'Error message for FIELD_NOT_FOUND.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorUnscorableFieldType: { text: 'Selected field type cannot be scored.', description: 'Error message for UNSCORABLE_FIELD_TYPE.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorInvalidPoints: { text: 'Question points must be greater than 0.', description: 'Error message for INVALID_POINTS.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorInvalidOptionId: { text: 'Please select a valid option for this question.', description: 'Error message for INVALID_OPTION_ID.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorInvalidCorrectValue: { text: 'Invalid correct answer value.', description: 'Error message for INVALID_CORRECT_VALUE.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorInvalidTolerance: { text: 'Tolerance must be a non-negative number.', description: 'Error message for INVALID_TOLERANCE.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorNotSubjectNode: { text: 'Quiz must be attached to a subject homework item.', description: 'Error message for NOT_SUBJECT_NODE.', surface: 'shell', maxChars: 60 },
  NLSEducationWorkQuizErrorAttemptsUsed: { text: 'Maximum quiz attempts reached.', description: 'Error message for QUIZ_ATTEMPTS_USED.', surface: 'shell', maxChars: 40 },
  NLSEducationWorkQuizErrorKeysNotFound: { text: 'Quiz answer keys not found.', description: 'Error message for KEYS_NOT_FOUND.', surface: 'shell', maxChars: 40 },
  NLSEducationWorkQuizErrorAnswerRequired: { text: 'An answer is required for this scored question.', description: 'Validation error when scored question has no answer.', surface: 'shell', maxChars: 65 },
  NLSEducationWorkQuizErrorPointsInvalid: { text: 'Points must be a positive number.', description: 'Validation error when points is zero or invalid.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizErrorToleranceInvalid: { text: 'Tolerance must be zero or positive.', description: 'Validation error when tolerance is negative or invalid.', surface: 'shell', maxChars: 50 },
  NLSEducationWorkQuizFormSearchPlaceholder: { text: 'Search forms…', description: 'Placeholder for form search input in quiz setup dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationWorkQuizFormLoadMore: { text: 'Load more', description: 'Button label to load more forms in quiz setup dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizNewFormBtn: { text: 'New form', description: 'Button to create a new form from quiz setup dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizDiscardFormTitle: { text: 'Discard unsaved form?', description: 'Title of confirm modal when closing form builder with unsaved changes.', surface: 'shell', maxChars: 35 },
  NLSEducationWorkQuizDiscardFormBody: { text: 'You have unsaved changes in this form. If you discard now, your changes will be lost.', description: 'Body text explaining loss of changes if discarding form builder.', surface: 'shell', maxChars: 120 },
  NLSEducationWorkQuizDiscardFormConfirm: { text: 'Discard', description: 'Confirm button to discard form builder changes.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkQuizDiscardFormCancel: { text: 'Keep editing', description: 'Cancel button to keep editing in form builder.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizCloseBtn: { text: 'Close quiz', description: 'Button to close an open quiz in homework drawer.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizCloseConfirmTitle: { text: 'Close this quiz?', description: 'Title of confirmation modal to close a quiz.', surface: 'shell', maxChars: 30 },
  NLSEducationWorkQuizCloseConfirmBody: { text: 'Closing this quiz is final. No further attempts can be submitted, and correct answers and explanations will become visible to families.', description: 'Body explaining that closing a quiz is final and reveals answers to families.', surface: 'shell', maxChars: 160 },
  NLSEducationWorkQuizCloseConfirmBtn: { text: 'Close quiz', description: 'Confirm button to close a quiz.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkQuizCloseCancelBtn: { text: 'Cancel', description: 'Cancel button when closing a quiz.', surface: 'shell', maxChars: 15 },
  NLSEducationHomeworkSourcePaper: { text: 'Paper', description: 'Homework submission source label for paper.', surface: 'shell', maxChars: 20 },
  NLSEducationHomeworkSourceWeb: { text: 'Web', description: 'Homework submission source label for web.', surface: 'shell', maxChars: 20 },
  NLSEducationHomeworkSourceMember: { text: 'Member app', description: 'Homework submission source label for member app.', surface: 'shell', maxChars: 25 },
  NLSEducationHomeworkSourceHelper: { text: 'Helper app', description: 'Homework submission source label for helper app.', surface: 'shell', maxChars: 25 },
  NLSEducationReviewPanelTitle: { text: 'Review submission', description: 'Title of homework review panel.', surface: 'shell', maxChars: 30 },
  NLSEducationReviewSubmittedAt: { text: 'Submitted at', description: 'Label for submission time in review panel.', surface: 'shell', maxChars: 20 },
  NLSEducationReviewSubmittedBy: { text: 'Submitted by', description: 'Label for submitter in review panel.', surface: 'shell', maxChars: 20 },
  NLSEducationReviewSource: { text: 'Source', description: 'Label for submission source in review panel.', surface: 'shell', maxChars: 15 },
  NLSEducationReviewNote: { text: 'Note', description: 'Label for submission note in review panel.', surface: 'shell', maxChars: 15 },
  NLSEducationReviewAttachments: { text: 'Attachments', description: 'Label for submission attachments in review panel.', surface: 'shell', maxChars: 20 },
  NLSEducationReviewScoreLabel: { text: 'Score', description: 'Label for review score field.', surface: 'shell', maxChars: 15 },
  NLSEducationReviewScoreOutOf: { text: 'out of {maxScore}', description: 'Score hint indicating maximum score.', surface: 'shell', maxChars: 20 },
  NLSEducationReviewFeedbackLabel: { text: 'Feedback', description: 'Label for return feedback field in review panel.', surface: 'shell', maxChars: 20 },
  NLSEducationReviewFeedbackPlaceholder: { text: 'Add feedback for the learner (at least 3 characters)…', description: 'Placeholder for return feedback field.', surface: 'shell', maxChars: 65 },
  NLSEducationReviewApproveBtn: { text: 'Approve', description: 'Approve button in review panel.', surface: 'shell', maxChars: 15 },
  NLSEducationReviewReturnBtn: { text: 'Return', description: 'Return button in review panel.', surface: 'shell', maxChars: 15 },
  NLSEducationReviewCloseBtn: { text: 'Close', description: 'Close button in review panel.', surface: 'shell', maxChars: 15 },
  NLSEducationReviewSubmitting: { text: 'Saving…', description: 'Saving in-progress label in review panel.', surface: 'shell', maxChars: 15 },

  // ED8 Grading Scheme, Assessments, and Gradebook
  NLSEducationGradingSchemeMenu: { text: 'Grading scheme…', description: 'Menu item in Year or Programme row to open grading scheme dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemeDialogTitle: { text: 'Grading scheme', description: 'Title of grading scheme dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemePresetLabel: { text: 'Preset', description: 'Preset selector label in grading scheme dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemePresetLetterAF: { text: 'Letter grades (A–F)', description: 'Option for A-F grading preset.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemePresetCBSE: { text: 'CBSE (A1–E)', description: 'Option for CBSE grading preset.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemePresetPercent: { text: 'Percentage only', description: 'Option for percentage-only grading preset.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemePresetPassFail: { text: 'Pass / Fail', description: 'Option for pass-fail grading preset.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemeBandsHeader: { text: 'Grade bands', description: 'Section header for grade bands in grading scheme dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemeBandLabel: { text: 'Band label', description: 'Input label for grade band letter or title.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeBandMinPercent: { text: 'Minimum percent', description: 'Input label for grade band minimum threshold percentage.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemeAddBand: { text: 'Add grade band', description: 'Button label to add a grade band.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeRemoveBand: { text: 'Remove grade band', description: 'Button label to remove a grade band.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeCategoriesHeader: { text: 'Categories', description: 'Section header for grading categories.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeCategoryName: { text: 'Category name', description: 'Input label for grading category name.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeCategoryWeight: { text: 'Category weight (%)', description: 'Input label for grading category weight percent.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemeAddCategory: { text: 'Add category', description: 'Button label to add a grading category.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeRemoveCategory: { text: 'Remove category', description: 'Button label to remove a grading category.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeTermsHeader: { text: 'Terms', description: 'Section header for academic terms in grading scheme.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeTermName: { text: 'Term name', description: 'Input label for term name.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeTermStart: { text: 'Start date', description: 'Input label for term start date.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeTermEnd: { text: 'End date', description: 'Input label for term end date.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeTermWeight: { text: 'Term weight (%)', description: 'Input label for term weight percent.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeEqualSplit: { text: 'Equal split', description: 'Button label to divide term weights equally.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeAddTerm: { text: 'Add term', description: 'Button label to add a term.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeRemoveTerm: { text: 'Remove term', description: 'Button label to remove a term.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemePassPercent: { text: 'Pass mark (%)', description: 'Input label for overall pass percentage.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeSave: { text: 'Save scheme', description: 'Button label to save grading scheme.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeSaving: { text: 'Saving…', description: 'Button label when saving grading scheme.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeLoading: { text: 'Loading grading scheme…', description: 'Loading state indicator in grading scheme dialog.', surface: 'shell', maxChars: 35 },
  NLSEducationGradingSchemeClose: { text: 'Close', description: 'Close button in grading scheme dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationGradingSchemePresetConfirmTitle: { text: 'Replace custom bands?', description: 'Dialog title when switching preset would overwrite custom bands.', surface: 'shell', maxChars: 35 },
  NLSEducationGradingSchemePresetConfirmBody: { text: 'Switching presets will replace your custom grade bands with the preset defaults.', description: 'Confirmation warning when changing presets.', surface: 'shell', maxChars: 90 },
  NLSEducationGradingSchemePresetConfirmBtn: { text: 'Replace', description: 'Confirm action to replace bands with preset.', surface: 'shell', maxChars: 15 },
  NLSEducationGradingSchemePresetCancelBtn: { text: 'Keep custom bands', description: 'Cancel action to retain custom bands.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeValTermWeights: { text: 'Term weights must total 100%.', description: 'Validation error when term weights do not sum to 100.', surface: 'shell', maxChars: 45 },
  NLSEducationGradingSchemeValCatWeights: { text: 'Category weights must total 100%.', description: 'Validation error when category weights do not sum to 100.', surface: 'shell', maxChars: 45 },
  NLSEducationGradingSchemeValTermDates: { text: 'End date must be on or after start date.', description: 'Validation error when a term end date precedes start date.', surface: 'shell', maxChars: 50 },
  NLSEducationGradingSchemeValTermOverlap: { text: 'Terms cannot have overlapping dates.', description: 'Validation error when terms overlap in date.', surface: 'shell', maxChars: 50 },
  NLSEducationGradingSchemeValBandPercent: { text: 'Band minimum percent must be between 0 and 100.', description: 'Validation error when a band minimum percentage is out of range.', surface: 'shell', maxChars: 60 },
  NLSEducationGradingSchemeValBandDuplicate: { text: 'Band labels and percentages must be unique.', description: 'Validation error when duplicate grade bands exist.', surface: 'shell', maxChars: 55 },
  NLSEducationGradingSchemeValZeroBandRequired: { text: 'A grade band with 0% minimum is required.', description: 'Validation error when no base 0% band exists.', surface: 'shell', maxChars: 55 },
  NLSEducationGradingSchemeTermDefaultName: { text: 'Term {n}', description: 'Default name for academic term in grading scheme.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingCategoryExam: { text: 'Exam', description: 'Default category name for exams.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingCategoryTest: { text: 'Test', description: 'Default category name for tests.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingCategoryHomework: { text: 'Homework', description: 'Default category name for homework.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingCategoryQuiz: { text: 'Quiz', description: 'Default category name for quizzes.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingBandPass: { text: 'Pass', description: 'Default label for Pass grade band.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingBandFail: { text: 'Fail', description: 'Default label for Fail grade band.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeReloadBtn: { text: 'Reload', description: 'Button label to reload scheme after conflict.', surface: 'shell', maxChars: 15 },
  NLSEducationGradingSchemeReloadConfirmTitle: { text: 'Discard unsaved changes?', description: 'Confirmation title when reloading conflicting scheme.', surface: 'shell', maxChars: 40 },
  NLSEducationGradingSchemeReloadConfirmBody: { text: 'Reloading will discard your unsaved changes and fetch the latest scheme.', description: 'Confirmation message when reloading conflicting scheme.', surface: 'shell', maxChars: 90 },
  NLSEducationGradingSchemeReloadConfirmBtn: { text: 'Reload scheme', description: 'Confirm action to reload scheme.', surface: 'shell', maxChars: 20 },

  // Assessments Tab
  NLSEducationAssessmentsTabTitle: { text: 'Assessments', description: 'Title heading for Assessments tab.', surface: 'shell', maxChars: 25 },
  NLSEducationAssessmentsClassSelectLabel: { text: 'Class', description: 'Label for class dropdown in assessments tab.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsSubjectSelectLabel: { text: 'Subject', description: 'Label for subject dropdown in assessments tab.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsTermFilterLabel: { text: 'Term', description: 'Label for term filter dropdown in assessments tab.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsAllTerms: { text: 'All terms', description: 'Option to view assessments across all terms.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsShowArchived: { text: 'Show archived', description: 'Toggle label to show archived assessments.', surface: 'shell', maxChars: 25 },
  NLSEducationAssessmentsNewBtn: { text: 'New assessment', description: 'Button to create a new assessment.', surface: 'shell', maxChars: 25 },
  NLSEducationAssessmentsEmpty: { text: 'No assessments recorded for this subject.', description: 'Empty state when no assessments are found.', surface: 'shell', maxChars: 60 },
  NLSEducationAssessmentsNoSubjects: { text: 'No subjects found for this class.', description: 'Empty state when class has no subject nodes.', surface: 'shell', maxChars: 50 },
  NLSEducationAssessmentsNoSchemeTitle: { text: 'Grading scheme required', description: 'Empty state title when grading scheme is not configured.', surface: 'shell', maxChars: 35 },
  NLSEducationAssessmentsNoSchemeManager: { text: 'Set up the grading scheme from Classes to manage assessments.', description: 'Manager prompt to set up grading scheme.', surface: 'shell', maxChars: 70 },
  NLSEducationAssessmentsNoSchemeStaff: { text: 'A grading scheme has not been configured yet for this academic year.', description: 'Staff message when grading scheme is missing.', surface: 'shell', maxChars: 75 },
  NLSEducationAssessmentsColTitle: { text: 'Title', description: 'Table column header for assessment title.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsColCategory: { text: 'Category', description: 'Table column header for assessment category.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsColTerm: { text: 'Term', description: 'Table column header for assessment term.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsColDate: { text: 'Date', description: 'Table column header for assessment date.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsColMaxMarks: { text: 'Max marks', description: 'Table column header for assessment maximum marks.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsColSource: { text: 'Source', description: 'Table column header for assessment source type.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsColStatus: { text: 'Status', description: 'Table column header for assessment status.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsColActions: { text: 'Actions', description: 'Table column header for assessment actions.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsSourceAssessment: { text: 'Assessment', description: 'Source chip label for direct assessment.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsSourceHomework: { text: 'Homework', description: 'Source chip label for homework item.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsSourceQuiz: { text: 'Quiz', description: 'Source chip label for quiz item.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsStatusActive: { text: 'Active', description: 'Chip indicating assessment is active.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsStatusArchived: { text: 'Archived', description: 'Chip indicating assessment is archived.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsEditBtn: { text: 'Edit', description: 'Button label to edit an assessment.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsArchiveBtn: { text: 'Archive', description: 'Button label to archive an assessment.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsArchiveConfirmTitle: { text: 'Archive this assessment?', description: 'Confirm title when archiving an assessment.', surface: 'shell', maxChars: 35 },
  NLSEducationAssessmentsArchiveConfirmBody: { text: 'Archiving removes this assessment from active grade calculations. It can be viewed under archived items.', description: 'Body text explaining archive effect.', surface: 'shell', maxChars: 120 },
  NLSEducationAssessmentsArchiveConfirmBtn: { text: 'Confirm archive', description: 'Confirm button to archive assessment.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsArchiveCancelBtn: { text: 'Cancel', description: 'Cancel button when archiving assessment.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsDialogTitleNew: { text: 'New assessment', description: 'Dialog title for creating an assessment.', surface: 'shell', maxChars: 25 },
  NLSEducationAssessmentsDialogTitleEdit: { text: 'Edit assessment', description: 'Dialog title for editing an assessment.', surface: 'shell', maxChars: 25 },
  NLSEducationAssessmentsDialogFieldTitle: { text: 'Title', description: 'Input label for assessment title in dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsDialogFieldCategory: { text: 'Category', description: 'Select label for assessment category in dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsDialogFieldTerm: { text: 'Term', description: 'Select label for assessment term in dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsDialogTermFromDateHint: { text: '(from date)', description: 'Hint shown when assessment term is derived from date.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsDialogFieldMaxMarks: { text: 'Maximum marks', description: 'Input label for maximum marks in dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationAssessmentsDialogFieldDate: { text: 'Date', description: 'Input label for assessment date in dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsDialogFieldMode: { text: 'Mode', description: 'Select label for assessment mode in dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsModeOffline: { text: 'Offline', description: 'Option for offline assessment mode.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsModeOnline: { text: 'Online', description: 'Option for online assessment mode.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsSaveBtn: { text: 'Save', description: 'Button to save edited assessment.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsCreateBtn: { text: 'Create', description: 'Button to create assessment.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsCancelBtn: { text: 'Cancel', description: 'Button to cancel assessment dialog.', surface: 'shell', maxChars: 15 },

  // WorkTab Grading Opt-in Section
  NLSEducationWorkGradingSectionTitle: { text: 'Grading', description: 'Header for grading section in expanded homework drawer.', surface: 'shell', maxChars: 20 },
  NLSEducationWorkGradingToggleLabel: { text: 'Counts toward the grade', description: 'Toggle label for homework opt-in to grading.', surface: 'shell', maxChars: 30 },
  NLSEducationWorkGradingNoMaxScoreHint: { text: 'A maximum score is required for homework to count toward the grade.', description: 'Hint displayed when homework has no max score.', surface: 'shell', maxChars: 75 },
  NLSEducationWorkGradingNoSchemeHint: { text: 'Set up a grading scheme in Classes for this year or programme.', description: 'Hint when no grading scheme exists for homework grading opt-in.', surface: 'shell', maxChars: 80 },
  NLSEducationWorkGradingTermLabel: { text: 'Term', description: 'Label for grading term select in homework detail.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkGradingCategoryLabel: { text: 'Category', description: 'Label for grading category select in homework detail.', surface: 'shell', maxChars: 15 },
  NLSEducationWorkGradingLoading: { text: 'Loading grading status…', description: 'Loading indicator for homework grading opt-in.', surface: 'shell', maxChars: 30 },

  // Grading Error Messages
  NLSEducationGradingErrorVersionConflict: { text: 'Another user has updated the grading scheme. Please reload and try again.', description: 'Error message when scheme version conflicts.', surface: 'shell', maxChars: 75 },
  NLSEducationGradingErrorSchemeLocked: { text: 'The grading scheme cannot be modified because one or more terms are locked.', description: 'Error message when scheme is locked by terms.', surface: 'shell', maxChars: 80 },
  NLSEducationGradingErrorSchemeLockedWithTerms: { text: 'The grading scheme cannot be modified because term(s) are locked: {terms}.', description: 'Error message when scheme is locked with specific term names.', surface: 'shell', maxChars: 100 },
  NLSEducationGradingErrorTermLocked: { text: 'Cannot modify marks or assessments in a locked term.', description: 'Error message when term is locked.', surface: 'shell', maxChars: 60 },
  NLSEducationGradingErrorInUse: { text: 'This item cannot be deleted because it is in use by assessments.', description: 'Error message when grading item is in use.', surface: 'shell', maxChars: 70 },
  NLSEducationGradingErrorTermInUse: { text: 'This term cannot be deleted because assessments are assigned to it.', description: 'Error message when term is in use by assessments.', surface: 'shell', maxChars: 75 },
  NLSEducationGradingErrorCategoryInUse: { text: 'This category cannot be deleted because assessments are assigned to it.', description: 'Error message when category is in use by assessments.', surface: 'shell', maxChars: 75 },
  NLSEducationGradingErrorInvalidPreset: { text: 'Invalid grading preset.', description: 'Error message for invalid preset.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingErrorInvalidPassPercent: { text: 'Pass percent must be an integer between 0 and 100.', description: 'Error message for invalid pass percent.', surface: 'shell', maxChars: 55 },
  NLSEducationGradingErrorInvalidTerms: { text: 'Terms must have valid dates and cannot overlap.', description: 'Error message for invalid terms.', surface: 'shell', maxChars: 50 },
  NLSEducationGradingErrorInvalidCategories: { text: 'Categories must have valid names and positive weights.', description: 'Error message for invalid categories.', surface: 'shell', maxChars: 60 },
  NLSEducationGradingErrorCategoryWeightsTotal: { text: 'Category weights must total 100%.', description: 'Error message when category weights do not sum to 100.', surface: 'shell', maxChars: 45 },
  NLSEducationGradingErrorInvalidBands: { text: 'Grade bands must be sorted from highest to lowest and include 0%.', description: 'Error message for invalid grade bands.', surface: 'shell', maxChars: 70 },
  NLSEducationGradingErrorSchemeRequired: { text: 'A grading scheme must be configured for this year or programme.', description: 'Error message when grading scheme is missing.', surface: 'shell', maxChars: 70 },
  NLSEducationGradingErrorUnknownCategory: { text: 'Selected category was not found in the grading scheme.', description: 'Error message for unknown category.', surface: 'shell', maxChars: 60 },
  NLSEducationGradingErrorUnknownTerm: { text: 'Selected term was not found in the grading scheme.', description: 'Error message for unknown term.', surface: 'shell', maxChars: 55 },
  NLSEducationGradingErrorTermRequired: { text: 'A term is required for this assessment.', description: 'Error message when assessment term is missing.', surface: 'shell', maxChars: 45 },
  NLSEducationGradingErrorCategoryRequired: { text: 'A category is required for this assessment.', description: 'Error message when assessment category is missing.', surface: 'shell', maxChars: 50 },
  NLSEducationGradingErrorMaxScoreRequired: { text: 'A maximum score is required for homework to count toward the grade.', description: 'Error message when homework max score is absent.', surface: 'shell', maxChars: 75 },
  NLSEducationGradingErrorSourceManaged: { text: 'This assessment is managed from its source work item and cannot be edited directly.', description: 'Error message when trying to edit homework assessment directly.', surface: 'shell', maxChars: 85 },
  NLSEducationGradingErrorAssessmentArchived: { text: 'This assessment has been archived.', description: 'Error message when accessing archived assessment.', surface: 'shell', maxChars: 40 },
  NLSEducationGradingErrorMaxBelowMarks: { text: 'Maximum marks cannot be less than entered marks.', description: 'Error message when reducing max marks below existing marks.', surface: 'shell', maxChars: 55 },
  NLSEducationGradingErrorInvalidScore: { text: 'Invalid score entered.', description: 'Error message for invalid mark score.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingErrorInvalidTerm: { text: 'Invalid term selected.', description: 'Error message for invalid term.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingErrorNoAssessments: { text: 'No assessments found for this subject and term.', description: 'Error message when locking a term with no assessments.', surface: 'shell', maxChars: 60 },
  NLSEducationGradingErrorAlreadyLocked: { text: 'Marks for this subject and term are already locked.', description: 'Error message when already locked.', surface: 'shell', maxChars: 55 },
  NLSEducationGradingErrorNotLocked: { text: 'Marks for this subject and term are not locked.', description: 'Error message when trying to unlock an unlocked term.', surface: 'shell', maxChars: 50 },
  NLSEducationGradingErrorMarksIncomplete: { text: 'Cannot lock marks while entries are incomplete.', description: 'Error message when locking with incomplete marks.', surface: 'shell', maxChars: 55 },
  NLSEducationGradingErrorRecomputePending: { text: 'Marks must be recomputed before locking.', description: 'Error message when recompute is pending.', surface: 'shell', maxChars: 50 },
  NLSEducationGradingErrorRecomputeIncomplete: { text: 'Cannot recompute marks while a term is locked.', description: 'Error message when recompute cannot complete.', surface: 'shell', maxChars: 55 },
  NLSEducationGradingErrorNotClassNode: { text: 'Selected node is not a class or batch.', description: 'Error message when target node is not a class.', surface: 'shell', maxChars: 45 },
  NLSEducationGradingErrorNodeAccessDenied: { text: 'You do not have access to this node.', description: 'Error message for restricted member node access denied.', surface: 'shell', maxChars: 45 },

  // View switcher in AssessmentsTab
  NLSEducationAssessmentsViewAssessments: { text: 'Assessments', description: 'View switcher tab for assessments list.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsViewMarks: { text: 'Marks', description: 'View switcher tab for marks grid.', surface: 'shell', maxChars: 15 },
  NLSEducationAssessmentsViewGradebook: { text: 'Gradebook', description: 'View switcher tab for subject gradebook.', surface: 'shell', maxChars: 20 },
  NLSEducationAssessmentsViewClassOverview: { text: 'Class overview', description: 'View switcher tab for class gradebook overview.', surface: 'shell', maxChars: 25 },

  // Marks Grid
  NLSEducationMarksGridTitle: { text: 'Marks grid', description: 'Header title for marks grid view.', surface: 'shell', maxChars: 25 },
  NLSEducationMarksGridSubtitle: { text: 'Enter and manage marks for active assessments.', description: 'Subtitle for marks grid.', surface: 'shell', maxChars: 60 },
  NLSEducationMarksGridLearnerCol: { text: 'Learner', description: 'Learner column header in marks grid.', surface: 'shell', maxChars: 20 },
  NLSEducationMarksGridRollNoCol: { text: 'Roll no.', description: 'Roll number column header in marks grid.', surface: 'shell', maxChars: 15 },
  NLSEducationMarksGridUndoBtn: { text: 'Undo', description: 'Button to undo last mark change.', surface: 'shell', maxChars: 15 },
  NLSEducationMarksGridLockedBanner: { text: 'This term is locked. Marks are read-only.', description: 'Banner warning when marks grid is locked.', surface: 'shell', maxChars: 60 },
  NLSEducationMarksGridFromWork: { text: 'From work', description: 'Badge or title for mark submitted from homework or quiz.', surface: 'shell', maxChars: 20 },
  NLSEducationMarksGridNotOnRoll: { text: 'Not on roll', description: 'Cell label when learner is not on roll for subject.', surface: 'shell', maxChars: 20 },
  NLSEducationMarksGridChangedAfterLock: { text: 'Changed after lock', description: 'Flag title indicating mark changed after term was locked.', surface: 'shell', maxChars: 30 },
  NLSEducationMarksGridSaving: { text: 'Saving…', description: 'Status text when mark is saving.', surface: 'shell', maxChars: 15 },
  NLSEducationMarksGridSaved: { text: 'Saved', description: 'Status text when mark is saved.', surface: 'shell', maxChars: 15 },
  NLSEducationMarksGridFailed: { text: 'Failed', description: 'Status text when mark save failed.', surface: 'shell', maxChars: 15 },
  NLSEducationMarksGridRetry: { text: 'Retry', description: 'Button to retry failed mark save.', surface: 'shell', maxChars: 15 },
  NLSEducationMarksGridInvalidScore: { text: 'Invalid score', description: 'Tooltip for invalid mark value.', surface: 'shell', maxChars: 20 },
  NLSEducationMarksGridErrorInvalidMark: { text: 'Use a number 0–{max}, Ab or Ex', description: 'Error message when entered mark is invalid or out of range.', surface: 'shell', maxChars: 45 },
  NLSEducationMarksGridErrorSourceManaged: { text: 'Only Ab or Ex for homework and quiz marks', description: 'Error message when numeric mark entered for source-managed homework/quiz.', surface: 'shell', maxChars: 50 },
  NLSEducationMarksGridErrorSummary: { text: '{notSaved} not saved · {invalid} invalid', description: 'Summary banner counting not saved and invalid cells.', surface: 'shell', maxChars: 45 },
  NLSEducationMarksGridErrorSummaryNotSavedOnly: { text: '{notSaved} not saved', description: 'Summary banner counting not saved cells.', surface: 'shell', maxChars: 25 },
  NLSEducationMarksGridErrorSummaryInvalidOnly: { text: '{invalid} invalid', description: 'Summary banner counting invalid cells.', surface: 'shell', maxChars: 25 },
  NLSEducationMarksGridMenuMark: { text: 'Set mark', description: 'Menu option to enter mark score.', surface: 'shell', maxChars: 20 },
  NLSEducationMarksGridMenuAbsent: { text: 'Mark absent (Ab)', description: 'Menu option to mark learner absent.', surface: 'shell', maxChars: 25 },
  NLSEducationMarksGridMenuExcused: { text: 'Mark excused (Ex)', description: 'Menu option to mark learner excused.', surface: 'shell', maxChars: 25 },
  NLSEducationMarksGridMenuClear: { text: 'Clear mark', description: 'Menu option to clear mark value.', surface: 'shell', maxChars: 20 },
  NLSEducationMarksGridMenuRemarks: { text: 'Remarks…', description: 'Menu option to edit mark remarks.', surface: 'shell', maxChars: 20 },
  NLSEducationMarksGridMenuHistory: { text: 'History…', description: 'Menu option to view mark history.', surface: 'shell', maxChars: 20 },
  NLSEducationMarksGridNoAssessments: { text: 'No active assessments in this term.', description: 'Empty message in marks grid when term has no assessments.', surface: 'shell', maxChars: 50 },
  NLSEducationMarksGridNoLearners: { text: 'No enrolled learners found.', description: 'Empty message in marks grid when class has no learners.', surface: 'shell', maxChars: 40 },

  // Remarks Dialog
  NLSEducationMarkRemarksTitle: { text: 'Remarks', description: 'Title of mark remarks dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationMarkRemarksSubtitle: { text: '{learner} · {assessment}', description: 'Subtitle for remarks dialog with learner and assessment names.', surface: 'shell', maxChars: 60 },
  NLSEducationMarkRemarksPlaceholder: { text: 'Enter remarks or teacher feedback (optional)…', description: 'Placeholder for mark remarks textarea.', surface: 'shell', maxChars: 60 },
  NLSEducationMarkRemarksSaveBtn: { text: 'Save remarks', description: 'Save button in mark remarks dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationMarkRemarksCancelBtn: { text: 'Cancel', description: 'Cancel button in mark remarks dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationMarkRemarksLoading: { text: 'Loading remarks…', description: 'Loading indicator in remarks dialog.', surface: 'shell', maxChars: 25 },

  // History Dialog
  NLSEducationMarkHistoryTitle: { text: 'Mark history', description: 'Title of mark history dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationMarkHistorySubtitle: { text: '{learner} · {assessment}', description: 'Subtitle for history dialog with learner and assessment names.', surface: 'shell', maxChars: 60 },
  NLSEducationMarkHistoryEmpty: { text: 'No history recorded for this mark.', description: 'Empty message when mark has no history.', surface: 'shell', maxChars: 50 },
  NLSEducationMarkHistoryLoading: { text: 'Loading history…', description: 'Loading indicator in mark history dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationMarkHistoryCloseBtn: { text: 'Close', description: 'Close button in mark history dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationMarkHistoryActionSet: { text: 'Mark updated', description: 'History event label for mark update.', surface: 'shell', maxChars: 25 },
  NLSEducationMarkHistoryActionLock: { text: 'Term locked', description: 'History event label for term lock.', surface: 'shell', maxChars: 20 },
  NLSEducationMarkHistoryActionUnlock: { text: 'Term unlocked', description: 'History event label for term unlock.', surface: 'shell', maxChars: 20 },
  NLSEducationMarkHistoryActionRecompute: { text: 'Marks recomputed', description: 'History event label for recompute.', surface: 'shell', maxChars: 25 },

  // Gradebook View
  NLSEducationGradebookTitle: { text: 'Subject gradebook', description: 'Header title for subject gradebook view.', surface: 'shell', maxChars: 25 },
  NLSEducationGradebookLearnerCol: { text: 'Learner', description: 'Learner column header in gradebook.', surface: 'shell', maxChars: 20 },
  NLSEducationGradebookRollNoCol: { text: 'Roll no.', description: 'Roll number column header in gradebook.', surface: 'shell', maxChars: 15 },
  NLSEducationGradebookCategoryAverage: { text: '{category} avg', description: 'Category average header in gradebook.', surface: 'shell', maxChars: 30 },
  NLSEducationGradebookTermPercentGrade: { text: 'Term total', description: 'Term summary header in gradebook.', surface: 'shell', maxChars: 20 },
  NLSEducationGradebookYearPercentGrade: { text: 'Year total', description: 'Year summary header in gradebook.', surface: 'shell', maxChars: 20 },
  NLSEducationGradebookMissingCount: { text: '{count} missing', description: 'Missing marks badge in gradebook summary.', surface: 'shell', maxChars: 20 },
  NLSEducationGradebookIncompleteTerm: { text: 'Incomplete', description: 'Indicator when year total has incomplete terms.', surface: 'shell', maxChars: 20 },
  NLSEducationGradebookNoData: { text: 'No gradebook data available for this subject.', description: 'Empty state message for subject gradebook.', surface: 'shell', maxChars: 60 },

  // Class Overview View
  NLSEducationClassGradebookTitle: { text: 'Class gradebook overview', description: 'Header title for class gradebook overview.', surface: 'shell', maxChars: 35 },
  NLSEducationClassGradebookLearnerCol: { text: 'Learner', description: 'Learner column header in class overview.', surface: 'shell', maxChars: 20 },
  NLSEducationClassGradebookRollNoCol: { text: 'Roll no.', description: 'Roll number column header in class overview.', surface: 'shell', maxChars: 15 },
  NLSEducationClassGradebookLockStatusLocked: { text: 'Locked', description: 'Chip indicating subject term is locked in class overview.', surface: 'shell', maxChars: 15 },
  NLSEducationClassGradebookLockStatusUnlocked: { text: 'Unlocked', description: 'Chip indicating subject term is unlocked in class overview.', surface: 'shell', maxChars: 15 },
  NLSEducationClassGradebookNoData: { text: 'No gradebook data available for this class.', description: 'Empty state message for class overview.', surface: 'shell', maxChars: 60 },

  // Lock Bar
  NLSEducationMarkLockBarStatusLocked: { text: 'Locked', description: 'Badge showing term is locked.', surface: 'shell', maxChars: 15 },
  NLSEducationMarkLockBarStatusUnlocked: { text: 'Unlocked', description: 'Badge showing term is unlocked.', surface: 'shell', maxChars: 15 },
  NLSEducationMarkLockBarStatusRecomputePending: { text: 'Recompute pending', description: 'Badge showing recompute is pending.', surface: 'shell', maxChars: 25 },
  NLSEducationMarkLockBarStatusRecomputeIncomplete: { text: 'Recompute incomplete', description: 'Badge showing recompute is incomplete.', surface: 'shell', maxChars: 25 },
  NLSEducationMarkLockBtn: { text: 'Lock term', description: 'Button to lock marks for term.', surface: 'shell', maxChars: 20 },
  NLSEducationMarkUnlockBtn: { text: 'Unlock term', description: 'Button to unlock marks for term.', surface: 'shell', maxChars: 20 },
  NLSEducationMarkRecomputeBtn: { text: 'Recompute marks', description: 'Button to recompute marks for term.', surface: 'shell', maxChars: 25 },
  NLSEducationMarkLockDialogTitle: { text: 'Lock marks for {term}?', description: 'Dialog title when locking marks for a term.', surface: 'shell', maxChars: 40 },
  NLSEducationMarkLockDialogBody: { text: 'Locking prevents further changes to marks and assessments in this term until an owner or administrator unlocks it.', description: 'Dialog explanation when locking marks.', surface: 'shell', maxChars: 130 },
  NLSEducationMarkLockDialogNoteLabel: { text: 'Note (optional, up to 200 characters)', description: 'Label for optional note when locking marks.', surface: 'shell', maxChars: 50 },
  NLSEducationMarkLockDialogConfirmBtn: { text: 'Lock term', description: 'Confirm action to lock term marks.', surface: 'shell', maxChars: 20 },
  NLSEducationMarkLockDialogCancelBtn: { text: 'Cancel', description: 'Cancel action in lock term dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationMarkUnlockDialogTitle: { text: 'Unlock marks for {term}?', description: 'Dialog title when unlocking marks for a term.', surface: 'shell', maxChars: 40 },
  NLSEducationMarkUnlockDialogBody: { text: 'Unlocking allows staff to modify marks and assessments in this term. All edits will be audited.', description: 'Dialog explanation when unlocking marks.', surface: 'shell', maxChars: 120 },
  NLSEducationMarkUnlockDialogNoteLabel: { text: 'Reason / note (optional, up to 200 characters)', description: 'Label for optional note when unlocking marks.', surface: 'shell', maxChars: 55 },
  NLSEducationMarkUnlockDialogConfirmBtn: { text: 'Unlock term', description: 'Confirm action to unlock term marks.', surface: 'shell', maxChars: 20 },
  NLSEducationMarkUnlockDialogCancelBtn: { text: 'Cancel', description: 'Cancel action in unlock term dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationMarkLockMissingBanner: { text: 'Cannot lock: {count} assessment(s) have missing marks.', description: 'Banner warning when locking fails due to incomplete marks.', surface: 'shell', maxChars: 70 },
  NLSEducationMarkLockMissingItem: { text: '{title} ({count} missing)', description: 'Missing assessment item link text with title and missing count.', surface: 'shell', maxChars: 50 },
  NLSEducationMarkLockPendingBanner: { text: 'Changes were made after lock. Recompute is required.', description: 'Banner warning when changed marks require recomputation.', surface: 'shell', maxChars: 65 },

  // ED9 Report Cards, Co-Scholastic, and ReportCardDocument
  NLSEducationTabReportCards: { text: 'Report cards', description: 'Report cards hub subtab label for teaching module.', surface: 'shell', maxChars: 20 },
  NLSEducationTabProgressReports: { text: 'Progress reports', description: 'Report cards hub subtab label for training module.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsTitle: { text: 'Report cards', description: 'Header title for Report Cards tab.', surface: 'shell', maxChars: 25 },
  NLSEducationReportCardsSubtitle: { text: 'Draft, review, and generate learner report cards and progress reports.', description: 'Subtitle for Report Cards tab.', surface: 'shell', maxChars: 75 },
  NLSEducationReportCardsNoAccessTitle: { text: 'Report cards unavailable', description: 'Title when user role cannot access report cards.', surface: 'shell', maxChars: 40 },
  NLSEducationReportCardsNoAccessBody: { text: 'You have no access to view or edit report cards.', description: 'Body when user role cannot access report cards.', surface: 'shell', maxChars: 80 },
  NLSEducationReportCardsSelectClass: { text: 'Select class', description: 'Placeholder for class select picker.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsSelectTerm: { text: 'Select term', description: 'Placeholder for term select picker.', surface: 'shell', maxChars: 20 },

  // View switcher in ReportCardsTab
  NLSEducationReportCardsViewRemarks: { text: 'Remarks & co-scholastic', description: 'View switcher tab for remarks and co-scholastic entry grid.', surface: 'shell', maxChars: 30 },
  NLSEducationReportCardsViewCards: { text: 'Report cards', description: 'View switcher tab for report cards list.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsViewPreview: { text: 'Preview', description: 'View switcher tab for report card document preview.', surface: 'shell', maxChars: 15 },

  // Remarks Grid
  NLSEducationReportCardsOverallCol: { text: 'Overall remark', description: 'Overall remark column header.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsLearnerCol: { text: 'Learner', description: 'Learner column header.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsRollNoCol: { text: 'Roll no.', description: 'Roll number column header.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsCharCount: { text: '{current}/{max}', description: 'Character count indicator for remark text input.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsPublishedBadge: { text: 'Published', description: 'Badge for learner whose card is published.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsSaving: { text: 'Saving…', description: 'Status text when remark or grade is saving.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsSaved: { text: 'Saved', description: 'Status text when remark or grade is saved.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsFailed: { text: 'Failed', description: 'Status text when remark or grade save failed.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsRetry: { text: 'Retry', description: 'Button to retry failed remark or grade save.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsSummary: { text: '{count} remarks saved', description: 'Live summary announcement when remarks are saved.', surface: 'shell', maxChars: 30 },
  NLSEducationReportCardsCoScholasticHeader: { text: 'Co-scholastic', description: 'Column group header for co-scholastic areas.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsNoSelectOption: { text: '-', description: 'Empty select option for unassigned co-scholastic grade.', surface: 'shell', maxChars: 5 },

  // Drafts generation
  NLSEducationReportCardsGenerateBtn: { text: 'Generate drafts', description: 'Button to generate report card drafts.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsGenerating: { text: 'Generating…', description: 'Button label while generating report cards.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsGenerateSuccess: { text: 'Report cards generated: {created} created, {refreshed} refreshed, {skippedPublished} skipped.', description: 'Success notification after generating report cards.', surface: 'shell', maxChars: 120 },
  NLSEducationReportCardsGenerateTermNotLocked: { text: 'All subjects in this class must be locked for this term before generating report cards.', description: 'Warning banner when subjects are unlocked.', surface: 'shell', maxChars: 90 },
  NLSEducationReportCardsUnlockedSubjects: { text: 'Unlocked subjects:', description: 'Header for unlocked subjects list.', surface: 'shell', maxChars: 25 },
  NLSEducationReportCardsGoToAssessments: { text: 'Go to assessments', description: 'Button/link to navigate to subject assessments.', surface: 'shell', maxChars: 25 },

  // Card list
  NLSEducationReportCardsStatusDraft: { text: 'Draft', description: 'Status pill for draft report card.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsStatusPublished: { text: 'Published v{version}', description: 'Status pill for published report card with version.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsStatusUpdated: { text: 'Updated', description: 'Status pill for republished report card.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsCardKindReportCard: { text: 'Report Card', description: 'Document kind label for report card.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsCardKindProgressReport: { text: 'Progress Report', description: 'Document kind label for progress report.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsPreviewBtn: { text: 'Preview', description: 'Button to open report card preview.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsBackToList: { text: 'Back to cards', description: 'Button to navigate back from preview to card list.', surface: 'shell', maxChars: 20 },
  NLSEducationReportCardsNoCards: { text: 'No report cards generated yet for this class and term.', description: 'Empty state message when no cards exist.', surface: 'shell', maxChars: 65 },
  NLSEducationReportCardsPublishClassBtn: { text: 'Publish class', description: 'Button to publish all report cards in a class.', surface: 'shell', maxChars: 25 },
  NLSEducationReportCardsPublishBtn: { text: 'Publish card', description: 'Button to publish a single report card.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsUnpublishBtn: { text: 'Unpublish', description: 'Button to unpublish a report card.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsPublishDialogTitle: { text: 'Publish report cards', description: 'Title of dialog confirming publication.', surface: 'shell', maxChars: 30 },
  NLSEducationReportCardsPublishSignerNotice: { text: 'Signer: {name} ({title})', description: 'Signer name and title in publish dialog.', surface: 'shell', maxChars: 50 },
  NLSEducationReportCardsPublishNoSigner: { text: 'No signer configured in settings.', description: 'Notice when no signer is set.', surface: 'shell', maxChars: 50 },
  NLSEducationReportCardsConfirmPublish: { text: 'Publish', description: 'Button to confirm report card publishing.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsUnpublishDialogTitle: { text: 'Unpublish report card', description: 'Title of dialog to unpublish a card.', surface: 'shell', maxChars: 30 },
  NLSEducationReportCardsUnpublishReasonPlaceholder: { text: 'Enter reason for unpublishing...', description: 'Placeholder for unpublish reason.', surface: 'shell', maxChars: 50 },
  NLSEducationReportCardsConfirmUnpublish: { text: 'Confirm unpublish', description: 'Button to confirm unpublishing.', surface: 'shell', maxChars: 25 },
  NLSEducationReportCardsCancel: { text: 'Cancel', description: 'Cancel action button.', surface: 'shell', maxChars: 15 },
  NLSEducationReportCardsLastUnpublishReason: { text: 'Last unpublish reason: {reason}', description: 'Display of last unpublish reason on draft card.', surface: 'shell', maxChars: 100 },
  NLSEducationReportCardsSignerMismatch: { text: 'Signer confirmation does not match configured signer.', description: 'Error message when signer mismatch occurs.', surface: 'shell', maxChars: 80 },
  NLSEducationReportCardsUnpublishDesc: { text: 'Unpublishing moves this card back to draft and revokes shared links.', description: 'Description in unpublish dialog.', surface: 'shell', maxChars: 90 },

  // Family Progress Tab
  NLSFamilyProgressTabTitle: { text: 'Progress', description: 'Title of family progress tab.', surface: 'shell', maxChars: 20 },
  NLSFamilyProgressNoCards: { text: 'No report cards published yet.', description: 'Empty state in family progress tab.', surface: 'shell', maxChars: 40 },
  NLSFamilyProgressEarlierVersions: { text: 'Earlier versions', description: 'Header for earlier versions list.', surface: 'shell', maxChars: 30 },
  NLSFamilyProgressVersionItem: { text: 'Version {version} ({date})', description: 'List item for earlier version.', surface: 'shell', maxChars: 40 },
  NLSFamilyProgressDownloadPdf: { text: 'Download PDF', description: 'Button to download report card PDF.', surface: 'shell', maxChars: 25 },
  NLSFamilyProgressUpdatedBadge: { text: 'Updated', description: 'Updated badge for republished card.', surface: 'shell', maxChars: 20 },
  NLSFamilyProgressRetry: { text: 'Retry', description: 'Retry button in family progress tab.', surface: 'shell', maxChars: 15 },
  NLSFamilyProgressLoadError: { text: 'Failed to load report cards.', description: 'Error loading family report cards.', surface: 'shell', maxChars: 40 },
  NLSFamilyProgressBackToLatest: { text: 'Back to latest', description: 'Button to return to latest published version.', surface: 'shell', maxChars: 20 },
  NLSFamilyProgressFrozenNotice: { text: 'Learner profile is archived or frozen.', description: 'Notice when learner is frozen.', surface: 'shell', maxChars: 50 },
  NLSFamilyProgressSelectTerm: { text: 'Select term', description: 'Aria label for family progress term selector.', surface: 'shell', maxChars: 20 },
  NLSFamilyProgressVersionLatest: { text: 'v{version} (latest)', description: 'Option label for latest report card version.', surface: 'shell', maxChars: 25 },
  NLSFamilyProgressVersionOption: { text: 'v{version}', description: 'Option label for report card version.', surface: 'shell', maxChars: 15 },
  NLSFamilyProgressCardLoadError: { text: 'Failed to load report card.', description: 'Error message when a specific report card fails to load.', surface: 'shell', maxChars: 40 },

  // Family Shell & Alerts
  NLSFamilyTabProgress: { text: 'Progress', description: 'Progress tab in family shell.', surface: 'shell', maxChars: 20 },
  NLSFamilyAlertEduReports: { text: 'Report cards', description: 'Toggle label for report card alerts.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertEduReportsDesc: { text: 'Get notified when new report cards are published', description: 'Description for report card alert toggle.', surface: 'shell', maxChars: 65 },
  NLSFamilyAlertEduFees: { text: 'Fees', description: 'Toggle label for fee and receipt alerts.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertEduFeesDesc: { text: 'Get notified about upcoming dues, overdue fees, and receipts', description: 'Description for fee alert toggle.', surface: 'shell', maxChars: 65 },
  NLSFamilyAlertEduBookings: { text: 'Bookings', description: 'Toggle label for booking and waitlist alerts.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertEduBookingsDesc: { text: 'Get notified about bookings, waitlist promotions, and expiry', description: 'Description for booking alert toggle.', surface: 'shell', maxChars: 65 },
  NLSFamilyAlertEduProgress: { text: 'Progress', description: 'Toggle label for progress and certificate alerts.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertEduProgressDesc: { text: 'Get notified about certificates, progress metrics, and level promotions', description: 'Description for progress alert toggle.', surface: 'shell', maxChars: 75 },
  NLSFamilyAlertEduAnnouncements: { text: 'Announcements', description: 'Toggle label for class and programme announcements.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertEduAnnouncementsDesc: { text: 'Get notified about class and programme announcements', description: 'Description for announcements alert toggle.', surface: 'shell', maxChars: 75 },
  NLSFamilyAlertEduMessages: { text: 'Messages', description: 'Toggle label for direct messages with teachers and trainers.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertEduMessagesDesc: { text: 'Get notified about direct messages from teachers and trainers', description: 'Description for direct messages alert toggle.', surface: 'shell', maxChars: 75 },
  NLSFamilyAlertEduEvents: { text: 'Events', description: 'Toggle label for school and class event alerts.', surface: 'shell', maxChars: 25 },
  NLSFamilyAlertEduEventsDesc: { text: 'Get notified about school events and RSVP requests', description: 'Description for school events alert toggle.', surface: 'shell', maxChars: 75 },

  // Shared Report Card Page
  NLSSharedReportCardSignInTitle: { text: 'Sign in to view report card', description: 'Title on shared report card sign-in screen.', surface: 'reports', maxChars: 45 },
  NLSSharedReportCardSignInPrompt: { text: 'Sign in with Google to view this report card.', description: 'Prompt on shared report card sign-in screen.', surface: 'reports', maxChars: 85 },
  NLSSharedReportCardSignInBtn: { text: 'Sign in with Google', description: 'Sign in with Google button.', surface: 'reports', maxChars: 30 },
  NLSSharedReportCardEmailNotAllowedTitle: { text: 'Access restricted', description: 'Title when email is not allowed.', surface: 'reports', maxChars: 30 },
  NLSSharedReportCardEmailNotAllowed: { text: 'This link is for the child\'s guardians; sign in with the email the school has on file.', description: 'Error notice when caller email is not on allowed list.', surface: 'reports', maxChars: 95 },
  NLSSharedReportCardSwitchAccount: { text: 'Switch account', description: 'Button to switch accounts.', surface: 'reports', maxChars: 25 },
  NLSSharedReportCardUnavailable: { text: 'This report card is unavailable or the share link has expired.', description: 'Notice when shared card is unavailable.', surface: 'reports', maxChars: 80 },
  NLSSharedReportCardDownloadPdf: { text: 'Download PDF', description: 'Button to download official PDF on shared page.', surface: 'reports', maxChars: 35 },

  // Report Center Subview
  NLSReportCenterNavReportCards: { text: 'Report cards', description: 'Report Center nav item for report cards.', surface: 'reports', maxChars: 25 },
  NLSReportCenterNavReportCardsSubtitle: { text: 'Browse published learner report cards', description: 'Subtitle for report cards nav item in Report Center.', surface: 'reports', maxChars: 45 },
  NLSReportCenterEduCardsClassCol: { text: 'Class', description: 'Class column in Report Center cards table.', surface: 'reports', maxChars: 20 },
  NLSReportCenterEduCardsTermCol: { text: 'Term', description: 'Term column in Report Center cards table.', surface: 'reports', maxChars: 20 },
  NLSReportCenterEduCardsLearnerCol: { text: 'Learner', description: 'Learner column in Report Center cards table.', surface: 'reports', maxChars: 20 },
  NLSReportCenterEduCardsStatusCol: { text: 'Status', description: 'Status column in Report Center cards table.', surface: 'reports', maxChars: 20 },
  NLSReportCenterEduCardsVersionCol: { text: 'Version', description: 'Version column in Report Center cards table.', surface: 'reports', maxChars: 20 },
  NLSReportCenterEduCardsActionsCol: { text: 'Actions', description: 'Actions column in Report Center cards table.', surface: 'reports', maxChars: 20 },
  NLSReportCenterEduCardsOpenBtn: { text: 'Open', description: 'Open button in Report Center cards table.', surface: 'reports', maxChars: 15 },
  NLSReportCenterEduCardsPdfBtn: { text: 'PDF', description: 'PDF download button in Report Center cards table.', surface: 'reports', maxChars: 15 },
  NLSReportCenterEduCardsNoCards: { text: 'No report cards found.', description: 'Empty state in Report Center cards table.', surface: 'reports', maxChars: 40 },
  NLSReportCenterEduCardsError: { text: 'Could not load report cards.', description: 'Error loading Report Center cards.', surface: 'reports', maxChars: 40 },
  NLSEducationReportRemarksNotTakingSubject: { text: 'Not taking this subject', description: 'Disabled text in report cards grid when learner is not taking this subject.', surface: 'shell', maxChars: 30 },
  NLSEducationReportGridLoadError: { text: 'Failed to load report card data', description: 'Error message when report card grid fails to load.', surface: 'shell', maxChars: 40 },
  NLSEducationReportSchemeLoadError: { text: 'Failed to load grading scheme', description: 'Error message when grading scheme fails to load.', surface: 'shell', maxChars: 40 },

  // Settings tab (Report cards section)
  NLSEducationSettingsReportCardsTitle: { text: 'Report cards', description: 'Section title for report cards in EduSettingsTab.', surface: 'shell', maxChars: 25 },
  NLSEducationSettingsReportCardsDesc: { text: 'Configure class ranking, signer details, and official signature image for report cards.', description: 'Description for report cards settings section.', surface: 'shell', maxChars: 95 },
  NLSEducationSettingsShowRankLabel: { text: 'Show class rank', description: 'Toggle label for showing class rank on report cards.', surface: 'shell', maxChars: 25 },
  NLSEducationSettingsShowRankHelp: { text: 'Displays class rank and total class size on learner report cards.', description: 'Helper text for show class rank toggle.', surface: 'shell', maxChars: 75 },
  NLSEducationSettingsSignerNameLabel: { text: 'Signer name', description: 'Label for signer name input.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsSignerNamePlaceholder: { text: 'e.g. Principal Jane Doe', description: 'Placeholder for signer name.', surface: 'shell', maxChars: 30 },
  NLSEducationSettingsSignerTitleLabel: { text: 'Signer title', description: 'Label for signer title input.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsSignerTitlePlaceholder: { text: 'e.g. Head of School', description: 'Placeholder for signer title.', surface: 'shell', maxChars: 30 },
  NLSEducationSettingsSignatureImageLabel: { text: 'Signature image', description: 'Label for signature image upload section.', surface: 'shell', maxChars: 25 },
  NLSEducationSettingsSignatureUploadBtn: { text: 'Upload signature', description: 'Button to upload signature image.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsSignatureRemoveBtn: { text: 'Remove signature', description: 'Button to remove current signature image.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsSignatureHint: { text: 'PNG or JPEG, up to 1 MB.', description: 'Format and size hint for signature upload.', surface: 'shell', maxChars: 30 },
  NLSEducationSettingsSignatureUploading: { text: 'Uploading signature…', description: 'Loading text while signature is uploading.', surface: 'shell', maxChars: 30 },
  NLSEducationSettingsSignatureAlt: { text: 'Signer signature', description: 'Alt text for signature preview image.', surface: 'shell', maxChars: 25 },
  NLSEducationSettingsReportCardsSaveBtn: { text: 'Save report card settings', description: 'Button to save report card settings in EduSettingsTab.', surface: 'shell', maxChars: 30 },
  NLSEducationSettingsReportCardsSaving: { text: 'Saving…', description: 'Button label while saving report card settings.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsReportCardsSaved: { text: 'Report card settings saved successfully.', description: 'Success notification for report card settings save.', surface: 'shell', maxChars: 50 },

  // Fee settings in EduSettingsTab (ED12)
  NLSEducationSettingsFeesSectionTitle: { text: 'Fees', description: 'Fees settings section title.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsFeesSectionDesc: { text: 'Manage staff fee duties, receipt sequence prefix, financial year, and reminders.', description: 'Fees settings section description.', surface: 'shell', maxChars: 120 },
  NLSEducationSettingsFeesDutyLabel: { text: 'Fees duty', description: 'Label for Fees duty staff picker.', surface: 'shell', maxChars: 25 },
  NLSEducationSettingsFeesDutyHelp: { text: 'Active staff members who can record payments and view fee plans and dues.', description: 'Help text for Fees duty.', surface: 'shell', maxChars: 100 },
  NLSEducationSettingsFeesDutyAddPlaceholder: { text: 'Select staff member to add…', description: 'Placeholder for staff selector.', surface: 'shell', maxChars: 40 },
  NLSEducationSettingsFeesDutyAddBtn: { text: 'Add duty', description: 'Button to add staff member to fees duty.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsFeesDutyRemoveBtn: { text: 'Remove', description: 'Button to remove staff member from fees duty.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsFeesDutyNone: { text: 'No staff assigned yet. Workspace owners and admins always have fee access.', description: 'Notice when no extra staff has fees duty.', surface: 'shell', maxChars: 100 },
  NLSEducationSettingsReceiptPrefixLabel: { text: 'Receipt prefix', description: 'Label for receipt prefix.', surface: 'shell', maxChars: 25 },
  NLSEducationSettingsReceiptPrefixPlaceholder: { text: 'e.g. RCP', description: 'Placeholder for receipt prefix.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsFyStartMonthLabel: { text: 'Financial year start month', description: 'Label for FY start month picker.', surface: 'shell', maxChars: 35 },
  NLSEducationSettingsTaxIdLabel: { text: 'Tax ID / GSTIN / PAN', description: 'Label for tax ID field.', surface: 'shell', maxChars: 30 },
  NLSEducationSettingsTaxIdPlaceholder: { text: 'Optional tax identifier', description: 'Placeholder for tax ID field.', surface: 'shell', maxChars: 35 },
  NLSEducationSettingsReminderDaysLabel: { text: 'Reminder days before', description: 'Label for reminder days before due date.', surface: 'shell', maxChars: 30 },
  NLSEducationSettingsReminderDaysHelp: { text: 'Send reminder N days before due date (0 to 30, default 3).', description: 'Help text for reminder days before.', surface: 'shell', maxChars: 75 },
  NLSEducationSettingsFeesSaveBtn: { text: 'Save fee settings', description: 'Button to save fee settings.', surface: 'shell', maxChars: 30 },
  NLSEducationSettingsFeesSaving: { text: 'Saving…', description: 'Saving indicator for fee settings.', surface: 'shell', maxChars: 20 },
  NLSEducationSettingsFeesSaved: { text: 'Fee settings saved successfully.', description: 'Success toast for fee settings.', surface: 'shell', maxChars: 50 },
  NLSEducationSettingsFeesSaveError: { text: 'Failed to save fee settings.', description: 'Error message for fee settings save.', surface: 'shell', maxChars: 40 },
  NLSEducationSettingsFeesRetryBtn: { text: 'Retry', description: 'Retry button for fee settings error.', surface: 'shell', maxChars: 15 },

  // Fees tab (plans & dues) (ED12)
  NLSEducationFeesTabPlans: { text: 'Plans', description: 'Plans subtab under Fees.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesTabDues: { text: 'Dues', description: 'Dues subtab under Fees.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesNewPlanBtn: { text: 'New plan', description: 'Button to create fee plan.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesShowArchived: { text: 'Show archived', description: 'Toggle label for showing archived plans.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesApplyToClassBtn: { text: 'Apply to class', description: 'Button to apply fee plan to class.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesEditPlanBtn: { text: 'Edit', description: 'Button to edit fee plan.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesArchivePlanBtn: { text: 'Archive', description: 'Button to archive fee plan.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesRestorePlanBtn: { text: 'Restore', description: 'Button to unarchive fee plan.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesPlanDialogTitleCreate: { text: 'Create fee plan', description: 'Title for create fee plan dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesPlanDialogTitleEdit: { text: 'Edit fee plan', description: 'Title for edit fee plan dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesPlanNameLabel: { text: 'Plan name', description: 'Label for plan name input.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesPlanNamePlaceholder: { text: 'e.g. Annual Tuition Fee', description: 'Placeholder for plan name input.', surface: 'shell', maxChars: 35 },
  NLSEducationFeesPlanKindLabel: { text: 'Plan kind', description: 'Label for plan kind selector.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesPlanKindFee: { text: 'Fee (Teaching)', description: 'Option for teaching fee plan.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesPlanKindPack: { text: 'Session pack (Training)', description: 'Option for session pack plan.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesPlanKindMembership: { text: 'Membership (Training)', description: 'Option for membership plan.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesPlanNodesLabel: { text: 'Applicable classes / programmes', description: 'Label for classes selection in plan dialog.', surface: 'shell', maxChars: 45 },
  NLSEducationFeesPlanItemsLabel: { text: 'Fee items', description: 'Label for fee items section.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesPlanAddItemBtn: { text: 'Add item', description: 'Button to add fee item row.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesPlanItemNamePlaceholder: { text: 'Item description', description: 'Placeholder for fee item label.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesPlanItemAmountPlaceholder: { text: 'Amount', description: 'Placeholder for fee item amount.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesPlanTotalAmount: { text: 'Total: {total}', description: 'Display total amount for plan.', surface: 'shell', maxChars: 35 },
  NLSEducationFeesPlanScheduleTypeLabel: { text: 'Schedule type', description: 'Label for schedule type dropdown.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesScheduleOneTime: { text: 'One-time', description: 'Schedule option one-time.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesScheduleMonthly: { text: 'Monthly', description: 'Schedule option monthly.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesScheduleTerm: { text: 'Per term', description: 'Schedule option per term.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesScheduleYearly: { text: 'Yearly', description: 'Schedule option yearly.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesScheduleCustom: { text: 'Custom instalments', description: 'Schedule option custom.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesDueDayLabel: { text: 'Due day of month (1–31)', description: 'Label for monthly due day.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesDueMonthLabel: { text: 'Due month', description: 'Label for yearly due month.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesPickTermsLabel: { text: 'Pick terms from scheme', description: 'Label for picking terms in fee schedule.', surface: 'shell', maxChars: 35 },
  NLSEducationFeesCustomInstalmentsLabel: { text: 'Instalments', description: 'Label for custom instalments list.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesAddInstalmentBtn: { text: 'Add instalment', description: 'Button to add instalment.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesInstalmentSumMismatch: { text: 'Instalments sum ({sum}) must equal fee total ({total}).', description: 'Validation error when custom instalments sum does not match plan total.', surface: 'shell', maxChars: 80 },
  NLSEducationFeesLateFeeTypeLabel: { text: 'Late fee rule', description: 'Label for late fee rule.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesLateFeeNone: { text: 'No late fee', description: 'Option for no late fee.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesLateFeeFlat: { text: 'Flat fee', description: 'Option for flat late fee.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesLateFeePercent: { text: 'Percentage fee', description: 'Option for percent late fee.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesLateFeeGraceDays: { text: 'Grace days', description: 'Label for grace days input.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesLateFeeAmountLabel: { text: 'Late fee amount', description: 'Label for flat late fee amount.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesLateFeeRateLabel: { text: 'Late fee rate (%)', description: 'Label for percentage late fee rate.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesPackSessionsLabel: { text: 'Total sessions', description: 'Label for pack total sessions.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesPackValidityDaysLabel: { text: 'Validity (days)', description: 'Label for pack validity in days.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesMembershipPeriodLabel: { text: 'Membership period', description: 'Label for membership duration.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesVersionConflictTitle: { text: 'Plan was updated', description: 'Title for version conflict banner.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesVersionConflictBody: { text: 'This fee plan was updated by someone else. Discard your unsaved changes and reload?', description: 'Body for version conflict prompt.', surface: 'shell', maxChars: 100 },
  NLSEducationFeesReloadBtn: { text: 'Reload latest', description: 'Button to reload after version conflict.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesConfirmArchiveTitle: { text: 'Archive fee plan', description: 'Confirmation title for archiving plan.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesConfirmArchiveBody: { text: 'Are you sure you want to archive "{name}"? New dues will not be created automatically.', description: 'Confirmation prompt for archiving plan.', surface: 'shell', maxChars: 120 },
  NLSEducationFeesApplySuccess: { text: '{assigned} learners assigned, {created} dues created.', description: 'Success notification after applying fee plan to class.', surface: 'shell', maxChars: 70 },
  NLSEducationFeesAdjustDialogTitle: { text: 'Adjust fee due', description: 'Title for adjustment dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesAdjustTypeLabel: { text: 'Adjustment type', description: 'Label for adjustment type select.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesAdjustValueLabel: { text: 'Value', description: 'Label for adjustment value input.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesAdjustReasonLabel: { text: 'Reason (required, 3–500 chars)', description: 'Label for adjustment reason textarea.', surface: 'shell', maxChars: 45 },
  NLSEducationFeesAdjustReasonPlaceholder: { text: 'State the justification for this adjustment or waiver…', description: 'Placeholder for adjustment reason textarea.', surface: 'shell', maxChars: 70 },
  NLSEducationFeesAdjustExistingTitle: { text: 'Existing adjustments', description: 'Section title for existing adjustments.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesAdjustRemoveBtn: { text: 'Remove', description: 'Button to remove adjustment.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesAdjustWaiverBtn: { text: 'Full waiver', description: 'Quick action button for full waiver.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesDueHistoryTitle: { text: 'Due history', description: 'Title for due audit history dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesDuesFilterStatus: { text: 'Filter by status', description: 'Placeholder for dues status filter.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesDuesFilterFrom: { text: 'From date', description: 'Placeholder for from date filter.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesDuesFilterTo: { text: 'To date', description: 'Placeholder for to date filter.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesOverdueBadge: { text: 'Overdue', description: 'Badge indicating overdue fee.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesPermissionDeniedTitle: { text: 'Access restricted', description: 'Title when user has no fee access permissions.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesPermissionDeniedBody: { text: 'Only administrators, staff with Fees duty, or class teachers can access fee information.', description: 'Body text when user lacks fee permissions.', surface: 'shell', maxChars: 110 },
  NLSEducationFeesNoPlansTitle: { text: 'No fee plans yet', description: 'Empty state title for plans view.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesNoPlansBody: { text: 'Create a fee plan to begin assigning schedules and generating dues.', description: 'Empty state body for plans view.', surface: 'shell', maxChars: 85 },
  NLSEducationFeesNoDuesTitle: { text: 'No fee dues found', description: 'Empty state title for dues view.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesNoDuesBody: { text: 'No fee dues match the current class or filters.', description: 'Empty state body for dues view.', surface: 'shell', maxChars: 60 },
  NLSEducationFeesThLearner: { text: 'Learner', description: 'Table header for learner name.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesThDueDate: { text: 'Due date', description: 'Table header for due date.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesThBase: { text: 'Base', description: 'Table header for base fee amount.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesThAdjustment: { text: 'Adjustment', description: 'Table header for adjustment amount.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesThLateFee: { text: 'Late fee', description: 'Table header for late fee amount.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesThPaid: { text: 'Paid', description: 'Table header for amount paid.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesThBalance: { text: 'Balance', description: 'Table header for remaining balance.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesThStatus: { text: 'Status', description: 'Table header for due status.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesThActions: { text: 'Actions', description: 'Table header for actions column.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesPlanStartDate: { text: 'Start date', description: 'Label for plan schedule start date.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesPlanEndDate: { text: 'End date', description: 'Label for plan schedule end date.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesActionAdjust: { text: 'Adjust', description: 'Button to adjust a fee due.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesActionHistory: { text: 'History', description: 'Button to view fee due history.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesClassPickerAll: { text: 'All classes', description: 'Picker option for all classes.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesStatusAll: { text: 'All statuses', description: 'Filter option for all statuses.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesYearPickerLabel: { text: 'Academic year', description: 'Label for academic year selector.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesProgrammePickerLabel: { text: 'Programme', description: 'Label for programme selector.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesActionSave: { text: 'Save', description: 'Save fee plan or adjustment.', surface: 'shell', maxChars: 16 },
  NLSEducationFeesActionCancel: { text: 'Cancel', description: 'Cancel dialog.', surface: 'shell', maxChars: 16 },
  NLSEducationFeesActionRetry: { text: 'Retry', description: 'Retry action on error.', surface: 'shell', maxChars: 16 },

  // ED12.b2: Receipts, Overdue, Payment Dialog & Family Fees
  NLSEducationFeesTabReceipts: { text: 'Receipts', description: 'Receipts subtab under Fees.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesTabOverdue: { text: 'Overdue', description: 'Overdue subtab under Fees.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesCurrencyNotice: { text: 'Set the workspace currency to view amounts and record payments.', description: 'Notice banner when workspace currency is not set.', surface: 'shell', maxChars: 75 },
  NLSEducationFeesSetCurrencyAction: { text: 'Set workspace currency', description: 'Link to settings to set workspace currency.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesErrorCurrencyNotSet: { text: 'Workspace currency is not set. Please configure currency in settings.', description: 'Error message when fee operation fails because currency is not set.', surface: 'shell', maxChars: 85 },
  NLSEducationFeesActionRecordPayment: { text: 'Record payment', description: 'Button to record fee payment.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesRecordPaymentTitle: { text: 'Record fee payment', description: 'Title for record payment dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesPaymentMethodLabel: { text: 'Payment method', description: 'Label for payment method selector.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesPaymentMethodCash: { text: 'Cash', description: 'Cash payment method option.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesPaymentMethodUpi: { text: 'UPI', description: 'UPI payment method option.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesPaymentMethodBank: { text: 'Bank transfer', description: 'Bank transfer payment method option.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesPaymentReferenceLabel: { text: 'Reference / Transaction ID', description: 'Label for reference input.', surface: 'shell', maxChars: 35 },
  NLSEducationFeesPaymentReferencePlaceholder: { text: 'Enter reference or transaction ID', description: 'Placeholder for reference input.', surface: 'shell', maxChars: 40 },
  NLSEducationFeesPaymentPaidOnLabel: { text: 'Payment date', description: 'Label for payment date input.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesPaymentNoteLabel: { text: 'Note (optional)', description: 'Label for payment note input.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesPaymentNotePlaceholder: { text: 'Add payment note…', description: 'Placeholder for payment note input.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesPaymentAmountReceivedLabel: { text: 'Amount received', description: 'Label for total amount received input.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesPaymentAllocationsLabel: { text: 'Dues allocation', description: 'Section header for dues allocation list.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesPaymentSuccessReceipt: { text: 'Receipt #{receiptNo} recorded successfully.', description: 'Success notification with receipt number.', surface: 'shell', maxChars: 55 },
  NLSEducationFeesPaymentOpenPdf: { text: 'Open PDF', description: 'Button to open receipt PDF.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesPaymentPdfPending: { text: 'Receipt PDF is being prepared…', description: 'Notice when receipt PDF is still pending render.', surface: 'shell', maxChars: 40 },
  NLSEducationFeesPaymentPdfFailed: { text: 'Receipt PDF generation failed.', description: 'Notice when receipt PDF failed render.', surface: 'shell', maxChars: 40 },
  NLSEducationFeesErrReferenceRequired: { text: 'Reference is required for UPI and Bank payments.', description: 'Validation error when reference is missing for electronic payment.', surface: 'shell', maxChars: 60 },
  NLSEducationFeesErrAmountExceedsBalance: { text: 'Amount cannot exceed open balance of {balance}.', description: 'Validation error when amount received exceeds total open balance.', surface: 'shell', maxChars: 60 },
  NLSEducationFeesErrZeroAmount: { text: 'Payment amount must be greater than zero.', description: 'Validation error when recorded payment total is zero.', surface: 'shell', maxChars: 50 },
  NLSEducationFeesAskOwnerCurrencyNotice: { text: 'Ask the workspace owner to set the currency.', description: 'Notice banner for non-owners when workspace currency is not set.', surface: 'shell', maxChars: 60 },
  NLSEducationFeesOwnerCurrencyNotice: { text: 'Set the workspace currency to view and record fees.', description: 'Notice banner for workspace owners when workspace currency is not set.', surface: 'shell', maxChars: 60 },
  NLSEducationFeesErrOverpayment: { text: 'Allocation cannot exceed the open due balance.', description: 'Validation error when allocated amount exceeds due balance.', surface: 'shell', maxChars: 60 },
  NLSEducationFeesErrFutureDate: { text: 'Payment date cannot be in the future.', description: 'Validation error when paidOn is after today.', surface: 'shell', maxChars: 50 },
  NLSEducationFeesErrDueNotOpen: { text: 'One or more fee dues are already paid or closed.', description: 'Error when paying closed due.', surface: 'shell', maxChars: 60 },
  NLSEducationFeesErrWrongLearner: { text: 'Selected dues belong to a different learner.', description: 'Error when dues learnerId does not match.', surface: 'shell', maxChars: 60 },
  NLSEducationFeesErrBudgetKeyMissing: { text: 'Budget encryption key is missing.', description: 'Error when budget HMAC key is missing on payment post.', surface: 'shell', maxChars: 50 },
  NLSEducationFeesReceiptsFilterMethod: { text: 'Filter by method', description: 'Placeholder for method filter select.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesReceiptsThReceiptNo: { text: 'Receipt #', description: 'Table header for receipt number.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesReceiptsThClass: { text: 'Class', description: 'Table header for class/programme name.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesReceiptsThAmount: { text: 'Amount', description: 'Table header for receipt total amount.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesReceiptsThMethod: { text: 'Method', description: 'Table header for payment method.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesReceiptsThReference: { text: 'Reference', description: 'Table header for payment reference.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesReceiptsThPaidOn: { text: 'Paid on', description: 'Table header for payment date.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesReceiptsThReceivedBy: { text: 'Received by', description: 'Table header for staff who recorded payment.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesReceiptsActionVoid: { text: 'Void', description: 'Button to void a fee receipt.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesReceiptsVoidModalTitle: { text: 'Void fee receipt', description: 'Title for confirm void modal.', surface: 'shell', maxChars: 30 },
  NLSEducationFeesReceiptsVoidModalBody: { text: 'Enter the reason for voiding receipt {receiptNo}. This action cannot be undone.', description: 'Body text explaining receipt void action.', surface: 'shell', maxChars: 95 },
  NLSEducationFeesReceiptsVoidReasonLabel: { text: 'Void reason (required, 3–500 chars)', description: 'Label for void reason textarea.', surface: 'shell', maxChars: 45 },
  NLSEducationFeesReceiptsVoidReasonPlaceholder: { text: 'State reason for voiding this receipt…', description: 'Placeholder for void reason input.', surface: 'shell', maxChars: 50 },
  NLSEducationFeesReceiptsVoidReasonError: { text: 'Reason must be between 3 and 500 characters.', description: 'Validation error when void reason length is outside 3-500 chars.', surface: 'shell', maxChars: 50 },
  NLSEducationFeesReceiptsVoidBadge: { text: 'Void', description: 'Status badge for voided receipt.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesReceiptsLoadMore: { text: 'Load more receipts', description: 'Button to page receipts list.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesReceiptsEmptyTitle: { text: 'No receipts found', description: 'Empty state title for receipts tab.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesReceiptsEmptyBody: { text: 'No payments have been recorded matching the selected filters.', description: 'Empty state body for receipts tab.', surface: 'shell', maxChars: 75 },
  NLSEducationFeesOverdueTotal: { text: 'Total overdue: {total}', description: 'Summary display of total overdue balance.', surface: 'shell', maxChars: 40 },
  NLSEducationFeesOverdueDays: { text: '{days} days', description: 'Display of number of days overdue.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesOverdueSortLabel: { text: 'Sort by', description: 'Label for overdue sort order.', surface: 'shell', maxChars: 15 },
  NLSEducationFeesOverdueSortDays: { text: 'Days overdue', description: 'Sort option days overdue.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesOverdueSortBalance: { text: 'Highest balance', description: 'Sort option highest balance.', surface: 'shell', maxChars: 20 },
  NLSEducationFeesOverdueEmptyTitle: { text: 'No overdue fees', description: 'Empty state title for overdue tab.', surface: 'shell', maxChars: 25 },
  NLSEducationFeesOverdueEmptyBody: { text: 'There are no overdue fee dues in the workspace.', description: 'Empty state body for overdue tab.', surface: 'shell', maxChars: 60 },
  NLSFamilyMoreFees: { text: 'Fees', description: 'Label for Fees row in Family More tab.', surface: 'shell', maxChars: 15 },
  NLSFamilyFeesHeading: { text: 'Fee dues & receipts', description: 'Header for Family Fees panel.', surface: 'shell', maxChars: 30 },
  NLSFamilyFeesTabUpcoming: { text: 'Upcoming', description: 'Subtab for upcoming family dues.', surface: 'shell', maxChars: 15 },
  NLSFamilyFeesTabOverdue: { text: 'Overdue', description: 'Subtab for overdue family dues.', surface: 'shell', maxChars: 15 },
  NLSFamilyFeesTabPaid: { text: 'Paid', description: 'Subtab for paid family dues.', surface: 'shell', maxChars: 15 },
  NLSFamilyFeesReceiptsHeading: { text: 'Receipts', description: 'Section header for family receipts.', surface: 'shell', maxChars: 20 },
  NLSFamilyFeesPayOnline: { text: 'Pay online', description: 'Button for online fee payment.', surface: 'shell', maxChars: 20 },
  NLSFamilyFeesPayOnlineComingSoon: { text: 'Online payment is coming soon.', description: 'Notice that online payment is coming soon.', surface: 'shell', maxChars: 40 },
  NLSFamilyFeesLateFeeBadge: { text: 'Late fee: {amount}', description: 'Badge indicating late fee component.', surface: 'shell', maxChars: 30 },
  NLSFamilyFeesEmptyDues: { text: 'No fee dues in this category.', description: 'Empty state when no dues in category.', surface: 'shell', maxChars: 40 },
  NLSFamilyFeesEmptyReceipts: { text: 'No payment receipts yet.', description: 'Empty state when no receipts.', surface: 'shell', maxChars: 30 },
  NLSFamilyFeesCurrencyNotice: { text: 'Workspace currency is not configured. Amounts cannot be displayed.', description: 'Notice for family fees when currency is unset.', surface: 'shell', maxChars: 80 },

  // Co-scholastic section in scheme dialog
  NLSEducationGradingSchemeCoScholasticHeader: { text: 'Co-scholastic areas', description: 'Section header for co-scholastic areas in scheme dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemeCoScholasticDesc: { text: 'Add school-defined co-scholastic areas and grading scale (e.g. Art, Sports, Discipline).', description: 'Description for co-scholastic section.', surface: 'shell', maxChars: 95 },
  NLSEducationGradingSchemeAreaName: { text: 'Area name', description: 'Input label for co-scholastic area name.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeAddArea: { text: 'Add area', description: 'Button to add a co-scholastic area.', surface: 'shell', maxChars: 15 },
  NLSEducationGradingSchemeRemoveArea: { text: 'Remove area', description: 'Button to remove a co-scholastic area.', surface: 'shell', maxChars: 15 },
  NLSEducationGradingSchemeAreaDefaultName: { text: 'Area {n}', description: 'Default name for added co-scholastic area.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeScaleHeader: { text: 'Co-scholastic scale', description: 'Subheader for co-scholastic scale.', surface: 'shell', maxChars: 25 },
  NLSEducationGradingSchemeScaleLabel: { text: 'Scale label', description: 'Input label for scale entry label.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeScaleDesc: { text: 'Description (optional)', description: 'Input label for scale entry description.', surface: 'shell', maxChars: 30 },
  NLSEducationGradingSchemeAddScale: { text: 'Add scale label', description: 'Button to add a co-scholastic scale item.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeRemoveScale: { text: 'Remove scale label', description: 'Button to remove a co-scholastic scale item.', surface: 'shell', maxChars: 20 },
  NLSEducationGradingSchemeValAreaNames: { text: 'Co-scholastic area names must be unique and 1–60 characters.', description: 'Validation error for co-scholastic area names.', surface: 'shell', maxChars: 65 },
  NLSEducationGradingSchemeValScaleLabels: { text: 'Co-scholastic scale must have 2–10 unique labels (1–8 characters each).', description: 'Validation error for co-scholastic scale labels.', surface: 'shell', maxChars: 75 },

  // Error mappings
  NLSEducationReportErrorInvalidSignature: { text: 'Invalid signature image. Please upload a valid PNG or JPEG under 1 MB.', description: 'Error message for invalid signature upload.', surface: 'shell', maxChars: 75 },
  NLSEducationReportErrorInvalidSettings: { text: 'Invalid report card settings. Signer name is required (1–80 characters).', description: 'Error message for invalid report card settings.', surface: 'shell', maxChars: 75 },
  NLSEducationGradingErrorAreaInUse: { text: 'This co-scholastic area has recorded grades and cannot be removed.', description: 'Error message when co-scholastic area is in use.', surface: 'shell', maxChars: 75 },
  NLSEducationGradingErrorAreaInUseNamed: { text: 'Co-scholastic area "{area}" has recorded grades and cannot be removed.', description: 'Error message naming in-use co-scholastic area.', surface: 'shell', maxChars: 80 },
  NLSEducationGradingErrorScaleInUse: { text: 'This co-scholastic scale label is in use and cannot be removed.', description: 'Error message when scale label is in use.', surface: 'shell', maxChars: 70 },
  NLSEducationGradingErrorScaleInUseNamed: { text: 'Co-scholastic scale label "{label}" is in use and cannot be removed.', description: 'Error message naming in-use scale label.', surface: 'shell', maxChars: 75 },
  NLSEducationGradingErrorInvalidCoScholastic: { text: 'Invalid co-scholastic configuration.', description: 'Error message for invalid co-scholastic config.', surface: 'shell', maxChars: 45 },
  NLSEducationGradingErrorInvalidCoScholasticArea: { text: 'Invalid co-scholastic area.', description: 'Error message for invalid co-scholastic area.', surface: 'shell', maxChars: 40 },
  NLSEducationGradingErrorInvalidCoScholasticLabel: { text: 'Invalid co-scholastic grade label.', description: 'Error message for invalid co-scholastic label.', surface: 'shell', maxChars: 45 },
  NLSEducationReportErrorCardPublished: { text: 'Cannot modify remarks or grades on a published report card.', description: 'Error message when trying to edit published card.', surface: 'shell', maxChars: 70 },
  NLSEducationReportErrorNotOnSubjectRoll: { text: 'Learner is not enrolled in this subject.', description: 'Error message when learner not on subject roll.', surface: 'shell', maxChars: 50 },
  NLSEducationReportErrorTermNotLocked: { text: 'All subjects in this class must be locked for this term before generating report cards.', description: 'Error message when subjects are unlocked.', surface: 'shell', maxChars: 90 },
  NLSEducationReportErrorInvalidRemarks: { text: 'Invalid remarks entered. Remarks must not exceed 1000 characters.', description: 'Error message for invalid remarks.', surface: 'shell', maxChars: 70 },
  NLSEducationReportErrorSignerMismatch: { text: 'Signer details do not match current workspace settings.', description: 'Error message for signer mismatch.', surface: 'shell', maxChars: 65 },
  NLSEducationReportErrorPdfNotReady: { text: 'PDF generation is in progress. Please try again shortly.', description: 'Error message when PDF is not ready.', surface: 'shell', maxChars: 60 },

  // ReportCardDocument
  NLSEducationDocumentReportCardTitle: { text: 'Report Card', description: 'Title of report card document.', surface: 'shell', maxChars: 20 },
  NLSEducationDocumentProgressReportTitle: { text: 'Progress Report', description: 'Title of progress report document.', surface: 'shell', maxChars: 20 },
  NLSEducationDocumentSchoolLogoAlt: { text: 'School logo', description: 'Alt text for school logo in report card.', surface: 'shell', maxChars: 20 },
  NLSEducationDocumentLearnerLabel: { text: 'Learner:', description: 'Learner detail label.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentRollNoLabel: { text: 'Roll no:', description: 'Roll number detail label.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentClassLabel: { text: 'Class:', description: 'Class detail label.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentTermLabel: { text: 'Term:', description: 'Term detail label.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentAcademicPerformance: { text: 'Academic Performance', description: 'Section title for subjects grade table.', surface: 'shell', maxChars: 30 },
  NLSEducationDocumentSubjectCol: { text: 'Subject', description: 'Subject column in document.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentCategoriesCol: { text: 'Categories', description: 'Categories column in document.', surface: 'shell', maxChars: 20 },
  NLSEducationDocumentTermPercentCol: { text: 'Term %', description: 'Term percent column in document.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentTermGradeCol: { text: 'Term Grade', description: 'Term grade column in document.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentYearPercentCol: { text: 'Year %', description: 'Year percent column in document.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentYearGradeCol: { text: 'Year Grade', description: 'Year grade column in document.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentAttendanceHeader: { text: 'Attendance', description: 'Attendance section header.', surface: 'shell', maxChars: 20 },
  NLSEducationDocumentAttendanceSummary: { text: 'Present: {present}, Late: {late}, Absent: {absent}, On Leave: {onLeave}, Excused: {excused} ({percent})', description: 'Attendance counts and percent summary.', surface: 'shell', maxChars: 140 },
  NLSEducationDocumentRankHeader: { text: 'Rank', description: 'Rank section header.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentRankSummary: { text: 'Rank {rank} of {classSize} ({overallPercent})', description: 'Class rank summary text.', surface: 'shell', maxChars: 45 },
  NLSEducationDocumentCoScholasticHeader: { text: 'Co-Scholastic', description: 'Co-scholastic section header in document.', surface: 'shell', maxChars: 20 },
  NLSEducationDocumentRemarksHeader: { text: 'Remarks', description: 'Remarks section header in document.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentOverallRemark: { text: 'Overall remark:', description: 'Overall remark label in document.', surface: 'shell', maxChars: 20 },
  NLSEducationDocumentDateLabel: { text: 'Date:', description: 'Date label next to signer.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentSignatureAlt: { text: 'Signature', description: 'Alt text for signature image.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentProgressSectionTitle: { text: 'Training Progress', description: 'Section title for training progress.', surface: 'shell', maxChars: 30 },
  NLSEducationDocumentProgressMetricsHeader: { text: 'Key Metrics', description: 'Header for progress metrics in document.', surface: 'shell', maxChars: 25 },
  NLSEducationDocumentProgressMetricCol: { text: 'Metric', description: 'Column header for metric name.', surface: 'shell', maxChars: 20 },
  NLSEducationDocumentProgressInitialCol: { text: 'Initial', description: 'Column header for initial metric value in term.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentProgressLatestCol: { text: 'Latest', description: 'Column header for latest metric value in term.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentProgressChangeCol: { text: 'Change', description: 'Column header for metric change.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentProgressTargetCol: { text: 'Target', description: 'Column header for metric target value.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentProgressLevelHeader: { text: 'Level', description: 'Header for level section in document.', surface: 'shell', maxChars: 15 },
  NLSEducationDocumentProgressCurrentLevel: { text: 'Current Level: {level}', description: 'Current level description.', surface: 'shell', maxChars: 40 },
  NLSEducationDocumentProgressCurrentLevelSince: { text: 'Current Level: {level} (since {since})', description: 'Current level with date.', surface: 'shell', maxChars: 50 },
  NLSEducationDocumentProgressPromotionsHeader: { text: 'Promotions in Term', description: 'Header for promotions list in document.', surface: 'shell', maxChars: 30 },
  NLSEducationDocumentProgressPracticeHeader: { text: 'Practice', description: 'Header for practice section in document.', surface: 'shell', maxChars: 20 },
  NLSEducationDocumentProgressPracticeSummary: { text: 'Total Practice: {minutes} minutes ({count} logs)', description: 'Practice minutes and log count summary.', surface: 'shell', maxChars: 60 },

  NLSEducationCalendarClassSessionFallback: { text: 'Class session', description: 'Fallback title for class session.', surface: 'shell', maxChars: 25 },
  NLSCalendarClassesFilterLabel: { text: 'Classes', description: 'Filter label for class sessions source.', surface: 'shell', maxChars: 20 },
  NLSCalendarClassesMine: { text: 'My classes', description: 'Filter option for teacher sessions.', surface: 'shell', maxChars: 20 },
  NLSCalendarClassesAll: { text: 'All classes', description: 'Filter option for all workspace classes.', surface: 'shell', maxChars: 20 },
  NLSCalendarClassesOff: { text: 'Off', description: 'Filter option to hide classes.', surface: 'shell', maxChars: 15 },
  NLSWorkspaceModulesToggleEnabled: { text: 'Enabled', description: 'Module enabled toggle status.', surface: 'settings', maxChars: 15 },
  NLSWorkspaceModulesToggleDisabled: { text: 'Disabled', description: 'Module disabled toggle status.', surface: 'settings', maxChars: 15 },
  NLSWorkspaceModulesToggleAria: { text: 'Toggle {module} module availability.', description: 'Module toggle aria label hint. {module} is module name.', surface: 'settings', maxChars: 60 },
  NLSWorkspaceModulesEnableStockFirst: { text: 'Enable Stock first', description: 'Helper Count prerequisite tooltip.', surface: 'settings', maxChars: 30 },
  NLSWorkspaceModulesEnableFormsFirst: { text: 'Enable Forms first', description: 'Helper Forms prerequisite tooltip.', surface: 'settings', maxChars: 30 },
  NLSWorkspaceModulesPermissionRequired: { text: 'Requires Edit workspace settings permission.', description: 'Permission required message for editing workspace modules.', surface: 'settings', maxChars: 60 },
  NLSWorkspaceModulesConfirmTurnOffTitle: { text: 'Turn off {module}?', description: 'Confirm dialog title when turning off a module. {module} is module name.', surface: 'settings', maxChars: 40 },
  NLSWorkspaceModulesConfirmTurnOffBody: { text: '{module} will be hidden for everyone in this workspace. Its data is kept and anything scheduled pauses until you turn it back on.', description: 'Confirm dialog body when turning off a module. {module} is module name.', surface: 'settings', maxChars: 150 },
  NLSWorkspaceModulesConfirmTurnOffConfirm: { text: 'Turn off', description: 'Confirm action label to turn off module.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModulesConfirmTurnOffCancel: { text: 'Keep on', description: 'Cancel action label to keep module on.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModulesStatusTurnedOn: { text: '{module} turned on.', description: 'Status message when module is turned on. {module} is module name.', surface: 'settings', maxChars: 40 },
  NLSWorkspaceModulesStatusTurnedOff: { text: '{module} turned off.', description: 'Status message when module is turned off. {module} is module name.', surface: 'settings', maxChars: 40 },
  NLSWorkspaceModulesErrorFallback: { text: 'Could not update resource setting.', description: 'Error fallback when updating a module fails.', surface: 'settings', maxChars: 50 },
  NLSWorkspaceModuleTurnedOffToast: { text: '{module} is turned off in this workspace.', description: 'Toast notification when a user accesses a turned-off workspace module.', surface: 'settings', maxChars: 70 },
  NLSWorkspaceModuleTurnOnAction: { text: 'Turn on', description: 'Action button label to turn on a module from toast.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceModuleTurnOnFirst: { text: 'Turn on {module} first.', description: 'Notice to turn on module before importing template.', surface: 'settings', maxChars: 40 },
  NLSAdminDashboardModulesNavDetail: { text: 'Add-ons, fun pack and helper app', description: 'Modules nav detail in workspace settings sidebar.', surface: 'settings', maxChars: 50 },
  NLSAdminDashboardModulesSectionSubtitle: { text: 'Toggle optional operational modules without deleting their existing data.', description: 'Modules section subtitle in admin settings header.', surface: 'settings', maxChars: 90 },
  NLSHelperFormsStatusPending: { text: 'Pending', description: 'Helper form request status filter.', surface: 'helper', maxChars: 20 },
  NLSHelperFormsStatusCompleted: { text: 'Completed', description: 'Helper form request status filter.', surface: 'helper', maxChars: 22 },
  NLSHelperFormsStatusCancelled: { text: 'Cancelled', description: 'Helper form request status filter.', surface: 'helper', maxChars: 22 },
  NLSHelperFormsStatusExpired: { text: 'Expired', description: 'Helper form request status filter.', surface: 'helper', maxChars: 20 },
  NLSHelperFormsStatusAll: { text: 'All', description: 'Helper form request status filter.', surface: 'helper', maxChars: 14 },
  NLSHelperFormsEmptyFilterTitle: { text: 'No forms in this filter.', description: 'Helper forms filter empty title.', surface: 'helper', maxChars: 50 },
  NLSHelperFormsEmptyFilterBody: { text: 'Switch the status filter above to see completed or cancelled requests.', description: 'Helper forms filter empty body.', surface: 'helper', maxChars: 95 },
  NLSHelperFormsFromRequester: { text: 'from {name}', description: 'Helper form request fallback source. {name} is requester display name.', surface: 'helper', maxChars: 70 },
  NLSHelperScanTitle: { text: 'Scan', description: 'Helper scan panel title.', surface: 'helper', maxChars: 18 },
  NLSHelperScanAction: { text: 'Scan', description: 'Helper scan start action.', surface: 'helper', maxChars: 18 },
  NLSHelperScanChooseTarget: { text: 'Choose target', description: 'Helper scan target chooser eyebrow.', surface: 'helper', maxChars: 30 },
  NLSHelperScanIn: { text: 'Scan in', description: 'Helper inventory scan-in label.', surface: 'helper', maxChars: 24 },
  NLSHelperScanOut: { text: 'Scan out', description: 'Helper inventory scan-out label.', surface: 'helper', maxChars: 24 },
  NLSHelperScanSearchPlaceholder: { text: 'Search workspace, tasks, subtasks, checklists', description: 'Helper scan target search placeholder.', surface: 'helper', maxChars: 70 },
  NLSHelperScanNoMatchingTarget: { text: 'No matching target.', description: 'Helper scan target empty state.', surface: 'helper', maxChars: 38 },
  NLSHelperScanIntro: { text: 'Scan a barcode, then choose where the scan belongs.', description: 'Helper scan intro body.', surface: 'helper', maxChars: 80 },
  NLSHelperScanTargetsBody: {
    text: 'Workspace, goals, tasks, subtasks, and assigned checklist items are available targets.',
    description: 'Helper scan target scope body.',
    surface: 'helper',
    maxChars: 125,
  },
  NLSHelperScanWorkspaceTitleFallback: { text: 'Workspace', description: 'Helper scan workspace title fallback.', surface: 'helper', maxChars: 28 },
  NLSHelperScanWorkspaceSubtitle: { text: 'Overall workspace inventory', description: 'Helper scan workspace target subtitle.', surface: 'helper', maxChars: 52 },
  NLSHelperScanChecklistSubtitle: { text: 'Checklist · {status}', description: 'Helper scan checklist target subtitle. {status} is a workflow status.', surface: 'helper', maxChars: 48 },
  NLSHelperStockCountEyebrow: { text: 'Stock audit', description: 'Helper stock count eyebrow.', surface: 'helper', maxChars: 28 },
  NLSHelperStockCountTitle: { text: 'Count inventory', description: 'Helper stock count title.', surface: 'helper', maxChars: 32 },
  NLSHelperStockCountScan: { text: 'Scan', description: 'Helper stock count scan button.', surface: 'helper', maxChars: 18 },
  NLSHelperStockCountManualPlaceholder: { text: 'Enter barcode', description: 'Helper stock count manual barcode placeholder.', surface: 'helper', maxChars: 34 },
  NLSHelperStockCountUseBarcodeAria: { text: 'Use barcode', description: 'Helper stock count manual submit accessible label.', surface: 'helper', maxChars: 32 },
  NLSHelperStockCountEmptyTitle: { text: 'Scan items to start a count round.', description: 'Helper stock count empty state title.', surface: 'helper', maxChars: 58 },
  NLSHelperStockCountEmptyBody: { text: 'Counts are suggestions. Admins approve before inventory changes.', description: 'Helper stock count empty body.', surface: 'helper', maxChars: 90 },
  NLSHelperStockCountRoundSummary: { text: '{count} item(s) queued - net {delta}', description: 'Helper stock count batch summary.', surface: 'helper', maxChars: 54 },
  NLSHelperStockCountSubmitting: { text: 'Submitting', description: 'Helper stock count submit busy label.', surface: 'helper', maxChars: 24 },
  NLSHelperStockCountSubmit: { text: 'Submit count', description: 'Helper stock count submit action.', surface: 'helper', maxChars: 28 },
  NLSHelperStockCountRemoveAria: { text: 'Remove {name}', description: 'Helper stock count remove row accessible label.', surface: 'helper', maxChars: 70 },
  NLSHelperStockCountNote: { text: 'Note', description: 'Helper stock count row note field.', surface: 'helper', maxChars: 18 },
  NLSHelperStockCountCounted: { text: 'Counted', description: 'Helper stock count quantity field.', surface: 'helper', maxChars: 20 },
  NLSHelperStockCountSystemQuantity: { text: 'System quantity at scan: {count}', description: 'Helper stock count system quantity text.', surface: 'helper', maxChars: 60 },
  NLSHelperStockCountUnknownBarcode: { text: 'No inventory item found for {barcode}.', description: 'Helper stock count unknown barcode toast.', surface: 'helper', maxChars: 70 },
  NLSHelperStockCountLookupFailed: { text: 'Could not look up inventory item.', description: 'Helper stock count lookup failure toast.', surface: 'helper', maxChars: 60 },
  NLSHelperStockCountQueued: { text: 'Stock count queued for review.', description: 'Helper stock count queued toast.', surface: 'helper', maxChars: 54 },
  NLSHelperStockCountQueueFailed: { text: 'Could not queue stock count.', description: 'Helper stock count queue failure toast.', surface: 'helper', maxChars: 54 },
  NLSHelperQuotaEyebrow: { text: 'My quota', description: 'Helper quota panel eyebrow.', surface: 'helper', maxChars: 26 },
  NLSHelperQuotaTitle: { text: 'Progress today', description: 'Helper quota panel title.', surface: 'helper', maxChars: 34 },
  NLSHelperQuotaLoading: { text: 'Loading quota...', description: 'Helper quota loading state.', surface: 'helper', maxChars: 34 },
  NLSHelperQuotaLoadFailed: { text: 'Could not load quota.', description: 'Helper quota error state.', surface: 'helper', maxChars: 42 },
  NLSHelperQuotaEmpty: { text: 'No active quota assigned.', description: 'Helper quota empty state.', surface: 'helper', maxChars: 44 },
  NLSHelperQuotaNoTargets: { text: 'This quota has no targets yet.', description: 'Helper quota empty-target state.', surface: 'helper', maxChars: 54 },
  NLSHelperQuotaTodayTitle: { text: "Today's assigned work would add", description: 'Helper quota projection heading.', surface: 'helper', maxChars: 58 },
  NLSHelperQuotaTodayEmpty: { text: 'No assigned work is scheduled for today.', description: 'Helper quota projection empty state.', surface: 'helper', maxChars: 64 },
  NLSHelperQuotaTargetTasks: { text: 'tasks', description: 'Helper quota target label for task completions.', surface: 'helper', maxChars: 20 },
  NLSHelperQuotaTargetSales: { text: 'sales', description: 'Helper quota target label for sales units.', surface: 'helper', maxChars: 20 },
  NLSHelperQuotaTargetChecklists: { text: 'checklists', description: 'Helper quota target label for checklist completions.', surface: 'helper', maxChars: 24 },
  NLSHelperQuotaTargetWeighted: { text: 'weighted progress', description: 'Helper quota target label for weighted progress.', surface: 'helper', maxChars: 34 },
  NLSHelperQuotaTargetHours: { text: 'hours', description: 'Helper quota target label for hours logged.', surface: 'helper', maxChars: 16 },
  NLSHelperReliabilityEyebrow: { text: 'Reliability', description: 'Helper reliability panel eyebrow.', surface: 'helper', maxChars: 28 },
  NLSHelperReliabilityTitle: { text: 'Vouches and signals', description: 'Helper reliability panel title.', surface: 'helper', maxChars: 40 },
  NLSHelperReliabilityLoadingShort: { text: '...', description: 'Helper reliability compact loading text.', surface: 'helper', maxChars: 8 },
  NLSHelperReliabilityPending: { text: 'New', description: 'Helper reliability pending snapshot label.', surface: 'helper', maxChars: 16 },
  NLSHelperReliabilityScans: { text: 'Scans', description: 'Helper reliability successful scans stat label.', surface: 'helper', maxChars: 18 },
  NLSHelperReliabilityDone: { text: 'Done', description: 'Helper reliability completed work stat label.', surface: 'helper', maxChars: 18 },
  NLSHelperReliabilityVouches: { text: 'Vouches', description: 'Helper reliability vouch stat label.', surface: 'helper', maxChars: 20 },
  NLSHelperReliabilityBlockers: { text: 'Blockers', description: 'Helper reliability blocker stat label.', surface: 'helper', maxChars: 22 },
  NLSHelperReliabilityStreak: { text: 'Completion signal', description: 'Helper reliability streak heading.', surface: 'helper', maxChars: 34 },
  NLSHelperReliabilityStreakBody: {
    text: '{count} completed in the last {days} days.',
    description: 'Helper reliability completion-window summary. {count} and {days} are numbers.',
    surface: 'helper',
    maxChars: 68,
  },
  NLSHelperReliabilityStreakPending: { text: 'No reliability window has been calculated yet.', description: 'Helper reliability missing-stats streak fallback.', surface: 'helper', maxChars: 72 },
  NLSHelperMeQuotaOffTitle: { text: 'Quota is off for this workspace', description: 'Helper quota disabled title.', surface: 'helper', maxChars: 48 },
  NLSHelperMeQuotaOffBody: { text: 'Your admin can enable it in workspace settings.', description: 'Helper quota disabled guidance.', surface: 'helper', maxChars: 65 },
  NLSHelperMeQuotaRetry: { text: "Couldn't load your quota — tap to retry", description: 'Helper quota retry action.', surface: 'helper', maxChars: 56 },
  NLSHelperMeReliabilityScansHint: { text: "Barcodes you've scanned this rolling 30-day window.", description: 'Helper reliability scans definition.', surface: 'helper', maxChars: 69 },
  NLSHelperMeReliabilityDoneHint: { text: "Tasks you've completed and had approved.", description: 'Helper reliability done definition.', surface: 'helper', maxChars: 56 },
  NLSHelperMeReliabilityVouchesHint: { text: 'Compliments admins or peers logged for your work.', description: 'Helper reliability vouches definition.', surface: 'helper', maxChars: 67 },
  NLSHelperMeReliabilityBlockersHint: { text: "Open + resolved blockers you've reported.", description: 'Helper reliability blockers definition.', surface: 'helper', maxChars: 56 },
  NLSHelperMeReliabilityKicksInAfter: {
    text: "Reliability score kicks in after 5 completed tasks. You're at {count}/5.",
    description: 'Helper reliability threshold placeholder.',
    surface: 'helper',
    maxChars: 98,
  },
  NLSHelperReliabilityRecentBlockers: { text: 'Recent blockers', description: 'Helper reliability recent blockers heading.', surface: 'helper', maxChars: 34 },
  NLSHelperReliabilityNoBlockers: { text: 'No open blockers on your current work.', description: 'Helper reliability empty blockers state.', surface: 'helper', maxChars: 64 },
  NLSHelperFieldPassDelegate: { text: 'Delegate this to a Field helper', description: 'Helper checklist detail Field Pass delegation action.', surface: 'helper', maxChars: 48 },
  NLSHelperFieldPassDelegateShort: { text: 'Field Pass', description: 'Short helper checklist detail Field Pass delegation label.', surface: 'helper', maxChars: 24 },
  NLSFieldPassQuickTitle: { text: 'Quick Field Pass', description: 'Shared quick Field Pass dialog title.', surface: 'common', maxChars: 36 },
  NLSFieldPassQuickBody: { text: 'Create a short-lived access link for this assigned work.', description: 'Shared quick Field Pass dialog body.', surface: 'common', maxChars: 80 },
  NLSFieldPassQuickUntitledTarget: { text: 'Assigned task', description: 'Fallback target title in quick Field Pass dialog.', surface: 'common', maxChars: 34 },
  NLSFieldPassQuickNoTargets: { text: 'Choose at least one assigned item.', description: 'Quick Field Pass no-target error.', surface: 'common', maxChars: 52 },
  NLSFieldPassQuickExpiresLabel: { text: 'Expires in', description: 'Quick Field Pass expiry selector label.', surface: 'common', maxChars: 28 },
  NLSFieldPassQuickExpires6h: { text: '6 hours', description: 'Quick Field Pass expiry option.', surface: 'common', maxChars: 20 },
  NLSFieldPassQuickExpires12h: { text: '12 hours', description: 'Quick Field Pass expiry option.', surface: 'common', maxChars: 20 },
  NLSFieldPassQuickExpires24h: { text: '1 day', description: 'Quick Field Pass expiry option.', surface: 'common', maxChars: 18 },
  NLSFieldPassQuickExpires72h: { text: '3 days', description: 'Quick Field Pass expiry option.', surface: 'common', maxChars: 18 },
  NLSFieldPassQuickDeleteOnCompletion: { text: 'Revoke when assigned work completes', description: 'Quick Field Pass delete-on-completion toggle label.', surface: 'common', maxChars: 60 },
  NLSFieldPassQuickCreate: { text: 'Create Field Pass', description: 'Quick Field Pass submit action.', surface: 'common', maxChars: 34 },
  NLSFieldPassQuickCreating: { text: 'Creating...', description: 'Quick Field Pass submit busy label.', surface: 'common', maxChars: 24 },
  NLSFieldPassQuickCopied: { text: 'Field Pass link copied.', description: 'Quick Field Pass copied status.', surface: 'common', maxChars: 44 },
  NLSFieldPassQuickCreateFailed: { text: 'Could not create Field Pass.', description: 'Quick Field Pass generic create failure.', surface: 'common', maxChars: 50 },
  NLSFieldPassQuickCopyLink: { text: 'Copy link', description: 'Quick Field Pass copy link action.', surface: 'common', maxChars: 24 },
  NLSFieldPassQuickShareLink: { text: 'Share link', description: 'Quick Field Pass share link action.', surface: 'common', maxChars: 24 },
  NLSFieldPassQuickTargetEmailLabel: { text: 'Address to (optional)', description: 'Quick Field Pass optional recipient email label.', surface: 'common', maxChars: 40 },
  NLSFieldPassQuickTargetEmailHint: {
    text: "If provided, this Field Pass appears in that user's Explore Incoming inbox after they sign in. Field Pass URL still works for anyone with the link.",
    description: 'Quick Field Pass optional recipient email hint.',
    surface: 'common',
    maxChars: 199,
  },
  NLSFieldPassAddressedTo: { text: 'Addressed to', description: 'Label shown next to the recipient email on a Field Pass row.', surface: 'common', maxChars: 28 },
  NLSExploreIncomingInvitesTitle: { text: 'Incoming invites', description: 'Explore Incoming Inbox section title.', surface: 'settings', maxChars: 34 },
  NLSExploreIncomingInvitesEmpty: { text: 'Nothing waiting for you here.', description: 'Explore Incoming Inbox empty state.', surface: 'settings', maxChars: 60 },
  NLSExploreIncomingTypeMember: { text: 'Member', description: 'Explore Incoming Inbox member-invite type chip.', surface: 'settings', maxChars: 20 },
  NLSExploreIncomingTypeFieldPass: { text: 'Field Pass', description: 'Explore Incoming Inbox field-pass type chip.', surface: 'settings', maxChars: 20 },
  NLSExploreIncomingFilterAll: { text: 'All', description: 'Explore Incoming Inbox filter: all types.', surface: 'settings', maxChars: 12 },
  NLSExploreIncomingFilterMember: { text: 'Member', description: 'Explore Incoming Inbox filter: member invites only.', surface: 'settings', maxChars: 12 },
  NLSExploreIncomingFilterFieldPass: { text: 'Field Pass', description: 'Explore Incoming Inbox filter: field passes only.', surface: 'settings', maxChars: 16 },
  NLSExploreIncomingActionAccept: { text: 'Accept', description: 'Explore Incoming Inbox accept action.', surface: 'settings', maxChars: 16 },
  NLSExploreIncomingActionDecline: { text: 'Decline', description: 'Explore Incoming Inbox decline action.', surface: 'settings', maxChars: 16 },
  NLSExploreIncomingActionCopyLink: { text: 'Copy link', description: 'Explore Incoming Inbox copy-link action for field pass.', surface: 'settings', maxChars: 18 },
  NLSExploreIncomingActionOpenNewTab: { text: 'Open in new tab', description: 'Explore Incoming Inbox open-in-new-tab action for field pass.', surface: 'settings', maxChars: 24 },
  NLSExploreIncomingActionDismiss: { text: 'Dismiss', description: 'Explore Incoming Inbox dismiss action for field pass.', surface: 'settings', maxChars: 16 },
  NLSExploreIncomingCapReached: {
    text: "You've reached the {cap}-workspace limit. Leave one before accepting another.",
    description: 'Explore Incoming Inbox inline error when accepting past cap.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSExploreIncomingFieldPassLinkMissing: {
    text: 'Link unavailable — ask the sender to reissue this Field Pass.',
    description: 'Explore Incoming Inbox field pass missing token error message.',
    surface: 'settings',
    maxChars: 80,
  },
  NLSExploreIncomingRefresh: { text: 'Refresh', description: 'Explore Incoming Inbox refresh action.', surface: 'settings', maxChars: 14 },
  NLSHelperSettingsTitle: { text: 'Settings', description: 'Helper settings heading.', surface: 'helper', maxChars: 24 },
  NLSHelperSettingsCloseAria: { text: 'Close settings', description: 'Accessible label for closing helper settings.', surface: 'helper', maxChars: 32 },
  NLSHelperSettingsAccount: { text: 'Account', description: 'Helper settings account section label.', surface: 'helper', maxChars: 22 },
  NLSHelperSettingsLanguage: { text: 'Language', description: 'Helper settings language section label.', surface: 'helper', maxChars: 22 },
  NLSHelperSettingsLanguageAria: { text: 'Helper language', description: 'Accessible label for helper language selector.', surface: 'helper', maxChars: 34 },
  NLSHelperSettingsLoadingLanguages: { text: 'Loading languages...', description: 'Helper settings loading languages status.', surface: 'helper', maxChars: 38 },
  NLSHelperSettingsTranslationCredits: { text: 'Translation is per-message and uses workspace credits.', description: 'Helper settings translation cost note.', surface: 'helper', maxChars: 80 },
  NLSHelperSettingsDelivery: { text: 'Delivery', description: 'Helper settings delivery section label.', surface: 'helper', maxChars: 22 },
  NLSHelperSettingsSigningOut: { text: 'Signing out...', description: 'Helper settings sign-out busy label.', surface: 'helper', maxChars: 30 },
  NLSHelperSettingsDialogAria: { text: 'Helper settings', description: 'Accessible label for helper settings dialog.', surface: 'helper', maxChars: 34 },
  // ── MobileSyncCenter (shared Helper + Member sync surface) ─────────────
  NLSMobileSyncHelperHeroEyebrow: { text: 'Helper Sync', description: 'Helper hero eyebrow in the sync center.', surface: 'helper', maxChars: 24 },
  NLSMobileSyncMemberHeroEyebrow: { text: 'Mobile sync', description: 'Member hero eyebrow in the sync center.', surface: 'helper', maxChars: 24 },
  NLSMobileSyncSyncing: { text: 'Syncing...', description: 'Sync-in-progress button label.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncSyncNow: { text: 'Sync Now', description: 'Trigger the sync worker manually.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncRetry: { text: 'Retry Sync', description: 'Retry a single failed sync operation.', surface: 'helper', maxChars: 26 },
  NLSMobileSyncDiscard: { text: 'Discard', description: 'Discard a pending sync operation.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncUseLatest: { text: 'Use Latest Workspace Version', description: 'Discard a conflicted sync operation in favor of the workspace copy.', surface: 'helper', maxChars: 44 },
  NLSMobileSyncOpenTask: { text: 'Open Task', description: 'Jump to the item a sync operation targets.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncPillOpen: { text: 'Open sync center', description: 'Sync pill action label (aria-label).', surface: 'helper', maxChars: 34 },
  NLSMobileSyncPillPendingCount: { text: '{count} pending', description: 'Sync pill count when items are queued. {count} is a number.', surface: 'helper', maxChars: 24 },
  NLSMobileSyncOperationsAria: { text: 'Mobile sync operations', description: 'Aria label for the sync operations list.', surface: 'helper', maxChars: 36 },
  NLSMobileSyncSyncedAtTime: { text: 'Synced {time}.', description: 'Completed-operation caption. {time} is a formatted time-of-day.', surface: 'helper', maxChars: 34 },
  NLSMobileSyncHeldSafely: { text: 'Held safely on this device until sync runs.', description: 'Queued-operation caption.', surface: 'helper', maxChars: 60 },
  NLSMobileSyncSending: { text: 'Sending this update to the workspace.', description: 'Syncing-operation caption.', surface: 'helper', maxChars: 58 },
  NLSMobileSyncMemberOfflinePolicy: { text: 'Offline policy', description: 'Member sync section eyebrow above the safe-offline card.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncSafeOfflineTitle: { text: 'Safe offline actions', description: 'Header for the offline-safe actions guide.', surface: 'helper', maxChars: 30 },
  NLSMobileSyncSafeOfflineBody: {
    text: 'Proof, comments, and status updates can queue. Meeting joins, live chat delivery, and AI need connection.',
    description: 'Body copy for the offline-safe actions guide.',
    surface: 'helper',
    maxChars: 142,
  },
  NLSMobileSyncFullCenterSubtitle: { text: "Full sync center · failed items retry when you're back online.", description: 'Helper sync hero subtitle.', surface: 'helper', maxChars: 86 },
  NLSMobileSyncBreakdownAria: { text: 'Sync queue breakdown by kind', description: 'Aria label for the per-mutation-kind pending breakdown.', surface: 'helper', maxChars: 41 },
  NLSMobileSyncBreakdownPendingSuffix: { text: 'pending', description: 'Suffix after each pending count in the breakdown table.', surface: 'helper', maxChars: 16 },
  NLSMobileSyncKindProofUpload: { text: 'Proof uploads', description: 'Breakdown row: CHECKLIST_PROOF_UPLOAD.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncKindStatusUpdate: { text: 'Status updates', description: 'Breakdown row: CHECKLIST_TOGGLE.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncKindComment: { text: 'Comments', description: 'Breakdown row: CHECKLIST_COMMENT.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncKindVoiceMemo: { text: 'Voice memos', description: 'Breakdown row: CHECKLIST_VOICE_MEMO.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncKindExpenseIntake: { text: 'Expense entries', description: 'Breakdown row: PENDING_EXPENSE_INTAKE.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncKindStockCount: { text: 'Stock counts', description: 'Breakdown row: STOCK_COUNT_ROUND.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncKindStockScan: { text: 'Stock scans', description: 'Breakdown row: STOCK_SCAN_SUGGESTION.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncKindWorkspaceActivity: { text: 'Activity updates', description: 'Breakdown row: WORKSPACE_ACTIVITY.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncKindEduBoarding: { text: 'Bus boarding', description: 'Breakdown row: EDU_BOARDING.', surface: 'helper', maxChars: 22 },
  NLSMobileSyncKindEduAttendanceMark: { text: 'Class attendance', description: 'Breakdown row: EDU_ATTENDANCE_MARK.', surface: 'helper', maxChars: 22 },

  NLSHelperSchoolSubtabRoll: { text: 'Roll', description: 'School tab subtab: roll call.', surface: 'helper', maxChars: 16 },
  NLSHelperSchoolSubtabGate: { text: 'Gate', description: 'School tab subtab: gate pickups.', surface: 'helper', maxChars: 16 },
  NLSHelperSchoolSubtabBus: { text: 'Bus', description: 'School tab subtab: transport routes.', surface: 'helper', maxChars: 16 },
  NLSHelperSchoolSubtabDesk: { text: 'Desk', description: 'School tab subtab: front desk / visitor log.', surface: 'helper', maxChars: 16 },
  NLSHelperSchoolSubtabsAria: { text: 'School sections', description: 'Aria label for school subtabs.', surface: 'helper', maxChars: 32 },

  NLSHelperSchoolRollTitle: { text: "Today's Roll", description: 'Helper school roll title.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolRollSelectSession: { text: 'Select session', description: 'Helper roll session selector label.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolRollEmpty: { text: 'No roll sessions assigned for today.', description: 'Empty state when no roll sessions assigned.', surface: 'helper', maxChars: 64 },
  NLSHelperSchoolRollSubmit: { text: 'Submit roll', description: 'Submit roll call button.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolRollSubmitting: { text: 'Submitting...', description: 'Submit roll in progress button label.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolRollSubmitted: { text: 'Roll submitted successfully', description: 'Roll submitted success banner.', surface: 'helper', maxChars: 40 },
  NLSHelperSchoolRollQueuedOffline: { text: 'Roll queued offline — will sync when connected', description: 'Offline roll queued banner.', surface: 'helper', maxChars: 64 },

  NLSHelperSchoolGateTitle: { text: 'Gate Pickups', description: 'Helper school gate title.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolGateSearchPlaceholder: { text: 'Search learner by name or roll no...', description: 'Learner search input placeholder.', surface: 'helper', maxChars: 48 },
  NLSHelperSchoolGateLearnerSelect: { text: 'Select learner', description: 'Learner select title.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolGateCollectorsTitle: { text: 'Authorized collectors on file', description: 'Collectors section title.', surface: 'helper', maxChars: 40 },
  NLSHelperSchoolGateNoCollectors: { text: 'No authorized collectors on file.', description: 'No collectors notice.', surface: 'helper', maxChars: 48 },
  NLSHelperSchoolGateRecordPickup: { text: 'Record pickup', description: 'Record pickup button.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolGateOneOffTitle: { text: 'Or enter one-off collector', description: 'One-off collector form heading.', surface: 'helper', maxChars: 36 },
  NLSHelperSchoolGateOneOffName: { text: 'Collector full name', description: 'One-off collector name label.', surface: 'helper', maxChars: 32 },
  NLSHelperSchoolGateOneOffRelation: { text: 'Relation to learner', description: 'One-off collector relation label.', surface: 'helper', maxChars: 32 },
  NLSHelperSchoolGateOfficeApprovalRequired: { text: 'Office must approve this pickup before departure.', description: 'Office approval required notice.', surface: 'helper', maxChars: 64 },
  NLSHelperSchoolGateRetryApproval: { text: 'Retry approval check', description: 'Retry approval button.', surface: 'helper', maxChars: 32 },
  NLSHelperSchoolGateOfflineWarning: { text: 'Gate pickups require an active connection for safety checks.', description: 'Offline warning for gate pickups.', surface: 'helper', maxChars: 72 },
  NLSHelperSchoolGatePickupRecorded: { text: 'Pickup recorded', description: 'Pickup recorded banner.', surface: 'helper', maxChars: 24 },

  NLSHelperSchoolBusTitle: { text: 'Transport Routes', description: 'Helper school bus title.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolBusSelectRoute: { text: 'Select route', description: 'Route select label.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolBusSelectLeg: { text: 'Leg', description: 'Route leg label.', surface: 'helper', maxChars: 16 },
  NLSHelperSchoolBusLegMorning: { text: 'Morning (AM)', description: 'AM route leg.', surface: 'helper', maxChars: 20 },
  NLSHelperSchoolBusLegAfternoon: { text: 'Afternoon (PM)', description: 'PM route leg.', surface: 'helper', maxChars: 20 },
  NLSHelperSchoolBusOpenRun: { text: 'Open route run', description: 'Open route run button.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolBusOpeningRun: { text: 'Opening run...', description: 'Opening run in progress.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolBusRunningLate: { text: 'Running late', description: 'Running late button.', surface: 'helper', maxChars: 20 },
  NLSHelperSchoolBusLateNoticeSent: { text: 'Late notice sent', description: 'Late notice sent message.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolBusCloseRun: { text: 'Close run', description: 'Close run button.', surface: 'helper', maxChars: 20 },
  NLSHelperSchoolBusCloseConfirmTitle: { text: 'Close Route Run', description: 'Close route run dialog title.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolBusCloseConfirmBody: { text: '{count} learners are not marked boarded. Close this run now?', description: 'Close route run confirmation body with count.', surface: 'helper', maxChars: 72 },
  NLSHelperSchoolBusConfirmClose: { text: 'Confirm & close', description: 'Confirm close button.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolBusCancel: { text: 'Cancel', description: 'Cancel button.', surface: 'helper', maxChars: 16 },
  NLSHelperSchoolBusBoarded: { text: 'Boarded', description: 'Boarded status.', surface: 'helper', maxChars: 16 },
  NLSHelperSchoolBusNotHere: { text: 'Not here', description: 'Not here status.', surface: 'helper', maxChars: 16 },

  NLSHelperSchoolDeskTitle: { text: 'Front Desk', description: 'Helper school desk title.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskLogVisitor: { text: 'Log Visitor', description: 'Log visitor heading.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskVisitorName: { text: 'Visitor name', description: 'Visitor name field.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskPurpose: { text: 'Purpose of visit', description: 'Visitor purpose field.', surface: 'helper', maxChars: 32 },
  NLSHelperSchoolDeskMeetingWhom: { text: 'Meeting whom', description: 'Meeting whom field.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskPassNo: { text: 'Badge / Pass #', description: 'Badge number field.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskCheckIn: { text: 'Check in visitor', description: 'Check in visitor button.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskCheckOut: { text: 'Check out', description: 'Check out visitor button.', surface: 'helper', maxChars: 20 },
  NLSHelperSchoolDeskCheckedOut: { text: 'Checked out', description: 'Visitor checked out banner.', surface: 'helper', maxChars: 20 },
  NLSHelperSchoolDeskActiveVisitors: { text: 'Active Visitors', description: 'Active visitors heading.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskNoVisitors: { text: 'No active visitors currently checked in.', description: 'Empty active visitors state.', surface: 'helper', maxChars: 56 },
  NLSHelperSchoolDeskWalkInTitle: { text: 'Walk-In Enquiry', description: 'Walk-in enquiry heading.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskPhone: { text: 'Contact phone', description: 'Phone field.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskInterest: { text: 'Course or grade of interest', description: 'Course or grade of interest field.', surface: 'helper', maxChars: 40 },
  NLSHelperSchoolDeskNotes: { text: 'Notes / remarks', description: 'Notes field.', surface: 'helper', maxChars: 24 },
  NLSHelperSchoolDeskSubmitEnquiry: { text: 'Log walk-in enquiry', description: 'Submit walk-in enquiry button.', surface: 'helper', maxChars: 28 },
  NLSHelperSchoolDeskEnquiryLogged: { text: 'Enquiry logged successfully', description: 'Walk-in enquiry logged success.', surface: 'helper', maxChars: 40 },
  NLSHelperSchoolGateNoLearnersFound: { text: 'No learners found', description: 'Empty state when no learners match gate search.', surface: 'helper', maxChars: 30 },
  NLSHelperSchoolGateClass: { text: 'Class: {class}', description: 'Learner class or batch label. {class} is class name.', surface: 'helper', maxChars: 30 },
  NLSHelperSchoolGateSelectArrow: { text: 'Select →', description: 'Select learner action button in gate list.', surface: 'helper', maxChars: 20 },
  NLSHelperSchoolGateChangeLearner: { text: 'Change', description: 'Change selected gate learner button.', surface: 'helper', maxChars: 15 },
  NLSHelperSchoolRollLoading: { text: 'Loading roll...', description: 'Loading state message for roll call subtab.', surface: 'helper', maxChars: 25 },
  NLSHelperSchoolBusRunId: { text: 'Run ID: {runId}', description: 'Bus run identifier display. {runId} is the run ID.', surface: 'helper', maxChars: 40 },
  NLSHelperSchoolBusStopOrder: { text: 'Order: {order}', description: 'Bus stop sequence order. {order} is the order number.', surface: 'helper', maxChars: 20 },
  NLSHelperSchoolDeskPassMeta: { text: 'Pass: {passNo} · In: {checkInAt}', description: 'Active visitor pass number and check-in time meta.', surface: 'helper', maxChars: 50 },
  NLSHelperSchoolRollLoadError: { text: 'Could not load roll', description: 'Error message when loading roll fails.', surface: 'helper', maxChars: 30 },
  NLSHelperSchoolRollSubmitError: { text: 'Failed to submit roll', description: 'Error message when submitting roll fails.', surface: 'helper', maxChars: 30 },
  NLSHelperSchoolDeskLogVisitorError: { text: 'Could not log visitor', description: 'Error message when logging visitor fails.', surface: 'helper', maxChars: 30 },
  NLSHelperSchoolDeskLogEnquiryError: { text: 'Could not log enquiry', description: 'Error message when logging enquiry fails.', surface: 'helper', maxChars: 30 },
  NLSHelperSchoolDeskVisitorCheckedIn: { text: 'Visitor checked in', description: 'Visitor checked in success feedback.', surface: 'helper', maxChars: 30 },

  // Field Pass School mode (EM10.b, EM9b-EM10b.fix1)
  NLSFieldPassSchoolEndsAt: { text: 'Ends at {time}', description: 'Pass end time indicator on Field Pass landing. {time} is the formatted time.', surface: 'common', maxChars: 40 },
  NLSFieldPassSchoolExpired: { text: 'This Field Pass has expired.', description: 'Notice shown when a school field pass has expired.', surface: 'common', maxChars: 40 },
  NLSFieldPassSchoolDraftRestored: { text: 'Draft restored from this device', description: 'Notice shown when an offline/local draft is restored.', surface: 'common', maxChars: 50 },
  NLSFieldPassSchoolSubmittedNotice: { text: 'Sent for the class teacher to accept', description: 'Confirmation notice that a school field pass submission has been sent for teacher acceptance.', surface: 'common', maxChars: 50 },
  NLSFieldPassSchoolHeadingSession: { text: 'Substitute teacher roll call', description: 'Header subtitle for substitute teacher session roll call.', surface: 'common', maxChars: 40 },
  NLSFieldPassSchoolHeadingExam: { text: 'Exam Duty', description: 'Header subtitle for exam duty without hall.', surface: 'common', maxChars: 30 },
  NLSFieldPassSchoolHeadingExamWithHall: { text: 'Exam Duty · Hall: {hall}', description: 'Header subtitle for exam duty with hall. {hall} is the hall name.', surface: 'common', maxChars: 50 },
  NLSFieldPassSchoolHeadingCoach: { text: 'Visiting coach session metrics', description: 'Header subtitle for visiting coach session metrics.', surface: 'common', maxChars: 40 },
  NLSFieldPassSchoolEmptySession: { text: 'No learners in session', description: 'Empty state message when no learners are in the roll call session.', surface: 'common', maxChars: 40 },
  NLSFieldPassSchoolEmptyAssigned: { text: 'No learners assigned', description: 'Empty state message when no learners are assigned to the pass.', surface: 'common', maxChars: 40 },
  NLSFieldPassSchoolSubmitting: { text: 'Submitting...', description: 'Busy button label while school field pass is submitting.', surface: 'common', maxChars: 20 },
  NLSFieldPassSchoolSubmitRoll: { text: 'Submit roll', description: 'Action button label to submit substitute teacher roll call.', surface: 'common', maxChars: 20 },
  NLSFieldPassSchoolSubmitExamSeats: { text: 'Submit exam seats', description: 'Action button label to submit exam hall seat assignments.', surface: 'common', maxChars: 25 },
  NLSFieldPassSchoolSubmitSessionMetrics: { text: 'Submit session metrics', description: 'Action button label to submit visiting coach session metrics.', surface: 'common', maxChars: 30 },
  NLSFieldPassSchoolPlaceholderSeat: { text: 'Seat', description: 'Placeholder for exam seat input.', surface: 'common', maxChars: 15 },
  NLSFieldPassSchoolPlaceholderScoreValue: { text: 'Score / Value', description: 'Placeholder for coach metric score or value input.', surface: 'common', maxChars: 20 },
  NLSFieldPassSchoolPlaceholderCoachNotes: { text: 'Coach observations / notes...', description: 'Placeholder for coach observations and notes input.', surface: 'common', maxChars: 40 },
  NLSFieldPassSchoolRollNumber: { text: 'Roll: {rollNo}', description: 'Roll number label. {rollNo} is the learner roll number.', surface: 'common', maxChars: 25 },
  NLSFieldPassSchoolStatusPresent: { text: 'Present', description: 'Exam attendance status Present button.', surface: 'common', maxChars: 15 },
  NLSFieldPassSchoolStatusAbsent: { text: 'Absent', description: 'Exam attendance status Absent button.', surface: 'common', maxChars: 15 },
  NLSFieldPassSchoolAriaSeatFor: { text: 'Seat for {name}', description: 'Accessible label for exam seat input. {name} is student name.', surface: 'common', maxChars: 60 },
  NLSFieldPassSchoolAriaMetricFor: { text: '{metric} for {name}', description: 'Accessible label for coach metric input. {metric} is metric name, {name} is student name.', surface: 'common', maxChars: 60 },
  NLSFieldPassSchoolAriaNotesFor: { text: 'Notes for {name}', description: 'Accessible label for coach notes input. {name} is student name.', surface: 'common', maxChars: 60 },
  NLSFieldPassSchoolErrorSubmitRoll: { text: 'Could not submit roll.', description: 'Error message when submitting roll fails.', surface: 'common', maxChars: 40 },
  NLSFieldPassSchoolErrorSubmitExamSeats: { text: 'Could not submit exam seats.', description: 'Error message when submitting exam seats fails.', surface: 'common', maxChars: 40 },
  NLSFieldPassSchoolErrorSubmitSessionMetrics: { text: 'Could not submit session metrics.', description: 'Error message when submitting session metrics fails.', surface: 'common', maxChars: 40 },
  NLSMobileSyncEmptyTitle: { text: 'Nothing waiting', description: 'Sync center empty state title.', surface: 'helper', maxChars: 24 },
  NLSMobileSyncEmptyBody: {
    text: 'Your {role} is clear. New offline actions will appear here.',
    description: 'Sync center empty state body. {role} is a localized noun phrase.',
    surface: 'helper',
    maxChars: 80,
  },
  NLSMobileSyncRoleHelper: { text: 'field work', description: 'Role noun injected into the sync empty state body for helpers.', surface: 'helper', maxChars: 20 },
  NLSMobileSyncRoleMember: { text: 'mobile work', description: 'Role noun injected into the sync empty state body for members.', surface: 'helper', maxChars: 20 },
  NLSMobileSettingsHelperAppLanguageTitle: { text: 'Helper app language', description: 'Helper mobile settings row title for UI-only app language.', surface: 'helper', maxChars: 42 },
  NLSMobileSettingsHelperAppLanguageBody: {
    text: 'Changes Helper app buttons, settings, and UI only. Chat translation is separate.',
    description: 'Helper mobile settings row body for UI-only app language.',
    surface: 'helper',
    maxChars: 120,
  },
  NLSMobileSettingsHelperAppLanguageSaved: { text: 'Helper app language saved.', description: 'Helper mobile settings status after saving UI-only app language.', surface: 'helper', maxChars: 50 },
  NLSMobileSettingsAlertsOn: { text: 'Alerts are on', description: 'Status indicating push alerts are granted.', surface: 'helper', maxChars: 32 },
  NLSMobileSettingsPermissionStatus: { text: 'Permission: {permission}', description: 'Push notification permission status.', surface: 'helper', maxChars: 36 },
  NLSMobileSettingsEnableAlerts: { text: 'Enable', description: 'Button to enable device push alerts.', surface: 'helper', maxChars: 16 },
  NLSHelperFirstRunLanguageTitle: { text: 'Choose Helper app language', description: 'Helper first-run language prompt title.', surface: 'helper', maxChars: 48 },
  NLSHelperFirstRunLanguageBody: {
    text: 'Pick the language for Helper app buttons and settings. Task names and chat translation stay separate.',
    description: 'Helper first-run language prompt body.',
    surface: 'helper',
    maxChars: 137,
  },
  NLSHelperFirstRunLanguageIndian: { text: 'Indian languages', description: 'Helper first-run language group for Indian languages.', surface: 'helper', maxChars: 32 },
  NLSHelperFirstRunLanguageInternational: { text: 'International languages', description: 'Helper first-run language group for international languages.', surface: 'helper', maxChars: 38 },
  NLSHelperFirstRunLanguageCurrent: { text: 'Current', description: 'Helper first-run language current selection chip.', surface: 'helper', maxChars: 18 },
  NLSHelperFirstRunLanguageSkip: { text: 'Skip / use workspace default', description: 'Helper first-run language skip action.', surface: 'helper', maxChars: 44 },
  NLSHelperFirstRunLanguageSaving: { text: 'Saving...', description: 'Helper first-run language save busy label.', surface: 'helper', maxChars: 24 },
  NLSHelperFirstRunLanguageError: { text: 'Could not save language.', description: 'Helper first-run language fallback error.', surface: 'helper', maxChars: 46 },
  NLSCapacitorRedirecting: { text: 'Redirecting...', description: 'Temporary native route redirect message.', surface: 'capacitor', maxChars: 34 },
  NLSCapacitorHelperAccessDenied: {
    text: 'Helper accounts can only use the Helper app routes: /helper/dashboard, /helper/calendar, /helper/meetings, /helper/chat, /helper/controls, and /helper/profile.',
    description: 'Access-denied copy when a helper account opens the desktop console.',
    surface: 'capacitor',
    maxChars: 180,
  },
  NLSHelperAppHelperOnly: {
    text: 'This app is for Helper accounts. Sign out and sign in with your Helper phone number.',
    description: 'Helper app access-denied message when signed in as a non-helper in wrapper mode.',
    surface: 'helper',
    maxChars: 120,
  },
  NLSHelperMeetingsStatUpcoming: { text: 'Upcoming', description: 'Helper meetings stats label: upcoming count.', surface: 'helper', maxChars: 14 },
  NLSHelperMeetingsStatToday: { text: 'Today', description: 'Helper meetings stats label: due today count.', surface: 'helper', maxChars: 14 },
  NLSHelperMeetingsStatReady: { text: 'Ready', description: 'Helper meetings stats label: readiness count.', surface: 'helper', maxChars: 14 },

  // ── MemberChat mobile surface + shared reply feature ────────────────────
  NLSChatActionReply: { text: 'Reply', description: 'Chat message action: reply to this message.', surface: 'chat', maxChars: 16 },
  NLSChatActionCopy: { text: 'Copy', description: 'Chat message action: copy text.', surface: 'chat', maxChars: 16 },
  NLSChatActionCancelReply: { text: 'Cancel reply', description: 'Chat composer action: cancel the in-progress reply.', surface: 'chat', maxChars: 24 },
  NLSChatActionSummarize: { text: 'Summarize with AI', description: 'Chat message action: ask AI to summarize this message.', surface: 'chat', maxChars: 28 },
  NLSChatActionAttach: { text: 'Attach', description: 'Chat composer action: attach a file.', surface: 'chat', maxChars: 16 },
  NLSChatActionOpenAiMagic: { text: 'Open AI Magic', description: 'Sheet CTA to open the AI Magic surface from chat.', surface: 'chat', maxChars: 22 },
  NLSChatActionStartConversation: { text: 'Start conversation', description: 'Empty-channel CTA that pre-fills a first message when channel metadata is available.', surface: 'chat', maxChars: 28 },
  NLSChatActionsForMessage: { text: 'Message actions', description: 'Aria label for the long-press message actions dialog.', surface: 'chat', maxChars: 28 },
  NLSChatSelectChannel: { text: 'Pick a channel', description: 'Header title when no channel is selected.', surface: 'chat', maxChars: 24 },
  NLSChatSelectChannelSub: {
    text: 'Choose a channel from the switcher to start reading and posting messages.',
    description: 'Empty-state helper when no channel is selected.',
    surface: 'chat',
    maxChars: 110,
  },
  NLSChatEmptyChannelTitle: { text: 'Nothing in {channel} yet', description: 'Empty-state title for a chosen channel with no messages. {channel} is a channel name.', surface: 'chat', maxChars: 44 },
  NLSChatEmptyChannelSub: { text: 'Say hello or share an update — everyone in the channel will see it.', description: 'Empty-state helper text under the title.', surface: 'chat', maxChars: 110 },
  NLSChatSheetChannelsTitle: { text: 'Channels', description: 'Bottom-sheet title for the channel switcher.', surface: 'chat', maxChars: 20 },
  NLSChatSheetMembersTitle: { text: 'Members', description: 'Bottom-sheet title for the members list.', surface: 'chat', maxChars: 20 },
  NLSChatSheetAiTitle: { text: 'AI Magic', description: 'Bottom-sheet title for the AI history / summary.', surface: 'chat', maxChars: 20 },
  NLSChatToastCopied: { text: 'Copied to clipboard', description: 'Toast after a chat message text is copied.', surface: 'chat', maxChars: 30 },
  NLSChatToastCopyFailed: { text: 'Could not copy — try selecting the text.', description: 'Toast when clipboard copy fails.', surface: 'chat', maxChars: 60 },
  NLSChatToastTranslateFailed: { text: 'Translation failed. Try again.', description: 'Toast when translation fails.', surface: 'chat', maxChars: 44 },
  NLSChatToastMoreComingSoon: { text: 'More actions are coming here soon.', description: 'Toast when the overflow menu is tapped in the pre-release build.', surface: 'chat', maxChars: 46 },
  NLSChatSendFailed: { text: 'Could not send the message.', description: 'Toast when sendMessage rejects with an unknown error.', surface: 'chat', maxChars: 44 },
  NLSChatDayToday: { text: 'Today', description: 'Sticky day divider label for messages sent today.', surface: 'chat', maxChars: 16 },
  NLSChatDayYesterday: { text: 'Yesterday', description: 'Sticky day divider label for messages sent yesterday.', surface: 'chat', maxChars: 16 },
  NLSChatDayJustNow: { text: 'Just now', description: 'Day divider fallback for messages whose timestamp has not resolved yet.', surface: 'chat', maxChars: 16 },
  NLSChatLabelReplyingTo: { text: 'Replying to {author}', description: 'Reply-chip label above the composer. {author} is the parent author name.', surface: 'chat', maxChars: 40 },
  NLSChatAriaReplyingTo: { text: 'Replying to {author}', description: 'Aria label for the inline reply quote and composer chip.', surface: 'chat', maxChars: 40 },
  NLSChatAriaOpenChannels: { text: 'Open channel switcher', description: 'Aria label for the header hamburger button.', surface: 'chat', maxChars: 34 },
  NLSChatAriaOpenMembers: { text: 'Open channel members', description: 'Aria label for the header title button that opens the members sheet.', surface: 'chat', maxChars: 34 },
  NLSChatAriaOpenAiMagic: { text: 'Open AI Magic', description: 'Aria label for the header sparkle button.', surface: 'chat', maxChars: 24 },
  NLSChatAriaMore: { text: 'More options', description: 'Aria label for the header overflow menu.', surface: 'chat', maxChars: 24 },
  NLSChatComposerPlaceholder: { text: 'Message {channel}', description: 'Composer placeholder. {channel} is the active channel name.', surface: 'chat', maxChars: 44 },
  NLSChatLabelOnline: { text: 'online', description: 'Header sub-line label, e.g. "4 online".', surface: 'chat', maxChars: 12 },
  NLSChatLabelJustYou: { text: 'Just you here', description: 'Header sub-line when there is only one member in the channel.', surface: 'chat', maxChars: 22 },
  NLSChatChannelFilterPlaceholder: { text: 'Search channels', description: 'Channel switcher search placeholder.', surface: 'chat', maxChars: 26 },
  NLSChatNoChannelMatches: { text: 'No channels match your search.', description: 'Empty result in the channel switcher search.', surface: 'chat', maxChars: 44 },
  NLSChatUnreadLabel: { text: 'Unread messages', description: 'Aria label for the unread badge on channel rows.', surface: 'chat', maxChars: 24 },
  NLSChatMembersEmpty: { text: 'No members yet.', description: 'Empty-state for the members sheet.', surface: 'chat', maxChars: 24 },
  NLSChatAttachmentGeneric: { text: 'Attachment', description: 'Fallback preview for a reply whose parent has no text.', surface: 'chat', maxChars: 16 },
  NLSChatLabelSomeone: { text: 'Someone', description: 'Fallback name used in typing indicator when the presence has no name.', surface: 'chat', maxChars: 18 },
  NLSChatTypingSingle: { text: '{name} is typing…', description: 'Typing indicator for one person. {name} is a display name.', surface: 'chat', maxChars: 34 },
  NLSChatTypingMulti: { text: '{count} people are typing…', description: 'Typing indicator for multiple people. {count} is a number.', surface: 'chat', maxChars: 40 },
  NLSChatLabelChannel: { text: 'channel', description: 'Generic "channel" fallback used as the composer placeholder when no channel is selected.', surface: 'chat', maxChars: 16 },
  NLSChatHistoryHiddenNotice: {
    text: 'Messages older than {days} days are hidden on your plan.',
    description: 'Notice at the top of a chat channel when message history older than the plan window is hidden. {days} is the history limit in days.',
    surface: 'chat',
    maxChars: 72,
  },
  NLSChatActionUpgrade: {
    text: 'Upgrade',
    description: 'Button action to open the upgrade modal from the chat history notice.',
    surface: 'chat',
    maxChars: 16,
  },
  NLSAnalysisPageTitle: { text: 'Analysis', description: 'Title for the workspace Analysis hub.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisPageSubtitle: { text: 'Workspace intelligence', description: 'Subtitle for the workspace Analysis hub.', surface: 'analysis', maxChars: 44 },
  NLSAnalysisTabOverview: { text: 'Overview', description: 'Analysis tab label for overview intelligence.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisTabWork: { text: 'Work', description: 'Analysis tab label for work intelligence.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisPlannedVsActualTitle: { text: 'Planned vs actual hours', description: 'Heading for planned vs actual hours card in Work tab.', surface: 'analysis', maxChars: 30 },
  NLSAnalysisPlannedVsActualEyebrow: { text: 'Goals', description: 'Eyebrow for planned vs actual hours card in Work tab.', surface: 'analysis', maxChars: 15 },
  NLSAnalysisPlannedVsActualEmpty: { text: 'No goals with planned or actual hours.', description: 'Empty state message when no goals have planned or actual hours.', surface: 'analysis', maxChars: 50 },
  NLSAnalysisPlannedLabel: { text: 'Planned', description: 'Label for planned hours.', surface: 'analysis', maxChars: 15 },
  NLSAnalysisActualLabel: { text: 'Actual', description: 'Label for actual hours.', surface: 'analysis', maxChars: 15 },
  NLSAnalysisVarianceLabel: { text: 'Variance', description: 'Label for hours variance.', surface: 'analysis', maxChars: 15 },
  NLSAnalysisTabTeam: { text: 'Team', description: 'Analysis tab label for team intelligence.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisTabOperations: { text: 'Operations', description: 'Analysis tab label for operations intelligence.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisTabGovernance: { text: 'Governance', description: 'Analysis tab label for governance intelligence.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisTabAiIntelligence: { text: 'AI Intelligence', description: 'Analysis tab label for AI intelligence.', surface: 'analysis', maxChars: 30 },
  NLSAnalysisOnboardingHeading: { text: 'Nothing to analyse yet', description: 'Heading for the empty Analysis workspace onboarding shell.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisOnboardingBody: {
    text: 'Add checklists or import work to see workspace-wide analytics here.',
    description: 'Body copy for the empty Analysis workspace onboarding shell.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisOnboardingCtaCreateChecklist: { text: 'Add your first checklist', description: 'CTA that opens the checklist surface from empty Analysis.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisOnboardingCtaOpenAiGenerator: { text: 'Import from spreadsheet', description: 'CTA that opens AI Magic from empty Analysis.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisOnboardingTabExplainOverview: {
    text: 'Health, work, and top signals at a glance',
    description: 'Overview tab explainer in empty Analysis onboarding.',
    surface: 'analysis',
    maxChars: 60,
  },
  NLSAnalysisOnboardingTabExplainWork: { text: 'Active, overdue, blocked, stalled work counts', description: 'Work tab explainer in empty Analysis onboarding.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisOnboardingTabExplainTeam: { text: 'Who owns what across the workspace', description: 'Team tab explainer in empty Analysis onboarding.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisOnboardingTabExplainOperations: {
    text: 'Meetings, uploads, resources, integrations',
    description: 'Operations tab explainer in empty Analysis onboarding.',
    surface: 'analysis',
    maxChars: 60,
  },
  NLSAnalysisOnboardingTabExplainGovernance: {
    text: 'Audit, retention, sensitive ops, security cues',
    description: 'Governance tab explainer in empty Analysis onboarding.',
    surface: 'analysis',
    maxChars: 60,
  },
  NLSAnalysisOnboardingTabExplainAiIntelligence: {
    text: 'AI usage, pulse health, generation signals',
    description: 'AI Intelligence tab explainer in empty Analysis onboarding.',
    surface: 'analysis',
    maxChars: 60,
  },
  NLSAnalysisHelpButtonAria: { text: 'Open Analysis help', description: 'Accessible label for the Analysis help button.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisHelpModalTitle: { text: 'Analysis — user guide', description: 'Title for the Analysis user guide modal.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisHelpModalContributorFooter: { text: 'See the contributor guide on GitHub', description: 'External contributor guide link in Analysis help.', surface: 'analysis', maxChars: 48 },
  NLSAnalysisSparklineAriaDescription: {
    text: 'Sparkline: {label} trending {direction} {delta} over the last {timeRange}',
    description: 'Accessible trend summary for Analysis sparklines.',
    surface: 'analysis',
    maxChars: 140,
  },
  NLSAnalysisSparklineDirectionUp: { text: 'up', description: 'Ascending Analysis sparkline direction.', surface: 'analysis', maxChars: 8 },
  NLSAnalysisSparklineDirectionDown: { text: 'down', description: 'Descending Analysis sparkline direction.', surface: 'analysis', maxChars: 8 },
  NLSAnalysisSparklineDirectionFlat: { text: 'flat', description: 'Flat Analysis sparkline direction.', surface: 'analysis', maxChars: 8 },
  NLSAnalysisSignalsOverTime: { text: 'Signals over time', description: 'Title for signals over time chart.', surface: 'analysis', maxChars: 30 },
  NLSAnalysisComposition: { text: 'Work composition', description: 'Title for work composition donut chart.', surface: 'analysis', maxChars: 30 },
  NLSAnalysisTrend: { text: 'Trend', description: 'Title for trend sparkline chart.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisActionGenerateAiSummary: { text: 'Generate AI Summary', description: 'Analysis action for generating an operational summary.', surface: 'analysis', maxChars: 34 },
  NLSAnalysisActionNewSnapshot: { text: 'New Snapshot', description: 'Analysis action for refreshing the snapshot.', surface: 'analysis', maxChars: 26 },
  NLSAnalysisActionsMenuAria: { text: 'More analysis actions', description: 'Accessible label for the Analysis header more actions menu trigger.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisSessionEyebrow: { text: 'Session', description: 'Eyebrow label for the Analysis sidecar session card.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisSessionHeading: { text: 'View State', description: 'Heading for the Analysis sidecar session card.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisActionSaveView: { text: 'Save View', description: 'Analysis action for saving the current view.', surface: 'analysis', maxChars: 22 },
  NLSAnalysisFilterTimeRange: { text: 'Time range', description: 'Analysis filter section label for time range.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisTimeRange24H: { text: '24h', description: 'Analysis time-range chip label for the previous 24 hours.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisTimeRange7D: { text: '7d', description: 'Analysis time-range chip label for the previous 7 days.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisTimeRange30D: { text: '30d', description: 'Analysis time-range chip label for the previous 30 days.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisTimeRange90D: { text: '90 days', description: 'Analysis time-range chip label for the previous 90 days.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisTimeRange12M: { text: '12 months', description: 'Analysis time-range chip label for the previous 12 months.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisCohortSliceHeading: { text: 'Slice by', description: 'Analysis cohort filtering heading.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisCohortSliceRole: { text: 'Role', description: 'Analysis cohort role group.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisCohortSliceCluster: { text: 'Assignee load', description: 'Analysis cohort assignee-load group.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisCohortSliceClusterHigh: { text: 'High', description: 'High assignee-load cohort.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisCohortSliceClusterMedium: { text: 'Medium', description: 'Medium assignee-load cohort.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisCohortSliceClusterLow: { text: 'Low', description: 'Low assignee-load cohort.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisCohortSliceClusterIdle: { text: 'Idle', description: 'Idle assignee-load cohort.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisCohortSliceCompareDisabled: { text: 'Slice disabled in Compare mode', description: 'Explanation for unavailable cohort slice.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisCohortSliceClearAction: { text: 'Clear slice', description: 'Clears the current Analysis cohort slice.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisFreshnessFreshSeconds: { text: 'Fresh · {seconds}s ago', description: 'Analysis data freshness in seconds.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisFreshnessFreshMinutes: { text: 'Fresh · {minutes}m ago', description: 'Analysis data freshness in minutes.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisFreshnessStale: { text: 'Stale · {minutes}m ago', description: 'Stale Analysis data freshness.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisSubscriptionErrorMessage: { text: 'Data source unavailable — try refresh', description: 'Analysis module subscription error.', surface: 'analysis', maxChars: 48 },
  NLSAnalysisSubscriptionErrorAction: { text: 'Refresh', description: 'Refreshes the Analysis data subscription.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisRoleGateViewerReduced: {
    text: 'Viewer access shows overview, work, and team intelligence only. Governance, budget, stock, automation, integrations, and AI-sensitive signals are hidden.',
    description: 'Reduced Analysis access explanation for Viewer roles.',
    surface: 'analysis',
    maxChars: 180,
  },
  NLSAnalysisChartActionExpand: { text: 'Expand chart', description: 'Analysis chart expand-to-modal action label.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisChartActionClose: { text: 'Close chart', description: 'Analysis chart popover close button label.', surface: 'analysis', maxChars: 22 },
  NLSAnalysisChartActionExportCsv: { text: 'Export CSV', description: 'Analysis chart popover CSV export action.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisChartActionExportPng: { text: 'Export PNG', description: 'Analysis chart popover PNG export action.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisChartActionOpenReports: { text: 'Open in Reports', description: 'Analysis chart popover Open-in-Reports action.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisChartActionCopyLink: { text: 'Copy link', description: 'Analysis chart popover copy-shareable-link action.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisChartCopyLinkDisabled: { text: 'Deep-linkable in Phase 2', description: 'Tooltip explaining the copy-link action is not yet enabled.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisChartCollapseRecords: { text: 'Collapse records', description: 'Analysis chart popover collapse records panel action.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisChartExpandRecords: { text: 'Expand records', description: 'Analysis chart popover expand records panel action.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisChartCollapseExport: { text: 'Collapse export bar', description: 'Analysis chart popover collapse export bar action.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisChartExpandExport: { text: 'Expand export bar', description: 'Analysis chart popover expand export bar action.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisChartExportShare: { text: 'Export & share', description: 'Analysis chart popover export bar heading when collapsed.', surface: 'analysis', maxChars: 22 },
  NLSAnalysisChartLabelRecords: { text: 'Records ({count})', description: 'Analysis chart popover record list header. {count} is the number of records.', surface: 'analysis', maxChars: 26 },
  NLSAnalysisChartRecordSearchPlaceholder: { text: 'Filter records…', description: 'Analysis chart popover record list search input placeholder.', surface: 'analysis', maxChars: 26 },
  NLSAnalysisChartEmptyRecords: {
    text: 'No records back this visualization yet.',
    description: 'Analysis chart popover empty state when no records are available.',
    surface: 'analysis',
    maxChars: 60,
  },
  NLSAnalysisChartKeyboardHint: { text: 'Hover points for values; rows drill into owning pages.', description: 'Bottom-of-popover accessibility hint.', surface: 'analysis', maxChars: 90 },
  NLSAnalysisModuleAuditSignalsTitle: { text: 'Audit Signals', description: 'Title for Audit Signals module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleAuditSignalsDesc: {
    text: 'Ledger spikes and unusual activity patterns summarized from workspace events.',
    description: 'Description for Audit Signals module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleSensitiveOpsTitle: { text: 'Sensitive Ops', description: 'Title for Sensitive Ops module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleSensitiveOpsDesc: {
    text: 'High-risk permission, deletion, export, and status operations in the selected window.',
    description: 'Description for Sensitive Ops module.',
    surface: 'analysis',
    maxChars: 110,
  },
  NLSAnalysisModuleRetentionStateTitle: { text: 'Retention State', description: 'Title for Retention State module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleRetentionStateDesc: {
    text: 'Audit retention posture and lifecycle purge windows for workspace events.',
    description: 'Description for Retention State module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleExportActivityTitle: { text: 'Export Activity', description: 'Title for Export Activity module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleExportActivityDesc: {
    text: 'Recent report exports, downloads, and compliance handoffs.',
    description: 'Description for Export Activity module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleProposalExecutionTitle: { text: 'Proposal Execution', description: 'Title for Proposal Execution module.', surface: 'analysis', maxChars: 30 },
  NLSAnalysisModuleProposalExecutionDesc: {
    text: 'AI proposal approvals, rejections, and execution outcomes.',
    description: 'Description for Proposal Execution module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleSecurityCuesTitle: { text: 'Security Cues', description: 'Title for Security Cues module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleSecurityCuesDesc: {
    text: 'Security anomalies and sensitive access warnings detected in the audit ledger.',
    description: 'Description for Security Cues module.',
    surface: 'analysis',
    maxChars: 110,
  },
  NLSAnalysisLiveModuleTopFive: { text: 'Top 5 records', description: 'Header for the top 5 records drillable list in a live module card.', surface: 'analysis', maxChars: 30 },
  NLSAnalysisLiveModuleEmpty: { text: 'No signals recorded in this window', description: 'Empty state message for live module card when no records exist.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisLiveModuleGated: {
    text: 'Admin or Editor access required to view raw signals.',
    description: 'Gated state message when user lacks permission to view live records.',
    surface: 'analysis',
    maxChars: 80,
  },
  NLSAnalysisLiveModuleLoading: { text: 'Loading module data', description: 'Aria-label for live module card skeleton loading state.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisLiveModuleAnomalyTooltip: {
    text: 'Anomaly detected: higher than normal volume',
    description: 'Tooltip on the anomaly badge shown when a live module is above baseline.',
    surface: 'analysis',
    maxChars: 60,
  },
  NLSAnalysisLiveModuleEyebrowGovernance: { text: 'Governance', description: 'Eyebrow chip on Governance live module cards.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisLiveModuleEyebrowAiIntelligence: { text: 'AI Intelligence', description: 'Eyebrow chip on AI Intelligence live module cards.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisModuleProposalUsageTitle: { text: 'Proposal Usage', description: 'Title for Proposal Usage module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleProposalUsageDesc: {
    text: 'Proposal creation, approval, and rejection volume across the workspace.',
    description: 'Description for Proposal Usage module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleLowConfidenceTitle: { text: 'Low-Confidence', description: 'Title for Low-Confidence module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleLowConfidenceDesc: {
    text: 'Clarification and low-confidence routing signals from AI interactions.',
    description: 'Description for Low-Confidence module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleClarificationTitle: { text: 'Clarification', description: 'Title for Clarification module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleClarificationDesc: {
    text: 'Missing-context and clarification prompts generated during timeline planning.',
    description: 'Description for Clarification module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleRetrievalHealthTitle: { text: 'Retrieval Health', description: 'Title for Retrieval Health module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleRetrievalHealthDesc: {
    text: 'Context source retrieval success, latency, and compression metrics.',
    description: 'Description for Retrieval Health module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleRecommendationTrendsTitle: { text: 'Recommendation Trends', description: 'Title for Recommendation Trends module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleRecommendationTrendsDesc: {
    text: 'AI resolution proposals and workspace optimization recommendations.',
    description: 'Description for Recommendation Trends module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModulePulseEventsTitle: { text: 'Pulse Events', description: 'Title for Pulse Events module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModulePulseEventsDesc: {
    text: 'Live stream of AI Pulse events and operational alerts across the workspace.',
    description: 'Description for Pulse Events module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisLiveModuleEyebrowTeam: { text: 'Team', description: 'Eyebrow chip for live Team modules.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisModuleRoleLoadTitle: { text: 'Role load', description: 'Title for Role Load module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleRoleLoadDesc: {
    text: 'Role-level member distribution and active item workload across the workspace.',
    description: 'Description for Role Load module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleContributionActivityTitle: { text: 'Contribution activity', description: 'Title for Contribution Activity module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleContributionActivityDesc: {
    text: 'Completed items and contributor activity tracked across checklist assignees.',
    description: 'Description for Contribution Activity module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleAssigneeLoadTitle: { text: 'Assignee load', description: 'Title for Assignee Load module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleAssigneeLoadDesc: {
    text: 'Member workload distribution across active and overdue assigned checklist work.',
    description: 'Description for Assignee Load module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleAutomationHealthTitle: { text: 'Automation health', description: 'Title for Automation Health module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleAutomationHealthDesc: {
    text: 'Recent automation run history, failures, and retries across the workspace.',
    description: 'Description for Automation Health module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleMeetingsReadinessTitle: { text: 'Meetings readiness', description: 'Title for Meetings Readiness module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleMeetingsReadinessDesc: {
    text: 'Provider readiness signals and RSVP pressure across recent meetings.',
    description: 'Description for Meetings Readiness module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleUploadProofBacklogTitle: { text: 'Upload proof backlog', description: 'Title for Upload Proof Backlog module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleUploadProofBacklogDesc: {
    text: 'Checklist items still waiting for required proof files, grouped by age.',
    description: 'Description for Upload Proof Backlog module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisModuleIntegrationHealthTitle: { text: 'Integration health', description: 'Title for Integration Health module.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisModuleIntegrationHealthDesc: {
    text: 'Link Hub provider connections and integration activity across the workspace.',
    description: 'Description for Integration Health module.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisLiveModuleEyebrowOperations: { text: 'Operations', description: 'Eyebrow chip for live Operations modules.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisSavedViewsHeading: { text: 'Saved views', description: 'Heading for Saved Views card in analysis sidecar.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisSavedViewsEyebrow: { text: 'Views', description: 'Eyebrow for Saved Views card in analysis sidecar.', surface: 'analysis', maxChars: 18 },
  NLSAnalysisSavedViewsEmpty: { text: 'No saved views yet.', description: 'Empty state message for Saved Views card.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisSavedViewsPresetChip: { text: 'Preset', description: 'Chip for default preset analysis views.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSavedViewsCustomChip: { text: 'Saved', description: 'Chip for user saved analysis views.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSavedViewCopyLink: { text: 'Copy link to view', description: 'Tooltip and accessible label for copying an analysis saved-view link.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisSavedViewCopyLinkSucceeded: { text: 'Link copied', description: 'Success toast after copying an analysis saved-view link.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisSavedViewCopyLinkFailed: { text: 'Could not copy — try again', description: 'Error toast when an analysis saved-view link cannot be copied.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisAnnotationLaneHeading: { text: 'Notes', description: 'Snapshot annotation lane heading.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAnnotationComposerPlaceholder: { text: 'Add a note about this snapshot…', description: 'Snapshot annotation composer placeholder.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisAnnotationComposerSave: { text: 'Save', description: 'Save snapshot annotation.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAnnotationEmpty: { text: 'No notes yet on this snapshot.', description: 'Empty snapshot annotation list.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisAnnotationEditAction: { text: 'Edit', description: 'Edit snapshot annotation action.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAnnotationDeleteAction: { text: 'Delete', description: 'Delete snapshot annotation action.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAnnotationDeleteConfirm: { text: 'Delete this note?', description: 'Snapshot annotation delete confirmation.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisAnnotationEditedIndicator: { text: 'edited {timestamp}', description: 'Snapshot annotation edited indicator.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisAnnotationCountBadge: { text: '{count}', description: 'Snapshot annotation count badge.', surface: 'analysis', maxChars: 8 },
  NLSAnalysisAnnotationCreateSucceeded: { text: 'Note added', description: 'Snapshot annotation create success.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAnnotationCreateFailed: { text: 'Could not add note — try again', description: 'Snapshot annotation create failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisAnnotationTextTooLong: { text: 'Notes are limited to 1,000 characters', description: 'Snapshot annotation length warning.', surface: 'analysis', maxChars: 48 },
  NLSAnalysisAnnotationExpandAria: { text: 'Show notes', description: 'Expand snapshot notes.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAnnotationCollapseAria: { text: 'Hide notes', description: 'Collapse snapshot notes.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAnnotationUpdateSucceeded: { text: 'Note updated', description: 'Snapshot annotation update success.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAnnotationUpdateFailed: { text: 'Could not update note — try again', description: 'Snapshot annotation update failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisAnnotationDeleteSucceeded: { text: 'Note deleted', description: 'Snapshot annotation deletion success.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAnnotationDeleteFailed: { text: 'Could not delete note — try again', description: 'Snapshot annotation deletion failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisAlertRulesHeading: { text: 'Alert rules', description: 'Heading for Analysis alert rules.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAlertRulesEyebrow: { text: 'Alerts', description: 'Eyebrow for Analysis alert rules.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesEmptyTitle: { text: 'No alert rules yet.', description: 'Empty Analysis alert rules state.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisAlertRulesEmptyDesc: {
    text: 'Adopt a template or create your own to be notified when metrics cross a threshold.',
    description: 'Empty Analysis alert rules description.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisAlertRulesCreateAction: { text: 'Create rule', description: 'Create Analysis alert rule.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAlertRulesLimitReached: { text: 'Rule limit reached (25). Delete an unused rule first.', description: 'Analysis alert rule cap.', surface: 'analysis', maxChars: 80 },
  NLSAnalysisAlertRulesTemplateHealthCriticalTitle: { text: 'Health score critical', description: 'Health alert template.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisAlertRulesTemplateHealthCriticalDesc: { text: 'Alert when overall health drops below 60.', description: 'Health alert template description.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisAlertRulesTemplateBlockedSpikeTitle: { text: 'Blocked items spike', description: 'Blocked alert template.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisAlertRulesTemplateBlockedSpikeDesc: { text: 'Alert when blocked items exceed 20.', description: 'Blocked alert template description.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisAlertRulesTemplateAdoptAction: { text: 'Adopt', description: 'Adopt alert template.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesRuleItemEditAction: { text: 'Edit', description: 'Edit alert rule.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesRuleItemDeleteAction: { text: 'Delete', description: 'Delete alert rule.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesRuleItemDeleteConfirm: { text: 'Delete this rule?', description: 'Confirm alert rule delete.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisAlertRulesRuleItemDisabledBadge: { text: 'Paused', description: 'Disabled alert rule badge.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesEditorHeading: { text: 'New alert rule', description: 'New alert editor heading.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisAlertRulesEditorEditHeading: { text: 'Edit alert rule', description: 'Edit alert editor heading.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisAlertRulesEditorNameLabel: { text: 'Rule name', description: 'Alert name label.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAlertRulesEditorMetricLabel: { text: 'Metric', description: 'Alert metric label.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesEditorThresholdTypeLabel: { text: 'Trigger', description: 'Alert threshold type label.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisAlertRulesEditorThresholdTypeAbsolute: { text: 'Absolute value', description: 'Absolute alert threshold.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisAlertRulesEditorThresholdTypeDelta: { text: 'Change from last snapshot', description: 'Delta alert threshold.', surface: 'analysis', maxChars: 36 },
  NLSAnalysisAlertRulesEditorThresholdTypeZScore: { text: 'Statistical outlier (z-score)', description: 'Z score alert threshold.', surface: 'analysis', maxChars: 36 },
  NLSAnalysisAlertRulesEditorComparatorLabel: { text: 'Compare', description: 'Alert comparator label.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisAlertRulesEditorComparatorGt: { text: 'greater than', description: 'GT comparator.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisAlertRulesEditorComparatorGte: { text: 'at least', description: 'GTE comparator.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisAlertRulesEditorComparatorLt: { text: 'less than', description: 'LT comparator.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisAlertRulesEditorComparatorLte: { text: 'at most', description: 'LTE comparator.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisAlertRulesEditorComparatorEq: { text: 'equals', description: 'EQ comparator.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesEditorValueLabel: { text: 'Value', description: 'Alert value label.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesEditorTargetLabel: { text: 'Notify via', description: 'Alert target label.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisAlertRulesEditorTargetChat: { text: 'Chat channel', description: 'Chat target.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisAlertRulesEditorTargetTask: { text: 'Task for a member', description: 'Task target.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisAlertRulesEditorTargetChannelLabel: { text: 'Channel', description: 'Alert channel label.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesEditorTargetAssigneeLabel: { text: 'Assignee', description: 'Alert assignee label.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisAlertRulesEditorSaveAction: { text: 'Save rule', description: 'Save alert rule.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAlertRulesEditorCancelAction: { text: 'Cancel', description: 'Cancel alert rule edit.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRulesSaveSucceeded: { text: 'Rule saved', description: 'Alert save success.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAlertRulesSaveFailed: { text: 'Could not save rule — try again', description: 'Alert save failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisAlertRulesDeleteSucceeded: { text: 'Rule deleted', description: 'Alert deletion success.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAlertRulesDeleteFailed: { text: 'Could not delete rule — try again', description: 'Alert deletion failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisAlertFiredChatText: {
    text: 'Alert: {ruleName} — {metric} is {value} (threshold {comparator} {threshold})',
    description: 'System Analysis alert chat message.',
    surface: 'analysis',
    maxChars: 120,
  },
  NLSAnalysisAlertTaskItemTitle: { text: 'Analysis alert: {ruleName}', description: 'Analysis alert inbox checklist title.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisAlertDispatchChatFailed: { text: 'Could not send alert to chat', description: 'Analysis chat alert delivery failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisAlertDispatchTaskFailed: { text: 'Could not create alert task', description: 'Analysis task alert delivery failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisAlertRuleFiringsExpandAria: { text: 'Show firings', description: 'Expand alert firing history.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAlertRuleFiringsCollapseAria: { text: 'Hide firings', description: 'Collapse alert firing history.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisAlertRuleFiringsEmpty: { text: 'No firings yet.', description: 'Empty alert firing history.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisAlertRuleFiringDeliveryStatusSent: { text: 'Sent', description: 'Successful alert delivery.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisAlertRuleFiringDeliveryStatusFailed: { text: 'Failed', description: 'Failed alert delivery.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisRowResolutionAcknowledgeAction: { text: 'Acknowledge', description: 'Acknowledge governance row.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisRowResolutionDismissAction: { text: 'Dismiss', description: 'Dismiss governance row.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisRowResolutionConvertAction: { text: 'Convert to Task', description: 'Convert governance row to task.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisRowResolutionReopenAction: { text: 'Reopen', description: 'Reopen governance row.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisRowResolutionChipAck: { text: 'Acknowledged', description: 'Acknowledged governance row.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisRowResolutionChipDismissed: { text: 'Dismissed', description: 'Dismissed governance row.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisRowResolutionChipConverted: { text: 'Converted', description: 'Converted governance row.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisRowResolutionTaskItemTitle: { text: '{scope}: {recordPrimary}', description: 'Governance conversion task title.', surface: 'analysis', maxChars: 80 },
  NLSAnalysisRowResolutionAckSucceeded: { text: 'Row acknowledged', description: 'Governance acknowledge success.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisRowResolutionAckFailed: { text: 'Could not acknowledge row', description: 'Governance acknowledge failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisRowResolutionDismissSucceeded: { text: 'Row dismissed', description: 'Governance dismiss success.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisRowResolutionDismissFailed: { text: 'Could not dismiss row', description: 'Governance dismiss failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisRowResolutionConvertSucceeded: { text: 'Task created', description: 'Governance conversion success.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisRowResolutionConvertFailed: { text: 'Could not create task', description: 'Governance conversion failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisRowResolutionReopenSucceeded: { text: 'Row reopened', description: 'Governance reopen success.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisRowResolutionReopenFailed: { text: 'Could not reopen row', description: 'Governance reopen failure.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisMobileBoardSubItemLabel: { text: 'Analysis', description: 'Member Board Analysis sub-tab label.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisMobileMetricTotal: { text: 'Total', description: 'Member Analysis total work metric label.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisMobileMetricActive: { text: 'Active', description: 'Member Analysis active work metric label.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisMobileMetricCompleted: { text: 'Completed', description: 'Member Analysis completed work metric label.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisMobileMetricOverdue: { text: 'Overdue', description: 'Member Analysis overdue work metric label.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisMobileHealthComponents: { text: 'Health components', description: 'Heading for Member Analysis dense health-score component bars.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisMobileScoreOutOf100: { text: '/100', description: 'Scale suffix shown beside the compact Member Analysis health state.', surface: 'analysis', maxChars: 8 },
  NLSAnalysisMobileEmpty: { text: 'No data yet on this workspace.', description: 'Member Analysis empty state.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisMobileLoading: { text: 'Loading analysis…', description: 'Member Analysis loading state.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisMobileError: { text: 'Could not load analysis', description: 'Member Analysis error state.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisMobileFilterAria: { text: 'Open Analysis filters', description: 'Accessible label for the member Analysis filter button.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisMobileViewsAria: { text: 'Open Analysis views', description: 'Accessible label for the member Analysis saved views button.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisMobileReportsUnavailable: { text: 'Reports are available on the web app.', description: 'Explains that the member app has no Reports destination.', surface: 'analysis', maxChars: 48 },
  NLSAnalysisMobileFilterTitle: { text: 'Analysis filters', description: 'Member Analysis filter sheet title.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisMobileViewsTitle: { text: 'Views and history', description: 'Member Analysis views sheet title.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisMobileTimeTitle: { text: 'Time window', description: 'Member Analysis time-range sheet title.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisMobileTimeAria: { text: 'Choose Analysis time window', description: 'Accessible label for the member Analysis time-window button.', surface: 'analysis', maxChars: 36 },
  NLSAnalysisMobileToolsAria: { text: 'Analysis tools', description: 'Accessible label for the member Analysis bottom tools row.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisMobileFilters: { text: 'Filters', description: 'Visible label for the member Analysis filters button.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisMobileFiltersWithCount: { text: 'Filters ({count})', description: 'Accessible label for the member Analysis filters button with active system count.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisMobileViews: { text: 'Views', description: 'Visible label for the member Analysis views button.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisMobileTime24Hours: { text: '24 hours', description: 'Full member Analysis time-sheet label for the previous 24 hours.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisMobileTime7Days: { text: '7 days', description: 'Full member Analysis time-sheet label for the previous 7 days.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisMobileTime30Days: { text: '30 days', description: 'Full member Analysis time-sheet label for the previous 30 days.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisMobileTime90Days: { text: '90 days', description: 'Full member Analysis time-sheet label for the previous 90 days.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisMobileTime12Months: { text: '12 months', description: 'Full member Analysis time-sheet label for the previous 12 months.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisMobileTimeSelected: { text: 'Selected', description: 'Accessible label for the selected member Analysis time window.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisMobileHistoricTimestamp: { text: 'Snapshot · {timestamp}', description: 'Historic mode banner timestamp in member Analysis.', surface: 'analysis', maxChars: 72 },
  NLSAnalysisMobileIncludeArchived: { text: 'Include archived', description: 'Member Analysis archived filter.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisMobileHighlightAnomalies: { text: 'Highlight anomalies', description: 'Member Analysis anomaly filter.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisMobileNoSignalsHeading: { text: 'No signals in this window', description: 'Heading for a compact empty Member Analysis module.', surface: 'analysis', maxChars: 36 },
  NLSAnalysisMobileNoSignalsDetail: { text: 'New activity will appear here when it is recorded.', description: 'Detail for a compact empty Member Analysis module.', surface: 'analysis', maxChars: 72 },
  NLSAnalysisMobileSelectAll: { text: 'Select all', description: 'Selects every included-system filter in the Member Analysis sheet.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisMobileClearAll: { text: 'Clear all', description: 'Clears every included-system filter in the Member Analysis sheet.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisMobileSavedViews: { text: 'Saved views', description: 'Member Analysis saved views section.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisMobileSnapshots: { text: 'Snapshot history', description: 'Member Analysis snapshot section.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisMobileBackToLive: { text: 'Back to live', description: 'Returns member Analysis from historic snapshot mode.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisMobileWebOnlyTab: { text: 'That tab is available on the web app.', description: 'Explains a saved view tab unavailable in member Analysis.', surface: 'analysis', maxChars: 48 },
  NLSAnalysisViewerGateTooltip: { text: 'Read-only for viewers', description: 'Explains disabled Analysis mutation controls for reduced viewers.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisDigestSubscribeAction: { text: 'Subscribe to digest', description: 'Subscribes a saved Analysis view to scheduled chat digests.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisDigestUnsubscribeAction: { text: 'Unsubscribe from digest', description: 'Stops scheduled chat digests for a saved Analysis view.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisDigestCadenceDaily: { text: 'Daily', description: 'Daily Analysis digest cadence.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisDigestCadenceWeekly: { text: 'Weekly', description: 'Weekly Analysis digest cadence.', surface: 'analysis', maxChars: 12 },
  NLSAnalysisDigestChannelLabel: { text: 'Deliver to channel', description: 'Channel picker for Analysis digest delivery.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisDigestSubscribeSucceeded: { text: 'Subscribed for {cadence} digest', description: 'Digest subscription success toast.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisDigestSubscribeFailed: { text: 'Could not subscribe — try again', description: 'Digest subscription error toast.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisDigestUnsubscribeSucceeded: { text: 'Unsubscribed', description: 'Digest unsubscribe success toast.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisDigestUnsubscribeFailed: { text: 'Could not unsubscribe — try again', description: 'Digest unsubscribe error toast.', surface: 'analysis', maxChars: 40 },
  NLSChatAnalyticsCardEyebrow: { text: 'Analysis snapshot', description: 'Eyebrow for an Analysis card embedded in a chat message.', surface: 'chat', maxChars: 24 },
  NLSChatAnalyticsCardLabel: { text: 'Analysis snapshot: {title}', description: 'Accessible label for an embedded Analysis chat card.', surface: 'chat', maxChars: 80 },
  NLSChatAnalyticsCardPreviewUnavailable: { text: 'Chart preview unavailable', description: 'Accessible label for an Analysis card with no sparkline image.', surface: 'chat', maxChars: 40 },
  NLSChatAnalyticsCardGenerated: { text: 'Generated {timestamp}', description: 'Timestamp label on an embedded Analysis chat card.', surface: 'chat', maxChars: 64 },
  NLSChatAnalyticsCardOpen: { text: 'Open in Analysis', description: 'Link from an embedded Analysis chat card to the originating module.', surface: 'chat', maxChars: 28 },
  NLSAnalysisDeepLinkViewLoaded: { text: 'Loaded saved view: {name}', description: 'Confirmation toast after loading an analysis saved view from a deep link.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisDeepLinkViewMissing: {
    text: 'Saved view not available in this workspace',
    description: 'Fallback toast when an analysis deep link is unavailable in the active workspace.',
    surface: 'analysis',
    maxChars: 60,
  },
  NLSAnalysisSaveViewDialogTitle: { text: 'Save analysis view', description: 'Title for save analysis view modal dialog.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisSaveViewDialogNameLabel: { text: 'View name', description: 'Label for view name input in save view dialog.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisSaveViewDialogNamePlaceholder: { text: 'e.g. Ops Overview', description: 'Placeholder for view name input in save view dialog.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisSaveViewDialogSave: { text: 'Save view', description: 'Button text to save analysis view.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisSaveViewDialogCancel: { text: 'Cancel', description: 'Cancel button in save analysis view dialog.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSaveViewDialogFiltersHeading: { text: 'Included filters', description: 'Heading for active filter summary in save view dialog.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisSaveViewDialogArchivedChip: {
    text: 'Archived',
    description: 'Chip in the Save Analysis View dialog indicating archived records are included in the current filter set.',
    surface: 'analysis',
    maxChars: 18,
  },
  NLSAnalysisSnapshotHistoryHeading: { text: 'Snapshot history', description: 'Heading for snapshot history card.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisSnapshotHistoryEyebrow: { text: 'History', description: 'Eyebrow for snapshot history card.', surface: 'analysis', maxChars: 18 },
  NLSAnalysisSnapshotHistoryEmpty: { text: 'No snapshots captured yet.', description: 'Empty state title for snapshot history.', surface: 'analysis', maxChars: 50 },
  NLSAnalysisSnapshotHistoryEmptyDesc: {
    text: 'Click "New snapshot" in the header to record workspace state.',
    description: 'Empty state description for snapshot history.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisSnapshotHistoryItemMeta: { text: 'Snapshot by {actor}', description: 'Meta line for snapshot history row.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisSnapshotHistoryLoading: { text: 'Loading history…', description: 'Loading state for snapshot history.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisSnapshotHistoricChip: { text: 'Historic — {timestamp}', description: 'Chip indicating historic snapshot mode.', surface: 'analysis', maxChars: 40 },
  NLSAnalysisStatusChipLive: { text: 'Live · local', description: 'Status chip for live analysis.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisStatusChipHistoric: { text: 'Historic snapshot', description: 'Status chip for historic analysis.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisSnapshotPulseLive: { text: 'Live pulse', description: 'Snapshot pulse heading in live analysis.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisSnapshotPulseHistoric: { text: 'Snapshot pulse', description: 'Snapshot pulse heading in historic analysis.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisSnapshotWriteFailed: { text: 'Could not create snapshot. Try again.', description: 'Error toast when writing an analysis snapshot fails.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisActionExportSnapshot: { text: 'Export snapshot', description: 'Header action to export the current analysis snapshot.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisActionExportPdf: { text: 'Export tab (PDF)', description: 'Header action to export the active analysis tab as a PDF.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisExportPdfInProgress: { text: 'Rendering PDF…', description: 'Progress toast while an analysis PDF is rendering.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisExportPdfSucceeded: { text: 'PDF ready — opening', description: 'Success toast after an analysis PDF is ready.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisExportPdfFailed: { text: 'Could not export PDF. Try again.', description: 'Error toast when an analysis PDF export fails.', surface: 'analysis', maxChars: 48 },
  NLSAnalysisExportPdfCompareUnavailable: {
    text: 'PDF export is unavailable in compare view',
    description: 'Tooltip for the disabled compare-view PDF export action.',
    surface: 'analysis',
    maxChars: 56,
  },
  NLSAnalysisActionExportComparison: { text: 'Export comparison', description: 'Header action to export the active analysis comparison.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisExportModuleCsv: { text: 'Export CSV', description: 'Action to export chart records as CSV.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisExportModulePng: { text: 'Export PNG', description: 'Action to export a chart image.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisExportPngUnavailable: { text: 'Chart not ready — try again in a moment', description: 'PNG export unavailable tooltip.', surface: 'analysis', maxChars: 56 },
  NLSAnalysisExportPngFailed: { text: 'Could not export PNG. Try again.', description: 'PNG export error toast.', surface: 'analysis', maxChars: 48 },
  NLSAnalysisExportCsvDisabledHistoric: { text: 'CSV export is available in live view only', description: 'Tooltip for historic CSV export restriction.', surface: 'analysis', maxChars: 56 },
  NLSAnalysisExportFailed: { text: 'Could not export. Try again.', description: 'Error toast when an analysis export fails.', surface: 'analysis', maxChars: 48 },
  NLSAnalysisExportSucceeded: { text: 'Export downloaded', description: 'Success toast after an analysis export downloads.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisSendToChatAction: { text: 'Send to chat', description: 'Action that posts an Analysis module card to a workspace chat channel.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisSendToChatChannelLabel: { text: 'Channel', description: 'Accessible label for the Analysis send-to-chat channel picker.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSendToChatSending: { text: 'Sending…', description: 'In-progress label while an Analysis card is being sent to chat.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisSendToChatSucceeded: { text: 'Sent to #{channel}', description: 'Success toast after sending an Analysis card to a channel.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisSendToChatFailed: { text: 'Could not send — try again', description: 'Error toast after an Analysis card cannot be sent to chat.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisSendToChatCompareUnavailable: {
    text: 'Send-to-chat is unavailable in compare view',
    description: 'Tooltip for disabled send-to-chat action in compare view.',
    surface: 'analysis',
    maxChars: 60,
  },
  NLSAnalysisSendToChatNoChannels: { text: 'No channels available', description: 'Empty state when no workspace chat channel can receive an Analysis card.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisChatCardEyebrow: { text: 'From Analysis', description: 'Eyebrow for an Analysis card embedded in chat.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisChatCardOpenInAnalysis: { text: 'Open in Analysis', description: 'Link on an Analysis chat card.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisDeepLinkModuleMissing: { text: 'Module not found in this workspace', description: 'Toast for an unknown Analysis module deep link.', surface: 'analysis', maxChars: 50 },
  NLSAnalysisCompareAction: { text: 'Compare', description: 'Header action to enter analysis compare mode.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisStatusChipCompare: { text: 'Compare · A vs B', description: 'Status chip for analysis compare mode.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisCompareModeChipA: { text: 'A', description: 'First compare-side picker label.', surface: 'analysis', maxChars: 4 },
  NLSAnalysisCompareModeChipB: { text: 'B', description: 'Second compare-side picker label.', surface: 'analysis', maxChars: 4 },
  NLSAnalysisCompareOptionLive: { text: 'Live', description: 'Live option in compare picker.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisCompareDropdownHeading: { text: 'Pick a snapshot', description: 'Heading for compare picker dropdown.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisCompareEmpty: { text: 'Take a snapshot first to compare', description: 'Empty state for compare picker history.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisCompareViewerLockedA: { text: 'Viewers can only compare against Live', description: 'Tooltip for viewer-locked A compare picker.', surface: 'analysis', maxChars: 60 },
  NLSAnalysisCompareScalarOnly: {
    text: 'Compare shows headline scalars only in this phase. Live modules return once history payload grows.',
    description: 'Informational strip explaining compare-mode scope.',
    surface: 'analysis',
    maxChars: 120,
  },
  NLSAnalysisCompareDeltaImproved: { text: 'Improved', description: 'Accessible label for an improved comparison delta.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisCompareDeltaRegressed: { text: 'Regressed', description: 'Accessible label for a regressed comparison delta.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisCompareDeltaFlat: { text: 'No change', description: 'Accessible label for an unchanged comparison delta.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisCompareAssigneeDiffAppeared: { text: 'Appeared', description: 'Assignee comparison section heading.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisCompareAssigneeDiffDisappeared: { text: 'Disappeared', description: 'Assignee comparison section heading.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisCompareAssigneeDiffRankChanged: { text: 'Rank changed', description: 'Assignee comparison section heading.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisSnapshotBackToLive: { text: 'Back to live', description: 'Button text to return from historic snapshot to live analysis.', surface: 'analysis', maxChars: 20 },
  NLSAnalysisSnapshotModuleNotPersisted: {
    text: 'Not persisted in historical snapshots',
    description: 'Placeholder message for modules that require live data streams.',
    surface: 'analysis',
    maxChars: 80,
  },
  NLSAnalysisSavedViewDeleteConfirm: { text: 'Delete view', description: 'Button tooltip or action to delete a saved view.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisIncludedSystemsHeading: { text: 'Included systems', description: 'Heading for Included Systems card in analysis sidecar.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisIncludedSystemsEyebrow: { text: 'Filters', description: 'Eyebrow for Included Systems card in analysis sidecar.', surface: 'analysis', maxChars: 18 },
  NLSAnalysisIncludedSystemsSelectAll: { text: 'All', description: 'Button to select all included systems in analysis.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisIncludedSystemsClear: { text: 'Clear', description: 'Button to clear all included systems in analysis.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisHighlightAnomaliesHeading: { text: 'Highlight anomalies', description: 'Heading for Highlight Anomalies toggle in analysis sidecar.', surface: 'analysis', maxChars: 24 },
  NLSAnalysisHighlightAnomaliesEyebrow: { text: 'Signals', description: 'Eyebrow for Highlight Anomalies toggle in analysis sidecar.', surface: 'analysis', maxChars: 18 },
  NLSAnalysisHighlightAnomaliesDesc: {
    text: 'Outline metric modules exceeding 2× 7-day baseline.',
    description: 'Description for Highlight Anomalies toggle in analysis sidecar.',
    surface: 'analysis',
    maxChars: 80,
  },
  NLSAnalysisFilteredEmptyHeading: { text: 'System filtered out', description: 'Heading shown when a tab or section is hidden by system filters.', surface: 'analysis', maxChars: 32 },
  NLSAnalysisFilteredEmptyDesc: {
    text: 'This system is currently unchecked in the Included Systems sidecar filter.',
    description: 'Description shown when a tab or section is hidden by system filters.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSAnalysisSystemWork: { text: 'Work', description: 'Label for Work system chip in analysis filters.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSystemTeam: { text: 'Team', description: 'Label for Team system chip in analysis filters.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSystemMeetings: { text: 'Meetings', description: 'Label for Meetings system chip in analysis filters.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSystemUploads: { text: 'Uploads', description: 'Label for Uploads system chip in analysis filters.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSystemAutomations: { text: 'Automations', description: 'Label for Automations system chip in analysis filters.', surface: 'analysis', maxChars: 18 },
  NLSAnalysisSystemIntegrations: { text: 'Integrations', description: 'Label for Integrations system chip in analysis filters.', surface: 'analysis', maxChars: 18 },
  NLSAnalysisSystemStock: { text: 'Stock', description: 'Label for Stock system chip in analysis filters.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSystemBudget: { text: 'Budget', description: 'Label for Budget system chip in analysis filters.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSystemForms: { text: 'Forms', description: 'Label for Forms system chip in analysis filters.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSystemGovernance: { text: 'Governance', description: 'Label for Governance system chip in analysis filters.', surface: 'analysis', maxChars: 18 },
  NLSAnalysisSystemAi: { text: 'AI', description: 'Label for AI system chip in analysis filters.', surface: 'analysis', maxChars: 16 },
  NLSAnalysisSystemChipShow: { text: 'Show {label}', description: 'Tooltip to include a system in analysis filters.', surface: 'analysis', maxChars: 28 },
  NLSAnalysisSystemChipHide: { text: 'Hide {label}', description: 'Tooltip to exclude a system in analysis filters.', surface: 'analysis', maxChars: 28 },
  NLSAiContextPickerTitle: { text: 'Attach context', description: 'Title of the AI context picker modal.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerSearchPlaceholder: { text: 'Search uploads, forms, chats…', description: 'Search bar placeholder inside the context picker modal.', surface: 'ai', maxChars: 54 },
  NLSAiContextPickerCategoryUploads: { text: 'Uploads', description: 'Context picker left-rail category for workspace uploads.', surface: 'ai', maxChars: 22 },
  NLSAiContextPickerCategoryForms: { text: 'Forms & Responses', description: 'Context picker left-rail category for forms and responses.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerCategoryDesigns: { text: 'Designs', description: 'Context picker left-rail category for design assets.', surface: 'ai', maxChars: 22 },
  NLSAiContextPickerCategoryChats: { text: 'Chats', description: 'Context picker left-rail category for chat messages.', surface: 'ai', maxChars: 18 },
  NLSAiContextPickerCategoryNewFile: { text: 'New from file', description: 'Context picker left-rail category for ingesting a new local file.', surface: 'ai', maxChars: 32 },
  NLSAiContextPickerCategoryGSheet: { text: 'Google Sheet', description: 'Context picker left-rail category for linking a Google Sheet.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerCategoryImage: { text: 'Image upload', description: 'Context picker left-rail category for uploading images.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerCategoryLink: { text: 'Public link', description: 'Context picker left-rail category for attaching a public URL.', surface: 'ai', maxChars: 26 },
  NLSAiContextPickerChatModeWhole: { text: 'Whole channel', description: 'Context picker chat pane segmented control option for whole channel.', surface: 'ai', maxChars: 30 },
  NLSAiContextPickerChatModeMessages: { text: 'Specific messages', description: 'Context picker chat pane segmented control option for specific messages.', surface: 'ai', maxChars: 38 },
  NLSAiContextPickerChatModeRange: { text: 'Date range', description: 'Context picker chat pane segmented control option for date-range slice.', surface: 'ai', maxChars: 26 },
  NLSAiContextPickerSelectAll: { text: 'Select all', description: 'Context picker select-all action label.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerUnselectAll: { text: 'Unselect all', description: 'Context picker unselect-all action label.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerCapTruncated: {
    text: 'Trimmed to the most recent {n} messages',
    description: 'Banner shown when chat selection is truncated by the cap. {n} is a number.',
    surface: 'ai',
    maxChars: 76,
  },
  NLSAiContextPickerCapBlocked: {
    text: 'Selection is too large — narrow the range or unselect messages',
    description: 'Error shown when chat selection exceeds the token budget.',
    surface: 'ai',
    maxChars: 90,
  },
  NLSAiComposerAttachContext: { text: 'Attach context…', description: 'AI Magic composer menu item for opening the context picker modal.', surface: 'ai', maxChars: 36 },
  NLSAiComposerAttachContextHint: { text: 'Uploads, forms, chats, sheets and more', description: 'AI Magic composer hint for the unified context picker row.', surface: 'ai', maxChars: 62 },
  NLSAiContextPickerAttachN: { text: 'Attach {count} items', description: 'Context picker attach button label with count. {count} is a number.', surface: 'ai', maxChars: 32 },
  NLSAiContextPickerAttachOne: { text: 'Attach 1 item', description: 'Context picker attach button label for 1 item.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerAttachEmpty: { text: 'Attach items', description: 'Context picker attach button label when empty.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerCostFixed: { text: '+{cr} Cr fixed', description: 'Context picker fixed ingest cost label. {cr} is a number.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerCostRange: { text: '≈ {lo}–{hi} Cr', description: 'Context picker estimated token cost range. {lo} and {hi} are numbers.', surface: 'ai', maxChars: 30 },
  NLSAiContextPickerCostEst: { text: 'est.', description: 'Abbreviation for estimated.', surface: 'ai', maxChars: 12 },
  NLSAiContextPickerNoItemsSelected: { text: 'No items selected', description: 'Context picker footer label when no items are in basket.', surface: 'ai', maxChars: 30 },
  NLSAiContextPickerSearchUploads: { text: 'Search uploads…', description: 'Placeholder for uploads search.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerSearchForms: { text: 'Search forms…', description: 'Placeholder for forms search.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerSearchDesigns: { text: 'Search designs…', description: 'Placeholder for designs search.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerSearchFilterMessages: { text: 'Filter messages…', description: 'Placeholder for chat messages search.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerBackToForms: { text: 'Back to forms', description: 'Button to return to form definitions list.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerSchema: { text: 'Schema', description: 'Action button to attach form definition schema only.', surface: 'ai', maxChars: 18 },
  NLSAiContextPickerResponses: { text: 'Responses', description: 'Action button to pick form responses.', surface: 'ai', maxChars: 22 },
  NLSAiContextPickerAttach: { text: 'Attach', description: 'Compact attach action label.', surface: 'ai', maxChars: 16 },
  NLSAiContextPickerUpdate: { text: 'Update', description: 'Compact update action label.', surface: 'ai', maxChars: 16 },
  NLSAiContextPickerAttached: { text: 'Attached', description: 'Compact attached status label.', surface: 'ai', maxChars: 18 },
  NLSAiContextPickerLoadMoreForms: { text: 'Load more forms', description: 'Button to load more forms.', surface: 'ai', maxChars: 30 },
  NLSAiContextPickerLoadMoreResponses: { text: 'Load more responses', description: 'Button to load more responses.', surface: 'ai', maxChars: 34 },
  NLSAiContextPickerComingNext: { text: 'Coming next', description: 'Badge indicating a feature is coming next.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerCostTooltip: { text: 'Adds ≈ {cr} Cr on send', description: 'Tooltip on context attachment chip in composer. {cr} is a number.', surface: 'ai', maxChars: 40 },
  NLSAiContextPickerLoadAndAttach: { text: 'Load & Attach', description: 'Button to load and attach chat messages or range.', surface: 'ai', maxChars: 20 },
  NLSAiContextPickerLoadHint: { text: 'Click Attach to load channel', description: 'Hint shown under channel name when not yet loaded.', surface: 'ai', maxChars: 42 },
  NLSAiContextPickerWillTrim: { text: 'Will trim to {n} most recent', description: 'Warning shown when channel message count exceeds cap. {n} is a number.', surface: 'ai', maxChars: 40 },
  NLSAiContextPickerSelectChannelHint: { text: 'Select a channel to browse messages', description: 'Prompt to pick a channel in specific messages mode.', surface: 'ai', maxChars: 44 },
  NLSAiContextPickerAriaDismissNotice: { text: 'Dismiss notice', description: 'Aria label for dismissing truncation warning banner.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerAttachRange: { text: 'Attach range', description: 'Button to attach chat date range.', surface: 'ai', maxChars: 20 },
  NLSAiContextPickerClickToLoad: { text: 'Click to load', description: 'Hint to click to load channel messages in range.', surface: 'ai', maxChars: 20 },
  NLSAiContextPickerRangeLast24h: { text: 'Last 24 hours', description: 'Option for chat history in last 24 hours.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerRangeLast7d: { text: 'Last 7 days', description: 'Option for chat history in last 7 days.', surface: 'ai', maxChars: 20 },
  NLSAiContextPickerRangeLast30d: { text: 'Last 30 days', description: 'Option for chat history in last 30 days.', surface: 'ai', maxChars: 20 },
  NLSAiContextPickerNoChannels: { text: 'No channels available.', description: 'Empty state when no chat channels exist.', surface: 'ai', maxChars: 32 },
  NLSAiContextPickerLoadingMessages: { text: 'Loading channel messages…', description: 'Loading indicator for channel messages.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerNoMessages: { text: 'No messages found.', description: 'Empty state when no chat messages match.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerMessagesLoaded: { text: '{count} messages loaded', description: 'Number of messages loaded for a channel. {count} is a number.', surface: 'ai', maxChars: 32 },
  NLSAiContextPickerMessagesInRange: { text: '{count} messages in range', description: 'Message count for selected date range. {count} is a number.', surface: 'ai', maxChars: 32 },
  NLSAiContextPickerMessagesSelected: { text: '{count} messages selected', description: 'Count of selected messages. {count} is a number.', surface: 'ai', maxChars: 32 },
  NLSAiContextPickerMessageSelectedOne: { text: '1 message selected', description: 'Count when 1 message is selected.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerFilterAll: { text: 'All', description: 'Filter chip for all file types.', surface: 'ai', maxChars: 12 },
  NLSAiContextPickerFilterImages: { text: 'Images', description: 'Filter chip for images.', surface: 'ai', maxChars: 18 },
  NLSAiContextPickerFilterDocuments: { text: 'Documents', description: 'Filter chip for documents.', surface: 'ai', maxChars: 20 },
  NLSAiContextPickerFilterSpreadsheets: { text: 'Spreadsheets', description: 'Filter chip for spreadsheets.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerFilterMedia: { text: 'Media', description: 'Filter chip for audio/video media.', surface: 'ai', maxChars: 18 },
  NLSAiContextPickerUntitledUpload: { text: 'Untitled upload', description: 'Fallback name for untitled file.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerNoFiles: { text: 'No files uploaded to this workspace yet.', description: 'Empty state when workspace has no uploads.', surface: 'ai', maxChars: 60 },
  NLSAiContextPickerNoFilesMatch: { text: 'No files match your current search or filter.', description: 'Empty state when search or filter returns no uploads.', surface: 'ai', maxChars: 60 },
  NLSAiContextPickerAriaClose: { text: 'Close', description: 'Aria label for close button in context picker modal.', surface: 'ai', maxChars: 18 },
  NLSAiContextPickerAriaCategories: { text: 'Context categories', description: 'Aria label for category navigation rail.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerLoadingDesigns: { text: 'Loading designs…', description: 'Loading state for design assets.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerNoDesigns: { text: 'No designs in this workspace yet.', description: 'Empty state when workspace has no designs.', surface: 'ai', maxChars: 50 },
  NLSAiContextPickerNoDesignsMatch: { text: 'No designs match your search.', description: 'Empty state when search has no matching designs.', surface: 'ai', maxChars: 50 },
  NLSAiContextPickerUntitledDesign: { text: 'Untitled design', description: 'Fallback label for design without title.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerLoadingForms: { text: 'Loading forms…', description: 'Loading state for form definitions.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerLoadingResponses: { text: 'Loading responses…', description: 'Loading state for form responses.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerNoForms: { text: 'No forms in this workspace yet.', description: 'Empty state when workspace has no forms.', surface: 'ai', maxChars: 50 },
  NLSAiContextPickerNoFormsMatch: { text: 'No forms match your search.', description: 'Empty state when search has no matching forms.', surface: 'ai', maxChars: 50 },
  NLSAiContextPickerNoResponses: { text: 'No responses found for this form.', description: 'Empty state when a form has no responses.', surface: 'ai', maxChars: 50 },
  NLSAiContextPickerUntitledForm: { text: 'Untitled form', description: 'Fallback label for form without title.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerAnonymous: { text: 'Anonymous', description: 'Fallback submitter name when respondent is unknown.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerUnknownDate: { text: 'Unknown date', description: 'Fallback date when submission timestamp is missing.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerSearchResponsesPlaceholder: { text: 'Search by name, status…', description: 'Placeholder for search responses input.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerResponsesSelected: { text: '{count} responses selected', description: 'Selection count in form responses picker. {count} is a number.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerResponseSelectedOne: { text: '1 response selected', description: 'Selection count for 1 response.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerNoResponsesAttachSchema: { text: 'No responses selected — attaches form schema only', description: 'Hint when no responses are checked.', surface: 'ai', maxChars: 60 },
  NLSAiContextPickerAttachSchemaTitle: { text: 'Attach form schema only', description: 'Tooltip on Schema button.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerPickResponsesTitle: { text: 'Pick specific responses', description: 'Tooltip on Responses button.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerFetchingSheet: { text: 'Fetching sheet…', description: 'Status while fetching Google Sheet CSV.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerSheetReady: { text: 'Sheet ready', description: 'Status when Google Sheet CSV was fetched successfully.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerFetchFailed: { text: 'Fetch failed', description: 'Error title when sheet fetch fails.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerFetchFailedMsg: { text: 'Failed to fetch the sheet.', description: 'Error message when Google Sheet fetch fails.', surface: 'ai', maxChars: 40 },
  NLSAiContextPickerNotGSheetUrl: { text: 'That does not look like a Google Sheets URL.', description: 'Error message for invalid Google Sheets URL.', surface: 'ai', maxChars: 54 },
  NLSAiContextPickerClear: { text: 'Clear', description: 'Button to clear input or state.', surface: 'ai', maxChars: 16 },
  NLSAiContextPickerChooseFile: { text: 'Choose file', description: 'Button to trigger local file selection.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerParsingFile: { text: 'Parsing {fileName}…', description: 'Status while parsing uploaded file.', surface: 'ai', maxChars: 40 },
  NLSAiContextPickerParseFailed: { text: 'Parse failed', description: 'Error title when file parsing fails.', surface: 'ai', maxChars: 24 },
  NLSAiContextPickerParseFailedMsg: { text: 'Failed to parse the file.', description: 'Error message when file parsing fails.', surface: 'ai', maxChars: 40 },
  NLSAiContextPickerTryAgain: { text: 'Try again', description: 'Button to retry after error.', surface: 'ai', maxChars: 20 },
  NLSAiContextPickerCheckingLink: { text: 'Checking link…', description: 'Status while checking public link.', surface: 'ai', maxChars: 28 },
  NLSAiContextPickerLinkSupported: { text: 'Line can read this link', description: 'Success status when link is readable.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerLinkNotSupported: { text: 'Link not supported', description: 'Error title when link cannot be read.', surface: 'ai', maxChars: 30 },
  NLSAiContextPickerLinkCannotRead: { text: 'Line cannot read this link.', description: 'Error message when link cannot be read.', surface: 'ai', maxChars: 40 },
  NLSAiContextPickerLinkCannotReach: { text: 'Line could not reach this link.', description: 'Error message when link cannot be reached.', surface: 'ai', maxChars: 40 },
  NLSAiContextPickerDragDropPrompt: { text: 'Click or drag images here', description: 'Dropzone prompt for image upload.', surface: 'ai', maxChars: 36 },
  NLSAiContextPickerImageFormatsHint: { text: 'JPG, PNG, GIF, WEBP · Multiple files supported', description: 'Supported image file formats hint.', surface: 'ai', maxChars: 54 },
  NLSAiContextPickerAriaUploadImages: { text: 'Upload images', description: 'Accessible label for image dropzone.', surface: 'ai', maxChars: 24 },
  NLSWorkspaceCurrencyHeroTitle: { text: 'What currency does your work happen in?', description: 'Hero title for workspace currency step in onboarding.', surface: 'onboarding', maxChars: 60 },
  NLSWorkspaceCurrencyHeroSubtitle: {
    text: "You can't change this later — pick with intention.",
    description: 'Hero subtitle for workspace currency step in onboarding.',
    surface: 'onboarding',
    maxChars: 70,
  },
  NLSWorkspaceCurrencyDetectedBadge: { text: 'Detected', description: 'Badge for detected currency based on user locale.', surface: 'onboarding', maxChars: 18 },
  NLSWorkspaceCurrencyShowAll: { text: 'Show all {count} currencies →', description: 'Action button to open full currency picker sheet.', surface: 'onboarding', maxChars: 40 },
  NLSWorkspaceCurrencySearchPlaceholder: { text: 'Search by code, name, or symbol', description: 'Placeholder for currency search input.', surface: 'onboarding', maxChars: 42 },
  NLSWorkspaceCurrencyContinueCTA: { text: 'Continue with {code}', description: 'Primary action CTA on currency picker step.', surface: 'onboarding', maxChars: 30 },
  NLSWorkspaceCurrencyLockedTooltip: {
    text: 'Locked to {code} — currency is set once per workspace.',
    description: 'Tooltip explaining currency is immutable post-creation.',
    surface: 'onboarding',
    maxChars: 73,
  },
  NLSWorkspaceCurrencyMigrationTitle: { text: 'Set your workspace currency', description: 'Title for existing workspace currency migration banner.', surface: 'onboarding', maxChars: 42 },
  NLSWorkspaceCurrencyMigrationBody: {
    text: "This workspace hasn't picked a currency yet. Pick now to display amounts correctly. Otherwise it will auto-lock to USD in {days}.",
    description: 'Body text explaining currency migration and countdown.',
    surface: 'onboarding',
    maxChars: 175,
  },
  NLSWorkspaceCurrencyMigrationSetNow: { text: 'Set currency now', description: 'Action button to pick workspace currency now.', surface: 'onboarding', maxChars: 25 },
  NLSWorkspaceCurrencyMigrationRemindLater: { text: 'Remind me later', description: 'Action button to snooze currency migration banner for 24h.', surface: 'onboarding', maxChars: 25 },
  NLSGoogleSheetsExportButton: { text: 'Export to Sheets', description: 'Button label for exporting items to Google Sheets.', surface: 'linkhub', maxChars: 24 },
  NLSGoogleSheetsExportDialogTitle: { text: 'Export to Google Sheets', description: 'Dialog title for Google Sheets export.', surface: 'linkhub', maxChars: 32 },
  NLSGoogleSheetsExportSuccess: { text: 'Successfully exported to Google Sheets.', description: 'Notification after successful Google Sheets export.', surface: 'linkhub', maxChars: 50 },
  NLSGoogleSheetsPasteUrlPrompt: { text: 'Or paste Google Sheet URL', description: 'Prompt for pasting Google Sheet link manually.', surface: 'linkhub', maxChars: 40 },
  NLSGoogleSheetsExporting: { text: 'Exporting to Sheets…', description: 'Loading state during Google Sheets export.', surface: 'linkhub', maxChars: 30 },
  NLSGoogleSheetsSelectSheet: { text: 'Select Google Sheet', description: 'Button to pick a Google Sheet via Picker.', surface: 'linkhub', maxChars: 30 },
  NLSGoogleSheetsNewSheet: { text: 'Create new Google Sheet', description: 'Option to create a new spreadsheet for export.', surface: 'linkhub', maxChars: 35 },
  NLSGoogleSheetsExistingSheet: { text: 'Update existing Google Sheet', description: 'Option to update an existing spreadsheet.', surface: 'linkhub', maxChars: 40 },
  NLSSlackPanelTitle: { text: 'Slack Channel Notifications', description: 'Title for Slack notifications panel.', surface: 'settings', maxChars: 40 },
  NLSSlackPanelSubtitle: {
    text: 'Route workspace task, checklist, and system alerts to a connected Slack channel.',
    description: 'Subtitle explaining Slack channel notification routing.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSSlackConnectedChannel: { text: 'Connected Channel', description: 'Label for connected Slack channel badge.', surface: 'settings', maxChars: 25 },
  NLSSlackNotConnected: {
    text: 'Not connected — connect Slack in Link Hub to enable channel notifications.',
    description: 'Status message when Slack is not connected.',
    surface: 'settings',
    maxChars: 90,
  },
  NLSSlackSendTestMessage: { text: 'Send test notification', description: 'Button label to trigger a test Slack message.', surface: 'settings', maxChars: 30 },
  NLSSlackTestSuccess: { text: 'Test notification sent to Slack.', description: 'Success notification after sending test Slack message.', surface: 'settings', maxChars: 45 },
  NLSSlackSendingTest: { text: 'Sending…', description: 'Button loading state while sending Slack notification.', surface: 'settings', maxChars: 20 },
  NLSSlackTaskNotificationsToggle: { text: 'Send task and checklist updates', description: 'Toggle label for sending task and checklist events to Slack.', surface: 'settings', maxChars: 50 },
  NLSLinkHubTabApiKeys: { text: 'API Keys', description: 'Tab label for Link Hub API Keys view.', surface: 'linkhub', maxChars: 20 },
  NLSLinkHubApiKeysTitle: { text: 'Public API Keys', description: 'Title for Link Hub API Keys panel.', surface: 'linkhub', maxChars: 30 },
  NLSLinkHubApiKeysSubtitle: {
    text: 'Generate and manage Bearer API keys for Zapier, Make, n8n, and custom webhooks.',
    description: 'Subtitle for Link Hub API Keys panel.',
    surface: 'linkhub',
    maxChars: 120,
  },
  NLSLinkHubGenerateKey: { text: 'Generate API Key', description: 'Button label to generate new API key.', surface: 'linkhub', maxChars: 25 },
  NLSLinkHubKeyName: { text: 'Key Name', description: 'Label for key name input.', surface: 'linkhub', maxChars: 20 },
  NLSLinkHubKeyScopes: { text: 'Scopes', description: 'Label for key scopes.', surface: 'linkhub', maxChars: 20 },
  NLSLinkHubKeyEntities: { text: 'Entities', description: 'Label for key entities.', surface: 'linkhub', maxChars: 20 },
  NLSLinkHubKeyRateLimit: { text: 'Rate Limit', description: 'Label for key rate limit.', surface: 'linkhub', maxChars: 20 },
  NLSLinkHubKeyStatus: { text: 'Status', description: 'Label for key status.', surface: 'linkhub', maxChars: 15 },
  NLSLinkHubKeyCreated: { text: 'Created', description: 'Label for key created date.', surface: 'linkhub', maxChars: 15 },
  NLSLinkHubKeyLastUsed: { text: 'Last Used', description: 'Label for key last used date.', surface: 'linkhub', maxChars: 15 },
  NLSLinkHubKeyRotate: { text: 'Rotate', description: 'Action button to rotate key.', surface: 'linkhub', maxChars: 15 },
  NLSLinkHubKeyRevoke: { text: 'Revoke', description: 'Action button to revoke key.', surface: 'linkhub', maxChars: 15 },
  NLSLinkHubKeyNever: { text: 'Never', description: 'Label when key has never been used.', surface: 'linkhub', maxChars: 10 },
  NLSLinkHubKeyPlaintextWarning: {
    text: 'Make sure to copy your API key now. You will not be able to see it again!',
    description: 'Warning prompt shown when minting key.',
    surface: 'linkhub',
    maxChars: 100,
  },
  NLSLinkHubKeyCopy: { text: 'Copy Key', description: 'Button to copy API key to clipboard.', surface: 'linkhub', maxChars: 15 },
  NLSLinkHubKeyCopied: { text: 'Copied!', description: 'Feedback when API key is copied.', surface: 'linkhub', maxChars: 15 },
  NLSLinkHubKeyDone: { text: 'Done', description: 'Button to close key modal.', surface: 'linkhub', maxChars: 15 },
  NLSLinkHubKeyDocsLink: { text: 'View API Documentation', description: 'Link to Swagger / API docs.', surface: 'linkhub', maxChars: 30 },
  NLSSupportCsatTitle: { text: 'How was your support experience?', description: 'Title for CSAT satisfaction rating card.', surface: 'settings', maxChars: 60 },
  NLSSupportCsatSubtitle: { text: 'Please rate your experience with Timelining Support.', description: 'Subtitle for CSAT card.', surface: 'settings', maxChars: 80 },
  NLSSupportCsatCommentPlaceholder: { text: 'Optional: Tell us what went well or how we can improve...', description: 'Placeholder for CSAT comment field.', surface: 'settings', maxChars: 120 },
  NLSSupportCsatSubmit: { text: 'Submit feedback', description: 'Button text to submit CSAT feedback.', surface: 'settings', maxChars: 24 },
  NLSSupportCsatThankYou: { text: 'Thank you for your feedback!', description: 'Confirmation message after submitting CSAT feedback.', surface: 'settings', maxChars: 60 },
  NLSSupportCsatSubmitted: { text: 'Feedback submitted', description: 'Status badge indicating CSAT feedback has been submitted.', surface: 'settings', maxChars: 40 },
  NLSStatusMarkersLabel: { text: 'Status markers', description: 'Label for status markers preference control.', surface: 'settings', maxChars: 24 },
  NLSStatusMarkersOptionShort: { text: 'Short', description: 'Option for short/compact status markers.', surface: 'settings', maxChars: 16 },
  NLSStatusMarkersOptionLong: { text: 'Long', description: 'Option for long/text status markers.', surface: 'settings', maxChars: 16 },
  NLSStatusMarkersHelp: { text: 'Choose between compact edge glyphs or text tags on review items.', description: 'Help text for status markers preference.', surface: 'settings', maxChars: 120 },
  NLSLoadingStyleLabel: { text: 'Loading style', description: 'Label for loading style preference control.', surface: 'settings', maxChars: 24 },
  NLSLoadingStyleOptionConstellation: { text: 'Constellation', description: 'Option for constellation loading animation.', surface: 'settings', maxChars: 20 },
  NLSLoadingStyleOptionStarfield: { text: 'Starfield', description: 'Option for starfield loading animation.', surface: 'settings', maxChars: 20 },
  NLSLoadingStyleHelp: { text: 'Choose how loading screens look.', description: 'Help text for loading style preference.', surface: 'settings', maxChars: 120 },
  NLSMarkerNew: { text: 'new', description: 'Status marker text for new/added items.', surface: 'manage', maxChars: 20 },
  NLSMarkerEdited: { text: 'edited', description: 'Status marker text for edited items.', surface: 'manage', maxChars: 20 },
  NLSMarkerRemoved: { text: 'removed', description: 'Status marker text for removed items.', surface: 'manage', maxChars: 20 },
  NLSMarkerNeedsAttention: { text: 'needs attention', description: 'Status marker text for items needing attention or invalid.', surface: 'manage', maxChars: 30 },
  NLSMarkerUnsavedChanges: { text: 'unsaved changes', description: 'Status marker text for items with unsaved changes.', surface: 'manage', maxChars: 30 },
  NLSValidationBannerNeedAttention: { text: '{count} need attention', description: 'Validation banner text for multiple items needing attention.', surface: 'manage', maxChars: 40 },
  NLSValidationBannerNeedAttentionSingular: { text: '1 needs attention', description: 'Validation banner text for single item needing attention.', surface: 'manage', maxChars: 40 },
  NLSValidationBannerMoreCount: { text: '+{count} more', description: 'Validation banner text for overflow count of issues.', surface: 'manage', maxChars: 20 },
  NLSAdminBudgetEnforcementLabel: { text: 'Budget enforcement', description: 'Label for budget enforcement setting in admin resources panel.', surface: 'settings', maxChars: 30 },
  NLSAdminBudgetEnforcementWarn: { text: 'Warn', description: 'Warn option for budget enforcement segmented control.', surface: 'settings', maxChars: 16 },
  NLSAdminBudgetEnforcementBlock: { text: 'Block', description: 'Block option for budget enforcement segmented control.', surface: 'settings', maxChars: 16 },
  NLSAdminBudgetEnforcementHint: { text: 'Block stops over-limit completions and over-limit manual expenses.', description: 'Hint explaining enforcement options under budget enforcement segmented control.', surface: 'settings', maxChars: 85 },
  NLSAdminBudgetEnforcementSaveFailed: { text: 'Could not update budget enforcement.', description: 'Error message when saving budget enforcement setting fails.', surface: 'settings', maxChars: 60 },
  NLSHelperCommentDeleteTitle: { text: 'Delete comment', description: 'Title for comment deletion confirmation modal.', surface: 'helper', maxChars: 28 },
  NLSHelperCommentDeleteBody: { text: 'Are you sure you want to delete this comment? This cannot be undone.', description: 'Body for comment deletion confirmation modal.', surface: 'helper', maxChars: 100 },
  NLSHelperCommentDeleteConfirm: { text: 'Delete comment', description: 'Confirm button label for comment deletion modal.', surface: 'helper', maxChars: 24 },
  NLSHelperCalendarViews: { text: 'Calendar views', description: 'Accessible label for calendar view mode tabs switcher.', surface: 'helper', maxChars: 28 },
  NLSDetailedViewFigmaDialogTitle: { text: 'Attach Figma file', description: 'Title for modal to attach a Figma file to a task.', surface: 'manage', maxChars: 28 },
  NLSDetailedViewFigmaDialogPrompt: { text: 'Paste the Figma file URL to attach.', description: 'Input prompt/placeholder for attaching a Figma file URL.', surface: 'manage', maxChars: 50 },
  NLSDetailedViewFigmaDialogAttach: { text: 'Attach', description: 'Confirm button to attach Figma file.', surface: 'manage', maxChars: 16 },
  NLSDetailedViewFigmaAttachedToast: { text: 'Figma file attached.', description: 'Success toast when a Figma file is attached.', surface: 'manage', maxChars: 30 },
  NLSDetailedViewFigmaAttachFailedToast: { text: 'Could not attach Figma file.', description: 'Error toast when attaching Figma file fails.', surface: 'manage', maxChars: 40 },
  NLSInvitesRecipientEmailLabel: { text: 'Recipient email', description: 'Accessible label for recipient email input.', surface: 'settings', maxChars: 24 },
  NLSDetailedViewContextMenuLabel: { text: 'Context menu', description: 'Accessible label for folder tree context menu.', surface: 'manage', maxChars: 20 },
  NLSDetailedViewScheduleStartTime: { text: 'Start time', description: 'Accessible label for schedule start time picker.', surface: 'manage', maxChars: 18 },
  NLSDetailedViewScheduleEndTime: { text: 'End time', description: 'Accessible label for schedule end time picker.', surface: 'manage', maxChars: 18 },
  NLSAccessScopeSectionTitle: { text: 'Access', description: 'Section title for member access scoping.', surface: 'member', maxChars: 20 },
  NLSAccessScopeOwnerAdminNote: { text: 'Owners and Admins always have access to all goals.', description: 'Note explaining owners and admins have full access across the workspace.', surface: 'member', maxChars: 80 },
  NLSAccessScopeModeAllGoals: { text: 'All goals', description: 'Access scope mode button for full workspace access.', surface: 'member', maxChars: 24 },
  NLSAccessScopeModeOnlyChosen: { text: 'Only chosen goals', description: 'Access scope mode button for restricted access.', surface: 'member', maxChars: 28 },
  NLSAccessScopeModeAria: { text: 'Goal access mode', description: 'Accessible label for the goal access mode selector.', surface: 'member', maxChars: 30 },
  NLSAccessScopeGrantNone: { text: 'None', description: 'No access option in goal access scope select.', surface: 'member', maxChars: 16 },
  NLSAccessScopeGrantView: { text: 'View', description: 'View access option in goal access scope select.', surface: 'member', maxChars: 16 },
  NLSAccessScopeGrantEdit: { text: 'Edit', description: 'Edit access option in goal access scope select.', surface: 'member', maxChars: 16 },
  NLSAccessScopeGrantAria: { text: 'Access for {title}', description: 'Accessible label for node access select.', surface: 'member', maxChars: 50 },
  NLSAccessScopeCeilingTip: { text: 'Viewers and read-only members cannot be given Edit access.', description: 'Tooltip explaining role ceiling disables Edit.', surface: 'member', maxChars: 70 },
  NLSAccessScopeInheritedFrom: { text: 'from {parent}', description: 'Label showing inherited access parent title.', surface: 'member', maxChars: 40 },
  NLSAccessScopeActionSave: { text: 'Save', description: 'Save button in access scope panel.', surface: 'member', maxChars: 16 },
  NLSAccessScopeActionSaving: { text: 'Saving…', description: 'Saving in progress state in access scope panel.', surface: 'member', maxChars: 18 },
  NLSAccessScopeActionDiscard: { text: 'Discard', description: 'Discard draft changes in access scope panel.', surface: 'member', maxChars: 18 },
  NLSAccessScopeSaved: { text: 'Changes saved', description: 'Success message after saving access scope.', surface: 'member', maxChars: 24 },
  NLSAccessScopeSaveFailed: { text: 'Failed to save access changes.', description: 'Error message when saving access scope fails.', surface: 'member', maxChars: 60 },
  NLSAccessScopeLoadFailed: { text: "Couldn't load access settings.", description: 'Error message when loading access scope fails.', surface: 'member', maxChars: 60 },
  NLSAccessScopeOverLimit: { text: 'Cannot grant access to more than 500 items.', description: 'Error message when grant count exceeds 500 limit.', surface: 'member', maxChars: 60 },
  NLSAccessScopeLoading: { text: 'Loading access settings…', description: 'Loading state while fetching member access scope.', surface: 'member', maxChars: 40 },
  NLSAccessScopeEmptyTree: { text: 'No goals found in this workspace.', description: 'Empty state message when no timeline goals exist.', surface: 'member', maxChars: 50 },
  NLSAccessScopeInviteFieldHint: { text: 'Choose which goals this member can access when joining.', description: 'Hint explaining access scoping on member invitation.', surface: 'member', maxChars: 70 },
  NLSLinkedGoalLabel: { text: 'Linked goal', description: 'Label for linked goal selector on channels and meetings.', surface: 'common', maxChars: 30 },
  NLSLinkedGoalNone: { text: 'None', description: 'Option to clear or leave channel/meeting unlinked from goals.', surface: 'common', maxChars: 15 },
  NLSLinkedGoalHint: { text: 'People limited to this goal can see it.', description: 'Hint explaining visibility when linked to a goal.', surface: 'common', maxChars: 60 },
  NLSOwnerConsoleNavDemoLane: { text: 'Demo lane', description: 'Owner Console nav item for the Demo lane view.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneTitle: { text: 'Demo lane', description: 'Heading of the Owner Console Demo lane view.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneSubtitle: { text: 'The demo website and phone apps used for demos, kept apart from SIT.', description: 'Subtitle of the Demo lane view.', surface: 'owner-console', maxChars: 90 },
  NLSOwnerConsoleDemoLaneFounderOnly: { text: 'Only the founder can see the demo lane.', description: 'Shown to non-founder owner roles on the Demo lane view.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleDemoLaneWebsiteTitle: { text: 'Website', description: 'Section title for the demo website releases.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleDemoLaneWebsiteHint: { text: 'Releases promoted to the demo address. Roll back serves an earlier release again within seconds.', description: 'Hint under the Website section title.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleDemoLaneWebsiteLive: { text: 'Live now', description: 'Label above the currently live demo website release.', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleDemoLaneWebsiteLiveUnknown: { text: 'Live release unknown', description: 'Shown when the live demo website release cannot be read.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneWebsiteRecent: { text: 'Last 10 releases', description: 'Label above the demo website release table.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneWebsiteEmpty: { text: 'No demo releases recorded yet. Promote the demo branch to create the first.', description: 'Empty state for the demo website release table.', surface: 'owner-console', maxChars: 90 },
  NLSOwnerConsoleDemoLaneColCommit: { text: 'Commit', description: 'Column header: short git sha of a demo release.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneColBuilt: { text: 'Built', description: 'Column header: build date of a demo release.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneColBranch: { text: 'Branch', description: 'Column header: git branch of a demo release.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneColVersion: { text: 'Hosting version', description: 'Column header: Firebase Hosting version id of a demo release.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneColStatus: { text: 'Status', description: 'Column header: live or not for a demo release.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneStatusLive: { text: 'Live', description: 'Badge for the live demo release.', surface: 'owner-console', maxChars: 12 },
  NLSOwnerConsoleDemoLaneRollBack: { text: 'Roll back', description: 'Button to serve an earlier demo website release again.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneRollBackTitle: { text: 'Roll back the demo website', description: 'Title of the roll back confirmation.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDemoLaneRollBackDescription: { text: 'The demo address will serve commit {sha} (built {built}) instead of the current release. Give a reason to confirm.', description: 'Body of the roll back confirmation.', surface: 'owner-console', maxChars: 140 },
  NLSOwnerConsoleDemoLaneRollBackConfirm: { text: 'Roll back', description: 'Confirm button of the roll back dialog.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneRollBackDone: { text: 'Demo website rolled back to {sha}.', description: 'Status after a successful roll back.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleDemoLaneRefresh: { text: 'Refresh', description: 'Button to reload the demo lane data.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneLoading: { text: 'Loading releases…', description: 'Shown while the demo website releases load.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneLoadFailed: { text: 'Could not load the demo releases', description: 'Heading above an error when loading the demo releases fails.', surface: 'owner-console', maxChars: 48 },
  NLSOwnerConsoleDemoLaneLiveError: { text: 'Could not read the live release', description: 'Heading above the Hosting error when the live release cannot be read.', surface: 'owner-console', maxChars: 48 },
  NLSOwnerConsoleDemoLaneRollBackFailed: { text: 'Roll back failed', description: 'Heading above the error when a roll back fails.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLanePhoneTitle: { text: 'Phone apps', description: 'Section title for the demo lane phone apps.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLanePhoneHint: { text: 'What the SIT and demo update channels point at. Promote sends a release to the demo channel; phones pick it up on next open.', description: 'Hint under the Phone apps title.', surface: 'owner-console', maxChars: 140 },
  NLSOwnerConsoleDemoLanePhoneNativeNote: { text: 'Native changes (widgets, voice) need a new APK install on the phone, not a promote.', description: 'Note that native changes need a new APK.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleDemoLanePhoneMember: { text: 'Member app', description: 'Name of the Member phone app.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLanePhoneHelper: { text: 'Helper app', description: 'Name of the Helper phone app.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLanePhoneSitChannel: { text: 'SIT channel', description: 'Label for the SIT update channel pointer.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLanePhoneDemoChannel: { text: 'Demo channel', description: 'Label for the demo update channel pointer.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLanePhoneNoRelease: { text: 'none', description: 'Shown when a channel has no release.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLanePhoneColVersion: { text: 'Version', description: 'Column header: phone app version.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLanePhoneColChannel: { text: 'Published to', description: 'Column header: channel a release was published to.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLanePhoneColDate: { text: 'Released', description: 'Column header: release date.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLanePhoneOnDemo: { text: 'On demo', description: 'Badge for the release the demo channel points at.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLanePhonePromote: { text: 'Promote to demo', description: 'Button to point the demo channel at a release.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLanePhoneRollBack: { text: 'Roll back', description: 'Button to point the demo channel at an older release.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLanePhonePromoteTitle: { text: 'Promote to demo', description: 'Title of the promote confirmation.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLanePhoneRollBackTitle: { text: 'Roll back the demo app', description: 'Title of the roll back confirmation.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDemoLanePhoneConfirmDescription: { text: 'The demo channel will serve {app} version {version}. Give a reason to confirm.', description: 'Body of the promote and roll back confirmation.', surface: 'owner-console', maxChars: 100 },
  NLSOwnerConsoleDemoLanePhoneDone: { text: 'Demo channel set: {app} {version}.', description: 'Status after a successful promote or roll back.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleDemoLanePhoneLoadFailed: { text: 'Could not load the phone releases', description: 'Heading above an error when loading phone releases fails.', surface: 'owner-console', maxChars: 48 },
  NLSOwnerConsoleDemoLanePhonePromoteFailed: { text: 'Could not change the demo channel', description: 'Heading above the error when a promote fails.', surface: 'owner-console', maxChars: 48 },
  NLSOwnerConsoleDemoLanePhoneEmpty: { text: 'No releases recorded yet. Publish a bundle to create the first.', description: 'Empty state for the phone release table.', surface: 'owner-console', maxChars: 90 },
  NLSOwnerConsoleDemoLanePhoneApkLabel: { text: 'Demo APK link', description: 'Label for the demo APK download link.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLanePhoneApkNone: { text: 'No demo APK published yet.', description: 'Shown when no demo APK has been published to the demo channel.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDemoLanePhoneApkCopy: { text: 'Copy', description: 'Button to copy the demo APK download link.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLanePhoneApkCopied: { text: 'Copied', description: 'Shown after copying the demo APK download link.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLanePhoneApkNote: { text: 'Open on the phone in Chrome to install; allow Install unknown apps once. The link always holds the newest published demo APK; Roll back changes only what the apps update to.', description: 'Note explaining demo APK installation and roll back behavior.', surface: 'owner-console', maxChars: 200 },
  NLSOwnerConsoleDemoLaneReadyTitle: { text: 'Ready to demo', description: 'Section title for the Ready to demo card.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneReadyHint: { text: 'Checks the demo workspace before a demo. Fixes open the workspace, where they ask you to confirm.', description: 'Hint under the Ready to demo title.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleDemoLaneReadyWorkspace: { text: 'Demo workspace', description: 'Label of the demo workspace picker.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneReadyWorkspaceNamed: { text: 'Workspace: {name}', description: 'Shows which demo workspace is checked when there is only one.', surface: 'owner-console', maxChars: 60 },
  NLSOwnerConsoleDemoLaneReadyNoWorkspace: { text: 'No demo-unlocked workspace yet', description: 'Shown when no workspace has demo unlock on.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDemoLaneReadyNoWorkspaceBody: { text: 'Turn on demo unlock for a workspace, then come back.', description: 'Hint under the no-demo-workspace message.', surface: 'owner-console', maxChars: 64 },
  NLSOwnerConsoleDemoLaneReadyOpenWorkspaces: { text: 'Open Workspaces', description: 'Button to open the Workspaces list.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneReadyWorkspacesFailed: { text: 'Could not load the workspaces', description: 'Heading above the error when the workspace list fails to load.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDemoLaneReadyLoadFailed: { text: 'Could not run the readiness checks', description: 'Heading above the error when the readiness callable fails.', surface: 'owner-console', maxChars: 48 },
  NLSOwnerConsoleDemoLaneReadyChecking: { text: 'Checking…', description: 'Shown while the readiness checks run.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneReadyOverallGreen: { text: 'Ready to demo', description: 'Overall result when every check is green.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneReadyOverallAmber: { text: 'Nearly ready', description: 'Overall result when the worst check is amber.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneReadyOverallRed: { text: 'Not ready', description: 'Overall result when any check is red.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneReadyStatusGreen: { text: 'OK', description: 'Status label of a green readiness check.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneReadyStatusAmber: { text: 'Check', description: 'Status label of an amber readiness check.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneReadyStatusRed: { text: 'Blocked', description: 'Status label of a red readiness check.', surface: 'owner-console', maxChars: 16 },
  NLSOwnerConsoleDemoLaneReadyCheckUnlock: { text: 'Demo unlock', description: 'Readiness check title.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneReadyCheckWallet: { text: 'Credits', description: 'Readiness check title.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneReadyCheckStorySaved: { text: 'Saved story', description: 'Readiness check title.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneReadyCheckStoryRestored: { text: 'Story restored', description: 'Readiness check title.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneReadyCheckReleaseSync: { text: 'Release sync', description: 'Readiness check title.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneReadyCheckFreeze: { text: 'Demo freeze', description: 'Readiness check title.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneReadyCheckMaintenance: { text: 'Maintenance mode', description: 'Readiness check title.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneReadyFixTurnOnUnlock: { text: 'Turn on unlock', description: 'Button that opens the workspace to turn demo unlock on.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneReadyFixRestoreStory: { text: 'Restore story', description: 'Button that opens the workspace to restore a saved story.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneReadyFixGrantCredits: { text: 'Grant credits', description: 'Button that opens the workspace to grant credits.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneFreezeTitle: { text: 'Demo freeze', description: 'Section title for the demo freeze control.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneFreezeHint: { text: 'While a freeze is active, the SIT deploy scripts refuse to run. It is a convention aid, not a lock.', description: 'Hint under the Demo freeze title.', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleDemoLaneFreezeNone: { text: 'No freeze is active.', description: 'Shown when no demo freeze is active.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDemoLaneFreezeActive: { text: 'Freeze active until {until} UTC', description: 'Shown while a demo freeze is active.', surface: 'owner-console', maxChars: 56 },
  NLSOwnerConsoleDemoLaneFreezeNote: { text: 'Note: {note}', description: 'Note of the active freeze.', surface: 'owner-console', maxChars: 340 },
  NLSOwnerConsoleDemoLaneFreezeSetBy: { text: 'Set by {user}', description: 'Who started the active freeze.', surface: 'owner-console', maxChars: 80 },
  NLSOwnerConsoleDemoLaneFreezeHours: { text: 'Hours (1 to 24)', description: 'Label of the freeze length input.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneFreezeNoteLabel: { text: 'Note (optional)', description: 'Label of the freeze note input.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneFreezeHoursInvalid: { text: 'Enter 1 to 24 hours.', description: 'Shown when the freeze length is out of range.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneFreezeStart: { text: 'Start freeze', description: 'Button to start a demo freeze.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneFreezeEnd: { text: 'End freeze now', description: 'Button to end the active demo freeze.', surface: 'owner-console', maxChars: 24 },
  NLSOwnerConsoleDemoLaneFreezeStartTitle: { text: 'Start demo freeze', description: 'Title of the start-freeze confirmation.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneFreezeStartDescription: { text: 'SIT deploys will be refused for {hours} h.', description: 'Description in the start-freeze confirmation.', surface: 'owner-console', maxChars: 64 },
  NLSOwnerConsoleDemoLaneFreezeEndTitle: { text: 'End demo freeze', description: 'Title of the end-freeze confirmation.', surface: 'owner-console', maxChars: 32 },
  NLSOwnerConsoleDemoLaneFreezeEndDescription: { text: 'SIT deploys can resume right away.', description: 'Description in the end-freeze confirmation.', surface: 'owner-console', maxChars: 64 },
  NLSOwnerConsoleDemoLaneFreezeStarted: { text: 'Demo freeze started.', description: 'Status after a freeze was started.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDemoLaneFreezeEnded: { text: 'Demo freeze ended.', description: 'Status after a freeze was ended.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDemoLaneFreezeFailed: { text: 'Could not change the freeze', description: 'Heading above the error when changing the freeze fails.', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleDemoLaneFreezeLoadFailed: { text: 'Could not read the freeze state', description: 'Heading above the error when the freeze state cannot be read.', surface: 'owner-console', maxChars: 48 },
  NLSWorkspaceBrandingTitle: { text: 'Workspace look', description: 'Workspace look and branding section title.', surface: 'settings', maxChars: 30 },
  NLSWorkspaceBrandingLogo: { text: 'Logo', description: 'Workspace logo field title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceBrandingLogoHint: { text: 'PNG or WebP, up to 256 KB', description: 'Allowed formats and maximum file size for logo upload.', surface: 'settings', maxChars: 40 },
  NLSWorkspaceBrandingAccent: { text: 'Accent colour', description: 'Workspace accent colour field title.', surface: 'settings', maxChars: 20 },
  NLSWorkspaceBrandingContrastWarn: { text: 'This colour may be hard to read', description: 'Warning shown when the chosen accent colour has low contrast against the surface.', surface: 'settings', maxChars: 50 },
  NLSWorkspaceBrandingRemove: { text: 'Remove logo', description: 'Action button to remove the workspace logo.', surface: 'settings', maxChars: 24 },
  NLSWorkspaceBrandingUploadFailed: { text: 'Could not upload workspace logo.', description: 'Error message when workspace logo upload fails.', surface: 'settings', maxChars: 50 },
  NLSWorkspaceBrandingRemoveFailed: { text: 'Could not remove workspace logo.', description: 'Error message when removing workspace logo fails.', surface: 'settings', maxChars: 50 },
  NLSWorkspaceBrandingAccentFailed: { text: 'Could not save accent colour.', description: 'Error message when saving workspace accent colour fails.', surface: 'settings', maxChars: 50 },
  NLSJoinCodesPanelTitle: { text: 'Staff Join Codes', description: 'Title of staff join codes panel.', surface: 'settings', maxChars: 30 },
  NLSJoinCodesPanelDescription: { text: 'Generate personal codes and PINs for team admins, editors, viewers, and helpers.', description: 'Description for staff join codes panel.', surface: 'settings', maxChars: 90 },
  NLSJoinCodesCreateOneBtn: { text: 'Create join code', description: 'Button label to open create staff join code dialog.', surface: 'settings', maxChars: 25 },
  NLSJoinCodesImportCsvBtn: { text: 'Import from CSV', description: 'Button label to open bulk CSV import dialog.', surface: 'settings', maxChars: 25 },
  NLSJoinCodesEmptyTitle: { text: 'No staff join codes yet', description: 'Empty state title for staff join codes.', surface: 'settings', maxChars: 35 },
  NLSJoinCodesEmptyBody: { text: 'Create a join code or import from CSV to invite staff members.', description: 'Empty state description for staff join codes.', surface: 'settings', maxChars: 80 },
  NLSJoinCodesCreateDialogTitle: { text: 'Create join code', description: 'Title of create staff join code dialog.', surface: 'settings', maxChars: 30 },
  NLSJoinCodesColName: { text: 'Name', description: 'Column label for recipient name.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesColRole: { text: 'Role', description: 'Column label for assigned role.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesColStatus: { text: 'Status', description: 'Column label for join code status.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesColCode: { text: 'Code', description: 'Column label for join code hint.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesColExpiry: { text: 'Expiry', description: 'Column label for join code expiration date.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesColActions: { text: 'Actions', description: 'Column label for join code table actions.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesStatusPending: { text: 'Pending', description: 'Status label for pending join code.', surface: 'settings', maxChars: 15 },
  NLSJoinCodesStatusClaimed: { text: 'Claimed', description: 'Status label for claimed join code.', surface: 'settings', maxChars: 15 },
  NLSJoinCodesStatusExpired: { text: 'Expired', description: 'Status label for expired join code.', surface: 'settings', maxChars: 15 },
  NLSJoinCodesStatusRevoked: { text: 'Revoked', description: 'Status label for revoked join code.', surface: 'settings', maxChars: 15 },
  NLSJoinCodesStatusLocked: { text: 'Locked', description: 'Status label for locked join code.', surface: 'settings', maxChars: 15 },
  NLSJoinCodesReissue: { text: 'Re-issue', description: 'Button label to re-issue join code.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesRevoke: { text: 'Revoke', description: 'Button label to revoke join code.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesRevokeConfirmTitle: { text: 'Revoke join code?', description: 'Confirmation title for revoking a join code.', surface: 'settings', maxChars: 35 },
  NLSJoinCodesRevokeConfirmBody: { text: 'This join code will be permanently invalidated. The recipient will not be able to use it.', description: 'Confirmation body for revoking a join code.', surface: 'settings', maxChars: 120 },
  NLSJoinCodesRevokeConfirmAction: { text: 'Revoke', description: 'Confirm button label for revoking a join code.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesOncePanelTitle: { text: 'Join Codes', description: 'Header title for join codes once panel.', surface: 'settings', maxChars: 30 },
  NLSJoinCodesOncePanelNotice: { text: "These PINs won't be shown again. Copy or distribute them now.", description: 'Notice warning that PINs will not be shown again.', surface: 'settings', maxChars: 90 },
  NLSJoinCodesCopy: { text: 'Copy code & PIN', description: 'Button label to copy code and PIN.', surface: 'settings', maxChars: 30 },
  NLSJoinCodesCopied: { text: 'Copied!', description: 'Feedback indicating code and PIN copied.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesDone: { text: 'Done', description: 'Button label to dismiss codes panel.', surface: 'settings', maxChars: 20 },
  NLSJoinCodesPrintSlips: { text: 'Print slips', description: 'Button label to open print slips sheet.', surface: 'settings', maxChars: 25 },
  NLSJoinCodesExpiresAt: { text: 'Expires {date}', description: 'Text indicating code expiry date.', surface: 'settings', maxChars: 35 },
  NLSJoinCodesSeatsFull: { text: 'Workspace roster seat limit reached ({used}/{seats}).', description: 'Error message when seat limit reached.', surface: 'settings', maxChars: 80 },
  NLSJoinCodesNotReissuable: { text: 'Claimed or revoked join codes cannot be re-issued.', description: 'Error message when code cannot be re-issued.', surface: 'settings', maxChars: 70 },
  NLSJoinCodesAlreadyUsed: { text: 'Cannot revoke an already claimed join code.', description: 'Error message when code is already claimed.', surface: 'settings', maxChars: 60 },
  NLSJoinCodesPartialCommitError: { text: '{created} of {total} join codes created. Failed to create remaining join codes.', description: 'Error message showing partial progress on bulk import failure.', surface: 'settings', maxChars: 85 },
  NLSJoinCodesGenericError: { text: 'An unexpected error occurred', description: 'Generic error message when an operation fails.', surface: 'settings', maxChars: 40 },
  NLSJoinCodesAdminOwnerOnly: { text: 'Only workspace owners can create join codes for the Admin role.', description: 'Validation error when non-owner selects Admin role.', surface: 'settings', maxChars: 75 },
  NLSJoinCodesInvalidRole: { text: 'Role must be ADMIN, EDITOR, VIEWER, or HELPER.', description: 'Validation error for invalid role.', surface: 'settings', maxChars: 60 },
  NLSJoinCodesNameRequired: { text: 'Name is required.', description: 'Validation error when name is missing.', surface: 'settings', maxChars: 30 },
  NLSJoinCodesRoleRequired: { text: 'Role is required.', description: 'Validation error when role is missing.', surface: 'settings', maxChars: 30 },
  NLSJoinCodesLoadError: { text: 'Failed to load join codes', description: 'Error message when loading join codes fails.', surface: 'settings', maxChars: 35 },
  NLSJoinCodesImportDialogTitle: { text: 'Import join codes from CSV', description: 'Dialog title for bulk CSV import of join codes.', surface: 'settings', maxChars: 40 },
  NLSJoinCodesSlipCode: { text: 'Code', description: 'Label for code on slip.', surface: 'settings', maxChars: 15 },
  NLSJoinCodesSlipPin: { text: 'PIN', description: 'Label for PIN on slip.', surface: 'settings', maxChars: 15 },
  NLSJoinCodesSlipKeepPrivate: { text: 'Keep this slip private', description: 'Privacy warning on slip.', surface: 'settings', maxChars: 35 },
  NLSJoinCodesSlipStep1: { text: '1. Open the app or scan the QR code', description: 'Instruction step 1 on slip.', surface: 'settings', maxChars: 50 },
  NLSJoinCodesSlipStep2: { text: '2. Sign in with your account', description: 'Instruction step 2 on slip.', surface: 'settings', maxChars: 50 },
  NLSJoinCodesSlipStep3: { text: '3. Enter your personal code and PIN', description: 'Instruction step 3 on slip.', surface: 'settings', maxChars: 50 },

  // APP.C3: Mobile Settings and Helper Calendar legend strings
  NLSMobileSettingsSaveNote: { text: 'Changes save as you edit.', description: 'Help text informing users that mobile settings changes save automatically as they edit.', surface: 'settings', maxChars: 40 },
  NLSHelperCalendarLegendTitle: { text: 'Calendar legend', description: 'Title and accessible label for calendar legend sheet.', surface: 'helper', maxChars: 30 },
  NLSHelperCalendarLegendDaily: { text: 'Daily digest / events', description: 'Legend label for daily digest or event indicator dot in calendar.', surface: 'helper', maxChars: 40 },
  NLSHelperCalendarLegendWeekly: { text: 'Weekly summary / milestones', description: 'Legend label for weekly summary or milestone indicator dot in calendar.', surface: 'helper', maxChars: 40 },
  NLSHelperCalendarLegendMeetings: { text: 'Scheduled meetings', description: 'Legend label for scheduled meetings indicator dot in calendar.', surface: 'helper', maxChars: 40 },

  // APP.D: Shell side menu and Helper sync status pill labels
  NLSSideMenuSettings: { text: 'Settings', description: 'Side navigation menu Settings entry label.', surface: 'member', maxChars: 20 },
  NLSHelperMenuStatusReady: { text: 'All synced', description: 'Helper menu drawer sync status pill text when all changes are synced.', surface: 'helper', maxChars: 20 },
  NLSHelperMenuStatusQueued: { text: 'Waiting to sync', description: 'Helper menu drawer sync status pill text when changes are queued.', surface: 'helper', maxChars: 24 },
  NLSHelperMenuStatusOffline: { text: 'Offline', description: 'Helper menu drawer sync status pill text when offline.', surface: 'helper', maxChars: 16 },
  NLSHelperMenuStatusFocus: { text: 'Focus on', description: 'Helper menu drawer sync status pill text when focus mode is active.', surface: 'helper', maxChars: 16 },

  // APP.E2: Helper single screens
  NLSHelperChecklistLiveStatus: { text: 'Live status', description: 'Accessible label for live status chip in helper checklist details.', surface: 'helper', maxChars: 24 },

  // FI12.common: StatusMark tone accessibility and labels
  NLSStatusMarkToneOk: { text: 'OK', description: 'Status mark tone OK label.', surface: 'common', maxChars: 16 },
  NLSStatusMarkToneLow: { text: 'Low', description: 'Status mark tone Low label.', surface: 'common', maxChars: 16 },
  NLSStatusMarkToneCritical: { text: 'Critical', description: 'Status mark tone Critical label.', surface: 'common', maxChars: 16 },
  NLSStatusMarkToneExpiring: { text: 'Expiring', description: 'Status mark tone Expiring label.', surface: 'common', maxChars: 16 },
  NLSStatusMarkToneWaiting: { text: 'Waiting', description: 'Status mark tone Waiting label.', surface: 'common', maxChars: 16 },

  // FI7.a: Barcode scanner camera permission
  NLSBarcodeScannerPermissionDenied: { text: 'Camera access denied. Type the code.', description: 'Error message when camera access is denied with instruction to enter code manually.', surface: 'common', maxChars: 64 },

  // FI17.b: Helper strings and follow-ups
  NLSHelperNoWorkspaceTitle: { text: 'Waiting for assignment', description: 'Helper no-workspace screen title.', surface: 'helper', maxChars: 40 },
  NLSHelperNoWorkspaceDescription: { text: 'This helper account does not have an active workspace yet. Accept an invite or wait for an admin to assign one.', description: 'Helper no-workspace screen description.', surface: 'helper', maxChars: 160 },
  NLSHelperIncomingInvites: { text: 'Incoming invites', description: 'Action button to open incoming invites.', surface: 'helper', maxChars: 24 },
  NLSHelperSignOut: { text: 'Sign out', description: 'Action button to sign out of helper account.', surface: 'helper', maxChars: 20 },

  NLSHelperMenuOverview: { text: 'Overview', description: 'Helper menu drawer Overview entry label.', surface: 'helper', maxChars: 20 },
  NLSHelperMenuOverviewDesc: { text: 'Read the workspace About page.', description: 'Helper menu drawer Overview entry description.', surface: 'helper', maxChars: 50 },
  NLSHelperMenuFormsDesc: { text: 'Fill out assigned forms and view responses.', description: 'Helper menu drawer Forms entry description.', surface: 'helper', maxChars: 60 },
  NLSHelperMenuFocus: { text: 'Focus mode', description: 'Helper menu drawer Focus mode entry label.', surface: 'helper', maxChars: 20 },
  NLSHelperMenuFocusDesc: { text: 'Deep-work overlay with a timer.', description: 'Helper menu drawer Focus mode entry description.', surface: 'helper', maxChars: 50 },
  NLSHelperMenuProfile: { text: 'Helper profile', description: 'Helper menu drawer profile entry label.', surface: 'helper', maxChars: 24 },
  NLSHelperMenuProfileDesc: { text: 'Manage your name, phone, and display preference.', description: 'Helper menu drawer profile entry description.', surface: 'helper', maxChars: 70 },
  NLSHelperMenuIncomingInvitesDesc: { text: 'Accept or reject workspace invites sent to you.', description: 'Helper menu drawer incoming invites entry description.', surface: 'helper', maxChars: 70 },
  NLSHelperMenuSettingsDesc: { text: 'Tune alerts, sync, privacy, and app behavior.', description: 'Helper menu drawer settings entry description.', surface: 'helper', maxChars: 65 },
  NLSHelperMenuHelp: { text: 'Help & support', description: 'Helper menu drawer help entry label.', surface: 'helper', maxChars: 24 },
  NLSHelperMenuHelpDesc: { text: 'Reach the team or browse helper guides.', description: 'Helper menu drawer help entry description.', surface: 'helper', maxChars: 60 },
  NLSHelperMenuAbout: { text: 'About Timelining', description: 'Helper menu drawer about entry label.', surface: 'helper', maxChars: 24 },
  NLSHelperMenuAboutDesc: { text: 'Build info, version, legal.', description: 'Helper menu drawer about entry description.', surface: 'helper', maxChars: 40 },
  NLSHelperMenuCloseAria: { text: 'Close menu', description: 'Accessible label for closing the helper menu.', surface: 'helper', maxChars: 24 },
  NLSHelperMenuDrawerAria: { text: 'Helper menu', description: 'Accessible label for helper menu drawer dialog.', surface: 'helper', maxChars: 24 },
  NLSHelperMenuPendingSuffix: { text: 'pending', description: 'Suffix for queued pending items in helper menu header.', surface: 'helper', maxChars: 18 },

  NLSHelperNotificationDismiss: { text: 'Dismiss', description: 'Label for swipe to dismiss notification in helper drawer.', surface: 'helper', maxChars: 18 },
  NLSHelperNotificationCloseAria: { text: 'Close notifications', description: 'Accessible label for closing helper notifications drawer.', surface: 'helper', maxChars: 30 },
  NLSHelperNotificationDrawerAria: { text: 'Notifications', description: 'Accessible label for helper notifications drawer dialog.', surface: 'helper', maxChars: 24 },
  NLSHelperNotificationHeader: { text: 'Notifications', description: 'Header title for helper notifications drawer.', surface: 'helper', maxChars: 24 },
  NLSHelperNotificationClearAll: { text: 'Clear all', description: 'Action button to clear all helper notifications.', surface: 'helper', maxChars: 20 },
  NLSHelperNotificationAllCaughtUp: { text: "You're all caught up.", description: 'Empty state text for helper notifications drawer.', surface: 'helper', maxChars: 36 },
  NLSHelperNotificationSettings: { text: 'Notification settings', description: 'Footer button to open helper notification settings.', surface: 'helper', maxChars: 32 },
  NLSHelperNotificationClearedSingle: { text: 'Notification cleared', description: 'Undo snackbar text when single notification is cleared.', surface: 'helper', maxChars: 30 },
  NLSHelperNotificationClearedMany: { text: '{count} notifications cleared', description: 'Undo snackbar text when multiple notifications are cleared.', surface: 'helper', maxChars: 40 },
  NLSHelperNotificationConfirmTitle: { text: 'Clear all notifications?', description: 'Title for clear all notifications modal.', surface: 'helper', maxChars: 36 },
  NLSHelperNotificationConfirmBody: { text: 'This clears every notification in your Helper drawer. You can undo it right away.', description: 'Body text for clear all notifications modal.', surface: 'helper', maxChars: 110 },
  NLSHelperNotificationOpeningLabel: { text: 'Opening notification', description: 'Loading step label when opening a notification.', surface: 'helper', maxChars: 32 },
  NLSHelperNotificationOpeningMsg1: { text: 'Switching to the linked workspace...', description: 'Loading message 1 when opening a notification.', surface: 'helper', maxChars: 60 },
  NLSHelperNotificationOpeningMsg2: { text: 'Opening the assigned screen...', description: 'Loading message 2 when opening a notification.', surface: 'helper', maxChars: 50 },
  NLSHelperNotificationOpeningMsg3: { text: 'Marking the helper signal as handled...', description: 'Loading message 3 when opening a notification.', surface: 'helper', maxChars: 60 },

  NLSHelperSyncPillOpenAria: { text: 'Open sync menu', description: 'Accessible label for opening the sync pill popover menu.', surface: 'helper', maxChars: 26 },
  NLSHelperSyncPillCloseAria: { text: 'Close sync menu', description: 'Accessible label for closing the sync pill popover menu.', surface: 'helper', maxChars: 26 },
  NLSHelperSyncPillPending: { text: '{count} pending', description: 'Pending mutations count in helper sync pill.', surface: 'helper', maxChars: 20 },
  NLSHelperSyncPillSyncing: { text: 'Syncing…', description: 'Label when syncing in sync pill popover.', surface: 'helper', maxChars: 20 },
  NLSHelperSyncPillSyncNow: { text: 'Sync now', description: 'Button label to trigger sync in sync pill popover.', surface: 'helper', maxChars: 20 },
  NLSHelperSyncPillOpenCenter: { text: 'Open sync center', description: 'Button label to open full sync center from sync pill popover.', surface: 'helper', maxChars: 28 },

  NLSHelperMeetingTimeTbd: { text: 'Time TBD', description: 'Fallback text when meeting time is not determined.', surface: 'helper', maxChars: 20 },
  NLSHelperMeetingDurationTbd: { text: 'Duration TBD', description: 'Fallback text when meeting duration is not determined.', surface: 'helper', maxChars: 20 },
  NLSHelperMeetingInProgress: { text: 'In progress', description: 'Meeting status badge text when meeting is in progress.', surface: 'helper', maxChars: 20 },
  NLSHelperMeetingStartedHoursAgo: { text: 'Started {hours}h ago', description: 'Meeting badge text when meeting started hours ago.', surface: 'helper', maxChars: 30 },
  NLSHelperMeetingStartingNow: { text: 'Starting now', description: 'Meeting badge text when meeting is starting now.', surface: 'helper', maxChars: 24 },
  NLSHelperMeetingReadinessTitle: { text: 'Readiness', description: 'Eyebrow for meeting readiness checklist meter.', surface: 'helper', maxChars: 20 },
  NLSHelperMeetingNoPrepChecklist: { text: 'No prep checklist', description: 'Text when meeting has no prep checklist.', surface: 'helper', maxChars: 30 },
  NLSHelperMeetingReadinessDone: { text: '{done} of {total} done', description: 'Meter completion text for meeting readiness.', surface: 'helper', maxChars: 30 },
  NLSHelperMeetingSelectWorkspace: { text: 'Select a workspace to see meetings.', description: 'Empty state message when no workspace is selected.', surface: 'helper', maxChars: 60 },
  NLSHelperMeetingAssignedHeader: { text: 'Assigned meetings', description: 'Eyebrow for assigned meetings header card.', surface: 'helper', maxChars: 30 },
  NLSHelperMeetingNoneYet: { text: 'No meetings yet', description: 'Heading when no meetings are scheduled.', surface: 'helper', maxChars: 30 },
  NLSHelperMeetingAssignmentsWillAppear: { text: 'Meeting assignments will appear here.', description: 'Subtitle when no meetings are scheduled.', surface: 'helper', maxChars: 60 },
  NLSHelperMeetingNoUpcoming: { text: 'No upcoming meetings', description: 'Empty state title for upcoming meetings.', surface: 'helper', maxChars: 30 },
  NLSHelperMeetingNoActiveOnCalendar: { text: 'No active assignment on the calendar.', description: 'Empty state subtitle for upcoming meetings.', surface: 'helper', maxChars: 50 },
  NLSHelperMeetingJoin: { text: 'Join', description: 'Action button to join meeting.', surface: 'helper', maxChars: 16 },
  NLSHelperMeetingAgenda: { text: 'Agenda', description: 'Heading for meeting agenda items section.', surface: 'helper', maxChars: 20 },
  NLSHelperMeetingPastHeader: { text: 'Past meetings · {count}', description: 'Summary header for past meetings details section.', surface: 'helper', maxChars: 36 },

  NLSHelperWorkspaceSelect: { text: 'Select workspace', description: 'Trigger label for workspace switcher when no workspace is active.', surface: 'helper', maxChars: 28 },
  NLSHelperWorkspaceNoMatch: { text: 'No workspaces match.', description: 'Empty search result in workspace switcher.', surface: 'helper', maxChars: 30 },
  NLSHelperWorkspaceEmpty: { text: "You aren't in any workspaces yet.", description: 'Empty state in workspace switcher when user has no workspaces.', surface: 'helper', maxChars: 50 },
  NLSHelperWorkspacePendingCount: { text: '{count} pending', description: 'Pending mutations count for a workspace in switcher row.', surface: 'helper', maxChars: 24 },

  NLSHelperInviteSoon: { text: 'soon', description: 'Fallback expiry string for invite landing page.', surface: 'helper', maxChars: 16 },
  NLSHelperInviteEyebrow: { text: 'Helper invite', description: 'Eyebrow for helper invite landing card.', surface: 'helper', maxChars: 24 },
  NLSHelperInviteTitle: { text: "You've been invited to help", description: 'Heading for helper invite landing card.', surface: 'helper', maxChars: 40 },
  NLSHelperInviteValidating: { text: 'Validating your invite…', description: 'Loading text on helper invite landing card.', surface: 'helper', maxChars: 36 },
  NLSHelperInviteFrom: { text: 'From {name}', description: 'Sender line on helper invite landing card.', surface: 'helper', maxChars: 50 },
  NLSHelperInviteJoining: { text: 'Joining {name}', description: 'Target workspace line on helper invite landing card.', surface: 'helper', maxChars: 50 },
  NLSHelperInviteExpires: { text: 'Expires {expiry}', description: 'Expiry line on helper invite landing card.', surface: 'helper', maxChars: 50 },
  NLSHelperInviteContinue: { text: 'Continue with phone', description: 'Continue button on helper invite landing card.', surface: 'helper', maxChars: 30 },
  NLSHelperInviteFooter: { text: "You'll verify your phone number, then this workspace will be added to your Helper account.", description: 'Footer explanation on helper invite landing card.', surface: 'helper', maxChars: 120 },

  NLSBarcodeScannerCameraFailed: { text: 'Camera scanner could not start.', description: 'Error message when camera scanner fails to initialize.', surface: 'common', maxChars: 50 },
  NLSBarcodeScannerEyebrow: { text: 'Scanner', description: 'Eyebrow for barcode scanner modal.', surface: 'common', maxChars: 20 },
  NLSBarcodeScannerCountTitle: { text: 'Stock count barcode', description: 'Header title for barcode scanner in count mode.', surface: 'common', maxChars: 36 },
  NLSBarcodeScannerInventoryTitle: { text: 'Inventory barcode', description: 'Header title for barcode scanner in inventory mode.', surface: 'common', maxChars: 36 },
  NLSBarcodeScannerCloseAria: { text: 'Close scanner', description: 'Accessible label for closing the barcode scanner.', surface: 'common', maxChars: 24 },
  NLSBarcodeScannerScanOut: { text: 'Scan out', description: 'Scan out direction button.', surface: 'common', maxChars: 20 },
  NLSBarcodeScannerScanIn: { text: 'Scan in', description: 'Scan in direction button.', surface: 'common', maxChars: 20 },
  NLSBarcodeScannerManualLabel: { text: 'Manual barcode', description: 'Label for manual barcode entry text input.', surface: 'common', maxChars: 24 },
  NLSBarcodeScannerManualPlaceholder: { text: 'Scan unavailable? Enter barcode', description: 'Placeholder for manual barcode entry input.', surface: 'common', maxChars: 50 },
  NLSBarcodeScannerUseBarcode: { text: 'Use barcode', description: 'Submit button to apply manual barcode.', surface: 'common', maxChars: 24 },

  NLSHelperCalendarTasks: { text: 'Tasks', description: 'Pill label for tasks count in helper calendar.', surface: 'helper', maxChars: 18 },
  NLSHelperCalendarDaily: { text: 'Daily', description: 'Pill label for daily count in helper calendar.', surface: 'helper', maxChars: 18 },
  NLSHelperCalendarWeekly: { text: 'Weekly', description: 'Pill label for weekly count in helper calendar.', surface: 'helper', maxChars: 18 },
  NLSHelperCalendarMeetings: { text: 'Meetings', description: 'Pill label for meetings count in helper calendar.', surface: 'helper', maxChars: 18 },
  NLSHelperCalendarWeeklyFocus: { text: 'Weekly focus', description: 'Section title for weekly focus recurring checklists.', surface: 'helper', maxChars: 24 },
  NLSHelperCalendarRecurringChecklistOne: { text: '1 recurring checklist', description: 'Subtitle for 1 recurring checklist in weekly focus.', surface: 'helper', maxChars: 32 },
  NLSHelperCalendarRecurringChecklistMany: { text: '{count} recurring checklists', description: 'Subtitle for multiple recurring checklists in weekly focus.', surface: 'helper', maxChars: 40 },
  NLSHelperCalendarPrevious: { text: 'Previous', description: 'Accessible label for previous period button in calendar.', surface: 'helper', maxChars: 20 },
  NLSHelperCalendarNext: { text: 'Next', description: 'Accessible label for next period button in calendar.', surface: 'helper', maxChars: 20 },
  NLSHelperCalendarViewDay: { text: 'Day', description: 'View mode label for day view in helper calendar.', surface: 'helper', maxChars: 16 },
  NLSHelperCalendarViewThreeDay: { text: '3 day', description: 'View mode label for 3 day view in helper calendar.', surface: 'helper', maxChars: 16 },
  NLSHelperCalendarViewWeek: { text: 'Week', description: 'View mode label for week view in helper calendar.', surface: 'helper', maxChars: 16 },
  NLSHelperCalendarViewMonth: { text: 'Month', description: 'View mode label for month view in helper calendar.', surface: 'helper', maxChars: 16 },

  NLSHelperChecklistDetailsAria: { text: 'Checklist details', description: 'Accessible label for checklist details modal.', surface: 'helper', maxChars: 30 },
  NLSHelperChecklistRecurringTitle: { text: 'Recurring occurrence', description: 'Title for recurring occurrence selector in checklist details.', surface: 'helper', maxChars: 32 },
  NLSHelperChecklistRecurringSubtitle: { text: 'Switch to another generated day or week without leaving the helper flow.', description: 'Subtitle for recurring occurrence selector.', surface: 'helper', maxChars: 90 },
  NLSHelperChecklistBriefTitle: { text: 'Work brief', description: 'Title for work brief section in checklist details.', surface: 'helper', maxChars: 24 },
  NLSHelperChecklistBriefSubtitle: { text: 'Quick execution context, constraints, and assignment signals.', description: 'Subtitle for work brief section.', surface: 'helper', maxChars: 80 },
  NLSHelperChecklistInstructionsTitle: { text: 'Instructions', description: 'Title for instructions section in checklist details.', surface: 'helper', maxChars: 24 },
  NLSHelperChecklistInstructionsSubtitle: { text: 'Keep the brief, description, and done criteria visible even when they are short.', description: 'Subtitle for instructions section.', surface: 'helper', maxChars: 95 },
  NLSHelperChecklistFormsTitle: { text: 'Forms', description: 'Title for forms section in checklist details.', surface: 'helper', maxChars: 20 },
  NLSHelperChecklistFormsSubtitle: { text: 'Open linked forms only when needed, keep them fillable here, and reuse cached definitions between views.', description: 'Subtitle for forms section.', surface: 'helper', maxChars: 120 },
  NLSHelperChecklistDesignsTitle: { text: 'Designs', description: 'Title for designs section in checklist details.', surface: 'helper', maxChars: 20 },
  NLSHelperChecklistDesignsSubtitle: { text: 'Preview reference designs in a cached read-only view so helpers can inspect them without opening the editing flows.', description: 'Subtitle for designs section.', surface: 'helper', maxChars: 130 },
  NLSHelperChecklistProofTitle: { text: 'Proof and media', description: 'Title for proof and media section in checklist details.', surface: 'helper', maxChars: 30 },
  NLSHelperChecklistProofSubtitle: { text: 'Review submitted evidence, preview attachments, and add new proof from camera or device.', description: 'Subtitle for proof section.', surface: 'helper', maxChars: 110 },
  NLSHelperChecklistCommentsTitle: { text: 'Comments', description: 'Title for comments section in checklist details.', surface: 'helper', maxChars: 20 },
  NLSHelperChecklistCommentsSubtitle: { text: 'Read the discussion as a list, preview other attachments, and edit or delete only your own comments.', description: 'Subtitle for comments section.', surface: 'helper', maxChars: 115 },
  NLSHelperChecklistStatusTitle: { text: 'Status and controls', description: 'Title for status and controls section in checklist details.', surface: 'helper', maxChars: 30 },
  NLSHelperChecklistStatusSubtitle: { text: 'Allowed transitions stay isolated here so helpers can change status without opening member editing flows.', description: 'Subtitle for status section.', surface: 'helper', maxChars: 120 },
  NLSHelperChecklistBlockerPlaceholder: { text: "What's blocking this? What do you need?", description: 'Placeholder for blocker details input.', surface: 'helper', maxChars: 60 },

  NLSHelperToastScannedIn: { text: '{name} scanned in.', description: 'Toast notification when item is scanned in.', surface: 'helper', maxChars: 50 },
  NLSHelperToastScannedOut: { text: '{name} scanned out.', description: 'Toast notification when item is scanned out.', surface: 'helper', maxChars: 50 },

  NLSMemberChannelSearchPlaceholder: { text: 'Search channels', description: 'Placeholder for channel search input in member chat.', surface: 'member', maxChars: 30 },
  NLSMemberChannelSearchAria: { text: 'Search channels', description: 'Accessible label for channel search input in member chat.', surface: 'member', maxChars: 30 },

  // CO2.b: Client outreach rail, module card, clients directory
  NLSShellRailTileClients: { text: 'Clients', description: 'Label for clients rail tile.', surface: 'shell', maxChars: 20 },
  NLSWorkspaceModuleClientOutreachSubtitle: { text: 'Outreach and communications for clients and partners.', description: 'Client outreach module card subtitle.', surface: 'settings', maxChars: 120 },
  NLSWorkspaceModulesPaidPlanRequired: { text: 'Requires a paid plan.', description: 'Tooltip when module requires a paid workspace plan.', surface: 'settings', maxChars: 40 },
  NLSClientsTitle: { text: 'Clients', description: 'Page title for client outreach.', surface: 'clients', maxChars: 30 },
  NLSClientsSubtitle: { text: 'Manage client directory and send outreach emails.', description: 'Page subtitle for clients.', surface: 'clients', maxChars: 80 },
  NLSClientsNewClient: { text: 'New client', description: 'Button label to add a new client.', surface: 'clients', maxChars: 30 },
  NLSClientsSearchPlaceholder: { text: 'Search clients by name, email, or company…', description: 'Placeholder for client search input.', surface: 'clients', maxChars: 60 },
  NLSClientsFilterActive: { text: 'Active', description: 'Filter chip for active clients.', surface: 'clients', maxChars: 20 },
  NLSClientsFilterArchived: { text: 'Archived', description: 'Filter chip for archived clients.', surface: 'clients', maxChars: 20 },
  NLSClientsEmptyTitle: { text: 'No clients found', description: 'Title when no clients match filter.', surface: 'clients', maxChars: 40 },
  NLSClientsEmptyBody: { text: 'Add clients to manage contacts and send outreach emails.', description: 'Body text for empty client directory.', surface: 'clients', maxChars: 120 },
  NLSClientsEmptyArchivedBody: { text: 'No archived clients.', description: 'Body text for empty archived list.', surface: 'clients', maxChars: 60 },
  NLSClientsLoadErrorTitle: { text: 'Failed to load clients', description: 'Error title when client list fails to load.', surface: 'clients', maxChars: 40 },
  NLSClientsRetry: { text: 'Retry', description: 'Button label to retry loading.', surface: 'clients', maxChars: 20 },
  NLSClientsBlockedBadge: { text: 'Blocked: {reason}', description: 'Badge indicating client is blocked.', surface: 'clients', maxChars: 60 },
  NLSClientsArchivedBadge: { text: 'Archived', description: 'Badge indicating client is archived.', surface: 'clients', maxChars: 20 },
  NLSClientsActionEdit: { text: 'Edit', description: 'Action button to edit client.', surface: 'clients', maxChars: 20 },
  NLSClientsActionArchive: { text: 'Archive', description: 'Action button to archive client.', surface: 'clients', maxChars: 20 },
  NLSClientsActionRestore: { text: 'Restore', description: 'Action button to restore client.', surface: 'clients', maxChars: 20 },
  NLSClientsActionErase: { text: 'Erase personal data', description: 'Action button to erase client personal data.', surface: 'clients', maxChars: 30 },
  NLSClientsActionCompose: { text: 'Send email', description: 'Action button to compose email to client.', surface: 'clients', maxChars: 20 },
  NLSClientsConfirmArchiveTitle: { text: 'Archive client?', description: 'Title for archive client confirmation modal.', surface: 'clients', maxChars: 40 },
  NLSClientsConfirmArchiveBody: { text: 'Are you sure you want to archive {name}? You can restore them later.', description: 'Confirmation body for archiving a client.', surface: 'clients', maxChars: 120 },
  NLSClientsConfirmEraseTitle: { text: 'Erase personal data?', description: 'Title for erase personal data confirmation modal.', surface: 'clients', maxChars: 40 },
  NLSClientsConfirmEraseBody: { text: 'This action permanently removes personal data (name, email, phone, notes) for {name}. This cannot be undone.', description: 'Confirmation body for erasing client data.', surface: 'clients', maxChars: 200 },
  NLSClientsConfirmEraseConfirm: { text: 'Erase data', description: 'Confirm button label for erasing data.', surface: 'clients', maxChars: 20 },
  NLSClientsAddSheetTitle: { text: 'New client', description: 'Sheet title for adding a client.', surface: 'clients', maxChars: 30 },
  NLSClientsEditSheetTitle: { text: 'Edit client', description: 'Sheet title for editing a client.', surface: 'clients', maxChars: 30 },
  NLSClientsFieldName: { text: 'Full name', description: 'Label for client name field.', surface: 'clients', maxChars: 30 },
  NLSClientsFieldEmail: { text: 'Email address', description: 'Label for client email field.', surface: 'clients', maxChars: 30 },
  NLSClientsFieldCompany: { text: 'Company', description: 'Label for client company field.', surface: 'clients', maxChars: 30 },
  NLSClientsFieldPhone: { text: 'Phone', description: 'Label for client phone field.', surface: 'clients', maxChars: 30 },
  NLSClientsFieldNotes: { text: 'Notes', description: 'Label for client notes field.', surface: 'clients', maxChars: 30 },
  NLSClientsSave: { text: 'Save client', description: 'Button label to save client.', surface: 'clients', maxChars: 30 },
  NLSClientsSaving: { text: 'Saving…', description: 'Loading label while saving client.', surface: 'clients', maxChars: 30 },
  NLSClientsCancel: { text: 'Cancel', description: 'Button label to cancel sheet.', surface: 'clients', maxChars: 20 },
  NLSClientsErrorNameRequired: { text: 'Client name must be between 1 and 120 characters.', description: 'Validation error for client name.', surface: 'clients', maxChars: 80 },
  NLSClientsErrorEmailInvalid: { text: 'Valid email address is required (maximum 254 characters).', description: 'Validation error for client email.', surface: 'clients', maxChars: 80 },
  NLSClientsErrorCompanyTooLong: { text: 'Company name must not exceed 120 characters.', description: 'Validation error for company.', surface: 'clients', maxChars: 80 },
  NLSClientsErrorPhoneTooLong: { text: 'Phone number must not exceed 40 characters.', description: 'Validation error for phone.', surface: 'clients', maxChars: 80 },
  NLSClientsErrorNotesTooLong: { text: 'Notes must not exceed 1000 characters.', description: 'Validation error for notes.', surface: 'clients', maxChars: 80 },
  NLSClientsErrorDisabled: { text: 'Client outreach is currently disabled for this workspace.', description: 'Error message when client outreach is disabled.', surface: 'clients', maxChars: 100 },
  NLSClientsErrorModuleDisabled: { text: 'The client outreach module is turned off.', description: 'Error message when module is disabled.', surface: 'clients', maxChars: 80 },
  NLSClientsErrorPaywall: { text: 'A paid subscription is required to use client outreach.', description: 'Error message when paywall applies.', surface: 'clients', maxChars: 100 },
  NLSClientsErrorSubscriptionRestricted: { text: 'Your subscription is restricted. Please update billing to use client outreach.', description: 'Error message when subscription is restricted.', surface: 'clients', maxChars: 120 },
  NLSClientsErrorEmailExists: { text: 'A client with this email already exists in this workspace.', description: 'Error message for duplicate client email.', surface: 'clients', maxChars: 100 },
  NLSClientsErrorDailyCap: { text: 'Daily outreach email cap reached. Try again tomorrow.', description: 'Error message when daily email cap is hit.', surface: 'clients', maxChars: 100 },
  NLSClientsErrorPermissionDenied: { text: 'You do not have permission to perform this client action.', description: 'Error message for permission denied.', surface: 'clients', maxChars: 100 },
  NLSClientsErrorNotFound: { text: 'The requested client was not found.', description: 'Error message when client not found.', surface: 'clients', maxChars: 80 },
  NLSClientsErrorInvalidArgument: { text: 'Invalid client information provided.', description: 'Error message for invalid argument.', surface: 'clients', maxChars: 80 },
  NLSClientsErrorSenderEmailUnverified: { text: 'Your email address must be verified to send outreach emails.', description: 'Error message when sender email is not verified.', surface: 'clients', maxChars: 80 },
  NLSClientsErrorAppBaseUrlMissing: { text: 'Outreach service is not properly configured (APP_BASE_URL is missing or invalid). Please contact support.', description: 'Error message when APP_BASE_URL is missing or invalid.', surface: 'clients', maxChars: 120 },
  NLSClientsErrorGeneric: { text: 'An unexpected error occurred. Please try again.', description: 'Generic error message.', surface: 'clients', maxChars: 80 },
  NLSClientsPaywallEyebrow: { text: 'Client outreach', description: 'Eyebrow for client outreach lock card.', surface: 'clients', maxChars: 30 },
  NLSClientsPaywallTitle: { text: 'Client outreach is a paid feature', description: 'Title for client outreach lock card.', surface: 'clients', maxChars: 60 },
  NLSClientsPaywallBody: { text: 'Upgrade your workspace plan to create clients, send outreach emails, and track delivery.', description: 'Body text for client outreach lock card.', surface: 'clients', maxChars: 150 },

  // CO3: Email templates and composer
  NLSEmailTemplateIntroName: { text: 'Introduction', description: 'Email template title for introduction.', surface: 'clients', maxChars: 30 },
  NLSEmailTemplateFollowUpName: { text: 'Follow-up', description: 'Email template title for follow up.', surface: 'clients', maxChars: 30 },
  NLSEmailTemplateThankYouName: { text: 'Thank you', description: 'Email template title for thank you.', surface: 'clients', maxChars: 30 },
  NLSEmailTemplateMeetingRequestName: { text: 'Meeting request', description: 'Email template title for meeting request.', surface: 'clients', maxChars: 30 },
  NLSEmailTemplateShareReportName: { text: 'Sharing a report', description: 'Email template title for sharing a report.', surface: 'clients', maxChars: 30 },
  NLSEmailTemplateFillFormName: { text: 'Please fill this form', description: 'Email template title for form request.', surface: 'clients', maxChars: 30 },
  NLSEmailTemplateQuoteName: { text: 'Your quote', description: 'Email template title for quote.', surface: 'clients', maxChars: 30 },
  NLSEmailSentWithTimelining: { text: 'Sent with Timelining', description: 'Email footer branding text.', surface: 'clients', maxChars: 30 },
  NLSEmailOptOut: { text: 'Unsubscribe / Opt out', description: 'Email footer opt out link text.', surface: 'clients', maxChars: 40 },
  NLSClientsComposerTitle: { text: 'Send outreach email', description: 'Title for email composer sheet.', surface: 'clients', maxChars: 40 },
  NLSClientEmailComposerTo: { text: 'To:', description: 'Recipient label in client email composer.', surface: 'clients', maxChars: 10 },
  NLSClientsComposerTemplateLabel: { text: 'Choose template', description: 'Label for template picker.', surface: 'clients', maxChars: 30 },
  NLSClientsComposerNoteLabel: { text: 'Personal note', description: 'Label for personal note textarea.', surface: 'clients', maxChars: 30 },
  NLSClientsComposerNotePlaceholder: { text: 'Add a personal note (no links, max 500 characters)…', description: 'Placeholder for personal note.', surface: 'clients', maxChars: 80 },
  NLSClientsComposerItemSlotLabel: { text: 'Attach item', description: 'Label for item slot.', surface: 'clients', maxChars: 30 },
  NLSClientsComposerItemSlotComingSoon: { text: 'Coming soon in CO5', description: 'Notice that items will be attachable soon.', surface: 'clients', maxChars: 40 },
  NLSClientsComposerLanguageNote: { text: 'Email will be sent in your app language ({locale}).', description: 'Notice that email renders in sender language.', surface: 'clients', maxChars: 80 },
  NLSClientsComposerSendButton: { text: 'Send email', description: 'Button label to send email.', surface: 'clients', maxChars: 30 },
  NLSClientsComposerSendingButton: { text: 'Sending…', description: 'Button loading label while email is sending.', surface: 'clients', maxChars: 30 },
  NLSClientsComposerSuccessToast: { text: 'Email sent successfully.', description: 'Toast notification on email send success.', surface: 'clients', maxChars: 40 },
  NLSClientsComposerPreviewLoading: { text: 'Rendering preview…', description: 'Loading text for email preview.', surface: 'clients', maxChars: 40 },
  NLSClientsComposerPreviewAria: { text: 'Email preview', description: 'Aria label for iframe preview.', surface: 'clients', maxChars: 30 },
  NLSClientsSentLogTitle: { text: 'Outreach history', description: 'Section title for client email history.', surface: 'clients', maxChars: 40 },
  NLSClientsSentLogEmpty: { text: 'No emails sent to this client yet.', description: 'Empty text for email sent log.', surface: 'clients', maxChars: 50 },
  NLSClientsSentStatusSending: { text: 'Sending…', description: 'Badge for email in flight.', surface: 'clients', maxChars: 20 },
  NLSClientsSentStatusSent: { text: 'Sent', description: 'Badge for sent email.', surface: 'clients', maxChars: 20 },
  NLSClientsSentStatusFailed: { text: 'Failed', description: 'Badge for failed email.', surface: 'clients', maxChars: 20 },
  NLSClientsSentStatusOpened: { text: 'Opened', description: 'Badge for opened email.', surface: 'clients', maxChars: 20 },
  NLSClientsNoteErrorTooLong: { text: 'Note must not exceed 500 characters.', description: 'Validation error when note is too long.', surface: 'clients', maxChars: 60 },
  NLSClientsNoteErrorNoUrls: { text: 'Links and URLs are not permitted in notes.', description: 'Validation error when note has urls.', surface: 'clients', maxChars: 60 },
  NLSClientsNoteErrorNoEmails: { text: 'Email addresses are not permitted in notes.', description: 'Validation error when note has emails.', surface: 'clients', maxChars: 60 },
  NLSClientsNoteErrorNoAngleBrackets: { text: 'HTML angle brackets (< and >) are not permitted.', description: 'Validation error when note has angle brackets.', surface: 'clients', maxChars: 60 },

  // CO4.b: Public client opt-out page
  NLSClientOptOutTitle: { text: 'Email preferences', description: 'Title for email opt out page.', surface: 'clients', maxChars: 40 },
  NLSClientOptOutButton: { text: 'Opt out of emails from {workspaceName}', description: 'Button to confirm opt-out.', surface: 'clients', maxChars: 60 },
  NLSClientOptOutSuccessTitle: { text: 'Unsubscribed', description: 'Title after successful opt-out.', surface: 'clients', maxChars: 30 },
  NLSClientOptOutSuccessBody: { text: 'You have been unsubscribed from {workspaceName} outreach emails.', description: 'Confirmation body after opt-out.', surface: 'clients', maxChars: 120 },
  NLSClientOptOutInvalidTokenTitle: { text: 'Invalid link', description: 'Title when opt out link is invalid or expired.', surface: 'clients', maxChars: 30 },
  NLSClientOptOutInvalidTokenBody: { text: 'This opt-out link is invalid or has expired.', description: 'Body when opt out link is invalid or expired.', surface: 'clients', maxChars: 80 },
  NLSClientOptOutErrorTitle: { text: 'Something went wrong', description: 'Title when opt out fails.', surface: 'clients', maxChars: 40 },
  NLSClientOptOutErrorBody: { text: 'Failed to update email preferences. Please try again later.', description: 'Body when opt out fails.', surface: 'clients', maxChars: 100 },
  NLSClientOptOutAlreadyTitle: { text: 'Already opted out', description: 'Title when client has already opted out.', surface: 'clients', maxChars: 40 },
  NLSClientOptOutAlreadyBody: { text: 'You are already unsubscribed from {workspaceName} outreach emails.', description: 'Body when client has already opted out.', surface: 'clients', maxChars: 120 },
  NLSClientOptOutLoading: { text: 'Loading…', description: 'Loading indicator on opt out page.', surface: 'clients', maxChars: 20 },
  NLSClientOptOutConfirming: { text: 'Unsubscribing…', description: 'Button loading label during opt out.', surface: 'clients', maxChars: 30 },
  NLSClientOptOutRetry: { text: 'Retry', description: 'Retry action button label.', surface: 'clients', maxChars: 20 },

  // CO5.b: Report Center shares & Report Picker in outreach composer
  NLSClientsComposerReportSelectLabel: { text: 'Report to share', description: 'Label for report picker in outreach email composer.', surface: 'clients', maxChars: 40 },
  NLSClientsComposerReportSelectPlaceholder: { text: 'Select a report…', description: 'Placeholder for report picker.', surface: 'clients', maxChars: 40 },
  NLSClientsComposerReportSelectEmpty: { text: 'No ready reports available in this workspace.', description: 'Notice when no ready reports exist.', surface: 'clients', maxChars: 80 },
  NLSClientsSharesEmailToClientButton: { text: 'Email to a client', description: 'Button in shares view to email a report to a client.', surface: 'clients', maxChars: 30 },
  NLSClientsSharesSelectClientTitle: { text: 'Email report to client', description: 'Dialog title to select client for report email.', surface: 'clients', maxChars: 40 },
  NLSClientsSharesSelectClientLabel: { text: 'Select client', description: 'Label for client picker.', surface: 'clients', maxChars: 30 },
  NLSClientsSharesSelectClientPlaceholder: { text: 'Choose a client…', description: 'Placeholder for client picker.', surface: 'clients', maxChars: 40 },
  NLSClientsSharesNoClientsFound: { text: 'No active clients found. Add a client first in the Clients page.', description: 'Notice when no clients are available to email.', surface: 'clients', maxChars: 100 },

  // CO6.b: Client Form Page & Composer Form Item Slot
  NLSClientFormLoading: { text: 'Loading form…', description: 'Loading state for client form page.', surface: 'clients', maxChars: 30 },
  NLSClientFormInvalidTitle: { text: 'Form unavailable', description: 'Title when form link is invalid, expired, or revoked.', surface: 'clients', maxChars: 40 },
  NLSClientFormInvalidBody: { text: 'This form link is invalid, expired, or has been revoked.', description: 'Body when form link is invalid or expired.', surface: 'clients', maxChars: 100 },
  NLSClientFormAlreadySubmittedTitle: { text: 'Already submitted', description: 'Title when form has already been submitted.', surface: 'clients', maxChars: 40 },
  NLSClientFormAlreadySubmittedBody: { text: 'Thank you! This form has already been completed.', description: 'Body when form was previously submitted.', surface: 'clients', maxChars: 100 },
  NLSClientFormSuccessTitle: { text: 'Thank you!', description: 'Title when form is successfully submitted.', surface: 'clients', maxChars: 40 },
  NLSClientFormSuccessBody: { text: 'Your response has been received.', description: 'Body when form is successfully submitted.', surface: 'clients', maxChars: 100 },
  NLSClientFormErrorTitle: { text: 'Unable to load form', description: 'Error title when loading form fails.', surface: 'clients', maxChars: 40 },
  NLSClientFormErrorBody: { text: 'Something went wrong loading this form. Please check your connection and try again.', description: 'Error body when loading form fails.', surface: 'clients', maxChars: 120 },
  NLSClientFormRetry: { text: 'Try again', description: 'Retry button label on client form page.', surface: 'clients', maxChars: 30 },
  NLSClientsComposerFormSelectLabel: { text: 'Form to send', description: 'Label for form picker in outreach email composer.', surface: 'clients', maxChars: 40 },
  NLSClientsComposerFormSelectPlaceholder: { text: 'Select a form…', description: 'Placeholder for form picker.', surface: 'clients', maxChars: 40 },
  NLSClientsComposerFormSelectEmpty: { text: 'No active forms available in this workspace.', description: 'Notice when no active forms exist.', surface: 'clients', maxChars: 80 },
  NLSClientsComposerFormExpiryLabel: { text: 'Link expiry (optional)', description: 'Label for optional expiry date for form link.', surface: 'clients', maxChars: 40 },
  NLSClientsFormResponsesTitle: { text: 'Form responses', description: 'Title for form responses list in client details.', surface: 'clients', maxChars: 40 },
  NLSClientsFormResponsesEmpty: { text: 'No form responses from this client yet.', description: 'Empty text for client form responses.', surface: 'clients', maxChars: 60 },
  NLSClientsSentItemForm: { text: 'Form: {name}', description: 'Sent log item label for form attachment.', surface: 'clients', maxChars: 60 },
  NLSClientsSentItemReport: { text: 'Report: {name}', description: 'Sent log item label for report attachment.', surface: 'clients', maxChars: 60 },
  NLSClientsSentOpenedAt: { text: 'Opened {date}', description: 'Sent log opened date format.', surface: 'clients', maxChars: 40 },
  NLSClientsSentSubmittedAt: { text: 'Submitted {date}', description: 'Sent log submitted date format.', surface: 'clients', maxChars: 40 },

  // CO7.b: Client quotes UI & public quote view
  NLSClientsQuotesTitle: { text: 'Quotes', description: 'Title for quotes modal or section.', surface: 'clients', maxChars: 30 },
  NLSClientsQuotesEmpty: { text: 'No quotes for this client yet.', description: 'Empty state when no quotes exist for a client.', surface: 'clients', maxChars: 60 },
  NLSClientsNewQuote: { text: 'New quote', description: 'Action button to create a new quote.', surface: 'clients', maxChars: 30 },
  NLSClientsEditQuote: { text: 'Edit quote', description: 'Action button to edit a draft quote.', surface: 'clients', maxChars: 30 },
  NLSClientsDeleteQuote: { text: 'Delete quote', description: 'Action button to delete a draft quote.', surface: 'clients', maxChars: 30 },
  NLSClientsQuoteDeleteConfirm: { text: 'Are you sure you want to delete this draft quote?', description: 'Confirmation prompt to delete draft quote.', surface: 'clients', maxChars: 80 },
  NLSClientsQuoteNumber: { text: 'Quote {number}', description: 'Display label for quote number.', surface: 'clients', maxChars: 40 },
  NLSClientsQuoteTotal: { text: 'Total: {total}', description: 'Display label for quote total amount.', surface: 'clients', maxChars: 40 },
  NLSClientsQuoteValidUntil: { text: 'Valid until {date}', description: 'Display label for quote validity date.', surface: 'clients', maxChars: 40 },
  NLSClientsQuoteStatusDraft: { text: 'Draft', description: 'Badge for draft quote status.', surface: 'clients', maxChars: 20 },
  NLSClientsQuoteStatusSent: { text: 'Sent', description: 'Badge for sent quote status.', surface: 'clients', maxChars: 20 },
  NLSClientsQuoteStatusAccepted: { text: 'Accepted', description: 'Badge for accepted quote status.', surface: 'clients', maxChars: 20 },
  NLSClientsQuoteStatusDeclined: { text: 'Declined', description: 'Badge for declined quote status.', surface: 'clients', maxChars: 20 },
  NLSClientsQuoteStatusExpired: { text: 'Expired', description: 'Badge for expired quote status.', surface: 'clients', maxChars: 20 },
  NLSClientsQuoteEditorTitleNew: { text: 'New quote', description: 'Title for quote editor modal when creating a quote.', surface: 'clients', maxChars: 40 },
  NLSClientsQuoteEditorTitleEdit: { text: 'Edit quote {number}', description: 'Title for quote editor modal when editing a quote.', surface: 'clients', maxChars: 50 },
  NLSClientsQuoteEditorLinesTitle: { text: 'Line items', description: 'Section title for quote line items.', surface: 'clients', maxChars: 30 },
  NLSClientsQuoteEditorAddLine: { text: 'Add line', description: 'Button to add a line item to a quote.', surface: 'clients', maxChars: 30 },
  NLSClientsQuoteEditorRemoveLine: { text: 'Remove', description: 'Button to remove a line item from a quote.', surface: 'clients', maxChars: 20 },
  NLSClientsQuoteEditorDescLabel: { text: 'Description', description: 'Label for line item description.', surface: 'clients', maxChars: 30 },
  NLSClientsQuoteEditorQtyLabel: { text: 'Qty', description: 'Label for line item quantity.', surface: 'clients', maxChars: 20 },
  NLSClientsQuoteEditorPriceLabel: { text: 'Unit price', description: 'Label for line item unit price.', surface: 'clients', maxChars: 30 },
  NLSClientsQuoteEditorTaxLabel: { text: 'Tax %', description: 'Label for quote tax percentage.', surface: 'clients', maxChars: 20 },
  NLSClientsQuoteEditorCurrencyLabel: { text: 'Currency', description: 'Label for quote currency.', surface: 'clients', maxChars: 20 },
  NLSClientsQuoteEditorNotesLabel: { text: 'Notes / terms', description: 'Label for quote notes or terms.', surface: 'clients', maxChars: 30 },
  NLSClientsQuoteEditorValidUntilLabel: { text: 'Valid until', description: 'Label for quote expiration date.', surface: 'clients', maxChars: 30 },
  NLSClientsQuoteEditorSubtotal: { text: 'Subtotal: {subtotal}', description: 'Label for computed quote subtotal.', surface: 'clients', maxChars: 40 },
  NLSClientsQuoteEditorTax: { text: 'Tax: {tax}', description: 'Label for computed quote tax.', surface: 'clients', maxChars: 40 },
  NLSClientsQuoteEditorTotal: { text: 'Total: {total}', description: 'Label for computed quote total.', surface: 'clients', maxChars: 40 },
  NLSClientsQuoteSaveButton: { text: 'Save quote', description: 'Button to save quote draft.', surface: 'clients', maxChars: 30 },
  NLSClientsQuoteSaving: { text: 'Saving…', description: 'Button loading label while saving quote.', surface: 'clients', maxChars: 30 },
  NLSClientsComposerQuoteSelectLabel: { text: 'Quote to send', description: 'Label for quote selector in email composer.', surface: 'clients', maxChars: 30 },
  NLSClientsComposerQuoteSelectPlaceholder: { text: 'Select a quote…', description: 'Placeholder for quote selector.', surface: 'clients', maxChars: 40 },
  NLSClientsComposerQuoteSelectEmpty: { text: 'No draft or sent quotes available for this client.', description: 'Notice when no eligible quotes exist.', surface: 'clients', maxChars: 80 },
  NLSClientsSentItemQuote: { text: 'Quote: {number}', description: 'Sent log label for attached quote.', surface: 'clients', maxChars: 40 },
  NLSClientsSentQuoteDecision: { text: 'Decision: {decision}', description: 'Sent log label for quote decision.', surface: 'clients', maxChars: 40 },
  NLSClientQuoteLoading: { text: 'Loading quote…', description: 'Loading state for public client quote page.', surface: 'clients', maxChars: 30 },
  NLSClientQuoteInvalidTitle: { text: 'Quote unavailable', description: 'Title when quote link is invalid, expired, or revoked.', surface: 'clients', maxChars: 40 },
  NLSClientQuoteInvalidBody: { text: 'This quote link is invalid, expired, or has been revoked.', description: 'Body when quote link is invalid or expired.', surface: 'clients', maxChars: 100 },
  NLSClientQuoteAcceptButton: { text: 'Accept quote', description: 'Button to accept quote on public page.', surface: 'clients', maxChars: 30 },
  NLSClientQuoteDeclineButton: { text: 'Decline quote', description: 'Button to decline quote on public page.', surface: 'clients', maxChars: 30 },
  NLSClientQuoteDecisionModalTitle: { text: 'Confirm quote decision', description: 'Dialog title when accepting or declining a quote.', surface: 'clients', maxChars: 40 },
  NLSClientQuoteDecisionNameLabel: { text: 'Your name', description: 'Label for client name input upon quote decision.', surface: 'clients', maxChars: 30 },
  NLSClientQuoteDecisionNamePlaceholder: { text: 'Enter your name…', description: 'Placeholder for client name input upon quote decision.', surface: 'clients', maxChars: 40 },
  NLSClientQuoteDecisionConfirm: { text: 'Submit decision', description: 'Confirm button to submit quote decision.', surface: 'clients', maxChars: 30 },
  NLSClientQuoteDecisionSubmitting: { text: 'Submitting…', description: 'Loading label while submitting quote decision.', surface: 'clients', maxChars: 30 },
  NLSClientQuoteAcceptedTitle: { text: 'Quote accepted', description: 'Title when quote is accepted.', surface: 'clients', maxChars: 40 },
  NLSClientQuoteAcceptedBody: { text: 'Thank you! You have accepted this quote.', description: 'Body text when quote is accepted.', surface: 'clients', maxChars: 80 },
  NLSClientQuoteDeclinedTitle: { text: 'Quote declined', description: 'Title when quote is declined.', surface: 'clients', maxChars: 40 },
  NLSClientQuoteDeclinedBody: { text: 'You have declined this quote.', description: 'Body text when quote is declined.', surface: 'clients', maxChars: 60 },
  NLSClientQuoteErrorTitle: { text: 'Unable to load quote', description: 'Error title when loading quote fails.', surface: 'clients', maxChars: 40 },
  NLSClientQuoteErrorBody: { text: 'Something went wrong loading this quote. Please try again.', description: 'Error body when loading quote fails.', surface: 'clients', maxChars: 100 },
  NLSClientQuoteRetry: { text: 'Try again', description: 'Retry button on public client quote page.', surface: 'clients', maxChars: 30 },
  // ED10.b1: Web staff booking screens (EQ164–EQ167)
  NLSEducationSlotEditorOpenForBooking: { text: 'Open for booking', description: 'Switch label in slot editor to enable bookings for a series.', surface: 'shell', maxChars: 25 },
  NLSEducationSlotEditorCapacityLabel: { text: 'Capacity', description: 'Label for booking capacity in slot editor.', surface: 'shell', maxChars: 20 },
  NLSEducationSlotEditorCapacityAria: { text: 'Session booking capacity', description: 'Aria label for capacity NumberStepper in slot editor.', surface: 'shell', maxChars: 30 },
  NLSEducationSlotEditorErrorNotTraining: { text: 'Only training series can be bookable.', description: 'Error message when attempting to make non-training series bookable.', surface: 'shell', maxChars: 60 },
  NLSEducationSlotEditorErrorInvalidCapacity: { text: 'Capacity must be an integer between 1 and 500.', description: 'Error message when series capacity is invalid.', surface: 'shell', maxChars: 60 },

  // Booking settings (EQ165)
  NLSEducationBookingSettingsMenuItem: { text: 'Booking settings', description: 'Menu item on programme root menu.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingSettingsTitle: { text: 'Booking settings', description: 'Title for booking settings dialog.', surface: 'shell', maxChars: 30 },
  NLSEducationBookingSettingsCancelWindow: { text: 'Cancellation window (hours)', description: 'Input label for cancel window in hours.', surface: 'shell', maxChars: 35 },
  NLSEducationBookingSettingsStrikesToBlock: { text: 'Strikes to block', description: 'Input label for number of strikes to trigger a block.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingSettingsStrikeWindowDays: { text: 'Strike window (days)', description: 'Input label for rolling strike window in days.', surface: 'shell', maxChars: 30 },
  NLSEducationBookingSettingsBlockDays: { text: 'Block duration (days)', description: 'Input label for block duration in days.', surface: 'shell', maxChars: 30 },
  NLSEducationBookingSettingsUsableWhen: { text: 'Usable when', description: 'Label for entitlement usability condition.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingSettingsUsablePaid: { text: 'Paid', description: 'Option for usable once paid.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingSettingsUsableAssigned: { text: 'Assigned', description: 'Option for usable when assigned.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingSettingsDropInToggle: { text: 'Enable pay-per-session drop-in', description: 'Toggle label for drop-in bookings.', surface: 'shell', maxChars: 40 },
  NLSEducationBookingSettingsDropInPrice: { text: 'Drop-in price', description: 'Input label for drop-in price.', surface: 'shell', maxChars: 20 },
  NLSEducationBookingSettingsMembershipCap: { text: 'Membership booking cap', description: 'Label for membership cap options.', surface: 'shell', maxChars: 30 },
  NLSEducationBookingSettingsCapNone: { text: 'None', description: 'Membership cap option none.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingSettingsCapWeek: { text: 'Per week', description: 'Membership cap option weekly.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingSettingsCapMonth: { text: 'Per month', description: 'Membership cap option monthly.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingSettingsCapCount: { text: 'Bookings limit', description: 'Input label for max bookings per period.', surface: 'shell', maxChars: 20 },
  NLSEducationBookingSettingsSave: { text: 'Save settings', description: 'Save button in booking settings dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationBookingSettingsSaving: { text: 'Saving…', description: 'Saving label in booking settings dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationBookingSettingsSuccess: { text: 'Booking settings saved successfully.', description: 'Success toast or alert for saved booking settings.', surface: 'shell', maxChars: 50 },

  // Session dialog Bookings section (EQ164)
  NLSEducationBookingSectionTitle: { text: 'Bookings', description: 'Section title for bookings in session dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationBookingCounts: { text: '{booked} / {capacity} booked', description: 'Booking capacity count display.', surface: 'shell', maxChars: 35 },
  NLSEducationBookingWaitlistCount: { text: '{count} on waitlist', description: 'Waitlist count display.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingRosterTitle: { text: 'Booked roster', description: 'Header for session booked roster.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingWaitlistTitle: { text: 'Waitlist', description: 'Header for session waitlist.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingWaitlistPos: { text: '#{pos}', description: 'Waitlist position badge.', surface: 'shell', maxChars: 10 },
  NLSEducationBookingEmptyRoster: { text: 'No trainees booked for this session.', description: 'Empty state message for booked roster.', surface: 'shell', maxChars: 45 },
  NLSEducationBookingEmptyWaitlist: { text: 'No trainees on the waitlist.', description: 'Empty state message for session waitlist.', surface: 'shell', maxChars: 40 },
  NLSEducationBookingActionBookFor: { text: 'Book for trainee', description: 'Action button to book a trainee.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingActionMove: { text: 'Move', description: 'Action button to move booking to another session.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingActionCancel: { text: 'Cancel booking', description: 'Action button to cancel a booking.', surface: 'shell', maxChars: 20 },
  NLSEducationBookingActionLeaveWaitlist: { text: 'Remove from waitlist', description: 'Action button to leave waitlist.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingModalBookTitle: { text: 'Book session for trainee', description: 'Modal title for booking for trainee.', surface: 'shell', maxChars: 30 },
  NLSEducationBookingModalMoveTitle: { text: 'Move booking', description: 'Modal title for moving booking.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingModalCancelTitle: { text: 'Cancel booking', description: 'Modal title for cancelling booking.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingPickerTrainee: { text: 'Select trainee', description: 'Label for trainee dropdown selector.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingPickerEntitlement: { text: 'Entitlement', description: 'Label for entitlement selector.', surface: 'shell', maxChars: 20 },
  NLSEducationBookingAutoPick: { text: 'Auto-pick entitlement', description: 'Option to auto-pick best entitlement.', surface: 'shell', maxChars: 30 },
  NLSEducationBookingDropInOption: { text: 'Pay per session (Drop-in)', description: 'Option for drop-in payment booking.', surface: 'shell', maxChars: 30 },
  NLSEducationBookingJoinWaitlist: { text: 'Join waitlist (session full)', description: 'Option or notice to join waitlist.', surface: 'shell', maxChars: 35 },
  NLSEducationBookingCancelReason: { text: 'Reason for cancellation', description: 'Label for cancellation reason input.', surface: 'shell', maxChars: 35 },
  NLSEducationBookingCancelReasonPlaceholder: { text: 'Enter cancellation reason…', description: 'Placeholder for cancellation reason.', surface: 'shell', maxChars: 40 },
  NLSEducationBookingWaiveLate: { text: 'Waive late cancellation penalty', description: 'Checkbox for staff to waive late cancellation fee/strike.', surface: 'shell', maxChars: 40 },
  NLSEducationBookingWaiveReason: { text: 'Waiver reason (required)', description: 'Label for waiver reason input.', surface: 'shell', maxChars: 35 },
  NLSEducationBookingMoveTargetSession: { text: 'Target session', description: 'Label for destination session selector in move modal.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingMoveReason: { text: 'Move reason', description: 'Label for move reason input.', surface: 'shell', maxChars: 25 },
  NLSEducationBookingConfirm: { text: 'Confirm', description: 'Confirm button in booking modals.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingStatusBooked: { text: 'Booked', description: 'Status pill for booked learner.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingStatusWaitlist: { text: 'Waitlist', description: 'Status pill for waitlisted learner.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingStatusAttended: { text: 'Attended', description: 'Status pill for attended learner.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingStatusNoShow: { text: 'No show', description: 'Status pill for no-show learner.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingStatusLateCancel: { text: 'Late cancel', description: 'Status pill for late-cancelled learner.', surface: 'shell', maxChars: 15 },

  // Bookings hub tab (EQ164)
  NLSEducationHubTabBookings: { text: 'Bookings', description: 'Hub tab for training session bookings.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingsWeekPrev: { text: 'Previous week', description: 'Button for previous week view.', surface: 'shell', maxChars: 20 },
  NLSEducationBookingsWeekNext: { text: 'Next week', description: 'Button for next week view.', surface: 'shell', maxChars: 20 },
  NLSEducationBookingsWeekThis: { text: 'This week', description: 'Button to return to current week.', surface: 'shell', maxChars: 15 },
  NLSEducationBookingsFullPill: { text: 'Full', description: 'Status pill when session is full.', surface: 'shell', maxChars: 10 },
  NLSEducationBookingsOpenPill: { text: 'Open', description: 'Status pill when session has open spots.', surface: 'shell', maxChars: 10 },
  NLSEducationBookingsEmptyTitle: { text: 'No bookable sessions', description: 'Empty state title in Bookings hub tab.', surface: 'shell', maxChars: 30 },
  NLSEducationBookingsEmptyBody: { text: 'There are no bookable sessions scheduled for this programme in the selected week.', description: 'Empty state description in Bookings hub tab.', surface: 'shell', maxChars: 85 },

  // Packs & bookings dialog (EQ166)
  NLSEducationLearnersBookingsBtn: { text: 'Packs & bookings', description: 'Row button in Learners tab to open bookings dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsTitle: { text: 'Packs & bookings — {name}', description: 'Title of trainee packs and bookings dialog.', surface: 'shell', maxChars: 45 },
  NLSEducationLearnerBookingsEntitlements: { text: 'Entitlements', description: 'Header for entitlements section in learner bookings dialog.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnerBookingsSessionsLeft: { text: '{left} sessions left ({used} used, {held} held of {total})', description: 'Sessions breakdown text for pack entitlement.', surface: 'shell', maxChars: 65 },
  NLSEducationLearnerBookingsValidDates: { text: 'Valid: {from} to {to}', description: 'Validity date range text.', surface: 'shell', maxChars: 35 },
  NLSEducationLearnerBookingsStatus: { text: 'Status: {status}', description: 'Status display for entitlement.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsFrozenBadge: { text: 'Frozen until {to}', description: 'Badge indicating membership is frozen.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsHistoryTitle: { text: 'Entitlement history', description: 'Header for entitlement history entries.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsActionExtend: { text: 'Extend', description: 'Button to extend pack or membership validity.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnerBookingsActionFreeze: { text: 'Freeze', description: 'Button to freeze membership.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnerBookingsActionUnfreeze: { text: 'Unfreeze', description: 'Button to unfreeze membership.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnerBookingsActionCredit: { text: 'Grant / revoke credit', description: 'Button to adjust credit balance.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsActionClearBlock: { text: 'Clear block', description: 'Button to clear active booking block.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnerBookingsExtendTitle: { text: 'Extend entitlement', description: 'Title for extend modal.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsExtendNewDate: { text: 'New expiry date', description: 'Input label for new validTo date.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsExtendReason: { text: 'Extension reason', description: 'Input label for extension reason.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsFreezeTitle: { text: 'Freeze membership', description: 'Title for freeze modal.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsFreezeFrom: { text: 'Freeze start date', description: 'Input label for freeze start date.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsFreezeTo: { text: 'Freeze end date', description: 'Input label for freeze end date.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsFreezeReason: { text: 'Freeze reason', description: 'Input label for freeze reason.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsUnfreezeTitle: { text: 'Unfreeze membership', description: 'Title for unfreeze modal.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsUnfreezeReason: { text: 'Unfreeze reason', description: 'Input label for unfreeze reason.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsCreditTitle: { text: 'Adjust booking credit', description: 'Title for credit modal.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsCreditGrant: { text: 'Grant credit', description: 'Option or button to grant credit.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnerBookingsCreditRevoke: { text: 'Revoke credit', description: 'Option or button to revoke credit.', surface: 'shell', maxChars: 20 },
  NLSEducationLearnerBookingsCreditReason: { text: 'Credit reason', description: 'Input label for credit adjustment reason.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsClearBlockTitle: { text: 'Clear booking block', description: 'Title for clear block modal.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsClearBlockReason: { text: 'Clear block reason', description: 'Input label for clear block reason.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsUpcomingTitle: { text: 'Upcoming bookings', description: 'Header for upcoming bookings list in learner dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsRecentTitle: { text: 'Recent bookings', description: 'Header for recent bookings list in learner dialog.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsStrikesTitle: { text: 'Strikes', description: 'Header for strikes section in learner dialog.', surface: 'shell', maxChars: 15 },
  NLSEducationLearnerBookingsStrikesCount: { text: '{count} active strike(s)', description: 'Display of active strike count.', surface: 'shell', maxChars: 25 },
  NLSEducationLearnerBookingsBlockedNotice: { text: 'Booking is blocked until {date}.', description: 'Notice banner when learner is blocked from booking.', surface: 'shell', maxChars: 50 },
  NLSEducationLearnerBookingsEmpty: { text: 'No active entitlements found.', description: 'Empty state when trainee has no entitlements.', surface: 'shell', maxChars: 35 },

  // Register hint (EQ167)
  NLSEducationRegisterBookingHint: { text: 'This session is open for booking. Present and Late marks use the booking credit; Absent marks count as a no-show that uses the credit and records a strike.', description: 'Hint line in register dialog for bookable sessions.', surface: 'shell', maxChars: 160 },

  // ED10.b2: Family booking (EQ168-EQ171)
  NLSFamilyTabBook: { text: 'Book', description: 'Label for Book tab in Family view.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookingsBookAheadDays: { text: 'Booking opens ahead (days)', description: 'Input label for how many days ahead booking opens.', surface: 'shell', maxChars: 35 },
  NLSFamilyBookProgrammePickerLabel: { text: 'Programme', description: 'Label for programme dropdown selector in Book tab.', surface: 'shell', maxChars: 20 },
  NLSFamilyBookProgrammePickerAria: { text: 'Select programme', description: 'Aria label for programme picker.', surface: 'shell', maxChars: 25 },
  NLSFamilyBookNoProgrammes: { text: 'No training programmes available for booking.', description: 'Notice when child has no training programmes.', surface: 'shell', maxChars: 50 },
  NLSFamilyBookNoSessions: { text: 'No bookable sessions available in the booking window.', description: 'Empty state when no sessions are open for booking.', surface: 'shell', maxChars: 65 },
  NLSFamilyBookSeatsLeft: { text: '{count} seats left', description: 'Seats remaining in session.', surface: 'shell', maxChars: 20 },
  NLSFamilyBookOneSeatLeft: { text: '1 seat left', description: 'One seat remaining in session.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookFullBadge: { text: 'Full', description: 'Badge indicating session is fully booked.', surface: 'shell', maxChars: 10 },
  NLSFamilyBookActionBook: { text: 'Book', description: 'Button to book a session.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookActionBookDropIn: { text: 'Book as drop-in ({price})', description: 'Button to book a drop-in session showing price.', surface: 'shell', maxChars: 35 },
  NLSFamilyBookActionJoinWaitlist: { text: 'Join waitlist', description: 'Button to join waitlist for full session.', surface: 'shell', maxChars: 20 },
  NLSFamilyBookActionLeaveWaitlist: { text: 'Leave waitlist', description: 'Button to withdraw from waitlist.', surface: 'shell', maxChars: 20 },
  NLSFamilyBookActionCancel: { text: 'Cancel', description: 'Button to cancel a booking.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookStatusBooked: { text: 'Booked', description: 'Status badge for booked session.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookStatusWaitlist: { text: 'Waitlist #{pos}', description: 'Status badge for waitlisted session with position.', surface: 'shell', maxChars: 20 },
  NLSFamilyBookCancelTitle: { text: 'Cancel booking', description: 'Title for cancel confirmation modal.', surface: 'shell', maxChars: 25 },
  NLSFamilyBookCancelConfirm: { text: 'Cancel booking', description: 'Confirm button in cancel modal.', surface: 'shell', maxChars: 20 },
  NLSFamilyBookCancelConfirmBody: { text: 'Are you sure you want to cancel this booking?', description: 'Confirmation prompt for cancelling a booking.', surface: 'shell', maxChars: 60 },
  NLSFamilyBookCancelLateWarning: { text: 'Cancelling now is inside the cancellation window and will use one session credit.', description: 'Warning when cancelling inside cancellation window.', surface: 'shell', maxChars: 90 },
  NLSFamilyBookCancelFreeOnceNotice: { text: 'This session was auto-promoted from the waitlist and can be cancelled free once.', description: 'Notice that auto-promoted session can be cancelled free once.', surface: 'shell', maxChars: 90 },
  NLSFamilyBookCancelledLateMessage: { text: 'Booking cancelled. Since it was cancelled within the cancellation window, one session credit was used.', description: 'Message when booking is cancelled late.', surface: 'shell', maxChars: 110 },
  NLSFamilyBookCancelledFreeOnceMessage: { text: 'Booking cancelled without using a session (free once grace).', description: 'Message when booking is cancelled using free once grace.', surface: 'shell', maxChars: 70 },
  NLSFamilyBookCancelledSuccessMessage: { text: 'Booking cancelled successfully.', description: 'Success message after cancelling booking.', surface: 'shell', maxChars: 40 },
  NLSFamilyBookWaitlistLeftMessage: { text: 'Removed from waitlist.', description: 'Message when removed from waitlist.', surface: 'shell', maxChars: 30 },
  NLSFamilyBookBookedSuccessMessage: { text: 'Session booked successfully.', description: 'Success message after booking session.', surface: 'shell', maxChars: 40 },
  NLSFamilyBookWaitlistedSuccessMessage: { text: 'Added to waitlist (position #{pos}).', description: 'Success message after joining waitlist.', surface: 'shell', maxChars: 45 },
  NLSFamilyBookDropInPaymentRequired: { text: 'Drop-in booked. Please pay at the front desk (due created in Fees).', description: 'Notice when drop-in requires payment.', surface: 'shell', maxChars: 85 },
  NLSFamilyBookTodayTomorrowTitle: { text: 'Booked sessions', description: 'Section title for booked sessions on Today tab.', surface: 'shell', maxChars: 25 },
  NLSFamilyBookingsCardTitle: { text: 'Packs, memberships & bookings', description: 'Card title for bookings panel in More tab.', surface: 'shell', maxChars: 40 },
  NLSFamilyMoreBookings: { text: 'Packs & bookings', description: 'Row label in Family More tab.', surface: 'shell', maxChars: 25 },
  NLSFamilyBookingsEntitlementsHeading: { text: 'Packs & memberships', description: 'Section heading for entitlements in Family Bookings panel.', surface: 'shell', maxChars: 30 },
  NLSFamilyBookingsHistoryHeading: { text: 'Booking history', description: 'Section heading for booking history.', surface: 'shell', maxChars: 25 },
  NLSFamilyBookingsDropInNote: { text: 'Drop-in session dues appear in Fees and are payable at the front desk.', description: 'Informational note about drop-in dues.', surface: 'shell', maxChars: 85 },
  NLSFamilyBookingsSessionsLeft: { text: '{left} sessions left ({used} used, {held} held of {total})', description: 'Session breakdown for pack.', surface: 'shell', maxChars: 65 },
  NLSFamilyBookingsMembershipCap: { text: '{count} sessions per {period}', description: 'Membership cap description.', surface: 'shell', maxChars: 35 },
  NLSFamilyBookingsValidDates: { text: 'Valid: {from} to {to}', description: 'Validity date range text.', surface: 'shell', maxChars: 35 },
  NLSFamilyBookingsFrozenBadge: { text: 'Frozen until {to}', description: 'Badge showing membership is frozen until date.', surface: 'shell', maxChars: 25 },
  NLSFamilyBookingsStatusActive: { text: 'Active', description: 'Status badge active.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookingsStatusFrozen: { text: 'Frozen', description: 'Status badge frozen.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookingsStatusExpired: { text: 'Expired', description: 'Status badge expired.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookingsNoEntitlements: { text: 'No active packs or memberships found.', description: 'Empty state message when no entitlements exist.', surface: 'shell', maxChars: 45 },
  NLSFamilyBookingsNoHistory: { text: 'No booking history available.', description: 'Empty state message when no bookings history.', surface: 'shell', maxChars: 35 },
  NLSFamilyBookingsStatusAttended: { text: 'Attended', description: 'Booking history status attended.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookingsStatusNoShow: { text: 'No show', description: 'Booking history status no show.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookingsStatusLateCancel: { text: 'Late cancel', description: 'Booking history status late cancel.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookingsStatusCancelled: { text: 'Cancelled', description: 'Booking history status cancelled.', surface: 'shell', maxChars: 15 },
  NLSFamilyBookErrorBookingBlocked: { text: 'Booking is blocked until {date} due to repeated strikes.', description: 'Error message when learner is blocked from booking.', surface: 'shell', maxChars: 75 },
  NLSFamilyBookErrorNoEntitlement: { text: 'No usable session packs or memberships found for this programme.', description: 'Error message when learner has no entitlement.', surface: 'shell', maxChars: 75 },
  NLSFamilyBookErrorBookingNotOpen: { text: 'Booking for this session is not open yet.', description: 'Error message when booking date exceeds book ahead window.', surface: 'shell', maxChars: 60 },
  NLSFamilyBookErrorSessionFull: { text: 'This session is full. You can join the waitlist.', description: 'Error message when session is full.', surface: 'shell', maxChars: 60 },
  NLSFamilyBookErrorPaymentRequired: { text: 'Payment is required before booking can be confirmed.', description: 'Error message when payment is required.', surface: 'shell', maxChars: 65 },
  NLSFamilyBookErrorMembershipCap: { text: 'Membership booking limit reached for this period.', description: 'Error message when membership cap is reached.', surface: 'shell', maxChars: 60 },
  NLSFamilyBookErrorAlreadyBooked: { text: 'Already booked for this session.', description: 'Error message when already booked.', surface: 'shell', maxChars: 40 },
  NLSFamilyBookErrorAlreadyWaitlisted: { text: 'Already on the waitlist for this session.', description: 'Error message when already on waitlist.', surface: 'shell', maxChars: 50 },
  NLSControlNoticeChatOff: { text: 'Your team admin turned chat off for you', description: 'Notice shown in chat composer when member chat control is OFF.', surface: 'chat', maxChars: 60 },
  NLSControlNoticeChatAnnouncementsOnly: { text: 'Only announcements can be posted here', description: 'Notice shown in chat composer when member is restricted to announcement channels.', surface: 'chat', maxChars: 60 },
  NLSControlNoticeChatNoDm: { text: 'Direct messages are turned off for your account', description: 'Notice shown in chat composer when member is restricted from direct messages.', surface: 'chat', maxChars: 65 },
  NLSControlNoticeAiOff: { text: 'AI is turned off for you by your team admin', description: 'Notice shown when AI control mode is OFF.', surface: 'ai', maxChars: 60 },
  NLSControlNoticeAiBudgetExceeded: { text: 'Your daily AI budget has been used up', description: 'Notice shown when member daily AI budget is exhausted.', surface: 'ai', maxChars: 60 },
  NLSControlNoticePageHidden: { text: 'Your team admin hid this page', description: 'Notice shown when trying to open a page that has been hidden by team admin controls.', surface: 'member', maxChars: 60 },
  NLSControlsVisiblePages: { text: 'Visible pages', description: 'Section title for visible pages control in member controls editor.', surface: 'member', maxChars: 30 },
  NLSControlsVisiblePagesDesc: { text: 'Choose which pages and tabs this member can see and access.', description: 'Description for visible pages control in member controls editor.', surface: 'member', maxChars: 80 },
  NLSControlsPageLocked: { text: 'Always visible', description: 'Tooltip or label indicating a never-hideable page is locked visible.', surface: 'member', maxChars: 25 },
  NLSControlsPagesBulkOption: { text: 'Visible pages', description: 'Bulk action dropdown option for visible pages.', surface: 'member', maxChars: 25 },
  NLSControlsGroupCore: { text: 'Core (Always visible)', description: 'Header for core/home pages group in visible pages control.', surface: 'member', maxChars: 35 },
  NLSControlsGroupMain: { text: 'Main & Mobile tabs', description: 'Header for main tabs group in visible pages control.', surface: 'member', maxChars: 35 },
  NLSControlsGroupWeb: { text: 'Web pages & tools', description: 'Header for web-only pages group in visible pages control.', surface: 'member', maxChars: 35 },
  NLSControlsFunPack: { text: 'Fun pack', description: 'Label for fun pack member control.', surface: 'member', maxChars: 20 },
  NLSControlsFunPackInherit: { text: 'Default (Inherit)', description: 'Option for fun pack inheriting workspace/profile default.', surface: 'member', maxChars: 30 },
  NLSControlsFunPackOff: { text: 'Off', description: 'Option for turning fun pack off.', surface: 'member', maxChars: 15 },
  NLSControlsChat: { text: 'Chat', description: 'Label for chat member control.', surface: 'member', maxChars: 20 },
  NLSControlsChatFull: { text: 'Full access', description: 'Option for unrestricted chat access.', surface: 'member', maxChars: 25 },
  NLSControlsChatAnnouncements: { text: 'Announcements only', description: 'Option for restricting chat to announcements.', surface: 'member', maxChars: 30 },
  NLSControlsChatNoDm: { text: 'No direct messages', description: 'Option for disabling direct messages in chat.', surface: 'member', maxChars: 30 },
  NLSControlsChatOff: { text: 'Off', description: 'Option for turning chat off.', surface: 'member', maxChars: 15 },
  NLSControlsAi: { text: 'AI', description: 'Label for AI member control.', surface: 'member', maxChars: 20 },
  NLSControlsAiInherit: { text: 'Inherit', description: 'Option for inheriting AI settings from profile.', surface: 'member', maxChars: 20 },
  NLSControlsAiOff: { text: 'Off', description: 'Option for disabling AI features.', surface: 'member', maxChars: 15 },
  NLSControlsAiBudget: { text: 'Daily budget', description: 'Option for setting a daily AI budget.', surface: 'member', maxChars: 25 },
  NLSControlsAiDailyCredits: { text: 'Daily credits', description: 'Field label for AI daily credits limit.', surface: 'member', maxChars: 25 },
  NLSControlsFromProfile: { text: 'from profile {name}', description: 'Indicates control value is inherited from a profile.', surface: 'member', maxChars: 40 },
  NLSControlsYourOverride: { text: 'your override', description: 'Indicates control value is a direct override.', surface: 'member', maxChars: 25 },
  NLSControlsResetToProfile: { text: 'Reset to profile', description: 'Action to clear override and reset control to profile value.', surface: 'member', maxChars: 30 },
  NLSControlsOwnersHaveNoControls: { text: 'Owners have no controls', description: 'Text indicating workspace owners are exempt from controls.', surface: 'member', maxChars: 40 },
  NLSControlsProfilePickerLabel: { text: 'Control profile', description: 'Label for selecting a control profile.', surface: 'member', maxChars: 25 },
  NLSControlsProfileNone: { text: 'None', description: 'Option for no control profile applied.', surface: 'member', maxChars: 15 },
  NLSControlsSave: { text: 'Save controls', description: 'Action button to save member controls.', surface: 'member', maxChars: 25 },
  NLSControlsProfilesAction: { text: 'Profiles', description: 'Action button to open control profiles manager.', surface: 'member', maxChars: 20 },
  NLSControlsUsedByCount: { text: 'used by {count}', description: 'Count of members using a control profile.', surface: 'member', maxChars: 30 },
  NLSControlsApplyProfile: { text: 'Apply profile', description: 'Bulk action to apply a profile to selected members.', surface: 'member', maxChars: 25 },
  NLSControlsSetControl: { text: 'Set control', description: 'Bulk action to set a specific control on selected members.', surface: 'member', maxChars: 25 },
  NLSControlsDeleteConfirmTitle: { text: 'Delete profile', description: 'Title for deleting a control profile dialog.', surface: 'member', maxChars: 30 },
  NLSControlsDeleteConfirmBody: { text: 'Deleting this profile will affect {count} people who will revert to default controls.', description: 'Body explaining impact of deleting a profile.', surface: 'member', maxChars: 100 },
  NLSControlsCreateProfile: { text: 'Create profile', description: 'Action button to create a control profile.', surface: 'member', maxChars: 25 },
  NLSControlsEditProfile: { text: 'Edit profile', description: 'Action button to edit a control profile.', surface: 'member', maxChars: 25 },
  NLSControlsProfileName: { text: 'Profile name', description: 'Label for control profile name input.', surface: 'member', maxChars: 30 },
  NLSControlsProfilesTitle: { text: 'Control Profiles', description: 'Title for control profiles manager panel or modal.', surface: 'member', maxChars: 30 },
  NLSControlsProfilesEmpty: { text: 'No control profiles yet.', description: 'Empty state message when no control profiles exist.', surface: 'member', maxChars: 50 },
  NLSControlsSelectMode: { text: 'Select', description: 'Toggle select mode for multi-select roster members.', surface: 'member', maxChars: 15 },
  NLSControlsSelectAllShown: { text: 'Select all shown', description: 'Select all visible members.', surface: 'member', maxChars: 25 },
  NLSControlsSelectedCount: { text: '{count} selected', description: 'Number of members selected.', surface: 'member', maxChars: 25 },
  NLSControlsBulkResult: { text: 'Updated {updated}, skipped {skipped}, failed {failed}', description: 'Summary message of bulk controls operation.', surface: 'member', maxChars: 60 },
  NLSControlsProfileDelete: { text: 'Delete', description: 'Action button to delete a profile.', surface: 'member', maxChars: 15 },
  NLSControlsChatAnnouncementsOnly: { text: 'Announcements only', description: 'Option for restricting chat to announcements.', surface: 'member', maxChars: 30 },
  NLSControlsSelectProfile: { text: 'Select profile', description: 'Label for selecting a profile in bulk dialog.', surface: 'member', maxChars: 25 },
  NLSControlsControlToSet: { text: 'Control to set', description: 'Label for control type picker in bulk dialog.', surface: 'member', maxChars: 25 },
  NLSControlsChatAccess: { text: 'Chat access', description: 'Label for chat access select in bulk dialog.', surface: 'member', maxChars: 25 },
  NLSControlsAiDailyCreditsAria: { text: 'Daily AI budget credits', description: 'Aria label for daily AI budget credits input.', surface: 'member', maxChars: 35 },
  NLSControlsSavedFeedback: { text: 'Controls saved.', description: 'Feedback text when member controls are saved successfully.', surface: 'member', maxChars: 25 },
  NLSControlsSaveFailedFeedback: { text: 'Could not save controls.', description: 'Feedback text when saving member controls fails.', surface: 'member', maxChars: 35 },

  // ED11.b1 Progress Metrics
  NLSEduProgressMetricsMenuItem: { text: 'Progress metrics', description: 'Menu item for progress metrics setup in classes builder.', surface: 'shell', maxChars: 25 },
  NLSEduProgressMetricsDialogTitle: { text: 'Progress metrics', description: 'Title for progress metrics dialog.', surface: 'shell', maxChars: 30 },
  NLSEduProgressMetricsDialogDesc: { text: 'Define tracking metrics and target values for this programme.', description: 'Description for progress metrics dialog.', surface: 'shell', maxChars: 90 },
  NLSEduProgressMetricsAddBtn: { text: 'Add metric', description: 'Button to add a new progress metric.', surface: 'shell', maxChars: 20 },
  NLSEduProgressMetricsEditAction: { text: 'Edit', description: 'Action to edit progress metric.', surface: 'shell', maxChars: 15 },
  NLSEduProgressMetricsArchiveAction: { text: 'Archive', description: 'Action to archive progress metric.', surface: 'shell', maxChars: 15 },
  NLSEduProgressMetricsUnarchiveAction: { text: 'Unarchive', description: 'Action to unarchive progress metric.', surface: 'shell', maxChars: 15 },
  NLSEduProgressMetricNameLabel: { text: 'Metric name', description: 'Label for progress metric name.', surface: 'shell', maxChars: 25 },
  NLSEduProgressMetricUnitLabel: { text: 'Unit (e.g. kg, reps, sec)', description: 'Label for metric unit.', surface: 'shell', maxChars: 35 },
  NLSEduProgressMetricKindLabel: { text: 'Type', description: 'Label for metric type.', surface: 'shell', maxChars: 20 },
  NLSEduProgressMetricKindNumber: { text: 'Number', description: 'Option for number metric type.', surface: 'shell', maxChars: 20 },
  NLSEduProgressMetricKindDuration: { text: 'Duration (seconds)', description: 'Option for duration metric type.', surface: 'shell', maxChars: 25 },
  NLSEduProgressMetricKindCount: { text: 'Count', description: 'Option for count metric type.', surface: 'shell', maxChars: 20 },
  NLSEduProgressMetricKindPassFail: { text: 'Pass / Fail', description: 'Option for pass fail metric type.', surface: 'shell', maxChars: 20 },
  NLSEduProgressMetricDirectionLabel: { text: 'Direction', description: 'Label for metric direction.', surface: 'shell', maxChars: 20 },
  NLSEduProgressMetricDirectionHigher: { text: 'Higher is better', description: 'Option higher is better.', surface: 'shell', maxChars: 25 },
  NLSEduProgressMetricDirectionLower: { text: 'Lower is better', description: 'Option lower is better.', surface: 'shell', maxChars: 25 },
  NLSEduProgressMetricTargetLabel: { text: 'Target value (optional)', description: 'Label for optional target value.', surface: 'shell', maxChars: 35 },
  NLSEduProgressMetricSelfLoggableLabel: { text: 'Allow trainees to self-report', description: 'Toggle label to allow self-reporting.', surface: 'shell', maxChars: 40 },
  NLSEduProgressMetricOrderLabel: { text: 'Display order', description: 'Label for metric order.', surface: 'shell', maxChars: 20 },
  NLSEduProgressMetricsEmpty: { text: 'No progress metrics configured for this programme yet.', description: 'Empty state message for progress metrics.', surface: 'shell', maxChars: 70 },
  NLSEduProgressMetricsStaleVersion: { text: 'This metric was modified by another session. The list has been reloaded.', description: 'Stale version conflict error.', surface: 'shell', maxChars: 85 },
  NLSEduProgressMetricsSaveBtn: { text: 'Save metric', description: 'Button label to save progress metric.', surface: 'shell', maxChars: 20 },

  // ED11.b1 Progress Levels
  NLSEduProgressLevelsMenuItem: { text: 'Levels', description: 'Menu item for levels setup in classes builder.', surface: 'shell', maxChars: 20 },
  NLSEduProgressLevelsDialogTitle: { text: 'Levels & criteria', description: 'Title for levels dialog.', surface: 'shell', maxChars: 30 },
  NLSEduProgressLevelsDialogDesc: { text: 'Set ordered levels and criteria for learner progression in this programme.', description: 'Description for levels dialog.', surface: 'shell', maxChars: 90 },
  NLSEduProgressLevelsAddBtn: { text: 'Add level', description: 'Button to add a new level.', surface: 'shell', maxChars: 20 },
  NLSEduProgressLevelsEditAction: { text: 'Edit', description: 'Action to edit level.', surface: 'shell', maxChars: 15 },
  NLSEduProgressLevelsRemoveAction: { text: 'Remove', description: 'Action to remove level.', surface: 'shell', maxChars: 15 },
  NLSEduProgressLevelNameLabel: { text: 'Level name', description: 'Label for level name.', surface: 'shell', maxChars: 25 },
  NLSEduProgressLevelColourLabel: { text: 'Colour', description: 'Label for level colour.', surface: 'shell', maxChars: 20 },
  NLSEduProgressLevelCriteriaHeading: { text: 'Progression criteria (advisory)', description: 'Section heading for level criteria.', surface: 'shell', maxChars: 40 },
  NLSEduProgressLevelMinAttendance: { text: 'Minimum attendance %', description: 'Label for min attendance criteria.', surface: 'shell', maxChars: 30 },
  NLSEduProgressLevelMinDays: { text: 'Minimum days at previous level', description: 'Label for min days at previous level.', surface: 'shell', maxChars: 40 },
  NLSEduProgressLevelMetricTargets: { text: 'Metric targets', description: 'Heading for metric targets list in criteria.', surface: 'shell', maxChars: 30 },
  NLSEduProgressLevelAddMetricTarget: { text: 'Add target', description: 'Button to add metric target to level.', surface: 'shell', maxChars: 20 },
  NLSEduProgressLevelCertTemplateLabel: { text: 'Certificate template (optional)', description: 'Label for optional certificate template selection.', surface: 'shell', maxChars: 40 },
  NLSEduProgressLevelCertTemplateNone: { text: 'None', description: 'Option for no certificate template.', surface: 'shell', maxChars: 15 },
  NLSEduProgressLevelsRemovalRefused: { text: 'Cannot remove level because learners are currently at this level: {holders}', description: 'Error message when level removal is refused due to active holders.', surface: 'shell', maxChars: 120 },
  NLSEduProgressLevelsEmpty: { text: 'No levels defined for this programme yet.', description: 'Empty state message for levels.', surface: 'shell', maxChars: 60 },
  NLSEduProgressLevelsSaveBtn: { text: 'Save levels', description: 'Button to save levels.', surface: 'shell', maxChars: 20 },

  // ED11.b1 Certificate Templates
  NLSEduCertTemplatesSectionTitle: { text: 'Certificate templates', description: 'Section title in Education settings for certificate templates.', surface: 'settings', maxChars: 35 },
  NLSEduCertTemplatesSectionDesc: { text: 'Create and manage certificate templates with placeholders for completions and achievements.', description: 'Section description for certificate templates in settings.', surface: 'settings', maxChars: 110 },
  NLSEduCertTemplatesAddBtn: { text: 'New template', description: 'Button to create a new certificate template.', surface: 'settings', maxChars: 20 },
  NLSEduCertTemplatesEditAction: { text: 'Edit', description: 'Action to edit template.', surface: 'settings', maxChars: 15 },
  NLSEduCertTemplatesArchiveAction: { text: 'Archive', description: 'Action to archive template.', surface: 'settings', maxChars: 15 },
  NLSEduCertTemplatesUnarchiveAction: { text: 'Unarchive', description: 'Action to unarchive template.', surface: 'settings', maxChars: 15 },
  NLSEduCertTemplateNameLabel: { text: 'Template name', description: 'Label for certificate template name.', surface: 'settings', maxChars: 25 },
  NLSEduCertTemplateTitleLabel: { text: 'Certificate title', description: 'Label for certificate title text.', surface: 'settings', maxChars: 30 },
  NLSEduCertTemplateBodyLabel: { text: 'Body text', description: 'Label for certificate body text.', surface: 'settings', maxChars: 25 },
  NLSEduCertTemplateBodyPlaceholderHint: { text: 'Available placeholders: {name}, {programme}, {level}, {issueDate}, {expiryDate}', description: 'Hint showing allowed placeholders.', surface: 'settings', maxChars: 90 },
  NLSEduCertTemplateValidityLabel: { text: 'Validity (months, 1–120 or empty for no expiry)', description: 'Label for certificate validity in months.', surface: 'settings', maxChars: 55 },
  NLSEduCertTemplatesEmpty: { text: 'No certificate templates created yet.', description: 'Empty state message for certificate templates.', surface: 'settings', maxChars: 60 },
  NLSEduCertTemplateSaveBtn: { text: 'Save template', description: 'Button label to save certificate template.', surface: 'settings', maxChars: 20 },
  NLSEduCertTemplateValidityMonths: { text: '{months} months', description: 'Validity in months label.', surface: 'settings', maxChars: 20 },
  NLSEduCertTemplateNoExpiry: { text: 'No expiry', description: 'Validity no expiry label.', surface: 'settings', maxChars: 20 },

  // ED11.b1 Hub Tab & ProgressTab
  NLSEducationTabProgress: { text: 'Progress', description: 'Progress sub-tab in education hub.', surface: 'shell', maxChars: 20 },
  NLSEduProgressSelectProgramme: { text: 'Programme', description: 'Label for programme selector in Progress tab.', surface: 'shell', maxChars: 20 },
  NLSEduProgressNoProgrammes: { text: 'No training programmes found.', description: 'Message when no training programmes exist.', surface: 'shell', maxChars: 40 },
  NLSEduProgressViewTrainees: { text: 'Trainees', description: 'SegmentedControl view option Trainees.', surface: 'shell', maxChars: 20 },
  NLSEduProgressViewRecord: { text: 'Record', description: 'SegmentedControl view option Record.', surface: 'shell', maxChars: 20 },
  NLSEduProgressViewSelfReports: { text: 'Self-reports', description: 'SegmentedControl view option Self-reports.', surface: 'shell', maxChars: 20 },
  NLSEduProgressTraineeCol: { text: 'Trainee', description: 'Column header Trainee.', surface: 'shell', maxChars: 20 },
  NLSEduProgressLevelCol: { text: 'Level', description: 'Column header Level.', surface: 'shell', maxChars: 20 },
  NLSEduProgressPendingCol: { text: 'Pending self-reports', description: 'Column header Pending self-reports.', surface: 'shell', maxChars: 30 },
  NLSEduProgressNoTrainees: { text: 'No trainees enrolled in this programme.', description: 'Empty state when no trainees in programme.', surface: 'shell', maxChars: 50 },
  NLSEduProgressTraineeRowAria: { text: 'View progress for {name}', description: 'Aria label on trainee row button stub.', surface: 'shell', maxChars: 45 },
  NLSEduProgressRecordMetricLabel: { text: 'Metric', description: 'Label for metric select in record grid.', surface: 'shell', maxChars: 20 },
  NLSEduProgressRecordDateLabel: { text: 'Date', description: 'Label for date in record grid.', surface: 'shell', maxChars: 20 },
  NLSEduProgressRecordFutureDateError: { text: 'Cannot record progress values for future dates.', description: 'Error message when date is in the future.', surface: 'shell', maxChars: 60 },
  NLSEduProgressRecordNotEnrolledError: { text: 'Trainee is not enrolled in programme on this date.', description: 'Row error when trainee is not enrolled.', surface: 'shell', maxChars: 65 },
  NLSEduProgressRecordSaveBtn: { text: 'Save values', description: 'Button to save progress grid values.', surface: 'shell', maxChars: 20 },
  NLSEduProgressRecordSavedSuccess: { text: 'Progress values saved successfully.', description: 'Success notification when values are saved.', surface: 'shell', maxChars: 50 },
  NLSEduProgressRecordNoLearners: { text: 'No active trainees found to record.', description: 'Notice when no active trainees to record.', surface: 'shell', maxChars: 45 },
  NLSEduProgressSelfReportsEmpty: { text: 'No pending self-reported values for this programme.', description: 'Empty state for self-reports queue.', surface: 'shell', maxChars: 65 },
  NLSEduProgressSelfReportsConfirmBtn: { text: 'Confirm', description: 'Button to confirm a self-reported value.', surface: 'shell', maxChars: 15 },
  NLSEduProgressSelfReportsRejectBtn: { text: 'Reject', description: 'Button to reject a self-reported value.', surface: 'shell', maxChars: 15 },
  NLSEduProgressSelfReportsBatchConfirm: { text: 'Confirm selected ({count})', description: 'Batch button to confirm selected self-reports.', surface: 'shell', maxChars: 30 },
  NLSEduProgressSelfReportsBatchReject: { text: 'Reject selected ({count})', description: 'Batch button to reject selected self-reports.', surface: 'shell', maxChars: 30 },
  NLSEduProgressSelfReportsRejectTitle: { text: 'Reject self-reported value', description: 'Dialog title for rejecting self-report.', surface: 'shell', maxChars: 35 },
  NLSEduProgressSelfReportsRejectReasonLabel: { text: 'Reason for rejection (required)', description: 'Label for reject reason field.', surface: 'shell', maxChars: 40 },
  NLSEduProgressSelfReportsRejectReasonError: { text: 'Please provide a reason between 1 and 500 characters.', description: 'Validation error for empty reject reason.', surface: 'shell', maxChars: 65 },
  NLSEduProgressSelfReportsNotPending: { text: 'This value is no longer pending.', description: 'Error when value was already confirmed or rejected.', surface: 'shell', maxChars: 45 },
  NLSEduProgressSelfReportsSelectAll: { text: 'Select all pending self-reports', description: 'Aria label for select all checkbox.', surface: 'shell', maxChars: 40 },
  NLSEduProgressSelfReportsSelectRow: { text: 'Select report for {name}', description: 'Aria label for row checkbox in self-reports queue.', surface: 'shell', maxChars: 40 },
  NLSEduProgressNoLevel: { text: 'No level', description: 'Label when learner has no assigned level.', surface: 'shell', maxChars: 15 },
  NLSEduProgressLoadProgFailed: { text: 'Failed to load programme progress.', description: 'Error fallback when loading programme progress fails.', surface: 'shell', maxChars: 45 },
  NLSEduProgressLoadReportsFailed: { text: 'Failed to load pending self-reports.', description: 'Error fallback when loading pending self-reports fails.', surface: 'shell', maxChars: 45 },
  NLSEduProgressConfirmReportsFailed: { text: 'Failed to confirm report(s).', description: 'Error fallback when confirming reports fails.', surface: 'shell', maxChars: 40 },
  NLSEduProgressRejectReportsFailed: { text: 'Failed to reject report(s).', description: 'Error fallback when rejecting reports fails.', surface: 'shell', maxChars: 40 },
  NLSEduProgressRecordSaveFailed: { text: 'Failed to save progress values.', description: 'Error fallback when saving progress values fails.', surface: 'shell', maxChars: 40 },
  NLSEduCertTemplateLoadFailed: { text: 'Failed to load certificate templates.', description: 'Error fallback when loading certificate templates fails.', surface: 'shell', maxChars: 45 },
  NLSEduCertTemplateSaveFailed: { text: 'Failed to save certificate template.', description: 'Error fallback when saving certificate template fails.', surface: 'shell', maxChars: 45 },
  NLSEduCertTemplateUpdateFailed: { text: 'Failed to update certificate template.', description: 'Error fallback when updating certificate template fails.', surface: 'shell', maxChars: 45 },
  NLSEduCertTemplateUnknownPlaceholder: { text: 'Unknown placeholder {placeholder} in {field}', description: 'Field error when an invalid placeholder is in certificate template.', surface: 'shell', maxChars: 65 },
  NLSEduProgressMetricsLoadFailed: { text: 'Failed to load progress metrics.', description: 'Error fallback when loading metrics fails.', surface: 'shell', maxChars: 40 },
  NLSEduProgressMetricsSaveFailed: { text: 'Failed to save progress metric.', description: 'Error fallback when saving metric fails.', surface: 'shell', maxChars: 40 },
  NLSEduProgressMetricsUpdateFailed: { text: 'Failed to update metric.', description: 'Error fallback when updating metric fails.', surface: 'shell', maxChars: 35 },
  NLSEduProgressLevelsLoadFailed: { text: 'Failed to load progress levels.', description: 'Error fallback when loading levels fails.', surface: 'shell', maxChars: 40 },
  NLSEduProgressLevelsSaveFailed: { text: 'Failed to save levels.', description: 'Error fallback when saving levels fails.', surface: 'shell', maxChars: 35 },
  NLSEduProgressPassValue: { text: 'PASS', description: 'Pass value in progress table.', surface: 'shell', maxChars: 10 },
  NLSEduProgressFailValue: { text: 'FAIL', description: 'Fail value in progress table.', surface: 'shell', maxChars: 10 },
  NLSEduProgressViewOptionsAria: { text: 'Progress view options', description: 'Aria label for Progress tab segmented view options.', surface: 'shell', maxChars: 30 },
  NLSEduProgressLevelCriteriaCount: { text: '{count} criteria', description: 'Criteria count summary on level card.', surface: 'shell', maxChars: 25 },
  NLSEduProgressLevelNoCriteria: { text: 'No criteria', description: 'Label when level has no criteria.', surface: 'shell', maxChars: 20 },
  NLSEduProgressLevelsHoldersDefault: { text: 'learners', description: 'Default fallback word for level holders.', surface: 'shell', maxChars: 15 },
  NLSEduProgressColValue: { text: 'Value', description: 'Table column header for value.', surface: 'shell', maxChars: 15 },
  NLSEduProgressColNote: { text: 'Note', description: 'Table column header for note.', surface: 'shell', maxChars: 15 },
  NLSEduProgressColReportedBy: { text: 'Reported by', description: 'Table column header for reporter.', surface: 'shell', maxChars: 20 },
  NLSEduProgressColActions: { text: 'Actions', description: 'Table column header for actions.', surface: 'shell', maxChars: 15 },

  // ED11.b2: Trainee Progress View
  NLSEduTraineeProgressBackBtn: { text: 'Back to trainees', description: 'Button to return to the trainees list table.', surface: 'shell', maxChars: 25 },
  NLSEduTraineeProgressAria: { text: 'Trainee progress for {name}', description: 'Accessible label for trainee progress view.', surface: 'shell', maxChars: 50 },
  NLSEduTraineeCurrentLevel: { text: 'Current level', description: 'Label for current level.', surface: 'shell', maxChars: 20 },
  NLSEduTraineeLevelSince: { text: 'Since {date}', description: 'Date since learner held current level.', surface: 'shell', maxChars: 25 },
  NLSEduTraineeNoCurrentLevel: { text: 'No level achieved yet', description: 'Text when learner has no level.', surface: 'shell', maxChars: 30 },
  NLSEduTraineeNextLevel: { text: 'Next target: {level}', description: 'Label indicating next progress level.', surface: 'shell', maxChars: 35 },
  NLSEduTraineeCriteriaAdvisoryHeader: { text: 'Progression criteria (advisory)', description: 'Header for advisory criteria checklist.', surface: 'shell', maxChars: 40 },
  NLSEduTraineeCriteriaAdvisoryNote: { text: 'Criteria are advisory indicators. Trainers may promote learners at any time.', description: 'Advisory explanation note for criteria checklist.', surface: 'shell', maxChars: 90 },
  NLSEduTraineeCriteriaAttendance: { text: 'Attendance: {actual}% (Target: {target}%)', description: 'Attendance criterion line.', surface: 'shell', maxChars: 45 },
  NLSEduTraineeCriteriaDays: { text: 'Days at level: {actual} (Target: {target})', description: 'Days at previous level criterion line.', surface: 'shell', maxChars: 45 },
  NLSEduTraineeCriteriaMetricTarget: { text: '{metric}: {actual} (Target: {target})', description: 'Metric target criterion line.', surface: 'shell', maxChars: 50 },
  NLSEduTraineeCriteriaMet: { text: 'Met', description: 'Status pill for met criterion.', surface: 'shell', maxChars: 15 },
  NLSEduTraineeCriteriaNotMet: { text: 'Not met', description: 'Status pill for unmet criterion.', surface: 'shell', maxChars: 15 },
  NLSEduTraineeMaxLevelReached: { text: 'Highest level achieved', description: 'Indicator when no further level exists.', surface: 'shell', maxChars: 30 },

  // ED11.b2: Promote Dialog & Actions
  NLSEduTraineePromoteBtn: { text: 'Promote', description: 'Button to trigger promote dialog.', surface: 'shell', maxChars: 15 },
  NLSEduTraineePromoteTitle: { text: 'Promote learner', description: 'Title for promote dialog.', surface: 'shell', maxChars: 25 },
  NLSEduTraineePromoteTargetLevelLabel: { text: 'Target level', description: 'Label for target level selector.', surface: 'shell', maxChars: 25 },
  NLSEduTraineePromoteDateLabel: { text: 'Effective date', description: 'Label for effective date input.', surface: 'shell', maxChars: 25 },
  NLSEduTraineePromoteNoteLabel: { text: 'Note', description: 'Label for promotion note textarea.', surface: 'shell', maxChars: 20 },
  NLSEduTraineePromoteNotePlaceholder: { text: 'Add notes regarding this promotion...', description: 'Placeholder for promotion note.', surface: 'shell', maxChars: 60 },
  NLSEduTraineePromoteDemotionNoteRequired: { text: 'A note is required when moving to a lower level.', description: 'Validation error when note is missing for demotion.', surface: 'shell', maxChars: 65 },
  NLSEduTraineePromoteFutureDateError: { text: 'Effective date cannot be in the future.', description: 'Validation error when promotion date is in the future.', surface: 'shell', maxChars: 50 },
  NLSEduTraineePromoteUnmetCriteriaWarning: { text: 'Some progression criteria are not yet met. Promotion is permitted as criteria are advisory.', description: 'Warning shown when criteria are unmet before promotion.', surface: 'shell', maxChars: 100 },
  NLSEduTraineePromoteCriteriaNextLevelOnlyNote: { text: 'Criteria are checked for the next level only.', description: 'Advisory note shown when selecting a higher level beyond next level.', surface: 'shell', maxChars: 50 },
  NLSEduTraineePromoteConfirmBtn: { text: 'Confirm promotion', description: 'Submit button in promote dialog.', surface: 'shell', maxChars: 25 },
  NLSEduTraineePromoteCancelBtn: { text: 'Cancel', description: 'Cancel button in promote dialog.', surface: 'shell', maxChars: 15 },
  NLSEduTraineePromoteCertBanner: { text: 'Level updated and certificate issued.', description: 'Success notification banner when certificate is issued upon promotion.', surface: 'shell', maxChars: 50 },
  NLSEduTraineePromoteSuccessBanner: { text: 'Learner level updated successfully.', description: 'Success notification banner for level update without certificate.', surface: 'shell', maxChars: 45 },
  NLSEduTraineePromoteFailed: { text: 'Failed to promote learner.', description: 'Error fallback when promotion fails.', surface: 'shell', maxChars: 35 },
  NLSEduProgressPromoteSameLevelError: { text: 'Learner is already at this level.', description: 'Error message when promoting to the existing level.', surface: 'shell', maxChars: 40 },

  // ED11.b2: Headings & Sections
  NLSEduTraineeMetricsHeading: { text: 'Progress metrics', description: 'Heading for progress charts section.', surface: 'shell', maxChars: 25 },
  NLSEduTraineeHistoryHeading: { text: 'Promotion history', description: 'Heading for promotion history section.', surface: 'shell', maxChars: 25 },
  NLSEduTraineeCertificatesHeading: { text: 'Certificates', description: 'Heading for trainee certificates section.', surface: 'shell', maxChars: 25 },
  NLSEduTraineePracticeHeading: { text: 'Practice logs', description: 'Heading for practice logs section.', surface: 'shell', maxChars: 25 },

  // ED11.b2: Metrics Charts
  NLSEduTraineeChartAria: { text: 'Progress chart for {name}', description: 'Aria label for a metric progress chart.', surface: 'shell', maxChars: 50 },
  NLSEduTraineeMetricNoValues: { text: 'No recorded values for this metric yet.', description: 'Empty state message inside metric card.', surface: 'shell', maxChars: 55 },
  NLSEduTraineeChartSelfReportedLegend: { text: 'Hollow points indicate self-reported values', description: 'Legend explanation for self-reported chart points.', surface: 'shell', maxChars: 55 },
  NLSEduTraineeMetricLatestValue: { text: 'Latest: {value} {unit}', description: 'Label displaying the latest recorded metric value.', surface: 'shell', maxChars: 30 },
  NLSEduTraineeMetricTargetValue: { text: 'Target: {target} {unit}', description: 'Label displaying the metric target value.', surface: 'shell', maxChars: 30 },

  // ED11.b2: History Section
  NLSEduTraineeHistoryEmpty: { text: 'No level changes recorded yet.', description: 'Empty state message for promotion history.', surface: 'shell', maxChars: 40 },
  NLSEduTraineeHistoryFromTo: { text: '{from} → {to}', description: 'Promotion progression from one level to another.', surface: 'shell', maxChars: 40 },
  NLSEduTraineeHistoryInitial: { text: 'Initial level: {to}', description: 'First promotion history entry label.', surface: 'shell', maxChars: 35 },
  NLSEduTraineeHistoryDateBy: { text: '{date} by {by}', description: 'Promotion date and author line.', surface: 'shell', maxChars: 40 },
  NLSEduTraineeHistoryCriteriaSnapshot: { text: 'Criteria evaluated: {summary}', description: 'Snapshot summary of evaluation at time of promotion.', surface: 'shell', maxChars: 60 },

  // ED11.b2: Certificates
  NLSEduCertColTitle: { text: 'Certificate', description: 'Column header Certificate.', surface: 'shell', maxChars: 20 },
  NLSEduCertColLevel: { text: 'Level', description: 'Column header Level in certificates.', surface: 'shell', maxChars: 15 },
  NLSEduCertColSource: { text: 'Source', description: 'Column header Source in certificates.', surface: 'shell', maxChars: 15 },
  NLSEduCertColIssueDate: { text: 'Issued', description: 'Column header Issue date in certificates.', surface: 'shell', maxChars: 15 },
  NLSEduCertColExpiryDate: { text: 'Expires', description: 'Column header Expiry date in certificates.', surface: 'shell', maxChars: 15 },
  NLSEduCertColStatus: { text: 'Status', description: 'Column header Status in certificates.', surface: 'shell', maxChars: 15 },
  NLSEduCertColActions: { text: 'Actions', description: 'Column header Actions in certificates.', surface: 'shell', maxChars: 15 },
  NLSEduCertSourceAuto: { text: 'Automatic', description: 'Label for AUTO certificate source.', surface: 'shell', maxChars: 15 },
  NLSEduCertSourceManual: { text: 'Manual', description: 'Label for MANUAL certificate source.', surface: 'shell', maxChars: 15 },
  NLSEduCertStatusIssued: { text: 'Issued', description: 'Status pill label for ISSUED certificate.', surface: 'shell', maxChars: 15 },
  NLSEduCertStatusRevoked: { text: 'Revoked', description: 'Status pill label for REVOKED certificate.', surface: 'shell', maxChars: 15 },
  NLSEduCertStatusSuperseded: { text: 'Superseded', description: 'Status pill label for SUPERSEDED certificate.', surface: 'shell', maxChars: 15 },
  NLSEduCertStatusExpired: { text: 'Expired', description: 'Status pill label for expired certificate.', surface: 'shell', maxChars: 15 },
  NLSEduCertOpenPdfBtn: { text: 'Open PDF', description: 'Button label to open certificate PDF.', surface: 'shell', maxChars: 15 },
  NLSEduCertPdfPending: { text: 'Being prepared', description: 'Status text when PDF is in PENDING status.', surface: 'shell', maxChars: 20 },
  NLSEduCertPdfFailed: { text: 'PDF generation failed', description: 'Error text when PDF generation failed.', surface: 'shell', maxChars: 30 },
  NLSEduCertPdfUrlFailed: { text: 'Failed to retrieve certificate PDF URL.', description: 'Error fallback when getting certificate PDF URL fails.', surface: 'shell', maxChars: 50 },
  NLSEduCertIssueBtn: { text: 'Issue certificate', description: 'Button to trigger issue certificate dialog.', surface: 'shell', maxChars: 25 },
  NLSEduCertIssueDialogTitle: { text: 'Issue certificate', description: 'Title for issue certificate dialog.', surface: 'shell', maxChars: 25 },
  NLSEduCertTemplateLabel: { text: 'Certificate template', description: 'Label for certificate template selector.', surface: 'shell', maxChars: 25 },
  NLSEduCertSelectTemplatePlaceholder: { text: 'Select a template...', description: 'Placeholder for certificate template selector.', surface: 'shell', maxChars: 35 },
  NLSEduCertLevelOptionalLabel: { text: 'Associated level (optional)', description: 'Label for optional level selector when issuing certificate.', surface: 'shell', maxChars: 35 },
  NLSEduCertConfirmIssueBtn: { text: 'Issue', description: 'Submit button in issue certificate dialog.', surface: 'shell', maxChars: 15 },
  NLSEduCertIssueSuccess: { text: 'Certificate issued successfully.', description: 'Success notification banner after issuing certificate.', surface: 'shell', maxChars: 40 },
  NLSEduCertIssueFailed: { text: 'Failed to issue certificate.', description: 'Error fallback when issuing certificate fails.', surface: 'shell', maxChars: 35 },
  NLSEduCertRevokeBtn: { text: 'Revoke', description: 'Button to revoke certificate.', surface: 'shell', maxChars: 15 },
  NLSEduCertRevokeTitle: { text: 'Revoke certificate', description: 'Title for revoke confirmation modal.', surface: 'shell', maxChars: 25 },
  NLSEduCertRevokeBody: { text: 'Revoking a certificate is permanent and marks it invalid for public verification.', description: 'Explanation in revoke confirmation modal.', surface: 'shell', maxChars: 95 },
  NLSEduCertRevokeReasonLabel: { text: 'Reason for revocation', description: 'Label for revocation reason input.', surface: 'shell', maxChars: 30 },
  NLSEduCertRevokeReasonPlaceholder: { text: 'State the reason for revoking this certificate (required, 1-500 characters)...', description: 'Placeholder for revocation reason.', surface: 'shell', maxChars: 85 },
  NLSEduCertRevokeReasonRequired: { text: 'Revocation reason is required (1-500 characters).', description: 'Validation error when revocation reason is blank.', surface: 'shell', maxChars: 60 },
  NLSEduCertRevokeConfirm: { text: 'Revoke certificate', description: 'Confirm action label for revoke modal.', surface: 'shell', maxChars: 25 },
  NLSEduCertRevokeSuccess: { text: 'Certificate revoked.', description: 'Success message after certificate revocation.', surface: 'shell', maxChars: 25 },
  NLSEduCertRevokeFailed: { text: 'Failed to revoke certificate.', description: 'Error fallback when revoking certificate fails.', surface: 'shell', maxChars: 35 },
  NLSEduCertRenewBtn: { text: 'Renew', description: 'Button to renew certificate.', surface: 'shell', maxChars: 15 },
  NLSEduCertRenewSuccess: { text: 'Certificate renewed successfully.', description: 'Success message after certificate renewal.', surface: 'shell', maxChars: 40 },
  NLSEduCertRenewFailed: { text: 'Failed to renew certificate.', description: 'Error fallback when renewing certificate fails.', surface: 'shell', maxChars: 35 },
  NLSEduCertificatesEmpty: { text: 'No certificates issued for this trainee yet.', description: 'Empty state message for certificates table.', surface: 'shell', maxChars: 55 },
  NLSEduCertTemplateNotFoundError: { text: 'Certificate template not found.', description: 'Error when certificate template does not exist.', surface: 'shell', maxChars: 40 },
  NLSEduCertTemplateArchivedError: { text: 'Certificate template is archived and cannot be issued.', description: 'Error when trying to use an archived certificate template.', surface: 'shell', maxChars: 65 },
  NLSEduCertInvalidStatusError: { text: 'This certificate cannot be revoked because it is not active.', description: 'Error when trying to revoke a non-issued certificate.', surface: 'shell', maxChars: 65 },

  // ED11.b2: Practice Logs
  NLSEduPracticeLogsEmpty: { text: 'No practice logs recorded for this trainee yet.', description: 'Empty state message for practice logs.', surface: 'shell', maxChars: 55 },
  NLSEduPracticeLogsLoadMore: { text: 'Load more', description: 'Button to paginate practice logs.', surface: 'shell', maxChars: 20 },
  NLSEduPracticeLogsLoading: { text: 'Loading practice logs...', description: 'Loading text for practice logs.', surface: 'shell', maxChars: 30 },
  NLSEduPracticeLogDuration: { text: '{minutes} mins', description: 'Duration of practice log in minutes.', surface: 'shell', maxChars: 15 },
  NLSEduPracticeLogFocusChip: { text: 'FOCUS', description: 'Status chip label for Focus practice log.', surface: 'shell', maxChars: 10 },
  NLSEduPracticeLogAttachmentsLabel: { text: 'Attachments', description: 'Label for practice log attachments.', surface: 'shell', maxChars: 20 },
  NLSEduPracticeLogCommentsHeader: { text: 'Feedback & comments', description: 'Header for comments section on practice log.', surface: 'shell', maxChars: 25 },
  NLSEduPracticeLogNoComments: { text: 'No comments yet.', description: 'Message when practice log has no comments.', surface: 'shell', maxChars: 20 },
  NLSEduPracticeLogAddCommentPlaceholder: { text: 'Write feedback or comment...', description: 'Placeholder for adding practice log comment.', surface: 'shell', maxChars: 40 },
  NLSEduPracticeLogAddCommentBtn: { text: 'Add comment', description: 'Button to post comment on practice log.', surface: 'shell', maxChars: 20 },
  NLSEduPracticeLogCommentsCapReached: { text: 'Comment limit reached (50 comments maximum).', description: 'Note shown when 50 comments limit is reached on a practice log.', surface: 'shell', maxChars: 50 },
  NLSEduPracticeLogCommentLengthError: { text: 'Comment must be between 1 and 1000 characters.', description: 'Validation error when comment text length is invalid.', surface: 'shell', maxChars: 55 },
  NLSEduPracticeLogAddCommentFailed: { text: 'Failed to add comment.', description: 'Error fallback when adding comment fails.', surface: 'shell', maxChars: 30 },

  // ED11.b3: Public Verify Page (EQ175)
  NLSVerifyCertificateDocTitle: { text: 'Certificate Verification', description: 'Document title for public certificate verification page.', surface: 'common', maxChars: 30 },
  NLSVerifyCertificateHeading: { text: 'Certificate Verification', description: 'Main heading on certificate verify page.', surface: 'common', maxChars: 30 },
  NLSVerifyCertificateTitleLabel: { text: 'Certificate', description: 'Label for certificate title.', surface: 'common', maxChars: 15 },
  NLSVerifyCertificateHolderLabel: { text: 'Recipient', description: 'Label for certificate holder.', surface: 'common', maxChars: 15 },
  NLSVerifyCertificateProgrammeLabel: { text: 'Programme', description: 'Label for certificate programme.', surface: 'common', maxChars: 15 },
  NLSVerifyCertificateIssuedLabel: { text: 'Issued', description: 'Label for certificate issue date.', surface: 'common', maxChars: 15 },
  NLSVerifyCertificateExpiresLabel: { text: 'Expires', description: 'Label for certificate expiry date.', surface: 'common', maxChars: 15 },
  NLSVerifyCertificateWorkspaceLabel: { text: 'Organisation', description: 'Label for certificate issuing organisation.', surface: 'common', maxChars: 20 },
  NLSVerifyCertificateNoExpiry: { text: 'Does not expire', description: 'Text when certificate has no expiry date.', surface: 'common', maxChars: 20 },
  NLSVerifyCertificateStatusValid: { text: 'Valid Certificate', description: 'Status badge/heading for valid certificate.', surface: 'common', maxChars: 25 },
  NLSVerifyCertificateStatusExpired: { text: 'Certificate Expired', description: 'Status badge/heading for expired certificate.', surface: 'common', maxChars: 25 },
  NLSVerifyCertificateStatusRevoked: { text: 'Certificate Revoked', description: 'Status badge/heading for revoked certificate.', surface: 'common', maxChars: 25 },
  NLSVerifyCertificateStatusReplaced: { text: 'Certificate Replaced', description: 'Status badge/heading for replaced/superseded certificate.', surface: 'common', maxChars: 25 },
  NLSVerifyCertificateLoading: { text: 'Verifying certificate...', description: 'Loading indicator on verify page.', surface: 'common', maxChars: 30 },
  NLSVerifyCertificateNotFound: { text: 'No certificate with this code', description: 'Message when certificate code is not found or malformed.', surface: 'common', maxChars: 35 },
  NLSVerifyCertificateRateLimited: { text: 'Too many checks, try again in a minute', description: 'Rate limit error message on certificate verify page.', surface: 'common', maxChars: 45 },
  NLSVerifyCertificateGenericError: { text: 'Unable to verify certificate. Please try again later.', description: 'Generic error message on certificate verify page.', surface: 'common', maxChars: 60 },
  NLSVerifyCertificateCodeLabel: { text: 'Verification code: {code}', description: 'Display of verification code.', surface: 'common', maxChars: 40 },
  NLSVerifyCertificateDemoNotice: { text: 'Demo certificate: not a real award', description: 'Notice shown when verifying a certificate in demo mode.', surface: 'common', maxChars: 50 },

  // ED11.b3: Family Training Section (EQ176)
  NLSFamilyTrainingHeading: { text: 'Training', description: 'Section heading for Training in FamilyProgressTab.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingSelectProgramme: { text: 'Training programme', description: 'Aria label for programme picker in family progress.', surface: 'shell', maxChars: 25 },
  NLSFamilyReportCardsHeading: { text: 'Report cards', description: 'Heading for report cards section in FamilyProgressTab.', surface: 'shell', maxChars: 25 },
  NLSFamilyTrainingCurrentLevel: { text: 'Current level: {level}', description: 'Display current level in family progress.', surface: 'shell', maxChars: 35 },
  NLSFamilyTrainingLevelSince: { text: 'Since {date}', description: 'Display date since current level was achieved.', surface: 'shell', maxChars: 25 },
  NLSFamilyTrainingNoLevel: { text: 'No level attained yet', description: 'Label when learner has no current level.', surface: 'shell', maxChars: 30 },
  NLSFamilyTrainingNextLevelCriteria: { text: 'Next level criteria ({level})', description: 'Header for next level criteria checklist in family progress.', surface: 'shell', maxChars: 40 },
  NLSFamilyTrainingSelfLogBtn: { text: 'Record value', description: 'Button to trigger self-log input for a metric.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingSelfLogTitle: { text: 'Record {metric}', description: 'Dialog title for self-logging a metric value.', surface: 'shell', maxChars: 30 },
  NLSFamilyTrainingSelfLogDate: { text: 'Date', description: 'Label for self-log date input.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingSelfLogNote: { text: 'Note (optional)', description: 'Label for self-log note input.', surface: 'shell', maxChars: 25 },
  NLSFamilyTrainingSelfLogNotePlaceholder: { text: 'Add an optional note...', description: 'Placeholder for self-log note.', surface: 'shell', maxChars: 40 },
  NLSFamilyTrainingSelfLogValue: { text: 'Value', description: 'Label for self-log value input.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingSelfLogPassFail: { text: 'Completed / Passed', description: 'Label for self-log toggle.', surface: 'shell', maxChars: 25 },
  NLSFamilyTrainingSelfLogPendingMarker: { text: 'Pending confirmation', description: 'Marker showing self-reported value is awaiting trainer review.', surface: 'shell', maxChars: 25 },
  NLSFamilyTrainingSelfLogSuccess: { text: 'Value recorded and submitted for confirmation.', description: 'Success notification for self-log.', surface: 'shell', maxChars: 50 },
  NLSFamilyTrainingSelfLogFailed: { text: 'Failed to record value.', description: 'Error notification for self-log.', surface: 'shell', maxChars: 30 },
  NLSFamilyTrainingSelfLogSaveBtn: { text: 'Save', description: 'Save button in self-log modal.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingSelfLogCancelBtn: { text: 'Cancel', description: 'Cancel button in self-log modal.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingCertificatesHeading: { text: 'Certificates', description: 'Heading for family certificates section.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingNoCertificates: { text: 'No certificates issued for this programme yet.', description: 'Empty state for certificates.', surface: 'shell', maxChars: 50 },
  NLSFamilyTrainingVerifyLink: { text: 'Verify link: {url}', description: 'Verify link text.', surface: 'shell', maxChars: 50 },
  NLSFamilyTrainingCopyVerifyLink: { text: 'Copy link', description: 'Action button to copy verify link.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingLinkCopied: { text: 'Link copied', description: 'Notification after copying verify link.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingPracticeLogsHeading: { text: 'Practice logs', description: 'Heading for family practice logs section.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingNoPracticeLogs: { text: 'No practice logs recorded yet.', description: 'Empty state for family practice logs.', surface: 'shell', maxChars: 40 },
  NLSFamilyTrainingAddPracticeLogBtn: { text: 'Add practice log', description: 'Button to open add practice log dialog.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingAddPracticeLogTitle: { text: 'Add practice log', description: 'Title for add practice log dialog.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingEditPracticeLogTitle: { text: 'Edit practice log', description: 'Title for edit practice log dialog.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingPracticeDate: { text: 'Date', description: 'Label for practice date.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingPracticeMinutes: { text: 'Minutes (1–1440)', description: 'Label for practice minutes input.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingPractisedWhat: { text: 'What was practised', description: 'Label for what was practised input.', surface: 'shell', maxChars: 30 },
  NLSFamilyTrainingPractisedPlaceholder: { text: 'Describe what was practised (required)...', description: 'Placeholder for what was practised.', surface: 'shell', maxChars: 50 },
  NLSFamilyTrainingPracticeNotes: { text: 'Notes (optional)', description: 'Label for practice notes textarea.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingPracticeNotesPlaceholder: { text: 'Additional notes or reflections...', description: 'Placeholder for practice notes.', surface: 'shell', maxChars: 45 },
  NLSFamilyTrainingPracticeFiles: { text: 'Attachments (up to 5 photos, videos, audio)', description: 'Label for practice log attachments.', surface: 'shell', maxChars: 50 },
  NLSFamilyTrainingPracticeMetricValues: { text: 'Progress metrics (optional)', description: 'Section header for metric values in practice log.', surface: 'shell', maxChars: 35 },
  NLSFamilyTrainingPracticeSaveBtn: { text: 'Save practice log', description: 'Submit button for practice log.', surface: 'shell', maxChars: 20 },
  NLSFamilyTrainingPracticeEditBtn: { text: 'Edit', description: 'Button to edit practice log.', surface: 'shell', maxChars: 10 },
  NLSFamilyTrainingPracticeDeleteBtn: { text: 'Delete', description: 'Button to delete practice log.', surface: 'shell', maxChars: 10 },
  NLSFamilyTrainingPracticeDeleteTitle: { text: 'Delete practice log', description: 'Confirm title for deleting practice log.', surface: 'shell', maxChars: 25 },
  NLSFamilyTrainingPracticeDeleteBody: { text: 'Are you sure you want to delete this practice log? Associated self-reported metric values will be removed.', description: 'Confirm body for deleting practice log.', surface: 'shell', maxChars: 120 },
  NLSFamilyTrainingPracticeDeleteConfirm: { text: 'Delete log', description: 'Confirm action button label for practice log deletion.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingPracticeSaveSuccess: { text: 'Practice log saved successfully.', description: 'Success notification after saving practice log.', surface: 'shell', maxChars: 40 },
  NLSFamilyTrainingPracticeDeleteSuccess: { text: 'Practice log deleted.', description: 'Success notification after deleting practice log.', surface: 'shell', maxChars: 25 },
  NLSFamilyTrainingPracticeSaveFailed: { text: 'Failed to save practice log.', description: 'Error fallback when saving practice log fails.', surface: 'shell', maxChars: 35 },
  NLSFamilyTrainingPracticeDeleteFailed: { text: 'Failed to delete practice log.', description: 'Error fallback when deleting practice log fails.', surface: 'shell', maxChars: 35 },
  NLSFamilyTrainingPracticeMinutesError: { text: 'Minutes must be an integer between 1 and 1440.', description: 'Validation error for minutes.', surface: 'shell', maxChars: 50 },
  NLSFamilyTrainingPractisedRequired: { text: 'What was practised is required (1-500 characters).', description: 'Validation error when what was practised is empty.', surface: 'shell', maxChars: 55 },
  NLSFamilyTrainingPracticeDateFuture: { text: 'Date cannot be in the future.', description: 'Validation error for future date.', surface: 'shell', maxChars: 35 },
  NLSFamilyTrainingPracticeMaxFiles: { text: 'Maximum of 5 files allowed.', description: 'Validation error for exceeding 5 files.', surface: 'shell', maxChars: 35 },

  // ED11.b3.fix1: Criteria line and status pills
  NLSFamilyTrainingCriteriaMetricItem: { text: '{metric}: {actual}{unit} (Target: {target}{unit})', description: 'Criterion line for metric targets in family progress.', surface: 'shell', maxChars: 65 },
  NLSFamilyTrainingCriteriaMetricFallback: { text: 'Metric', description: 'Fallback label for metric criterion name.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingCriteriaMet: { text: 'Met', description: 'Status pill for met criterion in family training.', surface: 'shell', maxChars: 15 },
  NLSFamilyTrainingCriteriaInProgress: { text: 'In progress', description: 'Status pill for in-progress criterion in family training.', surface: 'shell', maxChars: 15 },

  // ED11.b3: Focus Link (EQ176)
  NLSFocusSaveToProgrammeLabel: { text: 'Save to programme', description: 'Label for optional programme selector in Focus session.', surface: 'shell', maxChars: 25 },
  NLSFocusSaveToProgrammeNone: { text: 'None', description: 'Option for not saving focus session to a training programme.', surface: 'shell', maxChars: 10 },
  NLSFocusSaveToProgrammeFailed: { text: 'Could not record practice log for this programme.', description: 'Non-blocking error notification when Focus practice log creation fails.', surface: 'shell', maxChars: 55 },
  NLSFocusSaveToProgrammeDefaultPractised: { text: 'Focus session', description: 'Default fallback text for what was practised in focus session.', surface: 'shell', maxChars: 25 },

  // ED14.b2b: Media consent & Portfolio
  NLSFamilyMediaConsentTitle: { text: 'Media consent', description: 'Section title for media consent in child settings', surface: 'shell', maxChars: 25 },
  NLSFamilyMediaConsentExplanation: { text: 'Choose how photos and videos of this child may be shared by school staff.', description: 'Explanation for media consent setting', surface: 'shell', maxChars: 85 },
  NLSFamilyMediaConsentPhotosOk: { text: 'Photos OK', description: 'Media consent option: Photos OK', surface: 'shell', maxChars: 20 },
  NLSFamilyMediaConsentPhotosOkDesc: { text: 'May appear and be tagged in class photos', description: 'Description for Photos OK consent level', surface: 'shell', maxChars: 50 },
  NLSFamilyMediaConsentClassOnly: { text: 'Class only', description: 'Media consent option: Class only', surface: 'shell', maxChars: 20 },
  NLSFamilyMediaConsentClassOnlyDesc: { text: 'Only in posts for their own class', description: 'Description for Class only consent level', surface: 'shell', maxChars: 45 },
  NLSFamilyMediaConsentNone: { text: 'No photos', description: 'Media consent option: No photos', surface: 'shell', maxChars: 20 },
  NLSFamilyMediaConsentNoneDesc: { text: 'Never tagged in photo or video posts', description: 'Description for No photos consent level', surface: 'shell', maxChars: 45 },
  NLSFamilyMediaConsentSetBy: { text: 'Last updated by {setBy} on {date}', description: 'Information showing who set media consent and when', surface: 'shell', maxChars: 65 },
  NLSFamilyMediaConsentSetOn: { text: 'Last updated on {date}', description: 'Information showing when media consent was set', surface: 'shell', maxChars: 40 },
  NLSFamilyMediaConsentMinorReadOnly: { text: 'Only a parent or guardian can change media consent.', description: 'Notice for minor account that media consent is read-only', surface: 'shell', maxChars: 60 },
  NLSFamilyMediaConsentYou: { text: 'You', description: 'Label referring to the current user as updater of media consent', surface: 'shell', maxChars: 10 },
  NLSFamilyMediaConsentPromptTitle: { text: 'Media consent for class photos', description: 'Dialog title for first-open media consent prompt', surface: 'shell', maxChars: 40 },
  NLSFamilyMediaConsentPromptBody: { text: 'Please choose how photos and videos of your child may be shared in class updates.', description: 'Dialog body for first-open media consent prompt', surface: 'shell', maxChars: 85 },
  NLSFamilyMediaConsentPromptDecideLater: { text: 'Decide later', description: 'Action button to defer media consent choice', surface: 'shell', maxChars: 20 },
  NLSFamilyMediaConsentPromptSave: { text: 'Save preference', description: 'Action button to save media consent choice', surface: 'shell', maxChars: 25 },
  NLSFamilyMediaConsentPromptSaving: { text: 'Saving...', description: 'Saving state label for media consent prompt button', surface: 'shell', maxChars: 15 },
  NLSFamilyPortfolioHeading: { text: 'Portfolio', description: 'Section title for learner portfolio in progress tab', surface: 'shell', maxChars: 25 },
  NLSFamilyPortfolioDownloadPdf: { text: 'Download PDF', description: 'Button label to download portfolio PDF', surface: 'shell', maxChars: 20 },
  NLSFamilyPortfolioDownloadingPdf: { text: 'Generating PDF...', description: 'State while downloading portfolio PDF', surface: 'shell', maxChars: 25 },
  NLSFamilyPortfolioViewCertificate: { text: 'View certificate', description: 'Button to view certificate PDF', surface: 'shell', maxChars: 25 },
  NLSFamilyPortfolioHideFromView: { text: 'Hide from my view', description: 'Action to hide portfolio item from viewer', surface: 'shell', maxChars: 25 },
  NLSFamilyPortfolioUnhide: { text: 'Unhide', description: 'Action to unhide portfolio item', surface: 'shell', maxChars: 15 },
  NLSFamilyPortfolioShowHidden: { text: 'Show hidden items', description: 'Checkbox label to toggle viewing hidden portfolio items', surface: 'shell', maxChars: 30 },
  NLSFamilyPortfolioHiddenBadge: { text: 'Hidden from your view', description: 'Badge showing item is hidden from viewer', surface: 'shell', maxChars: 30 },
  NLSFamilyPortfolioEmptyTitle: { text: 'No portfolio items yet', description: 'Empty state title for portfolio', surface: 'shell', maxChars: 30 },
  NLSFamilyPortfolioEmptyBody: { text: 'Items from class activities, completed homework, certificates, and practice logs will appear here.', description: 'Empty state body for portfolio', surface: 'shell', maxChars: 100 },
  NLSFamilyPortfolioLoadMore: { text: 'Load more', description: 'Button to load more portfolio items', surface: 'shell', maxChars: 20 },
  // PL3.c1.fix1: Plan limits refusals
  NLSBoardLimitGoalsReached: { text: 'This board has reached its {limit}-goal limit.', description: 'Error message when board goal limit is reached.', surface: 'manage', maxChars: 60 },
  NLSBoardLimitTasksReached: { text: 'This board has reached its {limit}-task limit.', description: 'Error message when board task limit is reached.', surface: 'manage', maxChars: 60 },
  NLSBoardLimitSubtasksReached: { text: 'This board has reached its {limit}-subtask limit.', description: 'Error message when board subtask limit is reached.', surface: 'manage', maxChars: 60 },
  NLSParentLimitTasksReached: { text: 'That goal has reached its {limit}-task limit.', description: 'Error message when goal child task limit is reached.', surface: 'manage', maxChars: 60 },
  NLSParentLimitSubtasksReached: { text: 'That task has reached its {limit}-subtask limit.', description: 'Error message when task child subtask limit is reached.', surface: 'manage', maxChars: 60 },
  NLSBoardLimitChecklistsReached: { text: 'This board has reached its {limit}-checklist limit.', description: 'Error message when board checklist limit is reached.', surface: 'manage', maxChars: 60 },
  NLSParentLimitChecklistsReached: { text: 'That container has reached its {limit}-checklist limit.', description: 'Error message when container child checklist limit is reached.', surface: 'manage', maxChars: 60 },
  NLSRestructureFailedSubtreeRoom: { text: 'Cannot move {subtreeSize} items: target only has room for {roomLeft}.', description: 'Error message when moving or restoring subtree exceeds remaining room.', surface: 'manage', maxChars: 80 },
  // TT5.a Account Transfer (A7)
  NLSAccountTransferCodeInvalid: {
    text: 'The transfer code is invalid.',
    description: 'Error shown when the account transfer code does not exist.',
    surface: 'auth',
    maxChars: 50,
  },
  NLSAccountTransferCodeExpired: {
    text: 'The transfer code has expired.',
    description: 'Error shown when the account transfer code is older than 15 minutes.',
    surface: 'auth',
    maxChars: 50,
  },
  NLSAccountTransferCodeAlreadyUsed: {
    text: 'This transfer code has already been used.',
    description: 'Error shown when the account transfer code was already claimed.',
    surface: 'auth',
    maxChars: 60,
  },
  NLSAccountTransferEmailConfirmationRequired: {
    text: 'Transfer of owned workspaces requires email confirmation.',
    description: 'Error shown when claiming a transfer of owned workspaces before confirming via email.',
    surface: 'auth',
    maxChars: 80,
  },
  NLSAccountTransferExceedsCap: {
    text: 'Account transfer cannot proceed because it would exceed the 20-workspace limit.',
    description: 'Error shown when claiming a transfer would cause the new account to exceed 20 non-owned workspaces.',
    surface: 'auth',
    maxChars: 100,
  },
  NLSAccountTransferSelfTransfer: {
    text: 'Cannot transfer account to the same user.',
    description: 'Error shown when trying to claim a transfer with the same account that generated the code.',
    surface: 'auth',
    maxChars: 60,
  },
  NLSAccountTransferAnonymousNotAllowed: {
    text: 'Anonymous accounts cannot create or claim an account transfer.',
    description: 'Error shown when an anonymous user attempts an account transfer.',
    surface: 'auth',
    maxChars: 80,
  },
  NLSAccountTransferEmailSubject: {
    text: 'Confirm your Timelining account transfer',
    description: 'Subject line for account transfer confirmation email.',
    surface: 'auth',
    maxChars: 60,
  },
  NLSAccountTransferEmailBody: {
    text: 'Please confirm your transfer code to move your account to a new Google login.',
    description: 'Body text for account transfer confirmation email.',
    surface: 'auth',
    maxChars: 120,
  },
  NLSAccountTransferEmailToken: {
    text: 'Confirmation token: {token}',
    description: 'Confirmation token in account transfer email.',
    surface: 'auth',
    maxChars: 60,
  },
  NLSAccountTransferEmailLink: {
    text: 'Confirm account transfer: {url}',
    description: 'Confirm link in account transfer email.',
    surface: 'auth',
    maxChars: 120,
  },
  NLSAccountTransferEmailExpires: {
    text: 'This token expires in 15 minutes.',
    description: 'Expiry note in account transfer email.',
    surface: 'auth',
    maxChars: 60,
  },
  NLSAccountTransferAlreadyClaimed: {
    text: 'This transfer code has already been claimed.',
    description: 'Error shown when transfer code is already claimed by another user.',
    surface: 'auth',
    maxChars: 60,
  },
  NLSAccountTransferInvalidConfirmToken: {
    text: 'Invalid confirmation token.',
    description: 'Error shown when confirmation token does not match.',
    surface: 'auth',
    maxChars: 50,
  },
  NLSAccountTransferEmailRequired: {
    text: 'Transfer of owned workspaces requires an email address on file to send the confirmation.',
    description: 'Error shown when owner has no email on file for transfer.',
    surface: 'auth',
    maxChars: 100,
  },
  NLSAccountTransferTokenRequired: {
    text: 'Confirmation token is required.',
    description: 'Error shown when confirmation token is empty.',
    surface: 'auth',
    maxChars: 50,
  },
  NLSAccountTransferGenericError: {
    text: 'Account transfer failed. Please try again.',
    description: 'Fallback error for account transfer failure.',
    surface: 'auth',
    maxChars: 80,
  },
  NLSAccountTransferMoveGoogleButton: {
    text: 'Move to another Google account',
    description: 'Button in profile panel to start account transfer.',
    surface: 'auth',
    maxChars: 40,
  },
  NLSAccountTransferHaveCodeButton: {
    text: 'Have a transfer code?',
    description: 'Button in profile panel and onboarding to claim a transfer code.',
    surface: 'auth',
    maxChars: 30,
  },
  NLSAccountTransferDialogTitle: {
    text: 'Move to another Google account',
    description: 'Title of move account modal dialog.',
    surface: 'auth',
    maxChars: 40,
  },
  NLSAccountTransferExplainMoves: {
    text: 'Your workspaces, roles, owner status, and access controls will move to your new Google account.',
    description: 'Explanation of what transfers during account transfer.',
    surface: 'auth',
    maxChars: 120,
  },
  NLSAccountTransferExplainStays: {
    text: 'Your personal wallet, XP, notifications, and username will remain on this account.',
    description: 'Explanation of what stays during account transfer (T5).',
    surface: 'auth',
    maxChars: 120,
  },
  NLSAccountTransferCodeHeading: {
    text: 'Your transfer code',
    description: 'Heading above the generated transfer code.',
    surface: 'auth',
    maxChars: 30,
  },
  NLSAccountTransferExpiresIn: {
    text: 'Expires in {time}',
    description: 'Countdown timer for transfer code.',
    surface: 'auth',
    maxChars: 30,
  },
  NLSAccountTransferOwnerEmailCheck: {
    text: 'Check your email to confirm this transfer.',
    description: 'Notice to workspace owners to verify via email.',
    surface: 'auth',
    maxChars: 60,
  },
  NLSAccountTransferPasteTokenPlaceholder: {
    text: 'Paste confirmation token from email',
    description: 'Placeholder for token input field.',
    surface: 'auth',
    maxChars: 50,
  },
  NLSAccountTransferConfirmTokenButton: {
    text: 'Confirm with token',
    description: 'Button to submit confirmation token.',
    surface: 'auth',
    maxChars: 30,
  },
  NLSAccountTransferConfirmedNotice: {
    text: 'Email confirmation completed. You can now claim the code on your new account.',
    description: 'Notice when owner confirms via email token.',
    surface: 'auth',
    maxChars: 100,
  },
  NLSAccountTransferClaimDialogTitle: {
    text: 'Claim account transfer',
    description: 'Title of dialog to enter transfer code.',
    surface: 'auth',
    maxChars: 35,
  },
  NLSAccountTransferCodePlaceholder: {
    text: 'Enter transfer code',
    description: 'Placeholder for transfer code input.',
    surface: 'auth',
    maxChars: 30,
  },
  NLSAccountTransferClaimButton: {
    text: 'Claim transfer',
    description: 'Button to claim transfer code.',
    surface: 'auth',
    maxChars: 25,
  },
  NLSAccountTransferSuccessMessage: {
    text: '{count} workspaces moved successfully.',
    description: 'Success message after claiming transfer.',
    surface: 'auth',
    maxChars: 60,
  },
  NLSAccountTransferConfirmPageTitle: {
    text: 'Confirm account transfer',
    description: 'Title of confirm transfer webpage.',
    surface: 'auth',
    maxChars: 40,
  },
  NLSAccountTransferConfirmButton: {
    text: 'Confirm transfer',
    description: 'Button on confirmation page to confirm transfer.',
    surface: 'auth',
    maxChars: 30,
  },
  NLSAccountTransferWrongAccountWarning: {
    text: 'You must be signed in to the account that initiated the transfer ({email}).',
    description: 'Warning on confirm page when signed in as different user.',
    surface: 'auth',
    maxChars: 100,
  },
  NLSAccountTransferConfirmSuccess: {
    text: 'Transfer confirmed! You can now claim the code on your new Google account.',
    description: 'Success text on confirm page.',
    surface: 'auth',
    maxChars: 100,
  },
  NLSWorkspaceNoAccessToast: {
    text: "You don't have access to that workspace anymore",
    description: 'Toast shown when attempting to open a target in a workspace the user is not a member of.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSWorkspaceNoLongerInWorkspace: {
    text: "You're no longer in {name}",
    description: 'Toast shown when attempting to navigate to an item in a workspace user left.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSWorkspaceSwitchedToast: {
    text: 'Switched to {name}',
    description: 'Toast notification when switching active workspace.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSWorkspaceSwitchGuardTitle: {
    text: 'Before you switch',
    description: 'Title of the workspace switch unsaved work guard sheet.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSWorkspaceSwitchGuardBody: {
    text: 'This will be lost or sent to the wrong place:',
    description: 'Body explaining unsaved work in the switch guard sheet.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSWorkspaceSwitchGuardSave: {
    text: 'Save and switch',
    description: 'Action to save unsaved work and proceed with workspace switch.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSWorkspaceSwitchGuardDiscard: {
    text: 'Discard and switch',
    description: 'Action to discard unsaved work and proceed with workspace switch.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSWorkspaceSwitchGuardStay: {
    text: 'Stay',
    description: 'Action to stay in the current workspace and cancel switch.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSWorkspaceSwitchDiscardedAfterLeave: {
    text: 'Discarded {items}',
    description: 'Toast notification showing work discarded after leaving workspace.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSWorkspaceSwitchCurtainOpening: {
    text: 'Opening {name}…',
    description: 'Status message on the switch curtain while loading target workspace.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSWorkspaceSwitchAiJobFinished: {
    text: 'Line finished in {workspaceName}',
    description: 'Toast notification shown when an AI generation completes in a background workspace.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSWorkspaceSwitchAiJobBusyInOtherWorkspace: {
    text: 'Line is busy in {workspaceName}',
    description: 'Notice shown in AI panel when an AI generation is currently running in another workspace.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSWorkspaceSwitchAiJobOpenAction: {
    text: 'Open',
    description: 'Action on toast to open workspace where AI generation finished.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSRoleChangedToast: {
    text: 'Your role in {name} is now {role}',
    description: 'Toast notification when user role changes in a workspace.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSWorkspaceAccessLostNotice: {
    text: 'You no longer have access to {name}',
    description: 'Notice shown in sheet when access to current workspace is lost or revoked.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSWorkspaceDeletedNotice: {
    text: '{name} was deleted',
    description: 'Notice shown in sheet when current workspace was deleted.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSWorkspaceAccessLostOk: {
    text: 'OK',
    description: 'Confirmation action button on access lost sheet.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSMutationAccessLost: {
    text: "You no longer have access to {name}. These can't be sent.",
    description: "Notice shown when queued helper mutations cannot be sent because access to the workspace was lost.",
    surface: 'helper',
    maxChars: 120,
  },
  NLSInviteJoinedToast: {
    text: 'You joined {name}',
    description: 'Toast notification when user joins a new workspace.',
    surface: 'settings',
    maxChars: 50,
  },
  NLSInviteJoinedOpenAction: {
    text: 'Open',
    description: 'Action button in toast to open the joined workspace.',
    surface: 'settings',
    maxChars: 16,
  },
  NLSMobileSyncDiscardAll: {
    text: 'Discard all',
    description: 'Action button to discard all unsendable mutations in mobile sync center.',
    surface: 'helper',
    maxChars: 20,
  },
  NLSHelperPendingScanGuardDescription: {
    text: 'Pending barcode scan ({barcode})',
    description: 'Description of pending barcode scan blocking workspace switch.',
    surface: 'helper',
    maxChars: 60,
  },
  NLSManageDraftsGuardDescription: {
    text: '{count} unsaved changes',
    description: 'Description of unsaved manage draft changes blocking workspace switch.',
    surface: 'member',
    maxChars: 40,
  },
  NLSManageDraftsGuardDescriptionSingle: {
    text: '1 unsaved change',
    description: 'Description when 1 unsaved manage draft change is blocking workspace switch.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberChecklistEditGuardDescription: {
    text: 'Unsaved edits to {title}',
    description: 'Description when unsaved checklist item edits block workspace switch.',
    surface: 'member',
    maxChars: 60,
  },
  NLSHelperFormGuardDescription: {
    text: 'Unsubmitted form in progress (submit from form only)',
    description: 'Description when an unsubmitted helper form blocks workspace switch.',
    surface: 'helper',
    maxChars: 80,
  },
  NLSWebFormFillGuardDescription: {
    text: 'Unsubmitted form answers in {title}',
    description: 'Description when unsubmitted web form answers block workspace switch.',
    surface: 'member',
    maxChars: 60,
  },
  NLSFocusSessionGuardDescription: {
    text: 'Active focus session in progress',
    description: 'Description when an active focus session blocks workspace switch.',
    surface: 'member',
    maxChars: 60,
  },
  NLSHelperSwitchGuardCountDraftsBodyWithCount: {
    text: 'Unsubmitted stock count drafts ({count})',
    description: 'Body with count for count draft guard sheet on workspace switch.',
    surface: 'helper',
    maxChars: 80,
  },
  NLSToastActionUndo: {
    text: 'Undo',
    description: 'Undo action label in notification toasts.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSNotificationRowAccount: {
    text: 'Account',
    description: 'Label on notification rows that have no workspace.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSNotificationActionOpenLink: {
    text: 'Open link',
    description: 'Notification action to open internal link.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenOverview: {
    text: 'Open overview',
    description: 'Notification action to open overview.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenAiHistory: {
    text: 'Open AI history',
    description: 'Notification action to open AI history.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenProfile: {
    text: 'Open profile',
    description: 'Notification action to open profile.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenCredits: {
    text: 'Open credits',
    description: 'Notification action to open credits ledger.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenStockItem: {
    text: 'Open stock item',
    description: 'Notification action to open stock item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenStockHub: {
    text: 'Open stock hub',
    description: 'Notification action to open stock hub.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenChatThread: {
    text: 'Open chat thread',
    description: 'Notification action to open chat thread.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenChecklistItem: {
    text: 'Open checklist item',
    description: 'Notification action to open checklist item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenBoardNode: {
    text: 'Open board node',
    description: 'Notification action to open board node.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenRaid: {
    text: 'Open raid',
    description: 'Notification action to open raid.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenAchievement: {
    text: 'Open achievement',
    description: 'Notification action to open achievement.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenIncomingInvites: {
    text: 'Open incoming invites',
    description: 'Notification action to open incoming invites.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenMemberSettings: {
    text: 'Open member settings',
    description: 'Notification action to open member settings.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenTeam: {
    text: 'Open Team',
    description: 'Notification action to open team page.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenReport: {
    text: 'Open report',
    description: 'Notification action to open report.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenForm: {
    text: 'Open form',
    description: 'Notification action to open form.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSNotificationActionOpenClients: {
    text: 'Open clients',
    description: 'Notification action to open clients page.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSLeaveWorkspaceConfirmTitle: {
    text: 'Leave {workspace}',
    description: 'Title of leave workspace confirmation dialog.',
    surface: 'settings',
    maxChars: 60,
  },
  NLSLeaveWorkspaceConfirmBody: {
    text: "Leave {workspace}? You'll lose access to its boards, pages and chat. Your past messages stay. To return you'll need a new invite or join code.",
    description: 'Body of leave workspace confirmation dialog (TM22).',
    surface: 'settings',
    maxChars: 180,
  },
  NLSLeaveWorkspaceSoleOwnerRefusal: {
    text: 'The sole workspace owner cannot leave the workspace. Hand over ownership first.',
    description: 'Refusal text when last owner tries to leave workspace.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSLeaveWorkspaceManageOwnersLink: {
    text: 'Manage owners',
    description: 'Link to Owners tab when sole owner cannot leave.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSLeaveWorkspaceGuardianRefusal: {
    text: 'Guardians cannot unlink themselves from a learner. Ask the school to unlink your guardian access.',
    description: 'Refusal text when guardian tries to leave workspace (GUARDIAN_ONLY).',
    surface: 'settings',
    maxChars: 120,
  },
  NLSLeaveWorkspaceAction: {
    text: 'Leave workspace',
    description: 'Button to confirm leaving workspace.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSLeaveWorkspaceFailedGeneric: {
    text: 'Failed to leave workspace.',
    description: 'Generic error message when leaving a workspace fails.',
    surface: 'settings',
    maxChars: 80,
  },
  NLSOnboardingHaveJoinCodeTitle: {
    text: 'I have a join code',
    description: 'First card title on choose onboarding stage.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSOnboardingHaveJoinCodeDescription: {
    text: 'Join an existing workspace using a code and PIN from your administrator.',
    description: 'Description for join code card on choose onboarding stage.',
    surface: 'onboarding',
    maxChars: 100,
  },
  NLSOnboardingActionEnterJoinCode: {
    text: 'Enter code',
    description: 'CTA button on join code onboarding card.',
    surface: 'onboarding',
    maxChars: 20,
  },
  NLSWorkspaceMenuCreatePersonal: {
    text: 'Create my personal space',
    description: 'Action in workspace menu to create personal space.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSMemberMenuFamilyView: {
    text: 'Family view',
    description: 'Drawer menu item to switch to family view in member app.',
    surface: 'member',
    maxChars: 30,
  },
  NLSMutationNoLongerMember: {
    text: "You're no longer in {name}; your change is kept on this device",
    description: 'Notice shown when an offline queued mutation fails because user left the workspace.',
    surface: 'helper',
    maxChars: 120,
  },
  NLSWorkspaceFallbackThisWorkspace: {
    text: 'this workspace',
    description: 'Fallback text when workspace name is unavailable.',
    surface: 'common',
    maxChars: 40,
  },

  // =========================================================================
  // ED15.b1: StageBoard, AdmissionsTab, Enquiry Detail, Public Enquiry Page
  // =========================================================================
  NLSStageBoardMoveTo: {
    text: 'Move to…',
    description: 'Action menu label on a card to move it to another stage.',
    surface: 'common',
    maxChars: 25,
  },
  NLSStageBoardLoadMore: {
    text: 'Load more',
    description: 'Button to load more cards in a stage column.',
    surface: 'common',
    maxChars: 20,
  },
  NLSStageBoardLoading: {
    text: 'Loading…',
    description: 'Loading indicator for stage cards.',
    surface: 'common',
    maxChars: 20,
  },
  NLSStageBoardEmpty: {
    text: 'No items in this stage',
    description: 'Empty state message for an empty stage column.',
    surface: 'common',
    maxChars: 35,
  },
  NLSEduAdmissionsEmptyStage: {
    text: 'No enquiries in this stage',
    description: 'Empty stage placeholder for admissions board.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEducationAdmissionsSubtitle: {
    text: 'Manage enquiries, track admission stages, and schedule follow-ups.',
    description: 'Subtitle for education admissions tab.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSStageBoardMoveError: {
    text: 'Failed to move item',
    description: 'Error message when an item could not be moved.',
    surface: 'common',
    maxChars: 40,
  },
  NLSStageBoardAnnouncePick: {
    text: 'Picked up item {id} from {stage}. Use arrow keys to navigate, Space or Enter to drop, Escape to cancel.',
    description: 'Screen reader announcement when picking up an item.',
    surface: 'common',
    maxChars: 120,
  },
  NLSStageBoardAnnounceOver: {
    text: 'Item {id} is over {stage}.',
    description: 'Screen reader announcement when dragging over a stage column.',
    surface: 'common',
    maxChars: 60,
  },
  NLSStageBoardAnnounceDrop: {
    text: 'Moved item {id} to {stage}.',
    description: 'Screen reader announcement when an item is dropped into a stage.',
    surface: 'common',
    maxChars: 60,
  },
  NLSStageBoardAnnounceCancel: {
    text: 'Movement cancelled. Item {id} returned to {stage}.',
    description: 'Screen reader announcement when drag is cancelled.',
    surface: 'common',
    maxChars: 80,
  },

  // AdmissionsTab & Filters
  NLSEduAdmissionsFilterOwnerAll: {
    text: 'All staff',
    description: 'Owner filter option to show enquiries owned by anyone.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsFilterOwnerMe: {
    text: 'Assigned to me',
    description: 'Owner filter option to show enquiries owned by current user.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsFilterOwnerUnassigned: {
    text: 'Unassigned',
    description: 'Owner filter option to show unassigned enquiries.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsFilterOwnerLabel: {
    text: 'Owner',
    description: 'Label for owner filter.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsFilterSourceAll: {
    text: 'All sources',
    description: 'Source filter option to show enquiries from any source.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsFilterSourceLabel: {
    text: 'Source',
    description: 'Label for source filter.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsFilterFollowUpDue: {
    text: 'Follow-up due',
    description: 'Filter toggle to show enquiries with follow-up due.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsSearchPlaceholder: {
    text: 'Search by name...',
    description: 'Placeholder for admissions search input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsSearchHint: {
    text: 'Search matches the first word of contact or learner name.',
    description: 'Hint explaining search prefix matching behavior.',
    surface: 'shell',
    maxChars: 80,
  },

  // Stages
  NLSEduAdmissionsStageNew: {
    text: 'New',
    description: 'Stage label for new enquiries.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsStageContacted: {
    text: 'Contacted',
    description: 'Stage label for contacted enquiries.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsStageTrialBooked: {
    text: 'Trial Booked',
    description: 'Stage label for trial booked enquiries.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsStageTrialDone: {
    text: 'Trial Done',
    description: 'Stage label for trial completed enquiries.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsStageAdmitted: {
    text: 'Admitted',
    description: 'Stage label for admitted enquiries.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsStageLost: {
    text: 'Lost',
    description: 'Stage label for lost enquiries.',
    surface: 'shell',
    maxChars: 15,
  },

  // Sources
  NLSEduAdmissionsSourceForm: {
    text: 'Form',
    description: 'Source chip label for web form enquiries.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsSourceWalkIn: {
    text: 'Walk-in',
    description: 'Source chip label for walk-in enquiries.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsSourcePhone: {
    text: 'Phone',
    description: 'Source chip label for phone enquiries.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsSourceReferral: {
    text: 'Referral',
    description: 'Source chip label for referral enquiries.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsSourceOther: {
    text: 'Other',
    description: 'Source chip label for other enquiry sources.',
    surface: 'shell',
    maxChars: 15,
  },

  // Lost Reasons
  NLSEduAdmissionsLostReasonPrice: {
    text: 'Price too high',
    description: 'Reason for lost enquiry due to fees/price.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsLostReasonTiming: {
    text: 'Timing conflict',
    description: 'Reason for lost enquiry due to schedule/timing.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsLostReasonDistance: {
    text: 'Distance / location',
    description: 'Reason for lost enquiry due to location.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsLostReasonChoseOther: {
    text: 'Chose another school',
    description: 'Reason for lost enquiry when enquirer chose another provider.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduAdmissionsLostReasonNoResponse: {
    text: 'No response',
    description: 'Reason for lost enquiry when enquirer stopped responding.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsLostReasonOther: {
    text: 'Other reason',
    description: 'Generic other reason for lost enquiry.',
    surface: 'shell',
    maxChars: 30,
  },

  // Move refusal reasons
  NLSEduAdmissionsReasonUseBookTrial: {
    text: 'Use Book trial',
    description: 'Disabled move reason explaining that trial booking must be used.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsReasonUseAdmit: {
    text: 'Use Admit',
    description: 'Disabled move reason explaining that admit action must be used.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsReasonCannotMoveAdmitted: {
    text: 'Admitted enquiries cannot be moved',
    description: 'Disabled move reason for admitted enquiries.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEduAdmissionsReasonCannotMoveClosed: {
    text: 'Enquiry is closed',
    description: 'Disabled move reason for closed enquiries.',
    surface: 'shell',
    maxChars: 30,
  },

  // Lost Dialog
  NLSEduAdmissionsLostDialogTitle: {
    text: 'Mark enquiry as lost',
    description: 'Dialog title when marking an enquiry as lost.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduAdmissionsLostReasonSelect: {
    text: 'Select reason',
    description: 'Label for selecting lost reason.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsLostNotePlaceholder: {
    text: 'Optional note explaining why this enquiry was lost...',
    description: 'Placeholder for lost enquiry note.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduAdmissionsLostConfirmBtn: {
    text: 'Mark as Lost',
    description: 'Confirm action to mark enquiry as lost.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsLostCancelBtn: {
    text: 'Cancel',
    description: 'Cancel action in lost enquiry dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsReopenBtn: {
    text: 'Reopen enquiry',
    description: 'Button to reopen a lost enquiry back to new.',
    surface: 'shell',
    maxChars: 25,
  },

  // Card Badges
  NLSEduAdmissionsBadgeFollowUpDue: {
    text: 'Due',
    description: 'Badge for enquiry follow-up due today.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSEduAdmissionsBadgeFollowUpOverdue: {
    text: 'Overdue',
    description: 'Badge for overdue enquiry follow-up.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsAgeYears: {
    text: '{age} yrs',
    description: 'Age label in years.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsAgeMonths: {
    text: '{age} mos',
    description: 'Age label in months.',
    surface: 'shell',
    maxChars: 15,
  },

  // New enquiry dialog
  NLSEduAdmissionsNewEnquiryBtn: {
    text: 'New enquiry',
    description: 'Button to open new enquiry dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsNewEnquiryTitle: {
    text: 'Record new enquiry',
    description: 'Dialog title for recording a new staff enquiry.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduAdmissionsContactName: {
    text: 'Contact name',
    description: 'Label for contact person name input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsContactPhone: {
    text: 'Contact phone',
    description: 'Label for contact phone input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsContactEmail: {
    text: 'Contact email',
    description: 'Label for contact email input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsContactRelation: {
    text: 'Relation to learner',
    description: 'Label for relation to learner input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsLearnerName: {
    text: 'Learner name',
    description: 'Label for learner name input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsLearnerDob: {
    text: 'Date of birth',
    description: 'Label for learner date of birth input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsLearnerIsMinor: {
    text: 'Learner is a minor',
    description: 'Checkbox label indicating learner is a minor.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsInterestNode: {
    text: 'Class / Programme of interest',
    description: 'Label for class or programme selector in enquiry.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAdmissionsInterestNone: {
    text: 'General enquiry (no class selected)',
    description: 'Option for general enquiry without a specific class.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSEduAdmissionsOwner: {
    text: 'Assigned staff',
    description: 'Label for assigned staff owner select.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsFollowUpDate: {
    text: 'Follow-up date & time',
    description: 'Label for follow up date and time input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsDuplicateWarningTitle: {
    text: 'Possible duplicates found',
    description: 'Header warning when duplicates are found for phone or email.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAdmissionsDuplicateEnquiry: {
    text: 'Existing enquiry: {name} ({stage})',
    description: 'Warning row for duplicate enquiry.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEduAdmissionsDuplicateLearner: {
    text: 'Existing learner: {name}',
    description: 'Warning row for duplicate enrolled learner.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSEduAdmissionsOpenDuplicateBtn: {
    text: 'Open',
    description: 'Link button to open a duplicate enquiry or learner.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduAdmissionsSaveEnquiryBtn: {
    text: 'Save enquiry',
    description: 'Primary button to save new enquiry.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsSaving: {
    text: 'Saving…',
    description: 'Loading label while saving enquiry.',
    surface: 'shell',
    maxChars: 15,
  },

  // Enquiry Detail Panel
  NLSEduEnquiryDetailTitle: {
    text: 'Enquiry Details',
    description: 'Title for enquiry details sheet or panel.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEnquiryDetailClose: {
    text: 'Close',
    description: 'Button to close enquiry details panel.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduEnquiryDetailSave: {
    text: 'Save changes',
    description: 'Button to save edits to enquiry details.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEnquiryDetailEdit: {
    text: 'Edit',
    description: 'Button to enter edit mode for enquiry details.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduEnquiryDetailCancel: {
    text: 'Cancel',
    description: 'Button to cancel editing enquiry details.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduEnquiryDetailAssign: {
    text: 'Assign owner',
    description: 'Button or label to reassign enquiry owner.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEnquiryDetailSetFollowUp: {
    text: 'Set follow-up',
    description: 'Button or label to set follow-up date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEnquiryDetailAddNote: {
    text: 'Add note',
    description: 'Button to add note to enquiry history.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEnquiryDetailNotePlaceholder: {
    text: 'Write a note about this enquiry (up to 2,000 characters)...',
    description: 'Placeholder for adding a note in enquiry detail.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEnquiryDetailNoteCounter: {
    text: '{count} / 2000',
    description: 'Character counter for enquiry note.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEnquiryDetailHistory: {
    text: 'Activity history',
    description: 'Heading for enquiry timeline event history.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEnquiryDetailHistoryEmpty: {
    text: 'No events recorded yet.',
    description: 'Empty state message when enquiry has no events.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduEnquiryDetailConsentNotice: {
    text: 'Consent accepted on {date} (notice {version})',
    description: 'Consent audit line in enquiry detail for form submissions.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSEduEnquiryDetailOpenLearner: {
    text: 'View learner profile',
    description: 'Link button to open admitted learner profile.',
    surface: 'shell',
    maxChars: 30,
  },

  // Events
  NLSEduEnquiryEventCreated: {
    text: 'Enquiry created ({source})',
    description: 'Event history label when enquiry was created.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduEnquiryEventStage: {
    text: 'Stage changed to {stage}',
    description: 'Event history label when enquiry stage was changed.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduEnquiryEventAssign: {
    text: 'Assigned to {owner}',
    description: 'Event history label when enquiry owner was assigned.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduEnquiryEventFollowUp: {
    text: 'Follow-up scheduled for {date}',
    description: 'Event history label when follow-up was scheduled.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSEduEnquiryEventNote: {
    text: 'Note added',
    description: 'Event history label for added note.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEnquiryEventEdit: {
    text: 'Details updated',
    description: 'Event history label when enquiry details were edited.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEnquiryEventTrial: {
    text: 'Trial activity',
    description: 'Event history label for trial event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEnquiryEventTrialAttendance: {
    text: 'Trial attendance recorded',
    description: 'Event history label when trial attendance was marked.',
    surface: 'shell',
    maxChars: 40,
  },

  // Public enquiry page (/enquire/<token>)
  NLSEduEnquiryPageTitle: {
    text: 'Enquiry Form',
    description: 'Title for public school enquiry form page.',
    surface: 'common',
    maxChars: 25,
  },
  NLSEduEnquiryPageInterestsTitle: {
    text: 'Select area of interest',
    description: 'Section title for choosing program of interest.',
    surface: 'common',
    maxChars: 35,
  },
  NLSEduEnquiryPageContactHeader: {
    text: 'Your contact information',
    description: 'Section header for parent/enquirer contact information.',
    surface: 'common',
    maxChars: 35,
  },
  NLSEduEnquiryPageLearnerHeader: {
    text: 'Learner information',
    description: 'Section header for prospective learner information.',
    surface: 'common',
    maxChars: 35,
  },
  NLSEduEnquiryPageConsentHeader: {
    text: 'Privacy & Consent',
    description: 'Section header for consent and privacy notice.',
    surface: 'common',
    maxChars: 30,
  },
  NLSEduEnquiryPagePrivacyNotice: {
    text: 'We retain your enquiry details for {months} months to process your application and contact you about admissions.',
    description: 'Privacy notice explaining data retention period.',
    surface: 'common',
    maxChars: 120,
  },
  NLSEduEnquiryPageConsentCheck: {
    text: 'I consent to the collection and processing of my information for admissions purposes as stated in the privacy notice (notice {version}).',
    description: 'Consent agreement checkbox label on public enquiry form.',
    surface: 'common',
    maxChars: 150,
  },
  NLSEduEnquiryPageSubmitBtn: {
    text: 'Submit enquiry',
    description: 'Submit button on public enquiry page.',
    surface: 'common',
    maxChars: 25,
  },
  NLSEduEnquiryPageSubmitting: {
    text: 'Submitting enquiry...',
    description: 'Button state while public enquiry is being submitted.',
    surface: 'common',
    maxChars: 30,
  },
  NLSEduEnquiryPageSuccessTitle: {
    text: 'Thank you for your enquiry',
    description: 'Success screen heading after enquiry form submission.',
    surface: 'common',
    maxChars: 40,
  },
  NLSEduEnquiryPageSuccessBody: {
    text: 'We have received your enquiry and our team will get in touch with you shortly.',
    description: 'Success message after enquiry form submission.',
    surface: 'common',
    maxChars: 90,
  },
  NLSEduEnquiryPageRateLimited: {
    text: 'Too many tries, please wait a few minutes',
    description: 'Rate limit error message on public enquiry form.',
    surface: 'common',
    maxChars: 50,
  },
  NLSEduEnquiryPageInactive: {
    text: 'This enquiry form is currently inactive or expired.',
    description: 'Notice when public enquiry link is inactive.',
    surface: 'common',
    maxChars: 65,
  },
  NLSEduEnquiryPageNotFound: {
    text: 'Enquiry link not found.',
    description: 'Error when enquiry link does not exist.',
    surface: 'common',
    maxChars: 35,
  },
  NLSEduEnquiryPageContactRequired: {
    text: 'Please provide at least a phone number or email address.',
    description: 'Validation error when neither phone nor email is supplied.',
    surface: 'common',
    maxChars: 65,
  },
  NLSEduEnquiryPageNameRequired: {
    text: 'Name is required.',
    description: 'Validation error when name is empty.',
    surface: 'common',
    maxChars: 25,
  },

  // ED15.b2 - Book Trial Flow
  NLSEduAdmissionsBookTrialTitle: {
    text: 'Book Trial Session',
    description: 'Dialog title for booking a trial session.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsBookTrialSubmit: {
    text: 'Confirm Trial Booking',
    description: 'Submit button for booking a trial session.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsBookTrialSelectSeries: {
    text: 'Select class or program session',
    description: 'Label for selecting class series to book trial.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAdmissionsBookTrialSelectDate: {
    text: 'Select session date',
    description: 'Label for selecting session date for trial.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsBookTrialSeatsLeft: {
    text: '{count} seats left',
    description: 'Seats remaining label for bookable trial session.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsBookTrialFull: {
    text: 'Full (Waitlist)',
    description: 'Label when bookable session has no open seats.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsBookTrialAllowRepeat: {
    text: 'Book another trial (Allow repeat)',
    description: 'Admin toggle or button to allow repeat trial booking.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSEduAdmissionsActionBookTrial: {
    text: 'Book trial',
    description: 'Action button to open book trial dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsActionAdmit: {
    text: 'Admit',
    description: 'Action button to open admit enquiry dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsActionBookAnotherTrial: {
    text: 'Book another trial',
    description: 'Action button to book another trial for enquiry.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsViewAdmittedLearner: {
    text: 'View admitted learner',
    description: 'Link button to open admitted learner page.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsCancelTrialBtn: {
    text: 'Cancel trial',
    description: 'Button to cancel trial booking.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsCancelTrialConfirmTitle: {
    text: 'Cancel Trial Booking',
    description: 'Confirmation dialog title for cancelling trial.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsCancelTrialConfirmBody: {
    text: 'Are you sure you want to cancel this trial booking?',
    description: 'Confirmation dialog text for cancelling trial.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSEduAdmissionsCancelTrialConfirmBtn: {
    text: 'Cancel booking',
    description: 'Confirm button to cancel trial.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsReasonTrialDoneServerOnly: {
    text: 'Trial done is marked automatically when attendance is taken',
    description: 'Reason why moving to trial done is disabled on the board.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSEduAdmissionsDetailTrialSection: {
    text: 'Trial Details',
    description: 'Section header for trial booking info on enquiry detail.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsDetailTrialStatus: {
    text: 'Trial status: {status}',
    description: 'Status label of trial booking.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduAdmissionsDetailTrialSession: {
    text: 'Session: {session} on {date}',
    description: 'Trial session and date description.',
    surface: 'shell',
    maxChars: 60,
  },

  // ED15.b2 - Admit Flow
  NLSEduAdmissionsAdmitTitle: {
    text: 'Admit Enquiry',
    description: 'Dialog title for admitting an enquirer.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsAdmitSubmit: {
    text: 'Confirm Admission',
    description: 'Submit button for admitting an enquirer.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsAdmitTargetClass: {
    text: 'Target class / group',
    description: 'Label for target class selection on admission.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsAdmitGuardianEmail: {
    text: 'Guardian email',
    description: 'Input label for guardian email during admission.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsAdmitGuardianPhone: {
    text: 'Guardian phone',
    description: 'Input label for guardian phone during admission.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsAdmitFeePlan: {
    text: 'Fee plan (optional)',
    description: 'Input label for optional fee plan on admission.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsAdmitNoFeePlan: {
    text: 'None (No fee plan)',
    description: 'Option for not assigning a fee plan on admission.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsAdmitCredentialsTitle: {
    text: 'Guardian Join Credentials',
    description: 'Header for join credentials slip shown upon admission.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduAdmissionsAdmitCredentialsWarning: {
    text: 'Warning: This join code and PIN are shown only once. Please write them down or print this slip now. They cannot be retrieved again.',
    description: 'Warning that join credentials are only displayed once.',
    surface: 'shell',
    maxChars: 150,
  },
  NLSEduAdmissionsAdmitPrintBtn: {
    text: 'Print Slip',
    description: 'Button to print guardian credentials slip.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsAdmitCodeAlreadyIssued: {
    text: 'A guardian join code has already been issued for this learner.',
    description: 'Notice when join code was already previously issued.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSEduAdmissionsAdmitIssueNewCodeBtn: {
    text: 'Issue a new code',
    description: 'Button to issue a fresh guardian join code.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduAdmissionsAdmitDoneBtn: {
    text: 'Done',
    description: 'Done button to close admission credentials slip.',
    surface: 'shell',
    maxChars: 15,
  },

  // ED15.b2 - Admissions Settings
  NLSEduAdmissionsSettingsSectionTitle: {
    text: 'Admissions & Public Form',
    description: 'Section header for admissions settings.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduAdmissionsSettingsSectionDesc: {
    text: 'Manage public enquiry links, form options, data retention, and email notifications.',
    description: 'Section description for admissions settings.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSEduAdmissionsSettingsPublicLink: {
    text: 'Public Enquiry Link',
    description: 'Sub-heading for public enquiry link in settings.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsSettingsCreateLinkBtn: {
    text: 'Generate enquiry link',
    description: 'Button to create a public enquiry link.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsSettingsRotateLinkBtn: {
    text: 'Rotate link',
    description: 'Button to rotate the public enquiry link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsSettingsRotateConfirmTitle: {
    text: 'Rotate Enquiry Link',
    description: 'Confirm dialog title for rotating enquiry link.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsSettingsRotateConfirmBody: {
    text: 'Rotating this link will immediately invalidate the current public link and QR code. Existing submissions remain unaffected.',
    description: 'Confirm dialog body for rotating enquiry link.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSEduAdmissionsSettingsLinkActiveLabel: {
    text: 'Public enquiry form active',
    description: 'Toggle label to enable or disable the public enquiry link.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAdmissionsSettingsLinkCopied: {
    text: 'Link copied to clipboard',
    description: 'Status label when enquiry link is copied.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsSettingsCopyLink: {
    text: 'Copy link',
    description: 'Button label to copy enquiry link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsSettingsDownloadQrPng: {
    text: 'Download PNG',
    description: 'Button to download enquiry QR code as PNG.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsSettingsDownloadQrSvg: {
    text: 'Download SVG',
    description: 'Button to download enquiry QR code as SVG.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsSettingsLinkExistsRotateHint: {
    text: 'Link exists; rotate to get a new one',
    description: 'Hint explaining that raw token cannot be shown again after initial creation.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEduAdmissionsSettingsInterestsLabel: {
    text: 'Public form programs / classes',
    description: 'Label for selecting which classes or programs appear on public form.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSEduAdmissionsSettingsExtraFieldsLabel: {
    text: 'Extra questions form (optional)',
    description: 'Label for picking an active form definition for extra questions.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAdmissionsSettingsExtraFieldsNone: {
    text: 'None (No extra questions)',
    description: 'Option for no extra form questions.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduAdmissionsSettingsRetentionLabel: {
    text: 'Data retention (months, 3–24)',
    description: 'Label for data retention duration input in admissions settings.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAdmissionsSettingsAckEmailLabel: {
    text: 'Send acknowledgement email on enquiry submission',
    description: 'Toggle label for enquirer acknowledgement email.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduAdmissionsSettingsTrialEmailLabel: {
    text: 'Send confirmation email on trial booking',
    description: 'Toggle label for trial booking confirmation email.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSEduAdmissionsSettingsOutboundEmailDisabledNote: {
    text: 'Outbound email is currently disabled for this workspace. Automatic enquiry emails will not be sent until outbound email is enabled.',
    description: 'Notice displayed when workspace outbound email is off.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSEduAdmissionsSettingsSaveBtn: {
    text: 'Save admissions settings',
    description: 'Button to save admissions settings.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduAdmissionsSettingsSaving: {
    text: 'Saving settings…',
    description: 'Saving indicator for admissions settings.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduAdmissionsSettingsSuccess: {
    text: 'Admissions settings saved successfully.',
    description: 'Success notice when admissions settings are saved.',
    surface: 'shell',
    maxChars: 45,
  },

  // ED15.b2 - Server Error Mappings
  NLSEduAdmissionsErrorAlreadyHadTrial: {
    text: 'A trial booking already exists for this enquiry. Repeat trial requires admin permission.',
    description: 'Error when enquiry already had a trial booking.',
    surface: 'shell',
    maxChars: 95,
  },
  NLSEduAdmissionsErrorCapReached: {
    text: 'This session is at full capacity.',
    description: 'Error when session capacity is reached.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduAdmissionsErrorFeePlanWrongRoot: {
    text: 'The selected fee plan does not match the program of the chosen class.',
    description: 'Error when fee plan root does not match target class root.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSEduAdmissionsErrorFeePlanMismatch: {
    text: 'The selected fee plan type does not match the class module type.',
    description: 'Error when fee plan kind does not match target module.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSEduAdmissionsErrorConsentAttestationRequired: {
    text: 'Consent attestation is required for minor learners.',
    description: 'Error when minor learner admission is attempted without attestation.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSEduAdmissionsErrorEnquiryAlreadyAdmitted: {
    text: 'This enquiry has already been admitted.',
    description: 'Error when trying to admit an already admitted enquiry.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSEduAdmissionsErrorEnquiryClosed: {
    text: 'Cannot perform this action on a closed enquiry.',
    description: 'Error when attempting trial or admission on a closed enquiry.',
    surface: 'shell',
    maxChars: 55,
  },

  // ED16.b1: School Events & Staff Views
  NLSEduEventTabTitle: {
    text: 'Events',
    description: 'Header title for events section in timetable tab.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventNewButton: {
    text: 'New event',
    description: 'Button to create a new school event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventNoEvents: {
    text: 'No upcoming events found.',
    description: 'Empty state message when no events are scheduled.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEduEventUpcomingTitle: {
    text: 'Upcoming Events',
    description: 'Section heading for upcoming events.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventFilterAll: {
    text: 'All audiences',
    description: 'Filter option to show events for all audiences.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventFilterModuleAll: {
    text: 'All modules',
    description: 'Filter option to show events across modules.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventFilterTeaching: {
    text: 'Teaching',
    description: 'Filter option for teaching module events.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventFilterTraining: {
    text: 'Training',
    description: 'Filter option for training module events.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventStatusActive: {
    text: 'Active',
    description: 'Status label for active events.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventStatusCancelled: {
    text: 'Cancelled',
    description: 'Status label for cancelled events.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventAllDayBadge: {
    text: 'All day',
    description: 'Badge for all-day events.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventLoadMore: {
    text: 'Load more',
    description: 'Button to load more paged events.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventRsvpsCount: {
    text: '{going} going • {maybe} maybe • {notGoing} not going',
    description: 'Summary line for event RSVP counts.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventEditorCreateTitle: {
    text: 'New School Event',
    description: 'Dialog title when creating a new school event.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduEventEditorEditTitle: {
    text: 'Edit School Event',
    description: 'Dialog title when editing a school event.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduEventAudienceLabel: {
    text: 'Audience',
    description: 'Label for audience selector in event editor.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventAudienceAll: {
    text: 'All Classes (School-wide)',
    description: 'Audience option for all classes in event editor.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduEventAudienceRoot: {
    text: 'Year / Programme',
    description: 'Audience option for Year or Programme in event editor.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduEventAudienceNodes: {
    text: 'Specific Classes / Groups',
    description: 'Audience option for specific classes or groups.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduEventTitleLabel: {
    text: 'Title',
    description: 'Label for event title input field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventTitlePlaceholder: {
    text: 'e.g. Sports Day, Science Fair',
    description: 'Placeholder for event title field.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventDescriptionLabel: {
    text: 'Description',
    description: 'Label for event description input field.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventDescriptionPlaceholder: {
    text: 'Event details, schedule or instructions…',
    description: 'Placeholder for event description field.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventLocationLabel: {
    text: 'Place / Venue',
    description: 'Label for event physical location.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventLocationPlaceholder: {
    text: 'e.g. Main Auditorium, Sports Ground',
    description: 'Placeholder for event location.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventOnlineUrlLabel: {
    text: 'Online link',
    description: 'Label for online meeting URL.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduEventOnlineUrlPlaceholder: {
    text: 'https://…',
    description: 'Placeholder for online meeting URL.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventAllDayLabel: {
    text: 'All-day event',
    description: 'Toggle switch label for all-day events.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventStartDateLabel: {
    text: 'Start date',
    description: 'Label for event start date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventEndDateLabel: {
    text: 'End date',
    description: 'Label for event end date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventStartTimeLabel: {
    text: 'Start time',
    description: 'Label for event start time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventEndTimeLabel: {
    text: 'End time',
    description: 'Label for event end time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventRsvpSwitchLabel: {
    text: 'Request RSVPs',
    description: 'Toggle switch label to enable RSVPs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventRsvpDeadlineLabel: {
    text: 'RSVP deadline',
    description: 'Label for RSVP response deadline.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventRemindersLabel: {
    text: 'Reminders',
    description: 'Label for event reminder options.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventReminder1Week: {
    text: '1 week before',
    description: 'Option for reminder 1 week before event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventReminder1Day: {
    text: '1 day before',
    description: 'Option for reminder 1 day before event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventReminder1Hour: {
    text: '1 hour before',
    description: 'Option for reminder 1 hour before event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventReminder15Min: {
    text: '15 minutes before',
    description: 'Option for reminder 15 minutes before event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventAttachmentsLabel: {
    text: 'Attachments (images, videos, PDFs up to 5)',
    description: 'Label for event attachments picker.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSEduEventSaveButton: {
    text: 'Save event',
    description: 'Button to save changes to existing event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventCreateButton: {
    text: 'Create event',
    description: 'Button to create a new event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventCancelButton: {
    text: 'Cancel',
    description: 'Button to dismiss or cancel form.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventUploadingAttachments: {
    text: 'Uploading attachments…',
    description: 'Status message while uploading event files.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEduEventValidationErrorEndBeforeStart: {
    text: 'End date/time must be after or equal to start.',
    description: 'Validation error when event ends before it begins.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventValidationErrorTitleRequired: {
    text: 'Title is required.',
    description: 'Validation error when event title is missing.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduEventValidationErrorAudienceRequired: {
    text: 'Please select an audience.',
    description: 'Validation error when audience target is missing.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEduEventValidationErrorTooLong: {
    text: 'Events cannot exceed 31 days in duration.',
    description: 'Validation error when event duration exceeds 31 days.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventValidationErrorRsvpDeadline: {
    text: 'RSVP deadline must be on or before event start.',
    description: 'Validation error when RSVP deadline is after event start.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventValidationErrorHttpsUrl: {
    text: 'Online link must start with https://',
    description: 'Validation error when URL does not use https.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventDetailTitle: {
    text: 'Event Details',
    description: 'Dialog title for event detail dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventDetailEdit: {
    text: 'Edit',
    description: 'Action button to edit event.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventDetailCancel: {
    text: 'Cancel event',
    description: 'Action button to cancel event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventDetailDelete: {
    text: 'Delete',
    description: 'Action button to delete event.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventDetailRestore: {
    text: 'Restore event',
    description: 'Action button to restore cancelled event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventDetailCancelledNotice: {
    text: 'This event was cancelled: {reason}',
    description: 'Banner notice showing cancellation reason.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSEduEventDetailCancelModalTitle: {
    text: 'Cancel Event',
    description: 'Modal title when confirming event cancellation.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventDetailCancelReasonLabel: {
    text: 'Cancellation reason (required)',
    description: 'Input label for event cancellation reason.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEduEventDetailCancelReasonPlaceholder: {
    text: 'Why is this event being cancelled?',
    description: 'Placeholder for cancellation reason.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventDetailCancelConfirmButton: {
    text: 'Confirm cancellation',
    description: 'Confirm button to cancel the event.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventDetailDeleteModalTitle: {
    text: 'Delete Event',
    description: 'Modal title when confirming event deletion.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventDetailDeleteConfirmMessage: {
    text: 'Are you sure you want to permanently delete this event? This action cannot be undone.',
    description: 'Confirmation message before deleting event.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSEduEventDetailDeleteConfirmButton: {
    text: 'Delete event',
    description: 'Confirm button to permanently delete the event.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventDetailRsvpsTitle: {
    text: 'RSVP Responses',
    description: 'Section heading for event RSVP responses in detail dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventDetailNoRsvps: {
    text: 'No RSVPs recorded yet.',
    description: 'Notice when an event has no RSVP responses.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduEventDetailRsvpLearner: {
    text: 'Learner',
    description: 'Table column header for learner in RSVP list.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventDetailRsvpStatus: {
    text: 'Response',
    description: 'Table column header for RSVP status in RSVP list.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventDetailRsvpBy: {
    text: 'Responded by',
    description: 'Table column header for person who submitted RSVP.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduEventDetailRsvpWhen: {
    text: 'Date',
    description: 'Table column header for response date in RSVP list.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventDetailRsvpGoing: {
    text: 'Going',
    description: 'Badge text for Going RSVP status.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventDetailRsvpNotGoing: {
    text: 'Not going',
    description: 'Badge text for Not Going RSVP status.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventDetailRsvpMaybe: {
    text: 'Maybe',
    description: 'Badge text for Maybe RSVP status.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduEventDetailOpenAttachment: {
    text: 'Open attachment',
    description: 'Button tooltip or label to open attachment.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduEventErrorAdminOnly: {
    text: 'Only workspace owners and admins can post events to all classes.',
    description: 'Error when non-admin attempts to post school-wide event.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventErrorNodeRequired: {
    text: 'A class or group must be selected to view events.',
    description: 'Error when non-admin lists events without specifying nodeId.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventErrorUseCancel: {
    text: 'Events that have already started or ended cannot be deleted. Please cancel the event instead.',
    description: 'Error when attempting to delete an event that has started or completed.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSEduEventErrorHasRsvps: {
    text: 'Cannot delete an event that already has RSVPs. Please cancel the event instead.',
    description: 'Error when attempting to delete an event with RSVPs.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSEduEventErrorEventTooLong: {
    text: 'Events cannot exceed 31 days in duration.',
    description: 'Error when event duration exceeds 31 days.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventErrorRangeTooLong: {
    text: 'Date range cannot exceed 92 days.',
    description: 'Error when event list date range exceeds 92 days.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventErrorRsvpDeadlineAfterStart: {
    text: 'RSVP deadline must be on or before the event start.',
    description: 'Error when RSVP deadline is scheduled after event start.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventErrorInvalidOnlineUrl: {
    text: 'Online link must be a valid https:// URL.',
    description: 'Error when online meeting link is not a secure https URL.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventErrorPermissionDenied: {
    text: 'You do not have permission to perform this action.',
    description: 'Error when caller lacks permission for event operation.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduEventErrorGeneric: {
    text: 'Failed to process event. Please try again.',
    description: 'Generic fallback error message for event actions.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventErrorRsvpDeadlinePassed: {
    text: 'The RSVP deadline for this event has passed.',
    description: 'Error when attempting to RSVP after deadline.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSEduEventErrorEventCancelled: {
    text: 'This event has been cancelled.',
    description: 'Error when attempting to RSVP to a cancelled event.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventErrorRsvpDisabled: {
    text: 'RSVP is disabled for this event.',
    description: 'Error when attempting to RSVP to an event with RSVP disabled.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventErrorNotInAudience: {
    text: 'This event is not open to this learner.',
    description: 'Error when learner is not in event audience.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduEventErrorMinorReadOnly: {
    text: 'Learner accounts cannot change RSVPs.',
    description: 'Error when minor learner attempts to change RSVP.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduPtmErrorAdminOnly: {
    text: 'Only workspace admins can manage meeting days.',
    description: 'Error when non-admin attempts to configure PTM days.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduPtmErrorTeacherOrAdminOnly: {
    text: 'Only the assigned teacher or an admin can manage time windows.',
    description: 'Error when unauthorized user sets teacher windows.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduPtmErrorDayCancelled: {
    text: 'This meeting day has been cancelled.',
    description: 'Error when attempting action on a cancelled PTM day.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduPtmErrorBookingNotOpen: {
    text: 'Bookings for this meeting day are not open yet.',
    description: 'Error when booking before booking window opens.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSEduPtmErrorBookingClosed: {
    text: 'Bookings for this meeting day have closed.',
    description: 'Error when booking after booking window closes.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSEduPtmErrorCutoffPassed: {
    text: 'The cutoff time for rescheduling or cancelling has passed.',
    description: 'Error when attempting reschedule or cancel after cutoff.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduPtmErrorSlotTaken: {
    text: 'This time slot is no longer available.',
    description: 'Error when selected slot is already booked.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduPtmErrorSlotInBreak: {
    text: 'This time slot falls within a scheduled break.',
    description: 'Error when attempting to book during a break.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSEduPtmErrorChildAlreadyBooked: {
    text: 'A slot has already been booked for this learner with this teacher.',
    description: 'Error when child already has a slot with the teacher.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduPtmErrorGuardianClash: {
    text: 'You have another meeting booked at this same time.',
    description: 'Error when guardian has another child booked at the same slot.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduPtmErrorNotInAudience: {
    text: 'This meeting day is not open to this learner.',
    description: 'Error when learner is not part of the audience for the PTM day.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSEduPtmErrorMinorReadOnly: {
    text: 'Learner accounts cannot book, reschedule, or cancel meetings.',
    description: 'Error when minor self-learner attempts to modify booking.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduPtmErrorNotBookedFamily: {
    text: 'Only the booked family or staff can access this meeting.',
    description: 'Error when non-family attempts to access meeting join info.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduPtmErrorNoteStaffOnly: {
    text: 'Private meeting notes can only be viewed by staff.',
    description: 'Error when non-staff attempts to access private notes.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSEduPtmErrorPermissionDenied: {
    text: 'You do not have permission to perform this action.',
    description: 'Error when user lacks permission for PTM action.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduPtmErrorGeneric: {
    text: 'Failed to process meeting request. Please try again.',
    description: 'Generic fallback error message for PTM actions.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSFamilySubTabEvents: {
    text: 'Events',
    description: 'Events sub-tab in family class tab.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventsUpcoming: {
    text: 'Upcoming',
    description: 'Filter button for upcoming family events.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventsPast: {
    text: 'Past',
    description: 'Filter button for past family events.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventsEmptyUpcoming: {
    text: 'No upcoming events scheduled.',
    description: 'Empty state message for upcoming family events.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSFamilyEventsEmptyPast: {
    text: 'No past events found.',
    description: 'Empty state message for past family events.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSFamilyEventsLoading: {
    text: 'Loading events…',
    description: 'Loading state for family events.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyEventsLoadError: {
    text: 'Failed to load events.',
    description: 'Error message when loading family events fails.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSFamilyEventCancelled: {
    text: 'Cancelled',
    description: 'Badge indicating event is cancelled.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventCancelReason: {
    text: 'Reason: {reason}',
    description: 'Displays reason why event was cancelled.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSFamilyEventRsvpGoing: {
    text: 'Going',
    description: 'RSVP option: Going.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventRsvpNotGoing: {
    text: 'Not going',
    description: 'RSVP option: Not going.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventRsvpMaybe: {
    text: 'Maybe',
    description: 'RSVP option: Maybe.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventRsvpNone: {
    text: 'No response',
    description: 'Label when child has not responded to RSVP.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyEventDetailTitle: {
    text: 'Event details',
    description: 'Title for family event detail dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyEventAllDay: {
    text: 'All day',
    description: 'Label indicating all-day event.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventLocation: {
    text: 'Location',
    description: 'Location section label in event detail.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventOnlineLink: {
    text: 'Online link',
    description: 'Online link section label in event detail.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventAttachments: {
    text: 'Attachments',
    description: 'Attachments section label in event detail.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventRsvpTitle: {
    text: 'RSVP',
    description: 'RSVP section heading in event detail.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventRsvpDeadline: {
    text: 'RSVP by {deadline}',
    description: 'Notice of RSVP deadline.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSFamilyEventRsvpClosed: {
    text: 'RSVP is closed',
    description: 'Notice when RSVP deadline has passed.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyEventRsvpDisabled: {
    text: 'RSVP not required',
    description: 'Notice when RSVP is disabled.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyEventMinorReadOnly: {
    text: 'Learner accounts cannot change RSVPs.',
    description: 'Notice that minor learner accounts cannot submit RSVPs.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSFamilyEventOpenLink: {
    text: 'Open link',
    description: 'Button or link text to open external event URL.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyTodayEventsTitle: {
    text: 'Upcoming events',
    description: 'Title for upcoming events strip on Today tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyTodayEventsSeeAll: {
    text: 'See all',
    description: 'Link to view all events from Today strip.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventNotFound: {
    text: 'Event not found or access denied.',
    description: 'Toast when deep-linked event is not found or denied.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSFamilyEventSubmittingRsvp: {
    text: 'Saving RSVP…',
    description: 'Loading status while submitting RSVP.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyEventLoadMore: {
    text: 'Load more',
    description: 'Button to load more events.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyEventChildAnswer: {
    text: 'Answer',
    description: 'Label for child RSVP response in event list and detail.',
    surface: 'shell',
    maxChars: 20,
  },
  // PL3.d Workspace cap labels for pricing matrix, upgrade modal, and paywall
  NLSWorkspaceCap_maxGoalsPerBoard: { text: 'Goals per board', description: 'Label for maxGoalsPerBoard cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxTasksPerBoard: { text: 'Tasks per board', description: 'Label for maxTasksPerBoard cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxTasksPerGoal: { text: 'Tasks per goal', description: 'Label for maxTasksPerGoal cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxSubtasksPerBoard: { text: 'Subtasks per board', description: 'Label for maxSubtasksPerBoard cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxSubtasksPerTask: { text: 'Subtasks per task', description: 'Label for maxSubtasksPerTask cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxChecklistsPerBoard: { text: 'Checklists per board', description: 'Label for maxChecklistsPerBoard cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxChecklistsPerContainer: { text: 'Checklists per folder', description: 'Label for maxChecklistsPerContainer cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxWorkspaceMembers: { text: 'Workspace members', description: 'Label for maxWorkspaceMembers cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxPendingInvites: { text: 'Pending invites', description: 'Label for maxPendingInvites cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxEnabledDashboardViews: { text: 'Enabled dashboard views', description: 'Label for maxEnabledDashboardViews cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_maxAiPromptCharacters: { text: 'AI prompt characters', description: 'Label for maxAiPromptCharacters cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_titleMaxLength: { text: 'Checklist title characters', description: 'Label for titleMaxLength cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_commentMaxLength: { text: 'Discussion comment characters', description: 'Label for commentMaxLength cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_searchMaxLength: { text: 'Search field characters', description: 'Label for searchMaxLength cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_autoSpawnTitleMaxLength: { text: 'Auto-spawn title characters', description: 'Label for autoSpawnTitleMaxLength cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_weightageMax: { text: 'Checklist weightage', description: 'Label for weightageMax cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_estimatedHoursMax: { text: 'Estimated effort hours', description: 'Label for estimatedHoursMax cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_actualCostMax: { text: 'Expense outlay', description: 'Label for actualCostMax cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_countdownHoursMax: { text: 'Countdown window', description: 'Label for countdownHoursMax cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_delayHoursMax: { text: 'Delay duration', description: 'Label for delayHoursMax cap.', surface: 'common', maxChars: 35 },
  NLSWorkspaceCap_autoSpawnYOffsetMax: { text: 'Auto-spawn Y offset', description: 'Label for autoSpawnYOffsetMax cap.', surface: 'common', maxChars: 35 },
  // Suffixes for workspace limit definitions
  NLSWorkspaceCapSuffix_goals: { text: 'goals', description: 'Suffix for goals limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_tasks: { text: 'tasks', description: 'Suffix for tasks limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_subtasks: { text: 'subtasks', description: 'Suffix for subtasks limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_files: { text: 'files', description: 'Suffix for files limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_members: { text: 'members', description: 'Suffix for members limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_invites: { text: 'invites', description: 'Suffix for invites limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_views: { text: 'views', description: 'Suffix for views limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_chars: { text: 'chars', description: 'Suffix for chars limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_max: { text: 'max', description: 'Suffix for max limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_hours: { text: 'hours', description: 'Suffix for hours limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_currency: { text: 'currency', description: 'Suffix for currency limit.', surface: 'common', maxChars: 20 },
  NLSWorkspaceCapSuffix_px: { text: 'px', description: 'Suffix for px limit.', surface: 'common', maxChars: 20 },
  NLSEduDemoSeedNotFoundToast: {
    text: 'Demo workspace "{id}" not found.',
    description: 'Toast shown when deep link explore param specifies an unknown demo id.',
    surface: 'common',
    maxChars: 60,
  },
  NLSEduPtmSectionTitle: {
    text: 'Parent–teacher meetings',
    description: 'Section header for parent-teacher meetings in Timetable tab.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmSectionSubtitle: {
    text: 'Schedule meetings, configure teacher time windows, and manage slot bookings.',
    description: 'Subtitle for PTM section.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSEduPtmNewDayButton: {
    text: 'New meeting day',
    description: 'Button to create a new PTM day.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmDayStatusActive: {
    text: 'Active',
    description: 'Status pill for active PTM day.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmDayStatusCancelled: {
    text: 'Cancelled',
    description: 'Status pill for cancelled PTM day.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmMeetingTypeInPerson: {
    text: 'In person',
    description: 'Label for in-person meeting type.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmMeetingTypeOnline: {
    text: 'Online',
    description: 'Label for online meeting type.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmAudienceAll: {
    text: 'Whole school',
    description: 'Audience whole school.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmAudienceRoot: {
    text: 'Year / Programme',
    description: 'Audience year or programme.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmAudienceNodes: {
    text: 'Classes / Groups',
    description: 'Audience classes or groups.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmConfigureWindows: {
    text: 'Configure windows',
    description: 'Button to configure teacher time windows.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmViewSlotBoard: {
    text: 'Slot board',
    description: 'Button to open slot board.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmEditDay: {
    text: 'Edit day',
    description: 'Button to edit PTM day.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmCancelDay: {
    text: 'Cancel day',
    description: 'Button to cancel PTM day.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmDayEditorCreateTitle: {
    text: 'Create meeting day',
    description: 'Dialog title for creating PTM day.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmDayEditorEditTitle: {
    text: 'Edit meeting day',
    description: 'Dialog title for editing PTM day.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmDayTitleLabel: {
    text: 'Day title',
    description: 'Label for PTM day title input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmDayTitlePlaceholder: {
    text: 'e.g. Term 1 Parent-Teacher Conferences',
    description: 'Placeholder for PTM day title.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduPtmDayDescriptionLabel: {
    text: 'Description (optional)',
    description: 'Label for PTM day description.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmDayDateLabel: {
    text: 'Meeting date',
    description: 'Label for PTM meeting date picker.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmDayBookingOpenLabel: {
    text: 'Bookings open',
    description: 'Label for booking open date/time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmDayBookingCloseLabel: {
    text: 'Bookings close',
    description: 'Label for booking close date/time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmDayCutoffLabel: {
    text: 'Reschedule/cancel cutoff',
    description: 'Label for reschedule/cancel cutoff date/time.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmDayLocationLabel: {
    text: 'Location or notes',
    description: 'Label for PTM day location or notes.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmCancelDayTitle: {
    text: 'Cancel meeting day',
    description: 'Title for cancel day dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmCancelDayReasonLabel: {
    text: 'Reason for cancellation',
    description: 'Label for cancel reason.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmCancelDayConfirm: {
    text: 'Confirm cancellation',
    description: 'Confirm button to cancel day.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmWindowsDialogTitle: {
    text: 'Teacher time windows',
    description: 'Dialog title for teacher windows.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmTeacherPickerLabel: {
    text: 'Teacher',
    description: 'Picker label for selecting teacher.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmSlotDurationLabel: {
    text: 'Slot duration (minutes)',
    description: 'Label for slot duration.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmWindowStartLabel: {
    text: 'Window start',
    description: 'Label for window start time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmWindowEndLabel: {
    text: 'Window end',
    description: 'Label for window end time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmAddWindowButton: {
    text: 'Add window',
    description: 'Button to add another window.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmRemoveWindowButton: {
    text: 'Remove',
    description: 'Button to remove a window or break.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmBreaksLabel: {
    text: 'Scheduled breaks',
    description: 'Label for breaks section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmBreakStartLabel: {
    text: 'Break start',
    description: 'Label for break start time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmBreakEndLabel: {
    text: 'Break end',
    description: 'Label for break end time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmBreakNameLabel: {
    text: 'Break label',
    description: 'Label for break name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmAddBreakButton: {
    text: 'Add break',
    description: 'Button to add another break.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmRoomOrOnlineLabel: {
    text: 'Room / Meeting link',
    description: 'Label for room or online link.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmSlotPreviewTitle: {
    text: 'Live slot preview',
    description: 'Title for live slot preview section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmSlotPreviewCount: {
    text: '{count} slots generated',
    description: 'Slot count in preview.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmSlotBoardTitle: {
    text: 'Meeting slot board',
    description: 'Title for slot board dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmSlotStatusFree: {
    text: 'Free',
    description: 'Status for free slot.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduPtmSlotStatusBooked: {
    text: 'Booked',
    description: 'Status for booked slot.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduPtmSlotStatusCancelled: {
    text: 'Cancelled',
    description: 'Status for cancelled slot.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduPtmSlotStatusDone: {
    text: 'Done',
    description: 'Status for completed slot.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduPtmSlotStatusMet: {
    text: 'Met',
    description: 'Status for met slot.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduPtmSlotStatusNoShow: {
    text: 'No-show',
    description: 'Status for no-show slot.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduPtmBookOnBehalfButton: {
    text: 'Book on behalf',
    description: 'Button to book slot on family behalf.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmRescheduleButton: {
    text: 'Move booking',
    description: 'Button to move booking.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmCancelBookingButton: {
    text: 'Cancel booking',
    description: 'Button to cancel booking.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmOutcomeNotesButton: {
    text: 'Outcome & notes',
    description: 'Button to record outcome and notes.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmJoinMeetingButton: {
    text: 'Join meeting',
    description: 'Button to join online meeting.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmMeetingBadge: {
    text: 'PTM',
    description: 'Badge for PTM meeting.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSEduPtmBookOnBehalfTitle: {
    text: 'Book on family’s behalf',
    description: 'Dialog title for book on behalf.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmSelectLearnerLabel: {
    text: 'Select learner',
    description: 'Label for learner picker.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmBookConfirmButton: {
    text: 'Confirm booking',
    description: 'Button to confirm booking.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmRescheduleTitle: {
    text: 'Move meeting booking',
    description: 'Dialog title for reschedule.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmSelectNewSlotLabel: {
    text: 'Select new slot',
    description: 'Label for new slot picker.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmRescheduleConfirmButton: {
    text: 'Confirm move',
    description: 'Button to confirm move.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmCancelBookingTitle: {
    text: 'Cancel meeting booking',
    description: 'Title for cancel booking dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmCancelBookingReasonLabel: {
    text: 'Reason (optional)',
    description: 'Label for cancel reason.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmOutcomeNotesTitle: {
    text: 'Meeting outcome & notes',
    description: 'Title for outcome and notes dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmMarkMetButton: {
    text: 'Mark met',
    description: 'Button to mark meeting met.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmMarkNoShowButton: {
    text: 'Mark no-show',
    description: 'Button to mark meeting as no-show.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmPrivateNotesTitle: {
    text: 'Private notes',
    description: 'Title for private notes.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmPrivateNotesStaffBadge: {
    text: 'Private — staff only',
    description: 'Badge indicating private staff notes.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmPrivateNotesPlaceholder: {
    text: 'Add confidential teacher notes here (not visible to family)...',
    description: 'Placeholder for private notes.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSEduPtmSaveNotesButton: {
    text: 'Save notes',
    description: 'Button to save private notes.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmSummaryTitle: {
    text: 'Summary for family',
    description: 'Title for published summary.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSEduPtmSummaryPlaceholder: {
    text: 'Brief summary of discussion and next steps to share with the family...',
    description: 'Placeholder for summary.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSEduPtmPublishSummaryButton: {
    text: 'Publish summary',
    description: 'Button to publish summary.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmPublishSummaryConfirmTitle: {
    text: 'Publish summary to family?',
    description: 'Confirmation title.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEduPtmPublishSummaryConfirmBody: {
    text: 'This summary will be visible to the family in the learner’s progress tab. Are you sure you want to publish?',
    description: 'Confirmation body.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSEduPtmEmptyDays: {
    text: 'No parent-teacher meeting days scheduled.',
    description: 'Empty state text when no PTM days exist.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEduPtmEmptySlots: {
    text: 'No slots configured for this teacher yet.',
    description: 'Empty state text when no slots exist.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEduPtmCalendarTitle: {
    text: 'PTM: {title}',
    description: 'Calendar event title for PTM day or slot.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEduPtmKeepBookingButton: {
    text: 'Keep booking',
    description: 'Button to dismiss cancel booking dialog and keep the booking.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmFilterAllDays: {
    text: 'All days',
    description: 'Filter option to view all meeting days.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmViewDetailsButton: {
    text: 'Details',
    description: 'Button to view day details.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmBreakLabel: {
    text: 'Break',
    description: 'Pill label for scheduled break slot.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEduPtmBookedByLabel: {
    text: 'Booked by:',
    description: 'Label prefix for member or family who booked slot.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmLearnerLabel: {
    text: 'Learner:',
    description: 'Label prefix for learner name in reschedule dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmDefaultBreakName: {
    text: 'Tea break',
    description: 'Default label for newly added break.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmDurationMinutes: {
    text: '{minutes} min',
    description: 'Duration option label in minutes.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSEduPtmErrorWindowRequired: {
    text: 'At least one time window is required.',
    description: 'Validation message when no time windows are configured.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSEduPtmDayAudienceLabel: {
    text: 'Audience',
    description: 'Label for audience selector in day editor.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSEduPtmFamilyLabel: {
    text: 'Family',
    description: 'Label shown for booked-by when booker is not a staff member.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilySubTabMeetings: {
    text: 'Meetings',
    description: 'Label for meetings sub-tab in family class view',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyPtmDaysTitle: {
    text: 'Parent-Teacher Meetings',
    description: 'Title for PTM days section in family class view',
    surface: 'shell',
    maxChars: 50,
  },
  NLSFamilyPtmEmptyDays: {
    text: 'No upcoming parent-teacher meetings scheduled.',
    description: 'Empty state when no PTM days are scheduled',
    surface: 'shell',
    maxChars: 100,
  },
  NLSFamilyPtmBookSlot: {
    text: 'Book meeting',
    description: 'Button label to book a meeting slot',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyPtmSelectTeacher: {
    text: 'Select teacher',
    description: 'Label for teacher selector in PTM dialog',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyPtmSelectSlot: {
    text: 'Select slot',
    description: 'Label for slot selector in PTM dialog',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyPtmBookingOpenRange: {
    text: 'Booking: {open} – {close}',
    description: 'Display of booking open and close dates',
    surface: 'shell',
    maxChars: 100,
  },
  NLSFamilyPtmCutoffLabel: {
    text: 'Changes cutoff: {cutoff}',
    description: 'Display of reschedule/cancellation cutoff',
    surface: 'shell',
    maxChars: 80,
  },
  NLSFamilyPtmBookingNotOpenDesc: {
    text: 'Booking is not currently open for this day.',
    description: 'Explanation when booking is closed or not yet open',
    surface: 'shell',
    maxChars: 120,
  },
  NLSFamilyPtmCutoffPassedDesc: {
    text: 'The cutoff for moving or cancelling meetings has passed.',
    description: 'Explanation when reschedule cutoff has passed',
    surface: 'shell',
    maxChars: 120,
  },
  NLSFamilyPtmBookConfirmPrompt: {
    text: 'Confirm meeting with {teacherName} on {time}?',
    description: 'Confirmation prompt before booking a slot',
    surface: 'shell',
    maxChars: 150,
  },
  NLSFamilyPtmTwoChildClashWarning: {
    text: 'You already have a meeting booked for another child at this time.',
    description: 'Warning when selecting a slot that clashes with another child',
    surface: 'shell',
    maxChars: 140,
  },
  NLSFamilyPtmMinorReadOnlyNote: {
    text: 'Student accounts can view meeting details only. A guardian must book, move, or cancel.',
    description: 'Notice shown to minor self accounts',
    surface: 'shell',
    maxChars: 150,
  },
  NLSFamilyPtmSummariesSectionTitle: {
    text: 'Parent-Teacher Meeting Summaries',
    description: 'Heading for PTM summaries in Family Progress tab',
    surface: 'shell',
    maxChars: 60,
  },
  NLSFamilyPtmEmptySummaries: {
    text: 'No meeting summaries published yet.',
    description: 'Empty state for PTM summaries in Family Progress tab',
    surface: 'shell',
    maxChars: 80,
  },
  NLSFamilyPtmNextBookingTitle: {
    text: 'Next Parent-Teacher Meeting',
    description: 'Title for next PTM card on Today tab',
    surface: 'shell',
    maxChars: 60,
  },
  NLSFamilyPtmJoinMeeting: {
    text: 'Join meeting',
    description: 'Button label to join an online PTM meeting',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyPtmJoiningMeeting: {
    text: 'Joining meeting...',
    description: 'State message while connecting to meeting',
    surface: 'shell',
    maxChars: 40,
  },
  NLSFamilyPtmJoinErrorTitle: {
    text: 'Unable to join meeting',
    description: 'Title for join meeting error state page',
    surface: 'shell',
    maxChars: 50,
  },
  NLSFamilyPtmDayNotFound: {
    text: 'Meeting day not found.',
    description: 'Toast message when ptmDay deep link cannot be resolved',
    surface: 'shell',
    maxChars: 60,
  },
  NLSFamilyBackToFamily: {
    text: 'Back to Family',
    description: 'Button label to return to /family from join error page',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyPtmTeacherLabel: {
    text: 'Teacher',
    description: 'Teacher column or label in PTM booking',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyPtmSlotLabel: {
    text: 'Slot',
    description: 'Slot label in PTM booking',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyPtmExistingBooking: {
    text: 'Booked: {time} ({status})',
    description: 'Existing booking indicator',
    surface: 'shell',
    maxChars: 80,
  },
  NLSFamilyPtmMoveSlotTitle: {
    text: 'Move meeting',
    description: 'Dialog title for moving a meeting slot',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyPtmCancelSlotTitle: {
    text: 'Cancel meeting',
    description: 'Dialog title for cancelling a meeting',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyPtmCancelReasonLabel: {
    text: 'Reason for cancellation',
    description: 'Label for cancellation reason input',
    surface: 'shell',
    maxChars: 50,
  },
  NLSFamilyPtmCancelConfirmButton: {
    text: 'Confirm cancellation',
    description: 'Button to confirm cancellation',
    surface: 'shell',
    maxChars: 40,
  },
  NLSFamilyPtmMoveConfirmButton: {
    text: 'Move meeting',
    description: 'Button to confirm rescheduling',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyPtmKeepMeetingButton: {
    text: 'Keep meeting',
    description: 'Button to abort cancellation or move',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyPtmMeetingTypeLabel: {
    text: 'Type',
    description: 'Label for meeting type (online or in person)',
    surface: 'shell',
    maxChars: 20,
  },
  NLSFamilyPtmRoomLabel: {
    text: 'Room / Link',
    description: 'Label for room or link',
    surface: 'shell',
    maxChars: 30,
  },

  // --- R25 OC6.b: Workspace Welcome screen ---
  NLSWorkspaceWelcomeTitle: {
    text: 'Your workspace is set up',
    description: 'Header title for the workspace welcome screen after setup.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSWorkspaceWelcomeSubtitle: {
    text: 'Here is what was applied to your space. You can tweak everything now or jump straight to work.',
    description: 'Subtitle for the workspace welcome screen.',
    surface: 'onboarding',
    maxChars: 120,
  },
  NLSWorkspaceWelcomeSetupSummaryTitle: {
    text: 'Workspace setup',
    description: 'Section heading for setup summary.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSWorkspaceWelcomeOpeningViewLabel: {
    text: 'Opening view',
    description: 'Label for opening dashboard view in welcome screen.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSWorkspaceWelcomeEnabledViewsLabel: {
    text: 'Active views',
    description: 'Label for enabled dashboard views in welcome screen.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSWorkspaceWelcomeActiveModulesLabel: {
    text: 'Active modules',
    description: 'Label for active modules in welcome screen.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSWorkspaceWelcomeAiSuggestionsLabel: {
    text: 'AI suggestions',
    description: 'Label for AI suggestions state in welcome screen.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSWorkspaceWelcomeNotificationsLabel: {
    text: 'Notifications',
    description: 'Label for notifications state in welcome screen.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSWorkspaceWelcomeEnabled: {
    text: 'On',
    description: 'Badge or text indicating a feature is enabled.',
    surface: 'onboarding',
    maxChars: 10,
  },
  NLSWorkspaceWelcomeDisabled: {
    text: 'Off',
    description: 'Badge or text indicating a feature is disabled.',
    surface: 'onboarding',
    maxChars: 10,
  },
  NLSWorkspaceWelcomeChangeInSettings: {
    text: 'Change in Settings',
    description: 'Link action to open settings for a feature.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSWorkspaceWelcomeTemplatesSectionTitle: {
    text: 'Start with a template',
    description: 'Heading for template suggestions section in welcome screen.',
    surface: 'onboarding',
    maxChars: 40,
  },
  NLSWorkspaceWelcomeTemplatesSectionSubtitle: {
    text: 'Kickstart your work with boards tailored to your use case.',
    description: 'Subtitle for template suggestions section.',
    surface: 'onboarding',
    maxChars: 80,
  },
  NLSWorkspaceWelcomeTemplateAdd: {
    text: 'Add',
    description: 'Button label to add a template to the workspace.',
    surface: 'onboarding',
    maxChars: 16,
  },
  NLSWorkspaceWelcomeTemplateAdded: {
    text: 'Added',
    description: 'Button label after template has been added.',
    surface: 'onboarding',
    maxChars: 16,
  },
  NLSWorkspaceWelcomeTemplateAdding: {
    text: 'Adding...',
    description: 'Button label while template is being added.',
    surface: 'onboarding',
    maxChars: 16,
  },
  NLSWorkspaceWelcomeTemplateImportSuccess: {
    text: 'Added {name} to your workspace.',
    description: 'Toast message after successfully importing a template.',
    surface: 'onboarding',
    maxChars: 70,
  },
  NLSWorkspaceWelcomeTemplateImportFailed: {
    text: 'Could not add template to workspace.',
    description: 'Toast message when importing template fails.',
    surface: 'onboarding',
    maxChars: 60,
  },
  NLSWorkspaceWelcomeInviteSectionTitle: {
    text: 'Invite your team',
    description: 'Heading for invite section in welcome screen.',
    surface: 'onboarding',
    maxChars: 30,
  },
  NLSWorkspaceWelcomeInviteSectionSubtitle: {
    text: 'Bring your teammates or helpers into this space.',
    description: 'Subtitle for invite section in welcome screen.',
    surface: 'onboarding',
    maxChars: 80,
  },
  NLSWorkspaceWelcomeInviteMembersButton: {
    text: 'Invite team members',
    description: 'Button to open team members invite page.',
    surface: 'onboarding',
    maxChars: 32,
  },
  NLSWorkspaceWelcomeInviteHelpersButton: {
    text: 'Invite helpers',
    description: 'Button to open helper invites.',
    surface: 'onboarding',
    maxChars: 28,
  },
  NLSWorkspaceWelcomeTakeTourButton: {
    text: 'Take the tour',
    description: 'CTA button to launch welcome tour.',
    surface: 'onboarding',
    maxChars: 24,
  },
  NLSWorkspaceWelcomeMoreModulesHint: {
    text: 'More tools in Settings → Modules',
    description: 'Link hint for admins to view more modules in settings.',
    surface: 'onboarding',
    maxChars: 45,
  },
  NLSWorkspaceWelcomeGoToDashboard: {
    text: 'Go to dashboard',
    description: 'Primary CTA button to close welcome modal and go to dashboard.',
    surface: 'onboarding',
    maxChars: 25,
  },
  NLSWorkspaceWelcomeCloseAria: {
    text: 'Close welcome screen',
    description: 'Aria label for closing the welcome screen.',
    surface: 'onboarding',
    maxChars: 30,
  },

  // --- R25 OC7.b: Workspace Profile Panel ---
  NLSAdminWorkspaceProfileTitle: {
    text: 'Workspace profile',
    description: 'Title for the Workspace Profile panel in Board Settings Overview.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminWorkspaceProfileSubtitle: {
    text: 'Profile details that tune AI suggestions and team workspace setup.',
    description: 'Subtitle for the Workspace Profile panel.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSAdminWorkspaceProfileRoleLabel: {
    text: 'Role title',
    description: 'Label for role title in workspace profile.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminWorkspaceProfileUseCaseLabel: {
    text: 'Use case',
    description: 'Label for workspace use case in workspace profile.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminWorkspaceProfileAudienceLabel: {
    text: 'Who will use this',
    description: 'Label for audience type in workspace profile.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminWorkspaceProfileTeamSizeLabel: {
    text: 'Team size',
    description: 'Label for team size in workspace profile.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminWorkspaceProfileFocusAreasLabel: {
    text: 'Focus areas',
    description: 'Label for focus areas in workspace profile.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminWorkspaceProfileIntentLabel: {
    text: 'Warm-up intent',
    description: 'Label for warm-up intent sentence in workspace profile.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminWorkspaceProfileIntentHint: {
    text: 'Up to 280 characters.',
    description: 'Hint for intent sentence length limit.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSAdminWorkspaceProfileNote: {
    text: 'Views and modules are not changed — edit them in Dashboard Views and Modules.',
    description: 'Note explaining that profile updates do not re-apply views or modules (OC16).',
    surface: 'settings',
    maxChars: 100,
  },
  NLSAdminWorkspaceProfileAiSuggestionsTitle: {
    text: 'Ambient AI suggestions',
    description: 'Title for ambient AI suggestions toggle row.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSAdminWorkspaceProfileAiSuggestionsSubtitle: {
    text: 'Show ambient AI suggestions on lines and checklists across this workspace.',
    description: 'Subtitle for ambient AI suggestions toggle row.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSAdminWorkspaceProfileAiSuggestionsSavedToast: {
    text: 'AI suggestions updated.',
    description: 'Toast notification after instant setting saves AI suggestions.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSAdminWorkspaceProfileAiSuggestionsErrorToast: {
    text: 'Could not update AI suggestions.',
    description: 'Toast notification when instant setting save fails for AI suggestions.',
    surface: 'settings',
    maxChars: 50,
  },
  NLSAdminWorkspaceProfileSaveSuccess: {
    text: 'Workspace profile saved.',
    description: 'Toast after saving workspace profile.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSAdminWorkspaceProfileSaveError: {
    text: 'Could not save workspace profile.',
    description: 'Toast on workspace profile save error.',
    surface: 'settings',
    maxChars: 50,
  },
  NLSAdminWorkspaceProfileAudienceSolo: {
    text: 'Just me',
    description: 'Label for Just me audience option.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSAdminWorkspaceProfileAudienceHelpers: {
    text: 'Me + helpers',
    description: 'Label for Me + helpers audience option.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSAdminWorkspaceProfileAudienceTeam: {
    text: 'A team',
    description: 'Label for A team audience option.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSAdminWorkspaceProfileReadOnlyNotice: {
    text: 'Only workspace administrators can edit the workspace profile.',
    description: 'Notice shown to non-admins in workspace profile panel.',
    surface: 'settings',
    maxChars: 80,
  },
  NLSAdminWorkspaceProfileEmpty: {
    text: 'Not set',
    description: 'Placeholder when a profile field is empty.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSAdminWorkspaceProfileRolePlaceholder: {
    text: 'e.g. Founder, Product Manager, Designer',
    description: 'Role title input placeholder.',
    surface: 'settings',
    maxChars: 50,
  },
  NLSAdminWorkspaceProfileIntentPlaceholder: {
    text: 'What are you building or planning in this space?',
    description: 'Warm-up intent textarea placeholder.',
    surface: 'settings',
    maxChars: 60,
  },
  // EM2.b: Helper School Jobs Web Admin
  NLSInvitesHelperSchoolJobsTitle: {
    text: 'School jobs',
    description: 'Heading for helper school job assignments in side panel.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSInvitesHelperSchoolJobsBody: {
    text: 'Assign daily responsibilities in education workspaces.',
    description: 'Body text explaining helper school job assignments.',
    surface: 'settings',
    maxChars: 65,
  },
  NLSInvitesHelperJobRoll: {
    text: 'Roll (Class attendance)',
    description: 'Checkbox label for helper roll call job.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSInvitesHelperJobGate: {
    text: 'Gate (Pick-up & Arrival)',
    description: 'Checkbox label for helper gate job.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSInvitesHelperJobBus: {
    text: 'Bus (Boarding roll)',
    description: 'Checkbox label for helper bus job.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSInvitesHelperJobDesk: {
    text: 'Desk (Visitors & Walk-ins)',
    description: 'Checkbox label for helper front desk job.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSInvitesHelperRoutePickerLabel: {
    text: 'Assigned bus routes',
    description: 'Label for route picker when bus job is enabled.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSInvitesHelperRoutePickerEmpty: {
    text: 'No bus routes available',
    description: 'Message when no routes exist in workspace.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSInvitesHelperSessionPickerLabel: {
    text: 'Assigned class sessions',
    description: 'Label for session series picker when roll job is enabled.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSInvitesHelperSessionPickerEmpty: {
    text: 'No class sessions available',
    description: 'Message when no class sessions exist in workspace.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSInvitesHelperSchoolJobsSaveBtn: {
    text: 'Save school jobs',
    description: 'Button label to save helper school job assignments.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSInvitesHelperSchoolJobsSaving: {
    text: 'Saving...',
    description: 'Busy state for saving helper school jobs.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSInvitesHelperSchoolJobsSuccess: {
    text: 'School jobs updated.',
    description: 'Success toast/message when school jobs are saved.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSInvitesHelperSchoolJobsError: {
    text: 'Could not update school jobs.',
    description: 'Error message when saving school jobs fails.',
    surface: 'settings',
    maxChars: 45,
  },
  // EM2.b: Bus Routes Editor
  NLSEduBusRoutesSectionTitle: {
    text: 'Bus routes',
    description: 'Title for transport bus routes editor section in settings.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduBusRoutesSectionDesc: {
    text: 'Manage school transport routes, stops, learners per stop, and assigned helpers.',
    description: 'Description for transport bus routes editor section.',
    surface: 'settings',
    maxChars: 95,
  },
  NLSEduBusRoutesAddBtn: {
    text: 'Add route',
    description: 'Button label to add a new transport route.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduBusRoutesEmpty: {
    text: 'No bus routes created yet.',
    description: 'Empty state message when no bus routes exist.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduBusRouteName: {
    text: 'Route name',
    description: 'Label for route name input.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduBusRouteCode: {
    text: 'Route code',
    description: 'Label for route code input.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduBusRouteStops: {
    text: 'Stops',
    description: 'Heading for route stops in order.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduBusRouteAddStop: {
    text: 'Add stop',
    description: 'Button to add a stop to the route.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduBusRouteStopName: {
    text: 'Stop name',
    description: 'Placeholder/label for a transport stop name.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduBusRouteStopLearners: {
    text: 'Learners ({count})',
    description: 'Label showing count of learners assigned to stop.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduBusRouteHelpers: {
    text: 'Assigned helpers',
    description: 'Label for helpers assigned to route.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduBusRouteSaveBtn: {
    text: 'Save route',
    description: 'Save button label in bus route modal/form.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduBusRouteSaving: {
    text: 'Saving...',
    description: 'Busy state for saving bus route.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduBusRouteArchiveBtn: {
    text: 'Archive',
    description: 'Button to archive a bus route.',
    surface: 'settings',
    maxChars: 15,
  },
  NLSEduBusRouteConfirmArchive: {
    text: 'Archive route {name}?',
    description: 'Confirmation prompt to archive a bus route.',
    surface: 'settings',
    maxChars: 50,
  },
  NLSEduBusRouteSuccessSave: {
    text: 'Route saved successfully.',
    description: 'Success notification when route is saved.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduBusRouteSuccessArchive: {
    text: 'Route archived.',
    description: 'Success notification when route is archived.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduBusRouteError: {
    text: 'Failed to update bus route.',
    description: 'Error message when updating route fails.',
    surface: 'settings',
    maxChars: 45,
  },
  // EM3.b: School Field Pass
  NLSFieldPassModeChecklist: {
    text: 'Checklist pass',
    description: 'Mode switch label for standard checklist field pass.',
    surface: 'common',
    maxChars: 25,
  },
  NLSFieldPassModeSchool: {
    text: 'School pass',
    description: 'Mode switch label for education school field pass.',
    surface: 'common',
    maxChars: 25,
  },
  NLSFieldPassSchoolKindLabel: {
    text: 'School pass type',
    description: 'Label for selecting school pass kind.',
    surface: 'common',
    maxChars: 30,
  },
  NLSFieldPassKindSubstitute: {
    text: 'Substitute teacher (Class session)',
    description: 'Option label for substitute teacher school pass.',
    surface: 'common',
    maxChars: 45,
  },
  NLSFieldPassKindExam: {
    text: 'Exam duty (Assessment & Hall)',
    description: 'Option label for exam duty school pass.',
    surface: 'common',
    maxChars: 45,
  },
  NLSFieldPassKindCoach: {
    text: 'Visiting coach (Session & Metrics)',
    description: 'Option label for visiting coach school pass.',
    surface: 'common',
    maxChars: 45,
  },
  NLSFieldPassSchoolSessionSelect: {
    text: 'Class session',
    description: 'Label for class session picker in school pass.',
    surface: 'common',
    maxChars: 25,
  },
  NLSFieldPassSchoolAssessmentSelect: {
    text: 'Assessment',
    description: 'Label for assessment picker in school pass.',
    surface: 'common',
    maxChars: 25,
  },
  NLSFieldPassSchoolHallLabel: {
    text: 'Exam hall / Room',
    description: 'Label for exam hall in school pass.',
    surface: 'common',
    maxChars: 30,
  },
  NLSFieldPassSchoolMetricsLabel: {
    text: 'Metrics',
    description: 'Label for metric definitions picker in visiting coach school pass.',
    surface: 'common',
    maxChars: 25,
  },
  NLSFieldPassSchoolCreateSuccess: {
    text: 'School Field Pass created and link copied.',
    description: 'Notice when school field pass is created.',
    surface: 'common',
    maxChars: 55,
  },
  NLSFieldPassSchoolErrorMissingSession: {
    text: 'Please select a class session.',
    description: 'Validation error when session is not selected.',
    surface: 'common',
    maxChars: 45,
  },
  NLSFieldPassSchoolErrorMissingAssessment: {
    text: 'Please select an assessment.',
    description: 'Validation error when assessment is not selected.',
    surface: 'common',
    maxChars: 45,
  },
  // R33 WA8.a & WA8.b: Field Pass over WhatsApp and Clean-up
  NLSFieldPassSendWhatsApp: {
    text: 'Send to WhatsApp number',
    description: 'Option to deliver pass via WhatsApp',
    surface: 'common',
    maxChars: 36,
  },
  NLSFieldPassDeliveryPhoneLabel: {
    text: 'WhatsApp phone number',
    description: 'Phone input label',
    surface: 'common',
    maxChars: 32,
  },
  NLSFieldPassDeliveryPhonePlaceholder: {
    text: '+91 98765 43210',
    description: 'Phone placeholder',
    surface: 'common',
    maxChars: 20,
  },
  NLSFieldPassSentWhatsApp: {
    text: 'Sent on WhatsApp to {phone}',
    description: 'Notice after WhatsApp pass creation',
    surface: 'common',
    maxChars: 50,
  },
  NLSFieldPassCloseAndCleanUp: {
    text: 'Close & clean up',
    description: 'Button label for cleaning up a pass',
    surface: 'common',
    maxChars: 24,
  },
  NLSFieldPassConfirmCleanUpTitle: {
    text: 'Close & clean up this Field Pass?',
    description: 'Confirmation title',
    surface: 'common',
    maxChars: 50,
  },
  NLSFieldPassConfirmCleanUpBody: {
    text: 'This will keep proof photos, status history, and comments on their tasks, post a final summary card, and permanently delete pass chat messages and temporary media.',
    description: 'Confirmation description',
    surface: 'common',
    maxChars: 180,
  },
  NLSFieldPassConfirmCleanUpAction: {
    text: 'Close & clean up',
    description: 'Confirm action button',
    surface: 'common',
    maxChars: 24,
  },
  NLSFieldPassCleanedUp: {
    text: 'Field Pass closed and cleaned up.',
    description: 'Notice after cleanup',
    surface: 'common',
    maxChars: 40,
  },
  NLSFieldPassMakeHelper: {
    text: 'Make helper',
    description: 'Action button to invite as helper',
    surface: 'common',
    maxChars: 20,
  },
  NLSFieldPassEnterWhatsAppPhone: {
    text: 'Please enter a WhatsApp phone number.',
    description: 'Validation error when WhatsApp phone number is empty.',
    surface: 'common',
    maxChars: 45,
  },
  NLSFieldPassCleanUpFailed: {
    text: 'Could not clean up Field Pass.',
    description: 'Error message when clean up fails.',
    surface: 'common',
    maxChars: 40,
  },
  NLSFieldPassSummaryTitle: {
    text: 'Field Pass Summary · {name}',
    description: 'Field Pass summary card title with recipient name.',
    surface: 'common',
    maxChars: 60,
  },
  NLSFieldPassStatusClosedCleanedUp: {
    text: 'Closed & Cleaned Up',
    description: 'Status badge for closed and cleaned up Field Pass.',
    surface: 'common',
    maxChars: 25,
  },
  NLSFieldPassPhoneLabel: {
    text: 'Phone:',
    description: 'Label for phone number in summary card.',
    surface: 'common',
    maxChars: 15,
  },
  NLSFieldPassTasksCompletedLabel: {
    text: 'Tasks completed:',
    description: 'Label for tasks completed count in summary card.',
    surface: 'common',
    maxChars: 25,
  },
  NLSFieldPassProofsSubmittedLabel: {
    text: 'Proofs submitted:',
    description: 'Label for proofs submitted count in summary card.',
    surface: 'common',
    maxChars: 25,
  },
  NLSFieldPassActiveLabel: {
    text: 'Active: {date}',
    description: 'Active date in summary card.',
    surface: 'common',
    maxChars: 30,
  },
  NLSFieldPassHelperInviteSent: {
    text: 'Helper invite sent to {phone}.',
    description: 'Success notice when helper invite is sent from summary card.',
    surface: 'common',
    maxChars: 50,
  },
  NLSFieldPassHelperInviteFailed: {
    text: 'Could not send helper invite.',
    description: 'Error notice when helper invite fails from summary card.',
    surface: 'common',
    maxChars: 40,
  },
  NLSFieldPassFallbackName: {
    text: 'Field Pass',
    description: 'Fallback display name for Field Pass.',
    surface: 'common',
    maxChars: 20,
  },
  // EM3.b: Pending from passes
  NLSEducationPendingPassesTitle: {
    text: 'Pending from passes',
    description: 'Section title for pending field pass submissions in Attendance tab.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEducationPendingPassesDesc: {
    text: 'Review and accept attendance or duty logs submitted via School Field Passes.',
    description: 'Description for pending field pass submissions section.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSEducationPendingPassesEmpty: {
    text: 'No pending Field Pass submissions.',
    description: 'Empty state message when no pending submissions exist.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSEducationPendingPassHolder: {
    text: 'Pass holder: {name}',
    description: 'Pass holder name label in pending entry card.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEducationPendingPassSession: {
    text: 'Session: {session}',
    description: 'Session label in pending entry card.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSEducationPendingPassEntriesCount: {
    text: '{count} entries recorded',
    description: 'Count of entries recorded in pending field pass submission.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSEducationPendingPassAcceptBtn: {
    text: 'Accept',
    description: 'Button label to accept a pending field pass entry.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEducationPendingPassRejectBtn: {
    text: 'Reject',
    description: 'Button label to reject a pending field pass entry.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSEducationPendingPassReasonPlaceholder: {
    text: 'Reason (optional for accept, required for reject)',
    description: 'Placeholder for review reason text input.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSEducationPendingPassAccepted: {
    text: 'Field pass submission accepted.',
    description: 'Success message when field pass submission is accepted.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSEducationPendingPassRejected: {
    text: 'Field pass submission rejected.',
    description: 'Notification message when field pass submission is rejected.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSEducationPendingPassActionError: {
    text: 'Could not process submission review.',
    description: 'Error message when reviewing field pass submission fails.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSFamilyProgressAttendanceTitle: {
    text: 'Attendance',
    description: 'Title for the attendance card in family progress tab.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyProgressMarksTitle: {
    text: 'Marks',
    description: 'Title for the marks card in family progress tab.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyProgressOverallPresent: {
    text: '{percent}% present',
    description: 'Overall attendance percentage present.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyAttendanceLegendPresent: {
    text: 'Present: {count}',
    description: 'Text legend for present attendance count.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyAttendanceLegendLate: {
    text: 'Late: {count}',
    description: 'Text legend for late attendance count.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyAttendanceLegendAbsent: {
    text: 'Absent: {count}',
    description: 'Text legend for absent attendance count.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyAttendanceLegendExcused: {
    text: 'Excused: {count}',
    description: 'Text legend for excused attendance count.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyAttendanceBarAria: {
    text: '{month}: {present} present, {late} late, {absent} absent, {excused} excused',
    description: 'Accessibility aria label for monthly attendance stacked bar.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSFamilyProgressNoAttendance: {
    text: 'No attendance records yet',
    description: 'Empty state message when no attendance is available.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyProgressNoMarks: {
    text: 'No marks released yet',
    description: 'Empty state message for marks card in family progress tab.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyMoreLanguage: {
    text: 'Language',
    description: 'Row label for language switcher in more tab.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyMoreConsent: {
    text: 'Consent and privacy',
    description: 'Row label for consent and privacy in more tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyMorePtm: {
    text: 'Parent–teacher meetings',
    description: 'Row label for parent-teacher meetings in more tab.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyConsentAgreedStatus: {
    text: 'Agreed: {level}',
    description: 'Current consent agreement description.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSFamilyConsentWithdrawButton: {
    text: 'Withdraw consent',
    description: 'Button to withdraw media and profile consent.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSFamilyConsentConfirmTitle: {
    text: 'Withdraw consent?',
    description: 'Title of confirmation dialog to withdraw consent.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyConsentConfirmBody: {
    text: 'Withdrawing consent will revoke media permissions and freeze this learner account in this workspace. Are you sure you want to proceed?',
    description: 'Body of confirmation dialog explaining effect of withdrawing consent.',
    surface: 'shell',
    maxChars: 150,
  },
  NLSFamilyConsentConfirmWithdraw: {
    text: 'Yes, withdraw consent',
    description: 'Confirm button in withdraw consent dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSFamilyConsentConfirmCancel: {
    text: 'Cancel',
    description: 'Cancel button in withdraw consent dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSFamilyConsentWithdrawnSuccess: {
    text: 'Consent has been withdrawn',
    description: 'Status indicating consent has been withdrawn.',
    surface: 'shell',
    maxChars: 30,
  },
  /* -------------------------------------------------------------------------
   * R39 Sector Operations — Construction & Hospital Hubs, Places & QR, Workforce
   * ------------------------------------------------------------------------- */
  NLSShellRailTileConstruction: {
    text: 'Construction',
    description: 'Rail navigation tile label for Construction sector hub.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSShellRailTileHospital: {
    text: 'Hospital',
    description: 'Rail navigation tile label for Hospital sector hub.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSShellRailTileSites: {
    text: 'Sites',
    description: 'Rail navigation tile label for Sites sector hub.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSShellRailTileDelivery: {
    text: 'Delivery',
    description: 'Rail navigation tile label for Delivery sector hub.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSShellRailTileShops: {
    text: 'Shops',
    description: 'Rail navigation tile label for Shops sector hub.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSShellRailTileStays: {
    text: 'Stays',
    description: 'Rail navigation tile label for Stays sector hub.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSShellRailTileJobs: {
    text: 'Jobs',
    description: 'Rail navigation tile label for Jobs sector hub.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSShellRailTileClinics: {
    text: 'Clinics',
    description: 'Rail navigation tile label for Clinics sector hub.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSShellRailTilePlant: {
    text: 'Plant',
    description: 'Rail navigation tile label for Plant sector hub.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConstructionTitle: {
    text: 'Construction',
    description: 'Construction sector hub page title.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConstructionSubtitle: {
    text: 'Field operations, daily records and project execution',
    description: 'Construction hub subtitle.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsHospitalTitle: {
    text: 'Hospital',
    description: 'Hospital sector hub page title.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSubtitle: {
    text: 'Facility operations, shifts, assets and statutory logs',
    description: 'Hospital hub subtitle.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsTabTodayDpr: {
    text: 'Today & DPR',
    description: 'Construction hub tab label for Today & DPR.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabMaterials: {
    text: 'Materials',
    description: 'Construction hub tab label for Materials.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabQuality: {
    text: 'Quality & Snags',
    description: 'Construction hub tab label for Quality & Snags.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabIssues: {
    text: 'Issues & Approvals',
    description: 'Construction hub tab label for Issues & Approvals.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabProjects: {
    text: 'Projects',
    description: 'Construction hub tab label for Projects.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabReports: {
    text: 'Reports',
    description: 'Construction hub tab label for Reports.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabDrawings: {
    text: 'Drawings',
    description: 'Construction hub tab label for Drawings.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabMeasurement: {
    text: 'Measurement & bill packs',
    description: 'Construction hub tab label for Measurement & bill packs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTabToday: {
    text: 'Today',
    description: 'Hospital hub tab label for Today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabRequests: {
    text: 'Requests',
    description: 'Hospital hub tab label for Requests.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabEquipment: {
    text: 'Equipment',
    description: 'Hospital hub tab label for Equipment.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabRegisters: {
    text: 'Registers',
    description: 'Hospital hub tab label for Registers.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabPeople: {
    text: 'People & Shifts',
    description: 'Hospital hub tab label for People & Shifts.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabBeds: {
    text: 'Bed board',
    description: 'Hospital hub tab label for Bed board.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabPatients: {
    text: 'Patient directory',
    description: 'Hospital hub tab label for Patient directory.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabPlaces: {
    text: 'Places & QR',
    description: 'Setup tab label for Places & QR.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabWorkforce: {
    text: 'Workforce',
    description: 'Setup tab label for Workforce.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabTemplates: {
    text: 'Templates',
    description: 'Setup tab label for Templates.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSchedules: {
    text: 'Schedules',
    description: 'Setup tab label for Schedules.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabRosters: {
    text: 'Rosters',
    description: 'Setup tab label for Rosters.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabAssets: {
    text: 'Assets',
    description: 'Setup tab label for Assets.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabCompliance: {
    text: 'Compliance',
    description: 'Setup tab label for Compliance.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabWhatsApp: {
    text: 'On WhatsApp',
    description: 'Setup tab label for On WhatsApp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabExport: {
    text: 'Export & retention',
    description: 'Setup tab label for Export & retention.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabDevices: {
    text: 'Shared devices',
    description: 'Setup tab label for Shared devices.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabPhoneAccess: {
    text: 'Phone access',
    description: 'Setup tab label for Phone access.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPhoneAccessTitle: {
    text: 'Phone access',
    description: 'Title for Phone access management screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneAccessSubtitle: {
    text: 'Control which phone items and flows appear for each role and member.',
    description: 'Subtitle for Phone access management screen.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsPhoneAccessSurfaceHelper: {
    text: 'Helper phone items',
    description: 'Heading for helper phone surface table.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPhoneAccessSurfaceMember: {
    text: 'Member phone items',
    description: 'Heading for member phone surface table.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPhoneAccessUseRecommended: {
    text: 'Use recommended set',
    description: 'Button label to apply recommended phone items for a role.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneAccessEmptyTitle: {
    text: 'No phone items enabled',
    description: 'Empty state title when no phone items are enabled in workspace.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPhoneAccessEmptyDescription: {
    text: 'Phone items stay hidden until allowed by an Owner or Admin. Use a recommended set above to get started.',
    description: 'Empty state description when no phone items are enabled.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSOpsPhoneAccessPeopleTitle: {
    text: 'Person overrides',
    description: 'Heading for person overrides section.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPhoneAccessPeopleSubtitle: {
    text: 'Grant extra items or restrict specific items for individual members.',
    description: 'Subtitle for person overrides section.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsPhoneAccessAddOverride: {
    text: 'Add person override',
    description: 'Button label to add a person override.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneAccessRemoveOverride: {
    text: 'Remove override',
    description: 'Button label to remove a person override.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneAccessEffectiveItems: {
    text: 'Effective items',
    description: 'Label for effective items list.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneAccessNoOverrides: {
    text: 'No person overrides configured.',
    description: 'Text shown when no person overrides are configured.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPhoneAccessItemAllowed: {
    text: 'Allowed',
    description: 'Status badge when item is explicitly allowed.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhoneAccessItemDenied: {
    text: 'Denied',
    description: 'Status badge when item is explicitly denied.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhoneAccessItemDefault: {
    text: 'Role default',
    description: 'Status badge when item follows role default.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhoneAccessPermissionDenied: {
    text: 'Only workspace Owners and Admins can manage phone access.',
    description: 'Error message when user lacks admin permissions.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneAccessColItem: {
    text: 'Item',
    description: 'Column header for phone items.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhoneAccessAllowHeading: {
    text: 'Allow',
    description: 'Heading for allowed items override.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhoneAccessDenyHeading: {
    text: 'Deny',
    description: 'Heading for denied items override.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhoneAccessNone: {
    text: 'none',
    description: 'Label indicating no items.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhoneAccessSelectPerson: {
    text: 'Select person',
    description: 'Placeholder and label for selecting workspace person.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPhoneAccessItemsToAllow: {
    text: 'Items to Allow (+)',
    description: 'Section title for items to allow in override dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPhoneAccessItemsToDeny: {
    text: 'Items to Deny (-)',
    description: 'Section title for items to deny in override dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPhoneAccessCancel: {
    text: 'Cancel',
    description: 'Cancel button label in override dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhoneAccessSave: {
    text: 'Save',
    description: 'Save button label in override dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhoneItemCheckIn: {
    text: 'Place check-in',
    description: 'Label for place QR check-in phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemCheckInDesc: {
    text: 'QR scan check-in and colleague peer mark at shops outlets.',
    description: 'Description for place check-in phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemDueChecks: {
    text: 'Due checks',
    description: 'Label for due checks and schedule scans.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemDueChecksDesc: {
    text: 'Due tasks list, checkpoint barcode scans, and skip recording.',
    description: 'Description for due checks phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemDpr: {
    text: 'Daily progress',
    description: 'Label for daily progress entry.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemDprDesc: {
    text: 'Daily site progress report and field activity logging.',
    description: 'Description for daily progress phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemInspections: {
    text: 'Inspections',
    description: 'Label for quality inspections phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemInspectionsDesc: {
    text: 'Quality inspection hold points, pour cards, and outcome recording.',
    description: 'Description for quality inspections phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemHindrance: {
    text: 'Hindrance',
    description: 'Label for hindrance raise phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemHindranceDesc: {
    text: 'Report a site obstacle, delay or blocker from the floor.',
    description: 'Description for hindrance phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemRoll: {
    text: 'Attendance roll',
    description: 'Label for attendance roll marking.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemRollDesc: {
    text: 'Headcount and roster shift attendance marking on floor.',
    description: 'Description for attendance roll phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemRequest: {
    text: 'Raise request',
    description: 'Label for raising operational requests.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemRequestDesc: {
    text: 'Raise maintenance, issue, or service request from phone.',
    description: 'Description for raise request phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemRoomClean: {
    text: 'Room cleaning',
    description: 'Label for room and bed cleaning.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemRoomCleanDesc: {
    text: 'Housekeeping start clean, pause, resume, and clean proof.',
    description: 'Description for room cleaning phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemFoundItem: {
    text: 'Log found item',
    description: 'Label for logging lost & found items.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemFoundItemDesc: {
    text: 'Capture found item details and bin label on site.',
    description: 'Description for logging found item phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemBookingCheckIn: {
    text: 'Booking check-in',
    description: 'Label for booking check-in by place QR.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemBookingCheckInDesc: {
    text: 'Check in for scheduled booking session at place QR.',
    description: 'Description for booking check-in phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemApprovals: {
    text: 'Approvals',
    description: 'Label for mobile approvals tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemApprovalsDesc: {
    text: 'Mobile queue for operational approvals and authorisations.',
    description: 'Description for mobile approvals tab.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemRoster: {
    text: 'Roster',
    description: 'Label for mobile roster view tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemRosterDesc: {
    text: 'Weekly roster, shifts, and relief assignments on mobile.',
    description: 'Description for mobile roster view tab.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemRegisters: {
    text: 'Registers',
    description: 'Label for mobile registers tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemRegistersDesc: {
    text: 'Mobile register forms and checklist submission.',
    description: 'Description for mobile registers tab.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemReports: {
    text: 'Reports',
    description: 'Label for mobile reports tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemReportsDesc: {
    text: 'Summary reports and KPIs optimized for phone.',
    description: 'Description for mobile reports tab.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemRequests: {
    text: 'Requests queue',
    description: 'Label for mobile requests queue.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemRequestsDesc: {
    text: 'View and action assigned operational requests on phone.',
    description: 'Description for mobile requests queue.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemExceptions: {
    text: 'Exceptions',
    description: 'Label for hospital exception inbox phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemExceptionsDesc: {
    text: 'Hospital exception inbox for missed dues, spills, and blocked beds.',
    description: 'Description for hospital exception inbox phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemEquipment: {
    text: 'Equipment',
    description: 'Label for equipment down now and release phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemEquipmentDesc: {
    text: 'Down now biomedical equipment queue and authorized release.',
    description: 'Description for equipment down now phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemBeds: {
    text: 'Bed board',
    description: 'Label for ward bed board phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemBedsDesc: {
    text: 'Ward bed board, state management, and discharge clean triggers.',
    description: 'Description for ward bed board phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemBmw: {
    text: 'BMW register',
    description: 'Label for helper biomedical waste entry phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemBmwDesc: {
    text: 'Daily biomedical waste category weights and storage timer status.',
    description: 'Description for helper biomedical waste entry phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemMemberBmw: {
    text: 'BMW register',
    description: 'Label for member biomedical waste register phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemMemberBmwDesc: {
    text: 'Daily biomedical waste category weights, corrections, and storage status.',
    description: 'Description for member biomedical waste register phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemOnCall: {
    text: 'On-call duties',
    description: 'Label for helper on-call schedule phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemOnCallDesc: {
    text: 'View upcoming on-call duty slots and acknowledge assignments.',
    description: 'Description for helper on-call schedule phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemMemberOnCall: {
    text: 'On-call duties',
    description: 'Label for member on-call schedule phone item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemMemberOnCallDesc: {
    text: 'View upcoming on-call duty slots and acknowledge assignments.',
    description: 'Description for member on-call schedule phone item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPhoneItemPlant: {
    text: 'Plant operations',
    description: 'Plant Helper operations catalog item.',
    surface: 'helper',
    maxChars: 30,
  },
  NLSOpsPhoneItemPlantDesc: {
    text: 'Line checks, machine scan and breakdown reporting.',
    description: 'Description for plant helper catalog item.',
    surface: 'helper',
    maxChars: 80,
  },
  NLSOpsPhoneItemMemberPlant: {
    text: 'Plant manager',
    description: 'Plant Member operations catalog item.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPhoneItemMemberPlantDesc: {
    text: 'Machine status, breakdown releases and downtime reports.',
    description: 'Description for plant member catalog item.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSharedDevicesTitle: {
    text: 'Shared devices',
    description: 'Title for Shared devices management screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDevicesSubtitle: {
    text: 'Enrol floor devices, manage idle lock, view sessions, and reset worker PINs.',
    description: 'Subtitle for Shared devices management screen.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsSharedDevicesEnrolTitle: {
    text: 'Enrol a shared device',
    description: 'Heading for device enrolment card.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDevicesEnrolPlace: {
    text: 'Place',
    description: 'Place selector label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesEnrolPlacePlaceholder: {
    text: 'Choose an active place',
    description: 'Placeholder for place selection.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDevicesEnrolLabel: {
    text: 'Device label',
    description: 'Label for device name input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDevicesEnrolLabelPlaceholder: {
    text: 'e.g. Reception Tablet 1',
    description: 'Placeholder for device label.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDevicesEnrolButton: {
    text: 'Generate code',
    description: 'Button to generate enrolment code.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSharedDevicesCodeTitle: {
    text: 'Enrolment code generated',
    description: 'Title for enrolment code display.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDevicesCodeHint: {
    text: 'Enter this 6-digit code on the shared device. Valid for 10 minutes.',
    description: 'Hint for enrolment code validity.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSharedDevicesCodeExpiry: {
    text: 'Expires at {time}',
    description: 'Expiry time indicator.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDevicesDeviceUrl: {
    text: 'Device URL',
    description: 'URL label for shared device route.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSharedDevicesCopyCode: {
    text: 'Copy code',
    description: 'Button to copy enrolment code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesCopyUrl: {
    text: 'Copy URL',
    description: 'Button to copy device URL.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesCopied: {
    text: 'Copied',
    description: 'Notification toast that content was copied.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesCloseCode: {
    text: 'Done',
    description: 'Button to close the code display panel.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsSharedDevicesListTitle: {
    text: 'Enrolled devices',
    description: 'Section heading for enrolled devices table.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDevicesColLabel: {
    text: 'Device',
    description: 'Column header for device label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesColPlace: {
    text: 'Place',
    description: 'Column header for device place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesColState: {
    text: 'Status',
    description: 'Column header for device state.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesColLastSeen: {
    text: 'Last seen',
    description: 'Column header for last active time.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesColIdle: {
    text: 'Idle lock',
    description: 'Column header for idle timeout.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesColActions: {
    text: 'Actions',
    description: 'Column header for actions.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesNeverSeen: {
    text: 'Never',
    description: 'Indicator when a device has never connected.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesStateActive: {
    text: 'Active',
    description: 'Status badge for active device.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesStateRevoked: {
    text: 'Revoked',
    description: 'Status badge for revoked device.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesEditSettings: {
    text: 'Edit',
    description: 'Action to edit device settings.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsSharedDevicesRevoke: {
    text: 'Revoke',
    description: 'Action to revoke device access.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesRevokeConfirmTitle: {
    text: 'Revoke shared device',
    description: 'Confirmation dialog title.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDevicesRevokeConfirmBody: {
    text: 'Are you sure you want to revoke {label}? It will be signed out immediately and cannot be used until enrolled again.',
    description: 'Confirmation dialog description.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSOpsSharedDevicesIdleMinutesUnit: {
    text: '{count} min',
    description: 'Minutes representation for idle timeout.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesSaveSettings: {
    text: 'Save',
    description: 'Button to save settings changes.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsSharedDevicesCancel: {
    text: 'Cancel',
    description: 'Cancel button label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsSharedDevicesEditTitle: {
    text: 'Edit device settings',
    description: 'Modal title for editing device settings.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDevicesSessionsTitle: {
    text: 'Sessions log',
    description: 'Heading for session history.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDevicesSessionsAllDevices: {
    text: 'All devices',
    description: 'Filter option for all devices.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesColPerson: {
    text: 'Person',
    description: 'Column header for session user.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesColStart: {
    text: 'Started',
    description: 'Column header for session start time.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesColEnd: {
    text: 'Ended',
    description: 'Column header for session end time.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesColReason: {
    text: 'End reason',
    description: 'Column header for session end reason.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesReasonSwitch: {
    text: 'Switched',
    description: 'Session ended due to manual switch.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesReasonIdle: {
    text: 'Idle timeout',
    description: 'Session ended due to inactivity.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesReasonSignOut: {
    text: 'Signed out',
    description: 'Session ended due to sign-out.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesReasonRevoked: {
    text: 'Device revoked',
    description: 'Session ended due to device revocation.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesActiveSession: {
    text: 'Active',
    description: 'Indicator for currently active session.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesResetPin: {
    text: 'Reset PIN',
    description: 'Button label to reset a user PIN.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDevicesResetPinConfirmTitle: {
    text: 'Reset user PIN',
    description: 'Confirmation dialog title for resetting user PIN.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDevicesResetPinConfirmBody: {
    text: 'Reset the shared-device PIN for {person}? They will need to set a new PIN via phone verification before switching in again.',
    description: 'Confirmation message for resetting user PIN.',
    surface: 'shell',
    maxChars: 150,
  },
  NLSOpsSharedDevicesNoDevices: {
    text: 'No shared devices enrolled yet.',
    description: 'Empty state message for devices list.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsSharedDevicesNoSessions: {
    text: 'No sessions recorded yet.',
    description: 'Empty state message for sessions log.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsSharedDeviceErrorEnrolFailed: {
    text: 'Failed to generate enrolment code.',
    description: 'Error toast when enrol fails.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceErrorSettingsFailed: {
    text: 'Failed to update device settings.',
    description: 'Error toast when settings update fails.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceErrorRevokeFailed: {
    text: 'Failed to revoke device.',
    description: 'Error toast when revoke fails.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceErrorResetPinFailed: {
    text: 'Failed to reset user PIN.',
    description: 'Error toast when reset PIN fails.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceResetPinSuccess: {
    text: 'User PIN has been reset.',
    description: 'Success toast for PIN reset.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDeviceRevokeSuccess: {
    text: 'Device revoked.',
    description: 'Success toast for device revoke.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDeviceSettingsSuccess: {
    text: 'Device settings updated.',
    description: 'Success toast for settings save.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSharedDeviceSetPinTitle: {
    text: 'Shared-device PIN',
    description: 'Setting title for shared device PIN.',
    surface: 'helper',
    maxChars: 30,
  },
  NLSOpsSharedDeviceSetPinDesc: {
    text: 'Set a 4-digit PIN to switch into shared floor devices without full sign-in.',
    description: 'Setting description for shared device PIN.',
    surface: 'helper',
    maxChars: 90,
  },
  NLSOpsSharedDeviceEnterPin: {
    text: 'Enter 4-digit PIN',
    description: 'PIN input prompt.',
    surface: 'helper',
    maxChars: 30,
  },
  NLSOpsSharedDeviceConfirmPin: {
    text: 'Confirm 4-digit PIN',
    description: 'Confirm PIN input prompt.',
    surface: 'helper',
    maxChars: 30,
  },
  NLSOpsSharedDevicePinMismatch: {
    text: 'PINs do not match.',
    description: 'Error when entered PINs differ.',
    surface: 'helper',
    maxChars: 40,
  },
  NLSOpsSharedDevicePinWeak: {
    text: 'PIN is too weak. Avoid repeated or sequential digits.',
    description: 'Error when PIN is weak.',
    surface: 'helper',
    maxChars: 60,
  },
  NLSOpsSharedDevicePinSetSuccess: {
    text: 'Shared-device PIN updated.',
    description: 'Success message when PIN is saved.',
    surface: 'helper',
    maxChars: 40,
  },
  NLSOpsSharedDevicePinSetFailed: {
    text: 'Failed to set PIN.',
    description: 'Error when PIN set fails.',
    surface: 'helper',
    maxChars: 40,
  },
  NLSOpsSharedDeviceShellTitle: {
    text: 'Shared device',
    description: 'Header title for shared device shell.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDeviceNotEnrolled: {
    text: 'Enrol this device',
    description: 'Title on unenrolled shared device.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDeviceEnterEnrolCode: {
    text: 'Enrolment code',
    description: 'Label for enrolment code field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSharedDeviceEnrolSubmit: {
    text: 'Connect device',
    description: 'Submit button to enrol device.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSharedDeviceSignOutExistingUser: {
    text: 'A user is currently signed in. Sign them out to enrol this shared device?',
    description: 'Prompt when existing user is signed in.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsSharedDevicePeopleTitle: {
    text: 'Who is working today?',
    description: 'Prompt to pick a worker.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDevicePinSetBadge: {
    text: 'PIN set',
    description: 'Badge indicating user has a PIN configured.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDeviceNoPinBadge: {
    text: 'No PIN',
    description: 'Badge indicating user has no PIN yet.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDeviceEnterPinPrompt: {
    text: 'Enter PIN for {name}',
    description: 'Prompt for entering PIN.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDeviceWrongPin: {
    text: 'Wrong PIN, {count} tries left',
    description: 'Error message on incorrect PIN.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDeviceLocked: {
    text: 'Locked for {minutes} minutes',
    description: 'Lockout warning message.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDeviceNoPinYet: {
    text: 'No PIN set yet. Verify phone to set your PIN.',
    description: 'Notice to trigger phone OTP for setting PIN.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceVerifyPhone: {
    text: 'Verify phone number',
    description: 'Title for phone OTP modal.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSharedDevicePhonePrompt: {
    text: 'Enter the phone number registered to your account:',
    description: 'Phone number input instruction.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsSharedDeviceSendOtp: {
    text: 'Send code',
    description: 'Button to send OTP code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSharedDeviceEnterOtp: {
    text: 'Verification code',
    description: 'Label for OTP input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSharedDeviceConfirmOtp: {
    text: 'Verify & continue',
    description: 'Button to confirm OTP.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSharedDeviceSwitchPerson: {
    text: 'Switch person',
    description: 'Button to switch out current user on shared device.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSharedDeviceOfflineConnect: {
    text: 'Connect to switch in',
    description: 'Banner when device is offline.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSharedDeviceRevoked: {
    text: 'This device has been revoked. Please enrol again.',
    description: 'Banner when device was revoked.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceNotEligible: {
    text: 'You are not eligible to work on this device today.',
    description: 'Notice when worker is not scheduled or rostered for this place.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceOfflinePinWrong: {
    text: 'Incorrect PIN.',
    description: 'Error message when offline PIN is wrong.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSharedDeviceOfflinePinExpired: {
    text: 'PIN expired. Sign in online first.',
    description: 'Error message when offline PIN verifier is expired.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceOfflinePinNoVerifier: {
    text: 'Offline sign-in unavailable. Sign in online first.',
    description: 'Error message when offline PIN verifier is not found.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceOfflinePinLocked: {
    text: 'Account locked until {time}.',
    description: 'Error message when offline account is locked out.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSharedDeviceClockNoRunning: {
    text: 'No active clock to stop.',
    description: 'Error when stopping clock offline with no running clock.',
    surface: 'helper',
    maxChars: 40,
  },
  NLSOpsTabBookings: {
    text: 'Bookings & Desk',
    description: 'Setup tab label for Bookings & Desk.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingsTitle: {
    text: 'Bookings & Desk',
    description: 'Title for Ops Bookings & Desk screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingsSubtitle: {
    text: 'Sessions, desk check-ins, queue tokens, customer memberships and plans.',
    description: 'Subtitle for Ops Bookings & Desk screen.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsBookingTabToday: {
    text: 'Today',
    description: 'Tab label for Today desk operations.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTabCalendar: {
    text: 'Calendar',
    description: 'Tab label for Calendar week view.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTabCustomers: {
    text: 'Customers',
    description: 'Tab label for Customers directory.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTabPlans: {
    text: 'Plans',
    description: 'Tab label for Booking Plans.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTabLists: {
    text: 'Lists',
    description: 'Tab label for Desk Follow-up Lists.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTabSettings: {
    text: 'Settings',
    description: 'Tab label for Booking Settings.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTodaySelectPlace: {
    text: 'Select place',
    description: 'Placeholder for place selection dropdown.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingTodayNoPlaceSelected: {
    text: 'Select a place above to view sessions and queue tokens.',
    description: 'Prompt when no place is selected in Today tab.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsBookingTodaySessionsTitle: {
    text: "Today's Sessions",
    description: 'Section heading for sessions today.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingTodayNoSessions: {
    text: 'No sessions scheduled for today at this place.',
    description: 'Empty state message when no sessions exist today.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingTodayTokensTitle: {
    text: 'Queue Tokens',
    description: 'Section heading for queue tokens.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingTodayIssueToken: {
    text: 'Issue Token',
    description: 'Action button to issue a queue token.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingTodayCallNext: {
    text: 'Call Next',
    description: 'Action button to call next queue token.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingTodayMarkServed: {
    text: 'Served',
    description: 'Action button to mark token served.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTodaySkipToken: {
    text: 'Skip',
    description: 'Action button to skip token.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTodayOpenBoard: {
    text: 'Token Board',
    description: 'Button to open full-screen waiting room token board.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingTodayWaitingCount: {
    text: '{count} waiting',
    description: 'Waiting count pill in token desk.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingTodayCurrentToken: {
    text: 'Calling Token #{n}',
    description: 'Current called token display.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsBookingTodayNoTokens: {
    text: 'No queue tokens active.',
    description: 'Empty state when no tokens in queue.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingHoldDilation: {
    text: 'Hold (Dilation)',
    description: 'Action button to place token on dilation hold.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingHoldObservation: {
    text: 'Hold (Observation)',
    description: 'Action button to place token on post-vaccine observation hold.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingTokensHeld: {
    text: 'On Hold',
    description: 'Header for held queue tokens.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTokenReady: {
    text: 'Ready',
    description: 'Badge for token whose hold has expired.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBookingResourceDoctor: {
    text: 'Doctor / Practitioner',
    description: 'Label for doctor resource picker.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingResourceRoom: {
    text: 'Room',
    description: 'Label for room resource picker.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingResourceAsset: {
    text: 'Machine / Asset',
    description: 'Label for machine or asset resource picker.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingSelectDoctor: {
    text: 'None / Select doctor',
    description: 'Option for selecting a doctor.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsBookingSelectRoom: {
    text: 'None / Select room',
    description: 'Option for selecting a room.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsBookingSelectAsset: {
    text: 'None / Select machine',
    description: 'Option for selecting a machine or asset.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsBookingIsolationChair: {
    text: 'Isolation Chair',
    description: 'Checkbox to designate chair as isolation chair.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingGroupIsolation: {
    text: 'Isolation Chairs',
    description: 'Group header for isolation chairs.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingGroupStandard: {
    text: 'Standard Slots',
    description: 'Group header for standard chairs or slots.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingAllowIsolationChange: {
    text: 'Allow moving between isolation and standard chairs',
    description: 'Confirmation checkbox allowing move across isolation barrier.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingAddRecurringChairSlot: {
    text: 'Add Recurring Chair Slot',
    description: 'Button to open recurring chair slot dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsBookingTodayCheckInScanner: {
    text: 'Scan QR',
    description: 'Button to open customer check-in QR scanner.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingTodayManualCheckIn: {
    text: 'Manual Check-in',
    description: 'Button for manual booking check-in.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingTodayDeskOtp: {
    text: 'Phone OTP',
    description: 'Button for desk phone OTP check-in.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCheckInSuccess: {
    text: 'Customer check-in confirmed.',
    description: 'Confirmation toast after successful check-in.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingManualReasonPrompt: {
    text: 'Reason for manual check-in (3–500 characters)',
    description: 'Prompt label for manual check-in reason.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingManualReasonInvalid: {
    text: 'Reason must be between 3 and 500 characters.',
    description: 'Validation error for manual check-in reason.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingDeskOtpPrompt: {
    text: 'Customer phone number for check-in verification',
    description: 'Label for customer phone input in desk OTP flow.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingDeskOtpSent: {
    text: 'OTP sent via SMS. Enter 6-digit verification code.',
    description: 'Notice once OTP is sent to customer.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingDeskOtpEnterCode: {
    text: '6-digit verification code',
    description: 'Placeholder for 6-digit OTP code.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingDeskOtpVerify: {
    text: 'Verify & Check In',
    description: 'Submit button for desk OTP check-in.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarBookSession: {
    text: 'Book Customer',
    description: 'Button to open booking modal.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCalendarAddBookable: {
    text: 'New Bookable',
    description: 'Button to create a new bookable service/slot.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCalendarEditBookable: {
    text: 'Edit Bookable',
    description: 'Button to edit bookable.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCalendarArchiveBookable: {
    text: 'Archive',
    description: 'Button to archive bookable.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarMoveSession: {
    text: 'Move Booking',
    description: 'Action to move a booking to another slot.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCalendarCancelSession: {
    text: 'Cancel Booking',
    description: 'Action to cancel a customer booking.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCalendarLeaveWaitlist: {
    text: 'Leave Waitlist',
    description: 'Action to remove customer from waitlist.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCalendarPrevWeek: {
    text: 'Previous Week',
    description: 'Calendar week navigation previous.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarNextWeek: {
    text: 'Next Week',
    description: 'Calendar week navigation next.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarThisWeek: {
    text: 'This Week',
    description: 'Calendar week navigation today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarCapacity: {
    text: '{booked} / {capacity} booked',
    description: 'Slot capacity ratio pill.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarWaitlist: {
    text: '{count} waitlist',
    description: 'Slot waitlist count pill.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarNoBookables: {
    text: 'No active bookables found.',
    description: 'Empty state when no bookables exist.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCalendarSessionDetails: {
    text: 'Session Details',
    description: 'Dialog title for session bookings.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCalendarNoBookings: {
    text: 'No bookings for this session.',
    description: 'Empty state for bookings list in session.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCalendarConfirmMove: {
    text: 'Confirm Move',
    description: 'Action button to confirm moving booking.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarJoinWaitlistIfFull: {
    text: 'Join waitlist if session is full',
    description: 'Checkbox label when moving booking.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCalendarTargetBookable: {
    text: 'Target Bookable',
    description: 'Field label for target bookable.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCalendarTargetDate: {
    text: 'Target Date',
    description: 'Field label for target date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarSelectCustomer: {
    text: 'Select Customer',
    description: 'Label to select customer.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarBookCustomer: {
    text: 'Book Customer',
    description: 'Button to book customer into session.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarSaveBookable: {
    text: 'Save Bookable',
    description: 'Button to save bookable.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarCancel: {
    text: 'Cancel',
    description: 'Cancel button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarNameLabel: {
    text: 'Name',
    description: 'Field label for bookable name.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarNamePlaceholder: {
    text: 'e.g. Morning Physiotherapy',
    description: 'Placeholder for bookable name.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCalendarKindLabel: {
    text: 'Kind',
    description: 'Field label for bookable kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarPlaceLabel: {
    text: 'Place',
    description: 'Field label for place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarCapacityLabel: {
    text: 'Capacity',
    description: 'Field label for bookable capacity.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarDurationLabel: {
    text: 'Duration (minutes)',
    description: 'Field label for bookable duration.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarStartTimeLabel: {
    text: 'Start Time',
    description: 'Field label for start time.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCalendarPublicLabel: {
    text: 'Available for public booking',
    description: 'Checkbox label for public booking availability.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCalendarWeekdaysLabel: {
    text: 'Weekdays',
    description: 'Label for weekdays selection.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarBookableSaved: {
    text: 'Bookable saved successfully.',
    description: 'Toast for saved bookable.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCalendarBookableArchived: {
    text: 'Bookable archived.',
    description: 'Toast for archived bookable.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarBookedSuccess: {
    text: 'Customer booked into session.',
    description: 'Toast for customer booked.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCalendarMoveSuccess: {
    text: 'Booking moved successfully.',
    description: 'Toast for moved booking.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCalendarCancelSuccess: {
    text: 'Booking cancelled.',
    description: 'Toast for cancelled booking.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarLeaveWaitlistSuccess: {
    text: 'Left waitlist.',
    description: 'Toast for leaving waitlist.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCalendarNameRequired: {
    text: 'Bookable name is required.',
    description: 'Validation error for missing name.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCalendarCapacityInvalid: {
    text: 'Capacity must be between 1 and 1000.',
    description: 'Validation error for capacity.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCalendarDurationInvalid: {
    text: 'Duration must be between 5 and 720 minutes.',
    description: 'Validation error for duration.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingCustomersSearch: {
    text: 'Search customers by name',
    description: 'Search placeholder for customers list.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCustomersPhoneLookup: {
    text: 'Phone lookup (exact E.164)',
    description: 'Input placeholder for desk customer phone search.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCustomersLookupButton: {
    text: 'Lookup',
    description: 'Button to trigger phone lookup.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBookingCustomersNewCustomer: {
    text: 'New Customer',
    description: 'Action to register a new customer.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomersEditCustomer: {
    text: 'Edit Customer',
    description: 'Action to edit customer profile.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomersArchive: {
    text: 'Archive',
    description: 'Action to archive customer.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersConsentWhatsapp: {
    text: 'Consent to WhatsApp notifications',
    description: 'Consent checkbox for WhatsApp.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCustomersConsentReminders: {
    text: 'Consent to SMS / app reminders',
    description: 'Consent checkbox for reminders.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCustomersMembershipsTitle: {
    text: 'Memberships & Packs',
    description: 'Section heading for customer memberships.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCustomersGrantMembership: {
    text: 'Grant Membership',
    description: 'Action button to assign a membership plan to customer.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCustomersRenewMembership: {
    text: 'Renew',
    description: 'Action to renew customer membership.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersFreezeMembership: {
    text: 'Freeze',
    description: 'Action to freeze customer membership.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersUnfreezeMembership: {
    text: 'Unfreeze',
    description: 'Action to unfreeze customer membership.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersCancelMembership: {
    text: 'Cancel',
    description: 'Action to cancel customer membership.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersLedgerTitle: {
    text: 'Membership Ledger',
    description: 'Section heading for membership ledger history.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCustomersDuesTitle: {
    text: 'Dues & Charges (Records Only)',
    description: 'Section heading for customer dues.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCustomersRecordPaid: {
    text: 'Record Paid',
    description: 'Action to record due as paid.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomersWaiveDue: {
    text: 'Waive',
    description: 'Action to waive due.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersReceiptRefPrompt: {
    text: 'Receipt or Transaction Reference (e.g. UPI / Cheque)',
    description: 'Receipt reference input placeholder.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingCustomersWaiveReasonPrompt: {
    text: 'Reason for waiving due (3–500 characters)',
    description: 'Waive due reason prompt.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingCustomersStrikesTitle: {
    text: 'Strikes & Booking Blocks',
    description: 'Section heading for strikes and blocks.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsBookingCustomersWaiveStrike: {
    text: 'Waive Strike',
    description: 'Action to waive a booking strike.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomersClearBlock: {
    text: 'Clear Block',
    description: 'Action for Owner to clear customer booking block.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomersBlockActive: {
    text: 'Booking block active until {date}',
    description: 'Notice of active booking block.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCustomersRecordOnlyNotice: {
    text: 'Records only — no financial transaction is processed.',
    description: 'Notice indicating pricing and dues are records only.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsBookingPlansTitle: {
    text: 'Booking Plans',
    description: 'Section heading for booking plans.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingPlansNewPlan: {
    text: 'New Plan',
    description: 'Action to create a booking plan.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingPlansEditPlan: {
    text: 'Edit Plan',
    description: 'Action to edit booking plan.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingPlansArchivePlan: {
    text: 'Archive',
    description: 'Action to archive booking plan.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingPlansKindMembership: {
    text: 'Membership (Time-based)',
    description: 'Plan kind Membership label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingPlansKindPack: {
    text: 'Session Pack',
    description: 'Plan kind Pack label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingPlansKindDropIn: {
    text: 'Drop-In',
    description: 'Plan kind Drop-In label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingPlansPriceRecordNotice: {
    text: 'Prices and fees are administrative records only, without payment gateway integration.',
    description: 'Disclaimer on booking plan pricing.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsBookingListsRenewalsTitle: {
    text: 'Renewals Due (Next 14 Days)',
    description: 'Heading for membership renewals list.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingListsChurnTitle: {
    text: 'Churn Risk Calls',
    description: 'Heading for customer churn follow-up list.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingListsRecordCall: {
    text: 'Record Call',
    description: 'Button to record call attempt.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingListsOutcomeReached: {
    text: 'Reached',
    description: 'Call outcome Reached.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingListsOutcomeNoAnswer: {
    text: 'No Answer',
    description: 'Call outcome No Answer.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingListsOutcomeNotInterested: {
    text: 'Not Interested',
    description: 'Call outcome Not Interested.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingListsOutcomeRenewing: {
    text: 'Renewing',
    description: 'Call outcome Renewing.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingListsCallNotes: {
    text: 'Call notes & outcome details',
    description: 'Notes input placeholder for customer calls.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingListsLastCall: {
    text: 'Last call: {date} ({outcome})',
    description: 'Summary line for last recorded customer call.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingSettingsPublicLinkTitle: {
    text: 'Public Booking Page',
    description: 'Heading for public booking link configuration.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingSettingsEnablePublicLink: {
    text: 'Enable Public Booking Page',
    description: 'Toggle label for public booking link.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsBookingSettingsCopyLink: {
    text: 'Copy Booking Link',
    description: 'Button to copy public booking link.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingSettingsLinkCopied: {
    text: 'Booking link copied to clipboard.',
    description: 'Confirmation toast when link is copied.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingSettingsPolicyDefaults: {
    text: 'Policy Defaults',
    description: 'Heading for policy defaults card.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingTokenBoardTitle: {
    text: 'Waiting Room Token Board',
    description: 'Title for Token Board display screen.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingTokenBoardClose: {
    text: 'Close Board',
    description: 'Button to close full-screen Token Board.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingTokenBoardCallingNow: {
    text: 'Now Calling',
    description: 'Heading for currently called token on board.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingTokenBoardWaiting: {
    text: 'Waiting in Queue',
    description: 'Heading for waiting tokens on board.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingTokenBoardNoCalling: {
    text: 'No token currently called',
    description: 'Placeholder when no token is active.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingTokenBoardNoWaiting: {
    text: 'Queue is currently empty',
    description: 'Placeholder when no tokens are waiting.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingWaitingRoomTitle: {
    text: 'Waiting Room Display',
    description: 'Title for waiting room full-screen board.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingWaitingRoomNextUp: {
    text: 'Next Up',
    description: 'Section heading for next tokens waiting in queue.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingWaitingRoomLive: {
    text: 'Live • Auto-updating',
    description: 'Status label indicating waiting room screen auto-updates in real time.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingWaitingRoomFullScreen: {
    text: 'Full Screen',
    description: 'Action to toggle full screen display mode.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingWaitingRoomExitFullScreen: {
    text: 'Exit Full Screen',
    description: 'Action to exit full screen mode.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsTodayActionWaitingRoom: {
    text: 'Waiting room display',
    description: 'Action button to open full-screen waiting room display.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPublicBookingTitle: {
    text: 'Book an Appointment',
    description: 'Title of public booking page.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPublicBookingSubtitle: {
    text: 'Select your session and confirm with mobile verification.',
    description: 'Subtitle of public booking page.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsPublicBookingSignInPrompt: {
    text: 'Enter your mobile number to get started',
    description: 'Sign-in prompt on public booking page.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPublicBookingSendOtp: {
    text: 'Send Verification Code',
    description: 'Button to send OTP on public booking page.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPublicBookingOtpPrompt: {
    text: 'Enter 6-digit verification code',
    description: 'OTP input prompt on public booking page.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPublicBookingVerifyOtp: {
    text: 'Verify & Continue',
    description: 'Verify button on public booking page.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPublicBookingConsent: {
    text: 'I consent to receive booking updates on WhatsApp and SMS reminders',
    description: 'Consent checkbox label on public booking page.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPublicBookingMyBookings: {
    text: 'My Upcoming Bookings',
    description: 'Heading for customer bookings on public page.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPublicBookingCancel: {
    text: 'Cancel Appointment',
    description: 'Button to cancel appointment on public page.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPublicBookingSuccess: {
    text: 'Appointment booked successfully!',
    description: 'Success message after public booking.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPublicBookingUnavailableTitle: {
    text: 'Public Booking Unavailable',
    description: 'Title when public booking link is disabled or invalid.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPublicBookingUnavailableDesc: {
    text: 'This public booking link is invalid or has been disabled. Please contact the administrator.',
    description: 'Description when public booking link is disabled.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsPublicBookingRateLimit: {
    text: 'Booking limit reached (maximum 5 bookings per day per phone). Please try again tomorrow.',
    description: 'Rate limit notice for public bookings.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsBookingErrorFailedPrecondition: {
    text: 'Booking request failed: precondition not met.',
    description: 'Error message for failed-precondition.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingErrorResourceExhausted: {
    text: 'Rate limit reached. Please try again later.',
    description: 'Error message for resource-exhausted.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingErrorPermissionDenied: {
    text: 'Permission denied for this booking operation.',
    description: 'Error message for permission-denied.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingErrorStrikesBlocked: {
    text: 'Customer has exceeded strike limit and is currently blocked from booking.',
    description: 'Error message for strikes block.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsBookingErrorGeneral: {
    text: 'An error occurred while processing the booking request.',
    description: 'General error message for booking operations.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsBookingNotAvailableRole: {
    text: 'Not available for your role.',
    description: 'Notice when sub-records are restricted for current role.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingNavTabsAria: {
    text: 'Bookings navigation tabs',
    description: 'Aria label for bookings tab strip.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingTodayManualReasonPlaceholder: {
    text: 'e.g. Scanner error / Customer forgot device',
    description: 'Placeholder for manual check-in reason.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingCustomersPhoneLabel: {
    text: 'Phone Number:',
    description: 'Label for customer phone number.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomersConsentLabel: {
    text: 'Consent:',
    description: 'Label for customer marketing / notification consent.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersNamePlaceholder: {
    text: 'e.g. Rahul Verma',
    description: 'Placeholder for customer full name.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCustomersSelectPlanAria: {
    text: 'Select Plan',
    description: 'Aria label for membership plan dropdown.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomersFreezeReasonPlaceholder: {
    text: 'e.g. Travel / Medical',
    description: 'Placeholder for freeze membership reason.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBookingCustomersReceiptPlaceholder: {
    text: 'UPI-1234567890',
    description: 'Placeholder for payment receipt reference.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCustomersWaiveReasonPlaceholder: {
    text: 'Reason for waiving due (3–500 chars)',
    description: 'Placeholder for waiving due reason.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCustomersWaiveStrikePlaceholder: {
    text: 'Customer provided doctor note / emergency',
    description: 'Placeholder for strike waiver reason.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingCustomersUnblockPlaceholder: {
    text: 'Authorized unblock after in-person review',
    description: 'Placeholder for clearing customer block.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingCommonCancel: {
    text: 'Cancel',
    description: 'Generic cancel action label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBookingCustomersSaveCustomer: {
    text: 'Save Customer',
    description: 'Save customer dialog action.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingPlansSavePlan: {
    text: 'Save Plan',
    description: 'Save booking plan dialog action.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersFullNameLabel: {
    text: 'Full Name',
    description: 'Label for full name input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersPhoneInputLabel: {
    text: 'Phone Number',
    description: 'Label for phone input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersFreezeDaysLabel: {
    text: 'Freeze Days (1–180)',
    description: 'Label for freeze days input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomersReasonOptional: {
    text: 'Reason (optional)',
    description: 'Label for optional reason input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomersConfirmPayment: {
    text: 'Confirm Payment Record',
    description: 'Button to confirm offline payment record.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingCustomersConfirmWaive: {
    text: 'Confirm Waive',
    description: 'Button to confirm due waiver.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingCustomersWaiveStrikeReasonPrompt: {
    text: 'Reason for waiving strike (3–500 characters)',
    description: 'Prompt for strike waiver reason.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingCustomersClearBlockReasonPrompt: {
    text: 'Owner authorization reason (3–500 characters)',
    description: 'Prompt for owner block clearance reason.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingPlansRecordOnly: {
    text: '(record only)',
    description: 'Notice that plan due is an offline record only.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingPlansNamePlaceholder: {
    text: 'e.g. Standard Monthly Membership',
    description: 'Placeholder for plan name.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsBookingPlansKindAria: {
    text: 'Kind',
    description: 'Aria label for plan kind selection.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingPlansLateCancelLabel: {
    text: 'Late cancellation consumes session credit',
    description: 'Label for late cancellation checkbox.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBookingListsViewAria: {
    text: 'Lists View',
    description: 'Aria label for lists tab view toggle.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingListsChurnAria: {
    text: 'Churn Period',
    description: 'Aria label for churn period filter.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingListsLoading: {
    text: 'Loading list data...',
    description: 'Loading message in lists tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingListsSaveCall: {
    text: 'Save Call',
    description: 'Label for saving call outcome.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBookingListsOutcomeAria: {
    text: 'Call Outcome',
    description: 'Aria label for call outcome select.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingListsOutcomeLabel: {
    text: 'Call Outcome',
    description: 'Label for call outcome field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingListsCallNotesPlaceholder: {
    text: 'Notes from customer discussion...',
    description: 'Placeholder for call notes.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsBookingListsChurn14: {
    text: '14 days',
    description: '14 days filter option.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBookingListsChurn30: {
    text: '30 days',
    description: '30 days filter option.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBookingListsRefresh: {
    text: 'Refresh',
    description: 'Button to refresh lists.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBookingListsEmptyRenewals: {
    text: 'No memberships due for renewal in the next 14 days.',
    description: 'Empty state for renewals list.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBookingSettingsOwnerOnly: {
    text: 'Only workspace Owner and Admins can configure public booking links.',
    description: 'Notice that settings are Owner and Admin restricted.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsBookingSettingsPublicUrlLabel: {
    text: 'Public Booking URL:',
    description: 'Label for public booking URL.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingSettingsPublicUrlFallback: {
    text: 'Link active. (Token minted upon initial generation — re-enable to show new link)',
    description: 'Fallback text when public booking URL token is not in state.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsBookingSettingsEnablePublicLinkDesc: {
    text: 'Allow customers to view open sessions and book appointments without signing into the workspace.',
    description: 'Description for enabling public booking links.',
    surface: 'shell',
    maxChars: 110,
  },
  NLSOpsBookingSettingsStrikePolicyDesc: {
    text: '3 unexcused no-shows result in an automatic booking block. Strikes can be waived by desk staff, and blocks cleared by Workspace Owners.',
    description: 'Description of strike policy.',
    surface: 'shell',
    maxChars: 150,
  },
  NLSOpsBookingSettingsRateLimitsDesc: {
    text: 'Up to 5 bookings per mobile phone per day; up to 60 public bookings per workspace per hour.',
    description: 'Description of public booking rate limits.',
    surface: 'shell',
    maxChars: 110,
  },
  NLSOpsBookingSettingsWaitlistPromoDesc: {
    text: 'When a booked customer cancels, the next waitlist entry is automatically granted a reservation hold.',
    description: 'Description of waitlist promotion.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsBookingSettingsAdminRecordsDesc: {
    text: 'All dues, plan fees, and recorded payments are administrative logs only, operating without direct payment gateway integration.',
    description: 'Description of administrative pricing and fee records.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSOpsBookingSettingsStrikePolicyLabel: {
    text: 'Strike Policy:',
    description: 'Label for strike policy section in settings.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingSettingsRateLimitsLabel: {
    text: 'Rate Limits:',
    description: 'Label for rate limits section in settings.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingSettingsWaitlistPromoLabel: {
    text: 'Waitlist Promotion:',
    description: 'Label for waitlist promo section in settings.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBookingSettingsAdminRecordsLabel: {
    text: 'Administrative Records:',
    description: 'Label for admin records section in settings.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsBookingTokenBoardNotUpdating: {
    text: 'Board not updating',
    description: 'Warning displayed when token board polling fails consecutively.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSPublicBookingLoadingDetails: {
    text: 'Loading booking details...',
    description: 'Loading message on public booking page.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSPublicBookingMobileNumberLabel: {
    text: 'Mobile Number',
    description: 'Label for phone number input on public booking page.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSPublicBookingAvailableSessionsTitle: {
    text: 'Available Sessions',
    description: 'Section header for available booking sessions.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSPublicBookingReminderOptInLabel: {
    text: 'Receive reminder notifications before the scheduled session',
    description: 'Label for reminder opt-in checkbox.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSPublicBookingNoDates: {
    text: 'No open dates available',
    description: 'Message when no sessions are open for booking.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSPublicBookingSeatsLeft: {
    text: '{count} left',
    description: 'Indicator for remaining seats in a session.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSPublicBookingFull: {
    text: 'Full',
    description: 'Indicator when a session is full.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSPublicBookingCodeSent: {
    text: 'Verification code sent to {phone}.',
    description: 'Confirmation that verification code was sent to phone.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSPublicBookingChangePhone: {
    text: 'Change phone number',
    description: 'Button to change phone number during OTP flow.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSPublicBookingConfirmBooking: {
    text: 'Confirm Booking',
    description: 'Button to confirm booking appointment.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSPublicBookingNoUpcoming: {
    text: 'You have no upcoming appointments.',
    description: 'Empty state message for upcoming bookings.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSPublicBookingSessionFallback: {
    text: 'Session',
    description: 'Fallback title for a booking session.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSPublicBookingStatusLabel: {
    text: 'Status:',
    description: 'Label for booking status.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceTitle: {
    text: 'Export & retention',
    description: 'Page title for Ops evidence export and retention.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceSubtitle: {
    text: 'Evidence export = data + attachments + manifest with hashes + correction history (SO-P13)',
    description: 'Subtitle for Ops evidence export and retention.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsEvidenceNoticeBanner: {
    text: '{count} records reach their keep-for date in the next 30 days',
    description: 'Banner notice for upcoming record purges.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsEvidenceNewExportTitle: {
    text: 'New export',
    description: 'Card title for new evidence export form.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceScopeLabel: {
    text: 'Places',
    description: 'Label for place selection in export.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceAllPlaces: {
    text: 'All places I can read',
    description: 'Default text when no places are filtered.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsEvidenceFromDate: {
    text: 'From',
    description: 'Label for start date.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceToDate: {
    text: 'To',
    description: 'Label for end date.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceDateRangeTooWide: {
    text: 'Date range must be 3 years or less',
    description: 'Validation error when export date range exceeds 3 years.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsEvidenceDateRangeInvalid: {
    text: 'From date must be on or before To date',
    description: 'Validation error when export date range is inverted.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsEvidenceKindsLabel: {
    text: 'Kinds',
    description: 'Label for evidence kind selection.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceRestrictedSuffix: {
    text: '(restricted)',
    description: 'Badge text indicating a restricted evidence kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceStartExport: {
    text: 'Start export',
    description: 'Button text to start export.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceExportStarting: {
    text: 'Starting export...',
    description: 'Button text while export request is in flight.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceLimitReached: {
    text: 'Export limit reached (at most one active export per user, 10 per workspace per day).',
    description: 'Error message when quota is reached.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsEvidenceModuleDisabled: {
    text: 'Export failed: sector module is not enabled.',
    description: 'Error message when module is off.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsEvidencePermissionDenied: {
    text: 'You do not have permission to export evidence for this sector.',
    description: 'Error message for permission denial.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsEvidenceExportsListTitle: {
    text: 'Recent exports',
    description: 'Section title for past export jobs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceNoExports: {
    text: 'No evidence exports requested yet.',
    description: 'Empty state message for exports list.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsEvidenceStatusQueued: {
    text: 'Queued',
    description: 'Pill text for queued export state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceStatusRunning: {
    text: 'Running',
    description: 'Pill text for running export state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceStatusReady: {
    text: 'Ready',
    description: 'Pill text for ready export state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceStatusFailed: {
    text: 'Failed',
    description: 'Pill text for failed export state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceStatusExpired: {
    text: 'Expired',
    description: 'Pill text for expired export state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceDownload: {
    text: 'Download',
    description: 'Button text to download export files.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceExpiresIn: {
    text: 'Expires in {days}d',
    description: 'Expiry countdown badge.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceExpired: {
    text: 'Expired',
    description: 'Notice that export links have expired.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceSigningNotConfigured: {
    text: "Signing isn't set up yet. Ask an Owner.",
    description: 'Error message when signing key is missing.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsEvidenceHowToVerify: {
    text: 'How to verify',
    description: 'Link text to open public verification page.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceRetentionTitle: {
    text: 'Retention by record class',
    description: 'Card title for retention settings.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsEvidenceRetentionSubtitle: {
    text: 'Starter values say "check for your state"; legal hold overrides deletion.',
    description: 'Footnote for retention settings.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsEvidenceCheckYourState: {
    text: 'check for your state',
    description: 'Label indicating state-specific statutory check needed.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceSourceCited: {
    text: 'source cited',
    description: 'Label indicating statutory source cited.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceKeepDaysLabel: {
    text: 'Keep for (days)',
    description: 'Label for keep duration in days.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceShortenReasonLabel: {
    text: 'Reason for shortening below starter (3–500 characters)',
    description: 'Input label when shortening retention below starter.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsEvidenceSaveRetention: {
    text: 'Save retention settings',
    description: 'Button text to save retention settings.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsEvidenceLegalHoldsTitle: {
    text: 'Legal holds',
    description: 'Card title for legal holds list.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceSetLegalHold: {
    text: 'Set legal hold',
    description: 'Button text to open set legal hold modal.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceHoldTargetLabel: {
    text: 'Target',
    description: 'Target selection label for legal hold.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceHoldTargetPlace: {
    text: 'Place',
    description: 'Option for place legal hold.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceHoldTargetKind: {
    text: 'Kind',
    description: 'Option for kind legal hold.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceHoldTargetRecord: {
    text: 'Record',
    description: 'Option for specific record legal hold.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceHoldReasonLabel: {
    text: 'Reason (3–500 characters)',
    description: 'Reason input label for legal hold.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsEvidenceHoldReasonPlaceholder: {
    text: 'Enter reason for legal hold...',
    description: 'Placeholder for legal hold reason.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsEvidenceRequestLift: {
    text: 'Request lift',
    description: 'Button text to request lifting a legal hold.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceConfirmLift: {
    text: 'Confirm lift',
    description: 'Button text to confirm lifting a legal hold.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceCancelLift: {
    text: 'Cancel lift',
    description: 'Button text to cancel a pending lift request.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceAskerCannotConfirm: {
    text: 'Cannot confirm your own lift request; another Owner or Admin must confirm.',
    description: 'Warning that hold asker cannot self-confirm lift.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsEvidenceSoleOwnerCountdown: {
    text: 'Sole owner lift available in {hours}h',
    description: 'Countdown badge for sole owner lift delay.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsEvidenceRetentionBinTitle: {
    text: 'Retention bin',
    description: 'Card title for retention bin.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceRestoreBin: {
    text: 'Restore',
    description: 'Button text to restore record from retention bin.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceRestoreConfirmTitle: {
    text: 'Restore record from retention bin',
    description: 'Confirmation dialog title for restoring binned record.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsEvidenceRestoreConfirmBody: {
    text: 'This will restore the record and extend retention for 90 days. Proceed?',
    description: 'Confirmation dialog body for restoring binned record.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsEvidencePurgeCountdown: {
    text: 'Purges in {days}d',
    description: 'Countdown until permanent purge.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceNoBinEntries: {
    text: 'Retention bin is empty.',
    description: 'Empty state message for retention bin.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsEvidenceNoHolds: {
    text: 'No active legal holds.',
    description: 'Empty state message for legal holds.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsOfflinePrivacyCardTitle: {
    text: 'Offline and privacy',
    description: 'Card title for offline cache expiry settings.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsOfflinePrivacyCardSubtitle: {
    text: 'Configure device cache lifetime before automatic wipe.',
    description: 'Card subtitle for offline cache expiry.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsOfflineMaxDaysLabel: {
    text: 'Offline cache limit (days)',
    description: 'Label for offline max days input.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsOfflineMaxDaysHelp: {
    text: '1 to 30 days. Stale device cache is wiped if offline longer than this window.',
    description: 'Help text for offline max days.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsOfflinePrivacySaveButton: {
    text: 'Save offline limit',
    description: 'Button text to save offline privacy settings.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsOfflinePrivacySaving: {
    text: 'Saving...',
    description: 'Saving state text for offline privacy settings.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsOfflinePrivacySaved: {
    text: 'Offline privacy settings saved.',
    description: 'Success message after saving offline privacy settings.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsOfflinePrivacySaveError: {
    text: 'Could not save offline privacy settings.',
    description: 'Error message when saving offline privacy settings fails.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsOfflineOutboxExpired: {
    text: 'Update expired: device was offline longer than the allowed policy window.',
    description: 'Status message for an expired outbox mutation.',
    surface: 'shell',
    maxChars: 95,
  },
  NLSSessionRevokedMessage: {
    text: 'Your session was revoked by an administrator. Please sign in again.',
    description: 'Sign-in message when session or token is revoked.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSVerifyEvidenceTitle: {
    text: 'Verify evidence export',
    description: 'Title for the public evidence export verification page.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidenceSubtitle: {
    text: 'Verify integrity and authenticity of evidence exports using signed manifests',
    description: 'Subtitle for evidence verification page.',
    surface: 'common',
    maxChars: 90,
  },
  NLSVerifyEvidencePickManifest: {
    text: 'Select manifest.json',
    description: 'Label to upload or select manifest.json file.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidencePickSignature: {
    text: 'Select manifest.sig',
    description: 'Label to upload or select manifest.sig file.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidenceValid: {
    text: 'Valid export',
    description: 'Status when manifest signature is verified.',
    surface: 'common',
    maxChars: 20,
  },
  NLSVerifyEvidenceInvalid: {
    text: 'Not valid',
    description: 'Status when manifest signature verification fails.',
    surface: 'common',
    maxChars: 20,
  },
  NLSVerifyEvidenceMissingKeyVersion: {
    text: 'Manifest is missing keyVersion. Cannot determine signing key.',
    description: 'Error when manifest lacks keyVersion.',
    surface: 'common',
    maxChars: 80,
  },
  NLSVerifyEvidenceFetchingKey: {
    text: 'Fetching verification key...',
    description: 'Status while downloading public key.',
    surface: 'common',
    maxChars: 40,
  },
  NLSVerifyEvidenceKeyFetchFailed: {
    text: 'Failed to fetch verification public key.',
    description: 'Error when public key cannot be retrieved.',
    surface: 'common',
    maxChars: 50,
  },
  NLSVerifyEvidenceVerifying: {
    text: 'Verifying signature...',
    description: 'Status while verifying cryptographic signature.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidenceWorkspace: {
    text: 'Workspace',
    description: 'Workspace label in verification summary.',
    surface: 'common',
    maxChars: 20,
  },
  NLSVerifyEvidencePeriod: {
    text: 'Period',
    description: 'Period label in verification summary.',
    surface: 'common',
    maxChars: 20,
  },
  NLSVerifyEvidenceGenerated: {
    text: 'Generated',
    description: 'Generated time label in verification summary.',
    surface: 'common',
    maxChars: 20,
  },
  NLSVerifyEvidenceFiles: {
    text: 'Files in export',
    description: 'Heading for file table.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidenceOfflineInstructions: {
    text: 'Check each part offline',
    description: 'Label for offline shasum command instructions.',
    surface: 'common',
    maxChars: 40,
  },
  NLSVerifyEvidenceCopyOffline: {
    text: 'Copy check command',
    description: 'Button text to copy offline shasum check command.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidenceCopied: {
    text: 'Copied!',
    description: 'Tooltip or feedback when command copied.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOpsEvidenceSource_ops_records: {
    text: 'Daily records',
    description: 'Evidence source label for daily records.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceSource_ops_requests: {
    text: 'Requests & tasks',
    description: 'Evidence source label for requests and tasks.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceSource_ops_compliance_items: {
    text: 'Compliance',
    description: 'Evidence source label for compliance items.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceSource_ops_assets: {
    text: 'Assets',
    description: 'Evidence source label for assets.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceSource_ops_approvals: {
    text: 'Approvals',
    description: 'Evidence source label for approvals.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceSource_ops_attendance: {
    text: 'Attendance',
    description: 'Evidence source label for attendance.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceSource_ops_rosters: {
    text: 'Rosters',
    description: 'Evidence source label for rosters.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceSource_ops_wage_sheets: {
    text: 'Wage sheets',
    description: 'Evidence source label for wage sheets.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceRestrictedKind_INCIDENT: {
    text: 'Incidents',
    description: 'Restricted kind name for incident reports.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceRestrictedKind_COMPLAINT: {
    text: 'Complaints',
    description: 'Restricted kind name for complaints.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceRestrictedKind_STAFF_HEALTH: {
    text: 'Staff health',
    description: 'Restricted kind name for staff health records.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceRestrictedKind_PATIENT_DIRECTORY: {
    text: 'Patient directory',
    description: 'Restricted kind name for patient directory entries.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceHoldReasonRange: {
    text: 'Reason must be between 3 and 500 characters.',
    description: 'Validation error when legal hold reason is invalid length.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsEvidenceHoldSelectPlace: {
    text: 'Please select a place.',
    description: 'Validation error when place is not selected for legal hold.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsEvidenceHoldKindKeyRequired: {
    text: 'Kind key is required.',
    description: 'Validation error when kind key is missing for legal hold.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsEvidenceHoldRecordIdRequired: {
    text: 'Record ID is required.',
    description: 'Validation error when record ID is missing for legal hold.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsEvidenceHoldLiftReasonRange: {
    text: 'Lift reason must be between 3 and 500 characters.',
    description: 'Validation error when hold lift reason is invalid length.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsEvidenceHoldTargetKindLabel: {
    text: 'Kind key',
    description: 'Field label for legal hold kind key.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceHoldTargetRecordSource: {
    text: 'Source collection',
    description: 'Field label for legal hold source collection.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceHoldTargetRecordId: {
    text: 'Record ID',
    description: 'Field label for legal hold record ID.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceHoldLiftReasonDialogTitle: {
    text: 'Request legal hold lift',
    description: 'Dialog title for requesting a legal hold lift.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsEvidenceHoldLiftReasonDialogBody: {
    text: 'Provide the legal or operational reason for requesting this hold to be lifted.',
    description: 'Dialog body for requesting a legal hold lift.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsEvidenceHoldSubmit: {
    text: 'Set hold',
    description: 'Button text to submit new legal hold.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceHoldCancel: {
    text: 'Cancel',
    description: 'Button text to cancel dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceHoldStateActive: {
    text: 'Active',
    description: 'Status label for active legal hold.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceHoldStateLiftPending: {
    text: 'Lift pending',
    description: 'Status label for lift pending legal hold.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceHoldStateLifted: {
    text: 'Lifted',
    description: 'Status label for lifted legal hold.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceHoldLiftRequestedBy: {
    text: 'Lift requested by: {user} ({reason})',
    description: 'Details of lift request.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsEvidenceHoldSetBy: {
    text: 'Set by: {user}',
    description: 'Creator details of legal hold.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsEvidenceHoldTableTarget: {
    text: 'Target',
    description: 'Table header for legal hold target.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceHoldTableReason: {
    text: 'Reason',
    description: 'Table header for legal hold reason.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceHoldTableState: {
    text: 'State',
    description: 'Table header for legal hold state.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceHoldTableActions: {
    text: 'Actions',
    description: 'Table header for legal hold actions.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceRetentionShortenReasonRange: {
    text: 'Reason between 3 and 500 characters is required when shortening below starter duration.',
    description: 'Validation error when shortening retention period without reason.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsEvidenceRetentionKeepDaysRange: {
    text: 'Keep days must be between 30 and 36500.',
    description: 'Validation error when keep days is out of allowed range.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsEvidenceRetentionTableClass: {
    text: 'Record class',
    description: 'Table header for record class.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceRetentionTableKeepFor: {
    text: 'Keep duration',
    description: 'Table header for keep duration.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceRetentionTableSource: {
    text: 'Regulatory source',
    description: 'Table header for statutory source.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceRetentionTableActions: {
    text: 'Actions',
    description: 'Table header for actions.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceRetentionEdit: {
    text: 'Edit',
    description: 'Button text to edit retention settings.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceRetentionCancel: {
    text: 'Cancel',
    description: 'Button text to cancel retention editing.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceRetentionDaysSuffix: {
    text: '{days} days',
    description: 'Duration formatted in days.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceRetentionShortenReasonPlaceholder: {
    text: 'State justification reason...',
    description: 'Placeholder for reason when shortening retention duration.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsEvidenceHoldTargetKindPlaceholder: {
    text: 'e.g. RECORD:INCIDENT or ASSET',
    description: 'Placeholder for legal hold kind key input.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsEvidenceHoldTargetRecordIdPlaceholder: {
    text: 'Document ID',
    description: 'Placeholder for legal hold record document ID.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEvidenceHoldLiftReasonPlaceholder: {
    text: 'State why dispute or audit is settled...',
    description: 'Placeholder for reason when requesting legal hold lift.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsEvidenceHoldLiftReasonLabel: {
    text: 'Reason for lifting hold (3–500 chars)',
    description: 'Label for legal hold lift reason field.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsEvidenceHoldRequestSubmit: {
    text: 'Submit request',
    description: 'Button text to submit hold lift request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceBinTableRecord: {
    text: 'Record',
    description: 'Table header for binned record label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceBinTableSource: {
    text: 'Source',
    description: 'Table header for binned record source.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceBinTableBinned: {
    text: 'Binned at',
    description: 'Table header for binned date.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceBinTablePurge: {
    text: 'Purge countdown',
    description: 'Table header for purge countdown.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEvidenceBinTableActions: {
    text: 'Actions',
    description: 'Table header for bin actions.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsEvidenceBinRestore: {
    text: 'Restore',
    description: 'Button text to restore binned record.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceBinCancel: {
    text: 'Cancel',
    description: 'Button text to cancel restore dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsEvidenceBinShowingLimit: {
    text: 'Showing the first {count} entries.',
    description: 'Notice when bin entries count hits limit.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsEvidenceBinActionError: {
    text: 'Failed to restore record: {error}',
    description: 'Error message on bin restore failure.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsEvidenceLockChipHeld: {
    text: 'Held under legal hold · {classKey}',
    description: 'Status chip text when a record is subject to an active legal hold.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsEvidenceLockChipLocked: {
    text: 'Kept until {date} · {classKey}',
    description: 'Status chip text when a record is locked under statutory retention.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsEvidenceLegalMinimum: {
    text: 'Legal minimum: {days} days',
    description: 'Note indicating statutory minimum retention floor for a class.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsEvidenceUnconfirmedCheckState: {
    text: 'unconfirmed, check for your state',
    description: 'Badge indicating statutory retention period is unconfirmed and varies by state jurisdiction.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsEvidenceBelowFloorError: {
    text: 'Retention period for {key} cannot be set below legal floor of {floor} days.',
    description: 'Error message when user attempts to set retention below statutory floor.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSDeleteWorkspaceRetentionLockedNotice: {
    text: '{count} records will be kept sealed in an archive until {date}, as the law requires.',
    description: 'Notice in workspace deletion dialog when statutory records must be sealed.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSSettingsSectionSealedArchives: {
    text: 'Sealed legal archives',
    description: 'Account settings section title for sealed archives.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSSettingsSealedArchivesDescription: {
    text: 'Statutory records retained under legal lock from deleted workspaces.',
    description: 'Description of sealed archives in user settings.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSSettingsNoSealedArchives: {
    text: 'No sealed archives found for your account.',
    description: 'Empty state message when user has no sealed archives.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSSettingsSealedArchiveLockedCount: {
    text: '{count} locked records',
    description: 'Count of records locked in sealed archive.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSSettingsSealedArchiveNextPurge: {
    text: 'Next legal purge: {date}',
    description: 'Date of next scheduled purge in sealed archive.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSSettingsSealedArchiveStatusSealed: {
    text: 'Sealed',
    description: 'State label when archive is actively sealed.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSSettingsSealedArchiveStatusEmptied: {
    text: 'Emptied',
    description: 'State label when archive retention periods have fully expired.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSSettingsSealedArchiveRequestExport: {
    text: 'Request export',
    description: 'Button label to trigger export of sealed archive.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSSettingsSealedArchiveExportRequested: {
    text: 'Export requested',
    description: 'Status label while sealed archive export is generating.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSSettingsSealedArchiveDownload: {
    text: 'Download export',
    description: 'Button label to download sealed archive export package.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSVerifyEvidenceManifestDetails: {
    text: 'Manifest details',
    description: 'Section heading for manifest metadata.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidencePartsTableTitle: {
    text: 'Export parts and files',
    description: 'Section heading for files table.',
    surface: 'common',
    maxChars: 35,
  },
  NLSVerifyEvidenceTablePart: {
    text: 'Part',
    description: 'Table header for export part number.',
    surface: 'common',
    maxChars: 15,
  },
  NLSVerifyEvidenceTablePath: {
    text: 'File path',
    description: 'Table header for file path.',
    surface: 'common',
    maxChars: 20,
  },
  NLSVerifyEvidenceTableSize: {
    text: 'Size',
    description: 'Table header for file size.',
    surface: 'common',
    maxChars: 15,
  },
  NLSVerifyEvidenceTableSha256: {
    text: 'SHA-256',
    description: 'Table header for sha256 checksum.',
    surface: 'common',
    maxChars: 15,
  },
  NLSVerifyEvidenceChecksumTitle: {
    text: 'Offline verification',
    description: 'Section heading for offline checksum check.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidenceChecksumHelp: {
    text: 'Unzip all export parts into one directory, download SHA256SUMS into that directory, and run the command to verify all files.',
    description: 'Explanation for offline checksum verification.',
    surface: 'common',
    maxChars: 160,
  },
  NLSVerifyEvidenceDownloadChecksums: {
    text: 'Download SHA256SUMS',
    description: 'Button text to download SHA256SUMS file.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidenceChecksumCommandLabel: {
    text: 'Verification command',
    description: 'Label for shasum command snippet.',
    surface: 'common',
    maxChars: 30,
  },
  NLSVerifyEvidenceMissingFiles: {
    text: 'Please select both manifest.json and manifest.sig files.',
    description: 'Error when either manifest or signature is missing.',
    surface: 'common',
    maxChars: 80,
  },
  NLSVerifyEvidenceInvalidJson: {
    text: 'Failed to parse manifest.json. Ensure it is valid JSON.',
    description: 'Error when manifest is not valid JSON.',
    surface: 'common',
    maxChars: 80,
  },
  NLSVerifyEvidenceSignatureMismatch: {
    text: 'Digital signature did not match manifest content.',
    description: 'Error when signature is cryptographically invalid.',
    surface: 'common',
    maxChars: 80,
  },
  NLSVerifyEvidenceFailedGeneric: {
    text: 'Verification failed: {reason}',
    description: 'Generic error during verification.',
    surface: 'common',
    maxChars: 80,
  },
  NLSVerifyEvidenceKeyId: {
    text: 'Key version',
    description: 'Label for signing key version.',
    surface: 'common',
    maxChars: 20,
  },
  NLSVerifyEvidenceInvalidManifestFiles: {
    text: 'Manifest file entries are invalid: paths must be relative and checksums must be 64-character hexadecimal hashes.',
    description: 'Error when manifest contains invalid or dangerous file paths or hashes.',
    surface: 'common',
    maxChars: 120,
  },
  NLSVerifyEvidenceSelectFilesPrompt: {
    text: 'Select export files to verify',
    description: 'Heading for export file pickers on verification page.',
    surface: 'common',
    maxChars: 40,
  },
  NLSVerifyEvidenceVerifyButton: {
    text: 'Verify',
    description: 'Button text to start evidence verification.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOpsEvidenceScopePlacesSelected: {
    text: '{count} places selected',
    description: 'Count of selected places for export.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSectionsAria: {
    text: 'Operations sections',
    description: 'Aria label for operations section chip tabs.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSetupAria: {
    text: 'Operations setup',
    description: 'Aria label for operations setup chip tabs.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsComingSoonTitle: {
    text: '{tab} coming soon',
    description: 'Title for empty section state.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsComingSoonBody: {
    text: 'This section is scheduled for development in a subsequent sector operations phase.',
    description: 'Body for empty section state.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsPlacesTitle: {
    text: 'Places & QR',
    description: 'Places screen title.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlacesSubtitle: {
    text: 'One tree per sector with named levels · each place has a signed, revocable QR',
    description: 'Places screen subtitle.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsPlacesPrintQrSheet: {
    text: 'Print QR sheet',
    description: 'Button to open QR print sheet dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlacesAddFromOutline: {
    text: 'Add from outline',
    description: 'Button to add places from indented text outline.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlacesShowArchived: {
    text: 'Show archived',
    description: 'Toggle to show archived places.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesTreeHeader: {
    text: 'Tree',
    description: 'Card title for places tree.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesLevelsHint: {
    text: 'Levels: {levels}',
    description: 'Hint showing level progression.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPlacesEmptyTree: {
    text: 'No places in this sector tree yet',
    description: 'Empty state message for places tree.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlacesSelectPlaceholder: {
    text: 'Select a place from the tree to view its details and QR sticker',
    description: 'Placeholder when no place selected.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPlacesAddChild: {
    text: 'Add child',
    description: 'Button to add a child place under the selected place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesRename: {
    text: 'Rename / edit code',
    description: 'Button to rename place or change code.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlacesMove: {
    text: 'Move',
    description: 'Button to move place under another parent.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesArchive: {
    text: 'Archive',
    description: 'Button to archive place.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesUnarchive: {
    text: 'Unarchive',
    description: 'Button to unarchive place.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesRevokeReissue: {
    text: 'Revoke & reissue',
    description: 'Button to revoke existing QR token and generate a new one.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlacesArchiveConfirmTitle: {
    text: 'Archive place {name}?',
    description: 'Confirmation title for archiving place.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlacesArchiveConfirmBody: {
    text: 'Children places remain readable, but this place will be hidden from daily picks.',
    description: 'Confirmation body for archiving place.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsPlacesArchiveConfirmAction: {
    text: 'Archive place',
    description: 'Confirm button to archive place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesUnarchiveConfirmTitle: {
    text: 'Unarchive place {name}?',
    description: 'Confirmation title for unarchiving place.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlacesUnarchiveConfirmBody: {
    text: 'This place will be restored to active use in the places tree.',
    description: 'Confirmation body for unarchiving place.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsPlacesUnarchiveConfirmAction: {
    text: 'Unarchive place',
    description: 'Confirm button to unarchive place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesOutlineDialogTitle: {
    text: 'Add places from outline',
    description: 'Title of outline bulk-creation dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesOutlineDialogDesc: {
    text: 'Indent with 2 spaces or tabs. One place per line. Example:\nTower A\n  Floor 1\n    Room 101',
    description: 'Help text for outline format.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsPlacesOutlinePlaceholder: {
    text: 'Paste indented hierarchy here...',
    description: 'Textarea placeholder for outline.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesOutlineSubmit: {
    text: 'Create places',
    description: 'Button to submit outline.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesOutlineErrors: {
    text: 'Errors detected in outline:',
    description: 'Error header for outline parsing.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesNameLabel: {
    text: 'Name',
    description: 'Field label for place name.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesCodeLabel: {
    text: 'Code',
    description: 'Field label for place code.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesLevelLabel: {
    text: 'Level',
    description: 'Field label for place level.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesParentLabel: {
    text: 'Parent place',
    description: 'Field label for parent place in move dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlacesMoveDialogTitle: {
    text: 'Move place {name}',
    description: 'Title for move place dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesMoveSubmit: {
    text: 'Move place',
    description: 'Submit button for moving place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesAddChildDialogTitle: {
    text: 'Add child under {name}',
    description: 'Title for add child dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesRenameDialogTitle: {
    text: 'Rename place {name}',
    description: 'Title for rename dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesSave: {
    text: 'Save changes',
    description: 'Button to save changes.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesCancel: {
    text: 'Cancel',
    description: 'Button to cancel action.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesLegalHoldBlocked: {
    text: 'This place is on legal hold: {holdId}',
    description: 'Error message when attempting to archive or move a place that is under legal hold',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsQrPrintDialogTitle: {
    text: 'Print QR sheet',
    description: 'Dialog title for QR sheet print.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsQrPrintWarning: {
    text: 'Warning: Generating new QR stickers revokes and replaces previously printed stickers for those places.',
    description: 'Warning before printing QR tokens.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSOpsQrPrintSelectAll: {
    text: 'Select all ({count})',
    description: 'Select all places checkbox.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsQrPrintSelectSubtree: {
    text: 'Include this place and everything under it',
    description: 'Checkbox to select subtree.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsQrPrintPerPage: {
    text: 'Stickers per page',
    description: 'Label for stickers per page selector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsQrPrintPerPage8: {
    text: '8 per page (Large stickers)',
    description: 'Option for 8 stickers per page.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsQrPrintPerPage16: {
    text: '16 per page (Standard stickers)',
    description: 'Option for 16 stickers per page.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsQrPrintGenerating: {
    text: 'Generating tokens... ({current}/{total})',
    description: 'Progress label while generating tokens.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsQrPrintButton: {
    text: 'Print sheet',
    description: 'Button to trigger print.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsQrPrintClose: {
    text: 'Close',
    description: 'Close button for print dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesAccessAction: {
    text: 'Access',
    description: 'Access button on place item.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesAccessDialogTitle: {
    text: 'Place Access — {placeName}',
    description: 'Place access dialog title.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlacesAccessNotice: {
    text: 'Only members with restricted access are limited by this list. Owners and full-access members always see all records.',
    description: 'Plain notice explaining access rules.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSOpsPlacesAccessNoRestriction: {
    text: 'No restriction on this place',
    description: 'Option to clear restriction.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesAccessRestricted: {
    text: 'Restrict to specific members',
    description: 'Option to restrict place.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesAccessInheritedFrom: {
    text: 'Inherited restriction from {ancestorName}',
    description: 'Label showing inherited access.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlacesAccessNoInherited: {
    text: 'None (no parent restriction)',
    description: 'Label when no ancestor has access restrictions.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesAccessOwnList: {
    text: 'Direct list on this place',
    description: 'Direct place restriction list label.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesAccessEffectiveList: {
    text: 'Effective access list',
    description: 'Effective access list label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlacesAccessMemberPickerLabel: {
    text: 'Allowed members',
    description: 'Label for allowed members picker.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlacesAccessUpdatingPill: {
    text: 'Updating access… {count} records',
    description: 'Pill showing access propagation progress.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesAccessFailedPill: {
    text: 'Access update failed. Save again to retry.',
    description: 'Pill showing access propagation failure and retry note.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlacesAccessSave: {
    text: 'Save access',
    description: 'Save access button.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesAccessClose: {
    text: 'Close',
    description: 'Close access dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesAccessViewOnly: {
    text: 'View-only access (Admins and Owners can edit)',
    description: 'Read-only banner for editors.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsMembersPlacesSection: {
    text: 'Places access',
    description: 'Heading for places section in member panel.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsMembersPlacesAddPlace: {
    text: 'Add place',
    description: 'Add place button in member panel.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsMembersPlacesRemovePlace: {
    text: 'Remove',
    description: 'Remove place button in member panel.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsMembersPlacesInheritedWarning: {
    text: 'Access to {placeName} is inherited from {parentName}. The parent list must be changed to remove access.',
    description: 'Explanation when trying to remove an inherited place.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSOpsMembersPlacesInheritedOpenParent: {
    text: 'Open parent place',
    description: 'Button to open parent place.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsMembersPlacesEmpty: {
    text: 'No places granted yet.',
    description: 'Empty state for member places section.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsWorkforceTitle: {
    text: 'Workforce',
    description: 'Workforce screen title.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforceSubtitle: {
    text: 'People without app accounts + parties (contractors, vendors, clients) · no Aadhaar numbers or ID images',
    description: 'Workforce screen subtitle.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsWorkforceAddWorker: {
    text: 'Add worker',
    description: 'Button to add single worker.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforceAddGang: {
    text: 'Add gang',
    description: 'Button to quick-add a gang of workers.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforceAddParty: {
    text: 'Add party',
    description: 'Button to add a party.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforceWorkersTab: {
    text: 'Workers',
    description: 'Workers section heading.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforcePartiesTab: {
    text: 'Parties',
    description: 'Parties section heading.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforceSearchPlaceholder: {
    text: 'Search workers by name, code or trade...',
    description: 'Search input placeholder.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsWorkforceColCode: {
    text: 'Code',
    description: 'Column header for worker code.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsWorkforceColName: {
    text: 'Name',
    description: 'Column header for worker name.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsWorkforceColEmployer: {
    text: 'Employer / Party',
    description: 'Column header for party.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforceColTrade: {
    text: 'Trade',
    description: 'Column header for trade.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsWorkforceColPhone: {
    text: 'Phone',
    description: 'Column header for phone.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsWorkforceEmptyWorkers: {
    text: 'No workers recorded yet',
    description: 'Empty state message for workers table.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsWorkforceEmptyParties: {
    text: 'No parties recorded yet',
    description: 'Empty state message for parties list.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsWorkforceWorkerDialogTitle: {
    text: 'Worker details',
    description: 'Title of worker detail side dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsWorkforcePartyDialogTitle: {
    text: 'Party details',
    description: 'Title of party detail side dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsWorkforceAddGangTitle: {
    text: 'Add gang',
    description: 'Title of add gang dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforceGangPartyLabel: {
    text: 'Party / Employer',
    description: 'Field label for gang party.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforceGangTradeLabel: {
    text: 'Trade',
    description: 'Field label for gang trade.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsWorkforceGangModeCount: {
    text: 'Number of workers (auto-named)',
    description: 'Radio option for count mode in gang.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsWorkforceGangModeNames: {
    text: 'Pasted list of worker names',
    description: 'Radio option for names mode in gang.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsWorkforceGangCountLabel: {
    text: 'Worker count',
    description: 'Label for worker count.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforceGangNamesLabel: {
    text: 'Names (one per line)',
    description: 'Label for pasted worker names.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsWorkforceGangSubmit: {
    text: 'Add gang members',
    description: 'Submit button for gang entry.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforceArchiveWorkerConfirmTitle: {
    text: 'Archive worker {name}?',
    description: 'Confirm title for archiving worker.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsWorkforceArchiveWorkerConfirmBody: {
    text: 'This worker will be marked inactive and hidden from active shifts and daily roll call.',
    description: 'Confirm body for archiving worker.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsWorkforceArchiveWorkerConfirmAction: {
    text: 'Archive worker',
    description: 'Confirm button for archiving worker.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforcePartyMembersLabel: {
    text: 'Linked workspace members',
    description: 'Label for workspace members linked to party.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsWorkforcePartyMembersDesc: {
    text: 'Users in this list will be party-bound and see only this party’s workers and data.',
    description: 'Description of party-bound users.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsWorkforceKindLabel: {
    text: 'Party kind',
    description: 'Field label for party kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforceExternalRefLabel: {
    text: 'External reference',
    description: 'Field label for external reference.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforceEmailLabel: {
    text: 'Email',
    description: 'Field label for email address.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsWorkforcePhoneLabel: {
    text: 'Phone',
    description: 'Field label for phone number.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsWorkforcePartyScopePlaces: {
    text: 'Assigned places (scope)',
    description: 'Label for places assigned to party.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsWorkforceWorkerNamePlaceholder: {
    text: 'Worker full name',
    description: 'Placeholder for worker full name input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsWorkforceTradePlaceholder: {
    text: 'e.g. Shuttering or Electrician',
    description: 'Placeholder for worker trade.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsWorkforcePhonePlaceholder: {
    text: 'Optional mobile phone',
    description: 'Placeholder for worker mobile phone.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforceGangTradePlaceholder: {
    text: 'e.g. Shuttering or Helpers',
    description: 'Placeholder for gang trade.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsWorkforceGangNamesPlaceholder: {
    text: 'Sunil Kumar\nRamesh Yadav\nImran Shaikh',
    description: 'Placeholder for pasted gang names.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsWorkforcePartyNamePlaceholder: {
    text: 'e.g. Patil Constructions',
    description: 'Placeholder for subcontractor or party name.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsWorkforcePartyPhonePlaceholder: {
    text: 'Phone number',
    description: 'Placeholder for party phone number.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforcePartyEmailPlaceholder: {
    text: 'Email address',
    description: 'Placeholder for party email address.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforcePartyMembersLinked: {
    text: '{count} linked',
    description: 'Count of linked workspace members for party.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlacesChildNamePlaceholder: {
    text: 'e.g. Tower B or Room 101',
    description: 'Placeholder for child place name.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlacesChildCodePlaceholder: {
    text: 'e.g. TB or R101',
    description: 'Placeholder for child place code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlacesMoveRootOption: {
    text: '(Root Level)',
    description: 'Select option to move place to root level.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsQrPrintSubtreeButton: {
    text: '+ subtree',
    description: 'Button to include place subtree in QR print.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSCommonTreeListAriaLabel: {
    text: 'Tree list',
    description: 'Accessible label for tree list navigation.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSCommonTreeListExpand: {
    text: 'Expand {label}',
    description: 'Accessible label to expand a tree item.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSCommonTreeListCollapse: {
    text: 'Collapse {label}',
    description: 'Accessible label to collapse a tree item.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSCommonTreeListEmpty: {
    text: 'No items in tree',
    description: 'Empty state message for tree list.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsLevelClient: {
    text: 'Client',
    description: 'Name of place level Client.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelPost: {
    text: 'Post',
    description: 'Name of place level Post.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelArea: {
    text: 'Area',
    description: 'Name of place level Area.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelCheckpoint: {
    text: 'Checkpoint',
    description: 'Name of place level Checkpoint.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsSitesPresetLabel: {
    text: 'Preset',
    description: 'Label for site preset selector.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsSitesPresetGuarding: {
    text: 'Guarding',
    description: 'Site preset option: Guarding.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesPresetFacility: {
    text: 'Facility',
    description: 'Site preset option: Facility.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesPresetNone: {
    text: 'None',
    description: 'Site preset option: None.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsDeliveryTitle: {
    text: 'Delivery',
    description: 'Title for Delivery Hub screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsDeliverySubtitle: {
    text: 'Client projects, engagements, rates and margin',
    description: 'Subtitle for Delivery Hub screen.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsTabDeliveryToday: {
    text: 'Today',
    description: 'Section tab label for Delivery Today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabDeliveryEngagements: {
    text: 'Engagements',
    description: 'Section tab label for Delivery Engagements.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabDeliveryRequests: {
    text: 'Change requests',
    description: 'Section tab label for Delivery Change Requests.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabDeliveryUat: {
    text: 'UAT',
    description: 'Section tab label for Delivery UAT.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabDeliveryIntegrations: {
    text: 'GitHub & Jira',
    description: 'Section tab label for Delivery GitHub and Jira.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabDeliveryReports: {
    text: 'Client report',
    description: 'Section tab label for Delivery Client Report.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabDeliveryExport: {
    text: 'Export',
    description: 'Setup tab label for Delivery Accountant CSV Export.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryTodayHeading: {
    text: 'Delivery',
    description: 'Page title for Delivery Today screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryTodaySubtitle: {
    text: 'Active engagements, margin and delivery progress',
    description: 'Subtitle for Delivery Today screen.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsDeliveryTodayAdminMarginNote: {
    text: 'Margin and rates are admin-only: never shown to non-admins or on the client portal',
    description: 'Footnote on Today screen explaining admin-only margin visibility.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsDeliveryAccountantCsv: {
    text: 'Accountant CSV',
    description: 'Button text to open Accountant CSV export dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryKpiActiveEngagements: {
    text: 'Active engagements',
    description: 'KPI label for active engagements count.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryKpiActiveEngagementsDesc: {
    text: '{fixed} fixed · {tm} T&M · {retainer} retainer',
    description: 'KPI detail breakdown of active engagements by model.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryKpiNextMilestone: {
    text: 'Next milestone',
    description: 'KPI label for earliest upcoming milestone.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryKpiNextMilestoneNone: {
    text: 'No upcoming milestones',
    description: 'KPI value when no milestones are planned.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryKpiNextMilestoneDesc: {
    text: '{title} · due {date}',
    description: 'KPI detail describing the next due milestone title and date.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryWarningUnpricedHours: {
    text: '{count} unpriced hours logged across engagements with no rate set.',
    description: 'Warning box alert when hours lack a cost or bill rate.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsDeliveryWarningUnpricedSpend: {
    text: '{count} linked goals have expenses in a currency different from the engagement currency.',
    description: 'Warning box alert when budget expenses have mismatching currency.',
    surface: 'shell',
    maxChars: 110,
  },
  NLSOpsDeliveryWarningRetainerOverCap: {
    text: '{name} is over its included hours by {hours} h this month.',
    description: 'Warning alert when a retainer exceeds included hours.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsDeliveryEngagementsTableTitle: {
    text: 'Engagements',
    description: 'Heading for engagements overview table.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryEngagementsTableSubtitle: {
    text: 'margin from approved hours and expenses',
    description: 'Subtitle for engagements overview table.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryColEngagement: {
    text: 'Engagement',
    description: 'Table column header for engagement name.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryColClient: {
    text: 'Client',
    description: 'Table column header for client name.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryColModel: {
    text: 'Model',
    description: 'Table column header for billing model.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryColMargin: {
    text: 'Margin',
    description: 'Table column header for margin percentage.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryColNextMilestone: {
    text: 'Next',
    description: 'Table column header for next milestone.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryColStatus: {
    text: 'Status',
    description: 'Table column header for status.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryColDates: {
    text: 'Dates',
    description: 'Table column header for dates.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryColActions: {
    text: 'Actions',
    description: 'Table column header for actions.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryEngagementsTitle: {
    text: 'Engagements',
    description: 'Title for Delivery Engagements screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryEngagementsSubtitle: {
    text: 'Manage client engagements, commercial terms and milestones',
    description: 'Subtitle for Delivery Engagements screen.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsDeliveryBtnCreateEngagement: {
    text: 'New engagement',
    description: 'Button to open create engagement dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFilterStatus: {
    text: 'Status filter',
    description: 'Aria label for engagement status filter.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryStatusFilterAll: {
    text: 'All statuses',
    description: 'Option for showing all engagement statuses.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryStatusActive: {
    text: 'Active',
    description: 'Status label for active engagements.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryStatusPaused: {
    text: 'Paused',
    description: 'Status label for paused engagements.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryStatusClosed: {
    text: 'Closed',
    description: 'Status label for closed engagements.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryModelFixed: {
    text: 'Fixed price',
    description: 'Label for fixed-bid billing model.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsDeliveryModelTm: {
    text: 'T&M',
    description: 'Label for time-and-materials billing model.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryModelRetainer: {
    text: 'Retainer',
    description: 'Label for retainer billing model.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryDetailBack: {
    text: 'Back to engagements',
    description: 'Button to navigate back from detail to engagements list.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryBtnEditEngagement: {
    text: 'Edit engagement',
    description: 'Button to edit engagement details.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryBtnChangeStatus: {
    text: 'Change status',
    description: 'Button to change engagement status.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryConfirmCloseTitle: {
    text: 'Close engagement?',
    description: 'Title of confirmation dialog when closing engagement.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryConfirmCloseBody: {
    text: 'Closing this engagement will mark it completed.',
    description: 'Body of confirmation dialog when closing engagement.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsDeliveryConfirmCloseBtn: {
    text: 'Close engagement',
    description: 'Confirm button to close engagement.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMilestonesTitle: {
    text: 'Milestones',
    description: 'Heading for milestones section in engagement detail.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMilestonesSubtitle: {
    text: 'what the client portal shows',
    description: 'Subtitle for milestones section.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsDeliveryBtnAddMilestone: {
    text: 'Add milestone',
    description: 'Button to add a milestone.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMilestoneStatusPlanned: {
    text: 'Planned',
    description: 'Milestone status planned.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryMilestoneStatusDone: {
    text: 'Done',
    description: 'Milestone status done.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryMilestoneStatusInvoiced: {
    text: 'Invoiced',
    description: 'Milestone status invoiced.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryMilestoneStatusPaid: {
    text: 'Paid',
    description: 'Milestone status paid.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryMilestoneStepBackReason: {
    text: 'Reason for stepping back',
    description: 'Prompt label when stepping a milestone backward.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryMilestoneStepBackReasonRequired: {
    text: 'A reason is required to step a milestone back',
    description: 'Error validation when step back reason is empty.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryMilestoneForwardOnlyHint: {
    text: 'Milestones advance forward. Moving back one step requires an explanation.',
    description: 'Helper text on milestone forward-only progression.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsDeliveryMilestoneDialogTitle: {
    text: 'Milestone',
    description: 'Dialog title for adding or editing a milestone.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMilestoneFieldTitle: {
    text: 'Milestone title',
    description: 'Input label for milestone title.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMilestoneFieldDueDate: {
    text: 'Due date',
    description: 'Input label for milestone due date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMilestoneFieldStatus: {
    text: 'Milestone status',
    description: 'Select label for milestone status.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMilestoneAdvanceBtn: {
    text: 'Advance status',
    description: 'Button to advance milestone to next status.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMilestoneStepBackBtn: {
    text: 'Step back status',
    description: 'Button to move milestone back one status.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFinanceTitle: {
    text: 'Commercial setup',
    description: 'Heading for commercial finance section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFinanceSubtitle: {
    text: 'admin only · currency from client',
    description: 'Subtitle for commercial finance section.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsDeliveryFinanceClientCurrency: {
    text: 'Client currency',
    description: 'Label showing read-only client currency.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFinanceNeedsClientCurrency: {
    text: 'Client needs a currency set first before commercial finance can be configured.',
    description: 'Notice shown in commercial setup when the client has no currency configured.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsDeliveryFinanceTargetMargin: {
    text: 'Target margin %',
    description: 'Input label for target margin percentage.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFinanceFixedPrice: {
    text: 'Fixed price total',
    description: 'Input label for fixed price total amount.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFinanceMilestoneAmount: {
    text: 'Milestone amount',
    description: 'Input label for milestone amount in fixed engagements.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFinanceMonthlyFee: {
    text: 'Monthly retainer fee',
    description: 'Input label for monthly retainer fee.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFinanceIncludedHours: {
    text: 'Included hours / month',
    description: 'Input label for included hours in retainer.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFinanceOverageRate: {
    text: 'Overage bill rate / hour',
    description: 'Input label for retainer overage hourly rate.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFinanceRolloverCap: {
    text: 'Rollover cap (hours)',
    description: 'Input label for retainer rollover hours cap.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryBtnSaveFinance: {
    text: 'Save commercial setup',
    description: 'Button to save engagement finance setup.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryRateCardTitle: {
    text: 'Rate card',
    description: 'Heading for engagement rate card section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryRateCardSubtitle: {
    text: 'admin only · member bill rates',
    description: 'Subtitle for rate card section.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsDeliveryRateCardMember: {
    text: 'Member UID',
    description: 'Input label for team member user id in rate card.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryRateCardBillRate: {
    text: 'Bill rate / hour',
    description: 'Input label for billable rate in rate card.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryRateCardAddMember: {
    text: 'Add rate card entry',
    description: 'Button to add entry to rate card.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryRateCardNone: {
    text: 'No rate card overrides set',
    description: 'Empty state message when rate card is empty.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryAllocationsTitle: {
    text: 'Allocations',
    description: 'Heading for allocations section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsSubtitle: {
    text: 'admin only · planned staffing and shadow members',
    description: 'Subtitle for allocations section.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryAllocationsKindBilled: {
    text: 'Billed',
    description: 'Pill label for billed allocation.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryAllocationsKindShadow: {
    text: 'Shadow · not billed',
    description: 'Pill label for shadow allocation.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsHoursPerWeek: {
    text: 'Hours / week',
    description: 'Input label for hours per week allocation.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsPercent: {
    text: 'Percent (%)',
    description: 'Input label for percentage allocation.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsPerson: {
    text: 'Person UID',
    description: 'Input label for member user id in allocation.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsType: {
    text: 'Allocation kind',
    description: 'Select label for allocation kind.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsFrom: {
    text: 'From date',
    description: 'Input label for allocation start date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsTo: {
    text: 'To date (optional)',
    description: 'Input label for allocation end date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsDates: {
    text: 'Dates',
    description: 'Table column header for allocation dates.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsEndAction: {
    text: 'End allocation',
    description: 'Button to end an allocation.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsAddAction: {
    text: 'Add allocation',
    description: 'Button to add an allocation.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryAllocationsShadowHint: {
    text: 'Shadow allocations count in cost and margin but never appear to the client.',
    description: 'Footnote explaining shadow allocations.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsDeliveryAllocationsNone: {
    text: 'No allocations recorded',
    description: 'Empty state message for allocations.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryLinkedGoalsTitle: {
    text: 'Linked Goals',
    description: 'Heading for linked goals section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryLinkedGoalsSubtitle: {
    text: 'time entries and budget expenses link via Goal',
    description: 'Subtitle for linked goals section.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryLinkedGoalsNone: {
    text: 'No goals linked to this engagement',
    description: 'Empty state when no goals are linked.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsDeliveryLinkedGoalsLinkAction: {
    text: 'Link Goal',
    description: 'Button to link a goal.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryLinkedGoalsUnlinkAction: {
    text: 'Unlink',
    description: 'Button to unlink a goal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryLinkedGoalsUnlinkCopy: {
    text: 'Unlinking removes the engagement reference. The client stays on the Goal.',
    description: 'Note explaining goal unlink behaviour.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsDeliveryMarginTitle: {
    text: 'Live margin',
    description: 'Heading for margin card.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMarginSubtitle: {
    text: 'admin only · live calculation',
    description: 'Subtitle for margin card.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryMarginActual: {
    text: 'Live margin',
    description: 'KPI label for actual margin percentage.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMarginTarget: {
    text: 'Target',
    description: 'KPI detail label for target margin percentage.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryMarginForecast: {
    text: 'Forecast',
    description: 'KPI detail label for forecast margin percentage.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryMarginRevenue: {
    text: 'Revenue',
    description: 'KPI label for total revenue so far.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryMarginLabourCost: {
    text: 'Labour cost',
    description: 'KPI label for labour cost so far.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsDeliveryMarginExpenses: {
    text: 'Expenses',
    description: 'KPI label for expenses so far.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryMarginCostSoFar: {
    text: 'Cost so far',
    description: 'KPI label for total cost (labour + expenses).',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsDeliveryMarginUnpricedHours: {
    text: 'Unpriced hours',
    description: 'KPI label for unpriced hours.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsDeliveryMarginUnpricedSpend: {
    text: 'Unpriced expenses',
    description: 'KPI label for unpriced expenses in another currency.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMarginBtnRecompute: {
    text: 'Recompute margin',
    description: 'Button to trigger margin recompute callable.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMarginRecomputing: {
    text: 'Recomputing...',
    description: 'Button label during margin recompute.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryMarginNoAccountingNote: {
    text: 'No invoices or accounting entries are posted: the CSV goes to your accountant.',
    description: 'Footnote on engagement detail screen.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsDeliveryDialogCreateTitle: {
    text: 'Create engagement',
    description: 'Dialog title for creating an engagement.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryDialogEditTitle: {
    text: 'Edit engagement',
    description: 'Dialog title for editing an engagement.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFieldClient: {
    text: 'Client',
    description: 'Form label for selecting a client.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryFieldClientSelect: {
    text: 'Select client',
    description: 'Placeholder option for client selector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFieldName: {
    text: 'Engagement name',
    description: 'Form label for engagement name.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFieldModel: {
    text: 'Billing model',
    description: 'Form label for billing model.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFieldStartDate: {
    text: 'Start date',
    description: 'Form label for start date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryFieldEndDate: {
    text: 'End date (optional)',
    description: 'Form label for end date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryExportTitle: {
    text: 'Accountant CSV Export',
    description: 'Title of accountant CSV export dialog and tab.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryExportSubtitle: {
    text: 'Monthly breakdown of billable hours, revenue, labour cost and margin per engagement.',
    description: 'Subtitle of accountant CSV export dialog.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsDeliveryExportFrom: {
    text: 'From month',
    description: 'Form label for start month (YYYY-MM).',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryExportTo: {
    text: 'To month',
    description: 'Form label for end month (YYYY-MM).',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryExportEngagement: {
    text: 'Engagement (optional)',
    description: 'Form label for selecting specific engagement in export.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryExportAllEngagements: {
    text: 'All engagements',
    description: 'Option for exporting all engagements.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryExportBtnDownload: {
    text: 'Download CSV',
    description: 'Button to trigger CSV export download.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryExportDownloading: {
    text: 'Generating CSV...',
    description: 'Button state while CSV is being generated.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryExportMaxMonthsError: {
    text: 'Month range cannot exceed 24 months',
    description: 'Error message when export month range is greater than 24 months.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryExportSuccess: {
    text: 'CSV downloaded successfully',
    description: 'Success notification after CSV export.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryErrorGeneric: {
    text: 'An error occurred while saving delivery data.',
    description: 'Generic error message for failed delivery operation.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryErrorPermissionDenied: {
    text: 'You do not have permission to perform this delivery action.',
    description: 'Error message when action is rejected due to permission.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsDeliveryEmptyEngagements: {
    text: 'No engagements found',
    description: 'Empty state title for engagements list.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryEmptyEngagementsBody: {
    text: 'Create an engagement to start tracking delivery, milestones and margin.',
    description: 'Empty state body for engagements list.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsDeliveryBucketClientBillable: {
    text: 'Client billable hours',
    description: 'CSV column header for client billable hours.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryBucketFixedBid: {
    text: 'Fixed bid hours',
    description: 'CSV column header for fixed bid hours.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryBucketClientNonBillable: {
    text: 'Client non-billable hours',
    description: 'CSV column header for client non-billable hours.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryBucketInternal: {
    text: 'Internal hours',
    description: 'CSV column header for internal hours.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryBucketBillableTotal: {
    text: 'Billable hours',
    description: 'CSV column header for total billable hours.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryPlaceholderDate: {
    text: 'YYYY-MM-DD',
    description: 'Placeholder for date input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryPlaceholderMonth: {
    text: 'YYYY-MM',
    description: 'Placeholder for month input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryPlaceholderName: {
    text: 'Name',
    description: 'Placeholder for name input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryPlaceholderTitle: {
    text: 'Title',
    description: 'Placeholder for title input.',
    surface: 'shell',
    maxChars: 20,
  },
  // R39 E2.a + E2.b Delivery Change Requests, UAT & Client Approvers
  NLSOpsDeliveryKpiCrsAwaitingClient: {
    text: 'CRs awaiting client',
    description: 'KPI heading for change requests awaiting client approval.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryKpiCrsAwaitingClientSub: {
    text: 'oldest {days} days',
    description: 'KPI subtext showing age in days of oldest change request.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryKpiUnbilledChange: {
    text: 'Unbilled change',
    description: 'KPI heading for unbilled change hours.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryKpiUnbilledChangeSub: {
    text: 'logged against CR, not approved',
    description: 'KPI subtext for unbilled change hours.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsDeliveryBtnChangeRequest: {
    text: 'Change request',
    description: 'Button to navigate to change requests or open create dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUnbilledWarnBanner: {
    text: '{hours} hours were logged against {crNumber} ({title}), which the client has not approved yet. Ask the client, or move the hours.',
    description: 'Warning banner when time entries are logged against unapproved change requests.',
    surface: 'shell',
    maxChars: 150,
  },
  NLSOpsDeliveryCrsHeading: {
    text: 'Change requests',
    description: 'Heading for change requests screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsSubtitle: {
    text: "Every scope change, its hours and price, and the client's decision · a release is a tag on CRs",
    description: 'Subtitle for change requests screen.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsDeliveryCrsBtnNew: {
    text: 'Change request',
    description: 'Button to create a new change request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsColCr: {
    text: 'CR',
    description: 'Table column header for change request number.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsDeliveryCrsColEngagement: {
    text: 'Engagement',
    description: 'Table column header for engagement.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsColWhatChanges: {
    text: 'What changes',
    description: 'Table column header for what changes summary.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsColHours: {
    text: 'Hours',
    description: 'Table column header for estimated hours.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsColPrice: {
    text: 'Price effect',
    description: 'Table column header for price effect (admin only).',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsColRelease: {
    text: 'Release',
    description: 'Table column header for release tag.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsColDecision: {
    text: 'Client decision',
    description: 'Table column header for client decision status.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsColActions: {
    text: 'Actions',
    description: 'Table column header for actions.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsStatusDraft: {
    text: 'Draft',
    description: 'Pill status for draft change request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsStatusSent: {
    text: 'Sent',
    description: 'Pill status for sent change request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsStatusApproved: {
    text: 'Approved',
    description: 'Pill status for approved change request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsStatusRejected: {
    text: 'Rejected',
    description: 'Pill status for rejected change request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsStatusRevised: {
    text: 'Revision requested',
    description: 'Pill status for revision requested change request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsStatusWithdrawn: {
    text: 'Withdrawn',
    description: 'Pill status for withdrawn change request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsFooterNote: {
    text: 'Named client approvers only (1–3 people per engagement, link + OTP). A revised CR keeps the earlier version; rejected ones stay in the ledger.',
    description: 'Footer note on change requests screen.',
    surface: 'shell',
    maxChars: 160,
  },
  NLSOpsDeliveryCrsHistoryHeading: {
    text: '{crNumber} history',
    description: 'Heading for change request version history panel.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsHistorySubtitle: {
    text: 'frozen versions',
    description: 'Subtitle for change request version history panel.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsHistoryEmpty: {
    text: 'No version history available.',
    description: 'Empty state message for change request version history.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryCrsDialogTitle: {
    text: 'Raise change request',
    description: 'Dialog title for raising a change request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsDialogEditTitle: {
    text: 'Edit change request',
    description: 'Dialog title for editing a change request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsFieldTitle: {
    text: 'Title',
    description: 'Field label for change request title.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsFieldWhatChanges: {
    text: 'What changes',
    description: 'Field label for change request what changes description.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsFieldHours: {
    text: 'Estimated hours',
    description: 'Field label for change request estimated hours.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsFieldRelease: {
    text: 'Release tag (optional)',
    description: 'Field label for change request release tag.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsFieldLinkedNodes: {
    text: 'Linked task IDs (comma separated, optional)',
    description: 'Field label for linked task IDs.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsDeliveryCrsPriceDialogTitle: {
    text: 'Set change request price',
    description: 'Dialog title for configuring price effect on change request.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsDeliveryCrsPriceKind: {
    text: 'Price model',
    description: 'Field label for selecting price model.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsPriceAmount: {
    text: 'Fixed amount',
    description: 'Option label for fixed amount price effect.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsPriceTM: {
    text: 'Time & materials',
    description: 'Option label for time and materials price effect.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsPriceNone: {
    text: 'No price effect (included)',
    description: 'Option label for no price effect.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryCrsBtnSend: {
    text: 'Send to client',
    description: 'Button to send change request to client for approval.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryCrsBtnWithdraw: {
    text: 'Withdraw',
    description: 'Button to withdraw a sent change request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryCrsEmpty: {
    text: 'No change requests found',
    description: 'Empty state title for change requests table.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryCrsEmptyBody: {
    text: 'Raise a change request to record scope adjustments and obtain client sign-off.',
    description: 'Empty state body for change requests table.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsDeliveryUatHeading: {
    text: 'UAT',
    description: 'Heading for UAT screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatSubtitle: {
    text: 'A versioned UAT checklist, signed off by the client through a link',
    description: 'Subtitle for UAT screen.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsDeliveryUatBtnNewChecklist: {
    text: 'Checklist',
    description: 'Button to create or edit UAT checklist.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatBtnStartRound: {
    text: 'Start round',
    description: 'Button to start a new UAT round.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatBtnSendSignoff: {
    text: 'Send UAT link',
    description: 'Button to send UAT round sign-off link to client approvers.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatBtnCloseRound: {
    text: 'Close round',
    description: 'Button to close a UAT round.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatRoundTitle: {
    text: 'Round {number} · checklist v{version}',
    description: 'Header text for active UAT round.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsDeliveryUatItemWaitingCr: {
    text: 'Waiting for CR approval',
    description: 'Pill warning when UAT item is tied to an unapproved change request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatItemPassed: {
    text: 'Passed',
    description: 'Pill status for passed UAT item.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryUatItemFailed: {
    text: 'Failed',
    description: 'Pill status for failed UAT item.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryUatItemBlocked: {
    text: 'Blocked',
    description: 'Pill status for blocked UAT item.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryUatItemNotTested: {
    text: 'Not tested',
    description: 'Pill status for untested UAT item.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryUatSignoffTitle: {
    text: 'Sign-off',
    description: 'Heading for UAT sign-off status card.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatSignoffApprover: {
    text: 'Approver',
    description: 'Label indicating approver role.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryUatSignoffSigned: {
    text: 'Signed off',
    description: 'Pill status when client has signed off UAT round.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryUatSignoffPending: {
    text: 'Not yet',
    description: 'Pill status when sign-off is pending from client.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryUatSignoffRejected: {
    text: 'Rejected',
    description: 'Pill status when client rejected UAT round.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryUatSignoffDisclaimer: {
    text: 'Sign-off freezes checklist v{version} and its results. It is an acknowledgement by OTP, not a legal e-signature.',
    description: 'Legal disclaimer for UAT outside sign-off.',
    surface: 'shell',
    maxChars: 130,
  },
  NLSOpsDeliveryUatDialogChecklistTitle: {
    text: 'UAT checklist',
    description: 'Dialog title for editing UAT checklist.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatFieldChecklistTitle: {
    text: 'Checklist title',
    description: 'Field label for UAT checklist title.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatFieldItems: {
    text: 'Items (one per line, format: Description [CR-ID])',
    description: 'Field label for entering UAT checklist items.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryUatDialogStartRoundTitle: {
    text: 'Start UAT round',
    description: 'Dialog title for starting a new UAT round.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatFieldRelease: {
    text: 'Release',
    description: 'Field label for release tag in UAT round.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryUatDialogResultTitle: {
    text: 'Test result',
    description: 'Dialog title for updating item result in UAT round.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatFieldNote: {
    text: 'Note (optional)',
    description: 'Field label for note on test result.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryUatEmpty: {
    text: 'No UAT checklists or rounds found',
    description: 'Empty state title for UAT screen.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryUatEmptyBody: {
    text: 'Create a checklist and start a UAT round to track acceptance criteria.',
    description: 'Empty state body for UAT screen.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsDeliveryApproversHeading: {
    text: 'Client approvers',
    description: 'Heading for client approvers setup screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryApproversSubtitle: {
    text: 'Named client approvers for change requests and UAT sign-offs (1–3 people per engagement)',
    description: 'Subtitle for client approvers setup screen.',
    surface: 'shell',
    maxChars: 110,
  },
  NLSOpsDeliveryApproversAdminOnly: {
    text: 'Only workspace admins can configure client approvers.',
    description: 'Access denied message for non-admins on client approvers screen.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsDeliveryApproversCardTitle: {
    text: 'Client approvers for {engagement}',
    description: 'Card title for client approvers of an engagement.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsDeliveryApproversAddBtn: {
    text: 'Add approver',
    description: 'Button to add a client approver row.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryApproversSaveBtn: {
    text: 'Save approvers',
    description: 'Button to save client approvers.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryApproversFieldName: {
    text: 'Full name',
    description: 'Field label for client approver name.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryApproversFieldPhone: {
    text: 'Phone number (E.164, e.g. +919876543210)',
    description: 'Field label for client approver phone.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDeliveryApproversHelp: {
    text: 'These approvers receive outside OTP links for all change request decisions and UAT sign-offs on this engagement.',
    description: 'Footnote on client approvers screen.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsDeliveryApproversEmpty: {
    text: 'No client approvers configured yet. At least 1 approver is required before sending change requests.',
    description: 'Empty state on client approvers screen.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsDeliveryClientLinksPanelTitle: {
    text: 'Client portal links',
    description: 'Title for client portal links panel on client approvers screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryClientLinksPanelSubtitle: {
    text: 'One-time secure login-free portal links for client approvers',
    description: 'Subtitle for client portal links panel on client approvers screen.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsDeliveryClientLinksStatusActive: {
    text: 'Active until {date}',
    description: 'Status label for active client portal link.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryClientLinksStatusNotSent: {
    text: 'Not sent',
    description: 'Status label when client link has not been issued yet.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksStatusRevoked: {
    text: 'Revoked',
    description: 'Status label for revoked client link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksStatusExpired: {
    text: 'Expired',
    description: 'Status label for expired client link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksLastOpened: {
    text: 'Last opened: {date}',
    description: 'Label showing last time client link was opened.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryClientLinksNeverOpened: {
    text: 'Never opened',
    description: 'Label when client link was never opened.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksSendBtn: {
    text: 'Send link',
    description: 'Button to send or issue client portal link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksReissueBtn: {
    text: 'Reissue',
    description: 'Button to reissue client portal link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksRevokeBtn: {
    text: 'Revoke',
    description: 'Button to revoke client portal link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksActivityBtn: {
    text: 'Activity',
    description: 'Button to view client link activity log.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksCopyBtn: {
    text: 'Copy link',
    description: 'Button to copy client link to clipboard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksCopied: {
    text: 'Copied!',
    description: 'Tooltip or badge when link is copied to clipboard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryClientLinksShareWhatsApp: {
    text: 'Share on WhatsApp',
    description: 'Button to share client link via WhatsApp.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryClientLinksModalTitle: {
    text: 'New portal link generated',
    description: 'Modal title when link is generated.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryClientLinksModalSubtitle: {
    text: 'This link will only be shown once. Please copy or share it directly with the client.',
    description: 'Modal subtitle explaining link is shown once.',
    surface: 'shell',
    maxChars: 110,
  },
  NLSOpsDeliveryClientLinksActivityTitle: {
    text: 'Link activity for {name}',
    description: 'Activity modal title for client link.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryClientLinksNoActivity: {
    text: 'No activity recorded yet.',
    description: 'Empty state for link activity.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryClientLinkShareMessage: {
    text: 'Here is your secure client portal link for {engagement}: {url}',
    description: 'WhatsApp message template for sharing client link.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsClientPortalTitle: {
    text: 'Client Portal',
    description: 'Title of the client portal page.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClientPortalSubtitle: {
    text: 'Secure client portal for approvals, milestones and UAT',
    description: 'Subtitle of the client portal page.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsClientPortalPhoneVerifyTitle: {
    text: 'Verify your phone number',
    description: 'Title for phone number last 4 verification step.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsClientPortalPhoneVerifySubtitle: {
    text: 'Enter the last 4 digits of your phone number ({masked}) to receive a one-time verification code.',
    description: 'Instruction for phone last 4 verification.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsClientPortalLast4Label: {
    text: 'Last 4 digits',
    description: 'Field label for last 4 phone digits.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalLast4Submit: {
    text: 'Send verification code',
    description: 'Button to send OTP code.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClientPortalOtpTitle: {
    text: 'Enter verification code',
    description: 'Title for OTP code entry step.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsClientPortalOtpSubtitle: {
    text: 'Enter the 6-digit code sent to your phone.',
    description: 'Instruction for OTP entry.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsClientPortalOtpLabel: {
    text: 'Verification code',
    description: 'Field label for OTP code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalOtpSubmit: {
    text: 'Verify & Enter',
    description: 'Button to verify OTP and enter portal.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClientPortalResendCode: {
    text: 'Resend code',
    description: 'Button to resend OTP code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalTabMilestones: {
    text: 'Milestones',
    description: 'Tab label for milestones in portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalTabWaiting: {
    text: 'Waiting for you',
    description: 'Tab label for approvals waiting for client in portal.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClientPortalTabChanges: {
    text: 'Change requests',
    description: 'Tab label for change requests in portal.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClientPortalTabUat: {
    text: 'UAT',
    description: 'Tab label for UAT rounds in portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalTabReports: {
    text: 'Weekly reports',
    description: 'Tab label for weekly reports in portal.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClientPortalNoReports: {
    text: 'No reports yet',
    description: 'Message when no weekly reports have been published.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClientPortalMilestonesEmpty: {
    text: 'No milestones defined yet.',
    description: 'Empty state message for milestones in portal.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsClientPortalWaitingEmpty: {
    text: 'Nothing waiting for your review.',
    description: 'Empty state message when no approvals are pending.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsClientPortalChangesEmpty: {
    text: 'No change requests sent yet.',
    description: 'Empty state message for change requests in portal.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsClientPortalUatEmpty: {
    text: 'No UAT rounds published yet.',
    description: 'Empty state message for UAT rounds in portal.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsClientPortalApproveBtn: {
    text: 'Approve',
    description: 'Button to approve an approval request in portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalRejectBtn: {
    text: 'Reject',
    description: 'Button to reject an approval request in portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalRequestChangesBtn: {
    text: 'Ask for changes',
    description: 'Button to request changes on an approval request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalReviewBtn: {
    text: 'Review',
    description: 'Button to open review dialog for pending approval.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalRevisionCommentPlaceholder: {
    text: 'Describe what changes are needed (3-500 characters)...',
    description: 'Placeholder for revision requested comment.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsClientPortalDisclaimer: {
    text: 'acknowledgement, not a legal e-signature',
    description: 'Legal disclaimer for approvals in client portal.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsClientPortalStatusClosed: {
    text: 'This engagement is closed or archived.',
    description: 'Message shown when engagement is closed or archived.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsClientPortalLinkExpired: {
    text: 'This link has expired.',
    description: 'Message shown when client link is expired.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClientPortalLinkRevoked: {
    text: 'This link has been revoked.',
    description: 'Message shown when client link is revoked.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClientPortalLinkNotFound: {
    text: 'Link not found or invalid.',
    description: 'Message shown when client link does not exist.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsClientPortalUatBadgeClient: {
    text: 'Client',
    description: 'Badge shown on UAT results set by client.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalPassBtn: {
    text: 'Pass',
    description: 'Button to mark UAT item passed.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalFailBtn: {
    text: 'Fail',
    description: 'Button to mark UAT item failed.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalBlockBtn: {
    text: 'Block',
    description: 'Button to mark UAT item blocked.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalUatNotePlaceholder: {
    text: 'Add an optional note...',
    description: 'Placeholder for UAT item note.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDeliveryUatSourceClient: {
    text: 'Client',
    description: 'Badge indicating UAT result was set by client.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalPhoneMismatch: {
    text: 'The phone number does not match this client link.',
    description: 'Error shown when entered phone does not match client link.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsClientPortalOtpCapExhausted: {
    text: 'Too many attempts. Please try again later.',
    description: 'Error shown when OTP attempt limit is reached.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsClientPortalErrorGeneric: {
    text: 'An error occurred. Please try again.',
    description: 'Generic error message on client portal.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsClientPortalLoading: {
    text: 'Loading...',
    description: 'Loading indicator text on client portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalWorkspaceLabel: {
    text: 'Workspace',
    description: 'Label for workspace name on client portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalEngagementLabel: {
    text: 'Engagement',
    description: 'Label for engagement title on client portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalApproverLabel: {
    text: 'Approver',
    description: 'Label for client approver name on client portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalPhoneLabel: {
    text: 'Phone number',
    description: 'Label for phone number on client portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalPhonePlaceholder: {
    text: '+91 98765 43210',
    description: 'Placeholder for phone number input on client portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalSendOtpBtn: {
    text: 'Send code',
    description: 'Button to send OTP code on client portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalVerifyOtpBtn: {
    text: 'Verify and open',
    description: 'Button to verify OTP and open client portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClientPortalInvalidOtp: {
    text: 'Invalid verification code. Please check and try again.',
    description: 'Error message for invalid OTP code on client portal.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsClientPortalSectionMilestones: {
    text: 'Milestones',
    description: 'Section heading for milestones on client portal.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryPhoneChangesTitle: {
    text: 'Change requests',
    description: 'Header title for changes section on member phone.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryPhoneNewCrBtn: {
    text: 'New change request',
    description: 'Button on member phone to raise change request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryPhoneUatTitle: {
    text: 'UAT',
    description: 'Header title for UAT section on member phone.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryPhoneRemindBtn: {
    text: 'Remind the client',
    description: 'Button on member phone to remind client of pending UAT sign-off.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryPhoneLogged: {
    text: '{hours} h already logged',
    description: 'Subtext showing logged hours on change request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDeliveryPhoneWithClient: {
    text: 'With client',
    description: 'Pill text for change request currently with client.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryPlaceholderReason: {
    text: 'Reason',
    description: 'Placeholder for reason input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDeliveryPlaceholderUid: {
    text: 'User ID',
    description: 'Placeholder for UID input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesTitle: {
    text: 'Sites',
    description: 'Title for Sites Hub screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesSubtitle: {
    text: 'Guarding, facility and site operations',
    description: 'Subtitle for Sites Hub screen.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsTabSitesToday: {
    text: 'Today',
    description: 'Section tab label for Sites Today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSitesPosts: {
    text: 'Posts',
    description: 'Section tab label for Sites Posts.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSitesRounds: {
    text: 'Rounds',
    description: 'Section tab label for Sites Patrol Rounds.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSitesIncidents: {
    text: 'Incidents',
    description: 'Section tab label for Sites Incidents.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSitesClient: {
    text: 'Client portal',
    description: 'Section tab label for Sites Client Portal.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabSitesWages: {
    text: 'Wages',
    description: 'Section tab label for Sites Wages.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSitesPlaces: {
    text: 'Sites & checkpoints',
    description: 'Setup tab label for Sites & checkpoints in Sites sector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTabSitesWorkforce: {
    text: 'Workers',
    description: 'Setup tab label for Workers in Sites sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSitesTemplates: {
    text: 'Templates',
    description: 'Setup tab label for Templates in Sites sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSitesSchedules: {
    text: 'Round windows',
    description: 'Setup tab label for Round windows in Sites sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabSitesRosters: {
    text: 'Posts & shifts',
    description: 'Setup tab label for Posts & shifts in Sites sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabSitesImport: {
    text: 'CSV import',
    description: 'Setup tab label for CSV import in Sites sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSitesWhatsApp: {
    text: 'On WhatsApp',
    description: 'Setup tab label for On WhatsApp in Sites sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesWizardTitle: {
    text: 'Set up Sites',
    description: 'Title for Sites setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWizardGuardingTitle: {
    text: 'Security Guarding',
    description: 'Guarding option title in setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWizardGuardingDesc: {
    text: 'Fixed posts, patrol rounds, relief shifts, client reporting',
    description: 'Guarding option description in setup wizard.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesWizardFacilityTitle: {
    text: 'Facility Management',
    description: 'Facility option title in setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWizardFacilityDesc: {
    text: 'Cleaning, maintenance, scheduled rounds, area checklists',
    description: 'Facility option description in setup wizard.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesWizardClientLabel: {
    text: 'Client name',
    description: 'Input label for Client name in setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWizardSiteLabel: {
    text: 'Site name',
    description: 'Input label for Site name in setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWizardPostsLabel: {
    text: 'Posts (one per line, optional)',
    description: 'Input label for Posts list in setup wizard.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWizardAreasLabel: {
    text: 'Areas (one per line, optional)',
    description: 'Input label for Areas list in setup wizard.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWizardSubmit: {
    text: 'Create Client & Site',
    description: 'Submit button in setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWizardSubmitting: {
    text: 'Creating...',
    description: 'Submitting text in setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesWizardDoneTitle: {
    text: 'Site setup complete!',
    description: 'Success title in setup wizard.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesWizardAddCheckpoints: {
    text: 'Add checkpoints',
    description: 'Button to navigate to places setup after wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWizardPrintQr: {
    text: 'Print checkpoint QR sheet',
    description: 'Button to print QR sheet after wizard.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesWizardAdminNotice: {
    text: 'Ask an admin to set up Sites',
    description: 'Notice shown to non-admin users before Sites setup.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsSitesWizardNameRequired: {
    text: 'Client name and Site name are required.',
    description: 'Validation error when client or site name is missing in Sites setup wizard',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSitesWizardClientPlaceholder: {
    text: 'e.g. Acme Corporation',
    description: 'Placeholder for client name in Sites setup wizard',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWizardSitePlaceholder: {
    text: 'e.g. Headquarters',
    description: 'Placeholder for site name in Sites setup wizard',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWizardPostsPlaceholder: {
    text: 'Main Gate\nEast Entrance\nLoading Dock',
    description: 'Placeholder for posts list in Sites setup wizard',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSitesWizardAreasPlaceholder: {
    text: 'Floor 1\nFloor 2\nCafeteria',
    description: 'Placeholder for areas list in Sites setup wizard',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsQrFilterCheckpointsOnly: {
    text: 'Checkpoints only',
    description: 'Filter checkbox label in QR sheet print modal.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsLevelSite: {
    text: 'Site',
    description: 'Name of place level Site.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelBuilding: {
    text: 'Building',
    description: 'Name of place level Building.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelFloor: {
    text: 'Floor',
    description: 'Name of place level Floor.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelRoom: {
    text: 'Room',
    description: 'Name of place level Room.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelChainage: {
    text: 'Chainage',
    description: 'Name of place level Chainage.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelFacility: {
    text: 'Facility',
    description: 'Name of place level Facility.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelDepartment: {
    text: 'Department',
    description: 'Name of place level Department.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelUnit: {
    text: 'Unit',
    description: 'Name of place level Unit.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelWard: {
    text: 'Ward',
    description: 'Name of place level Ward.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelBed: {
    text: 'Bed',
    description: 'Name of place level Bed.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelGroup: {
    text: 'Group',
    description: 'Name of place level Group in Shops sector.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelOutlet: {
    text: 'Outlet',
    description: 'Name of place level Outlet in Shops sector.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelZone: {
    text: 'Zone',
    description: 'Name of place level Zone in Shops sector.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelProperty: {
    text: 'Property',
    description: 'Name of place level Property in Stays sector.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelBlock: {
    text: 'Block / Floor',
    description: 'Name of place level Block in Stays sector.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLevelServiceArea: {
    text: 'Service Area',
    description: 'Name of place level Service Area in Jobs sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsLevelOrg: {
    text: 'Organisation',
    description: 'Name of place level Organisation in Clinics sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsLevelChair: {
    text: 'Chair',
    description: 'Name of place level Chair in Clinics sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsLevelBay: {
    text: 'Bay',
    description: 'Name of place level Bay in Clinics sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsTitle: {
    text: 'Shops',
    description: 'Title for Shops Hub screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsSubtitle: {
    text: 'Multi-outlet operations and daily registers',
    description: 'Subtitle for Shops Hub screen.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsTabShopsToday: {
    text: 'Today',
    description: 'Section tab label for Shops Today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabShopsCash: {
    text: 'Cash & handover',
    description: 'Section tab label for Shops Cash & handover.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabShopsStock: {
    text: 'Stock',
    description: 'Section tab label for Shops Stock.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabShopsAudits: {
    text: 'Display audits',
    description: 'Section tab label for Shops Display audits.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabShopsStaff: {
    text: 'Staff',
    description: 'Section tab label for Shops Staff.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabShopsKitchen: {
    text: 'Kitchen',
    description: 'Section tab label for Shops Kitchen.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabShopsLicences: {
    text: 'Licences',
    description: 'Section tab label for Shops Licences.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabShopsPlaces: {
    text: 'Outlets & zones',
    description: 'Setup tab label for Outlets & zones in Shops sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabShopsWorkforce: {
    text: 'Staff',
    description: 'Setup tab label for Staff in Shops sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabShopsTemplates: {
    text: 'Templates',
    description: 'Setup tab label for Templates in Shops sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabShopsRosters: {
    text: 'Rotas',
    description: 'Setup tab label for Rotas in Shops sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabShopsCompliance: {
    text: 'Compliance',
    description: 'Setup tab label for Compliance in Shops sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabShopsImport: {
    text: 'POS CSV import',
    description: 'Setup tab label for POS CSV import in Shops sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabShopsWhatsApp: {
    text: 'On WhatsApp',
    description: 'Setup tab label for On WhatsApp in Shops sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsWizardTitle: {
    text: 'Set up Shops',
    description: 'Title for Shops setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsWizardAdminNotice: {
    text: 'Ask an admin to set up Shops',
    description: 'Notice shown to non-admin users before Shops setup.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsShopsWizardBusinessNameLabel: {
    text: 'Business name',
    description: 'Input label for Business name in Shops setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsWizardBusinessNamePlaceholder: {
    text: 'e.g. Fresh Basket Retail',
    description: 'Placeholder for Business name in Shops setup wizard.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsShopsWizardOutletsLabel: {
    text: 'Outlets',
    description: 'Label for Outlets section in Shops setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsWizardAddOutlet: {
    text: 'Add outlet',
    description: 'Button to add another outlet row in Shops setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsWizardRemoveOutlet: {
    text: 'Remove outlet',
    description: 'Button to remove an outlet in Shops setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsWizardOutletNamePlaceholder: {
    text: 'Outlet name (e.g. Baner)',
    description: 'Placeholder for outlet name in Shops setup wizard.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsShopsWizardSelectType: {
    text: 'Select type preset',
    description: 'Placeholder or label to select type preset for an outlet.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsWizardTypeGeneral: {
    text: 'General / Supermarket / Kirana',
    description: 'Outlet type option for General retail.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsShopsWizardTypeApparel: {
    text: 'Apparel & Electronics',
    description: 'Outlet type option for Apparel & Electronics.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsWizardTypeFood: {
    text: 'Food & Bakery',
    description: 'Outlet type option for Food & Bakery.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsWizardTypePharmacy: {
    text: 'Pharmacy Operations',
    description: 'Outlet type option for Pharmacy Operations.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsWizardTypeRestaurant: {
    text: 'Restaurant / Cloud Kitchen',
    description: 'Outlet type option for Restaurant & Cloud Kitchen.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsWizardZonesLabel: {
    text: 'Zones (optional)',
    description: 'Label for Zones section in Shops setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsWizardAddZone: {
    text: 'Add zone',
    description: 'Button to add another zone row in Shops setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsWizardRemoveZone: {
    text: 'Remove zone',
    description: 'Button to remove a zone row in Shops setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsWizardZoneNamePlaceholder: {
    text: 'Zone name (e.g. Counter 1)',
    description: 'Placeholder for zone name in Shops setup wizard.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsShopsWizardZoneKindFloor: {
    text: 'Floor',
    description: 'Zone kind option for Floor.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsWizardZoneKindCounter: {
    text: 'Counter',
    description: 'Zone kind option for Counter.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsWizardZoneKindGodown: {
    text: 'Godown',
    description: 'Zone kind option for Godown.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsWizardZoneKindColdRoom: {
    text: 'Cold Room',
    description: 'Zone kind option for Cold Room.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsWizardZoneKindStation: {
    text: 'Kitchen Station',
    description: 'Zone kind option for Kitchen Station.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsWizardSubmit: {
    text: 'Save & continue',
    description: 'Submit button in Shops setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsWizardSubmitting: {
    text: 'Setting up...',
    description: 'Submitting text in Shops setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsWizardDoneTitle: {
    text: 'Shops setup complete',
    description: 'Completion title in Shops setup wizard.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsWizardImportTitle: {
    text: 'Import items from your POS (CSV)',
    description: 'Title for POS CSV import step in Shops setup wizard.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsShopsWizardImportDesc: {
    text: 'Upload your inventory or items exported from your billing system.',
    description: 'Description for POS CSV import step in Shops setup wizard.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsShopsWizardImportButton: {
    text: 'Import POS items (CSV)',
    description: 'Button to open POS CSV import.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsWizardImportNote: {
    text: 'POS CSV import will open here once available.',
    description: 'Note under POS CSV import button in shops setup wizard.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsShopsWizardSkipButton: {
    text: 'Skip for now',
    description: 'Button to skip POS CSV import and finish setup.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlacesLoadFailed: {
    text: 'Could not load places.',
    description: 'Heading when loading places subscription fails in ops hub.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlacesLoadFailedBody: {
    text: 'Please check your connection and try again.',
    description: 'Body message when loading places subscription fails in ops hub.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsShopsWizardPermissionDenied: {
    text: 'Permission denied. Only admins can configure Shops.',
    description: 'Error message when user lacks permission to set up Shops.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsShopsWizardAlreadyExists: {
    text: 'Shops places already exist or operation was already completed.',
    description: 'Error message when opId or places already exist.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsShopsWizardModuleDisabled: {
    text: 'The Shops module is not enabled for this workspace.',
    description: 'Error message when Shops module is disabled.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsShopsWizardErrorGeneric: {
    text: 'Could not complete Shops setup. Please check details and try again.',
    description: 'Generic error message for Shops setup wizard.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsHospitalSetupTitle: {
    text: 'Hospital Facility Setup',
    description: 'Title for hospital facility setup wizard.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHospitalSetupSubtitle: {
    text: 'Configure your hospital facility, departments, wards, and duty roles',
    description: 'Subtitle for hospital facility setup wizard.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsHospitalSetupAdminNotice: {
    text: 'Only workspace Owners and Admins can configure the hospital facility.',
    description: 'Admin notice for hospital facility setup wizard.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsHospitalSetupStep1: {
    text: 'Facility Name',
    description: 'Step 1 title in hospital facility setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupFacilityNameLabel: {
    text: 'Facility Name',
    description: 'Label for facility name input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupFacilityNamePlaceholder: {
    text: 'e.g. City General Hospital',
    description: 'Placeholder for facility name input.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHospitalSetupStep2: {
    text: 'Departments',
    description: 'Step 2 title in hospital facility setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupDepartmentsDesc: {
    text: 'Select departments operating in this facility',
    description: 'Description for departments selection step.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsHospitalSetupAddDepartment: {
    text: 'Add Department',
    description: 'Button to add a custom department.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupCustomDeptKey: {
    text: 'Department Key',
    description: 'Label for custom department key input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupCustomDeptLabel: {
    text: 'Department Name',
    description: 'Label for custom department label input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupStep3: {
    text: 'Units & Wards',
    description: 'Step 3 title in hospital facility setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupWardsDesc: {
    text: 'Configure wards, rooms, and beds for each department',
    description: 'Description for wards step.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsHospitalSetupAddWard: {
    text: 'Add Ward',
    description: 'Button to add a ward.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupWardName: {
    text: 'Ward / Unit Name',
    description: 'Label for ward name input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupWardNamePlaceholder: {
    text: 'e.g. General Ward 1',
    description: 'Placeholder for ward name input.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHospitalSetupModeRoomsBeds: {
    text: 'Rooms & Beds',
    description: 'Mode for ward layout with rooms and beds.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupModeBedsOnly: {
    text: 'Beds Only',
    description: 'Mode for ward layout with beds only.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupRoomCount: {
    text: 'Rooms',
    description: 'Label for room count.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHospitalSetupBedsPerRoom: {
    text: 'Beds per Room',
    description: 'Label for beds per room count.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHospitalSetupBedCount: {
    text: 'Beds',
    description: 'Label for bed count.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHospitalSetupTotalBeds: {
    text: '{count} beds',
    description: 'Total beds label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupTotalPlaces: {
    text: 'Total places: {count} / 500',
    description: 'Total places count indicator.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHospitalSetupMaxPlacesWarning: {
    text: 'Maximum 500 places per setup run exceeded ({count}/500). Please reduce units, rooms, or beds.',
    description: 'Warning when places exceed 500 limit.',
    surface: 'shell',
    maxChars: 110,
  },
  NLSOpsHospitalSetupStep4: {
    text: 'Duty Chain',
    description: 'Step 4 title in hospital facility setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupDutyChainDesc: {
    text: 'Assign default duty chain roles for each unit/ward',
    description: 'Description for duty chain step.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsHospitalSetupInChargeRole: {
    text: 'Unit In-Charge',
    description: 'Label for in-charge role picker.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupBackupRole: {
    text: 'Unit Backup',
    description: 'Label for backup role picker.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupOnCallRole: {
    text: 'Unit On-Call',
    description: 'Label for on-call role picker.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupStep5: {
    text: 'Review & Create',
    description: 'Step 5 title in hospital facility setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupReviewSummary: {
    text: 'Facility Summary',
    description: 'Summary heading in review step.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupNext: {
    text: 'Next',
    description: 'Next step button in hospital wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHospitalSetupBack: {
    text: 'Back',
    description: 'Back step button in hospital wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHospitalSetupSubmit: {
    text: 'Create Facility',
    description: 'Submit button text in setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupSubmitting: {
    text: 'Creating Facility...',
    description: 'Submitting state in setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupStep6: {
    text: 'Setup Complete',
    description: 'Step 6 title in hospital facility setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupDoneTitle: {
    text: 'Facility Setup Complete',
    description: 'Success screen title.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHospitalSetupDoneDesc: {
    text: 'Your hospital facility and wards have been created.',
    description: 'Success screen description.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsHospitalSetupImportBedsTitle: {
    text: 'Import Beds from CSV',
    description: 'CSV import card title on done screen.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsHospitalSetupImportBedsDesc: {
    text: 'Bulk import bed numbers and details from a spreadsheet.',
    description: 'CSV import card description on done screen.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsHospitalSetupImportBedsButton: {
    text: 'Import Beds from CSV',
    description: 'Button to open CSV actions on done screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHospitalSetupGoToPlaces: {
    text: 'View Places',
    description: 'Button to view created places.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHospitalSetupRoomNamePattern: {
    text: 'Room {room}',
    description: 'Generated room name pattern.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHospitalSetupBedNamePattern: {
    text: 'Bed {room}-{bed}',
    description: 'Generated bed name pattern under a room.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHospitalSetupBedOnlyPattern: {
    text: 'Bed {bed}',
    description: 'Generated bed name pattern for beds only.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHospitalSetupPermissionDenied: {
    text: 'Permission denied. You must be an Owner or Admin.',
    description: 'Permission error in hospital wizard.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsHospitalSetupAlreadyExists: {
    text: 'Facility already exists or setup was already completed.',
    description: 'Already exists error in hospital wizard.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsHospitalSetupErrorGeneric: {
    text: 'Could not complete hospital setup. Please check details and try again.',
    description: 'Generic error message in hospital wizard.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsShopsStaffTitle: {
    text: 'Staff',
    description: 'Title for Shops Staff tab.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsStaffSubtitle: {
    text: 'Rota per outlet · attendance by outlet QR · leave and swaps · no GPS tracking',
    description: 'Subtitle for Shops Staff tab.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsShopsStaffPrintOutletQr: {
    text: 'Print outlet QR',
    description: 'Button to print QR sheet for an outlet.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsStaffOpenRota: {
    text: 'Open rota',
    description: 'Button to open rotas for an outlet.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsStaffOutletPickerLabel: {
    text: 'Outlet',
    description: 'Label for selecting an outlet in Shops Staff tab.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsStaffNoOutlets: {
    text: 'No outlets found. Set up an outlet in Outlets & zones first.',
    description: 'Empty state when no outlets exist in Shops sector.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsShopsStaffRollCardTitle: {
    text: "Today's roll",
    description: "Title of Today's roll card in Shops Staff tab.",
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsStaffRollPersonCol: {
    text: 'Person',
    description: 'Person column header in roll table.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsStaffRollShiftCol: {
    text: 'Shift',
    description: 'Shift column header in roll table.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsStaffRollStatusCol: {
    text: 'Status',
    description: 'Status column header in roll table.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsStaffRollHowCol: {
    text: 'How',
    description: 'How column header in roll table indicating attendance source.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsStaffRollActionsCol: {
    text: 'Actions',
    description: 'Actions column header in roll table.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsStaffRollEmpty: {
    text: 'No rostered staff for today.',
    description: 'Empty message when no staff is rostered today.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsShopsStaffRollNotice: {
    text: "Proxy marks and corrections need the manager's approval (SO-P16).",
    description: 'Footnote notice on roll card.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsShopsStaffHowQr: {
    text: 'Scanned outlet QR',
    description: 'How text when attendance was recorded via QR scan.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsStaffHowProxyPending: {
    text: 'Proxy by {name} · pending',
    description: 'How text when marked by colleague pending approval.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsShopsStaffHowProxyApproved: {
    text: 'Proxy by {name} (approved)',
    description: 'How text when peer proxy was approved.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsShopsStaffHowProxyRejected: {
    text: 'Proxy by {name} (rejected)',
    description: 'How text when peer proxy was rejected.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsShopsStaffHowManager: {
    text: 'Roll by manager',
    description: 'How text when marked manually by manager.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsStaffApproveProxy: {
    text: 'Approve',
    description: 'Button to approve pending peer proxy mark.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsStaffRejectProxy: {
    text: 'Reject',
    description: 'Button to reject pending peer proxy mark.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsStaffRejectDialogTitle: {
    text: 'Reject peer proxy mark',
    description: 'Title of reject dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsStaffRejectReasonLabel: {
    text: 'Reason for rejection',
    description: 'Label for rejection reason input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsStaffRejectReasonPlaceholder: {
    text: 'Provide reason for rejecting proxy mark (at least 3 characters)...',
    description: 'Placeholder for rejection reason input.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsShopsStaffRejectSubmit: {
    text: 'Reject mark',
    description: 'Confirm reject button.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsStaffRejectCancel: {
    text: 'Cancel',
    description: 'Cancel button in reject dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsStaffLeaveSwapsTitle: {
    text: 'Leave and swaps',
    description: 'Title of leave and swaps card in Shops Staff tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsStaffLeaveSwapsEmpty: {
    text: 'No pending leave or swap requests for this outlet.',
    description: 'Empty state for leave and swaps card.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsShopsStaffDecideApprove: {
    text: 'Approve',
    description: 'Approve button for leave/swap change request.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsStaffDecideReject: {
    text: 'Reject',
    description: 'Reject button for leave/swap change request.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsStaffGraceMinutesLabel: {
    text: 'Attendance grace (minutes)',
    description: 'Label for outlet attendance grace minutes setting.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsStaffGraceMinutesHelp: {
    text: 'Minutes after shift start before check-in is marked Late (0–120).',
    description: 'Help text for attendance grace minutes.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsShopsStaffDecideSuccess: {
    text: 'Decision recorded successfully.',
    description: 'Success message after deciding request or proxy mark.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsRegisterCashCountDenominations: {
    text: 'Denominations',
    description: 'Label for denominations list in template editor.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRegisterCashCountDenominationsHelp: {
    text: 'Comma-separated positive minor unit values (e.g. 50000, 20000)',
    description: 'Help text for denominations list.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsRegisterCashCountUseDefaults: {
    text: 'Use {currency} defaults',
    description: 'Button to populate denominations with default values for workspace currency.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRegisterCashCountTolerance: {
    text: 'Tolerance ({currency} minor units)',
    description: 'Label for tolerance in template editor.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRegisterCashCountToleranceHelp: {
    text: 'Acceptable difference between counted and expected before requiring a reason',
    description: 'Help text for cash count tolerance.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsRegisterCashCountCarryFloat: {
    text: 'Carry previous count as opening float',
    description: 'Checkbox to show previous closing count as opening float.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRegisterCashCountNoteCoinCol: {
    text: 'Note / coin',
    description: 'Column header for denomination note or coin.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRegisterCashCountQtyCol: {
    text: 'Count',
    description: 'Column header for quantity of notes/coins.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRegisterCashCountAmountCol: {
    text: 'Amount',
    description: 'Column header for calculated amount.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRegisterCashCountLooseLabel: {
    text: 'Loose cash',
    description: 'Label for loose cash input in denomination grid.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRegisterCashCountExpectedLabel: {
    text: 'Expected (billing / POS)',
    description: 'Label for expected cash input from billing system.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsRegisterCashCountOpeningFloat: {
    text: 'Opening float (previous closing: {amount})',
    description: 'Display line showing opening float from previous closing count.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsRegisterCashCountCountedTotal: {
    text: 'Counted',
    description: 'Label for total cash counted.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRegisterCashCountDifferenceTotal: {
    text: 'Difference',
    description: 'Label for difference between counted and expected.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRegisterCashCountMismatchReasonLabel: {
    text: 'Reason for mismatch (required)',
    description: 'Label for mismatch reason textarea.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRegisterCashCountMismatchReasonPlaceholder: {
    text: 'Provide reason for difference exceeding tolerance (at least 3 characters)...',
    description: 'Placeholder for mismatch reason textarea.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsRegisterCoSignToggle: {
    text: 'Require second-person co-sign',
    description: 'Toggle in template editor to require co-signer.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRegisterCoSignLabelInput: {
    text: 'Co-sign label',
    description: 'Label input for co-sign role description.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRegisterCoSignDefaultLabel: {
    text: 'Co-sign',
    description: 'Default co-sign label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRegisterCoSignPickerLabel: {
    text: 'Select co-signer',
    description: 'Label for co-signer selector in record form.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRegisterCoSignPickerPlaceholder: {
    text: 'Choose an active member or worker...',
    description: 'Placeholder for co-signer picker.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsRegisterCoSignStateAwaiting: {
    text: 'Awaiting co-sign',
    description: 'Badge text when record is awaiting second signature.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRegisterCoSignStateSigned: {
    text: 'Co-signed',
    description: 'Badge text when record has been co-signed.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRegisterCoSignStateDisputed: {
    text: 'Co-sign disputed',
    description: 'Badge text when record co-sign was disputed.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRegisterCoSignStateProxySigned: {
    text: 'Proxy co-signed',
    description: 'Badge text when record was proxy co-signed.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRegisterCoSignActionAgree: {
    text: 'Co-sign',
    description: 'Button to agree and co-sign.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRegisterCoSignActionDisagree: {
    text: 'Dispute',
    description: 'Button to disagree and dispute co-sign.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRegisterCoSignDialogTitle: {
    text: 'Co-sign register',
    description: 'Dialog title for record co-signing.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRegisterCoSignProxyDialogTitle: {
    text: 'Proxy co-sign register',
    description: 'Dialog title for record proxy co-signing.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRegisterCoSignActionProxy: {
    text: 'Proxy co-sign',
    description: 'Button to proxy co-sign for a worker without phone.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRegisterCoSignDisputeReasonLabel: {
    text: 'Dispute reason (required)',
    description: 'Label for dispute reason input.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsRegisterCoSignDisputeReasonPlaceholder: {
    text: 'Explain why you dispute this count (at least 3 characters)...',
    description: 'Placeholder for dispute reason input.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsRegisterCoSignProxyReasonLabel: {
    text: 'Proxy reason (required)',
    description: 'Label for proxy co-sign reason input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRegisterCoSignProxyReasonPlaceholder: {
    text: 'State reason for proxy co-signing on behalf of worker (at least 3 characters)...',
    description: 'Placeholder for proxy reason input.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsRegisterCoSignSignerNotice: {
    text: 'Signed by {name}',
    description: 'Notice showing who signed the record.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRegisterCoSignDisputeNotice: {
    text: 'Disputed by {name}: "{reason}"',
    description: 'Notice showing who disputed and why.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsRegisterCoSignProxyNotice: {
    text: 'Proxy co-signed by {name}: "{reason}"',
    description: 'Notice showing who proxy signed and why.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsRegisterCoSignConfirm: {
    text: 'Confirm',
    description: 'Button to confirm co-sign or dispute.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRegisterCoSignCancel: {
    text: 'Cancel',
    description: 'Button to cancel co-sign dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsCashTitle: {
    text: 'Cash count & shift handover',
    description: 'Title for Shops Cash screen.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsCashSubtitle: {
    text: '{outlet} · locked once both sign',
    description: 'Subtitle for Shops Cash screen.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsShopsCashWarnBox: {
    text: 'Store manager and owner get an exception. The record stays locked; a correction can only be added, never overwrite (A4).',
    description: 'Warning notice on cash handover screen.',
    surface: 'shell',
    maxChars: 130,
  },
  NLSOpsShopsCashStartButtonsTitle: {
    text: 'Start cash & handover register',
    description: 'Title for Start buttons card in Shops Cash tab.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsShopsCashTodayTitle: {
    text: "Today's registers",
    description: 'Title for today registers card in Shops Cash tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsCashTodayEmpty: {
    text: 'No registers recorded today for this outlet.',
    description: 'Empty message for today registers.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsShopsCashColTemplate: {
    text: 'Register',
    description: 'Column header for register template name.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsCashColAuthor: {
    text: 'Submitted by',
    description: 'Column header for who submitted the record.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsCashColTime: {
    text: 'Time',
    description: 'Column header for occurred/received time.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsCashColState: {
    text: 'Status',
    description: 'Column header for record status.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsCashColCoSign: {
    text: 'Co-sign',
    description: 'Column header for co-sign state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsCashColActions: {
    text: 'Actions',
    description: 'Column header for actions.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsCashNeedsYourCoSignTitle: {
    text: 'Needs your co-sign',
    description: 'Card title for records awaiting caller co-sign.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsCashNeedsYourCoSignEmpty: {
    text: 'No registers are currently awaiting your co-sign.',
    description: 'Empty message for awaiting co-sign card.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsShopsCashMismatchesTitle: {
    text: 'Cash mismatches (last 7 days)',
    description: 'Card title for recent cash mismatches.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsCashMismatchesEmpty: {
    text: 'No cash mismatches in the last 7 days.',
    description: 'Empty message for mismatches card.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsShopsCashColOutlet: {
    text: 'Outlet',
    description: 'Column header for outlet place name.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsCashColDiff: {
    text: 'Difference',
    description: 'Column header for cash difference.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsCashColReason: {
    text: 'Reason',
    description: 'Column header for mismatch reason.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsCashViewRecord: {
    text: 'View',
    description: 'Button to view record.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsShopsCashNoOutlets: {
    text: 'No outlets configured yet. Set up an outlet in Places first.',
    description: 'Notice when no outlets exist.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsShopsCashNoTemplates: {
    text: 'No approved cash count or opening/closing templates found. Create one in Templates.',
    description: 'Notice when no suitable templates exist for Cash tab.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsShopsCashCurrencyNotice: {
    text: 'Workspace currency is not configured. Cash count registers require workspace currency.',
    description: 'Notice when workspace currency is not configured.',
    surface: 'shell',
    maxChars: 95,
  },
  NLSOpsShopsAuditsTitle: {
    text: 'Display audits',
    description: 'Title for Display audits tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsAuditsSubtitle: {
    text: 'Photo checklists per zone: price tags, shelf and display, label presence · evidence, not surveillance',
    description: 'Subtitle for Display audits tab.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsShopsAuditsStartButton: {
    text: 'Start audit',
    description: 'Button label to start a new display audit.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShopsAuditsDialogTitle: {
    text: 'Start display audit',
    description: 'Dialog title for starting a display audit.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsAuditsOutletLabel: {
    text: 'Outlet',
    description: 'Outlet label in display audits screen.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsAuditsDialogOutletLabel: {
    text: 'Outlet',
    description: 'Outlet select label in start audit dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsAuditsDialogZoneLabel: {
    text: 'Zone (optional)',
    description: 'Zone select label in start audit dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsAuditsDialogZoneNone: {
    text: 'Whole outlet (no specific zone)',
    description: 'Option for no specific zone in start audit dialog.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsShopsAuditsDialogTemplateLabel: {
    text: 'Audit template',
    description: 'Template select label in start audit dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsAuditsDialogNoTemplates: {
    text: 'No approved display audit templates found. Create one in Templates.',
    description: 'Notice in dialog when no display audit templates exist.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsShopsAuditsDialogCancel: {
    text: 'Cancel',
    description: 'Cancel button in start audit dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsAuditsDialogSubmit: {
    text: 'Start',
    description: 'Start button in start audit dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsAuditsEmptyTitle: {
    text: 'No display audits',
    description: 'Empty state title for display audits tab.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsAuditsEmptyDesc: {
    text: 'No display audits have been recorded for this outlet in the last 30 days.',
    description: 'Empty state description for display audits tab.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsShopsAuditsMakeTemplate: {
    text: 'Make an audit template',
    description: 'Action button in empty state to navigate to templates.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsAuditsFootnote: {
    text: 'Label checks are a starter list, not legal advice or certification. Photos show shelves, not people.',
    description: 'Footnote on display audits screen.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsShopsAuditsPillOk: {
    text: '{count} photos · ok',
    description: 'Pill text for audit record with no issues.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsAuditsPillIssues: {
    text: '{photos} photos · {issues} issues',
    description: 'Pill text for audit record with issues.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsShopsAuditsPhotoAlt: {
    text: 'Audit thumbnail {index}',
    description: 'Alt text for audit photo thumbnail.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsAuditsNoPhotos: {
    text: 'No photos',
    description: 'Placeholder when an audit record has no photos.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsLicencesTitle: {
    text: 'Licences and renewals',
    description: 'Title for Shops licences tab.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShopsLicencesSubtitle: {
    text: 'Calendar templates as starters · an owner and a backup for each · reminders',
    description: 'Subtitle for Shops licences tab.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsShopsLicencesColOutlet: {
    text: 'Outlet',
    description: 'Column header for outlet in shops licences tab.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShopsLicencesFootnote: {
    text: 'Starters, not legal advice. Check what applies to each outlet (SO-P29).',
    description: 'Footnote on shops licences tab.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsStarterNotCertifiedBadge: {
    text: 'Starter, not certified',
    description: 'Badge on templates and registers indicating starter content, not legally certified.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTemplateSourceLabel: {
    text: 'Source',
    description: 'Label for template source attribution.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStarterToggleLabel: {
    text: 'Starter template',
    description: 'Toggle label for starter template status.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStarterConfirmUntickRules: {
    text: 'Are you sure you want to remove the starter designation? Please confirm that you have checked and verified all statutory rules and requirements.',
    description: 'Confirmation dialog prompt when unticking starter status.',
    surface: 'shell',
    maxChars: 160,
  },
  NLSOpsStaysRegistersTitle: {
    text: 'Registers & licences',
    description: 'Title of Stays Registers and licences screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysRegistersSubtitle: {
    text: 'Starter templates, labelled "starter, not certified"',
    description: 'Subtitle of Stays Registers and licences screen.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysRegistersFooter: {
    text: 'Starters, not legal advice (SO-D203).',
    description: 'Footer on Stays Registers and licences screen.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysRegistersItemCol: {
    text: 'Item',
    description: 'Column header for item in Stays registers table.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRegistersOwnerBackupCol: {
    text: 'Owner · backup',
    description: 'Column header for owner and backup in Stays registers table.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysRegistersDueCol: {
    text: 'Due',
    description: 'Column header for due date in Stays registers table.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRegistersStateCol: {
    text: 'State',
    description: 'Column header for state in Stays registers table.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRegistersReviewAndTurnOn: {
    text: 'Review and turn on',
    description: 'Action link on starter registers to review template and turn on schedule.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysRegistersAddToCalendar: {
    text: 'Add to calendar',
    description: 'Action button to add a licence to the compliance calendar.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysRegistersNotOnCalendarYet: {
    text: 'Not on the calendar yet',
    description: 'State label for licence not yet created on calendar.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysRegistersSelectProperty: {
    text: 'Property',
    description: 'Label for property picker in Stays registers screen.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRegistersNoProperties: {
    text: 'No properties available in this workspace.',
    description: 'Empty state message when no properties exist.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysRegistersScheduleRunning: {
    text: 'Running',
    description: 'Status label for running schedule.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysRegistersSchedulePaused: {
    text: 'Paused',
    description: 'Status label for paused schedule.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysCadenceDaily: {
    text: 'Daily',
    description: 'Daily cadence label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysCadenceWeekly: {
    text: 'Weekly',
    description: 'Weekly cadence label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysCadenceMonthly: {
    text: 'Monthly',
    description: 'Monthly cadence label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWeekdayMon: {
    text: 'Mon',
    description: 'Monday abbreviation.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysWeekdayTue: {
    text: 'Tue',
    description: 'Tuesday abbreviation.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysWeekdayWed: {
    text: 'Wed',
    description: 'Wednesday abbreviation.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysWeekdayThu: {
    text: 'Thu',
    description: 'Thursday abbreviation.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysWeekdayFri: {
    text: 'Fri',
    description: 'Friday abbreviation.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysWeekdaySat: {
    text: 'Sat',
    description: 'Saturday abbreviation.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysWeekdaySun: {
    text: 'Sun',
    description: 'Sunday abbreviation.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysUnknownRole: {
    text: 'Staff',
    description: 'Fallback role label when duty role is unknown.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysRestrictedCalendar: {
    text: 'On the calendar (restricted)',
    description: 'Shown when compliance item exists but caller has restricted access.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysReviewTemplate: {
    text: 'Review template',
    description: 'Action button to view starter template detail.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysTurnOnSchedule: {
    text: 'Turn on schedule',
    description: 'Action button to view and enable paused schedule.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysConfirmUntickStarterTitle: {
    text: 'Untick Starter Template',
    description: 'Title of confirmation modal when removing starter label from template.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysConfirmUntickStarterBody: {
    text: "This removes the 'Starter, not certified' badge. Confirm that you have reviewed and verified all rules against primary statutory sources.",
    description: 'Body text explaining requirement to check rules before removing starter label.',
    surface: 'shell',
    maxChars: 180,
  },
  NLSOpsStaysConfirmUntickStarterBtn: {
    text: 'Confirm',
    description: 'Confirm button to remove starter label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysTitle: {
    text: 'Stays',
    description: 'Title for Stays Hub screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysSubtitle: {
    text: 'Hotels, homestays, serviced apartments, and hostel operations',
    description: 'Subtitle for Stays Hub screen.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsTabStaysToday: {
    text: 'Today',
    description: 'Section tab label for Stays Today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysBoard: {
    text: 'Room board',
    description: 'Section tab label for Room board.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysHousekeeping: {
    text: 'Housekeeping',
    description: 'Section tab label for Housekeeping.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysRequests: {
    text: 'Guest requests',
    description: 'Section tab label for Guest requests.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysLinen: {
    text: 'Linen & minibar',
    description: 'Section tab label for Linen & minibar.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysLost: {
    text: 'Lost & found',
    description: 'Section tab label for Lost & found.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysGroup: {
    text: 'Group view',
    description: 'Extra tab label for Group view.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysExtras: {
    text: 'Spa, pool & extras',
    description: 'Extra tab label for Spa, pool & extras.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysExtrasTitle: {
    text: 'Spa, pool & extras',
    description: 'Title for the Stays extras desk screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysExtrasSubtitle: {
    text: 'Guest and visitor bookings for hotel extras. Rooms are never bookable.',
    description: 'Subtitle for the Stays extras desk screen.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysExtrasTabSetup: {
    text: 'Extras setup',
    description: 'Subtab label for managing extras bookables and suggested presets.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasFilterAllKinds: {
    text: 'All extras',
    description: 'Filter option to display all extra kinds.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasFilterAllPlaces: {
    text: 'All places',
    description: 'Filter option to display extras from all places.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtraKindSpa: {
    text: 'Spa',
    description: 'Label for Spa extra kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtraKindPool: {
    text: 'Pool',
    description: 'Label for Pool extra kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtraKindGym: {
    text: 'Gym',
    description: 'Label for Gym extra kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtraKindMeeting: {
    text: 'Meeting',
    description: 'Label for Meeting extra kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtraKindBanquet: {
    text: 'Banquet',
    description: 'Label for Banquet extra kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtraKindActivity: {
    text: 'Activity',
    description: 'Label for Activity extra kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtraKindShuttle: {
    text: 'Shuttle',
    description: 'Label for Shuttle extra kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtraKindDayPass: {
    text: 'Day pass',
    description: 'Label for Day pass extra kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtrasWaitlistCount: {
    text: '{count} waitlist',
    description: 'Label showing waitlist count on a session.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasBookForRoomBtn: {
    text: 'Book for room',
    description: 'Action button to book an extra for an in-house room.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysExtrasBookOutsideBtn: {
    text: 'Outside visitor',
    description: 'Action button to book an extra for an outside visitor.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysExtrasBookRoomTitle: {
    text: 'Book extra for room',
    description: 'Title for the book for a room dialog sheet.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysExtrasRoomLabel: {
    text: 'Occupied room',
    description: 'Label for selecting an occupied room.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysExtrasSelectRoomPlaceholder: {
    text: 'Select occupied room',
    description: 'Placeholder for selecting an occupied room.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysExtrasNoOccupiedRooms: {
    text: 'No occupied rooms found for this property.',
    description: 'Notice when no occupied rooms exist for booking.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysExtrasNoPropertyResolved: {
    text: 'No hotel property could be resolved for this extra.',
    description: 'Warning shown when the bookable place does not belong to a property.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysExtrasVisitorCustomerFallback: {
    text: 'Customer',
    description: 'Fallback label for outside visitor customer when display name is missing.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasGuestsLabel: {
    text: 'Guests (1–20)',
    description: 'Label for number of guests.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasJoinWaitlist: {
    text: 'Join waitlist if session is full',
    description: 'Checkbox to join waitlist when session is full.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysExtrasConfirmBooking: {
    text: 'Confirm booking',
    description: 'Submit button to confirm an extra booking.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysExtrasCancelBtn: {
    text: 'Cancel',
    description: 'Button to cancel a dialog or operation.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtrasBookingRoomInfo: {
    text: 'Room {room} · {guests} guests',
    description: 'Display text for an in-house booking row showing room and guests.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysExtrasInHouseChip: {
    text: 'In-house',
    description: 'Chip indicating an in-house room booking.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtrasMarkAttended: {
    text: 'Attended',
    description: 'Button or status for marking a booking as attended.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtrasAddBtn: {
    text: 'Add extra',
    description: 'Button to add a new Stays bookable extra.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasEditBtn: {
    text: 'Edit',
    description: 'Button to edit an existing Stays bookable extra.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtrasDialogAddTitle: {
    text: 'Add Stays extra',
    description: 'Dialog title when adding a Stays extra bookable.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysExtrasDialogEditTitle: {
    text: 'Edit Stays extra',
    description: 'Dialog title when editing a Stays extra bookable.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysExtrasNameLabel: {
    text: 'Name',
    description: 'Label for bookable name.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasKindLabel: {
    text: 'Kind',
    description: 'Label for bookable kind (SLOT/APPOINTMENT/RESOURCE).',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasExtraKindLabel: {
    text: 'Extra type',
    description: 'Label for extraKind selection (Spa, Pool, etc.).',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasPlaceLabel: {
    text: 'Location (Area or Property)',
    description: 'Label for place selection limited to AREA or PROPERTY.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysExtrasDurationLabel: {
    text: 'Duration (minutes)',
    description: 'Label for duration in minutes.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysExtrasCapacityLabel: {
    text: 'Capacity',
    description: 'Label for capacity.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysExtrasGuestBookableToggle: {
    text: 'Allow room QR guest booking',
    description: 'Toggle to allow guests to book this extra from room QR.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysExtrasGuestBookableHelp: {
    text: 'Guests scanning their room QR code will see this extra in their options.',
    description: 'Help text for guestBookable toggle.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsStaysExtrasPublicVisitorsToggle: {
    text: 'Public for outside visitors',
    description: 'Toggle to allow outside visitors to book this extra.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysExtrasPublicVisitorsHelp: {
    text: 'Day passes are public by default. Enable this to make other extras bookable on the public booking page.',
    description: 'Help text for publicForVisitors toggle.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsStaysStaffOnlyNotice: {
    text: 'Staff log extras for guests; there is no public booking page for hotels.',
    description: 'Notice explaining that stays extras are managed by staff only with no customer-facing booking.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsStaysSuggestedExtrasTitle: {
    text: 'Suggested extras',
    description: 'Title for suggested extras preset card.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysSuggestedExtrasDesc: {
    text: 'One-tap drafts per property preset, created inactive for review.',
    description: 'Description for suggested extras card.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysSuggestedExtrasAddBtn: {
    text: 'Add {name}',
    description: 'Button to add a suggested extra draft.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysSuggestedExtrasAdded: {
    text: 'Added',
    description: 'Indicator that a suggested extra has already been added.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtrasInactiveChip: {
    text: 'Inactive',
    description: 'Chip indicating an inactive bookable.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysExtrasActiveChip: {
    text: 'Active',
    description: 'Chip indicating an active bookable.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBookingCustomerNameLabel: {
    text: 'Customer ID / Name',
    description: 'Label for customer ID or name input in outside booking.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBookingCustomerNamePlaceholder: {
    text: 'Enter customer ID or name',
    description: 'Placeholder for customer ID or name input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestExtraLabel: {
    text: 'Extra bookable',
    description: 'Detail label for requested extra bookable in RequestsScreen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestPreferredTimeLabel: {
    text: 'Preferred time',
    description: 'Detail label for preferred time in RequestsScreen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestGuestsLabel: {
    text: 'Guests',
    description: 'Detail label for guests count in RequestsScreen.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestSessionPickerLabel: {
    text: 'Assign session',
    description: 'Label for assigning a session to confirm an extra booking request.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestConfirmExtraBtn: {
    text: 'Confirm extra booking',
    description: 'Button to confirm extra booking in RequestsScreen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestDeclineExtraBtn: {
    text: 'Decline extra booking',
    description: 'Button to decline extra booking in RequestsScreen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestDeclineDialogTitle: {
    text: 'Decline guest extra request',
    description: 'Dialog title for declining guest extra request.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsRequestDeclineReasonLabel: {
    text: 'Decline reason (3–200 characters)',
    description: 'Label for decline reason input.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRequestDeclineReasonPlaceholder: {
    text: 'Reason for declining this request (3–200 characters)…',
    description: 'Placeholder for declining extra request in RequestsScreen.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsRequestDeclineSubmitBtn: {
    text: 'Decline request',
    description: 'Button to submit decline in RequestsScreen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestCapacityRefusalInline: {
    text: 'Session is full. Request remains open.',
    description: 'Inline notice when extra request confirmation fails due to capacity.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRequestConfirmedBookingLink: {
    text: 'Confirmed booking: {bookingId}',
    description: 'Link/display to the confirmed booking in RequestsScreen.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsGuestRoomNoExtrasAvailable: {
    text: 'No extras are currently available for room booking.',
    description: 'Empty state message on GuestRoomPage when no guestBookables are offered.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsExtraRequestTimePlaceholder: {
    text: 'e.g. 4:00 PM',
    description: 'Placeholder for preferred time text on GuestRoomPage.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysExtrasErrRoomVacant: {
    text: 'Cannot book extra for a vacant room. Room must be occupied.',
    description: 'Error when booking extra for a vacant room.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsStaysExtrasErrCapacity: {
    text: 'Session capacity is full. Please choose another session or join waitlist.',
    description: 'Error when session is full.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsStaysExtrasErrPropertyMismatch: {
    text: 'Bookable and room must belong to the same property.',
    description: 'Error when bookable and room are at different properties.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsStaysExtrasErrPermission: {
    text: 'You do not have permission to manage Stays extras.',
    description: 'Error when user lacks permission.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysExtrasErrNotFound: {
    text: 'The requested bookable, room or session was not found.',
    description: 'Error when item not found.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysExtrasErrInvalid: {
    text: 'Invalid booking details provided. Please check inputs.',
    description: 'Error when input is invalid.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysExtrasErrGeneric: {
    text: 'Failed to process extra booking. Please try again.',
    description: 'Generic error for Stays extras.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsTabStaysPms: {
    text: 'PMS link',
    description: 'Extra tab label for PMS link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysRegisters: {
    text: 'Registers & licences',
    description: 'Extra tab label for Registers & licences.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabStaysPlaces: {
    text: 'Rooms, beds & areas',
    description: 'Setup tab label for Rooms, beds & areas in Stays sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabStaysWorkforce: {
    text: 'Staff',
    description: 'Setup tab label for Staff in Stays sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysTemplates: {
    text: 'Room checklists',
    description: 'Setup tab label for Room checklists in Stays sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysRosters: {
    text: 'Rosters',
    description: 'Setup tab label for Rosters in Stays sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysAssets: {
    text: 'Room assets',
    description: 'Setup tab label for Room assets in Stays sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysCompliance: {
    text: 'Licences',
    description: 'Setup tab label for Licences in Stays sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabStaysImport: {
    text: 'Departures CSV',
    description: 'Setup tab label for Departures CSV import in Stays sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabStaysWhatsApp: {
    text: 'On WhatsApp',
    description: 'Setup tab label for On WhatsApp in Stays sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsTitle: {
    text: 'Jobs',
    description: 'Title for Jobs Hub screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSubtitle: {
    text: 'Field service, installers, repairs, and recurring AMC visits',
    description: 'Subtitle for Jobs Hub screen.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsTabJobsToday: {
    text: 'Today',
    description: 'Section tab label for Jobs Today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsIntake: {
    text: 'Intake',
    description: 'Section tab label for Jobs Intake.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsCustomers: {
    text: 'Customers & assets',
    description: 'Section tab label for Jobs Customers & assets.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabJobsAmc: {
    text: 'AMC & warranty',
    description: 'Section tab label for Jobs AMC & warranty.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsParts: {
    text: 'Parts',
    description: 'Section tab label for Jobs Parts.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsMessages: {
    text: 'Messages',
    description: 'Section tab label for Jobs Messages.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsPlaces: {
    text: 'Service areas',
    description: 'Setup tab label for Service areas in Jobs sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabJobsWorkforce: {
    text: 'Technicians',
    description: 'Setup tab label for Technicians in Jobs sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsTemplates: {
    text: 'Job checklists',
    description: 'Setup tab label for Job checklists in Jobs sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsRosters: {
    text: 'Visit windows',
    description: 'Setup tab label for Visit windows in Jobs sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsAssets: {
    text: 'Installed assets',
    description: 'Setup tab label for Installed assets in Jobs sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsCompliance: {
    text: 'Compliance',
    description: 'Setup tab label for Compliance in Jobs sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabJobsImport: {
    text: 'Job sites CSV',
    description: 'Setup tab label for Job sites CSV import in Jobs sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabJobsWhatsApp: {
    text: 'On WhatsApp',
    description: 'Setup tab label for On WhatsApp in Jobs sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsCustomersSearchPlaceholder: {
    text: 'Search customers or tags...',
    description: 'Search placeholder on Job Customers tab.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsJobsAddCustomerBtn: {
    text: 'Customer',
    description: 'Add customer button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsAddSiteBtn: {
    text: 'Add site',
    description: 'Add service site button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsEditSiteBtn: {
    text: 'Edit site',
    description: 'Edit service site button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsAddAssetBtn: {
    text: 'Add asset',
    description: 'Add installed asset button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsNavigateBtn: {
    text: 'Navigate',
    description: 'Navigate map link button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsNoCustomersFound: {
    text: 'No customers found.',
    description: 'Empty state message when no customers match search.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsJobsContactsFetchError: {
    text: 'Failed to load customer contact details.',
    description: 'Inline notice when fetching customer contacts fails in Jobs tab.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsJobsNoSitesYet: {
    text: 'No service sites registered yet.',
    description: 'Empty state message when customer has no sites.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsJobsNoAssetsYet: {
    text: 'No installed assets registered for this site.',
    description: 'Empty state message when site has no assets.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsJobsCustomerDialogTitle: {
    text: 'Add Customer',
    description: 'Dialog title for adding customer in Jobs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsCustomerDialogEditTitle: {
    text: 'Edit Customer',
    description: 'Dialog title for editing customer in Jobs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsCustomerNameLabel: {
    text: 'Customer Name',
    description: 'Label for customer name field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsCustomerPhoneLabel: {
    text: 'Phone (E.164)',
    description: 'Label for customer phone field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsCustomerEmailLabel: {
    text: 'Email',
    description: 'Label for customer email field.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsCustomerNotesLabel: {
    text: 'Notes',
    description: 'Label for customer notes field.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsCustomerTagsLabel: {
    text: 'Tags (comma separated)',
    description: 'Label for customer tags field.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsCustomerMatchFoundTitle: {
    text: 'Customer already exists',
    description: 'Title when phone match is found for existing customer.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsJobsCustomerMatchFoundPrompt: {
    text: 'This phone number belongs to an existing customer. Add the Jobs sector to this customer?',
    description: 'Prompt when phone match is found for existing customer.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsJobsCustomerAddSectorBtn: {
    text: 'Add Jobs to this customer',
    description: 'Button to add Jobs sector to existing customer.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsJobsCustomerCancelBtn: {
    text: 'Cancel',
    description: 'Cancel button label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsCustomerSaveBtn: {
    text: 'Save',
    description: 'Save button label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsSiteDialogTitle: {
    text: 'Add Service Site',
    description: 'Dialog title for adding service site.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsSiteDialogEditTitle: {
    text: 'Edit Service Site',
    description: 'Dialog title for editing service site.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsSiteNameLabel: {
    text: 'Site Name',
    description: 'Label for site name field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSiteServiceAreaLabel: {
    text: 'Service Area',
    description: 'Label for service area field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSiteSelectAreaPlaceholder: {
    text: 'Select a service area...',
    description: 'Placeholder for service area select.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsSiteLine1Label: {
    text: 'Address Line 1',
    description: 'Label for address line 1 field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSiteLine2Label: {
    text: 'Address Line 2',
    description: 'Label for address line 2 field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSiteLocalityLabel: {
    text: 'Locality',
    description: 'Label for locality field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSiteCityLabel: {
    text: 'City',
    description: 'Label for city field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSiteRegionLabel: {
    text: 'Region',
    description: 'Label for region field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSitePostcodeLabel: {
    text: 'Postcode',
    description: 'Label for postcode field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSiteAccessNotesLabel: {
    text: 'Access Notes',
    description: 'Label for access notes field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSiteMoreGeoToggle: {
    text: 'More (Coordinates)',
    description: 'Collapsible toggle for geo coordinates.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsSiteLatLabel: {
    text: 'Latitude',
    description: 'Label for latitude field.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsSiteLngLabel: {
    text: 'Longitude',
    description: 'Label for longitude field.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsSiteUseDeviceLocationBtn: {
    text: "Use this device's location",
    description: 'Button to query browser location.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsJobsSiteLocationPermissionDenied: {
    text: 'Location access was denied.',
    description: 'Error when location permission denied.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsJobsSiteLocationUnavailable: {
    text: 'Location unavailable.',
    description: 'Error when location unavailable.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsJobsColAsset: {
    text: 'Installed asset',
    description: 'Table header for asset name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsColSerial: {
    text: 'Serial',
    description: 'Table header for serial number.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsColInstalled: {
    text: 'Installed',
    description: 'Table header for install date.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsColWarranty: {
    text: 'Warranty until',
    description: 'Table header for warranty end date.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsColAmc: {
    text: 'AMC',
    description: 'Table header for AMC status/until.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsWarrantyExpired: {
    text: 'Expired',
    description: 'Badge text when warranty has expired.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsWarrantyEndingSoon: {
    text: 'Ends in {days} days',
    description: 'Badge text when warranty ends in <= 60 days.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsAmcNone: {
    text: '—',
    description: 'Fallback text when no AMC contract exists.',
    surface: 'shell',
    maxChars: 5,
  },
  NLSOpsJobsCustomersAria: {
    text: 'Jobs customers and assets',
    description: 'Aria label for customers container.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsCustomerCardAria: {
    text: 'Customer card',
    description: 'Aria label for customer card.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsSiteCardAria: {
    text: 'Service site',
    description: 'Aria label for service site block.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsAssetTableAria: {
    text: 'Installed assets table',
    description: 'Aria label for installed assets table.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsNavigate: {
    text: 'Navigate',
    description: 'Link label for navigating to job location on map.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsMapAria: {
    text: 'Jobs map view',
    description: 'Aria label for jobs map view.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsTodayTitle: {
    text: "Today's Jobs",
    description: 'Header title for Jobs Today tab.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsDatePickerLabel: {
    text: 'Date',
    description: 'Label for board date picker.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsNewJobBtn: {
    text: 'New job',
    description: 'Button to open new job dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsScheduleBtn: {
    text: 'Schedule / assign',
    description: 'Button to open job scheduling dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsRevisitBtn: {
    text: 'Revisit',
    description: 'Button to open revisit dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsUnscheduledSectionTitle: {
    text: 'Unscheduled',
    description: 'Section header for unscheduled jobs.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsAnyTimeGroupTitle: {
    text: 'Any time',
    description: 'Group header for jobs without specific window.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsNoJobsForDate: {
    text: 'No jobs scheduled for this date.',
    description: 'Empty state message when no jobs are scheduled on selected date.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsJobsNoUnscheduledJobs: {
    text: 'No unscheduled jobs.',
    description: 'Empty state message for unscheduled jobs section.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsJobsNewJobDialogTitle: {
    text: 'New Job',
    description: 'Title for New Job dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsScheduleDialogTitle: {
    text: 'Schedule & Assign Job',
    description: 'Title for Schedule Job dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsRevisitDialogTitle: {
    text: 'Raise Revisit',
    description: 'Title for Raise Revisit dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsDialogSiteLabel: {
    text: 'Site',
    description: 'Label for site picker in dialogs.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsDialogSelectSitePlaceholder: {
    text: 'Select service site',
    description: 'Placeholder for service site select.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsJobsDialogTitleLabel: {
    text: 'Title',
    description: 'Label for job title in dialogs.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsDialogKindLabel: {
    text: 'Kind',
    description: 'Label for job kind in dialogs.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsDialogDetailsLabel: {
    text: 'Details / Reason',
    description: 'Label for job details or reason in dialogs.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsDialogDateLabel: {
    text: 'Scheduled date',
    description: 'Label for scheduled date input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsDialogWindowStartLabel: {
    text: 'Window start (HH:MM)',
    description: 'Label for window start time input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsDialogWindowEndLabel: {
    text: 'Window end (HH:MM)',
    description: 'Label for window end time input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsDialogAssigneeLabel: {
    text: 'Assign technician',
    description: 'Label for technician picker in dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsJobsDialogSelectAssigneePlaceholder: {
    text: 'Unassigned',
    description: 'Placeholder when no technician is assigned.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsDialogCancelBtn: {
    text: 'Cancel',
    description: 'Cancel button in dialogs.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsDialogSaveBtn: {
    text: 'Save',
    description: 'Save button in dialogs.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsJobsDialogRaiseBtn: {
    text: 'Raise job',
    description: 'Submit button to raise a job.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsJobsReasonLengthError: {
    text: 'Reason must be between 3 and 500 characters.',
    description: 'Validation error when revisit reason length is invalid.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysWizardTitle: {
    text: 'Set up Stays',
    description: 'Title for Stays setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardSubtitle: {
    text: 'Configure your properties, rooms, beds and checklists',
    description: 'Subtitle for Stays setup wizard.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysWizardAdminNotice: {
    text: 'Only workspace owners and administrators can set up Stays.',
    description: 'Admin notice when viewing Stays setup wizard without editor role.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysWizardGroupNameLabel: {
    text: 'Group or brand name',
    description: 'Label for hospitality group name input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardGroupNamePlaceholder: {
    text: 'e.g. Lakeview Hospitality Group',
    description: 'Placeholder for hospitality group name input.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysWizardPropertiesLabel: {
    text: 'Properties',
    description: 'Section label for properties in Stays setup wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardAddProperty: {
    text: 'Add property',
    description: 'Button to add a property in Stays setup wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardRemoveProperty: {
    text: 'Remove property',
    description: 'Button to remove a property in Stays setup wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardPropertyNamePlaceholder: {
    text: 'e.g. Hotel Lakeview',
    description: 'Placeholder for property name input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardSelectPreset: {
    text: 'Select preset…',
    description: 'Placeholder option to select a property preset.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardPresetBudget: {
    text: 'Budget hotel',
    description: 'Property preset label for Budget hotel.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardPresetBusiness: {
    text: 'Business hotel',
    description: 'Property preset label for Business hotel.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardPresetResort: {
    text: 'Resort / heritage',
    description: 'Property preset label for Resort / heritage.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardPresetHomestay: {
    text: 'Homestay / villa',
    description: 'Property preset label for Homestay / villa.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardPresetServiced: {
    text: 'Serviced apartment',
    description: 'Property preset label for Serviced apartment.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardPresetHostel: {
    text: 'Hostel',
    description: 'Property preset label for Hostel.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardBlocksLabel: {
    text: 'Blocks / floors',
    description: 'Section label for blocks/floors in Stays setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardAddBlock: {
    text: 'Add block / floor',
    description: 'Button to add a block or floor in Stays setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardRemoveBlock: {
    text: 'Remove block',
    description: 'Button to remove a block in Stays setup wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardBlockNamePlaceholder: {
    text: 'e.g. Floor 1 or East Wing',
    description: 'Placeholder for block name input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardBlockKindFloor: {
    text: 'Floor',
    description: 'Block kind label for Floor.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardBlockKindBlock: {
    text: 'Block',
    description: 'Block kind label for Block.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardBlockKindWing: {
    text: 'Wing',
    description: 'Block kind label for Wing.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardBlockKindVilla: {
    text: 'Villa',
    description: 'Block kind label for Villa.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardRoomRangeLabel: {
    text: 'Room range generator',
    description: 'Heading for room range generator.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardRoomPrefix: {
    text: 'Prefix',
    description: 'Label for room range prefix.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardRoomPrefixPlaceholder: {
    text: 'e.g. 10 or A',
    description: 'Placeholder for room prefix.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardRoomFrom: {
    text: 'From',
    description: 'Label for room range start number.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysWizardRoomTo: {
    text: 'To',
    description: 'Label for room range end number.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysWizardRoomCount: {
    text: 'Rooms: {count}',
    description: 'Count of rooms generated.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardHostelBeds: {
    text: 'Beds per room (1–24)',
    description: 'Label for beds per room in hostels.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardAreasLabel: {
    text: 'Common areas',
    description: 'Section label for common areas.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardAddArea: {
    text: 'Add area',
    description: 'Button to add a common area.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardRemoveArea: {
    text: 'Remove area',
    description: 'Button to remove a common area.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardAreaNamePlaceholder: {
    text: 'e.g. Main Lobby',
    description: 'Placeholder for common area name.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardAreaKindLobby: {
    text: 'Lobby',
    description: 'Area kind Lobby.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindCorridor: {
    text: 'Corridor',
    description: 'Area kind Corridor.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindWashroom: {
    text: 'Washroom',
    description: 'Area kind Washroom.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindPool: {
    text: 'Pool',
    description: 'Area kind Pool.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindGym: {
    text: 'Gym',
    description: 'Area kind Gym.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindSpa: {
    text: 'Spa',
    description: 'Area kind Spa.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindKitchen: {
    text: 'Kitchen',
    description: 'Area kind Kitchen.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindLaundry: {
    text: 'Laundry',
    description: 'Area kind Laundry.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindStore: {
    text: 'Store',
    description: 'Area kind Store.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindGarden: {
    text: 'Garden',
    description: 'Area kind Garden.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardAreaKindOther: {
    text: 'Other',
    description: 'Area kind Other.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysWizardRoomTypesLabel: {
    text: 'Room types',
    description: 'Section label for room types.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardAddRoomType: {
    text: 'Add room type',
    description: 'Button to add a room type.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardRemoveRoomType: {
    text: 'Remove room type',
    description: 'Button to remove a room type.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardRoomTypeNamePlaceholder: {
    text: 'e.g. Deluxe King',
    description: 'Placeholder for room type name.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardCleanMinutesLabel: {
    text: 'Clean time (mins)',
    description: 'Label for clean minutes.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardRequiredPhotosLabel: {
    text: 'Required photo proofs',
    description: 'Label for required photo proofs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardRoomRangeTypeLabel: {
    text: 'Room type for this range',
    description: 'Label for room range type selector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardSelectRoomType: {
    text: 'Select room type…',
    description: 'Placeholder to select room type.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardSubmit: {
    text: 'Create Stays places',
    description: 'Button to submit Stays setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardSubmitting: {
    text: 'Creating places…',
    description: 'Button text while submitting Stays setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysWizardDoneTitle: {
    text: 'Stays setup complete',
    description: 'Title for Stays wizard completion step.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardDoneDesc: {
    text: 'Your properties, blocks, rooms and areas have been created.',
    description: 'Description for Stays wizard completion step.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsStaysWizardPrintQr: {
    text: 'Print room QR sheet',
    description: 'Button to print room QR sheet.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysWizardFinish: {
    text: 'Go to Today',
    description: 'Button to finish wizard and go to Today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysWizardRoomLimitExceeded: {
    text: 'Maximum of 500 rooms allowed across the setup.',
    description: 'Error when room count exceeds 500.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysWizardPermissionDenied: {
    text: 'Permission denied. Owner or admin role required.',
    description: 'Error when non-admin attempts Stays wizard submit.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysWizardAlreadyExists: {
    text: 'Places already exist in Stays for this workspace.',
    description: 'Error when group place already exists.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysWizardModuleDisabled: {
    text: 'The Stays module is currently disabled for this workspace.',
    description: 'Error when Stays module is off.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysWizardErrorGeneric: {
    text: 'Failed to set up Stays. Please try again.',
    description: 'Generic error message for Stays setup wizard.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsClinicsTitle: {
    text: 'Clinics & labs',
    description: 'Title for Clinics & labs sector hub screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsSubtitle: {
    text: 'Centres, outpatient care, diagnostic labs and statutory registers',
    description: 'Subtitle for Clinics & labs sector hub screen.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsTabClinicsToday: {
    text: 'Today',
    description: 'Section tab label for Clinics Today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabClinicsColdChain: {
    text: 'Cold chain',
    description: 'Section tab label for Clinics Cold chain.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabClinicsWaste: {
    text: 'Biomedical waste',
    description: 'Section tab label for Clinics Biomedical waste.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTabClinicsPlaces: {
    text: 'Centres & rooms',
    description: 'Setup tab label for Centres & rooms in Clinics sector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTabClinicsWorkforce: {
    text: 'Staff & registrations',
    description: 'Setup tab label for Staff & registrations in Clinics sector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTabClinicsTemplates: {
    text: 'Registers',
    description: 'Setup tab label for Registers in Clinics sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabClinicsRosters: {
    text: 'Rosters',
    description: 'Setup tab label for Rosters in Clinics sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabClinicsAssets: {
    text: 'Machines & fridges',
    description: 'Setup tab label for Machines & fridges in Clinics sector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTabClinicsCompliance: {
    text: 'Licences',
    description: 'Setup tab label for Licences in Clinics sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabClinicsImport: {
    text: 'CSV import',
    description: 'Setup tab label for CSV import in Clinics sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabClinicsWhatsApp: {
    text: 'On WhatsApp',
    description: 'Setup tab label for On WhatsApp in Clinics sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantTitle: {
    text: 'Plant',
    description: 'Title for Plant sector hub screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantSubtitle: {
    text: 'Manufacturing plants, production lines, machines, breakdowns, and downtime tracking',
    description: 'Subtitle for Plant sector hub screen.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsTabPlantToday: {
    text: 'Shift today',
    description: 'Section tab label for Plant Shift today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabPlantMachines: {
    text: 'Machines',
    description: 'Section tab label for Plant Machines.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabPlantRegisters: {
    text: 'Registers',
    description: 'Section tab label for Plant Registers.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabPlantQuality: {
    text: 'Quality & safety',
    description: 'Section tab label for Plant Quality & safety.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabPlantDowntime: {
    text: 'Downtime',
    description: 'Section tab label for Plant Downtime.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabPlantPlaces: {
    text: 'Lines & machines',
    description: 'Setup tab label for Lines & machines in Plant sector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTabPlantWorkforce: {
    text: 'Staff',
    description: 'Setup tab label for Staff in Plant sector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTabPlantTemplates: {
    text: 'Templates',
    description: 'Setup tab label for Templates in Plant sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabPlantSchedules: {
    text: 'Check schedules',
    description: 'Setup tab label for Check schedules in Plant sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTabPlantRosters: {
    text: 'Shifts',
    description: 'Setup tab label for Shifts in Plant sector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabPlantAssets: {
    text: 'Machine register',
    description: 'Setup tab label for Machine register in Plant sector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsTabPlantImport: {
    text: 'CSV import',
    description: 'Setup tab label for CSV import in Plant sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantWizardTitle: {
    text: 'Set up your plant',
    description: 'Title for Plant setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantWizardSubtitle: {
    text: 'Configure your manufacturing plant, production lines, and machines.',
    description: 'Subtitle for Plant setup wizard.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPlantWizardStepPreset: {
    text: '1. Choose Industry Preset',
    description: 'Step 1 title in Plant setup wizard.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlantWizardStepPlant: {
    text: '2. Plant Details',
    description: 'Step 2 title in Plant setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantWizardStepLines: {
    text: '3. Lines & Floors',
    description: 'Step 3 title in Plant setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantWizardStepMachines: {
    text: '4. Machines',
    description: 'Step 4 title in Plant setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantPresetGarmentsTextiles: {
    text: 'Garments & Textiles',
    description: 'Plant preset label for garments and textiles.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantPresetFoodProcessing: {
    text: 'Food Processing',
    description: 'Plant preset label for food processing.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantPresetEngineeringMetal: {
    text: 'Engineering & Metal',
    description: 'Plant preset label for engineering and metal.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantPresetGenericMsme: {
    text: 'Generic MSME',
    description: 'Plant preset label for generic MSME manufacturing.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantWizardPlantName: {
    text: 'Plant Name',
    description: 'Label for plant name input in wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantWizardPlantNamePlaceholder: {
    text: 'e.g. Main Plant',
    description: 'Placeholder for plant name input in wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantWizardStructure: {
    text: 'Structure',
    description: 'Label for structure selector in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardStructureLines: {
    text: 'Direct Lines',
    description: 'Option for direct lines structure in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardStructureFloors: {
    text: 'Floors & Lines',
    description: 'Option for floors and lines structure in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardAddLine: {
    text: '+ Add Line',
    description: 'Button to add a line in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardAddFloor: {
    text: '+ Add Floor',
    description: 'Button to add a floor in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardLineName: {
    text: 'Line Name',
    description: 'Label for line name in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardFloorName: {
    text: 'Floor Name',
    description: 'Label for floor name in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardAddMachine: {
    text: '+ Add Machine',
    description: 'Button to add a machine in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardMachineName: {
    text: 'Machine Name',
    description: 'Label for machine name in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardMachineCode: {
    text: 'Code (optional)',
    description: 'Label for machine code in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardMachineCriticality: {
    text: 'Criticality',
    description: 'Label for machine criticality in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantWizardMachinePmInterval: {
    text: 'PM Interval (days)',
    description: 'Label for machine PM interval in wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantWizardSubmit: {
    text: 'Set Up Plant',
    description: 'Button to submit plant setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantWizardSubmitting: {
    text: 'Setting up plant...',
    description: 'Loading label while submitting plant wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantWizardNext: {
    text: 'Next',
    description: 'Next button in wizard.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlantWizardBack: {
    text: 'Back',
    description: 'Back button in wizard.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlantWizardDoneTitle: {
    text: 'Plant is ready!',
    description: 'Title for plant setup wizard completion.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantWizardDoneDesc: {
    text: 'Your plant, lines, and machines have been set up successfully.',
    description: 'Description for plant setup wizard completion.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPlantWizardDoneAction: {
    text: 'Go to Shift Today',
    description: 'Action button after completing plant setup wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantWizardErrorGeneric: {
    text: 'Failed to set up plant. Please try again.',
    description: 'Generic error message in plant setup wizard.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlantMachinesTitle: {
    text: 'Machines',
    description: 'Plant machines tab title.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantMachinesSubtitle: {
    text: 'Register with PM due dates · breakdowns with downtime reason · release to use after repair',
    description: 'Plant machines tab subtitle.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsPlantBreakdownBtn: {
    text: 'Breakdown',
    description: 'Button to raise breakdown.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantRaiseBreakdownDialogTitle: {
    text: 'Raise machine breakdown',
    description: 'Title for raise breakdown dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantMachineLabel: {
    text: 'Machine',
    description: 'Label for machine selector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantReasonLabel: {
    text: 'Downtime reason',
    description: 'Label for downtime reason selector.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantWhatLabel: {
    text: 'What happened?',
    description: 'Label for breakdown description.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantNoteLabel: {
    text: 'Explanation note',
    description: 'Label for downtime explanation note.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantReleaseToUse: {
    text: 'Release to use',
    description: 'Button to release machine back to use.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantAwaitingRelease: {
    text: 'Awaiting release',
    description: 'Status pill for machine awaiting release.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantAwaitingReleaseDesc: {
    text: 'Maintenance done · supervisor to release',
    description: 'Helper text when machine is repaired awaiting release.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlantStateRunning: {
    text: 'Running',
    description: 'Status pill for running machine.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantAllRunning: {
    text: 'All running',
    description: 'Status label in plant today KPI card when no machines are down',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantStateDown: {
    text: 'Down',
    description: 'Status pill for down machine.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantStatePmDue: {
    text: 'PM due',
    description: 'Status pill for machine with PM due.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantDowntimeTitle: {
    text: 'Downtime report',
    description: 'Downtime report header title.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantDowntimeSubtitle: {
    text: 'From breakdown records',
    description: 'Downtime report subtitle.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlantDownloadCsv: {
    text: 'CSV',
    description: 'Download CSV button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantNoPlanningFootnote: {
    text: 'No production planning, BOM or costing, by design (SO-D44).',
    description: 'Plant design principle footnote.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsPlantLineHeader: {
    text: 'Line',
    description: 'Table header for Line column.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantBreakdownsHeader: {
    text: 'Breakdowns',
    description: 'Table header for Breakdowns count column.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantDowntimeHeader: {
    text: 'Downtime',
    description: 'Table header for Downtime column.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantMainReasonHeader: {
    text: 'Main reason',
    description: 'Table header for Main reason column.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantPhoneMyLine: {
    text: 'My line',
    description: 'Header for My line phone card.',
    surface: 'helper',
    maxChars: 20,
  },
  NLSOpsPlantPhoneOfflineSafe: {
    text: 'Offline-safe',
    description: 'Offline safe badge on helper phone.',
    surface: 'helper',
    maxChars: 20,
  },
  NLSOpsPlantPhoneScanMachine: {
    text: 'Scan a machine QR',
    description: 'Button to scan a machine QR code.',
    surface: 'helper',
    maxChars: 30,
  },
  NLSOpsPlantPhoneMarkDownBtn: {
    text: 'Mark down and tell maintenance',
    description: 'Button to submit machine breakdown.',
    surface: 'helper',
    maxChars: 40,
  },
  NLSOpsPlantPhonePhotoBtn: {
    text: 'Photo',
    description: 'Attach photo button on breakdown form.',
    surface: 'helper',
    maxChars: 20,
  },
  NLSOpsPlantPhoneReadOnlyNote: {
    text: 'Read-only on phones.',
    description: 'Notice that downtime analytics are read only on phones.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantPhoneOfflineReleaseDisabled: {
    text: 'Offline: release unavailable',
    description: 'Message indicating machine release is online only.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlantKpiShiftChecks: {
    text: 'Shift checks',
    description: 'KPI title for checks due in current shift.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantKpiChecksToday: {
    text: 'Checks today',
    description: 'KPI title for checks due in current calendar day.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantKpiMachinesDown: {
    text: 'Machines down',
    description: 'KPI title for machines currently down in plant.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantKpiQualityHolds: {
    text: 'Quality holds',
    description: 'KPI title for open quality holds.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantKpiNearMisses: {
    text: 'Near misses this week',
    description: 'KPI title for recent near misses.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantHoldReleaseDifferentPerson: {
    text: 'release by a different person',
    description: 'Note on quality holds KPI explaining release requirement.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantNearMissRestricted: {
    text: 'restricted',
    description: 'Badge or note indicating near misses are restricted.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantMissedEscalatedNote: {
    text: '{count} missed → shift in-charge',
    description: 'Note on checks KPI when checks have been missed and escalated.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantMachineQrSheetBtn: {
    text: 'Machine QR sheet',
    description: 'Button to print or view QR sheet for machines.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantNoLinesConfigured: {
    text: 'No lines configured',
    description: 'Empty state message when a plant has no lines configured.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlantLineCardEmpty: {
    text: 'No checks or issues for this line',
    description: 'Empty state inside a line card on shift today tab.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsPlantRegistersTitle: {
    text: 'Registers',
    description: 'Title for plant registers desk tab.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantRegistersSubtitle: {
    text: 'Shift and line checklists · inspections · PPE and guarding · hygiene (food preset) · locked once submitted',
    description: 'Subtitle for plant registers desk tab.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsPlantStarterRegistersHeader: {
    text: 'Plant registers',
    description: 'Header for the list of plant register templates.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantMissedInboxHeader: {
    text: 'Missed-check inbox',
    description: 'Header for the inbox of missed checklist dues in plant.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlantNoMissedChecks: {
    text: 'No missed checks',
    description: 'Empty state text when there are no missed checks in the inbox.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantEscalatedTo: {
    text: 'Escalated: {level}',
    description: 'Label displaying escalation level of missed due.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlantNextEscalationAt: {
    text: 'Next escalation at {time}',
    description: 'Label displaying time for next escalation.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlantChecksDueThisShift: {
    text: 'Due this shift',
    description: 'Section header for checks due in the active shift.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantQualityHeaderTitle: {
    text: 'Quality & safety',
    description: 'Title for Plant quality & safety desk tab.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantQualityHeaderSubtitle: {
    text: 'Quality holds (restricted) · near misses (restricted)',
    description: 'Subtitle for Plant quality & safety desk tab.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsPlantQualityExportBtn: {
    text: 'Export',
    description: 'Export button label on plant quality tab.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantQualityHoldsHeader: {
    text: 'Quality holds',
    description: 'Header for quality holds list.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantNearMissesHeader: {
    text: 'Near misses',
    description: 'Header for near misses list.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantHoldReleaseNote: {
    text: 'A hold is released only by a different person from the one who raised it (J4).',
    description: 'Informational note explaining quality hold release rules.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsPlantNearMissExportNote: {
    text: 'Exports leave out who was involved unless the owner allows it.',
    description: 'Note explaining privacy in near miss exports.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsPlantRestrictedHoldsNotice: {
    text: 'Restricted: You need the QUALITY_RELEASER role to view quality holds.',
    description: 'Notice shown when caller does not have permission to view quality holds.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsPlantRestrictedNearMissNotice: {
    text: 'Restricted: You need the INCIDENT_HANDLER role to view near-miss records.',
    description: 'Notice shown when caller does not have permission to view near misses.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsPlantReleaseBtn: {
    text: 'Release',
    description: 'Button to release a quality hold.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantConfirmReleaseBtn: {
    text: 'Confirm Release',
    description: 'Button to confirm releasing a quality hold.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantReleaseReasonPlaceholder: {
    text: 'Release reason (e.g. re-inspected and cleared)',
    description: 'Placeholder for reason input when releasing a quality hold.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlantNoQualityHolds: {
    text: 'No quality holds recorded.',
    description: 'Empty state message when there are no quality holds.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantNoNearMisses: {
    text: 'No near-miss safety incidents recorded.',
    description: 'Empty state message when there are no near-miss incidents.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlantFilterAll: {
    text: 'All',
    description: 'Filter option for all items.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsPlantFilterOpen: {
    text: 'Open',
    description: 'Filter option for open items.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsPlantFilterReleased: {
    text: 'Released',
    description: 'Filter option for released items.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlantExportQueued: {
    text: 'Export queued (ID: {id}).',
    description: 'Message shown when an evidence export is queued.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlantNearMissIncludePeople: {
    text: 'Include people in near-miss exports',
    description: 'Toggle switch label in Quality tab Export area for including employee names in near-miss exports',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlantNearMissIncludePeopleDesc: {
    text: 'Names will appear in exports',
    description: 'Confirmation and explanatory note for near-miss export people toggle',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlantRaisedBy: {
    text: 'Raised by {name}',
    description: 'Label showing who raised a quality hold',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantRaisedByFallback: {
    text: 'Quality Control',
    description: 'Fallback display name for person who raised a quality hold when unassigned',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantMarkDoneBtn: {
    text: 'Mark done',
    description: 'Button to mark a quality hold as worked and done',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantHoldWorkedReady: {
    text: 'Worked · Ready for release',
    description: 'Status label for a quality hold that has been worked and is ready for release',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlantHoldItemFallback: {
    text: 'Item',
    description: 'Fallback label for quality hold item when name is missing',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantStatusOnHold: {
    text: 'On hold',
    description: 'Status label for an active quality hold awaiting release',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantStatusReported: {
    text: 'Reported',
    description: 'Status label for a reported near-miss incident',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantNearMissActionLabel: {
    text: 'Action: {action}',
    description: 'Immediate action label for near miss log in quality tab',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlantReleaseReasonRequired: {
    text: 'Please enter a release reason.',
    description: 'Validation error when releasing quality hold without reason',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlantReleaseFailed: {
    text: 'Failed to release hold.',
    description: 'Error message when releasing a quality hold fails',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantExportFailed: {
    text: 'Export failed.',
    description: 'Error message when quality evidence export fails',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantNearMissColWhen: {
    text: 'When',
    description: 'Table column header for near miss incident timestamp',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlantNearMissColWhere: {
    text: 'Where',
    description: 'Table column header for near miss incident location',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlantNearMissColCategory: {
    text: 'Category',
    description: 'Table column header for near miss category',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantNearMissColWhat: {
    text: 'What',
    description: 'Table column header for what nearly happened',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsPlantHazardFallback: {
    text: 'Hazard',
    description: 'Fallback label when near miss category is missing',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantCannotMarkDoneOffline: {
    text: 'Cannot mark done while offline.',
    description: 'Error message when attempting to mark a hold done without network connection',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsPlantNoMachinesNeedingAttention: {
    text: 'No machines needing attention.',
    description: 'Empty state message when no machines are down or awaiting release',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantNoDowntimeRecorded: {
    text: 'No downtime recorded.',
    description: 'Empty state message when no downtime is recorded for plant machines',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlantNoOpenHolds: {
    text: 'No open quality holds.',
    description: 'Empty state message when no quality holds are open',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantPhoneReleaseAllowedNote: {
    text: 'Release allowed only if you did not raise or do it.',
    description: 'Notice explaining that only independent persons can release quality holds',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlantPhoneReleaseNotRaiser: {
    text: '(you didn\'t raise it ✓)',
    description: 'Badge indicating current user is eligible to release quality hold',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantPhoneThisShiftTitle: {
    text: 'This shift',
    description: 'Section header for current shift checks on mobile',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlantPhoneMissedChecksSubtitle: {
    text: 'Missed and due checks across plant lines',
    description: 'Subtitle explaining missed checks card on mobile',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlantPhoneNoMissedChecks: {
    text: 'No missed checks this shift.',
    description: 'Empty state message when no checks are missed during current shift',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantHelperNearMissReported: {
    text: 'Near miss reported',
    description: 'Success message after reporting a near-miss incident on helper phone',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantHelperCheckSubmitted: {
    text: 'Check submitted',
    description: 'Success message after submitting a check record on helper phone',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantHelperWhereLabel: {
    text: 'Where',
    description: 'Form label for incident location on helper phone',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantHelperCategoryLabel: {
    text: 'Category',
    description: 'Form label for incident category on helper phone',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantHelperWhatNearlyHappenedLabel: {
    text: 'What nearly happened',
    description: 'Form label for near-miss description on helper phone',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlantHelperWhatNearlyHappenedPlaceholder: {
    text: 'What nearly happened (3–1000 characters)',
    description: 'Input placeholder for near-miss description on helper phone',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlantHelperImmediateActionLabel: {
    text: 'Immediate action (optional)',
    description: 'Form label for immediate action on helper phone',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlantHelperImmediateActionPlaceholder: {
    text: 'Immediate action taken',
    description: 'Input placeholder for immediate action on helper phone',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlantHelperNearMissNote: {
    text: 'Near misses are restricted and about fixing the cause, not blame. Names stay out of exports unless the owner allows.',
    description: 'Safety culture reminder note on helper near-miss form',
    surface: 'shell',
    maxChars: 150,
  },
  NLSOpsPlantHelperReportBtn: {
    text: 'Report',
    description: 'Button text to submit near-miss report on helper phone',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlantHelperMyChecksTitle: {
    text: 'My checks',
    description: 'Card title for helper line checks',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsWizardTitle: {
    text: 'Set up Clinics & labs',
    description: 'Title for Clinics setup wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsWizardSubtitle: {
    text: 'Configure your organisation, clinical centres, and treatment rooms',
    description: 'Subtitle for Clinics setup wizard.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsClinicsWizardStepOrg: {
    text: 'Organisation',
    description: 'Step label for Organisation in Clinics setup wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardStepSites: {
    text: 'Centres & sites',
    description: 'Step label for Centres in Clinics setup wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardStepRooms: {
    text: 'Floors & rooms',
    description: 'Step label for Rooms in Clinics setup wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardStepReview: {
    text: 'Review & create',
    description: 'Step label for Review in Clinics setup wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardOrgNameLabel: {
    text: 'Organisation name',
    description: 'Label for Organisation name input in Clinics wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsWizardOrgNamePlaceholder: {
    text: 'e.g. Sunrise Diagnostics',
    description: 'Placeholder for Organisation name input.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsClinicsWizardSitesHelp: {
    text: 'Add your primary clinic or diagnostic lab and any satellite centres.',
    description: 'Help text for Centres step in Clinics wizard.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsClinicsWizardAddSite: {
    text: 'Add centre',
    description: 'Button to add a centre in Clinics wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardRemoveSite: {
    text: 'Remove centre',
    description: 'Button to remove a centre in Clinics wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardSiteNameLabel: {
    text: 'Centre name',
    description: 'Label for Centre name input in Clinics wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsWizardSiteNamePlaceholder: {
    text: 'e.g. Kothrud Collection Centre',
    description: 'Placeholder for Centre name input.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsClinicsWizardPresetLabel: {
    text: 'Speciality / preset',
    description: 'Label for preset picker in Clinics wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsWizardSiteKindLabel: {
    text: 'Site kind',
    description: 'Label for site kind dropdown in Clinics wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardRoomsHelp: {
    text: 'Configure floors, zones, and generate room ranges for each centre.',
    description: 'Help text for rooms step in Clinics wizard.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsClinicsWizardAddFloor: {
    text: 'Add floor / zone',
    description: 'Button to add a floor or zone.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsWizardRemoveFloor: {
    text: 'Remove floor',
    description: 'Button to remove a floor or zone.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardFloorNameLabel: {
    text: 'Floor or zone name',
    description: 'Label for floor or zone name input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsWizardFloorNamePlaceholder: {
    text: 'e.g. Ground Floor or Phlebotomy Zone',
    description: 'Placeholder for floor/zone name.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsClinicsWizardAddRange: {
    text: 'Add range',
    description: 'Button to add a room range in Clinics wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardRemoveRange: {
    text: 'Remove range',
    description: 'Button to remove a room range in Clinics wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardRangePrefix: {
    text: 'Prefix',
    description: 'Prefix label for room range generator.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardRangePrefixPlaceholder: {
    text: 'e.g. Room- or Chair-',
    description: 'Placeholder for room prefix.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsWizardRangeFrom: {
    text: 'From',
    description: 'Start number for range generator.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardRangeTo: {
    text: 'To',
    description: 'End number for range generator.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardRangeLevel: {
    text: 'Level',
    description: 'Place level for range generator.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardLevelRoom: {
    text: 'Room',
    description: 'Room option in range generator.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardLevelChair: {
    text: 'Chair',
    description: 'Chair option in range generator.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardLevelBay: {
    text: 'Bay',
    description: 'Bay option in range generator.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardTotalPlacesCount: {
    text: '{count} places will be created',
    description: 'Counter showing total places in wizard.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsClinicsWizardMaxPlacesNotice: {
    text: '/ 500 max',
    description: 'Maximum places counter label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardLimitExceeded: {
    text: 'Total places exceeds the maximum limit of 500 places per setup.',
    description: 'Limit error in Clinics wizard.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsClinicsWizardReviewTitle: {
    text: 'Review organisation structure',
    description: 'Heading for review step in Clinics wizard.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsClinicsWizardReviewSummary: {
    text: '{siteCount} centres, {placeCount} total places',
    description: 'Summary line on review step.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsClinicsWizardBtnBack: {
    text: 'Back',
    description: 'Back button in Clinics wizard.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardBtnNext: {
    text: 'Next',
    description: 'Next button in Clinics wizard.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsWizardCreateBtn: {
    text: 'Create organisation',
    description: 'Submit button in Clinics wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsWizardCreating: {
    text: 'Creating places...',
    description: 'Creating spinner state in Clinics wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsWizardSuccessTitle: {
    text: 'Clinics setup complete',
    description: 'Title on wizard completion.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsWizardSuccessBody: {
    text: 'Your organisation, centres, and rooms have been created successfully.',
    description: 'Body text on wizard completion.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsClinicsWizardActionDone: {
    text: 'Go to Today',
    description: 'Button to close wizard and view today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsWizardActionCentres: {
    text: 'View centres & rooms',
    description: 'Button to view centres after wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsWizardReadOnlyTitle: {
    text: 'Clinics setup required',
    description: 'Title for non-owner empty state in Clinics wizard.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsWizardReadOnlyBody: {
    text: 'An organisation structure has not been created yet. A workspace Owner or Admin must complete initial setup.',
    description: 'Body text for non-owner empty state in Clinics wizard.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsClinicsWizardErrorGeneric: {
    text: 'Failed to create places. Please try again.',
    description: 'Generic error for Clinics wizard.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsClinicsWizardPermissionDenied: {
    text: 'Permission denied. Setup is restricted to Owners and Admins.',
    description: 'Permission denied error for Clinics wizard.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsClinicsWizardAlreadyExists: {
    text: 'An organisation place already exists in this workspace.',
    description: 'Already exists error for Clinics wizard.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsClinicsWizardModuleDisabled: {
    text: 'Clinics module is disabled in this workspace.',
    description: 'Module disabled error for Clinics wizard.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsClinicsFamilyConsult: {
    text: 'Consultation & OPD',
    description: 'Consult preset family.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsFamilyProcedure: {
    text: 'Procedures & therapy',
    description: 'Procedure preset family.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsFamilyLabs: {
    text: 'Pathology & collection',
    description: 'Labs preset family.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsFamilyImaging: {
    text: 'Imaging & radiology',
    description: 'Imaging preset family.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsFamilyBloodBank: {
    text: 'Blood bank & storage',
    description: 'Blood bank preset family.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsFamilyPharmacy: {
    text: 'Pharmacy & distribution',
    description: 'Pharmacy preset family.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsFamilyVet: {
    text: 'Veterinary care',
    description: 'Vet preset family.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsClinicsSiteKindClinic: {
    text: 'Clinic',
    description: 'Clinic site kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsSiteKindLab: {
    text: 'Lab',
    description: 'Lab site kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsSiteKindHub: {
    text: 'Hub',
    description: 'Hub site kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsSiteKindCollectionCentre: {
    text: 'Collection centre',
    description: 'Collection centre site kind.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsSiteKindPharmacy: {
    text: 'Pharmacy',
    description: 'Pharmacy site kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsSiteKindWarehouse: {
    text: 'Warehouse',
    description: 'Warehouse site kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsSiteKindCamp: {
    text: 'Camp / outreach',
    description: 'Camp site kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsTodayBanner: {
    text: 'Tasks carry a booking token or a sample barcode, never clinical data (SO-D229).',
    description: 'Privacy banner on Clinics Today screen.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsClinicsTodaySitesTitle: {
    text: 'Centres & sites',
    description: 'Title for sites table on Clinics Today screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsTodayNoSites: {
    text: 'No centres found in organisation.',
    description: 'Empty state for centres list.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsClinicsTodayActionCentres: {
    text: 'Centres & rooms',
    description: 'Quick link button to centres & rooms.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsTodayActionMachines: {
    text: 'Machines & fridges',
    description: 'Quick link button to machines & fridges.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsClinicsTodayColSite: {
    text: 'Centre',
    description: 'Table column header for Centre.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsTodayColKind: {
    text: 'Kind',
    description: 'Table column header for site kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsTodayColPreset: {
    text: 'Preset',
    description: 'Table column header for preset.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsTodayColFloors: {
    text: 'Floors/Zones',
    description: 'Table column header for floors/zones count.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClinicsTodayColRooms: {
    text: 'Rooms',
    description: 'Table column header for rooms count.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsTodayColChairs: {
    text: 'Chairs',
    description: 'Table column header for chairs count.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsClinicsTodayColBays: {
    text: 'Bays',
    description: 'Table column header for bays count.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsAssetsFieldRegistrations: {
    text: 'Statutory registrations',
    description: 'Label for asset registrations section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsAddRegistration: {
    text: 'Add registration',
    description: 'Button to add statutory registration.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsRemoveRegistration: {
    text: 'Remove',
    description: 'Button to remove statutory registration.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsAssetsRegistrationKind: {
    text: 'Licence / registration kind',
    description: 'Label for registration kind dropdown.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsRegistrationNumber: {
    text: 'Registration number',
    description: 'Label for registration certificate number.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsRegistrationIssuedBy: {
    text: 'Issued by',
    description: 'Label for issuing authority.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsRegistrationValidUntil: {
    text: 'Valid until (YYYY-MM-DD)',
    description: 'Label for registration validity date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldValidUntil: {
    text: 'Valid until',
    description: 'Short label for registration validity date.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsCategorySuggestions: {
    text: 'Suggestions:',
    description: 'Label for category suggestion chips.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsFieldTempBand: {
    text: 'Temperature band (°C)',
    description: 'Label for asset temperature band section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldTempBandMin: {
    text: 'Min temp (°C)',
    description: 'Label for temperature band minimum in Celsius.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsFieldTempBandMax: {
    text: 'Max temp (°C)',
    description: 'Label for temperature band maximum in Celsius.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsTempBandInvalid: {
    text: 'Min must be less than max (−200 to 100 °C)',
    description: 'Validation error when temperature band is invalid.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsAssetsTempBandRange: {
    text: '{min}°C to {max}°C',
    description: 'Temperature band range display formatted.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsSearchPlaceholder: {
    text: 'Search code, name, serial or registration...',
    description: 'Search input placeholder in assets screen.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsAssetsRegistrationsCount: {
    text: '{count} registrations',
    description: 'Count badge for registrations.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysRoomTypesTitle: {
    text: 'Room types',
    description: 'Card title for room types in places screen.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRoomTypesAdd: {
    text: 'Add room type',
    description: 'Button to add a room type in places screen.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRoomTypesName: {
    text: 'Type name',
    description: 'Label for room type name.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRoomTypesCleanMinutes: {
    text: 'Clean time (mins)',
    description: 'Label for clean minutes.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRoomTypesPhotos: {
    text: 'Required photo proofs (comma-separated)',
    description: 'Label for required photo proofs.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysRoomTypesSave: {
    text: 'Save room types',
    description: 'Button to save room types.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRoomTypesSaving: {
    text: 'Saving…',
    description: 'Button text while saving room types.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRoomTypesDelete: {
    text: 'Remove',
    description: 'Button to remove a room type.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysRoomTypesInUse: {
    text: 'Cannot remove room type because it is in use by rooms: {rooms}',
    description: 'Error message when removing a room type that is still in use.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysRoomTypeLabel: {
    text: 'Room type',
    description: 'Label for room type picker on room place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysRoomTypeNone: {
    text: 'None',
    description: 'Option for no room type assigned.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRoleFrontDesk: {
    text: 'Front Desk',
    description: 'Sector role label for Front Desk.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleRoomInspector: {
    text: 'Room Inspector',
    description: 'Sector role label for Room Inspector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindGuestRequest: {
    text: 'Guest Request',
    description: 'Request kind label for guest request.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindGuestComplaint: {
    text: 'Guest Complaint',
    description: 'Request kind label for guest complaint.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindExtraBooking: {
    text: 'Book spa / pool / activity',
    description: 'Request kind label for extras booking.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestKindMinibarCheck: {
    text: 'Minibar Check',
    description: 'Request kind label for check-out minibar check.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindLaundryShort: {
    text: 'Laundry Short Count',
    description: 'Request kind label for laundry short count.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysBoardTitle: {
    text: 'Room board',
    description: 'Title for room board screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysBoardSubtitle: {
    text: 'Tap a room to inspect, clean or change state.',
    description: 'Subtitle for room board.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysExportCsv: {
    text: 'Export',
    description: 'Export button label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLegendVacantDirty: {
    text: 'Vacant dirty',
    description: 'Legend label for vacant dirty.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLegendCleaning: {
    text: 'Cleaning',
    description: 'Legend label for cleaning.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLegendCleaned: {
    text: 'Cleaned → inspect',
    description: 'Legend label for cleaned.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLegendReady: {
    text: 'Ready',
    description: 'Legend label for ready.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLegendOccupied: {
    text: 'Occupied / stay-over',
    description: 'Legend label for occupied.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLegendOutOfOrder: {
    text: 'Out of order',
    description: 'Legend label for out of order.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLegendOutOfService: {
    text: 'Out of service',
    description: 'Legend label for out of service.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysFilterAll: {
    text: 'All',
    description: 'All filter option.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRoomOccupancyVacant: {
    text: 'Vacant',
    description: 'Vacant occupancy label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRoomOccupancyOccupied: {
    text: 'Occupied',
    description: 'Occupied occupancy label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRoomOccupancyDueOut: {
    text: 'Due out',
    description: 'Due out occupancy label.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRoomServiceInService: {
    text: 'In service',
    description: 'In service status label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLegendTitle: {
    text: 'Room status legend',
    description: 'Aria label for legend.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysItemsFailed: {
    text: 'items failed',
    description: 'Items failed suffix label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysStayOverSub: {
    text: 'Stay-over',
    description: 'Stay-over label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysMaintenanceNeeded: {
    text: 'Maintenance needed',
    description: 'Maintenance needed label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysFilterHk: {
    text: 'Housekeeping',
    description: 'Housekeeping filter.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysFilterOccupancy: {
    text: 'Occupancy',
    description: 'Occupancy filter.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysFilterService: {
    text: 'Service',
    description: 'Service status filter.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysFilterTags: {
    text: 'Tags',
    description: 'Tags filter.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysTagDnd: {
    text: 'DND',
    description: 'Do not disturb tag.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysTagVip: {
    text: 'VIP',
    description: 'VIP tag.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsStaysActionCheckOut: {
    text: 'Guest checked out',
    description: 'Check out action.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysActionMarkOccupied: {
    text: 'Mark occupied',
    description: 'Mark occupied action.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysActionSetDueOut: {
    text: 'Set due-out time',
    description: 'Set due out action.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysActionStartClean: {
    text: 'Start cleaning',
    description: 'Start cleaning action.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysActionCleaned: {
    text: 'Cleaned',
    description: 'Cleaned action.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysActionInspectPass: {
    text: 'Pass → Ready',
    description: 'Pass inspection action.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysActionInspectFail: {
    text: 'Fail inspection',
    description: 'Fail inspection action.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysActionPutOutOfOrder: {
    text: 'Put out of order',
    description: 'Put room out of order.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysActionPutOutOfService: {
    text: 'Put out of service',
    description: 'Put room out of service.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysActionBackInService: {
    text: 'Back in service',
    description: 'Return room to service.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysServiceReasonLabel: {
    text: 'Reason',
    description: 'Reason for putting out of order/service.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysFailedItemsLabel: {
    text: 'Failed items',
    description: 'Failed items on inspection.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysFailedItemsPlaceholder: {
    text: 'e.g. Bathroom mirror streaks, AC not cooling',
    description: 'Failed items placeholder.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysRecentEvents: {
    text: 'Recent history',
    description: 'Room recent history events.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysStaleRefreshNotice: {
    text: 'Room state has changed. Please refresh.',
    description: 'Stale version warning.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysSelfInspectionReason: {
    text: 'Self-inspection reason (required for owner)',
    description: 'Owner self inspection reason.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysTodayTitle: {
    text: 'Stays',
    description: 'Title for Stays today screen.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysKpiReady: {
    text: 'Rooms ready',
    description: 'KPI rooms ready.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysKpiToClean: {
    text: 'To clean',
    description: 'KPI to clean.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysKpiWaitingInspect: {
    text: 'Waiting inspection',
    description: 'KPI waiting inspection.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysKpiOutOfOrder: {
    text: 'Out of order',
    description: 'KPI out of order.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysNeedsYouNowTitle: {
    text: 'Needs you now',
    description: 'Card title for urgent actions.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysNeedsYouNowEmpty: {
    text: 'No urgent items right now.',
    description: 'Empty message for urgent items.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysDeparturesIntakeTitle: {
    text: 'Departures intake',
    description: 'Card title for departures intake.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysDeparturesIntakeSubtitle: {
    text: 'no PMS needed',
    description: 'Departures intake subtitle.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysDeparturesIntakeFooter: {
    text: 'Check-out minibar and damage alert goes to the runner (K3).',
    description: 'Intake card footer.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysBtnDeparturesCsv: {
    text: 'Departures CSV',
    description: 'Button to open departures CSV import.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysBtnRoomQrSheet: {
    text: 'Room QR sheet',
    description: 'Button to open room QR sheet.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysOneTapCheckedOut: {
    text: 'Checked out',
    description: 'One tap checked out button.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysSourceDesk: {
    text: 'Front desk one-tap',
    description: 'Desk source.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysSourceQr: {
    text: 'Room-door QR by attendant',
    description: 'QR source.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysSourceCsv: {
    text: 'Departures CSV',
    description: 'CSV source.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysSourcePms: {
    text: 'PMS webhook',
    description: 'PMS source.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysColRoom: {
    text: 'Room',
    description: 'Room column.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysColSource: {
    text: 'How we knew',
    description: 'How we knew column.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysColWhen: {
    text: 'When',
    description: 'When column.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysColNext: {
    text: 'Next',
    description: 'Next column.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysRedo: {
    text: 'Redo',
    description: 'Redo button on failed inspection item.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysMinibarCheckSetting: {
    text: 'Require minibar and damage check on check-out',
    description: 'Setting toggle in room types card.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysMinibarCheckSettingDesc: {
    text: 'Automatically raises a minibar check request for a runner upon guest check-out.',
    description: 'Setting description.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsStaysCleanPhotosPrompt: {
    text: 'Required photos for cleaning proof',
    description: 'Prompt for clean photos.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysPhotoBed: {
    text: 'Bed',
    description: 'Default clean photo label: Bed.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPhotoBathroom: {
    text: 'Bathroom',
    description: 'Default clean photo label: Bathroom.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPhotoMinibar: {
    text: 'Minibar and amenities',
    description: 'Default clean photo label: Minibar and amenities.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysRoomSelectProperty: {
    text: 'Select property',
    description: 'Property picker label on stays screens.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysSheetTitle: {
    text: 'Room {roomName}',
    description: 'Room sheet dialog title.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysDueOutTimeLabel: {
    text: 'Departure time',
    description: 'Departure time label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysSubmitProof: {
    text: 'Submit clean proof',
    description: 'Submit clean proof button.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPhotosRequiredNotice: {
    text: 'All required photos must be provided.',
    description: 'Notice when photos missing.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysInspectRoomTitle: {
    text: 'Inspect room',
    description: 'Inspect room heading.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysNoRoomsFound: {
    text: 'No rooms found for this property.',
    description: 'No rooms message.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysNoDepartures: {
    text: 'No departures scheduled.',
    description: 'No departures message.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysActionPauseClean: {
    text: 'Pause',
    description: 'Button to pause cleaning.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysActionResumeClean: {
    text: 'Resume',
    description: 'Button to resume cleaning.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysPauseReasonTitle: {
    text: 'Pause cleaning',
    description: 'Modal title to select pause reason.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPauseReasonPrompt: {
    text: 'Select reason for pause',
    description: 'Prompt to choose a reason for pausing cleaning.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysPauseReasonGuest: {
    text: 'Guest in room',
    description: 'Pause reason: guest in room.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPauseReasonDnd: {
    text: 'Do not disturb',
    description: 'Pause reason: do not disturb.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPauseReasonOther: {
    text: 'Other',
    description: 'Pause reason: other.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPausedChip: {
    text: 'Paused · {reason}',
    description: 'Room chip badge indicating cleaning is paused.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysHousekeepingTab: {
    text: 'Housekeeping',
    description: 'Housekeeping tab name in sector hub.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysHousekeepingTitle: {
    text: 'Housekeeping',
    description: 'Screen title for Stays housekeeping.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysHousekeepingSubtitle: {
    text: 'Time per room and fair productivity view',
    description: 'Housekeeping screen subtitle.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysHousekeepingTableNote: {
    text: 'QR start to done, minus pauses',
    description: 'Table note clarifying measurement methodology.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysHousekeepingYourNumbers: {
    text: 'Your numbers',
    description: 'Header for attendant view of their own performance.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysHousekeepingAllAttendants: {
    text: 'Attendant productivity',
    description: 'Header for supervisor view of all attendants.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysColAttendant: {
    text: 'Attendant',
    description: 'Column header for attendant name.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysColRoomsCleaned: {
    text: 'Rooms cleaned',
    description: 'Column header for count of rooms cleaned.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysColRoomsPerDay: {
    text: 'Rooms / day',
    description: 'Column header for average rooms cleaned per working day.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysColAvgTime: {
    text: 'Avg active mins',
    description: 'Column header for average active minutes per room.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysColMedianTime: {
    text: 'Median active mins',
    description: 'Column header for median active minutes per room.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysColPausedTime: {
    text: 'Total paused mins',
    description: 'Column header for total minutes spent in pause.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysColInspectionFails: {
    text: 'Inspection fails',
    description: 'Column header for count of failed inspections.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysReportEmpty: {
    text: 'No cleaning activity recorded in this period.',
    description: 'Empty state when no rooms were cleaned in selected range.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysDateRangeFrom: {
    text: 'From',
    description: 'Label for start date picker.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysDateRangeTo: {
    text: 'To',
    description: 'Label for end date picker.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysDateRangeApply: {
    text: 'Refresh',
    description: 'Button to refresh report with current parameters.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysNoPropertySelected: {
    text: 'Select a property to view housekeeping report.',
    description: 'Placeholder when no property is selected.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDepartments: {
    text: 'Departments',
    description: 'Header or label for departments.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsDepartment: {
    text: 'Department',
    description: 'Label for department field.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAllDepartments: {
    text: 'All departments',
    description: 'Filter option to include all departments.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsFilterDepartment: {
    text: 'Filter by department',
    description: 'Placeholder or label for department filter.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsNoDepartment: {
    text: 'No department',
    description: 'Label when no department is assigned.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsScheduleTargetMode: {
    text: 'Target mode',
    description: 'Schedule target mode selection.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsScheduleTargetExplicit: {
    text: 'Specific places',
    description: 'Schedule targets specific explicit place list.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsScheduleTargetLevel: {
    text: 'Every level under place',
    description: 'Schedule targets all places of a level under a root place.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsScheduleTargetOption: {
    text: 'Every {level} under {place}',
    description: 'Label describing schedule place level targeting.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsScheduleTargetRootPlace: {
    text: 'Root place',
    description: 'Root place for schedule level target.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsScheduleTargetLevelSelect: {
    text: 'Target level',
    description: 'Level to target under root place.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysMyRoomsToday: {
    text: 'My rooms today',
    description: 'Filter for rooms assigned to the current user today.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysAssignmentMode: {
    text: 'Assign rooms',
    description: 'Button to enter assignment mode on stays board.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysExitAssignmentMode: {
    text: 'Done assigning',
    description: 'Button to exit room assignment mode.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysAssignToAttendant: {
    text: 'Assign to attendant',
    description: 'Button or dialog title to assign rooms to an attendant.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysAssignAction: {
    text: 'Assign',
    description: 'Action to assign room.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysReassignAction: {
    text: 'Reassign',
    description: 'Action to reassign room.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysUnassignAction: {
    text: 'Unassign',
    description: 'Action to unassign room.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysAssignedTo: {
    text: 'Assigned: {name}',
    description: 'Chip or label showing assigned attendant.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysSelectRoomsToAssign: {
    text: 'Select rooms to assign',
    description: 'Notice prompting supervisor to select rooms.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysRoomsSelected: {
    text: '{count} rooms selected',
    description: 'Count of selected rooms for assignment.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysRostersByDepartment: {
    text: 'Rosters by department',
    description: 'Section title for today rosters grouped by department.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysViewFullRosters: {
    text: 'View full rosters',
    description: 'Link or button to open full rosters screen.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysNoStaffRostered: {
    text: 'No staff rostered today',
    description: 'Empty state when no staff is on duty.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysSelectAttendant: {
    text: 'Select attendant',
    description: 'Dropdown or prompt to select attendant for assignment.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysAssignedSuccess: {
    text: '{count} rooms assigned',
    description: 'Success message after assigning rooms.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysUnassignedSuccess: {
    text: 'Room unassigned',
    description: 'Success message after unassigning room.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysReassignedSuccess: {
    text: 'Room reassigned',
    description: 'Success message after reassigning room.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsNotSynced: {
    text: 'Not synced to PMS',
    description: 'Chip on room card indicating state has not synced to PMS',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsTodayConnected: {
    text: 'PMS connected',
    description: 'Indicator on Stays Today showing PMS is connected',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsTodayLastEvent: {
    text: 'PMS connected · last event {time}',
    description: 'Indicator on Stays Today with last event time',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysPmsTitle: {
    text: 'Hotel PMS',
    description: 'Title for Stays PMS Link Hub tile and panel',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsSubtitle: {
    text: 'Inbound reservation and room events, status sync, and write-back queue.',
    description: 'Subtitle for Hotel PMS integration panel',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsStaysPmsConnectBtn: {
    text: 'Connect Hotel PMS',
    description: 'Button to initialize Hotel PMS connector',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsAdapterLabel: {
    text: 'PMS Adapter',
    description: 'Label for PMS adapter selection',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsAdapterGeneric: {
    text: 'Generic PMS (Timelining format)',
    description: 'Label for generic Stays PMS webhook adapter',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysPmsEndpointUrl: {
    text: 'Inbound Webhook URL',
    description: 'Label for the workspace inbound PMS webhook URL',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsSecret: {
    text: 'Signing Secret',
    description: 'Label for the PMS signing secret',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsSecretWarning: {
    text: 'Save this secret now. It will not be shown again.',
    description: 'Warning shown when displaying generated secret',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysPmsCopy: {
    text: 'Copy',
    description: 'Copy button label',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysPmsCopied: {
    text: 'Copied',
    description: 'Copied button state',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysPmsStatusLabel: {
    text: 'Connection Status',
    description: 'Status label in PMS settings panel',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsActive: {
    text: 'Active',
    description: 'Active status',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysPmsDisabled: {
    text: 'Disabled',
    description: 'Disabled status',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysPmsLastEventLabel: {
    text: 'Last event received: {time}',
    description: 'Last event timestamp in PMS settings panel',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsStaysPmsNoEvents: {
    text: 'No events received yet.',
    description: 'Text when no events have arrived',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsPropertyMapTitle: {
    text: 'Property Code Mapping',
    description: 'Title for PMS property code mapping section',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsPropertyMapDesc: {
    text: 'Map your PMS property codes to Timelining properties.',
    description: 'Description for PMS property code mapping section',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysPmsPropertyCode: {
    text: 'PMS Property Code',
    description: 'Label for PMS property code input',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsWriteBackTitle: {
    text: 'Write-Back Queue',
    description: 'Title for write-back queue section',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsWriteBackDesc: {
    text: 'Post room readiness and service updates back to your PMS callback URL.',
    description: 'Description for write-back queue section',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysPmsWriteBackEnable: {
    text: 'Enable write-back queue',
    description: 'Checkbox to enable write-back queue',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsWriteBackUrl: {
    text: 'HTTPS Callback URL',
    description: 'Label for write-back HTTPS callback URL',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsRotateSecretBtn: {
    text: 'Rotate Secret',
    description: 'Button to rotate PMS secret',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsRotateSecretConfirmTitle: {
    text: 'Rotate PMS Signing Secret',
    description: 'Confirmation dialog title for rotating PMS secret',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsRotateSecretConfirmBody: {
    text: 'Rotating will generate a new secret immediately. The previous secret will remain valid for 24 hours.',
    description: 'Confirmation dialog body for rotating PMS secret',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsStaysPmsDisableBtn: {
    text: 'Disable Connection',
    description: 'Button to disable PMS connection',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsEnableBtn: {
    text: 'Enable Connection',
    description: 'Button to enable PMS connection',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsDeleteBtn: {
    text: 'Delete Connection',
    description: 'Button to delete PMS connection',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsDeleteConfirmTitle: {
    text: 'Delete PMS Connection',
    description: 'Confirmation dialog title for deleting PMS connection',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsDeleteConfirmBody: {
    text: 'Are you sure you want to delete this PMS connection? Inbound webhook events will be refused.',
    description: 'Confirmation dialog body for deleting PMS connection',
    surface: 'shell',
    maxChars: 110,
  },
  NLSOpsStaysPmsRecentEventsTitle: {
    text: 'Recent Inbound Events',
    description: 'Title for recent events section',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsRecentProblemsTitle: {
    text: 'Recent Problems (Unmapped / Ignored)',
    description: 'Title for problems section',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsStaysPmsNoProblems: {
    text: 'No problems reported.',
    description: 'Notice when there are no unmapped or ignored events',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsOutboxQueueTitle: {
    text: 'Write-Back Outbox Queue',
    description: 'Title for outbox queue section',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsRetryFailedBtn: {
    text: 'Retry Failed Items',
    description: 'Button to retry failed outbox items',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsSaveSettingsBtn: {
    text: 'Save Settings',
    description: 'Button to save PMS settings',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsSavedToast: {
    text: 'PMS settings saved successfully.',
    description: 'Toast after saving PMS settings',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysPmsAdapterEzee: {
    text: 'eZee Absolute',
    description: 'Option label for eZee Absolute PMS connector',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsAuthCode: {
    text: 'Auth Code',
    description: 'Label for eZee Absolute Auth Code field',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsAuthCodeDesc: {
    text: 'eZee API Auth Code provided by eZee support for this property.',
    description: 'Help description for eZee Auth Code field',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysPmsAuthCodeMasked: {
    text: 'Saved (write-only)',
    description: 'Placeholder indicating Auth Code is saved securely',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsAuthCodePlaceholder: {
    text: 'Enter eZee API Auth Code',
    description: 'Placeholder for eZee Auth Code input',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsTestConnectionBtn: {
    text: 'Test connection',
    description: 'Button to test eZee PMS connection and room mapping',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsTestingConnection: {
    text: 'Testing connection...',
    description: 'Button state while testing eZee PMS connection',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsTestResultSuccess: {
    text: 'Connected: {total} total rooms in eZee, {mapped} mapped.',
    description: 'Feedback banner showing successful test connection results',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysPmsTestResultUnmappedList: {
    text: 'Unmapped room names: {rooms}',
    description: 'List of unmapped rooms found in eZee',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsStaysPmsLastPollLabel: {
    text: 'Last poll',
    description: 'Label for last polling timestamp',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsLastPollNever: {
    text: 'Never',
    description: 'Status when polling has not run yet',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysPmsLastErrorLabel: {
    text: 'Last error',
    description: 'Label for last error code',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsEzeeHotelCode: {
    text: 'eZee Hotel Code',
    description: 'Label for eZee Hotel Code',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsEzeeHotelCodeHelp: {
    text: 'Map each eZee Hotel Code to a Timelining property.',
    description: 'Help text for eZee hotel code mapping',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsStaysPmsAdapterHotelogix: {
    text: 'Hotelogix',
    description: 'Option label for Hotelogix PMS connector',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsConsumerKey: {
    text: 'Consumer key',
    description: 'Label for Hotelogix Consumer Key field',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsConsumerKeyPlaceholder: {
    text: 'Enter Hotelogix Consumer Key',
    description: 'Placeholder for Hotelogix Consumer Key input',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsConsumerSecret: {
    text: 'Consumer secret',
    description: 'Label for Hotelogix Consumer Secret field',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsConsumerSecretPlaceholder: {
    text: 'Enter Hotelogix Consumer Secret',
    description: 'Placeholder for Hotelogix Consumer Secret input',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsHotelId: {
    text: 'Hotel ID',
    description: 'Label for Hotelogix Hotel ID field',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsHotelIdPlaceholder: {
    text: 'Enter Hotelogix Hotel ID',
    description: 'Placeholder for Hotelogix Hotel ID input',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsCounterId: {
    text: 'Counter ID',
    description: 'Label for Hotelogix Counter ID field',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysPmsCounterIdPlaceholder: {
    text: 'Enter Counter ID (e.g. 1)',
    description: 'Placeholder for Hotelogix Counter ID input',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsEmail: {
    text: 'API user email',
    description: 'Label for Hotelogix API User Email field',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsEmailPlaceholder: {
    text: 'Enter API user email',
    description: 'Placeholder for Hotelogix API user email input',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsPassword: {
    text: 'Password (optional)',
    description: 'Label for optional Hotelogix user password field',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsPasswordPlaceholder: {
    text: 'Enter password (if configured)',
    description: 'Placeholder for Hotelogix password input',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsRoomReadyNotSupported: {
    text: 'Room ready is not sent to Hotelogix (no API)',
    description: 'Note explaining room ready status cannot be sent to Hotelogix',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysPmsTestResultSuccessHotelogix: {
    text: 'Connected: {total} total rooms in Hotelogix, {mapped} mapped.',
    description: 'Feedback banner showing successful test connection results for Hotelogix',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysPmsAdapterStayflexi: {
    text: 'Stayflexi (report upload)',
    description: 'Option label for Stayflexi report upload PMS connector',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsAdapterDjubo: {
    text: 'Djubo (report upload)',
    description: 'Option label for Djubo report upload PMS connector',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsAdapterIdsNext: {
    text: 'IDS Next (report upload)',
    description: 'Option label for IDS Next report upload PMS connector',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsDjuboNoApiNote: {
    text: 'Djubo has no public housekeeping API: upload room-status or housekeeping report exports (.csv or .xlsx)',
    description: 'Information note explaining Djubo operates via report upload',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsStaysPmsIdsNextNoApiNote: {
    text: 'IDS Next has no public API: upload FortuneNext room-status reports (.csv or .xlsx), or configure HTNG interface middleware with our webhook endpoint.',
    description: 'Information note explaining IDS Next operates via report upload or HTNG middleware',
    surface: 'shell',
    maxChars: 160,
  },
  NLSOpsStaysPmsIdsNextMiddlewareLink: {
    text: 'View IDS Next / HTNG middleware setup guide',
    description: 'Link text to IDS Next HTNG middleware integration documentation',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysPmsStayflexiNoApiNote: {
    text: 'Stayflexi has no public API: upload Housekeeping report exports (.csv or .xlsx)',
    description: 'Information note explaining Stayflexi operates via housekeeping report upload',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsStaysPmsStayflexiUploadBtn: {
    text: 'Upload report',
    description: 'Button to trigger report file upload',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsStayflexiUploading: {
    text: 'Uploading report…',
    description: 'Button state while report upload is in progress',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsStayflexiFilePickerLabel: {
    text: 'Housekeeping report export',
    description: 'Label for file picker input',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsStayflexiFileHelp: {
    text: 'Supported formats: CSV or Excel (.xlsx). Maximum 2 MB and 2,000 rows.',
    description: 'Help text for Stayflexi file upload',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsStaysPmsStayflexiColumnMapTitle: {
    text: 'Column mapping',
    description: 'Section title for Stayflexi report column mapping',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsStayflexiColumnMapDesc: {
    text: 'Match columns from your Stayflexi export to required room fields.',
    description: 'Description for report column mapping step',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysPmsStayflexiColRoom: {
    text: 'Room name column',
    description: 'Label for room name column selector',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsStayflexiColStatus: {
    text: 'Housekeeping status column',
    description: 'Label for housekeeping status column selector',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsStayflexiColOccupancy: {
    text: 'Occupancy column (optional)',
    description: 'Label for optional occupancy column selector',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsStayflexiColBlocked: {
    text: 'Blocked / OOO column (optional)',
    description: 'Label for optional blocked column selector',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysPmsStayflexiColDueOut: {
    text: 'Due out column (optional)',
    description: 'Label for optional due-out column selector',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsStayflexiColStayover: {
    text: 'Stayover column (optional)',
    description: 'Label for optional stayover column selector',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysPmsStayflexiSelectColumn: {
    text: 'Select column…',
    description: 'Default placeholder option for column selectors',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsStayflexiLastImportLabel: {
    text: 'Last report import',
    description: 'Label displaying timestamp of last report upload',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsStayflexiNeverImported: {
    text: 'No report imported yet',
    description: 'Value when no report has been uploaded yet',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysPmsStayflexiImportSuccess: {
    text: 'Imported {rows} rows ({mapped} mapped rooms, {events} events applied).',
    description: 'Banner text confirming successful report import',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsStaysPmsStayflexiBaselineNote: {
    text: 'First upload established room baseline; subsequent uploads detect departures and status changes.',
    description: 'Note explaining baseline state on first upload',
    surface: 'shell',
    maxChars: 110,
  },
  NLSOpsStaysPmsStayflexiUnmappedRooms: {
    text: 'Unmapped rooms: {rooms}',
    description: 'Listing unmapped room names from report',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsStaysPmsStayflexiErrorFileTooLarge: {
    text: 'File exceeds 2 MB limit.',
    description: 'Error shown when uploaded file exceeds 2 megabytes',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysPmsStayflexiErrorTooManyRows: {
    text: 'Report exceeds 2,000 rows limit ({count} rows found).',
    description: 'Error shown when report exceeds maximum row limit',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysPmsStayflexiErrorMissingRequiredCols: {
    text: 'Required columns (Room and Status) could not be detected. Please map them below.',
    description: 'Error shown when automatic column detection fails to find mandatory columns',
    surface: 'shell',
    maxChars: 95,
  },
  NLSOpsStaysPmsStayflexiErrorNoRows: {
    text: 'Report file contains no data rows.',
    description: 'Error shown when uploaded file is empty or only has headers',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsStaysPmsStayflexiPropertySelectorLabel: {
    text: 'Target property',
    description: 'Label for property selector when uploading report',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysPmsStayflexiEditMappingBtn: {
    text: 'Edit column mapping',
    description: 'Button to toggle column mapping view',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostTitle: {
    text: 'Lost & found',
    description: 'Header title for Stays lost & found screen',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostSubtitle: {
    text: 'Log, track, and return found items, handle guest claims, or propose disposal.',
    description: 'Subtitle for Stays lost & found screen',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsStaysLostTabItems: {
    text: 'Items',
    description: 'Tab label for lost & found items list',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLostTabSettings: {
    text: 'Settings',
    description: 'Tab label for lost & found property settings',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLostLogItemBtn: {
    text: 'Log found item',
    description: 'Button to open log found item dialog',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostEmpty: {
    text: 'No lost & found items recorded yet.',
    description: 'Empty state message when no items are logged',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysLostEmptyFiltered: {
    text: 'No items match the selected filter.',
    description: 'Empty state message when filter yields no items',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysLostStatusLogged: {
    text: 'Logged',
    description: 'Item status label for LOGGED',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLostStatusClaimOpen: {
    text: 'Claim open',
    description: 'Item status label for CLAIM_OPEN',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostStatusReturned: {
    text: 'Returned',
    description: 'Item status label for RETURNED',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLostStatusDisposalProposed: {
    text: 'Disposal proposed',
    description: 'Item status label for DISPOSAL_PROPOSED',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostStatusDisposed: {
    text: 'Disposed',
    description: 'Item status label for DISPOSED',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLostCatClothing: {
    text: 'Clothing',
    description: 'Category label for CLOTHING',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCatElectronics: {
    text: 'Electronics',
    description: 'Category label for ELECTRONICS',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCatDocuments: {
    text: 'Documents',
    description: 'Category label for DOCUMENTS',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCatJewellery: {
    text: 'Jewellery',
    description: 'Category label for JEWELLERY',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCatMoney: {
    text: 'Money',
    description: 'Category label for MONEY',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCatKeys: {
    text: 'Keys',
    description: 'Category label for KEYS',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCatBags: {
    text: 'Bags',
    description: 'Category label for BAGS',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCatOther: {
    text: 'Other',
    description: 'Category label for OTHER',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostFilterAllProperties: {
    text: 'All properties',
    description: 'Filter option for all properties',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostFilterAllStatuses: {
    text: 'All statuses',
    description: 'Filter option for all statuses',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostFilterDisposalEligible: {
    text: 'Disposal eligible',
    description: 'Filter option for items past hold period',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostFilterAwaitingVerify: {
    text: 'Awaiting verify',
    description: 'Filter option for claims described awaiting desk verification',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostFilterAwaitingSecondSign: {
    text: 'Awaiting 2nd sign',
    description: 'Filter option for proposals awaiting second disposal signature',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostHoldUntilChip: {
    text: 'Hold until {date}',
    description: 'Chip indicating hold expiration date',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostEligibleDisposalChip: {
    text: 'Eligible for disposal',
    description: 'Chip indicating item is past hold period and eligible for disposal',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostValuableBadge: {
    text: 'Valuable',
    description: 'Badge for valuable category items',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLostColCategory: {
    text: 'Category',
    description: 'Column header for item category',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostColDescription: {
    text: 'Description',
    description: 'Column header for item description',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostColPlace: {
    text: 'Place',
    description: 'Column header for place where item was found',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostColFoundDate: {
    text: 'Found date',
    description: 'Column header for found date',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostColBin: {
    text: 'Storage bin',
    description: 'Column header for storage bin',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostColStatus: {
    text: 'Status',
    description: 'Column header for status',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostColActions: {
    text: 'Actions',
    description: 'Column header for actions',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostBinUnassigned: {
    text: 'Unassigned bin',
    description: 'Label when storage bin is empty',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostBinLabel: {
    text: 'Bin: {label}',
    description: 'Display label for storage bin',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostDialogLogTitle: {
    text: 'Log found item',
    description: 'Title for log found item dialog',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostDialogPropertyLabel: {
    text: 'Property',
    description: 'Form label for property',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostDialogPlaceLabel: {
    text: 'Place found',
    description: 'Form label for place found',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostDialogCategoryLabel: {
    text: 'Category',
    description: 'Form label for category',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostDialogValuableLabel: {
    text: 'Marked as valuable (longer hold)',
    description: 'Checkbox label for valuable flag',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostDialogDescriptionLabel: {
    text: 'Description (3–500 chars)',
    description: 'Form label for description',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostDialogDescriptionPlaceholder: {
    text: 'Describe the item (brand, colour, distinguishing marks)',
    description: 'Placeholder for description',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysLostDialogBinLabel: {
    text: 'Storage bin label',
    description: 'Form label for bin label',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostDialogBinPlaceholder: {
    text: 'e.g. Bin A-12, Front Desk Safe',
    description: 'Placeholder for bin label',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostDialogFoundTimeLabel: {
    text: 'Time found',
    description: 'Form label for found time',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostDialogSaveBtn: {
    text: 'Log item',
    description: 'Submit button for log found item dialog',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostDialogCancelBtn: {
    text: 'Cancel',
    description: 'Cancel button for dialogs',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLostItemLoggedSuccess: {
    text: 'Found item logged successfully.',
    description: 'Toast after logging found item',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostDrawerTitle: {
    text: 'Lost & found item details',
    description: 'Title for item view drawer',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostDrawerPhotos: {
    text: 'Photos ({count}/4)',
    description: 'Section header for item photos',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostAddPhotoBtn: {
    text: 'Add photo',
    description: 'Button to add photo to item',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCustodyLogTitle: {
    text: 'Custody history',
    description: 'Section header for custody log events',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostBinMoveTitle: {
    text: 'Move storage bin',
    description: 'Section header for moving bin',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostBinMoveBtn: {
    text: 'Update bin',
    description: 'Button to save new bin label',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostBinMovedSuccess: {
    text: 'Storage bin updated.',
    description: 'Toast after updating bin',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostCloseDrawerBtn: {
    text: 'Close',
    description: 'Button to close drawer',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLostCloseDetailsAria: {
    text: 'Close details',
    description: 'Aria label for close details button',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostEventLogged: {
    text: 'Item logged',
    description: 'Custody event label for LOGGED',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostEventBinMoved: {
    text: 'Storage bin updated',
    description: 'Custody event label for BIN_MOVED',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostEventPhotoAdded: {
    text: 'Photo attached',
    description: 'Custody event label for PHOTO_ADDED',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostEventClaimStarted: {
    text: 'Guest claim link issued',
    description: 'Custody event label for CLAIM_STARTED',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostEventClaimLinkReissued: {
    text: 'Guest claim link reissued',
    description: 'Custody event label for CLAIM_LINK_REISSUED',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostEventClaimDescribed: {
    text: 'Guest submitted item description',
    description: 'Custody event label for CLAIM_DESCRIBED',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostEventClaimMatched: {
    text: 'Claim verified as MATCH',
    description: 'Custody event label for CLAIM_MATCHED',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostEventClaimRejected: {
    text: 'Claim verified as NO MATCH',
    description: 'Custody event label for CLAIM_REJECTED',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostEventClaimCancelled: {
    text: 'Claim cancelled by desk',
    description: 'Custody event label for CLAIM_CANCELLED',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostEventClaimExpired: {
    text: 'Claim link expired',
    description: 'Custody event label for CLAIM_EXPIRED',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostEventHandedOver: {
    text: 'Item handed over to guest (pickup)',
    description: 'Custody event label for HANDED_OVER',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostEventCouriered: {
    text: 'Item dispatched via courier',
    description: 'Custody event label for COURIERED',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostEventDisposalProposed: {
    text: 'Disposal proposed',
    description: 'Custody event label for DISPOSAL_PROPOSED',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostEventDisposalSigned: {
    text: 'Disposal confirmed with second signature',
    description: 'Custody event label for DISPOSAL_SIGNED',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsStaysLostEventDisposalWithdrawn: {
    text: 'Disposal proposal withdrawn',
    description: 'Custody event label for DISPOSAL_WITHDRAWN',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostEventDisposed: {
    text: 'Item marked disposed',
    description: 'Custody event label for DISPOSED',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostEventClaimantDataPurged: {
    text: 'Claimant personal data purged (30 days)',
    description: 'Custody event label for CLAIMANT_DATA_PURGED',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsStaysLostEventClaimantSignInRemoved: {
    text: 'Claimant authentication removed',
    description: 'Custody event label for CLAIMANT_SIGN_IN_REMOVED',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostEventClaimantSignInKept: {
    text: 'Claimant authentication retained',
    description: 'Custody event label for CLAIMANT_SIGN_IN_KEPT',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostStartClaimBtn: {
    text: 'Start claim',
    description: 'Button to start guest claim',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostStartClaimTitle: {
    text: 'Start guest claim',
    description: 'Title for start claim dialog',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostClaimantNameLabel: {
    text: 'Guest full name',
    description: 'Form label for claimant name',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostClaimantPhoneLabel: {
    text: 'Guest phone number',
    description: 'Form label for claimant phone',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostStartClaimSubmitBtn: {
    text: 'Generate claim link',
    description: 'Button to generate claim link',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostClaimLinkModalTitle: {
    text: 'Guest claim link',
    description: 'Title for claim link dialog',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostClaimLinkWarning: {
    text: 'This link is shown once. Send it to the guest yourself.',
    description: 'Notice that claim link is displayed once',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysLostClaimLinkCopyBtn: {
    text: 'Copy link',
    description: 'Button to copy claim link',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostClaimLinkCopied: {
    text: 'Link copied to clipboard!',
    description: 'Toast after copying claim link',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostClaimLinkShareBtn: {
    text: 'Share link',
    description: 'Button to share claim link',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostClaimLinkShared: {
    text: 'Link shared.',
    description: 'Toast after sharing link',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostClaimLinkDoneBtn: {
    text: 'Done',
    description: 'Button to dismiss claim link dialog',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStaysLostReissueClaimBtn: {
    text: 'Reissue link',
    description: 'Button to reissue claim link',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCancelClaimBtn: {
    text: 'Cancel claim',
    description: 'Button to cancel claim',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostCancelClaimTitle: {
    text: 'Cancel guest claim',
    description: 'Title for cancel claim dialog',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostCancelReasonLabel: {
    text: 'Cancellation reason (3–500 chars)',
    description: 'Form label for cancel reason',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostCancelConfirmBtn: {
    text: 'Confirm cancellation',
    description: 'Button to confirm cancellation',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostClaimCancelledSuccess: {
    text: 'Claim has been cancelled.',
    description: 'Toast after cancelling claim',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostVerifyClaimBtn: {
    text: 'Verify claim',
    description: 'Button to verify claim description',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostVerifyClaimTitle: {
    text: 'Verify guest description',
    description: 'Title for verify claim dialog',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostVerifyItemDescTitle: {
    text: 'Logged item details & photos',
    description: 'Header for logged item details in verification',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostVerifyGuestDescTitle: {
    text: 'Guest submitted description',
    description: 'Header for guest description in verification',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostVerifyMatchBtn: {
    text: 'Confirm MATCH',
    description: 'Button to confirm claim match',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostVerifyNoMatchBtn: {
    text: 'Reject NO_MATCH',
    description: 'Button to reject claim mismatch',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostVerifyNoteLabel: {
    text: 'Verification note (optional)',
    description: 'Form label for verification note',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostVerifySuccess: {
    text: 'Claim verification recorded.',
    description: 'Toast after verifying claim',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostReturnItemBtn: {
    text: 'Complete return',
    description: 'Button to complete item return',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostReturnItemTitle: {
    text: 'Complete item return',
    description: 'Title for return dialog',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostReturnModeLabel: {
    text: 'Return method',
    description: 'Form label for return mode',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostReturnModePickup: {
    text: 'In-person pickup',
    description: 'Option for pickup return',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostReturnModeCourier: {
    text: 'Courier delivery',
    description: 'Option for courier return',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostCourierRefLabel: {
    text: 'Courier tracking reference (optional)',
    description: 'Form label for tracking reference',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostReturnSubmitBtn: {
    text: 'Mark item returned',
    description: 'Button to mark item returned',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostReturnSuccess: {
    text: 'Item marked as returned.',
    description: 'Toast after returning item',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostShowClaimantBtn: {
    text: 'Show claimant',
    description: 'Button to reveal claimant details',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostShowClaimantTitle: {
    text: 'Claimant details',
    description: 'Title for claimant details modal',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostShowClaimantWarning: {
    text: 'This view is logged.',
    description: 'Warning that claimant access is audited',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostClaimantPurgedNotice: {
    text: 'Claimant personal data was deleted after 30 days.',
    description: 'Notice displayed when claimant details are purged',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsStaysLostClaimantName: {
    text: 'Name: {name}',
    description: 'Display for claimant name',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostClaimantPhone: {
    text: 'Phone: {phone}',
    description: 'Display for claimant phone',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostClaimantAddress: {
    text: 'Courier address: {address}',
    description: 'Display for claimant courier address',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsStaysLostClaimPurgeScheduled: {
    text: 'Claimant details are deleted on {date}',
    description: 'Notice showing scheduled purge date for claimant PII',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsStaysLostClaimPhoneMasked: {
    text: 'Phone: {phone}',
    description: 'Display for masked phone number',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostClaimStatus: {
    text: 'Claim status: {status}',
    description: 'Display for claim status',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostClaimExpiresAt: {
    text: 'Link expires: {date}',
    description: 'Display for claim link expiration',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostProposeDisposalBtn: {
    text: 'Propose disposal',
    description: 'Button to propose item disposal',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostProposeDisposalTitle: {
    text: 'Propose item disposal',
    description: 'Title for propose disposal dialog',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostDisposalMethodLabel: {
    text: 'Disposal method',
    description: 'Form label for disposal method',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostDisposalMethodDonate: {
    text: 'Donate',
    description: 'Disposal method label for DONATE',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostDisposalMethodDiscard: {
    text: 'Discard',
    description: 'Disposal method label for DISCARD',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostDisposalMethodGiveToFinder: {
    text: 'Give to finder',
    description: 'Disposal method label for GIVE_TO_FINDER',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostDisposalMethodHandToPolice: {
    text: 'Hand to police',
    description: 'Disposal method label for HAND_TO_POLICE',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostDisposalNoteLabel: {
    text: 'Disposal note (3–500 chars)',
    description: 'Form label for disposal proposal note',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostProposeSubmitBtn: {
    text: 'Submit proposal',
    description: 'Button to submit disposal proposal',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostSignDisposalBtn: {
    text: 'Sign & complete disposal',
    description: 'Button to sign disposal proposal',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostSignDisposalTitle: {
    text: 'Second signature for disposal',
    description: 'Title for sign disposal dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostDisposalProposedBy: {
    text: 'Proposed by {name} on {date}',
    description: 'Details of who proposed disposal and when',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysLostDisposalProposalNote: {
    text: 'Proposal note: {note}',
    description: 'Details of disposal proposal note',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysLostSignSubmitBtn: {
    text: 'Sign and mark disposed',
    description: 'Submit button to confirm disposal signature',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostWithdrawDisposalBtn: {
    text: 'Withdraw proposal',
    description: 'Button to withdraw disposal proposal',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostWithdrawDisposalTitle: {
    text: 'Withdraw disposal proposal',
    description: 'Title for withdraw disposal dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostWithdrawReasonLabel: {
    text: 'Withdrawal reason (3–500 chars)',
    description: 'Form label for withdrawal reason',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostWithdrawSubmitBtn: {
    text: 'Withdraw proposal',
    description: 'Submit button to withdraw disposal',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostDisposedDetails: {
    text: 'Disposed via {method}. Proposed: {proposer} ({propDate}). Signed: {signer} ({signDate}).',
    description: 'Summary of completed disposal',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsStaysLostSettingsTitle: {
    text: 'Lost & found hold settings',
    description: 'Title for settings card',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostSettingsPropertySelect: {
    text: 'Select property',
    description: 'Select label for property in settings',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostHoldDaysLabel: {
    text: 'Standard hold period (days, 30–365)',
    description: 'Form label for standard hold days',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysLostValuableHoldDaysLabel: {
    text: 'Valuable items hold period (days, 30–730)',
    description: 'Form label for valuable hold days',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsStaysLostSettingsDefaultsNotice: {
    text: 'Defaults: 90 days standard, 180 days valuables.',
    description: 'Notice of default hold settings',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsStaysLostSettingsSaveBtn: {
    text: 'Save settings',
    description: 'Button to save lost found settings',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysLostHoldDaysBoundsError: {
    text: 'Standard hold period must be between 30 and 365 days.',
    description: 'Validation error when standard hold days is out of bounds',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysLostValuableHoldDaysBoundsError: {
    text: 'Valuable items hold period must be between 30 and 730 days.',
    description: 'Validation error when valuable hold days is out of bounds',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysLostSettingsSavedToast: {
    text: 'Settings saved successfully.',
    description: 'Toast after saving settings',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostSettingsConflict: {
    text: 'These settings were changed by someone else. Please reload and try again.',
    description: 'Version conflict error message for settings card',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsStaysTodayLostTitle: {
    text: 'Lost & found',
    description: 'Title for Stays Today lost & found card',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysTodayLostLoggedCount: {
    text: '{count} items logged',
    description: 'Count of logged items on Today card',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysTodayLostAwaitingVerifyCount: {
    text: '{count} claims awaiting verification',
    description: 'Count of described claims on Today card',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysTodayLostPastHoldCount: {
    text: '{count} items eligible for disposal',
    description: 'Count of items past hold period on Today card',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysTodayLostAwaitingSignCount: {
    text: '{count} proposals awaiting 2nd signature',
    description: 'Count of disposal proposals awaiting second signature on Today card',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsStaysTodayLostViewAllBtn: {
    text: 'View lost & found board',
    description: 'Link button to open lost & found desk screen',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysLostScanLogAction: {
    text: 'Log found item',
    description: 'Button text in room scan page for logging found item',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostScanLogTitle: {
    text: 'Log found item in {room}',
    description: 'Dialog title for logging found item on room scan page',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostScanPhotoLabel: {
    text: 'Take item photo (optional)',
    description: 'Label for camera photo capture input',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostScanCameraBtn: {
    text: 'Capture photo',
    description: 'Button to trigger camera on mobile device',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysLostScanSuccess: {
    text: 'Found item recorded successfully.',
    description: 'Success toast after helper logs found item from scan page',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysClaimLandingTitle: {
    text: 'Lost & found guest claim',
    description: 'Header title on guest claim page',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysClaimInvalidTitle: {
    text: 'Claim link not valid',
    description: 'Title for invalid claim link screen',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysClaimInvalidBody: {
    text: 'This link isn’t valid any more. Please contact the hotel.',
    description: 'Generic body message for expired, closed, unknown, or purged links',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysClaimPropertyLabel: {
    text: 'Hotel / Property',
    description: 'Label for hotel property on claim page',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimCategoryLabel: {
    text: 'Item category',
    description: 'Label for item category on claim page',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimPhoneMaskedLabel: {
    text: 'Registered phone',
    description: 'Label for masked phone on claim page',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimExpiryLabel: {
    text: 'Link expires',
    description: 'Label for link expiration date on claim page',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimSendOtpBtn: {
    text: 'Send verification code',
    description: 'Button to send OTP on claim page',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysClaimOtpCodeLabel: {
    text: 'Enter 6-digit verification code',
    description: 'Input label for OTP code on claim page',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysClaimVerifyOtpBtn: {
    text: 'Verify & continue',
    description: 'Button to verify OTP code',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimCapExhausted: {
    text: 'Verification attempts exceeded. Please contact the hotel.',
    description: 'Error shown when OTP attempts cap is exhausted',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsStaysClaimFormTitle: {
    text: 'Describe your item',
    description: 'Form title on guest claim page',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysClaimDescHelp: {
    text: 'Describe it so the hotel can confirm it’s yours: colour, brand, marks, contents.',
    description: 'Help text for item description input on claim page',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsStaysClaimDescPlaceholder: {
    text: 'e.g. Navy blue Dell laptop with a small scratch near the trackpad…',
    description: 'Placeholder for item description on claim page',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsStaysClaimReturnModeLabel: {
    text: 'How would you like to receive it?',
    description: 'Label for return mode selection on claim page',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsStaysClaimReturnPickup: {
    text: 'I will pick it up from the hotel',
    description: 'Pickup option on guest claim page',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysClaimReturnCourier: {
    text: 'Send via courier to my address',
    description: 'Courier delivery option on guest claim page',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysClaimAddressLine1: {
    text: 'Address line 1',
    description: 'Input label for courier address line 1',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimAddressLine2: {
    text: 'Address line 2',
    description: 'Input label for courier address line 2',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimAddressLine3: {
    text: 'Address line 3 (optional)',
    description: 'Input label for courier address line 3',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsStaysClaimCity: {
    text: 'City',
    description: 'Input label for city',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsStaysClaimPin: {
    text: 'PIN code (6 digits)',
    description: 'Input label for 6-digit postal code',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimState: {
    text: 'State',
    description: 'Input label for state',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimSubmitBtn: {
    text: 'Submit claim',
    description: 'Button to submit completed guest claim',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimThankYouTitle: {
    text: 'Claim submitted',
    description: 'Title for thank you screen on claim page',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsStaysClaimThankYouBody: {
    text: 'Thanks. The hotel will check and contact you.',
    description: 'Body message after submitting claim',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsStaysClaimPrivacyLine: {
    text: 'Your name, phone and address are used only to return this item and are deleted 30 days after the claim closes.',
    description: 'Privacy notice under guest claim form',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsStaysLostLoadingClaimant: {
    text: 'Loading claimant details…',
    description: 'Loading claimant data in modal',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostNoEvents: {
    text: 'No custody history recorded.',
    description: 'Empty state for custody events in drawer',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostErrGeneric: {
    text: 'An error occurred. Please try again.',
    description: 'Generic error message for lost and found actions',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsStaysLostErrNotFound: {
    text: 'Item or claim not found.',
    description: 'Error message when lost item or claim is not found',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostErrPermission: {
    text: 'You do not have permission to perform this action.',
    description: 'Permission error message for lost and found actions',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsStaysLostErrInvalidArg: {
    text: 'Please check your input values.',
    description: 'Validation error message for lost and found actions',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsStaysLostErrConflict: {
    text: 'Record was updated by someone else. Please refresh.',
    description: 'Concurrency conflict error message for lost and found actions',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsStaysLostErrSameProposer: {
    text: 'The second signature must be from a different person than the proposer.',
    description: 'Error message when proposer tries to sign disposal',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsStaysLostErrNotEligibleDisposal: {
    text: 'Item has not reached its hold expiration date yet.',
    description: 'Error message when proposing disposal before holdUntil',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlaceScanTitle: {
    text: 'Place check-in',
    description: 'Title for place QR scanner page.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlaceScanLoading: {
    text: 'Loading place scanner…',
    description: 'Loading message while scanning place token.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlaceScanNeutralTitle: {
    text: 'Place QR sticker',
    description: 'Title for neutral non-shops place sticker.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlaceScanNeutralDesc: {
    text: 'This sticker is registered for {placeName} ({placeLevel}, {placeSector}). QR attendance check-in is enabled for Shops outlets.',
    description: 'Description for neutral non-shops place sticker.',
    surface: 'shell',
    maxChars: 130,
  },
  NLSOpsPlaceScanNotRosteredTitle: {
    text: 'Not rostered now',
    description: 'Status when caller is not rostered on current shift.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlaceScanNotRosteredDesc: {
    text: 'You do not have a shift rostered at {placeName} right now. Check with your store manager.',
    description: 'Description when caller is not rostered.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsPlaceScanCanCheckInTitle: {
    text: 'Ready to check in',
    description: 'Status when caller can check in.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlaceScanShiftLabel: {
    text: 'Shift: {shiftName} ({shiftTime})',
    description: 'Shift label and time window.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlaceScanCheckInAction: {
    text: 'Check in',
    description: 'Button to check into shift via QR scan.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlaceScanCheckingIn: {
    text: 'Checking in…',
    description: 'Busy state during QR check in.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlaceScanCheckedInTitle: {
    text: 'Checked in',
    description: 'Status title when caller is checked in.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlaceScanCheckedInAt: {
    text: 'Checked in at {time}',
    description: 'Timestamp when caller checked in.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlaceScanCheckOutAction: {
    text: 'Check out',
    description: 'Button to check out of shift via QR scan.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlaceScanCheckingOut: {
    text: 'Checking out…',
    description: 'Busy state during QR check out.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlaceScanCheckedOutTitle: {
    text: 'Checked out',
    description: 'Status title when caller is checked out.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlaceScanCheckedOutAt: {
    text: 'Checked out at {time}',
    description: 'Timestamp when caller checked out.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPlaceScanMarkedByOtherTitle: {
    text: 'Attendance marked by manager',
    description: 'Status title when caller attendance was marked by manager.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlaceScanMarkedByOtherDesc: {
    text: 'Your attendance for this shift was already marked by your manager or on the roll. If an update is needed, request a correction.',
    description: 'Description when attendance was marked by manager.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSOpsPlaceScanColleaguesTitle: {
    text: 'Unmarked colleagues on this shift',
    description: 'Title for colleague proxy mark section.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlaceScanColleaguesDesc: {
    text: 'Mark colleague who forgot their phone or has no device (proxy mark).',
    description: 'Description for colleague proxy marking.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsPlaceScanColleaguesEmpty: {
    text: 'All colleagues on this shift are marked.',
    description: 'Empty state when all colleagues are marked.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlaceScanProxyAction: {
    text: 'Mark present (proxy)',
    description: 'Button to mark colleague present as peer proxy.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPlaceScanProxySubmitting: {
    text: 'Marking…',
    description: 'Busy state when proxy marking colleague.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPlaceScanProxySuccess: {
    text: 'Peer proxy mark submitted for manager approval.',
    description: 'Success message after proxy marking colleague.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsPlaceScanErrorInvalidToken: {
    text: 'This QR code is invalid or not found.',
    description: 'Error when place token is not found.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsPlaceScanErrorRevokedToken: {
    text: 'This QR code has been revoked.',
    description: 'Error when place token is revoked.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPlaceScanErrorAccessDenied: {
    text: 'You do not have access to this workspace.',
    description: 'Error when caller does not have workspace access.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlaceScanErrorModuleDisabled: {
    text: 'The Shops module is disabled for this workspace.',
    description: 'Error when shops module is disabled.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsPlaceScanErrorAlreadyCheckedOut: {
    text: 'You have already checked out of this shift.',
    description: 'Error when attempting second checkout.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPlaceScanErrorMarkedByOther: {
    text: 'Attendance was already marked by manager or roll.',
    description: 'Error when already marked on roll.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsPlaceScanErrorNotRostered: {
    text: 'No active roster assignment found for you now.',
    description: 'Error when caller is not rostered on current shift.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsPlaceScanErrorEarlyWindow: {
    text: 'It is too early to check in for this shift (window opens 60 minutes before start).',
    description: 'Error when caller attempts check in before early window opens.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsPlaceScanErrorGeneric: {
    text: 'Could not complete scan check-in. Please try again.',
    description: 'Generic scan check in error.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPlaceScanRoomDetails: {
    text: 'Room details',
    description: 'Aria label for stays room details section.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPlaceScanCleanProofForm: {
    text: 'Clean proof form',
    description: 'Aria label for clean proof submission form.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestRoomTitle: {
    text: 'Guest Requests',
    description: 'Header title for anonymous guest room scan page.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsGuestRoomSubtitle: {
    text: 'How can we help make your stay comfortable?',
    description: 'Subtitle for guest requests page.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsGuestRoomOptionTowels: {
    text: 'Towels',
    description: 'Option tile for requesting fresh towels.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsGuestRoomOptionWater: {
    text: 'Water',
    description: 'Option tile for requesting drinking water.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsGuestRoomOptionToiletries: {
    text: 'Toiletries',
    description: 'Option tile for requesting toiletries.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsGuestRoomOptionCleaning: {
    text: 'Room Cleaning',
    description: 'Option tile for requesting room cleaning.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsGuestRoomOptionBroken: {
    text: 'Something Broken',
    description: 'Option tile for reporting broken item in room.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestRoomOptionOther: {
    text: 'Other',
    description: 'Option tile for other requests.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsGuestRoomOptionComplaint: {
    text: 'Guest Complaint',
    description: 'Option tile for lodging a complaint.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestRoomOptionExtraBooking: {
    text: 'Book spa / pool / activity',
    description: 'Option tile for booking extras like spa, pool, gym or activity.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsGuestConfirmedTime: {
    text: 'Confirmed ({time})',
    description: 'Status text for confirmed extras booking.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestDeclinedReason: {
    text: 'Declined ({reason})',
    description: 'Status text for declined extras booking request.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsExtraRequestBookable: {
    text: 'Select extra',
    description: 'Label for selecting an extra bookable.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsExtraRequestTime: {
    text: 'Preferred time',
    description: 'Label for preferred time input on extra request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsExtraRequestGuests: {
    text: 'Number of guests',
    description: 'Label for guests count on extra request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestRoomNoteLabel: {
    text: 'Additional note (optional)',
    description: 'Label for optional note textarea.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsGuestRoomNotePlaceholder: {
    text: 'Tell us any details (no phone numbers or email addresses)…',
    description: 'Placeholder for note textarea.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsGuestRoomSubmit: {
    text: 'Submit Request',
    description: 'Button text to submit guest request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestRoomSubmitting: {
    text: 'Submitting…',
    description: 'Button text when guest request is submitting.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsGuestRoomSubmittedTitle: {
    text: 'Request Received',
    description: 'Success card title after guest request is submitted.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsGuestRoomSubmittedDesc: {
    text: "We've received your request and our team is on it.",
    description: 'Success card description.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsGuestRoomTrackStatus: {
    text: 'Track Request Status',
    description: 'Link text to status page for request.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsGuestRoomMessageWhatsApp: {
    text: 'Message us on WhatsApp',
    description: 'Link button to send request or message via WhatsApp.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsGuestRoomPreviousRequests: {
    text: 'Your Recent Requests',
    description: 'Header for previous requests list from this device.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsGuestRoomPreviousEmpty: {
    text: 'No previous requests from this device.',
    description: 'Empty state for previous requests list.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsGuestRoomErrorUnavailable: {
    text: 'Guest requests are not available for this room.',
    description: 'Error when room token is invalid or guest requests disabled.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsGuestRoomErrorRateLimit: {
    text: 'Please wait before submitting another request.',
    description: 'Error when rate limited.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsGuestRoomErrorEmail: {
    text: 'Please do not include email addresses in your note.',
    description: 'Validation error when note has email.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsGuestRoomErrorDigits: {
    text: 'Please do not include phone or card numbers in your note.',
    description: 'Validation error when note has 8+ digits.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsGuestRoomErrorTooLong: {
    text: 'Note must be 500 characters or less.',
    description: 'Validation error when note is too long.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsGuestRoomErrorGeneric: {
    text: 'Failed to submit request. Please try again.',
    description: 'Generic submission error.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsGuestStatusTitle: {
    text: 'Request Status',
    description: 'Title for guest request status page.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusSubtitle: {
    text: 'Real-time updates for your room request',
    description: 'Subtitle for guest request status page.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsGuestStatusBucketReceived: {
    text: 'Request Received',
    description: 'Bucket status text for RECEIVED.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusBucketOnIt: {
    text: 'Team is on it',
    description: 'Bucket status text for ON_IT.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusBucketDone: {
    text: 'Completed',
    description: 'Bucket status text for DONE.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusBucketClosed: {
    text: 'Closed',
    description: 'Bucket status text for CLOSED.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusCreatedAt: {
    text: 'Requested at',
    description: 'Label for request creation timestamp.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusAckAt: {
    text: 'Acknowledged at',
    description: 'Label for request acknowledgment timestamp.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusDoneAt: {
    text: 'Completed at',
    description: 'Label for request completion timestamp.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusStillProblemPrompt: {
    text: 'Is there still an issue with this request?',
    description: 'Prompt asking if the problem persists.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsGuestStatusStillProblemAction: {
    text: 'Still a problem?',
    description: 'Button text to open still a problem complaint form.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusStillProblemNoteLabel: {
    text: 'Please describe what is still wrong',
    description: 'Label for still a problem note.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsGuestStatusStillProblemSubmit: {
    text: 'Submit Follow-Up',
    description: 'Button text to submit still a problem.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestStatusStillProblemSuccess: {
    text: 'Follow-up complaint registered. Our team has been alerted.',
    description: 'Success message after still a problem submitted.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsGuestStatusNotFound: {
    text: 'Request status not found or expired.',
    description: 'Error when guest request status token not found or purged.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsGuestStatusBackToRoom: {
    text: 'Back to Room Requests',
    description: 'Button or link to return to guest room page.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsGuestBadgeLabel: {
    text: 'Guest · room QR',
    description: 'Badge on requests screen for guest QR raised requests.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsGuestNoteLabel: {
    text: 'Guest note',
    description: 'Label for guest note in request details.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPutRoomOutOfOrder: {
    text: 'Put room out of order',
    description: 'Action button on request detail for open fault to put room out of order.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSettingGuestRequestsTitle: {
    text: 'Guest requests by room QR',
    description: 'Settings switch label for enabling guest requests by room QR.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSettingGuestRequestsDesc: {
    text: 'Allow guests to scan room QR codes to request towels, water, cleaning, or report issues.',
    description: 'Settings switch description.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsTabRecords: {
    text: 'Records',
    description: 'Registers screen sub-tab label for Records.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabReviewQueue: {
    text: 'Review queue',
    description: 'Registers screen sub-tab label for Review queue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTabSectorRoles: {
    text: 'Sector roles',
    description: 'Registers screen sub-tab label for Sector roles.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsPhotoInvalidType: {
    text: 'Only JPEG, PNG, WebP, and HEIC images are allowed.',
    description: 'Error message when photo MIME type is not allowed.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPhotoTooLarge: {
    text: 'Photo file size must not exceed 10 MB.',
    description: 'Error message when photo size exceeds 10 MB.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsPhotoUploadPrompt: {
    text: 'Upload photo',
    description: 'Label or button to upload photo for a record.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPhotosCount: {
    text: '{count} photo(s)',
    description: 'Count of photos uploaded.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsNewTemplateBtn: {
    text: '+ Template',
    description: 'Button to create a new register template.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTemplateName: {
    text: 'Template name',
    description: 'Input label for template name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsTemplateKind: {
    text: 'Template kind',
    description: 'Input label for template kind.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsLateAfterMinutes: {
    text: 'Late after (minutes)',
    description: 'Input label for late threshold in minutes.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSafetyTagged: {
    text: 'Safety-tagged',
    description: 'Checkbox/toggle label for safety-tagged template.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPatientLinked: {
    text: 'Patient-linked',
    description: 'Checkbox/toggle label for patient-linked template.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAddField: {
    text: '+ Add field',
    description: 'Button to add field to template builder.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsFieldKey: {
    text: 'Field key',
    description: 'Input label for template field key.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsFieldLabel: {
    text: 'Field label',
    description: 'Input label for template field display label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsFieldType: {
    text: 'Field type',
    description: 'Select label for template field type.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsFieldRequired: {
    text: 'Required',
    description: 'Toggle label for required template field.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldMin: {
    text: 'Min',
    description: 'Input label for field minimum value.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldMax: {
    text: 'Max',
    description: 'Input label for field maximum value.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldUnit: {
    text: 'Unit',
    description: 'Input label for field measurement unit.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldOptions: {
    text: 'Options (comma-separated)',
    description: 'Input label for choice field options.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsFieldExceptionOn: {
    text: 'Exception on',
    description: 'Select label for YES_NO exception condition.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSaveDraft: {
    text: 'Save draft',
    description: 'Button to save template draft.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSubmitForApproval: {
    text: 'Submit for approval',
    description: 'Button to submit template version for approval.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsApprove: {
    text: 'Approve',
    description: 'Button to approve template version or record.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsVersionHistory: {
    text: 'Version history',
    description: 'Heading for template version history list.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSelfApprovedBadge: {
    text: 'Self-approved',
    description: 'Badge indicating item was self-approved.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSelfApprovalTitle: {
    text: 'Self-approval reason',
    description: 'Title for self-approval dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSelfApprovalDesc: {
    text: 'You are the author and sole approver in this workspace. Please provide a reason for self-approving (3 to 500 characters).',
    description: 'Description prompt for self-approval reason.',
    surface: 'shell',
    maxChars: 150,
  },
  NLSOpsSelfApprovalConfirm: {
    text: 'Confirm approval',
    description: 'Button to submit self-approval with reason.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSelfApprovalReasonPlaceholder: {
    text: 'Enter self-approval justification...',
    description: 'Placeholder for self-approval textarea.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsCharCount: {
    text: '{count}/500',
    description: 'Character counter for text input.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsNewRecord: {
    text: '+ New record',
    description: 'Button to fill a new register record.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSelectTemplate: {
    text: 'Template',
    description: 'Label to select template for record.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSelectPlace: {
    text: 'Place',
    description: 'Label to select place for record.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsOccurredAt: {
    text: 'Occurred at',
    description: 'Input label for record occurredAt time.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsResubmitRecord: {
    text: 'Resubmit',
    description: 'Button to resubmit a returned record.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSubmitRecord: {
    text: 'Submit record',
    description: 'Button to submit record.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsReturnedReasonTitle: {
    text: 'Return reason from reviewer:',
    description: 'Label for return reason display on returned record.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsLateBadge: {
    text: 'Late by {minutes}m',
    description: 'Badge showing record was submitted late.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsLate: {
    text: 'Late',
    description: 'Short badge for late record.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsLockedBadge: {
    text: 'Locked',
    description: 'Badge showing record is locked and immutable.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRecordTimeline: {
    text: 'Timeline',
    description: 'Heading for record event timeline.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCorrectionTrail: {
    text: 'Corrections',
    description: 'Heading for record corrections history.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAddCorrection: {
    text: 'Add correction',
    description: 'Button to open add correction dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCorrectionReason: {
    text: 'Reason for correction',
    description: 'Input label for correction reason.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSubmitCorrection: {
    text: 'Submit correction',
    description: 'Button to submit a correction.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsReviewQueueTitle: {
    text: 'Review queue',
    description: 'Heading for register review queue.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCannotReviewOwnRecord: {
    text: 'Cannot review or approve own record',
    description: 'Notice that authors cannot review/approve their own records.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsActionReview: {
    text: 'Review',
    description: 'Action button to mark record as reviewed.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsActionReturn: {
    text: 'Return',
    description: 'Action button to return record to author.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsActionApprove: {
    text: 'Approve',
    description: 'Action button to approve record.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsReturnDialogTitle: {
    text: 'Return record to author',
    description: 'Title for return record dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsReturnDialogPrompt: {
    text: 'Enter reason for returning this record to author:',
    description: 'Prompt text for return record reason.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSectorRolesTitle: {
    text: 'Sector roles',
    description: 'Heading for sector roles card.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSectorRolesDesc: {
    text: 'Assign operational approval and review permissions to workspace members.',
    description: 'Description for sector roles card.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsRoleTemplateApprover: {
    text: 'Template Approver',
    description: 'Sector role name for template approver.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleRecordReviewer: {
    text: 'Record Reviewer',
    description: 'Sector role name for record reviewer.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleRecordApprover: {
    text: 'Record Approver',
    description: 'Sector role name for record approver.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleRosterManager: {
    text: 'Roster Manager',
    description: 'Sector role name for roster manager.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleAttendanceMarker: {
    text: 'Attendance Marker',
    description: 'Sector role name for attendance marker.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleIncidentHandler: {
    text: 'Incident Handler',
    description: 'Sector role name for incident handler.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleRequestVerifier: {
    text: 'Request Verifier',
    description: 'Sector role name for request verifier.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleComplianceManager: {
    text: 'Compliance Manager',
    description: 'Sector role name for compliance manager.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleAssetReleaser: {
    text: 'Asset Releaser',
    description: 'Sector role name for asset releaser.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleMaintenance: {
    text: 'Maintenance Technician',
    description: 'Sector role name for plant maintenance technician.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRoleEvidenceExporter: {
    text: 'Evidence Exporter',
    description: 'Sector role name for evidence exporter.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleBookingDesk: {
    text: 'Booking Desk',
    description: 'Sector role name for booking desk.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleJobDispatcher: {
    text: 'Job Dispatcher',
    description: 'Sector role name for job dispatcher.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleGroupManager: {
    text: 'Group Manager',
    description: 'Sector role name for stays group manager.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleQualityReleaser: {
    text: 'Quality Releaser',
    description: 'Sector role name for plant quality releaser.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceCategoryLicence: {
    text: 'Licence',
    description: 'Compliance item category Licence.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceCategoryCertificate: {
    text: 'Certificate',
    description: 'Compliance item category Certificate.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceCategoryRegistration: {
    text: 'Registration',
    description: 'Compliance item category Registration.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceCategoryInspection: {
    text: 'Inspection',
    description: 'Compliance item category Inspection.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceCategoryInsurance: {
    text: 'Insurance',
    description: 'Compliance item category Insurance.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceCategoryOther: {
    text: 'Other',
    description: 'Compliance item category Other.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceStatusValid: {
    text: 'Valid',
    description: 'Compliance item status Valid.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceStatusDueSoon: {
    text: 'Due soon',
    description: 'Compliance item status Due soon.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceStatusExpired: {
    text: 'Expired',
    description: 'Compliance item status Expired.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceStatusArchived: {
    text: 'Archived',
    description: 'Compliance item status Archived.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceTitle: {
    text: 'Compliance calendar',
    description: 'Title for compliance calendar screen.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsComplianceSubtitle: {
    text: 'Licences · certificates · registrations · owner + backup · reminders',
    description: 'Subtitle for compliance calendar screen.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsComplianceExpiringSoon: {
    text: 'Expiring soon',
    description: 'Header for expiring soon compliance items strip.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceListTab: {
    text: 'List',
    description: 'Tab label for compliance list view.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceCalendarTab: {
    text: 'Calendar',
    description: 'Tab label for compliance calendar view.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceAddItem: {
    text: 'Add item',
    description: 'Button to create a new compliance item.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceEditItem: {
    text: 'Edit item',
    description: 'Button to edit an existing compliance item.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceRenewItem: {
    text: 'Renew',
    description: 'Button to renew a compliance item.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceAddEvidence: {
    text: 'Add evidence',
    description: 'Button to add evidence to a compliance item.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceArchiveItem: {
    text: 'Archive',
    description: 'Button to archive a compliance item.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceDisclaimer: {
    text: 'Items are your workspace records, not legal advice.',
    description: 'Compliance disclaimer text per SO-D19.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsComplianceColItem: {
    text: 'Item',
    description: 'Column header for item title.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceColAuthorityRef: {
    text: 'Authority / ref',
    description: 'Column header for authority and reference.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceColPlace: {
    text: 'Place',
    description: 'Column header for place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceColValidTo: {
    text: 'Valid to',
    description: 'Column header for valid to date.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceColOwner: {
    text: 'Owner',
    description: 'Column header for owner.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceColBackup: {
    text: 'Backup',
    description: 'Column header for backup.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceColEvidence: {
    text: 'Evidence',
    description: 'Column header for evidence.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceColStatus: {
    text: 'Status',
    description: 'Column header for status.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceExpiresInDays: {
    text: 'Expires in {days} days',
    description: 'Pill text for item expiring in days.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsComplianceExpiresToday: {
    text: 'Expires today',
    description: 'Pill text for item expiring today.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsComplianceExpiredDaysAgo: {
    text: 'Expired {days} days ago',
    description: 'Pill text for item expired days ago.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsComplianceWholeWorkspace: {
    text: 'Whole workspace',
    description: 'Place label for whole workspace compliance items.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceFilterAll: {
    text: 'All',
    description: 'Filter option for all items.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceFilterOwnerMe: {
    text: 'Owned by me',
    description: 'Filter option for items owned by current user.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceHistoryTitle: {
    text: 'History',
    description: 'Title for compliance item history section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceEvidenceTitle: {
    text: 'Evidence files',
    description: 'Title for compliance item evidence section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceNoEvidence: {
    text: 'No evidence files uploaded yet.',
    description: 'Message when no evidence files are attached.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsComplianceEvidenceInvalidType: {
    text: 'Invalid evidence file type. Allowed: PDF, JPEG, PNG, WebP, HEIC.',
    description: 'Error message when uploaded evidence file type is not permitted.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsComplianceEvidenceTooLarge: {
    text: 'Evidence file is too large. Maximum size is 10 MB.',
    description: 'Error message when uploaded evidence file exceeds size limit.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsComplianceEmptyList: {
    text: 'No compliance items found.',
    description: 'Message when compliance list is empty.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsComplianceFieldTitle: {
    text: 'Title (1–120 chars)',
    description: 'Form field label for item title.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceFieldCategory: {
    text: 'Category',
    description: 'Form field label for item category.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceFieldAuthority: {
    text: 'Authority (0–120 chars)',
    description: 'Form field label for issuing authority.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceFieldReference: {
    text: 'Reference (0–80 chars)',
    description: 'Form field label for reference number.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceFieldPlace: {
    text: 'Place (optional)',
    description: 'Form field label for place.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceFieldValidFrom: {
    text: 'Valid from (YYYY-MM-DD)',
    description: 'Form field label for valid from date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceFieldValidTo: {
    text: 'Valid to (YYYY-MM-DD)',
    description: 'Form field label for valid to date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceFieldOwner: {
    text: 'Owner',
    description: 'Form field label for item owner.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceFieldBackup: {
    text: 'Backup (optional)',
    description: 'Form field label for item backup owner.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceFieldNotes: {
    text: 'Notes (0–2000 chars)',
    description: 'Form field label for item notes.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceFieldReminders: {
    text: 'Reminders',
    description: 'Label for reminder days.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceBtnCancel: {
    text: 'Cancel',
    description: 'Button text to cancel dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsComplianceBtnCreate: {
    text: 'Create item',
    description: 'Button text to create compliance item.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceBtnUpdate: {
    text: 'Update item',
    description: 'Button text to update compliance item.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceBtnRenew: {
    text: 'Renew item',
    description: 'Button text to renew compliance item.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceBtnArchive: {
    text: 'Confirm archive',
    description: 'Button text to archive compliance item.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceBtnOpen: {
    text: 'Open ↗',
    description: 'Button text to open evidence file.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsComplianceArchiveReasonLabel: {
    text: 'Archive reason (3–500 chars)',
    description: 'Label for archive reason input.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsComplianceArchiveReasonPlaceholder: {
    text: 'State why this compliance item is being archived...',
    description: 'Placeholder for archive reason input.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsComplianceCurrentValidTo: {
    text: 'Current valid to',
    description: 'Label for current valid to date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsComplianceNewValidTo: {
    text: 'New valid to (must be after current)',
    description: 'Label for new valid to date in renew dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsComplianceNewValidFrom: {
    text: 'New valid from (optional)',
    description: 'Label for new valid from date in renew dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceNewReference: {
    text: 'New reference no. (optional)',
    description: 'Label for new reference number in renew dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsComplianceUploading: {
    text: 'Uploading evidence...',
    description: 'Message while evidence file is uploading.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceSaving: {
    text: 'Saving...',
    description: 'Button text while saving item.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsComplianceRenewing: {
    text: 'Renewing...',
    description: 'Button text while renewing item.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsComplianceArchiving: {
    text: 'Archiving...',
    description: 'Button text while archiving item.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsComplianceNone: {
    text: 'None',
    description: 'Label for none option.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsComplianceTitlePlaceholder: {
    text: 'e.g. Fire Safety Certificate',
    description: 'Placeholder for title input.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsComplianceAuthorityPlaceholder: {
    text: 'e.g. Municipal Corporation',
    description: 'Placeholder for authority input.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsComplianceReferencePlaceholder: {
    text: 'e.g. MC-2026-99',
    description: 'Placeholder for reference input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsComplianceAriaFilterStatus: {
    text: 'Filter status',
    description: 'Aria label for status filter select.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceAriaFilterCategory: {
    text: 'Filter category',
    description: 'Aria label for category filter select.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsComplianceAriaFilterPlace: {
    text: 'Filter place',
    description: 'Aria label for place filter select.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestKindSnag: {
    text: 'Snag',
    description: 'Request kind label for Snag.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindFault: {
    text: 'Fault',
    description: 'Request kind label for Fault.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindHindrance: {
    text: 'Hindrance',
    description: 'Request kind label for Hindrance.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindRfi: {
    text: 'RFI',
    description: 'Request kind label for Request for Information.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindSelection: {
    text: 'Selection',
    description: 'Request kind label for Selection.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindChangeRequest: {
    text: 'Change Request',
    description: 'Request kind label for Change Request.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindSupplyRequest: {
    text: 'Supply Request',
    description: 'Request kind label for Supply Request.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindIncident: {
    text: 'Incident',
    description: 'Request kind label for Incident.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindHousekeeping: {
    text: 'Housekeeping',
    description: 'Request kind label for Housekeeping.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindDischargeClean: {
    text: 'Discharge Clean',
    description: 'Request kind label for Discharge Clean.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindSpill: {
    text: 'Spill',
    description: 'Request kind label for Spill.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindReplenishment: {
    text: 'Replenishment',
    description: 'Request kind label for Replenishment.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindComplaint: {
    text: 'Complaint',
    description: 'Request kind label for Complaint.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindReliefNeeded: {
    text: 'Relief Needed',
    description: 'Request kind label for Relief Needed.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindClientComplaint: {
    text: 'Client Complaint',
    description: 'Request kind label for Client Complaint.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindPm: {
    text: 'Preventive maintenance',
    description: 'Built-in request kind for preventive maintenance.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestKindCalibration: {
    text: 'Calibration',
    description: 'Built-in request kind for equipment calibration.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindJob: {
    text: 'Job',
    description: 'Built-in request kind for Jobs sector.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindRevisit: {
    text: 'Revisit',
    description: 'Built-in request kind for Jobs revisit.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindQuoteRequest: {
    text: 'Quote Request',
    description: 'Built-in request kind for Jobs quote request.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindDrawingAck: {
    text: 'Drawing revised: acknowledge',
    description: 'Built-in request kind for acknowledging a revised drawing.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRequestKindInspection: {
    text: 'Inspection',
    description: 'Built-in request kind for quality inspection hold point.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindNcr: {
    text: 'Non-conformance report',
    description: 'Built-in request kind for non-conformance report (NCR).',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestKindBreakdown: {
    text: 'Machine Breakdown',
    description: 'Built-in request kind for plant machine breakdown.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindQualityHold: {
    text: 'Quality Hold',
    description: 'Built-in request kind for plant quality hold and release.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindNearMiss: {
    text: 'Near Miss',
    description: 'Built-in request kind for plant near-miss safety reporting.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestKindColdChainExcursion: {
    text: 'Cold Chain Excursion',
    description: 'Built-in request kind for cold chain temperature excursion.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestKindSampleRedraw: {
    text: 'Sample Redraw Request',
    description: 'Built-in request kind raised when a lab rejects a sample.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestKindSampleStageBreach: {
    text: 'Sample Stage SLA Breach',
    description: 'Built-in request kind raised when sample stage time limit is exceeded.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsRequestKindHomeCollection: {
    text: 'Home Sample Collection',
    description: 'Built-in request kind for home sample collection job.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsRequestKindNarcoticMismatch: {
    text: 'Narcotic Count Mismatch',
    description: 'Built-in request kind raised when narcotic count tallies differ from expected balance.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsRequestKindWaterQuality: {
    text: 'Water Quality Issue',
    description: 'Built-in request kind for RO water quality failure.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConTabInspections: {
    text: 'Inspections',
    description: 'Tab for construction hold point inspections.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConTabPourCards: {
    text: 'Pour cards',
    description: 'Tab for pour and pre-concealment cards.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConTabTests: {
    text: 'Tests',
    description: 'Tab for construction test samples and results.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConTabNcrs: {
    text: 'NCRs',
    description: 'Tab for non-conformance reports.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConTabSnags: {
    text: 'Snags',
    description: 'Tab for construction snags.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConNewPourCard: {
    text: 'New pour card',
    description: 'Button to create a new pour card.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConNewTestSample: {
    text: 'New test sample',
    description: 'Button to create a new test sample.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConRecordOutcome: {
    text: 'Record outcome',
    description: 'Button for assigned inspector to record inspection outcome.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConAddResult: {
    text: 'Add result',
    description: 'Button to add test result to sample.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConRequestInspection: {
    text: 'Request inspection',
    description: 'Button to request inspection hold point.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConInspectionDisabledReason: {
    text: 'All checklist items must be OK or NA before requesting inspection.',
    description: 'Tooltip or reason when inspection request is disabled.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConInspectionRequestCloseWarning: {
    text: 'The inspection request could not be closed; close it from Requests.',
    description: 'Warning on inspection card when request closing failed.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConReassignInspector: {
    text: 'Reassign inspector',
    description: 'Button to reassign inspector on a pour card.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConOverdueBadge: {
    text: 'Overdue',
    description: 'Badge indicating test sample is overdue.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConSelectProject: {
    text: 'Select project',
    description: 'Dropdown placeholder for selecting construction project.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConSnagsOpen: {
    text: 'Open Snags',
    description: 'Header for open snags section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConSnagsDone: {
    text: 'Done (Awaiting Verification)',
    description: 'Header for done snags awaiting verification.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConSnagsVerified: {
    text: 'Verified Closed',
    description: 'Header for verified closed snags.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesTitle: {
    text: 'Issues & Approvals',
    description: 'Title for construction issues screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesSubtitle: {
    text: 'Track RFIs, client selections, change requests, and site hindrances.',
    description: 'Subtitle for construction issues screen.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConIssuesRaiseButton: {
    text: 'Raise issue',
    description: 'Button to raise a new construction issue.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesEmptyTitle: {
    text: 'No issues found',
    description: 'Empty state title when no issues match filter.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConIssuesEmptyDesc: {
    text: 'No issues match the selected project and filter criteria.',
    description: 'Empty state description when no issues match filter.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConIssuesFilterKindAll: {
    text: 'All kinds',
    description: 'Filter option for all issue kinds.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFilterStateAll: {
    text: 'All states',
    description: 'Filter option for all issue states.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFilterOverdueOnly: {
    text: 'Overdue only',
    description: 'Toggle button or filter for overdue issues.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesColRegisterNo: {
    text: 'Register No.',
    description: 'Table column header for issue register number.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesColTitle: {
    text: 'Title',
    description: 'Table column header for issue title.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesColKind: {
    text: 'Kind',
    description: 'Table column header for issue kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesColResponsible: {
    text: 'Responsible party',
    description: 'Table column header for responsible party.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesColDue: {
    text: 'Due',
    description: 'Table column header for issue due date.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesColState: {
    text: 'State',
    description: 'Table column header for issue state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesColDecisionVer: {
    text: 'Decision',
    description: 'Table column header for issue decision version.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesColActions: {
    text: 'Actions',
    description: 'Table column header for actions.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesViewDetail: {
    text: 'View detail',
    description: 'Button or link to open issue details.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesDetailTitle: {
    text: 'Issue details',
    description: 'Header title for issue detail view.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesDetailDesc: {
    text: 'Description',
    description: 'Section header for issue description.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesDetailPlace: {
    text: 'Place',
    description: 'Label for issue place.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesDetailDrawing: {
    text: 'Drawing reference',
    description: 'Label for drawing reference link in issue detail.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesDetailImpact: {
    text: 'Impact assessment',
    description: 'Section header for issue impact.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesDetailTimeImpact: {
    text: 'Time impact',
    description: 'Label for time impact.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesDetailCostImpact: {
    text: 'Cost impact',
    description: 'Label for cost impact.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesDetailPhotos: {
    text: 'Photos',
    description: 'Section header for issue photos.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesDetailOptions: {
    text: 'Selection options',
    description: 'Section header for selection options.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesDetailApprovalStatus: {
    text: 'Client approval status',
    description: 'Label for outside client approval status.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesDetailSendApproval: {
    text: 'Send for client approval',
    description: 'Button to send issue for outside client approval.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesDetailTimeline: {
    text: 'Responses timeline',
    description: 'Section header for responses timeline.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesDetailNoResponses: {
    text: 'No responses recorded yet.',
    description: 'Empty state message when issue has no responses.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConIssuesDetailDecisions: {
    text: 'Decision history',
    description: 'Section header for decision history.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesDetailNoDecisions: {
    text: 'No decisions recorded yet.',
    description: 'Empty state message when issue has no decisions.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConIssuesDetailSuperseded: {
    text: 'Superseded',
    description: 'Badge for superseded decision version.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesDetailViaApproval: {
    text: 'Via client approval',
    description: 'Badge indicating decision was made via outside client approval.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesDetailEdits: {
    text: 'Audit edits log',
    description: 'Section header for issue edits log.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesDetailNoEdits: {
    text: 'No edits recorded.',
    description: 'Empty state message for edits log.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesActionRespond: {
    text: 'Respond',
    description: 'Button label to respond to an issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesActionDecide: {
    text: 'Decide',
    description: 'Button label to decide an issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesActionWithdraw: {
    text: 'Withdraw',
    description: 'Button label to withdraw an issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesActionClose: {
    text: 'Close',
    description: 'Button label to close an issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesActionEdit: {
    text: 'Edit issue',
    description: 'Button label to edit an issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFormKind: {
    text: 'Issue kind',
    description: 'Form label for issue kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFormTitle: {
    text: 'Title',
    description: 'Form label for issue title.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFormDescription: {
    text: 'Description',
    description: 'Form label for issue description.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFormPlace: {
    text: 'Place',
    description: 'Form label for issue place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFormResponsibleType: {
    text: 'Responsible party type',
    description: 'Form label for responsible party type.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesFormResponsibleName: {
    text: 'Responsible party name',
    description: 'Form label for responsible party name.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesFormResponsibleUid: {
    text: 'Internal team member',
    description: 'Form label for internal user uid.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesFormDrawing: {
    text: 'Drawing',
    description: 'Form label for drawing selector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFormFirstNoticed: {
    text: 'First noticed',
    description: 'Form label for first noticed timestamp.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesFormDue: {
    text: 'Response due date',
    description: 'Form label for response due timestamp.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesFormImpactNote: {
    text: 'Impact notes',
    description: 'Form label for impact notes.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesFormTimeImpact: {
    text: 'Time impact',
    description: 'Form label for time impact dropdown.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFormCostImpact: {
    text: 'Cost impact',
    description: 'Form label for cost impact dropdown.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFormAddOption: {
    text: 'Add option',
    description: 'Button to add selection option.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesFormOptionLabel: {
    text: 'Option label',
    description: 'Input placeholder for option label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesFormOptionNote: {
    text: 'Option note (optional)',
    description: 'Input placeholder for option note.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesFormRemoveOption: {
    text: 'Remove',
    description: 'Button to remove an option.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesRespondText: {
    text: 'Response text',
    description: 'Label for response text input.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesDecideText: {
    text: 'Decision instruction',
    description: 'Label for decision text input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesDecideChooseOption: {
    text: 'Select chosen option',
    description: 'Label for chosen option dropdown in selection decision.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesDecideRevisedInstruction: {
    text: 'Revised instruction (optional)',
    description: 'Label for revised instruction in decision dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConIssuesDecideSelfReason: {
    text: 'Self-decision justification (sole approver)',
    description: 'Label for self-decision reason input.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConIssuesDecideSupersedeReason: {
    text: 'Reason for superseding prior decision',
    description: 'Label for supersede reason input.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConIssuesWithdrawReason: {
    text: 'Withdrawal reason',
    description: 'Label for issue withdrawal reason input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesCloseConfirm: {
    text: 'Close issue',
    description: 'Dialog title for closing an issue.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesCloseConfirmText: {
    text: 'Are you sure you want to close this issue? The linked request will be completed.',
    description: 'Confirmation message for closing an issue.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsConIssuesOverdueBadge: {
    text: 'Overdue',
    description: 'Badge for overdue issue.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesRetry: {
    text: 'Retry',
    description: 'Button to retry loading issues.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesTableCaption: {
    text: 'Construction Issues and Approvals register',
    description: 'Screen reader caption for construction issues table.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConIssuesDetailImpactNote: {
    text: 'Impact note: ',
    description: 'Label for impact note in issue detail view.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesApprovalStateBanner: {
    text: 'Approval: {state}',
    description: 'Badge showing outside client approval state.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesErrorTitleLength: {
    text: 'Title must be at least 3 characters.',
    description: 'Validation error when issue title is too short.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConIssuesErrorDescRequired: {
    text: 'Description is required.',
    description: 'Validation error when issue description is missing.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsConIssuesErrorPlaceRequired: {
    text: 'Place is required.',
    description: 'Validation error when issue place is missing.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesErrorSelectionOptions: {
    text: 'SELECTION kind requires at least 2 named options.',
    description: 'Validation error when selection options are insufficient.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConIssuesFormTitlePlaceholder: {
    text: 'Enter brief title (3-120 chars)',
    description: 'Placeholder for issue title input.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsConIssuesFormDescPlaceholder: {
    text: 'Describe issue or query in detail',
    description: 'Placeholder for issue description textarea.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConIssuesFormRespNamePlaceholder: {
    text: 'Person or firm name',
    description: 'Placeholder for responsible party name input.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsConIssuesFormRespMemberPlaceholder: {
    text: 'Select assigned member',
    description: 'Placeholder for internal responsible member select.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsConIssuesFormImpactNotePlaceholder: {
    text: 'Notes on schedule or cost impact',
    description: 'Placeholder for impact note input.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsConIssuesFormOptionLabelPlaceholder: {
    text: 'Option {index} label',
    description: 'Placeholder for selection option label input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesRespondTextPlaceholder: {
    text: 'Provide answer, clarification, or recommendation (1-4000 characters)',
    description: 'Placeholder for response text textarea.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConIssuesDecideTextPlaceholder: {
    text: 'Formal decision outcome and technical instruction',
    description: 'Placeholder for decision text textarea.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConIssuesDecideRevisedPlaceholder: {
    text: 'Optional revised direction or follow-up note',
    description: 'Placeholder for revised instruction textarea.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsConIssuesDecideSupersedePlaceholder: {
    text: 'Reason why prior decision is being superseded (3-500 chars)',
    description: 'Placeholder for supersede reason input.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsConIssuesDecideSelfReasonPlaceholder: {
    text: 'Justification for self-decision if sole project approver',
    description: 'Placeholder for self-decision reason input.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsConIssuesWithdrawReasonPlaceholder: {
    text: 'Reason for withdrawing issue (3-500 characters)',
    description: 'Placeholder for withdraw reason input.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConIssueStateOpen: {
    text: 'Open',
    description: 'Issue state Open.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssueStateResponded: {
    text: 'Responded',
    description: 'Issue state Responded.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssueStateDecided: {
    text: 'Decided',
    description: 'Issue state Decided.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssueStateClosed: {
    text: 'Closed',
    description: 'Issue state Closed.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssueStateWithdrawn: {
    text: 'Withdrawn',
    description: 'Issue state Withdrawn.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesApprovalStateSent: {
    text: 'Sent',
    description: 'Approval state Sent.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesApprovalStateApproved: {
    text: 'Approved',
    description: 'Approval state Approved.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesApprovalStateRejected: {
    text: 'Rejected',
    description: 'Approval state Rejected.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesApprovalStateRevoked: {
    text: 'Revoked',
    description: 'Approval state Revoked.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesApprovalStateExpired: {
    text: 'Expired',
    description: 'Approval state Expired.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesApprovalStateSending: {
    text: 'Sending...',
    description: 'Approval state Sending.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssueDecidedViaInternal: {
    text: 'Internal',
    description: 'Decided via internal decision.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssueDecidedViaOutsideApproval: {
    text: 'Outside client approval',
    description: 'Decided via outside client approval.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConResponsibleTypeInternal: {
    text: 'Internal Team',
    description: 'Responsible party type Internal Team.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConResponsibleTypeSubcontractor: {
    text: 'Subcontractor',
    description: 'Responsible party type Subcontractor.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConResponsibleTypeClient: {
    text: 'Client / Owner',
    description: 'Responsible party type Client or Owner.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConResponsibleTypeConsultant: {
    text: 'Consultant / Architect',
    description: 'Responsible party type Consultant or Architect.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesDetailDecidedBy: {
    text: 'Decided by {name}',
    description: 'Decided by user name in decision item.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConIssuesDetailResponseBy: {
    text: 'Response #{index} by {name}',
    description: 'Response number and author in timeline.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConIssuesDetailEditBy: {
    text: '{date} - Edited by {name}: ',
    description: 'Edit entry author and timestamp.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConIssuesFormResponsibleMember: {
    text: 'Assigned team member',
    description: 'Label for assigned internal team member select.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConIssuesOptionChosen: {
    text: 'Chosen',
    description: 'Badge for chosen selection option.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesVersionLabel: {
    text: 'Version {version}',
    description: 'Label for decision version.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesChosenOptionLabel: {
    text: 'Chosen option: {label}',
    description: 'Label for chosen option in decision.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConIssuesSupersedeReasonLabel: {
    text: 'Supersede reason: {reason}',
    description: 'Label for supersede reason in decision history.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConIssuesSelfReasonLabel: {
    text: 'Self-decision reason: {reason}',
    description: 'Label for self-decision reason in decision history.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConIssuesActionSave: {
    text: 'Save Changes',
    description: 'Button to save edits to an issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesActionSubmitResponse: {
    text: 'Submit Response',
    description: 'Button to submit response to an issue.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesActionRecordDecision: {
    text: 'Record Decision',
    description: 'Button to record decision for an issue.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesActionConfirmWithdraw: {
    text: 'Confirm Withdraw',
    description: 'Button to confirm withdrawal of an issue.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesCancel: {
    text: 'Cancel',
    description: 'Button to cancel a dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesDialogClose: {
    text: 'Close',
    description: 'Button to close issue detail dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesSaving: {
    text: 'Saving...',
    description: 'Status while saving issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesSubmitting: {
    text: 'Submitting...',
    description: 'Status while submitting response.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesRecording: {
    text: 'Recording...',
    description: 'Status while recording decision.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesWithdrawing: {
    text: 'Withdrawing...',
    description: 'Status while withdrawing issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesClosing: {
    text: 'Closing...',
    description: 'Status while closing issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesPhotos: {
    text: 'Photos',
    description: 'Header for photos section in construction issue.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConIssuesAddPhotos: {
    text: 'Add photos',
    description: 'Label to select photos to upload for construction issue.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesPhotosFailed: {
    text: '{count} photo(s) failed to attach',
    description: 'Banner when some photos failed to upload or attach.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConIssuesPhotosRetry: {
    text: 'Retry photos',
    description: 'Button to retry attaching failed photos.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssuesRemovePhoto: {
    text: 'Remove',
    description: 'Button to remove a photo from an issue.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConIssuesPhotoThumbnailAlt: {
    text: 'Construction issue photo',
    description: 'Alt text for construction issue photo thumbnail.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSMobileSyncKindConIssueAttachPhotos: {
    text: 'Hindrance photos',
    description: 'Breakdown row and title for hindrance photo attachment sync operation.',
    surface: 'helper',
    maxChars: 25,
  },
  NLSOpsConImpactLevelNone: {
    text: 'None',
    description: 'Impact level none.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConImpactLevelPossible: {
    text: 'Possible',
    description: 'Impact level possible.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConImpactLevelLikely: {
    text: 'Likely',
    description: 'Impact level likely.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConImpactLevelConfirmed: {
    text: 'Confirmed',
    description: 'Impact level confirmed.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsAssetStatusAvailable: {
    text: 'Available',
    description: 'Asset status indicating equipment is available for use.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetStatusInUse: {
    text: 'In use',
    description: 'Asset status indicating equipment is currently in use.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetStatusIsolated: {
    text: 'Isolated',
    description: 'Asset status indicating equipment is isolated due to fault or hazard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetStatusMaintenance: {
    text: 'Maintenance',
    description: 'Asset status indicating equipment is under maintenance.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetStatusRetired: {
    text: 'Retired',
    description: 'Asset status indicating equipment is permanently retired.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetCriticalityLow: {
    text: 'Low',
    description: 'Asset criticality rating low.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsAssetCriticalityMedium: {
    text: 'Medium',
    description: 'Asset criticality rating medium.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsAssetCriticalityHigh: {
    text: 'High',
    description: 'Asset criticality rating high.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsAssetCriticalityCritical: {
    text: 'Critical',
    description: 'Asset criticality rating critical.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsAssetHistoryCreated: {
    text: 'Created',
    description: 'Asset history entry when created.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetHistoryUpdated: {
    text: 'Updated',
    description: 'Asset history entry when details updated.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetHistoryStatusChanged: {
    text: 'Status changed',
    description: 'Asset history entry when status changed.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetHistoryIsolated: {
    text: 'Isolated',
    description: 'Asset history entry when equipment is isolated.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetHistoryReleased: {
    text: 'Released to use',
    description: 'Asset history entry when equipment is released to use.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetHistoryRetired: {
    text: 'Retired',
    description: 'Asset history entry when equipment is retired.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetHistoryPmRaised: {
    text: 'PM request raised',
    description: 'Asset history entry when PM request is raised.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetHistoryCalibrationRaised: {
    text: 'Calibration request raised',
    description: 'Asset history entry when calibration request is raised.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetHistoryPmDone: {
    text: 'PM completed',
    description: 'Asset history entry when PM is marked done.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetHistoryCalibrationDone: {
    text: 'Calibration verified',
    description: 'Asset history entry when calibration is verified.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetHistoryAmcReminder: {
    text: 'AMC reminder',
    description: 'Asset history entry when AMC reminder triggers.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetHistoryWarrantyReminder: {
    text: 'Warranty reminder',
    description: 'Asset history entry when warranty reminder triggers.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsTitle: {
    text: 'Assets & Equipment',
    description: 'Title for Assets screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsSubtitle: {
    text: 'Register · work orders · PM / calibration / AMC due · release-to-use',
    description: 'Subtitle for Assets screen.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsAssetsAddAsset: {
    text: 'Asset',
    description: 'Button to add a new asset.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsEditAsset: {
    text: 'Edit asset',
    description: 'Button/action to edit an asset.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsIsolateAsset: {
    text: 'Isolate asset',
    description: 'Button/action to isolate an asset.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsReleaseAsset: {
    text: 'Release to use',
    description: 'Button/action to release an asset to use.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsRetireAsset: {
    text: 'Retire asset',
    description: 'Button/action to retire an asset.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsRaiseFault: {
    text: 'Raise fault',
    description: 'Button/action to raise a fault request for an asset.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsDueSoon: {
    text: 'Due soon',
    description: 'Header for due soon asset strip.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsRegister: {
    text: 'Asset register',
    description: 'Header for assets register table.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsColAsset: {
    text: 'Asset',
    description: 'Column header for asset name/code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsColPlace: {
    text: 'Place',
    description: 'Column header for asset location place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsColCriticality: {
    text: 'Criticality',
    description: 'Column header for asset criticality.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsColStatus: {
    text: 'Status',
    description: 'Column header for asset status.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsColDue: {
    text: 'Due',
    description: 'Column header for asset next due action.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsColNextPm: {
    text: 'Next PM',
    description: 'Column header for next PM date.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsColCalibration: {
    text: 'Calibration',
    description: 'Column header for next calibration date.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsColAmc: {
    text: 'AMC until',
    description: 'Column header for AMC expiry date.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsColWarranty: {
    text: 'Warranty until',
    description: 'Column header for warranty expiry date.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsEmptyList: {
    text: 'No assets found.',
    description: 'Empty state message when no assets match filters.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsAssetsFieldCode: {
    text: 'Asset code',
    description: 'Field label for asset code.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsFieldName: {
    text: 'Asset name',
    description: 'Field label for asset name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsFieldCategory: {
    text: 'Category',
    description: 'Field label for asset category.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsFieldModel: {
    text: 'Model',
    description: 'Field label for asset model.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsFieldSerial: {
    text: 'Serial number',
    description: 'Field label for asset serial number.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsFieldCriticality: {
    text: 'Criticality',
    description: 'Field label for asset criticality.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsFieldPlace: {
    text: 'Place',
    description: 'Field label for asset place location.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsFieldVendor: {
    text: 'Vendor details',
    description: 'Section label for vendor details.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldVendorName: {
    text: 'Vendor name',
    description: 'Field label for vendor name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsFieldVendorPhone: {
    text: 'Vendor phone',
    description: 'Field label for vendor phone.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsFieldVendorContract: {
    text: 'Contract / AMC number',
    description: 'Field label for vendor contract number.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldPmInterval: {
    text: 'PM interval (days)',
    description: 'Field label for PM interval days.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldPmNextDue: {
    text: 'Next PM due date',
    description: 'Field label for PM next due date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldPmLeadDays: {
    text: 'PM lead notice (days)',
    description: 'Field label for PM lead notice days.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldCalInterval: {
    text: 'Calibration interval (days)',
    description: 'Field label for calibration interval days.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldCalNextDue: {
    text: 'Next calibration due date',
    description: 'Field label for calibration next due date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldCalLeadDays: {
    text: 'Calibration lead notice (days)',
    description: 'Field label for calibration lead notice days.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldAmcUntil: {
    text: 'AMC expiry date',
    description: 'Field label for AMC expiry date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldWarrantyUntil: {
    text: 'Warranty expiry date',
    description: 'Field label for warranty expiry date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsFieldInstalledOn: {
    text: 'Installed on',
    description: 'Field label for asset installation date.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsReason: {
    text: 'Reason',
    description: 'Field label for reason.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsReasonPlaceholder: {
    text: 'Explain why this action is taken (3-500 characters)...',
    description: 'Placeholder for reason input.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsAssetsSelfReleaseReason: {
    text: 'Self-release explanation',
    description: 'Field label for self-release explanation by owner.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsSelfReleaseHint: {
    text: 'As workspace owner, you may release your own completed repair with an audited explanation.',
    description: 'Hint for owner self-release.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsAssetsIsolateHint: {
    text: 'Mark this equipment isolated from service.',
    description: 'Hint explaining isolation.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsAssetsRequestsTitle: {
    text: 'Requests & Work Orders',
    description: 'Section title for requests linked to asset.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsHistoryTitle: {
    text: 'Asset History',
    description: 'Section title for asset history timeline.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsPickerPlaceholder: {
    text: 'Select an asset (optional)...',
    description: 'Placeholder for asset select input.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsAssetsPickerRequiredPlaceholder: {
    text: 'Select an asset (required)...',
    description: 'Placeholder for required asset select input.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsAssetsFieldAsset: {
    text: 'Asset',
    description: 'Field label for asset picker.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAssetsAssetRequiredHint: {
    text: 'An asset is required for this request kind.',
    description: 'Validation notice when asset is required.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsAssetsIsolatedTag: {
    text: 'ISOLATED',
    description: 'Tag displayed on isolated assets.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsAssetsDueInDays: {
    text: 'Due in {days} days',
    description: 'Notice of days remaining until due.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsDueToday: {
    text: 'Due today',
    description: 'Notice that action is due today.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAssetsOverdueDays: {
    text: 'Overdue by {days} days',
    description: 'Notice that action is overdue by days.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAssetsAmcExpiresInDays: {
    text: 'AMC expires in {days} days',
    description: 'Notice of AMC days remaining.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsAssetsAmcExpiredDaysAgo: {
    text: 'AMC expired {days} days ago',
    description: 'Notice that AMC has expired.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsAssetsWarrantyExpiresInDays: {
    text: 'Warranty expires in {days} days',
    description: 'Notice of warranty days remaining.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsAssetsWarrantyExpiredDaysAgo: {
    text: 'Warranty expired {days} days ago',
    description: 'Notice that warranty has expired.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsReliefNeededTitle: {
    text: 'Relief needed: {roleLabel} at {placeName} ({dayKey})',
    description: 'Title of auto-created relief request when staff is absent.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsSitesPostsSubtitle: {
    text: 'Duty posts per site and shift · an absence raises a relief request',
    description: 'Subtitle for Sites Posts and relief screen.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsSitesSelectSite: {
    text: 'Select site',
    description: 'Placeholder and label to select a client site.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesDay: {
    text: 'Day',
    description: 'Label for Day picker.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsSitesSelectShift: {
    text: 'Select shift',
    description: 'Placeholder and label to select a shift.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesPostsBoardTitle: {
    text: 'Posts & relief',
    description: 'Title of posts and relief board.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesColPost: {
    text: 'Post',
    description: 'Column header for Post place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesColNeeds: {
    text: 'Needs',
    description: 'Column header for staff needs count.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesColOnDuty: {
    text: 'On duty',
    description: 'Column header for on-duty staff.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesColState: {
    text: 'State',
    description: 'Column header for coverage state.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesStateCovered: {
    text: 'Covered',
    description: 'Status pill for covered post.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesStateShort: {
    text: 'Short',
    description: 'Status pill for short coverage.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesStateReliefNeeded: {
    text: 'Relief needed',
    description: 'Status pill when relief request is open.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesStateReliefAssigned: {
    text: 'Relief assigned',
    description: 'Status pill when relief assignment was filled.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesNeedsFormat: {
    text: '{count} {role}',
    description: 'Formatted need count and role.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesAbsentTag: {
    text: 'absent',
    description: 'Tag indicating worker is absent on roll.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsSitesReliefInbox: {
    text: 'Relief inbox',
    description: 'Section header for relief inbox.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesReliefFootnote: {
    text: 'An absence on the roll raises RELIEF_NEEDED automatically and warns about coverage (A6).',
    description: 'Footnote explaining automatic relief requests.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsSitesFreeCandidates: {
    text: 'Free candidates:',
    description: 'Label listing free relief candidates.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesOffToday: {
    text: 'off today',
    description: 'Tag indicating candidate has no shift today.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesAssignBtn: {
    text: 'Assign',
    description: 'Button to assign relief candidate.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesAssignCandidateBtn: {
    text: 'Assign {name}',
    description: 'Button to assign specific relief candidate.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesAssignDialogTitle: {
    text: 'Assign relief',
    description: 'Dialog title for assigning relief.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesPrintPostSheet: {
    text: 'Print post sheet',
    description: 'Button to open post sheet print preview.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesNoPosts: {
    text: 'No duty posts configured for this site.',
    description: 'Empty state message when site has no posts.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSitesNoReliefNeeded: {
    text: 'No relief needed.',
    description: 'Empty state message when no open relief requests.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesNoSites: {
    text: 'No client sites found.',
    description: 'Empty state message when workspace has no SITE places.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesNoShifts: {
    text: 'No shifts available.',
    description: 'Empty state message when no shifts exist.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesPostSheetTitle: {
    text: 'Post sheet · {siteName}',
    description: 'Print sheet title for duty post sheet.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSitesPostSheetSubtitle: {
    text: '{dayKey} · {shiftName}',
    description: 'Print sheet subtitle for duty post sheet.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSitesErrorStaleRequest: {
    text: 'Cannot assign relief: request is no longer open.',
    description: 'Error message when relief request is stale.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorOnLeave: {
    text: 'Cannot assign relief: person is on approved leave.',
    description: 'Error message when relief candidate is on leave.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorDoubleBooked: {
    text: 'Cannot assign relief: person is already assigned to this shift.',
    description: 'Error message when relief candidate is double-booked.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorRoleCeiling: {
    text: 'Cannot assign relief: member role ceiling prevents duty assignment.',
    description: 'Error message when candidate violates role ceiling.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsSitesErrorGeneric: {
    text: 'Failed to assign relief.',
    description: 'Generic error message for failed relief assignment.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsSitesErrorNotFound: {
    text: 'Record or request not found.',
    description: 'Error message when relief target is not found.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorMissingAttributes: {
    text: 'Request missing required relief attributes.',
    description: 'Error message when relief request attributes are missing.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorInactivePerson: {
    text: 'Cannot assign relief: person is inactive in workspace.',
    description: 'Error message when relief candidate is inactive.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesIncidentsSubtitle: {
    text: 'Incidents (restricted) · client complaints · supply requests · supervisor site audits',
    description: 'Subtitle for Sites Incidents & audits screen.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsSitesIncidentsTitle: {
    text: 'Incidents',
    description: 'Card title for restricted incidents.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesRestrictedTag: {
    text: 'restricted',
    description: 'Badge or label indicating restricted visibility.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesAuditScoresTitle: {
    text: 'Site audit scores',
    description: 'Card title for site audit scores.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesSupervisorRegisterTag: {
    text: 'supervisor register',
    description: 'Subtitle/tag for supervisor register.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesColSite: {
    text: 'Site',
    description: 'Column header for site place name.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesColLastAudit: {
    text: 'Last audit',
    description: 'Column header for date of last audit.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesColScore: {
    text: 'Score',
    description: 'Column header for audit percentage score.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesRecordAuditBtn: {
    text: 'Record audit',
    description: 'Button to launch record audit handoff.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesComplaintsSupplyTitle: {
    text: 'Complaints and supply requests',
    description: 'Card title for complaints and supply requests.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesNoIncidents: {
    text: 'No incidents recorded.',
    description: 'Empty state message when no incidents.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesNoComplaintsSupply: {
    text: 'No complaints or supply requests.',
    description: 'Empty state message when no complaints or supplies.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsSitesNoAudits: {
    text: 'No site audits recorded.',
    description: 'Empty state message when no audits.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesNoAuditTemplate: {
    text: 'No supervisor site audit template configured.',
    description: 'Empty state message when no supervisor site audit template is configured.',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsSitesOpenTemplatesBtn: {
    text: 'Open templates',
    description: 'Button to open templates when no supervisor site audit template is configured.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesTodaySubtitle: {
    text: '{sitesCount} client sites · {shiftName}',
    description: 'Subtitle for Sites Today screen showing count of client sites and shift name',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesCheckpointQrSheetBtn: {
    text: 'Checkpoint QR sheet',
    description: 'Button to open checkpoint QR sheet',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesKpiPostsCovered: {
    text: 'Posts covered',
    description: 'KPI title for covered posts in Sites Today',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesKpiPostsCoveredAllOk: {
    text: 'All posts covered',
    description: 'Subtitle when all posts have coverage in Sites Today',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesKpiRoundsMissed: {
    text: 'Rounds missed',
    description: 'KPI title for missed patrol rounds in Sites Today',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesKpiRoundsNoneMissed: {
    text: 'None missed',
    description: 'Subtitle when no patrol rounds are missed',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesKpiRoundsMissedSubtitle: {
    text: '{site} · {time} round, grace over',
    description: 'Subtitle for missed patrol rounds KPI in Sites Today',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesKpiOpenIncidents: {
    text: 'Open incidents',
    description: 'KPI title for open incidents in Sites Today',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesKpiRestrictedCount: {
    text: '{count} restricted',
    description: 'Subtitle showing count of restricted incidents in Sites Today',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesKpiNoneRestricted: {
    text: 'None restricted',
    description: 'Subtitle when no restricted incidents exist in Sites Today',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesKpiCleaningChecks: {
    text: 'Cleaning checks done',
    description: 'KPI title for completed cleaning schedule checks in Sites Today',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesKpiCleaningChecksProof: {
    text: 'photo proof',
    description: 'Subtitle for cleaning checks KPI indicating photo proof requirement',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesNeedsYouNowTitle: {
    text: 'Needs you now',
    description: 'Card title for urgent Sites exceptions needing attention',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesNeedsYouNowEmpty: {
    text: 'All clear — nothing needs your immediate attention.',
    description: 'Empty state message when no urgent items exist in Needs you now',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesNeedsReliefItem: {
    text: 'Relief needed · {site} {post}',
    description: 'Title for open relief item in Needs you now',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesNeedsReliefSubtitle: {
    text: '{worker} absent · {shift}',
    description: 'Subtitle for open relief item in Needs you now',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesNeedsRoundMissedItem: {
    text: 'Round missed · {site}',
    description: 'Title for missed round item in Needs you now',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesNeedsRoundMissedSubtitle: {
    text: '{time} round · {scanned} of {total} checkpoints',
    description: 'Subtitle for missed round item in Needs you now',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesNeedsComplaintItem: {
    text: 'Client complaint · {site}',
    description: 'Title for client complaint item in Needs you now',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesNeedsSupplyItem: {
    text: 'Supply request · {site}',
    description: 'Title for supply request item in Needs you now',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesActionAssign: {
    text: 'Assign',
    description: 'Button text to assign relief in Needs you now',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesActionViewRound: {
    text: 'View',
    description: 'Button text to view round in Needs you now',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesActionOpen: {
    text: 'Open',
    description: 'Button text to open incident or request in Needs you now',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesSitesTonightTitle: {
    text: 'Sites tonight',
    description: 'Table title for Sites tonight summary on Sites Today screen',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesColClientSite: {
    text: 'Client site',
    description: 'Table column header for client site name',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesColPreset: {
    text: 'Preset',
    description: 'Table column header for site preset',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesColRounds: {
    text: 'Rounds',
    description: 'Table column header for patrol rounds status',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesColCleaning: {
    text: 'Cleaning',
    description: 'Table column header for cleaning checks count',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesRoundStateOnTime: {
    text: 'On time',
    description: 'Pill text when rounds are on time',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesRoundStateMissed: {
    text: '{count} missed',
    description: 'Pill text when rounds have missed dues',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesRoundsTitle: {
    text: 'Rounds',
    description: 'Screen title for Sites Rounds tab',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesRoundsSubtitle: {
    text: 'Patrol rounds by checkpoint QR · location at scan only · window + 15 minutes, then supervisor, then owner',
    description: 'Subtitle for Sites Rounds screen',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsSitesAddRoundBtn: {
    text: '+ Round',
    description: 'Button text to add a patrol round',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesEditRoundBtn: {
    text: 'Edit round',
    description: 'Button text to edit a patrol round',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesPauseRoundBtn: {
    text: 'Pause',
    description: 'Button text to pause a patrol schedule',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesResumeRoundBtn: {
    text: 'Resume',
    description: 'Button text to resume a paused patrol schedule',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesArchiveRoundBtn: {
    text: 'Archive',
    description: 'Button text to archive a patrol schedule',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesRoundDetailHeader: {
    text: '{site} · {time} round',
    description: 'Header for selected patrol round detail strip',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesScanned: {
    text: 'Scanned',
    description: 'Pill label for a scanned checkpoint',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesNotScanned: {
    text: 'Not scanned',
    description: 'Pill label for an unscanned checkpoint',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesOffWindowTag: {
    text: 'Off-window',
    description: 'Badge indicating a checkpoint scan was performed outside its due window',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesWindowTimeline: {
    text: 'Window {window} · grace till {graceEnd}',
    description: 'Timeline entry showing patrol window and grace end',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesMissedTimeline: {
    text: '{time} missed · {role} told',
    description: 'Timeline entry when a round was missed and role was notified',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesOwnerTimeline: {
    text: '{time} · owner told if still open',
    description: 'Timeline entry showing owner escalation step',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesEscalationHopTimeline: {
    text: '{time} · Hop {hop}: {role} told',
    description: 'Timeline entry showing an escalation hop notification',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesRoundsTonightTitle: {
    text: 'Rounds tonight',
    description: 'Title for list of patrol rounds tonight',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesColRound: {
    text: 'Round',
    description: 'Table column header for round time',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesColCheckpoints: {
    text: 'Checkpoints',
    description: 'Table column header for checkpoint scan progress',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesRoundStateDone: {
    text: 'Done',
    description: 'Pill text for completed patrol round',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesRoundStateInProgress: {
    text: 'In progress',
    description: 'Pill text for in progress patrol round',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesRoundStateUpcoming: {
    text: 'Upcoming',
    description: 'Pill text for upcoming patrol round',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesNoRoundsForShift: {
    text: 'No patrol rounds scheduled for this shift.',
    description: 'Empty state message when no rounds exist for selected shift',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesMissedInboxTitle: {
    text: 'Missed-round inbox',
    description: 'Title for missed-round inbox section',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesMissedInboxSubtitle: {
    text: 'Rounds missed in the last 7 days · admins can skip with reason',
    description: 'Subtitle for missed-round inbox section',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsSitesNoMissedRounds: {
    text: 'No missed rounds in the last 7 days.',
    description: 'Empty state message when there are no missed rounds',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesSkipBtn: {
    text: 'Skip',
    description: 'Button text to skip a missed due checkpoint',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesSkipDialogTitle: {
    text: 'Skip missed checkpoint',
    description: 'Title for skip missed checkpoint dialog',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesSkipReasonLabel: {
    text: 'Reason',
    description: 'Label for skip reason input',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesSkipReasonPlaceholder: {
    text: 'Reason for skipping (minimum 3 characters)',
    description: 'Placeholder for skip reason input',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSitesSkipDialogSubmit: {
    text: 'Skip checkpoint',
    description: 'Submit button text in skip dialog',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesSkipDialogCancel: {
    text: 'Cancel',
    description: 'Cancel button text in dialogs',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesCreateRoundDialogTitle: {
    text: 'Create patrol round',
    description: 'Title for create patrol round dialog',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesEditRoundDialogTitle: {
    text: 'Edit patrol round',
    description: 'Title for edit patrol round dialog',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesTemplateLabel: {
    text: 'Register template',
    description: 'Label for register template select',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesCheckpointsLabel: {
    text: 'Checkpoints (up to 50)',
    description: 'Label for checkpoint selection in patrol round dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesCadenceLabel: {
    text: 'Cadence',
    description: 'Label for cadence select in patrol round dialog',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesCadencePerShift: {
    text: 'Per shift',
    description: 'Option label for per-shift cadence',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesCadenceDaily: {
    text: 'Daily',
    description: 'Option label for daily cadence',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesStartMinuteLabel: {
    text: 'Start time (HH:MM)',
    description: 'Label for start time input in patrol round dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesDurationMinutesLabel: {
    text: 'Duration (minutes)',
    description: 'Label for duration minutes input in patrol round dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesAssigneeRoleLabel: {
    text: 'Assignee duty role',
    description: 'Label for assignee duty role select in patrol round dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesEscalationRoleLabel: {
    text: 'First-escalation duty role',
    description: 'Label for first-escalation duty role select in patrol round dialog',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesEscalationNone: {
    text: 'None (default chain)',
    description: 'Option for no first-escalation duty role in patrol round dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesGraceMinutesLabel: {
    text: 'Grace period (minutes)',
    description: 'Label for grace period input in patrol round dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesEscalateEveryMinutesLabel: {
    text: 'Escalate every (minutes)',
    description: 'Label for escalation frequency input in patrol round dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesSaveRoundBtn: {
    text: 'Save round',
    description: 'Submit button text to save round in dialog',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSitesCancelBtn: {
    text: 'Cancel',
    description: 'Cancel button text in patrol round dialog',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesSelectCheckpointsPlaceholder: {
    text: 'Select checkpoints...',
    description: 'Placeholder when selecting checkpoints in dialog',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSitesNoApprovedTemplates: {
    text: 'No approved Sites register templates found. Create one in Templates.',
    description: 'Notice when no approved register templates exist in Sites',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsSitesNoCheckpoints: {
    text: 'No checkpoints under selected site. Add checkpoints in Places.',
    description: 'Notice when no checkpoints exist under the selected site',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsSitesErrorPermissionDenied: {
    text: 'You do not have permission to perform this action.',
    description: 'Error message for permission denied server response',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorReasonRequired: {
    text: 'A skip reason of at least 3 characters is required.',
    description: 'Error message when skip reason is too short',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorCheckpointsRequired: {
    text: 'At least one checkpoint must be selected (up to 50).',
    description: 'Error message when no checkpoints are selected in patrol round dialog',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorTemplateRequired: {
    text: 'An approved Sites register template must be selected.',
    description: 'Error message when no template is selected in patrol round dialog',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorDutyRoleRequired: {
    text: 'An assignee duty role is required.',
    description: 'Error message when no assignee duty role is selected in patrol round dialog',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSitesErrorTemplateNotApproved: {
    text: 'Template current version is not approved.',
    description: 'Error message when template current version is not approved',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesErrorDueAlreadyClosed: {
    text: 'This round checkpoint is already completed or skipped.',
    description: 'Error message when trying to skip an already closed due checkpoint',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesWageAdminOnlyNotice: {
    text: 'Only workspace owners and admins can view wage sheets.',
    description: 'Notice shown to non-admins on the Sites wage sheet screen',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesWageTitle: {
    text: 'Wage sheet',
    description: 'Page title for operational wage sheet',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWageSubtitle: {
    text: 'Operational sheet, not payroll or a statutory register',
    description: 'Subtitle for operational wage sheet',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesWageWarnBox: {
    text: 'Operational sheet, not payroll or a statutory register. No PF or ESI is worked out and nothing is deducted (SO-D41).',
    description: 'Warning notice displayed on operational wage sheet screen',
    surface: 'shell',
    maxChars: 140,
  },
  NLSOpsSitesWageFootnote: {
    text: 'Days come from the attendance roll (A6). Gross = days × day rate + overtime × OT rate, nothing else.',
    description: 'Footnote explaining wage calculations on operational wage sheet screen',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsSitesWageLockBtn: {
    text: 'Lock period',
    description: 'Button to freeze/lock wage sheet for a period',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageUnlockBtn: {
    text: 'Unlock period',
    description: 'Button to unlock a previously locked wage sheet',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageDownloadCsv: {
    text: 'Download CSV',
    description: 'Button to download wage sheet as CSV',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageColWorker: {
    text: 'Worker',
    description: 'Column header for worker name',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageColDaysWorked: {
    text: 'Days worked',
    description: 'Column header for days worked count',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageColOvertimeH: {
    text: 'Overtime h',
    description: 'Column header for overtime hours',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageColDayRate: {
    text: 'Day rate (you enter)',
    description: 'Column header for day rate entered by agency',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWageColOtRate: {
    text: 'OT rate (you enter)',
    description: 'Column header for overtime rate entered by agency',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWageColGross: {
    text: 'Gross',
    description: 'Column header for gross amount calculated',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageColFlags: {
    text: 'Flags',
    description: 'Column header for wage sheet row flags',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageColActions: {
    text: 'Actions',
    description: 'Column header for row action buttons',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageTotals: {
    text: 'Totals',
    description: 'Row label for sheet totals',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageNoRateFlag: {
    text: 'No rate',
    description: 'Badge indicating worker has no configured wage rate',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageRateChangedFlag: {
    text: 'Rate changed',
    description: 'Badge indicating wage rate changed during the period',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageEditRate: {
    text: 'Set rate',
    description: 'Button to set or edit wage rate for a worker',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageRateDialogTitle: {
    text: 'Set wage rate',
    description: 'Dialog title for setting worker wage rate',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWageEffectiveFrom: {
    text: 'Effective from',
    description: 'Field label for effective from date in wage rate dialog',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWageDayRateLabel: {
    text: 'Day rate',
    description: 'Field label for day rate amount in wage rate dialog',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWageOtRateLabel: {
    text: 'OT rate per hour',
    description: 'Field label for overtime rate per hour in wage rate dialog',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWageSaveRateBtn: {
    text: 'Save rate',
    description: 'Submit button in wage rate dialog',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageCancelBtn: {
    text: 'Cancel',
    description: 'Cancel button in dialogs',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageNoCurrencyNote: {
    text: 'Workspace currency is not configured. Set a currency in workspace settings before setting rates.',
    description: 'Notice explaining rates cannot be set without workspace currency',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsSitesWageLockConfirmTitle: {
    text: 'Lock wage sheet period',
    description: 'Title for lock period confirmation dialog',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsSitesWageLockConfirmBody: {
    text: 'Locking this period freezes all worker day counts, overtime hours, and gross pay calculations. A locked sheet cannot be modified without owner unlock.',
    description: 'Confirmation body explaining what locking does',
    surface: 'shell',
    maxChars: 160,
  },
  NLSOpsSitesWageUnlockDialogTitle: {
    text: 'Unlock wage sheet',
    description: 'Dialog title for owner unlocking wage sheet',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWageUnlockReasonLabel: {
    text: 'Reason for unlocking',
    description: 'Field label for unlock reason',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSitesWageUnlockReasonPlaceholder: {
    text: 'Enter reason (minimum 3 characters)',
    description: 'Placeholder for unlock reason input',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSitesWageUnlockSubmitBtn: {
    text: 'Unlock sheet',
    description: 'Submit button to unlock wage sheet',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageChangedSinceLockBanner: {
    text: 'Attendance has changed since this wage sheet was locked. The locked export remains frozen; unlock the sheet to recalculate.',
    description: 'Banner warning when attendance changed since lock',
    surface: 'shell',
    maxChars: 160,
  },
  NLSOpsSitesWageLockedStateBadge: {
    text: 'Locked',
    description: 'Badge showing wage sheet is locked',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesWageDraftStateBadge: {
    text: 'Draft',
    description: 'Badge showing wage sheet is in draft state',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSitesWagePeriodFrom: {
    text: 'Period from',
    description: 'Label for period start date filter',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWagePeriodTo: {
    text: 'Period to',
    description: 'Label for period end date filter',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageFilterParty: {
    text: 'Party',
    description: 'Label for party filter dropdown',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageAllParties: {
    text: 'All parties',
    description: 'Option label for all parties in filter',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSitesWageEmptyPeriod: {
    text: 'No attendance records found for this period.',
    description: 'Empty state message when no attendance exists for period',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSitesWageLoading: {
    text: 'Loading wage sheet…',
    description: 'Loading indicator text on wage sheet screen',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRoleCeilingDisabled: {
    text: 'Workspace role is the ceiling (Viewers/Helpers cannot hold approver roles)',
    description: 'Notice why approver toggles are disabled for Viewers and Helpers.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsStateDraft: {
    text: 'Draft',
    description: 'Display label for DRAFT state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStateSubmitted: {
    text: 'Submitted',
    description: 'Display label for SUBMITTED state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStateReviewed: {
    text: 'Reviewed',
    description: 'Display label for REVIEWED state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStateApproved: {
    text: 'Approved',
    description: 'Display label for APPROVED state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStateReturned: {
    text: 'Returned',
    description: 'Display label for RETURNED state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsStatePendingApproval: {
    text: 'Pending approval',
    description: 'Display label for PENDING_APPROVAL state.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsFieldTypeNumber: {
    text: 'Number',
    description: 'Display label for NUMBER field type.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldTypeChoice: {
    text: 'Choice',
    description: 'Display label for CHOICE field type.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldTypeYesNo: {
    text: 'Yes/No',
    description: 'Display label for YES_NO field type.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldTypeDateTime: {
    text: 'Date/Time',
    description: 'Display label for DATE_TIME field type.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldTypePhoto: {
    text: 'Photo',
    description: 'Display label for PHOTO field type.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldTypeReading: {
    text: 'Reading',
    description: 'Display label for READING field type.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFieldTypeAcknowledge: {
    text: 'Acknowledgement',
    description: 'Display label for ACKNOWLEDGE field type.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsFieldTypeCashCount: {
    text: 'Cash count',
    description: 'Display label for CASH_COUNT field type.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsTemplatesHeading: {
    text: 'Templates',
    description: 'Heading for templates list.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRecordsHeading: {
    text: 'Records',
    description: 'Heading for records list.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRecordDetailsHeading: {
    text: 'Record details',
    description: 'Heading for record details view.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsFilterAll: {
    text: 'All',
    description: 'Filter option for all records.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsFilterState: {
    text: 'Filter by state',
    description: 'Filter select for record state.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsFilterTemplate: {
    text: 'Filter by template',
    description: 'Filter select for record template.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsFilterPlace: {
    text: 'Filter by place',
    description: 'Filter select for record place.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsFilterLateOnly: {
    text: 'Late only',
    description: 'Toggle to filter late records only.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRegistersTitle: {
    text: 'Registers & templates',
    description: 'Heading for RegistersScreen.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsRegistersSubtitle: {
    text: 'Versioned templates · records lock on submit · corrections append beside, never over',
    description: 'Subtitle for RegistersScreen.',
    surface: 'shell',
    maxChars: 110,
  },
  NLSOpsRegistersSubtabsAria: {
    text: 'Registers sections',
    description: 'Aria label for RegistersScreen sub-tabs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsEmptyTemplates: {
    text: 'No templates in this sector yet',
    description: 'Empty state for templates list.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsEmptyRecords: {
    text: 'No register records yet',
    description: 'Empty state for records list.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsEmptyReviewQueue: {
    text: 'No records waiting for review or approval',
    description: 'Empty state for review queue.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsTemplateVersionLabel: {
    text: 'v{version}',
    description: 'Short version label.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsTemplateNoApprovedVersion: {
    text: 'No approved version',
    description: 'Label when template has no approved version yet.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsFieldMaxCount: {
    text: 'Max photos',
    description: 'Input label for PHOTO field maxCount.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRemoveField: {
    text: 'Remove field',
    description: 'Button to remove a field from template builder.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsMoveFieldUp: {
    text: 'Move up',
    description: 'Button to move field up in template builder.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsMoveFieldDown: {
    text: 'Move down',
    description: 'Button to move field down in template builder.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsExceptionNone: {
    text: 'None',
    description: 'Option for no exception condition on YES_NO field.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsYes: {
    text: 'Yes',
    description: 'Yes option label.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsNo: {
    text: 'No',
    description: 'No option label.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsCapturedAt: {
    text: 'Captured at',
    description: 'Label for record capturedAt timestamp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsReceivedAt: {
    text: 'Received at',
    description: 'Label for record receivedAt timestamp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsExceptionsHeading: {
    text: 'Exceptions ({count})',
    description: 'Heading for exceptions section in record view.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsEditDraftVersion: {
    text: 'Edit draft',
    description: 'Button to edit a draft or create new draft version.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSubmittedByLabel: {
    text: 'Submitted by {uid}',
    description: 'Label showing who submitted a version or record.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsApprovedByLabel: {
    text: 'Approved by {uid}',
    description: 'Label showing who approved a version or record.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsReviewedByLabel: {
    text: 'Reviewed by {uid}',
    description: 'Label showing who reviewed a record.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsReturnedByLabel: {
    text: 'Returned by {uid}',
    description: 'Label showing who returned a record.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCorrectionEntryMeta: {
    text: '{author} · {time}',
    description: 'Metadata line for a correction entry.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsCorrectionDiffLine: {
    text: '{field}: {before} → {after}',
    description: 'Diff line for a corrected field.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsNoCorrectionsYet: {
    text: 'No corrections recorded.',
    description: 'Empty message when record has no corrections.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSelectPrompt: {
    text: 'Select...',
    description: 'Default placeholder option in select controls.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBuilderTitleNew: {
    text: 'New template draft',
    description: 'Heading when creating a new template.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBuilderTitleEdit: {
    text: 'Edit template draft',
    description: 'Heading when editing an existing template draft.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsKindCheck: {
    text: 'Check',
    description: 'Display label for CHECK template kind.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsKindInspection: {
    text: 'Inspection',
    description: 'Display label for INSPECTION template kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsKindLog: {
    text: 'Register log',
    description: 'Display label for LOG template kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsKindDpr: {
    text: 'Daily progress report',
    description: 'Display label for DPR template kind.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsKindOpening: {
    text: 'Opening checklist',
    description: 'Display label for OPENING template kind.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsKindClosing: {
    text: 'Closing checklist',
    description: 'Display label for CLOSING template kind.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsKindHandover: {
    text: 'Shift handover',
    description: 'Display label for HANDOVER template kind.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsKindDisplayAudit: {
    text: 'Display audit',
    description: 'Display label for DISPLAY_AUDIT template kind.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPhotoThumbnailAlt: {
    text: 'Record photo {index}',
    description: 'Alt text for record photo thumbnail.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsCorrectionPhotosLockedNote: {
    text: 'Photos lock once a record is submitted and cannot be changed via correction.',
    description: 'Note in correction dialog explaining that PHOTO fields are locked once a record is submitted (SO-P59).',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsInvalidNumberError: {
    text: 'Enter a valid number.',
    description: 'Inline validation error when a NUMBER or READING field input is not a valid number.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsInvalidFieldValueError: {
    text: 'Select or enter a valid value for this field.',
    description: 'Inline validation error when a field value in correction dialog is missing or invalid.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsPhotoLoadingPlaceholder: {
    text: 'Loading photo...',
    description: 'Placeholder text shown while a record photo download URL is loading.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsValueEmpty: {
    text: '—',
    description: 'Em-dash placeholder when a record field has no value.',
    surface: 'shell',
    maxChars: 5,
  },
  NLSOpsCorrectionFieldLabel: {
    text: 'Field to correct',
    description: 'Select label for choosing which template field to correct.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCorrectionNewValueLabel: {
    text: 'New value',
    description: 'Input label for the corrected field value.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsInvalidOccurredAtError: {
    text: 'Enter a valid occurred date and time.',
    description: 'Inline validation error when occurredAt in the record form is missing or invalid.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRostersSubtabRoster: {
    text: 'Roster',
    description: 'Sub-tab label for the main roster grid.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRostersSubtabSetup: {
    text: 'Setup',
    description: 'Sub-tab label for duty roles, shifts, and coverage rules setup.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRostersSubtabChanges: {
    text: 'Changes',
    description: 'Sub-tab label for roster change requests inbox.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRostersSubtabAttendance: {
    text: 'Attendance',
    description: 'Sub-tab label for operational attendance.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterSelectPlace: {
    text: 'Select place',
    description: 'Picker label to select place for roster.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterSelectRoster: {
    text: 'Select roster',
    description: 'Picker label to select active roster.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterNewButton: {
    text: 'New roster',
    description: 'Button to create a new roster.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterNewTitle: {
    text: 'Create new roster',
    description: 'Dialog title for new roster creation.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsRosterPeriodStart: {
    text: 'Period start',
    description: 'Form label for roster period start day.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterPeriodEnd: {
    text: 'Period end',
    description: 'Form label for roster period end day.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterSaveDraft: {
    text: 'Save draft',
    description: 'Button to save roster draft assignments.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterDraftSaved: {
    text: 'Draft saved successfully',
    description: 'Feedback message when draft is saved.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRosterCheckWarnings: {
    text: 'Check warnings',
    description: 'Button to preview warnings for current draft.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterNoWarnings: {
    text: 'No warnings found for draft.',
    description: 'Notification when preview warnings returns 0 warnings.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRosterWarningsFound: {
    text: '{count} warning(s) found in draft.',
    description: 'Heading or banner when preview warnings returns issues.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRosterPublishButton: {
    text: 'Publish',
    description: 'Button to publish current roster draft.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterPublishDialogTitle: {
    text: 'Publish roster with warnings',
    description: 'Title for confirmation dialog when publishing with warnings.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRosterPublishDialogBody: {
    text: 'The following warning(s) were found. Do you want to accept warnings and publish this roster?',
    description: 'Body prompt for publish confirmation dialog.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsRosterAcceptWarningsPublish: {
    text: 'Accept warnings & publish',
    description: 'Confirm action in publish warnings dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsRosterBlockingGapsBadge: {
    text: '{count} blocking gap(s)',
    description: 'Badge on roster toolbar showing count of blocking legal role gaps.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterBlockingBadge: {
    text: 'Blocking',
    description: 'Badge on warning item indicating it blocks roster publishing.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRosterBlockingPublishTitle: {
    text: 'Roster has required legal role gaps',
    description: 'Title of publish dialog when blocking legal role gaps exist.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRosterBlockingDialogBody: {
    text: 'This roster has blocking legal role gaps. Legally required roles must be staffed or overridden by a workspace owner with justification.',
    description: 'Description in publish dialog when blocking gaps exist.',
    surface: 'shell',
    maxChars: 160,
  },
  NLSOpsRosterOverrideReasonLabel: {
    text: 'Owner override reason (10–500 characters)',
    description: 'Label for override reason input textarea.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRosterOverrideReasonPlaceholder: {
    text: 'e.g. Locum registered pharmacist covering on-site under owner supervision',
    description: 'Placeholder for override reason textarea.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsRosterOverrideReasonHint: {
    text: '{count}/500 characters (minimum 10 required)',
    description: 'Character counter hint for override reason.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRosterPublishWithOverride: {
    text: 'Publish with override',
    description: 'Button for owner to publish with legal override.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterNonOwnerCannotPublish: {
    text: 'Only workspace owners can publish a roster with legal role gaps. Please contact an owner to review and provide an override justification.',
    description: 'Notice shown to non-owners explaining why they cannot publish.',
    surface: 'shell',
    maxChars: 160,
  },
  NLSOpsWorkforceRegistrationsTitle: {
    text: 'Registrations & Legal Roles',
    description: 'Section heading in workforce dialog for legal role registrations.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsWorkforceAddRegistration: {
    text: 'Add registration',
    description: 'Button to add a legal role registration for a worker.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforceNoRegistrations: {
    text: 'No registrations recorded for this worker.',
    description: 'Empty state message for worker registrations.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsWorkforceRenewRegistration: {
    text: 'Renew',
    description: 'Button to renew an existing registration.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsWorkforceRegistrationDialogTitle: {
    text: 'Add Registration',
    description: 'Dialog title for adding a registration.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsWorkforceLegalRoleLabel: {
    text: 'Legal role',
    description: 'Label for selecting legal role key.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsWorkforceRegistrationTitleLabel: {
    text: 'Registration title',
    description: 'Label for registration title input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsWorkforceValidFromLabel: {
    text: 'Valid from',
    description: 'Label for valid from date input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforceValidToLabel: {
    text: 'Valid to',
    description: 'Label for valid to date input.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsWorkforceReferenceLabel: {
    text: 'Registration number / reference',
    description: 'Label for registration reference number.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsWorkforceRenewDialogTitle: {
    text: 'Renew Registration',
    description: 'Dialog title for renewing a registration.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterPublishSuccess: {
    text: 'Roster published as version {version}',
    description: 'Feedback when roster publish completes.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRosterPublishedPill: {
    text: 'Published v{version} · {acked}/{total} acknowledged',
    description: 'Status pill showing published version and member acknowledgement count.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsRosterDraftPill: {
    text: 'Draft · {count} assignment(s)',
    description: 'Status pill for roster draft.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRosterAcknowledgeButton: {
    text: 'Acknowledge',
    description: 'Button for assigned user to acknowledge published roster.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterAcknowledgedSuccess: {
    text: 'Roster acknowledged.',
    description: 'Feedback when user acknowledges roster.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRosterPrintButton: {
    text: 'Print roster',
    description: 'Button to open print sheet.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterCellEditorTitle: {
    text: 'Assignments for {name} on {day}',
    description: 'Dialog title when editing cell assignments.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsRosterAddAssignment: {
    text: 'Add assignment',
    description: 'Button in cell editor to add an assignment.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterRemoveAssignment: {
    text: 'Remove',
    description: 'Button in cell editor to remove an assignment.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterShiftLabel: {
    text: 'Shift',
    description: 'Dropdown label for shift.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterDutyRoleLabel: {
    text: 'Duty role',
    description: 'Dropdown label for duty role.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterKindLabel: {
    text: 'Assignment kind',
    description: 'Dropdown label for assignment kind.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterKindShift: {
    text: 'Regular shift',
    description: 'Option for regular shift.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterKindOnCallPrimary: {
    text: 'On-call (Primary)',
    description: 'Option for primary on-call assignment.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterKindOnCallBackup: {
    text: 'On-call (Backup)',
    description: 'Option for backup on-call assignment.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterPersonLabel: {
    text: 'Person',
    description: 'Dropdown label for person.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterPagePrev: {
    text: 'Previous week',
    description: 'Pagination button for previous 7 days.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterPageNext: {
    text: 'Next week',
    description: 'Pagination button for next 7 days.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRosterPageIndicator: {
    text: 'Page {page} of {total}',
    description: 'Pagination indicator for grid.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterOvernightMark: {
    text: 'Overnight shift',
    description: 'Aria label for shift crossing midnight.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterEmptyState: {
    text: 'No rosters found for this place. Create a new roster to begin.',
    description: 'Empty state message when no rosters exist for place.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsRosterReadOnlyNotice: {
    text: 'Viewing roster in read-only mode. Roster manager permissions required to edit or publish.',
    description: 'Notice shown to viewers without roster manager role.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsSetupDutyRolesTitle: {
    text: 'Duty roles',
    description: 'Section heading for duty roles setup.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSetupShiftsTitle: {
    text: 'Shifts',
    description: 'Section heading for shifts setup.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSetupCoverageRulesTitle: {
    text: 'Coverage rules',
    description: 'Section heading for coverage rules setup.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSetupAddDutyRole: {
    text: 'New duty role',
    description: 'Button to add duty role.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSetupAddShift: {
    text: 'New shift',
    description: 'Button to add shift.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSetupAddCoverageRule: {
    text: 'New coverage rule',
    description: 'Button to add coverage rule.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsDutyRoleKey: {
    text: 'Role key',
    description: 'Input label for duty role key.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsDutyRoleLabel: {
    text: 'Role label',
    description: 'Input label for duty role display name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShiftName: {
    text: 'Shift name',
    description: 'Input label for shift name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShiftCode: {
    text: 'Code',
    description: 'Input label for short shift code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsShiftStartMinute: {
    text: 'Start time (HH:MM)',
    description: 'Input label for shift start time.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShiftEndMinute: {
    text: 'End time (HH:MM)',
    description: 'Input label for shift end time.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsShiftOvernightNotice: {
    text: 'Overnight: shift crosses midnight',
    description: 'Label indicating shift ends on the next morning.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCoverageRuleMinCount: {
    text: 'Minimum required staff',
    description: 'Input label for minimum coverage count.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsCoverageRuleRequiredSkill: {
    text: 'Required skill tag (optional)',
    description: 'Input label for required skill in coverage rule.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsChangesFilterMine: {
    text: 'My requests',
    description: 'Segmented filter for requests created by caller.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsChangesFilterToMe: {
    text: 'To me (counterparty)',
    description: 'Segmented filter for requests where caller is counterparty.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsChangesFilterDecide: {
    text: 'To decide (manager)',
    description: 'Segmented filter for requests needing manager decision.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsChangesRaiseButton: {
    text: 'Raise change request',
    description: 'Button to open change request form.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsChangesRaiseTitle: {
    text: 'Raise roster change request',
    description: 'Dialog title for raising roster change.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsChangesTypeLeave: {
    text: 'Leave request',
    description: 'Change request type for leave.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsChangesTypeSwap: {
    text: 'Shift swap',
    description: 'Change request type for shift swap.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsChangesTypeOnCallSwap: {
    text: 'On-call swap',
    description: 'Change request type for on-call swap.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsChangesReason: {
    text: 'Reason',
    description: 'Reason input label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsChangesCounterparty: {
    text: 'Counterparty member',
    description: 'Counterparty picker label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsChangesProxyWorker: {
    text: 'Worker (on whose behalf this request is made)',
    description: 'Worker picker label for proxy requests.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsChangesSelfApprovalReason: {
    text: 'Owner self-approval reason (3–500 characters)',
    description: 'Self-approval reason input for workspace owners.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsChangesAccept: {
    text: 'Accept',
    description: 'Button to accept swap request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsChangesDecline: {
    text: 'Decline',
    description: 'Button to decline swap request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsChangesApprove: {
    text: 'Approve',
    description: 'Button for manager to approve request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsChangesReject: {
    text: 'Reject',
    description: 'Button for manager to reject request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsChangesCancel: {
    text: 'Cancel request',
    description: 'Button for requester to cancel request.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsChangesNoRequests: {
    text: 'No change requests in this view.',
    description: 'Empty list message in changes tab.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsChangesApprovedWarningsTitle: {
    text: 'Auto-published version returned warnings',
    description: 'Banner title when auto-publish returns warnings.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsAttendanceTitle: {
    text: 'Operational attendance',
    description: 'Heading for attendance section.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsAttendanceSubtitle: {
    text: 'Operational attendance',
    description: 'Operational attendance label (not payroll).',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsAttendanceDayLabel: {
    text: 'Date',
    description: 'Date picker label in attendance tab.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceShiftLabel: {
    text: 'Shift',
    description: 'Shift picker label in attendance tab.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceRollPresent: {
    text: 'Present',
    description: 'Attendance roll status Present.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceRollAbsent: {
    text: 'Absent',
    description: 'Attendance roll status Absent.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceRollLate: {
    text: 'Late',
    description: 'Attendance roll status Late.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceRollHalfDay: {
    text: 'Half day',
    description: 'Attendance roll status Half day.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceRollOnLeave: {
    text: 'On leave',
    description: 'Attendance roll status On leave.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceMarkButton: {
    text: 'Submit attendance roll',
    description: 'Button to submit attendance markings.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsAttendanceLockedNotice: {
    text: 'Marked roll entries are locked. Request a correction to modify.',
    description: 'Notice indicating locked marked attendance.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsAttendanceAskCorrection: {
    text: 'Ask for correction',
    description: 'Button to request attendance correction.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAttendanceCorrectionReason: {
    text: 'Reason for correction (required)',
    description: 'Input label for correction request reason.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsAttendanceCorrectionsListTitle: {
    text: 'Pending attendance corrections',
    description: 'Section title for attendance corrections list.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsAttendanceImportButton: {
    text: 'Import CSV',
    description: 'Button to open attendance CSV import.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsAttendanceImportTitle: {
    text: 'Bulk import operational attendance',
    description: 'Title for CSV import dialog.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsAttendanceImportDryRun: {
    text: 'Dry run preview',
    description: 'Button to perform CSV dry run.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAttendanceImportCommit: {
    text: 'Commit import',
    description: 'Button to commit CSV import.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAttendanceImportSummary: {
    text: 'Dry run complete: {newCount} new entries, {correctionCount} correction requests, {errorCount} errors.',
    description: 'Summary of CSV import dry run.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsRosterPrintDraftMark: {
    text: 'DRAFT',
    description: 'Watermark or tag when printing draft roster.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRosterPrintLegendTitle: {
    text: 'Shift times legend',
    description: 'Heading for shift timings in print sheet.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterPrintPublishedMeta: {
    text: 'Version {version} published by {by} on {at}',
    description: 'Metadata line on published print sheet.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsRosterPrintDraftMeta: {
    text: 'Draft preview generated on {at}',
    description: 'Metadata line on draft print sheet.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsAttendanceSearchPersonPlaceholder: {
    text: 'Search rostered person...',
    description: 'Placeholder for searching rostered person in attendance.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsAttendanceDayPlaceholder: {
    text: 'YYYY-MM-DD',
    description: 'Placeholder for attendance day key.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceImportDialogTitle: {
    text: 'Bulk import operational attendance',
    description: 'Title of attendance CSV bulk import dialog.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsAttendanceImportCsvFormatHint: {
    text: 'Paste comma-separated rows: date,shift_code,person_kind,person_id_or_code,status',
    description: 'Format hint for attendance CSV import.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsAttendanceImportCsvPlaceholder: {
    text: '2026-11-01,shift-1,MEMBER,user-1,PRESENT\n2026-11-01,shift-1,WORKER,w-1,LATE',
    description: 'Placeholder sample CSV lines for attendance import.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsAttendanceImportRowErrors: {
    text: 'Row errors:',
    description: 'Header for CSV import row error list.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAttendanceTableColPerson: {
    text: 'Person',
    description: 'Column header for person in attendance table.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceTableColStatus: {
    text: 'Status',
    description: 'Column header for status in attendance table.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsAttendanceEmptyRoll: {
    text: 'No rostered personnel found for this place, day and shift.',
    description: 'Empty state message when no rostered personnel for roll.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsAttendanceAskCorrectionDialogTitle: {
    text: 'Ask correction for {name}',
    description: 'Title of ask correction dialog.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsAttendanceProposedStatusLabel: {
    text: 'Proposed status',
    description: 'Label for proposed status in correction dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAttendanceCorrectionReasonHint: {
    text: 'Reason (3–500 chars)',
    description: 'Label for correction reason field.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAttendanceSubmitCorrection: {
    text: 'Submit request',
    description: 'Button to submit attendance correction request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsAttendanceSelfApprovalBlocked: {
    text: 'Self-approval blocked',
    description: 'Notice shown when decider is the requester.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsAttendancePendingCorrectionsHeader: {
    text: 'Pending attendance corrections',
    description: 'Section header for pending attendance correction list.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsAttendanceNoPendingCorrections: {
    text: 'No pending corrections',
    description: 'Empty state message when no pending corrections.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsChangesRaiseDialogTitle: {
    text: 'Raise roster change request',
    description: 'Title of roster change request modal.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsChangesTypeLabel: {
    text: 'Change type',
    description: 'Label for change type select.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsChangesDateLabel: {
    text: 'Date',
    description: 'Label for change date field.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsChangesEndDateLabel: {
    text: 'End date (optional, for multi-day leave)',
    description: 'Label for multi-day leave end date.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsChangesShiftLabel: {
    text: 'Shift',
    description: 'Label for shift selector in change request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsChangesCounterpartyLabel: {
    text: 'Counterparty member',
    description: 'Label for counterparty in swap request.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsChangesCounterpartyPlaceholder: {
    text: 'Select counterparty...',
    description: 'Placeholder for selecting swap counterparty.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsChangesProxyCheckbox: {
    text: 'Raise on behalf of worker (proxy)',
    description: 'Checkbox label for proxy change requests.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsChangesWorkerLabel: {
    text: 'Worker',
    description: 'Label for worker selector in proxy request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsChangesWorkerPlaceholder: {
    text: 'Select worker...',
    description: 'Placeholder for selecting worker in proxy request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsChangesReasonHint: {
    text: 'Reason (3–500 chars)',
    description: 'Label for change reason textarea.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsChangesReasonPlaceholder: {
    text: 'Explain why this request is required...',
    description: 'Placeholder for change reason textarea.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsChangesOwnerReasonLabel: {
    text: 'Self-approval reason (mandatory for Owner, 3–500 chars)',
    description: 'Label for Owner self-approval justification.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsChangesOwnerReasonPlaceholder: {
    text: 'Owner override justification...',
    description: 'Placeholder for Owner self-approval justification.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsChangesSelfApprovalBlocked: {
    text: 'Self-approval requires Owner role',
    description: 'Notice shown when non-owner manager requests approval.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsChangesWarningsDialogTitle: {
    text: 'Auto-published with warnings',
    description: 'Title of modal showing warnings from auto-publish upon approval.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsChangesSubmitRequest: {
    text: 'Submit request',
    description: 'Button to submit roster change request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsChangesStateRejected: {
    text: 'Rejected',
    description: 'Status label for rejected roster change request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsChangesStateRejectedManager: {
    text: 'Rejected by manager',
    description: 'Status label when manager rejects roster change request.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsChangesStateRejectedCounterparty: {
    text: 'Declined by counterparty',
    description: 'Status label when counterparty declines roster change request.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsChangesProxyWorkerRequired: {
    text: 'Please select a worker for proxy request',
    description: 'Validation hint when proxy worker is not chosen.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsAttendanceImportRowErrorItem: {
    text: 'Row {row} ({field}): {message}',
    description: 'CSV row error item showing row number, field name and error message.',
    surface: 'shell',
    maxChars: 150,
  },
  NLSOpsCharsCounter: {
    text: '/500 chars (min 3)',
    description: 'Character limit note on 3-500 char text inputs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterNewDialogTitle: {
    text: 'New roster period',
    description: 'Title of new roster dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterPeriodHint: {
    text: 'Period must be at most 42 days (SO-P60).',
    description: 'Hint for max roster period length.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsRosterCurrentAssignmentsTitle: {
    text: 'Current assignments:',
    description: 'Header for current assignments in cell editor dialog.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRosterNoAssignmentsYet: {
    text: 'No assignments yet',
    description: 'Empty state inside cell editor.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterRemoveAssignmentAria: {
    text: 'Remove assignment',
    description: 'Aria label to remove an assignment from a cell.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterAddAssignmentButton: {
    text: 'Add assignment',
    description: 'Button to add assignment in cell editor.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterShiftField: {
    text: 'Shift',
    description: 'Label for shift in cell editor.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterDutyRoleField: {
    text: 'Duty role',
    description: 'Label for duty role in cell editor.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterKindField: {
    text: 'Kind',
    description: 'Label for assignment kind in cell editor.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterPersonField: {
    text: 'Person',
    description: 'Label for person in cell editor.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterSelectShiftPlaceholder: {
    text: 'Select shift...',
    description: 'Placeholder for shift select in cell editor.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterSelectDutyRolePlaceholder: {
    text: 'Select duty role...',
    description: 'Placeholder for duty role select in cell editor.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterSelectPersonPlaceholder: {
    text: 'Select person...',
    description: 'Placeholder for person select in cell editor.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRosterApplyButton: {
    text: 'Apply',
    description: 'Button to apply cell edits.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterDraftChangedNotice: {
    text: 'Draft changed elsewhere. Load latest version?',
    description: 'Notice shown when dirty draft detects newer server draft.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsRosterLoadLatestButton: {
    text: 'Load latest',
    description: 'Button to load latest server draft discarding local edits.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSetupNewDutyRole: {
    text: 'New duty role',
    description: 'Button to create a new duty role.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSetupNewShift: {
    text: 'New shift',
    description: 'Button to create a new shift.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSetupNewCoverageRule: {
    text: 'New coverage rule',
    description: 'Button to create a new coverage rule.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSetupEmptyDutyRoles: {
    text: 'No duty roles found.',
    description: 'Empty state message for duty roles.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSetupEmptyShifts: {
    text: 'No shifts found.',
    description: 'Empty state message for shifts.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSetupEmptyCoverageRules: {
    text: 'No coverage rules found for this place.',
    description: 'Empty state message for coverage rules.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSetupDutyRoleKeyPlaceholder: {
    text: 'Duty role key (e.g. resident_doctor)',
    description: 'Placeholder for duty role key.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsSetupDutyRoleLabelPlaceholder: {
    text: 'Label (e.g. Resident Doctor)',
    description: 'Placeholder for duty role label.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsSetupShiftNamePlaceholder: {
    text: 'Shift name (e.g. Day Shift)',
    description: 'Placeholder for shift name.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSetupShiftCodePlaceholder: {
    text: 'Shift code (e.g. D1)',
    description: 'Placeholder for shift code.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSetupShiftStartPlaceholder: {
    text: 'Start time (minutes 0-1439, e.g. 480 for 08:00)',
    description: 'Placeholder for shift start minute.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSetupShiftEndPlaceholder: {
    text: 'End time (minutes 0-1439, e.g. 1020 for 17:00)',
    description: 'Placeholder for shift end minute.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSetupShiftOvernightBadge: {
    text: 'Overnight shift (crosses midnight, end ≤ start)',
    description: 'Badge or text for overnight shift notice.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsSetupMinCountLabel: {
    text: 'Min headcount',
    description: 'Label for min count in coverage rule.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSetupRequiredSkillPlaceholder: {
    text: 'Required skill tag (optional)',
    description: 'Placeholder for required skill in coverage rule.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsEdit: {
    text: 'Edit',
    description: 'Edit button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsArchive: {
    text: 'Archive',
    description: 'Archive button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsArchivedBadge: {
    text: 'Archived',
    description: 'Archived status badge.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsClose: {
    text: 'Close',
    description: 'Close button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCancel: {
    text: 'Cancel',
    description: 'Cancel button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSave: {
    text: 'Save',
    description: 'Save button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCreate: {
    text: 'Create',
    description: 'Create button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterPrintPersonHeader: {
    text: 'Person',
    description: 'Person header on roster print sheet.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterPrintPeriodLabel: {
    text: 'Period:',
    description: 'Period label on roster print sheet.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterPrintEmptyPersonnel: {
    text: 'No assigned personnel for this period.',
    description: 'Empty state message on roster print sheet.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsRosterPrintOvernightTag: {
    text: '(Overnight)',
    description: 'Overnight tag in print sheet legend.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRosterPrintVersionBadge: {
    text: 'VERSION {version}',
    description: 'Version badge on print sheet.',
    surface: 'shell',
    maxChars: 25,
  },
  // ---------------------------------------------------------------------------
  // Ops Schedules & Dues (R39 A5.b, SO-P63)
  // ---------------------------------------------------------------------------
  NLSOpsSchedulesSubtabDue: {
    text: 'Due',
    description: 'Due sub-tab label in schedules screen.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesSubtabSchedules: {
    text: 'Schedules',
    description: 'Schedules sub-tab label in schedules screen.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesSubtabsAria: {
    text: 'Schedules subtabs',
    description: 'Aria label for schedules rail subtabs navigation.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesSubscriptionError: {
    text: 'Subscription error',
    description: 'Title for schedules subscription error banner.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDueEscalationWorker: {
    text: 'Worker',
    description: 'Fallback label for workforce worker in escalation.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesNoChanges: {
    text: 'No changes',
    description: 'Disabled save button text when no changes made to schedule.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesShiftOffset: {
    text: 'Starts after shift start',
    description: 'Field label for shift start offset time.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSchedulesShiftOffsetHours: {
    text: 'Starts after shift start (hours)',
    description: 'Aria label or field label for shift offset hours.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSchedulesShiftOffsetMinutes: {
    text: 'Starts after shift start (minutes)',
    description: 'Aria label or field label for shift offset minutes.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSchedulesStartTimeHours: {
    text: 'Start time (hours)',
    description: 'Aria label or field label for start time hours.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesStartTimeMinutes: {
    text: 'Start time (minutes)',
    description: 'Aria label or field label for start time minutes.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesDurationHours: {
    text: 'Duration (hours)',
    description: 'Aria label or field label for duration hours.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesDurationMinutes: {
    text: 'Duration (minutes)',
    description: 'Aria label or field label for duration minutes.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesLastDayOfMonth: {
    text: 'Last day of month',
    description: 'Option label for day -1 in dayOfMonth selector.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesMonthOfQuarterOption: {
    text: 'Month {month}',
    description: 'Option label for quarterly month selector.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesDueNow: {
    text: 'Due now',
    description: 'Toggle button label for due now list.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesHistory: {
    text: 'History',
    description: 'Toggle button label for history dues list.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesHistory7Days: {
    text: 'Last 7 days',
    description: 'History range option for 7 days.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesHistory30Days: {
    text: 'Last 30 days',
    description: 'History range option for 30 days.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesEmptyDue: {
    text: 'No dues found for this filter.',
    description: 'Empty state message when no dues match filters.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSchedulesEmptySchedules: {
    text: 'No schedules configured yet.',
    description: 'Empty state message when no schedules exist.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSchedulesLoading: {
    text: 'Loading schedules…',
    description: 'Loading indicator text for schedules.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsDuesLoading: {
    text: 'Loading dues…',
    description: 'Loading indicator text for dues.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSchedulesFilterPlace: {
    text: 'Place',
    description: 'Filter dropdown label for place.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesFilterStatus: {
    text: 'Status',
    description: 'Filter dropdown label for status.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesFilterTemplate: {
    text: 'Template',
    description: 'Filter dropdown label for template.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesFilterSchedule: {
    text: 'Schedule',
    description: 'Filter dropdown label for schedule.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesAllPlaces: {
    text: 'All places',
    description: 'Option to select all places in filter.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesAllStatuses: {
    text: 'All statuses',
    description: 'Option to select all statuses in filter.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesAllTemplates: {
    text: 'All templates',
    description: 'Option to select all templates in filter.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesAllSchedules: {
    text: 'All schedules',
    description: 'Option to select all schedules in filter.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesClearScheduleFilter: {
    text: 'Clear filter',
    description: 'Button label to clear schedule filter.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesRecordIt: {
    text: 'Record it',
    description: 'Action button to open register record form for a due.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesSkip: {
    text: 'Skip',
    description: 'Action button to skip a due.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesSkipDialogTitle: {
    text: 'Skip due',
    description: 'Title of dialog to skip a due.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesSkipReasonLabel: {
    text: 'Reason for skipping',
    description: 'Label for skip reason textarea.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsSchedulesSkipReasonPlaceholder: {
    text: 'Provide reason for skipping this due (3–500 chars)',
    description: 'Placeholder for skip reason textarea.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSchedulesSkipReasonCharCount: {
    text: '{current}/{max} characters',
    description: 'Character counter for skip reason.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSchedulesSkipConfirm: {
    text: 'Skip due',
    description: 'Confirm button label in skip due dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesSkipCancel: {
    text: 'Cancel',
    description: 'Cancel button label in skip due dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesSkipReasonLengthError: {
    text: 'Reason must be between 3 and 500 characters.',
    description: 'Validation error when skip reason is too short or too long.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsSchedulesNewSchedule: {
    text: 'New schedule',
    description: 'Button to open new schedule dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesEditSchedule: {
    text: 'Edit schedule',
    description: 'Title for edit schedule dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesCadence: {
    text: 'Cadence',
    description: 'Field label for cadence kind.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesCadencePerShift: {
    text: 'Per shift',
    description: 'Cadence option label for per shift.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesCadenceDaily: {
    text: 'Daily',
    description: 'Cadence option label for daily.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesCadenceWeekly: {
    text: 'Weekly',
    description: 'Cadence option label for weekly.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesCadenceMonthly: {
    text: 'Monthly',
    description: 'Cadence option label for monthly.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesCadenceQuarterly: {
    text: 'Quarterly',
    description: 'Cadence option label for quarterly.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesCadenceYearly: {
    text: 'Yearly',
    description: 'Cadence option label for yearly.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesCadenceEveryNDays: {
    text: 'Every N days',
    description: 'Cadence option label for every N days.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesPlaces: {
    text: 'Places',
    description: 'Field label for places multi-select.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesWindow: {
    text: 'Window',
    description: 'Header or label for window section.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesDutyRole: {
    text: 'Assignee duty role',
    description: 'Field label for assignee duty role.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSchedulesGraceMinutes: {
    text: 'Grace period (minutes)',
    description: 'Field label for grace minutes.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSchedulesEscalateMinutes: {
    text: 'Escalate every (minutes)',
    description: 'Field label for escalation interval minutes.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsSchedulesPause: {
    text: 'Pause',
    description: 'Row action button to pause schedule.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesResume: {
    text: 'Resume',
    description: 'Row action button to resume schedule.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesArchive: {
    text: 'Archive',
    description: 'Row action button to archive schedule.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesViewDues: {
    text: 'View dues',
    description: 'Row action button to view dues for this schedule.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesArchiveDialogTitle: {
    text: 'Archive schedule',
    description: 'Title of confirm dialog to archive schedule.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSchedulesArchiveConfirmNotice: {
    text: 'Archiving this schedule removes all dues that have not yet opened. Open and past dues remain in history.',
    description: 'Notice in archive schedule confirm dialog.',
    surface: 'shell',
    maxChars: 160,
  },
  NLSOpsSchedulesArchiveConfirm: {
    text: 'Archive schedule',
    description: 'Confirm button in archive schedule dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesArchiveCancel: {
    text: 'Cancel',
    description: 'Cancel button in archive schedule dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsSchedulesSummaryPreview: {
    text: 'Cadence preview: {summary}',
    description: 'Live preview of cadence summary in schedule dialog.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsSchedulesPlacesCount: {
    text: '{count} places',
    description: 'Count badge for multiple places.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesSinglePlace: {
    text: '1 place',
    description: 'Count badge for single place.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesShift: {
    text: 'Shift',
    description: 'Field label for shift picker.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesStartTime: {
    text: 'Start time',
    description: 'Field label for window start time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesDuration: {
    text: 'Duration (minutes)',
    description: 'Field label for window duration in minutes.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSchedulesIsoWeekday: {
    text: 'Day of week',
    description: 'Field label for weekly ISO weekday.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesDayOfMonth: {
    text: 'Day of month',
    description: 'Field label for monthly day of month.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesMonthOfQuarter: {
    text: 'Month of quarter',
    description: 'Field label for quarterly month of quarter.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesMonthOfYear: {
    text: 'Month',
    description: 'Field label for yearly month.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSchedulesIntervalDays: {
    text: 'Repeat every (days)',
    description: 'Field label for every-N-days interval.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsSchedulesAnchorDate: {
    text: 'Anchor date',
    description: 'Field label for every-N-days anchor date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsScheduleDatePlaceholder: {
    text: 'YYYY-MM-DD',
    description: 'Date placeholder for schedules.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsScheduleTimePlaceholder: {
    text: 'HH:mm',
    description: 'Time placeholder for schedules.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsSchedulesSave: {
    text: 'Save schedule',
    description: 'Submit button when editing schedule.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesCreate: {
    text: 'Create schedule',
    description: 'Submit button when creating schedule.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsSchedulesCancel: {
    text: 'Cancel',
    description: 'Cancel button in schedule dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDueEscalationHop: {
    text: 'Hop {hop}: {level} ({names})',
    description: 'Escalation hop summary for missed due.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsDueEscalationHopNoNames: {
    text: 'Hop {hop}: {level}',
    description: 'Escalation hop summary when no names resolved.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDueNextEscalation: {
    text: 'Next escalation at {time}',
    description: 'Next escalation time line for missed due.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsDueLevelOnShift: {
    text: 'On Shift',
    description: 'Escalation level label for on shift.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDueLevelOnCallPrimary: {
    text: 'On-call Primary',
    description: 'Escalation level label for primary on-call.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDueLevelOnCallBackup: {
    text: 'On-call Backup',
    description: 'Escalation level label for backup on-call.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDueLevelPlaceOwner: {
    text: 'Place Owner',
    description: 'Escalation level label for place owner.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsDueStatusOpen: {
    text: 'Open',
    description: 'Status pill text for OPEN due.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDueStatusDone: {
    text: 'Done',
    description: 'Status pill text for DONE due.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDueStatusLateDone: {
    text: 'Late Done',
    description: 'Status pill text for LATE_DONE due.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDueStatusMissed: {
    text: 'Missed',
    description: 'Status pill text for MISSED due.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDueStatusSkipped: {
    text: 'Skipped',
    description: 'Status pill text for SKIPPED due.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsDueSkippedReason: {
    text: 'Reason: {reason}',
    description: 'Display of skip reason on skipped due row.',
    surface: 'shell',
    maxChars: 520,
  },
  NLSOpsScheduleStatusActive: {
    text: 'Active',
    description: 'Status badge text for active schedule.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsScheduleStatusPaused: {
    text: 'Paused',
    description: 'Status badge text for paused schedule.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsScheduleStatusArchived: {
    text: 'Archived',
    description: 'Status badge text for archived schedule.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRecordRequestTemplateNotApproved: {
    text: 'Requested template is not approved. Opened form with defaults.',
    description: 'Notice when requested template from schedule due is unapproved.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsRecordRequestPlaceNotAllowed: {
    text: 'Requested place is not in your allowed places. Opened form with defaults.',
    description: 'Notice when requested place from schedule due is not in caller list.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsCadencePerShiftWhole: {
    text: 'Shift: {shift}, Whole shift',
    description: 'Cadence summary for per-shift whole shift.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsCadencePerShiftPart: {
    text: 'Shift: {shift}, +{start} for {duration}m',
    description: 'Cadence summary for per-shift partial window.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsCadencePerShiftWholeNoShift: {
    text: 'Whole shift',
    description: 'Cadence summary for whole shift without named shift.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCadenceDaily: {
    text: 'Daily, {timeRange}',
    description: 'Cadence summary for daily cadence.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCadenceWeekly: {
    text: 'Every {dayName}, {timeRange}',
    description: 'Cadence summary for weekly cadence.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCadenceMonthly: {
    text: 'Monthly on day {day}, {timeRange}',
    description: 'Cadence summary for monthly day of month.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCadenceMonthlyLastDay: {
    text: 'Monthly on last day, {timeRange}',
    description: 'Cadence summary for monthly last day.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCadenceQuarterly: {
    text: 'Quarterly, month {month} day {day}, {timeRange}',
    description: 'Cadence summary for quarterly day of month.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsCadenceQuarterlyLastDay: {
    text: 'Quarterly, month {month} last day, {timeRange}',
    description: 'Cadence summary for quarterly last day.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsCadenceYearly: {
    text: 'Yearly on {day} {monthName}, {timeRange}',
    description: 'Cadence summary for yearly day of month.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsCadenceYearlyLastDay: {
    text: 'Yearly in {monthName} (last day), {timeRange}',
    description: 'Cadence summary for yearly last day.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsCadenceEveryNDays: {
    text: 'Every {interval} days from {anchorDate}, {timeRange}',
    description: 'Cadence summary for every N days.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsScheduleErrorTemplateRequired: {
    text: 'Select a template.',
    description: 'Validation error when template is not selected.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsScheduleErrorPlacesRequired: {
    text: 'Select at least one place.',
    description: 'Validation error when no places are selected.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsScheduleErrorPlacesMax: {
    text: 'Cannot select more than 50 places.',
    description: 'Validation error when exceeding place limit.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsScheduleErrorShiftRequired: {
    text: 'Select a shift.',
    description: 'Validation error when shift is required but missing.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsScheduleErrorStartMinuteRange: {
    text: 'Start minute must be between 0 and 1439.',
    description: 'Validation error when start minute is out of range.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsScheduleErrorDurationRange: {
    text: 'Duration must be between 1 and {max} minutes.',
    description: 'Validation error when duration is out of range.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsScheduleErrorWeekdayRange: {
    text: 'Select a valid day of the week (1–7).',
    description: 'Validation error when weekly ISO weekday is out of range.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsScheduleErrorDayOfMonthRange: {
    text: 'Day of month must be 1–28 or -1 (last day).',
    description: 'Validation error when day of month is out of range.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsScheduleErrorMonthOfQuarterRange: {
    text: 'Month of quarter must be 1, 2, or 3.',
    description: 'Validation error when month of quarter is out of range.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsScheduleErrorMonthRange: {
    text: 'Month must be between 1 and 12.',
    description: 'Validation error when yearly month is out of range.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsScheduleErrorIntervalRange: {
    text: 'Interval must be between 2 and 365 days.',
    description: 'Validation error when every-N-days interval is out of range.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsScheduleErrorAnchorDateRequired: {
    text: 'Anchor date is required.',
    description: 'Validation error when anchor date is missing.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsScheduleErrorDutyRoleRequired: {
    text: 'Select an assignee duty role.',
    description: 'Validation error when assignee duty role is missing.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsScheduleErrorGraceRange: {
    text: 'Grace period must be between 0 and 240 minutes.',
    description: 'Validation error when grace minutes is out of range.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsScheduleErrorEscalateRange: {
    text: 'Escalation interval must be between 5 and 1440 minutes.',
    description: 'Validation error when escalate interval is out of range.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsScheduleErrorSkipReasonLength: {
    text: 'Skip reason must be between 3 and 500 characters.',
    description: 'Validation error when skip reason length is invalid.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsRequestsSubtabBoard: {
    text: 'Board',
    description: 'Requests board subtab.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsSubtabMyRequests: {
    text: 'My requests',
    description: 'My requests subtab.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsSubtabKinds: {
    text: 'Kinds',
    description: 'Request kinds admin subtab.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsRaiseBtn: {
    text: 'Raise request',
    description: 'Button to open raise request dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsColOpen: {
    text: 'Open',
    description: 'Requests board column for open requests.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsColAcknowledged: {
    text: 'Acknowledged',
    description: 'Requests board column for acknowledged requests.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsColInProgress: {
    text: 'In progress',
    description: 'Requests board column for in-progress and reopened requests.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsColDone: {
    text: 'Done',
    description: 'Requests board column for done requests.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsColVerified: {
    text: 'Verified',
    description: 'Requests board column for verified requests.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsFilterPlace: {
    text: 'All places',
    description: 'Filter option for all places.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsFilterKind: {
    text: 'All kinds',
    description: 'Filter option for all kinds.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsFilterAssignee: {
    text: 'All assignees',
    description: 'Filter option for all assignees.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsFilterAssigneeMe: {
    text: 'Assigned to me',
    description: 'Filter option for assigned to current user.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsFilterShowCancelled: {
    text: 'Show cancelled',
    description: 'Checkbox label to toggle display of cancelled requests.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsPillOverdue: {
    text: 'Overdue',
    description: 'Badge pill indicating request is overdue.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsRequestsPillEscalated: {
    text: 'Escalated',
    description: 'Badge pill indicating request is escalated.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsPillRestricted: {
    text: 'Restricted',
    description: 'Badge pill indicating request is restricted.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsPillReopened: {
    text: 'Reopened',
    description: 'Badge pill indicating request is reopened.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsEmpty: {
    text: 'No requests',
    description: 'Empty state message when no requests are found.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsDetailTitle: {
    text: 'Request details',
    description: 'Title for request detail dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsActAcknowledge: {
    text: 'Acknowledge',
    description: 'Action button to acknowledge request.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsActStart: {
    text: 'Start work',
    description: 'Action button to start request work.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsActAssign: {
    text: 'Assign',
    description: 'Action button to assign request.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsActMarkDone: {
    text: 'Mark done',
    description: 'Action button to mark request done.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsActVerify: {
    text: 'Verify',
    description: 'Action button to open verification dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsActReopen: {
    text: 'Reopen',
    description: 'Action button to open reopen dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsActCancel: {
    text: 'Cancel request',
    description: 'Action button to open cancel dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsDialogApprove: {
    text: 'Approve',
    description: 'Button to approve request verification.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDialogReject: {
    text: 'Reject',
    description: 'Button to reject request verification.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsReason: {
    text: 'Reason',
    description: 'Label for reason input field.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsReasonMinMax: {
    text: 'Reason must be between 3 and 500 characters.',
    description: 'Validation message for reason length.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsRequestsSelfVerifyStamp: {
    text: 'Owner self-verification requires a justification reason (3-500 characters).',
    description: 'Notice when owner is self-verifying completed work.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsRequestsTimeline: {
    text: 'Timeline',
    description: 'Heading for request events timeline.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsPhotos: {
    text: 'Photos',
    description: 'Heading for request photos section.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDonePhotos: {
    text: 'Completion photos',
    description: 'Heading for completion photos section.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsEscalationHops: {
    text: 'Escalation history',
    description: 'Heading for escalation hops list.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsAssignDialogTitle: {
    text: 'Assign request',
    description: 'Title for assign dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsSelectAssignee: {
    text: 'Select member',
    description: 'Placeholder for assignee selection.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsSubtabsAria: {
    text: 'Request subtabs',
    description: 'Aria label for requests subtab bar.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsRetryPhotos: {
    text: 'Retry photos',
    description: 'Button to retry uploading failed photos.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsPhotoUploadFailed: {
    text: 'Photo upload failed. You can retry.',
    description: 'Error banner when photo upload fails.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsRequestsPatientNotice: {
    text: 'Patient-linked requests cannot attach photos to protect patient privacy.',
    description: 'Privacy notice for patient-linked requests.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsRequestsTitleLabel: {
    text: 'Title',
    description: 'Label for request title.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDetailsLabel: {
    text: 'Details',
    description: 'Label for request details.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsPlaceLabel: {
    text: 'Place',
    description: 'Label for request place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsKindLabel: {
    text: 'Kind',
    description: 'Label for request kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsForWorker: {
    text: 'For a worker',
    description: 'Option to raise request on behalf of a worker.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsSelectWorker: {
    text: 'Select worker',
    description: 'Placeholder to select worker for proxy raise.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsAddCustomKind: {
    text: 'Add custom kind',
    description: 'Button to add custom request kind.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsEditKind: {
    text: 'Edit kind',
    description: 'Title or button to edit request kind.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsKindsFilterArchived: {
    text: 'Show archived',
    description: 'Checkbox to show archived request kinds.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsKindKey: {
    text: 'Kind key',
    description: 'Label for request kind key.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsKindLabelInput: {
    text: 'Display label',
    description: 'Label for request kind display name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsAckMinutes: {
    text: 'Acknowledge target (min)',
    description: 'Target minutes to acknowledge.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsDueMinutes: {
    text: 'Resolution target (min)',
    description: 'Target minutes to resolve.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsEscalateEveryMinutes: {
    text: 'Escalate every (min)',
    description: 'Minutes between escalation hops.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsDutyRole: {
    text: 'Owning duty role',
    description: 'Duty role assigned to own this kind.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsVerifyRequired: {
    text: 'Verification required',
    description: 'Toggle whether kind requires verification.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsVerifierRole: {
    text: 'Verifier sector role',
    description: 'Sector role authorized to verify this kind.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsRestrictedKind: {
    text: 'Restricted visibility',
    description: 'Toggle whether kind is restricted.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsViewerRoles: {
    text: 'Viewer sector roles',
    description: 'Sector roles allowed to view restricted requests.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsRequestsPhotosEnabled: {
    text: 'Photos enabled',
    description: 'Toggle whether kind allows photos.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRequestsKindLockedNotice: {
    text: 'Built-in restricted or patient-linked kinds cannot have privacy or photo rules modified.',
    description: 'Explanation why switches are locked on built-in kinds.',
    surface: 'shell',
    maxChars: 95,
  },
  NLSOpsRequestsArchiveKind: {
    text: 'Archive',
    description: 'Button to archive kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsUnarchiveKind: {
    text: 'Unarchive',
    description: 'Button to unarchive kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsSave: {
    text: 'Save',
    description: 'Button to save kind.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsClose: {
    text: 'Close',
    description: 'Button to close dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsCancel: {
    text: 'Cancel',
    description: 'Button to cancel dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDone: {
    text: 'Done',
    description: 'Button to confirm done in dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsAssign: {
    text: 'Assign',
    description: 'Button to assign request in dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsNone: {
    text: 'None',
    description: 'Option label for none.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDefaultVerifier: {
    text: 'Default (REQUEST_VERIFIER)',
    description: 'Option label for default verifier role.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsRequestsArchivedLabel: {
    text: '(Archived)',
    description: 'Label indicating kind is archived.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDetailRaiser: {
    text: 'Raiser',
    description: 'Label for request raiser.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDetailAssignee: {
    text: 'Assignee',
    description: 'Label for request assignee.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDetailTimes: {
    text: 'Times',
    description: 'Label for request times.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDetailUnassigned: {
    text: 'Unassigned',
    description: 'Label when request has no assignee.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsUnassigned: {
    text: 'Unassigned',
    description: 'Label when request is unassigned.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsRaisedLabel: {
    text: 'Raised',
    description: 'Prefix for request raised timestamp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsAckDueLabel: {
    text: 'Ack due',
    description: 'Prefix for request ack due timestamp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDueLabel: {
    text: 'Due',
    description: 'Prefix for request due timestamp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsAckedLabel: {
    text: 'Acked',
    description: 'Prefix for request acknowledged timestamp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsDoneLabel: {
    text: 'Done',
    description: 'Prefix for request done timestamp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsVerifiedLabel: {
    text: 'Verified',
    description: 'Prefix for request verified timestamp.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsRequestsCompletionPhotos: {
    text: 'Completion photos',
    description: 'Title for completion photos section.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOverviewTitle: {
    text: 'Overview',
    description: 'Overview page title.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewCustomize: {
    text: 'Customize',
    description: 'Button to enter customize mode on overview page.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewDoneEditing: {
    text: 'Done',
    description: 'Button to exit customize mode or finish editing.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewSave: {
    text: 'Save',
    description: 'Save customized overview configuration.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewDiscard: {
    text: 'Discard',
    description: 'Discard unsaved overview modifications.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewSaving: {
    text: 'Saving...',
    description: 'Saving state text for overview action bar.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewSaveBanner: {
    text: 'Changes are saved for everyone when you press Save',
    description: 'Disclaimer note shown in customize bar.',
    surface: 'common',
    maxChars: 80,
  },
  NLSOverviewHiddenCount: {
    text: '{count} hidden · ',
    description: 'Prefix text showing number of hidden sections.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewEditBrief: {
    text: 'Edit brief',
    description: 'Button to edit workspace brief inline.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBriefPlaceholder: {
    text: 'Describe what this workspace is for, current focus, or key milestones...',
    description: 'Placeholder for workspace brief textarea in customize mode.',
    surface: 'common',
    maxChars: 120,
  },
  NLSOverviewNeedYou: {
    text: '{count} need you',
    description: 'Pill text indicating attention items needing the user.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewGoalProgressRing: {
    text: 'goal progress',
    description: 'Caption under goal progress percentage in header ring.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPulseDone7d: {
    text: 'Done in 7 days',
    description: 'Pulse card metric label for items done in last 7 days.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewPulseOverdue: {
    text: 'Overdue items',
    description: 'Pulse card metric label for overdue checklist items.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewPulseActiveThisWeek: {
    text: 'Active this week',
    description: 'Pulse card metric label for active members this week.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewPulseBudgetUsed: {
    text: 'Budget used',
    description: 'Pulse card metric label for budget consumption.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewPulseOpenItems: {
    text: 'Open items',
    description: 'Pulse card metric label for open assigned items.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewPeopleTitle: {
    text: 'People',
    description: 'Title for people section.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewPeopleSubtitle: {
    text: 'Assigned checklist items',
    description: 'Subtitle for people section.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewTeamAnalysisLink: {
    text: 'Team analysis →',
    description: 'Link pointing to Team tab in Analysis hub.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewWorkAnalysisLink: {
    text: 'Work analysis →',
    description: 'Link pointing to Work tab in Analysis hub.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewActivityAllLink: {
    text: 'All activity →',
    description: 'Link pointing to full activity log.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewLegendDone: {
    text: 'done in 7 days',
    description: 'Legend label for completed items.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewLegendOpen: {
    text: 'open',
    description: 'Legend label for open items.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewLegendOverdue: {
    text: 'overdue',
    description: 'Legend label for overdue items.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewLegendReliability: {
    text: 'helper reliability',
    description: 'Legend label for helper reliability score.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewViewerYourWeek: {
    text: 'Your week',
    description: 'Card title for viewer self-stats.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewViewerTeam: {
    text: 'Team',
    description: 'Card title for viewer team aggregate stats.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewViewerNotice: {
    text: 'Your numbers are only visible to you and team leads',
    description: 'Privacy notice for viewers and helpers.',
    surface: 'common',
    maxChars: 70,
  },
  NLSOverviewAttentionTitle: {
    text: 'Needs attention',
    description: 'Title for needs attention section.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewGoalsDatesTitle: {
    text: 'Goals & dates',
    description: 'Title for goals and upcoming dates section.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewActivityTitle: {
    text: 'Activity',
    description: 'Title for activity section.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewModulesTitle: {
    text: 'Modules',
    description: 'Title for modules section.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewStockReorderPill: {
    text: '{count} items below reorder level',
    description: 'Stock status text for low stock items.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewStockTotalPill: {
    text: '{count} tracked',
    description: 'Total tracked stock items caption.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewBudgetUsedPill: {
    text: '{percent}% used',
    description: 'Budget percentage consumed caption.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewTeachingTitle: {
    text: 'Teaching / Training',
    description: 'Title for education module card.',
    surface: 'common',
    maxChars: 35,
  },
  NLSOverviewLockBanner: {
    text: '{name} is editing right now',
    description: 'Banner warning when another user holds the editing lock.',
    surface: 'common',
    maxChars: 60,
  },
  NLSOverviewUnsavedTitle: {
    text: 'Discard unsaved changes?',
    description: 'Title of confirmation dialog when leaving with unsaved changes.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewUnsavedMessage: {
    text: 'You have unsaved changes. Are you sure you want to discard them?',
    description: 'Body of confirmation dialog when leaving with unsaved changes.',
    surface: 'common',
    maxChars: 90,
  },
  NLSOverviewUnsavedKeepEditing: {
    text: 'Keep editing',
    description: 'Button to stay in edit mode.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewUnsavedDiscard: {
    text: 'Discard changes',
    description: 'Button to discard unsaved changes.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPersonalSpaceTitle: {
    text: 'Overview is for team workspaces',
    description: 'Heading when accessing overview in a personal space.',
    surface: 'common',
    maxChars: 50,
  },
  NLSOverviewPersonalSpaceDesc: {
    text: 'Personal spaces do not have Team/Public overview pages yet.',
    description: 'Explanation that personal workspaces lack overview pages.',
    surface: 'common',
    maxChars: 80,
  },
  NLSOverviewBackToDashboard: {
    text: 'Back to dashboard',
    description: 'Button returning to dashboard from personal space empty state.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewPersonSheetClose: {
    text: 'Close',
    description: 'Close button label for person side sheet.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewPersonSheetOpenProfile: {
    text: 'Open profile',
    description: 'Button in person sheet to open profile.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPersonSheetMessage: {
    text: 'Message',
    description: 'Button in person sheet to message teammate.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewPersonSheetReassign: {
    text: 'Reassign…',
    description: 'Button in person sheet to reassign items.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPersonSheetLast7Days: {
    text: 'Last 7 days',
    description: 'Label for 7-day sparkline in person sheet.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPersonSheetAssignedItems: {
    text: 'Assigned items',
    description: 'Heading for assigned items table in person sheet.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewPersonSheetItemHeader: {
    text: 'Item',
    description: 'Table header for item name.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewPersonSheetDueHeader: {
    text: 'Due',
    description: 'Table header for item due date.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewPersonSheetStatusHeader: {
    text: 'Status',
    description: 'Table header for item status.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewPersonSheetActionHeader: {
    text: 'Action',
    description: 'Table header for item action.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewPersonSheetEmptyItems: {
    text: 'No assigned items',
    description: 'Empty message in assigned items table.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewUntitledItem: {
    text: 'Untitled item',
    description: 'Fallback label for untitled items.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewReassignSuccess: {
    text: 'Item reassigned.',
    description: 'Toast message on successful item reassignment.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewReassignError: {
    text: 'Failed to reassign item.',
    description: 'Toast message on item reassignment error.',
    surface: 'common',
    maxChars: 50,
  },
  NLSOverviewStockTitle: {
    text: 'Stock',
    description: 'Title of the stock module card.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBudgetTitle: {
    text: 'Budget',
    description: 'Title of the budget module card.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewModuleOpenLink: {
    text: 'Open →',
    description: 'Link text to open a module page.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewTeachingAttendanceToday: {
    text: 'Attendance today',
    description: 'Label for attendance today metric.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewTeachingClassesToday: {
    text: 'Classes today',
    description: 'Label for classes today metric.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewTeachingAssessmentsDue: {
    text: 'Assessments due',
    description: 'Label for assessments due metric.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewPersonSheetReliabilityTitle: {
    text: 'Reliability ★ {score}',
    description: 'Heading for helper reliability panel in person sheet.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewPersonSheetReliabilityDesc: {
    text: 'From completed items, scans and blockers over 30 days.',
    description: 'Description of helper reliability calculation.',
    surface: 'common',
    maxChars: 70,
  },
  NLSOverviewSectionMoveUp: {
    text: 'Move {name} up',
    description: 'Aria label for move section up button.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewSectionMoveDown: {
    text: 'Move {name} down',
    description: 'Aria label for move section down button.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewSectionHide: {
    text: 'Hide {name}',
    description: 'Aria label for hide section button.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewSectionShow: {
    text: 'Show {name}',
    description: 'Aria label for show section button.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewFlagOverloaded: {
    text: 'Overloaded',
    description: 'Flag pill text when person has excessive open workload.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewFlagQuiet: {
    text: 'Quiet',
    description: 'Flag pill text when person has been inactive with open items.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewCustomSectionTitle: {
    text: 'Custom section',
    description: 'Default title for custom section badge.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewSaveError: {
    text: 'Overview save failed. Try again.',
    description: 'Error toast when saving overview fails.',
    surface: 'common',
    maxChars: 45,
  },
  NLSOverviewTeamWorkspaceSubtitle: {
    text: 'Team workspace',
    description: 'Subtitle suffix for team workspace overview.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewSectionShowBtn: {
    text: 'Show',
    description: 'Button text to unhide a section in edit mode.',
    surface: 'common',
    maxChars: 12,
  },
  NLSOverviewSectionHideBtn: {
    text: 'Hide',
    description: 'Button text to hide a section in edit mode.',
    surface: 'common',
    maxChars: 12,
  },
  NLSOverviewRoleMember: {
    text: 'Member',
    description: 'Fallback role label for a workspace member.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewRoleViewer: {
    text: 'Viewer',
    description: 'Fallback role label for a workspace viewer.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewRoleLead: {
    text: 'Lead',
    description: 'Fallback role label for a workspace lead.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewYouFallback: {
    text: 'You',
    description: 'Fallback display name for current user.',
    surface: 'common',
    maxChars: 10,
  },
  NLSOverviewGoalDuePrefix: {
    text: 'due {date}',
    description: 'Prefix for goal due date.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewUpcomingDaysIn: {
    text: 'in {days}d',
    description: 'Relative days remaining for upcoming date.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewHeaderTitle: {
    text: 'Header',
    description: 'Section toolbar label for Header.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewPulseTitle: {
    text: 'Pulse',
    description: 'Section toolbar label for Pulse.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewPillBlocked: {
    text: 'Blocked',
    description: 'Status pill label for blocked items.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewPhoneRefresh: {
    text: 'Refresh',
    description: 'Pull to refresh button label for screen readers and keyboard users.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewPhoneRefreshing: {
    text: 'Refreshing…',
    description: 'Accessible status label while refreshing overview.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPhoneCustomizeWebHint: {
    text: 'Customize this page on the web.',
    description: 'Hint shown at the bottom of phone overview for leads.',
    surface: 'common',
    maxChars: 50,
  },
  NLSOverviewPhoneAnalysisShortLink: {
    text: 'Analysis',
    description: 'Short link to Analysis on phone overview cards.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewPhoneLegendDoneShort: {
    text: 'done',
    description: 'Compact column header for done items in phone people row.',
    surface: 'common',
    maxChars: 12,
  },
  NLSOverviewPhoneLegendLateShort: {
    text: 'late',
    description: 'Compact column header for overdue items in phone people row.',
    surface: 'common',
    maxChars: 12,
  },
  NLSOverviewPhoneActiveNow: {
    text: 'Active now',
    description: 'Relative activity label when active within a minute.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewHelperMyWeek: {
    text: 'My week',
    description: 'Title for helper personal stats card on phone overview.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewHelperReliabilityPill: {
    text: '★ {score} reliability',
    description: 'Pill showing helper reliability score.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewHelperTeamPulse: {
    text: 'Team pulse',
    description: 'Title for helper team pulse card on phone overview.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewHelperYourOverdue: {
    text: 'Your overdue',
    description: 'Title for helper overdue items card on phone overview.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewHelperOpenInTasks: {
    text: 'Open in Tasks',
    description: 'Button on helper overdue card to open Tasks tab.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPersonSheetProfileShort: {
    text: 'Profile',
    description: 'Short profile button label in phone person bottom sheet.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewPersonSheetAssignedItemHeader: {
    text: 'Assigned item',
    description: 'Table header for assigned item in phone person bottom sheet.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPillLate: {
    text: 'Late',
    description: 'Short status pill label for overdue item in phone person bottom sheet.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewPillOpen: {
    text: 'Open',
    description: 'Capitalized status pill label for open item in phone person bottom sheet.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewAddSection: {
    text: '+ Add section',
    description: 'Tile button label to add a new custom section in customize mode.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewAddSectionDialogTitle: {
    text: 'Add a section',
    description: 'Title for the add custom section dialog.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewEditSectionDialogTitle: {
    text: 'Edit section',
    description: 'Title for the edit custom section dialog.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewSectionSubtitle: {
    text: 'Name it, then add blocks. Each block can be full, half or third width.',
    description: 'Subtitle instruction inside custom section dialog.',
    surface: 'common',
    maxChars: 120,
  },
  NLSOverviewSectionTitleLabel: {
    text: 'Section title',
    description: 'Field label for custom section title.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewSectionTitlePlaceholder: {
    text: 'Onboarding for new crew',
    description: 'Placeholder for section title.',
    surface: 'common',
    maxChars: 50,
  },
  NLSOverviewSectionTitleRequired: {
    text: 'Section title is required.',
    description: 'Validation error when section title is empty.',
    surface: 'common',
    maxChars: 45,
  },
  NLSOverviewSectionEdit: {
    text: 'Edit {name}',
    description: 'Accessible action to edit a custom section.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewSectionEditBtn: {
    text: 'Edit',
    description: 'Button text in section toolbar to edit a custom section.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewSectionDeleteBtn: {
    text: 'Delete section',
    description: 'Button text to delete a custom section in edit mode.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewBlockPickerGroupText: {
    text: 'Text',
    description: 'Block picker category title for text blocks.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockPickerGroupMedia: {
    text: 'Media',
    description: 'Block picker category title for media blocks.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockPickerGroupCards: {
    text: 'Cards',
    description: 'Block picker category title for card blocks.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockPickerGroupLiveData: {
    text: 'Live data',
    description: 'Block picker category title for live data blocks.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockKindText: {
    text: 'Text',
    description: 'Block kind title for text.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockKindTextDesc: {
    text: 'Paragraphs, bold, links',
    description: 'Block kind description for text.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewBlockKindCallout: {
    text: 'Callout',
    description: 'Block kind title for callout.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockKindCalloutDesc: {
    text: 'Note, warning or win',
    description: 'Block kind description for callout.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewBlockKindImage: {
    text: 'Image',
    description: 'Block kind title for image.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockKindImageDesc: {
    text: 'Upload or https URL',
    description: 'Block kind description for image.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewBlockKindEmbed: {
    text: 'Embed',
    description: 'Block kind title for embed.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockKindEmbedDesc: {
    text: 'YouTube, Vimeo, Loom, Figma, Docs',
    description: 'Block kind description for embed.',
    surface: 'common',
    maxChars: 50,
  },
  NLSOverviewBlockKindLinks: {
    text: 'Link cards',
    description: 'Block kind title for links.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockKindLinksDesc: {
    text: 'Goals, boards, forms, files, URLs',
    description: 'Block kind description for links.',
    surface: 'common',
    maxChars: 50,
  },
  NLSOverviewBlockKindPeople: {
    text: 'People cards',
    description: 'Block kind title for people.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockKindPeopleDesc: {
    text: 'Pick teammates, add a note',
    description: 'Block kind description for people.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewBlockKindChart: {
    text: 'Chart',
    description: 'Block kind title for chart.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockKindChartDesc: {
    text: 'Trend or metric from chart library',
    description: 'Block kind description for chart.',
    surface: 'common',
    maxChars: 50,
  },
  NLSOverviewMaxSectionsLimit: {
    text: 'Maximum of {count} custom sections allowed.',
    description: 'Limit reached message for custom sections.',
    surface: 'common',
    maxChars: 60,
  },
  NLSOverviewMaxBlocksLimit: {
    text: 'Maximum of {count} blocks per section allowed.',
    description: 'Limit reached message for blocks per section.',
    surface: 'common',
    maxChars: 60,
  },
  NLSOverviewBlockWidthLabel: {
    text: 'Width',
    description: 'Field label for block width choice.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewBlockWidthFull: {
    text: 'Full',
    description: 'Full width option for custom section block.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewBlockWidthHalf: {
    text: 'Half',
    description: 'Half width option for custom section block.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewBlockWidthThird: {
    text: 'Third',
    description: 'Third width option for custom section block.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewBlockMoveUp: {
    text: 'Move block up',
    description: 'Aria label to move block up.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewBlockMoveDown: {
    text: 'Move block down',
    description: 'Aria label to move block down.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewBlockDelete: {
    text: 'Delete block',
    description: 'Aria label to delete block.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewSaveSection: {
    text: 'Apply',
    description: 'Button text to apply custom section changes in dialog.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewCancel: {
    text: 'Cancel',
    description: 'Cancel button label.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewClose: {
    text: 'Close',
    description: 'Close button label.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewTextMarkdownLabel: {
    text: 'Markdown content',
    description: 'Field label for text block markdown.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewTextCharCount: {
    text: '{current} / {max} characters',
    description: 'Character counter for text block markdown.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewCalloutToneLabel: {
    text: 'Tone',
    description: 'Field label for callout tone.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewCalloutToneNote: {
    text: 'Note',
    description: 'Tone option note for callout block.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewCalloutToneWarning: {
    text: 'Warning',
    description: 'Tone option warning for callout block.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewCalloutToneWin: {
    text: 'Win',
    description: 'Tone option win for callout block.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewCalloutTitleLabel: {
    text: 'Title (optional)',
    description: 'Field label for callout title.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewCalloutBodyLabel: {
    text: 'Body',
    description: 'Field label for callout body.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewImageSourceLabel: {
    text: 'Source',
    description: 'Field label for image source type.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewImageUrlTab: {
    text: 'URL',
    description: 'Tab option for image URL.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewImageUploadTab: {
    text: 'Upload',
    description: 'Tab option for image file upload.',
    surface: 'common',
    maxChars: 15,
  },
  NLSOverviewImageUrlLabel: {
    text: 'Image URL (https://)',
    description: 'Field label for image URL input.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewImageUploadBtn: {
    text: 'Upload image',
    description: 'Button text to upload image file.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewImageAltLabel: {
    text: 'Alt text',
    description: 'Field label for image alt text.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewImageAltRequired: {
    text: 'Alt text is required.',
    description: 'Validation error when alt text is empty.',
    surface: 'common',
    maxChars: 35,
  },
  NLSOverviewImageRatioLabel: {
    text: 'Aspect ratio',
    description: 'Field label for image aspect ratio.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewEmbedUrlLabel: {
    text: 'Embed URL (https://)',
    description: 'Field label for embed URL.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewEmbedRatioLabel: {
    text: 'Aspect ratio',
    description: 'Field label for embed aspect ratio.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewEmbedInvalidUrl: {
    text: 'Only https:// URLs from allowed embed hosts are supported.',
    description: 'Inline error message when embed URL host is not in allowlist.',
    surface: 'common',
    maxChars: 70,
  },
  NLSOverviewEmbedDisallowed: {
    text: "This link can't be shown here",
    description: 'Fallback text on card when embed link cannot be shown in an iframe.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewEmbedTitle: {
    text: 'Embedded content',
    description: 'Accessible title for embed iframe.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewLinksAddWorkspaceItem: {
    text: 'Add workspace item',
    description: 'Button text to attach a workspace item link.',
    surface: 'common',
    maxChars: 30,
  },
  NLSOverviewLinksAddUrl: {
    text: 'Add link',
    description: 'Button text to add a web URL link.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewLinksUrlLabel: {
    text: 'URL (https://)',
    description: 'Field label for web link URL.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewLinksTitleLabel: {
    text: 'Label',
    description: 'Field label for web link label.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewLinksInvalidUrl: {
    text: 'Only https:// URLs are allowed.',
    description: 'Validation message for links block URL.',
    surface: 'common',
    maxChars: 40,
  },
  NLSOverviewLinksRemove: {
    text: 'Remove link',
    description: 'Aria label to remove a link item.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPeopleAddMember: {
    text: 'Add teammate',
    description: 'Button label to add a person to people block.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPeopleSelectMember: {
    text: 'Select teammate',
    description: 'Field label to select a member.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPeopleNoteLabel: {
    text: 'Note (optional)',
    description: 'Field label for member note in people block.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewPeopleRemove: {
    text: 'Remove person',
    description: 'Aria label to remove a person from people block.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOverviewChartSelectLabel: {
    text: 'Chart',
    description: 'Field label to select a chart for chart block.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewChartHeightLabel: {
    text: 'Height',
    description: 'Field label for chart height.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewChartNew: {
    text: 'New chart',
    description: 'Button label to open chart builder dialog.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOverviewEmptyBlocks: {
    text: 'No blocks added yet. Choose a block kind below to add one.',
    description: 'Empty state message in custom section dialog before blocks are added.',
    surface: 'common',
    maxChars: 75,
  },
  // Custom Charts (CH3.b / CH7.b)
  NLSAnalysisTabCharts: {
    text: 'Charts',
    description: 'Charts tab in Analysis Hub.',
    surface: 'analysis',
    maxChars: 20,
  },
  NLSChartsLibraryTitle: {
    text: 'Charts',
    description: 'Title of the charts library tab.',
    surface: 'analysis',
    maxChars: 30,
  },
  NLSChartsLibrarySearchPlaceholder: {
    text: 'Search charts by title...',
    description: 'Search placeholder for charts library.',
    surface: 'analysis',
    maxChars: 50,
  },
  NLSChartsFilterDatasetAll: {
    text: 'All datasets',
    description: 'Filter option for all datasets.',
    surface: 'analysis',
    maxChars: 30,
  },
  NLSChartsFilterCreatorAll: {
    text: 'All creators',
    description: 'Filter option for all creators.',
    surface: 'analysis',
    maxChars: 30,
  },
  NLSChartsNewChart: {
    text: 'New chart',
    description: 'Button to open chart builder dialog.',
    surface: 'analysis',
    maxChars: 25,
  },
  NLSChartsSuggestCharts: {
    text: 'Suggest charts',
    description: 'Button to suggest charts using AI.',
    surface: 'analysis',
    maxChars: 25,
  },
  NLSChartsSuggesting: {
    text: 'Suggesting charts...',
    description: 'Loading state while suggesting charts.',
    surface: 'analysis',
    maxChars: 40,
  },
  NLSChartsUsedInCount: {
    text: 'Used in {count}',
    description: 'Count of places a chart is placed.',
    surface: 'analysis',
    maxChars: 30,
  },
  NLSChartsMakeCopy: {
    text: 'Make a copy',
    description: 'Action to make a copy of a chart.',
    surface: 'analysis',
    maxChars: 30,
  },
  NLSChartsDelete: {
    text: 'Delete',
    description: 'Action to delete a chart.',
    surface: 'analysis',
    maxChars: 20,
  },
  NLSChartsDeleteConfirmTitle: {
    text: 'Delete chart?',
    description: 'Title for chart delete confirmation dialog.',
    surface: 'analysis',
    maxChars: 30,
  },
  NLSChartsDeleteConfirmMessage: {
    text: 'Are you sure you want to delete this chart?',
    description: 'Confirmation message for chart deletion.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSChartsDeleteUsedInWarning: {
    text: 'This chart is used in {count} place(s). Those places will show "Chart deleted".',
    description: 'Warning when deleting a chart that has active placements.',
    surface: 'analysis',
    maxChars: 150,
  },
  NLSChartsDeleteConfirmButton: {
    text: 'Delete chart',
    description: 'Confirm deletion button label.',
    surface: 'analysis',
    maxChars: 25,
  },
  NLSChartsCancel: {
    text: 'Cancel',
    description: 'Cancel button label.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsLimitReachedFree: {
    text: 'Saved chart limit reached (5 on Free plan). Upgrade to add more.',
    description: 'Error message when free chart limit is reached.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSChartsLimitReachedPaid: {
    text: 'Saved chart limit reached (50 on Paid plan). Upgrade to add more.',
    description: 'Error message when paid chart limit is reached.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSChartsLimitReachedGeneric: {
    text: 'Saved chart limit reached for your workspace plan.',
    description: 'Generic error message for chart limit.',
    surface: 'analysis',
    maxChars: 100,
  },
  NLSChartsChartDeleted: {
    text: 'Chart deleted',
    description: 'Label shown for a deleted chart placeholder.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsOpenInCharts: {
    text: 'Open in Charts',
    description: 'Menu action to open chart in Analysis Charts tab.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsDownloadCsv: {
    text: 'Download CSV',
    description: 'Menu action to download chart data as CSV.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsDownloadPng: {
    text: 'Download PNG',
    description: 'Menu action to download chart image as PNG.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsSendToChat: {
    text: 'Send to chat',
    description: 'Action menu item to post a custom chart card to chat.',
    surface: 'common',
    maxChars: 24,
  },
  NLSChartsSendToChatDialogTitle: {
    text: 'Send chart to chat',
    description: 'Modal title for sending a chart to chat channel.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChatChartCardLabel: {
    text: 'Chart: {title}',
    description: 'Aria label for the chat chart card container.',
    surface: 'chat',
    maxChars: 60,
  },
  NLSChatChartDeleted: {
    text: 'This chart was deleted',
    description: 'Notice shown beneath a fallback snapshot when a shared chat chart has been deleted.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatChartShowingSavedPicture: {
    text: 'Showing the saved picture',
    description: 'Notice shown beneath a fallback snapshot when a chart cannot be loaded live due to an error or missing access.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatChartSharedAt: {
    text: 'Shared {timestamp}',
    description: 'Caption timestamp for when a chart card was shared to chat.',
    surface: 'chat',
    maxChars: 40,
  },
  NLSChatChartOpenInCharts: {
    text: 'Open in Charts',
    description: 'Link to view the chart in the Charts tab.',
    surface: 'chat',
    maxChars: 30,
  },
  NLSChartsExplain: {
    text: 'Explain',
    description: 'Action to explain chart data using AI.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsEdit: {
    text: 'Edit',
    description: 'Menu action to edit chart in builder.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBack: {
    text: 'Back',
    description: 'Back button to return to charts library.',
    surface: 'analysis',
    maxChars: 20,
  },
  NLSChartsExplainTitle: {
    text: 'Chart explanation',
    description: 'Title for chart explanation panel/popover.',
    surface: 'analysis',
    maxChars: 40,
  },
  NLSChartsExplaining: {
    text: 'Generating explanation...',
    description: 'Loading message when explaining a chart.',
    surface: 'analysis',
    maxChars: 50,
  },
  NLSChartsBuilderTitleNew: {
    text: 'New chart',
    description: 'Title of chart builder dialog when creating new chart.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsBuilderTitleEdit: {
    text: 'Edit chart',
    description: 'Title of chart builder dialog when editing a chart.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsBuilderStepData: {
    text: 'Data',
    description: 'Builder step label for Data.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderStepMeasure: {
    text: 'Measure',
    description: 'Builder step label for Measure.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderStepBreakdown: {
    text: 'Break down',
    description: 'Builder step label for Break down.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderStepTime: {
    text: 'Time',
    description: 'Builder step label for Time.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderStepFilters: {
    text: 'Filters',
    description: 'Builder step label for Filters.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderStepType: {
    text: 'Type',
    description: 'Builder step label for Type.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderStepStyle: {
    text: 'Style',
    description: 'Builder step label for Style.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsAskLinePrompt: {
    text: 'Describe the chart',
    description: 'Placeholder for Ask Line text input in builder.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsAskLineButton: {
    text: 'Draft with Line',
    description: 'Button to trigger AI chart draft.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsAskLineCost: {
    text: 'Cost: {credits} credits',
    description: 'Credit cost label for drafting a chart with Line.',
    surface: 'common',
    maxChars: 35,
  },
  NLSChartsAskLineDrafting: {
    text: 'Drafting chart...',
    description: 'Loading state while drafting chart.',
    surface: 'common',
    maxChars: 35,
  },
  NLSChartsCantChartYet: {
    text: "I can't chart that yet",
    description: 'AI response when the requested dataset is unavailable or cannot be charted.',
    surface: 'common',
    maxChars: 50,
  },
  NLSChartsCreditsInsufficient: {
    text: 'Insufficient credits for AI action.',
    description: 'Error message when credit balance is too low.',
    surface: 'common',
    maxChars: 60,
  },
  NLSChartsSaveToLibrary: {
    text: 'Save to library',
    description: 'Button to save chart to library.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsSaveToLibrarySuccess: {
    text: 'Saved to library',
    description: 'Toast notification after saving a chart to the library.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsSaveFailed: {
    text: 'Failed to save chart to library',
    description: 'Error toast notification when saving a chart to the library fails.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsSaveAsCopy: {
    text: 'Save as copy',
    description: 'Button to save chart as a copy.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsUnsavedChangesTitle: {
    text: 'Unsaved changes',
    description: 'Title of unsaved changes confirmation dialog.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsUnsavedChangesMessage: {
    text: 'You have unsaved changes. Discard them?',
    description: 'Confirmation message when closing builder with unsaved changes.',
    surface: 'common',
    maxChars: 80,
  },
  NLSChartsDiscardChanges: {
    text: 'Discard',
    description: 'Confirm discard changes button.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsKeepEditing: {
    text: 'Keep editing',
    description: 'Keep editing button.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsLivePreview: {
    text: 'Preview',
    description: 'Badge or label on live chart preview.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsColorGuardWarning: {
    text: 'Low contrast on this theme surface (contrast < 3:1).',
    description: 'Warning message for custom color failing WCAG contrast.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsUseReadableShade: {
    text: 'Use readable shade',
    description: 'One-click button to fix low-contrast chart color.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsColorGuardOtherThemes: {
    text: 'Also low contrast on: {themes}',
    description: 'List of other themes where custom color fails contrast.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsEmptyLibrary: {
    text: 'No custom charts created yet.',
    description: 'Empty state message in charts library.',
    surface: 'analysis',
    maxChars: 60,
  },
  NLSChartsSaveSuccess: {
    text: 'Chart saved successfully',
    description: 'Feedback when chart is saved.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsTypeDisabledTargetRequired: {
    text: 'Target required for this chart type',
    description: 'Reason why gauge/progress/targetActual is disabled.',
    surface: 'common',
    maxChars: 60,
  },
  NLSChartsTypeDisabledSecondMeasureRequired: {
    text: 'Second measure required for combo chart',
    description: 'Reason why combo chart is disabled.',
    surface: 'common',
    maxChars: 60,
  },
  NLSChartsPermissionDenied: {
    text: 'You do not have permission to manage custom charts.',
    description: 'Message shown when user lacks chart.manage permission.',
    surface: 'common',
    maxChars: 70,
  },
  NLSChartsNoAccess: {
    text: 'You do not have access to view this chart.',
    description: 'Message shown when chart cannot be accessed.',
    surface: 'common',
    maxChars: 60,
  },
  NLSChartsPreviewCardTitle: {
    text: 'Chart draft',
    description: 'Title for preview card in Line chat turn.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsStarterCharts: {
    text: 'Starter charts',
    description: 'Button label to open the starter charts shelf.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsStarterShelfTitle: {
    text: 'Starter charts',
    description: 'Title of the starter charts modal shelf.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterShelfDesc: {
    text: 'Add pre-built starter charts to your workspace library.',
    description: 'Subtitle for the starter charts shelf.',
    surface: 'common',
    maxChars: 80,
  },
  NLSChartsStarterAddToLibrary: {
    text: 'Add to library',
    description: 'Action button to add a starter chart to the library.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsStarterAdded: {
    text: 'Added',
    description: 'Badge or label when a starter chart was added.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsStarterAlreadyInLibrary: {
    text: 'In library',
    description: 'Badge indicating this starter chart is already in the library.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartsStarterNotAvailable: {
    text: 'Not available yet',
    description: 'Label shown for starter charts whose dataset is not yet supported.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsStarterOpenAssignedTitle: {
    text: 'Open assigned items',
    description: 'Title for starter chart: open assigned items.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterOpenAssignedDesc: {
    text: 'Total number of incomplete tasks assigned to team members.',
    description: 'Description for starter chart: open assigned items.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterOverdueItemsTitle: {
    text: 'Overdue items',
    description: 'Title for starter chart: overdue items.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterOverdueItemsDesc: {
    text: 'Items past their due date that require attention.',
    description: 'Description for starter chart: overdue items.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterDone7DaysTitle: {
    text: 'Done in 7 days',
    description: 'Title for starter chart: done in 7 days.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterDone7DaysDesc: {
    text: 'Number of work items completed in the past week.',
    description: 'Description for starter chart: done in 7 days.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterGoalProgressTitle: {
    text: 'Goal progress',
    description: 'Title for starter chart: goal progress for one goal.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterGoalProgressDesc: {
    text: 'Average completion percentage toward a single target goal.',
    description: 'Description for starter chart: goal progress for one goal.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterStockOnHandTitle: {
    text: 'Stock on hand',
    description: 'Title for starter chart: stock on hand for one item.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterStockOnHandDesc: {
    text: 'Current inventory quantity available for an individual stock item.',
    description: 'Description for starter chart: stock on hand for one item.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterBudgetUsedTitle: {
    text: 'Budget used %',
    description: 'Title for starter chart: budget used %.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterBudgetUsedDesc: {
    text: 'Percentage of allocated budget spent against expense limits.',
    description: 'Description for starter chart: budget used %.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterDonePerWeekTitle: {
    text: 'Done per week',
    description: 'Title for starter chart: done per week (12 weeks, bar).',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterDonePerWeekDesc: {
    text: 'Weekly completed work count over the last 12 weeks.',
    description: 'Description for starter chart: done per week.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterWorkByStatusTitle: {
    text: 'Work by status',
    description: 'Title for starter chart: work by status (donut).',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterWorkByStatusDesc: {
    text: 'Distribution of active items broken down by current workflow status.',
    description: 'Description for starter chart: work by status.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterOverdueByPersonTitle: {
    text: 'Overdue by person',
    description: 'Title for starter chart: overdue by person (horizontal bar).',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterOverdueByPersonDesc: {
    text: 'Overdue task count attributed to each team member.',
    description: 'Description for starter chart: overdue by person.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterSpendByCategoryTitle: {
    text: 'Spend by category this month',
    description: 'Title for starter chart: spend by category this month (donut).',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterSpendByCategoryDesc: {
    text: 'Breakdown of monthly expenditure by expense category.',
    description: 'Description for starter chart: spend by category this month.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterQuotaAttainmentTitle: {
    text: 'Quota attainment by person',
    description: 'Title for starter chart: quota attainment by person.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterQuotaAttainmentDesc: {
    text: 'Target vs actual progress for individual quotas.',
    description: 'Description for starter chart: quota attainment by person.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartsStarterFormResponsesTitle: {
    text: 'Form responses per week',
    description: 'Title for starter chart: form responses per week (line).',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartsStarterFormResponsesDesc: {
    text: 'Trend of form submission volumes over the past 12 weeks.',
    description: 'Description for starter chart: form responses per week.',
    surface: 'common',
    maxChars: 90,
  },
  NLSChartLabelRestricted: {
    text: 'Restricted',
    description: 'Label for chart record viewer cannot read.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartLabelUnknown: {
    text: 'Unknown',
    description: 'Label for chart record that is missing or deleted.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFilterOpIs: {
    text: 'is',
    description: 'Filter operator label for exact match.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFilterOpIsNot: {
    text: 'is not',
    description: 'Filter operator label for negation.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFilterOpIn: {
    text: 'in',
    description: 'Filter operator label for set membership.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFilterOpGt: {
    text: 'greater than',
    description: 'Filter operator label for greater than comparison.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFilterOpLt: {
    text: 'less than',
    description: 'Filter operator label for less than comparison.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFilterOpBetween: {
    text: 'between',
    description: 'Filter operator label for range comparison.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFilterOpIsEmpty: {
    text: 'is empty',
    description: 'Filter operator label for empty check.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderFiltersCount: {
    text: 'Filters ({count})',
    description: 'Header text for filters list in chart builder.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsBuilderAddFilter: {
    text: 'Add Filter',
    description: 'Button text to add a new filter in chart builder.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderFilterValue: {
    text: 'Value',
    description: 'Placeholder and label for filter value in chart builder.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderFilterRemove: {
    text: 'Remove',
    description: 'Button text to remove a filter in chart builder.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartsBuilderNoValuesFound: {
    text: 'No values found',
    description: 'Empty state message when no filter values match query.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsBuilderLoadingValues: {
    text: 'Loading values...',
    description: 'Loading state message while fetching filter values.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsBuilderLoadValuesError: {
    text: 'Failed to load values',
    description: 'Error message when fetching filter values fails.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartsBuilderValueYes: {
    text: 'Yes',
    description: 'Boolean true option label in filter picker.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartsBuilderValueNo: {
    text: 'No',
    description: 'Boolean false option label in filter picker.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartsBuilderValueFrom: {
    text: 'From',
    description: 'Placeholder for start of between range filter.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartsBuilderValueTo: {
    text: 'To',
    description: 'Placeholder for end of between range filter.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartValueTodo: {
    text: 'To Do',
    description: 'Status enum value label for To Do.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueInProgress: {
    text: 'In Progress',
    description: 'Status enum value label for In Progress.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartValueReview: {
    text: 'Review',
    description: 'Status enum value label for Review.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueDone: {
    text: 'Done',
    description: 'Status enum value label for Done.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueBlocked: {
    text: 'Blocked',
    description: 'Status enum value label for Blocked.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueLow: {
    text: 'Low',
    description: 'Priority enum value label for Low.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartValueMedium: {
    text: 'Medium',
    description: 'Priority enum value label for Medium.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartValueHigh: {
    text: 'High',
    description: 'Priority enum value label for High.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartValueUrgent: {
    text: 'Urgent',
    description: 'Priority enum value label for Urgent.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartValueTask: {
    text: 'Task',
    description: 'Work type enum value label for Task.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueMilestone: {
    text: 'Milestone',
    description: 'Work type enum value label for Milestone.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueBug: {
    text: 'Bug',
    description: 'Work type enum value label for Bug.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueIssue: {
    text: 'Issue',
    description: 'Work type enum value label for Issue.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueDeliverable: {
    text: 'Deliverable',
    description: 'Work type enum value label for Deliverable.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartValueIncome: {
    text: 'Income',
    description: 'Income/expense enum value label for Income.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueExpense: {
    text: 'Expense',
    description: 'Income/expense enum value label for Expense.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueBillable: {
    text: 'Billable',
    description: 'Time kind enum value label for Billable.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartValueNonBillable: {
    text: 'Non-Billable',
    description: 'Time kind enum value label for Non-Billable.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartValueTrue: {
    text: 'Yes',
    description: 'Boolean enum value label for true.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartValueFalse: {
    text: 'No',
    description: 'Boolean enum value label for false.',
    surface: 'common',
    maxChars: 15,
  },
  NLSChartDatasetBudget: {
    text: 'Budget',
    description: 'Dataset label for Budget.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartDatasetDaily: {
    text: 'Daily Snapshot',
    description: 'Dataset label for Daily Snapshot.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartDatasetFocus: {
    text: 'Focus Sessions',
    description: 'Dataset label for Focus Sessions.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartDatasetForms: {
    text: 'Forms',
    description: 'Dataset label for Forms.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartDatasetQuota: {
    text: 'Quota & Targets',
    description: 'Dataset label for Quota.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartDatasetReliability: {
    text: 'Reliability',
    description: 'Dataset label for Reliability.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartDatasetStock: {
    text: 'Stock & Inventory',
    description: 'Dataset label for Stock.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartDatasetTime: {
    text: 'Time Tracking',
    description: 'Dataset label for Time Tracking.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartDatasetWork: {
    text: 'Work Items',
    description: 'Dataset label for Work Items.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldActualValue: {
    text: 'Actual Value',
    description: 'Catalog field label for Actual Value.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartFieldAmount: {
    text: 'Amount',
    description: 'Catalog field label for Amount.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldAssignee: {
    text: 'Assignee',
    description: 'Catalog field label for Assignee.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldAttainment: {
    text: 'Attainment',
    description: 'Catalog field label for Attainment.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldBoard: {
    text: 'Board',
    description: 'Catalog field label for Board.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldBoardId: {
    text: 'Board',
    description: 'Catalog field label for Board ID.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldBudgetLimit: {
    text: 'Budget Limit',
    description: 'Catalog field label for Budget Limit.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartFieldBudgetSpentToDate: {
    text: 'Budget Spent to Date',
    description: 'Catalog field label for Budget Spent to Date.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartFieldCategory: {
    text: 'Category',
    description: 'Catalog field label for Category.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldCount: {
    text: 'Count',
    description: 'Catalog field label for Count.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldCountDate: {
    text: 'Count Date',
    description: 'Catalog field label for Count Date.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldCountsSubmitted: {
    text: 'Counts Submitted',
    description: 'Catalog field label for Counts Submitted.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartFieldCreated: {
    text: 'Created Date',
    description: 'Catalog field label for Created Date.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldDay: {
    text: 'Day',
    description: 'Catalog field label for Day.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldDone: {
    text: 'Completed Date',
    description: 'Catalog field label for Completed Date.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldDoneCount: {
    text: 'Completed Count',
    description: 'Catalog field label for Completed Count.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldDoneToday: {
    text: 'Done Today',
    description: 'Catalog field label for Done Today.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldDue: {
    text: 'Due Date',
    description: 'Catalog field label for Due Date.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldEntries: {
    text: 'Entries',
    description: 'Catalog field label for Entries.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldEntryDate: {
    text: 'Entry Date',
    description: 'Catalog field label for Entry Date.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldForm: {
    text: 'Form',
    description: 'Catalog field label for Form.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldGoal: {
    text: 'Goal',
    description: 'Catalog field label for Goal.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldGoalProgressAvg: {
    text: 'Average Goal Progress',
    description: 'Catalog field label for Average Goal Progress.',
    surface: 'common',
    maxChars: 30,
  },
  NLSChartFieldHours: {
    text: 'Hours',
    description: 'Catalog field label for Hours.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldIncomeExpense: {
    text: 'Income / Expense',
    description: 'Catalog field label for Income / Expense.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldItem: {
    text: 'Item',
    description: 'Catalog field label for Item.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldItemsCreated: {
    text: 'Items Created',
    description: 'Catalog field label for Items Created.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldKind: {
    text: 'Kind',
    description: 'Catalog field label for Kind.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldLocation: {
    text: 'Location',
    description: 'Catalog field label for Location.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldLowStock: {
    text: 'Low Stock',
    description: 'Catalog field label for Low Stock.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldLowStockCount: {
    text: 'Low Stock Count',
    description: 'Catalog field label for Low Stock Count.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldLowStockItems: {
    text: 'Low Stock Items',
    description: 'Catalog field label for Low Stock Items.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldMinutes: {
    text: 'Minutes',
    description: 'Catalog field label for Minutes.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldMovementDate: {
    text: 'Movement Date',
    description: 'Catalog field label for Movement Date.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldNumberField: {
    text: 'Number Field',
    description: 'Catalog field label for Number Field.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldOnHand: {
    text: 'On Hand',
    description: 'Catalog field label for On Hand.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldOnTimePct: {
    text: 'On-Time %',
    description: 'Catalog field label for On-Time %.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldOpenItems: {
    text: 'Open Items',
    description: 'Catalog field label for Open Items.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldOverdue: {
    text: 'Overdue',
    description: 'Catalog field label for Overdue.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldOverdueCount: {
    text: 'Overdue Count',
    description: 'Catalog field label for Overdue Count.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldOverdueItems: {
    text: 'Overdue Items',
    description: 'Catalog field label for Overdue Items.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldParentGoal: {
    text: 'Parent Goal',
    description: 'Catalog field label for Parent Goal.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldPeriod: {
    text: 'Period',
    description: 'Catalog field label for Period.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldPeriodDate: {
    text: 'Period Date',
    description: 'Catalog field label for Period Date.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldPerson: {
    text: 'Person',
    description: 'Catalog field label for Person.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldPriority: {
    text: 'Priority',
    description: 'Catalog field label for Priority.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldProgress: {
    text: 'Progress',
    description: 'Catalog field label for Progress.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldProofsApproved: {
    text: 'Proofs Approved',
    description: 'Catalog field label for Proofs Approved.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldProofsPending: {
    text: 'Proofs Pending',
    description: 'Catalog field label for Proofs Pending.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldProofsReturned: {
    text: 'Proofs Returned',
    description: 'Catalog field label for Proofs Returned.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldQuestionAnswer: {
    text: 'Question Answer',
    description: 'Catalog field label for Question Answer.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartQuestionSelectLabel: {
    text: 'Question',
    description: 'Label for the form question dropdown in chart builder.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartQuestionSelectDefault: {
    text: 'First choice question (default)',
    description: 'Option for defaulting to the first choice question in chart builder.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartNumberSelectLabel: {
    text: 'Number question',
    description: 'Label for the form number field dropdown in chart builder.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartNumberSelectDefault: {
    text: 'First number field (default)',
    description: 'Option for defaulting to the first number field in chart builder.',
    surface: 'common',
    maxChars: 40,
  },
  NLSChartFieldReliabilityScore: {
    text: 'Reliability Score',
    description: 'Catalog field label for Reliability Score.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldResponses: {
    text: 'Responses',
    description: 'Catalog field label for Responses.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldResponsesToday: {
    text: 'Responses Today',
    description: 'Catalog field label for Responses Today.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldSessions: {
    text: 'Sessions',
    description: 'Catalog field label for Sessions.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldStatus: {
    text: 'Status',
    description: 'Catalog field label for Status.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldStockValue: {
    text: 'Stock Value',
    description: 'Catalog field label for Stock Value.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldSubmittedAt: {
    text: 'Submitted At',
    description: 'Catalog field label for Submitted At.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldSubmitter: {
    text: 'Submitter',
    description: 'Catalog field label for Submitter.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldSupplier: {
    text: 'Supplier',
    description: 'Catalog field label for Supplier.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldTag: {
    text: 'Tag',
    description: 'Catalog field label for Tag.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldTarget: {
    text: 'Target',
    description: 'Catalog field label for Target.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldTargetValue: {
    text: 'Target Value',
    description: 'Catalog field label for Target Value.',
    surface: 'common',
    maxChars: 25,
  },
  NLSChartFieldTask: {
    text: 'Task',
    description: 'Catalog field label for Task.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldTemplate: {
    text: 'Template',
    description: 'Catalog field label for Template.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldType: {
    text: 'Type',
    description: 'Catalog field label for Type.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldValue: {
    text: 'Value',
    description: 'Catalog field label for Value.',
    surface: 'common',
    maxChars: 20,
  },
  NLSChartFieldVendor: {
    text: 'Vendor',
    description: 'Catalog field label for Vendor.',
    surface: 'common',
    maxChars: 25,
  },
  NLSOwnerConsoleRepairPlaceAccessTitle: {
    text: 'Repair place access',
    description: 'Title for the Owner Console repair place access card.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleRepairPlaceAccessDesc: {
    text: 'Backfill missing readAccess on sector operations places across workspaces.',
    description: 'Description for repair place access card.',
    surface: 'owner-console',
    maxChars: 100,
  },
  NLSOwnerConsoleRepairCheckBtn: {
    text: 'Check',
    description: 'Button to run a dry-run check of places missing readAccess.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleRepairApplyBtn: {
    text: 'Repair',
    description: 'Button to open confirmation dialog and apply place access repairs.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleRepairConfirmTitle: {
    text: 'Confirm place access repair',
    description: 'Title for confirmation dialog to apply place access repairs.',
    surface: 'owner-console',
    maxChars: 40,
  },
  NLSOwnerConsoleRepairConfirmDesc: {
    text: 'This will set readAccess: null on all scanned places that are currently missing readAccess.',
    description: 'Description in confirm dialog for place access repair.',
    surface: 'owner-console',
    maxChars: 120,
  },
  NLSOwnerConsoleRepairConfirmBtn: {
    text: 'Confirm',
    description: 'Button to confirm and execute place access repairs.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleRepairCancelBtn: {
    text: 'Cancel',
    description: 'Button to cancel place access repair.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleRepairWsCol: {
    text: 'Workspace',
    description: 'Table column header for workspace name.',
    surface: 'owner-console',
    maxChars: 24,
  },
  NLSOwnerConsoleRepairScannedCol: {
    text: 'Scanned',
    description: 'Table column header for scanned place count.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleRepairMissingCol: {
    text: 'Missing',
    description: 'Table column header for missing readAccess count.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleRepairUpdatedCol: {
    text: 'Updated',
    description: 'Table column header for updated count.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleRepairNoPlaces: {
    text: 'No workspaces with sector operations places found.',
    description: 'Message when no places are found.',
    surface: 'owner-console',
    maxChars: 60,
  },
  NLSReportsChartAsOf: {
    text: 'As of {date}',
    description: 'Timestamp subtitle for exported dated chart images in reports.',
    surface: 'reports',
    maxChars: 40,
  },
  NLSTourGuideWaitingHint: {
    text: 'Waiting for you…',
    description: 'Hint shown in the tour spotlight popover when waiting for user action.',
    surface: 'common',
    maxChars: 40,
  },
  NLSKitInboxGoalTitle: {
    text: '{kitTitle} inbox',
    description: 'Title for starter kit inbox goal that receives kit automation tasks.',
    surface: 'starter-kits',
    maxChars: 80,
  },
  NLSKitCrewChannelName: {
    text: '{kitTitle} crew',
    description: 'Name for starter kit crew chat channel.',
    surface: 'starter-kits',
    maxChars: 64,
  },
  /* -------------------------------------------------------------------------
   * R19 Timesheets — TS0.b & TS2.b Hub, Settings, My Time, Timer Bar
   * ------------------------------------------------------------------------- */
  NLSShellRailTileTimesheets: {
    text: 'Timesheets',
    description: 'Label for timesheets rail tile.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSTimesheetsTitle: {
    text: 'Timesheets',
    description: 'Timesheets hub title.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSubtitle: {
    text: 'Track worked time, log hours, and manage weekly timesheets.',
    description: 'Timesheets hub subtitle.',
    surface: 'timesheets',
    maxChars: 80,
  },
  NLSTimesheetsOffTitle: {
    text: 'Timesheets is off.',
    description: 'Timesheets disabled module title.',
    surface: 'timesheets',
    maxChars: 28,
  },
  NLSTimesheetsOffBody: {
    text: 'An admin can enable Timesheets from Workspace Settings to track work hours.',
    description: 'Timesheets disabled module body.',
    surface: 'timesheets',
    maxChars: 90,
  },
  NLSTimesheetsTierRequired: {
    text: 'Timesheets requires an Essential plan or higher.',
    description: 'Timesheets tier upgrade prompt.',
    surface: 'timesheets',
    maxChars: 60,
  },
  NLSTimesheetsTabsAria: {
    text: 'Timesheets hub tabs',
    description: 'Accessible label for timesheets hub navigation tabs.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsTabMyTime: {
    text: 'My time',
    description: 'Timesheets My time tab label.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsTabApprovals: {
    text: 'Approvals',
    description: 'Timesheets Approvals tab label.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsTabTeam: {
    text: 'Team',
    description: 'Timesheets Team tab label.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsTabClients: {
    text: 'Clients',
    description: 'Timesheets Clients tab label.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsTabExports: {
    text: 'Exports',
    description: 'Timesheets Exports tab label.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportAction: {
    text: 'Export billable hours',
    description: 'Button/action label to export billable hours.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsExportDialogTitle: {
    text: 'Export billable hours',
    description: 'Title of the export billable hours dialog.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsExportClientLabel: {
    text: 'Client',
    description: 'Label for client picker in export dialog.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportAllClients: {
    text: 'All clients',
    description: 'Option to export all clients.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportPeriodLabel: {
    text: 'Period',
    description: 'Label for period picker in export dialog.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportPeriodThisWeek: {
    text: 'This week',
    description: 'Period option: this week.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportPeriodLastWeek: {
    text: 'Last week',
    description: 'Period option: last week.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportPeriodThisMonth: {
    text: 'This month',
    description: 'Period option: this month.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportPeriodLastMonth: {
    text: 'Last month',
    description: 'Period option: last month.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportPeriodCustom: {
    text: 'Custom',
    description: 'Period option: custom date range.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportFromLabel: {
    text: 'From date',
    description: 'Label for start date in custom period.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportToLabel: {
    text: 'To date',
    description: 'Label for end date in custom period.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportFormatLabel: {
    text: 'Format',
    description: 'Label for file format selection.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportButtonSubmit: {
    text: 'Export',
    description: 'Submit button for export dialog.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportButtonCancel: {
    text: 'Cancel',
    description: 'Cancel button for export dialog.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsExportSuccess: {
    text: 'Export completed. Your download will begin shortly.',
    description: 'Success message after billable export.',
    surface: 'timesheets',
    maxChars: 60,
  },
  NLSTimesheetsExportError: {
    text: 'Failed to export billable hours.',
    description: 'Error message when billable export fails.',
    surface: 'timesheets',
    maxChars: 50,
  },
  NLSTimesheetsExportTabDescription: {
    text: 'Export approved billable hours and revenue breakdowns for clients.',
    description: 'Description header on Exports tab.',
    surface: 'timesheets',
    maxChars: 80,
  },
  NLSTimesheetsExportPayrollAction: {
    text: 'Export payroll',
    description: 'Button/action label to export payroll hours.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsExportPayrollDialogTitle: {
    text: 'Export payroll',
    description: 'Title of the export payroll dialog.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsExportPayrollThresholdsNote: {
    text: 'Regular and overtime hours are calculated per your workspace Settings thresholds.',
    description: 'Explanation in payroll dialog that overtime follows settings.',
    surface: 'timesheets',
    maxChars: 90,
  },
  NLSTimesheetsExportPayrollZeroThresholdsWarning: {
    text: 'Overtime thresholds are currently set to 0. All hours will be classified as regular.',
    description: 'Warning shown when daily and weekly overtime thresholds are both 0.',
    surface: 'timesheets',
    maxChars: 90,
  },
  NLSTimesheetsExportPayrollSuccess: {
    text: 'Payroll export completed. Your download will begin shortly.',
    description: 'Success message after payroll export.',
    surface: 'timesheets',
    maxChars: 70,
  },
  NLSTimesheetsExportPayrollError: {
    text: 'Failed to export payroll hours.',
    description: 'Error message when payroll export fails.',
    surface: 'timesheets',
    maxChars: 50,
  },
  NLSTimesheetsTabSettings: {
    text: 'Settings',
    description: 'Timesheets Settings tab label.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsTabComingSoon: {
    text: 'Coming in a later step',
    description: 'Placeholder message for timesheet tabs coming soon.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsSettingsAutoSourcesTitle: {
    text: 'Automatic sources',
    description: 'Timesheet settings auto sources heading.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsAutoSourcesHelp: {
    text: 'Automatically create draft entries from other workspace activities.',
    description: 'Timesheet settings auto sources explanation.',
    surface: 'timesheets',
    maxChars: 80,
  },
  NLSTimesheetsSettingsSessions: {
    text: 'Sessions',
    description: 'Toggle auto-logging from teaching/training sessions.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSettingsMeetings: {
    text: 'Meetings',
    description: 'Toggle auto-logging from meetings.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSettingsFocus: {
    text: 'Focus',
    description: 'Toggle auto-logging from Focus sessions.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSettingsWeekStart: {
    text: 'Week start',
    description: 'Timesheet settings week start day.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSettingsWeekStartMonday: {
    text: 'Monday',
    description: 'Monday week start option.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsSettingsWeekStartSunday: {
    text: 'Sunday',
    description: 'Sunday week start option.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsSettingsRounding: {
    text: 'Rounding',
    description: 'Timesheet settings time rounding interval.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSettingsRoundingNone: {
    text: 'None (exact minutes)',
    description: 'Exact time rounding option.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsRounding5Min: {
    text: '5 minutes',
    description: '5 minute rounding option.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSettingsRounding6Min: {
    text: '6 minutes (0.1 hr)',
    description: '6 minute rounding option.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsSettingsRounding15Min: {
    text: '15 minutes (0.25 hr)',
    description: '15 minute rounding option.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsOvertimeTitle: {
    text: 'Overtime rules',
    description: 'Timesheet settings overtime section heading.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsDailyThreshold: {
    text: 'Daily threshold (hours)',
    description: 'Daily overtime threshold in hours.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsWeeklyThreshold: {
    text: 'Weekly threshold (hours)',
    description: 'Weekly overtime threshold in hours.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsOvertimeMultiplier: {
    text: 'Overtime multiplier',
    description: 'Overtime rate multiplier.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsOvertimeTierRequired: {
    text: 'Overtime rules require the Professional plan.',
    description: 'Overtime rules tier lock description.',
    surface: 'timesheets',
    maxChars: 60,
  },
  NLSTimesheetsSettingsGpsTitle: {
    text: 'Location tracking',
    description: 'Timesheet settings GPS heading.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsGpsLabel: {
    text: 'Require GPS at clock-in / clock-out',
    description: 'Timesheet settings GPS toggle label.',
    surface: 'timesheets',
    maxChars: 45,
  },
  NLSTimesheetsSettingsGpsConsentNote: {
    text: 'GPS location is captured only at clock-in and clock-out events. It is never tracked continuously and is visible only to admins and approvers.',
    description: 'Timesheet settings GPS consent disclaimer.',
    surface: 'timesheets',
    maxChars: 160,
  },
  NLSTimesheetsSettingsRemindersTitle: {
    text: 'Reminders',
    description: 'Timesheet settings reminders section title.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSettingsRemindSubmit: {
    text: 'Remind members to submit timesheets',
    description: 'Toggle timesheet submit reminder.',
    surface: 'timesheets',
    maxChars: 45,
  },
  NLSTimesheetsSettingsRemindApprove: {
    text: 'Remind approvers of pending timesheets',
    description: 'Toggle timesheet approval reminder.',
    surface: 'timesheets',
    maxChars: 45,
  },
  NLSTimesheetsSettingsSave: {
    text: 'Save settings',
    description: 'Save timesheet settings button.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSettingsSavedToast: {
    text: 'Timesheet settings saved.',
    description: 'Toast after saving timesheet settings.',
    surface: 'timesheets',
    maxChars: 35,
  },
  NLSTimesheetsWeekPickerPrev: {
    text: 'Previous week',
    description: 'Week picker previous button aria label.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsWeekPickerNext: {
    text: 'Next week',
    description: 'Week picker next button aria label.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsWeekPickerCurrent: {
    text: 'This week',
    description: 'Week picker button to jump to current week.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsGridNoItem: {
    text: 'No item',
    description: 'Grid row label when time is not linked to an item.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsGridTotal: {
    text: 'Total',
    description: 'Grid totals row and column label.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsGridRegular: {
    text: 'Regular',
    description: 'Regular hours breakdown label.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsGridOvertime: {
    text: 'Overtime',
    description: 'Overtime hours breakdown label.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsGridStartTimer: {
    text: 'Start timer',
    description: 'Start timer button on a grid item row.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsGridClockIn: {
    text: 'Clock in',
    description: 'Clock in button on the no item row.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsEntriesTitle: {
    text: 'Time entries',
    description: 'Heading for entries list under weekly grid.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsAddEntry: {
    text: 'Add entry',
    description: 'Button to open add time entry dialog.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsEditEntry: {
    text: 'Edit entry',
    description: 'Button to edit time entry.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSubmitWeek: {
    text: 'Submit week',
    description: 'Button to submit weekly timesheet.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSubmitConfirmTitle: {
    text: 'Submit timesheet?',
    description: 'Title of confirm modal when submitting week.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSubmitConfirmBody: {
    text: 'Are you sure you want to submit your timesheet for this week? Once submitted, entries cannot be edited unless reopened by an admin.',
    description: 'Body text of confirm modal when submitting week.',
    surface: 'timesheets',
    maxChars: 160,
  },
  NLSTimesheetsSubmittedToast: {
    text: 'Timesheet submitted successfully.',
    description: 'Toast after submitting week.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsTimerBarStop: {
    text: 'Stop',
    description: 'Stop button on running timer bar.',
    surface: 'timesheets',
    maxChars: 10,
  },
  NLSTimesheetsTimerBarClockedIn: {
    text: 'Clocked in',
    description: 'Running timer bar label when no node is selected.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsUndo: {
    text: 'Undo',
    description: 'Undo action label on toast.',
    surface: 'timesheets',
    maxChars: 10,
  },
  NLSTimesheetsPreviousTimerStopped: {
    text: 'Previous timer stopped.',
    description: 'Toast notification when earlier timer was stopped.',
    surface: 'timesheets',
    maxChars: 35,
  },
  NLSTimesheetsEntrySavedToast: {
    text: 'Time entry saved.',
    description: 'Toast after saving time entry.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsNoItemTitle: {
    text: 'General work (no item)',
    description: 'Display title for time not linked to any goal or task.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsNodePickerLabel: {
    text: 'Item (optional)',
    description: 'Field label for selecting goal or task.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsStartLabel: {
    text: 'Start',
    description: 'Start date and time label.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsEndLabel: {
    text: 'End',
    description: 'End date and time label.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsNoteLabel: {
    text: 'Note (optional)',
    description: 'Time entry note field label.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsBillableLabel: {
    text: 'Billable',
    description: 'Billable checkbox or toggle label.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsSelectNodePlaceholder: {
    text: 'Select goal, task, or subtask…',
    description: 'Placeholder for item selection.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsClearItem: {
    text: 'Clear item',
    description: 'Clear item button.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsNoEntries: {
    text: 'No time entries for this week.',
    description: 'Empty state when no entries recorded.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsHoursFormat: {
    text: '{hours}h',
    description: 'Format for hours display.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsDurationMinutesFormat: {
    text: '{minutes}m',
    description: 'Format for duration in minutes.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsDurationHoursMinutesFormat: {
    text: '{hours}h {minutes}m',
    description: 'Format for duration in hours and minutes.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSaveEntry: {
    text: 'Save entry',
    description: 'Save entry button in add/edit dialog.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsDeleteEntry: {
    text: 'Delete entry',
    description: 'Delete time entry button.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsOvertimeSplitFormat: {
    text: 'Regular: {regular} · Overtime: {overtime}',
    description: 'Overtime split summary text.',
    surface: 'timesheets',
    maxChars: 50,
  },
  NLSMemberControlsSectionTime: {
    text: 'Time',
    description: 'Member controls section for time tracking and clock.',
    surface: 'member',
    maxChars: 20,
  },
  NLSTimesheetsClockCardTitle: {
    text: 'Time tracking',
    description: 'Header title for mobile timesheet clock card.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsClockIn: {
    text: 'Clock in',
    description: 'Button action to clock in.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsClockOut: {
    text: 'Clock out',
    description: 'Button action to clock out.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsClockedInSince: {
    text: 'Clocked in since {time}',
    description: 'Status label indicating when clock was started.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsClockedOut: {
    text: 'Clocked out',
    description: 'Status label when user is not clocked in.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsClockWaitingToSync: {
    text: 'Waiting to sync',
    description: 'Status badge when clock mutation is pending in offline outbox.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsLocationCaptured: {
    text: 'Location captured',
    description: 'Badge indicating GPS coordinates were recorded at clock event.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsClockInOnItem: {
    text: 'Clock in on item',
    description: 'Button/trigger to pick an item before clocking in.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsClockNoItemSelected: {
    text: 'No item selected',
    description: 'Placeholder when no item is linked to the clock.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsTodayTotal: {
    text: "Today's total",
    description: 'Label for total logged time today on clock card.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsClockOvertimeHint: {
    text: 'Running over threshold ({hours}h). Remember to clock out when shift ends.',
    description: 'In-app warning hint when clock exceeds overtime or 12h threshold.',
    surface: 'timesheets',
    maxChars: 90,
  },
  NLSTimesheetsGpsConsentTitle: {
    text: 'Location capture consent',
    description: 'Title for GPS location capture consent dialog.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsGpsConsentDecline: {
    text: 'Decline',
    description: 'Button to decline GPS location capture.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsGpsConsentAllow: {
    text: 'Allow location',
    description: 'Button to accept GPS location capture.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsGpsUnavailableToast: {
    text: 'Clocked in without location (location unavailable or denied)',
    description: 'Toast shown when clocking in without GPS due to failure/denial.',
    surface: 'timesheets',
    maxChars: 80,
  },
  NLSTimesheetsItemStartTimer: {
    text: 'Start timer',
    description: 'Start timer button on an item.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsItemStopTimer: {
    text: 'Stop timer',
    description: 'Stop timer button on an item.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsItemStopTimerElapsed: {
    text: 'Stop · {elapsed}',
    description: 'Stop button on item timer with ticking elapsed time.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsItemLoggedWithEstimate: {
    text: 'Logged {logged} / Est {estimate}',
    description: 'Display of logged time vs estimated hours on an item.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsItemLoggedOnly: {
    text: 'Logged {logged}',
    description: 'Display of logged time on an item when no estimate exists.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsItemTimerAction: {
    text: 'Timer',
    description: 'Timer action label on member card menu.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsTimerActionFailed: {
    text: 'Timer action failed.',
    description: 'Error toast when starting or stopping an item timer fails.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSMobileSyncKindTimeClock: {
    text: 'Time clock',
    description: 'Breakdown row for TIME_CLOCK mutation in Mobile Sync Center.',
    surface: 'helper',
    maxChars: 22,
  },
  NLSMobileSyncKindDueRecord: {
    text: 'Due records',
    description: 'Breakdown row for OPS_DUE_RECORD mutation in Mobile Sync Center.',
    surface: 'helper',
    maxChars: 22,
  },
  NLSMobileSyncKindCheckpointScan: {
    text: 'Checkpoint scans',
    description: 'Breakdown row for OPS_CHECKPOINT_SCAN mutation in Mobile Sync Center.',
    surface: 'helper',
    maxChars: 22,
  },
  NLSMobileSyncKindSkipDue: {
    text: 'Skipped dues',
    description: 'Breakdown row for OPS_SKIP_DUE mutation in Mobile Sync Center.',
    surface: 'helper',
    maxChars: 22,
  },
  NLSMobileSyncKindRaiseRequest: {
    text: 'Requests',
    description: 'Breakdown row for OPS_RAISE_REQUEST mutation in Mobile Sync Center.',
    surface: 'helper',
    maxChars: 22,
  },
  NLSMobileSyncKindAttendanceRoll: {
    text: 'Attendance rolls',
    description: 'Breakdown row for OPS_ATTENDANCE_ROLL mutation in Mobile Sync Center.',
    surface: 'helper',
    maxChars: 22,
  },
  NLSMobileSyncKindBreakdown: {
    text: 'Breakdown',
    description: 'Breakdown row for BREAKDOWN request in Mobile Sync Center.',
    surface: 'helper',
    maxChars: 25,
  },
  NLSClientsFieldBillingMode: {
    text: 'Billing mode',
    description: 'Label for client billing mode selector.',
    surface: 'clients',
    maxChars: 30,
  },
  NLSClientsFieldBillingModeHourly: {
    text: 'Hourly',
    description: 'Hourly billing mode option.',
    surface: 'clients',
    maxChars: 20,
  },
  NLSClientsFieldBillingModeFixed: {
    text: 'Fixed bid',
    description: 'Fixed bid billing mode option.',
    surface: 'clients',
    maxChars: 20,
  },
  NLSClientsFieldBillingModeNonBillable: {
    text: 'Non-billable',
    description: 'Non-billable billing mode option.',
    surface: 'clients',
    maxChars: 20,
  },
  NLSClientsFieldCurrency: {
    text: 'Currency',
    description: 'Label for client currency input.',
    surface: 'clients',
    maxChars: 20,
  },
  NLSGoalFieldClient: {
    text: 'Client',
    description: 'Label for goal client link selector.',
    surface: 'checklist',
    maxChars: 30,
  },
  NLSGoalFieldClientNone: {
    text: 'No client (Internal)',
    description: 'Option for no client on goal.',
    surface: 'checklist',
    maxChars: 40,
  },
  NLSGoalFieldClientSelect: {
    text: 'Select client',
    description: 'Placeholder for goal client selector.',
    surface: 'checklist',
    maxChars: 30,
  },
  NLSTimesheetsTabRates: {
    text: 'Rates',
    description: 'Timesheets Rates tab label.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsBucketClientBillable: {
    text: 'Client billable',
    description: 'Chip label for client billable time entries.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsBucketFixedBid: {
    text: 'Fixed bid',
    description: 'Chip label for fixed bid time entries.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsBucketClientNonBillable: {
    text: 'Client non-billable',
    description: 'Chip label for client non-billable time entries.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsBucketInternal: {
    text: 'Internal',
    description: 'Chip label for internal time entries.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsBucketOverride: {
    text: 'Override bucket',
    description: 'Menu label to override time entry bucket.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsBucketOverridden: {
    text: 'Overridden',
    description: 'Badge indicating bucket was manually overridden.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsSettingsBackfillTitle: {
    text: 'Time entry bucket backfill',
    description: 'Heading for bucket backfill in timesheet settings.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsSettingsBackfillHelp: {
    text: 'Fill missing billing buckets on historical time entries based on linked goals and clients.',
    description: 'Help description for bucket backfill tool.',
    surface: 'timesheets',
    maxChars: 120,
  },
  NLSTimesheetsSettingsBackfillButton: {
    text: 'Fill missing buckets',
    description: 'Button to trigger bucket backfill.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsBackfilling: {
    text: 'Filling buckets…',
    description: 'Loading label while backfilling buckets.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsBackfillSuccess: {
    text: 'Buckets backfilled: {updated} updated, {skipped} skipped.',
    description: 'Success message after backfilling buckets.',
    surface: 'timesheets',
    maxChars: 80,
  },
  NLSTimesheetsRatesTitle: {
    text: 'Rates & billability',
    description: 'Header title for Timesheets Rates tab.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsRatesSubtitle: {
    text: 'Manage internal member cost rates and client bill rates.',
    description: 'Subtitle for Timesheets Rates tab.',
    surface: 'timesheets',
    maxChars: 80,
  },
  NLSTimesheetsRatesMemberRatesTitle: {
    text: 'Member rates',
    description: 'Section title for Member Rates.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsRatesClientRatesTitle: {
    text: 'Client default bill rates',
    description: 'Section title for Client Default Bill Rates.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsRatesSetRate: {
    text: 'Set rate',
    description: 'Button to set or update rate.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsRatesCostRate: {
    text: 'Cost rate',
    description: 'Label for cost rate column/field.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsRatesBillRate: {
    text: 'Bill rate',
    description: 'Label for bill rate column/field.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsRatesEffectiveFrom: {
    text: 'Effective from',
    description: 'Label for rate effective date.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsRatesHistory: {
    text: 'Rate history',
    description: 'Heading or modal title for rate history.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsRatesNoHistory: {
    text: 'No rate history recorded.',
    description: 'Empty text for rate history.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsRatesSuccessToast: {
    text: 'Rate saved successfully.',
    description: 'Toast notification after saving a rate.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsRatesCurrency: {
    text: 'Currency',
    description: 'Currency label for rates.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsRatesNoClientRate: {
    text: 'No default bill rate set.',
    description: 'Placeholder when client has no rate.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsRatesNoMemberRate: {
    text: 'No rate set.',
    description: 'Placeholder when member has no rate.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsUnpostedLabourTitle: {
    text: 'Weeks without a cost rate',
    description: 'Title for the panel listing approved timesheet weeks missing a cost rate.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsUnpostedLabourSubtitle: {
    text: 'Approved weeks that were skipped because no cost rate was active.',
    description: 'Subtitle explaining unposted labour weeks.',
    surface: 'timesheets',
    maxChars: 80,
  },
  NLSTimesheetsUnpostedLabourEmpty: {
    text: 'No approved weeks are missing a cost rate.',
    description: 'Empty state message when all approved weeks have labour costs posted.',
    surface: 'timesheets',
    maxChars: 60,
  },
  NLSTimesheetsPostLabourOfferTitle: {
    text: 'Post labour costs',
    description: 'Title for the dialog offering to post labour for newly rated member.',
    surface: 'timesheets',
    maxChars: 35,
  },
  NLSTimesheetsPostLabourConfirm: {
    text: 'Post labour',
    description: 'Button text to confirm posting labour costs.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsPostLabourSuccessToast: {
    text: 'Labour costs posted to Budget.',
    description: 'Toast message after pending labour costs are posted.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsFilterToReview: {
    text: 'To review',
    description: 'Filter option for submitted timesheets awaiting review.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsFilterApproved: {
    text: 'Approved',
    description: 'Filter option for approved timesheets.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsFilterRejected: {
    text: 'Rejected',
    description: 'Filter option for rejected timesheets.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsApproveSelected: {
    text: 'Approve selected ({count})',
    description: 'Bulk approve button label with selected count.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsActionApprove: {
    text: 'Approve',
    description: 'Button to approve a timesheet week.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsActionReject: {
    text: 'Reject',
    description: 'Button to reject a timesheet week.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsActionReopen: {
    text: 'Reopen',
    description: 'Button for admins to reopen an approved timesheet week.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsRejectDialogTitle: {
    text: 'Reject timesheet week',
    description: 'Title for reject timesheet modal.',
    surface: 'timesheets',
    maxChars: 35,
  },
  NLSTimesheetsRejectDialogBody: {
    text: 'Provide feedback explaining why this timesheet is being rejected so the member can make corrections.',
    description: 'Instructional body for reject timesheet modal.',
    surface: 'timesheets',
    maxChars: 120,
  },
  NLSTimesheetsRejectNotePlaceholder: {
    text: 'Reason for rejection (required)...',
    description: 'Placeholder for reject note textarea.',
    surface: 'timesheets',
    maxChars: 50,
  },
  NLSTimesheetsReopenDialogTitle: {
    text: 'Reopen timesheet week',
    description: 'Title for reopen timesheet modal.',
    surface: 'timesheets',
    maxChars: 35,
  },
  NLSTimesheetsReopenDialogBody: {
    text: 'Provide a reason for reopening this approved timesheet week. This action will be audited.',
    description: 'Instructional body for reopen timesheet modal.',
    surface: 'timesheets',
    maxChars: 120,
  },
  NLSTimesheetsReopenReasonPlaceholder: {
    text: 'Reason for reopening (required)...',
    description: 'Placeholder for reopen reason textarea.',
    surface: 'timesheets',
    maxChars: 50,
  },
  NLSTimesheetsApprovalsEmptyTitle: {
    text: 'No timesheets to show',
    description: 'Empty state title for approvals queue.',
    surface: 'timesheets',
    maxChars: 35,
  },
  NLSTimesheetsApprovalsEmptyDescription: {
    text: 'There are no timesheets matching the selected filter.',
    description: 'Empty state description for approvals queue.',
    surface: 'timesheets',
    maxChars: 70,
  },
  NLSTimesheetsCannotReviewPartialNotice: {
    text: 'Some entries outside your edit access',
    description: 'Notice explaining why an editor cannot review a week with entries outside their permissions.',
    surface: 'timesheets',
    maxChars: 50,
  },
  NLSTimesheetsBulkApproveComplete: {
    text: 'Bulk approval complete: {succeeded} approved, {failed} failed.',
    description: 'Toast message after bulk approval run.',
    surface: 'timesheets',
    maxChars: 70,
  },
  NLSTimesheetsWeekApprovedToast: {
    text: 'Timesheet week approved.',
    description: 'Toast message after single week approval.',
    surface: 'timesheets',
    maxChars: 35,
  },
  NLSTimesheetsWeekRejectedToast: {
    text: 'Timesheet week rejected.',
    description: 'Toast message after single week rejection.',
    surface: 'timesheets',
    maxChars: 35,
  },
  NLSTimesheetsWeekReopenedToast: {
    text: 'Timesheet week reopened.',
    description: 'Toast message after single week reopen.',
    surface: 'timesheets',
    maxChars: 35,
  },
  NLSTimesheetsWeekRejectedNotice: {
    text: 'This timesheet was rejected.',
    description: 'Notice banner on My Time tab when a week was rejected.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsResubmitWeek: {
    text: 'Resubmit week',
    description: 'Button label to resubmit a rejected timesheet week.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsAutoBanner: {
    text: '{count} automatic entries to review',
    description: 'Banner message showing number of unconfirmed automatic drafts to review.',
    surface: 'timesheets',
    maxChars: 50,
  },
  NLSTimesheetsAutoChip: {
    text: 'Auto',
    description: 'Chip label indicating an entry was generated automatically.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsAutoOverlapsChip: {
    text: 'Overlaps',
    description: 'Warning chip indicating an automatic entry overlaps with existing time.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsAutoKeep: {
    text: 'Keep',
    description: 'Button to confirm and keep an automatic draft entry.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsAutoDelete: {
    text: 'Delete',
    description: 'Button to delete an unconfirmed automatic draft entry.',
    surface: 'timesheets',
    maxChars: 15,
  },
  NLSTimesheetsAutoDeleteConfirmTitle: {
    text: 'Delete automatic entry',
    description: 'Title for confirmation modal when deleting an automatic draft.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsAutoDeleteConfirmBody: {
    text: 'Are you sure you want to delete this automatic time entry?',
    description: 'Body text for confirmation modal when deleting an automatic draft.',
    surface: 'timesheets',
    maxChars: 100,
  },
  NLSTimesheetsEntryCount: {
    text: '{count} entries',
    description: 'Display for number of entries in a timesheet week.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsBillableMinutesTotal: {
    text: '{hours} billable',
    description: 'Display for billable hours in a timesheet week.',
    surface: 'timesheets',
    maxChars: 25,
  },
  NLSTimesheetsShowingFirst200: {
    text: 'Showing the first 200 entries',
    description: 'Notice displayed when entries in an approval week are capped at 200.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsTeamTabTitle: {
    text: 'Team',
    description: 'Timesheets team tab title.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsTeamLiveBoard: {
    text: "Who's working now",
    description: 'Live team clock board section header.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsTeamEmpty: {
    text: 'Nobody is clocked in right now',
    description: 'Empty state message when no team members are clocked in.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsTeamSince: {
    text: 'since {time}',
    description: 'Live elapsed duration since user clocked in.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsTeamLocation: {
    text: 'Location',
    description: 'Location chip label on team clock board.',
    surface: 'timesheets',
    maxChars: 16,
  },
  NLSTimesheetsTeamRefresh: {
    text: 'Refresh',
    description: 'Refresh team clock board button.',
    surface: 'timesheets',
    maxChars: 16,
  },
  NLSTimesheetsExpectedWeeklyHours: {
    text: 'Expected weekly hours',
    description: 'Capacity field label for member expected weekly hours.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsSettingsExpectedWeeklyThreshold: {
    text: 'Expected weekly capacity (hours)',
    description: 'Timesheet settings expected weekly capacity field label.',
    surface: 'timesheets',
    maxChars: 40,
  },
  NLSTimesheetsSettingsExpectedWeeklyHelp: {
    text: 'Default baseline hours per week used for team utilisation calculations.',
    description: 'Timesheet settings expected weekly capacity help text.',
    surface: 'timesheets',
    maxChars: 100,
  },
  NLSTimesheetsReportTemplateWeeklyHours: {
    text: 'Weekly Hours',
    description: 'Report template weekly hours tab.',
    surface: 'timesheets',
    maxChars: 24,
  },
  NLSTimesheetsReportTemplateBillableSummary: {
    text: 'Billable Summary',
    description: 'Report template billable summary tab.',
    surface: 'timesheets',
    maxChars: 24,
  },
  NLSTimesheetsReportTemplatePlannedVsActual: {
    text: 'Planned vs Actual',
    description: 'Report template planned vs actual tab.',
    surface: 'timesheets',
    maxChars: 24,
  },
  NLSTimesheetsReportTemplateUtilisation: {
    text: 'Utilisation',
    description: 'Report template utilisation tab.',
    surface: 'timesheets',
    maxChars: 24,
  },
  NLSTimesheetsReportIncludeSubmitted: {
    text: 'Include submitted',
    description: 'Toggle to include submitted timesheets in report.',
    surface: 'timesheets',
    maxChars: 30,
  },
  NLSTimesheetsReportIncludesSubmittedBadge: {
    text: 'Includes submitted',
    description: 'Badge indicating report includes submitted time.',
    surface: 'timesheets',
    maxChars: 24,
  },
  NLSTimesheetsReportDownloadCsv: {
    text: 'Download CSV',
    description: 'Download CSV export button for team reports.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsReportBillableShare: {
    text: 'Billable share',
    description: 'Header or label for billable share percentage.',
    surface: 'timesheets',
    maxChars: 20,
  },
  NLSTimesheetsReportCapacityUpdated: {
    text: 'Capacity updated',
    description: 'Toast notification on capacity update.',
    surface: 'timesheets',
    maxChars: 24,
  },
  NLSOpsApprovalLegalDisclaimer: {
    text: 'acknowledgement, not a legal e-signature',
    description: 'Legal disclaimer label shown on outside approval snapshot and review.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsApprovalAsksForApproval: {
    text: 'asks for your approval',
    description: 'Subtitle on approval landing header.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsApprovalVersion: {
    text: 'Version {version}',
    description: 'Version label for snapshot.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsApprovalExpiresOn: {
    text: 'Link expires {date} · only this item is visible.',
    description: 'Expiry line on approval review.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalRunsOnTimelining: {
    text: 'Runs on Timelining.',
    description: 'Branding footer note.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsApprovalPhonePrompt: {
    text: 'We will send a 6-digit verification code to your phone.',
    description: 'Prompt on phone entry step.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsApprovalPhoneMismatch: {
    text: 'Phone number does not match approver.',
    description: 'Error when entered phone does not match masked phone.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsApprovalSendCode: {
    text: 'Send code',
    description: 'Button to send OTP code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsApprovalCodeSentTo: {
    text: 'We sent a 6-digit code to {phone}.',
    description: 'Notice on code entry step.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsApprovalVerifyCode: {
    text: 'Verify code',
    description: 'Button to verify entered code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsApprovalResendCode: {
    text: 'Resend code',
    description: 'Button to resend OTP code.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsApprovalInvalidCode: {
    text: 'Invalid code. Please check and try again.',
    description: 'Error for incorrect OTP code.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsApprovalCapReached: {
    text: 'Maximum verification attempts reached for this approval. Please contact the sender.',
    description: 'Error message when SMS cap is reached.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsApprovalNotFound: {
    text: 'Approval link not found.',
    description: 'Error state when approval link does not exist.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsApprovalRevoked: {
    text: 'This approval link has been revoked.',
    description: 'Error state when approval has been revoked.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsApprovalExpired: {
    text: 'This approval link has expired.',
    description: 'Error state when approval has expired.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsApprovalClosed: {
    text: 'This approval link is closed.',
    description: 'Error state when approval is closed.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsApprovalAlreadyDecided: {
    text: 'This approval has already been decided.',
    description: 'Notice when approval was already decided.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsApprovalPermissionDenied: {
    text: 'You do not have permission to access this approval.',
    description: 'Error when phone number or user is not authorized.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalNetworkError: {
    text: 'Network connection issue. Please check your connection and try again.',
    description: 'Network error message.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsApprovalGenericError: {
    text: 'An unexpected error occurred. Please try again.',
    description: 'Generic fallback error for outside approval.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalApprove: {
    text: 'Approve',
    description: 'Button to approve.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsApprovalReject: {
    text: 'Reject',
    description: 'Button to reject.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsApprovalComment: {
    text: 'Comment',
    description: 'Button to submit a comment.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsApprovalCommentOptional: {
    text: 'Comment (optional)',
    description: 'Label for optional comment input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsApprovalReasonRequired: {
    text: 'Reason for rejection (required, 3–1000 characters)',
    description: 'Label for rejection reason.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalCommentCountLeft: {
    text: '{count} comments remaining',
    description: 'Count of remaining allowed comments.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsApprovalDoneApprovedTitle: {
    text: 'You approved this on {time}.',
    description: 'Done state confirmation header when approved.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalDoneRejectedTitle: {
    text: 'You rejected this on {time}.',
    description: 'Done state confirmation header when rejected.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalDoneDecidedTitle: {
    text: 'You decided this on {time}.',
    description: 'Done state confirmation header when decided.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalDoneTitle: {
    text: 'You {decision} this on {time}.',
    description: 'Done state confirmation header.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalDoneMessage: {
    text: '{workspace} has your answer. You can close this page.',
    description: 'Done state confirmation message.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalDoneRevisionRequestedTitle: {
    text: 'You requested changes on {time}.',
    description: 'Done state confirmation header when revision requested.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalAskChanges: {
    text: 'Ask for changes',
    description: 'Button label to request changes on a change request.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsSendForOutsideApproval: {
    text: 'Send for outside approval',
    description: 'Action button to open outside approval dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsOutsideApprovalsTitle: {
    text: 'Outside approvals',
    description: 'Section heading for outside approvals.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsApprovalModeAll: {
    text: 'ALL',
    description: 'Mode label for requiring all approvers.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsApprovalModeAny: {
    text: 'ANY',
    description: 'Mode label for requiring any approver.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsApprovalModeAllDesc: {
    text: 'Every approver must approve.',
    description: 'Description for ALL mode.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsApprovalModeAnyDesc: {
    text: 'First approver approves for all.',
    description: 'Description for ANY mode.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsApprovalApproversLabel: {
    text: 'Approvers (1–5)',
    description: 'Label for approvers list in send dialog.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsApprovalAddApprover: {
    text: '+ Add approver',
    description: 'Button to add another approver.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsApprovalRemoveApprover: {
    text: 'Remove',
    description: 'Button to remove an approver.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsApprovalApproverName: {
    text: 'Approver name',
    description: 'Placeholder/label for approver name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsApprovalApproverPhone: {
    text: 'Phone number',
    description: 'Placeholder/label for approver phone.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsApprovalExpiryDays: {
    text: 'Expires in (1–30 days)',
    description: 'Label for expiry days input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsApprovalMessage: {
    text: 'Message (optional)',
    description: 'Label for optional message input.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsApprovalSmsNotice: {
    text: 'Each approver receives a text message code (up to 5 per approver).',
    description: 'Notice about SMS usage in send dialog.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsApprovalDistinctPhonesError: {
    text: 'Each approver must have a distinct phone number.',
    description: 'Validation error when phones are duplicated.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsApprovalNameRequiredError: {
    text: 'Approver name must be 1–80 characters.',
    description: 'Validation error for approver name.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsApprovalPhoneInvalidError: {
    text: 'Please enter a valid phone number.',
    description: 'Validation error for invalid phone.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsApprovalLinksShownOnce: {
    text: 'Approval links (shown once — cannot be displayed again)',
    description: 'Header for generated links modal.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsApprovalCopyLink: {
    text: 'Copy link',
    description: 'Button to copy approval link.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsApprovalLinkCopied: {
    text: 'Copied!',
    description: 'Confirmation toast when link copied.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsApprovalShareLink: {
    text: 'Share',
    description: 'Button to share approval link.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsApprovalReissueLink: {
    text: 'Reissue link',
    description: 'Button to reissue an approver link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsApprovalRevoke: {
    text: 'Revoke',
    description: 'Button to revoke an outside approval.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsApprovalRevokeConfirm: {
    text: 'Revoke outside approval',
    description: 'Modal title to confirm revoke.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsApprovalRevokeReason: {
    text: 'Reason for revocation (3–500 characters)',
    description: 'Label for revocation reason input.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsApprovalItemChangedWarning: {
    text: 'Item has changed since sent for approval',
    description: 'Warning when subjectVersion differs from live version.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsApprovalAdminOnlyToggle: {
    text: 'Admin-only outside approval',
    description: 'Toggle label for admin-only approval setting on request kinds and templates.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsApprovalAdminOnlyDesc: {
    text: 'Only Owners and Admins can send this item for outside approval.',
    description: 'Explanation for admin-only approval toggle.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsApprovalStaleVersionError: {
    text: 'Settings were modified by another user. Please reload the page.',
    description: 'Error when approval settings version is stale.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsApprovalMaxAdminOnlyKindsError: {
    text: 'Maximum of 200 Admin-only approval kinds reached.',
    description: 'Error when max admin-only kinds limit is hit.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCsvImportTitle: {
    text: 'Import {entity} from CSV',
    description: 'Title for ops CSV bulk import dialog.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsCsvExportTitle: {
    text: 'Export {entity} to CSV',
    description: 'Title for ops CSV export action.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsCsvStepUpload: {
    text: 'Upload',
    description: 'Step 1 in ops CSV wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvStepMap: {
    text: 'Map columns',
    description: 'Step 2 in ops CSV wizard.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvStepDryRun: {
    text: 'Review',
    description: 'Step 3 in ops CSV wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvStepApply: {
    text: 'Apply',
    description: 'Step 4 in ops CSV wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvActionImport: {
    text: 'Import',
    description: 'Import button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvActionExport: {
    text: 'Export',
    description: 'Export button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvActionNext: {
    text: 'Next',
    description: 'Next step button in wizard.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvActionDryRun: {
    text: 'Run dry run',
    description: 'Run dry run button label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvActionApply: {
    text: 'Apply import',
    description: 'Apply import button label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvActionResume: {
    text: 'Resume',
    description: 'Resume import button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvActionDownloadErrors: {
    text: 'Download errors CSV',
    description: 'Download errors button label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsCsvActionSkipColumn: {
    text: 'Skip',
    description: 'Skip column mapping option.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvFileHeaderLabel: {
    text: 'File column',
    description: 'Header for CSV file column.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsCsvDestinationColumnLabel: {
    text: 'Maps to',
    description: 'Header for destination property select.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsCsvRequiredBadge: {
    text: 'Required',
    description: 'Badge for required columns.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvDuplicateMappingError: {
    text: "Column '{column}' is mapped multiple times.",
    description: 'Error when column is mapped more than once.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsCsvUnmappedRequiredWarning: {
    text: 'Missing required columns: {columns}',
    description: 'Warning when required columns are unmapped.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsCsvDryRunSummaryTotal: {
    text: 'Total: {count}',
    description: 'Dry run summary total rows badge.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvDryRunSummaryValid: {
    text: 'Valid: {count}',
    description: 'Dry run summary valid rows badge.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvDryRunSummaryCreate: {
    text: 'To create: {count}',
    description: 'Dry run summary create count badge.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvDryRunSummaryUpdate: {
    text: 'To update: {count}',
    description: 'Dry run summary update count badge.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvDryRunSummaryUnchanged: {
    text: 'Unchanged: {count}',
    description: 'Dry run summary unchanged count badge.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvDryRunSummaryErrors: {
    text: 'Errors: {count}',
    description: 'Dry run summary error count badge.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvDryRunSummaryWarnings: {
    text: 'Warnings: {count}',
    description: 'Dry run summary warning count badge.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvPreviewTableTitle: {
    text: 'Preview (first {count} rows)',
    description: 'Heading for preview table in dry run step.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCsvRowErrorsTableTitle: {
    text: 'Validation errors ({count})',
    description: 'Heading for row errors table.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCsvRowWarningsTableTitle: {
    text: 'Warnings ({count})',
    description: 'Heading for row warnings table.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCsvColRowNumber: {
    text: 'Row',
    description: 'Table column header for row number.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsCsvColField: {
    text: 'Column',
    description: 'Table column header for field/column name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColError: {
    text: 'Error',
    description: 'Table column header for error message.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColWarning: {
    text: 'Warning',
    description: 'Table column header for warning message.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvApplyingProgress: {
    text: 'Applying import: {processed} of {total} rows...',
    description: 'Progress message while applying import.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCsvApplyComplete: {
    text: 'Import complete!',
    description: 'Success heading when apply finishes.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsCsvApplyCompleteDesc: {
    text: 'Successfully processed {total} rows ({created} created, {updated} updated).',
    description: 'Success description after import applies.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsCsvApplyPartial: {
    text: 'Import finished with partial errors',
    description: 'Heading when apply finishes with partial errors.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCsvApplyPartialDesc: {
    text: 'Some rows could not be applied. Review errors below or click Resume to retry.',
    description: 'Description for partial import results.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsCsvApplyFailed: {
    text: 'Import apply failed',
    description: 'Heading when apply fails.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCsvGenericError: {
    text: 'An error occurred during import.',
    description: 'Generic error message for ops import.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCsvNetworkError: {
    text: 'Network error or timeout. Please check your connection and resume.',
    description: 'Network error message for ops import.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsCsvFileErrorTooLarge: {
    text: 'The CSV file exceeds the 1 MB limit.',
    description: 'File error when file is too large.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCsvFileErrorTooManyRows: {
    text: 'The CSV file exceeds the limit of 2,000 data rows.',
    description: 'File error when row count exceeds limit.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsCsvFileErrorTooManyColumns: {
    text: 'The CSV file exceeds the limit of 64 columns.',
    description: 'File error when column count exceeds limit.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCsvFileErrorCellTooLong: {
    text: 'A cell in the CSV file exceeds 1,000 characters.',
    description: 'File error when cell text exceeds limit.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsCsvFileErrorUnterminatedQuote: {
    text: 'The CSV file contains an unterminated quoted field.',
    description: 'File error on unterminated quote.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsCsvFileErrorParseError: {
    text: 'Could not parse the CSV file. Please verify it is a valid CSV.',
    description: 'File error on general parse error.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsCsvFileErrorMissingColumns: {
    text: 'The CSV file is missing required columns: {columns}',
    description: 'File error when required columns are missing.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsCsvFileErrorRowErrors: {
    text: 'The CSV file contains validation errors that must be fixed before applying.',
    description: 'File error when apply is blocked by row errors.',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsCsvFileErrorOpIdReused: {
    text: 'This operation ID was already used with different parameters.',
    description: 'File error on opId reuse mismatch.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsCsvFileErrorExportTooLarge: {
    text: 'Export exceeds 10,000 rows. Please filter by place.',
    description: 'Error when export exceeds max rows limit.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsCsvFileErrorNoPlaceLevel: {
    text: 'Cannot determine place level hierarchy for this sector.',
    description: 'Error when place level resolution fails.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsCsvPermissionDenied: {
    text: 'You do not have permission to import or export this data.',
    description: 'Permission denied error for ops CSV.',
    surface: 'shell',
    maxChars: 75,
  },
  NLSOpsCsvRowErrorRequired: {
    text: 'Required value is missing.',
    description: 'Row error for REQUIRED code.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCsvRowErrorTooLong: {
    text: 'Value exceeds maximum allowed length.',
    description: 'Row error for TOO_LONG code.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsCsvRowErrorBadNumber: {
    text: 'Must be a valid number.',
    description: 'Row error for BAD_NUMBER code.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCsvRowErrorBadDate: {
    text: 'Must be a valid date (YYYY-MM-DD).',
    description: 'Row error for BAD_DATE code.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsCsvRowErrorBadValue: {
    text: 'Value is not one of the allowed options.',
    description: 'Row error for BAD_VALUE code.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsCsvRowErrorDuplicateInFile: {
    text: 'Duplicate key in this file.',
    description: 'Row error for DUPLICATE_IN_FILE code.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCsvRowErrorUnknownPlace: {
    text: 'Place not found in workspace.',
    description: 'Row error for UNKNOWN_PLACE code.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsCsvRowErrorUnknownParty: {
    text: 'Party not found in workspace.',
    description: 'Row error for UNKNOWN_PARTY code.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsCsvRowErrorUnknownSupplier: {
    text: 'Supplier not found in workspace.',
    description: 'Row error for UNKNOWN_SUPPLIER code.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsCsvRowErrorAmbiguousSupplier: {
    text: 'Multiple suppliers match this name.',
    description: 'Row error for AMBIGUOUS_SUPPLIER code.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsCsvRowErrorNotAMember: {
    text: 'Email does not match an active workspace member.',
    description: 'Row error for NOT_A_MEMBER code.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCsvRowErrorRoleNotAllowed: {
    text: 'Sector role is not permitted.',
    description: 'Row error for ROLE_NOT_ALLOWED code.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsCsvRowErrorQuantityIgnored: {
    text: 'Quantity column ignored for existing stock item.',
    description: 'Row warning for QUANTITY_IGNORED code.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsCsvRowErrorBadSector: {
    text: 'Sector is invalid or disabled.',
    description: 'Row error for BAD_SECTOR code.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsCsvRowErrorCoreRefused: {
    text: 'The server refused this row during apply.',
    description: 'Row error for CORE_REFUSED code.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSOpsCsvRowErrorGeneric: {
    text: 'Invalid row data.',
    description: 'Generic fallback row error for ops CSV import.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCsvExportPlaces: {
    text: 'Export places',
    description: 'Button to export places CSV.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvImportPlaces: {
    text: 'Import places',
    description: 'Button to import places CSV.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvExportAssets: {
    text: 'Export assets',
    description: 'Button to export assets CSV.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvImportAssets: {
    text: 'Import assets',
    description: 'Button to import assets CSV.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvExportWorkers: {
    text: 'Export workers',
    description: 'Button to export workers CSV.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvImportWorkers: {
    text: 'Import workers',
    description: 'Button to import workers CSV.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvExportParties: {
    text: 'Export parties',
    description: 'Button to export parties CSV.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvImportParties: {
    text: 'Import parties',
    description: 'Button to import parties CSV.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvExportStaff: {
    text: 'Export staff roles',
    description: 'Button to export staff sector roles CSV.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsCsvImportStaff: {
    text: 'Import staff roles',
    description: 'Button to import staff sector roles CSV.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsCsvExportStock: {
    text: 'Export stock items',
    description: 'Button to export stock items CSV.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsCsvExportEmptyTemplate: {
    text: 'Blank template downloaded.',
    description: 'Toast when export is empty template.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsCsvEntityPlaces: {
    text: 'Places',
    description: 'Entity label for Places in CSV import/export.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvEntityAssets: {
    text: 'Assets',
    description: 'Entity label for Assets in CSV import/export.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvEntityWorkers: {
    text: 'Workers',
    description: 'Entity label for Workers in CSV import/export.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvEntityParties: {
    text: 'Parties',
    description: 'Entity label for Parties in CSV import/export.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvEntityStaff: {
    text: 'Staff',
    description: 'Entity label for Staff in CSV import/export.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvEntityStockItems: {
    text: 'Stock Items',
    description: 'Entity label for Stock Items in CSV import/export.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvEntityDepartures: {
    text: 'Departures',
    description: 'Entity label for Departures in CSV import.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvEntityJobSites: {
    text: 'Job Sites',
    description: 'Entity label for Job Sites in CSV import.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvEntityPlantMachines: {
    text: 'Plant Machines',
    description: 'Entity label for Plant Machines in CSV import.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColJobSitesCustomerName: {
    text: 'Customer Name',
    description: 'CSV column label for customer name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColJobSitesPhone: {
    text: 'Phone',
    description: 'CSV column label for customer phone.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColJobSitesEmail: {
    text: 'Email',
    description: 'CSV column label for customer email.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColJobSitesSiteName: {
    text: 'Site Name',
    description: 'CSV column label for job site name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColJobSitesLine1: {
    text: 'Address Line 1',
    description: 'CSV column label for site address line 1.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColJobSitesLine2: {
    text: 'Address Line 2',
    description: 'CSV column label for site address line 2.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColJobSitesLocality: {
    text: 'Locality',
    description: 'CSV column label for site locality.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColJobSitesCity: {
    text: 'City',
    description: 'CSV column label for site city.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColJobSitesRegion: {
    text: 'Region',
    description: 'CSV column label for site region/state.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColJobSitesPostcode: {
    text: 'Postcode',
    description: 'CSV column label for site postcode.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColJobSitesServiceArea: {
    text: 'Service Area',
    description: 'CSV column label for site service area.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColDeparturesProperty: {
    text: 'Property',
    description: 'CSV column label for departures property.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColDeparturesRoom: {
    text: 'Room',
    description: 'CSV column label for departures room.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColDeparturesTime: {
    text: 'Departure Time',
    description: 'CSV column label for departures scheduled time.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColPlacesPath: {
    text: 'Path',
    description: 'CSV column label for place path.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColPlacesId: {
    text: 'Place ID',
    description: 'CSV column label for place id.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColAssetsId: {
    text: 'Asset ID',
    description: 'CSV column label for asset id.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColAssetsCreatedAt: {
    text: 'Created At',
    description: 'CSV column label for asset created at.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColStaffRoles: {
    text: 'Sector Roles',
    description: 'CSV column label for staff sector roles.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColStaffUid: {
    text: 'User ID',
    description: 'CSV column label for staff user id.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColWorkersSkills: {
    text: 'Skills',
    description: 'CSV column label for worker skills.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColWorkersId: {
    text: 'Worker ID',
    description: 'CSV column label for worker id.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColWorkersStatus: {
    text: 'Status',
    description: 'CSV column label for worker status.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColPartiesContactName: {
    text: 'Contact Name',
    description: 'CSV column label for party contact name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColPartiesContactAddress: {
    text: 'Contact Address',
    description: 'CSV column label for party contact address.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsCsvColPartiesId: {
    text: 'Party ID',
    description: 'CSV column label for party id.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColStockItemsSku: {
    text: 'SKU',
    description: 'CSV column label for stock item SKU.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsCsvColStockItemsName: {
    text: 'Item Name',
    description: 'CSV column label for stock item name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColStockItemsUnit: {
    text: 'Unit of Measure',
    description: 'CSV column label for stock item unit.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColStockItemsCategory: {
    text: 'Category',
    description: 'CSV column label for stock item category.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColStockItemsLocation: {
    text: 'Location',
    description: 'CSV column label for stock item location.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColStockItemsCostPerUnit: {
    text: 'Cost per Unit',
    description: 'CSV column label for stock item cost per unit.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColStockItemsMinThreshold: {
    text: 'Min Threshold',
    description: 'CSV column label for stock item minimum threshold.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColStockItemsOpeningQuantity: {
    text: 'Opening Quantity',
    description: 'CSV column label for stock item opening quantity.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvColStockItemsSupplier: {
    text: 'Supplier',
    description: 'CSV column label for stock item supplier.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColStockItemsBarcode: {
    text: 'Barcode',
    description: 'CSV column label for stock item barcode.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColStockItemsId: {
    text: 'Stock Item ID',
    description: 'CSV column label for stock item id.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvColStockItemsCurrentQuantity: {
    text: 'Current Quantity',
    description: 'CSV column label for stock item current quantity.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsCsvErrorsHeaderRow: {
    text: 'Row',
    description: 'Header for row column in downloaded import errors CSV.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsCsvErrorsHeaderColumn: {
    text: 'Column',
    description: 'Header for column name in downloaded import errors CSV.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsCsvErrorsHeaderCode: {
    text: 'Code',
    description: 'Header for error code in downloaded import errors CSV.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsCsvErrorsHeaderError: {
    text: 'Error',
    description: 'Header for error message in downloaded import errors CSV.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConProjectsTitle: {
    text: 'Construction Projects',
    description: 'Header title for construction projects screen.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConProjectsSubtitle: {
    text: 'Manage projects, trade packages, and role approvers.',
    description: 'Subtitle for construction projects screen.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConNewProject: {
    text: 'New project',
    description: 'Button label to create a new construction project.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConEditProject: {
    text: 'Edit project',
    description: 'Button label to edit a construction project.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConProjectDetails: {
    text: 'Project details',
    description: 'Title for project details view.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConProjectName: {
    text: 'Project name',
    description: 'Field label for project name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConProjectCode: {
    text: 'Project code',
    description: 'Field label for project code.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConProjectSitePlace: {
    text: 'Site place',
    description: 'Field label for site place.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConProjectClientParty: {
    text: 'Client party',
    description: 'Field label for client party.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConProjectStartDate: {
    text: 'Start date',
    description: 'Field label for project start date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConProjectEndDate: {
    text: 'End date',
    description: 'Field label for project end date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConProjectStatus: {
    text: 'Project status',
    description: 'Field label for project status.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConPackagesTab: {
    text: 'Trade packages',
    description: 'Tab label for project packages.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConApproversTab: {
    text: 'Role approvers',
    description: 'Tab label for project approvers.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConAddPackage: {
    text: 'Add package',
    description: 'Button label to add a package.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConPackageKey: {
    text: 'Package key',
    description: 'Field label for package slug key.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConPackageName: {
    text: 'Package name',
    description: 'Field label for package name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConPackageTrade: {
    text: 'Trade',
    description: 'Field label for trade description.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConPackageContractor: {
    text: 'Contractor party',
    description: 'Field label for contractor party.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDrawingsTitle: {
    text: 'Drawing Register',
    description: 'Header title for drawing register screen.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConDrawingsSubtitle: {
    text: 'Register, revise, supersede, and track construction drawings.',
    description: 'Subtitle for drawing register screen.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConAddDrawing: {
    text: 'Add drawing',
    description: 'Button label to add a drawing.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConIssueRevision: {
    text: 'Issue revision',
    description: 'Button label to issue drawing revision.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDrawingNumber: {
    text: 'Drawing number',
    description: 'Field label for drawing number.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDrawingTitle: {
    text: 'Drawing title',
    description: 'Field label for drawing title.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDrawingDiscipline: {
    text: 'Discipline',
    description: 'Field label for drawing discipline.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDrawingPackage: {
    text: 'Trade package',
    description: 'Field label for trade package.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDrawingInitialRevision: {
    text: 'Initial revision',
    description: 'Field label for initial revision.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDrawingRevision: {
    text: 'Revision',
    description: 'Field label for drawing revision.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDrawingLink: {
    text: 'Drawing URL (https)',
    description: 'Field label for drawing link URL.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDrawingNote: {
    text: 'Revision note',
    description: 'Field label for revision note.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConWithdrawDrawing: {
    text: 'Withdraw drawing',
    description: 'Button label to withdraw drawing.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConWithdrawConfirmTitle: {
    text: 'Withdraw drawing',
    description: 'Confirmation dialog title for withdrawing a drawing.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConWithdrawConfirmBody: {
    text: 'Are you sure you want to withdraw this drawing? Withdrawn drawings cannot receive new revisions.',
    description: 'Confirmation dialog body for withdrawing a drawing.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsConRevisionHistory: {
    text: 'Revision history',
    description: 'Header for drawing revision history.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConSupersedeWarning: {
    text: 'Revision will supersede current revision. Open requests & records citing the current revision will be flagged and receive an acknowledgement request.',
    description: 'Warning message shown when issuing a new revision.',
    surface: 'shell',
    maxChars: 160,
  },
  NLSOpsConNoProjects: {
    text: 'No construction projects found.',
    description: 'Empty state message when no projects are found.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConNoDrawings: {
    text: 'No drawings found for this project.',
    description: 'Empty state message when no drawings are found.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConCancel: {
    text: 'Cancel',
    description: 'Cancel button label in construction dialogs.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConSave: {
    text: 'Create Project',
    description: 'Save/create button label in construction dialogs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConClose: {
    text: 'Close',
    description: 'Close button label in construction drawers.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConTodayTitle: {
    text: 'Today & DPR',
    description: 'Title for Construction Today and DPR tab.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConTodaySubtitle: {
    text: 'Daily production, site diary, shift progress, and open hindrances.',
    description: 'Subtitle for Construction Today tab.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSOpsConTodayShift: {
    text: 'Shift: {shift}',
    description: 'Shift label for today card.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConTodayDprStatus: {
    text: 'DPR Status',
    description: 'DPR status label on today screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConTodayDueChecks: {
    text: 'Due Checks',
    description: 'Due checks section title on today screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConTodayHindrances: {
    text: 'Open Hindrances',
    description: 'Open hindrances section title on today screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConTodayRollProvisional: {
    text: "Today's Roll (Provisional)",
    description: "Today's attendance roll title labelled provisional on today screen.",
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConTodayHeadcount: {
    text: 'Headcount: {count}',
    description: 'Total gang headcount on today screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConTodayDiary: {
    text: 'Site Diary',
    description: 'Site diary feed title on today screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConTodayAddDiaryEntry: {
    text: 'Add Diary Entry',
    description: 'Add diary entry button label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDiaryKind: {
    text: 'Event Kind',
    description: 'Diary entry event kind field label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDiaryText: {
    text: 'Diary Note',
    description: 'Diary entry text field label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDiaryOccurredAt: {
    text: 'Claimed Occurred Time',
    description: 'Diary entry occurred time field label.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConDiarySupersedes: {
    text: 'Supersedes Entry ID',
    description: 'Superseded diary entry id field label.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConCreateDpr: {
    text: 'Create DPR',
    description: 'Create DPR button label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConEditDpr: {
    text: 'Edit DPR',
    description: 'Edit DPR button label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConReviewDpr: {
    text: 'Review DPR',
    description: 'Review DPR button label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConApproveDpr: {
    text: 'Approve DPR',
    description: 'Approve DPR button label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConViewDpr: {
    text: 'View DPR',
    description: 'View DPR button label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDprEditorTitle: {
    text: 'Daily Progress Report Editor',
    description: 'DPR editor drawer or modal title.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConDprReviewTitle: {
    text: 'DPR Review & Approval',
    description: 'DPR review drawer or modal title.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConDprWeather: {
    text: 'Weather',
    description: 'Weather observations field label in DPR.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprSafetyExceptions: {
    text: 'Safety Exceptions',
    description: 'Safety exceptions checkbox label in DPR.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConDprSafetyNotes: {
    text: 'Safety Notes',
    description: 'Safety notes text field label in DPR.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConDprKeyReceipts: {
    text: 'Key Receipts',
    description: 'Key material/equipment receipts field label in DPR.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConDprInspectionRefs: {
    text: 'Inspection / Test References',
    description: 'Inspection and test references field label in DPR.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConDprLines: {
    text: 'Production Lines',
    description: 'DPR production lines section title.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConDprAddLine: {
    text: 'Add Line',
    description: 'Add production line button label.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConDprRemoveLine: {
    text: 'Remove',
    description: 'Remove production line button label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConDprLinePlace: {
    text: 'Location',
    description: 'DPR line location field label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprLineTrade: {
    text: 'Trade',
    description: 'DPR line trade field label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprLinePackage: {
    text: 'Package',
    description: 'DPR line trade package field label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprLineActivity: {
    text: 'Activity',
    description: 'DPR line activity field label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprLinePlannedQty: {
    text: 'Planned Qty',
    description: 'DPR line planned quantity field label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprLineDoneQty: {
    text: 'Done Qty',
    description: 'DPR line completed quantity field label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprLineUnit: {
    text: 'Unit',
    description: 'DPR line unit field label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConDprLineGangs: {
    text: 'Gangs / Labour Headcount',
    description: 'DPR line gangs section label.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConDprLineEquipment: {
    text: 'Equipment Deployed',
    description: 'DPR line equipment section label.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConDprLineHindrance: {
    text: 'Hindrances',
    description: 'DPR line hindrances link field label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprSaveDraft: {
    text: 'Save as Draft',
    description: 'Save DPR as draft button label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprSubmit: {
    text: 'Submit DPR',
    description: 'Submit DPR button label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprReviewButton: {
    text: 'Record Review',
    description: 'Record review action button label in DPR review.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprReturnButton: {
    text: 'Return to Author',
    description: 'Return DPR action button label in DPR review.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprApproveButton: {
    text: 'Approve DPR',
    description: 'Approve DPR action button label in DPR review.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConDprReturnReason: {
    text: 'Return Reason (required)',
    description: 'Return reason prompt label in DPR review.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConDprReviewNotes: {
    text: 'Review Notes (optional)',
    description: 'Review notes prompt label in DPR review.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConDprAmendments: {
    text: 'Amendment History',
    description: 'Amendment and correction history section title in DPR review.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSMemberTabBookings: {
    text: 'Bookings',
    description: 'Tab label for Member Bookings.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsTitle: {
    text: 'My Bookings',
    description: 'Header title for member bookings view.',
    surface: 'member',
    maxChars: 30,
  },
  NLSMemberBookingsSubtitle: {
    text: 'Upcoming and past bookings, passes, memberships, and QR check-in.',
    description: 'Subtitle for member bookings view.',
    surface: 'member',
    maxChars: 100,
  },
  NLSMemberBookingsLinkCardTitle: {
    text: 'Link your bookings',
    description: 'Title for link bookings card.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberBookingsLinkCardDesc: {
    text: 'Link your account phone to access your memberships, passes, and session bookings at this workspace.',
    description: 'Description for link bookings card.',
    surface: 'member',
    maxChars: 120,
  },
  NLSMemberBookingsLinkAction: {
    text: 'Link my bookings',
    description: 'Button label to link booking record.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsLinkingAction: {
    text: 'Linking…',
    description: 'Busy state for link action.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsLinkSuccess: {
    text: 'Your bookings have been linked successfully.',
    description: 'Success notice when bookings are linked.',
    surface: 'member',
    maxChars: 60,
  },
  NLSMemberBookingsNoPhoneRecord: {
    text: 'No booking record for this phone at {workspace}; ask the desk.',
    description: 'Error message when phone is not found in customer records.',
    surface: 'member',
    maxChars: 100,
  },
  NLSMemberBookingsAddPhoneTitle: {
    text: 'Verify phone number',
    description: 'Title for phone number verification in linking card.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberBookingsAddPhoneDesc: {
    text: 'Enter your phone number to receive a verification code and link your customer record.',
    description: 'Description for phone verification.',
    surface: 'member',
    maxChars: 120,
  },
  NLSMemberBookingsPhonePlaceholder: {
    text: '+91 98765 43210',
    description: 'Placeholder for phone input.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsSendCodeAction: {
    text: 'Send code',
    description: 'Button to send phone verification code.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsSendingCodeAction: {
    text: 'Sending…',
    description: 'Sending verification code button state.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsVerifyCodeTitle: {
    text: 'Enter 6-digit code',
    description: 'Prompt to enter OTP code.',
    surface: 'member',
    maxChars: 30,
  },
  NLSMemberBookingsVerifyCodeAction: {
    text: 'Verify & link',
    description: 'Button to verify code and link bookings.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsVerifyingAction: {
    text: 'Verifying…',
    description: 'Verifying code button state.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsResendCodeAction: {
    text: 'Resend code',
    description: 'Button to resend phone verification code.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsTabUpcoming: {
    text: 'My Bookings',
    description: 'Subtab for my bookings.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsTabBook: {
    text: 'Book',
    description: 'Subtab to book a session.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsTabPlans: {
    text: 'Plans & Dues',
    description: 'Subtab for member plans and dues.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsMyQrAction: {
    text: 'My QR',
    description: 'Button to open personal check-in QR code modal.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsScanQrAction: {
    text: 'Scan Place QR',
    description: 'Button to scan place QR code.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsFilterUpcoming: {
    text: 'Upcoming',
    description: 'Filter chip for upcoming bookings.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsFilterPast: {
    text: 'Past',
    description: 'Filter chip for past bookings.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsEmptyUpcoming: {
    text: 'No upcoming bookings scheduled.',
    description: 'Empty state message for upcoming bookings.',
    surface: 'member',
    maxChars: 60,
  },
  NLSMemberBookingsEmptyPast: {
    text: 'No past booking records found.',
    description: 'Empty state message for past bookings.',
    surface: 'member',
    maxChars: 60,
  },
  NLSMemberBookingsStateBooked: {
    text: 'Booked',
    description: 'Booking status BOOKED.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsStateWaitlisted: {
    text: 'Waitlisted (Position #{position})',
    description: 'Booking status WAITLISTED with position.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberBookingsStateAttended: {
    text: 'Attended',
    description: 'Booking status ATTENDED.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsStateNoShow: {
    text: 'No Show',
    description: 'Booking status NO_SHOW.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsStateCancelled: {
    text: 'Cancelled',
    description: 'Booking status CANCELLED.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsStateLateCancelled: {
    text: 'Late Cancelled',
    description: 'Booking status LATE_CANCELLED.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsCancelAction: {
    text: 'Cancel',
    description: 'Button to cancel booking.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsMoveAction: {
    text: 'Move session',
    description: 'Button to move booking to another occurrence.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsLeaveWaitlistAction: {
    text: 'Leave waitlist',
    description: 'Button to leave waitlist.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsLateCancelWarning: {
    text: 'Late cancel: a credit is used as you are inside the {hours}-hour cancellation window.',
    description: 'Notice warning that late cancellation consumes a credit.',
    surface: 'member',
    maxChars: 120,
  },
  NLSMemberBookingsConfirmCancelTitle: {
    text: 'Cancel booking?',
    description: 'Confirmation title for cancelling booking.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberBookingsConfirmCancelDesc: {
    text: 'Are you sure you want to cancel your session on {time}?',
    description: 'Confirmation description for cancelling booking.',
    surface: 'member',
    maxChars: 100,
  },
  NLSMemberBookingsConfirmLeaveWaitlist: {
    text: 'Are you sure you want to leave the waitlist for this session?',
    description: 'Confirmation prompt to leave waitlist.',
    surface: 'member',
    maxChars: 100,
  },
  NLSMemberBookingsMoveModalTitle: {
    text: 'Move to another session',
    description: 'Title of move session modal.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberBookingsSelectSessionPrompt: {
    text: 'Choose another available date and time:',
    description: 'Prompt for selecting another session to move to.',
    surface: 'member',
    maxChars: 60,
  },
  NLSMemberBookingsNoAlternateSessions: {
    text: 'No other upcoming occurrences available for this bookable.',
    description: 'Notice when no alternate occurrences exist.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsErrorFailedPrecondition: {
    text: 'Unable to complete action: requirement or policy not met.',
    description: 'Error message for failed-precondition.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsErrorResourceExhausted: {
    text: 'Capacity or booking limit reached for this session.',
    description: 'Error message for resource-exhausted.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsErrorPermissionDenied: {
    text: 'You do not have access to book or modify this session.',
    description: 'Error message for permission-denied.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsErrorBlockedByStrikes: {
    text: 'Booking blocked due to repeated no-shows until {date}.',
    description: 'Error message when blocked by strikes with date.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsErrorNotFound: {
    text: 'Requested booking or session was not found.',
    description: 'Error message for not-found.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsErrorUnauthenticated: {
    text: 'Please sign in to complete this booking action.',
    description: 'Error message for unauthenticated.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsErrorDeadlineExceeded: {
    text: 'The booking operation timed out. Please try again.',
    description: 'Error message for deadline-exceeded.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsErrorGeneric: {
    text: 'Something went wrong. Please try again.',
    description: 'Generic error message.',
    surface: 'member',
    maxChars: 60,
  },
  NLSMemberBookingsSectorFilter: {
    text: 'Sector',
    description: 'Sector filter label.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsEmptyBookables: {
    text: 'No bookable sessions available under your active plan(s).',
    description: 'Empty state message when no bookables found.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsBookAction: {
    text: 'Book session',
    description: 'Button to book session.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsJoinWaitlistAction: {
    text: 'Join waitlist',
    description: 'Button to join waitlist.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsSeatsLeft: {
    text: '{count} seats left',
    description: 'Label displaying seats available.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsSessionFull: {
    text: 'Session full',
    description: 'Label indicating session has no seats left.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsBookingSuccess: {
    text: 'Session booked successfully!',
    description: 'Success toast/notice on booking.',
    surface: 'member',
    maxChars: 50,
  },
  NLSMemberBookingsWaitlistSuccess: {
    text: 'Added to waitlist! Position #{position}',
    description: 'Success toast/notice on joining waitlist.',
    surface: 'member',
    maxChars: 60,
  },
  NLSMemberBookingsQrModalTitle: {
    text: 'Personal check-in QR',
    description: 'Title of personal QR code modal.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberBookingsQrCountdown: {
    text: 'Valid for {time} (rotates every 10 minutes)',
    description: 'Countdown timer description for QR.',
    surface: 'member',
    maxChars: 60,
  },
  NLSMemberBookingsQrExpired: {
    text: 'QR code expired, tap to refresh',
    description: 'Expired state label prompting user to refresh.',
    surface: 'member',
    maxChars: 50,
  },
  NLSMemberBookingsQrRefreshAction: {
    text: 'Refresh QR',
    description: 'Button to refresh QR code.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsScanModalTitle: {
    text: 'Scan Place QR to Check In',
    description: 'Title of scan place QR modal.',
    surface: 'member',
    maxChars: 50,
  },
  NLSMemberBookingsScanSuccess: {
    text: 'Successfully checked in to your session at {place}!',
    description: 'Success message after scanning place QR.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsCheckInAction: {
    text: 'Check in to my booking',
    description: 'Button on /pq/ scan page to check in to booking.',
    surface: 'member',
    maxChars: 30,
  },
  NLSMemberBookingsCheckingInAction: {
    text: 'Checking in…',
    description: 'Busy state for checking in to booking.',
    surface: 'member',
    maxChars: 25,
  },
  NLSMemberBookingsScanErrorNoBooking: {
    text: 'No active booking found for you at this place today.',
    description: 'Error when no booking found at place.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsScanErrorWindow: {
    text: 'Check-in window is not active (allowed from 30 mins before start until end).',
    description: 'Error when scan is outside check-in window.',
    surface: 'member',
    maxChars: 90,
  },
  NLSMemberBookingsScanErrorNotLinked: {
    text: 'Your account is not linked to a customer record in this workspace.',
    description: 'Error when account is not linked.',
    surface: 'member',
    maxChars: 80,
  },
  NLSMemberBookingsMembershipsTitle: {
    text: 'My Memberships & Passes',
    description: 'Section title for memberships.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberBookingsEmptyMemberships: {
    text: 'No memberships or passes currently active.',
    description: 'Empty state for memberships.',
    surface: 'member',
    maxChars: 60,
  },
  NLSMemberBookingsValidTo: {
    text: 'Valid until {date}',
    description: 'Validity date label for membership.',
    surface: 'member',
    maxChars: 30,
  },
  NLSMemberBookingsSessionsUsed: {
    text: '{used} of {total} sessions used',
    description: 'Session pack usage count.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberBookingsFrozenRange: {
    text: 'Frozen from {from} to {to}',
    description: 'Frozen membership date range.',
    surface: 'member',
    maxChars: 40,
  },
  NLSMemberBookingsLedgerTitle: {
    text: 'Activity Ledger',
    description: 'Section title for membership ledger entries.',
    surface: 'member',
    maxChars: 30,
  },
  NLSMemberBookingsShowMore: {
    text: 'Show more',
    description: 'Button to reveal more ledger items.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsDuesTitle: {
    text: 'Dues & Receipts',
    description: 'Section title for booking dues.',
    surface: 'member',
    maxChars: 30,
  },
  NLSMemberBookingsEmptyDues: {
    text: 'No outstanding dues or payment records.',
    description: 'Empty state for dues.',
    surface: 'member',
    maxChars: 60,
  },
  NLSMemberBookingsPriceRecordOnly: {
    text: 'Record only, no payment',
    description: 'Label stating fee amounts are records only with no payment transaction.',
    surface: 'member',
    maxChars: 35,
  },
  NLSMemberBookingsDueStateDue: {
    text: 'DUE',
    description: 'Dues status DUE.',
    surface: 'member',
    maxChars: 15,
  },
  NLSMemberBookingsDueStatePaid: {
    text: 'RECORDED PAID',
    description: 'Dues status RECORDED_PAID.',
    surface: 'member',
    maxChars: 20,
  },
  NLSMemberBookingsDueStateWaived: {
    text: 'WAIVED',
    description: 'Dues status WAIVED.',
    surface: 'member',
    maxChars: 15,
  },
  NLSMemberBookingsReceiptRef: {
    text: 'Receipt: {ref}',
    description: 'Receipt reference label.',
    surface: 'member',
    maxChars: 40,
  },
  NLSOwnerConsoleNavSupportAccess: {
    text: 'Support Access',
    description: 'Owner Console rail navigation item for support access logs.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessTitle: {
    text: 'Support Access Log',
    description: 'Page title for the support access log panel in Owner Console.',
    surface: 'owner-console',
    maxChars: 40,
  },
  NLSOwnerConsoleSupportAccessSubtitle: {
    text: 'Append-only ledger of operator reads across customer workspaces and data',
    description: 'Subtitle for the support access log panel in Owner Console.',
    surface: 'owner-console',
    maxChars: 100,
  },
  NLSOwnerConsoleSupportAccessFilterWorkspace: {
    text: 'Workspace ID',
    description: 'Workspace ID filter placeholder in support access panel.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessFilterActor: {
    text: 'Actor UID',
    description: 'Actor UID filter placeholder in support access panel.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessFilterPathClass: {
    text: 'Path Class',
    description: 'Path class filter label in support access panel.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessAllPathClasses: {
    text: 'All Path Classes',
    description: 'All path classes option in support access panel.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessApplyFilters: {
    text: 'Apply filters',
    description: 'Button to apply filters in support access panel.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessResetFilters: {
    text: 'Reset',
    description: 'Button to reset filters in support access panel.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleSupportAccessColTimestamp: {
    text: 'Time',
    description: 'Timestamp column header in support access log table.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleSupportAccessColActor: {
    text: 'Actor',
    description: 'Actor column header in support access log table.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleSupportAccessColPathClass: {
    text: 'Path Class',
    description: 'Path class column header in support access log table.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessColCallable: {
    text: 'Callable',
    description: 'Callable name column header in support access log table.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessColWorkspace: {
    text: 'Workspace',
    description: 'Workspace ID column header in support access log table.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessColTarget: {
    text: 'Target / Subject',
    description: 'Target or Subject ID column header in support access log table.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessColReason: {
    text: 'Reason',
    description: 'Access reason column header in support access log table.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleSupportAccessColItems: {
    text: 'Items',
    description: 'Item count column header in support access log table.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleSupportAccessEmptyTitle: {
    text: 'No support access records',
    description: 'Empty state title for support access log table.',
    surface: 'owner-console',
    maxChars: 40,
  },
  NLSOwnerConsoleSupportAccessEmptyBody: {
    text: 'Operator reads through sensitive support views and callables will appear here.',
    description: 'Empty state description for support access log table.',
    surface: 'owner-console',
    maxChars: 120,
  },
  NLSOwnerConsoleSupportAccessHelpOne: {
    text: 'Reads of customer workspaces, people, tickets, telemetry, and diagnostics are recorded automatically.',
    description: 'First help item for support access log.',
    surface: 'owner-console',
    maxChars: 140,
  },
  NLSOwnerConsoleSupportAccessHelpTwo: {
    text: 'Each entry records the actor, callable, target ID, reason code, and server timestamp.',
    description: 'Second help item for support access log.',
    surface: 'owner-console',
    maxChars: 140,
  },
  NLSOwnerConsoleSupportAccessHelpThree: {
    text: 'The support_access_log is append-only and cannot be updated or deleted from clients.',
    description: 'Third help item for support access log.',
    surface: 'owner-console',
    maxChars: 140,
  },
  NLSOwnerConsoleSupportAccessLoading: {
    text: 'Loading support access log…',
    description: 'Loading status text in support access panel.',
    surface: 'owner-console',
    maxChars: 40,
  },
  NLSOwnerConsoleSupportAccessLoadError: {
    text: 'Could not load support access log.',
    description: 'Error text when support access log fails to load.',
    surface: 'owner-console',
    maxChars: 60,
  },
  NLSOwnerConsoleSupportAccessRetry: {
    text: 'Retry',
    description: 'Retry button in support access panel.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleSupportAccessLoadMore: {
    text: 'Load more',
    description: 'Load more button in support access panel.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleAccessReasonDialogTitle: {
    text: 'Access reason required',
    description: 'Title of the access reason dialog shown before opening sensitive customer views.',
    surface: 'owner-console',
    maxChars: 40,
  },
  NLSOwnerConsoleAccessReasonDialogSubtitle: {
    text: 'State the ticket and justification before viewing customer data.',
    description: 'Subtitle explaining why an access reason is required.',
    surface: 'owner-console',
    maxChars: 80,
  },
  NLSOwnerConsoleAccessReasonCodeLabel: {
    text: 'Reason code',
    description: 'Label for the reason code dropdown in access reason dialog.',
    surface: 'owner-console',
    maxChars: 24,
  },
  NLSOwnerConsoleAccessReasonTicketIdLabel: {
    text: 'Ticket ID',
    description: 'Label for the ticket ID input in access reason dialog.',
    surface: 'owner-console',
    maxChars: 24,
  },
  NLSOwnerConsoleAccessReasonTicketIdPlaceholder: {
    text: 'e.g. TKT-12345',
    description: 'Placeholder for the ticket ID input.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleAccessReasonNoteLabel: {
    text: 'Note (optional)',
    description: 'Label for the optional note textarea in access reason dialog.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleAccessReasonNotePlaceholder: {
    text: 'Additional context for audit ledger (optional)',
    description: 'Placeholder for the optional note textarea.',
    surface: 'owner-console',
    maxChars: 60,
  },
  NLSOwnerConsoleAccessReasonContinueAction: {
    text: 'Continue',
    description: 'Submit button text in access reason dialog.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleAccessReasonCancelAction: {
    text: 'Cancel',
    description: 'Cancel button text in access reason dialog.',
    surface: 'owner-console',
    maxChars: 20,
  },
  NLSOwnerConsoleReasonSupportTicket: {
    text: 'Support ticket',
    description: 'Option label for SUPPORT_TICKET access reason code.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleReasonIncident: {
    text: 'Incident',
    description: 'Option label for INCIDENT access reason code.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleReasonAbuseReview: {
    text: 'Abuse review',
    description: 'Option label for ABUSE_REVIEW access reason code.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleReasonUserRequest: {
    text: 'User request',
    description: 'Option label for USER_REQUEST access reason code.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleReasonLegalRequest: {
    text: 'Legal request',
    description: 'Option label for LEGAL_REQUEST access reason code.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleReasonDataMigration: {
    text: 'Data migration',
    description: 'Option label for DATA_MIGRATION access reason code.',
    surface: 'owner-console',
    maxChars: 30,
  },
  NLSOwnerConsoleReasonRoutineReview: {
    text: 'Routine review',
    description: 'Option label for ROUTINE_REVIEW access reason code.',
    surface: 'owner-console',
    maxChars: 30,
  },
  /* -------------------------------------------------------------------------
   * R18 LG34.b — Legal Briefs 3 + 4: Education Privacy, Retention, Data Requests
   * ------------------------------------------------------------------------- */
  NLSEduPrivacySettingsTitle: {
    text: 'Student data',
    description: 'Title of student data privacy settings card in education settings.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduPrivacySettingsDesc: {
    text: 'Manage student data retention and guardian screen options.',
    description: 'Description of student data privacy settings card.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSEduPrivacyRetentionLabel: {
    text: 'Retention period',
    description: 'Label for retention period in student data settings.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduPrivacyRetentionHelp: {
    text: "Keep a learner's records for {retentionDays} days after they leave (30–3650 days, default 365).",
    description: 'Help text for learner record retention period.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSEduPrivacyRetentionInputLabel: {
    text: 'Retention (days)',
    description: 'Input label for retention days.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduPrivacyRetentionMinMaxError: {
    text: 'Retention period must be an integer between 30 and 3650 days.',
    description: 'Validation error when retention days are outside bounds.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSEduPrivacyScreenModeLabel: {
    text: 'Parent screen mode',
    description: 'Label for parent screen mode setting.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduPrivacyScreenModeConsent: {
    text: 'Consent checkbox (standard)',
    description: 'Option label for standard consent checkbox.',
    surface: 'settings',
    maxChars: 50,
  },
  NLSEduPrivacyScreenModeNotice: {
    text: 'Information notice (EU / EEA / UK)',
    description: 'Option label for information notice mode in EU/EEA/UK.',
    surface: 'settings',
    maxChars: 60,
  },
  NLSEduPrivacyScreenModeNoticeDisabledHelp: {
    text: 'Available only for schools in the EU, EEA or UK. Set the country in Workspace settings.',
    description: 'Help text when information notice mode is disabled outside EU/EEA/UK.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSEduPrivacyScreenModeNoticeLegalNotice: {
    text: 'Use this only if your school relies on public task or contract. Check with your legal adviser first.',
    description: 'Legal warning text under information notice mode.',
    surface: 'settings',
    maxChars: 140,
  },
  NLSEduPrivacySaveBtn: {
    text: 'Save privacy settings',
    description: 'Save button for privacy settings.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduPrivacySaving: {
    text: 'Saving...',
    description: 'Saving state for privacy settings.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduPrivacySaveSuccess: {
    text: 'Student data privacy settings saved.',
    description: 'Success message after saving privacy settings.',
    surface: 'settings',
    maxChars: 60,
  },
  NLSEduPrivacyVersionConflict: {
    text: 'Settings have been updated by another user. Please reload the page.',
    description: 'Conflict message when expectedVersion mismatches.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSEduPrivacyRegionRefused: {
    text: 'Information notice is allowed only when workspace country is in the EU, EEA or UK.',
    description: 'Error when server rejects notice mode due to region.',
    surface: 'settings',
    maxChars: 120,
  },
  NLSEduPrivacyRetryBtn: {
    text: 'Retry',
    description: 'Retry button for privacy settings.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduLearnerMarkLeftAction: {
    text: 'Mark as left',
    description: 'Action to mark a learner as left.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduLearnerMarkAlumniAction: {
    text: 'Mark as alumni',
    description: 'Action to mark a learner as alumni.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduLearnerMarkActiveAction: {
    text: 'Mark as active again',
    description: 'Action to mark a learner as active again.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSEduLearnerMarkLeftConfirmTitle: {
    text: 'Mark learner as left?',
    description: 'Confirmation dialog title for marking learner as left.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduLearnerMarkAlumniConfirmTitle: {
    text: 'Mark learner as alumni?',
    description: 'Confirmation dialog title for marking learner as alumni.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduLearnerMarkActiveConfirmTitle: {
    text: 'Mark learner as active again?',
    description: 'Confirmation dialog title for reactivating learner.',
    surface: 'settings',
    maxChars: 45,
  },
  NLSEduLearnerMarkLeftConfirmBody: {
    text: 'Their records are deleted {retentionDays} days after they leave, as set in Education settings.',
    description: 'Confirmation message explaining retention deletion for left learners.',
    surface: 'settings',
    maxChars: 130,
  },
  NLSEduLearnerMarkAlumniConfirmBody: {
    text: 'Their records are deleted {retentionDays} days after they leave, as set in Education settings.',
    description: 'Confirmation message explaining retention deletion for alumni.',
    surface: 'settings',
    maxChars: 130,
  },
  NLSEduLearnerMarkActiveConfirmBody: {
    text: 'Mark this learner as active again? Their records will be retained while active.',
    description: 'Confirmation message for reactivating learner.',
    surface: 'settings',
    maxChars: 110,
  },
  NLSEduLearnerLeftDate: {
    text: 'Left: {date}',
    description: 'Label displaying the date a learner left.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduLearnerStatusUpdating: {
    text: 'Updating status...',
    description: 'Status update progress message.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduConsentNoticeBodyWithRetention: {
    text: '{institutionName} processes student records for classes, attendance, and family communication. As a guardian, you can see attendance, homework, and marks. Records are kept for {retentionDays} days after the learner leaves, as set by {institutionName}. Consent can be withdrawn anytime in Settings, which freezes data and schedules deletion after 30 days.',
    description: 'Parent consent notice body with institution name and retention period.',
    surface: 'common',
    maxChars: 380,
  },
  NLSEduConsentNoticeCheckboxLabel: {
    text: "I am the parent or lawful guardian of this student. I have read this notice and give my consent for {institutionName} to process my child's personal data in Timelining.",
    description: 'Checkbox agreement label for parent consent notice.',
    surface: 'common',
    maxChars: 200,
  },
  NLSEduNoticeAcknowledgeBtn: {
    text: 'I have read this',
    description: 'Acknowledge button for information notice mode in join flow.',
    surface: 'common',
    maxChars: 30,
  },
  NLSFamilyConsentObjectButton: {
    text: 'Object to processing',
    description: 'Button to object to data processing for public task/contract basis.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyConsentConfirmObjectTitle: {
    text: 'Object to processing?',
    description: 'Title of confirmation dialog to object to data processing.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSFamilyConsentConfirmObjectBody: {
    text: 'The school will review your objection to processing this learner’s records.',
    description: 'Body of confirmation dialog explaining school objection review.',
    surface: 'shell',
    maxChars: 120,
  },
  NLSFamilyConsentConfirmObjectBtn: {
    text: 'Yes, submit objection',
    description: 'Confirm button in object to processing dialog.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSFamilyConsentObjectSuccess: {
    text: 'Objection submitted. The school will review it.',
    description: 'Status indicating objection has been recorded for school review.',
    surface: 'shell',
    maxChars: 70,
  },
  NLSFamilyConsentWithdrawConfirmBody30Days: {
    text: 'Withdrawing consent immediately freezes your child’s data. It will be permanently deleted after 30 days.',
    description: 'Confirmation body explaining 30-day freeze and deletion.',
    surface: 'shell',
    maxChars: 140,
  },
  NLSEduDataRequestsTitle: {
    text: 'Data requests',
    description: 'Section title for data requests list in education settings.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduDataRequestsDesc: {
    text: 'Consent withdrawals, retention expiries, and processing objections.',
    description: 'Description of data requests list.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSEduDataRequestsColType: {
    text: 'Type',
    description: 'Table column header for data request type.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduDataRequestsColStatus: {
    text: 'Status',
    description: 'Table column header for data request status.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduDataRequestsColLearner: {
    text: 'Learner',
    description: 'Table column header for learner in data request.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduDataRequestsColEraseAfter: {
    text: 'Erase after',
    description: 'Table column header for erase after date.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduDataRequestsColActions: {
    text: 'Actions',
    description: 'Table column header for actions in data requests table.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduDataRequestsTypeWithdrawal: {
    text: 'Consent Withdrawn',
    description: 'Display label for CONSENT_WITHDRAWN request type.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduDataRequestsTypeRetention: {
    text: 'Retention Expired',
    description: 'Display label for RETENTION_EXPIRED request type.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduDataRequestsTypeObjection: {
    text: 'Objection',
    description: 'Display label for OBJECTION request type.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduDataRequestsStatusScheduled: {
    text: 'Scheduled',
    description: 'Display label for SCHEDULED request status.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduDataRequestsStatusOpen: {
    text: 'Open',
    description: 'Display label for OPEN request status.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduDataRequestsStatusInProgress: {
    text: 'In progress',
    description: 'Display label for IN_PROGRESS request status.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduDataRequestsStatusDone: {
    text: 'Done',
    description: 'Display label for DONE request status.',
    surface: 'settings',
    maxChars: 20,
  },
  NLSEduDataRequestsStatusCancelled: {
    text: 'Cancelled',
    description: 'Display label for CANCELLED request status.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduDataRequestsCancelBtn: {
    text: 'Cancel request',
    description: 'Button to cancel a scheduled data request.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduDataRequestsCancelDialogTitle: {
    text: 'Cancel data request',
    description: 'Dialog title for cancelling a scheduled data request.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSEduDataRequestsCancelReasonLabel: {
    text: 'Reason for cancellation (3–500 characters)',
    description: 'Input label for data request cancellation reason.',
    surface: 'settings',
    maxChars: 60,
  },
  NLSEduDataRequestsCancelReasonLengthError: {
    text: 'Reason must be between 3 and 500 characters.',
    description: 'Validation error when cancellation reason is too short or too long.',
    surface: 'settings',
    maxChars: 70,
  },
  NLSEduDataRequestsCancelConfirmBtn: {
    text: 'Confirm cancellation',
    description: 'Confirm button in cancel data request dialog.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSEduDataRequestsCancelling: {
    text: 'Cancelling...',
    description: 'In-flight cancelling state for data request.',
    surface: 'settings',
    maxChars: 25,
  },
  NLSEduDataRequestsCancelSuccess: {
    text: 'Data request cancelled.',
    description: 'Success message after cancelling data request.',
    surface: 'settings',
    maxChars: 40,
  },
  NLSEduDataRequestsObjectionReviewNote: {
    text: 'Review this objection with the parent.',
    description: 'Notice shown for open objection requests.',
    surface: 'settings',
    maxChars: 60,
  },
  NLSEduDataRequestsEmptyTitle: {
    text: 'No data requests',
    description: 'Title for empty data requests list.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduDataRequestsEmptyDesc: {
    text: 'Data requests for consent withdrawal, expiry, or objections will appear here.',
    description: 'Description for empty data requests list.',
    surface: 'settings',
    maxChars: 100,
  },
  NLSEduDataRequestsLearnerErased: {
    text: 'Learner {pseudonym}',
    description: 'Display text for erased learner pseudonym.',
    surface: 'settings',
    maxChars: 35,
  },
  NLSEduDataRequestsErasedLearnerFallback: {
    text: 'Erased learner',
    description: 'Fallback label for erased learner when learner doc is not present.',
    surface: 'settings',
    maxChars: 30,
  },
  NLSEduConsentDefaultInstitutionName: {
    text: 'The institution',
    description: 'Fallback name for the institution when unspecified in join code details.',
    surface: 'common',
    maxChars: 30,
  },
  NLSEduLearnerFrozenError: {
    text: 'This learner record is frozen and its status cannot be changed.',
    description: 'Error message when attempting to change status of a frozen learner.',
    surface: 'settings',
    maxChars: 80,
  },
  NLSPublicRoutePrivacyTitle: {
    text: 'Privacy Policy',
    description: 'Title for privacy policy page.',
    surface: 'common',
    maxChars: 40,
  },
  NLSPublicRouteTermsTitle: {
    text: 'Terms of Service',
    description: 'Title for terms of service page.',
    surface: 'common',
    maxChars: 40,
  },
  NLSPublicRouteRefundsTitle: {
    text: 'Refund and Cancellation Policy',
    description: 'Title for refund policy page.',
    surface: 'common',
    maxChars: 50,
  },
  NLSPublicRouteGrievanceTitle: {
    text: 'Grievance Redressal',
    description: 'Title for grievance redressal page.',
    surface: 'common',
    maxChars: 40,
  },
  NLSPublicFooterLegalNav: {
    text: 'Legal links',
    description: 'Accessibility label for footer legal navigation.',
    surface: 'common',
    maxChars: 30,
  },
  NLSPublicLinkPrivacy: {
    text: 'Privacy Policy',
    description: 'Link to privacy policy.',
    surface: 'common',
    maxChars: 30,
  },
  NLSPublicLinkTerms: {
    text: 'Terms of Service',
    description: 'Link to terms of service.',
    surface: 'common',
    maxChars: 30,
  },
  NLSPublicLinkRefunds: {
    text: 'Refund Policy',
    description: 'Link to refund policy.',
    surface: 'common',
    maxChars: 30,
  },
  NLSPublicLinkGrievance: {
    text: 'Grievance Redressal',
    description: 'Link to grievance redressal.',
    surface: 'common',
    maxChars: 30,
  },
  NLSContactGrievancePrefix: {
    text: 'For statutory complaints and grievance redressal under Indian law,',
    description: 'Prefix text on contact page directing to grievance redressal.',
    surface: 'common',
    maxChars: 80,
  },
  NLSContactGrievanceLink: {
    text: 'visit Grievance Redressal',
    description: 'Link text on contact page directing to grievance redressal.',
    surface: 'common',
    maxChars: 40,
  },
  NLSAuthConsentNoticePrefix: {
    text: 'By continuing you agree to the ',
    description: 'Prefix for Login consent disclaimer below buttons.',
    surface: 'auth',
    maxChars: 40,
  },
  NLSAuthConsentTermsLink: {
    text: 'Terms',
    description: 'Terms link text in Login consent disclaimer.',
    surface: 'auth',
    maxChars: 20,
  },
  NLSAuthConsentAndText: {
    text: ' and ',
    description: 'And separator in Login consent disclaimer.',
    surface: 'auth',
    maxChars: 15,
  },
  NLSAuthConsentPrivacyLink: {
    text: 'Privacy Policy',
    description: 'Privacy link text in Login consent disclaimer.',
    surface: 'auth',
    maxChars: 30,
  },
  NLSConsentNoticeTitle: {
    text: 'Updated Terms and Privacy Policy',
    description: 'Title of one-time consent update dialog when legal versions are bumped.',
    surface: 'common',
    maxChars: 50,
  },
  NLSConsentNoticeBodyBoth: {
    text: 'Our Terms of Service and Privacy Policy have been updated. Please review the changes before continuing.',
    description: 'Notice message when both Terms and Privacy versions changed.',
    surface: 'common',
    maxChars: 140,
  },
  NLSConsentNoticeBodyTerms: {
    text: 'Our Terms of Service have been updated. Please review the changes before continuing.',
    description: 'Notice message when Terms version changed.',
    surface: 'common',
    maxChars: 120,
  },
  NLSConsentNoticeBodyPrivacy: {
    text: 'Our Privacy Policy has been updated. Please review the changes before continuing.',
    description: 'Notice message when Privacy Policy version changed.',
    surface: 'common',
    maxChars: 120,
  },
  NLSConsentNoticeActionAgree: {
    text: 'OK',
    description: 'Action button to accept updated terms in consent dialog.',
    surface: 'common',
    maxChars: 20,
  },
  NLSConsentNoticeError: {
    text: 'Could not record your agreement. Please check your connection and try again.',
    description: 'Error message when updating consent fails.',
    surface: 'common',
    maxChars: 100,
  },
  NLSConsentNoticeActionRetry: {
    text: 'Retry',
    description: 'Retry button when updating consent fails.',
    surface: 'common',
    maxChars: 20,
  },
  NLSOpsLoading: {
    text: 'Loading…',
    description: 'Generic loading indicator for ops surfaces',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedBoardTitle: {
    text: 'Bed board',
    description: 'Title for hospital bed board screen',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBedBoardSubtitle: {
    text: 'Live hospital bed occupancy and cleaning status',
    description: 'Subtitle for hospital bed board screen',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsBedWardSelectLabel: {
    text: 'Ward',
    description: 'Label for ward selector dropdown',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedWardSelectAll: {
    text: 'Select a ward',
    description: 'Placeholder for ward selector',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBedFilterAll: {
    text: 'All',
    description: 'Filter label for all beds',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBedFilterReady: {
    text: 'Ready',
    description: 'Filter label for ready beds',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBedFilterOccupied: {
    text: 'Occupied',
    description: 'Filter label for occupied beds',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsBedFilterAwaitingClean: {
    text: 'Awaiting Clean',
    description: 'Filter label for awaiting clean beds',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBedFilterCleaning: {
    text: 'Cleaning',
    description: 'Filter label for cleaning beds',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedFilterBlocked: {
    text: 'Blocked',
    description: 'Filter label for blocked beds',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedStateReady: {
    text: 'Ready',
    description: 'Bed state label for ready',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedStateOccupied: {
    text: 'Occupied',
    description: 'Bed state label for occupied',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedStateAwaitingClean: {
    text: 'Awaiting Clean',
    description: 'Bed state label for awaiting clean',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBedStateCleaning: {
    text: 'Cleaning',
    description: 'Bed state label for cleaning',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedStateBlocked: {
    text: 'Blocked',
    description: 'Bed state label for blocked',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedCleanedPending: {
    text: 'Cleaned, pending release',
    description: 'Status label when bed clean is done but not verified',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsBedActionMarkOccupied: {
    text: 'Mark Occupied',
    description: 'Button label to mark bed occupied',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBedActionDischarge: {
    text: 'Discharge',
    description: 'Button label to discharge patient and trigger cleaning',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBedActionBlock: {
    text: 'Block',
    description: 'Button label to block bed',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedActionUnblock: {
    text: 'Unblock',
    description: 'Button label to unblock bed',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedActionOverride: {
    text: 'Override Ready',
    description: 'Button label for admin override to ready',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsBedActionHistory: {
    text: 'History',
    description: 'Button label to open bed history drawer',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedDischargeConfirmTitle: {
    text: 'Confirm Discharge',
    description: 'Title for discharge confirmation dialog',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBedDischargeConfirmBody: {
    text: 'Discharging will mark this bed as awaiting clean and automatically raise a discharge cleaning request.',
    description: 'Message explaining discharge clean request creation',
    surface: 'shell',
    maxChars: 150,
  },
  NLSOpsBedBlockTitle: {
    text: 'Block Bed',
    description: 'Title for block bed dialog',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBedBlockReasonLabel: {
    text: 'Reason for blocking',
    description: 'Label for block reason input',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBedBlockReasonPlaceholder: {
    text: 'Enter reason (e.g. maintenance required, 3-200 chars)',
    description: 'Placeholder for block reason input',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsBedOverrideTitle: {
    text: 'Override Bed to Ready',
    description: 'Title for override ready dialog',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsBedOverrideReasonLabel: {
    text: 'Reason for override',
    description: 'Label for override reason input',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBedOverrideReasonPlaceholder: {
    text: 'Enter reason for administrative override (3-500 chars)',
    description: 'Placeholder for override reason input',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsBedHistoryDrawerTitle: {
    text: 'Bed History',
    description: 'Title for bed history drawer',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBedHistoryEmpty: {
    text: 'No events recorded for this bed',
    description: 'Empty state message for bed history',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsBedNoWardsTitle: {
    text: 'No wards configured',
    description: 'Title when hospital has no wards in places',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBedNoWardsDesc: {
    text: 'Configure wards and beds in Hospital Setup or Places.',
    description: 'Description when no wards found',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsBedNoBedsTitle: {
    text: 'No beds in this ward',
    description: 'Title when selected ward has no beds',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsBedNoBedsDesc: {
    text: 'Add rooms and beds to this ward under Places.',
    description: 'Description when selected ward has no beds',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsBedReasonRequired: {
    text: 'Reason must be at least 3 characters',
    description: 'Validation message for reason input',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsBedConfirmBtn: {
    text: 'Confirm',
    description: 'Confirm button text',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedCancelBtn: {
    text: 'Cancel',
    description: 'Cancel button text',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsBedCloseBtn: {
    text: 'Close',
    description: 'Close button text',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosRequestsUnitFilterAll: {
    text: 'All Units & Wards',
    description: 'Filter option for all hospital units and wards',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosRequestsUnitFilterLabel: {
    text: 'Unit / Ward',
    description: 'Label for hospital unit filter',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosRequestsBedStatus: {
    text: 'Bed Status',
    description: 'Header or label for bed status on discharge clean request',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosRequestsComplaintHint: {
    text: 'No patient or clinical details',
    description: 'Hint line for complaint request form in hospital',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsHosRequestsReplenishmentHint: {
    text: 'Non-clinical supplies only',
    description: 'Hint line for replenishment request form in hospital',
    surface: 'shell',
    maxChars: 60,
  },

  // --- Hospital People & Shifts (R39 C6.b) ---
  NLSOpsHosSubtabOnCall: {
    text: 'On-call',
    description: 'Subtab for on-call duty board in hospital rosters',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosSubtabSkillMix: {
    text: 'Skill mix',
    description: 'Subtab for skill mix matrix in hospital rosters',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosSubtabHistory: {
    text: 'History',
    description: 'Subtab for version history in hospital rosters',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosOnCallBoardTitle: {
    text: 'On-call Duty Board',
    description: 'Title for hospital on-call duty board',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosOnCallPrimary: {
    text: 'Primary',
    description: 'Label for primary on-call doctor/nurse',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosOnCallBackup: {
    text: 'Backup',
    description: 'Label for backup on-call doctor/nurse',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosOnCallAck: {
    text: 'Acknowledge',
    description: 'Button to acknowledge on-call duty',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosOnCallAcked: {
    text: 'Acknowledged',
    description: 'Badge indicating on-call duty is acknowledged',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosOnCallNotAcked: {
    text: 'Not Acknowledged',
    description: 'Badge indicating on-call duty is not acknowledged',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosOnCallAckSuccess: {
    text: 'On-call duty acknowledged',
    description: 'Success message after acknowledging on-call duty',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHosSkillMixTitle: {
    text: 'Skill Mix & Coverage Matrix',
    description: 'Title for skill mix matrix view',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHosSkillMixActual: {
    text: 'Actual',
    description: 'Column header for actual staffed count in skill mix',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosSkillMixRequired: {
    text: 'Required',
    description: 'Column header for required minimum count in skill mix',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosSkillMixShortfall: {
    text: 'Short',
    description: 'Indicator for shortfall in skill mix count',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosSkillMixOk: {
    text: 'Met',
    description: 'Indicator for met requirement in skill mix count',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosHistoryTitle: {
    text: 'Roster Version History & Audit',
    description: 'Title for roster version history view',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHosHistoryVersion: {
    text: 'Version',
    description: 'Label for roster version number',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosHistoryPublishedBy: {
    text: 'Published By',
    description: 'Label for who published a roster version',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosHistoryPublishedAt: {
    text: 'Published At',
    description: 'Label for when a roster version was published',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosHistoryAcks: {
    text: 'Acknowledgements',
    description: 'Count or label for on-call acknowledgements',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosHistoryDiffAdded: {
    text: 'Added',
    description: 'Label for added assignments in diff',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosHistoryDiffRemoved: {
    text: 'Removed',
    description: 'Label for removed assignments in diff',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosHistoryDiffChanged: {
    text: 'Changed',
    description: 'Label for changed assignments in diff',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosStaffGroupLabel: {
    text: 'Staff Group',
    description: 'Label for hospital staff group selector',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosStaffGroupAny: {
    text: 'Any Group',
    description: 'Option for any staff group',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosStaffGroupDoctor: {
    text: 'Doctor',
    description: 'Doctor staff group label',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosStaffGroupNurse: {
    text: 'Nurse',
    description: 'Nurse staff group label',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosStaffGroupSupport: {
    text: 'Support',
    description: 'Support staff group label',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosStaffGroupOther: {
    text: 'Other',
    description: 'Other staff group label',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosSkillTagsLabel: {
    text: 'Starter Hospital Skill / Privilege Tags',
    description: 'Label for hospital starter skill tags selector',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsHosFreeTagsLabel: {
    text: 'Custom Tags',
    description: 'Label for custom skill tags input',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosOverrideReasonLabel: {
    text: 'Override Reason (3-500 characters)',
    description: 'Label for owner override reason input',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHosOverrideReasonPlaceholder: {
    text: 'Record operational reason for overriding missing skill/privilege tags...',
    description: 'Placeholder for owner override reason input on hospital roster changes',
    surface: 'shell',
    maxChars: 85,
  },
  NLSOpsHosOverrideTitle: {
    text: 'Owner Override — Replacement Tag Missing',
    description: 'Dialog title for owner override of replacement tag check',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsHosOverrideConfirm: {
    text: 'Confirm Override & Approve',
    description: 'Button to confirm owner override and approve swap',
    surface: 'shell',
    maxChars: 30,
  },

  // --- Hospital Registers (R39 C5.b) ---
  NLSOpsHosRegistersTitle: {
    text: 'Hospital Registers',
    description: 'Title for hospital registers screen',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosRegistersTabChecks: {
    text: 'Checks & logs',
    description: 'Subtab for checklists and logs register',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosRegistersTabWaste: {
    text: 'Waste',
    description: 'Subtab for biomedical waste register',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosRegistersTabTraining: {
    text: 'Training & health',
    description: 'Subtab for staff training and health checks',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosRegistersTabIncidents: {
    text: 'Incidents',
    description: 'Subtab for incidents and CAPA register',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosRegistersTabLicences: {
    text: 'Licences',
    description: 'Subtab for compliance licences register',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosRegistersTabStarters: {
    text: 'Starter templates',
    description: 'Subtab for hospital starter templates overview',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosBmwDailyTitle: {
    text: 'Biomedical Waste Daily Register',
    description: 'Title for BMW daily entry section',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHosBmwYellow: {
    text: 'Yellow (Incinerable)',
    description: 'BMW yellow category label',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosBmwRed: {
    text: 'Red (Autoclavable)',
    description: 'BMW red category label',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosBmwWhite: {
    text: 'White (Sharps)',
    description: 'BMW white category label',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosBmwBlue: {
    text: 'Blue (Glassware)',
    description: 'BMW blue category label',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosBmwStorageTimer: {
    text: 'Storage Timer',
    description: 'Header for BMW storage timer',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosBmwStorageExceeded: {
    text: 'STORAGE LIMIT EXCEEDED',
    description: 'Alert badge when BMW storage time exceeds threshold',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosBmwStorageOk: {
    text: 'Within Storage Limit',
    description: 'Badge when BMW storage time is within threshold',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosBmwRecordPickup: {
    text: 'Record Pickup',
    description: 'Button to record waste collector pickup',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosBmwSubmitDay: {
    text: 'Submit & Lock Day',
    description: 'Button to submit and lock BMW daily record',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosBmwCorrectDay: {
    text: 'Submit Correction',
    description: 'Button to correct locked BMW record',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosBmwConfirmUnusual: {
    text: 'Unusual Weight Confirmation',
    description: 'Dialog title for unusual BMW weight confirmation',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsHosBmwConfirmUnusualDesc: {
    text: 'Weight exceeds configured threshold. Please confirm this reading is accurate.',
    description: 'Confirmation prompt for unusual BMW weight',
    surface: 'shell',
    maxChars: 90,
  },
  NLSOpsHosBmwMonthlySummary: {
    text: 'Monthly BMW Summary',
    description: 'Header for BMW monthly summary table',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosStaffStatusTitle: {
    text: 'Staff Training & Health Checks',
    description: 'Title for staff training and health status view',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsHosStaffStatusInduction: {
    text: 'Induction Training',
    description: 'Column header for induction training date',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosStaffStatusLastAnnual: {
    text: 'Last Annual Training',
    description: 'Column header for last annual training date',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosStaffStatusAnnualDue: {
    text: 'Annual Training Status',
    description: 'Column header for annual training due status',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosStaffStatusImmunisation: {
    text: 'Immunisation Status',
    description: 'Column header for immunisation status',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosStaffStatusHealthCheck: {
    text: 'Last Health Check',
    description: 'Column header for last health check date',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosStaffStatusEdit: {
    text: 'Update Status',
    description: 'Button or action to update staff status',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosStaffStatusOk: {
    text: 'OK',
    description: 'Status badge when training is current',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsHosStaffStatusDue: {
    text: 'DUE SOON',
    description: 'Status badge when annual training is due soon',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosStaffStatusOverdue: {
    text: 'OVERDUE',
    description: 'Status badge when annual training is overdue',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosIncidentsTitle: {
    text: 'Incidents & CAPA Register',
    description: 'Title for incidents and CAPA register',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosIncidentReport: {
    text: 'Report Incident',
    description: 'Button to report a non-clinical operational incident',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosIncidentReview: {
    text: 'Review Incident',
    description: 'Action to review an incident',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosIncidentKind: {
    text: 'Kind',
    description: 'Incident kind label',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosIncidentSeverity: {
    text: 'Severity',
    description: 'Incident severity label',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosIncidentState: {
    text: 'State',
    description: 'Incident workflow state label',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsHosIncidentOccurredAt: {
    text: 'Occurred At',
    description: 'Incident timestamp label',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosIncidentPlace: {
    text: 'Location / Unit',
    description: 'Incident place label',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosIncidentSummary: {
    text: 'Summary (no clinical details)',
    description: 'Incident summary label with privacy reminder',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsHosIncidentCapa: {
    text: 'CAPA Items',
    description: 'Corrective and Preventive Actions list title',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosIncidentAddCapa: {
    text: 'Add CAPA Action',
    description: 'Button to add a CAPA action item',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosIncidentCompleteCapa: {
    text: 'Complete Action',
    description: 'Action to complete a CAPA item',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsHosIncidentRecordEffectiveness: {
    text: 'Record Effectiveness',
    description: 'Action to record effectiveness review on CAPA',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosLicencesTitle: {
    text: 'Hospital Compliance Licences',
    description: 'Title for hospital compliance licences register',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsHosLicencesAddStarters: {
    text: 'Add Starter Licences',
    description: 'Button to seed hospital starter compliance items',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsHosLicencesStartersAdded: {
    text: 'Starter licences added',
    description: 'Success message after adding starter compliance items',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsHosStartersTitle: {
    text: 'Hospital Starter Checklists & Logs',
    description: 'Title for hospital starter templates overview',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsHosStartersStatus: {
    text: 'Template Status',
    description: 'Header for template version status',
    surface: 'shell',
    maxChars: 20,
  },

  // --- Construction Measurement & Bill-Support Packs (R39 B6.b) ---
  NLSOpsConMeasureTitle: {
    text: 'Measurement & Bill-Support',
    description: 'Title for construction measurements screen.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureSubtitle: {
    text: 'Track quantities, progress measurement lines, cumulative totals, and bill-support evidence packs.',
    description: 'Subtitle for construction measurements screen.',
    surface: 'shell',
    maxChars: 100,
  },
  NLSOpsConMeasureTabLines: {
    text: 'Measurement lines',
    description: 'Tab label for measurement lines register.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureTabDisputed: {
    text: 'Disputed lines',
    description: 'Tab label for disputed measurement lines.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureTabTotals: {
    text: 'Cumulative totals',
    description: 'Tab label for item cumulative quantities.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureTabParties: {
    text: 'Parties',
    description: 'Tab label for subcontractors and suppliers.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureTabPacks: {
    text: 'Bill-support packs',
    description: 'Tab label for evidence packs list.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureAddLine: {
    text: 'New measurement',
    description: 'Button to record a new measurement line.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureAddParty: {
    text: 'Add party',
    description: 'Button to add a subcontractor or supplier.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureBuildPack: {
    text: 'Build bill-support pack',
    description: 'Button to initiate bill-support evidence export.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureFilterPartyAll: {
    text: 'All parties',
    description: 'Filter option for all parties.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureFilterStateAll: {
    text: 'All states',
    description: 'Filter option for all measurement states.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureFilterPeriodAll: {
    text: 'All periods',
    description: 'Filter option for all measurement periods.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColItem: {
    text: 'Item & Description',
    description: 'Table column header for item reference and description.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureColParty: {
    text: 'Party',
    description: 'Table column header for subcontractor or supplier party.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColPlace: {
    text: 'Place',
    description: 'Table column header for location or site place.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColDayPeriod: {
    text: 'Date & Period',
    description: 'Table column header for measured day and period label.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColMeasuredQty: {
    text: 'Measured qty',
    description: 'Table column header for measured quantity.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColAcceptedQty: {
    text: 'Accepted qty',
    description: 'Table column header for accepted quantity.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColCumulativeQty: {
    text: 'Cumulative accepted',
    description: 'Table column header for cumulative accepted total.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureColState: {
    text: 'State',
    description: 'Table column header for measurement state.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureColActions: {
    text: 'Actions',
    description: 'Table column header for actions.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureViewDetail: {
    text: 'View detail',
    description: 'Button to open measurement detail dialog.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureEmptyLinesTitle: {
    text: 'No measurements found',
    description: 'Empty state title for measurement lines.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureEmptyLinesDesc: {
    text: 'No measurement lines match the selected project and filter criteria.',
    description: 'Empty state description for measurement lines.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConMeasureEmptyPartiesTitle: {
    text: 'No parties configured',
    description: 'Empty state title for parties list.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureEmptyPartiesDesc: {
    text: 'Add subcontractors, suppliers, or in-house gangs for this project.',
    description: 'Empty state description for parties list.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConMeasureEmptyTotalsTitle: {
    text: 'No totals yet',
    description: 'Empty state title for cumulative totals.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureEmptyTotalsDesc: {
    text: 'Cumulative item totals will appear as measurement lines are recorded.',
    description: 'Empty state description for cumulative totals.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConMeasureEmptyDisputedTitle: {
    text: 'No disputed lines',
    description: 'Empty state title for disputed measurements.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureEmptyDisputedDesc: {
    text: 'There are currently no disputed measurement lines in this project.',
    description: 'Empty state description for disputed measurements.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConMeasureEmptyPacksTitle: {
    text: 'No bill-support packs',
    description: 'Empty state title for bill-support packs.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureEmptyPacksDesc: {
    text: 'Generate an evidence pack for a subcontractor or supplier period.',
    description: 'Empty state description for bill-support packs.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConMeasureStateDraft: {
    text: 'Draft',
    description: 'Measurement state Draft.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureStateSubmitted: {
    text: 'Submitted',
    description: 'Measurement state Submitted.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureStateAccepted: {
    text: 'Accepted',
    description: 'Measurement state Accepted.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureStateDisputed: {
    text: 'Disputed',
    description: 'Measurement state Disputed.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureStateCancelled: {
    text: 'Cancelled',
    description: 'Measurement state Cancelled.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConPartyTypeSubcontractor: {
    text: 'Subcontractor',
    description: 'Party type Subcontractor.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConPartyTypeSupplier: {
    text: 'Supplier',
    description: 'Party type Supplier.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConPartyTypeInHouseGang: {
    text: 'In-House Gang',
    description: 'Party type In-House Gang.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureDialogNewLineTitle: {
    text: 'Record Measurement',
    description: 'Title for measurement creation dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureDialogEditLineTitle: {
    text: 'Edit Measurement',
    description: 'Title for measurement edit dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureDialogDetailTitle: {
    text: 'Measurement Details',
    description: 'Title for measurement detail drawer or dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureDialogPartyTitle: {
    text: 'Party Details',
    description: 'Title for party create/edit dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureDialogAcceptTitle: {
    text: 'Accept Measurement',
    description: 'Title for accepting measurement line.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureDialogDisputeTitle: {
    text: 'Dispute Measurement',
    description: 'Title for disputing measurement line.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureDialogCorrectTitle: {
    text: 'Correct Measurement',
    description: 'Title for correcting accepted measurement.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureDialogPackTitle: {
    text: 'Build Bill-Support Pack',
    description: 'Title for bill-support export dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureFieldParty: {
    text: 'Party',
    description: 'Field label for party.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureFieldWorkOrder: {
    text: 'Work order / Contract ref',
    description: 'Field label for work order reference.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureFieldItemRef: {
    text: 'BOQ / Item ref',
    description: 'Field label for BOQ item code.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldItemDesc: {
    text: 'Item description',
    description: 'Field label for item description.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldUnit: {
    text: 'Measurement unit',
    description: 'Field label for measurement unit.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldPlace: {
    text: 'Place / Location',
    description: 'Field label for place.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldMeasuredDay: {
    text: 'Measurement date',
    description: 'Field label for measurement date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldPeriod: {
    text: 'Period label (e.g. RA-07)',
    description: 'Field label for billing or measurement period label.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureFieldMeasuredQty: {
    text: 'Measured quantity',
    description: 'Field label for measured quantity.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldAcceptedQty: {
    text: 'Accepted quantity',
    description: 'Field label for accepted quantity.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldDiscrepancyNote: {
    text: 'Discrepancy note (required when accepted qty differs)',
    description: 'Field label for discrepancy note.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConMeasureFieldDisputeReason: {
    text: 'Dispute reason',
    description: 'Field label for dispute reason.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldCorrectionReason: {
    text: 'Correction reason',
    description: 'Field label for correction reason.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldNewAcceptedQty: {
    text: 'New accepted quantity',
    description: 'Field label for updated accepted quantity.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureFieldSelfApprovalReason: {
    text: 'Self-action reason (required when sole approver)',
    description: 'Field label for self-acceptance or self-dispute reason.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConMeasureFieldInspectionRefs: {
    text: 'Linked inspections & pour cards',
    description: 'Field label for linked inspection records.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConMeasureFieldSourceLinks: {
    text: 'Source links (DPR, issues, drawings)',
    description: 'Field label for source links.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConMeasureFieldPartyName: {
    text: 'Party name',
    description: 'Field label for party name.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldPartyType: {
    text: 'Party type',
    description: 'Field label for party type.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldPartyTrade: {
    text: 'Trade / Specialty',
    description: 'Field label for party trade.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldPartyExtRef: {
    text: 'External vendor code',
    description: 'Field label for external vendor code.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldPartyProjects: {
    text: 'Assigned projects',
    description: 'Field label for assigned projects.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldPartyArchived: {
    text: 'Archived',
    description: 'Field label for archived status.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureFieldPackFrom: {
    text: 'Period start date',
    description: 'Field label for pack start date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureFieldPackTo: {
    text: 'Period end date',
    description: 'Field label for pack end date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureActionSubmit: {
    text: 'Submit for verification',
    description: 'Button to submit measurement line.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureActionAccept: {
    text: 'Accept quantity',
    description: 'Button to accept measurement quantity.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureActionDispute: {
    text: 'Dispute',
    description: 'Button to dispute measurement line.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureActionResubmit: {
    text: 'Resubmit',
    description: 'Button to resubmit disputed line.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureActionCorrect: {
    text: 'Post correction',
    description: 'Button to correct an accepted quantity.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureActionSaveDraft: {
    text: 'Save draft',
    description: 'Button to save measurement as draft.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureActionSaveParty: {
    text: 'Save party',
    description: 'Button to save party.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureActionArchiveParty: {
    text: 'Archive party',
    description: 'Button to archive party.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureActionUnarchiveParty: {
    text: 'Unarchive party',
    description: 'Button to unarchive party.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureActionGeneratePack: {
    text: 'Generate pack',
    description: 'Button to trigger evidence pack generation.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureActionCancel: {
    text: 'Cancel',
    description: 'Button to cancel a dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureActionClose: {
    text: 'Close',
    description: 'Button to close dialog.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureSectionRevisions: {
    text: 'Revisions history',
    description: 'Section header for measurement revisions.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureSectionCorrections: {
    text: 'Corrections log',
    description: 'Section header for measurement corrections.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureSectionInspections: {
    text: 'Verified inspection records',
    description: 'Section header for inspection references.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureSectionPhotos: {
    text: 'Measurement photos',
    description: 'Section header for photos.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureRevisionLabel: {
    text: 'Revision #{revision} by {name} on {date}',
    description: 'Label for revision entry in detail.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConMeasureCorrectionLabel: {
    text: 'Correction #{number} by {name} on {date}',
    description: 'Label for correction entry in detail.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConMeasureOriginalQtyLabel: {
    text: 'Original: {qty}',
    description: 'Label for original quantity.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureNewQtyLabel: {
    text: 'Adjusted: {qty}',
    description: 'Label for adjusted quantity.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureTotalsHeader: {
    text: 'Item Cumulative Progress',
    description: 'Header for cumulative totals view.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureColWorkOrder: {
    text: 'Work order',
    description: 'Column header for work order in totals.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColItemRef: {
    text: 'Item ref',
    description: 'Column header for item ref in totals.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColUnit: {
    text: 'Unit',
    description: 'Column header for unit in totals.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasureColTotalMeasured: {
    text: 'Total measured',
    description: 'Column header for total measured quantity.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColTotalAccepted: {
    text: 'Total accepted',
    description: 'Column header for total accepted quantity.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColDisputedCount: {
    text: 'Disputed lines',
    description: 'Column header for disputed line count.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColLineCount: {
    text: 'Total lines',
    description: 'Column header for total line count.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureColLastDay: {
    text: 'Last measured day',
    description: 'Column header for last measured date.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasurePackStatusQueued: {
    text: 'Queued',
    description: 'Status Queued for bill-support pack.',
    surface: 'shell',
    maxChars: 15,
  },
  NLSOpsConMeasurePackStatusRunning: {
    text: 'Building...',
    description: 'Status Building for bill-support pack.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasurePackStatusReady: {
    text: 'Ready for download',
    description: 'Status Ready for bill-support pack.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasurePackStatusFailed: {
    text: 'Build failed',
    description: 'Status Failed for bill-support pack.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasurePackDownload: {
    text: 'Download ZIP',
    description: 'Button to download bill-support pack ZIP.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureDialogCancelTitle: {
    text: 'Cancel Measurement Line',
    description: 'Title for cancel measurement dialog.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureFieldCancellationReason: {
    text: 'Cancellation Reason',
    description: 'Label for measurement cancellation reason.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureActionConfirmCancel: {
    text: 'Confirm Cancellation',
    description: 'Action button to confirm cancellation of measurement.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConMeasureActionCancelLine: {
    text: 'Cancel Line',
    description: 'Action button to cancel a draft or disputed measurement line.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConMeasureErrorKeyFieldsLocked: {
    text: 'Cannot change party, work order ref, item ref, or unit on a submitted measurement. Cancel the line and raise a new one instead.',
    description: 'Error message when attempting to change locked keys on submitted measurement.',
    surface: 'shell',
    maxChars: 150,
  },
  NLSOpsConMeasureErrorSelectParty: {
    text: 'Please select a party.',
    description: 'Validation error when party is not selected.',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsConMeasureErrorWorkOrderRequired: {
    text: 'Work order / contract ref is required.',
    description: 'Validation error when work order ref is empty.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsConMeasureErrorItemRefRequired: {
    text: 'BOQ / Item ref is required.',
    description: 'Validation error when item ref is empty.',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsConMeasureErrorMeasuredQtyNonNegative: {
    text: 'Measured quantity must be a non-negative number.',
    description: 'Validation error for non-negative measured quantity.',
    surface: 'shell',
    maxChars: 55,
  },
  NLSOpsConMeasureErrorAcceptedQtyNonNegative: {
    text: 'Accepted quantity must be non-negative.',
    description: 'Validation error for non-negative accepted quantity.',
    surface: 'shell',
    maxChars: 45,
  },
  NLSOpsConMeasureErrorDiscrepancyNoteRequired: {
    text: 'Discrepancy note (3–500 chars) is required when accepted quantity differs.',
    description: 'Validation error when accepted quantity differs without note.',
    surface: 'shell',
    maxChars: 80,
  },
  NLSOpsConMeasureErrorDisputeReasonLength: {
    text: 'Dispute reason must be between 3 and 500 characters.',
    description: 'Validation error for dispute reason length.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConMeasureErrorNewAcceptedQtyNonNegative: {
    text: 'New accepted quantity must be non-negative.',
    description: 'Validation error for non-negative new accepted quantity.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConMeasureErrorCorrectionReasonLength: {
    text: 'Correction reason must be between 3 and 500 characters.',
    description: 'Validation error for correction reason length.',
    surface: 'shell',
    maxChars: 60,
  },
  NLSOpsConMeasureErrorPartyNameMinLength: {
    text: 'Party name must be at least 2 characters.',
    description: 'Validation error for party name minimum length.',
    surface: 'shell',
    maxChars: 50,
  },
  NLSOpsConMeasureErrorPackDatesRequired: {
    text: 'Start and end dates are required.',
    description: 'Validation error when pack export dates are missing.',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsConMeasureErrorCancellationReasonLength: {
    text: 'Cancellation reason must be between 3 and 500 characters.',
    description: 'Validation error for cancellation reason length.',
    surface: 'shell',
    maxChars: 65,
  },
  NLSOpsConUnitM: {
    text: 'm',
    description: 'Linear meters unit symbol.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsConUnitM2: {
    text: 'm²',
    description: 'Square meters unit symbol.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsConUnitM3: {
    text: 'm³',
    description: 'Cubic meters unit symbol.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsConUnitKg: {
    text: 'kg',
    description: 'Kilograms unit symbol.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsConUnitT: {
    text: 't',
    description: 'Metric tonnes unit symbol.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsConUnitNos: {
    text: 'nos',
    description: 'Number / count unit symbol.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsConUnitRmt: {
    text: 'rmt',
    description: 'Running meters unit symbol.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsConUnitLot: {
    text: 'lot',
    description: 'Lump-sum lot unit symbol.',
    surface: 'shell',
    maxChars: 10,
  },
  NLSOpsConInspectionTypePourCard: {
    text: 'Pour Card',
    description: 'Label for pour card inspection reference.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConInspectionTypeInspection: {
    text: 'Inspection',
    description: 'Label for inspection reference.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConSourceKindDpr: {
    text: 'Daily Progress Report',
    description: 'Label for DPR source link.',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsConSourceKindIssue: {
    text: 'Issue',
    description: 'Label for Issue source link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsConSourceKindDrawing: {
    text: 'Drawing',
    description: 'Label for Drawing source link.',
    surface: 'shell',
    maxChars: 20,
  },
  NLSOpsUnitSelectLabel: {
    text: 'Unit / Ward',
    description: 'Label for unit picker dropdown',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsShiftSelectLabel: {
    text: 'Shift',
    description: 'Label for shift picker dropdown',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsRoleBiomedicalEngineer: {
    text: 'Biomedical Engineer',
    description: 'Hospital sector role label for biomedical engineer (asset releaser).',
    surface: 'shell',
    maxChars: 30,
  },
  NLSOpsPresetCategoryLabel: {
    text: 'Equipment Category Preset',
    description: 'Picker label for hospital equipment preset category',
    surface: 'shell',
    maxChars: 35,
  },
  NLSOpsPresetNone: {
    text: 'Custom / Other',
    description: 'Option for no preset category',
    surface: 'shell',
    maxChars: 25,
  },
  NLSOpsPresetStarterHint: {
    text: 'Starter values, not certified',
    description: 'Hint informing user preset values are starters and not certified medical calibrations',
    surface: 'shell',
    maxChars: 40,
  },
  NLSOpsPresetVentilator: { text: 'Ventilator', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetDefibrillator: { text: 'Defibrillator', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetInfusionPump: { text: 'Infusion Pump', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetSyringePump: { text: 'Syringe Pump', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetPatientMonitor: { text: 'Patient Monitor', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetEcgMachine: { text: 'ECG Machine', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetSuctionMachine: { text: 'Suction Machine', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetOxygenConcentrator: { text: 'Oxygen Concentrator', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetAnaesthesiaWorkstation: { text: 'Anaesthesia Workstation', description: 'Preset label', surface: 'shell', maxChars: 30 },
  NLSOpsPresetAutoclave: { text: 'Autoclave', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetXRayUnit: { text: 'X-Ray Unit', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetUltrasound: { text: 'Ultrasound', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetPulseOximeter: { text: 'Pulse Oximeter', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetBpMonitor: { text: 'BP Monitor', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetNebuliser: { text: 'Nebuliser', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetHospitalBedElectric: { text: 'Hospital Bed (Electric)', description: 'Preset label', surface: 'shell', maxChars: 30 },
  NLSOpsPresetWheelchair: { text: 'Wheelchair', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsPresetStretcher: { text: 'Stretcher', description: 'Preset label', surface: 'shell', maxChars: 25 },
  NLSOpsHosTodayTitle: { text: 'Hospital Today', description: 'Hospital today page title', surface: 'shell', maxChars: 30 },
  NLSOpsHosTodaySubtitle: { text: 'Unit dues, shift overview, and exception inbox', description: 'Hospital today subtitle', surface: 'shell', maxChars: 60 },
  NLSOpsHosTodayUnitLabel: { text: 'Unit / Department', description: 'Unit picker label', surface: 'shell', maxChars: 25 },
  NLSOpsHosTodayShiftLabel: { text: 'Shift', description: 'Shift picker label', surface: 'shell', maxChars: 25 },
  NLSOpsHosTodayNoShifts: { text: 'No shifts defined', description: 'Fallback when no shifts defined', surface: 'shell', maxChars: 30 },
  NLSOpsHosTodayGroupMissedEscalated: { text: 'Missed & Escalated ({count})', description: 'Missed and escalated dues header', surface: 'shell', maxChars: 35 },
  NLSOpsHosTodayGroupDueNow: { text: 'Due Now ({count})', description: 'Due now header', surface: 'shell', maxChars: 30 },
  NLSOpsHosTodayGroupUpcoming: { text: 'Upcoming ({count})', description: 'Upcoming dues header', surface: 'shell', maxChars: 30 },
  NLSOpsHosTodayGroupDoneSkipped: { text: 'Done & Skipped ({count})', description: 'Done and skipped dues header', surface: 'shell', maxChars: 35 },
  NLSOpsHosTodayNoDuesInGroup: { text: 'No items in this section', description: 'Empty notice in due group', surface: 'shell', maxChars: 35 },
  NLSOpsHosTodayExceptionsTitle: { text: 'Exception Inbox', description: 'Exception inbox header', surface: 'shell', maxChars: 30 },
  NLSOpsHosTodayExceptionsNone: { text: 'No active exceptions for this unit', description: 'Empty state for exceptions', surface: 'shell', maxChars: 45 },
  NLSOpsHosTodayExcSpill: { text: 'Urgent Spill: {title}', description: 'Spill exception item', surface: 'shell', maxChars: 50 },
  NLSOpsHosTodayExcCriticalFault: { text: 'Critical Fault: {title}', description: 'Critical fault exception item', surface: 'shell', maxChars: 50 },
  NLSOpsHosTodayExcBlockedBed: { text: 'Blocked Bed: {name}', description: 'Blocked bed exception item', surface: 'shell', maxChars: 40 },
  NLSOpsHosTodayExcSkippedDue: { text: 'Skipped Due: {template}', description: 'Skipped due exception item', surface: 'shell', maxChars: 50 },
  NLSOpsHosTodayExcMissedDue: { text: 'Missed Due: {template}', description: 'Missed due exception item', surface: 'shell', maxChars: 50 },
  NLSOpsHosTodayNoSchedulesTitle: { text: 'No schedules for unit', description: 'No schedules title', surface: 'shell', maxChars: 35 },
  NLSOpsHosTodayNoSchedulesBody: { text: 'No active check schedules are assigned to this unit.', description: 'No schedules body', surface: 'shell', maxChars: 80 },
  NLSOpsHosTodayGoSchedulesBtn: { text: 'Configure Schedules', description: 'Button navigating to schedules', surface: 'shell', maxChars: 30 },
  NLSOpsHosTodayRecordBtn: { text: 'Record', description: 'Record due check button', surface: 'shell', maxChars: 20 },
  NLSOpsHosTodaySkipBtn: { text: 'Skip', description: 'Skip due check button', surface: 'shell', maxChars: 20 },
  NLSOpsHosTodaySkipDialogTitle: { text: 'Skip Due Check', description: 'Dialog title for skipping due', surface: 'shell', maxChars: 30 },
  NLSOpsHosTodaySkipReasonLabel: { text: 'Reason for skipping *', description: 'Label for skip reason', surface: 'shell', maxChars: 30 },
  NLSOpsHosTodaySkipReasonPlaceholder: { text: 'Clinical or operational reason for skipping', description: 'Placeholder for skip reason', surface: 'shell', maxChars: 60 },
  NLSOpsHosTodaySkipReasonRequired: { text: 'Skip reason must be at least 3 characters', description: 'Validation message for skip reason', surface: 'shell', maxChars: 50 },
  NLSOpsHosTodayLoading: { text: 'Loading Today checks...', description: 'Loading message', surface: 'shell', maxChars: 35 },
  NLSOpsHosTodayLoadError: { text: 'Failed to load Today data', description: 'Error message', surface: 'shell', maxChars: 40 },
  NLSOpsAssetServiceRecordBtn: { text: 'Record Service Visit', description: 'Button to record vendor service visit', surface: 'shell', maxChars: 30 },
  NLSOpsAssetServiceDialogTitle: { text: 'Record Vendor Service Visit', description: 'Dialog title for service visit', surface: 'shell', maxChars: 35 },
  NLSOpsAssetServiceVisitDayKey: { text: 'Visit Date', description: 'Visit date field label', surface: 'shell', maxChars: 20 },
  NLSOpsAssetServiceKind: { text: 'Service Kind', description: 'Service kind field label', surface: 'shell', maxChars: 25 },
  NLSOpsAssetServiceVendorName: { text: 'Vendor Name', description: 'Vendor name field label', surface: 'shell', maxChars: 25 },
  NLSOpsAssetServiceEngineerName: { text: 'Engineer Name', description: 'Service engineer name field label', surface: 'shell', maxChars: 30 },
  NLSOpsAssetServiceSummary: { text: 'Summary of Work *', description: 'Summary field label', surface: 'shell', maxChars: 30 },
  NLSOpsAssetServiceSummaryPlaceholder: { text: 'Summary of work performed, tests completed (3-1000 characters)', description: 'Placeholder for summary', surface: 'shell', maxChars: 70 },
  NLSOpsAssetServiceInvoiceRef: { text: 'Invoice / DC Ref', description: 'Invoice ref field label', surface: 'shell', maxChars: 25 },
  NLSOpsAssetServiceInvoiceRefPlaceholder: { text: 'Reference number (no financial amounts)', description: 'Placeholder for invoice ref', surface: 'shell', maxChars: 50 },
  NLSOpsAssetServiceRequestId: { text: 'Related Request ID', description: 'Related request ID field label', surface: 'shell', maxChars: 25 },
  NLSOpsAssetServiceFiles: { text: 'Attached Reports', description: 'Attached reports field label', surface: 'shell', maxChars: 25 },
  NLSOpsAssetServiceUploadBtn: { text: 'Upload Report (PDF / Image)', description: 'Button to upload service report', surface: 'shell', maxChars: 35 },
  NLSOpsAssetServiceEmpty: { text: 'No service visits recorded for this equipment', description: 'Empty service visits notice', surface: 'shell', maxChars: 55 },
  NLSOpsAssetServiceSummaryRequired: { text: 'Service summary is required (min 3 characters)', description: 'Validation message', surface: 'shell', maxChars: 50 },
  NLSOpsHosEquipTabRegister: { text: 'Register', description: 'Equipment register tab', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipTabDownNow: { text: 'Down Now ({count})', description: 'Down now equipment tab', surface: 'shell', maxChars: 25 },
  NLSOpsHosEquipTabDowntime: { text: 'Downtime Analysis', description: 'Equipment downtime analysis tab', surface: 'shell', maxChars: 25 },
  NLSOpsHosEquipDownSince: { text: 'Down Since', description: 'Down since column header', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipDownSinceUnknown: { text: 'since —', description: 'Unknown down since placeholder', surface: 'shell', maxChars: 15 },
  NLSOpsHosEquipDownReason: { text: 'Reason', description: 'Down reason column header', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipLinkedFault: { text: 'Linked Fault', description: 'Linked fault request column', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipMonthPicker: { text: 'Month', description: 'Month selector label', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipAssetCol: { text: 'Equipment', description: 'Equipment name column', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipCategoryCol: { text: 'Category', description: 'Equipment category column', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipMinutesCol: { text: 'Downtime (min)', description: 'Downtime minutes column', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipHoursCol: { text: 'Downtime (hrs)', description: 'Downtime hours column', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipExportCsv: { text: 'Export Downtime CSV', description: 'Export downtime CSV button', surface: 'shell', maxChars: 30 },
  NLSOpsHosEquipNoDownNow: { text: 'No equipment currently isolated or in maintenance', description: 'Empty down now state', surface: 'shell', maxChars: 60 },
  NLSOpsHosEquipNoDowntimeInMonth: { text: 'No equipment downtime recorded in this month', description: 'Empty downtime state', surface: 'shell', maxChars: 60 },
  NLSOpsHosEquipTotalDowntime: { text: 'Total Downtime: {hours} hrs ({minutes} mins)', description: 'Total downtime summary', surface: 'shell', maxChars: 50 },
  NLSOpsHosEquipByCategoryTitle: { text: 'Downtime by Equipment Category', description: 'Category breakdown section title', surface: 'shell', maxChars: 40 },
  NLSOpsHosEquipServiceVisitsTitle: { text: 'Service & Maintenance Visits', description: 'Title for service visits section', surface: 'shell', maxChars: 40 },
  NLSOpsHosEquipTabsAria: { text: 'Equipment tabs', description: 'Aria label for equipment tabs', surface: 'shell', maxChars: 30 },
  NLSOpsHosEquipReleaseAction: { text: 'Release', description: 'Release button label', surface: 'shell', maxChars: 20 },
  NLSOpsHosEquipReleaseDialogTitle: { text: 'Release Equipment', description: 'Release equipment dialog title', surface: 'shell', maxChars: 30 },
  NLSOpsHosEquipReleaseNotes: { text: 'Release Notes', description: 'Release notes input label', surface: 'shell', maxChars: 30 },
  NLSOpsAssetCardTabDetails: { text: 'Details', description: 'Asset card details tab', surface: 'shell', maxChars: 20 },
  NLSOpsAssetCardTabService: { text: 'Service Visits', description: 'Asset card service tab', surface: 'shell', maxChars: 25 },
  NLSOpsAssetCardTabsAria: { text: 'Asset card tabs', description: 'Aria label for asset card tabs', surface: 'shell', maxChars: 30 },
  NLSOpsAssetServiceFileUploaded: { text: 'File attached: {name}', description: 'Notice of uploaded file', surface: 'shell', maxChars: 40 },
  NLSOpsAssetServiceDownloadFile: { text: 'Download report', description: 'Download report file link', surface: 'shell', maxChars: 25 },
  NLSOpsAssetServiceUploading: { text: 'Uploading…', description: 'Hint while uploading service file', surface: 'shell', maxChars: 20 },
  // R39 C8.b: Hospital light patient directory keys
  NLSOpsHospitalPatientsTitle: { text: 'Patient directory', description: 'Title of hospital patient directory screen', surface: 'shell', maxChars: 30 },
  NLSOpsHospitalPatientsSubtitle: { text: 'Light operations directory for active visits and consents.', description: 'Subtitle of patient directory screen', surface: 'shell', maxChars: 80 },
  NLSOpsHospitalPatientsSearchPlaceholder: { text: 'Search by name, ref or pseudo ref...', description: 'Search placeholder for patient directory', surface: 'shell', maxChars: 50 },
  NLSOpsHospitalPatientsSearchAria: { text: 'Search patients', description: 'Aria label for patient search input', surface: 'shell', maxChars: 30 },
  NLSOpsHospitalPatientsAddBtn: { text: 'Add patient', description: 'Button to open add patient dialog', surface: 'shell', maxChars: 20 },
  NLSOpsHospitalPatientsColName: { text: 'Patient / Ref', description: 'Table column header for patient name and pseudoRef', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalPatientsColHospitalRef: { text: 'Hospital ID', description: 'Table column header for hospital reference number', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalPatientsColPhone: { text: 'Phone', description: 'Table column header for patient phone number', surface: 'shell', maxChars: 20 },
  NLSOpsHospitalPatientsColVisits: { text: "Today's visit", description: 'Table column header for current visit status', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalPatientsColActions: { text: 'Actions', description: 'Table column header for patient actions', surface: 'shell', maxChars: 20 },
  NLSOpsHospitalPatientsEmpty: { text: 'No patients found.', description: 'Empty state message when no patients match', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientsNoVisits: { text: 'No active visit', description: 'Status indicating no active visit today', surface: 'shell', maxChars: 30 },
  NLSOpsHospitalPatientsCheckInBtn: { text: 'Check in', description: 'Button to check in patient for a visit', surface: 'shell', maxChars: 20 },
  NLSOpsHospitalPatientsCheckOutBtn: { text: 'Check out', description: 'Button to check out patient from active visit', surface: 'shell', maxChars: 20 },
  NLSOpsHospitalPatientsWithdrawConsentBtn: { text: 'Withdraw consent', description: 'Button to withdraw consent for patient', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalPatientsActiveVisit: { text: 'Active in {department}', description: 'Status indicating patient is currently in a department', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientsAnonymized: { text: 'Consent withdrawn (anonymized)', description: 'Status badge when patient consent has been withdrawn', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientsLoading: { text: 'Loading patients...', description: 'Loading indicator text', surface: 'shell', maxChars: 30 },

  // Add Patient Dialog
  NLSOpsHospitalPatientAddDialogTitle: { text: 'Register patient', description: 'Dialog title for adding a new patient', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientNameLabel: { text: 'Full name', description: 'Field label for patient name', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalPatientPhoneLabel: { text: 'Phone number (optional)', description: 'Field label for patient phone', surface: 'shell', maxChars: 35 },
  NLSOpsHospitalPatientHospitalRefLabel: { text: 'Hospital record ID (optional)', description: 'Field label for hospital internal ID', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientIsMinorLabel: { text: 'Patient is a minor', description: 'Checkbox label indicating patient is under 18', surface: 'shell', maxChars: 35 },
  NLSOpsHospitalPatientRepNameLabel: { text: 'Representative / Guardian name', description: 'Field label for guardian name', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientRepPhoneLabel: { text: 'Representative phone (optional)', description: 'Field label for guardian phone', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientRepRelationLabel: { text: 'Relationship to patient', description: 'Field label for representative relation', surface: 'shell', maxChars: 35 },
  NLSOpsHospitalPatientBasisLabel: { text: 'Legal basis for record', description: 'Selection label for data processing basis', surface: 'shell', maxChars: 35 },
  NLSOpsHospitalPatientBasisConsent: { text: 'Explicit consent', description: 'Option label for explicit consent basis', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalPatientBasisLegitimate: { text: 'Legitimate medical use', description: 'Option label for legitimate use basis', surface: 'shell', maxChars: 35 },
  NLSOpsHospitalPatientNoticeHeading: { text: 'Privacy & Data Notice', description: 'Notice heading in add patient dialog', surface: 'shell', maxChars: 35 },
  NLSOpsHospitalPatientSaveBtn: { text: 'Save & register', description: 'Submit button in add patient dialog', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalPatientCancelBtn: { text: 'Cancel', description: 'Cancel button label', surface: 'shell', maxChars: 20 },
  NLSOpsHospitalPatientCreating: { text: 'Registering...', description: 'Button loading state while creating patient', surface: 'shell', maxChars: 25 },

  // Visit Check-In Dialog
  NLSOpsHospitalVisitCheckInDialogTitle: { text: 'Check in patient visit', description: 'Title for visit check in dialog', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalVisitDeptLabel: { text: 'Department', description: 'Field label for department select', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalVisitConfirmBtn: { text: 'Confirm check-in', description: 'Button to confirm visit check-in', surface: 'shell', maxChars: 25 },

  // Consent Withdrawal Dialog
  NLSOpsHospitalConsentWithdrawDialogTitle: { text: 'Withdraw patient consent', description: 'Dialog title for withdrawing consent', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalConsentWithdrawWarning: { text: 'Withdrawing consent permanently clears patient name and phone number. Visit records and pseudo-ref will be retained for audit.', description: 'Warning text for consent withdrawal', surface: 'shell', maxChars: 160 },
  NLSOpsHospitalConsentWithdrawConfirmBtn: { text: 'Confirm withdrawal', description: 'Button to confirm consent withdrawal', surface: 'shell', maxChars: 25 },

  // Settings Card
  NLSOpsHospitalPatientSettingsCardTitle: { text: 'Patient directory settings', description: 'Title for hospital patient settings card', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientSettingsCardSubtitle: { text: 'Light operations directory for front-desk visit tracking.', description: 'Subtitle for hospital patient settings card', surface: 'shell', maxChars: 80 },
  NLSOpsHospitalPatientToggleLabel: { text: 'Enable patient directory', description: 'Toggle label for enabling patient directory', surface: 'shell', maxChars: 35 },
  NLSOpsHospitalPatientNeedsLegalSetup: { text: 'Needs legal setup', description: 'Badge or label when legal setup is not confirmed', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalPatientLegalSetupExplainer: { text: 'Timelining confirms legal setup once the hospital terms and patient notice review are done.', description: 'Explanation when legal setup is pending', surface: 'shell', maxChars: 120 },
  NLSOpsHospitalPatientNoticeVersion: { text: 'Notice version: {version}', description: 'Display of active notice version', surface: 'shell', maxChars: 30 },
  NLSOpsHospitalPatientNoticeEditorLabel: { text: 'Patient privacy notice text', description: 'Label for patient notice textarea editor', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientNoticeSaveBtn: { text: 'Save notice text', description: 'Button to save updated notice text', surface: 'shell', maxChars: 25 },
  NLSOpsHospitalPatientNoticeSaving: { text: 'Saving...', description: 'Saving notice in progress', surface: 'shell', maxChars: 20 },
  NLSOpsHospitalPatientNoticeSuccess: { text: 'Notice updated successfully.', description: 'Success notification for notice update', surface: 'shell', maxChars: 40 },
  NLSOpsHospitalPatientDirectoryOff: { text: 'Patient directory is currently turned off.', description: 'Information banner when directory is disabled', surface: 'shell', maxChars: 60 },

  // Owner Feed Landing (/owner/<token>)
  NLSOpsOwnerFeedLoading: { text: 'Loading digest...', description: 'Loading state on owner feed landing', surface: 'shell', maxChars: 30 },
  NLSOpsOwnerFeedInvalidTitle: { text: 'Link not valid', description: 'Title for invalid owner feed link', surface: 'shell', maxChars: 30 },
  NLSOpsOwnerFeedInvalidBody: { text: "This link isn't valid any more — ask the property for a new one.", description: 'Message body for invalid owner feed link', surface: 'shell', maxChars: 80 },
  NLSOpsOwnerFeedVerifyTitle: { text: 'Owner digest', description: 'Title on owner feed phone verification screen', surface: 'shell', maxChars: 30 },
  NLSOpsOwnerFeedVerifySubtitle: { text: "Enter your phone number to view today's property digest.", description: 'Subtitle on owner feed phone verification screen', surface: 'shell', maxChars: 70 },
  NLSOpsOwnerFeedPropertyLabel: { text: 'Property', description: 'Label for property name', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedOwnerLabel: { text: 'Owner', description: 'Label for owner name', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedPhoneLabel: { text: 'Phone number', description: 'Label for phone number input', surface: 'shell', maxChars: 25 },
  NLSOpsOwnerFeedPhonePlaceholder: { text: '+1 555 123 4567', description: 'Placeholder for phone number input', surface: 'shell', maxChars: 25 },
  NLSOpsOwnerFeedSendOtpBtn: { text: 'Send code', description: 'Button to send OTP code to owner phone', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedOtpTitle: { text: 'Enter verification code', description: 'Title for OTP verification step', surface: 'shell', maxChars: 35 },
  NLSOpsOwnerFeedOtpSubtitle: { text: 'A 6-digit code was sent to your phone.', description: 'Subtitle for OTP verification step', surface: 'shell', maxChars: 50 },
  NLSOpsOwnerFeedOtpLabel: { text: 'Verification code', description: 'Label for OTP input', surface: 'shell', maxChars: 25 },
  NLSOpsOwnerFeedVerifyOtpBtn: { text: 'Verify & open digest', description: 'Button to confirm OTP and open feed', surface: 'shell', maxChars: 30 },
  NLSOpsOwnerFeedChangePhoneBtn: { text: 'Use different number', description: 'Button to return to phone entry step', surface: 'shell', maxChars: 30 },
  NLSOpsOwnerFeedPhoneMismatch: { text: 'The phone number does not match this owner link.', description: 'Error when entered phone last 4 digits do not match link info', surface: 'shell', maxChars: 60 },
  NLSOpsOwnerFeedCapExhausted: { text: 'Too many attempts. Please try again later.', description: 'Error when OTP attempts or rate limit exceeded', surface: 'shell', maxChars: 50 },
  NLSOpsOwnerFeedInvalidOtp: { text: 'Invalid verification code. Please check and try again.', description: 'Error when verification code is wrong', surface: 'shell', maxChars: 60 },
  NLSOpsOwnerFeedGenericError: { text: 'Something went wrong. Please try again.', description: 'Generic error on owner feed landing', surface: 'shell', maxChars: 50 },
  NLSOpsOwnerFeedRefreshBtn: { text: 'Refresh', description: 'Button to refresh feed content with existing session', surface: 'shell', maxChars: 15 },
  NLSOpsOwnerFeedSignOutBtn: { text: 'Sign out', description: 'Button to sign out from owner feed session', surface: 'shell', maxChars: 15 },
  NLSOpsOwnerFeedSummaryLine: { text: '{ready} ready · {onDuty} on duty · {issues} open issues', description: 'Summary line for daily owner digest', surface: 'shell', maxChars: 60 },
  NLSOpsOwnerFeedNoDigests: { text: 'No digests available yet.', description: 'Message when digest history is empty', surface: 'shell', maxChars: 35 },
  NLSOpsOwnerFeedRoomsTitle: { text: 'Rooms', description: 'Title for rooms section in feed', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedReadyRoomsSubtitle: { text: 'Rooms ready today', description: 'Subtitle for rooms cleaned and ready today', surface: 'shell', maxChars: 30 },
  NLSOpsOwnerFeedNoReadyRooms: { text: 'No rooms made ready today', description: 'Empty state when no rooms were prepared today', surface: 'shell', maxChars: 35 },
  NLSOpsOwnerFeedPhotoPlaceholder: { text: 'Photo unavailable', description: 'Fallback text when room photo fails to load', surface: 'shell', maxChars: 25 },
  NLSOpsOwnerFeedClosePreview: { text: 'Close', description: 'Button to close full size photo modal', surface: 'shell', maxChars: 15 },
  NLSOpsOwnerFeedAttendanceTitle: { text: 'Attendance', description: 'Title for attendance section in feed', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedOnDutyCount: { text: '{count} on duty', description: 'Chip label for count of staff on duty', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedNoAttendance: { text: 'No attendance records today', description: 'Empty state when no attendance was recorded', surface: 'shell', maxChars: 35 },
  NLSOpsOwnerFeedInTime: { text: 'In: {time}', description: 'Display of shift actual in time', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedOutTime: { text: 'Out: {time}', description: 'Display of shift actual out time', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedStockTitle: { text: 'Stock', description: 'Title for stock section in feed', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedLowStockSubtitle: { text: 'Low stock items', description: 'Subtitle for low stock section', surface: 'shell', maxChars: 25 },
  NLSOpsOwnerFeedNoLowStock: { text: 'All monitored stock at normal levels', description: 'Message when no items are low stock', surface: 'shell', maxChars: 45 },
  NLSOpsOwnerFeedIssuesTitle: { text: 'Open issues', description: 'Title for open issues section in feed', surface: 'shell', maxChars: 25 },
  NLSOpsOwnerFeedNoIssues: { text: 'No open issues', description: 'Empty state when there are no open issues', surface: 'shell', maxChars: 25 },
  NLSOpsOwnerFeedShowingTrimmed: { text: 'Showing {shown} of {total}', description: 'Notice when issues list is trimmed', surface: 'shell', maxChars: 30 },
  NLSOpsOwnerFeedApprovalsTitle: { text: 'Approvals', description: 'Title for approvals section in feed', surface: 'shell', maxChars: 20 },
  NLSOpsOwnerFeedApprovalsNotice: { text: 'Approve from the link you got for each approval.', description: 'Notice instructing owners how to approve items', surface: 'shell', maxChars: 60 },
  NLSOpsOwnerFeedNoApprovals: { text: 'No pending approvals', description: 'Empty state when no approvals are pending', surface: 'shell', maxChars: 30 },
  NLSOpsOwnerFeedSentAt: { text: 'Sent {time}', description: 'Sent timestamp label', surface: 'shell', maxChars: 25 },
  NLSOpsOwnerFeedExpiresAt: { text: 'Expires {time}', description: 'Expires timestamp label', surface: 'shell', maxChars: 25 },
  NLSOpsOwnerFeedDaysAgo: { text: '{days}d ago', description: 'Relative age in days', surface: 'shell', maxChars: 15 },
  NLSOpsOwnerFeedToday: { text: 'Today', description: 'Label for today date tab', surface: 'shell', maxChars: 15 },

  // Stays Owner Feed Settings Card (StaysOwnerFeedCard.tsx)
  NLSOpsStaysOwnerFeedCardTitle: { text: 'Owner digest feed', description: 'Title for owner feed card on property sheet', surface: 'shell', maxChars: 30 },
  NLSOpsStaysOwnerFeedCardSubtitle: { text: 'Daily property proof digest sent to owners living elsewhere.', description: 'Subtitle for owner feed card on property sheet', surface: 'shell', maxChars: 70 },
  NLSOpsStaysOwnerFeedEnabledLabel: { text: 'Enable owner digest', description: 'Toggle label for enabling owner digest', surface: 'shell', maxChars: 30 },
  NLSOpsStaysOwnerFeedSendAtLabel: { text: 'Daily send time', description: 'Field label for scheduled send time', surface: 'shell', maxChars: 25 },
  NLSOpsStaysOwnerFeedSendAtHint: { text: 'Property time (workspace time zone).', description: 'Hint explaining send time zone', surface: 'shell', maxChars: 45 },
  NLSOpsStaysOwnerFeedOwnersSectionTitle: { text: 'Owners (1–3)', description: 'Section title for owners list', surface: 'shell', maxChars: 25 },
  NLSOpsStaysOwnerFeedAddOwnerBtn: { text: 'Add owner', description: 'Button to add another owner', surface: 'shell', maxChars: 20 },
  NLSOpsStaysOwnerFeedOwnerNameLabel: { text: 'Owner name', description: 'Field label for owner name', surface: 'shell', maxChars: 20 },
  NLSOpsStaysOwnerFeedOwnerNamePlaceholder: { text: 'Full name', description: 'Placeholder for owner name', surface: 'shell', maxChars: 25 },
  NLSOpsStaysOwnerFeedOwnerPhoneLabel: { text: 'Phone number (E.164 with country code)', description: 'Field label for owner phone', surface: 'shell', maxChars: 45 },
  NLSOpsStaysOwnerFeedOwnerPhonePlaceholder: { text: '+1 555 123 4567', description: 'Placeholder for owner phone', surface: 'shell', maxChars: 25 },
  NLSOpsStaysOwnerFeedOwnerEmailLabel: { text: 'Email address (optional)', description: 'Field label for owner email', surface: 'shell', maxChars: 35 },
  NLSOpsStaysOwnerFeedOwnerEmailPlaceholder: { text: 'owner@example.com', description: 'Placeholder for owner email', surface: 'shell', maxChars: 30 },
  NLSOpsStaysOwnerFeedClearEmail: { text: 'Clear email', description: 'Button label to clear owner email', surface: 'shell', maxChars: 15 },
  NLSOpsStaysOwnerFeedRemoveOwnerBtn: { text: 'Remove', description: 'Button to remove an owner', surface: 'shell', maxChars: 15 },
  NLSOpsStaysOwnerFeedReissueLinkBtn: { text: 'New link', description: 'Button to reissue an owner link', surface: 'shell', maxChars: 15 },
  NLSOpsStaysOwnerFeedRemoveConfirmTitle: { text: 'Remove owner', description: 'Dialog title for removing an owner', surface: 'shell', maxChars: 25 },
  NLSOpsStaysOwnerFeedRemoveConfirmBody: { text: 'Are you sure you want to remove this owner? Their digest link will stop working immediately.', description: 'Dialog warning when removing an owner', surface: 'shell', maxChars: 110 },
  NLSOpsStaysOwnerFeedReissueConfirmTitle: { text: 'Generate new link', description: 'Dialog title for reissuing link', surface: 'shell', maxChars: 25 },
  NLSOpsStaysOwnerFeedReissueConfirmBody: { text: 'Generating a new link immediately deactivates the previous link. Are you sure you want to continue?', description: 'Dialog warning when reissuing link', surface: 'shell', maxChars: 110 },
  NLSOpsStaysOwnerFeedConfirmBtn: { text: 'Confirm', description: 'Confirm action button label', surface: 'shell', maxChars: 15 },
  NLSOpsStaysOwnerFeedCancelBtn: { text: 'Cancel', description: 'Cancel action button label', surface: 'shell', maxChars: 15 },
  NLSOpsStaysOwnerFeedStockLocationsTitle: { text: 'Stock locations (optional, up to 5)', description: 'Title for stock locations selection', surface: 'shell', maxChars: 45 },
  NLSOpsStaysOwnerFeedStockLocationsHint: { text: 'Select stock locations to include in the daily digest.', description: 'Hint for stock locations selection', surface: 'shell', maxChars: 60 },
  NLSOpsStaysOwnerFeedSaveBtn: { text: 'Save feed settings', description: 'Button to save owner feed settings', surface: 'shell', maxChars: 25 },
  NLSOpsStaysOwnerFeedSaving: { text: 'Saving...', description: 'Button label while saving feed settings', surface: 'shell', maxChars: 20 },
  NLSOpsStaysOwnerFeedSaveSuccess: { text: 'Owner feed settings saved.', description: 'Success notification banner', surface: 'shell', maxChars: 35 },
  NLSOpsStaysOwnerFeedConflictError: { text: 'Feed settings were modified by someone else. Please reload and try again.', description: 'Conflict error banner on concurrent version edit', surface: 'shell', maxChars: 85 },
  NLSOpsStaysOwnerFeedLinkModalTitle: { text: 'Owner digest link', description: 'Title for one-time link display modal', surface: 'shell', maxChars: 30 },
  NLSOpsStaysOwnerFeedLinkModalNotice: { text: 'Links are shown once. Make a new link if it is lost.', description: 'Notice that one-time links are not stored', surface: 'shell', maxChars: 65 },
  NLSOpsStaysOwnerFeedLinkCopyBtn: { text: 'Copy link', description: 'Button to copy one-time link', surface: 'shell', maxChars: 20 },
  NLSOpsStaysOwnerFeedLinkCopied: { text: 'Copied!', description: 'Button state after copying link', surface: 'shell', maxChars: 15 },
  NLSOpsStaysOwnerFeedLinkShareBtn: { text: 'Share link', description: 'Button to share one-time link', surface: 'shell', maxChars: 20 },
  NLSOpsStaysOwnerFeedLinkShared: { text: 'Shared!', description: 'Button state after sharing link', surface: 'shell', maxChars: 15 },
  NLSOpsStaysOwnerFeedLinkDoneBtn: { text: 'Done', description: 'Button to dismiss one-time link modal', surface: 'shell', maxChars: 15 },
  NLSOpsStaysOwnerFeedSendNowTitle: { text: "Send today's digest now", description: 'Section title for sending digest now', surface: 'shell', maxChars: 35 },
  NLSOpsStaysOwnerFeedSendNowDesc: { text: 'Send an instant digest preview to one owner.', description: 'Description for send now preview action', surface: 'shell', maxChars: 50 },
  NLSOpsStaysOwnerFeedPickOwnerLabel: { text: 'Select owner', description: 'Label for owner picker in send now section', surface: 'shell', maxChars: 20 },
  NLSOpsStaysOwnerFeedSendNowBtn: { text: 'Send now', description: 'Button to send digest immediately', surface: 'shell', maxChars: 20 },
  NLSOpsStaysOwnerFeedSendingNow: { text: 'Sending...', description: 'Button state while sending digest immediately', surface: 'shell', maxChars: 20 },
  NLSOpsStaysOwnerFeedSendNowSuccess: { text: "Sent today's digest via {channel} ({status}).", description: 'Success notification after sending digest now', surface: 'shell', maxChars: 55 },
  NLSOpsStaysOwnerFeedRecentDigestsTitle: { text: 'Recent digests', description: 'Section title for recent owner digests', surface: 'shell', maxChars: 25 },
  NLSOpsStaysOwnerFeedNoRecentDigests: { text: 'No digests have been sent yet.', description: 'Empty state for recent digests list', surface: 'shell', maxChars: 35 },
  NLSOpsStaysOwnerFeedRecentDigestItem: { text: '{dateKey} · Built {time} · {ready} ready · {issues} issues', description: 'Summary line for a recent digest item', surface: 'shell', maxChars: 65 },
  NLSOpsStaysOwnerFeedPhoneChangeNote: { text: 'To change phone number, remove and re-add the owner.', description: 'Note on owner row explaining how to update phone', surface: 'shell', maxChars: 65 },
  NLSOpsStaysOwnerFeedMaxOwnersReached: { text: 'Maximum 3 owners allowed.', description: 'Validation message when maximum owners reached', surface: 'shell', maxChars: 30 },
  NLSOpsStaysOwnerFeedMinOwnersRequired: { text: 'At least 1 owner is required.', description: 'Validation message when owners list is empty', surface: 'shell', maxChars: 35 },
  NLSOpsStaysOwnerFeedDistinctPhonesRequired: { text: 'Each owner must have a unique phone number.', description: 'Validation message when duplicate phones entered', surface: 'shell', maxChars: 50 },
  NLSOpsStaysOwnerFeedInvalidTime: { text: 'Send time must be in HH:MM format (e.g. 21:00).', description: 'Validation message for invalid send time', surface: 'shell', maxChars: 55 },
  NLSOpsStaysOwnerFeedErrPermission: { text: 'You do not have permission to manage owner feed.', description: 'Permission error message', surface: 'shell', maxChars: 55 },
  NLSOpsStaysOwnerFeedErrNotFound: { text: 'Feed or property not found.', description: 'Not found error message', surface: 'shell', maxChars: 35 },
  NLSOpsStaysOwnerFeedErrGeneric: { text: 'An error occurred while saving owner feed settings.', description: 'Generic error message for owner feed settings', surface: 'shell', maxChars: 60 },

  // Stays Group View & Template Push (R39 K11.b / SO-P125)
  NLSOpsStaysGroupTab: { text: 'Group', description: 'Tab title for Stays Group View', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupTitle: { text: 'Group Overview', description: 'Title for Stays Group View screen', surface: 'shell', maxChars: 25 },
  NLSOpsStaysGroupSubtitle: { text: 'Cross-property readiness, guest requests, missed checks, and compliance scorecard.', description: 'Subtitle for Stays Group View screen', surface: 'shell', maxChars: 90 },
  NLSOpsStaysGroupScorecardTitle: { text: 'Scorecard', description: 'Title for property scorecard breakdown', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupScorecardAverage: { text: 'Group Average Score', description: 'Scorecard average score label', surface: 'shell', maxChars: 25 },
  NLSOpsStaysGroupScorecardReadiness: { text: 'Readiness', description: 'Scorecard readiness component label', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupScorecardRequests: { text: 'Requests', description: 'Scorecard requests component label', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupScorecardChecks: { text: 'Missed Checks', description: 'Scorecard missed checks component label', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupScorecardLicences: { text: 'Licences', description: 'Scorecard licences component label', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupReadinessBar: { text: 'Room Readiness', description: 'Section label for room readiness distribution', surface: 'shell', maxChars: 25 },
  NLSOpsStaysGroupReady: { text: 'Ready', description: 'Label for ready rooms count', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupInspected: { text: 'Inspected', description: 'Label for inspected rooms count', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupCleaned: { text: 'Cleaned', description: 'Label for cleaned rooms count', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupCleaning: { text: 'Cleaning', description: 'Label for cleaning rooms count', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupVacantDirty: { text: 'Vacant Dirty', description: 'Label for vacant dirty rooms count', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupOccupied: { text: 'Occupied', description: 'Label for occupied rooms count', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupOutOfOrder: { text: 'Out of Order', description: 'Label for out of order rooms count', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupTotalRooms: { text: 'Total Rooms', description: 'Label for total rooms count', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupOpenRequests: { text: 'Open Requests', description: 'Label for open guest requests count', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupOverdueRequests: { text: 'Overdue Requests', description: 'Label for overdue guest requests count', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupMissedChecks7Days: { text: 'Missed Checks (7d)', description: 'Label for missed checks in last 7 days', surface: 'shell', maxChars: 25 },
  NLSOpsStaysGroupExpiredLicences: { text: 'Expired Licences', description: 'Label for expired licences count', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupExpiringLicences30Days: { text: 'Expiring in 30d', description: 'Label for licences expiring in 30 days', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupSortScorecard: { text: 'Score (High to Low)', description: 'Sort option by scorecard descending', surface: 'shell', maxChars: 25 },
  NLSOpsStaysGroupSortName: { text: 'Property Name (A-Z)', description: 'Sort option by property name ascending', surface: 'shell', maxChars: 25 },
  NLSOpsStaysGroupSortLabel: { text: 'Sort by:', description: 'Label for sorting controls', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupPushTemplateBtn: { text: 'Push template', description: 'Button to open push template sheet', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupPushTemplateTitle: { text: 'Push template to properties', description: 'Title for push template sheet', surface: 'shell', maxChars: 35 },
  NLSOpsStaysGroupPushTemplateDesc: { text: 'Distribute a group register or checklist template across child properties.', description: 'Description for push template sheet', surface: 'shell', maxChars: 85 },
  NLSOpsStaysGroupSelectTemplate: { text: 'Select template', description: 'Label for template picker', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupNoTemplatesAvailable: { text: 'No templates found on this group place. Create a template first.', description: 'Notice when group place has no templates', surface: 'shell', maxChars: 80 },
  NLSOpsStaysGroupSelectTargetProperties: { text: 'Target properties', description: 'Section title for target properties checklist', surface: 'shell', maxChars: 25 },
  NLSOpsStaysGroupSelectAll: { text: 'Select all', description: 'Button to select all properties', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupDeselectAll: { text: 'Deselect all', description: 'Button to deselect all properties', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupPushAction: { text: 'Push template', description: 'Action button to execute template push', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupPushing: { text: 'Pushing template...', description: 'Button label while pushing template', surface: 'shell', maxChars: 25 },
  NLSOpsStaysGroupPushResultsTitle: { text: 'Push results', description: 'Title for push results summary', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupStatusCreated: { text: 'Created', description: 'Status indicating template copy created', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupStatusUpdated: { text: 'Updated', description: 'Status indicating template copy updated', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupStatusLocalChangesKept: { text: 'Local changes kept', description: 'Status indicating local modifications preserved', surface: 'shell', maxChars: 25 },
  NLSOpsStaysGroupStatusNoAccess: { text: 'No access', description: 'Status indicating caller lacked property access', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupPushSuccess: { text: 'Template push completed.', description: 'Push template success notice', surface: 'shell', maxChars: 30 },
  NLSOpsStaysGroupOpenPropertyBoard: { text: 'Board', description: 'Button to open property room board', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupOpenPropertyToday: { text: 'Today', description: 'Button to open property today view', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupOpenPropertyRequests: { text: 'Requests', description: 'Button to open property requests', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupOpenPropertyCompliance: { text: 'Licences', description: 'Button to open property compliance', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupTotalsTitle: { text: 'Group Totals', description: 'Title for group totals summary bar', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupTotalProperties: { text: 'Properties', description: 'Count label for properties', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupAverageScore: { text: 'Average Score', description: 'Label for group average scorecard score', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupAverageReadiness: { text: 'Average Readiness', description: 'Label for group average ready percentage', surface: 'shell', maxChars: 20 },
  NLSOpsStaysGroupLoading: { text: 'Loading stays group overview...', description: 'Loading state text', surface: 'shell', maxChars: 40 },
  NLSOpsStaysGroupEmpty: { text: 'No properties found under this group place.', description: 'Empty state message when no child properties exist', surface: 'shell', maxChars: 55 },
  NLSOpsStaysGroupRetry: { text: 'Retry', description: 'Button to retry loading group view', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupClose: { text: 'Close', description: 'Button to close sheet or modal', surface: 'shell', maxChars: 15 },
  NLSOpsStaysGroupErrPermission: { text: 'Access denied. Only Owner, Admin, or Group Manager can view group overview.', description: 'Permission error for group view', surface: 'shell', maxChars: 85 },
  NLSOpsStaysGroupErrNotFound: { text: 'Group place not found.', description: 'Not found error for group place', surface: 'shell', maxChars: 30 },
  NLSOpsStaysGroupErrNotGroup: { text: 'Specified place is not a GROUP level place.', description: 'Place is not a group error', surface: 'shell', maxChars: 50 },
  NLSOpsStaysGroupErrDisabled: { text: 'Stays module is not enabled in this workspace.', description: 'Stays module disabled error', surface: 'shell', maxChars: 50 },
  NLSOpsStaysGroupErrGeneric: { text: 'An error occurred while loading stays group view.', description: 'Generic error message for group view', surface: 'shell', maxChars: 55 },
  NLSOpsStaysGroupErrConflict: { text: 'A conflict occurred during template push. Please retry.', description: 'Conflict error during template push', surface: 'shell', maxChars: 60 },

  // Owner Console Cards
  NLSOwnerConsoleHosPatientLegalSetupTitle: { text: 'Hospital patient legal setup', description: 'Title for the hospital patient legal setup card in owner console', surface: 'owner-console', maxChars: 40 },
  NLSOwnerConsoleHosPatientLegalSetupDesc: { text: 'Confirm hospital terms and patient notice review before directory can be enabled.', description: 'Description for hospital patient legal setup card', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleHosPatientLegalStatusConfirmed: { text: 'Legal setup confirmed', description: 'Status indicating legal setup is confirmed', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHosPatientLegalStatusNotConfirmed: { text: 'Legal setup not confirmed', description: 'Status indicating legal setup is not confirmed', surface: 'owner-console', maxChars: 30 },
  NLSOwnerConsoleHosPatientLegalConfirmBtn: { text: 'Confirm legal setup done', description: 'Button to confirm legal setup', surface: 'owner-console', maxChars: 35 },
  NLSOwnerConsoleHosPatientLegalRevokeBtn: { text: 'Revoke', description: 'Button to revoke legal setup confirmation', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleHosPatientLegalConfirmDialogTitle: { text: 'Confirm hospital patient legal setup', description: 'Dialog title for confirming legal setup', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleHosPatientLegalConfirmDialogBody: { text: 'Are you sure hospital terms and patient notice review have been completed for this workspace?', description: 'Dialog message for confirming legal setup', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleHosPatientLegalRevokeDialogTitle: { text: 'Revoke hospital patient legal setup', description: 'Dialog title for revoking legal setup', surface: 'owner-console', maxChars: 50 },
  NLSOwnerConsoleHosPatientLegalRevokeDialogBody: { text: 'Revoking legal setup will prevent this workspace from re-enabling patient directory.', description: 'Dialog message for revoking legal setup', surface: 'owner-console', maxChars: 120 },
  NLSOwnerConsoleHosPatientLegalCancelBtn: { text: 'Cancel', description: 'Cancel button label', surface: 'owner-console', maxChars: 20 },
  NLSOwnerConsoleHosPatientLegalSuccess: { text: 'Updated legal setup.', description: 'Success notification', surface: 'owner-console', maxChars: 30 },

  // Cold Chain & Logger Import (R39 M6.b)
  NLSOpsColdChainTitle: { text: 'Cold chain & water', description: 'Title for Clinics Cold chain tab', surface: 'shell', maxChars: 35 },
  NLSOpsColdChainSubtitle: { text: 'Gauge photos twice a day, data-logger files, and out of range excursions', description: 'Subtitle for Clinics Cold chain tab', surface: 'shell', maxChars: 90 },
  NLSOpsColdChainAddStarters: { text: 'Add starter logs', description: 'Button to seed starter templates for clinics', surface: 'shell', maxChars: 25 },
  NLSOpsColdChainAddStartersSuccess: { text: 'Starter templates added successfully', description: 'Success message after adding starter templates', surface: 'shell', maxChars: 45 },
  NLSOpsColdChainAddStartersError: { text: 'Failed to add starter templates', description: 'Error message if adding starter templates fails', surface: 'shell', maxChars: 45 },
  NLSOpsColdChainImportLogger: { text: 'Logger file', description: 'Button to open logger import dialog', surface: 'shell', maxChars: 20 },
  NLSOpsColdChainAssetsHeading: { text: 'Monitored units', description: 'Heading for list of cold chain assets', surface: 'shell', maxChars: 30 },
  NLSOpsColdChainNoAssets: { text: 'No assets configured with a temperature band.', description: 'Empty state message when no assets have a temp band', surface: 'shell', maxChars: 60 },
  NLSOpsColdChainOpenExcursions: { text: 'Open excursion requests', description: 'Heading for list of open excursion requests', surface: 'shell', maxChars: 35 },
  NLSOpsColdChainNoExcursions: { text: 'No open excursions.', description: 'Empty state message when there are no open excursions', surface: 'shell', maxChars: 30 },
  NLSOpsColdChainLatestReading: { text: 'Latest reading', description: 'Label for latest recorded temperature', surface: 'shell', maxChars: 25 },
  NLSOpsColdChainTodayChecks: { text: "Today's checks", description: "Label for today's temperature checks", surface: 'shell', maxChars: 25 },
  NLSOpsColdChainDone: { text: 'Done', description: 'Badge or label when check is completed', surface: 'shell', maxChars: 15 },
  NLSOpsColdChainDue: { text: 'Due', description: 'Badge or label when check is due', surface: 'shell', maxChars: 15 },
  NLSOpsColdChainTempBand: { text: 'Band', description: 'Label for temperature band', surface: 'shell', maxChars: 15 },
  NLSOpsColdChainImportTitle: { text: 'Import data logger CSV', description: 'Title for logger import dialog', surface: 'shell', maxChars: 35 },
  NLSOpsColdChainSelectAsset: { text: 'Select asset', description: 'Label for asset selector in import dialog', surface: 'shell', maxChars: 25 },
  NLSOpsColdChainChooseFile: { text: 'Select CSV file', description: 'Label for file input in import dialog', surface: 'shell', maxChars: 25 },
  NLSOpsColdChainTimestampCol: { text: 'Timestamp column', description: 'Label for timestamp column dropdown in import dialog', surface: 'shell', maxChars: 30 },
  NLSOpsColdChainTempCol: { text: 'Temperature column', description: 'Label for temperature column dropdown in import dialog', surface: 'shell', maxChars: 30 },
  NLSOpsColdChainProbeCol: { text: 'Probe column (optional)', description: 'Label for probe column dropdown in import dialog', surface: 'shell', maxChars: 35 },
  NLSOpsColdChainPreviewHeading: { text: 'Preview (first 5 rows)', description: 'Heading for CSV preview table in import dialog', surface: 'shell', maxChars: 35 },
  NLSOpsColdChainImportBtn: { text: 'Import', description: 'Submit button for CSV import', surface: 'shell', maxChars: 20 },
  NLSOpsColdChainImporting: { text: 'Importing...', description: 'Import in progress button text', surface: 'shell', maxChars: 20 },
  NLSOpsColdChainImportSuccess: { text: 'Import complete', description: 'Success title after logger CSV import', surface: 'shell', maxChars: 25 },
  NLSOpsColdChainImportSummary: { text: 'Processed {total} readings across {excursions} excursion windows ({requests} requests raised).', description: 'Summary message of import results', surface: 'shell', maxChars: 100 },
  NLSOpsColdChainImportReplay: { text: 'File was previously imported. No new requests were raised.', description: 'Replay notification message', surface: 'shell', maxChars: 80 },
  NLSOpsColdChainClose: { text: 'Close', description: 'Close dialog button label', surface: 'shell', maxChars: 15 },
  // Biomedical Waste (BMW) Panel & Hand-off (R39 M7.b)
  NLSOpsBmwSubtitle: { text: 'Bio-medical waste segregation register (BMW Rules, 4-colour categories)', description: 'BMW register description subtitle', surface: 'shell', maxChars: 80 },
  NLSOpsBmwSelectPlace: { text: 'Select Place', description: 'Aria label for BMW place selector', surface: 'shell', maxChars: 20 },
  NLSOpsBmwBagsTitle: { text: 'Waste Bags', description: 'Title for segregated bags section', surface: 'shell', maxChars: 25 },
  NLSOpsBmwAddBag: { text: 'Add Bag', description: 'Button to add a waste bag', surface: 'shell', maxChars: 20 },
  NLSOpsBmwBarcode: { text: 'Barcode', description: 'Label for bag barcode', surface: 'shell', maxChars: 15 },
  NLSOpsBmwBarcodePlaceholder: { text: 'e.g. BMW-2026-001', description: 'Placeholder for bag barcode', surface: 'shell', maxChars: 25 },
  NLSOpsBmwColor: { text: 'Colour / Category', description: 'Label for bag colour category', surface: 'shell', maxChars: 25 },
  NLSOpsBmwWeightKg: { text: 'Weight (kg)', description: 'Label for weight in kg', surface: 'shell', maxChars: 20 },
  NLSOpsBmwBagCount: { text: 'Bag Count', description: 'Label for count of bags', surface: 'shell', maxChars: 20 },
  NLSOpsBmwTotalWeight: { text: 'Total Weight (kg)', description: 'Label for total weight in kg', surface: 'shell', maxChars: 25 },
  NLSOpsBmwBagMismatchWarning: { text: 'Bag weights differ from category weight by more than 10%.', description: 'Warning when bag weights mismatch daily weights', surface: 'shell', maxChars: 80 },
  NLSOpsBmwPreTreatmentPhotos: { text: 'Pre-treatment Photos', description: 'Label for pre-treatment photos section', surface: 'shell', maxChars: 30 },
  NLSOpsBmwPreTreatmentPhotoRequired: { text: 'Pre-treatment photos are required for Yellow and Red waste.', description: 'Requirement notice for pre-treatment photos', surface: 'shell', maxChars: 80 },
  NLSOpsBmwAddPhoto: { text: 'Add Photo', description: 'Button to add pre-treatment photo', surface: 'shell', maxChars: 20 },
  NLSOpsBmwPhotoPlaceholder: { text: 'A4/workspace/bmw/filename.jpg', description: 'Placeholder for A4 storage photo path', surface: 'shell', maxChars: 40 },
  NLSOpsBmwSettings: { text: 'BMW Settings', description: 'Button to open BMW settings dialog', surface: 'shell', maxChars: 20 },
  NLSOpsBmwRequirePreTreatment: { text: 'Require pre-treatment photos for Yellow and Red', description: 'Checkbox label for pre-treatment photo setting', surface: 'shell', maxChars: 60 },
  NLSOpsBmwStorageHours: { text: 'Storage limit (hours)', description: 'Label for max storage hours setting', surface: 'shell', maxChars: 30 },
  NLSOpsBmwCollectors: { text: 'Authorized Waste Collectors', description: 'Header for collectors list in settings', surface: 'shell', maxChars: 35 },
  NLSOpsBmwCollectorName: { text: 'Collector Name', description: 'Label for collector person name', surface: 'shell', maxChars: 25 },
  NLSOpsBmwCbwtfName: { text: 'CBWTF Facility Name', description: 'Label for common bio-medical waste treatment facility name', surface: 'shell', maxChars: 30 },
  NLSOpsBmwCollectorPhone: { text: 'Phone (E.164, e.g. +91...)', description: 'Label for collector phone number input', surface: 'shell', maxChars: 35 },
  NLSOpsBmwAddCollector: { text: 'Add Collector', description: 'Button to add collector to list', surface: 'shell', maxChars: 20 },
  NLSOpsBmwStartHandover: { text: 'Start Hand-off', description: 'Button to begin waste collector handover', surface: 'shell', maxChars: 20 },
  NLSOpsBmwHandoverTitle: { text: 'Biomedical Waste Hand-off', description: 'Title for handover QR and link dialog', surface: 'shell', maxChars: 35 },
  NLSOpsBmwHandoverDesc: { text: 'Scan QR code or open link on collector phone to verify hand-off.', description: 'Instructions for handover link', surface: 'shell', maxChars: 80 },
  NLSOpsBmwCopyLink: { text: 'Copy Link', description: 'Button to copy handover link', surface: 'shell', maxChars: 15 },
  NLSOpsBmwLinkCopied: { text: 'Link copied to clipboard.', description: 'Feedback message when link is copied', surface: 'shell', maxChars: 35 },
  NLSOpsBmwAttachReceipt: { text: 'Attach Receipt Photo', description: 'Button to attach receipt photo to pickup', surface: 'shell', maxChars: 30 },
  NLSOpsBmwReceiptPhoto: { text: 'Receipt Photo Path', description: 'Label for receipt photo input', surface: 'shell', maxChars: 25 },
  NLSOpsBmwSaveDraft: { text: 'Save Draft', description: 'Button to save BMW draft', surface: 'shell', maxChars: 20 },
  NLSOpsBmwDate: { text: 'Date', description: 'Label for date field', surface: 'shell', maxChars: 10 },
  NLSOpsBmwDatePlaceholder: { text: 'YYYY-MM-DD', description: 'Date format placeholder', surface: 'shell', maxChars: 12 },
  NLSOpsBmwPickupManifest: { text: 'Manifest / Barcode Ref', description: 'Label for pickup manifest reference', surface: 'shell', maxChars: 30 },
  NLSOpsBmwPickupVehicle: { text: 'Vehicle Reg / Ref', description: 'Label for pickup vehicle reference', surface: 'shell', maxChars: 25 },
  NLSOpsBmwPickupManifestPlaceholder: { text: 'e.g. CPCB-MAN-2026-9921', description: 'Placeholder for pickup manifest', surface: 'shell', maxChars: 30 },
  NLSOpsBmwPickupVehiclePlaceholder: { text: 'e.g. DL-01-AB-1234', description: 'Placeholder for pickup vehicle', surface: 'shell', maxChars: 25 },
  NLSOpsBmwPickupRecorded: { text: 'BMW waste collector pickup recorded.', description: 'Feedback message when pickup is recorded', surface: 'shell', maxChars: 50 },
  NLSOpsBmwCorrectionReason: { text: 'Correction Reason', description: 'Label for correction reason input', surface: 'shell', maxChars: 25 },
  NLSOpsBmwCorrectionPlaceholder: { text: 'Explain why the submitted daily weight is being corrected...', description: 'Placeholder for correction reason', surface: 'shell', maxChars: 70 },
  NLSOpsBmwWeightLabel: { text: 'Weight (kg, max 3 dec)', description: 'Helper label for weight input', surface: 'shell', maxChars: 30 },
  NLSOpsBmwBagCountLabel: { text: 'Bag count', description: 'Helper label for bag count input', surface: 'shell', maxChars: 20 },
  NLSOpsBmwHoursLabel: { text: 'hours', description: 'Unit for hours stored', surface: 'shell', maxChars: 10 },
  NLSOpsBmwMaxLimit: { text: 'max', description: 'Max limit suffix', surface: 'shell', maxChars: 10 },
  NLSOpsBmwCategoryHeader: { text: 'Category', description: 'Table header for BMW category', surface: 'shell', maxChars: 15 },
  NLSOpsBmwTotalHeader: { text: 'Total', description: 'Total row header', surface: 'shell', maxChars: 15 },
  NLSOpsBmwRemove: { text: 'Remove', description: 'Button to remove an item', surface: 'shell', maxChars: 15 },
  NLSOpsBmwSaveSettings: { text: 'Save Settings', description: 'Button to save BMW settings', surface: 'shell', maxChars: 20 },
  NLSOpsBmwAttach: { text: 'Attach', description: 'Button to attach a photo', surface: 'shell', maxChars: 15 },
  NLSOpsBmwCancel: { text: 'Cancel', description: 'Button to cancel action', surface: 'shell', maxChars: 15 },
  NLSOpsBmwConfirmMismatch: { text: 'Confirm Mismatch & Submit', description: 'Button to confirm bag weight mismatch and submit', surface: 'shell', maxChars: 35 },
  NLSOpsBmwHandoverVerified: { text: 'Hand-off successfully confirmed!', description: 'Success message after handover confirmed', surface: 'shell', maxChars: 45 },
  NLSOpsBmwHandoverPhonePrompt: { text: 'Enter your registered phone number to verify handover.', description: 'Instruction for collector phone verification', surface: 'shell', maxChars: 70 },
  NLSOpsBmwHandoverOtpPrompt: { text: 'Enter the OTP sent to your phone.', description: 'Instruction for collector OTP entry', surface: 'shell', maxChars: 50 },
  NLSOpsBmwHandoverOutstandingBags: { text: 'Outstanding Bags for Hand-off', description: 'Header for bags to confirm', surface: 'shell', maxChars: 40 },
  NLSOpsBmwHandoverConfirmPickup: { text: 'Confirm Pickup', description: 'Button to confirm pickup of bags', surface: 'shell', maxChars: 20 },
  NLSOpsBmwHandoverInvalid: { text: 'This handover link is invalid or has expired.', description: 'Error state when handover link is invalid', surface: 'shell', maxChars: 60 },
  NLSOpsBmwHandoverLoading: { text: 'Loading handover details...', description: 'Loading state for handover landing', surface: 'shell', maxChars: 35 },
  NLSOpsBmwHandoverPhoneLabel: { text: 'Registered Phone Number', description: 'Label for collector phone input', surface: 'shell', maxChars: 35 },
  NLSOpsBmwHandoverPhonePlaceholder: { text: '+919876543210', description: 'Placeholder for collector phone number', surface: 'shell', maxChars: 15 },
  NLSOpsBmwHandoverVerifyPhone: { text: 'Send Verification Code', description: 'Button to send OTP for handover', surface: 'shell', maxChars: 30 },
  NLSOpsBmwHandoverOtpLabel: { text: '6-digit OTP', description: 'Label for OTP input', surface: 'shell', maxChars: 20 },
  NLSOpsBmwHandoverOtpPlaceholder: { text: '123456', description: 'Placeholder for 6-digit OTP', surface: 'shell', maxChars: 10 },
  NLSOpsBmwHandoverConfirmAction: { text: 'Confirm & Record Hand-off', description: 'Button to complete handover verification', surface: 'shell', maxChars: 35 },
  NLSOpsBmwHandoverConfirmedTitle: { text: 'Hand-off Completed', description: 'Title when handover is confirmed', surface: 'shell', maxChars: 25 },
  NLSOpsBmwHandoverConfirmedDesc: { text: 'The waste bags have been transferred. Thank you.', description: 'Success description after handover confirmed', surface: 'shell', maxChars: 60 },
  NLSOpsBmwHandoverPickupId: { text: 'Pickup Reference', description: 'Label for confirmed pickup ID', surface: 'shell', maxChars: 25 },
  NLSOpsBmwHandoverMaskedPhoneNotice: { text: 'Authorized phones: {phones}', description: 'Notice of masked collector phones', surface: 'shell', maxChars: 60 },
  NLSOpsBmwHandoverNoBags: { text: 'No unpicked bags available for hand-off.', description: 'Notice when no bags are available', surface: 'shell', maxChars: 50 },
  NLSOpsBmwClose: { text: 'Close', description: 'Close button label', surface: 'shell', maxChars: 15 },
  NLSOpsBmwPickupHeader: { text: 'Pickups', description: 'Pickups section header', surface: 'shell', maxChars: 20 },

  NLSOpsTabClinicsImaging: { text: 'Imaging', description: 'Section tab label for Clinics Imaging.', surface: 'shell', maxChars: 25 },
  NLSOpsCliImagingSummary: { text: 'PCPNDT Form F register, pre-scan statutory verification, monthly returns, TLD swaps and AERB QA checks.', description: 'Summary for Clinics Imaging tab.', surface: 'shell', maxChars: 120 },
  NLSOpsCliImagingNewFormF: { text: 'New Form F record', description: 'Button to create a new Form F entry.', surface: 'shell', maxChars: 30 },
  NLSOpsCliImagingPrecheckTitle: { text: 'PCPNDT statutory pre-check', description: 'Heading for pre-check verification panel.', surface: 'shell', maxChars: 35 },
  NLSOpsCliImagingPrecheckPassed: { text: 'Compliant: machine and doctor registered for scan', description: 'Status badge when pre-check succeeds.', surface: 'shell', maxChars: 60 },
  NLSOpsCliImagingPrecheckRefused: { text: 'Refused: Form F pre-check failed', description: 'Banner when Form F pre-check fails.', surface: 'shell', maxChars: 50 },
  NLSOpsCliImagingPrecheckMachineMissing: { text: 'Ultrasound machine lacks valid PCPNDT Form B registration for this scan date', description: 'Pre-check failure for machine registration.', surface: 'shell', maxChars: 90 },
  NLSOpsCliImagingPrecheckDoctorMissing: { text: 'Performing doctor lacks active PCPNDT_DOCTOR legal registration', description: 'Pre-check failure for doctor legal registration.', surface: 'shell', maxChars: 80 },
  NLSOpsCliImagingPatientName: { text: 'Patient name', description: 'Field label for patient name.', surface: 'shell', maxChars: 25 },
  NLSOpsCliImagingPatientAge: { text: 'Age', description: 'Field label for patient age.', surface: 'shell', maxChars: 15 },
  NLSOpsCliImagingChildrenCount: { text: 'Children count', description: 'Field label for living children count.', surface: 'shell', maxChars: 25 },
  NLSOpsCliImagingIndication: { text: 'Clinical indication', description: 'Field label for ultrasound indication.', surface: 'shell', maxChars: 30 },
  NLSOpsCliImagingMachineAsset: { text: 'Ultrasound machine', description: 'Field label for machine asset.', surface: 'shell', maxChars: 30 },
  NLSOpsCliImagingDoctor: { text: 'Performing doctor', description: 'Field label for performing doctor.', surface: 'shell', maxChars: 30 },
  NLSOpsCliImagingDeclarationSigned: { text: 'Declaration signed by patient & doctor', description: 'Field label for signed declaration.', surface: 'shell', maxChars: 50 },
  NLSOpsCliImagingSignedPhoto: { text: 'Signed Form F physical copy photo', description: 'Field label for signed photo path.', surface: 'shell', maxChars: 50 },
  NLSOpsCliImagingSubmit: { text: 'Submit Form F', description: 'Button to submit Form F record.', surface: 'shell', maxChars: 25 },
  NLSOpsCliImagingDueFilings: { text: 'Monthly Form F filings (due 5th)', description: 'Header for monthly filings schedule section.', surface: 'shell', maxChars: 40 },
  NLSOpsCliImagingTldSwaps: { text: 'Quarterly TLD badge swaps', description: 'Header for TLD badge swaps section.', surface: 'shell', maxChars: 35 },
  NLSOpsCliImagingAerbDates: { text: 'AERB QA compliance (2-yearly)', description: 'Header for AERB QA dates section.', surface: 'shell', maxChars: 40 },
  NLSOpsCliImagingNoRecords: { text: 'No Form F records yet', description: 'Empty state for Form F records.', surface: 'shell', maxChars: 35 },
  NLSOpsCliImagingNoFilings: { text: 'No filings currently due', description: 'Empty state for monthly filings.', surface: 'shell', maxChars: 35 },
  NLSOpsCliImagingNoTldSwaps: { text: 'No TLD badge swaps due', description: 'Empty state for TLD swaps.', surface: 'shell', maxChars: 35 },
  NLSOpsCliImagingNoAerb: { text: 'No AERB compliance items logged', description: 'Empty state for AERB items.', surface: 'shell', maxChars: 40 },
  NLSOpsCliImagingStatus: { text: 'Status', description: 'Table header for status.', surface: 'shell', maxChars: 15 },
  NLSOpsCliImagingValidUntil: { text: 'Valid until', description: 'Table header for valid until date.', surface: 'shell', maxChars: 20 },
  NLSOpsCliImagingDaysRemaining: { text: 'Days remaining', description: 'Table header for days remaining.', surface: 'shell', maxChars: 20 },
  NLSOpsCliImagingAction: { text: 'Action', description: 'Table header for action.', surface: 'shell', maxChars: 15 },
  NLSOpsCliImagingClose: { text: 'Close', description: 'Close button label.', surface: 'shell', maxChars: 15 },
  NLSOpsCliFormFPatientName: { text: 'Patient name', description: 'Template field label for patient name.', surface: 'shell', maxChars: 25 },
  NLSOpsCliFormFPatientAge: { text: 'Patient age', description: 'Template field label for patient age.', surface: 'shell', maxChars: 20 },
  NLSOpsCliFormFChildrenCount: { text: 'Living children', description: 'Template field label for living children.', surface: 'shell', maxChars: 25 },
  NLSOpsCliFormFIndication: { text: 'Indication', description: 'Template field label for indication.', surface: 'shell', maxChars: 20 },
  NLSOpsCliFormFMachineAsset: { text: 'Machine asset', description: 'Template field label for machine asset.', surface: 'shell', maxChars: 25 },
  NLSOpsCliFormFDoctorRef: { text: 'Doctor reference', description: 'Template field label for doctor reference.', surface: 'shell', maxChars: 25 },
  NLSOpsCliFormFDeclarationSigned: { text: 'Declaration signed', description: 'Template field label for declaration signed.', surface: 'shell', maxChars: 30 },
  NLSOpsCliFormFSignedCopyPhoto: { text: 'Signed copy photo', description: 'Template field label for signed copy photo.', surface: 'shell', maxChars: 30 },
  NLSOpsCliLeadApronPhoto: { text: 'Apron photo', description: 'Template field label for lead apron photo.', surface: 'shell', maxChars: 25 },
  NLSOpsCliLeadApronResult: { text: 'Inspection result', description: 'Template field label for inspection result.', surface: 'shell', maxChars: 25 },
  NLSOpsCliSpokeOpeningPhoto: { text: 'Spoke opening photo', description: 'Template field label for spoke opening inspection photo.', surface: 'shell', maxChars: 30 },
  NLSOpsCliImagingFormFHeading: { text: 'PCPNDT Form F register', description: 'Heading for Form F section', surface: 'shell', maxChars: 30 },
  NLSOpsCliImagingTableAria: { text: 'PCPNDT Form F register entries', description: 'Aria label for Form F table', surface: 'shell', maxChars: 40 },
  NLSOpsCliImagingPrecheckAria: { text: 'Pre-check results and warnings', description: 'Aria label for pre-check region', surface: 'shell', maxChars: 40 },
  NLSOpsCliImagingSelectMachinePlaceholder: { text: 'Select ultrasound machine...', description: 'Placeholder option for machine select', surface: 'shell', maxChars: 35 },
  NLSOpsCliImagingSelectDoctorPlaceholder: { text: 'Select performing doctor...', description: 'Placeholder option for doctor select', surface: 'shell', maxChars: 35 },
  NLSOpsCliImagingMonthlyDay5Note: { text: 'Due on the 5th of each month', description: 'Subtext for monthly schedule', surface: 'shell', maxChars: 35 },
  NLSOpsCliImagingQuarterlySwapNote: { text: 'Quarterly TLD badge exchange', description: 'Subtext for quarterly TLD swap', surface: 'shell', maxChars: 35 },
  NLSOpsCliImagingSignedPhotoPlaceholder: { text: 'Storage path of signed Form F copy', description: 'Placeholder for signed photo path', surface: 'shell', maxChars: 40 },
  NLSOpsCliIndicationRoutine: { text: 'Routine obstetric', description: 'Indication option', surface: 'shell', maxChars: 25 },
  NLSOpsCliIndicationFoetalAnomaly: { text: 'Foetal anomaly scan', description: 'Indication option', surface: 'shell', maxChars: 25 },
  NLSOpsCliIndicationGrowth: { text: 'Growth retardation', description: 'Indication option', surface: 'shell', maxChars: 25 },
  NLSOpsCliIndicationOther: { text: 'Other medical indication', description: 'Indication option', surface: 'shell', maxChars: 25 },
  // Clinics Sample Chain of Custody (R39 M4.b, SO-D228, SO-D229)
  NLSOpsTabClinicsSamples: { text: 'Sample Custody', description: 'Section tab label for Clinics Sample Chain of Custody.', surface: 'shell', maxChars: 25 },
  NLSOpsSampleCustodySubtitle: { text: 'Sample chain of custody tracking (scan barcodes, cold box proof, runner OTP hand-off, lab receipt)', description: 'Sample custody subtitle description', surface: 'shell', maxChars: 100 },
  NLSOpsSampleScanBarcode: { text: 'Scan barcode', description: 'Input label for barcode scanner', surface: 'shell', maxChars: 25 },
  NLSOpsSampleBarcodePlaceholder: { text: 'Scan or type sample barcode', description: 'Placeholder for barcode scanner input', surface: 'shell', maxChars: 40 },
  NLSOpsSampleAddBarcode: { text: 'Add barcode', description: 'Button to add scanned barcode', surface: 'shell', maxChars: 20 },
  NLSOpsSampleCreateBag: { text: 'Create Sample Bag', description: 'Button to create a new sample bag', surface: 'shell', maxChars: 25 },
  NLSOpsSampleStageTimeline: { text: 'Stage Timeline', description: 'Header for sample bag stage timeline', surface: 'shell', maxChars: 25 },
  NLSOpsSampleAttachColdBoxPhoto: { text: 'Attach Cold Box Photo', description: 'Button to attach cold box temperature photo', surface: 'shell', maxChars: 30 },
  NLSOpsSamplePhotoPath: { text: 'Photo path', description: 'Label for cold box photo storage path', surface: 'shell', maxChars: 20 },
  NLSOpsSampleSavePhoto: { text: 'Save photo', description: 'Button to save cold box photo', surface: 'shell', maxChars: 20 },
  NLSOpsSampleStartHandover: { text: 'Handover to Runner', description: 'Button to generate runner handover token', surface: 'shell', maxChars: 25 },
  NLSOpsSampleLabReceipt: { text: 'Lab receipt', description: 'Button to begin lab receipt workflow', surface: 'shell', maxChars: 20 },
  NLSOpsSampleReject: { text: 'Reject', description: 'Button or label for rejecting a sample', surface: 'shell', maxChars: 15 },
  NLSOpsSampleConfirmLabReceipt: { text: 'Confirm lab receipt', description: 'Button to submit lab sample receipt and rejections', surface: 'shell', maxChars: 25 },
  NLSOpsSampleRunnerPhone: { text: 'Registered runner phone', description: 'Label for runner phone input on handover page', surface: 'shell', maxChars: 35 },
  NLSOpsSampleSendOtp: { text: 'Send OTP', description: 'Button to request phone OTP for handover', surface: 'shell', maxChars: 20 },
  NLSOpsSampleEnterOtp: { text: 'Enter 6-digit OTP', description: 'Label for OTP input on handover page', surface: 'shell', maxChars: 25 },
  NLSOpsSampleConfirmHandover: { text: 'Confirm handover', description: 'Button to complete sample handover with OTP', surface: 'shell', maxChars: 25 },
  NLSOpsSampleHandoverConfirmed: { text: 'Handover confirmed', description: 'Heading when sample handover is successfully verified', surface: 'shell', maxChars: 30 },
  NLSOpsSampleHandoverInvalid: { text: 'Invalid or expired handover token', description: 'Error message for invalid handover token', surface: 'shell', maxChars: 45 },
  NLSOpsSampleHandoverLoading: { text: 'Loading handover details...', description: 'Loading message on handover page', surface: 'shell', maxChars: 35 },
  NLSOpsSampleHandoverTitle: { text: 'Sample Chain of Custody Hand-off', description: 'Heading for sample handover page', surface: 'shell', maxChars: 40 },
  NLSOpsSampleHandoverSubtitle: { text: 'Confirm receipt of sample bag from clinic / collection centre.', description: 'Subtitle on sample handover page', surface: 'shell', maxChars: 80 },
  NLSOpsSampleReasonHemolyzed: { text: 'Hemolyzed', description: 'Sample reject reason: hemolyzed', surface: 'shell', maxChars: 20 },
  NLSOpsSampleReasonLeaked: { text: 'Leaked in transit', description: 'Sample reject reason: leaked', surface: 'shell', maxChars: 25 },
  NLSOpsSampleReasonLabelUnreadable: { text: 'Label unreadable', description: 'Sample reject reason: label unreadable', surface: 'shell', maxChars: 25 },
  NLSOpsSampleReasonInsufficient: { text: 'Insufficient quantity', description: 'Sample reject reason: insufficient quantity', surface: 'shell', maxChars: 25 },
  NLSOpsSampleReasonTempExcursion: { text: 'Temperature excursion', description: 'Sample reject reason: temperature excursion', surface: 'shell', maxChars: 25 },
  NLSOpsSampleReasonOther: { text: 'Other rejection reason', description: 'Sample reject reason: other', surface: 'shell', maxChars: 25 },
  NLSOpsSampleInvalidBarcodeFormat: { text: 'Invalid sample barcode format (alphanumeric, 4-40 chars).', description: 'Error when sample barcode fails format validation', surface: 'shell', maxChars: 65 },
  NLSOpsSampleBarcodeAlreadyScanned: { text: 'Barcode already scanned into this bag.', description: 'Error when barcode is already in the scanned list', surface: 'shell', maxChars: 50 },
  NLSOpsSampleAriaScannerSection: { text: 'Sample Bag Scanner', description: 'Aria label for scanner section in sample custody tab', surface: 'shell', maxChars: 30 },
  NLSOpsSampleAriaScannedBarcodes: { text: 'Scanned Barcodes', description: 'Aria label for scanned barcodes container', surface: 'shell', maxChars: 30 },
  NLSOpsSampleAriaAttachPhotoDialog: { text: 'Attach Photo Dialog', description: 'Aria label for attach photo dialog', surface: 'shell', maxChars: 30 },
  NLSOpsSampleAriaHandoverDialog: { text: 'Runner Handover Details', description: 'Aria label for runner handover dialog', surface: 'shell', maxChars: 35 },
  NLSOpsSampleHandoverToken: { text: 'Runner Handover Token:', description: 'Label for runner handover token', surface: 'shell', maxChars: 30 },
  NLSOpsSampleUrl: { text: 'URL:', description: 'Label for URL link', surface: 'shell', maxChars: 15 },
  NLSOpsSampleDone: { text: 'Done', description: 'Done button label in sample custody dialogs', surface: 'shell', maxChars: 15 },
  NLSOpsSampleCancel: { text: 'Cancel', description: 'Cancel button label in sample custody dialogs', surface: 'shell', maxChars: 15 },
  NLSOpsSampleAriaLabReceiptForm: { text: 'Lab Receipt Form', description: 'Aria label for lab receipt dialog', surface: 'shell', maxChars: 30 },
  NLSOpsSampleLabReceiptSubtitle: { text: 'Mark rejected barcodes below; all un-rejected barcodes will be marked received.', description: 'Instruction text in lab receipt dialog', surface: 'shell', maxChars: 90 },
  NLSOpsSampleLoadingBags: { text: 'Loading bags...', description: 'Loading indicator text for sample bags', surface: 'shell', maxChars: 25 },
  NLSOpsSampleNoBagsFound: { text: 'No sample bags found.', description: 'Empty state message when no sample bags exist', surface: 'shell', maxChars: 35 },
  NLSOpsSampleAriaTimeline: { text: 'Sample Stage Timeline', description: 'Aria label for sample stage timeline', surface: 'shell', maxChars: 30 },
  NLSOpsSampleAriaBagsList: { text: 'Sample Bags', description: 'Aria label for sample bags list', surface: 'shell', maxChars: 25 },
  NLSOpsSampleAriaRemoveBarcode: { text: 'Remove barcode {barcode}', description: 'Aria label for removing a scanned barcode', surface: 'shell', maxChars: 50 },
  NLSOpsSampleRejectBarcodeAction: { text: 'Reject {barcode}', description: 'Button label to reject barcode in lab receipt', surface: 'shell', maxChars: 50 },
  NLSOpsSampleRejectingBarcodeAction: { text: 'Rejecting: {reason}', description: 'Button label when rejecting barcode with reason in lab receipt', surface: 'shell', maxChars: 50 },
  NLSOpsSampleBarcodesCount: { text: 'Barcodes ({count}):', description: 'Label for barcode count in bag card', surface: 'shell', maxChars: 30 },
  NLSOpsSampleCopyLink: { text: 'Copy Link', description: 'Button to copy handover link to clipboard', surface: 'shell', maxChars: 20 },
  NLSOpsSampleLinkCopied: { text: 'Link Copied', description: 'Badge indicating handover link was copied', surface: 'shell', maxChars: 20 },
  NLSOpsSampleColdBoxPhotoThumbnail: { text: 'Cold box photo thumbnail', description: 'Alt text for cold box photo thumbnail image', surface: 'shell', maxChars: 35 },
  NLSOpsSampleUploadingPhoto: { text: 'Uploading photo...', description: 'Status message while photo upload is in progress', surface: 'shell', maxChars: 25 },
  NLSOpsSampleOtpExhaustedOrNotRegistered: { text: 'OTP attempts exhausted or phone number not registered.', description: 'Error message when OTP cannot be sent for runner', surface: 'shell', maxChars: 65 },
  NLSOpsSampleMetaPlaceLabel: { text: 'Place:', description: 'Label for collection centre place on runner handover page', surface: 'shell', maxChars: 20 },
  NLSOpsSampleMetaBagIdLabel: { text: 'Bag ID:', description: 'Label for bag ID on runner handover page', surface: 'shell', maxChars: 20 },
  NLSOpsSampleMetaBarcodesLabel: { text: 'Barcodes:', description: 'Label for barcodes count on runner handover page', surface: 'shell', maxChars: 20 },
  NLSOpsSamplePhonePlaceholder: { text: '+91...', description: 'Placeholder for runner phone number input', surface: 'shell', maxChars: 20 },
  NLSOpsSampleOtpPlaceholder: { text: '123456', description: 'Placeholder for 6-digit OTP input', surface: 'shell', maxChars: 15 },
  NLSOpsSampleConfirmedCount: { text: 'Confirmed {count} samples.', description: 'Confirmation message showing count of confirmed samples', surface: 'shell', maxChars: 40 },
  NLSOpsBookingResourceDoctorPlaceholder: { text: 'e.g. Dr. Sharma', description: 'Placeholder for doctor name input', surface: 'shell', maxChars: 30 },
  NLSOpsBookingResourceAssetPlaceholder: { text: 'e.g. Machine ID', description: 'Placeholder for machine asset ID input', surface: 'shell', maxChars: 30 },

  // Clinics Narcotics Vault (R39 M9.b, SO-P267, SO-D230, SO-D235)
  NLSOpsTabClinicsVault: { text: 'Narcotics Vault', description: 'Clinics narcotics vault and custody tab', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsVaultSubtitle: { text: 'Narcotics custody ledger, two-person counts, and prescription verification', description: 'Clinics vault subtitle', surface: 'shell', maxChars: 90 },
  NLSOpsClinicsVaultSelectVault: { text: 'Select narcotics vault', description: 'Label for vault select dropdown', surface: 'shell', maxChars: 30 },
  NLSOpsClinicsVaultNoVaults: { text: 'No narcotics vaults found. Create an asset with category "Narcotics vault" in Assets.', description: 'Notice when no narcotics vault assets exist', surface: 'shell', maxChars: 90 },
  NLSOpsClinicsVaultAddItem: { text: 'Add Item', description: 'Button to add narcotic item', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultRecordMove: { text: 'Record Move', description: 'Button to record narcotic move', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultStartCount: { text: 'Start Count', description: 'Button to start two-person count', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultSubmitTally: { text: 'Submit Tally', description: 'Button to submit own count tally', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultExportCsv: { text: 'Export Register (CSV)', description: 'Button to export narcotics monthly register', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsVaultItemName: { text: 'Item Name', description: 'Label for item name', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsVaultItemNamePlaceholder: { text: 'e.g. Morphine Sulfate 10mg/ml', description: 'Placeholder for item name', surface: 'shell', maxChars: 40 },
  NLSOpsClinicsVaultSchedule: { text: 'Schedule', description: 'Label for narcotic schedule', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultUnit: { text: 'Unit', description: 'Label for narcotic packaging unit', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultBalance: { text: 'Balance', description: 'Label for item stock balance', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultMoveKind: { text: 'Move Type', description: 'Label for narcotic move kind', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultMoveQty: { text: 'Quantity', description: 'Label for move quantity', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultMoveNote: { text: 'Note (max 240 chars)', description: 'Label for move note', surface: 'shell', maxChars: 30 },
  NLSOpsClinicsVaultMoveWitness: { text: 'Witness', description: 'Label for move witness', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultMoveWitnessRequired: { text: 'Wastage requires an active witness', description: 'Validation error when witness is missing on wastage', surface: 'shell', maxChars: 50 },
  NLSOpsClinicsVaultRxPhoto: { text: 'Prescription Photo (Schedule X / NDPS Issue)', description: 'Label for prescription photo upload', surface: 'shell', maxChars: 55 },
  NLSOpsClinicsVaultViewRx: { text: 'View Rx', description: 'Button to view prescription photo', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsVaultRxDenied: { text: 'Only the workspace owner or a registered pharmacist today can view prescription photos.', description: 'Permission error when Rx photo access is denied', surface: 'shell', maxChars: 100 },
  NLSOpsClinicsVaultCountCounter: { text: 'Counter', description: 'Label for primary count taker', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultCountWitness: { text: 'Witness', description: 'Label for count witness', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultCountState: { text: 'Count State', description: 'Label for count state', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultCountExpected: { text: 'Expected', description: 'Label for expected balance', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultCountTally: { text: 'Tally', description: 'Label for physical count tally', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultCountBlindNotice: { text: 'Count in progress. Tallies remain confidential until both counter and witness have submitted.', description: 'Notice for blind two-person count', surface: 'shell', maxChars: 100 },
  NLSOpsClinicsVaultCountMismatchNotice: { text: 'Narcotic mismatch detected. A compliance request has been raised.', description: 'Notice when count mismatch occurs', surface: 'shell', maxChars: 80 },
  NLSOpsClinicsVaultCountDisputedNotice: { text: 'Counter and witness tallies do not match. Count disputed.', description: 'Notice when count is disputed', surface: 'shell', maxChars: 80 },
  NLSOpsClinicsVaultCountAgreedNotice: { text: 'Count agreed.', description: 'Notice when count is agreed', surface: 'shell', maxChars: 30 },
  NLSOpsClinicsVaultRecentMoves: { text: 'Recent Moves', description: 'Header for recent moves ledger', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsVaultRecentCounts: { text: 'Recent Counts', description: 'Header for recent two-person counts', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsVaultNoItems: { text: 'No narcotic items in this vault.', description: 'Empty state for vault items', surface: 'shell', maxChars: 40 },
  NLSOpsClinicsVaultNoMoves: { text: 'No move history recorded.', description: 'Empty state for moves', surface: 'shell', maxChars: 40 },
  NLSOpsClinicsVaultNoCounts: { text: 'No counts recorded.', description: 'Empty state for counts', surface: 'shell', maxChars: 40 },
  NLSOpsClinicsVaultArchive: { text: 'Archive', description: 'Button to archive item', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsVaultArchivedBadge: { text: 'Archived', description: 'Badge for archived items', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsVaultSelectVaultFirst: { text: 'Select a vault to view items and moves', description: 'Prompt to select vault first', surface: 'shell', maxChars: 50 },
  NLSOpsClinicsVaultSelectItem: { text: 'Select Item', description: 'Label for selecting item in move dialog', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsVaultRxModalTitle: { text: 'Prescription Photo', description: 'Title of Rx photo dialog', surface: 'shell', maxChars: 30 },
  NLSOpsClinicsVaultSave: { text: 'Save', description: 'Save button in vault dialogs', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsVaultCancel: { text: 'Cancel', description: 'Cancel button in vault dialogs', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsVaultClose: { text: 'Close', description: 'Close button in vault dialogs', surface: 'shell', maxChars: 15 },

  // Clinics Hub & Spokes (R39 M5.b, SO-P213)
  NLSOpsTabClinicsHub: { text: 'Hub & Spokes', description: 'Clinics hub and spokes tab', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsHubSubtitle: { text: 'Collection centre spoke performance, sample logistics, and opening checklist tracking', description: 'Clinics hub subtitle', surface: 'shell', maxChars: 100 },
  NLSOpsClinicsHubSelectHub: { text: 'Select Hub', description: 'Label for hub selector', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsHubNoHubs: { text: 'No hub or lab sites found. Create a site with kind Lab or Hub in Places.', description: 'Notice when no hub sites exist', surface: 'shell', maxChars: 90 },
  NLSOpsClinicsHubDays7: { text: '7 Days', description: 'Toggle button for 7 days window', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsHubDays30: { text: '30 Days', description: 'Toggle button for 30 days window', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsHubSpokeName: { text: 'Spoke Centre', description: 'Column header for spoke name', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsHubOpeningPct: { text: 'Opening %', description: 'Column header for opening checklist compliance', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsHubColdBoxPct: { text: 'Cold Box %', description: 'Column header for cold box photo proof percentage', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsHubPickupsPct: { text: 'Pickups %', description: 'Column header for on-time pickups percentage', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsHubRejectedPct: { text: 'Rejected %', description: 'Column header for rejected barcode percentage', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsHubScore: { text: 'Score', description: 'Column header for composite spoke score', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsHubNoSpokes: { text: 'No collection centres linked to this hub.', description: 'Empty state for spokes table', surface: 'shell', maxChars: 50 },
  NLSOpsClinicsHubSelectHubFirst: { text: 'Select a hub to view spoke performance.', description: 'Prompt to select hub first', surface: 'shell', maxChars: 50 },
  NLSOpsClinicsHubSetHub: { text: 'Parent Hub / Processing Lab', description: 'Field label for setting hub on collection centre', surface: 'shell', maxChars: 40 },
  NLSOpsClinicsHubNone: { text: 'None', description: 'Option for no parent hub', surface: 'shell', maxChars: 15 },

  // Clinics Specialty Loops (R39 M17, SO-P268, SO-D227)
  NLSOpsTabClinicsLoops: { text: 'Specialty Loops', description: 'Section tab label for Clinics Specialty Loops.', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsLoopsSubtitle: { text: 'Job orders, dialyzer reuse logs, and IVF witness verification', description: 'Clinics loops subtitle', surface: 'shell', maxChars: 100 },
  NLSOpsClinicsLoopsJobOrders: { text: 'Job Orders', description: 'Header for Job Orders board', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsLoopsNewOrder: { text: 'New Order', description: 'Button to create job order', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsLoopsDialyzers: { text: 'Dialyzer Reuse', description: 'Header for Dialyzer reuse section', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsLoopsRegisterDialyzer: { text: 'Register Dialyzer', description: 'Button to register dialyzer', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsLoopsIvfWitness: { text: 'IVF Witness Queue', description: 'Header for IVF witness queue', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsLoopsNewWitnessStep: { text: 'New Witness Step', description: 'Button to create witness step', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsLoopsOverdueChip: { text: 'Overdue', description: 'Chip indicating overdue job order', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsLoopsRemakeChip: { text: 'Remake #{count}', description: 'Chip indicating remake count', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsLoopsAdvanceStage: { text: 'Advance Stage', description: 'Button to advance job order stage', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsLoopsMarkRemake: { text: 'Request Remake', description: 'Button to request remake', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsLoopsRecordUse: { text: 'Record Reuse', description: 'Button to record dialyzer reuse', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsLoopsConfirmWitness: { text: 'Confirm', description: 'Button to confirm witness step', surface: 'shell', maxChars: 15 },
  NLSOpsClinicsLoopsBlindConfirmPrompt: { text: 'Re-enter case token to confirm (blind):', description: 'Prompt for blind confirmation', surface: 'shell', maxChars: 50 },
  NLSOpsClinicsLoopsNoJobOrders: { text: 'No job orders found.', description: 'Empty state for job orders', surface: 'shell', maxChars: 40 },
  NLSOpsClinicsLoopsNoDialyzers: { text: 'No dialyzers registered.', description: 'Empty state for dialyzers', surface: 'shell', maxChars: 40 },
  NLSOpsClinicsLoopsNoWitnessSteps: { text: 'No pending witness steps for you.', description: 'Empty state for IVF witness steps', surface: 'shell', maxChars: 50 },
  NLSOpsClinicsTurnaroundOverrideTitle: { text: 'Room Turnaround Required', description: 'Title of room turnaround override dialog', surface: 'shell', maxChars: 35 },
  NLSOpsClinicsTurnaroundOverrideNotice: { text: 'Room turnaround record missing after previous booking. Owner or Admin override required.', description: 'Notice explaining turnaround requirement', surface: 'shell', maxChars: 90 },
  NLSOpsClinicsTurnaroundOverrideReason: { text: 'Override Reason', description: 'Label for override reason input', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsTurnaroundOverrideConfirm: { text: 'Override & Check In', description: 'Button to override and check in', surface: 'shell', maxChars: 30 },

  // Clinics Teleconsultation (R39 M18, SO-P268, SO-D236)
  NLSOpsTabClinicsTele: { text: 'Teleconsult', description: 'Section tab label for Clinics Teleconsultation.', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsTeleSubtitle: { text: 'Consent tracking, ID verification by link, and post-consult field orders', description: 'Clinics teleconsult subtitle', surface: 'shell', maxChars: 100 },
  NLSOpsClinicsTeleSessions: { text: 'Teleconsult Sessions', description: 'Header for teleconsult sessions', surface: 'shell', maxChars: 30 },
  NLSOpsClinicsTeleNewSession: { text: 'New Session', description: 'Button to create teleconsult session', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsTeleStartSession: { text: 'Start Session', description: 'Button to start teleconsult session', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsTeleSendLink: { text: 'Send Consent Link', description: 'Button to send consent link', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsTeleResendLink: { text: 'Resend Link', description: 'Button to resend consent link', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsTeleCreateOrder: { text: 'Create Field Order', description: 'Button to create post-consult field order', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsTeleNoSessions: { text: 'No teleconsult sessions found.', description: 'Empty state for teleconsult sessions', surface: 'shell', maxChars: 40 },
  NLSOpsClinicsTeleConsentPending: { text: 'Pending Consent', description: 'Badge for pending consent', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsTeleConsented: { text: 'Consented', description: 'Badge for consented session', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsTeleExpired: { text: 'Expired', description: 'Badge for expired consent', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsTeleRevoked: { text: 'Revoked', description: 'Badge for revoked consent', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsTeleConsentLandingTitle: { text: 'Teleconsultation Consent', description: 'Title of public consent landing page', surface: 'shell', maxChars: 35 },
  NLSOpsClinicsTeleConsentLandingSubtitle: { text: 'Verify your phone number on file to review and submit consent.', description: 'Subtitle of consent landing page', surface: 'shell', maxChars: 80 },
  NLSOpsClinicsTeleConsentLandingTick: { text: 'I agree to the teleconsultation terms and consent to consultation.', description: 'Checkbox label for consent tick', surface: 'shell', maxChars: 80 },
  NLSOpsClinicsTeleConsentLandingSubmit: { text: 'Submit Consent', description: 'Button to submit consent', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsTeleConsentLandingSuccess: { text: 'Consent Confirmed. You may close this page.', description: 'Success message after submitting consent', surface: 'shell', maxChars: 60 },
  NLSOpsClinicsTeleConsentLandingSendOtp: { text: 'Send Code', description: 'Button to send OTP code', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsTeleConsentLandingVerifyOtp: { text: 'Verify Code', description: 'Button to verify OTP code', surface: 'shell', maxChars: 20 },
  NLSOpsClinicsTeleOrderHomeDraw: { text: 'Home Sample Draw', description: 'Option for home draw order', surface: 'shell', maxChars: 25 },
  NLSOpsClinicsTeleOrderMedicineDelivery: { text: 'Medicine Delivery', description: 'Option for medicine delivery order', surface: 'shell', maxChars: 25 },

  // M17 Starter Templates Labels
  NLSOpsCliLn2RefillSupplier: { text: 'Supplier', description: 'Label for LN2 supplier', surface: 'shell', maxChars: 20 },
  NLSOpsCliLn2RefillLevelBefore: { text: 'Level before (cm)', description: 'Level before refill in cm', surface: 'shell', maxChars: 25 },
  NLSOpsCliLn2RefillLevelAfter: { text: 'Level after (cm)', description: 'Level after refill in cm', surface: 'shell', maxChars: 25 },
  NLSOpsCliLn2RefillPhoto: { text: 'Refill photo', description: 'Photo of tank refill proof', surface: 'shell', maxChars: 25 },
  NLSOpsCliAutoclaveCycleNumber: { text: 'Cycle number', description: 'Cycle number of autoclave run', surface: 'shell', maxChars: 20 },
  NLSOpsCliAutoclaveStripPhoto: { text: 'Chemical indicator strip photo', description: 'Photo of indicator strip', surface: 'shell', maxChars: 40 },
  NLSOpsCliAutoclaveCyclePassed: { text: 'Cycle passed indicator check', description: 'Pass or fail of sterilization cycle', surface: 'shell', maxChars: 40 },
  NLSOpsCliPanchakarmaSanitized: { text: 'Treatment room sanitized', description: 'Checklist item for sanitization', surface: 'shell', maxChars: 35 },
  NLSOpsCliPanchakarmaDroniCleaned: { text: 'Droni massage table cleaned and dried', description: 'Checklist item for Droni cleaning', surface: 'shell', maxChars: 45 },
  NLSOpsCliPanchakarmaLinens: { text: 'Fresh sterile linens placed', description: 'Checklist item for linens', surface: 'shell', maxChars: 35 },
  NLSOpsCliPanchakarmaSteamBox: { text: 'Swedana steam box inspected', description: 'Checklist item for steam box', surface: 'shell', maxChars: 35 },
  NLSOpsCliPanchakarmaTurnaroundPhoto: { text: 'Turnaround photo proof', description: 'Photo proof of clean room', surface: 'shell', maxChars: 30 },
  NLSOpsCliPanchakarmaReady: { text: 'Room ready for next patient', description: 'Checklist item for readiness', surface: 'shell', maxChars: 35 },

  // M17 Request Kinds Labels
  NLSOpsRequestKindJobOrderOverdue: { text: 'Job Order Overdue', description: 'Job order overdue request kind label', surface: 'shell', maxChars: 30 },
  NLSOpsRequestKindDialyzerReuseFail: { text: 'Dialyzer Reuse Safety Failure', description: 'Dialyzer reuse failure request kind label', surface: 'shell', maxChars: 40 },
  NLSOpsRequestKindIvfWitnessMismatch: { text: 'IVF Witness Mismatch', description: 'IVF witness mismatch request kind label', surface: 'shell', maxChars: 30 },
  NLSOpsRequestKindAutoclaveFail: { text: 'Autoclave Sterilization Failure', description: 'Autoclave failure request kind label', surface: 'shell', maxChars: 40 },
  NLSOpsRequestKindTeleDelivery: { text: 'Teleconsult Medicine Delivery', description: 'Request kind for medicine delivery after teleconsult', surface: 'shell', maxChars: 40 },

  // M18 Legal Role Label
  NLSOpsCliLegalRoleRegisteredMedicalPractitioner: { text: 'Registered Medical Practitioner', description: 'Legal role label for RMP under Telemedicine guidelines', surface: 'shell', maxChars: 40 },

  // M11 Connector Framework Labels
  NLSOpsCsvEntityConnectorEvents: { text: 'Connector Events', description: 'CSV import entity label for connector events', surface: 'shell', maxChars: 30 },
  NLSOpsCsvColConnectorEventId: { text: 'Event ID', description: 'CSV column label for connector event id', surface: 'shell', maxChars: 20 },
  NLSOpsCsvColConnectorRefKind: { text: 'Reference Type', description: 'CSV column label for connector ref kind', surface: 'shell', maxChars: 25 },
  NLSOpsCsvColConnectorRef: { text: 'Reference', description: 'CSV column label for connector reference identifier', surface: 'shell', maxChars: 30 },
  NLSOpsCsvColConnectorStatus: { text: 'Status', description: 'CSV column label for connector event status', surface: 'shell', maxChars: 25 },
  NLSOpsCsvColConnectorOccurredAt: { text: 'Occurred At', description: 'CSV column label for connector event timestamp', surface: 'shell', maxChars: 25 },
  NLSOpsCsvColConnectorRejectionReason: { text: 'Rejection Reason', description: 'CSV column label for connector rejection reason', surface: 'shell', maxChars: 40 },
  NLSOpsCsvColConnectorId: { text: 'Connector ID', description: 'CSV column label for connector id', surface: 'shell', maxChars: 30 },
  NLSLinkHubClinicsConnectorsTitle: { text: 'Lab / clinic systems', description: 'Link Hub integration title for lab/clinic systems', surface: 'shell', maxChars: 35 },
  NLSLinkHubClinicsConnectorsDesc: { text: 'Inbound webhooks and CSV batch events for laboratory and clinic information systems.', description: 'Link Hub integration description', surface: 'shell', maxChars: 120 },
  NLSLinkHubClinicsConnectorsTag: { text: 'Clinics', description: 'Tag for clinics connectors in Link Hub', surface: 'shell', maxChars: 15 },
  NLSOpsConnectorsPanelTitle: { text: 'Lab / Clinic Systems', description: 'Title of the connectors management panel', surface: 'shell', maxChars: 35 },
  NLSOpsConnectorsEmptyState: { text: 'No connectors configured yet.', description: 'Empty state message when no connectors exist', surface: 'shell', maxChars: 50 },
  NLSOpsConnectorsCreateButton: { text: 'Create Connector', description: 'Button to open create connector modal', surface: 'shell', maxChars: 25 },
  NLSOpsConnectorsCreateModalTitle: { text: 'New Lab / Clinic Connector', description: 'Modal title for creating connector', surface: 'shell', maxChars: 35 },
  NLSOpsConnectorsNameLabel: { text: 'Connector Name', description: 'Label for connector name field', surface: 'shell', maxChars: 25 },
  NLSOpsConnectorsKindLabel: { text: 'Connector Type', description: 'Label for connector kind selector', surface: 'shell', maxChars: 25 },
  NLSOpsConnectorsWebhookUrlLabel: { text: 'Webhook URL', description: 'Label for connector webhook endpoint URL', surface: 'shell', maxChars: 25 },
  NLSOpsConnectorsSecretLabel: { text: 'Signing Secret', description: 'Label for connector webhook secret', surface: 'shell', maxChars: 25 },
  NLSOpsConnectorsSecretNotice: { text: 'Save this secret now. It will never be displayed again.', description: 'Warning notice that secret is shown once', surface: 'shell', maxChars: 80 },
  NLSOpsConnectorsRotateSecretButton: { text: 'Rotate Secret', description: 'Button to rotate connector secret', surface: 'shell', maxChars: 20 },
  NLSOpsConnectorsRotateConfirmTitle: { text: 'Rotate Signing Secret?', description: 'Confirmation title for secret rotation', surface: 'shell', maxChars: 35 },
  NLSOpsConnectorsRotateConfirmBody: { text: 'A 24-hour grace window allows existing integrations to continue with the previous secret while you update your systems.', description: 'Confirmation body for secret rotation', surface: 'shell', maxChars: 160 },
  NLSOpsConnectorsPauseButton: { text: 'Pause', description: 'Button to pause connector', surface: 'shell', maxChars: 15 },
  NLSOpsConnectorsResumeButton: { text: 'Resume', description: 'Button to resume connector', surface: 'shell', maxChars: 15 },
  NLSOpsConnectorsDeleteButton: { text: 'Delete', description: 'Button to delete connector', surface: 'shell', maxChars: 15 },
  NLSOpsConnectorsDeleteConfirmTitle: { text: 'Delete Connector?', description: 'Confirmation title for deleting connector', surface: 'shell', maxChars: 30 },
  NLSOpsConnectorsDeleteConfirmBody: { text: 'This will permanently remove the connector and invalidate all incoming webhooks.', description: 'Confirmation body for deleting connector', surface: 'shell', maxChars: 120 },
  NLSOpsConnectorsEventMapTitle: { text: 'Status Mapping', description: 'Title for event map editor section', surface: 'shell', maxChars: 25 },
  NLSOpsConnectorsEventMapVendorWord: { text: 'Vendor Status Word', description: 'Header for vendor status input in event map', surface: 'shell', maxChars: 30 },
  NLSOpsConnectorsEventMapAllowedStatus: { text: 'Target Status', description: 'Header for target status dropdown in event map', surface: 'shell', maxChars: 25 },
  NLSOpsConnectorsEventMapAddRow: { text: 'Add Mapping', description: 'Button to add row to event map', surface: 'shell', maxChars: 20 },
  NLSOpsConnectorsEventLogTitle: { text: 'Recent Events (Last 50)', description: 'Title for event log table', surface: 'shell', maxChars: 35 },
  NLSOpsConnectorsEventLogEmpty: { text: 'No events received yet.', description: 'Empty state message for event log table', surface: 'shell', maxChars: 40 },
  NLSOpsConnectorsEventColTime: { text: 'Received At', description: 'Column header for received time', surface: 'shell', maxChars: 20 },
  NLSOpsConnectorsEventColRef: { text: 'Reference', description: 'Column header for event reference', surface: 'shell', maxChars: 20 },
  NLSOpsConnectorsEventColStatus: { text: 'Status', description: 'Column header for event status', surface: 'shell', maxChars: 20 },
  NLSOpsConnectorsEventColOutcome: { text: 'Outcome', description: 'Column header for event outcome', surface: 'shell', maxChars: 20 },
  NLSOpsConnectorsEventColReason: { text: 'Reason / Message', description: 'Column header for error reason or message', surface: 'shell', maxChars: 30 },
  NLSOpsConnectorsEventReplayButton: { text: 'Replay', description: 'Button to replay an error event', surface: 'shell', maxChars: 15 },
  NLSOpsConnectorsUploadCsvButton: { text: 'Upload CSV', description: 'Button to upload events CSV file', surface: 'shell', maxChars: 20 },
  NLSOpsConnectorsCopySuccess: { text: 'Copied to clipboard', description: 'Toast notification after copying secret or URL', surface: 'shell', maxChars: 30 },
  NLSOpsConnectorsCopyButton: { text: 'Copy', description: 'Button to copy text', surface: 'shell', maxChars: 15 },
  NLSOpsConnectorsActiveState: { text: 'Active', description: 'Label for active connector state', surface: 'shell', maxChars: 15 },
  NLSOpsConnectorsPausedState: { text: 'Paused', description: 'Label for paused connector state', surface: 'shell', maxChars: 15 },
  NLSOpsConnectorsNotReplayable: { text: 'Not replayable', description: 'Label shown for input error events that cannot be replayed', surface: 'shell', maxChars: 20 },

  // R39 N1.b Fitness & sport
  NLSOpsFitnessTitle: { text: 'Fitness & sport', description: 'Title for Fitness & sport sector hub screen', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessSubtitle: { text: 'Gyms, studios, pools, sports academies and turf venues', description: 'Subtitle for Fitness & sport sector hub screen', surface: 'shell', maxChars: 75 },
  NLSOpsTabFitnessToday: { text: 'Today', description: 'Section tab label for Fitness Today', surface: 'shell', maxChars: 20 },
  NLSOpsTabFitnessPlaces: { text: 'Branches, floors, pool', description: 'Setup tab label for Branches, floors, pool in Fitness sector', surface: 'shell', maxChars: 30 },
  NLSOpsTabFitnessWorkforce: { text: 'Trainers & lifeguards', description: 'Setup tab label for Trainers & lifeguards in Fitness sector', surface: 'shell', maxChars: 30 },
  NLSOpsTabFitnessTemplates: { text: 'Checklists', description: 'Setup tab label for Checklists in Fitness sector', surface: 'shell', maxChars: 20 },
  NLSOpsTabFitnessRosters: { text: 'Rosters', description: 'Setup tab label for Rosters in Fitness sector', surface: 'shell', maxChars: 20 },
  NLSOpsTabFitnessAssets: { text: 'Machines & spots', description: 'Setup tab label for Machines & spots in Fitness sector', surface: 'shell', maxChars: 30 },
  NLSOpsTabFitnessCompliance: { text: 'Certificates', description: 'Setup tab label for Certificates in Fitness sector', surface: 'shell', maxChars: 20 },
  NLSOpsTabFitnessImport: { text: 'Members CSV', description: 'Setup tab label for Members CSV in Fitness sector', surface: 'shell', maxChars: 25 },
  NLSOpsTabFitnessWhatsApp: { text: 'On WhatsApp', description: 'Setup tab label for On WhatsApp in Fitness sector', surface: 'shell', maxChars: 20 },

  NLSShellRailTileFitness: { text: 'Fitness & sport', description: 'Right rail tile label for Fitness & sport', surface: 'shell', maxChars: 25 },
  NLSShellRailShortFitness: { text: 'Fitness', description: 'Right rail short label for Fitness', surface: 'shell', maxChars: 15 },

  NLSOpsLevelBranch: { text: 'Branch', description: 'Place level label for Branch', surface: 'shell', maxChars: 20 },
  NLSOpsLevelGround: { text: 'Ground', description: 'Place level label for Ground', surface: 'shell', maxChars: 20 },
  NLSOpsLevelClubhouse: { text: 'Clubhouse', description: 'Place level label for Clubhouse', surface: 'shell', maxChars: 20 },
  NLSOpsLevelStudio: { text: 'Studio', description: 'Place level label for Studio', surface: 'shell', maxChars: 20 },
  NLSOpsLevelPool: { text: 'Pool', description: 'Place level label for Pool', surface: 'shell', maxChars: 20 },
  NLSOpsLevelCourt: { text: 'Court', description: 'Place level label for Court', surface: 'shell', maxChars: 20 },
  NLSOpsLevelLane: { text: 'Lane', description: 'Place level label for Lane', surface: 'shell', maxChars: 20 },

  NLSOpsAssetsFieldSpotNumber: { text: 'Spot number', description: 'Label for asset spot number field', surface: 'shell', maxChars: 20 },
  NLSOpsAssetsFieldUsageUnit: { text: 'Usage unit', description: 'Label for asset usage unit field', surface: 'shell', maxChars: 20 },
  NLSOpsAssetsUsageUnitNone: { text: 'None', description: 'Usage unit option None', surface: 'shell', maxChars: 15 },
  NLSOpsAssetsUsageUnitHours: { text: 'Hours', description: 'Usage unit option Hours', surface: 'shell', maxChars: 15 },
  NLSOpsAssetsUsageUnitSessions: { text: 'Sessions', description: 'Usage unit option Sessions', surface: 'shell', maxChars: 15 },

  NLSOpsCsvEntityMembers: { text: 'Members', description: 'Import entity name for Members CSV', surface: 'shell', maxChars: 20 },
  NLSOpsCsvColMembersExternalId: { text: 'External ID', description: 'Column label for external member ID', surface: 'shell', maxChars: 20 },
  NLSOpsCsvColMembersTags: { text: 'Tags', description: 'Column label for member tags', surface: 'shell', maxChars: 20 },
  NLSOpsCsvColMembersBranchPath: { text: 'Branch path', description: 'Column label for member home branch path', surface: 'shell', maxChars: 20 },
  NLSOpsCsvColMembersPlanName: { text: 'Plan name', description: 'Column label for booking plan name', surface: 'shell', maxChars: 20 },
  NLSOpsCsvColMembersValidFrom: { text: 'Valid from', description: 'Column label for membership start date', surface: 'shell', maxChars: 20 },
  NLSOpsCsvColMembersValidTo: { text: 'Valid to', description: 'Column label for membership end date', surface: 'shell', maxChars: 20 },
  NLSOpsCsvColMembersSessionsUsed: { text: 'Sessions used', description: 'Column label for attended sessions count', surface: 'shell', maxChars: 20 },
  NLSOpsCsvColMembersConsentWhatsapp: { text: 'Consent WhatsApp', description: 'Column label for WhatsApp consent', surface: 'shell', maxChars: 25 },
  NLSOpsCsvColMembersConsentReminders: { text: 'Consent reminders', description: 'Column label for reminders consent', surface: 'shell', maxChars: 25 },

  NLSOpsFitnessWizardTitle: { text: 'Set up Fitness & sport', description: 'Title for Fitness setup wizard', surface: 'shell', maxChars: 30 },
  NLSOpsFitnessWizardSubtitle: { text: 'Configure your sports facility, branches, zones, and equipment', description: 'Subtitle for Fitness setup wizard', surface: 'shell', maxChars: 70 },
  NLSOpsFitnessWizardStepOrg: { text: 'Organisation', description: 'Step label for Organisation in Fitness wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardStepBranches: { text: 'Branches & grounds', description: 'Step label for Branches in Fitness wizard', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardStepZones: { text: 'Floors & zones', description: 'Step label for Zones in Fitness wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardStepReview: { text: 'Review & create', description: 'Step label for Review in Fitness wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardBtnBack: { text: 'Back', description: 'Button label for previous step in wizard', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardBtnNext: { text: 'Next', description: 'Button label for next step in wizard', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardOrgNameLabel: { text: 'Organisation name', description: 'Label for Organisation name input in Fitness wizard', surface: 'shell', maxChars: 30 },
  NLSOpsFitnessWizardOrgNamePlaceholder: { text: 'e.g. FitZone, City Sports Club', description: 'Placeholder for Organisation name input', surface: 'shell', maxChars: 40 },
  NLSOpsFitnessWizardAddBranch: { text: 'Add branch', description: 'Button label to add a branch in wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardRemoveBranch: { text: 'Remove', description: 'Button label to remove a branch in wizard', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardBranchNameLabel: { text: 'Branch name', description: 'Label for Branch name input in wizard', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardBranchLevelLabel: { text: 'Type', description: 'Label for branch type select in wizard', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardPresetLabel: { text: 'Preset', description: 'Label for fitness preset select in wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardMemberModeLabel: { text: 'Member mode', description: 'Label for member mode select in wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardMemberModeFull: { text: 'Full mode (managed here)', description: 'Option for full member mode', surface: 'shell', maxChars: 30 },
  NLSOpsFitnessWizardMemberModeBeside: { text: 'Beside mode (external billing)', description: 'Option for beside member mode', surface: 'shell', maxChars: 35 },
  NLSOpsFitnessWizardBesideSystemLabel: { text: 'Billing software', description: 'Label for beside system select in wizard', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardBesideSystemNameLabel: { text: 'Software name', description: 'Label for custom beside system name in wizard', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardWomenOnlyLabel: { text: 'Women-only branch', description: 'Label for women-only checkbox in wizard', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardOpen24hLabel: { text: 'Open 24 hours', description: 'Label for 24-hour open checkbox in wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardTierLabel: { text: 'Gym tier', description: 'Label for gym tier select in wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardTierBudget: { text: 'Budget', description: 'Option for Budget tier', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardTierMid: { text: 'Mid-range', description: 'Option for Mid-range tier', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardTierPremium: { text: 'Premium', description: 'Option for Premium tier', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardClientNameLabel: { text: 'Client / Society name', description: 'Label for Clubhouse client parent name input', surface: 'shell', maxChars: 30 },
  NLSOpsFitnessWizardClientNamePlaceholder: { text: 'e.g. Prestige Lakeside RWA', description: 'Placeholder for Clubhouse client parent name', surface: 'shell', maxChars: 35 },
  NLSOpsFitnessWizardFloorsZonesTitle: { text: 'Floors, zones & ranges', description: 'Title for Floors & zones section in wizard', surface: 'shell', maxChars: 30 },
  NLSOpsFitnessWizardAddFloor: { text: 'Add floor / zone', description: 'Button label to add a floor or zone in wizard', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardRemoveFloor: { text: 'Remove', description: 'Button label to remove a floor or zone', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardFloorNameLabel: { text: 'Floor / zone name', description: 'Label for floor or zone name input', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardFloorNamePlaceholder: { text: 'e.g. Ground Floor, Cardio Zone', description: 'Placeholder for floor or zone name', surface: 'shell', maxChars: 35 },
  NLSOpsFitnessWizardAddRange: { text: 'Add range', description: 'Button label to add a range generator in wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardRemoveRange: { text: 'Remove', description: 'Button label to remove a range generator', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardRangePrefixLabel: { text: 'Prefix', description: 'Label for range prefix input in wizard', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardRangeFromLabel: { text: 'From', description: 'Label for range start number in wizard', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardRangeToLabel: { text: 'To', description: 'Label for range end number in wizard', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessWizardRangeLevelLabel: { text: 'Unit type', description: 'Label for range level select in wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardPoolLanesLabel: { text: 'Pool lanes count (1–12)', description: 'Label for pool lanes count input in wizard', surface: 'shell', maxChars: 30 },
  NLSOpsFitnessWizardTotalPlaces: { text: 'Total places to create: {count}', description: 'Live count of places in wizard', surface: 'shell', maxChars: 40 },
  NLSOpsFitnessWizardLimitWarning: { text: 'Limit exceeded: Maximum 500 places allowed in setup wizard.', description: 'Error message when places count exceeds 500', surface: 'shell', maxChars: 70 },
  NLSOpsFitnessWizardReviewTitle: { text: 'Review configuration', description: 'Title for review step in wizard', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardCreateButton: { text: 'Create sports facilities', description: 'Button label to create places in wizard', surface: 'shell', maxChars: 30 },
  NLSOpsFitnessWizardCreating: { text: 'Creating...', description: 'Button label while creating places in wizard', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessWizardSuccess: { text: 'Fitness & sport facilities set up successfully!', description: 'Success toast message in wizard', surface: 'shell', maxChars: 60 },
  NLSOpsFitnessWizardReadOnlyEmpty: { text: 'Setup in progress. Only workspace owners and admins can configure fitness places.', description: 'Read-only empty state message in wizard', surface: 'shell', maxChars: 85 },
  NLSOpsFitnessWizardErrorGeneric: { text: 'Failed to set up fitness places. Please try again.', description: 'Generic error message for wizard', surface: 'shell', maxChars: 60 },
  NLSOpsFitnessWizardPermissionDenied: { text: 'Only workspace owners and admins can set up fitness places.', description: 'Permission denied error for wizard', surface: 'shell', maxChars: 65 },
  NLSOpsFitnessWizardAlreadyExists: { text: 'Places have already been created for this workspace.', description: 'Already exists error for wizard', surface: 'shell', maxChars: 60 },
  NLSOpsFitnessWizardModuleDisabled: { text: 'Fitness & sport module is not enabled for this workspace.', description: 'Module disabled error for wizard', surface: 'shell', maxChars: 65 },
  NLSOpsFitnessWizardPresetGroupGyms: { text: 'Gyms & studios', description: 'Preset group label for Gyms & studios', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardPresetGroupWater: { text: 'Water & sport', description: 'Preset group label for Water & sport', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessWizardPresetGroupProgrammes: { text: 'Programmes & specialized', description: 'Preset group label for Programmes', surface: 'shell', maxChars: 30 },

  NLSOpsFitnessTodayTitle: { text: 'Branches & facilities', description: 'Title for Fitness Today branches card', surface: 'shell', maxChars: 30 },
  NLSOpsFitnessTodayActionBranches: { text: 'Branches, floors, pool', description: 'Action button label on Fitness Today', surface: 'shell', maxChars: 30 },
  NLSOpsFitnessTodayActionMachines: { text: 'Machines & spots', description: 'Action button label on Fitness Today', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessTodayActionMembersCsv: { text: 'Members CSV', description: 'Action button label on Fitness Today', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessTodayChipFull: { text: 'Full', description: 'Full member mode chip', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessTodayChipBeside: { text: 'Beside', description: 'Beside member mode chip', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessTodayChipWomenOnly: { text: 'Women-only', description: 'Women-only branch chip', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessTodayChip24h: { text: '24-hour', description: '24-hour branch chip', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessTodayColBranch: { text: 'Branch', description: 'Column label for branch name', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessTodayColType: { text: 'Type', description: 'Column label for branch type', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessTodayColPreset: { text: 'Preset', description: 'Column label for branch preset', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessTodayColFloors: { text: 'Floors / Zones', description: 'Column label for floors/zones count', surface: 'shell', maxChars: 20 },
  NLSOpsFitnessTodayColStudios: { text: 'Studios', description: 'Column label for studios count', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessTodayColPools: { text: 'Pools', description: 'Column label for pools count', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessTodayColCourts: { text: 'Courts', description: 'Column label for courts count', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessTodayColLanes: { text: 'Lanes', description: 'Column label for lanes count', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessTodayColRooms: { text: 'Rooms', description: 'Column label for rooms count', surface: 'shell', maxChars: 15 },
  NLSOpsFitnessTodayNoBranches: { text: 'No branches configured yet.', description: 'Empty state message for branches table', surface: 'shell', maxChars: 35 },
  NLSOpsFitnessTodayModeCardTitle: { text: 'Operations mode', description: 'Title for mode card on Today screen', surface: 'shell', maxChars: 25 },
  NLSOpsFitnessTodayBesideNotice: { text: 'Beside mode lets a gym keep its billing software while floor operations run here.', description: 'Footnote notice explaining beside mode', surface: 'shell', maxChars: 90 },

} as const satisfies Record<string, NlsStringMetadata>

export type NlsKey = keyof typeof STRINGS_NLS
export type NlsStringMap = Record<NlsKey, string>

export const NLS_KEYS = Object.keys(STRINGS_NLS) as NlsKey[]
